import { n as __export, t as __commonJSMin } from "./chunk-DPPnyiuk.mjs";
import { r as LinearWebhookClient } from "./webhooks-CgtCOSvZ.mjs";

//#region src/types.ts
/**
* The error types returned by the Linear API
*/
let LinearErrorType = /* @__PURE__ */ function(LinearErrorType$1) {
	LinearErrorType$1["FeatureNotAccessible"] = "FeatureNotAccessible";
	LinearErrorType$1["InvalidInput"] = "InvalidInput";
	LinearErrorType$1["Ratelimited"] = "Ratelimited";
	LinearErrorType$1["NetworkError"] = "NetworkError";
	LinearErrorType$1["AuthenticationError"] = "AuthenticationError";
	LinearErrorType$1["Forbidden"] = "Forbidden";
	LinearErrorType$1["BootstrapError"] = "BootstrapError";
	LinearErrorType$1["Unknown"] = "Unknown";
	LinearErrorType$1["InternalError"] = "InternalError";
	LinearErrorType$1["Other"] = "Other";
	LinearErrorType$1["UserError"] = "UserError";
	LinearErrorType$1["GraphqlError"] = "GraphqlError";
	LinearErrorType$1["LockTimeout"] = "LockTimeout";
	LinearErrorType$1["UsageLimitExceeded"] = "UsageLimitExceeded";
	return LinearErrorType$1;
}({});

//#endregion
//#region src/utils.ts
/**
* Serialize an object into an encoded user agent string
*
* @param seed user agent properties to serialize
* @returns the serialized user agent string
*/
function serializeUserAgent(seed) {
	return Object.entries(seed).reduce((acc, [key, value]) => {
		const encoded = `${key}@${encodeURIComponent(value)}`;
		return acc ? `${acc} ${encoded}` : encoded;
	}, "");
}
/**
* Capitalize the first character of a string
*
* @param str the string to capitalize
*/
function capitalize(str) {
	return str ? `${str.charAt(0).toUpperCase()}${str.slice(1)}` : void 0;
}
/**
* Type safe check for non defined values
*/
function nonNullable(value) {
	return value !== null && value !== void 0;
}
/**
* Return the key matching the value in an object
*/
function getKeyByValue(obj, value) {
	return Object.keys(obj).find((key) => obj[key] === value);
}

//#endregion
//#region src/error.ts
/**
* A map between the Linear API string type and the LinearErrorType enum
*/
const errorMap = {
	[LinearErrorType.FeatureNotAccessible]: "feature not accessible",
	[LinearErrorType.InvalidInput]: "invalid input",
	[LinearErrorType.Ratelimited]: "ratelimited",
	[LinearErrorType.NetworkError]: "network error",
	[LinearErrorType.AuthenticationError]: "authentication error",
	[LinearErrorType.Forbidden]: "forbidden",
	[LinearErrorType.BootstrapError]: "bootstrap error",
	[LinearErrorType.Unknown]: "unknown",
	[LinearErrorType.InternalError]: "internal error",
	[LinearErrorType.Other]: "other",
	[LinearErrorType.UserError]: "user error",
	[LinearErrorType.GraphqlError]: "graphql error",
	[LinearErrorType.LockTimeout]: "lock timeout",
	[LinearErrorType.UsageLimitExceeded]: "usage limit exceeded"
};
/**
* Match the error type or return unknown
*/
function getErrorType(type) {
	return getKeyByValue(errorMap, type) ?? LinearErrorType.Unknown;
}
/**
* The error shown if no other message is available
*/
const defaultError = "Unknown error from LinearClient";
/**
* One of potentially many graphql errors returned by the Linear API
*
* @error the raw graphql error returned on the error response
*/
var LinearGraphQLError = class {
	/** The type of this graphql error */
	type;
	/** A friendly error message */
	message;
	/** If this error is caused by the user input */
	userError;
	/** The path to the graphql node at which the error occured */
	path;
	constructor(error) {
		this.type = getErrorType(error?.extensions?.type);
		this.userError = error?.extensions?.userError;
		this.path = error?.path;
		/** Select most readable message */
		this.message = error?.extensions?.userPresentableMessage ?? error?.message ?? error?.extensions?.type ?? defaultError;
	}
};
/**
* An error from the Linear API
*
* @param error a raw error returned from the LinearGraphQLClient
*/
var LinearError = class extends Error {
	/** The type of the first error returned by the Linear API */
	type;
	/** A list of graphql errors returned by the Linear API */
	errors;
	/** The graphql query that caused this error */
	query;
	/** The graphql variables that caused this error */
	variables;
	/** Any data returned by this request */
	data;
	/** The http status of this request */
	status;
	/** The raw LinearGraphQLClient error */
	raw;
	constructor(error, errors, type) {
		/** Find messages, duplicate and join, or default */
		super(Array.from(new Set([
			capitalize(error?.message?.split(": {")?.[0]),
			error?.response?.error,
			errors?.[0]?.message
		].filter(nonNullable))).filter(nonNullable).join(" - ") ?? defaultError);
		this.type = type;
		/** Set error properties */
		this.errors = errors;
		this.query = error?.request?.query;
		this.variables = error?.request?.variables;
		this.status = error?.response?.status;
		this.data = error?.response?.data;
		this.raw = error;
	}
};
var FeatureNotAccessibleLinearError = class extends LinearError {
	constructor(error, errors) {
		super(error, errors, LinearErrorType.FeatureNotAccessible);
	}
};
var InvalidInputLinearError = class extends LinearError {
	constructor(error, errors) {
		super(error, errors, LinearErrorType.InvalidInput);
	}
};
var RatelimitedLinearError = class extends LinearError {
	constructor(error, errors) {
		super(error, errors, LinearErrorType.Ratelimited);
		const headers = error?.response?.headers;
		this.retryAfter = this.parseNumber(headers?.get("retry-after"));
		this.requestsLimit = this.parseNumber(headers?.get("x-ratelimit-requests-limit"));
		this.requestsRemaining = this.parseNumber(headers?.get("x-ratelimit-requests-remaining"));
		this.requestsResetAt = this.parseNumber(headers?.get("x-ratelimit-requests-reset"));
		this.complexityLimit = this.parseNumber(headers?.get("x-ratelimit-complexity-limit"));
		this.complexityRemaining = this.parseNumber(headers?.get("x-ratelimit-complexity-remaining"));
		this.complexityResetAt = this.parseNumber(headers?.get("x-ratelimit-complexity-reset"));
	}
	/** How long, in seconds, the user agent should wait before making a follow-up request. */
	retryAfter;
	/** The max amount of requests allowed in the duration. */
	requestsLimit;
	/** The remaining requests before rate limiting kicks in. */
	requestsRemaining;
	/** Unix timestamp at which the requests will be reset. */
	requestsResetAt;
	/** The max amount of complexity allowed in the duration. */
	complexityLimit;
	/** The remaining complexity before rate limiting kicks in. */
	complexityRemaining;
	/** Unix timestamp at which the complexity will be reset. */
	complexityResetAt;
	parseNumber(value) {
		if (value === void 0 || value === null || value === "") return;
		return Number(value) ?? void 0;
	}
};
var NetworkLinearError = class extends LinearError {
	constructor(error, errors) {
		super(error, errors, LinearErrorType.NetworkError);
	}
};
var AuthenticationLinearError = class extends LinearError {
	constructor(error, errors) {
		super(error, errors, LinearErrorType.AuthenticationError);
	}
};
var ForbiddenLinearError = class extends LinearError {
	constructor(error, errors) {
		super(error, errors, LinearErrorType.Forbidden);
	}
};
var BootstrapLinearError = class extends LinearError {
	constructor(error, errors) {
		super(error, errors, LinearErrorType.BootstrapError);
	}
};
var UnknownLinearError = class extends LinearError {
	constructor(error, errors) {
		super(error, errors, LinearErrorType.Unknown);
	}
};
var InternalLinearError = class extends LinearError {
	constructor(error, errors) {
		super(error, errors, LinearErrorType.InternalError);
	}
};
var OtherLinearError = class extends LinearError {
	constructor(error, errors) {
		super(error, errors, LinearErrorType.Other);
	}
};
var UserLinearError = class extends LinearError {
	constructor(error, errors) {
		super(error, errors, LinearErrorType.UserError);
	}
};
var GraphqlLinearError = class extends LinearError {
	constructor(error, errors) {
		super(error, errors, LinearErrorType.GraphqlError);
	}
};
var LockTimeoutLinearError = class extends LinearError {
	constructor(error, errors) {
		super(error, errors, LinearErrorType.LockTimeout);
	}
};
var UsageLimitExceededLinearError = class extends LinearError {
	constructor(error, errors) {
		super(error, errors, LinearErrorType.UsageLimitExceeded);
	}
};
/**
* A map between the Linear error type and the LinearError class
*/
const errorConstructorMap = {
	[LinearErrorType.FeatureNotAccessible]: FeatureNotAccessibleLinearError,
	[LinearErrorType.InvalidInput]: InvalidInputLinearError,
	[LinearErrorType.Ratelimited]: RatelimitedLinearError,
	[LinearErrorType.NetworkError]: NetworkLinearError,
	[LinearErrorType.AuthenticationError]: AuthenticationLinearError,
	[LinearErrorType.Forbidden]: ForbiddenLinearError,
	[LinearErrorType.BootstrapError]: BootstrapLinearError,
	[LinearErrorType.Unknown]: UnknownLinearError,
	[LinearErrorType.InternalError]: InternalLinearError,
	[LinearErrorType.Other]: OtherLinearError,
	[LinearErrorType.UserError]: UserLinearError,
	[LinearErrorType.GraphqlError]: GraphqlLinearError,
	[LinearErrorType.LockTimeout]: LockTimeoutLinearError,
	[LinearErrorType.UsageLimitExceeded]: UsageLimitExceededLinearError
};
function parseLinearError(error) {
	if (error instanceof LinearError) return error;
	/** Parse graphQL errors */
	const errors = (error?.response?.errors ?? []).map((graphqlError) => {
		return new LinearGraphQLError(graphqlError);
	});
	/** Set type based first graphql error or http status */
	const status = error?.response?.status;
	return new (errorConstructorMap[errors[0]?.type ?? (status === 403 ? LinearErrorType.Forbidden : status === 429 ? LinearErrorType.Ratelimited : `${status}`.startsWith("4") ? LinearErrorType.AuthenticationError : status === 500 ? LinearErrorType.InternalError : `${status}`.startsWith("5") ? LinearErrorType.NetworkError : LinearErrorType.Unknown)] ?? LinearError)(error, errors);
}

//#endregion
//#region ../../node_modules/.pnpm/graphql@15.10.1/node_modules/graphql/jsutils/nodejsCustomInspectSymbol.js
var require_nodejsCustomInspectSymbol = /* @__PURE__ */ __commonJSMin(((exports) => {
	Object.defineProperty(exports, "__esModule", { value: true });
	exports.default = void 0;
	var _default = typeof Symbol === "function" && typeof Symbol.for === "function" ? Symbol.for("nodejs.util.inspect.custom") : void 0;
	exports.default = _default;
}));

//#endregion
//#region ../../node_modules/.pnpm/graphql@15.10.1/node_modules/graphql/jsutils/inspect.js
var require_inspect = /* @__PURE__ */ __commonJSMin(((exports) => {
	Object.defineProperty(exports, "__esModule", { value: true });
	exports.default = inspect;
	var _nodejsCustomInspectSymbol$1 = _interopRequireDefault$3(require_nodejsCustomInspectSymbol());
	function _interopRequireDefault$3(obj) {
		return obj && obj.__esModule ? obj : { default: obj };
	}
	function _typeof(obj) {
		"@babel/helpers - typeof";
		if (typeof Symbol === "function" && typeof Symbol.iterator === "symbol") _typeof = function _typeof$1(obj$1) {
			return typeof obj$1;
		};
		else _typeof = function _typeof$1(obj$1) {
			return obj$1 && typeof Symbol === "function" && obj$1.constructor === Symbol && obj$1 !== Symbol.prototype ? "symbol" : typeof obj$1;
		};
		return _typeof(obj);
	}
	var MAX_ARRAY_LENGTH = 10;
	var MAX_RECURSIVE_DEPTH = 2;
	/**
	* Used to print values in error messages.
	*/
	function inspect(value) {
		return formatValue(value, []);
	}
	function formatValue(value, seenValues) {
		switch (_typeof(value)) {
			case "string": return JSON.stringify(value);
			case "function": return value.name ? "[function ".concat(value.name, "]") : "[function]";
			case "object":
				if (value === null) return "null";
				return formatObjectValue(value, seenValues);
			default: return String(value);
		}
	}
	function formatObjectValue(value, previouslySeenValues) {
		if (previouslySeenValues.indexOf(value) !== -1) return "[Circular]";
		var seenValues = [].concat(previouslySeenValues, [value]);
		var customInspectFn = getCustomFn(value);
		if (customInspectFn !== void 0) {
			var customValue = customInspectFn.call(value);
			if (customValue !== value) return typeof customValue === "string" ? customValue : formatValue(customValue, seenValues);
		} else if (Array.isArray(value)) return formatArray(value, seenValues);
		return formatObject(value, seenValues);
	}
	function formatObject(object, seenValues) {
		var keys = Object.keys(object);
		if (keys.length === 0) return "{}";
		if (seenValues.length > MAX_RECURSIVE_DEPTH) return "[" + getObjectTag(object) + "]";
		return "{ " + keys.map(function(key) {
			var value = formatValue(object[key], seenValues);
			return key + ": " + value;
		}).join(", ") + " }";
	}
	function formatArray(array, seenValues) {
		if (array.length === 0) return "[]";
		if (seenValues.length > MAX_RECURSIVE_DEPTH) return "[Array]";
		var len = Math.min(MAX_ARRAY_LENGTH, array.length);
		var remaining = array.length - len;
		var items = [];
		for (var i = 0; i < len; ++i) items.push(formatValue(array[i], seenValues));
		if (remaining === 1) items.push("... 1 more item");
		else if (remaining > 1) items.push("... ".concat(remaining, " more items"));
		return "[" + items.join(", ") + "]";
	}
	function getCustomFn(object) {
		var customInspectFn = object[String(_nodejsCustomInspectSymbol$1.default)];
		if (typeof customInspectFn === "function") return customInspectFn;
		if (typeof object.inspect === "function") return object.inspect;
	}
	function getObjectTag(object) {
		var tag = Object.prototype.toString.call(object).replace(/^\[object /, "").replace(/]$/, "");
		if (tag === "Object" && typeof object.constructor === "function") {
			var name = object.constructor.name;
			if (typeof name === "string" && name !== "") return name;
		}
		return tag;
	}
}));

//#endregion
//#region ../../node_modules/.pnpm/graphql@15.10.1/node_modules/graphql/jsutils/invariant.js
var require_invariant = /* @__PURE__ */ __commonJSMin(((exports) => {
	Object.defineProperty(exports, "__esModule", { value: true });
	exports.default = invariant;
	function invariant(condition, message) {
		if (!Boolean(condition)) throw new Error(message != null ? message : "Unexpected invariant triggered.");
	}
}));

//#endregion
//#region ../../node_modules/.pnpm/graphql@15.10.1/node_modules/graphql/jsutils/defineInspect.js
var require_defineInspect = /* @__PURE__ */ __commonJSMin(((exports) => {
	Object.defineProperty(exports, "__esModule", { value: true });
	exports.default = defineInspect;
	var _invariant = _interopRequireDefault$2(require_invariant());
	var _nodejsCustomInspectSymbol = _interopRequireDefault$2(require_nodejsCustomInspectSymbol());
	function _interopRequireDefault$2(obj) {
		return obj && obj.__esModule ? obj : { default: obj };
	}
	/**
	* The `defineInspect()` function defines `inspect()` prototype method as alias of `toJSON`
	*/
	function defineInspect(classObject) {
		var fn = classObject.prototype.toJSON;
		typeof fn === "function" || (0, _invariant.default)(0);
		classObject.prototype.inspect = fn;
		if (_nodejsCustomInspectSymbol.default) classObject.prototype[_nodejsCustomInspectSymbol.default] = fn;
	}
}));

//#endregion
//#region ../../node_modules/.pnpm/graphql@15.10.1/node_modules/graphql/language/ast.js
var require_ast = /* @__PURE__ */ __commonJSMin(((exports) => {
	Object.defineProperty(exports, "__esModule", { value: true });
	exports.isNode = isNode;
	exports.Token = exports.Location = void 0;
	var _defineInspect = _interopRequireDefault$1(require_defineInspect());
	function _interopRequireDefault$1(obj) {
		return obj && obj.__esModule ? obj : { default: obj };
	}
	/**
	* Contains a range of UTF-8 character offsets and token references that
	* identify the region of the source from which the AST derived.
	*/
	var Location = /* @__PURE__ */ function() {
		/**
		* The character offset at which this Node begins.
		*/
		/**
		* The character offset at which this Node ends.
		*/
		/**
		* The Token at which this Node begins.
		*/
		/**
		* The Token at which this Node ends.
		*/
		/**
		* The Source document the AST represents.
		*/
		function Location$1(startToken, endToken, source) {
			this.start = startToken.start;
			this.end = endToken.end;
			this.startToken = startToken;
			this.endToken = endToken;
			this.source = source;
		}
		var _proto = Location$1.prototype;
		_proto.toJSON = function toJSON() {
			return {
				start: this.start,
				end: this.end
			};
		};
		return Location$1;
	}();
	exports.Location = Location;
	(0, _defineInspect.default)(Location);
	/**
	* Represents a range of characters represented by a lexical token
	* within a Source.
	*/
	var Token = /* @__PURE__ */ function() {
		/**
		* The kind of Token.
		*/
		/**
		* The character offset at which this Node begins.
		*/
		/**
		* The character offset at which this Node ends.
		*/
		/**
		* The 1-indexed line number on which this Token appears.
		*/
		/**
		* The 1-indexed column number at which this Token begins.
		*/
		/**
		* For non-punctuation tokens, represents the interpreted value of the token.
		*/
		/**
		* Tokens exist as nodes in a double-linked-list amongst all tokens
		* including ignored tokens. <SOF> is always the first node and <EOF>
		* the last.
		*/
		function Token$1(kind, start, end, line, column, prev, value) {
			this.kind = kind;
			this.start = start;
			this.end = end;
			this.line = line;
			this.column = column;
			this.value = value;
			this.prev = prev;
			this.next = null;
		}
		var _proto2 = Token$1.prototype;
		_proto2.toJSON = function toJSON() {
			return {
				kind: this.kind,
				value: this.value,
				line: this.line,
				column: this.column
			};
		};
		return Token$1;
	}();
	exports.Token = Token;
	(0, _defineInspect.default)(Token);
	/**
	* @internal
	*/
	function isNode(maybeNode) {
		return maybeNode != null && typeof maybeNode.kind === "string";
	}
}));
/**
* The list of all possible AST node types.
*/

//#endregion
//#region ../../node_modules/.pnpm/graphql@15.10.1/node_modules/graphql/language/visitor.js
var require_visitor = /* @__PURE__ */ __commonJSMin(((exports) => {
	Object.defineProperty(exports, "__esModule", { value: true });
	exports.visit = visit;
	exports.visitInParallel = visitInParallel;
	exports.getVisitFn = getVisitFn;
	exports.BREAK = exports.QueryDocumentKeys = void 0;
	var _inspect = _interopRequireDefault(require_inspect());
	var _ast = require_ast();
	function _interopRequireDefault(obj) {
		return obj && obj.__esModule ? obj : { default: obj };
	}
	var QueryDocumentKeys = {
		Name: [],
		Document: ["definitions"],
		OperationDefinition: [
			"name",
			"variableDefinitions",
			"directives",
			"selectionSet"
		],
		VariableDefinition: [
			"variable",
			"type",
			"defaultValue",
			"directives"
		],
		Variable: ["name"],
		SelectionSet: ["selections"],
		Field: [
			"alias",
			"name",
			"arguments",
			"directives",
			"selectionSet"
		],
		Argument: ["name", "value"],
		FragmentSpread: ["name", "directives"],
		InlineFragment: [
			"typeCondition",
			"directives",
			"selectionSet"
		],
		FragmentDefinition: [
			"name",
			"variableDefinitions",
			"typeCondition",
			"directives",
			"selectionSet"
		],
		IntValue: [],
		FloatValue: [],
		StringValue: [],
		BooleanValue: [],
		NullValue: [],
		EnumValue: [],
		ListValue: ["values"],
		ObjectValue: ["fields"],
		ObjectField: ["name", "value"],
		Directive: ["name", "arguments"],
		NamedType: ["name"],
		ListType: ["type"],
		NonNullType: ["type"],
		SchemaDefinition: [
			"description",
			"directives",
			"operationTypes"
		],
		OperationTypeDefinition: ["type"],
		ScalarTypeDefinition: [
			"description",
			"name",
			"directives"
		],
		ObjectTypeDefinition: [
			"description",
			"name",
			"interfaces",
			"directives",
			"fields"
		],
		FieldDefinition: [
			"description",
			"name",
			"arguments",
			"type",
			"directives"
		],
		InputValueDefinition: [
			"description",
			"name",
			"type",
			"defaultValue",
			"directives"
		],
		InterfaceTypeDefinition: [
			"description",
			"name",
			"interfaces",
			"directives",
			"fields"
		],
		UnionTypeDefinition: [
			"description",
			"name",
			"directives",
			"types"
		],
		EnumTypeDefinition: [
			"description",
			"name",
			"directives",
			"values"
		],
		EnumValueDefinition: [
			"description",
			"name",
			"directives"
		],
		InputObjectTypeDefinition: [
			"description",
			"name",
			"directives",
			"fields"
		],
		DirectiveDefinition: [
			"description",
			"name",
			"arguments",
			"locations"
		],
		SchemaExtension: ["directives", "operationTypes"],
		ScalarTypeExtension: ["name", "directives"],
		ObjectTypeExtension: [
			"name",
			"interfaces",
			"directives",
			"fields"
		],
		InterfaceTypeExtension: [
			"name",
			"interfaces",
			"directives",
			"fields"
		],
		UnionTypeExtension: [
			"name",
			"directives",
			"types"
		],
		EnumTypeExtension: [
			"name",
			"directives",
			"values"
		],
		InputObjectTypeExtension: [
			"name",
			"directives",
			"fields"
		]
	};
	exports.QueryDocumentKeys = QueryDocumentKeys;
	var BREAK = Object.freeze({});
	/**
	* visit() will walk through an AST using a depth-first traversal, calling
	* the visitor's enter function at each node in the traversal, and calling the
	* leave function after visiting that node and all of its child nodes.
	*
	* By returning different values from the enter and leave functions, the
	* behavior of the visitor can be altered, including skipping over a sub-tree of
	* the AST (by returning false), editing the AST by returning a value or null
	* to remove the value, or to stop the whole traversal by returning BREAK.
	*
	* When using visit() to edit an AST, the original AST will not be modified, and
	* a new version of the AST with the changes applied will be returned from the
	* visit function.
	*
	*     const editedAST = visit(ast, {
	*       enter(node, key, parent, path, ancestors) {
	*         // @return
	*         //   undefined: no action
	*         //   false: skip visiting this node
	*         //   visitor.BREAK: stop visiting altogether
	*         //   null: delete this node
	*         //   any value: replace this node with the returned value
	*       },
	*       leave(node, key, parent, path, ancestors) {
	*         // @return
	*         //   undefined: no action
	*         //   false: no action
	*         //   visitor.BREAK: stop visiting altogether
	*         //   null: delete this node
	*         //   any value: replace this node with the returned value
	*       }
	*     });
	*
	* Alternatively to providing enter() and leave() functions, a visitor can
	* instead provide functions named the same as the kinds of AST nodes, or
	* enter/leave visitors at a named key, leading to four permutations of the
	* visitor API:
	*
	* 1) Named visitors triggered when entering a node of a specific kind.
	*
	*     visit(ast, {
	*       Kind(node) {
	*         // enter the "Kind" node
	*       }
	*     })
	*
	* 2) Named visitors that trigger upon entering and leaving a node of
	*    a specific kind.
	*
	*     visit(ast, {
	*       Kind: {
	*         enter(node) {
	*           // enter the "Kind" node
	*         }
	*         leave(node) {
	*           // leave the "Kind" node
	*         }
	*       }
	*     })
	*
	* 3) Generic visitors that trigger upon entering and leaving any node.
	*
	*     visit(ast, {
	*       enter(node) {
	*         // enter any node
	*       },
	*       leave(node) {
	*         // leave any node
	*       }
	*     })
	*
	* 4) Parallel visitors for entering and leaving nodes of a specific kind.
	*
	*     visit(ast, {
	*       enter: {
	*         Kind(node) {
	*           // enter the "Kind" node
	*         }
	*       },
	*       leave: {
	*         Kind(node) {
	*           // leave the "Kind" node
	*         }
	*       }
	*     })
	*/
	exports.BREAK = BREAK;
	function visit(root, visitor) {
		var visitorKeys = arguments.length > 2 && arguments[2] !== void 0 ? arguments[2] : QueryDocumentKeys;
		var stack = void 0;
		var inArray = Array.isArray(root);
		var keys = [root];
		var index = -1;
		var edits = [];
		var node = void 0;
		var key = void 0;
		var parent = void 0;
		var path = [];
		var ancestors = [];
		var newRoot = root;
		do {
			index++;
			var isLeaving = index === keys.length;
			var isEdited = isLeaving && edits.length !== 0;
			if (isLeaving) {
				key = ancestors.length === 0 ? void 0 : path[path.length - 1];
				node = parent;
				parent = ancestors.pop();
				if (isEdited) {
					if (inArray) node = node.slice();
					else {
						var clone = {};
						for (var _i2 = 0, _Object$keys2 = Object.keys(node); _i2 < _Object$keys2.length; _i2++) {
							var k = _Object$keys2[_i2];
							clone[k] = node[k];
						}
						node = clone;
					}
					var editOffset = 0;
					for (var ii = 0; ii < edits.length; ii++) {
						var editKey = edits[ii][0];
						var editValue = edits[ii][1];
						if (inArray) editKey -= editOffset;
						if (inArray && editValue === null) {
							node.splice(editKey, 1);
							editOffset++;
						} else node[editKey] = editValue;
					}
				}
				index = stack.index;
				keys = stack.keys;
				edits = stack.edits;
				inArray = stack.inArray;
				stack = stack.prev;
			} else {
				key = parent ? inArray ? index : keys[index] : void 0;
				node = parent ? parent[key] : newRoot;
				if (node === null || node === void 0) continue;
				if (parent) path.push(key);
			}
			var result = void 0;
			if (!Array.isArray(node)) {
				if (!(0, _ast.isNode)(node)) throw new Error("Invalid AST Node: ".concat((0, _inspect.default)(node), "."));
				var visitFn = getVisitFn(visitor, node.kind, isLeaving);
				if (visitFn) {
					result = visitFn.call(visitor, node, key, parent, path, ancestors);
					if (result === BREAK) break;
					if (result === false) {
						if (!isLeaving) {
							path.pop();
							continue;
						}
					} else if (result !== void 0) {
						edits.push([key, result]);
						if (!isLeaving) if ((0, _ast.isNode)(result)) node = result;
						else {
							path.pop();
							continue;
						}
					}
				}
			}
			if (result === void 0 && isEdited) edits.push([key, node]);
			if (isLeaving) path.pop();
			else {
				var _visitorKeys$node$kin;
				stack = {
					inArray,
					index,
					keys,
					edits,
					prev: stack
				};
				inArray = Array.isArray(node);
				keys = inArray ? node : (_visitorKeys$node$kin = visitorKeys[node.kind]) !== null && _visitorKeys$node$kin !== void 0 ? _visitorKeys$node$kin : [];
				index = -1;
				edits = [];
				if (parent) ancestors.push(parent);
				parent = node;
			}
		} while (stack !== void 0);
		if (edits.length !== 0) newRoot = edits[edits.length - 1][1];
		return newRoot;
	}
	/**
	* Creates a new visitor instance which delegates to many visitors to run in
	* parallel. Each visitor will be visited for each node before moving on.
	*
	* If a prior visitor edits a node, no following visitors will see that node.
	*/
	function visitInParallel(visitors) {
		var skipping = new Array(visitors.length);
		return {
			enter: function enter(node) {
				for (var i = 0; i < visitors.length; i++) if (skipping[i] == null) {
					var fn = getVisitFn(visitors[i], node.kind, false);
					if (fn) {
						var result = fn.apply(visitors[i], arguments);
						if (result === false) skipping[i] = node;
						else if (result === BREAK) skipping[i] = BREAK;
						else if (result !== void 0) return result;
					}
				}
			},
			leave: function leave(node) {
				for (var i = 0; i < visitors.length; i++) if (skipping[i] == null) {
					var fn = getVisitFn(visitors[i], node.kind, true);
					if (fn) {
						var result = fn.apply(visitors[i], arguments);
						if (result === BREAK) skipping[i] = BREAK;
						else if (result !== void 0 && result !== false) return result;
					}
				} else if (skipping[i] === node) skipping[i] = null;
			}
		};
	}
	/**
	* Given a visitor instance, if it is leaving or not, and a node kind, return
	* the function the visitor runtime should call.
	*/
	function getVisitFn(visitor, kind, isLeaving) {
		var kindVisitor = visitor[kind];
		if (kindVisitor) {
			if (!isLeaving && typeof kindVisitor === "function") return kindVisitor;
			var kindSpecificVisitor = isLeaving ? kindVisitor.leave : kindVisitor.enter;
			if (typeof kindSpecificVisitor === "function") return kindSpecificVisitor;
		} else {
			var specificVisitor = isLeaving ? visitor.leave : visitor.enter;
			if (specificVisitor) {
				if (typeof specificVisitor === "function") return specificVisitor;
				var specificKindVisitor = specificVisitor[kind];
				if (typeof specificKindVisitor === "function") return specificKindVisitor;
			}
		}
	}
}));

//#endregion
//#region ../../node_modules/.pnpm/graphql@15.10.1/node_modules/graphql/language/blockString.js
var require_blockString = /* @__PURE__ */ __commonJSMin(((exports) => {
	Object.defineProperty(exports, "__esModule", { value: true });
	exports.dedentBlockStringValue = dedentBlockStringValue;
	exports.getBlockStringIndentation = getBlockStringIndentation;
	exports.printBlockString = printBlockString;
	/**
	* Produces the value of a block string from its parsed raw value, similar to
	* CoffeeScript's block string, Python's docstring trim or Ruby's strip_heredoc.
	*
	* This implements the GraphQL spec's BlockStringValue() static algorithm.
	*
	* @internal
	*/
	function dedentBlockStringValue(rawString) {
		var lines = rawString.split(/\r\n|[\n\r]/g);
		var commonIndent = getBlockStringIndentation(rawString);
		if (commonIndent !== 0) for (var i = 1; i < lines.length; i++) lines[i] = lines[i].slice(commonIndent);
		var startLine = 0;
		while (startLine < lines.length && isBlank(lines[startLine])) ++startLine;
		var endLine = lines.length;
		while (endLine > startLine && isBlank(lines[endLine - 1])) --endLine;
		return lines.slice(startLine, endLine).join("\n");
	}
	function isBlank(str) {
		for (var i = 0; i < str.length; ++i) if (str[i] !== " " && str[i] !== "	") return false;
		return true;
	}
	/**
	* @internal
	*/
	function getBlockStringIndentation(value) {
		var _commonIndent;
		var isFirstLine = true;
		var isEmptyLine = true;
		var indent$1 = 0;
		var commonIndent = null;
		for (var i = 0; i < value.length; ++i) switch (value.charCodeAt(i)) {
			case 13: if (value.charCodeAt(i + 1) === 10) ++i;
			case 10:
				isFirstLine = false;
				isEmptyLine = true;
				indent$1 = 0;
				break;
			case 9:
			case 32:
				++indent$1;
				break;
			default:
				if (isEmptyLine && !isFirstLine && (commonIndent === null || indent$1 < commonIndent)) commonIndent = indent$1;
				isEmptyLine = false;
		}
		return (_commonIndent = commonIndent) !== null && _commonIndent !== void 0 ? _commonIndent : 0;
	}
	/**
	* Print a block string in the indented block form by adding a leading and
	* trailing blank line. However, if a block string starts with whitespace and is
	* a single-line, adding a leading blank line would strip that whitespace.
	*
	* @internal
	*/
	function printBlockString(value) {
		var indentation = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : "";
		var preferMultipleLines = arguments.length > 2 && arguments[2] !== void 0 ? arguments[2] : false;
		var isSingleLine = value.indexOf("\n") === -1;
		var hasLeadingSpace = value[0] === " " || value[0] === "	";
		var hasTrailingQuote = value[value.length - 1] === "\"";
		var hasTrailingSlash = value[value.length - 1] === "\\";
		var printAsMultipleLines = !isSingleLine || hasTrailingQuote || hasTrailingSlash || preferMultipleLines;
		var result = "";
		if (printAsMultipleLines && !(isSingleLine && hasLeadingSpace)) result += "\n" + indentation;
		result += indentation ? value.replace(/\n/g, "\n" + indentation) : value;
		if (printAsMultipleLines) result += "\n";
		return "\"\"\"" + result.replace(/"""/g, "\\\"\"\"") + "\"\"\"";
	}
}));

//#endregion
//#region ../../node_modules/.pnpm/graphql@15.10.1/node_modules/graphql/language/printer.js
var require_printer = /* @__PURE__ */ __commonJSMin(((exports) => {
	Object.defineProperty(exports, "__esModule", { value: true });
	exports.print = print$1;
	var _visitor = require_visitor();
	var _blockString = require_blockString();
	/**
	* Converts an AST into a string, using one set of reasonable
	* formatting rules.
	*/
	function print$1(ast) {
		return (0, _visitor.visit)(ast, { leave: printDocASTReducer });
	}
	var MAX_LINE_LENGTH = 80;
	var printDocASTReducer = {
		Name: function Name(node) {
			return node.value;
		},
		Variable: function Variable(node) {
			return "$" + node.name;
		},
		Document: function Document$1(node) {
			return join(node.definitions, "\n\n") + "\n";
		},
		OperationDefinition: function OperationDefinition(node) {
			var op = node.operation;
			var name = node.name;
			var varDefs = wrap("(", join(node.variableDefinitions, ", "), ")");
			var directives = join(node.directives, " ");
			var selectionSet = node.selectionSet;
			return !name && !directives && !varDefs && op === "query" ? selectionSet : join([
				op,
				join([name, varDefs]),
				directives,
				selectionSet
			], " ");
		},
		VariableDefinition: function VariableDefinition(_ref) {
			var variable = _ref.variable, type = _ref.type, defaultValue = _ref.defaultValue, directives = _ref.directives;
			return variable + ": " + type + wrap(" = ", defaultValue) + wrap(" ", join(directives, " "));
		},
		SelectionSet: function SelectionSet(_ref2) {
			var selections = _ref2.selections;
			return block(selections);
		},
		Field: function Field(_ref3) {
			var alias = _ref3.alias, name = _ref3.name, args = _ref3.arguments, directives = _ref3.directives, selectionSet = _ref3.selectionSet;
			var prefix = wrap("", alias, ": ") + name;
			var argsLine = prefix + wrap("(", join(args, ", "), ")");
			if (argsLine.length > MAX_LINE_LENGTH) argsLine = prefix + wrap("(\n", indent(join(args, "\n")), "\n)");
			return join([
				argsLine,
				join(directives, " "),
				selectionSet
			], " ");
		},
		Argument: function Argument(_ref4) {
			var name = _ref4.name, value = _ref4.value;
			return name + ": " + value;
		},
		FragmentSpread: function FragmentSpread(_ref5) {
			var name = _ref5.name, directives = _ref5.directives;
			return "..." + name + wrap(" ", join(directives, " "));
		},
		InlineFragment: function InlineFragment(_ref6) {
			var typeCondition = _ref6.typeCondition, directives = _ref6.directives, selectionSet = _ref6.selectionSet;
			return join([
				"...",
				wrap("on ", typeCondition),
				join(directives, " "),
				selectionSet
			], " ");
		},
		FragmentDefinition: function FragmentDefinition(_ref7) {
			var name = _ref7.name, typeCondition = _ref7.typeCondition, variableDefinitions = _ref7.variableDefinitions, directives = _ref7.directives, selectionSet = _ref7.selectionSet;
			return "fragment ".concat(name).concat(wrap("(", join(variableDefinitions, ", "), ")"), " ") + "on ".concat(typeCondition, " ").concat(wrap("", join(directives, " "), " ")) + selectionSet;
		},
		IntValue: function IntValue(_ref8) {
			return _ref8.value;
		},
		FloatValue: function FloatValue(_ref9) {
			return _ref9.value;
		},
		StringValue: function StringValue(_ref10, key) {
			var value = _ref10.value;
			return _ref10.block ? (0, _blockString.printBlockString)(value, key === "description" ? "" : "  ") : JSON.stringify(value);
		},
		BooleanValue: function BooleanValue(_ref11) {
			return _ref11.value ? "true" : "false";
		},
		NullValue: function NullValue() {
			return "null";
		},
		EnumValue: function EnumValue(_ref12) {
			return _ref12.value;
		},
		ListValue: function ListValue(_ref13) {
			var values = _ref13.values;
			return "[" + join(values, ", ") + "]";
		},
		ObjectValue: function ObjectValue(_ref14) {
			var fields = _ref14.fields;
			return "{" + join(fields, ", ") + "}";
		},
		ObjectField: function ObjectField(_ref15) {
			var name = _ref15.name, value = _ref15.value;
			return name + ": " + value;
		},
		Directive: function Directive(_ref16) {
			var name = _ref16.name, args = _ref16.arguments;
			return "@" + name + wrap("(", join(args, ", "), ")");
		},
		NamedType: function NamedType(_ref17) {
			return _ref17.name;
		},
		ListType: function ListType(_ref18) {
			return "[" + _ref18.type + "]";
		},
		NonNullType: function NonNullType(_ref19) {
			return _ref19.type + "!";
		},
		SchemaDefinition: addDescription(function(_ref20) {
			var directives = _ref20.directives, operationTypes = _ref20.operationTypes;
			return join([
				"schema",
				join(directives, " "),
				block(operationTypes)
			], " ");
		}),
		OperationTypeDefinition: function OperationTypeDefinition(_ref21) {
			var operation = _ref21.operation, type = _ref21.type;
			return operation + ": " + type;
		},
		ScalarTypeDefinition: addDescription(function(_ref22) {
			var name = _ref22.name, directives = _ref22.directives;
			return join([
				"scalar",
				name,
				join(directives, " ")
			], " ");
		}),
		ObjectTypeDefinition: addDescription(function(_ref23) {
			var name = _ref23.name, interfaces = _ref23.interfaces, directives = _ref23.directives, fields = _ref23.fields;
			return join([
				"type",
				name,
				wrap("implements ", join(interfaces, " & ")),
				join(directives, " "),
				block(fields)
			], " ");
		}),
		FieldDefinition: addDescription(function(_ref24) {
			var name = _ref24.name, args = _ref24.arguments, type = _ref24.type, directives = _ref24.directives;
			return name + (hasMultilineItems(args) ? wrap("(\n", indent(join(args, "\n")), "\n)") : wrap("(", join(args, ", "), ")")) + ": " + type + wrap(" ", join(directives, " "));
		}),
		InputValueDefinition: addDescription(function(_ref25) {
			var name = _ref25.name, type = _ref25.type, defaultValue = _ref25.defaultValue, directives = _ref25.directives;
			return join([
				name + ": " + type,
				wrap("= ", defaultValue),
				join(directives, " ")
			], " ");
		}),
		InterfaceTypeDefinition: addDescription(function(_ref26) {
			var name = _ref26.name, interfaces = _ref26.interfaces, directives = _ref26.directives, fields = _ref26.fields;
			return join([
				"interface",
				name,
				wrap("implements ", join(interfaces, " & ")),
				join(directives, " "),
				block(fields)
			], " ");
		}),
		UnionTypeDefinition: addDescription(function(_ref27) {
			var name = _ref27.name, directives = _ref27.directives, types = _ref27.types;
			return join([
				"union",
				name,
				join(directives, " "),
				types && types.length !== 0 ? "= " + join(types, " | ") : ""
			], " ");
		}),
		EnumTypeDefinition: addDescription(function(_ref28) {
			var name = _ref28.name, directives = _ref28.directives, values = _ref28.values;
			return join([
				"enum",
				name,
				join(directives, " "),
				block(values)
			], " ");
		}),
		EnumValueDefinition: addDescription(function(_ref29) {
			var name = _ref29.name, directives = _ref29.directives;
			return join([name, join(directives, " ")], " ");
		}),
		InputObjectTypeDefinition: addDescription(function(_ref30) {
			var name = _ref30.name, directives = _ref30.directives, fields = _ref30.fields;
			return join([
				"input",
				name,
				join(directives, " "),
				block(fields)
			], " ");
		}),
		DirectiveDefinition: addDescription(function(_ref31) {
			var name = _ref31.name, args = _ref31.arguments, repeatable = _ref31.repeatable, locations = _ref31.locations;
			return "directive @" + name + (hasMultilineItems(args) ? wrap("(\n", indent(join(args, "\n")), "\n)") : wrap("(", join(args, ", "), ")")) + (repeatable ? " repeatable" : "") + " on " + join(locations, " | ");
		}),
		SchemaExtension: function SchemaExtension(_ref32) {
			var directives = _ref32.directives, operationTypes = _ref32.operationTypes;
			return join([
				"extend schema",
				join(directives, " "),
				block(operationTypes)
			], " ");
		},
		ScalarTypeExtension: function ScalarTypeExtension(_ref33) {
			var name = _ref33.name, directives = _ref33.directives;
			return join([
				"extend scalar",
				name,
				join(directives, " ")
			], " ");
		},
		ObjectTypeExtension: function ObjectTypeExtension(_ref34) {
			var name = _ref34.name, interfaces = _ref34.interfaces, directives = _ref34.directives, fields = _ref34.fields;
			return join([
				"extend type",
				name,
				wrap("implements ", join(interfaces, " & ")),
				join(directives, " "),
				block(fields)
			], " ");
		},
		InterfaceTypeExtension: function InterfaceTypeExtension(_ref35) {
			var name = _ref35.name, interfaces = _ref35.interfaces, directives = _ref35.directives, fields = _ref35.fields;
			return join([
				"extend interface",
				name,
				wrap("implements ", join(interfaces, " & ")),
				join(directives, " "),
				block(fields)
			], " ");
		},
		UnionTypeExtension: function UnionTypeExtension(_ref36) {
			var name = _ref36.name, directives = _ref36.directives, types = _ref36.types;
			return join([
				"extend union",
				name,
				join(directives, " "),
				types && types.length !== 0 ? "= " + join(types, " | ") : ""
			], " ");
		},
		EnumTypeExtension: function EnumTypeExtension(_ref37) {
			var name = _ref37.name, directives = _ref37.directives, values = _ref37.values;
			return join([
				"extend enum",
				name,
				join(directives, " "),
				block(values)
			], " ");
		},
		InputObjectTypeExtension: function InputObjectTypeExtension(_ref38) {
			var name = _ref38.name, directives = _ref38.directives, fields = _ref38.fields;
			return join([
				"extend input",
				name,
				join(directives, " "),
				block(fields)
			], " ");
		}
	};
	function addDescription(cb) {
		return function(node) {
			return join([node.description, cb(node)], "\n");
		};
	}
	/**
	* Given maybeArray, print an empty string if it is null or empty, otherwise
	* print all items together separated by separator if provided
	*/
	function join(maybeArray) {
		var _maybeArray$filter$jo;
		var separator = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : "";
		return (_maybeArray$filter$jo = maybeArray === null || maybeArray === void 0 ? void 0 : maybeArray.filter(function(x) {
			return x;
		}).join(separator)) !== null && _maybeArray$filter$jo !== void 0 ? _maybeArray$filter$jo : "";
	}
	/**
	* Given array, print each item on its own line, wrapped in an
	* indented "{ }" block.
	*/
	function block(array) {
		return wrap("{\n", indent(join(array, "\n")), "\n}");
	}
	/**
	* If maybeString is not null or empty, then wrap with start and end, otherwise print an empty string.
	*/
	function wrap(start, maybeString) {
		var end = arguments.length > 2 && arguments[2] !== void 0 ? arguments[2] : "";
		return maybeString != null && maybeString !== "" ? start + maybeString + end : "";
	}
	function indent(str) {
		return wrap("  ", str.replace(/\n/g, "\n  "));
	}
	function isMultiline(str) {
		return str.indexOf("\n") !== -1;
	}
	function hasMultilineItems(maybeArray) {
		return maybeArray != null && maybeArray.some(isMultiline);
	}
}));

//#endregion
//#region src/graphql-client.ts
var import_printer = require_printer();
/**
* Identical class to graphql-request ClientError
* Ensures parseLinearError is compatible with custom graphql-request clients
*
* @param response the raw response from the Linear API
* @param request information about the request resulting in the error
*/
var GraphQLClientError = class GraphQLClientError extends Error {
	response;
	request;
	constructor(response, request) {
		const message = `${GraphQLClientError.extractMessage(response)}: ${JSON.stringify({
			response,
			request
		})}`;
		super(message);
		Object.setPrototypeOf(this, GraphQLClientError.prototype);
		this.response = response;
		this.request = request;
		if (typeof Error.captureStackTrace === "function") Error.captureStackTrace(this, GraphQLClientError);
	}
	static extractMessage(response) {
		try {
			return response.errors?.[0]?.message ?? `GraphQL Error (Code: ${response.status})`;
		} catch (e) {
			return `GraphQL Error (Code: ${response.status})`;
		}
	}
};
/**
* Create an isomorphic GraphQL client
* Originally forked from graphql-request to remove the external dependency
*
* @param url base url to send the request to
* @param options the request options
*/
var LinearGraphQLClient = class {
	url;
	options;
	constructor(url, options) {
		this.url = url;
		this.options = options || {};
	}
	async rawRequest(query, variables, requestHeaders) {
		const { headers, ...others } = this.options;
		const body = JSON.stringify({
			query,
			variables
		});
		const fetch$1 = globalThis.fetch;
		const response = await fetch$1(this.url, {
			method: "POST",
			headers: {
				...typeof body === "string" ? { "Content-Type": "application/json" } : {},
				...resolveHeaders(headers),
				...resolveHeaders(requestHeaders)
			},
			body,
			...others
		});
		const result = await getResult(response);
		if (typeof result !== "string" && response.ok && !result.errors && result.data) return {
			...result,
			headers: response.headers,
			status: response.status
		};
		else throw parseLinearError(new GraphQLClientError({
			...typeof result === "string" ? { error: result } : result,
			status: response.status,
			headers: response.headers
		}, {
			query,
			variables
		}));
	}
	/**
	* Send a GraphQL document to the server.
	*/
	async request(document, variables, requestHeaders) {
		const { headers, ...others } = this.options;
		const query = typeof document === "string" ? document : (0, import_printer.print)(document);
		const body = JSON.stringify({
			query,
			variables
		});
		const response = await fetch(this.url, {
			method: "POST",
			headers: {
				...typeof body === "string" ? { "Content-Type": "application/json" } : {},
				...resolveHeaders(headers),
				...resolveHeaders(requestHeaders)
			},
			body,
			...others
		});
		const result = await getResult(response);
		if (typeof result !== "string" && response.ok && !result.errors && result.data) return result.data;
		else throw new GraphQLClientError({
			...typeof result === "string" ? { error: result } : result,
			status: response.status,
			headers: response.headers
		}, {
			query,
			variables
		});
	}
	setHeaders(headers) {
		this.options.headers = headers;
		return this;
	}
	/**
	* Attach a header to the client. All subsequent requests will have this header.
	*/
	setHeader(key, value) {
		const { headers } = this.options;
		if (headers) headers[key] = value;
		else this.options.headers = { [key]: value };
		return this;
	}
};
/**
* Parse the raw response
*
* @param response raw response from the Linear API
*/
function getResult(response) {
	const contentType = response.headers.get("Content-Type");
	if (contentType && contentType.startsWith("application/json")) return response.json();
	else return response.text();
}
/**
* Convert the given headers configuration into a plain object.
*/
function resolveHeaders(headers) {
	let oHeaders = {};
	if (headers) if (typeof Headers !== "undefined" && headers instanceof Headers) oHeaders = headersToObject(headers);
	else if (Array.isArray(headers)) headers.forEach(([name, value]) => {
		oHeaders[name] = value;
	});
	else oHeaders = headers;
	return oHeaders;
}
/**
* Convert Headers instance into regular object
*/
function headersToObject(headers) {
	const o = {};
	headers.forEach((v, k) => {
		o[k] = v;
	});
	return o;
}

//#endregion
//#region src/_generated_documents.ts
var _generated_documents_exports = /* @__PURE__ */ __export({
	ActorBotFragmentDoc: () => ActorBotFragmentDoc,
	AdministrableTeamsDocument: () => AdministrableTeamsDocument,
	AgentActivitiesDocument: () => AgentActivitiesDocument,
	AgentActivityActionContentFragmentDoc: () => AgentActivityActionContentFragmentDoc,
	AgentActivityConnectionFragmentDoc: () => AgentActivityConnectionFragmentDoc,
	AgentActivityDocument: () => AgentActivityDocument,
	AgentActivityElicitationContentFragmentDoc: () => AgentActivityElicitationContentFragmentDoc,
	AgentActivityErrorContentFragmentDoc: () => AgentActivityErrorContentFragmentDoc,
	AgentActivityFragmentDoc: () => AgentActivityFragmentDoc,
	AgentActivityPayloadFragmentDoc: () => AgentActivityPayloadFragmentDoc,
	AgentActivityPromptContentFragmentDoc: () => AgentActivityPromptContentFragmentDoc,
	AgentActivityResponseContentFragmentDoc: () => AgentActivityResponseContentFragmentDoc,
	AgentActivitySignal: () => AgentActivitySignal,
	AgentActivityThoughtContentFragmentDoc: () => AgentActivityThoughtContentFragmentDoc,
	AgentActivityType: () => AgentActivityType,
	AgentActivityWebhookPayloadFragmentDoc: () => AgentActivityWebhookPayloadFragmentDoc,
	AgentSessionConnectionFragmentDoc: () => AgentSessionConnectionFragmentDoc,
	AgentSessionCreateOnCommentDocument: () => AgentSessionCreateOnCommentDocument,
	AgentSessionCreateOnIssueDocument: () => AgentSessionCreateOnIssueDocument,
	AgentSessionDocument: () => AgentSessionDocument,
	AgentSessionEventWebhookPayloadFragmentDoc: () => AgentSessionEventWebhookPayloadFragmentDoc,
	AgentSessionFragmentDoc: () => AgentSessionFragmentDoc,
	AgentSessionPayloadFragmentDoc: () => AgentSessionPayloadFragmentDoc,
	AgentSessionStatus: () => AgentSessionStatus,
	AgentSessionToPullRequestConnectionFragmentDoc: () => AgentSessionToPullRequestConnectionFragmentDoc,
	AgentSessionToPullRequestFragmentDoc: () => AgentSessionToPullRequestFragmentDoc,
	AgentSessionType: () => AgentSessionType,
	AgentSessionUpdateExternalUrlDocument: () => AgentSessionUpdateExternalUrlDocument,
	AgentSessionWebhookPayloadFragmentDoc: () => AgentSessionWebhookPayloadFragmentDoc,
	AgentSession_ActivitiesDocument: () => AgentSession_ActivitiesDocument,
	AgentSessionsDocument: () => AgentSessionsDocument,
	AiPromptRulesFragmentDoc: () => AiPromptRulesFragmentDoc,
	AirbyteIntegrationConnectDocument: () => AirbyteIntegrationConnectDocument,
	AppUserNotificationWebhookPayloadFragmentDoc: () => AppUserNotificationWebhookPayloadFragmentDoc,
	AppUserTeamAccessChangedWebhookPayloadFragmentDoc: () => AppUserTeamAccessChangedWebhookPayloadFragmentDoc,
	ApplicationFragmentDoc: () => ApplicationFragmentDoc,
	ApplicationInfoDocument: () => ApplicationInfoDocument,
	ArchiveCustomerNeedDocument: () => ArchiveCustomerNeedDocument,
	ArchiveCycleDocument: () => ArchiveCycleDocument,
	ArchiveInitiativeDocument: () => ArchiveInitiativeDocument,
	ArchiveInitiativeUpdateDocument: () => ArchiveInitiativeUpdateDocument,
	ArchiveIntegrationDocument: () => ArchiveIntegrationDocument,
	ArchiveIssueDocument: () => ArchiveIssueDocument,
	ArchiveNotificationDocument: () => ArchiveNotificationDocument,
	ArchivePayloadFragmentDoc: () => ArchivePayloadFragmentDoc,
	ArchiveProjectDocument: () => ArchiveProjectDocument,
	ArchiveProjectStatusDocument: () => ArchiveProjectStatusDocument,
	ArchiveProjectUpdateDocument: () => ArchiveProjectUpdateDocument,
	ArchiveResponseFragmentDoc: () => ArchiveResponseFragmentDoc,
	ArchiveRoadmapDocument: () => ArchiveRoadmapDocument,
	ArchiveWorkflowStateDocument: () => ArchiveWorkflowStateDocument,
	AsksChannelConnectPayloadFragmentDoc: () => AsksChannelConnectPayloadFragmentDoc,
	AsksWebFormsAuthDocument: () => AsksWebFormsAuthDocument,
	AsksWebFormsAuthResponseFragmentDoc: () => AsksWebFormsAuthResponseFragmentDoc,
	AttachmentConnectionFragmentDoc: () => AttachmentConnectionFragmentDoc,
	AttachmentDocument: () => AttachmentDocument,
	AttachmentFragmentDoc: () => AttachmentFragmentDoc,
	AttachmentIssueDocument: () => AttachmentIssueDocument,
	AttachmentIssue_AttachmentsDocument: () => AttachmentIssue_AttachmentsDocument,
	AttachmentIssue_BotActorDocument: () => AttachmentIssue_BotActorDocument,
	AttachmentIssue_ChildrenDocument: () => AttachmentIssue_ChildrenDocument,
	AttachmentIssue_CommentsDocument: () => AttachmentIssue_CommentsDocument,
	AttachmentIssue_DocumentsDocument: () => AttachmentIssue_DocumentsDocument,
	AttachmentIssue_FormerAttachmentsDocument: () => AttachmentIssue_FormerAttachmentsDocument,
	AttachmentIssue_FormerNeedsDocument: () => AttachmentIssue_FormerNeedsDocument,
	AttachmentIssue_HistoryDocument: () => AttachmentIssue_HistoryDocument,
	AttachmentIssue_InverseRelationsDocument: () => AttachmentIssue_InverseRelationsDocument,
	AttachmentIssue_LabelsDocument: () => AttachmentIssue_LabelsDocument,
	AttachmentIssue_NeedsDocument: () => AttachmentIssue_NeedsDocument,
	AttachmentIssue_RelationsDocument: () => AttachmentIssue_RelationsDocument,
	AttachmentIssue_SubscribersDocument: () => AttachmentIssue_SubscribersDocument,
	AttachmentLinkDiscordDocument: () => AttachmentLinkDiscordDocument,
	AttachmentLinkFrontDocument: () => AttachmentLinkFrontDocument,
	AttachmentLinkGitHubIssueDocument: () => AttachmentLinkGitHubIssueDocument,
	AttachmentLinkGitHubPrDocument: () => AttachmentLinkGitHubPrDocument,
	AttachmentLinkGitLabMrDocument: () => AttachmentLinkGitLabMrDocument,
	AttachmentLinkIntercomDocument: () => AttachmentLinkIntercomDocument,
	AttachmentLinkJiraIssueDocument: () => AttachmentLinkJiraIssueDocument,
	AttachmentLinkSalesforceDocument: () => AttachmentLinkSalesforceDocument,
	AttachmentLinkSlackDocument: () => AttachmentLinkSlackDocument,
	AttachmentLinkUrlDocument: () => AttachmentLinkUrlDocument,
	AttachmentLinkZendeskDocument: () => AttachmentLinkZendeskDocument,
	AttachmentPayloadFragmentDoc: () => AttachmentPayloadFragmentDoc,
	AttachmentSourcesPayloadFragmentDoc: () => AttachmentSourcesPayloadFragmentDoc,
	AttachmentSyncToSlackDocument: () => AttachmentSyncToSlackDocument,
	AttachmentWebhookPayloadFragmentDoc: () => AttachmentWebhookPayloadFragmentDoc,
	AttachmentsDocument: () => AttachmentsDocument,
	AttachmentsForUrlDocument: () => AttachmentsForUrlDocument,
	AuditEntriesDocument: () => AuditEntriesDocument,
	AuditEntryConnectionFragmentDoc: () => AuditEntryConnectionFragmentDoc,
	AuditEntryFragmentDoc: () => AuditEntryFragmentDoc,
	AuditEntryTypeFragmentDoc: () => AuditEntryTypeFragmentDoc,
	AuditEntryTypesDocument: () => AuditEntryTypesDocument,
	AuditEntryWebhookPayloadFragmentDoc: () => AuditEntryWebhookPayloadFragmentDoc,
	AuthIdentityProviderFragmentDoc: () => AuthIdentityProviderFragmentDoc,
	AuthOrganizationFragmentDoc: () => AuthOrganizationFragmentDoc,
	AuthResolverResponseFragmentDoc: () => AuthResolverResponseFragmentDoc,
	AuthUserFragmentDoc: () => AuthUserFragmentDoc,
	AuthenticationSessionResponseFragmentDoc: () => AuthenticationSessionResponseFragmentDoc,
	AuthenticationSessionType: () => AuthenticationSessionType,
	AuthenticationSessionsDocument: () => AuthenticationSessionsDocument,
	AvailableUsersDocument: () => AvailableUsersDocument,
	BaseWebhookPayloadFragmentDoc: () => BaseWebhookPayloadFragmentDoc,
	CommentChildWebhookPayloadFragmentDoc: () => CommentChildWebhookPayloadFragmentDoc,
	CommentConnectionFragmentDoc: () => CommentConnectionFragmentDoc,
	CommentDocument: () => CommentDocument,
	CommentFragmentDoc: () => CommentFragmentDoc,
	CommentPayloadFragmentDoc: () => CommentPayloadFragmentDoc,
	CommentResolveDocument: () => CommentResolveDocument,
	CommentUnresolveDocument: () => CommentUnresolveDocument,
	CommentWebhookPayloadFragmentDoc: () => CommentWebhookPayloadFragmentDoc,
	Comment_BotActorDocument: () => Comment_BotActorDocument,
	Comment_ChildrenDocument: () => Comment_ChildrenDocument,
	Comment_CreatedIssuesDocument: () => Comment_CreatedIssuesDocument,
	Comment_DocumentContentDocument: () => Comment_DocumentContentDocument,
	Comment_DocumentContent_AiPromptRulesDocument: () => Comment_DocumentContent_AiPromptRulesDocument,
	Comment_ExternalThreadDocument: () => Comment_ExternalThreadDocument,
	CommentsDocument: () => CommentsDocument,
	ContactPayloadFragmentDoc: () => ContactPayloadFragmentDoc,
	ContextViewType: () => ContextViewType,
	CreateAgentActivityDocument: () => CreateAgentActivityDocument,
	CreateAttachmentDocument: () => CreateAttachmentDocument,
	CreateCommentDocument: () => CreateCommentDocument,
	CreateContactDocument: () => CreateContactDocument,
	CreateCsvExportReportDocument: () => CreateCsvExportReportDocument,
	CreateCsvExportReportPayloadFragmentDoc: () => CreateCsvExportReportPayloadFragmentDoc,
	CreateCustomViewDocument: () => CreateCustomViewDocument,
	CreateCustomerDocument: () => CreateCustomerDocument,
	CreateCustomerNeedDocument: () => CreateCustomerNeedDocument,
	CreateCustomerStatusDocument: () => CreateCustomerStatusDocument,
	CreateCustomerTierDocument: () => CreateCustomerTierDocument,
	CreateCycleDocument: () => CreateCycleDocument,
	CreateDocumentDocument: () => CreateDocumentDocument,
	CreateEmailIntakeAddressDocument: () => CreateEmailIntakeAddressDocument,
	CreateEmojiDocument: () => CreateEmojiDocument,
	CreateEntityExternalLinkDocument: () => CreateEntityExternalLinkDocument,
	CreateFavoriteDocument: () => CreateFavoriteDocument,
	CreateGitAutomationStateDocument: () => CreateGitAutomationStateDocument,
	CreateGitAutomationTargetBranchDocument: () => CreateGitAutomationTargetBranchDocument,
	CreateInitiativeDocument: () => CreateInitiativeDocument,
	CreateInitiativeRelationDocument: () => CreateInitiativeRelationDocument,
	CreateInitiativeToProjectDocument: () => CreateInitiativeToProjectDocument,
	CreateInitiativeUpdateDocument: () => CreateInitiativeUpdateDocument,
	CreateInitiativeUpdateReminderDocument: () => CreateInitiativeUpdateReminderDocument,
	CreateIntegrationGithubCommitDocument: () => CreateIntegrationGithubCommitDocument,
	CreateIntegrationTemplateDocument: () => CreateIntegrationTemplateDocument,
	CreateIntegrationsSettingsDocument: () => CreateIntegrationsSettingsDocument,
	CreateIssueBatchDocument: () => CreateIssueBatchDocument,
	CreateIssueDocument: () => CreateIssueDocument,
	CreateIssueLabelDocument: () => CreateIssueLabelDocument,
	CreateIssueRelationDocument: () => CreateIssueRelationDocument,
	CreateNotificationSubscriptionDocument: () => CreateNotificationSubscriptionDocument,
	CreateOrJoinOrganizationResponseFragmentDoc: () => CreateOrJoinOrganizationResponseFragmentDoc,
	CreateOrganizationFromOnboardingDocument: () => CreateOrganizationFromOnboardingDocument,
	CreateOrganizationInviteDocument: () => CreateOrganizationInviteDocument,
	CreateProjectDocument: () => CreateProjectDocument,
	CreateProjectLabelDocument: () => CreateProjectLabelDocument,
	CreateProjectMilestoneDocument: () => CreateProjectMilestoneDocument,
	CreateProjectRelationDocument: () => CreateProjectRelationDocument,
	CreateProjectStatusDocument: () => CreateProjectStatusDocument,
	CreateProjectUpdateDocument: () => CreateProjectUpdateDocument,
	CreateProjectUpdateReminderDocument: () => CreateProjectUpdateReminderDocument,
	CreatePushSubscriptionDocument: () => CreatePushSubscriptionDocument,
	CreateReactionDocument: () => CreateReactionDocument,
	CreateRoadmapDocument: () => CreateRoadmapDocument,
	CreateRoadmapToProjectDocument: () => CreateRoadmapToProjectDocument,
	CreateTeamDocument: () => CreateTeamDocument,
	CreateTeamMembershipDocument: () => CreateTeamMembershipDocument,
	CreateTemplateDocument: () => CreateTemplateDocument,
	CreateTimeScheduleDocument: () => CreateTimeScheduleDocument,
	CreateTriageResponsibilityDocument: () => CreateTriageResponsibilityDocument,
	CreateViewPreferencesDocument: () => CreateViewPreferencesDocument,
	CreateWebhookDocument: () => CreateWebhookDocument,
	CreateWorkflowStateDocument: () => CreateWorkflowStateDocument,
	CustomResourceWebhookPayloadFragmentDoc: () => CustomResourceWebhookPayloadFragmentDoc,
	CustomViewConnectionFragmentDoc: () => CustomViewConnectionFragmentDoc,
	CustomViewDocument: () => CustomViewDocument,
	CustomViewFragmentDoc: () => CustomViewFragmentDoc,
	CustomViewHasSubscribersDocument: () => CustomViewHasSubscribersDocument,
	CustomViewHasSubscribersPayloadFragmentDoc: () => CustomViewHasSubscribersPayloadFragmentDoc,
	CustomViewNotificationSubscriptionFragmentDoc: () => CustomViewNotificationSubscriptionFragmentDoc,
	CustomViewPayloadFragmentDoc: () => CustomViewPayloadFragmentDoc,
	CustomViewSuggestionPayloadFragmentDoc: () => CustomViewSuggestionPayloadFragmentDoc,
	CustomView_InitiativesDocument: () => CustomView_InitiativesDocument,
	CustomView_IssuesDocument: () => CustomView_IssuesDocument,
	CustomView_OrganizationViewPreferencesDocument: () => CustomView_OrganizationViewPreferencesDocument,
	CustomView_OrganizationViewPreferences_PreferencesDocument: () => CustomView_OrganizationViewPreferences_PreferencesDocument,
	CustomView_ProjectsDocument: () => CustomView_ProjectsDocument,
	CustomView_UserViewPreferencesDocument: () => CustomView_UserViewPreferencesDocument,
	CustomView_UserViewPreferences_PreferencesDocument: () => CustomView_UserViewPreferences_PreferencesDocument,
	CustomView_ViewPreferencesValuesDocument: () => CustomView_ViewPreferencesValuesDocument,
	CustomViewsDocument: () => CustomViewsDocument,
	CustomerChildWebhookPayloadFragmentDoc: () => CustomerChildWebhookPayloadFragmentDoc,
	CustomerConnectionFragmentDoc: () => CustomerConnectionFragmentDoc,
	CustomerDocument: () => CustomerDocument,
	CustomerFragmentDoc: () => CustomerFragmentDoc,
	CustomerMergeDocument: () => CustomerMergeDocument,
	CustomerNeedArchivePayloadFragmentDoc: () => CustomerNeedArchivePayloadFragmentDoc,
	CustomerNeedChildWebhookPayloadFragmentDoc: () => CustomerNeedChildWebhookPayloadFragmentDoc,
	CustomerNeedConnectionFragmentDoc: () => CustomerNeedConnectionFragmentDoc,
	CustomerNeedCreateFromAttachmentDocument: () => CustomerNeedCreateFromAttachmentDocument,
	CustomerNeedDocument: () => CustomerNeedDocument,
	CustomerNeedFragmentDoc: () => CustomerNeedFragmentDoc,
	CustomerNeedNotificationFragmentDoc: () => CustomerNeedNotificationFragmentDoc,
	CustomerNeedPayloadFragmentDoc: () => CustomerNeedPayloadFragmentDoc,
	CustomerNeedUpdatePayloadFragmentDoc: () => CustomerNeedUpdatePayloadFragmentDoc,
	CustomerNeedWebhookPayloadFragmentDoc: () => CustomerNeedWebhookPayloadFragmentDoc,
	CustomerNeed_ProjectAttachmentDocument: () => CustomerNeed_ProjectAttachmentDocument,
	CustomerNeedsDocument: () => CustomerNeedsDocument,
	CustomerNotificationFragmentDoc: () => CustomerNotificationFragmentDoc,
	CustomerNotificationSubscriptionFragmentDoc: () => CustomerNotificationSubscriptionFragmentDoc,
	CustomerPayloadFragmentDoc: () => CustomerPayloadFragmentDoc,
	CustomerStatusChildWebhookPayloadFragmentDoc: () => CustomerStatusChildWebhookPayloadFragmentDoc,
	CustomerStatusConnectionFragmentDoc: () => CustomerStatusConnectionFragmentDoc,
	CustomerStatusDocument: () => CustomerStatusDocument,
	CustomerStatusFragmentDoc: () => CustomerStatusFragmentDoc,
	CustomerStatusPayloadFragmentDoc: () => CustomerStatusPayloadFragmentDoc,
	CustomerStatusType: () => CustomerStatusType,
	CustomerStatusesDocument: () => CustomerStatusesDocument,
	CustomerTierChildWebhookPayloadFragmentDoc: () => CustomerTierChildWebhookPayloadFragmentDoc,
	CustomerTierConnectionFragmentDoc: () => CustomerTierConnectionFragmentDoc,
	CustomerTierDocument: () => CustomerTierDocument,
	CustomerTierFragmentDoc: () => CustomerTierFragmentDoc,
	CustomerTierPayloadFragmentDoc: () => CustomerTierPayloadFragmentDoc,
	CustomerTiersDocument: () => CustomerTiersDocument,
	CustomerUnsyncDocument: () => CustomerUnsyncDocument,
	CustomerUpsertDocument: () => CustomerUpsertDocument,
	CustomerVisibilityMode: () => CustomerVisibilityMode,
	CustomerWebhookPayloadFragmentDoc: () => CustomerWebhookPayloadFragmentDoc,
	CustomersDocument: () => CustomersDocument,
	CycleArchivePayloadFragmentDoc: () => CycleArchivePayloadFragmentDoc,
	CycleChildWebhookPayloadFragmentDoc: () => CycleChildWebhookPayloadFragmentDoc,
	CycleConnectionFragmentDoc: () => CycleConnectionFragmentDoc,
	CycleDocument: () => CycleDocument,
	CycleFragmentDoc: () => CycleFragmentDoc,
	CycleNotificationSubscriptionFragmentDoc: () => CycleNotificationSubscriptionFragmentDoc,
	CyclePayloadFragmentDoc: () => CyclePayloadFragmentDoc,
	CyclePeriod: () => CyclePeriod,
	CycleShiftAllDocument: () => CycleShiftAllDocument,
	CycleStartUpcomingCycleTodayDocument: () => CycleStartUpcomingCycleTodayDocument,
	CycleWebhookPayloadFragmentDoc: () => CycleWebhookPayloadFragmentDoc,
	Cycle_IssuesDocument: () => Cycle_IssuesDocument,
	Cycle_UncompletedIssuesUponCloseDocument: () => Cycle_UncompletedIssuesUponCloseDocument,
	CyclesDocument: () => CyclesDocument,
	DateResolutionType: () => DateResolutionType,
	Day: () => Day,
	DeleteAttachmentDocument: () => DeleteAttachmentDocument,
	DeleteCommentDocument: () => DeleteCommentDocument,
	DeleteCustomViewDocument: () => DeleteCustomViewDocument,
	DeleteCustomerDocument: () => DeleteCustomerDocument,
	DeleteCustomerNeedDocument: () => DeleteCustomerNeedDocument,
	DeleteCustomerStatusDocument: () => DeleteCustomerStatusDocument,
	DeleteCustomerTierDocument: () => DeleteCustomerTierDocument,
	DeleteDocumentDocument: () => DeleteDocumentDocument,
	DeleteEmailIntakeAddressDocument: () => DeleteEmailIntakeAddressDocument,
	DeleteEmojiDocument: () => DeleteEmojiDocument,
	DeleteEntityExternalLinkDocument: () => DeleteEntityExternalLinkDocument,
	DeleteFavoriteDocument: () => DeleteFavoriteDocument,
	DeleteGitAutomationStateDocument: () => DeleteGitAutomationStateDocument,
	DeleteGitAutomationTargetBranchDocument: () => DeleteGitAutomationTargetBranchDocument,
	DeleteInitiativeDocument: () => DeleteInitiativeDocument,
	DeleteInitiativeRelationDocument: () => DeleteInitiativeRelationDocument,
	DeleteInitiativeToProjectDocument: () => DeleteInitiativeToProjectDocument,
	DeleteIntegrationDocument: () => DeleteIntegrationDocument,
	DeleteIntegrationIntercomDocument: () => DeleteIntegrationIntercomDocument,
	DeleteIntegrationTemplateDocument: () => DeleteIntegrationTemplateDocument,
	DeleteIssueDocument: () => DeleteIssueDocument,
	DeleteIssueImportDocument: () => DeleteIssueImportDocument,
	DeleteIssueLabelDocument: () => DeleteIssueLabelDocument,
	DeleteIssueRelationDocument: () => DeleteIssueRelationDocument,
	DeleteNotificationSubscriptionDocument: () => DeleteNotificationSubscriptionDocument,
	DeleteOrganizationCancelDocument: () => DeleteOrganizationCancelDocument,
	DeleteOrganizationDocument: () => DeleteOrganizationDocument,
	DeleteOrganizationDomainDocument: () => DeleteOrganizationDomainDocument,
	DeleteOrganizationInviteDocument: () => DeleteOrganizationInviteDocument,
	DeletePayloadFragmentDoc: () => DeletePayloadFragmentDoc,
	DeleteProjectDocument: () => DeleteProjectDocument,
	DeleteProjectLabelDocument: () => DeleteProjectLabelDocument,
	DeleteProjectMilestoneDocument: () => DeleteProjectMilestoneDocument,
	DeleteProjectRelationDocument: () => DeleteProjectRelationDocument,
	DeleteProjectUpdateDocument: () => DeleteProjectUpdateDocument,
	DeletePushSubscriptionDocument: () => DeletePushSubscriptionDocument,
	DeleteReactionDocument: () => DeleteReactionDocument,
	DeleteRoadmapDocument: () => DeleteRoadmapDocument,
	DeleteRoadmapToProjectDocument: () => DeleteRoadmapToProjectDocument,
	DeleteTeamCyclesDocument: () => DeleteTeamCyclesDocument,
	DeleteTeamDocument: () => DeleteTeamDocument,
	DeleteTeamKeyDocument: () => DeleteTeamKeyDocument,
	DeleteTeamMembershipDocument: () => DeleteTeamMembershipDocument,
	DeleteTemplateDocument: () => DeleteTemplateDocument,
	DeleteTimeScheduleDocument: () => DeleteTimeScheduleDocument,
	DeleteTriageResponsibilityDocument: () => DeleteTriageResponsibilityDocument,
	DeleteViewPreferencesDocument: () => DeleteViewPreferencesDocument,
	DeleteWebhookDocument: () => DeleteWebhookDocument,
	DocumentArchivePayloadFragmentDoc: () => DocumentArchivePayloadFragmentDoc,
	DocumentChildWebhookPayloadFragmentDoc: () => DocumentChildWebhookPayloadFragmentDoc,
	DocumentConnectionFragmentDoc: () => DocumentConnectionFragmentDoc,
	DocumentContentChildWebhookPayloadFragmentDoc: () => DocumentContentChildWebhookPayloadFragmentDoc,
	DocumentContentFragmentDoc: () => DocumentContentFragmentDoc,
	DocumentContentHistoryDocument: () => DocumentContentHistoryDocument,
	DocumentContentHistoryPayloadFragmentDoc: () => DocumentContentHistoryPayloadFragmentDoc,
	DocumentContentHistoryTypeFragmentDoc: () => DocumentContentHistoryTypeFragmentDoc,
	DocumentDocument: () => DocumentDocument,
	DocumentFragmentDoc: () => DocumentFragmentDoc,
	DocumentNotificationFragmentDoc: () => DocumentNotificationFragmentDoc,
	DocumentPayloadFragmentDoc: () => DocumentPayloadFragmentDoc,
	DocumentSearchPayloadFragmentDoc: () => DocumentSearchPayloadFragmentDoc,
	DocumentSearchResultFragmentDoc: () => DocumentSearchResultFragmentDoc,
	DocumentWebhookPayloadFragmentDoc: () => DocumentWebhookPayloadFragmentDoc,
	Document_CommentsDocument: () => Document_CommentsDocument,
	DocumentsDocument: () => DocumentsDocument,
	DraftConnectionFragmentDoc: () => DraftConnectionFragmentDoc,
	DraftFragmentDoc: () => DraftFragmentDoc,
	EmailIntakeAddressDocument: () => EmailIntakeAddressDocument,
	EmailIntakeAddressFragmentDoc: () => EmailIntakeAddressFragmentDoc,
	EmailIntakeAddressPayloadFragmentDoc: () => EmailIntakeAddressPayloadFragmentDoc,
	EmailIntakeAddressRotateDocument: () => EmailIntakeAddressRotateDocument,
	EmailIntakeAddressType: () => EmailIntakeAddressType,
	EmailIntakeAddress_SesDomainIdentityDocument: () => EmailIntakeAddress_SesDomainIdentityDocument,
	EmailTokenUserAccountAuthDocument: () => EmailTokenUserAccountAuthDocument,
	EmailUnsubscribeDocument: () => EmailUnsubscribeDocument,
	EmailUnsubscribePayloadFragmentDoc: () => EmailUnsubscribePayloadFragmentDoc,
	EmailUserAccountAuthChallengeDocument: () => EmailUserAccountAuthChallengeDocument,
	EmailUserAccountAuthChallengeResponseFragmentDoc: () => EmailUserAccountAuthChallengeResponseFragmentDoc,
	EmojiConnectionFragmentDoc: () => EmojiConnectionFragmentDoc,
	EmojiDocument: () => EmojiDocument,
	EmojiFragmentDoc: () => EmojiFragmentDoc,
	EmojiPayloadFragmentDoc: () => EmojiPayloadFragmentDoc,
	EmojisDocument: () => EmojisDocument,
	EntityExternalLinkConnectionFragmentDoc: () => EntityExternalLinkConnectionFragmentDoc,
	EntityExternalLinkDocument: () => EntityExternalLinkDocument,
	EntityExternalLinkFragmentDoc: () => EntityExternalLinkFragmentDoc,
	EntityExternalLinkPayloadFragmentDoc: () => EntityExternalLinkPayloadFragmentDoc,
	EntityFragmentDoc: () => EntityFragmentDoc,
	EntityWebhookPayloadFragmentDoc: () => EntityWebhookPayloadFragmentDoc,
	ExternalEntityInfoFragmentDoc: () => ExternalEntityInfoFragmentDoc,
	ExternalEntityInfoGithubMetadataFragmentDoc: () => ExternalEntityInfoGithubMetadataFragmentDoc,
	ExternalEntityInfoJiraMetadataFragmentDoc: () => ExternalEntityInfoJiraMetadataFragmentDoc,
	ExternalEntitySlackMetadataFragmentDoc: () => ExternalEntitySlackMetadataFragmentDoc,
	ExternalSyncService: () => ExternalSyncService,
	ExternalUserChildWebhookPayloadFragmentDoc: () => ExternalUserChildWebhookPayloadFragmentDoc,
	ExternalUserConnectionFragmentDoc: () => ExternalUserConnectionFragmentDoc,
	ExternalUserDocument: () => ExternalUserDocument,
	ExternalUserFragmentDoc: () => ExternalUserFragmentDoc,
	ExternalUsersDocument: () => ExternalUsersDocument,
	FacetConnectionFragmentDoc: () => FacetConnectionFragmentDoc,
	FacetFragmentDoc: () => FacetFragmentDoc,
	FacetPageSource: () => FacetPageSource,
	FavoriteConnectionFragmentDoc: () => FavoriteConnectionFragmentDoc,
	FavoriteDocument: () => FavoriteDocument,
	FavoriteFragmentDoc: () => FavoriteFragmentDoc,
	FavoritePayloadFragmentDoc: () => FavoritePayloadFragmentDoc,
	Favorite_ChildrenDocument: () => Favorite_ChildrenDocument,
	FavoritesDocument: () => FavoritesDocument,
	FeedSummarySchedule: () => FeedSummarySchedule,
	FetchDataPayloadFragmentDoc: () => FetchDataPayloadFragmentDoc,
	FileUploadDeletePayloadFragmentDoc: () => FileUploadDeletePayloadFragmentDoc,
	FileUploadDocument: () => FileUploadDocument,
	FrequencyResolutionType: () => FrequencyResolutionType,
	FrontAttachmentPayloadFragmentDoc: () => FrontAttachmentPayloadFragmentDoc,
	GitAutomationStateConnectionFragmentDoc: () => GitAutomationStateConnectionFragmentDoc,
	GitAutomationStateFragmentDoc: () => GitAutomationStateFragmentDoc,
	GitAutomationStatePayloadFragmentDoc: () => GitAutomationStatePayloadFragmentDoc,
	GitAutomationStates: () => GitAutomationStates,
	GitAutomationTargetBranchFragmentDoc: () => GitAutomationTargetBranchFragmentDoc,
	GitAutomationTargetBranchPayloadFragmentDoc: () => GitAutomationTargetBranchPayloadFragmentDoc,
	GitHubCommitIntegrationPayloadFragmentDoc: () => GitHubCommitIntegrationPayloadFragmentDoc,
	GitHubEnterpriseServerInstallVerificationPayloadFragmentDoc: () => GitHubEnterpriseServerInstallVerificationPayloadFragmentDoc,
	GitHubEnterpriseServerPayloadFragmentDoc: () => GitHubEnterpriseServerPayloadFragmentDoc,
	GitLabIntegrationCreatePayloadFragmentDoc: () => GitLabIntegrationCreatePayloadFragmentDoc,
	GitLinkKind: () => GitLinkKind,
	GithubOrgType: () => GithubOrgType,
	GoogleUserAccountAuthDocument: () => GoogleUserAccountAuthDocument,
	GuidanceRuleWebhookPayloadFragmentDoc: () => GuidanceRuleWebhookPayloadFragmentDoc,
	IdentityProviderFragmentDoc: () => IdentityProviderFragmentDoc,
	IdentityProviderType: () => IdentityProviderType,
	ImageUploadFromUrlDocument: () => ImageUploadFromUrlDocument,
	ImageUploadFromUrlPayloadFragmentDoc: () => ImageUploadFromUrlPayloadFragmentDoc,
	ImportFileUploadDocument: () => ImportFileUploadDocument,
	InitiativeArchivePayloadFragmentDoc: () => InitiativeArchivePayloadFragmentDoc,
	InitiativeChildWebhookPayloadFragmentDoc: () => InitiativeChildWebhookPayloadFragmentDoc,
	InitiativeConnectionFragmentDoc: () => InitiativeConnectionFragmentDoc,
	InitiativeDocument: () => InitiativeDocument,
	InitiativeFragmentDoc: () => InitiativeFragmentDoc,
	InitiativeHistoryConnectionFragmentDoc: () => InitiativeHistoryConnectionFragmentDoc,
	InitiativeHistoryFragmentDoc: () => InitiativeHistoryFragmentDoc,
	InitiativeNotificationFragmentDoc: () => InitiativeNotificationFragmentDoc,
	InitiativeNotificationSubscriptionFragmentDoc: () => InitiativeNotificationSubscriptionFragmentDoc,
	InitiativePayloadFragmentDoc: () => InitiativePayloadFragmentDoc,
	InitiativeRelationConnectionFragmentDoc: () => InitiativeRelationConnectionFragmentDoc,
	InitiativeRelationDocument: () => InitiativeRelationDocument,
	InitiativeRelationFragmentDoc: () => InitiativeRelationFragmentDoc,
	InitiativeRelationPayloadFragmentDoc: () => InitiativeRelationPayloadFragmentDoc,
	InitiativeRelationsDocument: () => InitiativeRelationsDocument,
	InitiativeStatus: () => InitiativeStatus,
	InitiativeTab: () => InitiativeTab,
	InitiativeToProjectConnectionFragmentDoc: () => InitiativeToProjectConnectionFragmentDoc,
	InitiativeToProjectDocument: () => InitiativeToProjectDocument,
	InitiativeToProjectFragmentDoc: () => InitiativeToProjectFragmentDoc,
	InitiativeToProjectPayloadFragmentDoc: () => InitiativeToProjectPayloadFragmentDoc,
	InitiativeToProjectsDocument: () => InitiativeToProjectsDocument,
	InitiativeUpdateArchivePayloadFragmentDoc: () => InitiativeUpdateArchivePayloadFragmentDoc,
	InitiativeUpdateChildWebhookPayloadFragmentDoc: () => InitiativeUpdateChildWebhookPayloadFragmentDoc,
	InitiativeUpdateConnectionFragmentDoc: () => InitiativeUpdateConnectionFragmentDoc,
	InitiativeUpdateDocument: () => InitiativeUpdateDocument,
	InitiativeUpdateFragmentDoc: () => InitiativeUpdateFragmentDoc,
	InitiativeUpdateHealthType: () => InitiativeUpdateHealthType,
	InitiativeUpdatePayloadFragmentDoc: () => InitiativeUpdatePayloadFragmentDoc,
	InitiativeUpdateReminderPayloadFragmentDoc: () => InitiativeUpdateReminderPayloadFragmentDoc,
	InitiativeUpdateWebhookPayloadFragmentDoc: () => InitiativeUpdateWebhookPayloadFragmentDoc,
	InitiativeUpdate_CommentsDocument: () => InitiativeUpdate_CommentsDocument,
	InitiativeUpdatesDocument: () => InitiativeUpdatesDocument,
	InitiativeWebhookPayloadFragmentDoc: () => InitiativeWebhookPayloadFragmentDoc,
	Initiative_DocumentContentDocument: () => Initiative_DocumentContentDocument,
	Initiative_DocumentContent_AiPromptRulesDocument: () => Initiative_DocumentContent_AiPromptRulesDocument,
	Initiative_DocumentsDocument: () => Initiative_DocumentsDocument,
	Initiative_HistoryDocument: () => Initiative_HistoryDocument,
	Initiative_InitiativeUpdatesDocument: () => Initiative_InitiativeUpdatesDocument,
	Initiative_LinksDocument: () => Initiative_LinksDocument,
	Initiative_ProjectsDocument: () => Initiative_ProjectsDocument,
	Initiative_SubInitiativesDocument: () => Initiative_SubInitiativesDocument,
	InitiativesDocument: () => InitiativesDocument,
	IntegrationActorWebhookPayloadFragmentDoc: () => IntegrationActorWebhookPayloadFragmentDoc,
	IntegrationAsksConnectChannelDocument: () => IntegrationAsksConnectChannelDocument,
	IntegrationChildWebhookPayloadFragmentDoc: () => IntegrationChildWebhookPayloadFragmentDoc,
	IntegrationConnectionFragmentDoc: () => IntegrationConnectionFragmentDoc,
	IntegrationDiscordDocument: () => IntegrationDiscordDocument,
	IntegrationDocument: () => IntegrationDocument,
	IntegrationFigmaDocument: () => IntegrationFigmaDocument,
	IntegrationFragmentDoc: () => IntegrationFragmentDoc,
	IntegrationFrontDocument: () => IntegrationFrontDocument,
	IntegrationGitHubEnterpriseServerConnectDocument: () => IntegrationGitHubEnterpriseServerConnectDocument,
	IntegrationGitHubPersonalDocument: () => IntegrationGitHubPersonalDocument,
	IntegrationGithubConnectDocument: () => IntegrationGithubConnectDocument,
	IntegrationGithubImportConnectDocument: () => IntegrationGithubImportConnectDocument,
	IntegrationGithubImportRefreshDocument: () => IntegrationGithubImportRefreshDocument,
	IntegrationGitlabConnectDocument: () => IntegrationGitlabConnectDocument,
	IntegrationGongDocument: () => IntegrationGongDocument,
	IntegrationGoogleSheetsDocument: () => IntegrationGoogleSheetsDocument,
	IntegrationHasScopesDocument: () => IntegrationHasScopesDocument,
	IntegrationHasScopesPayloadFragmentDoc: () => IntegrationHasScopesPayloadFragmentDoc,
	IntegrationIntercomDocument: () => IntegrationIntercomDocument,
	IntegrationJiraPersonalDocument: () => IntegrationJiraPersonalDocument,
	IntegrationLoomDocument: () => IntegrationLoomDocument,
	IntegrationPayloadFragmentDoc: () => IntegrationPayloadFragmentDoc,
	IntegrationRequestDocument: () => IntegrationRequestDocument,
	IntegrationRequestPayloadFragmentDoc: () => IntegrationRequestPayloadFragmentDoc,
	IntegrationSalesforceDocument: () => IntegrationSalesforceDocument,
	IntegrationSentryConnectDocument: () => IntegrationSentryConnectDocument,
	IntegrationService: () => IntegrationService,
	IntegrationSlackAsksDocument: () => IntegrationSlackAsksDocument,
	IntegrationSlackCustomViewNotificationsDocument: () => IntegrationSlackCustomViewNotificationsDocument,
	IntegrationSlackCustomerChannelLinkDocument: () => IntegrationSlackCustomerChannelLinkDocument,
	IntegrationSlackDocument: () => IntegrationSlackDocument,
	IntegrationSlackImportEmojisDocument: () => IntegrationSlackImportEmojisDocument,
	IntegrationSlackOrAsksUpdateSlackTeamNameDocument: () => IntegrationSlackOrAsksUpdateSlackTeamNameDocument,
	IntegrationSlackOrgProjectUpdatesPostDocument: () => IntegrationSlackOrgProjectUpdatesPostDocument,
	IntegrationSlackPersonalDocument: () => IntegrationSlackPersonalDocument,
	IntegrationSlackPostDocument: () => IntegrationSlackPostDocument,
	IntegrationSlackProjectPostDocument: () => IntegrationSlackProjectPostDocument,
	IntegrationSlackWorkspaceNamePayloadFragmentDoc: () => IntegrationSlackWorkspaceNamePayloadFragmentDoc,
	IntegrationTemplateConnectionFragmentDoc: () => IntegrationTemplateConnectionFragmentDoc,
	IntegrationTemplateDocument: () => IntegrationTemplateDocument,
	IntegrationTemplateFragmentDoc: () => IntegrationTemplateFragmentDoc,
	IntegrationTemplatePayloadFragmentDoc: () => IntegrationTemplatePayloadFragmentDoc,
	IntegrationTemplatesDocument: () => IntegrationTemplatesDocument,
	IntegrationZendeskDocument: () => IntegrationZendeskDocument,
	IntegrationsDocument: () => IntegrationsDocument,
	IntegrationsSettingsDocument: () => IntegrationsSettingsDocument,
	IntegrationsSettingsFragmentDoc: () => IntegrationsSettingsFragmentDoc,
	IntegrationsSettingsPayloadFragmentDoc: () => IntegrationsSettingsPayloadFragmentDoc,
	IssueAddLabelDocument: () => IssueAddLabelDocument,
	IssueArchivePayloadFragmentDoc: () => IssueArchivePayloadFragmentDoc,
	IssueAssignedToYouNotificationWebhookPayloadFragmentDoc: () => IssueAssignedToYouNotificationWebhookPayloadFragmentDoc,
	IssueBatchPayloadFragmentDoc: () => IssueBatchPayloadFragmentDoc,
	IssueChildWebhookPayloadFragmentDoc: () => IssueChildWebhookPayloadFragmentDoc,
	IssueCommentMentionNotificationWebhookPayloadFragmentDoc: () => IssueCommentMentionNotificationWebhookPayloadFragmentDoc,
	IssueCommentReactionNotificationWebhookPayloadFragmentDoc: () => IssueCommentReactionNotificationWebhookPayloadFragmentDoc,
	IssueConnectionFragmentDoc: () => IssueConnectionFragmentDoc,
	IssueDocument: () => IssueDocument,
	IssueEmojiReactionNotificationWebhookPayloadFragmentDoc: () => IssueEmojiReactionNotificationWebhookPayloadFragmentDoc,
	IssueExternalSyncDisableDocument: () => IssueExternalSyncDisableDocument,
	IssueFigmaFileKeySearchDocument: () => IssueFigmaFileKeySearchDocument,
	IssueFilterSuggestionDocument: () => IssueFilterSuggestionDocument,
	IssueFilterSuggestionPayloadFragmentDoc: () => IssueFilterSuggestionPayloadFragmentDoc,
	IssueFragmentDoc: () => IssueFragmentDoc,
	IssueHistoryConnectionFragmentDoc: () => IssueHistoryConnectionFragmentDoc,
	IssueHistoryFragmentDoc: () => IssueHistoryFragmentDoc,
	IssueImportCheckCsvDocument: () => IssueImportCheckCsvDocument,
	IssueImportCheckPayloadFragmentDoc: () => IssueImportCheckPayloadFragmentDoc,
	IssueImportCheckSyncDocument: () => IssueImportCheckSyncDocument,
	IssueImportCreateAsanaDocument: () => IssueImportCreateAsanaDocument,
	IssueImportCreateClubhouseDocument: () => IssueImportCreateClubhouseDocument,
	IssueImportCreateCsvJiraDocument: () => IssueImportCreateCsvJiraDocument,
	IssueImportCreateGithubDocument: () => IssueImportCreateGithubDocument,
	IssueImportCreateJiraDocument: () => IssueImportCreateJiraDocument,
	IssueImportDeletePayloadFragmentDoc: () => IssueImportDeletePayloadFragmentDoc,
	IssueImportFragmentDoc: () => IssueImportFragmentDoc,
	IssueImportJqlCheckDocument: () => IssueImportJqlCheckDocument,
	IssueImportJqlCheckPayloadFragmentDoc: () => IssueImportJqlCheckPayloadFragmentDoc,
	IssueImportPayloadFragmentDoc: () => IssueImportPayloadFragmentDoc,
	IssueImportProcessDocument: () => IssueImportProcessDocument,
	IssueImportSyncCheckPayloadFragmentDoc: () => IssueImportSyncCheckPayloadFragmentDoc,
	IssueLabelChildWebhookPayloadFragmentDoc: () => IssueLabelChildWebhookPayloadFragmentDoc,
	IssueLabelConnectionFragmentDoc: () => IssueLabelConnectionFragmentDoc,
	IssueLabelDocument: () => IssueLabelDocument,
	IssueLabelFragmentDoc: () => IssueLabelFragmentDoc,
	IssueLabelPayloadFragmentDoc: () => IssueLabelPayloadFragmentDoc,
	IssueLabelRestoreDocument: () => IssueLabelRestoreDocument,
	IssueLabelRetireDocument: () => IssueLabelRetireDocument,
	IssueLabelWebhookPayloadFragmentDoc: () => IssueLabelWebhookPayloadFragmentDoc,
	IssueLabel_ChildrenDocument: () => IssueLabel_ChildrenDocument,
	IssueLabel_IssuesDocument: () => IssueLabel_IssuesDocument,
	IssueLabelsDocument: () => IssueLabelsDocument,
	IssueMentionNotificationWebhookPayloadFragmentDoc: () => IssueMentionNotificationWebhookPayloadFragmentDoc,
	IssueNewCommentNotificationWebhookPayloadFragmentDoc: () => IssueNewCommentNotificationWebhookPayloadFragmentDoc,
	IssueNotificationFragmentDoc: () => IssueNotificationFragmentDoc,
	IssuePayloadFragmentDoc: () => IssuePayloadFragmentDoc,
	IssuePriorityValueFragmentDoc: () => IssuePriorityValueFragmentDoc,
	IssuePriorityValuesDocument: () => IssuePriorityValuesDocument,
	IssueRelationConnectionFragmentDoc: () => IssueRelationConnectionFragmentDoc,
	IssueRelationDocument: () => IssueRelationDocument,
	IssueRelationFragmentDoc: () => IssueRelationFragmentDoc,
	IssueRelationHistoryPayloadFragmentDoc: () => IssueRelationHistoryPayloadFragmentDoc,
	IssueRelationPayloadFragmentDoc: () => IssueRelationPayloadFragmentDoc,
	IssueRelationType: () => IssueRelationType,
	IssueRelationsDocument: () => IssueRelationsDocument,
	IssueReminderDocument: () => IssueReminderDocument,
	IssueRemoveLabelDocument: () => IssueRemoveLabelDocument,
	IssueSearchDocument: () => IssueSearchDocument,
	IssueSearchPayloadFragmentDoc: () => IssueSearchPayloadFragmentDoc,
	IssueSearchResultFragmentDoc: () => IssueSearchResultFragmentDoc,
	IssueSlaWebhookPayloadFragmentDoc: () => IssueSlaWebhookPayloadFragmentDoc,
	IssueStateSpanConnectionFragmentDoc: () => IssueStateSpanConnectionFragmentDoc,
	IssueStateSpanFragmentDoc: () => IssueStateSpanFragmentDoc,
	IssueStatusChangedNotificationWebhookPayloadFragmentDoc: () => IssueStatusChangedNotificationWebhookPayloadFragmentDoc,
	IssueSubscribeDocument: () => IssueSubscribeDocument,
	IssueSuggestionConnectionFragmentDoc: () => IssueSuggestionConnectionFragmentDoc,
	IssueSuggestionFragmentDoc: () => IssueSuggestionFragmentDoc,
	IssueSuggestionMetadataFragmentDoc: () => IssueSuggestionMetadataFragmentDoc,
	IssueSuggestionState: () => IssueSuggestionState,
	IssueSuggestionType: () => IssueSuggestionType,
	IssueTitleSuggestionFromCustomerRequestDocument: () => IssueTitleSuggestionFromCustomerRequestDocument,
	IssueTitleSuggestionFromCustomerRequestPayloadFragmentDoc: () => IssueTitleSuggestionFromCustomerRequestPayloadFragmentDoc,
	IssueUnassignedFromYouNotificationWebhookPayloadFragmentDoc: () => IssueUnassignedFromYouNotificationWebhookPayloadFragmentDoc,
	IssueUnsubscribeDocument: () => IssueUnsubscribeDocument,
	IssueVcsBranchSearchDocument: () => IssueVcsBranchSearchDocument,
	IssueVcsBranchSearch_AttachmentsDocument: () => IssueVcsBranchSearch_AttachmentsDocument,
	IssueVcsBranchSearch_BotActorDocument: () => IssueVcsBranchSearch_BotActorDocument,
	IssueVcsBranchSearch_ChildrenDocument: () => IssueVcsBranchSearch_ChildrenDocument,
	IssueVcsBranchSearch_CommentsDocument: () => IssueVcsBranchSearch_CommentsDocument,
	IssueVcsBranchSearch_DocumentsDocument: () => IssueVcsBranchSearch_DocumentsDocument,
	IssueVcsBranchSearch_FormerAttachmentsDocument: () => IssueVcsBranchSearch_FormerAttachmentsDocument,
	IssueVcsBranchSearch_FormerNeedsDocument: () => IssueVcsBranchSearch_FormerNeedsDocument,
	IssueVcsBranchSearch_HistoryDocument: () => IssueVcsBranchSearch_HistoryDocument,
	IssueVcsBranchSearch_InverseRelationsDocument: () => IssueVcsBranchSearch_InverseRelationsDocument,
	IssueVcsBranchSearch_LabelsDocument: () => IssueVcsBranchSearch_LabelsDocument,
	IssueVcsBranchSearch_NeedsDocument: () => IssueVcsBranchSearch_NeedsDocument,
	IssueVcsBranchSearch_RelationsDocument: () => IssueVcsBranchSearch_RelationsDocument,
	IssueVcsBranchSearch_SubscribersDocument: () => IssueVcsBranchSearch_SubscribersDocument,
	IssueWebhookPayloadFragmentDoc: () => IssueWebhookPayloadFragmentDoc,
	IssueWithDescriptionChildWebhookPayloadFragmentDoc: () => IssueWithDescriptionChildWebhookPayloadFragmentDoc,
	Issue_AttachmentsDocument: () => Issue_AttachmentsDocument,
	Issue_BotActorDocument: () => Issue_BotActorDocument,
	Issue_ChildrenDocument: () => Issue_ChildrenDocument,
	Issue_CommentsDocument: () => Issue_CommentsDocument,
	Issue_DocumentsDocument: () => Issue_DocumentsDocument,
	Issue_FormerAttachmentsDocument: () => Issue_FormerAttachmentsDocument,
	Issue_FormerNeedsDocument: () => Issue_FormerNeedsDocument,
	Issue_HistoryDocument: () => Issue_HistoryDocument,
	Issue_InverseRelationsDocument: () => Issue_InverseRelationsDocument,
	Issue_LabelsDocument: () => Issue_LabelsDocument,
	Issue_NeedsDocument: () => Issue_NeedsDocument,
	Issue_RelationsDocument: () => Issue_RelationsDocument,
	Issue_SubscribersDocument: () => Issue_SubscribersDocument,
	IssuesDocument: () => IssuesDocument,
	JoinOrganizationFromOnboardingDocument: () => JoinOrganizationFromOnboardingDocument,
	LabelNotificationSubscriptionFragmentDoc: () => LabelNotificationSubscriptionFragmentDoc,
	LeaveOrganizationDocument: () => LeaveOrganizationDocument,
	LogoutAllSessionsDocument: () => LogoutAllSessionsDocument,
	LogoutDocument: () => LogoutDocument,
	LogoutOtherSessionsDocument: () => LogoutOtherSessionsDocument,
	LogoutResponseFragmentDoc: () => LogoutResponseFragmentDoc,
	LogoutSessionDocument: () => LogoutSessionDocument,
	NodeFragmentDoc: () => NodeFragmentDoc,
	NotificationArchiveAllDocument: () => NotificationArchiveAllDocument,
	NotificationArchivePayloadFragmentDoc: () => NotificationArchivePayloadFragmentDoc,
	NotificationBatchActionPayloadFragmentDoc: () => NotificationBatchActionPayloadFragmentDoc,
	NotificationCategory: () => NotificationCategory,
	NotificationCategoryPreferencesFragmentDoc: () => NotificationCategoryPreferencesFragmentDoc,
	NotificationChannel: () => NotificationChannel,
	NotificationChannelPreferencesFragmentDoc: () => NotificationChannelPreferencesFragmentDoc,
	NotificationConnectionFragmentDoc: () => NotificationConnectionFragmentDoc,
	NotificationDeliveryPreferencesChannelFragmentDoc: () => NotificationDeliveryPreferencesChannelFragmentDoc,
	NotificationDeliveryPreferencesDayFragmentDoc: () => NotificationDeliveryPreferencesDayFragmentDoc,
	NotificationDeliveryPreferencesFragmentDoc: () => NotificationDeliveryPreferencesFragmentDoc,
	NotificationDeliveryPreferencesScheduleFragmentDoc: () => NotificationDeliveryPreferencesScheduleFragmentDoc,
	NotificationDocument: () => NotificationDocument,
	NotificationFragmentDoc: () => NotificationFragmentDoc,
	NotificationMarkReadAllDocument: () => NotificationMarkReadAllDocument,
	NotificationMarkUnreadAllDocument: () => NotificationMarkUnreadAllDocument,
	NotificationPayloadFragmentDoc: () => NotificationPayloadFragmentDoc,
	NotificationSnoozeAllDocument: () => NotificationSnoozeAllDocument,
	NotificationSubscriptionConnectionFragmentDoc: () => NotificationSubscriptionConnectionFragmentDoc,
	NotificationSubscriptionDocument: () => NotificationSubscriptionDocument,
	NotificationSubscriptionFragmentDoc: () => NotificationSubscriptionFragmentDoc,
	NotificationSubscriptionPayloadFragmentDoc: () => NotificationSubscriptionPayloadFragmentDoc,
	NotificationSubscriptionsDocument: () => NotificationSubscriptionsDocument,
	NotificationUnsnoozeAllDocument: () => NotificationUnsnoozeAllDocument,
	NotificationsDocument: () => NotificationsDocument,
	OAuthAppWebhookPayloadFragmentDoc: () => OAuthAppWebhookPayloadFragmentDoc,
	OAuthAuthorizationWebhookPayloadFragmentDoc: () => OAuthAuthorizationWebhookPayloadFragmentDoc,
	OAuthClientApprovalStatus: () => OAuthClientApprovalStatus,
	OauthClientActorWebhookPayloadFragmentDoc: () => OauthClientActorWebhookPayloadFragmentDoc,
	OauthClientApprovalFragmentDoc: () => OauthClientApprovalFragmentDoc,
	OauthClientApprovalNotificationFragmentDoc: () => OauthClientApprovalNotificationFragmentDoc,
	OauthClientChildWebhookPayloadFragmentDoc: () => OauthClientChildWebhookPayloadFragmentDoc,
	OrganizationAcceptedOrExpiredInviteDetailsPayloadFragmentDoc: () => OrganizationAcceptedOrExpiredInviteDetailsPayloadFragmentDoc,
	OrganizationCancelDeletePayloadFragmentDoc: () => OrganizationCancelDeletePayloadFragmentDoc,
	OrganizationDeleteChallengeDocument: () => OrganizationDeleteChallengeDocument,
	OrganizationDeletePayloadFragmentDoc: () => OrganizationDeletePayloadFragmentDoc,
	OrganizationDocument: () => OrganizationDocument,
	OrganizationDomainAuthType: () => OrganizationDomainAuthType,
	OrganizationDomainFragmentDoc: () => OrganizationDomainFragmentDoc,
	OrganizationExistsDocument: () => OrganizationExistsDocument,
	OrganizationExistsPayloadFragmentDoc: () => OrganizationExistsPayloadFragmentDoc,
	OrganizationFragmentDoc: () => OrganizationFragmentDoc,
	OrganizationInviteConnectionFragmentDoc: () => OrganizationInviteConnectionFragmentDoc,
	OrganizationInviteDocument: () => OrganizationInviteDocument,
	OrganizationInviteFragmentDoc: () => OrganizationInviteFragmentDoc,
	OrganizationInviteFullDetailsPayloadFragmentDoc: () => OrganizationInviteFullDetailsPayloadFragmentDoc,
	OrganizationInvitePayloadFragmentDoc: () => OrganizationInvitePayloadFragmentDoc,
	OrganizationInviteStatus: () => OrganizationInviteStatus,
	OrganizationInvitesDocument: () => OrganizationInvitesDocument,
	OrganizationIpRestrictionFragmentDoc: () => OrganizationIpRestrictionFragmentDoc,
	OrganizationMetaFragmentDoc: () => OrganizationMetaFragmentDoc,
	OrganizationOriginWebhookPayloadFragmentDoc: () => OrganizationOriginWebhookPayloadFragmentDoc,
	OrganizationPayloadFragmentDoc: () => OrganizationPayloadFragmentDoc,
	OrganizationStartTrialDocument: () => OrganizationStartTrialDocument,
	OrganizationStartTrialForPlanDocument: () => OrganizationStartTrialForPlanDocument,
	OrganizationStartTrialPayloadFragmentDoc: () => OrganizationStartTrialPayloadFragmentDoc,
	Organization_IntegrationsDocument: () => Organization_IntegrationsDocument,
	Organization_LabelsDocument: () => Organization_LabelsDocument,
	Organization_ProjectLabelsDocument: () => Organization_ProjectLabelsDocument,
	Organization_SubscriptionDocument: () => Organization_SubscriptionDocument,
	Organization_TeamsDocument: () => Organization_TeamsDocument,
	Organization_TemplatesDocument: () => Organization_TemplatesDocument,
	Organization_UsersDocument: () => Organization_UsersDocument,
	OtherNotificationType: () => OtherNotificationType,
	OtherNotificationWebhookPayloadFragmentDoc: () => OtherNotificationWebhookPayloadFragmentDoc,
	PageInfoFragmentDoc: () => PageInfoFragmentDoc,
	PaginationNulls: () => PaginationNulls,
	PaginationOrderBy: () => PaginationOrderBy,
	PaginationSortOrder: () => PaginationSortOrder,
	PaidSubscriptionFragmentDoc: () => PaidSubscriptionFragmentDoc,
	PasskeyLoginStartResponseFragmentDoc: () => PasskeyLoginStartResponseFragmentDoc,
	PostNotificationFragmentDoc: () => PostNotificationFragmentDoc,
	PostType: () => PostType,
	ProductIntelligenceScope: () => ProductIntelligenceScope,
	ProjectAddLabelDocument: () => ProjectAddLabelDocument,
	ProjectArchivePayloadFragmentDoc: () => ProjectArchivePayloadFragmentDoc,
	ProjectAttachmentFragmentDoc: () => ProjectAttachmentFragmentDoc,
	ProjectChildWebhookPayloadFragmentDoc: () => ProjectChildWebhookPayloadFragmentDoc,
	ProjectConnectionFragmentDoc: () => ProjectConnectionFragmentDoc,
	ProjectDocument: () => ProjectDocument,
	ProjectExternalSyncDisableDocument: () => ProjectExternalSyncDisableDocument,
	ProjectFilterSuggestionDocument: () => ProjectFilterSuggestionDocument,
	ProjectFilterSuggestionPayloadFragmentDoc: () => ProjectFilterSuggestionPayloadFragmentDoc,
	ProjectFragmentDoc: () => ProjectFragmentDoc,
	ProjectHistoryConnectionFragmentDoc: () => ProjectHistoryConnectionFragmentDoc,
	ProjectHistoryFragmentDoc: () => ProjectHistoryFragmentDoc,
	ProjectLabelChildWebhookPayloadFragmentDoc: () => ProjectLabelChildWebhookPayloadFragmentDoc,
	ProjectLabelConnectionFragmentDoc: () => ProjectLabelConnectionFragmentDoc,
	ProjectLabelDocument: () => ProjectLabelDocument,
	ProjectLabelFragmentDoc: () => ProjectLabelFragmentDoc,
	ProjectLabelPayloadFragmentDoc: () => ProjectLabelPayloadFragmentDoc,
	ProjectLabelRestoreDocument: () => ProjectLabelRestoreDocument,
	ProjectLabelRetireDocument: () => ProjectLabelRetireDocument,
	ProjectLabelWebhookPayloadFragmentDoc: () => ProjectLabelWebhookPayloadFragmentDoc,
	ProjectLabel_ChildrenDocument: () => ProjectLabel_ChildrenDocument,
	ProjectLabel_ProjectsDocument: () => ProjectLabel_ProjectsDocument,
	ProjectLabelsDocument: () => ProjectLabelsDocument,
	ProjectMilestoneChildWebhookPayloadFragmentDoc: () => ProjectMilestoneChildWebhookPayloadFragmentDoc,
	ProjectMilestoneConnectionFragmentDoc: () => ProjectMilestoneConnectionFragmentDoc,
	ProjectMilestoneDocument: () => ProjectMilestoneDocument,
	ProjectMilestoneFragmentDoc: () => ProjectMilestoneFragmentDoc,
	ProjectMilestoneMoveIssueToTeamFragmentDoc: () => ProjectMilestoneMoveIssueToTeamFragmentDoc,
	ProjectMilestoneMovePayloadFragmentDoc: () => ProjectMilestoneMovePayloadFragmentDoc,
	ProjectMilestoneMoveProjectTeamsFragmentDoc: () => ProjectMilestoneMoveProjectTeamsFragmentDoc,
	ProjectMilestonePayloadFragmentDoc: () => ProjectMilestonePayloadFragmentDoc,
	ProjectMilestoneStatus: () => ProjectMilestoneStatus,
	ProjectMilestone_DocumentContentDocument: () => ProjectMilestone_DocumentContentDocument,
	ProjectMilestone_DocumentContent_AiPromptRulesDocument: () => ProjectMilestone_DocumentContent_AiPromptRulesDocument,
	ProjectMilestone_IssuesDocument: () => ProjectMilestone_IssuesDocument,
	ProjectMilestonesDocument: () => ProjectMilestonesDocument,
	ProjectNotificationFragmentDoc: () => ProjectNotificationFragmentDoc,
	ProjectNotificationSubscriptionFragmentDoc: () => ProjectNotificationSubscriptionFragmentDoc,
	ProjectPayloadFragmentDoc: () => ProjectPayloadFragmentDoc,
	ProjectRelationConnectionFragmentDoc: () => ProjectRelationConnectionFragmentDoc,
	ProjectRelationDocument: () => ProjectRelationDocument,
	ProjectRelationFragmentDoc: () => ProjectRelationFragmentDoc,
	ProjectRelationPayloadFragmentDoc: () => ProjectRelationPayloadFragmentDoc,
	ProjectRelationsDocument: () => ProjectRelationsDocument,
	ProjectRemoveLabelDocument: () => ProjectRemoveLabelDocument,
	ProjectSearchPayloadFragmentDoc: () => ProjectSearchPayloadFragmentDoc,
	ProjectSearchResultFragmentDoc: () => ProjectSearchResultFragmentDoc,
	ProjectStatusArchivePayloadFragmentDoc: () => ProjectStatusArchivePayloadFragmentDoc,
	ProjectStatusChildWebhookPayloadFragmentDoc: () => ProjectStatusChildWebhookPayloadFragmentDoc,
	ProjectStatusConnectionFragmentDoc: () => ProjectStatusConnectionFragmentDoc,
	ProjectStatusCountPayloadFragmentDoc: () => ProjectStatusCountPayloadFragmentDoc,
	ProjectStatusDocument: () => ProjectStatusDocument,
	ProjectStatusFragmentDoc: () => ProjectStatusFragmentDoc,
	ProjectStatusPayloadFragmentDoc: () => ProjectStatusPayloadFragmentDoc,
	ProjectStatusType: () => ProjectStatusType,
	ProjectStatusesDocument: () => ProjectStatusesDocument,
	ProjectTab: () => ProjectTab,
	ProjectUpdateArchivePayloadFragmentDoc: () => ProjectUpdateArchivePayloadFragmentDoc,
	ProjectUpdateChildWebhookPayloadFragmentDoc: () => ProjectUpdateChildWebhookPayloadFragmentDoc,
	ProjectUpdateConnectionFragmentDoc: () => ProjectUpdateConnectionFragmentDoc,
	ProjectUpdateDocument: () => ProjectUpdateDocument,
	ProjectUpdateFragmentDoc: () => ProjectUpdateFragmentDoc,
	ProjectUpdateHealthType: () => ProjectUpdateHealthType,
	ProjectUpdatePayloadFragmentDoc: () => ProjectUpdatePayloadFragmentDoc,
	ProjectUpdateReminderFrequency: () => ProjectUpdateReminderFrequency,
	ProjectUpdateReminderPayloadFragmentDoc: () => ProjectUpdateReminderPayloadFragmentDoc,
	ProjectUpdateWebhookPayloadFragmentDoc: () => ProjectUpdateWebhookPayloadFragmentDoc,
	ProjectUpdate_CommentsDocument: () => ProjectUpdate_CommentsDocument,
	ProjectUpdatesDocument: () => ProjectUpdatesDocument,
	ProjectWebhookPayloadFragmentDoc: () => ProjectWebhookPayloadFragmentDoc,
	Project_CommentsDocument: () => Project_CommentsDocument,
	Project_DocumentContentDocument: () => Project_DocumentContentDocument,
	Project_DocumentContent_AiPromptRulesDocument: () => Project_DocumentContent_AiPromptRulesDocument,
	Project_DocumentsDocument: () => Project_DocumentsDocument,
	Project_ExternalLinksDocument: () => Project_ExternalLinksDocument,
	Project_HistoryDocument: () => Project_HistoryDocument,
	Project_InitiativeToProjectsDocument: () => Project_InitiativeToProjectsDocument,
	Project_InitiativesDocument: () => Project_InitiativesDocument,
	Project_InverseRelationsDocument: () => Project_InverseRelationsDocument,
	Project_IssuesDocument: () => Project_IssuesDocument,
	Project_LabelsDocument: () => Project_LabelsDocument,
	Project_MembersDocument: () => Project_MembersDocument,
	Project_NeedsDocument: () => Project_NeedsDocument,
	Project_ProjectMilestonesDocument: () => Project_ProjectMilestonesDocument,
	Project_ProjectUpdatesDocument: () => Project_ProjectUpdatesDocument,
	Project_RelationsDocument: () => Project_RelationsDocument,
	Project_TeamsDocument: () => Project_TeamsDocument,
	ProjectsDocument: () => ProjectsDocument,
	PullRequestMergeMethod: () => PullRequestMergeMethod,
	PullRequestNotificationFragmentDoc: () => PullRequestNotificationFragmentDoc,
	PullRequestReviewTool: () => PullRequestReviewTool,
	PullRequestStatus: () => PullRequestStatus,
	PushSubscriptionFragmentDoc: () => PushSubscriptionFragmentDoc,
	PushSubscriptionPayloadFragmentDoc: () => PushSubscriptionPayloadFragmentDoc,
	PushSubscriptionTestDocument: () => PushSubscriptionTestDocument,
	PushSubscriptionTestPayloadFragmentDoc: () => PushSubscriptionTestPayloadFragmentDoc,
	PushSubscriptionType: () => PushSubscriptionType,
	RateLimitPayloadFragmentDoc: () => RateLimitPayloadFragmentDoc,
	RateLimitResultPayloadFragmentDoc: () => RateLimitResultPayloadFragmentDoc,
	RateLimitStatusDocument: () => RateLimitStatusDocument,
	ReactionFragmentDoc: () => ReactionFragmentDoc,
	ReactionPayloadFragmentDoc: () => ReactionPayloadFragmentDoc,
	ReactionWebhookPayloadFragmentDoc: () => ReactionWebhookPayloadFragmentDoc,
	RefreshGoogleSheetsDataDocument: () => RefreshGoogleSheetsDataDocument,
	ReleaseArchivePayloadFragmentDoc: () => ReleaseArchivePayloadFragmentDoc,
	ReleaseChannel: () => ReleaseChannel,
	ReleasePayloadFragmentDoc: () => ReleasePayloadFragmentDoc,
	ReleasePipelineArchivePayloadFragmentDoc: () => ReleasePipelineArchivePayloadFragmentDoc,
	ReleasePipelinePayloadFragmentDoc: () => ReleasePipelinePayloadFragmentDoc,
	ReleasePipelineType: () => ReleasePipelineType,
	ReleaseStageArchivePayloadFragmentDoc: () => ReleaseStageArchivePayloadFragmentDoc,
	ReleaseStagePayloadFragmentDoc: () => ReleaseStagePayloadFragmentDoc,
	ReleaseStageType: () => ReleaseStageType,
	RepositorySuggestionFragmentDoc: () => RepositorySuggestionFragmentDoc,
	RepositorySuggestionsPayloadFragmentDoc: () => RepositorySuggestionsPayloadFragmentDoc,
	ResendOrganizationInviteByEmailDocument: () => ResendOrganizationInviteByEmailDocument,
	ResendOrganizationInviteDocument: () => ResendOrganizationInviteDocument,
	RoadmapArchivePayloadFragmentDoc: () => RoadmapArchivePayloadFragmentDoc,
	RoadmapConnectionFragmentDoc: () => RoadmapConnectionFragmentDoc,
	RoadmapDocument: () => RoadmapDocument,
	RoadmapFragmentDoc: () => RoadmapFragmentDoc,
	RoadmapPayloadFragmentDoc: () => RoadmapPayloadFragmentDoc,
	RoadmapToProjectConnectionFragmentDoc: () => RoadmapToProjectConnectionFragmentDoc,
	RoadmapToProjectDocument: () => RoadmapToProjectDocument,
	RoadmapToProjectFragmentDoc: () => RoadmapToProjectFragmentDoc,
	RoadmapToProjectPayloadFragmentDoc: () => RoadmapToProjectPayloadFragmentDoc,
	RoadmapToProjectsDocument: () => RoadmapToProjectsDocument,
	Roadmap_ProjectsDocument: () => Roadmap_ProjectsDocument,
	RoadmapsDocument: () => RoadmapsDocument,
	SLADayCountType: () => SLADayCountType,
	SamlTokenUserAccountAuthDocument: () => SamlTokenUserAccountAuthDocument,
	SearchDocumentsDocument: () => SearchDocumentsDocument,
	SearchDocuments_ArchivePayloadDocument: () => SearchDocuments_ArchivePayloadDocument,
	SearchIssuesDocument: () => SearchIssuesDocument,
	SearchIssues_ArchivePayloadDocument: () => SearchIssues_ArchivePayloadDocument,
	SearchProjectsDocument: () => SearchProjectsDocument,
	SearchProjects_ArchivePayloadDocument: () => SearchProjects_ArchivePayloadDocument,
	SemanticSearchDocument: () => SemanticSearchDocument,
	SemanticSearchPayloadFragmentDoc: () => SemanticSearchPayloadFragmentDoc,
	SemanticSearchResultFragmentDoc: () => SemanticSearchResultFragmentDoc,
	SemanticSearchResultType: () => SemanticSearchResultType,
	SendStrategy: () => SendStrategy,
	SesDomainIdentityDnsRecordFragmentDoc: () => SesDomainIdentityDnsRecordFragmentDoc,
	SesDomainIdentityFragmentDoc: () => SesDomainIdentityFragmentDoc,
	SlaStatus: () => SlaStatus,
	SlackAsksTeamSettingsFragmentDoc: () => SlackAsksTeamSettingsFragmentDoc,
	SlackChannelConnectPayloadFragmentDoc: () => SlackChannelConnectPayloadFragmentDoc,
	SlackChannelNameMappingFragmentDoc: () => SlackChannelNameMappingFragmentDoc,
	SlackChannelType: () => SlackChannelType,
	SsoUrlFromEmailDocument: () => SsoUrlFromEmailDocument,
	SsoUrlFromEmailResponseFragmentDoc: () => SsoUrlFromEmailResponseFragmentDoc,
	SuccessPayloadFragmentDoc: () => SuccessPayloadFragmentDoc,
	SuspendUserDocument: () => SuspendUserDocument,
	SyncedExternalThreadFragmentDoc: () => SyncedExternalThreadFragmentDoc,
	TeamArchivePayloadFragmentDoc: () => TeamArchivePayloadFragmentDoc,
	TeamChildWebhookPayloadFragmentDoc: () => TeamChildWebhookPayloadFragmentDoc,
	TeamConnectionFragmentDoc: () => TeamConnectionFragmentDoc,
	TeamDocument: () => TeamDocument,
	TeamFragmentDoc: () => TeamFragmentDoc,
	TeamMembershipConnectionFragmentDoc: () => TeamMembershipConnectionFragmentDoc,
	TeamMembershipDocument: () => TeamMembershipDocument,
	TeamMembershipFragmentDoc: () => TeamMembershipFragmentDoc,
	TeamMembershipPayloadFragmentDoc: () => TeamMembershipPayloadFragmentDoc,
	TeamMembershipsDocument: () => TeamMembershipsDocument,
	TeamNotificationSubscriptionFragmentDoc: () => TeamNotificationSubscriptionFragmentDoc,
	TeamOriginWebhookPayloadFragmentDoc: () => TeamOriginWebhookPayloadFragmentDoc,
	TeamPayloadFragmentDoc: () => TeamPayloadFragmentDoc,
	TeamRoleType: () => TeamRoleType,
	TeamWithParentWebhookPayloadFragmentDoc: () => TeamWithParentWebhookPayloadFragmentDoc,
	Team_CyclesDocument: () => Team_CyclesDocument,
	Team_GitAutomationStatesDocument: () => Team_GitAutomationStatesDocument,
	Team_IssuesDocument: () => Team_IssuesDocument,
	Team_LabelsDocument: () => Team_LabelsDocument,
	Team_MembersDocument: () => Team_MembersDocument,
	Team_MembershipsDocument: () => Team_MembershipsDocument,
	Team_ProjectsDocument: () => Team_ProjectsDocument,
	Team_StatesDocument: () => Team_StatesDocument,
	Team_TemplatesDocument: () => Team_TemplatesDocument,
	Team_WebhooksDocument: () => Team_WebhooksDocument,
	TeamsDocument: () => TeamsDocument,
	TemplateConnectionFragmentDoc: () => TemplateConnectionFragmentDoc,
	TemplateDocument: () => TemplateDocument,
	TemplateFragmentDoc: () => TemplateFragmentDoc,
	TemplatePayloadFragmentDoc: () => TemplatePayloadFragmentDoc,
	TemplatesDocument: () => TemplatesDocument,
	TemplatesForIntegrationDocument: () => TemplatesForIntegrationDocument,
	TimeScheduleConnectionFragmentDoc: () => TimeScheduleConnectionFragmentDoc,
	TimeScheduleDocument: () => TimeScheduleDocument,
	TimeScheduleEntryFragmentDoc: () => TimeScheduleEntryFragmentDoc,
	TimeScheduleFragmentDoc: () => TimeScheduleFragmentDoc,
	TimeSchedulePayloadFragmentDoc: () => TimeSchedulePayloadFragmentDoc,
	TimeScheduleRefreshIntegrationScheduleDocument: () => TimeScheduleRefreshIntegrationScheduleDocument,
	TimeScheduleUpsertExternalDocument: () => TimeScheduleUpsertExternalDocument,
	TimeSchedulesDocument: () => TimeSchedulesDocument,
	TriageResponsibilitiesDocument: () => TriageResponsibilitiesDocument,
	TriageResponsibilityAction: () => TriageResponsibilityAction,
	TriageResponsibilityConnectionFragmentDoc: () => TriageResponsibilityConnectionFragmentDoc,
	TriageResponsibilityDocument: () => TriageResponsibilityDocument,
	TriageResponsibilityFragmentDoc: () => TriageResponsibilityFragmentDoc,
	TriageResponsibilityManualSelectionFragmentDoc: () => TriageResponsibilityManualSelectionFragmentDoc,
	TriageResponsibilityPayloadFragmentDoc: () => TriageResponsibilityPayloadFragmentDoc,
	TriageResponsibility_ManualSelectionDocument: () => TriageResponsibility_ManualSelectionDocument,
	TypedDocumentString: () => TypedDocumentString,
	UnarchiveCustomerNeedDocument: () => UnarchiveCustomerNeedDocument,
	UnarchiveDocumentDocument: () => UnarchiveDocumentDocument,
	UnarchiveInitiativeDocument: () => UnarchiveInitiativeDocument,
	UnarchiveInitiativeUpdateDocument: () => UnarchiveInitiativeUpdateDocument,
	UnarchiveIssueDocument: () => UnarchiveIssueDocument,
	UnarchiveNotificationDocument: () => UnarchiveNotificationDocument,
	UnarchiveProjectDocument: () => UnarchiveProjectDocument,
	UnarchiveProjectStatusDocument: () => UnarchiveProjectStatusDocument,
	UnarchiveProjectUpdateDocument: () => UnarchiveProjectUpdateDocument,
	UnarchiveRoadmapDocument: () => UnarchiveRoadmapDocument,
	UnarchiveTeamDocument: () => UnarchiveTeamDocument,
	UnsuspendUserDocument: () => UnsuspendUserDocument,
	UpdateAgentSessionDocument: () => UpdateAgentSessionDocument,
	UpdateAttachmentDocument: () => UpdateAttachmentDocument,
	UpdateCommentDocument: () => UpdateCommentDocument,
	UpdateCustomViewDocument: () => UpdateCustomViewDocument,
	UpdateCustomerDocument: () => UpdateCustomerDocument,
	UpdateCustomerNeedDocument: () => UpdateCustomerNeedDocument,
	UpdateCustomerStatusDocument: () => UpdateCustomerStatusDocument,
	UpdateCustomerTierDocument: () => UpdateCustomerTierDocument,
	UpdateCycleDocument: () => UpdateCycleDocument,
	UpdateDocumentDocument: () => UpdateDocumentDocument,
	UpdateEmailIntakeAddressDocument: () => UpdateEmailIntakeAddressDocument,
	UpdateEntityExternalLinkDocument: () => UpdateEntityExternalLinkDocument,
	UpdateFavoriteDocument: () => UpdateFavoriteDocument,
	UpdateGitAutomationStateDocument: () => UpdateGitAutomationStateDocument,
	UpdateGitAutomationTargetBranchDocument: () => UpdateGitAutomationTargetBranchDocument,
	UpdateInitiativeDocument: () => UpdateInitiativeDocument,
	UpdateInitiativeRelationDocument: () => UpdateInitiativeRelationDocument,
	UpdateInitiativeToProjectDocument: () => UpdateInitiativeToProjectDocument,
	UpdateInitiativeUpdateDocument: () => UpdateInitiativeUpdateDocument,
	UpdateIntegrationIntercomSettingsDocument: () => UpdateIntegrationIntercomSettingsDocument,
	UpdateIntegrationsSettingsDocument: () => UpdateIntegrationsSettingsDocument,
	UpdateIssueBatchDocument: () => UpdateIssueBatchDocument,
	UpdateIssueDocument: () => UpdateIssueDocument,
	UpdateIssueImportDocument: () => UpdateIssueImportDocument,
	UpdateIssueLabelDocument: () => UpdateIssueLabelDocument,
	UpdateIssueRelationDocument: () => UpdateIssueRelationDocument,
	UpdateNotificationCategoryChannelSubscriptionDocument: () => UpdateNotificationCategoryChannelSubscriptionDocument,
	UpdateNotificationDocument: () => UpdateNotificationDocument,
	UpdateNotificationSubscriptionDocument: () => UpdateNotificationSubscriptionDocument,
	UpdateOrganizationDocument: () => UpdateOrganizationDocument,
	UpdateOrganizationInviteDocument: () => UpdateOrganizationInviteDocument,
	UpdateProjectDocument: () => UpdateProjectDocument,
	UpdateProjectLabelDocument: () => UpdateProjectLabelDocument,
	UpdateProjectMilestoneDocument: () => UpdateProjectMilestoneDocument,
	UpdateProjectRelationDocument: () => UpdateProjectRelationDocument,
	UpdateProjectStatusDocument: () => UpdateProjectStatusDocument,
	UpdateProjectUpdateDocument: () => UpdateProjectUpdateDocument,
	UpdateRoadmapDocument: () => UpdateRoadmapDocument,
	UpdateRoadmapToProjectDocument: () => UpdateRoadmapToProjectDocument,
	UpdateTeamDocument: () => UpdateTeamDocument,
	UpdateTeamMembershipDocument: () => UpdateTeamMembershipDocument,
	UpdateTemplateDocument: () => UpdateTemplateDocument,
	UpdateTimeScheduleDocument: () => UpdateTimeScheduleDocument,
	UpdateTriageResponsibilityDocument: () => UpdateTriageResponsibilityDocument,
	UpdateUserDocument: () => UpdateUserDocument,
	UpdateUserFlagDocument: () => UpdateUserFlagDocument,
	UpdateUserSettingsDocument: () => UpdateUserSettingsDocument,
	UpdateViewPreferencesDocument: () => UpdateViewPreferencesDocument,
	UpdateWebhookDocument: () => UpdateWebhookDocument,
	UpdateWorkflowStateDocument: () => UpdateWorkflowStateDocument,
	UploadFileFragmentDoc: () => UploadFileFragmentDoc,
	UploadFileHeaderFragmentDoc: () => UploadFileHeaderFragmentDoc,
	UploadPayloadFragmentDoc: () => UploadPayloadFragmentDoc,
	UserActorWebhookPayloadFragmentDoc: () => UserActorWebhookPayloadFragmentDoc,
	UserAdminPayloadFragmentDoc: () => UserAdminPayloadFragmentDoc,
	UserChangeRoleDocument: () => UserChangeRoleDocument,
	UserChildWebhookPayloadFragmentDoc: () => UserChildWebhookPayloadFragmentDoc,
	UserConnectionFragmentDoc: () => UserConnectionFragmentDoc,
	UserContextViewType: () => UserContextViewType,
	UserDemoteAdminDocument: () => UserDemoteAdminDocument,
	UserDemoteMemberDocument: () => UserDemoteMemberDocument,
	UserDiscordConnectDocument: () => UserDiscordConnectDocument,
	UserDocument: () => UserDocument,
	UserExternalUserDisconnectDocument: () => UserExternalUserDisconnectDocument,
	UserFlagType: () => UserFlagType,
	UserFlagUpdateOperation: () => UserFlagUpdateOperation,
	UserFragmentDoc: () => UserFragmentDoc,
	UserNotificationSubscriptionFragmentDoc: () => UserNotificationSubscriptionFragmentDoc,
	UserPayloadFragmentDoc: () => UserPayloadFragmentDoc,
	UserPromoteAdminDocument: () => UserPromoteAdminDocument,
	UserPromoteMemberDocument: () => UserPromoteMemberDocument,
	UserRoleType: () => UserRoleType,
	UserSettingsCustomSidebarThemeFragmentDoc: () => UserSettingsCustomSidebarThemeFragmentDoc,
	UserSettingsCustomThemeFragmentDoc: () => UserSettingsCustomThemeFragmentDoc,
	UserSettingsDocument: () => UserSettingsDocument,
	UserSettingsFlagPayloadFragmentDoc: () => UserSettingsFlagPayloadFragmentDoc,
	UserSettingsFlagsResetDocument: () => UserSettingsFlagsResetDocument,
	UserSettingsFlagsResetPayloadFragmentDoc: () => UserSettingsFlagsResetPayloadFragmentDoc,
	UserSettingsFragmentDoc: () => UserSettingsFragmentDoc,
	UserSettingsPayloadFragmentDoc: () => UserSettingsPayloadFragmentDoc,
	UserSettingsThemeDeviceType: () => UserSettingsThemeDeviceType,
	UserSettingsThemeFragmentDoc: () => UserSettingsThemeFragmentDoc,
	UserSettingsThemeMode: () => UserSettingsThemeMode,
	UserSettingsThemePreset: () => UserSettingsThemePreset,
	UserSettings_NotificationCategoryPreferencesDocument: () => UserSettings_NotificationCategoryPreferencesDocument,
	UserSettings_NotificationCategoryPreferences_AppsAndIntegrationsDocument: () => UserSettings_NotificationCategoryPreferences_AppsAndIntegrationsDocument,
	UserSettings_NotificationCategoryPreferences_AssignmentsDocument: () => UserSettings_NotificationCategoryPreferences_AssignmentsDocument,
	UserSettings_NotificationCategoryPreferences_CommentsAndRepliesDocument: () => UserSettings_NotificationCategoryPreferences_CommentsAndRepliesDocument,
	UserSettings_NotificationCategoryPreferences_CustomersDocument: () => UserSettings_NotificationCategoryPreferences_CustomersDocument,
	UserSettings_NotificationCategoryPreferences_DocumentChangesDocument: () => UserSettings_NotificationCategoryPreferences_DocumentChangesDocument,
	UserSettings_NotificationCategoryPreferences_FeedDocument: () => UserSettings_NotificationCategoryPreferences_FeedDocument,
	UserSettings_NotificationCategoryPreferences_MentionsDocument: () => UserSettings_NotificationCategoryPreferences_MentionsDocument,
	UserSettings_NotificationCategoryPreferences_PostsAndUpdatesDocument: () => UserSettings_NotificationCategoryPreferences_PostsAndUpdatesDocument,
	UserSettings_NotificationCategoryPreferences_ReactionsDocument: () => UserSettings_NotificationCategoryPreferences_ReactionsDocument,
	UserSettings_NotificationCategoryPreferences_RemindersDocument: () => UserSettings_NotificationCategoryPreferences_RemindersDocument,
	UserSettings_NotificationCategoryPreferences_ReviewsDocument: () => UserSettings_NotificationCategoryPreferences_ReviewsDocument,
	UserSettings_NotificationCategoryPreferences_StatusChangesDocument: () => UserSettings_NotificationCategoryPreferences_StatusChangesDocument,
	UserSettings_NotificationCategoryPreferences_SubscriptionsDocument: () => UserSettings_NotificationCategoryPreferences_SubscriptionsDocument,
	UserSettings_NotificationCategoryPreferences_SystemDocument: () => UserSettings_NotificationCategoryPreferences_SystemDocument,
	UserSettings_NotificationCategoryPreferences_TriageDocument: () => UserSettings_NotificationCategoryPreferences_TriageDocument,
	UserSettings_NotificationChannelPreferencesDocument: () => UserSettings_NotificationChannelPreferencesDocument,
	UserSettings_NotificationDeliveryPreferencesDocument: () => UserSettings_NotificationDeliveryPreferencesDocument,
	UserSettings_NotificationDeliveryPreferences_MobileDocument: () => UserSettings_NotificationDeliveryPreferences_MobileDocument,
	UserSettings_NotificationDeliveryPreferences_Mobile_ScheduleDocument: () => UserSettings_NotificationDeliveryPreferences_Mobile_ScheduleDocument,
	UserSettings_NotificationDeliveryPreferences_Mobile_Schedule_FridayDocument: () => UserSettings_NotificationDeliveryPreferences_Mobile_Schedule_FridayDocument,
	UserSettings_NotificationDeliveryPreferences_Mobile_Schedule_MondayDocument: () => UserSettings_NotificationDeliveryPreferences_Mobile_Schedule_MondayDocument,
	UserSettings_NotificationDeliveryPreferences_Mobile_Schedule_SaturdayDocument: () => UserSettings_NotificationDeliveryPreferences_Mobile_Schedule_SaturdayDocument,
	UserSettings_NotificationDeliveryPreferences_Mobile_Schedule_SundayDocument: () => UserSettings_NotificationDeliveryPreferences_Mobile_Schedule_SundayDocument,
	UserSettings_NotificationDeliveryPreferences_Mobile_Schedule_ThursdayDocument: () => UserSettings_NotificationDeliveryPreferences_Mobile_Schedule_ThursdayDocument,
	UserSettings_NotificationDeliveryPreferences_Mobile_Schedule_TuesdayDocument: () => UserSettings_NotificationDeliveryPreferences_Mobile_Schedule_TuesdayDocument,
	UserSettings_NotificationDeliveryPreferences_Mobile_Schedule_WednesdayDocument: () => UserSettings_NotificationDeliveryPreferences_Mobile_Schedule_WednesdayDocument,
	UserSettings_ThemeDocument: () => UserSettings_ThemeDocument,
	UserSettings_Theme_CustomDocument: () => UserSettings_Theme_CustomDocument,
	UserSettings_Theme_Custom_SidebarDocument: () => UserSettings_Theme_Custom_SidebarDocument,
	UserUnlinkFromIdentityProviderDocument: () => UserUnlinkFromIdentityProviderDocument,
	UserWebhookPayloadFragmentDoc: () => UserWebhookPayloadFragmentDoc,
	User_AssignedIssuesDocument: () => User_AssignedIssuesDocument,
	User_CreatedIssuesDocument: () => User_CreatedIssuesDocument,
	User_DelegatedIssuesDocument: () => User_DelegatedIssuesDocument,
	User_DraftsDocument: () => User_DraftsDocument,
	User_TeamMembershipsDocument: () => User_TeamMembershipsDocument,
	User_TeamsDocument: () => User_TeamsDocument,
	UsersDocument: () => UsersDocument,
	VerifyGitHubEnterpriseServerInstallationDocument: () => VerifyGitHubEnterpriseServerInstallationDocument,
	ViewPreferencesFragmentDoc: () => ViewPreferencesFragmentDoc,
	ViewPreferencesPayloadFragmentDoc: () => ViewPreferencesPayloadFragmentDoc,
	ViewPreferencesType: () => ViewPreferencesType,
	ViewPreferencesValuesFragmentDoc: () => ViewPreferencesValuesFragmentDoc,
	ViewType: () => ViewType,
	ViewerDocument: () => ViewerDocument,
	Viewer_AssignedIssuesDocument: () => Viewer_AssignedIssuesDocument,
	Viewer_CreatedIssuesDocument: () => Viewer_CreatedIssuesDocument,
	Viewer_DelegatedIssuesDocument: () => Viewer_DelegatedIssuesDocument,
	Viewer_DraftsDocument: () => Viewer_DraftsDocument,
	Viewer_TeamMembershipsDocument: () => Viewer_TeamMembershipsDocument,
	Viewer_TeamsDocument: () => Viewer_TeamsDocument,
	WebhookConnectionFragmentDoc: () => WebhookConnectionFragmentDoc,
	WebhookDocument: () => WebhookDocument,
	WebhookFailureEventFragmentDoc: () => WebhookFailureEventFragmentDoc,
	WebhookFragmentDoc: () => WebhookFragmentDoc,
	WebhookPayloadFragmentDoc: () => WebhookPayloadFragmentDoc,
	WebhooksDocument: () => WebhooksDocument,
	WorkflowStateArchivePayloadFragmentDoc: () => WorkflowStateArchivePayloadFragmentDoc,
	WorkflowStateChildWebhookPayloadFragmentDoc: () => WorkflowStateChildWebhookPayloadFragmentDoc,
	WorkflowStateConnectionFragmentDoc: () => WorkflowStateConnectionFragmentDoc,
	WorkflowStateDocument: () => WorkflowStateDocument,
	WorkflowStateFragmentDoc: () => WorkflowStateFragmentDoc,
	WorkflowStatePayloadFragmentDoc: () => WorkflowStatePayloadFragmentDoc,
	WorkflowState_IssuesDocument: () => WorkflowState_IssuesDocument,
	WorkflowStatesDocument: () => WorkflowStatesDocument
});
/** A modifier that provides additional instructions on how the activity should be interpreted. */
let AgentActivitySignal = /* @__PURE__ */ function(AgentActivitySignal$1) {
	AgentActivitySignal$1["Auth"] = "auth";
	AgentActivitySignal$1["Continue"] = "continue";
	AgentActivitySignal$1["Select"] = "select";
	AgentActivitySignal$1["Stop"] = "stop";
	return AgentActivitySignal$1;
}({});
/** The type of an agent activity. */
let AgentActivityType = /* @__PURE__ */ function(AgentActivityType$1) {
	AgentActivityType$1["Action"] = "action";
	AgentActivityType$1["Elicitation"] = "elicitation";
	AgentActivityType$1["Error"] = "error";
	AgentActivityType$1["Prompt"] = "prompt";
	AgentActivityType$1["Response"] = "response";
	AgentActivityType$1["Thought"] = "thought";
	return AgentActivityType$1;
}({});
/** The status of an agent session. */
let AgentSessionStatus = /* @__PURE__ */ function(AgentSessionStatus$1) {
	AgentSessionStatus$1["Active"] = "active";
	AgentSessionStatus$1["AwaitingInput"] = "awaitingInput";
	AgentSessionStatus$1["Complete"] = "complete";
	AgentSessionStatus$1["Error"] = "error";
	AgentSessionStatus$1["Pending"] = "pending";
	AgentSessionStatus$1["Stale"] = "stale";
	return AgentSessionStatus$1;
}({});
/** [DEPRECATED] The type of an agent session. */
let AgentSessionType = /* @__PURE__ */ function(AgentSessionType$1) {
	AgentSessionType$1["CommentThread"] = "commentThread";
	return AgentSessionType$1;
}({});
let AuthenticationSessionType = /* @__PURE__ */ function(AuthenticationSessionType$1) {
	AuthenticationSessionType$1["Android"] = "android";
	AuthenticationSessionType$1["Desktop"] = "desktop";
	AuthenticationSessionType$1["Ios"] = "ios";
	AuthenticationSessionType$1["Web"] = "web";
	return AuthenticationSessionType$1;
}({});
let ContextViewType = /* @__PURE__ */ function(ContextViewType$1) {
	ContextViewType$1["ActiveCycle"] = "activeCycle";
	ContextViewType$1["ActiveIssues"] = "activeIssues";
	ContextViewType$1["Backlog"] = "backlog";
	ContextViewType$1["Triage"] = "triage";
	ContextViewType$1["UpcomingCycle"] = "upcomingCycle";
	return ContextViewType$1;
}({});
/** [DEPRECATED] A type of customer status. */
let CustomerStatusType = /* @__PURE__ */ function(CustomerStatusType$1) {
	CustomerStatusType$1["Active"] = "active";
	CustomerStatusType$1["Inactive"] = "inactive";
	return CustomerStatusType$1;
}({});
/** Mode that controls who can see and set Customers in Slack Asks. */
let CustomerVisibilityMode = /* @__PURE__ */ function(CustomerVisibilityMode$1) {
	CustomerVisibilityMode$1["LinearOnly"] = "LinearOnly";
	CustomerVisibilityMode$1["SlackMembers"] = "SlackMembers";
	CustomerVisibilityMode$1["SlackMembersAndGuests"] = "SlackMembersAndGuests";
	return CustomerVisibilityMode$1;
}({});
let CyclePeriod = /* @__PURE__ */ function(CyclePeriod$1) {
	CyclePeriod$1["After"] = "after";
	CyclePeriod$1["Before"] = "before";
	CyclePeriod$1["During"] = "during";
	return CyclePeriod$1;
}({});
/** By which resolution is a date defined. */
let DateResolutionType = /* @__PURE__ */ function(DateResolutionType$1) {
	DateResolutionType$1["HalfYear"] = "halfYear";
	DateResolutionType$1["Month"] = "month";
	DateResolutionType$1["Quarter"] = "quarter";
	DateResolutionType$1["Year"] = "year";
	return DateResolutionType$1;
}({});
/** The day of the week. */
let Day = /* @__PURE__ */ function(Day$1) {
	Day$1["Friday"] = "Friday";
	Day$1["Monday"] = "Monday";
	Day$1["Saturday"] = "Saturday";
	Day$1["Sunday"] = "Sunday";
	Day$1["Thursday"] = "Thursday";
	Day$1["Tuesday"] = "Tuesday";
	Day$1["Wednesday"] = "Wednesday";
	return Day$1;
}({});
/** The type of the email address. */
let EmailIntakeAddressType = /* @__PURE__ */ function(EmailIntakeAddressType$1) {
	EmailIntakeAddressType$1["Asks"] = "asks";
	EmailIntakeAddressType$1["Team"] = "team";
	EmailIntakeAddressType$1["Template"] = "template";
	return EmailIntakeAddressType$1;
}({});
/** The service that syncs an external entity to Linear. */
let ExternalSyncService = /* @__PURE__ */ function(ExternalSyncService$1) {
	ExternalSyncService$1["Github"] = "github";
	ExternalSyncService$1["Jira"] = "jira";
	ExternalSyncService$1["Slack"] = "slack";
	return ExternalSyncService$1;
}({});
let FacetPageSource = /* @__PURE__ */ function(FacetPageSource$1) {
	FacetPageSource$1["Feed"] = "feed";
	FacetPageSource$1["Projects"] = "projects";
	FacetPageSource$1["TeamIssues"] = "teamIssues";
	return FacetPageSource$1;
}({});
/** Cadence to generate feed summary */
let FeedSummarySchedule = /* @__PURE__ */ function(FeedSummarySchedule$1) {
	FeedSummarySchedule$1["Daily"] = "daily";
	FeedSummarySchedule$1["Never"] = "never";
	FeedSummarySchedule$1["Weekly"] = "weekly";
	return FeedSummarySchedule$1;
}({});
/** By which resolution is frequency defined. */
let FrequencyResolutionType = /* @__PURE__ */ function(FrequencyResolutionType$1) {
	FrequencyResolutionType$1["Daily"] = "daily";
	FrequencyResolutionType$1["Weekly"] = "weekly";
	return FrequencyResolutionType$1;
}({});
/** The various states of a pull/merge request. */
let GitAutomationStates = /* @__PURE__ */ function(GitAutomationStates$1) {
	GitAutomationStates$1["Draft"] = "draft";
	GitAutomationStates$1["Merge"] = "merge";
	GitAutomationStates$1["Mergeable"] = "mergeable";
	GitAutomationStates$1["Review"] = "review";
	GitAutomationStates$1["Start"] = "start";
	return GitAutomationStates$1;
}({});
/** [Internal] The kind of link between an issue and a pull request. */
let GitLinkKind = /* @__PURE__ */ function(GitLinkKind$1) {
	GitLinkKind$1["Closes"] = "closes";
	GitLinkKind$1["Contributes"] = "contributes";
	GitLinkKind$1["Links"] = "links";
	return GitLinkKind$1;
}({});
let GithubOrgType = /* @__PURE__ */ function(GithubOrgType$1) {
	GithubOrgType$1["Organization"] = "organization";
	GithubOrgType$1["User"] = "user";
	return GithubOrgType$1;
}({});
/** The type of identity provider. */
let IdentityProviderType = /* @__PURE__ */ function(IdentityProviderType$1) {
	IdentityProviderType$1["General"] = "general";
	IdentityProviderType$1["WebForms"] = "webForms";
	return IdentityProviderType$1;
}({});
let InitiativeStatus = /* @__PURE__ */ function(InitiativeStatus$1) {
	InitiativeStatus$1["Active"] = "Active";
	InitiativeStatus$1["Completed"] = "Completed";
	InitiativeStatus$1["Planned"] = "Planned";
	return InitiativeStatus$1;
}({});
/** Different tabs available inside an initiative. */
let InitiativeTab = /* @__PURE__ */ function(InitiativeTab$1) {
	InitiativeTab$1["Overview"] = "overview";
	InitiativeTab$1["Projects"] = "projects";
	InitiativeTab$1["Updates"] = "updates";
	return InitiativeTab$1;
}({});
/** The health type when the update is created. */
let InitiativeUpdateHealthType = /* @__PURE__ */ function(InitiativeUpdateHealthType$1) {
	InitiativeUpdateHealthType$1["AtRisk"] = "atRisk";
	InitiativeUpdateHealthType$1["OffTrack"] = "offTrack";
	InitiativeUpdateHealthType$1["OnTrack"] = "onTrack";
	return InitiativeUpdateHealthType$1;
}({});
/** Linear supported integration services. */
let IntegrationService = /* @__PURE__ */ function(IntegrationService$1) {
	IntegrationService$1["Airbyte"] = "airbyte";
	IntegrationService$1["Discord"] = "discord";
	IntegrationService$1["Email"] = "email";
	IntegrationService$1["Figma"] = "figma";
	IntegrationService$1["FigmaPlugin"] = "figmaPlugin";
	IntegrationService$1["Front"] = "front";
	IntegrationService$1["Github"] = "github";
	IntegrationService$1["GithubCodeAccessPersonal"] = "githubCodeAccessPersonal";
	IntegrationService$1["GithubCommit"] = "githubCommit";
	IntegrationService$1["GithubEnterpriseServer"] = "githubEnterpriseServer";
	IntegrationService$1["GithubImport"] = "githubImport";
	IntegrationService$1["GithubPersonal"] = "githubPersonal";
	IntegrationService$1["Gitlab"] = "gitlab";
	IntegrationService$1["Gong"] = "gong";
	IntegrationService$1["GoogleCalendarPersonal"] = "googleCalendarPersonal";
	IntegrationService$1["GoogleSheets"] = "googleSheets";
	IntegrationService$1["Intercom"] = "intercom";
	IntegrationService$1["Jira"] = "jira";
	IntegrationService$1["JiraPersonal"] = "jiraPersonal";
	IntegrationService$1["LaunchDarkly"] = "launchDarkly";
	IntegrationService$1["LaunchDarklyPersonal"] = "launchDarklyPersonal";
	IntegrationService$1["Loom"] = "loom";
	IntegrationService$1["McpServer"] = "mcpServer";
	IntegrationService$1["McpServerPersonal"] = "mcpServerPersonal";
	IntegrationService$1["Notion"] = "notion";
	IntegrationService$1["Opsgenie"] = "opsgenie";
	IntegrationService$1["PagerDuty"] = "pagerDuty";
	IntegrationService$1["Salesforce"] = "salesforce";
	IntegrationService$1["Sentry"] = "sentry";
	IntegrationService$1["Slack"] = "slack";
	IntegrationService$1["SlackAsks"] = "slackAsks";
	IntegrationService$1["SlackCustomViewNotifications"] = "slackCustomViewNotifications";
	IntegrationService$1["SlackInitiativePost"] = "slackInitiativePost";
	IntegrationService$1["SlackOrgInitiativeUpdatesPost"] = "slackOrgInitiativeUpdatesPost";
	IntegrationService$1["SlackOrgProjectUpdatesPost"] = "slackOrgProjectUpdatesPost";
	IntegrationService$1["SlackPersonal"] = "slackPersonal";
	IntegrationService$1["SlackPost"] = "slackPost";
	IntegrationService$1["SlackProjectPost"] = "slackProjectPost";
	IntegrationService$1["SlackProjectUpdatesPost"] = "slackProjectUpdatesPost";
	IntegrationService$1["Zendesk"] = "zendesk";
	return IntegrationService$1;
}({});
/** The type of the issue relation. */
let IssueRelationType = /* @__PURE__ */ function(IssueRelationType$1) {
	IssueRelationType$1["Blocks"] = "blocks";
	IssueRelationType$1["Duplicate"] = "duplicate";
	IssueRelationType$1["Related"] = "related";
	IssueRelationType$1["Similar"] = "similar";
	return IssueRelationType$1;
}({});
let IssueSuggestionState = /* @__PURE__ */ function(IssueSuggestionState$1) {
	IssueSuggestionState$1["Accepted"] = "accepted";
	IssueSuggestionState$1["Active"] = "active";
	IssueSuggestionState$1["Dismissed"] = "dismissed";
	IssueSuggestionState$1["Stale"] = "stale";
	return IssueSuggestionState$1;
}({});
let IssueSuggestionType = /* @__PURE__ */ function(IssueSuggestionType$1) {
	IssueSuggestionType$1["Assignee"] = "assignee";
	IssueSuggestionType$1["Label"] = "label";
	IssueSuggestionType$1["Project"] = "project";
	IssueSuggestionType$1["RelatedIssue"] = "relatedIssue";
	IssueSuggestionType$1["SimilarIssue"] = "similarIssue";
	IssueSuggestionType$1["Team"] = "team";
	return IssueSuggestionType$1;
}({});
/** The categories of notifications a user can subscribe to. */
let NotificationCategory = /* @__PURE__ */ function(NotificationCategory$1) {
	NotificationCategory$1["AppsAndIntegrations"] = "appsAndIntegrations";
	NotificationCategory$1["Assignments"] = "assignments";
	NotificationCategory$1["CommentsAndReplies"] = "commentsAndReplies";
	NotificationCategory$1["Customers"] = "customers";
	NotificationCategory$1["DocumentChanges"] = "documentChanges";
	NotificationCategory$1["Feed"] = "feed";
	NotificationCategory$1["Mentions"] = "mentions";
	NotificationCategory$1["PostsAndUpdates"] = "postsAndUpdates";
	NotificationCategory$1["Reactions"] = "reactions";
	NotificationCategory$1["Reminders"] = "reminders";
	NotificationCategory$1["Reviews"] = "reviews";
	NotificationCategory$1["StatusChanges"] = "statusChanges";
	NotificationCategory$1["Subscriptions"] = "subscriptions";
	NotificationCategory$1["System"] = "system";
	NotificationCategory$1["Triage"] = "triage";
	return NotificationCategory$1;
}({});
/** The delivery channels a user can receive notifications in. */
let NotificationChannel = /* @__PURE__ */ function(NotificationChannel$1) {
	NotificationChannel$1["Desktop"] = "desktop";
	NotificationChannel$1["Email"] = "email";
	NotificationChannel$1["Mobile"] = "mobile";
	NotificationChannel$1["Slack"] = "slack";
	return NotificationChannel$1;
}({});
/** The different requests statuses possible for an OAuth client approval request. */
let OAuthClientApprovalStatus = /* @__PURE__ */ function(OAuthClientApprovalStatus$1) {
	OAuthClientApprovalStatus$1["Approved"] = "approved";
	OAuthClientApprovalStatus$1["Denied"] = "denied";
	OAuthClientApprovalStatus$1["Requested"] = "requested";
	return OAuthClientApprovalStatus$1;
}({});
/** What type of auth is the domain used for. */
let OrganizationDomainAuthType = /* @__PURE__ */ function(OrganizationDomainAuthType$1) {
	OrganizationDomainAuthType$1["General"] = "general";
	OrganizationDomainAuthType$1["Saml"] = "saml";
	return OrganizationDomainAuthType$1;
}({});
/** The different statuses possible for an organization invite. */
let OrganizationInviteStatus = /* @__PURE__ */ function(OrganizationInviteStatus$1) {
	OrganizationInviteStatus$1["Accepted"] = "accepted";
	OrganizationInviteStatus$1["Expired"] = "expired";
	OrganizationInviteStatus$1["Pending"] = "pending";
	return OrganizationInviteStatus$1;
}({});
/** A generic type of notification. */
let OtherNotificationType = /* @__PURE__ */ function(OtherNotificationType$1) {
	OtherNotificationType$1["CustomerAddedAsOwner"] = "customerAddedAsOwner";
	OtherNotificationType$1["CustomerNeedCreated"] = "customerNeedCreated";
	OtherNotificationType$1["CustomerNeedMarkedAsImportant"] = "customerNeedMarkedAsImportant";
	OtherNotificationType$1["CustomerNeedResolved"] = "customerNeedResolved";
	OtherNotificationType$1["DocumentCommentMention"] = "documentCommentMention";
	OtherNotificationType$1["DocumentCommentReaction"] = "documentCommentReaction";
	OtherNotificationType$1["DocumentContentChange"] = "documentContentChange";
	OtherNotificationType$1["DocumentDeleted"] = "documentDeleted";
	OtherNotificationType$1["DocumentMention"] = "documentMention";
	OtherNotificationType$1["DocumentMoved"] = "documentMoved";
	OtherNotificationType$1["DocumentNewComment"] = "documentNewComment";
	OtherNotificationType$1["DocumentReminder"] = "documentReminder";
	OtherNotificationType$1["DocumentRestored"] = "documentRestored";
	OtherNotificationType$1["DocumentSubscribed"] = "documentSubscribed";
	OtherNotificationType$1["DocumentThreadResolved"] = "documentThreadResolved";
	OtherNotificationType$1["DocumentUnsubscribed"] = "documentUnsubscribed";
	OtherNotificationType$1["FeedSummaryGenerated"] = "feedSummaryGenerated";
	OtherNotificationType$1["InitiativeAddedAsOwner"] = "initiativeAddedAsOwner";
	OtherNotificationType$1["InitiativeCommentMention"] = "initiativeCommentMention";
	OtherNotificationType$1["InitiativeCommentReaction"] = "initiativeCommentReaction";
	OtherNotificationType$1["InitiativeDescriptionContentChange"] = "initiativeDescriptionContentChange";
	OtherNotificationType$1["InitiativeMention"] = "initiativeMention";
	OtherNotificationType$1["InitiativeNewComment"] = "initiativeNewComment";
	OtherNotificationType$1["InitiativeReminder"] = "initiativeReminder";
	OtherNotificationType$1["InitiativeThreadResolved"] = "initiativeThreadResolved";
	OtherNotificationType$1["InitiativeUpdateCommentMention"] = "initiativeUpdateCommentMention";
	OtherNotificationType$1["InitiativeUpdateCommentReaction"] = "initiativeUpdateCommentReaction";
	OtherNotificationType$1["InitiativeUpdateCreated"] = "initiativeUpdateCreated";
	OtherNotificationType$1["InitiativeUpdateMention"] = "initiativeUpdateMention";
	OtherNotificationType$1["InitiativeUpdateNewComment"] = "initiativeUpdateNewComment";
	OtherNotificationType$1["InitiativeUpdatePrompt"] = "initiativeUpdatePrompt";
	OtherNotificationType$1["InitiativeUpdateReaction"] = "initiativeUpdateReaction";
	OtherNotificationType$1["IssueAddedToTriage"] = "issueAddedToTriage";
	OtherNotificationType$1["IssueAddedToView"] = "issueAddedToView";
	OtherNotificationType$1["IssueBlocking"] = "issueBlocking";
	OtherNotificationType$1["IssueCreated"] = "issueCreated";
	OtherNotificationType$1["IssueDue"] = "issueDue";
	OtherNotificationType$1["IssuePriorityUrgent"] = "issuePriorityUrgent";
	OtherNotificationType$1["IssueReminder"] = "issueReminder";
	OtherNotificationType$1["IssueReopened"] = "issueReopened";
	OtherNotificationType$1["IssueSlaBreached"] = "issueSlaBreached";
	OtherNotificationType$1["IssueSlaHighRisk"] = "issueSlaHighRisk";
	OtherNotificationType$1["IssueStatusChangedAll"] = "issueStatusChangedAll";
	OtherNotificationType$1["IssueSubscribed"] = "issueSubscribed";
	OtherNotificationType$1["IssueThreadResolved"] = "issueThreadResolved";
	OtherNotificationType$1["IssueUnblocked"] = "issueUnblocked";
	OtherNotificationType$1["IssueUnsubscribed"] = "issueUnsubscribed";
	OtherNotificationType$1["OauthClientApprovalCreated"] = "oauthClientApprovalCreated";
	OtherNotificationType$1["ProjectAddedAsLead"] = "projectAddedAsLead";
	OtherNotificationType$1["ProjectAddedAsMember"] = "projectAddedAsMember";
	OtherNotificationType$1["ProjectCommentMention"] = "projectCommentMention";
	OtherNotificationType$1["ProjectCommentReaction"] = "projectCommentReaction";
	OtherNotificationType$1["ProjectDescriptionContentChange"] = "projectDescriptionContentChange";
	OtherNotificationType$1["ProjectMention"] = "projectMention";
	OtherNotificationType$1["ProjectMilestoneCommentMention"] = "projectMilestoneCommentMention";
	OtherNotificationType$1["ProjectMilestoneCommentReaction"] = "projectMilestoneCommentReaction";
	OtherNotificationType$1["ProjectMilestoneDescriptionContentChange"] = "projectMilestoneDescriptionContentChange";
	OtherNotificationType$1["ProjectMilestoneMention"] = "projectMilestoneMention";
	OtherNotificationType$1["ProjectMilestoneNewComment"] = "projectMilestoneNewComment";
	OtherNotificationType$1["ProjectMilestoneThreadResolved"] = "projectMilestoneThreadResolved";
	OtherNotificationType$1["ProjectNewComment"] = "projectNewComment";
	OtherNotificationType$1["ProjectReminder"] = "projectReminder";
	OtherNotificationType$1["ProjectThreadResolved"] = "projectThreadResolved";
	OtherNotificationType$1["ProjectUpdateCommentMention"] = "projectUpdateCommentMention";
	OtherNotificationType$1["ProjectUpdateCommentReaction"] = "projectUpdateCommentReaction";
	OtherNotificationType$1["ProjectUpdateCreated"] = "projectUpdateCreated";
	OtherNotificationType$1["ProjectUpdateMention"] = "projectUpdateMention";
	OtherNotificationType$1["ProjectUpdateNewComment"] = "projectUpdateNewComment";
	OtherNotificationType$1["ProjectUpdatePrompt"] = "projectUpdatePrompt";
	OtherNotificationType$1["ProjectUpdateReaction"] = "projectUpdateReaction";
	OtherNotificationType$1["PullRequestApproved"] = "pullRequestApproved";
	OtherNotificationType$1["PullRequestChangesRequested"] = "pullRequestChangesRequested";
	OtherNotificationType$1["PullRequestChecksFailed"] = "pullRequestChecksFailed";
	OtherNotificationType$1["PullRequestCommentMention"] = "pullRequestCommentMention";
	OtherNotificationType$1["PullRequestCommented"] = "pullRequestCommented";
	OtherNotificationType$1["PullRequestMention"] = "pullRequestMention";
	OtherNotificationType$1["PullRequestRemovedFromMergeQueue"] = "pullRequestRemovedFromMergeQueue";
	OtherNotificationType$1["PullRequestReviewRequested"] = "pullRequestReviewRequested";
	OtherNotificationType$1["PullRequestReviewRerequested"] = "pullRequestReviewRerequested";
	OtherNotificationType$1["System"] = "system";
	OtherNotificationType$1["TeamUpdateCommentMention"] = "teamUpdateCommentMention";
	OtherNotificationType$1["TeamUpdateCommentReaction"] = "teamUpdateCommentReaction";
	OtherNotificationType$1["TeamUpdateCreated"] = "teamUpdateCreated";
	OtherNotificationType$1["TeamUpdateMention"] = "teamUpdateMention";
	OtherNotificationType$1["TeamUpdateNewComment"] = "teamUpdateNewComment";
	OtherNotificationType$1["TeamUpdateReaction"] = "teamUpdateReaction";
	OtherNotificationType$1["TriageResponsibilityIssueAddedToTriage"] = "triageResponsibilityIssueAddedToTriage";
	return OtherNotificationType$1;
}({});
/** How to treat NULL values, whether they should appear first or last */
let PaginationNulls = /* @__PURE__ */ function(PaginationNulls$1) {
	PaginationNulls$1["First"] = "first";
	PaginationNulls$1["Last"] = "last";
	return PaginationNulls$1;
}({});
/** By which field should the pagination order by */
let PaginationOrderBy = /* @__PURE__ */ function(PaginationOrderBy$1) {
	PaginationOrderBy$1["CreatedAt"] = "createdAt";
	PaginationOrderBy$1["UpdatedAt"] = "updatedAt";
	return PaginationOrderBy$1;
}({});
/** Whether to sort in ascending or descending order */
let PaginationSortOrder = /* @__PURE__ */ function(PaginationSortOrder$1) {
	PaginationSortOrder$1["Ascending"] = "Ascending";
	PaginationSortOrder$1["Descending"] = "Descending";
	return PaginationSortOrder$1;
}({});
/** Type of Post */
let PostType = /* @__PURE__ */ function(PostType$1) {
	PostType$1["Summary"] = "summary";
	PostType$1["Update"] = "update";
	return PostType$1;
}({});
/** [Internal] The scope of product intelligence suggestion data for a team. */
let ProductIntelligenceScope = /* @__PURE__ */ function(ProductIntelligenceScope$1) {
	ProductIntelligenceScope$1["None"] = "none";
	ProductIntelligenceScope$1["Team"] = "team";
	ProductIntelligenceScope$1["TeamHierarchy"] = "teamHierarchy";
	ProductIntelligenceScope$1["Workspace"] = "workspace";
	return ProductIntelligenceScope$1;
}({});
/** The status of a project milestone. */
let ProjectMilestoneStatus = /* @__PURE__ */ function(ProjectMilestoneStatus$1) {
	ProjectMilestoneStatus$1["Done"] = "done";
	ProjectMilestoneStatus$1["Next"] = "next";
	ProjectMilestoneStatus$1["Overdue"] = "overdue";
	ProjectMilestoneStatus$1["Unstarted"] = "unstarted";
	return ProjectMilestoneStatus$1;
}({});
/** A type of project status. */
let ProjectStatusType = /* @__PURE__ */ function(ProjectStatusType$1) {
	ProjectStatusType$1["Backlog"] = "backlog";
	ProjectStatusType$1["Canceled"] = "canceled";
	ProjectStatusType$1["Completed"] = "completed";
	ProjectStatusType$1["Paused"] = "paused";
	ProjectStatusType$1["Planned"] = "planned";
	ProjectStatusType$1["Started"] = "started";
	return ProjectStatusType$1;
}({});
/** Different tabs available inside a project. */
let ProjectTab = /* @__PURE__ */ function(ProjectTab$1) {
	ProjectTab$1["Customers"] = "customers";
	ProjectTab$1["Documents"] = "documents";
	ProjectTab$1["Issues"] = "issues";
	ProjectTab$1["Updates"] = "updates";
	return ProjectTab$1;
}({});
/** The health type when the project update is created. */
let ProjectUpdateHealthType = /* @__PURE__ */ function(ProjectUpdateHealthType$1) {
	ProjectUpdateHealthType$1["AtRisk"] = "atRisk";
	ProjectUpdateHealthType$1["OffTrack"] = "offTrack";
	ProjectUpdateHealthType$1["OnTrack"] = "onTrack";
	return ProjectUpdateHealthType$1;
}({});
/** The frequency at which to send project update reminders. */
let ProjectUpdateReminderFrequency = /* @__PURE__ */ function(ProjectUpdateReminderFrequency$1) {
	ProjectUpdateReminderFrequency$1["Month"] = "month";
	ProjectUpdateReminderFrequency$1["Never"] = "never";
	ProjectUpdateReminderFrequency$1["TwoWeeks"] = "twoWeeks";
	ProjectUpdateReminderFrequency$1["Week"] = "week";
	return ProjectUpdateReminderFrequency$1;
}({});
/** The method used to merge a pull request. */
let PullRequestMergeMethod = /* @__PURE__ */ function(PullRequestMergeMethod$1) {
	PullRequestMergeMethod$1["Merge"] = "MERGE";
	PullRequestMergeMethod$1["Rebase"] = "REBASE";
	PullRequestMergeMethod$1["Squash"] = "SQUASH";
	return PullRequestMergeMethod$1;
}({});
let PullRequestReviewTool = /* @__PURE__ */ function(PullRequestReviewTool$1) {
	PullRequestReviewTool$1["Graphite"] = "graphite";
	PullRequestReviewTool$1["Source"] = "source";
	return PullRequestReviewTool$1;
}({});
/** The status of a pull request. */
let PullRequestStatus = /* @__PURE__ */ function(PullRequestStatus$1) {
	PullRequestStatus$1["Approved"] = "approved";
	PullRequestStatus$1["Closed"] = "closed";
	PullRequestStatus$1["Draft"] = "draft";
	PullRequestStatus$1["InReview"] = "inReview";
	PullRequestStatus$1["Merged"] = "merged";
	PullRequestStatus$1["Open"] = "open";
	return PullRequestStatus$1;
}({});
/** The different push subscription types. */
let PushSubscriptionType = /* @__PURE__ */ function(PushSubscriptionType$1) {
	PushSubscriptionType$1["Apple"] = "apple";
	PushSubscriptionType$1["AppleDevelopment"] = "appleDevelopment";
	PushSubscriptionType$1["Firebase"] = "firebase";
	PushSubscriptionType$1["Web"] = "web";
	return PushSubscriptionType$1;
}({});
/** Features release channel. */
let ReleaseChannel = /* @__PURE__ */ function(ReleaseChannel$1) {
	ReleaseChannel$1["Beta"] = "beta";
	ReleaseChannel$1["Development"] = "development";
	ReleaseChannel$1["Internal"] = "internal";
	ReleaseChannel$1["PreRelease"] = "preRelease";
	ReleaseChannel$1["PrivateBeta"] = "privateBeta";
	ReleaseChannel$1["Public"] = "public";
	return ReleaseChannel$1;
}({});
/** A type of release pipeline. */
let ReleasePipelineType = /* @__PURE__ */ function(ReleasePipelineType$1) {
	ReleasePipelineType$1["Continuous"] = "continuous";
	ReleasePipelineType$1["Scheduled"] = "scheduled";
	return ReleasePipelineType$1;
}({});
/** A type of release stage. */
let ReleaseStageType = /* @__PURE__ */ function(ReleaseStageType$1) {
	ReleaseStageType$1["Canceled"] = "canceled";
	ReleaseStageType$1["Completed"] = "completed";
	ReleaseStageType$1["Planned"] = "planned";
	ReleaseStageType$1["Started"] = "started";
	return ReleaseStageType$1;
}({});
let SLADayCountType = /* @__PURE__ */ function(SLADayCountType$1) {
	SLADayCountType$1["All"] = "all";
	SLADayCountType$1["OnlyBusinessDays"] = "onlyBusinessDays";
	return SLADayCountType$1;
}({});
/** The type of the semantic search result. */
let SemanticSearchResultType = /* @__PURE__ */ function(SemanticSearchResultType$1) {
	SemanticSearchResultType$1["Document"] = "document";
	SemanticSearchResultType$1["Initiative"] = "initiative";
	SemanticSearchResultType$1["Issue"] = "issue";
	SemanticSearchResultType$1["Project"] = "project";
	return SemanticSearchResultType$1;
}({});
let SendStrategy = /* @__PURE__ */ function(SendStrategy$1) {
	SendStrategy$1["Desktop"] = "desktop";
	SendStrategy$1["DesktopAndPush"] = "desktopAndPush";
	SendStrategy$1["DesktopThenPush"] = "desktopThenPush";
	SendStrategy$1["Push"] = "push";
	return SendStrategy$1;
}({});
let SlaStatus = /* @__PURE__ */ function(SlaStatus$1) {
	SlaStatus$1["Breached"] = "Breached";
	SlaStatus$1["Completed"] = "Completed";
	SlaStatus$1["Failed"] = "Failed";
	SlaStatus$1["HighRisk"] = "HighRisk";
	SlaStatus$1["LowRisk"] = "LowRisk";
	SlaStatus$1["MediumRisk"] = "MediumRisk";
	return SlaStatus$1;
}({});
let SlackChannelType = /* @__PURE__ */ function(SlackChannelType$1) {
	SlackChannelType$1["DirectMessage"] = "DirectMessage";
	SlackChannelType$1["MultiPersonDirectMessage"] = "MultiPersonDirectMessage";
	SlackChannelType$1["Private"] = "Private";
	SlackChannelType$1["PrivateGroup"] = "PrivateGroup";
	SlackChannelType$1["Public"] = "Public";
	return SlackChannelType$1;
}({});
/** All possible roles within a team in terms of access to team settings and operations. */
let TeamRoleType = /* @__PURE__ */ function(TeamRoleType$1) {
	TeamRoleType$1["Member"] = "member";
	TeamRoleType$1["Owner"] = "owner";
	return TeamRoleType$1;
}({});
/** Which action should be taken after an issue is added to triage. */
let TriageResponsibilityAction = /* @__PURE__ */ function(TriageResponsibilityAction$1) {
	TriageResponsibilityAction$1["Assign"] = "assign";
	TriageResponsibilityAction$1["Notify"] = "notify";
	return TriageResponsibilityAction$1;
}({});
let UserContextViewType = /* @__PURE__ */ function(UserContextViewType$1) {
	UserContextViewType$1["Assigned"] = "assigned";
	return UserContextViewType$1;
}({});
/** The types of flags that the user can have. */
let UserFlagType = /* @__PURE__ */ function(UserFlagType$1) {
	UserFlagType$1["All"] = "all";
	UserFlagType$1["AnalyticsWelcomeDismissed"] = "analyticsWelcomeDismissed";
	UserFlagType$1["CanPlaySnake"] = "canPlaySnake";
	UserFlagType$1["CanPlayTetris"] = "canPlayTetris";
	UserFlagType$1["CommandMenuClearShortcutTip"] = "commandMenuClearShortcutTip";
	UserFlagType$1["CompletedOnboarding"] = "completedOnboarding";
	UserFlagType$1["CycleWelcomeDismissed"] = "cycleWelcomeDismissed";
	UserFlagType$1["DesktopDownloadToastDismissed"] = "desktopDownloadToastDismissed";
	UserFlagType$1["DesktopInstalled"] = "desktopInstalled";
	UserFlagType$1["DesktopTabsOnboardingDismissed"] = "desktopTabsOnboardingDismissed";
	UserFlagType$1["DueDateShortcutMigration"] = "dueDateShortcutMigration";
	UserFlagType$1["EditorSlashCommandUsed"] = "editorSlashCommandUsed";
	UserFlagType$1["EmptyActiveIssuesDismissed"] = "emptyActiveIssuesDismissed";
	UserFlagType$1["EmptyBacklogDismissed"] = "emptyBacklogDismissed";
	UserFlagType$1["EmptyCustomViewsDismissed"] = "emptyCustomViewsDismissed";
	UserFlagType$1["EmptyMyIssuesDismissed"] = "emptyMyIssuesDismissed";
	UserFlagType$1["EmptyParagraphSlashCommandTip"] = "emptyParagraphSlashCommandTip";
	UserFlagType$1["FigmaPluginBannerDismissed"] = "figmaPluginBannerDismissed";
	UserFlagType$1["FigmaPromptDismissed"] = "figmaPromptDismissed";
	UserFlagType$1["HelpIslandFeatureInsightsDismissed"] = "helpIslandFeatureInsightsDismissed";
	UserFlagType$1["ImportBannerDismissed"] = "importBannerDismissed";
	UserFlagType$1["InitiativesBannerDismissed"] = "initiativesBannerDismissed";
	UserFlagType$1["InsightsHelpDismissed"] = "insightsHelpDismissed";
	UserFlagType$1["InsightsWelcomeDismissed"] = "insightsWelcomeDismissed";
	UserFlagType$1["IssueLabelSuggestionUsed"] = "issueLabelSuggestionUsed";
	UserFlagType$1["IssueMovePromptCompleted"] = "issueMovePromptCompleted";
	UserFlagType$1["JoinTeamIntroductionDismissed"] = "joinTeamIntroductionDismissed";
	UserFlagType$1["ListSelectionTip"] = "listSelectionTip";
	UserFlagType$1["MigrateThemePreference"] = "migrateThemePreference";
	UserFlagType$1["MilestoneOnboardingIsSeenAndDismissed"] = "milestoneOnboardingIsSeenAndDismissed";
	UserFlagType$1["ProjectBacklogWelcomeDismissed"] = "projectBacklogWelcomeDismissed";
	UserFlagType$1["ProjectBoardOnboardingIsSeenAndDismissed"] = "projectBoardOnboardingIsSeenAndDismissed";
	UserFlagType$1["ProjectUpdatesWelcomeDismissed"] = "projectUpdatesWelcomeDismissed";
	UserFlagType$1["ProjectWelcomeDismissed"] = "projectWelcomeDismissed";
	UserFlagType$1["PulseWelcomeDismissed"] = "pulseWelcomeDismissed";
	UserFlagType$1["RewindBannerDismissed"] = "rewindBannerDismissed";
	UserFlagType$1["SlackAgentPromoFromCreateNewIssueShown"] = "slackAgentPromoFromCreateNewIssueShown";
	UserFlagType$1["SlackBotWelcomeMessageShown"] = "slackBotWelcomeMessageShown";
	UserFlagType$1["SlackCommentReactionTipShown"] = "slackCommentReactionTipShown";
	UserFlagType$1["TeamsPageIntroductionDismissed"] = "teamsPageIntroductionDismissed";
	UserFlagType$1["ThreadedCommentsNudgeIsSeen"] = "threadedCommentsNudgeIsSeen";
	UserFlagType$1["TriageWelcomeDismissed"] = "triageWelcomeDismissed";
	UserFlagType$1["TryCyclesDismissed"] = "tryCyclesDismissed";
	UserFlagType$1["TryGithubDismissed"] = "tryGithubDismissed";
	UserFlagType$1["TryInvitePeopleDismissed"] = "tryInvitePeopleDismissed";
	UserFlagType$1["TryRoadmapsDismissed"] = "tryRoadmapsDismissed";
	UserFlagType$1["TryTriageDismissed"] = "tryTriageDismissed";
	UserFlagType$1["UpdatedSlackThreadSyncIntegration"] = "updatedSlackThreadSyncIntegration";
	return UserFlagType$1;
}({});
/** Operations that can be applied to UserFlagType. */
let UserFlagUpdateOperation = /* @__PURE__ */ function(UserFlagUpdateOperation$1) {
	UserFlagUpdateOperation$1["Clear"] = "clear";
	UserFlagUpdateOperation$1["Decr"] = "decr";
	UserFlagUpdateOperation$1["Incr"] = "incr";
	UserFlagUpdateOperation$1["Lock"] = "lock";
	return UserFlagUpdateOperation$1;
}({});
/** The different permission roles available to users on an organization. */
let UserRoleType = /* @__PURE__ */ function(UserRoleType$1) {
	UserRoleType$1["Admin"] = "admin";
	UserRoleType$1["App"] = "app";
	UserRoleType$1["Guest"] = "guest";
	UserRoleType$1["Owner"] = "owner";
	UserRoleType$1["User"] = "user";
	return UserRoleType$1;
}({});
/** Device type for theme */
let UserSettingsThemeDeviceType = /* @__PURE__ */ function(UserSettingsThemeDeviceType$1) {
	UserSettingsThemeDeviceType$1["Desktop"] = "desktop";
	UserSettingsThemeDeviceType$1["MobileWeb"] = "mobileWeb";
	return UserSettingsThemeDeviceType$1;
}({});
/** Theme color mode */
let UserSettingsThemeMode = /* @__PURE__ */ function(UserSettingsThemeMode$1) {
	UserSettingsThemeMode$1["Dark"] = "dark";
	UserSettingsThemeMode$1["Light"] = "light";
	return UserSettingsThemeMode$1;
}({});
/** Theme preset options */
let UserSettingsThemePreset = /* @__PURE__ */ function(UserSettingsThemePreset$1) {
	UserSettingsThemePreset$1["ClassicDark"] = "classicDark";
	UserSettingsThemePreset$1["Custom"] = "custom";
	UserSettingsThemePreset$1["Dark"] = "dark";
	UserSettingsThemePreset$1["Light"] = "light";
	UserSettingsThemePreset$1["MagicBlue"] = "magicBlue";
	UserSettingsThemePreset$1["PureLight"] = "pureLight";
	UserSettingsThemePreset$1["System"] = "system";
	return UserSettingsThemePreset$1;
}({});
/** The type of view preferences (either user or organization level preferences). */
let ViewPreferencesType = /* @__PURE__ */ function(ViewPreferencesType$1) {
	ViewPreferencesType$1["Organization"] = "organization";
	ViewPreferencesType$1["User"] = "user";
	return ViewPreferencesType$1;
}({});
/** The client view this custom view is targeting. */
let ViewType = /* @__PURE__ */ function(ViewType$1) {
	ViewType$1["ActiveIssues"] = "activeIssues";
	ViewType$1["Agents"] = "agents";
	ViewType$1["AllIssues"] = "allIssues";
	ViewType$1["Archive"] = "archive";
	ViewType$1["Backlog"] = "backlog";
	ViewType$1["Board"] = "board";
	ViewType$1["CompletedCycle"] = "completedCycle";
	ViewType$1["CustomView"] = "customView";
	ViewType$1["CustomViews"] = "customViews";
	ViewType$1["Customer"] = "customer";
	ViewType$1["Customers"] = "customers";
	ViewType$1["Cycle"] = "cycle";
	ViewType$1["Dashboards"] = "dashboards";
	ViewType$1["EmbeddedCustomerNeeds"] = "embeddedCustomerNeeds";
	ViewType$1["FeedAll"] = "feedAll";
	ViewType$1["FeedCreated"] = "feedCreated";
	ViewType$1["FeedFollowing"] = "feedFollowing";
	ViewType$1["FeedPopular"] = "feedPopular";
	ViewType$1["Inbox"] = "inbox";
	ViewType$1["Initiative"] = "initiative";
	ViewType$1["InitiativeOverview"] = "initiativeOverview";
	ViewType$1["InitiativeOverviewSubInitiatives"] = "initiativeOverviewSubInitiatives";
	ViewType$1["Initiatives"] = "initiatives";
	ViewType$1["InitiativesCompleted"] = "initiativesCompleted";
	ViewType$1["InitiativesPlanned"] = "initiativesPlanned";
	ViewType$1["IssueIdentifiers"] = "issueIdentifiers";
	ViewType$1["Label"] = "label";
	ViewType$1["MyIssues"] = "myIssues";
	ViewType$1["MyIssuesActivity"] = "myIssuesActivity";
	ViewType$1["MyIssuesCreatedByMe"] = "myIssuesCreatedByMe";
	ViewType$1["MyIssuesSubscribedTo"] = "myIssuesSubscribedTo";
	ViewType$1["MyReviews"] = "myReviews";
	ViewType$1["Project"] = "project";
	ViewType$1["ProjectCustomerNeeds"] = "projectCustomerNeeds";
	ViewType$1["ProjectDocuments"] = "projectDocuments";
	ViewType$1["ProjectLabel"] = "projectLabel";
	ViewType$1["Projects"] = "projects";
	ViewType$1["ProjectsAll"] = "projectsAll";
	ViewType$1["ProjectsBacklog"] = "projectsBacklog";
	ViewType$1["ProjectsClosed"] = "projectsClosed";
	ViewType$1["QuickView"] = "quickView";
	ViewType$1["Release"] = "release";
	ViewType$1["Reviews"] = "reviews";
	ViewType$1["Roadmap"] = "roadmap";
	ViewType$1["RoadmapAll"] = "roadmapAll";
	ViewType$1["RoadmapBacklog"] = "roadmapBacklog";
	ViewType$1["RoadmapClosed"] = "roadmapClosed";
	ViewType$1["Roadmaps"] = "roadmaps";
	ViewType$1["Search"] = "search";
	ViewType$1["SplitSearch"] = "splitSearch";
	ViewType$1["SubIssues"] = "subIssues";
	ViewType$1["Teams"] = "teams";
	ViewType$1["Triage"] = "triage";
	ViewType$1["UserProfile"] = "userProfile";
	ViewType$1["UserProfileCreatedByUser"] = "userProfileCreatedByUser";
	ViewType$1["WorkspaceMembers"] = "workspaceMembers";
	return ViewType$1;
}({});
var TypedDocumentString = class extends String {
	__apiType;
	value;
	__meta__;
	constructor(value, __meta__) {
		super(value);
		this.value = value;
		this.__meta__ = __meta__;
	}
	toString() {
		return this.value;
	}
};
const EntityFragmentDoc = new TypedDocumentString(`
    fragment Entity on Entity {
  __typename
  updatedAt
  archivedAt
  createdAt
  id
}
    `, { fragmentName: "Entity" });
const CustomViewNotificationSubscriptionFragmentDoc = new TypedDocumentString(`
    fragment CustomViewNotificationSubscription on CustomViewNotificationSubscription {
  __typename
  cycle {
    id
  }
  initiative {
    id
  }
  label {
    id
  }
  project {
    id
  }
  customView {
    id
  }
  customer {
    id
  }
  updatedAt
  team {
    id
  }
  archivedAt
  createdAt
  notificationSubscriptionTypes
  userContextViewType
  contextViewType
  id
  subscriber {
    id
  }
  user {
    id
  }
  active
}
    `, { fragmentName: "CustomViewNotificationSubscription" });
const CustomerNotificationSubscriptionFragmentDoc = new TypedDocumentString(`
    fragment CustomerNotificationSubscription on CustomerNotificationSubscription {
  __typename
  customView {
    id
  }
  cycle {
    id
  }
  initiative {
    id
  }
  label {
    id
  }
  project {
    id
  }
  customer {
    id
  }
  updatedAt
  team {
    id
  }
  archivedAt
  createdAt
  notificationSubscriptionTypes
  userContextViewType
  contextViewType
  id
  subscriber {
    id
  }
  user {
    id
  }
  active
}
    `, { fragmentName: "CustomerNotificationSubscription" });
const CycleNotificationSubscriptionFragmentDoc = new TypedDocumentString(`
    fragment CycleNotificationSubscription on CycleNotificationSubscription {
  __typename
  customView {
    id
  }
  initiative {
    id
  }
  label {
    id
  }
  project {
    id
  }
  customer {
    id
  }
  cycle {
    id
  }
  updatedAt
  team {
    id
  }
  archivedAt
  createdAt
  notificationSubscriptionTypes
  userContextViewType
  contextViewType
  id
  subscriber {
    id
  }
  user {
    id
  }
  active
}
    `, { fragmentName: "CycleNotificationSubscription" });
const CustomerNeedArchivePayloadFragmentDoc = new TypedDocumentString(`
    fragment CustomerNeedArchivePayload on CustomerNeedArchivePayload {
  __typename
  entity {
    id
  }
  lastSyncId
  success
}
    `, { fragmentName: "CustomerNeedArchivePayload" });
const CycleArchivePayloadFragmentDoc = new TypedDocumentString(`
    fragment CycleArchivePayload on CycleArchivePayload {
  __typename
  entity {
    id
  }
  lastSyncId
  success
}
    `, { fragmentName: "CycleArchivePayload" });
const DeletePayloadFragmentDoc = new TypedDocumentString(`
    fragment DeletePayload on DeletePayload {
  __typename
  entityId
  lastSyncId
  success
}
    `, { fragmentName: "DeletePayload" });
const DocumentArchivePayloadFragmentDoc = new TypedDocumentString(`
    fragment DocumentArchivePayload on DocumentArchivePayload {
  __typename
  entity {
    id
  }
  lastSyncId
  success
}
    `, { fragmentName: "DocumentArchivePayload" });
const InitiativeArchivePayloadFragmentDoc = new TypedDocumentString(`
    fragment InitiativeArchivePayload on InitiativeArchivePayload {
  __typename
  entity {
    id
  }
  lastSyncId
  success
}
    `, { fragmentName: "InitiativeArchivePayload" });
const InitiativeUpdateArchivePayloadFragmentDoc = new TypedDocumentString(`
    fragment InitiativeUpdateArchivePayload on InitiativeUpdateArchivePayload {
  __typename
  entity {
    id
  }
  lastSyncId
  success
}
    `, { fragmentName: "InitiativeUpdateArchivePayload" });
const IssueArchivePayloadFragmentDoc = new TypedDocumentString(`
    fragment IssueArchivePayload on IssueArchivePayload {
  __typename
  entity {
    id
  }
  lastSyncId
  success
}
    `, { fragmentName: "IssueArchivePayload" });
const ActorBotFragmentDoc = new TypedDocumentString(`
    fragment ActorBot on ActorBot {
  __typename
  avatarUrl
  name
  userDisplayName
  subType
  type
  id
}
    `, { fragmentName: "ActorBot" });
const CustomerNeedNotificationFragmentDoc = new TypedDocumentString(`
    fragment CustomerNeedNotification on CustomerNeedNotification {
  __typename
  type
  customerNeedId
  botActor {
    ...ActorBot
  }
  category
  customerNeed {
    id
  }
  externalUserActor {
    id
  }
  relatedIssue {
    id
  }
  updatedAt
  relatedProject {
    id
  }
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
    fragment ActorBot on ActorBot {
  __typename
  avatarUrl
  name
  userDisplayName
  subType
  type
  id
}`, { fragmentName: "CustomerNeedNotification" });
const CustomerNotificationFragmentDoc = new TypedDocumentString(`
    fragment CustomerNotification on CustomerNotification {
  __typename
  type
  customerId
  botActor {
    ...ActorBot
  }
  category
  customer {
    id
  }
  externalUserActor {
    id
  }
  updatedAt
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
    fragment ActorBot on ActorBot {
  __typename
  avatarUrl
  name
  userDisplayName
  subType
  type
  id
}`, { fragmentName: "CustomerNotification" });
const DocumentNotificationFragmentDoc = new TypedDocumentString(`
    fragment DocumentNotification on DocumentNotification {
  __typename
  reactionEmoji
  type
  commentId
  documentId
  parentCommentId
  botActor {
    ...ActorBot
  }
  category
  externalUserActor {
    id
  }
  updatedAt
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
    fragment ActorBot on ActorBot {
  __typename
  avatarUrl
  name
  userDisplayName
  subType
  type
  id
}`, { fragmentName: "DocumentNotification" });
const InitiativeNotificationFragmentDoc = new TypedDocumentString(`
    fragment InitiativeNotification on InitiativeNotification {
  __typename
  reactionEmoji
  type
  commentId
  initiativeId
  initiativeUpdateId
  parentCommentId
  botActor {
    ...ActorBot
  }
  category
  comment {
    id
  }
  document {
    id
  }
  externalUserActor {
    id
  }
  initiative {
    id
  }
  initiativeUpdate {
    id
  }
  updatedAt
  parentComment {
    id
  }
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
    fragment ActorBot on ActorBot {
  __typename
  avatarUrl
  name
  userDisplayName
  subType
  type
  id
}`, { fragmentName: "InitiativeNotification" });
const NotificationSubscriptionFragmentDoc = new TypedDocumentString(`
    fragment NotificationSubscription on NotificationSubscription {
  __typename
  customView {
    id
  }
  cycle {
    id
  }
  initiative {
    id
  }
  label {
    id
  }
  project {
    id
  }
  customer {
    id
  }
  updatedAt
  team {
    id
  }
  archivedAt
  createdAt
  userContextViewType
  contextViewType
  id
  subscriber {
    id
  }
  user {
    id
  }
  active
}
    `, { fragmentName: "NotificationSubscription" });
const IssueNotificationFragmentDoc = new TypedDocumentString(`
    fragment IssueNotification on IssueNotification {
  __typename
  reactionEmoji
  type
  commentId
  issueId
  parentCommentId
  botActor {
    ...ActorBot
  }
  category
  comment {
    id
  }
  externalUserActor {
    id
  }
  issue {
    id
  }
  updatedAt
  parentComment {
    id
  }
  subscriptions {
    ...NotificationSubscription
  }
  team {
    id
  }
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
    fragment ActorBot on ActorBot {
  __typename
  avatarUrl
  name
  userDisplayName
  subType
  type
  id
}
fragment NotificationSubscription on NotificationSubscription {
  __typename
  customView {
    id
  }
  cycle {
    id
  }
  initiative {
    id
  }
  label {
    id
  }
  project {
    id
  }
  customer {
    id
  }
  updatedAt
  team {
    id
  }
  archivedAt
  createdAt
  userContextViewType
  contextViewType
  id
  subscriber {
    id
  }
  user {
    id
  }
  active
}`, { fragmentName: "IssueNotification" });
const OauthClientApprovalFragmentDoc = new TypedDocumentString(`
    fragment OauthClientApproval on OauthClientApproval {
  __typename
  newlyRequestedScopes
  updatedAt
  requesterId
  responderId
  requestReason
  denyReason
  scopes
  status
  archivedAt
  createdAt
  id
  oauthClientId
}
    `, { fragmentName: "OauthClientApproval" });
const OauthClientApprovalNotificationFragmentDoc = new TypedDocumentString(`
    fragment OauthClientApprovalNotification on OauthClientApprovalNotification {
  __typename
  type
  oauthClientApprovalId
  oauthClientApproval {
    ...OauthClientApproval
  }
  botActor {
    ...ActorBot
  }
  category
  externalUserActor {
    id
  }
  updatedAt
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
    fragment ActorBot on ActorBot {
  __typename
  avatarUrl
  name
  userDisplayName
  subType
  type
  id
}
fragment OauthClientApproval on OauthClientApproval {
  __typename
  newlyRequestedScopes
  updatedAt
  requesterId
  responderId
  requestReason
  denyReason
  scopes
  status
  archivedAt
  createdAt
  id
  oauthClientId
}`, { fragmentName: "OauthClientApprovalNotification" });
const PostNotificationFragmentDoc = new TypedDocumentString(`
    fragment PostNotification on PostNotification {
  __typename
  reactionEmoji
  type
  commentId
  parentCommentId
  postId
  botActor {
    ...ActorBot
  }
  category
  externalUserActor {
    id
  }
  updatedAt
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
    fragment ActorBot on ActorBot {
  __typename
  avatarUrl
  name
  userDisplayName
  subType
  type
  id
}`, { fragmentName: "PostNotification" });
const ProjectNotificationFragmentDoc = new TypedDocumentString(`
    fragment ProjectNotification on ProjectNotification {
  __typename
  reactionEmoji
  type
  commentId
  parentCommentId
  projectId
  projectMilestoneId
  projectUpdateId
  botActor {
    ...ActorBot
  }
  category
  comment {
    id
  }
  document {
    id
  }
  externalUserActor {
    id
  }
  updatedAt
  parentComment {
    id
  }
  project {
    id
  }
  projectUpdate {
    id
  }
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
    fragment ActorBot on ActorBot {
  __typename
  avatarUrl
  name
  userDisplayName
  subType
  type
  id
}`, { fragmentName: "ProjectNotification" });
const PullRequestNotificationFragmentDoc = new TypedDocumentString(`
    fragment PullRequestNotification on PullRequestNotification {
  __typename
  type
  pullRequestCommentId
  pullRequestId
  botActor {
    ...ActorBot
  }
  category
  externalUserActor {
    id
  }
  updatedAt
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
    fragment ActorBot on ActorBot {
  __typename
  avatarUrl
  name
  userDisplayName
  subType
  type
  id
}`, { fragmentName: "PullRequestNotification" });
const NotificationFragmentDoc = new TypedDocumentString(`
    fragment Notification on Notification {
  __typename
  type
  botActor {
    ...ActorBot
  }
  category
  externalUserActor {
    id
  }
  updatedAt
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
  ... on CustomerNeedNotification {
    ...CustomerNeedNotification
  }
  ... on CustomerNotification {
    ...CustomerNotification
  }
  ... on DocumentNotification {
    ...DocumentNotification
  }
  ... on InitiativeNotification {
    ...InitiativeNotification
  }
  ... on IssueNotification {
    ...IssueNotification
  }
  ... on OauthClientApprovalNotification {
    ...OauthClientApprovalNotification
  }
  ... on PostNotification {
    ...PostNotification
  }
  ... on ProjectNotification {
    ...ProjectNotification
  }
  ... on PullRequestNotification {
    ...PullRequestNotification
  }
}
    fragment ActorBot on ActorBot {
  __typename
  avatarUrl
  name
  userDisplayName
  subType
  type
  id
}
fragment CustomerNeedNotification on CustomerNeedNotification {
  __typename
  type
  customerNeedId
  botActor {
    ...ActorBot
  }
  category
  customerNeed {
    id
  }
  externalUserActor {
    id
  }
  relatedIssue {
    id
  }
  updatedAt
  relatedProject {
    id
  }
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment CustomerNotification on CustomerNotification {
  __typename
  type
  customerId
  botActor {
    ...ActorBot
  }
  category
  customer {
    id
  }
  externalUserActor {
    id
  }
  updatedAt
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment DocumentNotification on DocumentNotification {
  __typename
  reactionEmoji
  type
  commentId
  documentId
  parentCommentId
  botActor {
    ...ActorBot
  }
  category
  externalUserActor {
    id
  }
  updatedAt
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment PostNotification on PostNotification {
  __typename
  reactionEmoji
  type
  commentId
  parentCommentId
  postId
  botActor {
    ...ActorBot
  }
  category
  externalUserActor {
    id
  }
  updatedAt
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment ProjectNotification on ProjectNotification {
  __typename
  reactionEmoji
  type
  commentId
  parentCommentId
  projectId
  projectMilestoneId
  projectUpdateId
  botActor {
    ...ActorBot
  }
  category
  comment {
    id
  }
  document {
    id
  }
  externalUserActor {
    id
  }
  updatedAt
  parentComment {
    id
  }
  project {
    id
  }
  projectUpdate {
    id
  }
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment PullRequestNotification on PullRequestNotification {
  __typename
  type
  pullRequestCommentId
  pullRequestId
  botActor {
    ...ActorBot
  }
  category
  externalUserActor {
    id
  }
  updatedAt
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment InitiativeNotification on InitiativeNotification {
  __typename
  reactionEmoji
  type
  commentId
  initiativeId
  initiativeUpdateId
  parentCommentId
  botActor {
    ...ActorBot
  }
  category
  comment {
    id
  }
  document {
    id
  }
  externalUserActor {
    id
  }
  initiative {
    id
  }
  initiativeUpdate {
    id
  }
  updatedAt
  parentComment {
    id
  }
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment IssueNotification on IssueNotification {
  __typename
  reactionEmoji
  type
  commentId
  issueId
  parentCommentId
  botActor {
    ...ActorBot
  }
  category
  comment {
    id
  }
  externalUserActor {
    id
  }
  issue {
    id
  }
  updatedAt
  parentComment {
    id
  }
  subscriptions {
    ...NotificationSubscription
  }
  team {
    id
  }
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment OauthClientApprovalNotification on OauthClientApprovalNotification {
  __typename
  type
  oauthClientApprovalId
  oauthClientApproval {
    ...OauthClientApproval
  }
  botActor {
    ...ActorBot
  }
  category
  externalUserActor {
    id
  }
  updatedAt
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment NotificationSubscription on NotificationSubscription {
  __typename
  customView {
    id
  }
  cycle {
    id
  }
  initiative {
    id
  }
  label {
    id
  }
  project {
    id
  }
  customer {
    id
  }
  updatedAt
  team {
    id
  }
  archivedAt
  createdAt
  userContextViewType
  contextViewType
  id
  subscriber {
    id
  }
  user {
    id
  }
  active
}
fragment OauthClientApproval on OauthClientApproval {
  __typename
  newlyRequestedScopes
  updatedAt
  requesterId
  responderId
  requestReason
  denyReason
  scopes
  status
  archivedAt
  createdAt
  id
  oauthClientId
}`, { fragmentName: "Notification" });
const NotificationArchivePayloadFragmentDoc = new TypedDocumentString(`
    fragment NotificationArchivePayload on NotificationArchivePayload {
  __typename
  entity {
    ...Notification
  }
  lastSyncId
  success
}
    fragment ActorBot on ActorBot {
  __typename
  avatarUrl
  name
  userDisplayName
  subType
  type
  id
}
fragment CustomerNeedNotification on CustomerNeedNotification {
  __typename
  type
  customerNeedId
  botActor {
    ...ActorBot
  }
  category
  customerNeed {
    id
  }
  externalUserActor {
    id
  }
  relatedIssue {
    id
  }
  updatedAt
  relatedProject {
    id
  }
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment CustomerNotification on CustomerNotification {
  __typename
  type
  customerId
  botActor {
    ...ActorBot
  }
  category
  customer {
    id
  }
  externalUserActor {
    id
  }
  updatedAt
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment DocumentNotification on DocumentNotification {
  __typename
  reactionEmoji
  type
  commentId
  documentId
  parentCommentId
  botActor {
    ...ActorBot
  }
  category
  externalUserActor {
    id
  }
  updatedAt
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment Notification on Notification {
  __typename
  type
  botActor {
    ...ActorBot
  }
  category
  externalUserActor {
    id
  }
  updatedAt
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
  ... on CustomerNeedNotification {
    ...CustomerNeedNotification
  }
  ... on CustomerNotification {
    ...CustomerNotification
  }
  ... on DocumentNotification {
    ...DocumentNotification
  }
  ... on InitiativeNotification {
    ...InitiativeNotification
  }
  ... on IssueNotification {
    ...IssueNotification
  }
  ... on OauthClientApprovalNotification {
    ...OauthClientApprovalNotification
  }
  ... on PostNotification {
    ...PostNotification
  }
  ... on ProjectNotification {
    ...ProjectNotification
  }
  ... on PullRequestNotification {
    ...PullRequestNotification
  }
}
fragment PostNotification on PostNotification {
  __typename
  reactionEmoji
  type
  commentId
  parentCommentId
  postId
  botActor {
    ...ActorBot
  }
  category
  externalUserActor {
    id
  }
  updatedAt
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment ProjectNotification on ProjectNotification {
  __typename
  reactionEmoji
  type
  commentId
  parentCommentId
  projectId
  projectMilestoneId
  projectUpdateId
  botActor {
    ...ActorBot
  }
  category
  comment {
    id
  }
  document {
    id
  }
  externalUserActor {
    id
  }
  updatedAt
  parentComment {
    id
  }
  project {
    id
  }
  projectUpdate {
    id
  }
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment PullRequestNotification on PullRequestNotification {
  __typename
  type
  pullRequestCommentId
  pullRequestId
  botActor {
    ...ActorBot
  }
  category
  externalUserActor {
    id
  }
  updatedAt
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment InitiativeNotification on InitiativeNotification {
  __typename
  reactionEmoji
  type
  commentId
  initiativeId
  initiativeUpdateId
  parentCommentId
  botActor {
    ...ActorBot
  }
  category
  comment {
    id
  }
  document {
    id
  }
  externalUserActor {
    id
  }
  initiative {
    id
  }
  initiativeUpdate {
    id
  }
  updatedAt
  parentComment {
    id
  }
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment IssueNotification on IssueNotification {
  __typename
  reactionEmoji
  type
  commentId
  issueId
  parentCommentId
  botActor {
    ...ActorBot
  }
  category
  comment {
    id
  }
  externalUserActor {
    id
  }
  issue {
    id
  }
  updatedAt
  parentComment {
    id
  }
  subscriptions {
    ...NotificationSubscription
  }
  team {
    id
  }
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment OauthClientApprovalNotification on OauthClientApprovalNotification {
  __typename
  type
  oauthClientApprovalId
  oauthClientApproval {
    ...OauthClientApproval
  }
  botActor {
    ...ActorBot
  }
  category
  externalUserActor {
    id
  }
  updatedAt
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment NotificationSubscription on NotificationSubscription {
  __typename
  customView {
    id
  }
  cycle {
    id
  }
  initiative {
    id
  }
  label {
    id
  }
  project {
    id
  }
  customer {
    id
  }
  updatedAt
  team {
    id
  }
  archivedAt
  createdAt
  userContextViewType
  contextViewType
  id
  subscriber {
    id
  }
  user {
    id
  }
  active
}
fragment OauthClientApproval on OauthClientApproval {
  __typename
  newlyRequestedScopes
  updatedAt
  requesterId
  responderId
  requestReason
  denyReason
  scopes
  status
  archivedAt
  createdAt
  id
  oauthClientId
}`, { fragmentName: "NotificationArchivePayload" });
const ProjectArchivePayloadFragmentDoc = new TypedDocumentString(`
    fragment ProjectArchivePayload on ProjectArchivePayload {
  __typename
  entity {
    id
  }
  lastSyncId
  success
}
    `, { fragmentName: "ProjectArchivePayload" });
const ProjectStatusArchivePayloadFragmentDoc = new TypedDocumentString(`
    fragment ProjectStatusArchivePayload on ProjectStatusArchivePayload {
  __typename
  entity {
    id
  }
  lastSyncId
  success
}
    `, { fragmentName: "ProjectStatusArchivePayload" });
const ProjectUpdateArchivePayloadFragmentDoc = new TypedDocumentString(`
    fragment ProjectUpdateArchivePayload on ProjectUpdateArchivePayload {
  __typename
  entity {
    id
  }
  lastSyncId
  success
}
    `, { fragmentName: "ProjectUpdateArchivePayload" });
const ReleaseArchivePayloadFragmentDoc = new TypedDocumentString(`
    fragment ReleaseArchivePayload on ReleaseArchivePayload {
  __typename
  lastSyncId
  success
}
    `, { fragmentName: "ReleaseArchivePayload" });
const ReleasePipelineArchivePayloadFragmentDoc = new TypedDocumentString(`
    fragment ReleasePipelineArchivePayload on ReleasePipelineArchivePayload {
  __typename
  lastSyncId
  success
}
    `, { fragmentName: "ReleasePipelineArchivePayload" });
const ReleaseStageArchivePayloadFragmentDoc = new TypedDocumentString(`
    fragment ReleaseStageArchivePayload on ReleaseStageArchivePayload {
  __typename
  lastSyncId
  success
}
    `, { fragmentName: "ReleaseStageArchivePayload" });
const RoadmapArchivePayloadFragmentDoc = new TypedDocumentString(`
    fragment RoadmapArchivePayload on RoadmapArchivePayload {
  __typename
  entity {
    id
  }
  lastSyncId
  success
}
    `, { fragmentName: "RoadmapArchivePayload" });
const TeamArchivePayloadFragmentDoc = new TypedDocumentString(`
    fragment TeamArchivePayload on TeamArchivePayload {
  __typename
  entity {
    id
  }
  lastSyncId
  success
}
    `, { fragmentName: "TeamArchivePayload" });
const WorkflowStateArchivePayloadFragmentDoc = new TypedDocumentString(`
    fragment WorkflowStateArchivePayload on WorkflowStateArchivePayload {
  __typename
  entity {
    id
  }
  lastSyncId
  success
}
    `, { fragmentName: "WorkflowStateArchivePayload" });
const ArchivePayloadFragmentDoc = new TypedDocumentString(`
    fragment ArchivePayload on ArchivePayload {
  __typename
  lastSyncId
  success
  ... on CustomerNeedArchivePayload {
    ...CustomerNeedArchivePayload
  }
  ... on CycleArchivePayload {
    ...CycleArchivePayload
  }
  ... on DeletePayload {
    ...DeletePayload
  }
  ... on DocumentArchivePayload {
    ...DocumentArchivePayload
  }
  ... on InitiativeArchivePayload {
    ...InitiativeArchivePayload
  }
  ... on InitiativeUpdateArchivePayload {
    ...InitiativeUpdateArchivePayload
  }
  ... on IssueArchivePayload {
    ...IssueArchivePayload
  }
  ... on NotificationArchivePayload {
    ...NotificationArchivePayload
  }
  ... on ProjectArchivePayload {
    ...ProjectArchivePayload
  }
  ... on ProjectStatusArchivePayload {
    ...ProjectStatusArchivePayload
  }
  ... on ProjectUpdateArchivePayload {
    ...ProjectUpdateArchivePayload
  }
  ... on ReleaseArchivePayload {
    ...ReleaseArchivePayload
  }
  ... on ReleasePipelineArchivePayload {
    ...ReleasePipelineArchivePayload
  }
  ... on ReleaseStageArchivePayload {
    ...ReleaseStageArchivePayload
  }
  ... on RoadmapArchivePayload {
    ...RoadmapArchivePayload
  }
  ... on TeamArchivePayload {
    ...TeamArchivePayload
  }
  ... on WorkflowStateArchivePayload {
    ...WorkflowStateArchivePayload
  }
}
    fragment ActorBot on ActorBot {
  __typename
  avatarUrl
  name
  userDisplayName
  subType
  type
  id
}
fragment CustomerNeedNotification on CustomerNeedNotification {
  __typename
  type
  customerNeedId
  botActor {
    ...ActorBot
  }
  category
  customerNeed {
    id
  }
  externalUserActor {
    id
  }
  relatedIssue {
    id
  }
  updatedAt
  relatedProject {
    id
  }
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment CustomerNotification on CustomerNotification {
  __typename
  type
  customerId
  botActor {
    ...ActorBot
  }
  category
  customer {
    id
  }
  externalUserActor {
    id
  }
  updatedAt
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment DocumentNotification on DocumentNotification {
  __typename
  reactionEmoji
  type
  commentId
  documentId
  parentCommentId
  botActor {
    ...ActorBot
  }
  category
  externalUserActor {
    id
  }
  updatedAt
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment CustomerNeedArchivePayload on CustomerNeedArchivePayload {
  __typename
  entity {
    id
  }
  lastSyncId
  success
}
fragment CycleArchivePayload on CycleArchivePayload {
  __typename
  entity {
    id
  }
  lastSyncId
  success
}
fragment DocumentArchivePayload on DocumentArchivePayload {
  __typename
  entity {
    id
  }
  lastSyncId
  success
}
fragment InitiativeArchivePayload on InitiativeArchivePayload {
  __typename
  entity {
    id
  }
  lastSyncId
  success
}
fragment InitiativeUpdateArchivePayload on InitiativeUpdateArchivePayload {
  __typename
  entity {
    id
  }
  lastSyncId
  success
}
fragment IssueArchivePayload on IssueArchivePayload {
  __typename
  entity {
    id
  }
  lastSyncId
  success
}
fragment NotificationArchivePayload on NotificationArchivePayload {
  __typename
  entity {
    ...Notification
  }
  lastSyncId
  success
}
fragment ProjectArchivePayload on ProjectArchivePayload {
  __typename
  entity {
    id
  }
  lastSyncId
  success
}
fragment ProjectStatusArchivePayload on ProjectStatusArchivePayload {
  __typename
  entity {
    id
  }
  lastSyncId
  success
}
fragment ProjectUpdateArchivePayload on ProjectUpdateArchivePayload {
  __typename
  entity {
    id
  }
  lastSyncId
  success
}
fragment ReleaseArchivePayload on ReleaseArchivePayload {
  __typename
  lastSyncId
  success
}
fragment ReleasePipelineArchivePayload on ReleasePipelineArchivePayload {
  __typename
  lastSyncId
  success
}
fragment ReleaseStageArchivePayload on ReleaseStageArchivePayload {
  __typename
  lastSyncId
  success
}
fragment RoadmapArchivePayload on RoadmapArchivePayload {
  __typename
  entity {
    id
  }
  lastSyncId
  success
}
fragment TeamArchivePayload on TeamArchivePayload {
  __typename
  entity {
    id
  }
  lastSyncId
  success
}
fragment WorkflowStateArchivePayload on WorkflowStateArchivePayload {
  __typename
  entity {
    id
  }
  lastSyncId
  success
}
fragment DeletePayload on DeletePayload {
  __typename
  entityId
  lastSyncId
  success
}
fragment Notification on Notification {
  __typename
  type
  botActor {
    ...ActorBot
  }
  category
  externalUserActor {
    id
  }
  updatedAt
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
  ... on CustomerNeedNotification {
    ...CustomerNeedNotification
  }
  ... on CustomerNotification {
    ...CustomerNotification
  }
  ... on DocumentNotification {
    ...DocumentNotification
  }
  ... on InitiativeNotification {
    ...InitiativeNotification
  }
  ... on IssueNotification {
    ...IssueNotification
  }
  ... on OauthClientApprovalNotification {
    ...OauthClientApprovalNotification
  }
  ... on PostNotification {
    ...PostNotification
  }
  ... on ProjectNotification {
    ...ProjectNotification
  }
  ... on PullRequestNotification {
    ...PullRequestNotification
  }
}
fragment PostNotification on PostNotification {
  __typename
  reactionEmoji
  type
  commentId
  parentCommentId
  postId
  botActor {
    ...ActorBot
  }
  category
  externalUserActor {
    id
  }
  updatedAt
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment ProjectNotification on ProjectNotification {
  __typename
  reactionEmoji
  type
  commentId
  parentCommentId
  projectId
  projectMilestoneId
  projectUpdateId
  botActor {
    ...ActorBot
  }
  category
  comment {
    id
  }
  document {
    id
  }
  externalUserActor {
    id
  }
  updatedAt
  parentComment {
    id
  }
  project {
    id
  }
  projectUpdate {
    id
  }
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment PullRequestNotification on PullRequestNotification {
  __typename
  type
  pullRequestCommentId
  pullRequestId
  botActor {
    ...ActorBot
  }
  category
  externalUserActor {
    id
  }
  updatedAt
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment InitiativeNotification on InitiativeNotification {
  __typename
  reactionEmoji
  type
  commentId
  initiativeId
  initiativeUpdateId
  parentCommentId
  botActor {
    ...ActorBot
  }
  category
  comment {
    id
  }
  document {
    id
  }
  externalUserActor {
    id
  }
  initiative {
    id
  }
  initiativeUpdate {
    id
  }
  updatedAt
  parentComment {
    id
  }
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment IssueNotification on IssueNotification {
  __typename
  reactionEmoji
  type
  commentId
  issueId
  parentCommentId
  botActor {
    ...ActorBot
  }
  category
  comment {
    id
  }
  externalUserActor {
    id
  }
  issue {
    id
  }
  updatedAt
  parentComment {
    id
  }
  subscriptions {
    ...NotificationSubscription
  }
  team {
    id
  }
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment OauthClientApprovalNotification on OauthClientApprovalNotification {
  __typename
  type
  oauthClientApprovalId
  oauthClientApproval {
    ...OauthClientApproval
  }
  botActor {
    ...ActorBot
  }
  category
  externalUserActor {
    id
  }
  updatedAt
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment NotificationSubscription on NotificationSubscription {
  __typename
  customView {
    id
  }
  cycle {
    id
  }
  initiative {
    id
  }
  label {
    id
  }
  project {
    id
  }
  customer {
    id
  }
  updatedAt
  team {
    id
  }
  archivedAt
  createdAt
  userContextViewType
  contextViewType
  id
  subscriber {
    id
  }
  user {
    id
  }
  active
}
fragment OauthClientApproval on OauthClientApproval {
  __typename
  newlyRequestedScopes
  updatedAt
  requesterId
  responderId
  requestReason
  denyReason
  scopes
  status
  archivedAt
  createdAt
  id
  oauthClientId
}`, { fragmentName: "ArchivePayload" });
const LabelNotificationSubscriptionFragmentDoc = new TypedDocumentString(`
    fragment LabelNotificationSubscription on LabelNotificationSubscription {
  __typename
  customView {
    id
  }
  cycle {
    id
  }
  initiative {
    id
  }
  project {
    id
  }
  customer {
    id
  }
  label {
    id
  }
  updatedAt
  team {
    id
  }
  archivedAt
  createdAt
  notificationSubscriptionTypes
  userContextViewType
  contextViewType
  id
  subscriber {
    id
  }
  user {
    id
  }
  active
}
    `, { fragmentName: "LabelNotificationSubscription" });
const ProjectNotificationSubscriptionFragmentDoc = new TypedDocumentString(`
    fragment ProjectNotificationSubscription on ProjectNotificationSubscription {
  __typename
  customView {
    id
  }
  cycle {
    id
  }
  initiative {
    id
  }
  label {
    id
  }
  customer {
    id
  }
  updatedAt
  project {
    id
  }
  team {
    id
  }
  archivedAt
  createdAt
  notificationSubscriptionTypes
  userContextViewType
  contextViewType
  id
  subscriber {
    id
  }
  user {
    id
  }
  active
}
    `, { fragmentName: "ProjectNotificationSubscription" });
const TeamNotificationSubscriptionFragmentDoc = new TypedDocumentString(`
    fragment TeamNotificationSubscription on TeamNotificationSubscription {
  __typename
  customView {
    id
  }
  cycle {
    id
  }
  initiative {
    id
  }
  label {
    id
  }
  project {
    id
  }
  customer {
    id
  }
  updatedAt
  team {
    id
  }
  archivedAt
  createdAt
  notificationSubscriptionTypes
  userContextViewType
  contextViewType
  id
  subscriber {
    id
  }
  user {
    id
  }
  active
}
    `, { fragmentName: "TeamNotificationSubscription" });
const UserNotificationSubscriptionFragmentDoc = new TypedDocumentString(`
    fragment UserNotificationSubscription on UserNotificationSubscription {
  __typename
  customView {
    id
  }
  cycle {
    id
  }
  initiative {
    id
  }
  label {
    id
  }
  project {
    id
  }
  customer {
    id
  }
  updatedAt
  team {
    id
  }
  archivedAt
  createdAt
  notificationSubscriptionTypes
  userContextViewType
  contextViewType
  id
  user {
    id
  }
  subscriber {
    id
  }
  active
}
    `, { fragmentName: "UserNotificationSubscription" });
const SesDomainIdentityDnsRecordFragmentDoc = new TypedDocumentString(`
    fragment SesDomainIdentityDnsRecord on SesDomainIdentityDnsRecord {
  __typename
  content
  name
  type
  isVerified
}
    `, { fragmentName: "SesDomainIdentityDnsRecord" });
const SesDomainIdentityFragmentDoc = new TypedDocumentString(`
    fragment SesDomainIdentity on SesDomainIdentity {
  __typename
  region
  dnsRecords {
    ...SesDomainIdentityDnsRecord
  }
  domain
  updatedAt
  archivedAt
  createdAt
  id
  creator {
    id
  }
  canSendFromCustomDomain
}
    fragment SesDomainIdentityDnsRecord on SesDomainIdentityDnsRecord {
  __typename
  content
  name
  type
  isVerified
}`, { fragmentName: "SesDomainIdentity" });
const EmailIntakeAddressFragmentDoc = new TypedDocumentString(`
    fragment EmailIntakeAddress on EmailIntakeAddress {
  __typename
  sesDomainIdentity {
    ...SesDomainIdentity
  }
  issueCanceledAutoReply
  issueCompletedAutoReply
  issueCreatedAutoReply
  forwardingEmailAddress
  updatedAt
  senderName
  team {
    id
  }
  template {
    id
  }
  archivedAt
  createdAt
  type
  id
  creator {
    id
  }
  address
  repliesEnabled
  customerRequestsEnabled
  issueCanceledAutoReplyEnabled
  issueCompletedAutoReplyEnabled
  issueCreatedAutoReplyEnabled
  useUserNamesInReplies
  enabled
}
    fragment SesDomainIdentityDnsRecord on SesDomainIdentityDnsRecord {
  __typename
  content
  name
  type
  isVerified
}
fragment SesDomainIdentity on SesDomainIdentity {
  __typename
  region
  dnsRecords {
    ...SesDomainIdentityDnsRecord
  }
  domain
  updatedAt
  archivedAt
  createdAt
  id
  creator {
    id
  }
  canSendFromCustomDomain
}`, { fragmentName: "EmailIntakeAddress" });
const AuthIdentityProviderFragmentDoc = new TypedDocumentString(`
    fragment AuthIdentityProvider on AuthIdentityProvider {
  __typename
  ssoBinding
  ssoEndpoint
  priority
  ssoSignAlgo
  issuerEntityId
  spEntityId
  createdAt
  type
  id
  samlEnabled
  scimEnabled
  defaultMigrated
  ssoSigningCert
}
    `, { fragmentName: "AuthIdentityProvider" });
const InitiativeNotificationSubscriptionFragmentDoc = new TypedDocumentString(`
    fragment InitiativeNotificationSubscription on InitiativeNotificationSubscription {
  __typename
  customView {
    id
  }
  cycle {
    id
  }
  label {
    id
  }
  project {
    id
  }
  customer {
    id
  }
  initiative {
    id
  }
  updatedAt
  team {
    id
  }
  archivedAt
  createdAt
  notificationSubscriptionTypes
  userContextViewType
  contextViewType
  id
  subscriber {
    id
  }
  user {
    id
  }
  active
}
    `, { fragmentName: "InitiativeNotificationSubscription" });
const OrganizationIpRestrictionFragmentDoc = new TypedDocumentString(`
    fragment OrganizationIpRestriction on OrganizationIpRestriction {
  __typename
  range
  description
  type
  enabled
}
    `, { fragmentName: "OrganizationIpRestriction" });
const ProjectStatusFragmentDoc = new TypedDocumentString(`
    fragment ProjectStatus on ProjectStatus {
  __typename
  description
  color
  updatedAt
  name
  position
  archivedAt
  createdAt
  type
  id
  indefinite
}
    `, { fragmentName: "ProjectStatus" });
const PaidSubscriptionFragmentDoc = new TypedDocumentString(`
    fragment PaidSubscription on PaidSubscription {
  __typename
  collectionMethod
  creator {
    id
  }
  cancelAt
  canceledAt
  nextBillingAt
  updatedAt
  seatsMaximum
  seatsMinimum
  seats
  pendingChangeType
  type
  archivedAt
  createdAt
  id
}
    `, { fragmentName: "PaidSubscription" });
const OrganizationFragmentDoc = new TypedDocumentString(`
    fragment Organization on Organization {
  __typename
  allowedAuthServices
  allowedFileUploadContentTypes
  createdIssueCount
  customersConfiguration
  defaultFeedSummarySchedule
  gitBranchFormat
  ipRestrictions {
    ...OrganizationIpRestriction
  }
  userCount
  customerCount
  previousUrlKeys
  periodUploadVolume
  securitySettings
  initiativeUpdateRemindersDay
  projectUpdateRemindersDay
  releaseChannel
  initiativeUpdateRemindersHour
  projectUpdateRemindersHour
  updatedAt
  fiscalYearStartMonth
  initiativeUpdateReminderFrequencyInWeeks
  projectUpdateReminderFrequencyInWeeks
  logoUrl
  name
  projectStatuses {
    ...ProjectStatus
  }
  subscription {
    ...PaidSubscription
  }
  urlKey
  deletionRequestedAt
  archivedAt
  createdAt
  trialStartsAt
  trialEndsAt
  id
  hipaaComplianceEnabled
  samlEnabled
  scimEnabled
  gitLinkbackDescriptionsEnabled
  gitLinkbackMessagesEnabled
  gitPublicLinkbackMessagesEnabled
  aiDiscussionSummariesEnabled
  aiThreadSummariesEnabled
  feedEnabled
  customersEnabled
  roadmapEnabled
  projectUpdatesReminderFrequency
  allowMembersToInvite
  restrictTeamCreationToAdmins
  restrictLabelManagementToAdmins
  slaDayCount
}
    fragment ProjectStatus on ProjectStatus {
  __typename
  description
  color
  updatedAt
  name
  position
  archivedAt
  createdAt
  type
  id
  indefinite
}
fragment PaidSubscription on PaidSubscription {
  __typename
  collectionMethod
  creator {
    id
  }
  cancelAt
  canceledAt
  nextBillingAt
  updatedAt
  seatsMaximum
  seatsMinimum
  seats
  pendingChangeType
  type
  archivedAt
  createdAt
  id
}
fragment OrganizationIpRestriction on OrganizationIpRestriction {
  __typename
  range
  description
  type
  enabled
}`, { fragmentName: "Organization" });
const AuthenticationSessionResponseFragmentDoc = new TypedDocumentString(`
    fragment AuthenticationSessionResponse on AuthenticationSessionResponse {
  __typename
  client
  countryCodes
  updatedAt
  location
  ip
  isCurrentSession
  locationCity
  locationCountryCode
  locationCountry
  locationRegionCode
  name
  operatingSystem
  service
  userAgent
  createdAt
  type
  browserType
  lastActiveAt
  id
}
    `, { fragmentName: "AuthenticationSessionResponse" });
const BaseWebhookPayloadFragmentDoc = new TypedDocumentString(`
    fragment BaseWebhookPayload on BaseWebhookPayload {
  __typename
  organizationId
  webhookId
  createdAt
  webhookTimestamp
}
    `, { fragmentName: "BaseWebhookPayload" });
const CustomerNeedChildWebhookPayloadFragmentDoc = new TypedDocumentString(`
    fragment CustomerNeedChildWebhookPayload on CustomerNeedChildWebhookPayload {
  __typename
  attachmentId
  id
  customerId
  issueId
  projectId
}
    `, { fragmentName: "CustomerNeedChildWebhookPayload" });
const ProjectLabelChildWebhookPayloadFragmentDoc = new TypedDocumentString(`
    fragment ProjectLabelChildWebhookPayload on ProjectLabelChildWebhookPayload {
  __typename
  id
  color
  name
  parentId
}
    `, { fragmentName: "ProjectLabelChildWebhookPayload" });
const IntegrationChildWebhookPayloadFragmentDoc = new TypedDocumentString(`
    fragment IntegrationChildWebhookPayload on IntegrationChildWebhookPayload {
  __typename
  id
  service
}
    `, { fragmentName: "IntegrationChildWebhookPayload" });
const IdentityProviderFragmentDoc = new TypedDocumentString(`
    fragment IdentityProvider on IdentityProvider {
  __typename
  ssoBinding
  ssoEndpoint
  priority
  ssoSignAlgo
  issuerEntityId
  updatedAt
  spEntityId
  archivedAt
  createdAt
  type
  id
  samlEnabled
  scimEnabled
  defaultMigrated
  allowNameChange
  ssoSigningCert
}
    `, { fragmentName: "IdentityProvider" });
const OrganizationDomainFragmentDoc = new TypedDocumentString(`
    fragment OrganizationDomain on OrganizationDomain {
  __typename
  name
  verificationEmail
  verified
  disableOrganizationCreation
  identityProvider {
    ...IdentityProvider
  }
  updatedAt
  archivedAt
  createdAt
  id
  creator {
    id
  }
  authType
  claimed
}
    fragment IdentityProvider on IdentityProvider {
  __typename
  ssoBinding
  ssoEndpoint
  priority
  ssoSignAlgo
  issuerEntityId
  updatedAt
  spEntityId
  archivedAt
  createdAt
  type
  id
  samlEnabled
  scimEnabled
  defaultMigrated
  allowNameChange
  ssoSigningCert
}`, { fragmentName: "OrganizationDomain" });
const WebhookFailureEventFragmentDoc = new TypedDocumentString(`
    fragment WebhookFailureEvent on WebhookFailureEvent {
  __typename
  responseOrError
  httpStatus
  url
  createdAt
  executionId
  id
  webhook {
    id
  }
}
    `, { fragmentName: "WebhookFailureEvent" });
const UserChildWebhookPayloadFragmentDoc = new TypedDocumentString(`
    fragment UserChildWebhookPayload on UserChildWebhookPayload {
  __typename
  id
  url
  avatarUrl
  email
  name
}
    `, { fragmentName: "UserChildWebhookPayload" });
const CommentChildWebhookPayloadFragmentDoc = new TypedDocumentString(`
    fragment CommentChildWebhookPayload on CommentChildWebhookPayload {
  __typename
  id
  documentContentId
  initiativeUpdateId
  issueId
  projectUpdateId
  userId
  body
}
    `, { fragmentName: "CommentChildWebhookPayload" });
const InitiativeChildWebhookPayloadFragmentDoc = new TypedDocumentString(`
    fragment InitiativeChildWebhookPayload on InitiativeChildWebhookPayload {
  __typename
  id
  url
  name
}
    `, { fragmentName: "InitiativeChildWebhookPayload" });
const ProjectChildWebhookPayloadFragmentDoc = new TypedDocumentString(`
    fragment ProjectChildWebhookPayload on ProjectChildWebhookPayload {
  __typename
  id
  url
  name
}
    `, { fragmentName: "ProjectChildWebhookPayload" });
const DocumentChildWebhookPayloadFragmentDoc = new TypedDocumentString(`
    fragment DocumentChildWebhookPayload on DocumentChildWebhookPayload {
  __typename
  id
  initiativeId
  projectId
  initiative {
    ...InitiativeChildWebhookPayload
  }
  project {
    ...ProjectChildWebhookPayload
  }
  title
}
    fragment ProjectChildWebhookPayload on ProjectChildWebhookPayload {
  __typename
  id
  url
  name
}
fragment InitiativeChildWebhookPayload on InitiativeChildWebhookPayload {
  __typename
  id
  url
  name
}`, { fragmentName: "DocumentChildWebhookPayload" });
const TeamChildWebhookPayloadFragmentDoc = new TypedDocumentString(`
    fragment TeamChildWebhookPayload on TeamChildWebhookPayload {
  __typename
  id
  key
  name
}
    `, { fragmentName: "TeamChildWebhookPayload" });
const IssueWithDescriptionChildWebhookPayloadFragmentDoc = new TypedDocumentString(`
    fragment IssueWithDescriptionChildWebhookPayload on IssueWithDescriptionChildWebhookPayload {
  __typename
  id
  team {
    ...TeamChildWebhookPayload
  }
  teamId
  url
  description
  identifier
  title
}
    fragment TeamChildWebhookPayload on TeamChildWebhookPayload {
  __typename
  id
  key
  name
}`, { fragmentName: "IssueWithDescriptionChildWebhookPayload" });
const ProjectUpdateChildWebhookPayloadFragmentDoc = new TypedDocumentString(`
    fragment ProjectUpdateChildWebhookPayload on ProjectUpdateChildWebhookPayload {
  __typename
  id
  userId
  body
  project {
    ...ProjectChildWebhookPayload
  }
}
    fragment ProjectChildWebhookPayload on ProjectChildWebhookPayload {
  __typename
  id
  url
  name
}`, { fragmentName: "ProjectUpdateChildWebhookPayload" });
const OtherNotificationWebhookPayloadFragmentDoc = new TypedDocumentString(`
    fragment OtherNotificationWebhookPayload on OtherNotificationWebhookPayload {
  __typename
  actorId
  commentId
  documentId
  id
  externalUserActorId
  issueId
  parentCommentId
  projectId
  projectUpdateId
  userId
  actor {
    ...UserChildWebhookPayload
  }
  comment {
    ...CommentChildWebhookPayload
  }
  document {
    ...DocumentChildWebhookPayload
  }
  reactionEmoji
  issue {
    ...IssueWithDescriptionChildWebhookPayload
  }
  parentComment {
    ...CommentChildWebhookPayload
  }
  project {
    ...ProjectChildWebhookPayload
  }
  projectUpdate {
    ...ProjectUpdateChildWebhookPayload
  }
  archivedAt
  createdAt
  updatedAt
  type
}
    fragment CommentChildWebhookPayload on CommentChildWebhookPayload {
  __typename
  id
  documentContentId
  initiativeUpdateId
  issueId
  projectUpdateId
  userId
  body
}
fragment DocumentChildWebhookPayload on DocumentChildWebhookPayload {
  __typename
  id
  initiativeId
  projectId
  initiative {
    ...InitiativeChildWebhookPayload
  }
  project {
    ...ProjectChildWebhookPayload
  }
  title
}
fragment ProjectUpdateChildWebhookPayload on ProjectUpdateChildWebhookPayload {
  __typename
  id
  userId
  body
  project {
    ...ProjectChildWebhookPayload
  }
}
fragment ProjectChildWebhookPayload on ProjectChildWebhookPayload {
  __typename
  id
  url
  name
}
fragment TeamChildWebhookPayload on TeamChildWebhookPayload {
  __typename
  id
  key
  name
}
fragment UserChildWebhookPayload on UserChildWebhookPayload {
  __typename
  id
  url
  avatarUrl
  email
  name
}
fragment InitiativeChildWebhookPayload on InitiativeChildWebhookPayload {
  __typename
  id
  url
  name
}
fragment IssueWithDescriptionChildWebhookPayload on IssueWithDescriptionChildWebhookPayload {
  __typename
  id
  team {
    ...TeamChildWebhookPayload
  }
  teamId
  url
  description
  identifier
  title
}`, { fragmentName: "OtherNotificationWebhookPayload" });
const IntegrationActorWebhookPayloadFragmentDoc = new TypedDocumentString(`
    fragment IntegrationActorWebhookPayload on IntegrationActorWebhookPayload {
  __typename
  id
  service
  type
}
    `, { fragmentName: "IntegrationActorWebhookPayload" });
const OauthClientActorWebhookPayloadFragmentDoc = new TypedDocumentString(`
    fragment OauthClientActorWebhookPayload on OauthClientActorWebhookPayload {
  __typename
  id
  name
  type
}
    `, { fragmentName: "OauthClientActorWebhookPayload" });
const OrganizationOriginWebhookPayloadFragmentDoc = new TypedDocumentString(`
    fragment OrganizationOriginWebhookPayload on OrganizationOriginWebhookPayload {
  __typename
  type
}
    `, { fragmentName: "OrganizationOriginWebhookPayload" });
const OAuthAppWebhookPayloadFragmentDoc = new TypedDocumentString(`
    fragment OAuthAppWebhookPayload on OAuthAppWebhookPayload {
  __typename
  organizationId
  oauthClientId
  webhookId
  createdAt
  action
  type
  webhookTimestamp
}
    `, { fragmentName: "OAuthAppWebhookPayload" });
const OauthClientChildWebhookPayloadFragmentDoc = new TypedDocumentString(`
    fragment OauthClientChildWebhookPayload on OauthClientChildWebhookPayload {
  __typename
  id
  name
}
    `, { fragmentName: "OauthClientChildWebhookPayload" });
const OAuthAuthorizationWebhookPayloadFragmentDoc = new TypedDocumentString(`
    fragment OAuthAuthorizationWebhookPayload on OAuthAuthorizationWebhookPayload {
  __typename
  oauthClient {
    ...OauthClientChildWebhookPayload
  }
  user {
    ...UserChildWebhookPayload
  }
  oauthClientId
  organizationId
  userId
  webhookId
  activeTokensForUser
  createdAt
  action
  type
  webhookTimestamp
}
    fragment UserChildWebhookPayload on UserChildWebhookPayload {
  __typename
  id
  url
  avatarUrl
  email
  name
}
fragment OauthClientChildWebhookPayload on OauthClientChildWebhookPayload {
  __typename
  id
  name
}`, { fragmentName: "OAuthAuthorizationWebhookPayload" });
const DocumentContentChildWebhookPayloadFragmentDoc = new TypedDocumentString(`
    fragment DocumentContentChildWebhookPayload on DocumentContentChildWebhookPayload {
  __typename
  document {
    ...DocumentChildWebhookPayload
  }
  project {
    ...ProjectChildWebhookPayload
  }
}
    fragment DocumentChildWebhookPayload on DocumentChildWebhookPayload {
  __typename
  id
  initiativeId
  projectId
  initiative {
    ...InitiativeChildWebhookPayload
  }
  project {
    ...ProjectChildWebhookPayload
  }
  title
}
fragment ProjectChildWebhookPayload on ProjectChildWebhookPayload {
  __typename
  id
  url
  name
}
fragment InitiativeChildWebhookPayload on InitiativeChildWebhookPayload {
  __typename
  id
  url
  name
}`, { fragmentName: "DocumentContentChildWebhookPayload" });
const ExternalUserChildWebhookPayloadFragmentDoc = new TypedDocumentString(`
    fragment ExternalUserChildWebhookPayload on ExternalUserChildWebhookPayload {
  __typename
  id
  email
  name
}
    `, { fragmentName: "ExternalUserChildWebhookPayload" });
const InitiativeUpdateChildWebhookPayloadFragmentDoc = new TypedDocumentString(`
    fragment InitiativeUpdateChildWebhookPayload on InitiativeUpdateChildWebhookPayload {
  __typename
  id
  bodyData
  editedAt
  health
}
    `, { fragmentName: "InitiativeUpdateChildWebhookPayload" });
const IssueChildWebhookPayloadFragmentDoc = new TypedDocumentString(`
    fragment IssueChildWebhookPayload on IssueChildWebhookPayload {
  __typename
  id
  team {
    ...TeamChildWebhookPayload
  }
  teamId
  url
  identifier
  title
}
    fragment TeamChildWebhookPayload on TeamChildWebhookPayload {
  __typename
  id
  key
  name
}`, { fragmentName: "IssueChildWebhookPayload" });
const CommentWebhookPayloadFragmentDoc = new TypedDocumentString(`
    fragment CommentWebhookPayload on CommentWebhookPayload {
  __typename
  resolvingCommentId
  documentContentId
  id
  externalUserId
  initiativeUpdateId
  issueId
  parentId
  postId
  projectUpdateId
  userId
  resolvingUserId
  body
  botActor
  documentContent {
    ...DocumentContentChildWebhookPayload
  }
  syncedWith
  externalUser {
    ...ExternalUserChildWebhookPayload
  }
  initiativeUpdate {
    ...InitiativeUpdateChildWebhookPayload
  }
  issue {
    ...IssueChildWebhookPayload
  }
  parent {
    ...CommentChildWebhookPayload
  }
  projectUpdate {
    ...ProjectUpdateChildWebhookPayload
  }
  quotedText
  reactionData
  archivedAt
  createdAt
  updatedAt
  user {
    ...UserChildWebhookPayload
  }
  editedAt
  resolvedAt
}
    fragment CommentChildWebhookPayload on CommentChildWebhookPayload {
  __typename
  id
  documentContentId
  initiativeUpdateId
  issueId
  projectUpdateId
  userId
  body
}
fragment DocumentContentChildWebhookPayload on DocumentContentChildWebhookPayload {
  __typename
  document {
    ...DocumentChildWebhookPayload
  }
  project {
    ...ProjectChildWebhookPayload
  }
}
fragment DocumentChildWebhookPayload on DocumentChildWebhookPayload {
  __typename
  id
  initiativeId
  projectId
  initiative {
    ...InitiativeChildWebhookPayload
  }
  project {
    ...ProjectChildWebhookPayload
  }
  title
}
fragment ProjectUpdateChildWebhookPayload on ProjectUpdateChildWebhookPayload {
  __typename
  id
  userId
  body
  project {
    ...ProjectChildWebhookPayload
  }
}
fragment ProjectChildWebhookPayload on ProjectChildWebhookPayload {
  __typename
  id
  url
  name
}
fragment TeamChildWebhookPayload on TeamChildWebhookPayload {
  __typename
  id
  key
  name
}
fragment UserChildWebhookPayload on UserChildWebhookPayload {
  __typename
  id
  url
  avatarUrl
  email
  name
}
fragment ExternalUserChildWebhookPayload on ExternalUserChildWebhookPayload {
  __typename
  id
  email
  name
}
fragment InitiativeUpdateChildWebhookPayload on InitiativeUpdateChildWebhookPayload {
  __typename
  id
  bodyData
  editedAt
  health
}
fragment InitiativeChildWebhookPayload on InitiativeChildWebhookPayload {
  __typename
  id
  url
  name
}
fragment IssueChildWebhookPayload on IssueChildWebhookPayload {
  __typename
  id
  team {
    ...TeamChildWebhookPayload
  }
  teamId
  url
  identifier
  title
}`, { fragmentName: "CommentWebhookPayload" });
const AttachmentWebhookPayloadFragmentDoc = new TypedDocumentString(`
    fragment AttachmentWebhookPayload on AttachmentWebhookPayload {
  __typename
  metadata
  source
  subtitle
  creatorId
  id
  originalIssueId
  issueId
  externalUserCreatorId
  url
  sourceType
  archivedAt
  createdAt
  updatedAt
  title
  groupBySource
}
    `, { fragmentName: "AttachmentWebhookPayload" });
const CustomerChildWebhookPayloadFragmentDoc = new TypedDocumentString(`
    fragment CustomerChildWebhookPayload on CustomerChildWebhookPayload {
  __typename
  id
  domains
  externalIds
  name
}
    `, { fragmentName: "CustomerChildWebhookPayload" });
const CustomerNeedWebhookPayloadFragmentDoc = new TypedDocumentString(`
    fragment CustomerNeedWebhookPayload on CustomerNeedWebhookPayload {
  __typename
  attachmentId
  commentId
  creatorId
  customerId
  id
  issueId
  projectAttachmentId
  projectId
  attachment {
    ...AttachmentWebhookPayload
  }
  body
  customer {
    ...CustomerChildWebhookPayload
  }
  originalIssueId
  issue {
    ...IssueChildWebhookPayload
  }
  priority
  project {
    ...ProjectChildWebhookPayload
  }
  archivedAt
  createdAt
  updatedAt
}
    fragment CustomerChildWebhookPayload on CustomerChildWebhookPayload {
  __typename
  id
  domains
  externalIds
  name
}
fragment ProjectChildWebhookPayload on ProjectChildWebhookPayload {
  __typename
  id
  url
  name
}
fragment TeamChildWebhookPayload on TeamChildWebhookPayload {
  __typename
  id
  key
  name
}
fragment IssueChildWebhookPayload on IssueChildWebhookPayload {
  __typename
  id
  team {
    ...TeamChildWebhookPayload
  }
  teamId
  url
  identifier
  title
}
fragment AttachmentWebhookPayload on AttachmentWebhookPayload {
  __typename
  metadata
  source
  subtitle
  creatorId
  id
  originalIssueId
  issueId
  externalUserCreatorId
  url
  sourceType
  archivedAt
  createdAt
  updatedAt
  title
  groupBySource
}`, { fragmentName: "CustomerNeedWebhookPayload" });
const CustomerStatusChildWebhookPayloadFragmentDoc = new TypedDocumentString(`
    fragment CustomerStatusChildWebhookPayload on CustomerStatusChildWebhookPayload {
  __typename
  id
  color
  description
  displayName
  name
  type
}
    `, { fragmentName: "CustomerStatusChildWebhookPayload" });
const CustomerTierChildWebhookPayloadFragmentDoc = new TypedDocumentString(`
    fragment CustomerTierChildWebhookPayload on CustomerTierChildWebhookPayload {
  __typename
  id
  color
  description
  displayName
  name
}
    `, { fragmentName: "CustomerTierChildWebhookPayload" });
const CustomerWebhookPayloadFragmentDoc = new TypedDocumentString(`
    fragment CustomerWebhookPayload on CustomerWebhookPayload {
  __typename
  slackChannelId
  statusId
  tierId
  id
  mainSourceId
  ownerId
  url
  revenue
  approximateNeedCount
  status {
    ...CustomerStatusChildWebhookPayload
  }
  tier {
    ...CustomerTierChildWebhookPayload
  }
  logoUrl
  slugId
  domains
  externalIds
  name
  size
  archivedAt
  createdAt
  updatedAt
}
    fragment CustomerStatusChildWebhookPayload on CustomerStatusChildWebhookPayload {
  __typename
  id
  color
  description
  displayName
  name
  type
}
fragment CustomerTierChildWebhookPayload on CustomerTierChildWebhookPayload {
  __typename
  id
  color
  description
  displayName
  name
}`, { fragmentName: "CustomerWebhookPayload" });
const CycleWebhookPayloadFragmentDoc = new TypedDocumentString(`
    fragment CycleWebhookPayload on CycleWebhookPayload {
  __typename
  inheritedFromId
  id
  uncompletedIssuesUponCloseIds
  completedAt
  description
  endsAt
  name
  completedScopeHistory
  completedIssueCountHistory
  inProgressScopeHistory
  number
  startsAt
  teamId
  autoArchivedAt
  archivedAt
  createdAt
  updatedAt
  scopeHistory
  issueCountHistory
}
    `, { fragmentName: "CycleWebhookPayload" });
const DocumentWebhookPayloadFragmentDoc = new TypedDocumentString(`
    fragment DocumentWebhookPayload on DocumentWebhookPayload {
  __typename
  trashed
  id
  initiativeId
  lastAppliedTemplateId
  projectId
  resourceFolderId
  creatorId
  updatedById
  subscriberIds
  color
  content
  description
  slugId
  icon
  sortOrder
  hiddenAt
  archivedAt
  createdAt
  updatedAt
  title
}
    `, { fragmentName: "DocumentWebhookPayload" });
const ProjectLabelWebhookPayloadFragmentDoc = new TypedDocumentString(`
    fragment ProjectLabelWebhookPayload on ProjectLabelWebhookPayload {
  __typename
  id
  color
  creatorId
  description
  name
  parentId
  archivedAt
  createdAt
  updatedAt
  isGroup
}
    `, { fragmentName: "ProjectLabelWebhookPayload" });
const ProjectUpdateWebhookPayloadFragmentDoc = new TypedDocumentString(`
    fragment ProjectUpdateWebhookPayload on ProjectUpdateWebhookPayload {
  __typename
  id
  url
  bodyData
  body
  editedAt
  health
  projectId
  project {
    ...ProjectChildWebhookPayload
  }
  reactionData
  slugId
  archivedAt
  createdAt
  updatedAt
  userId
  user {
    ...UserChildWebhookPayload
  }
}
    fragment ProjectChildWebhookPayload on ProjectChildWebhookPayload {
  __typename
  id
  url
  name
}
fragment UserChildWebhookPayload on UserChildWebhookPayload {
  __typename
  id
  url
  avatarUrl
  email
  name
}`, { fragmentName: "ProjectUpdateWebhookPayload" });
const ProjectMilestoneChildWebhookPayloadFragmentDoc = new TypedDocumentString(`
    fragment ProjectMilestoneChildWebhookPayload on ProjectMilestoneChildWebhookPayload {
  __typename
  id
  name
  targetDate
}
    `, { fragmentName: "ProjectMilestoneChildWebhookPayload" });
const ProjectStatusChildWebhookPayloadFragmentDoc = new TypedDocumentString(`
    fragment ProjectStatusChildWebhookPayload on ProjectStatusChildWebhookPayload {
  __typename
  id
  color
  name
  type
}
    `, { fragmentName: "ProjectStatusChildWebhookPayload" });
const ProjectWebhookPayloadFragmentDoc = new TypedDocumentString(`
    fragment ProjectWebhookPayload on ProjectWebhookPayload {
  __typename
  labelIds
  memberIds
  teamIds
  id
  convertedFromIssueId
  lastAppliedTemplateId
  lastUpdateId
  leadId
  statusId
  creatorId
  url
  autoArchivedAt
  canceledAt
  completedAt
  content
  documentContentId
  startDate
  health
  icon
  initiatives {
    ...InitiativeChildWebhookPayload
  }
  milestones {
    ...ProjectMilestoneChildWebhookPayload
  }
  completedScopeHistory
  completedIssueCountHistory
  inProgressScopeHistory
  priority
  lead {
    ...UserChildWebhookPayload
  }
  status {
    ...ProjectStatusChildWebhookPayload
  }
  color
  description
  name
  slugId
  startDateResolution
  targetDateResolution
  prioritySortOrder
  sortOrder
  targetDate
  archivedAt
  createdAt
  updatedAt
  healthUpdatedAt
  projectUpdateRemindersPausedUntilAt
  startedAt
  scopeHistory
  issueCountHistory
  trashed
}
    fragment ProjectMilestoneChildWebhookPayload on ProjectMilestoneChildWebhookPayload {
  __typename
  id
  name
  targetDate
}
fragment ProjectStatusChildWebhookPayload on ProjectStatusChildWebhookPayload {
  __typename
  id
  color
  name
  type
}
fragment UserChildWebhookPayload on UserChildWebhookPayload {
  __typename
  id
  url
  avatarUrl
  email
  name
}
fragment InitiativeChildWebhookPayload on InitiativeChildWebhookPayload {
  __typename
  id
  url
  name
}`, { fragmentName: "ProjectWebhookPayload" });
const ReactionWebhookPayloadFragmentDoc = new TypedDocumentString(`
    fragment ReactionWebhookPayload on ReactionWebhookPayload {
  __typename
  emoji
  commentId
  id
  externalUserId
  initiativeUpdateId
  issueId
  postId
  projectUpdateId
  userId
  comment {
    ...CommentChildWebhookPayload
  }
  issue {
    ...IssueChildWebhookPayload
  }
  projectUpdate {
    ...ProjectUpdateChildWebhookPayload
  }
  archivedAt
  createdAt
  updatedAt
  user {
    ...UserChildWebhookPayload
  }
}
    fragment CommentChildWebhookPayload on CommentChildWebhookPayload {
  __typename
  id
  documentContentId
  initiativeUpdateId
  issueId
  projectUpdateId
  userId
  body
}
fragment ProjectUpdateChildWebhookPayload on ProjectUpdateChildWebhookPayload {
  __typename
  id
  userId
  body
  project {
    ...ProjectChildWebhookPayload
  }
}
fragment ProjectChildWebhookPayload on ProjectChildWebhookPayload {
  __typename
  id
  url
  name
}
fragment TeamChildWebhookPayload on TeamChildWebhookPayload {
  __typename
  id
  key
  name
}
fragment UserChildWebhookPayload on UserChildWebhookPayload {
  __typename
  id
  url
  avatarUrl
  email
  name
}
fragment IssueChildWebhookPayload on IssueChildWebhookPayload {
  __typename
  id
  team {
    ...TeamChildWebhookPayload
  }
  teamId
  url
  identifier
  title
}`, { fragmentName: "ReactionWebhookPayload" });
const IssueStatusChangedNotificationWebhookPayloadFragmentDoc = new TypedDocumentString(`
    fragment IssueStatusChangedNotificationWebhookPayload on IssueStatusChangedNotificationWebhookPayload {
  __typename
  type
  actorId
  id
  externalUserActorId
  issueId
  userId
  actor {
    ...UserChildWebhookPayload
  }
  issue {
    ...IssueWithDescriptionChildWebhookPayload
  }
  archivedAt
  createdAt
  updatedAt
}
    fragment TeamChildWebhookPayload on TeamChildWebhookPayload {
  __typename
  id
  key
  name
}
fragment UserChildWebhookPayload on UserChildWebhookPayload {
  __typename
  id
  url
  avatarUrl
  email
  name
}
fragment IssueWithDescriptionChildWebhookPayload on IssueWithDescriptionChildWebhookPayload {
  __typename
  id
  team {
    ...TeamChildWebhookPayload
  }
  teamId
  url
  description
  identifier
  title
}`, { fragmentName: "IssueStatusChangedNotificationWebhookPayload" });
const UserWebhookPayloadFragmentDoc = new TypedDocumentString(`
    fragment UserWebhookPayload on UserWebhookPayload {
  __typename
  id
  url
  avatarUrl
  description
  displayName
  email
  timezone
  name
  disableReason
  archivedAt
  createdAt
  updatedAt
  guest
  active
  admin
  app
  owner
}
    `, { fragmentName: "UserWebhookPayload" });
const GuidanceRuleWebhookPayloadFragmentDoc = new TypedDocumentString(`
    fragment GuidanceRuleWebhookPayload on GuidanceRuleWebhookPayload {
  __typename
  body
}
    `, { fragmentName: "GuidanceRuleWebhookPayload" });
const AgentActivityWebhookPayloadFragmentDoc = new TypedDocumentString(`
    fragment AgentActivityWebhookPayload on AgentActivityWebhookPayload {
  __typename
  signal
  signalMetadata
  agentSessionId
  sourceCommentId
  id
  userId
  content
  archivedAt
  createdAt
  updatedAt
}
    `, { fragmentName: "AgentActivityWebhookPayload" });
const AgentSessionWebhookPayloadFragmentDoc = new TypedDocumentString(`
    fragment AgentSessionWebhookPayload on AgentSessionWebhookPayload {
  __typename
  summary
  sourceMetadata
  appUserId
  sourceCommentId
  id
  creatorId
  issueId
  organizationId
  commentId
  status
  creator {
    ...UserChildWebhookPayload
  }
  issue {
    ...IssueWithDescriptionChildWebhookPayload
  }
  comment {
    ...CommentChildWebhookPayload
  }
  archivedAt
  createdAt
  updatedAt
  endedAt
  startedAt
  type
}
    fragment CommentChildWebhookPayload on CommentChildWebhookPayload {
  __typename
  id
  documentContentId
  initiativeUpdateId
  issueId
  projectUpdateId
  userId
  body
}
fragment TeamChildWebhookPayload on TeamChildWebhookPayload {
  __typename
  id
  key
  name
}
fragment UserChildWebhookPayload on UserChildWebhookPayload {
  __typename
  id
  url
  avatarUrl
  email
  name
}
fragment IssueWithDescriptionChildWebhookPayload on IssueWithDescriptionChildWebhookPayload {
  __typename
  id
  team {
    ...TeamChildWebhookPayload
  }
  teamId
  url
  description
  identifier
  title
}`, { fragmentName: "AgentSessionWebhookPayload" });
const AgentSessionEventWebhookPayloadFragmentDoc = new TypedDocumentString(`
    fragment AgentSessionEventWebhookPayload on AgentSessionEventWebhookPayload {
  __typename
  promptContext
  guidance {
    ...GuidanceRuleWebhookPayload
  }
  oauthClientId
  appUserId
  organizationId
  webhookId
  agentActivity {
    ...AgentActivityWebhookPayload
  }
  agentSession {
    ...AgentSessionWebhookPayload
  }
  previousComments {
    ...CommentChildWebhookPayload
  }
  createdAt
  action
  type
  webhookTimestamp
}
    fragment CommentChildWebhookPayload on CommentChildWebhookPayload {
  __typename
  id
  documentContentId
  initiativeUpdateId
  issueId
  projectUpdateId
  userId
  body
}
fragment TeamChildWebhookPayload on TeamChildWebhookPayload {
  __typename
  id
  key
  name
}
fragment UserChildWebhookPayload on UserChildWebhookPayload {
  __typename
  id
  url
  avatarUrl
  email
  name
}
fragment IssueWithDescriptionChildWebhookPayload on IssueWithDescriptionChildWebhookPayload {
  __typename
  id
  team {
    ...TeamChildWebhookPayload
  }
  teamId
  url
  description
  identifier
  title
}
fragment GuidanceRuleWebhookPayload on GuidanceRuleWebhookPayload {
  __typename
  body
}
fragment AgentActivityWebhookPayload on AgentActivityWebhookPayload {
  __typename
  signal
  signalMetadata
  agentSessionId
  sourceCommentId
  id
  userId
  content
  archivedAt
  createdAt
  updatedAt
}
fragment AgentSessionWebhookPayload on AgentSessionWebhookPayload {
  __typename
  summary
  sourceMetadata
  appUserId
  sourceCommentId
  id
  creatorId
  issueId
  organizationId
  commentId
  status
  creator {
    ...UserChildWebhookPayload
  }
  issue {
    ...IssueWithDescriptionChildWebhookPayload
  }
  comment {
    ...CommentChildWebhookPayload
  }
  archivedAt
  createdAt
  updatedAt
  endedAt
  startedAt
  type
}`, { fragmentName: "AgentSessionEventWebhookPayload" });
const AuditEntryWebhookPayloadFragmentDoc = new TypedDocumentString(`
    fragment AuditEntryWebhookPayload on AuditEntryWebhookPayload {
  __typename
  requestInformation
  metadata
  countryCode
  ip
  id
  organizationId
  actorId
  archivedAt
  createdAt
  updatedAt
  type
}
    `, { fragmentName: "AuditEntryWebhookPayload" });
const InitiativeUpdateWebhookPayloadFragmentDoc = new TypedDocumentString(`
    fragment InitiativeUpdateWebhookPayload on InitiativeUpdateWebhookPayload {
  __typename
  id
  url
  bodyData
  body
  editedAt
  health
  initiativeId
  initiative {
    ...InitiativeChildWebhookPayload
  }
  reactionData
  slugId
  archivedAt
  createdAt
  updatedAt
  userId
  user {
    ...UserChildWebhookPayload
  }
}
    fragment UserChildWebhookPayload on UserChildWebhookPayload {
  __typename
  id
  url
  avatarUrl
  email
  name
}
fragment InitiativeChildWebhookPayload on InitiativeChildWebhookPayload {
  __typename
  id
  url
  name
}`, { fragmentName: "InitiativeUpdateWebhookPayload" });
const InitiativeWebhookPayloadFragmentDoc = new TypedDocumentString(`
    fragment InitiativeWebhookPayload on InitiativeWebhookPayload {
  __typename
  id
  lastUpdateId
  organizationId
  creatorId
  ownerId
  url
  color
  status
  updateRemindersDay
  description
  updateReminderFrequencyInWeeks
  updateReminderFrequency
  health
  updateRemindersHour
  icon
  lastUpdate {
    ...InitiativeUpdateChildWebhookPayload
  }
  name
  parentInitiative {
    ...InitiativeChildWebhookPayload
  }
  projects {
    ...ProjectChildWebhookPayload
  }
  targetDateResolution
  frequencyResolution
  sortOrder
  subInitiatives {
    ...InitiativeChildWebhookPayload
  }
  targetDate
  archivedAt
  createdAt
  updatedAt
  slugId
  creator {
    ...UserChildWebhookPayload
  }
  owner {
    ...UserChildWebhookPayload
  }
  healthUpdatedAt
  completedAt
  startedAt
  trashed
}
    fragment ProjectChildWebhookPayload on ProjectChildWebhookPayload {
  __typename
  id
  url
  name
}
fragment UserChildWebhookPayload on UserChildWebhookPayload {
  __typename
  id
  url
  avatarUrl
  email
  name
}
fragment InitiativeUpdateChildWebhookPayload on InitiativeUpdateChildWebhookPayload {
  __typename
  id
  bodyData
  editedAt
  health
}
fragment InitiativeChildWebhookPayload on InitiativeChildWebhookPayload {
  __typename
  id
  url
  name
}`, { fragmentName: "InitiativeWebhookPayload" });
const IssueAssignedToYouNotificationWebhookPayloadFragmentDoc = new TypedDocumentString(`
    fragment IssueAssignedToYouNotificationWebhookPayload on IssueAssignedToYouNotificationWebhookPayload {
  __typename
  type
  actorId
  id
  externalUserActorId
  issueId
  userId
  actor {
    ...UserChildWebhookPayload
  }
  issue {
    ...IssueWithDescriptionChildWebhookPayload
  }
  archivedAt
  createdAt
  updatedAt
}
    fragment TeamChildWebhookPayload on TeamChildWebhookPayload {
  __typename
  id
  key
  name
}
fragment UserChildWebhookPayload on UserChildWebhookPayload {
  __typename
  id
  url
  avatarUrl
  email
  name
}
fragment IssueWithDescriptionChildWebhookPayload on IssueWithDescriptionChildWebhookPayload {
  __typename
  id
  team {
    ...TeamChildWebhookPayload
  }
  teamId
  url
  description
  identifier
  title
}`, { fragmentName: "IssueAssignedToYouNotificationWebhookPayload" });
const IssueCommentMentionNotificationWebhookPayloadFragmentDoc = new TypedDocumentString(`
    fragment IssueCommentMentionNotificationWebhookPayload on IssueCommentMentionNotificationWebhookPayload {
  __typename
  type
  actorId
  commentId
  id
  externalUserActorId
  issueId
  parentCommentId
  userId
  actor {
    ...UserChildWebhookPayload
  }
  comment {
    ...CommentChildWebhookPayload
  }
  issue {
    ...IssueWithDescriptionChildWebhookPayload
  }
  parentComment {
    ...CommentChildWebhookPayload
  }
  archivedAt
  createdAt
  updatedAt
}
    fragment CommentChildWebhookPayload on CommentChildWebhookPayload {
  __typename
  id
  documentContentId
  initiativeUpdateId
  issueId
  projectUpdateId
  userId
  body
}
fragment TeamChildWebhookPayload on TeamChildWebhookPayload {
  __typename
  id
  key
  name
}
fragment UserChildWebhookPayload on UserChildWebhookPayload {
  __typename
  id
  url
  avatarUrl
  email
  name
}
fragment IssueWithDescriptionChildWebhookPayload on IssueWithDescriptionChildWebhookPayload {
  __typename
  id
  team {
    ...TeamChildWebhookPayload
  }
  teamId
  url
  description
  identifier
  title
}`, { fragmentName: "IssueCommentMentionNotificationWebhookPayload" });
const IssueCommentReactionNotificationWebhookPayloadFragmentDoc = new TypedDocumentString(`
    fragment IssueCommentReactionNotificationWebhookPayload on IssueCommentReactionNotificationWebhookPayload {
  __typename
  type
  actorId
  commentId
  id
  externalUserActorId
  issueId
  parentCommentId
  userId
  actor {
    ...UserChildWebhookPayload
  }
  comment {
    ...CommentChildWebhookPayload
  }
  reactionEmoji
  issue {
    ...IssueWithDescriptionChildWebhookPayload
  }
  parentComment {
    ...CommentChildWebhookPayload
  }
  archivedAt
  createdAt
  updatedAt
}
    fragment CommentChildWebhookPayload on CommentChildWebhookPayload {
  __typename
  id
  documentContentId
  initiativeUpdateId
  issueId
  projectUpdateId
  userId
  body
}
fragment TeamChildWebhookPayload on TeamChildWebhookPayload {
  __typename
  id
  key
  name
}
fragment UserChildWebhookPayload on UserChildWebhookPayload {
  __typename
  id
  url
  avatarUrl
  email
  name
}
fragment IssueWithDescriptionChildWebhookPayload on IssueWithDescriptionChildWebhookPayload {
  __typename
  id
  team {
    ...TeamChildWebhookPayload
  }
  teamId
  url
  description
  identifier
  title
}`, { fragmentName: "IssueCommentReactionNotificationWebhookPayload" });
const IssueEmojiReactionNotificationWebhookPayloadFragmentDoc = new TypedDocumentString(`
    fragment IssueEmojiReactionNotificationWebhookPayload on IssueEmojiReactionNotificationWebhookPayload {
  __typename
  type
  actorId
  id
  externalUserActorId
  issueId
  userId
  actor {
    ...UserChildWebhookPayload
  }
  reactionEmoji
  issue {
    ...IssueWithDescriptionChildWebhookPayload
  }
  archivedAt
  createdAt
  updatedAt
}
    fragment TeamChildWebhookPayload on TeamChildWebhookPayload {
  __typename
  id
  key
  name
}
fragment UserChildWebhookPayload on UserChildWebhookPayload {
  __typename
  id
  url
  avatarUrl
  email
  name
}
fragment IssueWithDescriptionChildWebhookPayload on IssueWithDescriptionChildWebhookPayload {
  __typename
  id
  team {
    ...TeamChildWebhookPayload
  }
  teamId
  url
  description
  identifier
  title
}`, { fragmentName: "IssueEmojiReactionNotificationWebhookPayload" });
const IssueLabelWebhookPayloadFragmentDoc = new TypedDocumentString(`
    fragment IssueLabelWebhookPayload on IssueLabelWebhookPayload {
  __typename
  id
  color
  creatorId
  description
  name
  inheritedFromId
  parentId
  teamId
  archivedAt
  createdAt
  updatedAt
  isGroup
}
    `, { fragmentName: "IssueLabelWebhookPayload" });
const IssueMentionNotificationWebhookPayloadFragmentDoc = new TypedDocumentString(`
    fragment IssueMentionNotificationWebhookPayload on IssueMentionNotificationWebhookPayload {
  __typename
  type
  actorId
  id
  externalUserActorId
  issueId
  userId
  actor {
    ...UserChildWebhookPayload
  }
  issue {
    ...IssueWithDescriptionChildWebhookPayload
  }
  archivedAt
  createdAt
  updatedAt
}
    fragment TeamChildWebhookPayload on TeamChildWebhookPayload {
  __typename
  id
  key
  name
}
fragment UserChildWebhookPayload on UserChildWebhookPayload {
  __typename
  id
  url
  avatarUrl
  email
  name
}
fragment IssueWithDescriptionChildWebhookPayload on IssueWithDescriptionChildWebhookPayload {
  __typename
  id
  team {
    ...TeamChildWebhookPayload
  }
  teamId
  url
  description
  identifier
  title
}`, { fragmentName: "IssueMentionNotificationWebhookPayload" });
const IssueNewCommentNotificationWebhookPayloadFragmentDoc = new TypedDocumentString(`
    fragment IssueNewCommentNotificationWebhookPayload on IssueNewCommentNotificationWebhookPayload {
  __typename
  type
  actorId
  commentId
  id
  externalUserActorId
  issueId
  parentCommentId
  userId
  actor {
    ...UserChildWebhookPayload
  }
  comment {
    ...CommentChildWebhookPayload
  }
  issue {
    ...IssueWithDescriptionChildWebhookPayload
  }
  parentComment {
    ...CommentChildWebhookPayload
  }
  archivedAt
  createdAt
  updatedAt
}
    fragment CommentChildWebhookPayload on CommentChildWebhookPayload {
  __typename
  id
  documentContentId
  initiativeUpdateId
  issueId
  projectUpdateId
  userId
  body
}
fragment TeamChildWebhookPayload on TeamChildWebhookPayload {
  __typename
  id
  key
  name
}
fragment UserChildWebhookPayload on UserChildWebhookPayload {
  __typename
  id
  url
  avatarUrl
  email
  name
}
fragment IssueWithDescriptionChildWebhookPayload on IssueWithDescriptionChildWebhookPayload {
  __typename
  id
  team {
    ...TeamChildWebhookPayload
  }
  teamId
  url
  description
  identifier
  title
}`, { fragmentName: "IssueNewCommentNotificationWebhookPayload" });
const IssueUnassignedFromYouNotificationWebhookPayloadFragmentDoc = new TypedDocumentString(`
    fragment IssueUnassignedFromYouNotificationWebhookPayload on IssueUnassignedFromYouNotificationWebhookPayload {
  __typename
  type
  actorId
  id
  externalUserActorId
  issueId
  userId
  actor {
    ...UserChildWebhookPayload
  }
  issue {
    ...IssueWithDescriptionChildWebhookPayload
  }
  archivedAt
  createdAt
  updatedAt
}
    fragment TeamChildWebhookPayload on TeamChildWebhookPayload {
  __typename
  id
  key
  name
}
fragment UserChildWebhookPayload on UserChildWebhookPayload {
  __typename
  id
  url
  avatarUrl
  email
  name
}
fragment IssueWithDescriptionChildWebhookPayload on IssueWithDescriptionChildWebhookPayload {
  __typename
  id
  team {
    ...TeamChildWebhookPayload
  }
  teamId
  url
  description
  identifier
  title
}`, { fragmentName: "IssueUnassignedFromYouNotificationWebhookPayload" });
const AppUserNotificationWebhookPayloadFragmentDoc = new TypedDocumentString(`
    fragment AppUserNotificationWebhookPayload on AppUserNotificationWebhookPayload {
  __typename
  oauthClientId
  appUserId
  organizationId
  webhookId
  createdAt
  action
  type
  webhookTimestamp
}
    `, { fragmentName: "AppUserNotificationWebhookPayload" });
const AppUserTeamAccessChangedWebhookPayloadFragmentDoc = new TypedDocumentString(`
    fragment AppUserTeamAccessChangedWebhookPayload on AppUserTeamAccessChangedWebhookPayload {
  __typename
  oauthClientId
  appUserId
  organizationId
  addedTeamIds
  removedTeamIds
  webhookId
  createdAt
  action
  type
  webhookTimestamp
  canAccessAllPublicTeams
}
    `, { fragmentName: "AppUserTeamAccessChangedWebhookPayload" });
const CustomResourceWebhookPayloadFragmentDoc = new TypedDocumentString(`
    fragment CustomResourceWebhookPayload on CustomResourceWebhookPayload {
  __typename
  organizationId
  webhookId
  createdAt
  action
  type
  webhookTimestamp
}
    `, { fragmentName: "CustomResourceWebhookPayload" });
const EntityWebhookPayloadFragmentDoc = new TypedDocumentString(`
    fragment EntityWebhookPayload on EntityWebhookPayload {
  __typename
  organizationId
  updatedFrom
  webhookId
  createdAt
  action
  type
  url
  webhookTimestamp
}
    `, { fragmentName: "EntityWebhookPayload" });
const CycleChildWebhookPayloadFragmentDoc = new TypedDocumentString(`
    fragment CycleChildWebhookPayload on CycleChildWebhookPayload {
  __typename
  id
  endsAt
  name
  number
  startsAt
}
    `, { fragmentName: "CycleChildWebhookPayload" });
const WorkflowStateChildWebhookPayloadFragmentDoc = new TypedDocumentString(`
    fragment WorkflowStateChildWebhookPayload on WorkflowStateChildWebhookPayload {
  __typename
  id
  color
  name
  type
}
    `, { fragmentName: "WorkflowStateChildWebhookPayload" });
const IssueLabelChildWebhookPayloadFragmentDoc = new TypedDocumentString(`
    fragment IssueLabelChildWebhookPayload on IssueLabelChildWebhookPayload {
  __typename
  id
  color
  name
  parentId
}
    `, { fragmentName: "IssueLabelChildWebhookPayload" });
const IssueWebhookPayloadFragmentDoc = new TypedDocumentString(`
    fragment IssueWebhookPayload on IssueWebhookPayload {
  __typename
  trashed
  labelIds
  integrationSourceType
  previousIdentifiers
  delegateId
  cycleId
  id
  externalUserCreatorId
  stateId
  lastAppliedTemplateId
  parentId
  projectMilestoneId
  projectId
  recurringIssueTemplateId
  sourceCommentId
  teamId
  creatorId
  assigneeId
  subscriberIds
  url
  delegate {
    ...UserChildWebhookPayload
  }
  botActor
  cycle {
    ...CycleChildWebhookPayload
  }
  descriptionData
  description
  dueDate
  syncedWith
  estimate
  externalUserCreator {
    ...ExternalUserChildWebhookPayload
  }
  identifier
  state {
    ...WorkflowStateChildWebhookPayload
  }
  title
  number
  priorityLabel
  labels {
    ...IssueLabelChildWebhookPayload
  }
  prioritySortOrder
  sortOrder
  subIssueSortOrder
  priority
  projectMilestone {
    ...ProjectMilestoneChildWebhookPayload
  }
  project {
    ...ProjectChildWebhookPayload
  }
  reactionData
  team {
    ...TeamChildWebhookPayload
  }
  archivedAt
  createdAt
  updatedAt
  startedTriageAt
  addedToCycleAt
  addedToProjectAt
  addedToTeamAt
  autoArchivedAt
  autoClosedAt
  canceledAt
  completedAt
  startedAt
  triagedAt
  slaBreachesAt
  slaHighRiskAt
  slaMediumRiskAt
  slaStartedAt
  snoozedUntilAt
  slaType
  creator {
    ...UserChildWebhookPayload
  }
  assignee {
    ...UserChildWebhookPayload
  }
}
    fragment CycleChildWebhookPayload on CycleChildWebhookPayload {
  __typename
  id
  endsAt
  name
  number
  startsAt
}
fragment ProjectMilestoneChildWebhookPayload on ProjectMilestoneChildWebhookPayload {
  __typename
  id
  name
  targetDate
}
fragment ProjectChildWebhookPayload on ProjectChildWebhookPayload {
  __typename
  id
  url
  name
}
fragment TeamChildWebhookPayload on TeamChildWebhookPayload {
  __typename
  id
  key
  name
}
fragment UserChildWebhookPayload on UserChildWebhookPayload {
  __typename
  id
  url
  avatarUrl
  email
  name
}
fragment WorkflowStateChildWebhookPayload on WorkflowStateChildWebhookPayload {
  __typename
  id
  color
  name
  type
}
fragment ExternalUserChildWebhookPayload on ExternalUserChildWebhookPayload {
  __typename
  id
  email
  name
}
fragment IssueLabelChildWebhookPayload on IssueLabelChildWebhookPayload {
  __typename
  id
  color
  name
  parentId
}`, { fragmentName: "IssueWebhookPayload" });
const IssueSlaWebhookPayloadFragmentDoc = new TypedDocumentString(`
    fragment IssueSlaWebhookPayload on IssueSlaWebhookPayload {
  __typename
  organizationId
  webhookId
  issueData {
    ...IssueWebhookPayload
  }
  createdAt
  action
  type
  url
  webhookTimestamp
}
    fragment CycleChildWebhookPayload on CycleChildWebhookPayload {
  __typename
  id
  endsAt
  name
  number
  startsAt
}
fragment ProjectMilestoneChildWebhookPayload on ProjectMilestoneChildWebhookPayload {
  __typename
  id
  name
  targetDate
}
fragment ProjectChildWebhookPayload on ProjectChildWebhookPayload {
  __typename
  id
  url
  name
}
fragment TeamChildWebhookPayload on TeamChildWebhookPayload {
  __typename
  id
  key
  name
}
fragment UserChildWebhookPayload on UserChildWebhookPayload {
  __typename
  id
  url
  avatarUrl
  email
  name
}
fragment WorkflowStateChildWebhookPayload on WorkflowStateChildWebhookPayload {
  __typename
  id
  color
  name
  type
}
fragment ExternalUserChildWebhookPayload on ExternalUserChildWebhookPayload {
  __typename
  id
  email
  name
}
fragment IssueLabelChildWebhookPayload on IssueLabelChildWebhookPayload {
  __typename
  id
  color
  name
  parentId
}
fragment IssueWebhookPayload on IssueWebhookPayload {
  __typename
  trashed
  labelIds
  integrationSourceType
  previousIdentifiers
  delegateId
  cycleId
  id
  externalUserCreatorId
  stateId
  lastAppliedTemplateId
  parentId
  projectMilestoneId
  projectId
  recurringIssueTemplateId
  sourceCommentId
  teamId
  creatorId
  assigneeId
  subscriberIds
  url
  delegate {
    ...UserChildWebhookPayload
  }
  botActor
  cycle {
    ...CycleChildWebhookPayload
  }
  descriptionData
  description
  dueDate
  syncedWith
  estimate
  externalUserCreator {
    ...ExternalUserChildWebhookPayload
  }
  identifier
  state {
    ...WorkflowStateChildWebhookPayload
  }
  title
  number
  priorityLabel
  labels {
    ...IssueLabelChildWebhookPayload
  }
  prioritySortOrder
  sortOrder
  subIssueSortOrder
  priority
  projectMilestone {
    ...ProjectMilestoneChildWebhookPayload
  }
  project {
    ...ProjectChildWebhookPayload
  }
  reactionData
  team {
    ...TeamChildWebhookPayload
  }
  archivedAt
  createdAt
  updatedAt
  startedTriageAt
  addedToCycleAt
  addedToProjectAt
  addedToTeamAt
  autoArchivedAt
  autoClosedAt
  canceledAt
  completedAt
  startedAt
  triagedAt
  slaBreachesAt
  slaHighRiskAt
  slaMediumRiskAt
  slaStartedAt
  snoozedUntilAt
  slaType
  creator {
    ...UserChildWebhookPayload
  }
  assignee {
    ...UserChildWebhookPayload
  }
}`, { fragmentName: "IssueSlaWebhookPayload" });
const SemanticSearchResultFragmentDoc = new TypedDocumentString(`
    fragment SemanticSearchResult on SemanticSearchResult {
  __typename
  document {
    id
  }
  initiative {
    id
  }
  issue {
    id
  }
  project {
    id
  }
  type
  id
}
    `, { fragmentName: "SemanticSearchResult" });
const SemanticSearchPayloadFragmentDoc = new TypedDocumentString(`
    fragment SemanticSearchPayload on SemanticSearchPayload {
  __typename
  enabled
  results {
    ...SemanticSearchResult
  }
}
    fragment SemanticSearchResult on SemanticSearchResult {
  __typename
  document {
    id
  }
  initiative {
    id
  }
  issue {
    id
  }
  project {
    id
  }
  type
  id
}`, { fragmentName: "SemanticSearchPayload" });
const ApplicationFragmentDoc = new TypedDocumentString(`
    fragment Application on Application {
  __typename
  name
  imageUrl
  description
  developer
  id
  clientId
  developerUrl
}
    `, { fragmentName: "Application" });
const TeamWithParentWebhookPayloadFragmentDoc = new TypedDocumentString(`
    fragment TeamWithParentWebhookPayload on TeamWithParentWebhookPayload {
  __typename
  id
  key
  name
  parentId
  displayName
}
    `, { fragmentName: "TeamWithParentWebhookPayload" });
const TeamOriginWebhookPayloadFragmentDoc = new TypedDocumentString(`
    fragment TeamOriginWebhookPayload on TeamOriginWebhookPayload {
  __typename
  team {
    ...TeamWithParentWebhookPayload
  }
  type
}
    fragment TeamWithParentWebhookPayload on TeamWithParentWebhookPayload {
  __typename
  id
  key
  name
  parentId
  displayName
}`, { fragmentName: "TeamOriginWebhookPayload" });
const IntegrationsSettingsFragmentDoc = new TypedDocumentString(`
    fragment IntegrationsSettings on IntegrationsSettings {
  __typename
  initiative {
    id
  }
  project {
    id
  }
  team {
    id
  }
  updatedAt
  archivedAt
  createdAt
  contextViewType
  id
  slackIssueNewComment
  slackInitiativeUpdateCreated
  slackIssueAddedToTriage
  slackIssueCreated
  slackProjectUpdateCreated
  slackIssueSlaHighRisk
  slackIssueSlaBreached
  slackIssueAddedToView
  slackIssueStatusChangedDone
  slackIssueStatusChangedAll
  slackProjectUpdateCreatedToTeam
  slackProjectUpdateCreatedToWorkspace
}
    `, { fragmentName: "IntegrationsSettings" });
const InitiativePayloadFragmentDoc = new TypedDocumentString(`
    fragment InitiativePayload on InitiativePayload {
  __typename
  lastSyncId
  initiative {
    id
  }
  success
}
    `, { fragmentName: "InitiativePayload" });
const InitiativeToProjectPayloadFragmentDoc = new TypedDocumentString(`
    fragment InitiativeToProjectPayload on InitiativeToProjectPayload {
  __typename
  lastSyncId
  initiativeToProject {
    id
  }
  success
}
    `, { fragmentName: "InitiativeToProjectPayload" });
const NotificationDeliveryPreferencesDayFragmentDoc = new TypedDocumentString(`
    fragment NotificationDeliveryPreferencesDay on NotificationDeliveryPreferencesDay {
  __typename
  end
  start
}
    `, { fragmentName: "NotificationDeliveryPreferencesDay" });
const NotificationDeliveryPreferencesScheduleFragmentDoc = new TypedDocumentString(`
    fragment NotificationDeliveryPreferencesSchedule on NotificationDeliveryPreferencesSchedule {
  __typename
  friday {
    ...NotificationDeliveryPreferencesDay
  }
  monday {
    ...NotificationDeliveryPreferencesDay
  }
  saturday {
    ...NotificationDeliveryPreferencesDay
  }
  sunday {
    ...NotificationDeliveryPreferencesDay
  }
  thursday {
    ...NotificationDeliveryPreferencesDay
  }
  tuesday {
    ...NotificationDeliveryPreferencesDay
  }
  wednesday {
    ...NotificationDeliveryPreferencesDay
  }
  disabled
}
    fragment NotificationDeliveryPreferencesDay on NotificationDeliveryPreferencesDay {
  __typename
  end
  start
}`, { fragmentName: "NotificationDeliveryPreferencesSchedule" });
const NotificationDeliveryPreferencesChannelFragmentDoc = new TypedDocumentString(`
    fragment NotificationDeliveryPreferencesChannel on NotificationDeliveryPreferencesChannel {
  __typename
  schedule {
    ...NotificationDeliveryPreferencesSchedule
  }
  notificationsDisabled
}
    fragment NotificationDeliveryPreferencesDay on NotificationDeliveryPreferencesDay {
  __typename
  end
  start
}
fragment NotificationDeliveryPreferencesSchedule on NotificationDeliveryPreferencesSchedule {
  __typename
  friday {
    ...NotificationDeliveryPreferencesDay
  }
  monday {
    ...NotificationDeliveryPreferencesDay
  }
  saturday {
    ...NotificationDeliveryPreferencesDay
  }
  sunday {
    ...NotificationDeliveryPreferencesDay
  }
  thursday {
    ...NotificationDeliveryPreferencesDay
  }
  tuesday {
    ...NotificationDeliveryPreferencesDay
  }
  wednesday {
    ...NotificationDeliveryPreferencesDay
  }
  disabled
}`, { fragmentName: "NotificationDeliveryPreferencesChannel" });
const NotificationDeliveryPreferencesFragmentDoc = new TypedDocumentString(`
    fragment NotificationDeliveryPreferences on NotificationDeliveryPreferences {
  __typename
  mobile {
    ...NotificationDeliveryPreferencesChannel
  }
}
    fragment NotificationDeliveryPreferencesChannel on NotificationDeliveryPreferencesChannel {
  __typename
  schedule {
    ...NotificationDeliveryPreferencesSchedule
  }
  notificationsDisabled
}
fragment NotificationDeliveryPreferencesDay on NotificationDeliveryPreferencesDay {
  __typename
  end
  start
}
fragment NotificationDeliveryPreferencesSchedule on NotificationDeliveryPreferencesSchedule {
  __typename
  friday {
    ...NotificationDeliveryPreferencesDay
  }
  monday {
    ...NotificationDeliveryPreferencesDay
  }
  saturday {
    ...NotificationDeliveryPreferencesDay
  }
  sunday {
    ...NotificationDeliveryPreferencesDay
  }
  thursday {
    ...NotificationDeliveryPreferencesDay
  }
  tuesday {
    ...NotificationDeliveryPreferencesDay
  }
  wednesday {
    ...NotificationDeliveryPreferencesDay
  }
  disabled
}`, { fragmentName: "NotificationDeliveryPreferences" });
const NotificationChannelPreferencesFragmentDoc = new TypedDocumentString(`
    fragment NotificationChannelPreferences on NotificationChannelPreferences {
  __typename
  slack
  desktop
  email
  mobile
}
    `, { fragmentName: "NotificationChannelPreferences" });
const NotificationCategoryPreferencesFragmentDoc = new TypedDocumentString(`
    fragment NotificationCategoryPreferences on NotificationCategoryPreferences {
  __typename
  customers {
    ...NotificationChannelPreferences
  }
  feed {
    ...NotificationChannelPreferences
  }
  appsAndIntegrations {
    ...NotificationChannelPreferences
  }
  assignments {
    ...NotificationChannelPreferences
  }
  commentsAndReplies {
    ...NotificationChannelPreferences
  }
  documentChanges {
    ...NotificationChannelPreferences
  }
  mentions {
    ...NotificationChannelPreferences
  }
  postsAndUpdates {
    ...NotificationChannelPreferences
  }
  reactions {
    ...NotificationChannelPreferences
  }
  reminders {
    ...NotificationChannelPreferences
  }
  reviews {
    ...NotificationChannelPreferences
  }
  statusChanges {
    ...NotificationChannelPreferences
  }
  subscriptions {
    ...NotificationChannelPreferences
  }
  system {
    ...NotificationChannelPreferences
  }
  triage {
    ...NotificationChannelPreferences
  }
}
    fragment NotificationChannelPreferences on NotificationChannelPreferences {
  __typename
  slack
  desktop
  email
  mobile
}`, { fragmentName: "NotificationCategoryPreferences" });
const UserSettingsCustomSidebarThemeFragmentDoc = new TypedDocumentString(`
    fragment UserSettingsCustomSidebarTheme on UserSettingsCustomSidebarTheme {
  __typename
  accent
  base
  contrast
}
    `, { fragmentName: "UserSettingsCustomSidebarTheme" });
const UserSettingsCustomThemeFragmentDoc = new TypedDocumentString(`
    fragment UserSettingsCustomTheme on UserSettingsCustomTheme {
  __typename
  sidebar {
    ...UserSettingsCustomSidebarTheme
  }
  accent
  base
  contrast
}
    fragment UserSettingsCustomSidebarTheme on UserSettingsCustomSidebarTheme {
  __typename
  accent
  base
  contrast
}`, { fragmentName: "UserSettingsCustomTheme" });
const UserSettingsThemeFragmentDoc = new TypedDocumentString(`
    fragment UserSettingsTheme on UserSettingsTheme {
  __typename
  custom {
    ...UserSettingsCustomTheme
  }
  preset
}
    fragment UserSettingsCustomSidebarTheme on UserSettingsCustomSidebarTheme {
  __typename
  accent
  base
  contrast
}
fragment UserSettingsCustomTheme on UserSettingsCustomTheme {
  __typename
  sidebar {
    ...UserSettingsCustomSidebarTheme
  }
  accent
  base
  contrast
}`, { fragmentName: "UserSettingsTheme" });
const UserSettingsFragmentDoc = new TypedDocumentString(`
    fragment UserSettings on UserSettings {
  __typename
  calendarHash
  unsubscribedFrom
  updatedAt
  notificationDeliveryPreferences {
    ...NotificationDeliveryPreferences
  }
  archivedAt
  createdAt
  id
  user {
    id
  }
  feedSummarySchedule
  feedLastSeenTime
  notificationCategoryPreferences {
    ...NotificationCategoryPreferences
  }
  notificationChannelPreferences {
    ...NotificationChannelPreferences
  }
  theme {
    ...UserSettingsTheme
  }
  subscribedToDPA
  subscribedToChangelog
  subscribedToInviteAccepted
  subscribedToPrivacyLegalUpdates
  autoAssignToSelf
  showFullUserNames
}
    fragment NotificationCategoryPreferences on NotificationCategoryPreferences {
  __typename
  customers {
    ...NotificationChannelPreferences
  }
  feed {
    ...NotificationChannelPreferences
  }
  appsAndIntegrations {
    ...NotificationChannelPreferences
  }
  assignments {
    ...NotificationChannelPreferences
  }
  commentsAndReplies {
    ...NotificationChannelPreferences
  }
  documentChanges {
    ...NotificationChannelPreferences
  }
  mentions {
    ...NotificationChannelPreferences
  }
  postsAndUpdates {
    ...NotificationChannelPreferences
  }
  reactions {
    ...NotificationChannelPreferences
  }
  reminders {
    ...NotificationChannelPreferences
  }
  reviews {
    ...NotificationChannelPreferences
  }
  statusChanges {
    ...NotificationChannelPreferences
  }
  subscriptions {
    ...NotificationChannelPreferences
  }
  system {
    ...NotificationChannelPreferences
  }
  triage {
    ...NotificationChannelPreferences
  }
}
fragment NotificationChannelPreferences on NotificationChannelPreferences {
  __typename
  slack
  desktop
  email
  mobile
}
fragment NotificationDeliveryPreferences on NotificationDeliveryPreferences {
  __typename
  mobile {
    ...NotificationDeliveryPreferencesChannel
  }
}
fragment NotificationDeliveryPreferencesChannel on NotificationDeliveryPreferencesChannel {
  __typename
  schedule {
    ...NotificationDeliveryPreferencesSchedule
  }
  notificationsDisabled
}
fragment NotificationDeliveryPreferencesDay on NotificationDeliveryPreferencesDay {
  __typename
  end
  start
}
fragment NotificationDeliveryPreferencesSchedule on NotificationDeliveryPreferencesSchedule {
  __typename
  friday {
    ...NotificationDeliveryPreferencesDay
  }
  monday {
    ...NotificationDeliveryPreferencesDay
  }
  saturday {
    ...NotificationDeliveryPreferencesDay
  }
  sunday {
    ...NotificationDeliveryPreferencesDay
  }
  thursday {
    ...NotificationDeliveryPreferencesDay
  }
  tuesday {
    ...NotificationDeliveryPreferencesDay
  }
  wednesday {
    ...NotificationDeliveryPreferencesDay
  }
  disabled
}
fragment UserSettingsCustomSidebarTheme on UserSettingsCustomSidebarTheme {
  __typename
  accent
  base
  contrast
}
fragment UserSettingsCustomTheme on UserSettingsCustomTheme {
  __typename
  sidebar {
    ...UserSettingsCustomSidebarTheme
  }
  accent
  base
  contrast
}
fragment UserSettingsTheme on UserSettingsTheme {
  __typename
  custom {
    ...UserSettingsCustomTheme
  }
  preset
}`, { fragmentName: "UserSettings" });
const UserActorWebhookPayloadFragmentDoc = new TypedDocumentString(`
    fragment UserActorWebhookPayload on UserActorWebhookPayload {
  __typename
  id
  url
  avatarUrl
  email
  name
  type
}
    `, { fragmentName: "UserActorWebhookPayload" });
const IssueImportJqlCheckPayloadFragmentDoc = new TypedDocumentString(`
    fragment IssueImportJqlCheckPayload on IssueImportJqlCheckPayload {
  __typename
  error
  count
  success
}
    `, { fragmentName: "IssueImportJqlCheckPayload" });
const IssueImportSyncCheckPayloadFragmentDoc = new TypedDocumentString(`
    fragment IssueImportSyncCheckPayload on IssueImportSyncCheckPayload {
  __typename
  error
  canSync
}
    `, { fragmentName: "IssueImportSyncCheckPayload" });
const AgentActivityActionContentFragmentDoc = new TypedDocumentString(`
    fragment AgentActivityActionContent on AgentActivityActionContent {
  __typename
  action
  parameter
  result
  type
}
    `, { fragmentName: "AgentActivityActionContent" });
const AgentActivityElicitationContentFragmentDoc = new TypedDocumentString(`
    fragment AgentActivityElicitationContent on AgentActivityElicitationContent {
  __typename
  body
  type
}
    `, { fragmentName: "AgentActivityElicitationContent" });
const AgentActivityErrorContentFragmentDoc = new TypedDocumentString(`
    fragment AgentActivityErrorContent on AgentActivityErrorContent {
  __typename
  body
  type
}
    `, { fragmentName: "AgentActivityErrorContent" });
const AgentActivityPromptContentFragmentDoc = new TypedDocumentString(`
    fragment AgentActivityPromptContent on AgentActivityPromptContent {
  __typename
  body
  type
}
    `, { fragmentName: "AgentActivityPromptContent" });
const AgentActivityResponseContentFragmentDoc = new TypedDocumentString(`
    fragment AgentActivityResponseContent on AgentActivityResponseContent {
  __typename
  body
  type
}
    `, { fragmentName: "AgentActivityResponseContent" });
const AgentActivityThoughtContentFragmentDoc = new TypedDocumentString(`
    fragment AgentActivityThoughtContent on AgentActivityThoughtContent {
  __typename
  body
  type
}
    `, { fragmentName: "AgentActivityThoughtContent" });
const AgentActivityFragmentDoc = new TypedDocumentString(`
    fragment AgentActivity on AgentActivity {
  __typename
  signal
  sourceMetadata
  signalMetadata
  agentSession {
    id
  }
  sourceComment {
    id
  }
  content {
    ... on AgentActivityActionContent {
      ...AgentActivityActionContent
    }
    ... on AgentActivityElicitationContent {
      ...AgentActivityElicitationContent
    }
    ... on AgentActivityErrorContent {
      ...AgentActivityErrorContent
    }
    ... on AgentActivityPromptContent {
      ...AgentActivityPromptContent
    }
    ... on AgentActivityResponseContent {
      ...AgentActivityResponseContent
    }
    ... on AgentActivityThoughtContent {
      ...AgentActivityThoughtContent
    }
  }
  updatedAt
  archivedAt
  createdAt
  id
  user {
    id
  }
  ephemeral
}
    fragment AgentActivityPromptContent on AgentActivityPromptContent {
  __typename
  body
  type
}
fragment AgentActivityResponseContent on AgentActivityResponseContent {
  __typename
  body
  type
}
fragment AgentActivityThoughtContent on AgentActivityThoughtContent {
  __typename
  body
  type
}
fragment AgentActivityActionContent on AgentActivityActionContent {
  __typename
  action
  parameter
  result
  type
}
fragment AgentActivityElicitationContent on AgentActivityElicitationContent {
  __typename
  body
  type
}
fragment AgentActivityErrorContent on AgentActivityErrorContent {
  __typename
  body
  type
}`, { fragmentName: "AgentActivity" });
const PageInfoFragmentDoc = new TypedDocumentString(`
    fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}
    `, { fragmentName: "PageInfo" });
const AgentActivityConnectionFragmentDoc = new TypedDocumentString(`
    fragment AgentActivityConnection on AgentActivityConnection {
  __typename
  nodes {
    ...AgentActivity
  }
  pageInfo {
    ...PageInfo
  }
}
    fragment AgentActivity on AgentActivity {
  __typename
  signal
  sourceMetadata
  signalMetadata
  agentSession {
    id
  }
  sourceComment {
    id
  }
  content {
    ... on AgentActivityActionContent {
      ...AgentActivityActionContent
    }
    ... on AgentActivityElicitationContent {
      ...AgentActivityElicitationContent
    }
    ... on AgentActivityErrorContent {
      ...AgentActivityErrorContent
    }
    ... on AgentActivityPromptContent {
      ...AgentActivityPromptContent
    }
    ... on AgentActivityResponseContent {
      ...AgentActivityResponseContent
    }
    ... on AgentActivityThoughtContent {
      ...AgentActivityThoughtContent
    }
  }
  updatedAt
  archivedAt
  createdAt
  id
  user {
    id
  }
  ephemeral
}
fragment AgentActivityPromptContent on AgentActivityPromptContent {
  __typename
  body
  type
}
fragment AgentActivityResponseContent on AgentActivityResponseContent {
  __typename
  body
  type
}
fragment AgentActivityThoughtContent on AgentActivityThoughtContent {
  __typename
  body
  type
}
fragment AgentActivityActionContent on AgentActivityActionContent {
  __typename
  action
  parameter
  result
  type
}
fragment AgentActivityElicitationContent on AgentActivityElicitationContent {
  __typename
  body
  type
}
fragment AgentActivityErrorContent on AgentActivityErrorContent {
  __typename
  body
  type
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`, { fragmentName: "AgentActivityConnection" });
const AgentActivityPayloadFragmentDoc = new TypedDocumentString(`
    fragment AgentActivityPayload on AgentActivityPayload {
  __typename
  agentActivity {
    id
  }
  lastSyncId
  success
}
    `, { fragmentName: "AgentActivityPayload" });
const AgentSessionFragmentDoc = new TypedDocumentString(`
    fragment AgentSession on AgentSession {
  __typename
  plan
  summary
  sourceMetadata
  context
  externalLink
  appUser {
    id
  }
  sourceComment {
    id
  }
  comment {
    id
  }
  status
  creator {
    id
  }
  issue {
    id
  }
  updatedAt
  archivedAt
  createdAt
  endedAt
  startedAt
  dismissedAt
  id
  dismissedBy {
    id
  }
  externalUrls
  type
}
    `, { fragmentName: "AgentSession" });
const AgentSessionConnectionFragmentDoc = new TypedDocumentString(`
    fragment AgentSessionConnection on AgentSessionConnection {
  __typename
  nodes {
    ...AgentSession
  }
  pageInfo {
    ...PageInfo
  }
}
    fragment AgentSession on AgentSession {
  __typename
  plan
  summary
  sourceMetadata
  context
  externalLink
  appUser {
    id
  }
  sourceComment {
    id
  }
  comment {
    id
  }
  status
  creator {
    id
  }
  issue {
    id
  }
  updatedAt
  archivedAt
  createdAt
  endedAt
  startedAt
  dismissedAt
  id
  dismissedBy {
    id
  }
  externalUrls
  type
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`, { fragmentName: "AgentSessionConnection" });
const AgentSessionPayloadFragmentDoc = new TypedDocumentString(`
    fragment AgentSessionPayload on AgentSessionPayload {
  __typename
  agentSession {
    id
  }
  lastSyncId
  success
}
    `, { fragmentName: "AgentSessionPayload" });
const AgentSessionToPullRequestFragmentDoc = new TypedDocumentString(`
    fragment AgentSessionToPullRequest on AgentSessionToPullRequest {
  __typename
  agentSession {
    id
  }
  updatedAt
  archivedAt
  createdAt
  id
}
    `, { fragmentName: "AgentSessionToPullRequest" });
const AgentSessionToPullRequestConnectionFragmentDoc = new TypedDocumentString(`
    fragment AgentSessionToPullRequestConnection on AgentSessionToPullRequestConnection {
  __typename
  nodes {
    ...AgentSessionToPullRequest
  }
  pageInfo {
    ...PageInfo
  }
}
    fragment AgentSessionToPullRequest on AgentSessionToPullRequest {
  __typename
  agentSession {
    id
  }
  updatedAt
  archivedAt
  createdAt
  id
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`, { fragmentName: "AgentSessionToPullRequestConnection" });
const SlackAsksTeamSettingsFragmentDoc = new TypedDocumentString(`
    fragment SlackAsksTeamSettings on SlackAsksTeamSettings {
  __typename
  id
  hasDefaultAsk
}
    `, { fragmentName: "SlackAsksTeamSettings" });
const SlackChannelNameMappingFragmentDoc = new TypedDocumentString(`
    fragment SlackChannelNameMapping on SlackChannelNameMapping {
  __typename
  id
  name
  autoCreateTemplateId
  autoCreateOnBotMention
  postCancellationUpdates
  postCompletionUpdates
  postAcceptedFromTriageUpdates
  botAdded
  isPrivate
  isShared
  aiTitles
  autoCreateOnMessage
  autoCreateOnEmoji
  teams {
    ...SlackAsksTeamSettings
  }
}
    fragment SlackAsksTeamSettings on SlackAsksTeamSettings {
  __typename
  id
  hasDefaultAsk
}`, { fragmentName: "SlackChannelNameMapping" });
const AsksChannelConnectPayloadFragmentDoc = new TypedDocumentString(`
    fragment AsksChannelConnectPayload on AsksChannelConnectPayload {
  __typename
  lastSyncId
  integration {
    id
  }
  mapping {
    ...SlackChannelNameMapping
  }
  addBot
  success
}
    fragment SlackChannelNameMapping on SlackChannelNameMapping {
  __typename
  id
  name
  autoCreateTemplateId
  autoCreateOnBotMention
  postCancellationUpdates
  postCompletionUpdates
  postAcceptedFromTriageUpdates
  botAdded
  isPrivate
  isShared
  aiTitles
  autoCreateOnMessage
  autoCreateOnEmoji
  teams {
    ...SlackAsksTeamSettings
  }
}
fragment SlackAsksTeamSettings on SlackAsksTeamSettings {
  __typename
  id
  hasDefaultAsk
}`, { fragmentName: "AsksChannelConnectPayload" });
const AsksWebFormsAuthResponseFragmentDoc = new TypedDocumentString(`
    fragment AsksWebFormsAuthResponse on AsksWebFormsAuthResponse {
  __typename
  name
  email
}
    `, { fragmentName: "AsksWebFormsAuthResponse" });
const AttachmentFragmentDoc = new TypedDocumentString(`
    fragment Attachment on Attachment {
  __typename
  sourceType
  subtitle
  title
  metadata
  groupBySource
  source
  url
  bodyData
  creator {
    id
  }
  issue {
    id
  }
  originalIssue {
    id
  }
  updatedAt
  externalUserCreator {
    id
  }
  archivedAt
  createdAt
  id
}
    `, { fragmentName: "Attachment" });
const AttachmentConnectionFragmentDoc = new TypedDocumentString(`
    fragment AttachmentConnection on AttachmentConnection {
  __typename
  nodes {
    ...Attachment
  }
  pageInfo {
    ...PageInfo
  }
}
    fragment Attachment on Attachment {
  __typename
  sourceType
  subtitle
  title
  metadata
  groupBySource
  source
  url
  bodyData
  creator {
    id
  }
  issue {
    id
  }
  originalIssue {
    id
  }
  updatedAt
  externalUserCreator {
    id
  }
  archivedAt
  createdAt
  id
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`, { fragmentName: "AttachmentConnection" });
const AttachmentPayloadFragmentDoc = new TypedDocumentString(`
    fragment AttachmentPayload on AttachmentPayload {
  __typename
  lastSyncId
  attachment {
    id
  }
  success
}
    `, { fragmentName: "AttachmentPayload" });
const AttachmentSourcesPayloadFragmentDoc = new TypedDocumentString(`
    fragment AttachmentSourcesPayload on AttachmentSourcesPayload {
  __typename
  sources
}
    `, { fragmentName: "AttachmentSourcesPayload" });
const AuditEntryFragmentDoc = new TypedDocumentString(`
    fragment AuditEntry on AuditEntry {
  __typename
  requestInformation
  metadata
  countryCode
  ip
  actorId
  updatedAt
  archivedAt
  createdAt
  id
  actor {
    id
  }
  type
}
    `, { fragmentName: "AuditEntry" });
const AuditEntryConnectionFragmentDoc = new TypedDocumentString(`
    fragment AuditEntryConnection on AuditEntryConnection {
  __typename
  nodes {
    ...AuditEntry
  }
  pageInfo {
    ...PageInfo
  }
}
    fragment AuditEntry on AuditEntry {
  __typename
  requestInformation
  metadata
  countryCode
  ip
  actorId
  updatedAt
  archivedAt
  createdAt
  id
  actor {
    id
  }
  type
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`, { fragmentName: "AuditEntryConnection" });
const AuditEntryTypeFragmentDoc = new TypedDocumentString(`
    fragment AuditEntryType on AuditEntryType {
  __typename
  description
  type
}
    `, { fragmentName: "AuditEntryType" });
const AuthOrganizationFragmentDoc = new TypedDocumentString(`
    fragment AuthOrganization on AuthOrganization {
  __typename
  allowedAuthServices
  previousUrlKeys
  serviceId
  releaseChannel
  logoUrl
  name
  urlKey
  region
  deletionRequestedAt
  createdAt
  id
  samlEnabled
  scimEnabled
  enabled
  userCount
}
    `, { fragmentName: "AuthOrganization" });
const AuthUserFragmentDoc = new TypedDocumentString(`
    fragment AuthUser on AuthUser {
  __typename
  avatarUrl
  organization {
    ...AuthOrganization
  }
  createdAt
  displayName
  email
  name
  userAccountId
  active
  role
  id
}
    fragment AuthOrganization on AuthOrganization {
  __typename
  allowedAuthServices
  previousUrlKeys
  serviceId
  releaseChannel
  logoUrl
  name
  urlKey
  region
  deletionRequestedAt
  createdAt
  id
  samlEnabled
  scimEnabled
  enabled
  userCount
}`, { fragmentName: "AuthUser" });
const AuthResolverResponseFragmentDoc = new TypedDocumentString(`
    fragment AuthResolverResponse on AuthResolverResponse {
  __typename
  token
  email
  lastUsedOrganizationId
  users {
    ...AuthUser
  }
  lockedUsers {
    ...AuthUser
  }
  lockedOrganizations {
    ...AuthOrganization
  }
  availableOrganizations {
    ...AuthOrganization
  }
  allowDomainAccess
  id
}
    fragment AuthUser on AuthUser {
  __typename
  avatarUrl
  organization {
    ...AuthOrganization
  }
  createdAt
  displayName
  email
  name
  userAccountId
  active
  role
  id
}
fragment AuthOrganization on AuthOrganization {
  __typename
  allowedAuthServices
  previousUrlKeys
  serviceId
  releaseChannel
  logoUrl
  name
  urlKey
  region
  deletionRequestedAt
  createdAt
  id
  samlEnabled
  scimEnabled
  enabled
  userCount
}`, { fragmentName: "AuthResolverResponse" });
const ReactionFragmentDoc = new TypedDocumentString(`
    fragment Reaction on Reaction {
  __typename
  emoji
  comment {
    id
  }
  externalUser {
    id
  }
  initiativeUpdate {
    id
  }
  issue {
    id
  }
  updatedAt
  projectUpdate {
    id
  }
  archivedAt
  createdAt
  id
  user {
    id
  }
}
    `, { fragmentName: "Reaction" });
const AiPromptRulesFragmentDoc = new TypedDocumentString(`
    fragment AiPromptRules on AiPromptRules {
  __typename
  updatedAt
  archivedAt
  createdAt
  id
  updatedBy {
    id
  }
}
    `, { fragmentName: "AiPromptRules" });
const DocumentContentFragmentDoc = new TypedDocumentString(`
    fragment DocumentContent on DocumentContent {
  __typename
  aiPromptRules {
    ...AiPromptRules
  }
  content
  contentState
  document {
    id
  }
  initiative {
    id
  }
  issue {
    id
  }
  updatedAt
  projectMilestone {
    id
  }
  project {
    id
  }
  restoredAt
  archivedAt
  createdAt
  id
}
    fragment AiPromptRules on AiPromptRules {
  __typename
  updatedAt
  archivedAt
  createdAt
  id
  updatedBy {
    id
  }
}`, { fragmentName: "DocumentContent" });
const ExternalEntityInfoGithubMetadataFragmentDoc = new TypedDocumentString(`
    fragment ExternalEntityInfoGithubMetadata on ExternalEntityInfoGithubMetadata {
  __typename
  number
  owner
  repo
}
    `, { fragmentName: "ExternalEntityInfoGithubMetadata" });
const ExternalEntityInfoJiraMetadataFragmentDoc = new TypedDocumentString(`
    fragment ExternalEntityInfoJiraMetadata on ExternalEntityInfoJiraMetadata {
  __typename
  issueTypeId
  projectId
  issueKey
}
    `, { fragmentName: "ExternalEntityInfoJiraMetadata" });
const ExternalEntitySlackMetadataFragmentDoc = new TypedDocumentString(`
    fragment ExternalEntitySlackMetadata on ExternalEntitySlackMetadata {
  __typename
  messageUrl
  channelId
  channelName
  isFromSlack
}
    `, { fragmentName: "ExternalEntitySlackMetadata" });
const ExternalEntityInfoFragmentDoc = new TypedDocumentString(`
    fragment ExternalEntityInfo on ExternalEntityInfo {
  __typename
  metadata {
    ... on ExternalEntityInfoGithubMetadata {
      ...ExternalEntityInfoGithubMetadata
    }
    ... on ExternalEntityInfoJiraMetadata {
      ...ExternalEntityInfoJiraMetadata
    }
    ... on ExternalEntitySlackMetadata {
      ...ExternalEntitySlackMetadata
    }
  }
  id
  service
}
    fragment ExternalEntityInfoGithubMetadata on ExternalEntityInfoGithubMetadata {
  __typename
  number
  owner
  repo
}
fragment ExternalEntityInfoJiraMetadata on ExternalEntityInfoJiraMetadata {
  __typename
  issueTypeId
  projectId
  issueKey
}
fragment ExternalEntitySlackMetadata on ExternalEntitySlackMetadata {
  __typename
  messageUrl
  channelId
  channelName
  isFromSlack
}`, { fragmentName: "ExternalEntityInfo" });
const SyncedExternalThreadFragmentDoc = new TypedDocumentString(`
    fragment SyncedExternalThread on SyncedExternalThread {
  __typename
  name
  displayName
  url
  subType
  type
  isPersonalIntegrationRequired
  isPersonalIntegrationConnected
  isConnected
  id
}
    `, { fragmentName: "SyncedExternalThread" });
const CommentFragmentDoc = new TypedDocumentString(`
    fragment Comment on Comment {
  __typename
  agentSession {
    id
  }
  url
  reactionData
  reactions {
    ...Reaction
  }
  resolvingCommentId
  documentContentId
  initiativeUpdateId
  issueId
  parentId
  projectUpdateId
  botActor {
    ...ActorBot
  }
  body
  resolvingComment {
    id
  }
  documentContent {
    ...DocumentContent
  }
  syncedWith {
    ...ExternalEntityInfo
  }
  externalThread {
    ...SyncedExternalThread
  }
  externalUser {
    id
  }
  initiativeUpdate {
    id
  }
  issue {
    id
  }
  updatedAt
  parent {
    id
  }
  projectUpdate {
    id
  }
  quotedText
  archivedAt
  createdAt
  resolvedAt
  editedAt
  id
  resolvingUser {
    id
  }
  user {
    id
  }
}
    fragment ActorBot on ActorBot {
  __typename
  avatarUrl
  name
  userDisplayName
  subType
  type
  id
}
fragment SyncedExternalThread on SyncedExternalThread {
  __typename
  name
  displayName
  url
  subType
  type
  isPersonalIntegrationRequired
  isPersonalIntegrationConnected
  isConnected
  id
}
fragment DocumentContent on DocumentContent {
  __typename
  aiPromptRules {
    ...AiPromptRules
  }
  content
  contentState
  document {
    id
  }
  initiative {
    id
  }
  issue {
    id
  }
  updatedAt
  projectMilestone {
    id
  }
  project {
    id
  }
  restoredAt
  archivedAt
  createdAt
  id
}
fragment Reaction on Reaction {
  __typename
  emoji
  comment {
    id
  }
  externalUser {
    id
  }
  initiativeUpdate {
    id
  }
  issue {
    id
  }
  updatedAt
  projectUpdate {
    id
  }
  archivedAt
  createdAt
  id
  user {
    id
  }
}
fragment AiPromptRules on AiPromptRules {
  __typename
  updatedAt
  archivedAt
  createdAt
  id
  updatedBy {
    id
  }
}
fragment ExternalEntityInfo on ExternalEntityInfo {
  __typename
  metadata {
    ... on ExternalEntityInfoGithubMetadata {
      ...ExternalEntityInfoGithubMetadata
    }
    ... on ExternalEntityInfoJiraMetadata {
      ...ExternalEntityInfoJiraMetadata
    }
    ... on ExternalEntitySlackMetadata {
      ...ExternalEntitySlackMetadata
    }
  }
  id
  service
}
fragment ExternalEntityInfoGithubMetadata on ExternalEntityInfoGithubMetadata {
  __typename
  number
  owner
  repo
}
fragment ExternalEntityInfoJiraMetadata on ExternalEntityInfoJiraMetadata {
  __typename
  issueTypeId
  projectId
  issueKey
}
fragment ExternalEntitySlackMetadata on ExternalEntitySlackMetadata {
  __typename
  messageUrl
  channelId
  channelName
  isFromSlack
}`, { fragmentName: "Comment" });
const CommentConnectionFragmentDoc = new TypedDocumentString(`
    fragment CommentConnection on CommentConnection {
  __typename
  nodes {
    ...Comment
  }
  pageInfo {
    ...PageInfo
  }
}
    fragment ActorBot on ActorBot {
  __typename
  avatarUrl
  name
  userDisplayName
  subType
  type
  id
}
fragment Comment on Comment {
  __typename
  agentSession {
    id
  }
  url
  reactionData
  reactions {
    ...Reaction
  }
  resolvingCommentId
  documentContentId
  initiativeUpdateId
  issueId
  parentId
  projectUpdateId
  botActor {
    ...ActorBot
  }
  body
  resolvingComment {
    id
  }
  documentContent {
    ...DocumentContent
  }
  syncedWith {
    ...ExternalEntityInfo
  }
  externalThread {
    ...SyncedExternalThread
  }
  externalUser {
    id
  }
  initiativeUpdate {
    id
  }
  issue {
    id
  }
  updatedAt
  parent {
    id
  }
  projectUpdate {
    id
  }
  quotedText
  archivedAt
  createdAt
  resolvedAt
  editedAt
  id
  resolvingUser {
    id
  }
  user {
    id
  }
}
fragment SyncedExternalThread on SyncedExternalThread {
  __typename
  name
  displayName
  url
  subType
  type
  isPersonalIntegrationRequired
  isPersonalIntegrationConnected
  isConnected
  id
}
fragment DocumentContent on DocumentContent {
  __typename
  aiPromptRules {
    ...AiPromptRules
  }
  content
  contentState
  document {
    id
  }
  initiative {
    id
  }
  issue {
    id
  }
  updatedAt
  projectMilestone {
    id
  }
  project {
    id
  }
  restoredAt
  archivedAt
  createdAt
  id
}
fragment Reaction on Reaction {
  __typename
  emoji
  comment {
    id
  }
  externalUser {
    id
  }
  initiativeUpdate {
    id
  }
  issue {
    id
  }
  updatedAt
  projectUpdate {
    id
  }
  archivedAt
  createdAt
  id
  user {
    id
  }
}
fragment AiPromptRules on AiPromptRules {
  __typename
  updatedAt
  archivedAt
  createdAt
  id
  updatedBy {
    id
  }
}
fragment ExternalEntityInfo on ExternalEntityInfo {
  __typename
  metadata {
    ... on ExternalEntityInfoGithubMetadata {
      ...ExternalEntityInfoGithubMetadata
    }
    ... on ExternalEntityInfoJiraMetadata {
      ...ExternalEntityInfoJiraMetadata
    }
    ... on ExternalEntitySlackMetadata {
      ...ExternalEntitySlackMetadata
    }
  }
  id
  service
}
fragment ExternalEntityInfoGithubMetadata on ExternalEntityInfoGithubMetadata {
  __typename
  number
  owner
  repo
}
fragment ExternalEntityInfoJiraMetadata on ExternalEntityInfoJiraMetadata {
  __typename
  issueTypeId
  projectId
  issueKey
}
fragment ExternalEntitySlackMetadata on ExternalEntitySlackMetadata {
  __typename
  messageUrl
  channelId
  channelName
  isFromSlack
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`, { fragmentName: "CommentConnection" });
const CommentPayloadFragmentDoc = new TypedDocumentString(`
    fragment CommentPayload on CommentPayload {
  __typename
  comment {
    id
  }
  lastSyncId
  success
}
    `, { fragmentName: "CommentPayload" });
const ContactPayloadFragmentDoc = new TypedDocumentString(`
    fragment ContactPayload on ContactPayload {
  __typename
  success
}
    `, { fragmentName: "ContactPayload" });
const CreateCsvExportReportPayloadFragmentDoc = new TypedDocumentString(`
    fragment CreateCsvExportReportPayload on CreateCsvExportReportPayload {
  __typename
  success
}
    `, { fragmentName: "CreateCsvExportReportPayload" });
const CreateOrJoinOrganizationResponseFragmentDoc = new TypedDocumentString(`
    fragment CreateOrJoinOrganizationResponse on CreateOrJoinOrganizationResponse {
  __typename
  organization {
    ...AuthOrganization
  }
  user {
    ...AuthUser
  }
}
    fragment AuthUser on AuthUser {
  __typename
  avatarUrl
  organization {
    ...AuthOrganization
  }
  createdAt
  displayName
  email
  name
  userAccountId
  active
  role
  id
}
fragment AuthOrganization on AuthOrganization {
  __typename
  allowedAuthServices
  previousUrlKeys
  serviceId
  releaseChannel
  logoUrl
  name
  urlKey
  region
  deletionRequestedAt
  createdAt
  id
  samlEnabled
  scimEnabled
  enabled
  userCount
}`, { fragmentName: "CreateOrJoinOrganizationResponse" });
const ViewPreferencesValuesFragmentDoc = new TypedDocumentString(`
    fragment ViewPreferencesValues on ViewPreferencesValues {
  __typename
  issueGrouping
  viewOrdering
  issueSubGrouping
  showCompletedIssues
}
    `, { fragmentName: "ViewPreferencesValues" });
const ViewPreferencesFragmentDoc = new TypedDocumentString(`
    fragment ViewPreferences on ViewPreferences {
  __typename
  updatedAt
  archivedAt
  createdAt
  id
  type
  preferences {
    ...ViewPreferencesValues
  }
  viewType
}
    fragment ViewPreferencesValues on ViewPreferencesValues {
  __typename
  issueGrouping
  viewOrdering
  issueSubGrouping
  showCompletedIssues
}`, { fragmentName: "ViewPreferences" });
const CustomViewFragmentDoc = new TypedDocumentString(`
    fragment CustomView on CustomView {
  __typename
  viewPreferencesValues {
    ...ViewPreferencesValues
  }
  color
  userViewPreferences {
    ...ViewPreferences
  }
  slugId
  description
  feedItemFilterData
  initiativeFilterData
  filterData
  projectFilterData
  filters
  icon
  updatedAt
  modelName
  name
  organizationViewPreferences {
    ...ViewPreferences
  }
  team {
    id
  }
  archivedAt
  createdAt
  id
  creator {
    id
  }
  updatedBy {
    id
  }
  owner {
    id
  }
  shared
}
    fragment ViewPreferences on ViewPreferences {
  __typename
  updatedAt
  archivedAt
  createdAt
  id
  type
  preferences {
    ...ViewPreferencesValues
  }
  viewType
}
fragment ViewPreferencesValues on ViewPreferencesValues {
  __typename
  issueGrouping
  viewOrdering
  issueSubGrouping
  showCompletedIssues
}`, { fragmentName: "CustomView" });
const CustomViewConnectionFragmentDoc = new TypedDocumentString(`
    fragment CustomViewConnection on CustomViewConnection {
  __typename
  nodes {
    ...CustomView
  }
  pageInfo {
    ...PageInfo
  }
}
    fragment CustomView on CustomView {
  __typename
  viewPreferencesValues {
    ...ViewPreferencesValues
  }
  color
  userViewPreferences {
    ...ViewPreferences
  }
  slugId
  description
  feedItemFilterData
  initiativeFilterData
  filterData
  projectFilterData
  filters
  icon
  updatedAt
  modelName
  name
  organizationViewPreferences {
    ...ViewPreferences
  }
  team {
    id
  }
  archivedAt
  createdAt
  id
  creator {
    id
  }
  updatedBy {
    id
  }
  owner {
    id
  }
  shared
}
fragment ViewPreferences on ViewPreferences {
  __typename
  updatedAt
  archivedAt
  createdAt
  id
  type
  preferences {
    ...ViewPreferencesValues
  }
  viewType
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}
fragment ViewPreferencesValues on ViewPreferencesValues {
  __typename
  issueGrouping
  viewOrdering
  issueSubGrouping
  showCompletedIssues
}`, { fragmentName: "CustomViewConnection" });
const CustomViewHasSubscribersPayloadFragmentDoc = new TypedDocumentString(`
    fragment CustomViewHasSubscribersPayload on CustomViewHasSubscribersPayload {
  __typename
  hasSubscribers
}
    `, { fragmentName: "CustomViewHasSubscribersPayload" });
const CustomViewPayloadFragmentDoc = new TypedDocumentString(`
    fragment CustomViewPayload on CustomViewPayload {
  __typename
  customView {
    id
  }
  lastSyncId
  success
}
    `, { fragmentName: "CustomViewPayload" });
const CustomViewSuggestionPayloadFragmentDoc = new TypedDocumentString(`
    fragment CustomViewSuggestionPayload on CustomViewSuggestionPayload {
  __typename
  description
  icon
  name
}
    `, { fragmentName: "CustomViewSuggestionPayload" });
const CustomerFragmentDoc = new TypedDocumentString(`
    fragment Customer on Customer {
  __typename
  slackChannelId
  mainSourceId
  revenue
  approximateNeedCount
  status {
    id
  }
  logoUrl
  name
  slugId
  domains
  externalIds
  integration {
    id
  }
  updatedAt
  size
  tier {
    id
  }
  archivedAt
  createdAt
  id
  owner {
    id
  }
  url
}
    `, { fragmentName: "Customer" });
const CustomerConnectionFragmentDoc = new TypedDocumentString(`
    fragment CustomerConnection on CustomerConnection {
  __typename
  nodes {
    ...Customer
  }
  pageInfo {
    ...PageInfo
  }
}
    fragment Customer on Customer {
  __typename
  slackChannelId
  mainSourceId
  revenue
  approximateNeedCount
  status {
    id
  }
  logoUrl
  name
  slugId
  domains
  externalIds
  integration {
    id
  }
  updatedAt
  size
  tier {
    id
  }
  archivedAt
  createdAt
  id
  owner {
    id
  }
  url
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`, { fragmentName: "CustomerConnection" });
const ProjectAttachmentFragmentDoc = new TypedDocumentString(`
    fragment ProjectAttachment on ProjectAttachment {
  __typename
  sourceType
  metadata
  source
  subtitle
  creator {
    id
  }
  updatedAt
  archivedAt
  createdAt
  id
  title
  url
}
    `, { fragmentName: "ProjectAttachment" });
const CustomerNeedFragmentDoc = new TypedDocumentString(`
    fragment CustomerNeed on CustomerNeed {
  __typename
  url
  attachment {
    id
  }
  comment {
    id
  }
  creator {
    id
  }
  customer {
    id
  }
  originalIssue {
    id
  }
  issue {
    id
  }
  updatedAt
  body
  projectAttachment {
    ...ProjectAttachment
  }
  project {
    id
  }
  archivedAt
  createdAt
  id
  priority
}
    fragment ProjectAttachment on ProjectAttachment {
  __typename
  sourceType
  metadata
  source
  subtitle
  creator {
    id
  }
  updatedAt
  archivedAt
  createdAt
  id
  title
  url
}`, { fragmentName: "CustomerNeed" });
const CustomerNeedConnectionFragmentDoc = new TypedDocumentString(`
    fragment CustomerNeedConnection on CustomerNeedConnection {
  __typename
  nodes {
    ...CustomerNeed
  }
  pageInfo {
    ...PageInfo
  }
}
    fragment CustomerNeed on CustomerNeed {
  __typename
  url
  attachment {
    id
  }
  comment {
    id
  }
  creator {
    id
  }
  customer {
    id
  }
  originalIssue {
    id
  }
  issue {
    id
  }
  updatedAt
  body
  projectAttachment {
    ...ProjectAttachment
  }
  project {
    id
  }
  archivedAt
  createdAt
  id
  priority
}
fragment ProjectAttachment on ProjectAttachment {
  __typename
  sourceType
  metadata
  source
  subtitle
  creator {
    id
  }
  updatedAt
  archivedAt
  createdAt
  id
  title
  url
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`, { fragmentName: "CustomerNeedConnection" });
const CustomerNeedPayloadFragmentDoc = new TypedDocumentString(`
    fragment CustomerNeedPayload on CustomerNeedPayload {
  __typename
  need {
    id
  }
  lastSyncId
  success
}
    `, { fragmentName: "CustomerNeedPayload" });
const CustomerNeedUpdatePayloadFragmentDoc = new TypedDocumentString(`
    fragment CustomerNeedUpdatePayload on CustomerNeedUpdatePayload {
  __typename
  need {
    id
  }
  lastSyncId
  updatedRelatedNeeds {
    ...CustomerNeed
  }
  success
}
    fragment CustomerNeed on CustomerNeed {
  __typename
  url
  attachment {
    id
  }
  comment {
    id
  }
  creator {
    id
  }
  customer {
    id
  }
  originalIssue {
    id
  }
  issue {
    id
  }
  updatedAt
  body
  projectAttachment {
    ...ProjectAttachment
  }
  project {
    id
  }
  archivedAt
  createdAt
  id
  priority
}
fragment ProjectAttachment on ProjectAttachment {
  __typename
  sourceType
  metadata
  source
  subtitle
  creator {
    id
  }
  updatedAt
  archivedAt
  createdAt
  id
  title
  url
}`, { fragmentName: "CustomerNeedUpdatePayload" });
const CustomerPayloadFragmentDoc = new TypedDocumentString(`
    fragment CustomerPayload on CustomerPayload {
  __typename
  customer {
    id
  }
  lastSyncId
  success
}
    `, { fragmentName: "CustomerPayload" });
const CustomerStatusFragmentDoc = new TypedDocumentString(`
    fragment CustomerStatus on CustomerStatus {
  __typename
  description
  color
  displayName
  updatedAt
  name
  position
  archivedAt
  createdAt
  type
  id
}
    `, { fragmentName: "CustomerStatus" });
const CustomerStatusConnectionFragmentDoc = new TypedDocumentString(`
    fragment CustomerStatusConnection on CustomerStatusConnection {
  __typename
  nodes {
    ...CustomerStatus
  }
  pageInfo {
    ...PageInfo
  }
}
    fragment CustomerStatus on CustomerStatus {
  __typename
  description
  color
  displayName
  updatedAt
  name
  position
  archivedAt
  createdAt
  type
  id
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`, { fragmentName: "CustomerStatusConnection" });
const CustomerStatusPayloadFragmentDoc = new TypedDocumentString(`
    fragment CustomerStatusPayload on CustomerStatusPayload {
  __typename
  status {
    id
  }
  lastSyncId
  success
}
    `, { fragmentName: "CustomerStatusPayload" });
const CustomerTierFragmentDoc = new TypedDocumentString(`
    fragment CustomerTier on CustomerTier {
  __typename
  description
  color
  displayName
  updatedAt
  name
  position
  archivedAt
  createdAt
  id
}
    `, { fragmentName: "CustomerTier" });
const CustomerTierConnectionFragmentDoc = new TypedDocumentString(`
    fragment CustomerTierConnection on CustomerTierConnection {
  __typename
  nodes {
    ...CustomerTier
  }
  pageInfo {
    ...PageInfo
  }
}
    fragment CustomerTier on CustomerTier {
  __typename
  description
  color
  displayName
  updatedAt
  name
  position
  archivedAt
  createdAt
  id
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`, { fragmentName: "CustomerTierConnection" });
const CustomerTierPayloadFragmentDoc = new TypedDocumentString(`
    fragment CustomerTierPayload on CustomerTierPayload {
  __typename
  tier {
    id
  }
  lastSyncId
  success
}
    `, { fragmentName: "CustomerTierPayload" });
const CycleFragmentDoc = new TypedDocumentString(`
    fragment Cycle on Cycle {
  __typename
  completedAt
  name
  inheritedFrom {
    id
  }
  description
  endsAt
  updatedAt
  completedScopeHistory
  completedIssueCountHistory
  inProgressScopeHistory
  number
  progress
  startsAt
  team {
    id
  }
  autoArchivedAt
  archivedAt
  createdAt
  scopeHistory
  issueCountHistory
  id
  isActive
  isFuture
  isPast
  isNext
  isPrevious
}
    `, { fragmentName: "Cycle" });
const CycleConnectionFragmentDoc = new TypedDocumentString(`
    fragment CycleConnection on CycleConnection {
  __typename
  nodes {
    ...Cycle
  }
  pageInfo {
    ...PageInfo
  }
}
    fragment Cycle on Cycle {
  __typename
  completedAt
  name
  inheritedFrom {
    id
  }
  description
  endsAt
  updatedAt
  completedScopeHistory
  completedIssueCountHistory
  inProgressScopeHistory
  number
  progress
  startsAt
  team {
    id
  }
  autoArchivedAt
  archivedAt
  createdAt
  scopeHistory
  issueCountHistory
  id
  isActive
  isFuture
  isPast
  isNext
  isPrevious
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`, { fragmentName: "CycleConnection" });
const CyclePayloadFragmentDoc = new TypedDocumentString(`
    fragment CyclePayload on CyclePayload {
  __typename
  cycle {
    id
  }
  lastSyncId
  success
}
    `, { fragmentName: "CyclePayload" });
const DocumentFragmentDoc = new TypedDocumentString(`
    fragment Document on Document {
  __typename
  trashed
  documentContentId
  url
  color
  title
  slugId
  content
  icon
  initiative {
    id
  }
  issue {
    id
  }
  lastAppliedTemplate {
    id
  }
  updatedAt
  sortOrder
  project {
    id
  }
  hiddenAt
  archivedAt
  createdAt
  id
  creator {
    id
  }
  updatedBy {
    id
  }
}
    `, { fragmentName: "Document" });
const DocumentConnectionFragmentDoc = new TypedDocumentString(`
    fragment DocumentConnection on DocumentConnection {
  __typename
  nodes {
    ...Document
  }
  pageInfo {
    ...PageInfo
  }
}
    fragment Document on Document {
  __typename
  trashed
  documentContentId
  url
  color
  title
  slugId
  content
  icon
  initiative {
    id
  }
  issue {
    id
  }
  lastAppliedTemplate {
    id
  }
  updatedAt
  sortOrder
  project {
    id
  }
  hiddenAt
  archivedAt
  createdAt
  id
  creator {
    id
  }
  updatedBy {
    id
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`, { fragmentName: "DocumentConnection" });
const DocumentContentHistoryTypeFragmentDoc = new TypedDocumentString(`
    fragment DocumentContentHistoryType on DocumentContentHistoryType {
  __typename
  actorIds
  id
  createdAt
  contentDataSnapshotAt
}
    `, { fragmentName: "DocumentContentHistoryType" });
const DocumentContentHistoryPayloadFragmentDoc = new TypedDocumentString(`
    fragment DocumentContentHistoryPayload on DocumentContentHistoryPayload {
  __typename
  history {
    ...DocumentContentHistoryType
  }
  success
}
    fragment DocumentContentHistoryType on DocumentContentHistoryType {
  __typename
  actorIds
  id
  createdAt
  contentDataSnapshotAt
}`, { fragmentName: "DocumentContentHistoryPayload" });
const DocumentPayloadFragmentDoc = new TypedDocumentString(`
    fragment DocumentPayload on DocumentPayload {
  __typename
  document {
    id
  }
  lastSyncId
  success
}
    `, { fragmentName: "DocumentPayload" });
const ArchiveResponseFragmentDoc = new TypedDocumentString(`
    fragment ArchiveResponse on ArchiveResponse {
  __typename
  archive
  totalCount
  databaseVersion
  includesDependencies
}
    `, { fragmentName: "ArchiveResponse" });
const DocumentSearchResultFragmentDoc = new TypedDocumentString(`
    fragment DocumentSearchResult on DocumentSearchResult {
  __typename
  trashed
  metadata
  documentContentId
  url
  color
  title
  slugId
  content
  icon
  initiative {
    id
  }
  issue {
    id
  }
  lastAppliedTemplate {
    id
  }
  updatedAt
  sortOrder
  project {
    id
  }
  hiddenAt
  archivedAt
  createdAt
  id
  creator {
    id
  }
  updatedBy {
    id
  }
}
    `, { fragmentName: "DocumentSearchResult" });
const DocumentSearchPayloadFragmentDoc = new TypedDocumentString(`
    fragment DocumentSearchPayload on DocumentSearchPayload {
  __typename
  archivePayload {
    ...ArchiveResponse
  }
  totalCount
  nodes {
    ...DocumentSearchResult
  }
  pageInfo {
    ...PageInfo
  }
}
    fragment ArchiveResponse on ArchiveResponse {
  __typename
  archive
  totalCount
  databaseVersion
  includesDependencies
}
fragment DocumentSearchResult on DocumentSearchResult {
  __typename
  trashed
  metadata
  documentContentId
  url
  color
  title
  slugId
  content
  icon
  initiative {
    id
  }
  issue {
    id
  }
  lastAppliedTemplate {
    id
  }
  updatedAt
  sortOrder
  project {
    id
  }
  hiddenAt
  archivedAt
  createdAt
  id
  creator {
    id
  }
  updatedBy {
    id
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`, { fragmentName: "DocumentSearchPayload" });
const DraftFragmentDoc = new TypedDocumentString(`
    fragment Draft on Draft {
  __typename
  data
  parentComment {
    id
  }
  customerNeed {
    id
  }
  initiative {
    id
  }
  initiativeUpdate {
    id
  }
  issue {
    id
  }
  updatedAt
  project {
    id
  }
  projectUpdate {
    id
  }
  team {
    id
  }
  bodyData
  archivedAt
  createdAt
  id
  user {
    id
  }
  isAutogenerated
}
    `, { fragmentName: "Draft" });
const DraftConnectionFragmentDoc = new TypedDocumentString(`
    fragment DraftConnection on DraftConnection {
  __typename
  nodes {
    ...Draft
  }
  pageInfo {
    ...PageInfo
  }
}
    fragment Draft on Draft {
  __typename
  data
  parentComment {
    id
  }
  customerNeed {
    id
  }
  initiative {
    id
  }
  initiativeUpdate {
    id
  }
  issue {
    id
  }
  updatedAt
  project {
    id
  }
  projectUpdate {
    id
  }
  team {
    id
  }
  bodyData
  archivedAt
  createdAt
  id
  user {
    id
  }
  isAutogenerated
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`, { fragmentName: "DraftConnection" });
const EmailIntakeAddressPayloadFragmentDoc = new TypedDocumentString(`
    fragment EmailIntakeAddressPayload on EmailIntakeAddressPayload {
  __typename
  emailIntakeAddress {
    id
  }
  lastSyncId
  success
}
    `, { fragmentName: "EmailIntakeAddressPayload" });
const EmailUnsubscribePayloadFragmentDoc = new TypedDocumentString(`
    fragment EmailUnsubscribePayload on EmailUnsubscribePayload {
  __typename
  success
}
    `, { fragmentName: "EmailUnsubscribePayload" });
const EmailUserAccountAuthChallengeResponseFragmentDoc = new TypedDocumentString(`
    fragment EmailUserAccountAuthChallengeResponse on EmailUserAccountAuthChallengeResponse {
  __typename
  authType
  success
}
    `, { fragmentName: "EmailUserAccountAuthChallengeResponse" });
const EmojiFragmentDoc = new TypedDocumentString(`
    fragment Emoji on Emoji {
  __typename
  url
  name
  updatedAt
  source
  archivedAt
  createdAt
  id
  creator {
    id
  }
}
    `, { fragmentName: "Emoji" });
const EmojiConnectionFragmentDoc = new TypedDocumentString(`
    fragment EmojiConnection on EmojiConnection {
  __typename
  nodes {
    ...Emoji
  }
  pageInfo {
    ...PageInfo
  }
}
    fragment Emoji on Emoji {
  __typename
  url
  name
  updatedAt
  source
  archivedAt
  createdAt
  id
  creator {
    id
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`, { fragmentName: "EmojiConnection" });
const EmojiPayloadFragmentDoc = new TypedDocumentString(`
    fragment EmojiPayload on EmojiPayload {
  __typename
  emoji {
    id
  }
  lastSyncId
  success
}
    `, { fragmentName: "EmojiPayload" });
const EntityExternalLinkFragmentDoc = new TypedDocumentString(`
    fragment EntityExternalLink on EntityExternalLink {
  __typename
  initiative {
    id
  }
  updatedAt
  url
  label
  sortOrder
  archivedAt
  createdAt
  id
  creator {
    id
  }
}
    `, { fragmentName: "EntityExternalLink" });
const EntityExternalLinkConnectionFragmentDoc = new TypedDocumentString(`
    fragment EntityExternalLinkConnection on EntityExternalLinkConnection {
  __typename
  nodes {
    ...EntityExternalLink
  }
  pageInfo {
    ...PageInfo
  }
}
    fragment EntityExternalLink on EntityExternalLink {
  __typename
  initiative {
    id
  }
  updatedAt
  url
  label
  sortOrder
  archivedAt
  createdAt
  id
  creator {
    id
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`, { fragmentName: "EntityExternalLinkConnection" });
const EntityExternalLinkPayloadFragmentDoc = new TypedDocumentString(`
    fragment EntityExternalLinkPayload on EntityExternalLinkPayload {
  __typename
  lastSyncId
  entityExternalLink {
    id
  }
  success
}
    `, { fragmentName: "EntityExternalLinkPayload" });
const ExternalUserFragmentDoc = new TypedDocumentString(`
    fragment ExternalUser on ExternalUser {
  __typename
  avatarUrl
  displayName
  email
  name
  updatedAt
  lastSeen
  archivedAt
  createdAt
  id
}
    `, { fragmentName: "ExternalUser" });
const ExternalUserConnectionFragmentDoc = new TypedDocumentString(`
    fragment ExternalUserConnection on ExternalUserConnection {
  __typename
  nodes {
    ...ExternalUser
  }
  pageInfo {
    ...PageInfo
  }
}
    fragment ExternalUser on ExternalUser {
  __typename
  avatarUrl
  displayName
  email
  name
  updatedAt
  lastSeen
  archivedAt
  createdAt
  id
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`, { fragmentName: "ExternalUserConnection" });
const FacetFragmentDoc = new TypedDocumentString(`
    fragment Facet on Facet {
  __typename
  updatedAt
  sourceFeedUser {
    id
  }
  sourceInitiative {
    id
  }
  sourcePage
  sourceProject {
    id
  }
  sourceTeam {
    id
  }
  sortOrder
  targetCustomView {
    id
  }
  archivedAt
  createdAt
  id
}
    `, { fragmentName: "Facet" });
const FacetConnectionFragmentDoc = new TypedDocumentString(`
    fragment FacetConnection on FacetConnection {
  __typename
  nodes {
    ...Facet
  }
  pageInfo {
    ...PageInfo
  }
}
    fragment Facet on Facet {
  __typename
  updatedAt
  sourceFeedUser {
    id
  }
  sourceInitiative {
    id
  }
  sourcePage
  sourceProject {
    id
  }
  sourceTeam {
    id
  }
  sortOrder
  targetCustomView {
    id
  }
  archivedAt
  createdAt
  id
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`, { fragmentName: "FacetConnection" });
const FavoriteFragmentDoc = new TypedDocumentString(`
    fragment Favorite on Favorite {
  __typename
  customView {
    id
  }
  customer {
    id
  }
  cycle {
    id
  }
  document {
    id
  }
  initiative {
    id
  }
  issue {
    id
  }
  label {
    id
  }
  projectLabel {
    id
  }
  project {
    id
  }
  user {
    id
  }
  updatedAt
  folderName
  sortOrder
  owner {
    id
  }
  parent {
    id
  }
  initiativeTab
  projectTab
  predefinedViewTeam {
    id
  }
  archivedAt
  createdAt
  predefinedViewType
  type
  id
  url
  projectTeam {
    id
  }
}
    `, { fragmentName: "Favorite" });
const FavoriteConnectionFragmentDoc = new TypedDocumentString(`
    fragment FavoriteConnection on FavoriteConnection {
  __typename
  nodes {
    ...Favorite
  }
  pageInfo {
    ...PageInfo
  }
}
    fragment Favorite on Favorite {
  __typename
  customView {
    id
  }
  customer {
    id
  }
  cycle {
    id
  }
  document {
    id
  }
  initiative {
    id
  }
  issue {
    id
  }
  label {
    id
  }
  projectLabel {
    id
  }
  project {
    id
  }
  user {
    id
  }
  updatedAt
  folderName
  sortOrder
  owner {
    id
  }
  parent {
    id
  }
  initiativeTab
  projectTab
  predefinedViewTeam {
    id
  }
  archivedAt
  createdAt
  predefinedViewType
  type
  id
  url
  projectTeam {
    id
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`, { fragmentName: "FavoriteConnection" });
const FavoritePayloadFragmentDoc = new TypedDocumentString(`
    fragment FavoritePayload on FavoritePayload {
  __typename
  lastSyncId
  favorite {
    id
  }
  success
}
    `, { fragmentName: "FavoritePayload" });
const FetchDataPayloadFragmentDoc = new TypedDocumentString(`
    fragment FetchDataPayload on FetchDataPayload {
  __typename
  query
  data
  filters
  success
}
    `, { fragmentName: "FetchDataPayload" });
const FileUploadDeletePayloadFragmentDoc = new TypedDocumentString(`
    fragment FileUploadDeletePayload on FileUploadDeletePayload {
  __typename
  success
}
    `, { fragmentName: "FileUploadDeletePayload" });
const FrontAttachmentPayloadFragmentDoc = new TypedDocumentString(`
    fragment FrontAttachmentPayload on FrontAttachmentPayload {
  __typename
  lastSyncId
  attachment {
    id
  }
  success
}
    `, { fragmentName: "FrontAttachmentPayload" });
const GitAutomationTargetBranchFragmentDoc = new TypedDocumentString(`
    fragment GitAutomationTargetBranch on GitAutomationTargetBranch {
  __typename
  updatedAt
  branchPattern
  team {
    id
  }
  archivedAt
  createdAt
  id
  isRegex
}
    `, { fragmentName: "GitAutomationTargetBranch" });
const GitAutomationStateFragmentDoc = new TypedDocumentString(`
    fragment GitAutomationState on GitAutomationState {
  __typename
  state {
    id
  }
  event
  updatedAt
  targetBranch {
    ...GitAutomationTargetBranch
  }
  team {
    id
  }
  archivedAt
  createdAt
  id
  branchPattern
}
    fragment GitAutomationTargetBranch on GitAutomationTargetBranch {
  __typename
  updatedAt
  branchPattern
  team {
    id
  }
  archivedAt
  createdAt
  id
  isRegex
}`, { fragmentName: "GitAutomationState" });
const GitAutomationStateConnectionFragmentDoc = new TypedDocumentString(`
    fragment GitAutomationStateConnection on GitAutomationStateConnection {
  __typename
  nodes {
    ...GitAutomationState
  }
  pageInfo {
    ...PageInfo
  }
}
    fragment GitAutomationTargetBranch on GitAutomationTargetBranch {
  __typename
  updatedAt
  branchPattern
  team {
    id
  }
  archivedAt
  createdAt
  id
  isRegex
}
fragment GitAutomationState on GitAutomationState {
  __typename
  state {
    id
  }
  event
  updatedAt
  targetBranch {
    ...GitAutomationTargetBranch
  }
  team {
    id
  }
  archivedAt
  createdAt
  id
  branchPattern
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`, { fragmentName: "GitAutomationStateConnection" });
const GitAutomationStatePayloadFragmentDoc = new TypedDocumentString(`
    fragment GitAutomationStatePayload on GitAutomationStatePayload {
  __typename
  gitAutomationState {
    ...GitAutomationState
  }
  lastSyncId
  success
}
    fragment GitAutomationTargetBranch on GitAutomationTargetBranch {
  __typename
  updatedAt
  branchPattern
  team {
    id
  }
  archivedAt
  createdAt
  id
  isRegex
}
fragment GitAutomationState on GitAutomationState {
  __typename
  state {
    id
  }
  event
  updatedAt
  targetBranch {
    ...GitAutomationTargetBranch
  }
  team {
    id
  }
  archivedAt
  createdAt
  id
  branchPattern
}`, { fragmentName: "GitAutomationStatePayload" });
const GitAutomationTargetBranchPayloadFragmentDoc = new TypedDocumentString(`
    fragment GitAutomationTargetBranchPayload on GitAutomationTargetBranchPayload {
  __typename
  targetBranch {
    ...GitAutomationTargetBranch
  }
  lastSyncId
  success
}
    fragment GitAutomationTargetBranch on GitAutomationTargetBranch {
  __typename
  updatedAt
  branchPattern
  team {
    id
  }
  archivedAt
  createdAt
  id
  isRegex
}`, { fragmentName: "GitAutomationTargetBranchPayload" });
const GitHubCommitIntegrationPayloadFragmentDoc = new TypedDocumentString(`
    fragment GitHubCommitIntegrationPayload on GitHubCommitIntegrationPayload {
  __typename
  lastSyncId
  integration {
    id
  }
  webhookSecret
  success
}
    `, { fragmentName: "GitHubCommitIntegrationPayload" });
const GitHubEnterpriseServerInstallVerificationPayloadFragmentDoc = new TypedDocumentString(`
    fragment GitHubEnterpriseServerInstallVerificationPayload on GitHubEnterpriseServerInstallVerificationPayload {
  __typename
  success
}
    `, { fragmentName: "GitHubEnterpriseServerInstallVerificationPayload" });
const GitHubEnterpriseServerPayloadFragmentDoc = new TypedDocumentString(`
    fragment GitHubEnterpriseServerPayload on GitHubEnterpriseServerPayload {
  __typename
  installUrl
  lastSyncId
  integration {
    id
  }
  setupUrl
  webhookSecret
  success
}
    `, { fragmentName: "GitHubEnterpriseServerPayload" });
const GitLabIntegrationCreatePayloadFragmentDoc = new TypedDocumentString(`
    fragment GitLabIntegrationCreatePayload on GitLabIntegrationCreatePayload {
  __typename
  lastSyncId
  integration {
    id
  }
  webhookSecret
  success
}
    `, { fragmentName: "GitLabIntegrationCreatePayload" });
const ImageUploadFromUrlPayloadFragmentDoc = new TypedDocumentString(`
    fragment ImageUploadFromUrlPayload on ImageUploadFromUrlPayload {
  __typename
  url
  lastSyncId
  success
}
    `, { fragmentName: "ImageUploadFromUrlPayload" });
const InitiativeFragmentDoc = new TypedDocumentString(`
    fragment Initiative on Initiative {
  __typename
  trashed
  url
  parentInitiative {
    id
  }
  integrationsSettings {
    id
  }
  documentContent {
    ...DocumentContent
  }
  updateRemindersDay
  description
  targetDate
  updateReminderFrequency
  health
  updateRemindersHour
  icon
  color
  content
  slugId
  lastUpdate {
    id
  }
  updatedAt
  updateReminderFrequencyInWeeks
  name
  targetDateResolution
  frequencyResolution
  sortOrder
  status
  archivedAt
  createdAt
  healthUpdatedAt
  startedAt
  completedAt
  id
  creator {
    id
  }
  owner {
    id
  }
}
    fragment DocumentContent on DocumentContent {
  __typename
  aiPromptRules {
    ...AiPromptRules
  }
  content
  contentState
  document {
    id
  }
  initiative {
    id
  }
  issue {
    id
  }
  updatedAt
  projectMilestone {
    id
  }
  project {
    id
  }
  restoredAt
  archivedAt
  createdAt
  id
}
fragment AiPromptRules on AiPromptRules {
  __typename
  updatedAt
  archivedAt
  createdAt
  id
  updatedBy {
    id
  }
}`, { fragmentName: "Initiative" });
const InitiativeConnectionFragmentDoc = new TypedDocumentString(`
    fragment InitiativeConnection on InitiativeConnection {
  __typename
  nodes {
    ...Initiative
  }
  pageInfo {
    ...PageInfo
  }
}
    fragment DocumentContent on DocumentContent {
  __typename
  aiPromptRules {
    ...AiPromptRules
  }
  content
  contentState
  document {
    id
  }
  initiative {
    id
  }
  issue {
    id
  }
  updatedAt
  projectMilestone {
    id
  }
  project {
    id
  }
  restoredAt
  archivedAt
  createdAt
  id
}
fragment AiPromptRules on AiPromptRules {
  __typename
  updatedAt
  archivedAt
  createdAt
  id
  updatedBy {
    id
  }
}
fragment Initiative on Initiative {
  __typename
  trashed
  url
  parentInitiative {
    id
  }
  integrationsSettings {
    id
  }
  documentContent {
    ...DocumentContent
  }
  updateRemindersDay
  description
  targetDate
  updateReminderFrequency
  health
  updateRemindersHour
  icon
  color
  content
  slugId
  lastUpdate {
    id
  }
  updatedAt
  updateReminderFrequencyInWeeks
  name
  targetDateResolution
  frequencyResolution
  sortOrder
  status
  archivedAt
  createdAt
  healthUpdatedAt
  startedAt
  completedAt
  id
  creator {
    id
  }
  owner {
    id
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`, { fragmentName: "InitiativeConnection" });
const InitiativeHistoryFragmentDoc = new TypedDocumentString(`
    fragment InitiativeHistory on InitiativeHistory {
  __typename
  entries
  initiative {
    id
  }
  updatedAt
  archivedAt
  createdAt
  id
}
    `, { fragmentName: "InitiativeHistory" });
const InitiativeHistoryConnectionFragmentDoc = new TypedDocumentString(`
    fragment InitiativeHistoryConnection on InitiativeHistoryConnection {
  __typename
  nodes {
    ...InitiativeHistory
  }
  pageInfo {
    ...PageInfo
  }
}
    fragment InitiativeHistory on InitiativeHistory {
  __typename
  entries
  initiative {
    id
  }
  updatedAt
  archivedAt
  createdAt
  id
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`, { fragmentName: "InitiativeHistoryConnection" });
const InitiativeRelationFragmentDoc = new TypedDocumentString(`
    fragment InitiativeRelation on InitiativeRelation {
  __typename
  relatedInitiative {
    id
  }
  updatedAt
  user {
    id
  }
  initiative {
    id
  }
  sortOrder
  archivedAt
  createdAt
  id
}
    `, { fragmentName: "InitiativeRelation" });
const InitiativeRelationConnectionFragmentDoc = new TypedDocumentString(`
    fragment InitiativeRelationConnection on InitiativeRelationConnection {
  __typename
  nodes {
    ...InitiativeRelation
  }
  pageInfo {
    ...PageInfo
  }
}
    fragment InitiativeRelation on InitiativeRelation {
  __typename
  relatedInitiative {
    id
  }
  updatedAt
  user {
    id
  }
  initiative {
    id
  }
  sortOrder
  archivedAt
  createdAt
  id
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`, { fragmentName: "InitiativeRelationConnection" });
const InitiativeRelationPayloadFragmentDoc = new TypedDocumentString(`
    fragment InitiativeRelationPayload on InitiativeRelationPayload {
  __typename
  lastSyncId
  initiativeRelation {
    ...InitiativeRelation
  }
  success
}
    fragment InitiativeRelation on InitiativeRelation {
  __typename
  relatedInitiative {
    id
  }
  updatedAt
  user {
    id
  }
  initiative {
    id
  }
  sortOrder
  archivedAt
  createdAt
  id
}`, { fragmentName: "InitiativeRelationPayload" });
const InitiativeToProjectFragmentDoc = new TypedDocumentString(`
    fragment InitiativeToProject on InitiativeToProject {
  __typename
  initiative {
    id
  }
  updatedAt
  project {
    id
  }
  sortOrder
  archivedAt
  createdAt
  id
}
    `, { fragmentName: "InitiativeToProject" });
const InitiativeToProjectConnectionFragmentDoc = new TypedDocumentString(`
    fragment InitiativeToProjectConnection on InitiativeToProjectConnection {
  __typename
  nodes {
    ...InitiativeToProject
  }
  pageInfo {
    ...PageInfo
  }
}
    fragment InitiativeToProject on InitiativeToProject {
  __typename
  initiative {
    id
  }
  updatedAt
  project {
    id
  }
  sortOrder
  archivedAt
  createdAt
  id
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`, { fragmentName: "InitiativeToProjectConnection" });
const InitiativeUpdateFragmentDoc = new TypedDocumentString(`
    fragment InitiativeUpdate on InitiativeUpdate {
  __typename
  reactionData
  commentCount
  reactions {
    ...Reaction
  }
  url
  diffMarkdown
  diff
  health
  initiative {
    id
  }
  updatedAt
  archivedAt
  createdAt
  editedAt
  id
  body
  slugId
  user {
    id
  }
  isDiffHidden
  isStale
}
    fragment Reaction on Reaction {
  __typename
  emoji
  comment {
    id
  }
  externalUser {
    id
  }
  initiativeUpdate {
    id
  }
  issue {
    id
  }
  updatedAt
  projectUpdate {
    id
  }
  archivedAt
  createdAt
  id
  user {
    id
  }
}`, { fragmentName: "InitiativeUpdate" });
const InitiativeUpdateConnectionFragmentDoc = new TypedDocumentString(`
    fragment InitiativeUpdateConnection on InitiativeUpdateConnection {
  __typename
  nodes {
    ...InitiativeUpdate
  }
  pageInfo {
    ...PageInfo
  }
}
    fragment Reaction on Reaction {
  __typename
  emoji
  comment {
    id
  }
  externalUser {
    id
  }
  initiativeUpdate {
    id
  }
  issue {
    id
  }
  updatedAt
  projectUpdate {
    id
  }
  archivedAt
  createdAt
  id
  user {
    id
  }
}
fragment InitiativeUpdate on InitiativeUpdate {
  __typename
  reactionData
  commentCount
  reactions {
    ...Reaction
  }
  url
  diffMarkdown
  diff
  health
  initiative {
    id
  }
  updatedAt
  archivedAt
  createdAt
  editedAt
  id
  body
  slugId
  user {
    id
  }
  isDiffHidden
  isStale
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`, { fragmentName: "InitiativeUpdateConnection" });
const InitiativeUpdatePayloadFragmentDoc = new TypedDocumentString(`
    fragment InitiativeUpdatePayload on InitiativeUpdatePayload {
  __typename
  lastSyncId
  initiativeUpdate {
    id
  }
  success
}
    `, { fragmentName: "InitiativeUpdatePayload" });
const InitiativeUpdateReminderPayloadFragmentDoc = new TypedDocumentString(`
    fragment InitiativeUpdateReminderPayload on InitiativeUpdateReminderPayload {
  __typename
  lastSyncId
  success
}
    `, { fragmentName: "InitiativeUpdateReminderPayload" });
const IntegrationFragmentDoc = new TypedDocumentString(`
    fragment Integration on Integration {
  __typename
  service
  updatedAt
  team {
    id
  }
  archivedAt
  createdAt
  id
  creator {
    id
  }
}
    `, { fragmentName: "Integration" });
const IntegrationConnectionFragmentDoc = new TypedDocumentString(`
    fragment IntegrationConnection on IntegrationConnection {
  __typename
  nodes {
    ...Integration
  }
  pageInfo {
    ...PageInfo
  }
}
    fragment Integration on Integration {
  __typename
  service
  updatedAt
  team {
    id
  }
  archivedAt
  createdAt
  id
  creator {
    id
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`, { fragmentName: "IntegrationConnection" });
const IntegrationHasScopesPayloadFragmentDoc = new TypedDocumentString(`
    fragment IntegrationHasScopesPayload on IntegrationHasScopesPayload {
  __typename
  missingScopes
  hasAllScopes
}
    `, { fragmentName: "IntegrationHasScopesPayload" });
const IntegrationPayloadFragmentDoc = new TypedDocumentString(`
    fragment IntegrationPayload on IntegrationPayload {
  __typename
  lastSyncId
  integration {
    id
  }
  success
}
    `, { fragmentName: "IntegrationPayload" });
const IntegrationRequestPayloadFragmentDoc = new TypedDocumentString(`
    fragment IntegrationRequestPayload on IntegrationRequestPayload {
  __typename
  success
}
    `, { fragmentName: "IntegrationRequestPayload" });
const IntegrationSlackWorkspaceNamePayloadFragmentDoc = new TypedDocumentString(`
    fragment IntegrationSlackWorkspaceNamePayload on IntegrationSlackWorkspaceNamePayload {
  __typename
  name
  success
}
    `, { fragmentName: "IntegrationSlackWorkspaceNamePayload" });
const IntegrationTemplateFragmentDoc = new TypedDocumentString(`
    fragment IntegrationTemplate on IntegrationTemplate {
  __typename
  foreignEntityId
  integration {
    id
  }
  updatedAt
  template {
    id
  }
  archivedAt
  createdAt
  id
}
    `, { fragmentName: "IntegrationTemplate" });
const IntegrationTemplateConnectionFragmentDoc = new TypedDocumentString(`
    fragment IntegrationTemplateConnection on IntegrationTemplateConnection {
  __typename
  nodes {
    ...IntegrationTemplate
  }
  pageInfo {
    ...PageInfo
  }
}
    fragment IntegrationTemplate on IntegrationTemplate {
  __typename
  foreignEntityId
  integration {
    id
  }
  updatedAt
  template {
    id
  }
  archivedAt
  createdAt
  id
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`, { fragmentName: "IntegrationTemplateConnection" });
const IntegrationTemplatePayloadFragmentDoc = new TypedDocumentString(`
    fragment IntegrationTemplatePayload on IntegrationTemplatePayload {
  __typename
  integrationTemplate {
    id
  }
  lastSyncId
  success
}
    `, { fragmentName: "IntegrationTemplatePayload" });
const IntegrationsSettingsPayloadFragmentDoc = new TypedDocumentString(`
    fragment IntegrationsSettingsPayload on IntegrationsSettingsPayload {
  __typename
  lastSyncId
  integrationsSettings {
    id
  }
  success
}
    `, { fragmentName: "IntegrationsSettingsPayload" });
const IssueFragmentDoc = new TypedDocumentString(`
    fragment Issue on Issue {
  __typename
  trashed
  reactionData
  labelIds
  integrationSourceType
  url
  identifier
  priorityLabel
  previousIdentifiers
  reactions {
    ...Reaction
  }
  customerTicketCount
  branchName
  delegate {
    id
  }
  botActor {
    ...ActorBot
  }
  sourceComment {
    id
  }
  cycle {
    id
  }
  dueDate
  estimate
  syncedWith {
    ...ExternalEntityInfo
  }
  externalUserCreator {
    id
  }
  asksExternalUserRequester {
    id
  }
  asksRequester {
    id
  }
  description
  title
  number
  lastAppliedTemplate {
    id
  }
  updatedAt
  boardOrder
  prioritySortOrder
  sortOrder
  subIssueSortOrder
  parent {
    id
  }
  priority
  project {
    id
  }
  projectMilestone {
    id
  }
  recurringIssueTemplate {
    id
  }
  team {
    id
  }
  archivedAt
  createdAt
  startedTriageAt
  triagedAt
  addedToCycleAt
  addedToProjectAt
  addedToTeamAt
  autoArchivedAt
  autoClosedAt
  canceledAt
  completedAt
  startedAt
  slaStartedAt
  slaBreachesAt
  slaHighRiskAt
  slaMediumRiskAt
  snoozedUntilAt
  slaType
  id
  assignee {
    id
  }
  creator {
    id
  }
  snoozedBy {
    id
  }
  favorite {
    id
  }
  state {
    id
  }
}
    fragment ActorBot on ActorBot {
  __typename
  avatarUrl
  name
  userDisplayName
  subType
  type
  id
}
fragment Reaction on Reaction {
  __typename
  emoji
  comment {
    id
  }
  externalUser {
    id
  }
  initiativeUpdate {
    id
  }
  issue {
    id
  }
  updatedAt
  projectUpdate {
    id
  }
  archivedAt
  createdAt
  id
  user {
    id
  }
}
fragment ExternalEntityInfo on ExternalEntityInfo {
  __typename
  metadata {
    ... on ExternalEntityInfoGithubMetadata {
      ...ExternalEntityInfoGithubMetadata
    }
    ... on ExternalEntityInfoJiraMetadata {
      ...ExternalEntityInfoJiraMetadata
    }
    ... on ExternalEntitySlackMetadata {
      ...ExternalEntitySlackMetadata
    }
  }
  id
  service
}
fragment ExternalEntityInfoGithubMetadata on ExternalEntityInfoGithubMetadata {
  __typename
  number
  owner
  repo
}
fragment ExternalEntityInfoJiraMetadata on ExternalEntityInfoJiraMetadata {
  __typename
  issueTypeId
  projectId
  issueKey
}
fragment ExternalEntitySlackMetadata on ExternalEntitySlackMetadata {
  __typename
  messageUrl
  channelId
  channelName
  isFromSlack
}`, { fragmentName: "Issue" });
const IssueBatchPayloadFragmentDoc = new TypedDocumentString(`
    fragment IssueBatchPayload on IssueBatchPayload {
  __typename
  lastSyncId
  issues {
    ...Issue
  }
  success
}
    fragment ActorBot on ActorBot {
  __typename
  avatarUrl
  name
  userDisplayName
  subType
  type
  id
}
fragment Reaction on Reaction {
  __typename
  emoji
  comment {
    id
  }
  externalUser {
    id
  }
  initiativeUpdate {
    id
  }
  issue {
    id
  }
  updatedAt
  projectUpdate {
    id
  }
  archivedAt
  createdAt
  id
  user {
    id
  }
}
fragment Issue on Issue {
  __typename
  trashed
  reactionData
  labelIds
  integrationSourceType
  url
  identifier
  priorityLabel
  previousIdentifiers
  reactions {
    ...Reaction
  }
  customerTicketCount
  branchName
  delegate {
    id
  }
  botActor {
    ...ActorBot
  }
  sourceComment {
    id
  }
  cycle {
    id
  }
  dueDate
  estimate
  syncedWith {
    ...ExternalEntityInfo
  }
  externalUserCreator {
    id
  }
  asksExternalUserRequester {
    id
  }
  asksRequester {
    id
  }
  description
  title
  number
  lastAppliedTemplate {
    id
  }
  updatedAt
  boardOrder
  prioritySortOrder
  sortOrder
  subIssueSortOrder
  parent {
    id
  }
  priority
  project {
    id
  }
  projectMilestone {
    id
  }
  recurringIssueTemplate {
    id
  }
  team {
    id
  }
  archivedAt
  createdAt
  startedTriageAt
  triagedAt
  addedToCycleAt
  addedToProjectAt
  addedToTeamAt
  autoArchivedAt
  autoClosedAt
  canceledAt
  completedAt
  startedAt
  slaStartedAt
  slaBreachesAt
  slaHighRiskAt
  slaMediumRiskAt
  snoozedUntilAt
  slaType
  id
  assignee {
    id
  }
  creator {
    id
  }
  snoozedBy {
    id
  }
  favorite {
    id
  }
  state {
    id
  }
}
fragment ExternalEntityInfo on ExternalEntityInfo {
  __typename
  metadata {
    ... on ExternalEntityInfoGithubMetadata {
      ...ExternalEntityInfoGithubMetadata
    }
    ... on ExternalEntityInfoJiraMetadata {
      ...ExternalEntityInfoJiraMetadata
    }
    ... on ExternalEntitySlackMetadata {
      ...ExternalEntitySlackMetadata
    }
  }
  id
  service
}
fragment ExternalEntityInfoGithubMetadata on ExternalEntityInfoGithubMetadata {
  __typename
  number
  owner
  repo
}
fragment ExternalEntityInfoJiraMetadata on ExternalEntityInfoJiraMetadata {
  __typename
  issueTypeId
  projectId
  issueKey
}
fragment ExternalEntitySlackMetadata on ExternalEntitySlackMetadata {
  __typename
  messageUrl
  channelId
  channelName
  isFromSlack
}`, { fragmentName: "IssueBatchPayload" });
const IssueConnectionFragmentDoc = new TypedDocumentString(`
    fragment IssueConnection on IssueConnection {
  __typename
  nodes {
    ...Issue
  }
  pageInfo {
    ...PageInfo
  }
}
    fragment ActorBot on ActorBot {
  __typename
  avatarUrl
  name
  userDisplayName
  subType
  type
  id
}
fragment Reaction on Reaction {
  __typename
  emoji
  comment {
    id
  }
  externalUser {
    id
  }
  initiativeUpdate {
    id
  }
  issue {
    id
  }
  updatedAt
  projectUpdate {
    id
  }
  archivedAt
  createdAt
  id
  user {
    id
  }
}
fragment Issue on Issue {
  __typename
  trashed
  reactionData
  labelIds
  integrationSourceType
  url
  identifier
  priorityLabel
  previousIdentifiers
  reactions {
    ...Reaction
  }
  customerTicketCount
  branchName
  delegate {
    id
  }
  botActor {
    ...ActorBot
  }
  sourceComment {
    id
  }
  cycle {
    id
  }
  dueDate
  estimate
  syncedWith {
    ...ExternalEntityInfo
  }
  externalUserCreator {
    id
  }
  asksExternalUserRequester {
    id
  }
  asksRequester {
    id
  }
  description
  title
  number
  lastAppliedTemplate {
    id
  }
  updatedAt
  boardOrder
  prioritySortOrder
  sortOrder
  subIssueSortOrder
  parent {
    id
  }
  priority
  project {
    id
  }
  projectMilestone {
    id
  }
  recurringIssueTemplate {
    id
  }
  team {
    id
  }
  archivedAt
  createdAt
  startedTriageAt
  triagedAt
  addedToCycleAt
  addedToProjectAt
  addedToTeamAt
  autoArchivedAt
  autoClosedAt
  canceledAt
  completedAt
  startedAt
  slaStartedAt
  slaBreachesAt
  slaHighRiskAt
  slaMediumRiskAt
  snoozedUntilAt
  slaType
  id
  assignee {
    id
  }
  creator {
    id
  }
  snoozedBy {
    id
  }
  favorite {
    id
  }
  state {
    id
  }
}
fragment ExternalEntityInfo on ExternalEntityInfo {
  __typename
  metadata {
    ... on ExternalEntityInfoGithubMetadata {
      ...ExternalEntityInfoGithubMetadata
    }
    ... on ExternalEntityInfoJiraMetadata {
      ...ExternalEntityInfoJiraMetadata
    }
    ... on ExternalEntitySlackMetadata {
      ...ExternalEntitySlackMetadata
    }
  }
  id
  service
}
fragment ExternalEntityInfoGithubMetadata on ExternalEntityInfoGithubMetadata {
  __typename
  number
  owner
  repo
}
fragment ExternalEntityInfoJiraMetadata on ExternalEntityInfoJiraMetadata {
  __typename
  issueTypeId
  projectId
  issueKey
}
fragment ExternalEntitySlackMetadata on ExternalEntitySlackMetadata {
  __typename
  messageUrl
  channelId
  channelName
  isFromSlack
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`, { fragmentName: "IssueConnection" });
const IssueFilterSuggestionPayloadFragmentDoc = new TypedDocumentString(`
    fragment IssueFilterSuggestionPayload on IssueFilterSuggestionPayload {
  __typename
  filter
  logId
}
    `, { fragmentName: "IssueFilterSuggestionPayload" });
const IssueRelationHistoryPayloadFragmentDoc = new TypedDocumentString(`
    fragment IssueRelationHistoryPayload on IssueRelationHistoryPayload {
  __typename
  identifier
  type
}
    `, { fragmentName: "IssueRelationHistoryPayload" });
const UserFragmentDoc = new TypedDocumentString(`
    fragment User on User {
  __typename
  statusUntilAt
  description
  avatarUrl
  createdIssueCount
  disableReason
  avatarBackgroundColor
  statusEmoji
  initials
  statusLabel
  updatedAt
  lastSeen
  timezone
  archivedAt
  createdAt
  id
  gitHubUserId
  displayName
  email
  name
  url
  active
  guest
  app
  admin
  owner
  isAssignable
  isMentionable
  isMe
  supportsAgentSessions
  canAccessAnyPublicTeam
  calendarHash
  inviteHash
}
    `, { fragmentName: "User" });
const IssueImportFragmentDoc = new TypedDocumentString(`
    fragment IssueImport on IssueImport {
  __typename
  progress
  errorMetadata
  csvFileUrl
  serviceMetadata
  teamName
  mapping
  displayName
  creatorId
  updatedAt
  service
  status
  archivedAt
  createdAt
  id
  error
}
    `, { fragmentName: "IssueImport" });
const IssueLabelFragmentDoc = new TypedDocumentString(`
    fragment IssueLabel on IssueLabel {
  __typename
  lastAppliedAt
  color
  description
  name
  updatedAt
  inheritedFrom {
    id
  }
  parent {
    id
  }
  team {
    id
  }
  archivedAt
  createdAt
  id
  creator {
    id
  }
  retiredBy {
    id
  }
  isGroup
}
    `, { fragmentName: "IssueLabel" });
const IssueHistoryFragmentDoc = new TypedDocumentString(`
    fragment IssueHistory on IssueHistory {
  __typename
  triageResponsibilityAutoAssigned
  relationChanges {
    ...IssueRelationHistoryPayload
  }
  addedLabelIds
  removedLabelIds
  actor {
    id
  }
  descriptionUpdatedBy {
    ...User
  }
  actors {
    ...User
  }
  fromDelegate {
    id
  }
  toDelegate {
    id
  }
  botActor {
    ...ActorBot
  }
  fromCycle {
    id
  }
  toCycle {
    id
  }
  attachmentId
  customerNeedId
  toCycleId
  toParentId
  toConvertedProjectId
  toProjectId
  toStateId
  fromCycleId
  fromParentId
  fromProjectId
  fromStateId
  fromTeamId
  toTeamId
  fromAssigneeId
  toAssigneeId
  actorId
  issueImport {
    ...IssueImport
  }
  issue {
    id
  }
  addedLabels {
    ...IssueLabel
  }
  removedLabels {
    ...IssueLabel
  }
  updatedAt
  attachment {
    id
  }
  toConvertedProject {
    id
  }
  fromParent {
    id
  }
  toParent {
    id
  }
  fromProject {
    id
  }
  toProject {
    id
  }
  fromState {
    id
  }
  toState {
    id
  }
  fromTeam {
    id
  }
  toTeam {
    id
  }
  archivedAt
  createdAt
  id
  toAssignee {
    id
  }
  fromAssignee {
    id
  }
  triageResponsibilityNotifiedUsers {
    ...User
  }
  fromDueDate
  toDueDate
  fromEstimate
  toEstimate
  fromPriority
  toPriority
  fromTitle
  toTitle
  archived
  autoArchived
  autoClosed
  trashed
  updatedDescription
}
    fragment ActorBot on ActorBot {
  __typename
  avatarUrl
  name
  userDisplayName
  subType
  type
  id
}
fragment User on User {
  __typename
  statusUntilAt
  description
  avatarUrl
  createdIssueCount
  disableReason
  avatarBackgroundColor
  statusEmoji
  initials
  statusLabel
  updatedAt
  lastSeen
  timezone
  archivedAt
  createdAt
  id
  gitHubUserId
  displayName
  email
  name
  url
  active
  guest
  app
  admin
  owner
  isAssignable
  isMentionable
  isMe
  supportsAgentSessions
  canAccessAnyPublicTeam
  calendarHash
  inviteHash
}
fragment IssueImport on IssueImport {
  __typename
  progress
  errorMetadata
  csvFileUrl
  serviceMetadata
  teamName
  mapping
  displayName
  creatorId
  updatedAt
  service
  status
  archivedAt
  createdAt
  id
  error
}
fragment IssueRelationHistoryPayload on IssueRelationHistoryPayload {
  __typename
  identifier
  type
}
fragment IssueLabel on IssueLabel {
  __typename
  lastAppliedAt
  color
  description
  name
  updatedAt
  inheritedFrom {
    id
  }
  parent {
    id
  }
  team {
    id
  }
  archivedAt
  createdAt
  id
  creator {
    id
  }
  retiredBy {
    id
  }
  isGroup
}`, { fragmentName: "IssueHistory" });
const IssueHistoryConnectionFragmentDoc = new TypedDocumentString(`
    fragment IssueHistoryConnection on IssueHistoryConnection {
  __typename
  nodes {
    ...IssueHistory
  }
  pageInfo {
    ...PageInfo
  }
}
    fragment ActorBot on ActorBot {
  __typename
  avatarUrl
  name
  userDisplayName
  subType
  type
  id
}
fragment IssueHistory on IssueHistory {
  __typename
  triageResponsibilityAutoAssigned
  relationChanges {
    ...IssueRelationHistoryPayload
  }
  addedLabelIds
  removedLabelIds
  actor {
    id
  }
  descriptionUpdatedBy {
    ...User
  }
  actors {
    ...User
  }
  fromDelegate {
    id
  }
  toDelegate {
    id
  }
  botActor {
    ...ActorBot
  }
  fromCycle {
    id
  }
  toCycle {
    id
  }
  attachmentId
  customerNeedId
  toCycleId
  toParentId
  toConvertedProjectId
  toProjectId
  toStateId
  fromCycleId
  fromParentId
  fromProjectId
  fromStateId
  fromTeamId
  toTeamId
  fromAssigneeId
  toAssigneeId
  actorId
  issueImport {
    ...IssueImport
  }
  issue {
    id
  }
  addedLabels {
    ...IssueLabel
  }
  removedLabels {
    ...IssueLabel
  }
  updatedAt
  attachment {
    id
  }
  toConvertedProject {
    id
  }
  fromParent {
    id
  }
  toParent {
    id
  }
  fromProject {
    id
  }
  toProject {
    id
  }
  fromState {
    id
  }
  toState {
    id
  }
  fromTeam {
    id
  }
  toTeam {
    id
  }
  archivedAt
  createdAt
  id
  toAssignee {
    id
  }
  fromAssignee {
    id
  }
  triageResponsibilityNotifiedUsers {
    ...User
  }
  fromDueDate
  toDueDate
  fromEstimate
  toEstimate
  fromPriority
  toPriority
  fromTitle
  toTitle
  archived
  autoArchived
  autoClosed
  trashed
  updatedDescription
}
fragment User on User {
  __typename
  statusUntilAt
  description
  avatarUrl
  createdIssueCount
  disableReason
  avatarBackgroundColor
  statusEmoji
  initials
  statusLabel
  updatedAt
  lastSeen
  timezone
  archivedAt
  createdAt
  id
  gitHubUserId
  displayName
  email
  name
  url
  active
  guest
  app
  admin
  owner
  isAssignable
  isMentionable
  isMe
  supportsAgentSessions
  canAccessAnyPublicTeam
  calendarHash
  inviteHash
}
fragment IssueImport on IssueImport {
  __typename
  progress
  errorMetadata
  csvFileUrl
  serviceMetadata
  teamName
  mapping
  displayName
  creatorId
  updatedAt
  service
  status
  archivedAt
  createdAt
  id
  error
}
fragment IssueRelationHistoryPayload on IssueRelationHistoryPayload {
  __typename
  identifier
  type
}
fragment IssueLabel on IssueLabel {
  __typename
  lastAppliedAt
  color
  description
  name
  updatedAt
  inheritedFrom {
    id
  }
  parent {
    id
  }
  team {
    id
  }
  archivedAt
  createdAt
  id
  creator {
    id
  }
  retiredBy {
    id
  }
  isGroup
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`, { fragmentName: "IssueHistoryConnection" });
const IssueImportCheckPayloadFragmentDoc = new TypedDocumentString(`
    fragment IssueImportCheckPayload on IssueImportCheckPayload {
  __typename
  success
}
    `, { fragmentName: "IssueImportCheckPayload" });
const IssueImportDeletePayloadFragmentDoc = new TypedDocumentString(`
    fragment IssueImportDeletePayload on IssueImportDeletePayload {
  __typename
  lastSyncId
  issueImport {
    ...IssueImport
  }
  success
}
    fragment IssueImport on IssueImport {
  __typename
  progress
  errorMetadata
  csvFileUrl
  serviceMetadata
  teamName
  mapping
  displayName
  creatorId
  updatedAt
  service
  status
  archivedAt
  createdAt
  id
  error
}`, { fragmentName: "IssueImportDeletePayload" });
const IssueImportPayloadFragmentDoc = new TypedDocumentString(`
    fragment IssueImportPayload on IssueImportPayload {
  __typename
  lastSyncId
  issueImport {
    ...IssueImport
  }
  success
}
    fragment IssueImport on IssueImport {
  __typename
  progress
  errorMetadata
  csvFileUrl
  serviceMetadata
  teamName
  mapping
  displayName
  creatorId
  updatedAt
  service
  status
  archivedAt
  createdAt
  id
  error
}`, { fragmentName: "IssueImportPayload" });
const IssueLabelConnectionFragmentDoc = new TypedDocumentString(`
    fragment IssueLabelConnection on IssueLabelConnection {
  __typename
  nodes {
    ...IssueLabel
  }
  pageInfo {
    ...PageInfo
  }
}
    fragment IssueLabel on IssueLabel {
  __typename
  lastAppliedAt
  color
  description
  name
  updatedAt
  inheritedFrom {
    id
  }
  parent {
    id
  }
  team {
    id
  }
  archivedAt
  createdAt
  id
  creator {
    id
  }
  retiredBy {
    id
  }
  isGroup
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`, { fragmentName: "IssueLabelConnection" });
const IssueLabelPayloadFragmentDoc = new TypedDocumentString(`
    fragment IssueLabelPayload on IssueLabelPayload {
  __typename
  lastSyncId
  issueLabel {
    id
  }
  success
}
    `, { fragmentName: "IssueLabelPayload" });
const IssuePayloadFragmentDoc = new TypedDocumentString(`
    fragment IssuePayload on IssuePayload {
  __typename
  lastSyncId
  issue {
    id
  }
  success
}
    `, { fragmentName: "IssuePayload" });
const IssuePriorityValueFragmentDoc = new TypedDocumentString(`
    fragment IssuePriorityValue on IssuePriorityValue {
  __typename
  label
  priority
}
    `, { fragmentName: "IssuePriorityValue" });
const IssueRelationFragmentDoc = new TypedDocumentString(`
    fragment IssueRelation on IssueRelation {
  __typename
  issue {
    id
  }
  updatedAt
  relatedIssue {
    id
  }
  type
  archivedAt
  createdAt
  id
}
    `, { fragmentName: "IssueRelation" });
const IssueRelationConnectionFragmentDoc = new TypedDocumentString(`
    fragment IssueRelationConnection on IssueRelationConnection {
  __typename
  nodes {
    ...IssueRelation
  }
  pageInfo {
    ...PageInfo
  }
}
    fragment IssueRelation on IssueRelation {
  __typename
  issue {
    id
  }
  updatedAt
  relatedIssue {
    id
  }
  type
  archivedAt
  createdAt
  id
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`, { fragmentName: "IssueRelationConnection" });
const IssueRelationPayloadFragmentDoc = new TypedDocumentString(`
    fragment IssueRelationPayload on IssueRelationPayload {
  __typename
  lastSyncId
  issueRelation {
    id
  }
  success
}
    `, { fragmentName: "IssueRelationPayload" });
const IssueSearchResultFragmentDoc = new TypedDocumentString(`
    fragment IssueSearchResult on IssueSearchResult {
  __typename
  trashed
  reactionData
  labelIds
  integrationSourceType
  url
  identifier
  priorityLabel
  metadata
  previousIdentifiers
  reactions {
    ...Reaction
  }
  customerTicketCount
  branchName
  delegate {
    id
  }
  botActor {
    ...ActorBot
  }
  sourceComment {
    id
  }
  cycle {
    id
  }
  dueDate
  estimate
  syncedWith {
    ...ExternalEntityInfo
  }
  externalUserCreator {
    id
  }
  asksExternalUserRequester {
    id
  }
  asksRequester {
    id
  }
  description
  title
  number
  lastAppliedTemplate {
    id
  }
  updatedAt
  boardOrder
  prioritySortOrder
  sortOrder
  subIssueSortOrder
  parent {
    id
  }
  priority
  project {
    id
  }
  projectMilestone {
    id
  }
  recurringIssueTemplate {
    id
  }
  team {
    id
  }
  archivedAt
  createdAt
  startedTriageAt
  triagedAt
  addedToCycleAt
  addedToProjectAt
  addedToTeamAt
  autoArchivedAt
  autoClosedAt
  canceledAt
  completedAt
  startedAt
  slaStartedAt
  slaBreachesAt
  slaHighRiskAt
  slaMediumRiskAt
  snoozedUntilAt
  slaType
  id
  assignee {
    id
  }
  creator {
    id
  }
  snoozedBy {
    id
  }
  favorite {
    id
  }
  state {
    id
  }
}
    fragment ActorBot on ActorBot {
  __typename
  avatarUrl
  name
  userDisplayName
  subType
  type
  id
}
fragment Reaction on Reaction {
  __typename
  emoji
  comment {
    id
  }
  externalUser {
    id
  }
  initiativeUpdate {
    id
  }
  issue {
    id
  }
  updatedAt
  projectUpdate {
    id
  }
  archivedAt
  createdAt
  id
  user {
    id
  }
}
fragment ExternalEntityInfo on ExternalEntityInfo {
  __typename
  metadata {
    ... on ExternalEntityInfoGithubMetadata {
      ...ExternalEntityInfoGithubMetadata
    }
    ... on ExternalEntityInfoJiraMetadata {
      ...ExternalEntityInfoJiraMetadata
    }
    ... on ExternalEntitySlackMetadata {
      ...ExternalEntitySlackMetadata
    }
  }
  id
  service
}
fragment ExternalEntityInfoGithubMetadata on ExternalEntityInfoGithubMetadata {
  __typename
  number
  owner
  repo
}
fragment ExternalEntityInfoJiraMetadata on ExternalEntityInfoJiraMetadata {
  __typename
  issueTypeId
  projectId
  issueKey
}
fragment ExternalEntitySlackMetadata on ExternalEntitySlackMetadata {
  __typename
  messageUrl
  channelId
  channelName
  isFromSlack
}`, { fragmentName: "IssueSearchResult" });
const IssueSearchPayloadFragmentDoc = new TypedDocumentString(`
    fragment IssueSearchPayload on IssueSearchPayload {
  __typename
  archivePayload {
    ...ArchiveResponse
  }
  totalCount
  nodes {
    ...IssueSearchResult
  }
  pageInfo {
    ...PageInfo
  }
}
    fragment ActorBot on ActorBot {
  __typename
  avatarUrl
  name
  userDisplayName
  subType
  type
  id
}
fragment Reaction on Reaction {
  __typename
  emoji
  comment {
    id
  }
  externalUser {
    id
  }
  initiativeUpdate {
    id
  }
  issue {
    id
  }
  updatedAt
  projectUpdate {
    id
  }
  archivedAt
  createdAt
  id
  user {
    id
  }
}
fragment ArchiveResponse on ArchiveResponse {
  __typename
  archive
  totalCount
  databaseVersion
  includesDependencies
}
fragment ExternalEntityInfo on ExternalEntityInfo {
  __typename
  metadata {
    ... on ExternalEntityInfoGithubMetadata {
      ...ExternalEntityInfoGithubMetadata
    }
    ... on ExternalEntityInfoJiraMetadata {
      ...ExternalEntityInfoJiraMetadata
    }
    ... on ExternalEntitySlackMetadata {
      ...ExternalEntitySlackMetadata
    }
  }
  id
  service
}
fragment ExternalEntityInfoGithubMetadata on ExternalEntityInfoGithubMetadata {
  __typename
  number
  owner
  repo
}
fragment ExternalEntityInfoJiraMetadata on ExternalEntityInfoJiraMetadata {
  __typename
  issueTypeId
  projectId
  issueKey
}
fragment ExternalEntitySlackMetadata on ExternalEntitySlackMetadata {
  __typename
  messageUrl
  channelId
  channelName
  isFromSlack
}
fragment IssueSearchResult on IssueSearchResult {
  __typename
  trashed
  reactionData
  labelIds
  integrationSourceType
  url
  identifier
  priorityLabel
  metadata
  previousIdentifiers
  reactions {
    ...Reaction
  }
  customerTicketCount
  branchName
  delegate {
    id
  }
  botActor {
    ...ActorBot
  }
  sourceComment {
    id
  }
  cycle {
    id
  }
  dueDate
  estimate
  syncedWith {
    ...ExternalEntityInfo
  }
  externalUserCreator {
    id
  }
  asksExternalUserRequester {
    id
  }
  asksRequester {
    id
  }
  description
  title
  number
  lastAppliedTemplate {
    id
  }
  updatedAt
  boardOrder
  prioritySortOrder
  sortOrder
  subIssueSortOrder
  parent {
    id
  }
  priority
  project {
    id
  }
  projectMilestone {
    id
  }
  recurringIssueTemplate {
    id
  }
  team {
    id
  }
  archivedAt
  createdAt
  startedTriageAt
  triagedAt
  addedToCycleAt
  addedToProjectAt
  addedToTeamAt
  autoArchivedAt
  autoClosedAt
  canceledAt
  completedAt
  startedAt
  slaStartedAt
  slaBreachesAt
  slaHighRiskAt
  slaMediumRiskAt
  snoozedUntilAt
  slaType
  id
  assignee {
    id
  }
  creator {
    id
  }
  snoozedBy {
    id
  }
  favorite {
    id
  }
  state {
    id
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`, { fragmentName: "IssueSearchPayload" });
const IssueStateSpanFragmentDoc = new TypedDocumentString(`
    fragment IssueStateSpan on IssueStateSpan {
  __typename
  startedAt
  endedAt
  id
  state {
    id
  }
  stateId
}
    `, { fragmentName: "IssueStateSpan" });
const IssueStateSpanConnectionFragmentDoc = new TypedDocumentString(`
    fragment IssueStateSpanConnection on IssueStateSpanConnection {
  __typename
  nodes {
    ...IssueStateSpan
  }
  pageInfo {
    ...PageInfo
  }
}
    fragment IssueStateSpan on IssueStateSpan {
  __typename
  startedAt
  endedAt
  id
  state {
    id
  }
  stateId
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`, { fragmentName: "IssueStateSpanConnection" });
const IssueSuggestionMetadataFragmentDoc = new TypedDocumentString(`
    fragment IssueSuggestionMetadata on IssueSuggestionMetadata {
  __typename
  appliedAutomationRuleId
  classification
  evalLogId
  rank
  reasons
  score
  variant
}
    `, { fragmentName: "IssueSuggestionMetadata" });
const IssueSuggestionFragmentDoc = new TypedDocumentString(`
    fragment IssueSuggestion on IssueSuggestion {
  __typename
  updatedAt
  archivedAt
  createdAt
  id
  dismissalReason
  issue {
    id
  }
  issueId
  metadata {
    ...IssueSuggestionMetadata
  }
  state
  stateChangedAt
  suggestedIssue {
    id
  }
  suggestedIssueId
  suggestedLabel {
    id
  }
  suggestedLabelId
  suggestedProject {
    id
  }
  suggestedTeam {
    id
  }
  suggestedUser {
    id
  }
  suggestedUserId
  type
}
    fragment IssueSuggestionMetadata on IssueSuggestionMetadata {
  __typename
  appliedAutomationRuleId
  classification
  evalLogId
  rank
  reasons
  score
  variant
}`, { fragmentName: "IssueSuggestion" });
const IssueSuggestionConnectionFragmentDoc = new TypedDocumentString(`
    fragment IssueSuggestionConnection on IssueSuggestionConnection {
  __typename
  nodes {
    ...IssueSuggestion
  }
  pageInfo {
    ...PageInfo
  }
}
    fragment IssueSuggestion on IssueSuggestion {
  __typename
  updatedAt
  archivedAt
  createdAt
  id
  dismissalReason
  issue {
    id
  }
  issueId
  metadata {
    ...IssueSuggestionMetadata
  }
  state
  stateChangedAt
  suggestedIssue {
    id
  }
  suggestedIssueId
  suggestedLabel {
    id
  }
  suggestedLabelId
  suggestedProject {
    id
  }
  suggestedTeam {
    id
  }
  suggestedUser {
    id
  }
  suggestedUserId
  type
}
fragment IssueSuggestionMetadata on IssueSuggestionMetadata {
  __typename
  appliedAutomationRuleId
  classification
  evalLogId
  rank
  reasons
  score
  variant
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`, { fragmentName: "IssueSuggestionConnection" });
const IssueTitleSuggestionFromCustomerRequestPayloadFragmentDoc = new TypedDocumentString(`
    fragment IssueTitleSuggestionFromCustomerRequestPayload on IssueTitleSuggestionFromCustomerRequestPayload {
  __typename
  lastSyncId
  title
}
    `, { fragmentName: "IssueTitleSuggestionFromCustomerRequestPayload" });
const LogoutResponseFragmentDoc = new TypedDocumentString(`
    fragment LogoutResponse on LogoutResponse {
  __typename
  success
}
    `, { fragmentName: "LogoutResponse" });
const NodeFragmentDoc = new TypedDocumentString(`
    fragment Node on Node {
  __typename
  id
}
    `, { fragmentName: "Node" });
const NotificationBatchActionPayloadFragmentDoc = new TypedDocumentString(`
    fragment NotificationBatchActionPayload on NotificationBatchActionPayload {
  __typename
  lastSyncId
  notifications {
    ...Notification
  }
  success
}
    fragment ActorBot on ActorBot {
  __typename
  avatarUrl
  name
  userDisplayName
  subType
  type
  id
}
fragment CustomerNeedNotification on CustomerNeedNotification {
  __typename
  type
  customerNeedId
  botActor {
    ...ActorBot
  }
  category
  customerNeed {
    id
  }
  externalUserActor {
    id
  }
  relatedIssue {
    id
  }
  updatedAt
  relatedProject {
    id
  }
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment CustomerNotification on CustomerNotification {
  __typename
  type
  customerId
  botActor {
    ...ActorBot
  }
  category
  customer {
    id
  }
  externalUserActor {
    id
  }
  updatedAt
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment DocumentNotification on DocumentNotification {
  __typename
  reactionEmoji
  type
  commentId
  documentId
  parentCommentId
  botActor {
    ...ActorBot
  }
  category
  externalUserActor {
    id
  }
  updatedAt
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment Notification on Notification {
  __typename
  type
  botActor {
    ...ActorBot
  }
  category
  externalUserActor {
    id
  }
  updatedAt
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
  ... on CustomerNeedNotification {
    ...CustomerNeedNotification
  }
  ... on CustomerNotification {
    ...CustomerNotification
  }
  ... on DocumentNotification {
    ...DocumentNotification
  }
  ... on InitiativeNotification {
    ...InitiativeNotification
  }
  ... on IssueNotification {
    ...IssueNotification
  }
  ... on OauthClientApprovalNotification {
    ...OauthClientApprovalNotification
  }
  ... on PostNotification {
    ...PostNotification
  }
  ... on ProjectNotification {
    ...ProjectNotification
  }
  ... on PullRequestNotification {
    ...PullRequestNotification
  }
}
fragment PostNotification on PostNotification {
  __typename
  reactionEmoji
  type
  commentId
  parentCommentId
  postId
  botActor {
    ...ActorBot
  }
  category
  externalUserActor {
    id
  }
  updatedAt
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment ProjectNotification on ProjectNotification {
  __typename
  reactionEmoji
  type
  commentId
  parentCommentId
  projectId
  projectMilestoneId
  projectUpdateId
  botActor {
    ...ActorBot
  }
  category
  comment {
    id
  }
  document {
    id
  }
  externalUserActor {
    id
  }
  updatedAt
  parentComment {
    id
  }
  project {
    id
  }
  projectUpdate {
    id
  }
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment PullRequestNotification on PullRequestNotification {
  __typename
  type
  pullRequestCommentId
  pullRequestId
  botActor {
    ...ActorBot
  }
  category
  externalUserActor {
    id
  }
  updatedAt
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment InitiativeNotification on InitiativeNotification {
  __typename
  reactionEmoji
  type
  commentId
  initiativeId
  initiativeUpdateId
  parentCommentId
  botActor {
    ...ActorBot
  }
  category
  comment {
    id
  }
  document {
    id
  }
  externalUserActor {
    id
  }
  initiative {
    id
  }
  initiativeUpdate {
    id
  }
  updatedAt
  parentComment {
    id
  }
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment IssueNotification on IssueNotification {
  __typename
  reactionEmoji
  type
  commentId
  issueId
  parentCommentId
  botActor {
    ...ActorBot
  }
  category
  comment {
    id
  }
  externalUserActor {
    id
  }
  issue {
    id
  }
  updatedAt
  parentComment {
    id
  }
  subscriptions {
    ...NotificationSubscription
  }
  team {
    id
  }
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment OauthClientApprovalNotification on OauthClientApprovalNotification {
  __typename
  type
  oauthClientApprovalId
  oauthClientApproval {
    ...OauthClientApproval
  }
  botActor {
    ...ActorBot
  }
  category
  externalUserActor {
    id
  }
  updatedAt
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment NotificationSubscription on NotificationSubscription {
  __typename
  customView {
    id
  }
  cycle {
    id
  }
  initiative {
    id
  }
  label {
    id
  }
  project {
    id
  }
  customer {
    id
  }
  updatedAt
  team {
    id
  }
  archivedAt
  createdAt
  userContextViewType
  contextViewType
  id
  subscriber {
    id
  }
  user {
    id
  }
  active
}
fragment OauthClientApproval on OauthClientApproval {
  __typename
  newlyRequestedScopes
  updatedAt
  requesterId
  responderId
  requestReason
  denyReason
  scopes
  status
  archivedAt
  createdAt
  id
  oauthClientId
}`, { fragmentName: "NotificationBatchActionPayload" });
const NotificationConnectionFragmentDoc = new TypedDocumentString(`
    fragment NotificationConnection on NotificationConnection {
  __typename
  nodes {
    ...Notification
  }
  pageInfo {
    ...PageInfo
  }
}
    fragment ActorBot on ActorBot {
  __typename
  avatarUrl
  name
  userDisplayName
  subType
  type
  id
}
fragment CustomerNeedNotification on CustomerNeedNotification {
  __typename
  type
  customerNeedId
  botActor {
    ...ActorBot
  }
  category
  customerNeed {
    id
  }
  externalUserActor {
    id
  }
  relatedIssue {
    id
  }
  updatedAt
  relatedProject {
    id
  }
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment CustomerNotification on CustomerNotification {
  __typename
  type
  customerId
  botActor {
    ...ActorBot
  }
  category
  customer {
    id
  }
  externalUserActor {
    id
  }
  updatedAt
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment DocumentNotification on DocumentNotification {
  __typename
  reactionEmoji
  type
  commentId
  documentId
  parentCommentId
  botActor {
    ...ActorBot
  }
  category
  externalUserActor {
    id
  }
  updatedAt
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment Notification on Notification {
  __typename
  type
  botActor {
    ...ActorBot
  }
  category
  externalUserActor {
    id
  }
  updatedAt
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
  ... on CustomerNeedNotification {
    ...CustomerNeedNotification
  }
  ... on CustomerNotification {
    ...CustomerNotification
  }
  ... on DocumentNotification {
    ...DocumentNotification
  }
  ... on InitiativeNotification {
    ...InitiativeNotification
  }
  ... on IssueNotification {
    ...IssueNotification
  }
  ... on OauthClientApprovalNotification {
    ...OauthClientApprovalNotification
  }
  ... on PostNotification {
    ...PostNotification
  }
  ... on ProjectNotification {
    ...ProjectNotification
  }
  ... on PullRequestNotification {
    ...PullRequestNotification
  }
}
fragment PostNotification on PostNotification {
  __typename
  reactionEmoji
  type
  commentId
  parentCommentId
  postId
  botActor {
    ...ActorBot
  }
  category
  externalUserActor {
    id
  }
  updatedAt
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment ProjectNotification on ProjectNotification {
  __typename
  reactionEmoji
  type
  commentId
  parentCommentId
  projectId
  projectMilestoneId
  projectUpdateId
  botActor {
    ...ActorBot
  }
  category
  comment {
    id
  }
  document {
    id
  }
  externalUserActor {
    id
  }
  updatedAt
  parentComment {
    id
  }
  project {
    id
  }
  projectUpdate {
    id
  }
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment PullRequestNotification on PullRequestNotification {
  __typename
  type
  pullRequestCommentId
  pullRequestId
  botActor {
    ...ActorBot
  }
  category
  externalUserActor {
    id
  }
  updatedAt
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment InitiativeNotification on InitiativeNotification {
  __typename
  reactionEmoji
  type
  commentId
  initiativeId
  initiativeUpdateId
  parentCommentId
  botActor {
    ...ActorBot
  }
  category
  comment {
    id
  }
  document {
    id
  }
  externalUserActor {
    id
  }
  initiative {
    id
  }
  initiativeUpdate {
    id
  }
  updatedAt
  parentComment {
    id
  }
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment IssueNotification on IssueNotification {
  __typename
  reactionEmoji
  type
  commentId
  issueId
  parentCommentId
  botActor {
    ...ActorBot
  }
  category
  comment {
    id
  }
  externalUserActor {
    id
  }
  issue {
    id
  }
  updatedAt
  parentComment {
    id
  }
  subscriptions {
    ...NotificationSubscription
  }
  team {
    id
  }
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment OauthClientApprovalNotification on OauthClientApprovalNotification {
  __typename
  type
  oauthClientApprovalId
  oauthClientApproval {
    ...OauthClientApproval
  }
  botActor {
    ...ActorBot
  }
  category
  externalUserActor {
    id
  }
  updatedAt
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment NotificationSubscription on NotificationSubscription {
  __typename
  customView {
    id
  }
  cycle {
    id
  }
  initiative {
    id
  }
  label {
    id
  }
  project {
    id
  }
  customer {
    id
  }
  updatedAt
  team {
    id
  }
  archivedAt
  createdAt
  userContextViewType
  contextViewType
  id
  subscriber {
    id
  }
  user {
    id
  }
  active
}
fragment OauthClientApproval on OauthClientApproval {
  __typename
  newlyRequestedScopes
  updatedAt
  requesterId
  responderId
  requestReason
  denyReason
  scopes
  status
  archivedAt
  createdAt
  id
  oauthClientId
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`, { fragmentName: "NotificationConnection" });
const NotificationPayloadFragmentDoc = new TypedDocumentString(`
    fragment NotificationPayload on NotificationPayload {
  __typename
  lastSyncId
  notification {
    ...Notification
  }
  success
}
    fragment ActorBot on ActorBot {
  __typename
  avatarUrl
  name
  userDisplayName
  subType
  type
  id
}
fragment CustomerNeedNotification on CustomerNeedNotification {
  __typename
  type
  customerNeedId
  botActor {
    ...ActorBot
  }
  category
  customerNeed {
    id
  }
  externalUserActor {
    id
  }
  relatedIssue {
    id
  }
  updatedAt
  relatedProject {
    id
  }
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment CustomerNotification on CustomerNotification {
  __typename
  type
  customerId
  botActor {
    ...ActorBot
  }
  category
  customer {
    id
  }
  externalUserActor {
    id
  }
  updatedAt
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment DocumentNotification on DocumentNotification {
  __typename
  reactionEmoji
  type
  commentId
  documentId
  parentCommentId
  botActor {
    ...ActorBot
  }
  category
  externalUserActor {
    id
  }
  updatedAt
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment Notification on Notification {
  __typename
  type
  botActor {
    ...ActorBot
  }
  category
  externalUserActor {
    id
  }
  updatedAt
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
  ... on CustomerNeedNotification {
    ...CustomerNeedNotification
  }
  ... on CustomerNotification {
    ...CustomerNotification
  }
  ... on DocumentNotification {
    ...DocumentNotification
  }
  ... on InitiativeNotification {
    ...InitiativeNotification
  }
  ... on IssueNotification {
    ...IssueNotification
  }
  ... on OauthClientApprovalNotification {
    ...OauthClientApprovalNotification
  }
  ... on PostNotification {
    ...PostNotification
  }
  ... on ProjectNotification {
    ...ProjectNotification
  }
  ... on PullRequestNotification {
    ...PullRequestNotification
  }
}
fragment PostNotification on PostNotification {
  __typename
  reactionEmoji
  type
  commentId
  parentCommentId
  postId
  botActor {
    ...ActorBot
  }
  category
  externalUserActor {
    id
  }
  updatedAt
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment ProjectNotification on ProjectNotification {
  __typename
  reactionEmoji
  type
  commentId
  parentCommentId
  projectId
  projectMilestoneId
  projectUpdateId
  botActor {
    ...ActorBot
  }
  category
  comment {
    id
  }
  document {
    id
  }
  externalUserActor {
    id
  }
  updatedAt
  parentComment {
    id
  }
  project {
    id
  }
  projectUpdate {
    id
  }
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment PullRequestNotification on PullRequestNotification {
  __typename
  type
  pullRequestCommentId
  pullRequestId
  botActor {
    ...ActorBot
  }
  category
  externalUserActor {
    id
  }
  updatedAt
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment InitiativeNotification on InitiativeNotification {
  __typename
  reactionEmoji
  type
  commentId
  initiativeId
  initiativeUpdateId
  parentCommentId
  botActor {
    ...ActorBot
  }
  category
  comment {
    id
  }
  document {
    id
  }
  externalUserActor {
    id
  }
  initiative {
    id
  }
  initiativeUpdate {
    id
  }
  updatedAt
  parentComment {
    id
  }
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment IssueNotification on IssueNotification {
  __typename
  reactionEmoji
  type
  commentId
  issueId
  parentCommentId
  botActor {
    ...ActorBot
  }
  category
  comment {
    id
  }
  externalUserActor {
    id
  }
  issue {
    id
  }
  updatedAt
  parentComment {
    id
  }
  subscriptions {
    ...NotificationSubscription
  }
  team {
    id
  }
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment OauthClientApprovalNotification on OauthClientApprovalNotification {
  __typename
  type
  oauthClientApprovalId
  oauthClientApproval {
    ...OauthClientApproval
  }
  botActor {
    ...ActorBot
  }
  category
  externalUserActor {
    id
  }
  updatedAt
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment NotificationSubscription on NotificationSubscription {
  __typename
  customView {
    id
  }
  cycle {
    id
  }
  initiative {
    id
  }
  label {
    id
  }
  project {
    id
  }
  customer {
    id
  }
  updatedAt
  team {
    id
  }
  archivedAt
  createdAt
  userContextViewType
  contextViewType
  id
  subscriber {
    id
  }
  user {
    id
  }
  active
}
fragment OauthClientApproval on OauthClientApproval {
  __typename
  newlyRequestedScopes
  updatedAt
  requesterId
  responderId
  requestReason
  denyReason
  scopes
  status
  archivedAt
  createdAt
  id
  oauthClientId
}`, { fragmentName: "NotificationPayload" });
const NotificationSubscriptionConnectionFragmentDoc = new TypedDocumentString(`
    fragment NotificationSubscriptionConnection on NotificationSubscriptionConnection {
  __typename
  nodes {
    ...NotificationSubscription
  }
  pageInfo {
    ...PageInfo
  }
}
    fragment NotificationSubscription on NotificationSubscription {
  __typename
  customView {
    id
  }
  cycle {
    id
  }
  initiative {
    id
  }
  label {
    id
  }
  project {
    id
  }
  customer {
    id
  }
  updatedAt
  team {
    id
  }
  archivedAt
  createdAt
  userContextViewType
  contextViewType
  id
  subscriber {
    id
  }
  user {
    id
  }
  active
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`, { fragmentName: "NotificationSubscriptionConnection" });
const NotificationSubscriptionPayloadFragmentDoc = new TypedDocumentString(`
    fragment NotificationSubscriptionPayload on NotificationSubscriptionPayload {
  __typename
  lastSyncId
  notificationSubscription {
    ...NotificationSubscription
  }
  success
}
    fragment NotificationSubscription on NotificationSubscription {
  __typename
  customView {
    id
  }
  cycle {
    id
  }
  initiative {
    id
  }
  label {
    id
  }
  project {
    id
  }
  customer {
    id
  }
  updatedAt
  team {
    id
  }
  archivedAt
  createdAt
  userContextViewType
  contextViewType
  id
  subscriber {
    id
  }
  user {
    id
  }
  active
}`, { fragmentName: "NotificationSubscriptionPayload" });
const OrganizationAcceptedOrExpiredInviteDetailsPayloadFragmentDoc = new TypedDocumentString(`
    fragment OrganizationAcceptedOrExpiredInviteDetailsPayload on OrganizationAcceptedOrExpiredInviteDetailsPayload {
  __typename
  status
}
    `, { fragmentName: "OrganizationAcceptedOrExpiredInviteDetailsPayload" });
const OrganizationCancelDeletePayloadFragmentDoc = new TypedDocumentString(`
    fragment OrganizationCancelDeletePayload on OrganizationCancelDeletePayload {
  __typename
  success
}
    `, { fragmentName: "OrganizationCancelDeletePayload" });
const OrganizationDeletePayloadFragmentDoc = new TypedDocumentString(`
    fragment OrganizationDeletePayload on OrganizationDeletePayload {
  __typename
  success
}
    `, { fragmentName: "OrganizationDeletePayload" });
const OrganizationExistsPayloadFragmentDoc = new TypedDocumentString(`
    fragment OrganizationExistsPayload on OrganizationExistsPayload {
  __typename
  success
  exists
}
    `, { fragmentName: "OrganizationExistsPayload" });
const OrganizationInviteFragmentDoc = new TypedDocumentString(`
    fragment OrganizationInvite on OrganizationInvite {
  __typename
  metadata
  external
  email
  updatedAt
  archivedAt
  createdAt
  acceptedAt
  expiresAt
  id
  role
  inviter {
    id
  }
  invitee {
    id
  }
}
    `, { fragmentName: "OrganizationInvite" });
const OrganizationInviteConnectionFragmentDoc = new TypedDocumentString(`
    fragment OrganizationInviteConnection on OrganizationInviteConnection {
  __typename
  nodes {
    ...OrganizationInvite
  }
  pageInfo {
    ...PageInfo
  }
}
    fragment OrganizationInvite on OrganizationInvite {
  __typename
  metadata
  external
  email
  updatedAt
  archivedAt
  createdAt
  acceptedAt
  expiresAt
  id
  role
  inviter {
    id
  }
  invitee {
    id
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`, { fragmentName: "OrganizationInviteConnection" });
const OrganizationInviteFullDetailsPayloadFragmentDoc = new TypedDocumentString(`
    fragment OrganizationInviteFullDetailsPayload on OrganizationInviteFullDetailsPayload {
  __typename
  allowedAuthServices
  organizationId
  organizationName
  email
  inviter
  status
  organizationLogoUrl
  role
  createdAt
  accepted
  expired
}
    `, { fragmentName: "OrganizationInviteFullDetailsPayload" });
const OrganizationInvitePayloadFragmentDoc = new TypedDocumentString(`
    fragment OrganizationInvitePayload on OrganizationInvitePayload {
  __typename
  lastSyncId
  organizationInvite {
    id
  }
  success
}
    `, { fragmentName: "OrganizationInvitePayload" });
const OrganizationMetaFragmentDoc = new TypedDocumentString(`
    fragment OrganizationMeta on OrganizationMeta {
  __typename
  allowedAuthServices
  region
}
    `, { fragmentName: "OrganizationMeta" });
const OrganizationPayloadFragmentDoc = new TypedDocumentString(`
    fragment OrganizationPayload on OrganizationPayload {
  __typename
  lastSyncId
  success
}
    `, { fragmentName: "OrganizationPayload" });
const OrganizationStartTrialPayloadFragmentDoc = new TypedDocumentString(`
    fragment OrganizationStartTrialPayload on OrganizationStartTrialPayload {
  __typename
  success
}
    `, { fragmentName: "OrganizationStartTrialPayload" });
const PasskeyLoginStartResponseFragmentDoc = new TypedDocumentString(`
    fragment PasskeyLoginStartResponse on PasskeyLoginStartResponse {
  __typename
  options
  success
}
    `, { fragmentName: "PasskeyLoginStartResponse" });
const ProjectFragmentDoc = new TypedDocumentString(`
    fragment Project on Project {
  __typename
  trashed
  labelIds
  url
  integrationsSettings {
    id
  }
  documentContent {
    ...DocumentContent
  }
  updateRemindersDay
  targetDate
  startDate
  updateReminderFrequency
  health
  updateRemindersHour
  icon
  lastUpdate {
    id
  }
  lastAppliedTemplate {
    id
  }
  updatedAt
  updateReminderFrequencyInWeeks
  completedScopeHistory
  completedIssueCountHistory
  inProgressScopeHistory
  progress
  scope
  priorityLabel
  priority
  lead {
    id
  }
  convertedFromIssue {
    id
  }
  color
  content
  description
  name
  slugId
  targetDateResolution
  startDateResolution
  frequencyResolution
  prioritySortOrder
  sortOrder
  status {
    id
  }
  archivedAt
  createdAt
  healthUpdatedAt
  autoArchivedAt
  canceledAt
  completedAt
  startedAt
  projectUpdateRemindersPausedUntilAt
  scopeHistory
  issueCountHistory
  id
  creator {
    id
  }
  favorite {
    id
  }
  slackIssueComments
  slackNewIssue
  slackIssueStatuses
  state
}
    fragment DocumentContent on DocumentContent {
  __typename
  aiPromptRules {
    ...AiPromptRules
  }
  content
  contentState
  document {
    id
  }
  initiative {
    id
  }
  issue {
    id
  }
  updatedAt
  projectMilestone {
    id
  }
  project {
    id
  }
  restoredAt
  archivedAt
  createdAt
  id
}
fragment AiPromptRules on AiPromptRules {
  __typename
  updatedAt
  archivedAt
  createdAt
  id
  updatedBy {
    id
  }
}`, { fragmentName: "Project" });
const ProjectConnectionFragmentDoc = new TypedDocumentString(`
    fragment ProjectConnection on ProjectConnection {
  __typename
  nodes {
    ...Project
  }
  pageInfo {
    ...PageInfo
  }
}
    fragment DocumentContent on DocumentContent {
  __typename
  aiPromptRules {
    ...AiPromptRules
  }
  content
  contentState
  document {
    id
  }
  initiative {
    id
  }
  issue {
    id
  }
  updatedAt
  projectMilestone {
    id
  }
  project {
    id
  }
  restoredAt
  archivedAt
  createdAt
  id
}
fragment Project on Project {
  __typename
  trashed
  labelIds
  url
  integrationsSettings {
    id
  }
  documentContent {
    ...DocumentContent
  }
  updateRemindersDay
  targetDate
  startDate
  updateReminderFrequency
  health
  updateRemindersHour
  icon
  lastUpdate {
    id
  }
  lastAppliedTemplate {
    id
  }
  updatedAt
  updateReminderFrequencyInWeeks
  completedScopeHistory
  completedIssueCountHistory
  inProgressScopeHistory
  progress
  scope
  priorityLabel
  priority
  lead {
    id
  }
  convertedFromIssue {
    id
  }
  color
  content
  description
  name
  slugId
  targetDateResolution
  startDateResolution
  frequencyResolution
  prioritySortOrder
  sortOrder
  status {
    id
  }
  archivedAt
  createdAt
  healthUpdatedAt
  autoArchivedAt
  canceledAt
  completedAt
  startedAt
  projectUpdateRemindersPausedUntilAt
  scopeHistory
  issueCountHistory
  id
  creator {
    id
  }
  favorite {
    id
  }
  slackIssueComments
  slackNewIssue
  slackIssueStatuses
  state
}
fragment AiPromptRules on AiPromptRules {
  __typename
  updatedAt
  archivedAt
  createdAt
  id
  updatedBy {
    id
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`, { fragmentName: "ProjectConnection" });
const ProjectFilterSuggestionPayloadFragmentDoc = new TypedDocumentString(`
    fragment ProjectFilterSuggestionPayload on ProjectFilterSuggestionPayload {
  __typename
  filter
  logId
}
    `, { fragmentName: "ProjectFilterSuggestionPayload" });
const ProjectHistoryFragmentDoc = new TypedDocumentString(`
    fragment ProjectHistory on ProjectHistory {
  __typename
  entries
  updatedAt
  project {
    id
  }
  archivedAt
  createdAt
  id
}
    `, { fragmentName: "ProjectHistory" });
const ProjectHistoryConnectionFragmentDoc = new TypedDocumentString(`
    fragment ProjectHistoryConnection on ProjectHistoryConnection {
  __typename
  nodes {
    ...ProjectHistory
  }
  pageInfo {
    ...PageInfo
  }
}
    fragment ProjectHistory on ProjectHistory {
  __typename
  entries
  updatedAt
  project {
    id
  }
  archivedAt
  createdAt
  id
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`, { fragmentName: "ProjectHistoryConnection" });
const ProjectLabelFragmentDoc = new TypedDocumentString(`
    fragment ProjectLabel on ProjectLabel {
  __typename
  lastAppliedAt
  color
  description
  name
  updatedAt
  parent {
    id
  }
  archivedAt
  createdAt
  id
  creator {
    id
  }
  retiredBy {
    id
  }
  isGroup
}
    `, { fragmentName: "ProjectLabel" });
const ProjectLabelConnectionFragmentDoc = new TypedDocumentString(`
    fragment ProjectLabelConnection on ProjectLabelConnection {
  __typename
  nodes {
    ...ProjectLabel
  }
  pageInfo {
    ...PageInfo
  }
}
    fragment ProjectLabel on ProjectLabel {
  __typename
  lastAppliedAt
  color
  description
  name
  updatedAt
  parent {
    id
  }
  archivedAt
  createdAt
  id
  creator {
    id
  }
  retiredBy {
    id
  }
  isGroup
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`, { fragmentName: "ProjectLabelConnection" });
const ProjectLabelPayloadFragmentDoc = new TypedDocumentString(`
    fragment ProjectLabelPayload on ProjectLabelPayload {
  __typename
  lastSyncId
  projectLabel {
    id
  }
  success
}
    `, { fragmentName: "ProjectLabelPayload" });
const ProjectMilestoneFragmentDoc = new TypedDocumentString(`
    fragment ProjectMilestone on ProjectMilestone {
  __typename
  documentContent {
    ...DocumentContent
  }
  updatedAt
  name
  sortOrder
  targetDate
  progress
  description
  project {
    id
  }
  status
  archivedAt
  createdAt
  id
}
    fragment DocumentContent on DocumentContent {
  __typename
  aiPromptRules {
    ...AiPromptRules
  }
  content
  contentState
  document {
    id
  }
  initiative {
    id
  }
  issue {
    id
  }
  updatedAt
  projectMilestone {
    id
  }
  project {
    id
  }
  restoredAt
  archivedAt
  createdAt
  id
}
fragment AiPromptRules on AiPromptRules {
  __typename
  updatedAt
  archivedAt
  createdAt
  id
  updatedBy {
    id
  }
}`, { fragmentName: "ProjectMilestone" });
const ProjectMilestoneConnectionFragmentDoc = new TypedDocumentString(`
    fragment ProjectMilestoneConnection on ProjectMilestoneConnection {
  __typename
  nodes {
    ...ProjectMilestone
  }
  pageInfo {
    ...PageInfo
  }
}
    fragment DocumentContent on DocumentContent {
  __typename
  aiPromptRules {
    ...AiPromptRules
  }
  content
  contentState
  document {
    id
  }
  initiative {
    id
  }
  issue {
    id
  }
  updatedAt
  projectMilestone {
    id
  }
  project {
    id
  }
  restoredAt
  archivedAt
  createdAt
  id
}
fragment ProjectMilestone on ProjectMilestone {
  __typename
  documentContent {
    ...DocumentContent
  }
  updatedAt
  name
  sortOrder
  targetDate
  progress
  description
  project {
    id
  }
  status
  archivedAt
  createdAt
  id
}
fragment AiPromptRules on AiPromptRules {
  __typename
  updatedAt
  archivedAt
  createdAt
  id
  updatedBy {
    id
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`, { fragmentName: "ProjectMilestoneConnection" });
const ProjectMilestoneMoveIssueToTeamFragmentDoc = new TypedDocumentString(`
    fragment ProjectMilestoneMoveIssueToTeam on ProjectMilestoneMoveIssueToTeam {
  __typename
  issueId
  teamId
}
    `, { fragmentName: "ProjectMilestoneMoveIssueToTeam" });
const ProjectMilestoneMoveProjectTeamsFragmentDoc = new TypedDocumentString(`
    fragment ProjectMilestoneMoveProjectTeams on ProjectMilestoneMoveProjectTeams {
  __typename
  projectId
  teamIds
}
    `, { fragmentName: "ProjectMilestoneMoveProjectTeams" });
const ProjectMilestoneMovePayloadFragmentDoc = new TypedDocumentString(`
    fragment ProjectMilestoneMovePayload on ProjectMilestoneMovePayload {
  __typename
  previousIssueTeamIds {
    ...ProjectMilestoneMoveIssueToTeam
  }
  previousProjectTeamIds {
    ...ProjectMilestoneMoveProjectTeams
  }
  lastSyncId
  projectMilestone {
    id
  }
  success
}
    fragment ProjectMilestoneMoveIssueToTeam on ProjectMilestoneMoveIssueToTeam {
  __typename
  issueId
  teamId
}
fragment ProjectMilestoneMoveProjectTeams on ProjectMilestoneMoveProjectTeams {
  __typename
  projectId
  teamIds
}`, { fragmentName: "ProjectMilestoneMovePayload" });
const ProjectMilestonePayloadFragmentDoc = new TypedDocumentString(`
    fragment ProjectMilestonePayload on ProjectMilestonePayload {
  __typename
  lastSyncId
  projectMilestone {
    id
  }
  success
}
    `, { fragmentName: "ProjectMilestonePayload" });
const ProjectPayloadFragmentDoc = new TypedDocumentString(`
    fragment ProjectPayload on ProjectPayload {
  __typename
  lastSyncId
  project {
    id
  }
  success
}
    `, { fragmentName: "ProjectPayload" });
const ProjectRelationFragmentDoc = new TypedDocumentString(`
    fragment ProjectRelation on ProjectRelation {
  __typename
  updatedAt
  user {
    id
  }
  projectMilestone {
    id
  }
  relatedProjectMilestone {
    id
  }
  project {
    id
  }
  relatedProject {
    id
  }
  type
  archivedAt
  createdAt
  anchorType
  relatedAnchorType
  id
}
    `, { fragmentName: "ProjectRelation" });
const ProjectRelationConnectionFragmentDoc = new TypedDocumentString(`
    fragment ProjectRelationConnection on ProjectRelationConnection {
  __typename
  nodes {
    ...ProjectRelation
  }
  pageInfo {
    ...PageInfo
  }
}
    fragment ProjectRelation on ProjectRelation {
  __typename
  updatedAt
  user {
    id
  }
  projectMilestone {
    id
  }
  relatedProjectMilestone {
    id
  }
  project {
    id
  }
  relatedProject {
    id
  }
  type
  archivedAt
  createdAt
  anchorType
  relatedAnchorType
  id
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`, { fragmentName: "ProjectRelationConnection" });
const ProjectRelationPayloadFragmentDoc = new TypedDocumentString(`
    fragment ProjectRelationPayload on ProjectRelationPayload {
  __typename
  lastSyncId
  projectRelation {
    id
  }
  success
}
    `, { fragmentName: "ProjectRelationPayload" });
const ProjectSearchResultFragmentDoc = new TypedDocumentString(`
    fragment ProjectSearchResult on ProjectSearchResult {
  __typename
  trashed
  labelIds
  metadata
  url
  integrationsSettings {
    id
  }
  documentContent {
    ...DocumentContent
  }
  updateRemindersDay
  targetDate
  startDate
  updateReminderFrequency
  health
  updateRemindersHour
  icon
  lastUpdate {
    id
  }
  lastAppliedTemplate {
    id
  }
  updatedAt
  updateReminderFrequencyInWeeks
  completedScopeHistory
  completedIssueCountHistory
  inProgressScopeHistory
  progress
  scope
  priorityLabel
  priority
  lead {
    id
  }
  convertedFromIssue {
    id
  }
  color
  content
  description
  name
  slugId
  targetDateResolution
  startDateResolution
  frequencyResolution
  prioritySortOrder
  sortOrder
  status {
    id
  }
  archivedAt
  createdAt
  healthUpdatedAt
  autoArchivedAt
  canceledAt
  completedAt
  startedAt
  projectUpdateRemindersPausedUntilAt
  scopeHistory
  issueCountHistory
  id
  creator {
    id
  }
  favorite {
    id
  }
  slackIssueComments
  slackNewIssue
  slackIssueStatuses
  state
}
    fragment DocumentContent on DocumentContent {
  __typename
  aiPromptRules {
    ...AiPromptRules
  }
  content
  contentState
  document {
    id
  }
  initiative {
    id
  }
  issue {
    id
  }
  updatedAt
  projectMilestone {
    id
  }
  project {
    id
  }
  restoredAt
  archivedAt
  createdAt
  id
}
fragment AiPromptRules on AiPromptRules {
  __typename
  updatedAt
  archivedAt
  createdAt
  id
  updatedBy {
    id
  }
}`, { fragmentName: "ProjectSearchResult" });
const ProjectSearchPayloadFragmentDoc = new TypedDocumentString(`
    fragment ProjectSearchPayload on ProjectSearchPayload {
  __typename
  archivePayload {
    ...ArchiveResponse
  }
  totalCount
  nodes {
    ...ProjectSearchResult
  }
  pageInfo {
    ...PageInfo
  }
}
    fragment DocumentContent on DocumentContent {
  __typename
  aiPromptRules {
    ...AiPromptRules
  }
  content
  contentState
  document {
    id
  }
  initiative {
    id
  }
  issue {
    id
  }
  updatedAt
  projectMilestone {
    id
  }
  project {
    id
  }
  restoredAt
  archivedAt
  createdAt
  id
}
fragment AiPromptRules on AiPromptRules {
  __typename
  updatedAt
  archivedAt
  createdAt
  id
  updatedBy {
    id
  }
}
fragment ArchiveResponse on ArchiveResponse {
  __typename
  archive
  totalCount
  databaseVersion
  includesDependencies
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}
fragment ProjectSearchResult on ProjectSearchResult {
  __typename
  trashed
  labelIds
  metadata
  url
  integrationsSettings {
    id
  }
  documentContent {
    ...DocumentContent
  }
  updateRemindersDay
  targetDate
  startDate
  updateReminderFrequency
  health
  updateRemindersHour
  icon
  lastUpdate {
    id
  }
  lastAppliedTemplate {
    id
  }
  updatedAt
  updateReminderFrequencyInWeeks
  completedScopeHistory
  completedIssueCountHistory
  inProgressScopeHistory
  progress
  scope
  priorityLabel
  priority
  lead {
    id
  }
  convertedFromIssue {
    id
  }
  color
  content
  description
  name
  slugId
  targetDateResolution
  startDateResolution
  frequencyResolution
  prioritySortOrder
  sortOrder
  status {
    id
  }
  archivedAt
  createdAt
  healthUpdatedAt
  autoArchivedAt
  canceledAt
  completedAt
  startedAt
  projectUpdateRemindersPausedUntilAt
  scopeHistory
  issueCountHistory
  id
  creator {
    id
  }
  favorite {
    id
  }
  slackIssueComments
  slackNewIssue
  slackIssueStatuses
  state
}`, { fragmentName: "ProjectSearchPayload" });
const ProjectStatusConnectionFragmentDoc = new TypedDocumentString(`
    fragment ProjectStatusConnection on ProjectStatusConnection {
  __typename
  nodes {
    ...ProjectStatus
  }
  pageInfo {
    ...PageInfo
  }
}
    fragment ProjectStatus on ProjectStatus {
  __typename
  description
  color
  updatedAt
  name
  position
  archivedAt
  createdAt
  type
  id
  indefinite
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`, { fragmentName: "ProjectStatusConnection" });
const ProjectStatusCountPayloadFragmentDoc = new TypedDocumentString(`
    fragment ProjectStatusCountPayload on ProjectStatusCountPayload {
  __typename
  privateCount
  archivedTeamCount
  count
}
    `, { fragmentName: "ProjectStatusCountPayload" });
const ProjectStatusPayloadFragmentDoc = new TypedDocumentString(`
    fragment ProjectStatusPayload on ProjectStatusPayload {
  __typename
  lastSyncId
  status {
    id
  }
  success
}
    `, { fragmentName: "ProjectStatusPayload" });
const ProjectUpdateFragmentDoc = new TypedDocumentString(`
    fragment ProjectUpdate on ProjectUpdate {
  __typename
  reactionData
  commentCount
  reactions {
    ...Reaction
  }
  url
  diffMarkdown
  diff
  health
  updatedAt
  project {
    id
  }
  archivedAt
  createdAt
  editedAt
  id
  body
  slugId
  user {
    id
  }
  isDiffHidden
  isStale
}
    fragment Reaction on Reaction {
  __typename
  emoji
  comment {
    id
  }
  externalUser {
    id
  }
  initiativeUpdate {
    id
  }
  issue {
    id
  }
  updatedAt
  projectUpdate {
    id
  }
  archivedAt
  createdAt
  id
  user {
    id
  }
}`, { fragmentName: "ProjectUpdate" });
const ProjectUpdateConnectionFragmentDoc = new TypedDocumentString(`
    fragment ProjectUpdateConnection on ProjectUpdateConnection {
  __typename
  nodes {
    ...ProjectUpdate
  }
  pageInfo {
    ...PageInfo
  }
}
    fragment Reaction on Reaction {
  __typename
  emoji
  comment {
    id
  }
  externalUser {
    id
  }
  initiativeUpdate {
    id
  }
  issue {
    id
  }
  updatedAt
  projectUpdate {
    id
  }
  archivedAt
  createdAt
  id
  user {
    id
  }
}
fragment ProjectUpdate on ProjectUpdate {
  __typename
  reactionData
  commentCount
  reactions {
    ...Reaction
  }
  url
  diffMarkdown
  diff
  health
  updatedAt
  project {
    id
  }
  archivedAt
  createdAt
  editedAt
  id
  body
  slugId
  user {
    id
  }
  isDiffHidden
  isStale
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`, { fragmentName: "ProjectUpdateConnection" });
const ProjectUpdatePayloadFragmentDoc = new TypedDocumentString(`
    fragment ProjectUpdatePayload on ProjectUpdatePayload {
  __typename
  lastSyncId
  projectUpdate {
    id
  }
  success
}
    `, { fragmentName: "ProjectUpdatePayload" });
const ProjectUpdateReminderPayloadFragmentDoc = new TypedDocumentString(`
    fragment ProjectUpdateReminderPayload on ProjectUpdateReminderPayload {
  __typename
  lastSyncId
  success
}
    `, { fragmentName: "ProjectUpdateReminderPayload" });
const PushSubscriptionFragmentDoc = new TypedDocumentString(`
    fragment PushSubscription on PushSubscription {
  __typename
  updatedAt
  archivedAt
  createdAt
  id
}
    `, { fragmentName: "PushSubscription" });
const PushSubscriptionPayloadFragmentDoc = new TypedDocumentString(`
    fragment PushSubscriptionPayload on PushSubscriptionPayload {
  __typename
  lastSyncId
  entity {
    ...PushSubscription
  }
  success
}
    fragment PushSubscription on PushSubscription {
  __typename
  updatedAt
  archivedAt
  createdAt
  id
}`, { fragmentName: "PushSubscriptionPayload" });
const PushSubscriptionTestPayloadFragmentDoc = new TypedDocumentString(`
    fragment PushSubscriptionTestPayload on PushSubscriptionTestPayload {
  __typename
  success
}
    `, { fragmentName: "PushSubscriptionTestPayload" });
const RateLimitResultPayloadFragmentDoc = new TypedDocumentString(`
    fragment RateLimitResultPayload on RateLimitResultPayload {
  __typename
  period
  remainingAmount
  requestedAmount
  reset
  allowedAmount
  type
}
    `, { fragmentName: "RateLimitResultPayload" });
const RateLimitPayloadFragmentDoc = new TypedDocumentString(`
    fragment RateLimitPayload on RateLimitPayload {
  __typename
  identifier
  kind
  limits {
    ...RateLimitResultPayload
  }
}
    fragment RateLimitResultPayload on RateLimitResultPayload {
  __typename
  period
  remainingAmount
  requestedAmount
  reset
  allowedAmount
  type
}`, { fragmentName: "RateLimitPayload" });
const ReactionPayloadFragmentDoc = new TypedDocumentString(`
    fragment ReactionPayload on ReactionPayload {
  __typename
  lastSyncId
  reaction {
    ...Reaction
  }
  success
}
    fragment Reaction on Reaction {
  __typename
  emoji
  comment {
    id
  }
  externalUser {
    id
  }
  initiativeUpdate {
    id
  }
  issue {
    id
  }
  updatedAt
  projectUpdate {
    id
  }
  archivedAt
  createdAt
  id
  user {
    id
  }
}`, { fragmentName: "ReactionPayload" });
const ReleasePayloadFragmentDoc = new TypedDocumentString(`
    fragment ReleasePayload on ReleasePayload {
  __typename
  lastSyncId
  success
}
    `, { fragmentName: "ReleasePayload" });
const ReleasePipelinePayloadFragmentDoc = new TypedDocumentString(`
    fragment ReleasePipelinePayload on ReleasePipelinePayload {
  __typename
  lastSyncId
  success
}
    `, { fragmentName: "ReleasePipelinePayload" });
const ReleaseStagePayloadFragmentDoc = new TypedDocumentString(`
    fragment ReleaseStagePayload on ReleaseStagePayload {
  __typename
  lastSyncId
  success
}
    `, { fragmentName: "ReleaseStagePayload" });
const RepositorySuggestionFragmentDoc = new TypedDocumentString(`
    fragment RepositorySuggestion on RepositorySuggestion {
  __typename
  confidence
  hostname
  repositoryFullName
}
    `, { fragmentName: "RepositorySuggestion" });
const RepositorySuggestionsPayloadFragmentDoc = new TypedDocumentString(`
    fragment RepositorySuggestionsPayload on RepositorySuggestionsPayload {
  __typename
  suggestions {
    ...RepositorySuggestion
  }
}
    fragment RepositorySuggestion on RepositorySuggestion {
  __typename
  confidence
  hostname
  repositoryFullName
}`, { fragmentName: "RepositorySuggestionsPayload" });
const RoadmapFragmentDoc = new TypedDocumentString(`
    fragment Roadmap on Roadmap {
  __typename
  url
  description
  updatedAt
  name
  color
  slugId
  sortOrder
  archivedAt
  createdAt
  id
  creator {
    id
  }
  owner {
    id
  }
}
    `, { fragmentName: "Roadmap" });
const RoadmapConnectionFragmentDoc = new TypedDocumentString(`
    fragment RoadmapConnection on RoadmapConnection {
  __typename
  nodes {
    ...Roadmap
  }
  pageInfo {
    ...PageInfo
  }
}
    fragment Roadmap on Roadmap {
  __typename
  url
  description
  updatedAt
  name
  color
  slugId
  sortOrder
  archivedAt
  createdAt
  id
  creator {
    id
  }
  owner {
    id
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`, { fragmentName: "RoadmapConnection" });
const RoadmapPayloadFragmentDoc = new TypedDocumentString(`
    fragment RoadmapPayload on RoadmapPayload {
  __typename
  lastSyncId
  roadmap {
    id
  }
  success
}
    `, { fragmentName: "RoadmapPayload" });
const RoadmapToProjectFragmentDoc = new TypedDocumentString(`
    fragment RoadmapToProject on RoadmapToProject {
  __typename
  updatedAt
  project {
    id
  }
  roadmap {
    id
  }
  sortOrder
  archivedAt
  createdAt
  id
}
    `, { fragmentName: "RoadmapToProject" });
const RoadmapToProjectConnectionFragmentDoc = new TypedDocumentString(`
    fragment RoadmapToProjectConnection on RoadmapToProjectConnection {
  __typename
  nodes {
    ...RoadmapToProject
  }
  pageInfo {
    ...PageInfo
  }
}
    fragment RoadmapToProject on RoadmapToProject {
  __typename
  updatedAt
  project {
    id
  }
  roadmap {
    id
  }
  sortOrder
  archivedAt
  createdAt
  id
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`, { fragmentName: "RoadmapToProjectConnection" });
const RoadmapToProjectPayloadFragmentDoc = new TypedDocumentString(`
    fragment RoadmapToProjectPayload on RoadmapToProjectPayload {
  __typename
  lastSyncId
  roadmapToProject {
    id
  }
  success
}
    `, { fragmentName: "RoadmapToProjectPayload" });
const SlackChannelConnectPayloadFragmentDoc = new TypedDocumentString(`
    fragment SlackChannelConnectPayload on SlackChannelConnectPayload {
  __typename
  lastSyncId
  integration {
    id
  }
  nudgeToConnectMainSlackIntegration
  nudgeToUpdateMainSlackIntegration
  addBot
  success
}
    `, { fragmentName: "SlackChannelConnectPayload" });
const SsoUrlFromEmailResponseFragmentDoc = new TypedDocumentString(`
    fragment SsoUrlFromEmailResponse on SsoUrlFromEmailResponse {
  __typename
  samlSsoUrl
  success
}
    `, { fragmentName: "SsoUrlFromEmailResponse" });
const SuccessPayloadFragmentDoc = new TypedDocumentString(`
    fragment SuccessPayload on SuccessPayload {
  __typename
  lastSyncId
  success
}
    `, { fragmentName: "SuccessPayload" });
const TeamFragmentDoc = new TypedDocumentString(`
    fragment Team on Team {
  __typename
  cycleIssueAutoAssignCompleted
  cycleLockToActive
  cycleIssueAutoAssignStarted
  cycleCalenderUrl
  upcomingCycleCount
  issueCount
  autoArchivePeriod
  autoClosePeriod
  securitySettings
  integrationsSettings {
    id
  }
  activeCycle {
    id
  }
  triageResponsibility {
    id
  }
  scimGroupName
  autoCloseStateId
  cycleCooldownTime
  cycleStartDay
  defaultTemplateForMembers {
    id
  }
  defaultTemplateForNonMembers {
    id
  }
  defaultProjectTemplate {
    id
  }
  defaultIssueState {
    id
  }
  cycleDuration
  icon
  defaultTemplateForMembersId
  defaultTemplateForNonMembersId
  issueEstimationType
  updatedAt
  displayName
  color
  description
  name
  key
  archivedAt
  createdAt
  timezone
  id
  mergeWorkflowState {
    id
  }
  draftWorkflowState {
    id
  }
  startWorkflowState {
    id
  }
  mergeableWorkflowState {
    id
  }
  reviewWorkflowState {
    id
  }
  markedAsDuplicateWorkflowState {
    id
  }
  triageIssueState {
    id
  }
  defaultIssueEstimate
  setIssueSortOrderOnStateChange
  allMembersCanJoin
  requirePriorityToLeaveTriage
  autoCloseChildIssues
  autoCloseParentIssues
  scimManaged
  private
  inheritIssueEstimation
  inheritWorkflowStatuses
  cyclesEnabled
  issueEstimationExtended
  issueEstimationAllowZero
  aiDiscussionSummariesEnabled
  aiThreadSummariesEnabled
  groupIssueHistory
  slackIssueComments
  slackNewIssue
  slackIssueStatuses
  triageEnabled
  inviteHash
  issueOrderingNoPriorityFirst
  issueSortOrderDefaultToBottom
}
    `, { fragmentName: "Team" });
const TeamConnectionFragmentDoc = new TypedDocumentString(`
    fragment TeamConnection on TeamConnection {
  __typename
  nodes {
    ...Team
  }
  pageInfo {
    ...PageInfo
  }
}
    fragment Team on Team {
  __typename
  cycleIssueAutoAssignCompleted
  cycleLockToActive
  cycleIssueAutoAssignStarted
  cycleCalenderUrl
  upcomingCycleCount
  issueCount
  autoArchivePeriod
  autoClosePeriod
  securitySettings
  integrationsSettings {
    id
  }
  activeCycle {
    id
  }
  triageResponsibility {
    id
  }
  scimGroupName
  autoCloseStateId
  cycleCooldownTime
  cycleStartDay
  defaultTemplateForMembers {
    id
  }
  defaultTemplateForNonMembers {
    id
  }
  defaultProjectTemplate {
    id
  }
  defaultIssueState {
    id
  }
  cycleDuration
  icon
  defaultTemplateForMembersId
  defaultTemplateForNonMembersId
  issueEstimationType
  updatedAt
  displayName
  color
  description
  name
  key
  archivedAt
  createdAt
  timezone
  id
  mergeWorkflowState {
    id
  }
  draftWorkflowState {
    id
  }
  startWorkflowState {
    id
  }
  mergeableWorkflowState {
    id
  }
  reviewWorkflowState {
    id
  }
  markedAsDuplicateWorkflowState {
    id
  }
  triageIssueState {
    id
  }
  defaultIssueEstimate
  setIssueSortOrderOnStateChange
  allMembersCanJoin
  requirePriorityToLeaveTriage
  autoCloseChildIssues
  autoCloseParentIssues
  scimManaged
  private
  inheritIssueEstimation
  inheritWorkflowStatuses
  cyclesEnabled
  issueEstimationExtended
  issueEstimationAllowZero
  aiDiscussionSummariesEnabled
  aiThreadSummariesEnabled
  groupIssueHistory
  slackIssueComments
  slackNewIssue
  slackIssueStatuses
  triageEnabled
  inviteHash
  issueOrderingNoPriorityFirst
  issueSortOrderDefaultToBottom
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`, { fragmentName: "TeamConnection" });
const TeamMembershipFragmentDoc = new TypedDocumentString(`
    fragment TeamMembership on TeamMembership {
  __typename
  updatedAt
  sortOrder
  team {
    id
  }
  archivedAt
  createdAt
  id
  user {
    id
  }
  owner
}
    `, { fragmentName: "TeamMembership" });
const TeamMembershipConnectionFragmentDoc = new TypedDocumentString(`
    fragment TeamMembershipConnection on TeamMembershipConnection {
  __typename
  nodes {
    ...TeamMembership
  }
  pageInfo {
    ...PageInfo
  }
}
    fragment TeamMembership on TeamMembership {
  __typename
  updatedAt
  sortOrder
  team {
    id
  }
  archivedAt
  createdAt
  id
  user {
    id
  }
  owner
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`, { fragmentName: "TeamMembershipConnection" });
const TeamMembershipPayloadFragmentDoc = new TypedDocumentString(`
    fragment TeamMembershipPayload on TeamMembershipPayload {
  __typename
  lastSyncId
  teamMembership {
    id
  }
  success
}
    `, { fragmentName: "TeamMembershipPayload" });
const TeamPayloadFragmentDoc = new TypedDocumentString(`
    fragment TeamPayload on TeamPayload {
  __typename
  lastSyncId
  team {
    id
  }
  success
}
    `, { fragmentName: "TeamPayload" });
const TemplateFragmentDoc = new TypedDocumentString(`
    fragment Template on Template {
  __typename
  templateData
  description
  lastAppliedAt
  type
  updatedAt
  name
  inheritedFrom {
    id
  }
  sortOrder
  team {
    id
  }
  archivedAt
  createdAt
  id
  creator {
    id
  }
  lastUpdatedBy {
    id
  }
}
    `, { fragmentName: "Template" });
const TemplateConnectionFragmentDoc = new TypedDocumentString(`
    fragment TemplateConnection on TemplateConnection {
  __typename
  nodes {
    ...Template
  }
  pageInfo {
    ...PageInfo
  }
}
    fragment Template on Template {
  __typename
  templateData
  description
  lastAppliedAt
  type
  updatedAt
  name
  inheritedFrom {
    id
  }
  sortOrder
  team {
    id
  }
  archivedAt
  createdAt
  id
  creator {
    id
  }
  lastUpdatedBy {
    id
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`, { fragmentName: "TemplateConnection" });
const TemplatePayloadFragmentDoc = new TypedDocumentString(`
    fragment TemplatePayload on TemplatePayload {
  __typename
  lastSyncId
  template {
    id
  }
  success
}
    `, { fragmentName: "TemplatePayload" });
const TimeScheduleEntryFragmentDoc = new TypedDocumentString(`
    fragment TimeScheduleEntry on TimeScheduleEntry {
  __typename
  userId
  userEmail
  endsAt
  startsAt
}
    `, { fragmentName: "TimeScheduleEntry" });
const TimeScheduleFragmentDoc = new TypedDocumentString(`
    fragment TimeSchedule on TimeSchedule {
  __typename
  externalUrl
  integration {
    id
  }
  externalId
  updatedAt
  name
  entries {
    ...TimeScheduleEntry
  }
  archivedAt
  createdAt
  id
}
    fragment TimeScheduleEntry on TimeScheduleEntry {
  __typename
  userId
  userEmail
  endsAt
  startsAt
}`, { fragmentName: "TimeSchedule" });
const TimeScheduleConnectionFragmentDoc = new TypedDocumentString(`
    fragment TimeScheduleConnection on TimeScheduleConnection {
  __typename
  nodes {
    ...TimeSchedule
  }
  pageInfo {
    ...PageInfo
  }
}
    fragment TimeSchedule on TimeSchedule {
  __typename
  externalUrl
  integration {
    id
  }
  externalId
  updatedAt
  name
  entries {
    ...TimeScheduleEntry
  }
  archivedAt
  createdAt
  id
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}
fragment TimeScheduleEntry on TimeScheduleEntry {
  __typename
  userId
  userEmail
  endsAt
  startsAt
}`, { fragmentName: "TimeScheduleConnection" });
const TimeSchedulePayloadFragmentDoc = new TypedDocumentString(`
    fragment TimeSchedulePayload on TimeSchedulePayload {
  __typename
  lastSyncId
  success
  timeSchedule {
    id
  }
}
    `, { fragmentName: "TimeSchedulePayload" });
const TriageResponsibilityManualSelectionFragmentDoc = new TypedDocumentString(`
    fragment TriageResponsibilityManualSelection on TriageResponsibilityManualSelection {
  __typename
  userIds
}
    `, { fragmentName: "TriageResponsibilityManualSelection" });
const TriageResponsibilityFragmentDoc = new TypedDocumentString(`
    fragment TriageResponsibility on TriageResponsibility {
  __typename
  manualSelection {
    ...TriageResponsibilityManualSelection
  }
  action
  updatedAt
  team {
    id
  }
  archivedAt
  createdAt
  timeSchedule {
    id
  }
  id
  currentUser {
    id
  }
}
    fragment TriageResponsibilityManualSelection on TriageResponsibilityManualSelection {
  __typename
  userIds
}`, { fragmentName: "TriageResponsibility" });
const TriageResponsibilityConnectionFragmentDoc = new TypedDocumentString(`
    fragment TriageResponsibilityConnection on TriageResponsibilityConnection {
  __typename
  nodes {
    ...TriageResponsibility
  }
  pageInfo {
    ...PageInfo
  }
}
    fragment TriageResponsibility on TriageResponsibility {
  __typename
  manualSelection {
    ...TriageResponsibilityManualSelection
  }
  action
  updatedAt
  team {
    id
  }
  archivedAt
  createdAt
  timeSchedule {
    id
  }
  id
  currentUser {
    id
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}
fragment TriageResponsibilityManualSelection on TriageResponsibilityManualSelection {
  __typename
  userIds
}`, { fragmentName: "TriageResponsibilityConnection" });
const TriageResponsibilityPayloadFragmentDoc = new TypedDocumentString(`
    fragment TriageResponsibilityPayload on TriageResponsibilityPayload {
  __typename
  lastSyncId
  success
  triageResponsibility {
    id
  }
}
    `, { fragmentName: "TriageResponsibilityPayload" });
const UploadFileHeaderFragmentDoc = new TypedDocumentString(`
    fragment UploadFileHeader on UploadFileHeader {
  __typename
  key
  value
}
    `, { fragmentName: "UploadFileHeader" });
const UploadFileFragmentDoc = new TypedDocumentString(`
    fragment UploadFile on UploadFile {
  __typename
  assetUrl
  contentType
  filename
  uploadUrl
  size
  headers {
    ...UploadFileHeader
  }
  metaData
}
    fragment UploadFileHeader on UploadFileHeader {
  __typename
  key
  value
}`, { fragmentName: "UploadFile" });
const UploadPayloadFragmentDoc = new TypedDocumentString(`
    fragment UploadPayload on UploadPayload {
  __typename
  uploadFile {
    ...UploadFile
  }
  lastSyncId
  success
}
    fragment UploadFile on UploadFile {
  __typename
  assetUrl
  contentType
  filename
  uploadUrl
  size
  headers {
    ...UploadFileHeader
  }
  metaData
}
fragment UploadFileHeader on UploadFileHeader {
  __typename
  key
  value
}`, { fragmentName: "UploadPayload" });
const UserAdminPayloadFragmentDoc = new TypedDocumentString(`
    fragment UserAdminPayload on UserAdminPayload {
  __typename
  success
}
    `, { fragmentName: "UserAdminPayload" });
const UserConnectionFragmentDoc = new TypedDocumentString(`
    fragment UserConnection on UserConnection {
  __typename
  nodes {
    ...User
  }
  pageInfo {
    ...PageInfo
  }
}
    fragment User on User {
  __typename
  statusUntilAt
  description
  avatarUrl
  createdIssueCount
  disableReason
  avatarBackgroundColor
  statusEmoji
  initials
  statusLabel
  updatedAt
  lastSeen
  timezone
  archivedAt
  createdAt
  id
  gitHubUserId
  displayName
  email
  name
  url
  active
  guest
  app
  admin
  owner
  isAssignable
  isMentionable
  isMe
  supportsAgentSessions
  canAccessAnyPublicTeam
  calendarHash
  inviteHash
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`, { fragmentName: "UserConnection" });
const UserPayloadFragmentDoc = new TypedDocumentString(`
    fragment UserPayload on UserPayload {
  __typename
  lastSyncId
  user {
    id
  }
  success
}
    `, { fragmentName: "UserPayload" });
const UserSettingsFlagPayloadFragmentDoc = new TypedDocumentString(`
    fragment UserSettingsFlagPayload on UserSettingsFlagPayload {
  __typename
  flag
  value
  lastSyncId
  success
}
    `, { fragmentName: "UserSettingsFlagPayload" });
const UserSettingsFlagsResetPayloadFragmentDoc = new TypedDocumentString(`
    fragment UserSettingsFlagsResetPayload on UserSettingsFlagsResetPayload {
  __typename
  lastSyncId
  success
}
    `, { fragmentName: "UserSettingsFlagsResetPayload" });
const UserSettingsPayloadFragmentDoc = new TypedDocumentString(`
    fragment UserSettingsPayload on UserSettingsPayload {
  __typename
  lastSyncId
  success
}
    `, { fragmentName: "UserSettingsPayload" });
const ViewPreferencesPayloadFragmentDoc = new TypedDocumentString(`
    fragment ViewPreferencesPayload on ViewPreferencesPayload {
  __typename
  lastSyncId
  viewPreferences {
    ...ViewPreferences
  }
  success
}
    fragment ViewPreferences on ViewPreferences {
  __typename
  updatedAt
  archivedAt
  createdAt
  id
  type
  preferences {
    ...ViewPreferencesValues
  }
  viewType
}
fragment ViewPreferencesValues on ViewPreferencesValues {
  __typename
  issueGrouping
  viewOrdering
  issueSubGrouping
  showCompletedIssues
}`, { fragmentName: "ViewPreferencesPayload" });
const WebhookFragmentDoc = new TypedDocumentString(`
    fragment Webhook on Webhook {
  __typename
  secret
  updatedAt
  resourceTypes
  team {
    id
  }
  archivedAt
  createdAt
  id
  creator {
    id
  }
  url
  label
  allPublicTeams
  enabled
}
    `, { fragmentName: "Webhook" });
const WebhookConnectionFragmentDoc = new TypedDocumentString(`
    fragment WebhookConnection on WebhookConnection {
  __typename
  nodes {
    ...Webhook
  }
  pageInfo {
    ...PageInfo
  }
}
    fragment Webhook on Webhook {
  __typename
  secret
  updatedAt
  resourceTypes
  team {
    id
  }
  archivedAt
  createdAt
  id
  creator {
    id
  }
  url
  label
  allPublicTeams
  enabled
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`, { fragmentName: "WebhookConnection" });
const WebhookPayloadFragmentDoc = new TypedDocumentString(`
    fragment WebhookPayload on WebhookPayload {
  __typename
  lastSyncId
  webhook {
    id
  }
  success
}
    `, { fragmentName: "WebhookPayload" });
const WorkflowStateFragmentDoc = new TypedDocumentString(`
    fragment WorkflowState on WorkflowState {
  __typename
  description
  updatedAt
  position
  inheritedFrom {
    id
  }
  color
  name
  team {
    id
  }
  archivedAt
  createdAt
  type
  id
}
    `, { fragmentName: "WorkflowState" });
const WorkflowStateConnectionFragmentDoc = new TypedDocumentString(`
    fragment WorkflowStateConnection on WorkflowStateConnection {
  __typename
  nodes {
    ...WorkflowState
  }
  pageInfo {
    ...PageInfo
  }
}
    fragment WorkflowState on WorkflowState {
  __typename
  description
  updatedAt
  position
  inheritedFrom {
    id
  }
  color
  name
  team {
    id
  }
  archivedAt
  createdAt
  type
  id
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`, { fragmentName: "WorkflowStateConnection" });
const WorkflowStatePayloadFragmentDoc = new TypedDocumentString(`
    fragment WorkflowStatePayload on WorkflowStatePayload {
  __typename
  lastSyncId
  workflowState {
    id
  }
  success
}
    `, { fragmentName: "WorkflowStatePayload" });
const AdministrableTeamsDocument = new TypedDocumentString(`
    query administrableTeams($after: String, $before: String, $filter: TeamFilter, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  administrableTeams(
    after: $after
    before: $before
    filter: $filter
    first: $first
    includeArchived: $includeArchived
    last: $last
    orderBy: $orderBy
  ) {
    ...TeamConnection
  }
}
    fragment Team on Team {
  __typename
  cycleIssueAutoAssignCompleted
  cycleLockToActive
  cycleIssueAutoAssignStarted
  cycleCalenderUrl
  upcomingCycleCount
  issueCount
  autoArchivePeriod
  autoClosePeriod
  securitySettings
  integrationsSettings {
    id
  }
  activeCycle {
    id
  }
  triageResponsibility {
    id
  }
  scimGroupName
  autoCloseStateId
  cycleCooldownTime
  cycleStartDay
  defaultTemplateForMembers {
    id
  }
  defaultTemplateForNonMembers {
    id
  }
  defaultProjectTemplate {
    id
  }
  defaultIssueState {
    id
  }
  cycleDuration
  icon
  defaultTemplateForMembersId
  defaultTemplateForNonMembersId
  issueEstimationType
  updatedAt
  displayName
  color
  description
  name
  key
  archivedAt
  createdAt
  timezone
  id
  mergeWorkflowState {
    id
  }
  draftWorkflowState {
    id
  }
  startWorkflowState {
    id
  }
  mergeableWorkflowState {
    id
  }
  reviewWorkflowState {
    id
  }
  markedAsDuplicateWorkflowState {
    id
  }
  triageIssueState {
    id
  }
  defaultIssueEstimate
  setIssueSortOrderOnStateChange
  allMembersCanJoin
  requirePriorityToLeaveTriage
  autoCloseChildIssues
  autoCloseParentIssues
  scimManaged
  private
  inheritIssueEstimation
  inheritWorkflowStatuses
  cyclesEnabled
  issueEstimationExtended
  issueEstimationAllowZero
  aiDiscussionSummariesEnabled
  aiThreadSummariesEnabled
  groupIssueHistory
  slackIssueComments
  slackNewIssue
  slackIssueStatuses
  triageEnabled
  inviteHash
  issueOrderingNoPriorityFirst
  issueSortOrderDefaultToBottom
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}
fragment TeamConnection on TeamConnection {
  __typename
  nodes {
    ...Team
  }
  pageInfo {
    ...PageInfo
  }
}`);
const AgentActivitiesDocument = new TypedDocumentString(`
    query agentActivities($after: String, $before: String, $filter: AgentActivityFilter, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  agentActivities(
    after: $after
    before: $before
    filter: $filter
    first: $first
    includeArchived: $includeArchived
    last: $last
    orderBy: $orderBy
  ) {
    ...AgentActivityConnection
  }
}
    fragment AgentActivity on AgentActivity {
  __typename
  signal
  sourceMetadata
  signalMetadata
  agentSession {
    id
  }
  sourceComment {
    id
  }
  content {
    ... on AgentActivityActionContent {
      ...AgentActivityActionContent
    }
    ... on AgentActivityElicitationContent {
      ...AgentActivityElicitationContent
    }
    ... on AgentActivityErrorContent {
      ...AgentActivityErrorContent
    }
    ... on AgentActivityPromptContent {
      ...AgentActivityPromptContent
    }
    ... on AgentActivityResponseContent {
      ...AgentActivityResponseContent
    }
    ... on AgentActivityThoughtContent {
      ...AgentActivityThoughtContent
    }
  }
  updatedAt
  archivedAt
  createdAt
  id
  user {
    id
  }
  ephemeral
}
fragment AgentActivityPromptContent on AgentActivityPromptContent {
  __typename
  body
  type
}
fragment AgentActivityResponseContent on AgentActivityResponseContent {
  __typename
  body
  type
}
fragment AgentActivityThoughtContent on AgentActivityThoughtContent {
  __typename
  body
  type
}
fragment AgentActivityActionContent on AgentActivityActionContent {
  __typename
  action
  parameter
  result
  type
}
fragment AgentActivityElicitationContent on AgentActivityElicitationContent {
  __typename
  body
  type
}
fragment AgentActivityErrorContent on AgentActivityErrorContent {
  __typename
  body
  type
}
fragment AgentActivityConnection on AgentActivityConnection {
  __typename
  nodes {
    ...AgentActivity
  }
  pageInfo {
    ...PageInfo
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`);
const AgentActivityDocument = new TypedDocumentString(`
    query agentActivity($id: String!) {
  agentActivity(id: $id) {
    ...AgentActivity
  }
}
    fragment AgentActivity on AgentActivity {
  __typename
  signal
  sourceMetadata
  signalMetadata
  agentSession {
    id
  }
  sourceComment {
    id
  }
  content {
    ... on AgentActivityActionContent {
      ...AgentActivityActionContent
    }
    ... on AgentActivityElicitationContent {
      ...AgentActivityElicitationContent
    }
    ... on AgentActivityErrorContent {
      ...AgentActivityErrorContent
    }
    ... on AgentActivityPromptContent {
      ...AgentActivityPromptContent
    }
    ... on AgentActivityResponseContent {
      ...AgentActivityResponseContent
    }
    ... on AgentActivityThoughtContent {
      ...AgentActivityThoughtContent
    }
  }
  updatedAt
  archivedAt
  createdAt
  id
  user {
    id
  }
  ephemeral
}
fragment AgentActivityPromptContent on AgentActivityPromptContent {
  __typename
  body
  type
}
fragment AgentActivityResponseContent on AgentActivityResponseContent {
  __typename
  body
  type
}
fragment AgentActivityThoughtContent on AgentActivityThoughtContent {
  __typename
  body
  type
}
fragment AgentActivityActionContent on AgentActivityActionContent {
  __typename
  action
  parameter
  result
  type
}
fragment AgentActivityElicitationContent on AgentActivityElicitationContent {
  __typename
  body
  type
}
fragment AgentActivityErrorContent on AgentActivityErrorContent {
  __typename
  body
  type
}`);
const AgentSessionDocument = new TypedDocumentString(`
    query agentSession($id: String!) {
  agentSession(id: $id) {
    ...AgentSession
  }
}
    fragment AgentSession on AgentSession {
  __typename
  plan
  summary
  sourceMetadata
  context
  externalLink
  appUser {
    id
  }
  sourceComment {
    id
  }
  comment {
    id
  }
  status
  creator {
    id
  }
  issue {
    id
  }
  updatedAt
  archivedAt
  createdAt
  endedAt
  startedAt
  dismissedAt
  id
  dismissedBy {
    id
  }
  externalUrls
  type
}`);
const AgentSession_ActivitiesDocument = new TypedDocumentString(`
    query agentSession_activities($id: String!, $after: String, $before: String, $filter: AgentActivityFilter, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  agentSession(id: $id) {
    activities(
      after: $after
      before: $before
      filter: $filter
      first: $first
      includeArchived: $includeArchived
      last: $last
      orderBy: $orderBy
    ) {
      ...AgentActivityConnection
    }
  }
}
    fragment AgentActivity on AgentActivity {
  __typename
  signal
  sourceMetadata
  signalMetadata
  agentSession {
    id
  }
  sourceComment {
    id
  }
  content {
    ... on AgentActivityActionContent {
      ...AgentActivityActionContent
    }
    ... on AgentActivityElicitationContent {
      ...AgentActivityElicitationContent
    }
    ... on AgentActivityErrorContent {
      ...AgentActivityErrorContent
    }
    ... on AgentActivityPromptContent {
      ...AgentActivityPromptContent
    }
    ... on AgentActivityResponseContent {
      ...AgentActivityResponseContent
    }
    ... on AgentActivityThoughtContent {
      ...AgentActivityThoughtContent
    }
  }
  updatedAt
  archivedAt
  createdAt
  id
  user {
    id
  }
  ephemeral
}
fragment AgentActivityPromptContent on AgentActivityPromptContent {
  __typename
  body
  type
}
fragment AgentActivityResponseContent on AgentActivityResponseContent {
  __typename
  body
  type
}
fragment AgentActivityThoughtContent on AgentActivityThoughtContent {
  __typename
  body
  type
}
fragment AgentActivityActionContent on AgentActivityActionContent {
  __typename
  action
  parameter
  result
  type
}
fragment AgentActivityElicitationContent on AgentActivityElicitationContent {
  __typename
  body
  type
}
fragment AgentActivityErrorContent on AgentActivityErrorContent {
  __typename
  body
  type
}
fragment AgentActivityConnection on AgentActivityConnection {
  __typename
  nodes {
    ...AgentActivity
  }
  pageInfo {
    ...PageInfo
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`);
const AgentSessionsDocument = new TypedDocumentString(`
    query agentSessions($after: String, $before: String, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  agentSessions(
    after: $after
    before: $before
    first: $first
    includeArchived: $includeArchived
    last: $last
    orderBy: $orderBy
  ) {
    ...AgentSessionConnection
  }
}
    fragment AgentSession on AgentSession {
  __typename
  plan
  summary
  sourceMetadata
  context
  externalLink
  appUser {
    id
  }
  sourceComment {
    id
  }
  comment {
    id
  }
  status
  creator {
    id
  }
  issue {
    id
  }
  updatedAt
  archivedAt
  createdAt
  endedAt
  startedAt
  dismissedAt
  id
  dismissedBy {
    id
  }
  externalUrls
  type
}
fragment AgentSessionConnection on AgentSessionConnection {
  __typename
  nodes {
    ...AgentSession
  }
  pageInfo {
    ...PageInfo
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`);
const ApplicationInfoDocument = new TypedDocumentString(`
    query applicationInfo($clientId: String!) {
  applicationInfo(clientId: $clientId) {
    ...Application
  }
}
    fragment Application on Application {
  __typename
  name
  imageUrl
  description
  developer
  id
  clientId
  developerUrl
}`);
const AttachmentDocument = new TypedDocumentString(`
    query attachment($id: String!) {
  attachment(id: $id) {
    ...Attachment
  }
}
    fragment Attachment on Attachment {
  __typename
  sourceType
  subtitle
  title
  metadata
  groupBySource
  source
  url
  bodyData
  creator {
    id
  }
  issue {
    id
  }
  originalIssue {
    id
  }
  updatedAt
  externalUserCreator {
    id
  }
  archivedAt
  createdAt
  id
}`);
const AttachmentIssueDocument = new TypedDocumentString(`
    query attachmentIssue($id: String!) {
  attachmentIssue(id: $id) {
    ...Issue
  }
}
    fragment ActorBot on ActorBot {
  __typename
  avatarUrl
  name
  userDisplayName
  subType
  type
  id
}
fragment Reaction on Reaction {
  __typename
  emoji
  comment {
    id
  }
  externalUser {
    id
  }
  initiativeUpdate {
    id
  }
  issue {
    id
  }
  updatedAt
  projectUpdate {
    id
  }
  archivedAt
  createdAt
  id
  user {
    id
  }
}
fragment Issue on Issue {
  __typename
  trashed
  reactionData
  labelIds
  integrationSourceType
  url
  identifier
  priorityLabel
  previousIdentifiers
  reactions {
    ...Reaction
  }
  customerTicketCount
  branchName
  delegate {
    id
  }
  botActor {
    ...ActorBot
  }
  sourceComment {
    id
  }
  cycle {
    id
  }
  dueDate
  estimate
  syncedWith {
    ...ExternalEntityInfo
  }
  externalUserCreator {
    id
  }
  asksExternalUserRequester {
    id
  }
  asksRequester {
    id
  }
  description
  title
  number
  lastAppliedTemplate {
    id
  }
  updatedAt
  boardOrder
  prioritySortOrder
  sortOrder
  subIssueSortOrder
  parent {
    id
  }
  priority
  project {
    id
  }
  projectMilestone {
    id
  }
  recurringIssueTemplate {
    id
  }
  team {
    id
  }
  archivedAt
  createdAt
  startedTriageAt
  triagedAt
  addedToCycleAt
  addedToProjectAt
  addedToTeamAt
  autoArchivedAt
  autoClosedAt
  canceledAt
  completedAt
  startedAt
  slaStartedAt
  slaBreachesAt
  slaHighRiskAt
  slaMediumRiskAt
  snoozedUntilAt
  slaType
  id
  assignee {
    id
  }
  creator {
    id
  }
  snoozedBy {
    id
  }
  favorite {
    id
  }
  state {
    id
  }
}
fragment ExternalEntityInfo on ExternalEntityInfo {
  __typename
  metadata {
    ... on ExternalEntityInfoGithubMetadata {
      ...ExternalEntityInfoGithubMetadata
    }
    ... on ExternalEntityInfoJiraMetadata {
      ...ExternalEntityInfoJiraMetadata
    }
    ... on ExternalEntitySlackMetadata {
      ...ExternalEntitySlackMetadata
    }
  }
  id
  service
}
fragment ExternalEntityInfoGithubMetadata on ExternalEntityInfoGithubMetadata {
  __typename
  number
  owner
  repo
}
fragment ExternalEntityInfoJiraMetadata on ExternalEntityInfoJiraMetadata {
  __typename
  issueTypeId
  projectId
  issueKey
}
fragment ExternalEntitySlackMetadata on ExternalEntitySlackMetadata {
  __typename
  messageUrl
  channelId
  channelName
  isFromSlack
}`);
const AttachmentIssue_AttachmentsDocument = new TypedDocumentString(`
    query attachmentIssue_attachments($id: String!, $after: String, $before: String, $filter: AttachmentFilter, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  attachmentIssue(id: $id) {
    attachments(
      after: $after
      before: $before
      filter: $filter
      first: $first
      includeArchived: $includeArchived
      last: $last
      orderBy: $orderBy
    ) {
      ...AttachmentConnection
    }
  }
}
    fragment Attachment on Attachment {
  __typename
  sourceType
  subtitle
  title
  metadata
  groupBySource
  source
  url
  bodyData
  creator {
    id
  }
  issue {
    id
  }
  originalIssue {
    id
  }
  updatedAt
  externalUserCreator {
    id
  }
  archivedAt
  createdAt
  id
}
fragment AttachmentConnection on AttachmentConnection {
  __typename
  nodes {
    ...Attachment
  }
  pageInfo {
    ...PageInfo
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`);
const AttachmentIssue_BotActorDocument = new TypedDocumentString(`
    query attachmentIssue_botActor($id: String!) {
  attachmentIssue(id: $id) {
    botActor {
      ...ActorBot
    }
  }
}
    fragment ActorBot on ActorBot {
  __typename
  avatarUrl
  name
  userDisplayName
  subType
  type
  id
}`);
const AttachmentIssue_ChildrenDocument = new TypedDocumentString(`
    query attachmentIssue_children($id: String!, $after: String, $before: String, $filter: IssueFilter, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  attachmentIssue(id: $id) {
    children(
      after: $after
      before: $before
      filter: $filter
      first: $first
      includeArchived: $includeArchived
      last: $last
      orderBy: $orderBy
    ) {
      ...IssueConnection
    }
  }
}
    fragment ActorBot on ActorBot {
  __typename
  avatarUrl
  name
  userDisplayName
  subType
  type
  id
}
fragment Reaction on Reaction {
  __typename
  emoji
  comment {
    id
  }
  externalUser {
    id
  }
  initiativeUpdate {
    id
  }
  issue {
    id
  }
  updatedAt
  projectUpdate {
    id
  }
  archivedAt
  createdAt
  id
  user {
    id
  }
}
fragment Issue on Issue {
  __typename
  trashed
  reactionData
  labelIds
  integrationSourceType
  url
  identifier
  priorityLabel
  previousIdentifiers
  reactions {
    ...Reaction
  }
  customerTicketCount
  branchName
  delegate {
    id
  }
  botActor {
    ...ActorBot
  }
  sourceComment {
    id
  }
  cycle {
    id
  }
  dueDate
  estimate
  syncedWith {
    ...ExternalEntityInfo
  }
  externalUserCreator {
    id
  }
  asksExternalUserRequester {
    id
  }
  asksRequester {
    id
  }
  description
  title
  number
  lastAppliedTemplate {
    id
  }
  updatedAt
  boardOrder
  prioritySortOrder
  sortOrder
  subIssueSortOrder
  parent {
    id
  }
  priority
  project {
    id
  }
  projectMilestone {
    id
  }
  recurringIssueTemplate {
    id
  }
  team {
    id
  }
  archivedAt
  createdAt
  startedTriageAt
  triagedAt
  addedToCycleAt
  addedToProjectAt
  addedToTeamAt
  autoArchivedAt
  autoClosedAt
  canceledAt
  completedAt
  startedAt
  slaStartedAt
  slaBreachesAt
  slaHighRiskAt
  slaMediumRiskAt
  snoozedUntilAt
  slaType
  id
  assignee {
    id
  }
  creator {
    id
  }
  snoozedBy {
    id
  }
  favorite {
    id
  }
  state {
    id
  }
}
fragment ExternalEntityInfo on ExternalEntityInfo {
  __typename
  metadata {
    ... on ExternalEntityInfoGithubMetadata {
      ...ExternalEntityInfoGithubMetadata
    }
    ... on ExternalEntityInfoJiraMetadata {
      ...ExternalEntityInfoJiraMetadata
    }
    ... on ExternalEntitySlackMetadata {
      ...ExternalEntitySlackMetadata
    }
  }
  id
  service
}
fragment ExternalEntityInfoGithubMetadata on ExternalEntityInfoGithubMetadata {
  __typename
  number
  owner
  repo
}
fragment ExternalEntityInfoJiraMetadata on ExternalEntityInfoJiraMetadata {
  __typename
  issueTypeId
  projectId
  issueKey
}
fragment ExternalEntitySlackMetadata on ExternalEntitySlackMetadata {
  __typename
  messageUrl
  channelId
  channelName
  isFromSlack
}
fragment IssueConnection on IssueConnection {
  __typename
  nodes {
    ...Issue
  }
  pageInfo {
    ...PageInfo
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`);
const AttachmentIssue_CommentsDocument = new TypedDocumentString(`
    query attachmentIssue_comments($id: String!, $after: String, $before: String, $filter: CommentFilter, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  attachmentIssue(id: $id) {
    comments(
      after: $after
      before: $before
      filter: $filter
      first: $first
      includeArchived: $includeArchived
      last: $last
      orderBy: $orderBy
    ) {
      ...CommentConnection
    }
  }
}
    fragment ActorBot on ActorBot {
  __typename
  avatarUrl
  name
  userDisplayName
  subType
  type
  id
}
fragment Comment on Comment {
  __typename
  agentSession {
    id
  }
  url
  reactionData
  reactions {
    ...Reaction
  }
  resolvingCommentId
  documentContentId
  initiativeUpdateId
  issueId
  parentId
  projectUpdateId
  botActor {
    ...ActorBot
  }
  body
  resolvingComment {
    id
  }
  documentContent {
    ...DocumentContent
  }
  syncedWith {
    ...ExternalEntityInfo
  }
  externalThread {
    ...SyncedExternalThread
  }
  externalUser {
    id
  }
  initiativeUpdate {
    id
  }
  issue {
    id
  }
  updatedAt
  parent {
    id
  }
  projectUpdate {
    id
  }
  quotedText
  archivedAt
  createdAt
  resolvedAt
  editedAt
  id
  resolvingUser {
    id
  }
  user {
    id
  }
}
fragment SyncedExternalThread on SyncedExternalThread {
  __typename
  name
  displayName
  url
  subType
  type
  isPersonalIntegrationRequired
  isPersonalIntegrationConnected
  isConnected
  id
}
fragment DocumentContent on DocumentContent {
  __typename
  aiPromptRules {
    ...AiPromptRules
  }
  content
  contentState
  document {
    id
  }
  initiative {
    id
  }
  issue {
    id
  }
  updatedAt
  projectMilestone {
    id
  }
  project {
    id
  }
  restoredAt
  archivedAt
  createdAt
  id
}
fragment Reaction on Reaction {
  __typename
  emoji
  comment {
    id
  }
  externalUser {
    id
  }
  initiativeUpdate {
    id
  }
  issue {
    id
  }
  updatedAt
  projectUpdate {
    id
  }
  archivedAt
  createdAt
  id
  user {
    id
  }
}
fragment AiPromptRules on AiPromptRules {
  __typename
  updatedAt
  archivedAt
  createdAt
  id
  updatedBy {
    id
  }
}
fragment ExternalEntityInfo on ExternalEntityInfo {
  __typename
  metadata {
    ... on ExternalEntityInfoGithubMetadata {
      ...ExternalEntityInfoGithubMetadata
    }
    ... on ExternalEntityInfoJiraMetadata {
      ...ExternalEntityInfoJiraMetadata
    }
    ... on ExternalEntitySlackMetadata {
      ...ExternalEntitySlackMetadata
    }
  }
  id
  service
}
fragment ExternalEntityInfoGithubMetadata on ExternalEntityInfoGithubMetadata {
  __typename
  number
  owner
  repo
}
fragment ExternalEntityInfoJiraMetadata on ExternalEntityInfoJiraMetadata {
  __typename
  issueTypeId
  projectId
  issueKey
}
fragment ExternalEntitySlackMetadata on ExternalEntitySlackMetadata {
  __typename
  messageUrl
  channelId
  channelName
  isFromSlack
}
fragment CommentConnection on CommentConnection {
  __typename
  nodes {
    ...Comment
  }
  pageInfo {
    ...PageInfo
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`);
const AttachmentIssue_DocumentsDocument = new TypedDocumentString(`
    query attachmentIssue_documents($id: String!, $after: String, $before: String, $filter: DocumentFilter, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  attachmentIssue(id: $id) {
    documents(
      after: $after
      before: $before
      filter: $filter
      first: $first
      includeArchived: $includeArchived
      last: $last
      orderBy: $orderBy
    ) {
      ...DocumentConnection
    }
  }
}
    fragment Document on Document {
  __typename
  trashed
  documentContentId
  url
  color
  title
  slugId
  content
  icon
  initiative {
    id
  }
  issue {
    id
  }
  lastAppliedTemplate {
    id
  }
  updatedAt
  sortOrder
  project {
    id
  }
  hiddenAt
  archivedAt
  createdAt
  id
  creator {
    id
  }
  updatedBy {
    id
  }
}
fragment DocumentConnection on DocumentConnection {
  __typename
  nodes {
    ...Document
  }
  pageInfo {
    ...PageInfo
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`);
const AttachmentIssue_FormerAttachmentsDocument = new TypedDocumentString(`
    query attachmentIssue_formerAttachments($id: String!, $after: String, $before: String, $filter: AttachmentFilter, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  attachmentIssue(id: $id) {
    formerAttachments(
      after: $after
      before: $before
      filter: $filter
      first: $first
      includeArchived: $includeArchived
      last: $last
      orderBy: $orderBy
    ) {
      ...AttachmentConnection
    }
  }
}
    fragment Attachment on Attachment {
  __typename
  sourceType
  subtitle
  title
  metadata
  groupBySource
  source
  url
  bodyData
  creator {
    id
  }
  issue {
    id
  }
  originalIssue {
    id
  }
  updatedAt
  externalUserCreator {
    id
  }
  archivedAt
  createdAt
  id
}
fragment AttachmentConnection on AttachmentConnection {
  __typename
  nodes {
    ...Attachment
  }
  pageInfo {
    ...PageInfo
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`);
const AttachmentIssue_FormerNeedsDocument = new TypedDocumentString(`
    query attachmentIssue_formerNeeds($id: String!, $after: String, $before: String, $filter: CustomerNeedFilter, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  attachmentIssue(id: $id) {
    formerNeeds(
      after: $after
      before: $before
      filter: $filter
      first: $first
      includeArchived: $includeArchived
      last: $last
      orderBy: $orderBy
    ) {
      ...CustomerNeedConnection
    }
  }
}
    fragment CustomerNeed on CustomerNeed {
  __typename
  url
  attachment {
    id
  }
  comment {
    id
  }
  creator {
    id
  }
  customer {
    id
  }
  originalIssue {
    id
  }
  issue {
    id
  }
  updatedAt
  body
  projectAttachment {
    ...ProjectAttachment
  }
  project {
    id
  }
  archivedAt
  createdAt
  id
  priority
}
fragment ProjectAttachment on ProjectAttachment {
  __typename
  sourceType
  metadata
  source
  subtitle
  creator {
    id
  }
  updatedAt
  archivedAt
  createdAt
  id
  title
  url
}
fragment CustomerNeedConnection on CustomerNeedConnection {
  __typename
  nodes {
    ...CustomerNeed
  }
  pageInfo {
    ...PageInfo
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`);
const AttachmentIssue_HistoryDocument = new TypedDocumentString(`
    query attachmentIssue_history($id: String!, $after: String, $before: String, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  attachmentIssue(id: $id) {
    history(
      after: $after
      before: $before
      first: $first
      includeArchived: $includeArchived
      last: $last
      orderBy: $orderBy
    ) {
      ...IssueHistoryConnection
    }
  }
}
    fragment ActorBot on ActorBot {
  __typename
  avatarUrl
  name
  userDisplayName
  subType
  type
  id
}
fragment IssueHistory on IssueHistory {
  __typename
  triageResponsibilityAutoAssigned
  relationChanges {
    ...IssueRelationHistoryPayload
  }
  addedLabelIds
  removedLabelIds
  actor {
    id
  }
  descriptionUpdatedBy {
    ...User
  }
  actors {
    ...User
  }
  fromDelegate {
    id
  }
  toDelegate {
    id
  }
  botActor {
    ...ActorBot
  }
  fromCycle {
    id
  }
  toCycle {
    id
  }
  attachmentId
  customerNeedId
  toCycleId
  toParentId
  toConvertedProjectId
  toProjectId
  toStateId
  fromCycleId
  fromParentId
  fromProjectId
  fromStateId
  fromTeamId
  toTeamId
  fromAssigneeId
  toAssigneeId
  actorId
  issueImport {
    ...IssueImport
  }
  issue {
    id
  }
  addedLabels {
    ...IssueLabel
  }
  removedLabels {
    ...IssueLabel
  }
  updatedAt
  attachment {
    id
  }
  toConvertedProject {
    id
  }
  fromParent {
    id
  }
  toParent {
    id
  }
  fromProject {
    id
  }
  toProject {
    id
  }
  fromState {
    id
  }
  toState {
    id
  }
  fromTeam {
    id
  }
  toTeam {
    id
  }
  archivedAt
  createdAt
  id
  toAssignee {
    id
  }
  fromAssignee {
    id
  }
  triageResponsibilityNotifiedUsers {
    ...User
  }
  fromDueDate
  toDueDate
  fromEstimate
  toEstimate
  fromPriority
  toPriority
  fromTitle
  toTitle
  archived
  autoArchived
  autoClosed
  trashed
  updatedDescription
}
fragment User on User {
  __typename
  statusUntilAt
  description
  avatarUrl
  createdIssueCount
  disableReason
  avatarBackgroundColor
  statusEmoji
  initials
  statusLabel
  updatedAt
  lastSeen
  timezone
  archivedAt
  createdAt
  id
  gitHubUserId
  displayName
  email
  name
  url
  active
  guest
  app
  admin
  owner
  isAssignable
  isMentionable
  isMe
  supportsAgentSessions
  canAccessAnyPublicTeam
  calendarHash
  inviteHash
}
fragment IssueImport on IssueImport {
  __typename
  progress
  errorMetadata
  csvFileUrl
  serviceMetadata
  teamName
  mapping
  displayName
  creatorId
  updatedAt
  service
  status
  archivedAt
  createdAt
  id
  error
}
fragment IssueRelationHistoryPayload on IssueRelationHistoryPayload {
  __typename
  identifier
  type
}
fragment IssueLabel on IssueLabel {
  __typename
  lastAppliedAt
  color
  description
  name
  updatedAt
  inheritedFrom {
    id
  }
  parent {
    id
  }
  team {
    id
  }
  archivedAt
  createdAt
  id
  creator {
    id
  }
  retiredBy {
    id
  }
  isGroup
}
fragment IssueHistoryConnection on IssueHistoryConnection {
  __typename
  nodes {
    ...IssueHistory
  }
  pageInfo {
    ...PageInfo
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`);
const AttachmentIssue_InverseRelationsDocument = new TypedDocumentString(`
    query attachmentIssue_inverseRelations($id: String!, $after: String, $before: String, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  attachmentIssue(id: $id) {
    inverseRelations(
      after: $after
      before: $before
      first: $first
      includeArchived: $includeArchived
      last: $last
      orderBy: $orderBy
    ) {
      ...IssueRelationConnection
    }
  }
}
    fragment IssueRelation on IssueRelation {
  __typename
  issue {
    id
  }
  updatedAt
  relatedIssue {
    id
  }
  type
  archivedAt
  createdAt
  id
}
fragment IssueRelationConnection on IssueRelationConnection {
  __typename
  nodes {
    ...IssueRelation
  }
  pageInfo {
    ...PageInfo
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`);
const AttachmentIssue_LabelsDocument = new TypedDocumentString(`
    query attachmentIssue_labels($id: String!, $after: String, $before: String, $filter: IssueLabelFilter, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  attachmentIssue(id: $id) {
    labels(
      after: $after
      before: $before
      filter: $filter
      first: $first
      includeArchived: $includeArchived
      last: $last
      orderBy: $orderBy
    ) {
      ...IssueLabelConnection
    }
  }
}
    fragment IssueLabel on IssueLabel {
  __typename
  lastAppliedAt
  color
  description
  name
  updatedAt
  inheritedFrom {
    id
  }
  parent {
    id
  }
  team {
    id
  }
  archivedAt
  createdAt
  id
  creator {
    id
  }
  retiredBy {
    id
  }
  isGroup
}
fragment IssueLabelConnection on IssueLabelConnection {
  __typename
  nodes {
    ...IssueLabel
  }
  pageInfo {
    ...PageInfo
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`);
const AttachmentIssue_NeedsDocument = new TypedDocumentString(`
    query attachmentIssue_needs($id: String!, $after: String, $before: String, $filter: CustomerNeedFilter, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  attachmentIssue(id: $id) {
    needs(
      after: $after
      before: $before
      filter: $filter
      first: $first
      includeArchived: $includeArchived
      last: $last
      orderBy: $orderBy
    ) {
      ...CustomerNeedConnection
    }
  }
}
    fragment CustomerNeed on CustomerNeed {
  __typename
  url
  attachment {
    id
  }
  comment {
    id
  }
  creator {
    id
  }
  customer {
    id
  }
  originalIssue {
    id
  }
  issue {
    id
  }
  updatedAt
  body
  projectAttachment {
    ...ProjectAttachment
  }
  project {
    id
  }
  archivedAt
  createdAt
  id
  priority
}
fragment ProjectAttachment on ProjectAttachment {
  __typename
  sourceType
  metadata
  source
  subtitle
  creator {
    id
  }
  updatedAt
  archivedAt
  createdAt
  id
  title
  url
}
fragment CustomerNeedConnection on CustomerNeedConnection {
  __typename
  nodes {
    ...CustomerNeed
  }
  pageInfo {
    ...PageInfo
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`);
const AttachmentIssue_RelationsDocument = new TypedDocumentString(`
    query attachmentIssue_relations($id: String!, $after: String, $before: String, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  attachmentIssue(id: $id) {
    relations(
      after: $after
      before: $before
      first: $first
      includeArchived: $includeArchived
      last: $last
      orderBy: $orderBy
    ) {
      ...IssueRelationConnection
    }
  }
}
    fragment IssueRelation on IssueRelation {
  __typename
  issue {
    id
  }
  updatedAt
  relatedIssue {
    id
  }
  type
  archivedAt
  createdAt
  id
}
fragment IssueRelationConnection on IssueRelationConnection {
  __typename
  nodes {
    ...IssueRelation
  }
  pageInfo {
    ...PageInfo
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`);
const AttachmentIssue_SubscribersDocument = new TypedDocumentString(`
    query attachmentIssue_subscribers($id: String!, $after: String, $before: String, $filter: UserFilter, $first: Int, $includeArchived: Boolean, $includeDisabled: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  attachmentIssue(id: $id) {
    subscribers(
      after: $after
      before: $before
      filter: $filter
      first: $first
      includeArchived: $includeArchived
      includeDisabled: $includeDisabled
      last: $last
      orderBy: $orderBy
    ) {
      ...UserConnection
    }
  }
}
    fragment User on User {
  __typename
  statusUntilAt
  description
  avatarUrl
  createdIssueCount
  disableReason
  avatarBackgroundColor
  statusEmoji
  initials
  statusLabel
  updatedAt
  lastSeen
  timezone
  archivedAt
  createdAt
  id
  gitHubUserId
  displayName
  email
  name
  url
  active
  guest
  app
  admin
  owner
  isAssignable
  isMentionable
  isMe
  supportsAgentSessions
  canAccessAnyPublicTeam
  calendarHash
  inviteHash
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}
fragment UserConnection on UserConnection {
  __typename
  nodes {
    ...User
  }
  pageInfo {
    ...PageInfo
  }
}`);
const AttachmentsDocument = new TypedDocumentString(`
    query attachments($after: String, $before: String, $filter: AttachmentFilter, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  attachments(
    after: $after
    before: $before
    filter: $filter
    first: $first
    includeArchived: $includeArchived
    last: $last
    orderBy: $orderBy
  ) {
    ...AttachmentConnection
  }
}
    fragment Attachment on Attachment {
  __typename
  sourceType
  subtitle
  title
  metadata
  groupBySource
  source
  url
  bodyData
  creator {
    id
  }
  issue {
    id
  }
  originalIssue {
    id
  }
  updatedAt
  externalUserCreator {
    id
  }
  archivedAt
  createdAt
  id
}
fragment AttachmentConnection on AttachmentConnection {
  __typename
  nodes {
    ...Attachment
  }
  pageInfo {
    ...PageInfo
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`);
const AttachmentsForUrlDocument = new TypedDocumentString(`
    query attachmentsForURL($after: String, $before: String, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy, $url: String!) {
  attachmentsForURL(
    after: $after
    before: $before
    first: $first
    includeArchived: $includeArchived
    last: $last
    orderBy: $orderBy
    url: $url
  ) {
    ...AttachmentConnection
  }
}
    fragment Attachment on Attachment {
  __typename
  sourceType
  subtitle
  title
  metadata
  groupBySource
  source
  url
  bodyData
  creator {
    id
  }
  issue {
    id
  }
  originalIssue {
    id
  }
  updatedAt
  externalUserCreator {
    id
  }
  archivedAt
  createdAt
  id
}
fragment AttachmentConnection on AttachmentConnection {
  __typename
  nodes {
    ...Attachment
  }
  pageInfo {
    ...PageInfo
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`);
const AuditEntriesDocument = new TypedDocumentString(`
    query auditEntries($after: String, $before: String, $filter: AuditEntryFilter, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  auditEntries(
    after: $after
    before: $before
    filter: $filter
    first: $first
    includeArchived: $includeArchived
    last: $last
    orderBy: $orderBy
  ) {
    ...AuditEntryConnection
  }
}
    fragment AuditEntry on AuditEntry {
  __typename
  requestInformation
  metadata
  countryCode
  ip
  actorId
  updatedAt
  archivedAt
  createdAt
  id
  actor {
    id
  }
  type
}
fragment AuditEntryConnection on AuditEntryConnection {
  __typename
  nodes {
    ...AuditEntry
  }
  pageInfo {
    ...PageInfo
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`);
const AuditEntryTypesDocument = new TypedDocumentString(`
    query auditEntryTypes {
  auditEntryTypes {
    ...AuditEntryType
  }
}
    fragment AuditEntryType on AuditEntryType {
  __typename
  description
  type
}`);
const AuthenticationSessionsDocument = new TypedDocumentString(`
    query authenticationSessions {
  authenticationSessions {
    ...AuthenticationSessionResponse
  }
}
    fragment AuthenticationSessionResponse on AuthenticationSessionResponse {
  __typename
  client
  countryCodes
  updatedAt
  location
  ip
  isCurrentSession
  locationCity
  locationCountryCode
  locationCountry
  locationRegionCode
  name
  operatingSystem
  service
  userAgent
  createdAt
  type
  browserType
  lastActiveAt
  id
}`);
const AvailableUsersDocument = new TypedDocumentString(`
    query availableUsers {
  availableUsers {
    ...AuthResolverResponse
  }
}
    fragment AuthUser on AuthUser {
  __typename
  avatarUrl
  organization {
    ...AuthOrganization
  }
  createdAt
  displayName
  email
  name
  userAccountId
  active
  role
  id
}
fragment AuthOrganization on AuthOrganization {
  __typename
  allowedAuthServices
  previousUrlKeys
  serviceId
  releaseChannel
  logoUrl
  name
  urlKey
  region
  deletionRequestedAt
  createdAt
  id
  samlEnabled
  scimEnabled
  enabled
  userCount
}
fragment AuthResolverResponse on AuthResolverResponse {
  __typename
  token
  email
  lastUsedOrganizationId
  users {
    ...AuthUser
  }
  lockedUsers {
    ...AuthUser
  }
  lockedOrganizations {
    ...AuthOrganization
  }
  availableOrganizations {
    ...AuthOrganization
  }
  allowDomainAccess
  id
}`);
const CommentDocument = new TypedDocumentString(`
    query comment($hash: String, $id: String) {
  comment(hash: $hash, id: $id) {
    ...Comment
  }
}
    fragment ActorBot on ActorBot {
  __typename
  avatarUrl
  name
  userDisplayName
  subType
  type
  id
}
fragment Comment on Comment {
  __typename
  agentSession {
    id
  }
  url
  reactionData
  reactions {
    ...Reaction
  }
  resolvingCommentId
  documentContentId
  initiativeUpdateId
  issueId
  parentId
  projectUpdateId
  botActor {
    ...ActorBot
  }
  body
  resolvingComment {
    id
  }
  documentContent {
    ...DocumentContent
  }
  syncedWith {
    ...ExternalEntityInfo
  }
  externalThread {
    ...SyncedExternalThread
  }
  externalUser {
    id
  }
  initiativeUpdate {
    id
  }
  issue {
    id
  }
  updatedAt
  parent {
    id
  }
  projectUpdate {
    id
  }
  quotedText
  archivedAt
  createdAt
  resolvedAt
  editedAt
  id
  resolvingUser {
    id
  }
  user {
    id
  }
}
fragment SyncedExternalThread on SyncedExternalThread {
  __typename
  name
  displayName
  url
  subType
  type
  isPersonalIntegrationRequired
  isPersonalIntegrationConnected
  isConnected
  id
}
fragment DocumentContent on DocumentContent {
  __typename
  aiPromptRules {
    ...AiPromptRules
  }
  content
  contentState
  document {
    id
  }
  initiative {
    id
  }
  issue {
    id
  }
  updatedAt
  projectMilestone {
    id
  }
  project {
    id
  }
  restoredAt
  archivedAt
  createdAt
  id
}
fragment Reaction on Reaction {
  __typename
  emoji
  comment {
    id
  }
  externalUser {
    id
  }
  initiativeUpdate {
    id
  }
  issue {
    id
  }
  updatedAt
  projectUpdate {
    id
  }
  archivedAt
  createdAt
  id
  user {
    id
  }
}
fragment AiPromptRules on AiPromptRules {
  __typename
  updatedAt
  archivedAt
  createdAt
  id
  updatedBy {
    id
  }
}
fragment ExternalEntityInfo on ExternalEntityInfo {
  __typename
  metadata {
    ... on ExternalEntityInfoGithubMetadata {
      ...ExternalEntityInfoGithubMetadata
    }
    ... on ExternalEntityInfoJiraMetadata {
      ...ExternalEntityInfoJiraMetadata
    }
    ... on ExternalEntitySlackMetadata {
      ...ExternalEntitySlackMetadata
    }
  }
  id
  service
}
fragment ExternalEntityInfoGithubMetadata on ExternalEntityInfoGithubMetadata {
  __typename
  number
  owner
  repo
}
fragment ExternalEntityInfoJiraMetadata on ExternalEntityInfoJiraMetadata {
  __typename
  issueTypeId
  projectId
  issueKey
}
fragment ExternalEntitySlackMetadata on ExternalEntitySlackMetadata {
  __typename
  messageUrl
  channelId
  channelName
  isFromSlack
}`);
const Comment_BotActorDocument = new TypedDocumentString(`
    query comment_botActor($hash: String, $id: String) {
  comment(hash: $hash, id: $id) {
    botActor {
      ...ActorBot
    }
  }
}
    fragment ActorBot on ActorBot {
  __typename
  avatarUrl
  name
  userDisplayName
  subType
  type
  id
}`);
const Comment_ChildrenDocument = new TypedDocumentString(`
    query comment_children($hash: String, $id: String, $after: String, $before: String, $filter: CommentFilter, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  comment(hash: $hash, id: $id) {
    children(
      after: $after
      before: $before
      filter: $filter
      first: $first
      includeArchived: $includeArchived
      last: $last
      orderBy: $orderBy
    ) {
      ...CommentConnection
    }
  }
}
    fragment ActorBot on ActorBot {
  __typename
  avatarUrl
  name
  userDisplayName
  subType
  type
  id
}
fragment Comment on Comment {
  __typename
  agentSession {
    id
  }
  url
  reactionData
  reactions {
    ...Reaction
  }
  resolvingCommentId
  documentContentId
  initiativeUpdateId
  issueId
  parentId
  projectUpdateId
  botActor {
    ...ActorBot
  }
  body
  resolvingComment {
    id
  }
  documentContent {
    ...DocumentContent
  }
  syncedWith {
    ...ExternalEntityInfo
  }
  externalThread {
    ...SyncedExternalThread
  }
  externalUser {
    id
  }
  initiativeUpdate {
    id
  }
  issue {
    id
  }
  updatedAt
  parent {
    id
  }
  projectUpdate {
    id
  }
  quotedText
  archivedAt
  createdAt
  resolvedAt
  editedAt
  id
  resolvingUser {
    id
  }
  user {
    id
  }
}
fragment SyncedExternalThread on SyncedExternalThread {
  __typename
  name
  displayName
  url
  subType
  type
  isPersonalIntegrationRequired
  isPersonalIntegrationConnected
  isConnected
  id
}
fragment DocumentContent on DocumentContent {
  __typename
  aiPromptRules {
    ...AiPromptRules
  }
  content
  contentState
  document {
    id
  }
  initiative {
    id
  }
  issue {
    id
  }
  updatedAt
  projectMilestone {
    id
  }
  project {
    id
  }
  restoredAt
  archivedAt
  createdAt
  id
}
fragment Reaction on Reaction {
  __typename
  emoji
  comment {
    id
  }
  externalUser {
    id
  }
  initiativeUpdate {
    id
  }
  issue {
    id
  }
  updatedAt
  projectUpdate {
    id
  }
  archivedAt
  createdAt
  id
  user {
    id
  }
}
fragment AiPromptRules on AiPromptRules {
  __typename
  updatedAt
  archivedAt
  createdAt
  id
  updatedBy {
    id
  }
}
fragment ExternalEntityInfo on ExternalEntityInfo {
  __typename
  metadata {
    ... on ExternalEntityInfoGithubMetadata {
      ...ExternalEntityInfoGithubMetadata
    }
    ... on ExternalEntityInfoJiraMetadata {
      ...ExternalEntityInfoJiraMetadata
    }
    ... on ExternalEntitySlackMetadata {
      ...ExternalEntitySlackMetadata
    }
  }
  id
  service
}
fragment ExternalEntityInfoGithubMetadata on ExternalEntityInfoGithubMetadata {
  __typename
  number
  owner
  repo
}
fragment ExternalEntityInfoJiraMetadata on ExternalEntityInfoJiraMetadata {
  __typename
  issueTypeId
  projectId
  issueKey
}
fragment ExternalEntitySlackMetadata on ExternalEntitySlackMetadata {
  __typename
  messageUrl
  channelId
  channelName
  isFromSlack
}
fragment CommentConnection on CommentConnection {
  __typename
  nodes {
    ...Comment
  }
  pageInfo {
    ...PageInfo
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`);
const Comment_CreatedIssuesDocument = new TypedDocumentString(`
    query comment_createdIssues($hash: String, $id: String, $after: String, $before: String, $filter: IssueFilter, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  comment(hash: $hash, id: $id) {
    createdIssues(
      after: $after
      before: $before
      filter: $filter
      first: $first
      includeArchived: $includeArchived
      last: $last
      orderBy: $orderBy
    ) {
      ...IssueConnection
    }
  }
}
    fragment ActorBot on ActorBot {
  __typename
  avatarUrl
  name
  userDisplayName
  subType
  type
  id
}
fragment Reaction on Reaction {
  __typename
  emoji
  comment {
    id
  }
  externalUser {
    id
  }
  initiativeUpdate {
    id
  }
  issue {
    id
  }
  updatedAt
  projectUpdate {
    id
  }
  archivedAt
  createdAt
  id
  user {
    id
  }
}
fragment Issue on Issue {
  __typename
  trashed
  reactionData
  labelIds
  integrationSourceType
  url
  identifier
  priorityLabel
  previousIdentifiers
  reactions {
    ...Reaction
  }
  customerTicketCount
  branchName
  delegate {
    id
  }
  botActor {
    ...ActorBot
  }
  sourceComment {
    id
  }
  cycle {
    id
  }
  dueDate
  estimate
  syncedWith {
    ...ExternalEntityInfo
  }
  externalUserCreator {
    id
  }
  asksExternalUserRequester {
    id
  }
  asksRequester {
    id
  }
  description
  title
  number
  lastAppliedTemplate {
    id
  }
  updatedAt
  boardOrder
  prioritySortOrder
  sortOrder
  subIssueSortOrder
  parent {
    id
  }
  priority
  project {
    id
  }
  projectMilestone {
    id
  }
  recurringIssueTemplate {
    id
  }
  team {
    id
  }
  archivedAt
  createdAt
  startedTriageAt
  triagedAt
  addedToCycleAt
  addedToProjectAt
  addedToTeamAt
  autoArchivedAt
  autoClosedAt
  canceledAt
  completedAt
  startedAt
  slaStartedAt
  slaBreachesAt
  slaHighRiskAt
  slaMediumRiskAt
  snoozedUntilAt
  slaType
  id
  assignee {
    id
  }
  creator {
    id
  }
  snoozedBy {
    id
  }
  favorite {
    id
  }
  state {
    id
  }
}
fragment ExternalEntityInfo on ExternalEntityInfo {
  __typename
  metadata {
    ... on ExternalEntityInfoGithubMetadata {
      ...ExternalEntityInfoGithubMetadata
    }
    ... on ExternalEntityInfoJiraMetadata {
      ...ExternalEntityInfoJiraMetadata
    }
    ... on ExternalEntitySlackMetadata {
      ...ExternalEntitySlackMetadata
    }
  }
  id
  service
}
fragment ExternalEntityInfoGithubMetadata on ExternalEntityInfoGithubMetadata {
  __typename
  number
  owner
  repo
}
fragment ExternalEntityInfoJiraMetadata on ExternalEntityInfoJiraMetadata {
  __typename
  issueTypeId
  projectId
  issueKey
}
fragment ExternalEntitySlackMetadata on ExternalEntitySlackMetadata {
  __typename
  messageUrl
  channelId
  channelName
  isFromSlack
}
fragment IssueConnection on IssueConnection {
  __typename
  nodes {
    ...Issue
  }
  pageInfo {
    ...PageInfo
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`);
const Comment_DocumentContentDocument = new TypedDocumentString(`
    query comment_documentContent($hash: String, $id: String) {
  comment(hash: $hash, id: $id) {
    documentContent {
      ...DocumentContent
    }
  }
}
    fragment DocumentContent on DocumentContent {
  __typename
  aiPromptRules {
    ...AiPromptRules
  }
  content
  contentState
  document {
    id
  }
  initiative {
    id
  }
  issue {
    id
  }
  updatedAt
  projectMilestone {
    id
  }
  project {
    id
  }
  restoredAt
  archivedAt
  createdAt
  id
}
fragment AiPromptRules on AiPromptRules {
  __typename
  updatedAt
  archivedAt
  createdAt
  id
  updatedBy {
    id
  }
}`);
const Comment_DocumentContent_AiPromptRulesDocument = new TypedDocumentString(`
    query comment_documentContent_aiPromptRules($hash: String, $id: String) {
  comment(hash: $hash, id: $id) {
    documentContent {
      aiPromptRules {
        ...AiPromptRules
      }
    }
  }
}
    fragment AiPromptRules on AiPromptRules {
  __typename
  updatedAt
  archivedAt
  createdAt
  id
  updatedBy {
    id
  }
}`);
const Comment_ExternalThreadDocument = new TypedDocumentString(`
    query comment_externalThread($hash: String, $id: String) {
  comment(hash: $hash, id: $id) {
    externalThread {
      ...SyncedExternalThread
    }
  }
}
    fragment SyncedExternalThread on SyncedExternalThread {
  __typename
  name
  displayName
  url
  subType
  type
  isPersonalIntegrationRequired
  isPersonalIntegrationConnected
  isConnected
  id
}`);
const CommentsDocument = new TypedDocumentString(`
    query comments($after: String, $before: String, $filter: CommentFilter, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  comments(
    after: $after
    before: $before
    filter: $filter
    first: $first
    includeArchived: $includeArchived
    last: $last
    orderBy: $orderBy
  ) {
    ...CommentConnection
  }
}
    fragment ActorBot on ActorBot {
  __typename
  avatarUrl
  name
  userDisplayName
  subType
  type
  id
}
fragment Comment on Comment {
  __typename
  agentSession {
    id
  }
  url
  reactionData
  reactions {
    ...Reaction
  }
  resolvingCommentId
  documentContentId
  initiativeUpdateId
  issueId
  parentId
  projectUpdateId
  botActor {
    ...ActorBot
  }
  body
  resolvingComment {
    id
  }
  documentContent {
    ...DocumentContent
  }
  syncedWith {
    ...ExternalEntityInfo
  }
  externalThread {
    ...SyncedExternalThread
  }
  externalUser {
    id
  }
  initiativeUpdate {
    id
  }
  issue {
    id
  }
  updatedAt
  parent {
    id
  }
  projectUpdate {
    id
  }
  quotedText
  archivedAt
  createdAt
  resolvedAt
  editedAt
  id
  resolvingUser {
    id
  }
  user {
    id
  }
}
fragment SyncedExternalThread on SyncedExternalThread {
  __typename
  name
  displayName
  url
  subType
  type
  isPersonalIntegrationRequired
  isPersonalIntegrationConnected
  isConnected
  id
}
fragment DocumentContent on DocumentContent {
  __typename
  aiPromptRules {
    ...AiPromptRules
  }
  content
  contentState
  document {
    id
  }
  initiative {
    id
  }
  issue {
    id
  }
  updatedAt
  projectMilestone {
    id
  }
  project {
    id
  }
  restoredAt
  archivedAt
  createdAt
  id
}
fragment Reaction on Reaction {
  __typename
  emoji
  comment {
    id
  }
  externalUser {
    id
  }
  initiativeUpdate {
    id
  }
  issue {
    id
  }
  updatedAt
  projectUpdate {
    id
  }
  archivedAt
  createdAt
  id
  user {
    id
  }
}
fragment AiPromptRules on AiPromptRules {
  __typename
  updatedAt
  archivedAt
  createdAt
  id
  updatedBy {
    id
  }
}
fragment ExternalEntityInfo on ExternalEntityInfo {
  __typename
  metadata {
    ... on ExternalEntityInfoGithubMetadata {
      ...ExternalEntityInfoGithubMetadata
    }
    ... on ExternalEntityInfoJiraMetadata {
      ...ExternalEntityInfoJiraMetadata
    }
    ... on ExternalEntitySlackMetadata {
      ...ExternalEntitySlackMetadata
    }
  }
  id
  service
}
fragment ExternalEntityInfoGithubMetadata on ExternalEntityInfoGithubMetadata {
  __typename
  number
  owner
  repo
}
fragment ExternalEntityInfoJiraMetadata on ExternalEntityInfoJiraMetadata {
  __typename
  issueTypeId
  projectId
  issueKey
}
fragment ExternalEntitySlackMetadata on ExternalEntitySlackMetadata {
  __typename
  messageUrl
  channelId
  channelName
  isFromSlack
}
fragment CommentConnection on CommentConnection {
  __typename
  nodes {
    ...Comment
  }
  pageInfo {
    ...PageInfo
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`);
const CustomViewDocument = new TypedDocumentString(`
    query customView($id: String!) {
  customView(id: $id) {
    ...CustomView
  }
}
    fragment CustomView on CustomView {
  __typename
  viewPreferencesValues {
    ...ViewPreferencesValues
  }
  color
  userViewPreferences {
    ...ViewPreferences
  }
  slugId
  description
  feedItemFilterData
  initiativeFilterData
  filterData
  projectFilterData
  filters
  icon
  updatedAt
  modelName
  name
  organizationViewPreferences {
    ...ViewPreferences
  }
  team {
    id
  }
  archivedAt
  createdAt
  id
  creator {
    id
  }
  updatedBy {
    id
  }
  owner {
    id
  }
  shared
}
fragment ViewPreferences on ViewPreferences {
  __typename
  updatedAt
  archivedAt
  createdAt
  id
  type
  preferences {
    ...ViewPreferencesValues
  }
  viewType
}
fragment ViewPreferencesValues on ViewPreferencesValues {
  __typename
  issueGrouping
  viewOrdering
  issueSubGrouping
  showCompletedIssues
}`);
const CustomView_InitiativesDocument = new TypedDocumentString(`
    query customView_initiatives($id: String!, $after: String, $before: String, $filter: InitiativeFilter, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  customView(id: $id) {
    initiatives(
      after: $after
      before: $before
      filter: $filter
      first: $first
      includeArchived: $includeArchived
      last: $last
      orderBy: $orderBy
    ) {
      ...InitiativeConnection
    }
  }
}
    fragment DocumentContent on DocumentContent {
  __typename
  aiPromptRules {
    ...AiPromptRules
  }
  content
  contentState
  document {
    id
  }
  initiative {
    id
  }
  issue {
    id
  }
  updatedAt
  projectMilestone {
    id
  }
  project {
    id
  }
  restoredAt
  archivedAt
  createdAt
  id
}
fragment AiPromptRules on AiPromptRules {
  __typename
  updatedAt
  archivedAt
  createdAt
  id
  updatedBy {
    id
  }
}
fragment Initiative on Initiative {
  __typename
  trashed
  url
  parentInitiative {
    id
  }
  integrationsSettings {
    id
  }
  documentContent {
    ...DocumentContent
  }
  updateRemindersDay
  description
  targetDate
  updateReminderFrequency
  health
  updateRemindersHour
  icon
  color
  content
  slugId
  lastUpdate {
    id
  }
  updatedAt
  updateReminderFrequencyInWeeks
  name
  targetDateResolution
  frequencyResolution
  sortOrder
  status
  archivedAt
  createdAt
  healthUpdatedAt
  startedAt
  completedAt
  id
  creator {
    id
  }
  owner {
    id
  }
}
fragment InitiativeConnection on InitiativeConnection {
  __typename
  nodes {
    ...Initiative
  }
  pageInfo {
    ...PageInfo
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`);
const CustomView_IssuesDocument = new TypedDocumentString(`
    query customView_issues($id: String!, $after: String, $before: String, $filter: IssueFilter, $first: Int, $includeArchived: Boolean, $includeSubTeams: Boolean, $last: Int, $orderBy: PaginationOrderBy, $sort: [IssueSortInput!]) {
  customView(id: $id) {
    issues(
      after: $after
      before: $before
      filter: $filter
      first: $first
      includeArchived: $includeArchived
      includeSubTeams: $includeSubTeams
      last: $last
      orderBy: $orderBy
      sort: $sort
    ) {
      ...IssueConnection
    }
  }
}
    fragment ActorBot on ActorBot {
  __typename
  avatarUrl
  name
  userDisplayName
  subType
  type
  id
}
fragment Reaction on Reaction {
  __typename
  emoji
  comment {
    id
  }
  externalUser {
    id
  }
  initiativeUpdate {
    id
  }
  issue {
    id
  }
  updatedAt
  projectUpdate {
    id
  }
  archivedAt
  createdAt
  id
  user {
    id
  }
}
fragment Issue on Issue {
  __typename
  trashed
  reactionData
  labelIds
  integrationSourceType
  url
  identifier
  priorityLabel
  previousIdentifiers
  reactions {
    ...Reaction
  }
  customerTicketCount
  branchName
  delegate {
    id
  }
  botActor {
    ...ActorBot
  }
  sourceComment {
    id
  }
  cycle {
    id
  }
  dueDate
  estimate
  syncedWith {
    ...ExternalEntityInfo
  }
  externalUserCreator {
    id
  }
  asksExternalUserRequester {
    id
  }
  asksRequester {
    id
  }
  description
  title
  number
  lastAppliedTemplate {
    id
  }
  updatedAt
  boardOrder
  prioritySortOrder
  sortOrder
  subIssueSortOrder
  parent {
    id
  }
  priority
  project {
    id
  }
  projectMilestone {
    id
  }
  recurringIssueTemplate {
    id
  }
  team {
    id
  }
  archivedAt
  createdAt
  startedTriageAt
  triagedAt
  addedToCycleAt
  addedToProjectAt
  addedToTeamAt
  autoArchivedAt
  autoClosedAt
  canceledAt
  completedAt
  startedAt
  slaStartedAt
  slaBreachesAt
  slaHighRiskAt
  slaMediumRiskAt
  snoozedUntilAt
  slaType
  id
  assignee {
    id
  }
  creator {
    id
  }
  snoozedBy {
    id
  }
  favorite {
    id
  }
  state {
    id
  }
}
fragment ExternalEntityInfo on ExternalEntityInfo {
  __typename
  metadata {
    ... on ExternalEntityInfoGithubMetadata {
      ...ExternalEntityInfoGithubMetadata
    }
    ... on ExternalEntityInfoJiraMetadata {
      ...ExternalEntityInfoJiraMetadata
    }
    ... on ExternalEntitySlackMetadata {
      ...ExternalEntitySlackMetadata
    }
  }
  id
  service
}
fragment ExternalEntityInfoGithubMetadata on ExternalEntityInfoGithubMetadata {
  __typename
  number
  owner
  repo
}
fragment ExternalEntityInfoJiraMetadata on ExternalEntityInfoJiraMetadata {
  __typename
  issueTypeId
  projectId
  issueKey
}
fragment ExternalEntitySlackMetadata on ExternalEntitySlackMetadata {
  __typename
  messageUrl
  channelId
  channelName
  isFromSlack
}
fragment IssueConnection on IssueConnection {
  __typename
  nodes {
    ...Issue
  }
  pageInfo {
    ...PageInfo
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`);
const CustomView_OrganizationViewPreferencesDocument = new TypedDocumentString(`
    query customView_organizationViewPreferences($id: String!) {
  customView(id: $id) {
    organizationViewPreferences {
      ...ViewPreferences
    }
  }
}
    fragment ViewPreferences on ViewPreferences {
  __typename
  updatedAt
  archivedAt
  createdAt
  id
  type
  preferences {
    ...ViewPreferencesValues
  }
  viewType
}
fragment ViewPreferencesValues on ViewPreferencesValues {
  __typename
  issueGrouping
  viewOrdering
  issueSubGrouping
  showCompletedIssues
}`);
const CustomView_OrganizationViewPreferences_PreferencesDocument = new TypedDocumentString(`
    query customView_organizationViewPreferences_preferences($id: String!) {
  customView(id: $id) {
    organizationViewPreferences {
      preferences {
        ...ViewPreferencesValues
      }
    }
  }
}
    fragment ViewPreferencesValues on ViewPreferencesValues {
  __typename
  issueGrouping
  viewOrdering
  issueSubGrouping
  showCompletedIssues
}`);
const CustomView_ProjectsDocument = new TypedDocumentString(`
    query customView_projects($id: String!, $after: String, $before: String, $filter: ProjectFilter, $first: Int, $includeArchived: Boolean, $includeSubTeams: Boolean, $last: Int, $orderBy: PaginationOrderBy, $sort: [ProjectSortInput!]) {
  customView(id: $id) {
    projects(
      after: $after
      before: $before
      filter: $filter
      first: $first
      includeArchived: $includeArchived
      includeSubTeams: $includeSubTeams
      last: $last
      orderBy: $orderBy
      sort: $sort
    ) {
      ...ProjectConnection
    }
  }
}
    fragment DocumentContent on DocumentContent {
  __typename
  aiPromptRules {
    ...AiPromptRules
  }
  content
  contentState
  document {
    id
  }
  initiative {
    id
  }
  issue {
    id
  }
  updatedAt
  projectMilestone {
    id
  }
  project {
    id
  }
  restoredAt
  archivedAt
  createdAt
  id
}
fragment Project on Project {
  __typename
  trashed
  labelIds
  url
  integrationsSettings {
    id
  }
  documentContent {
    ...DocumentContent
  }
  updateRemindersDay
  targetDate
  startDate
  updateReminderFrequency
  health
  updateRemindersHour
  icon
  lastUpdate {
    id
  }
  lastAppliedTemplate {
    id
  }
  updatedAt
  updateReminderFrequencyInWeeks
  completedScopeHistory
  completedIssueCountHistory
  inProgressScopeHistory
  progress
  scope
  priorityLabel
  priority
  lead {
    id
  }
  convertedFromIssue {
    id
  }
  color
  content
  description
  name
  slugId
  targetDateResolution
  startDateResolution
  frequencyResolution
  prioritySortOrder
  sortOrder
  status {
    id
  }
  archivedAt
  createdAt
  healthUpdatedAt
  autoArchivedAt
  canceledAt
  completedAt
  startedAt
  projectUpdateRemindersPausedUntilAt
  scopeHistory
  issueCountHistory
  id
  creator {
    id
  }
  favorite {
    id
  }
  slackIssueComments
  slackNewIssue
  slackIssueStatuses
  state
}
fragment AiPromptRules on AiPromptRules {
  __typename
  updatedAt
  archivedAt
  createdAt
  id
  updatedBy {
    id
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}
fragment ProjectConnection on ProjectConnection {
  __typename
  nodes {
    ...Project
  }
  pageInfo {
    ...PageInfo
  }
}`);
const CustomView_UserViewPreferencesDocument = new TypedDocumentString(`
    query customView_userViewPreferences($id: String!) {
  customView(id: $id) {
    userViewPreferences {
      ...ViewPreferences
    }
  }
}
    fragment ViewPreferences on ViewPreferences {
  __typename
  updatedAt
  archivedAt
  createdAt
  id
  type
  preferences {
    ...ViewPreferencesValues
  }
  viewType
}
fragment ViewPreferencesValues on ViewPreferencesValues {
  __typename
  issueGrouping
  viewOrdering
  issueSubGrouping
  showCompletedIssues
}`);
const CustomView_UserViewPreferences_PreferencesDocument = new TypedDocumentString(`
    query customView_userViewPreferences_preferences($id: String!) {
  customView(id: $id) {
    userViewPreferences {
      preferences {
        ...ViewPreferencesValues
      }
    }
  }
}
    fragment ViewPreferencesValues on ViewPreferencesValues {
  __typename
  issueGrouping
  viewOrdering
  issueSubGrouping
  showCompletedIssues
}`);
const CustomView_ViewPreferencesValuesDocument = new TypedDocumentString(`
    query customView_viewPreferencesValues($id: String!) {
  customView(id: $id) {
    viewPreferencesValues {
      ...ViewPreferencesValues
    }
  }
}
    fragment ViewPreferencesValues on ViewPreferencesValues {
  __typename
  issueGrouping
  viewOrdering
  issueSubGrouping
  showCompletedIssues
}`);
const CustomViewHasSubscribersDocument = new TypedDocumentString(`
    query customViewHasSubscribers($id: String!) {
  customViewHasSubscribers(id: $id) {
    ...CustomViewHasSubscribersPayload
  }
}
    fragment CustomViewHasSubscribersPayload on CustomViewHasSubscribersPayload {
  __typename
  hasSubscribers
}`);
const CustomViewsDocument = new TypedDocumentString(`
    query customViews($after: String, $before: String, $filter: CustomViewFilter, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy, $sort: [CustomViewSortInput!]) {
  customViews(
    after: $after
    before: $before
    filter: $filter
    first: $first
    includeArchived: $includeArchived
    last: $last
    orderBy: $orderBy
    sort: $sort
  ) {
    ...CustomViewConnection
  }
}
    fragment CustomView on CustomView {
  __typename
  viewPreferencesValues {
    ...ViewPreferencesValues
  }
  color
  userViewPreferences {
    ...ViewPreferences
  }
  slugId
  description
  feedItemFilterData
  initiativeFilterData
  filterData
  projectFilterData
  filters
  icon
  updatedAt
  modelName
  name
  organizationViewPreferences {
    ...ViewPreferences
  }
  team {
    id
  }
  archivedAt
  createdAt
  id
  creator {
    id
  }
  updatedBy {
    id
  }
  owner {
    id
  }
  shared
}
fragment ViewPreferences on ViewPreferences {
  __typename
  updatedAt
  archivedAt
  createdAt
  id
  type
  preferences {
    ...ViewPreferencesValues
  }
  viewType
}
fragment CustomViewConnection on CustomViewConnection {
  __typename
  nodes {
    ...CustomView
  }
  pageInfo {
    ...PageInfo
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}
fragment ViewPreferencesValues on ViewPreferencesValues {
  __typename
  issueGrouping
  viewOrdering
  issueSubGrouping
  showCompletedIssues
}`);
const CustomerDocument = new TypedDocumentString(`
    query customer($id: String!) {
  customer(id: $id) {
    ...Customer
  }
}
    fragment Customer on Customer {
  __typename
  slackChannelId
  mainSourceId
  revenue
  approximateNeedCount
  status {
    id
  }
  logoUrl
  name
  slugId
  domains
  externalIds
  integration {
    id
  }
  updatedAt
  size
  tier {
    id
  }
  archivedAt
  createdAt
  id
  owner {
    id
  }
  url
}`);
const CustomerNeedDocument = new TypedDocumentString(`
    query customerNeed($hash: String, $id: String) {
  customerNeed(hash: $hash, id: $id) {
    ...CustomerNeed
  }
}
    fragment CustomerNeed on CustomerNeed {
  __typename
  url
  attachment {
    id
  }
  comment {
    id
  }
  creator {
    id
  }
  customer {
    id
  }
  originalIssue {
    id
  }
  issue {
    id
  }
  updatedAt
  body
  projectAttachment {
    ...ProjectAttachment
  }
  project {
    id
  }
  archivedAt
  createdAt
  id
  priority
}
fragment ProjectAttachment on ProjectAttachment {
  __typename
  sourceType
  metadata
  source
  subtitle
  creator {
    id
  }
  updatedAt
  archivedAt
  createdAt
  id
  title
  url
}`);
const CustomerNeed_ProjectAttachmentDocument = new TypedDocumentString(`
    query customerNeed_projectAttachment($hash: String, $id: String) {
  customerNeed(hash: $hash, id: $id) {
    projectAttachment {
      ...ProjectAttachment
    }
  }
}
    fragment ProjectAttachment on ProjectAttachment {
  __typename
  sourceType
  metadata
  source
  subtitle
  creator {
    id
  }
  updatedAt
  archivedAt
  createdAt
  id
  title
  url
}`);
const CustomerNeedsDocument = new TypedDocumentString(`
    query customerNeeds($after: String, $before: String, $filter: CustomerNeedFilter, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  customerNeeds(
    after: $after
    before: $before
    filter: $filter
    first: $first
    includeArchived: $includeArchived
    last: $last
    orderBy: $orderBy
  ) {
    ...CustomerNeedConnection
  }
}
    fragment CustomerNeed on CustomerNeed {
  __typename
  url
  attachment {
    id
  }
  comment {
    id
  }
  creator {
    id
  }
  customer {
    id
  }
  originalIssue {
    id
  }
  issue {
    id
  }
  updatedAt
  body
  projectAttachment {
    ...ProjectAttachment
  }
  project {
    id
  }
  archivedAt
  createdAt
  id
  priority
}
fragment ProjectAttachment on ProjectAttachment {
  __typename
  sourceType
  metadata
  source
  subtitle
  creator {
    id
  }
  updatedAt
  archivedAt
  createdAt
  id
  title
  url
}
fragment CustomerNeedConnection on CustomerNeedConnection {
  __typename
  nodes {
    ...CustomerNeed
  }
  pageInfo {
    ...PageInfo
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`);
const CustomerStatusDocument = new TypedDocumentString(`
    query customerStatus($id: String!) {
  customerStatus(id: $id) {
    ...CustomerStatus
  }
}
    fragment CustomerStatus on CustomerStatus {
  __typename
  description
  color
  displayName
  updatedAt
  name
  position
  archivedAt
  createdAt
  type
  id
}`);
const CustomerStatusesDocument = new TypedDocumentString(`
    query customerStatuses($after: String, $before: String, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  customerStatuses(
    after: $after
    before: $before
    first: $first
    includeArchived: $includeArchived
    last: $last
    orderBy: $orderBy
  ) {
    ...CustomerStatusConnection
  }
}
    fragment CustomerStatus on CustomerStatus {
  __typename
  description
  color
  displayName
  updatedAt
  name
  position
  archivedAt
  createdAt
  type
  id
}
fragment CustomerStatusConnection on CustomerStatusConnection {
  __typename
  nodes {
    ...CustomerStatus
  }
  pageInfo {
    ...PageInfo
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`);
const CustomerTierDocument = new TypedDocumentString(`
    query customerTier($id: String!) {
  customerTier(id: $id) {
    ...CustomerTier
  }
}
    fragment CustomerTier on CustomerTier {
  __typename
  description
  color
  displayName
  updatedAt
  name
  position
  archivedAt
  createdAt
  id
}`);
const CustomerTiersDocument = new TypedDocumentString(`
    query customerTiers($after: String, $before: String, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  customerTiers(
    after: $after
    before: $before
    first: $first
    includeArchived: $includeArchived
    last: $last
    orderBy: $orderBy
  ) {
    ...CustomerTierConnection
  }
}
    fragment CustomerTier on CustomerTier {
  __typename
  description
  color
  displayName
  updatedAt
  name
  position
  archivedAt
  createdAt
  id
}
fragment CustomerTierConnection on CustomerTierConnection {
  __typename
  nodes {
    ...CustomerTier
  }
  pageInfo {
    ...PageInfo
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`);
const CustomersDocument = new TypedDocumentString(`
    query customers($after: String, $before: String, $filter: CustomerFilter, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy, $sorts: [CustomerSortInput!]) {
  customers(
    after: $after
    before: $before
    filter: $filter
    first: $first
    includeArchived: $includeArchived
    last: $last
    orderBy: $orderBy
    sorts: $sorts
  ) {
    ...CustomerConnection
  }
}
    fragment Customer on Customer {
  __typename
  slackChannelId
  mainSourceId
  revenue
  approximateNeedCount
  status {
    id
  }
  logoUrl
  name
  slugId
  domains
  externalIds
  integration {
    id
  }
  updatedAt
  size
  tier {
    id
  }
  archivedAt
  createdAt
  id
  owner {
    id
  }
  url
}
fragment CustomerConnection on CustomerConnection {
  __typename
  nodes {
    ...Customer
  }
  pageInfo {
    ...PageInfo
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`);
const CycleDocument = new TypedDocumentString(`
    query cycle($id: String!) {
  cycle(id: $id) {
    ...Cycle
  }
}
    fragment Cycle on Cycle {
  __typename
  completedAt
  name
  inheritedFrom {
    id
  }
  description
  endsAt
  updatedAt
  completedScopeHistory
  completedIssueCountHistory
  inProgressScopeHistory
  number
  progress
  startsAt
  team {
    id
  }
  autoArchivedAt
  archivedAt
  createdAt
  scopeHistory
  issueCountHistory
  id
  isActive
  isFuture
  isPast
  isNext
  isPrevious
}`);
const Cycle_IssuesDocument = new TypedDocumentString(`
    query cycle_issues($id: String!, $after: String, $before: String, $filter: IssueFilter, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  cycle(id: $id) {
    issues(
      after: $after
      before: $before
      filter: $filter
      first: $first
      includeArchived: $includeArchived
      last: $last
      orderBy: $orderBy
    ) {
      ...IssueConnection
    }
  }
}
    fragment ActorBot on ActorBot {
  __typename
  avatarUrl
  name
  userDisplayName
  subType
  type
  id
}
fragment Reaction on Reaction {
  __typename
  emoji
  comment {
    id
  }
  externalUser {
    id
  }
  initiativeUpdate {
    id
  }
  issue {
    id
  }
  updatedAt
  projectUpdate {
    id
  }
  archivedAt
  createdAt
  id
  user {
    id
  }
}
fragment Issue on Issue {
  __typename
  trashed
  reactionData
  labelIds
  integrationSourceType
  url
  identifier
  priorityLabel
  previousIdentifiers
  reactions {
    ...Reaction
  }
  customerTicketCount
  branchName
  delegate {
    id
  }
  botActor {
    ...ActorBot
  }
  sourceComment {
    id
  }
  cycle {
    id
  }
  dueDate
  estimate
  syncedWith {
    ...ExternalEntityInfo
  }
  externalUserCreator {
    id
  }
  asksExternalUserRequester {
    id
  }
  asksRequester {
    id
  }
  description
  title
  number
  lastAppliedTemplate {
    id
  }
  updatedAt
  boardOrder
  prioritySortOrder
  sortOrder
  subIssueSortOrder
  parent {
    id
  }
  priority
  project {
    id
  }
  projectMilestone {
    id
  }
  recurringIssueTemplate {
    id
  }
  team {
    id
  }
  archivedAt
  createdAt
  startedTriageAt
  triagedAt
  addedToCycleAt
  addedToProjectAt
  addedToTeamAt
  autoArchivedAt
  autoClosedAt
  canceledAt
  completedAt
  startedAt
  slaStartedAt
  slaBreachesAt
  slaHighRiskAt
  slaMediumRiskAt
  snoozedUntilAt
  slaType
  id
  assignee {
    id
  }
  creator {
    id
  }
  snoozedBy {
    id
  }
  favorite {
    id
  }
  state {
    id
  }
}
fragment ExternalEntityInfo on ExternalEntityInfo {
  __typename
  metadata {
    ... on ExternalEntityInfoGithubMetadata {
      ...ExternalEntityInfoGithubMetadata
    }
    ... on ExternalEntityInfoJiraMetadata {
      ...ExternalEntityInfoJiraMetadata
    }
    ... on ExternalEntitySlackMetadata {
      ...ExternalEntitySlackMetadata
    }
  }
  id
  service
}
fragment ExternalEntityInfoGithubMetadata on ExternalEntityInfoGithubMetadata {
  __typename
  number
  owner
  repo
}
fragment ExternalEntityInfoJiraMetadata on ExternalEntityInfoJiraMetadata {
  __typename
  issueTypeId
  projectId
  issueKey
}
fragment ExternalEntitySlackMetadata on ExternalEntitySlackMetadata {
  __typename
  messageUrl
  channelId
  channelName
  isFromSlack
}
fragment IssueConnection on IssueConnection {
  __typename
  nodes {
    ...Issue
  }
  pageInfo {
    ...PageInfo
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`);
const Cycle_UncompletedIssuesUponCloseDocument = new TypedDocumentString(`
    query cycle_uncompletedIssuesUponClose($id: String!, $after: String, $before: String, $filter: IssueFilter, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  cycle(id: $id) {
    uncompletedIssuesUponClose(
      after: $after
      before: $before
      filter: $filter
      first: $first
      includeArchived: $includeArchived
      last: $last
      orderBy: $orderBy
    ) {
      ...IssueConnection
    }
  }
}
    fragment ActorBot on ActorBot {
  __typename
  avatarUrl
  name
  userDisplayName
  subType
  type
  id
}
fragment Reaction on Reaction {
  __typename
  emoji
  comment {
    id
  }
  externalUser {
    id
  }
  initiativeUpdate {
    id
  }
  issue {
    id
  }
  updatedAt
  projectUpdate {
    id
  }
  archivedAt
  createdAt
  id
  user {
    id
  }
}
fragment Issue on Issue {
  __typename
  trashed
  reactionData
  labelIds
  integrationSourceType
  url
  identifier
  priorityLabel
  previousIdentifiers
  reactions {
    ...Reaction
  }
  customerTicketCount
  branchName
  delegate {
    id
  }
  botActor {
    ...ActorBot
  }
  sourceComment {
    id
  }
  cycle {
    id
  }
  dueDate
  estimate
  syncedWith {
    ...ExternalEntityInfo
  }
  externalUserCreator {
    id
  }
  asksExternalUserRequester {
    id
  }
  asksRequester {
    id
  }
  description
  title
  number
  lastAppliedTemplate {
    id
  }
  updatedAt
  boardOrder
  prioritySortOrder
  sortOrder
  subIssueSortOrder
  parent {
    id
  }
  priority
  project {
    id
  }
  projectMilestone {
    id
  }
  recurringIssueTemplate {
    id
  }
  team {
    id
  }
  archivedAt
  createdAt
  startedTriageAt
  triagedAt
  addedToCycleAt
  addedToProjectAt
  addedToTeamAt
  autoArchivedAt
  autoClosedAt
  canceledAt
  completedAt
  startedAt
  slaStartedAt
  slaBreachesAt
  slaHighRiskAt
  slaMediumRiskAt
  snoozedUntilAt
  slaType
  id
  assignee {
    id
  }
  creator {
    id
  }
  snoozedBy {
    id
  }
  favorite {
    id
  }
  state {
    id
  }
}
fragment ExternalEntityInfo on ExternalEntityInfo {
  __typename
  metadata {
    ... on ExternalEntityInfoGithubMetadata {
      ...ExternalEntityInfoGithubMetadata
    }
    ... on ExternalEntityInfoJiraMetadata {
      ...ExternalEntityInfoJiraMetadata
    }
    ... on ExternalEntitySlackMetadata {
      ...ExternalEntitySlackMetadata
    }
  }
  id
  service
}
fragment ExternalEntityInfoGithubMetadata on ExternalEntityInfoGithubMetadata {
  __typename
  number
  owner
  repo
}
fragment ExternalEntityInfoJiraMetadata on ExternalEntityInfoJiraMetadata {
  __typename
  issueTypeId
  projectId
  issueKey
}
fragment ExternalEntitySlackMetadata on ExternalEntitySlackMetadata {
  __typename
  messageUrl
  channelId
  channelName
  isFromSlack
}
fragment IssueConnection on IssueConnection {
  __typename
  nodes {
    ...Issue
  }
  pageInfo {
    ...PageInfo
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`);
const CyclesDocument = new TypedDocumentString(`
    query cycles($after: String, $before: String, $filter: CycleFilter, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  cycles(
    after: $after
    before: $before
    filter: $filter
    first: $first
    includeArchived: $includeArchived
    last: $last
    orderBy: $orderBy
  ) {
    ...CycleConnection
  }
}
    fragment Cycle on Cycle {
  __typename
  completedAt
  name
  inheritedFrom {
    id
  }
  description
  endsAt
  updatedAt
  completedScopeHistory
  completedIssueCountHistory
  inProgressScopeHistory
  number
  progress
  startsAt
  team {
    id
  }
  autoArchivedAt
  archivedAt
  createdAt
  scopeHistory
  issueCountHistory
  id
  isActive
  isFuture
  isPast
  isNext
  isPrevious
}
fragment CycleConnection on CycleConnection {
  __typename
  nodes {
    ...Cycle
  }
  pageInfo {
    ...PageInfo
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`);
const DocumentDocument = new TypedDocumentString(`
    query document($id: String!) {
  document(id: $id) {
    ...Document
  }
}
    fragment Document on Document {
  __typename
  trashed
  documentContentId
  url
  color
  title
  slugId
  content
  icon
  initiative {
    id
  }
  issue {
    id
  }
  lastAppliedTemplate {
    id
  }
  updatedAt
  sortOrder
  project {
    id
  }
  hiddenAt
  archivedAt
  createdAt
  id
  creator {
    id
  }
  updatedBy {
    id
  }
}`);
const Document_CommentsDocument = new TypedDocumentString(`
    query document_comments($id: String!, $after: String, $before: String, $filter: CommentFilter, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  document(id: $id) {
    comments(
      after: $after
      before: $before
      filter: $filter
      first: $first
      includeArchived: $includeArchived
      last: $last
      orderBy: $orderBy
    ) {
      ...CommentConnection
    }
  }
}
    fragment ActorBot on ActorBot {
  __typename
  avatarUrl
  name
  userDisplayName
  subType
  type
  id
}
fragment Comment on Comment {
  __typename
  agentSession {
    id
  }
  url
  reactionData
  reactions {
    ...Reaction
  }
  resolvingCommentId
  documentContentId
  initiativeUpdateId
  issueId
  parentId
  projectUpdateId
  botActor {
    ...ActorBot
  }
  body
  resolvingComment {
    id
  }
  documentContent {
    ...DocumentContent
  }
  syncedWith {
    ...ExternalEntityInfo
  }
  externalThread {
    ...SyncedExternalThread
  }
  externalUser {
    id
  }
  initiativeUpdate {
    id
  }
  issue {
    id
  }
  updatedAt
  parent {
    id
  }
  projectUpdate {
    id
  }
  quotedText
  archivedAt
  createdAt
  resolvedAt
  editedAt
  id
  resolvingUser {
    id
  }
  user {
    id
  }
}
fragment SyncedExternalThread on SyncedExternalThread {
  __typename
  name
  displayName
  url
  subType
  type
  isPersonalIntegrationRequired
  isPersonalIntegrationConnected
  isConnected
  id
}
fragment DocumentContent on DocumentContent {
  __typename
  aiPromptRules {
    ...AiPromptRules
  }
  content
  contentState
  document {
    id
  }
  initiative {
    id
  }
  issue {
    id
  }
  updatedAt
  projectMilestone {
    id
  }
  project {
    id
  }
  restoredAt
  archivedAt
  createdAt
  id
}
fragment Reaction on Reaction {
  __typename
  emoji
  comment {
    id
  }
  externalUser {
    id
  }
  initiativeUpdate {
    id
  }
  issue {
    id
  }
  updatedAt
  projectUpdate {
    id
  }
  archivedAt
  createdAt
  id
  user {
    id
  }
}
fragment AiPromptRules on AiPromptRules {
  __typename
  updatedAt
  archivedAt
  createdAt
  id
  updatedBy {
    id
  }
}
fragment ExternalEntityInfo on ExternalEntityInfo {
  __typename
  metadata {
    ... on ExternalEntityInfoGithubMetadata {
      ...ExternalEntityInfoGithubMetadata
    }
    ... on ExternalEntityInfoJiraMetadata {
      ...ExternalEntityInfoJiraMetadata
    }
    ... on ExternalEntitySlackMetadata {
      ...ExternalEntitySlackMetadata
    }
  }
  id
  service
}
fragment ExternalEntityInfoGithubMetadata on ExternalEntityInfoGithubMetadata {
  __typename
  number
  owner
  repo
}
fragment ExternalEntityInfoJiraMetadata on ExternalEntityInfoJiraMetadata {
  __typename
  issueTypeId
  projectId
  issueKey
}
fragment ExternalEntitySlackMetadata on ExternalEntitySlackMetadata {
  __typename
  messageUrl
  channelId
  channelName
  isFromSlack
}
fragment CommentConnection on CommentConnection {
  __typename
  nodes {
    ...Comment
  }
  pageInfo {
    ...PageInfo
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`);
const DocumentContentHistoryDocument = new TypedDocumentString(`
    query documentContentHistory($id: String!) {
  documentContentHistory(id: $id) {
    ...DocumentContentHistoryPayload
  }
}
    fragment DocumentContentHistoryPayload on DocumentContentHistoryPayload {
  __typename
  history {
    ...DocumentContentHistoryType
  }
  success
}
fragment DocumentContentHistoryType on DocumentContentHistoryType {
  __typename
  actorIds
  id
  createdAt
  contentDataSnapshotAt
}`);
const DocumentsDocument = new TypedDocumentString(`
    query documents($after: String, $before: String, $filter: DocumentFilter, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  documents(
    after: $after
    before: $before
    filter: $filter
    first: $first
    includeArchived: $includeArchived
    last: $last
    orderBy: $orderBy
  ) {
    ...DocumentConnection
  }
}
    fragment Document on Document {
  __typename
  trashed
  documentContentId
  url
  color
  title
  slugId
  content
  icon
  initiative {
    id
  }
  issue {
    id
  }
  lastAppliedTemplate {
    id
  }
  updatedAt
  sortOrder
  project {
    id
  }
  hiddenAt
  archivedAt
  createdAt
  id
  creator {
    id
  }
  updatedBy {
    id
  }
}
fragment DocumentConnection on DocumentConnection {
  __typename
  nodes {
    ...Document
  }
  pageInfo {
    ...PageInfo
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`);
const EmailIntakeAddressDocument = new TypedDocumentString(`
    query emailIntakeAddress($id: String!) {
  emailIntakeAddress(id: $id) {
    ...EmailIntakeAddress
  }
}
    fragment SesDomainIdentityDnsRecord on SesDomainIdentityDnsRecord {
  __typename
  content
  name
  type
  isVerified
}
fragment EmailIntakeAddress on EmailIntakeAddress {
  __typename
  sesDomainIdentity {
    ...SesDomainIdentity
  }
  issueCanceledAutoReply
  issueCompletedAutoReply
  issueCreatedAutoReply
  forwardingEmailAddress
  updatedAt
  senderName
  team {
    id
  }
  template {
    id
  }
  archivedAt
  createdAt
  type
  id
  creator {
    id
  }
  address
  repliesEnabled
  customerRequestsEnabled
  issueCanceledAutoReplyEnabled
  issueCompletedAutoReplyEnabled
  issueCreatedAutoReplyEnabled
  useUserNamesInReplies
  enabled
}
fragment SesDomainIdentity on SesDomainIdentity {
  __typename
  region
  dnsRecords {
    ...SesDomainIdentityDnsRecord
  }
  domain
  updatedAt
  archivedAt
  createdAt
  id
  creator {
    id
  }
  canSendFromCustomDomain
}`);
const EmailIntakeAddress_SesDomainIdentityDocument = new TypedDocumentString(`
    query emailIntakeAddress_sesDomainIdentity($id: String!) {
  emailIntakeAddress(id: $id) {
    sesDomainIdentity {
      ...SesDomainIdentity
    }
  }
}
    fragment SesDomainIdentityDnsRecord on SesDomainIdentityDnsRecord {
  __typename
  content
  name
  type
  isVerified
}
fragment SesDomainIdentity on SesDomainIdentity {
  __typename
  region
  dnsRecords {
    ...SesDomainIdentityDnsRecord
  }
  domain
  updatedAt
  archivedAt
  createdAt
  id
  creator {
    id
  }
  canSendFromCustomDomain
}`);
const EmojiDocument = new TypedDocumentString(`
    query emoji($id: String!) {
  emoji(id: $id) {
    ...Emoji
  }
}
    fragment Emoji on Emoji {
  __typename
  url
  name
  updatedAt
  source
  archivedAt
  createdAt
  id
  creator {
    id
  }
}`);
const EmojisDocument = new TypedDocumentString(`
    query emojis($after: String, $before: String, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  emojis(
    after: $after
    before: $before
    first: $first
    includeArchived: $includeArchived
    last: $last
    orderBy: $orderBy
  ) {
    ...EmojiConnection
  }
}
    fragment Emoji on Emoji {
  __typename
  url
  name
  updatedAt
  source
  archivedAt
  createdAt
  id
  creator {
    id
  }
}
fragment EmojiConnection on EmojiConnection {
  __typename
  nodes {
    ...Emoji
  }
  pageInfo {
    ...PageInfo
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`);
const EntityExternalLinkDocument = new TypedDocumentString(`
    query entityExternalLink($id: String!) {
  entityExternalLink(id: $id) {
    ...EntityExternalLink
  }
}
    fragment EntityExternalLink on EntityExternalLink {
  __typename
  initiative {
    id
  }
  updatedAt
  url
  label
  sortOrder
  archivedAt
  createdAt
  id
  creator {
    id
  }
}`);
const ExternalUserDocument = new TypedDocumentString(`
    query externalUser($id: String!) {
  externalUser(id: $id) {
    ...ExternalUser
  }
}
    fragment ExternalUser on ExternalUser {
  __typename
  avatarUrl
  displayName
  email
  name
  updatedAt
  lastSeen
  archivedAt
  createdAt
  id
}`);
const ExternalUsersDocument = new TypedDocumentString(`
    query externalUsers($after: String, $before: String, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  externalUsers(
    after: $after
    before: $before
    first: $first
    includeArchived: $includeArchived
    last: $last
    orderBy: $orderBy
  ) {
    ...ExternalUserConnection
  }
}
    fragment ExternalUser on ExternalUser {
  __typename
  avatarUrl
  displayName
  email
  name
  updatedAt
  lastSeen
  archivedAt
  createdAt
  id
}
fragment ExternalUserConnection on ExternalUserConnection {
  __typename
  nodes {
    ...ExternalUser
  }
  pageInfo {
    ...PageInfo
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`);
const FavoriteDocument = new TypedDocumentString(`
    query favorite($id: String!) {
  favorite(id: $id) {
    ...Favorite
  }
}
    fragment Favorite on Favorite {
  __typename
  customView {
    id
  }
  customer {
    id
  }
  cycle {
    id
  }
  document {
    id
  }
  initiative {
    id
  }
  issue {
    id
  }
  label {
    id
  }
  projectLabel {
    id
  }
  project {
    id
  }
  user {
    id
  }
  updatedAt
  folderName
  sortOrder
  owner {
    id
  }
  parent {
    id
  }
  initiativeTab
  projectTab
  predefinedViewTeam {
    id
  }
  archivedAt
  createdAt
  predefinedViewType
  type
  id
  url
  projectTeam {
    id
  }
}`);
const Favorite_ChildrenDocument = new TypedDocumentString(`
    query favorite_children($id: String!, $after: String, $before: String, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  favorite(id: $id) {
    children(
      after: $after
      before: $before
      first: $first
      includeArchived: $includeArchived
      last: $last
      orderBy: $orderBy
    ) {
      ...FavoriteConnection
    }
  }
}
    fragment Favorite on Favorite {
  __typename
  customView {
    id
  }
  customer {
    id
  }
  cycle {
    id
  }
  document {
    id
  }
  initiative {
    id
  }
  issue {
    id
  }
  label {
    id
  }
  projectLabel {
    id
  }
  project {
    id
  }
  user {
    id
  }
  updatedAt
  folderName
  sortOrder
  owner {
    id
  }
  parent {
    id
  }
  initiativeTab
  projectTab
  predefinedViewTeam {
    id
  }
  archivedAt
  createdAt
  predefinedViewType
  type
  id
  url
  projectTeam {
    id
  }
}
fragment FavoriteConnection on FavoriteConnection {
  __typename
  nodes {
    ...Favorite
  }
  pageInfo {
    ...PageInfo
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`);
const FavoritesDocument = new TypedDocumentString(`
    query favorites($after: String, $before: String, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  favorites(
    after: $after
    before: $before
    first: $first
    includeArchived: $includeArchived
    last: $last
    orderBy: $orderBy
  ) {
    ...FavoriteConnection
  }
}
    fragment Favorite on Favorite {
  __typename
  customView {
    id
  }
  customer {
    id
  }
  cycle {
    id
  }
  document {
    id
  }
  initiative {
    id
  }
  issue {
    id
  }
  label {
    id
  }
  projectLabel {
    id
  }
  project {
    id
  }
  user {
    id
  }
  updatedAt
  folderName
  sortOrder
  owner {
    id
  }
  parent {
    id
  }
  initiativeTab
  projectTab
  predefinedViewTeam {
    id
  }
  archivedAt
  createdAt
  predefinedViewType
  type
  id
  url
  projectTeam {
    id
  }
}
fragment FavoriteConnection on FavoriteConnection {
  __typename
  nodes {
    ...Favorite
  }
  pageInfo {
    ...PageInfo
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`);
const InitiativeDocument = new TypedDocumentString(`
    query initiative($id: String!) {
  initiative(id: $id) {
    ...Initiative
  }
}
    fragment DocumentContent on DocumentContent {
  __typename
  aiPromptRules {
    ...AiPromptRules
  }
  content
  contentState
  document {
    id
  }
  initiative {
    id
  }
  issue {
    id
  }
  updatedAt
  projectMilestone {
    id
  }
  project {
    id
  }
  restoredAt
  archivedAt
  createdAt
  id
}
fragment AiPromptRules on AiPromptRules {
  __typename
  updatedAt
  archivedAt
  createdAt
  id
  updatedBy {
    id
  }
}
fragment Initiative on Initiative {
  __typename
  trashed
  url
  parentInitiative {
    id
  }
  integrationsSettings {
    id
  }
  documentContent {
    ...DocumentContent
  }
  updateRemindersDay
  description
  targetDate
  updateReminderFrequency
  health
  updateRemindersHour
  icon
  color
  content
  slugId
  lastUpdate {
    id
  }
  updatedAt
  updateReminderFrequencyInWeeks
  name
  targetDateResolution
  frequencyResolution
  sortOrder
  status
  archivedAt
  createdAt
  healthUpdatedAt
  startedAt
  completedAt
  id
  creator {
    id
  }
  owner {
    id
  }
}`);
const Initiative_DocumentContentDocument = new TypedDocumentString(`
    query initiative_documentContent($id: String!) {
  initiative(id: $id) {
    documentContent {
      ...DocumentContent
    }
  }
}
    fragment DocumentContent on DocumentContent {
  __typename
  aiPromptRules {
    ...AiPromptRules
  }
  content
  contentState
  document {
    id
  }
  initiative {
    id
  }
  issue {
    id
  }
  updatedAt
  projectMilestone {
    id
  }
  project {
    id
  }
  restoredAt
  archivedAt
  createdAt
  id
}
fragment AiPromptRules on AiPromptRules {
  __typename
  updatedAt
  archivedAt
  createdAt
  id
  updatedBy {
    id
  }
}`);
const Initiative_DocumentContent_AiPromptRulesDocument = new TypedDocumentString(`
    query initiative_documentContent_aiPromptRules($id: String!) {
  initiative(id: $id) {
    documentContent {
      aiPromptRules {
        ...AiPromptRules
      }
    }
  }
}
    fragment AiPromptRules on AiPromptRules {
  __typename
  updatedAt
  archivedAt
  createdAt
  id
  updatedBy {
    id
  }
}`);
const Initiative_DocumentsDocument = new TypedDocumentString(`
    query initiative_documents($id: String!, $after: String, $before: String, $filter: DocumentFilter, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  initiative(id: $id) {
    documents(
      after: $after
      before: $before
      filter: $filter
      first: $first
      includeArchived: $includeArchived
      last: $last
      orderBy: $orderBy
    ) {
      ...DocumentConnection
    }
  }
}
    fragment Document on Document {
  __typename
  trashed
  documentContentId
  url
  color
  title
  slugId
  content
  icon
  initiative {
    id
  }
  issue {
    id
  }
  lastAppliedTemplate {
    id
  }
  updatedAt
  sortOrder
  project {
    id
  }
  hiddenAt
  archivedAt
  createdAt
  id
  creator {
    id
  }
  updatedBy {
    id
  }
}
fragment DocumentConnection on DocumentConnection {
  __typename
  nodes {
    ...Document
  }
  pageInfo {
    ...PageInfo
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`);
const Initiative_HistoryDocument = new TypedDocumentString(`
    query initiative_history($id: String!, $after: String, $before: String, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  initiative(id: $id) {
    history(
      after: $after
      before: $before
      first: $first
      includeArchived: $includeArchived
      last: $last
      orderBy: $orderBy
    ) {
      ...InitiativeHistoryConnection
    }
  }
}
    fragment InitiativeHistory on InitiativeHistory {
  __typename
  entries
  initiative {
    id
  }
  updatedAt
  archivedAt
  createdAt
  id
}
fragment InitiativeHistoryConnection on InitiativeHistoryConnection {
  __typename
  nodes {
    ...InitiativeHistory
  }
  pageInfo {
    ...PageInfo
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`);
const Initiative_InitiativeUpdatesDocument = new TypedDocumentString(`
    query initiative_initiativeUpdates($id: String!, $after: String, $before: String, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  initiative(id: $id) {
    initiativeUpdates(
      after: $after
      before: $before
      first: $first
      includeArchived: $includeArchived
      last: $last
      orderBy: $orderBy
    ) {
      ...InitiativeUpdateConnection
    }
  }
}
    fragment Reaction on Reaction {
  __typename
  emoji
  comment {
    id
  }
  externalUser {
    id
  }
  initiativeUpdate {
    id
  }
  issue {
    id
  }
  updatedAt
  projectUpdate {
    id
  }
  archivedAt
  createdAt
  id
  user {
    id
  }
}
fragment InitiativeUpdate on InitiativeUpdate {
  __typename
  reactionData
  commentCount
  reactions {
    ...Reaction
  }
  url
  diffMarkdown
  diff
  health
  initiative {
    id
  }
  updatedAt
  archivedAt
  createdAt
  editedAt
  id
  body
  slugId
  user {
    id
  }
  isDiffHidden
  isStale
}
fragment InitiativeUpdateConnection on InitiativeUpdateConnection {
  __typename
  nodes {
    ...InitiativeUpdate
  }
  pageInfo {
    ...PageInfo
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`);
const Initiative_LinksDocument = new TypedDocumentString(`
    query initiative_links($id: String!, $after: String, $before: String, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  initiative(id: $id) {
    links(
      after: $after
      before: $before
      first: $first
      includeArchived: $includeArchived
      last: $last
      orderBy: $orderBy
    ) {
      ...EntityExternalLinkConnection
    }
  }
}
    fragment EntityExternalLink on EntityExternalLink {
  __typename
  initiative {
    id
  }
  updatedAt
  url
  label
  sortOrder
  archivedAt
  createdAt
  id
  creator {
    id
  }
}
fragment EntityExternalLinkConnection on EntityExternalLinkConnection {
  __typename
  nodes {
    ...EntityExternalLink
  }
  pageInfo {
    ...PageInfo
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`);
const Initiative_ProjectsDocument = new TypedDocumentString(`
    query initiative_projects($id: String!, $after: String, $before: String, $filter: ProjectFilter, $first: Int, $includeArchived: Boolean, $includeSubInitiatives: Boolean, $last: Int, $orderBy: PaginationOrderBy, $sort: [ProjectSortInput!]) {
  initiative(id: $id) {
    projects(
      after: $after
      before: $before
      filter: $filter
      first: $first
      includeArchived: $includeArchived
      includeSubInitiatives: $includeSubInitiatives
      last: $last
      orderBy: $orderBy
      sort: $sort
    ) {
      ...ProjectConnection
    }
  }
}
    fragment DocumentContent on DocumentContent {
  __typename
  aiPromptRules {
    ...AiPromptRules
  }
  content
  contentState
  document {
    id
  }
  initiative {
    id
  }
  issue {
    id
  }
  updatedAt
  projectMilestone {
    id
  }
  project {
    id
  }
  restoredAt
  archivedAt
  createdAt
  id
}
fragment Project on Project {
  __typename
  trashed
  labelIds
  url
  integrationsSettings {
    id
  }
  documentContent {
    ...DocumentContent
  }
  updateRemindersDay
  targetDate
  startDate
  updateReminderFrequency
  health
  updateRemindersHour
  icon
  lastUpdate {
    id
  }
  lastAppliedTemplate {
    id
  }
  updatedAt
  updateReminderFrequencyInWeeks
  completedScopeHistory
  completedIssueCountHistory
  inProgressScopeHistory
  progress
  scope
  priorityLabel
  priority
  lead {
    id
  }
  convertedFromIssue {
    id
  }
  color
  content
  description
  name
  slugId
  targetDateResolution
  startDateResolution
  frequencyResolution
  prioritySortOrder
  sortOrder
  status {
    id
  }
  archivedAt
  createdAt
  healthUpdatedAt
  autoArchivedAt
  canceledAt
  completedAt
  startedAt
  projectUpdateRemindersPausedUntilAt
  scopeHistory
  issueCountHistory
  id
  creator {
    id
  }
  favorite {
    id
  }
  slackIssueComments
  slackNewIssue
  slackIssueStatuses
  state
}
fragment AiPromptRules on AiPromptRules {
  __typename
  updatedAt
  archivedAt
  createdAt
  id
  updatedBy {
    id
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}
fragment ProjectConnection on ProjectConnection {
  __typename
  nodes {
    ...Project
  }
  pageInfo {
    ...PageInfo
  }
}`);
const Initiative_SubInitiativesDocument = new TypedDocumentString(`
    query initiative_subInitiatives($id: String!, $after: String, $before: String, $filter: InitiativeFilter, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy, $sort: [InitiativeSortInput!]) {
  initiative(id: $id) {
    subInitiatives(
      after: $after
      before: $before
      filter: $filter
      first: $first
      includeArchived: $includeArchived
      last: $last
      orderBy: $orderBy
      sort: $sort
    ) {
      ...InitiativeConnection
    }
  }
}
    fragment DocumentContent on DocumentContent {
  __typename
  aiPromptRules {
    ...AiPromptRules
  }
  content
  contentState
  document {
    id
  }
  initiative {
    id
  }
  issue {
    id
  }
  updatedAt
  projectMilestone {
    id
  }
  project {
    id
  }
  restoredAt
  archivedAt
  createdAt
  id
}
fragment AiPromptRules on AiPromptRules {
  __typename
  updatedAt
  archivedAt
  createdAt
  id
  updatedBy {
    id
  }
}
fragment Initiative on Initiative {
  __typename
  trashed
  url
  parentInitiative {
    id
  }
  integrationsSettings {
    id
  }
  documentContent {
    ...DocumentContent
  }
  updateRemindersDay
  description
  targetDate
  updateReminderFrequency
  health
  updateRemindersHour
  icon
  color
  content
  slugId
  lastUpdate {
    id
  }
  updatedAt
  updateReminderFrequencyInWeeks
  name
  targetDateResolution
  frequencyResolution
  sortOrder
  status
  archivedAt
  createdAt
  healthUpdatedAt
  startedAt
  completedAt
  id
  creator {
    id
  }
  owner {
    id
  }
}
fragment InitiativeConnection on InitiativeConnection {
  __typename
  nodes {
    ...Initiative
  }
  pageInfo {
    ...PageInfo
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`);
const InitiativeRelationDocument = new TypedDocumentString(`
    query initiativeRelation($id: String!) {
  initiativeRelation(id: $id) {
    ...ProjectRelation
  }
}
    fragment ProjectRelation on ProjectRelation {
  __typename
  updatedAt
  user {
    id
  }
  projectMilestone {
    id
  }
  relatedProjectMilestone {
    id
  }
  project {
    id
  }
  relatedProject {
    id
  }
  type
  archivedAt
  createdAt
  anchorType
  relatedAnchorType
  id
}`);
const InitiativeRelationsDocument = new TypedDocumentString(`
    query initiativeRelations($after: String, $before: String, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  initiativeRelations(
    after: $after
    before: $before
    first: $first
    includeArchived: $includeArchived
    last: $last
    orderBy: $orderBy
  ) {
    ...InitiativeRelationConnection
  }
}
    fragment InitiativeRelation on InitiativeRelation {
  __typename
  relatedInitiative {
    id
  }
  updatedAt
  user {
    id
  }
  initiative {
    id
  }
  sortOrder
  archivedAt
  createdAt
  id
}
fragment InitiativeRelationConnection on InitiativeRelationConnection {
  __typename
  nodes {
    ...InitiativeRelation
  }
  pageInfo {
    ...PageInfo
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`);
const InitiativeToProjectDocument = new TypedDocumentString(`
    query initiativeToProject($id: String!) {
  initiativeToProject(id: $id) {
    ...InitiativeToProject
  }
}
    fragment InitiativeToProject on InitiativeToProject {
  __typename
  initiative {
    id
  }
  updatedAt
  project {
    id
  }
  sortOrder
  archivedAt
  createdAt
  id
}`);
const InitiativeToProjectsDocument = new TypedDocumentString(`
    query initiativeToProjects($after: String, $before: String, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  initiativeToProjects(
    after: $after
    before: $before
    first: $first
    includeArchived: $includeArchived
    last: $last
    orderBy: $orderBy
  ) {
    ...InitiativeToProjectConnection
  }
}
    fragment InitiativeToProject on InitiativeToProject {
  __typename
  initiative {
    id
  }
  updatedAt
  project {
    id
  }
  sortOrder
  archivedAt
  createdAt
  id
}
fragment InitiativeToProjectConnection on InitiativeToProjectConnection {
  __typename
  nodes {
    ...InitiativeToProject
  }
  pageInfo {
    ...PageInfo
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`);
const InitiativeUpdateDocument = new TypedDocumentString(`
    query initiativeUpdate($id: String!) {
  initiativeUpdate(id: $id) {
    ...InitiativeUpdate
  }
}
    fragment Reaction on Reaction {
  __typename
  emoji
  comment {
    id
  }
  externalUser {
    id
  }
  initiativeUpdate {
    id
  }
  issue {
    id
  }
  updatedAt
  projectUpdate {
    id
  }
  archivedAt
  createdAt
  id
  user {
    id
  }
}
fragment InitiativeUpdate on InitiativeUpdate {
  __typename
  reactionData
  commentCount
  reactions {
    ...Reaction
  }
  url
  diffMarkdown
  diff
  health
  initiative {
    id
  }
  updatedAt
  archivedAt
  createdAt
  editedAt
  id
  body
  slugId
  user {
    id
  }
  isDiffHidden
  isStale
}`);
const InitiativeUpdate_CommentsDocument = new TypedDocumentString(`
    query initiativeUpdate_comments($id: String!, $after: String, $before: String, $filter: CommentFilter, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  initiativeUpdate(id: $id) {
    comments(
      after: $after
      before: $before
      filter: $filter
      first: $first
      includeArchived: $includeArchived
      last: $last
      orderBy: $orderBy
    ) {
      ...CommentConnection
    }
  }
}
    fragment ActorBot on ActorBot {
  __typename
  avatarUrl
  name
  userDisplayName
  subType
  type
  id
}
fragment Comment on Comment {
  __typename
  agentSession {
    id
  }
  url
  reactionData
  reactions {
    ...Reaction
  }
  resolvingCommentId
  documentContentId
  initiativeUpdateId
  issueId
  parentId
  projectUpdateId
  botActor {
    ...ActorBot
  }
  body
  resolvingComment {
    id
  }
  documentContent {
    ...DocumentContent
  }
  syncedWith {
    ...ExternalEntityInfo
  }
  externalThread {
    ...SyncedExternalThread
  }
  externalUser {
    id
  }
  initiativeUpdate {
    id
  }
  issue {
    id
  }
  updatedAt
  parent {
    id
  }
  projectUpdate {
    id
  }
  quotedText
  archivedAt
  createdAt
  resolvedAt
  editedAt
  id
  resolvingUser {
    id
  }
  user {
    id
  }
}
fragment SyncedExternalThread on SyncedExternalThread {
  __typename
  name
  displayName
  url
  subType
  type
  isPersonalIntegrationRequired
  isPersonalIntegrationConnected
  isConnected
  id
}
fragment DocumentContent on DocumentContent {
  __typename
  aiPromptRules {
    ...AiPromptRules
  }
  content
  contentState
  document {
    id
  }
  initiative {
    id
  }
  issue {
    id
  }
  updatedAt
  projectMilestone {
    id
  }
  project {
    id
  }
  restoredAt
  archivedAt
  createdAt
  id
}
fragment Reaction on Reaction {
  __typename
  emoji
  comment {
    id
  }
  externalUser {
    id
  }
  initiativeUpdate {
    id
  }
  issue {
    id
  }
  updatedAt
  projectUpdate {
    id
  }
  archivedAt
  createdAt
  id
  user {
    id
  }
}
fragment AiPromptRules on AiPromptRules {
  __typename
  updatedAt
  archivedAt
  createdAt
  id
  updatedBy {
    id
  }
}
fragment ExternalEntityInfo on ExternalEntityInfo {
  __typename
  metadata {
    ... on ExternalEntityInfoGithubMetadata {
      ...ExternalEntityInfoGithubMetadata
    }
    ... on ExternalEntityInfoJiraMetadata {
      ...ExternalEntityInfoJiraMetadata
    }
    ... on ExternalEntitySlackMetadata {
      ...ExternalEntitySlackMetadata
    }
  }
  id
  service
}
fragment ExternalEntityInfoGithubMetadata on ExternalEntityInfoGithubMetadata {
  __typename
  number
  owner
  repo
}
fragment ExternalEntityInfoJiraMetadata on ExternalEntityInfoJiraMetadata {
  __typename
  issueTypeId
  projectId
  issueKey
}
fragment ExternalEntitySlackMetadata on ExternalEntitySlackMetadata {
  __typename
  messageUrl
  channelId
  channelName
  isFromSlack
}
fragment CommentConnection on CommentConnection {
  __typename
  nodes {
    ...Comment
  }
  pageInfo {
    ...PageInfo
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`);
const InitiativeUpdatesDocument = new TypedDocumentString(`
    query initiativeUpdates($after: String, $before: String, $filter: InitiativeUpdateFilter, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  initiativeUpdates(
    after: $after
    before: $before
    filter: $filter
    first: $first
    includeArchived: $includeArchived
    last: $last
    orderBy: $orderBy
  ) {
    ...InitiativeUpdateConnection
  }
}
    fragment Reaction on Reaction {
  __typename
  emoji
  comment {
    id
  }
  externalUser {
    id
  }
  initiativeUpdate {
    id
  }
  issue {
    id
  }
  updatedAt
  projectUpdate {
    id
  }
  archivedAt
  createdAt
  id
  user {
    id
  }
}
fragment InitiativeUpdate on InitiativeUpdate {
  __typename
  reactionData
  commentCount
  reactions {
    ...Reaction
  }
  url
  diffMarkdown
  diff
  health
  initiative {
    id
  }
  updatedAt
  archivedAt
  createdAt
  editedAt
  id
  body
  slugId
  user {
    id
  }
  isDiffHidden
  isStale
}
fragment InitiativeUpdateConnection on InitiativeUpdateConnection {
  __typename
  nodes {
    ...InitiativeUpdate
  }
  pageInfo {
    ...PageInfo
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`);
const InitiativesDocument = new TypedDocumentString(`
    query initiatives($after: String, $before: String, $filter: InitiativeFilter, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy, $sort: [InitiativeSortInput!]) {
  initiatives(
    after: $after
    before: $before
    filter: $filter
    first: $first
    includeArchived: $includeArchived
    last: $last
    orderBy: $orderBy
    sort: $sort
  ) {
    ...InitiativeConnection
  }
}
    fragment DocumentContent on DocumentContent {
  __typename
  aiPromptRules {
    ...AiPromptRules
  }
  content
  contentState
  document {
    id
  }
  initiative {
    id
  }
  issue {
    id
  }
  updatedAt
  projectMilestone {
    id
  }
  project {
    id
  }
  restoredAt
  archivedAt
  createdAt
  id
}
fragment AiPromptRules on AiPromptRules {
  __typename
  updatedAt
  archivedAt
  createdAt
  id
  updatedBy {
    id
  }
}
fragment Initiative on Initiative {
  __typename
  trashed
  url
  parentInitiative {
    id
  }
  integrationsSettings {
    id
  }
  documentContent {
    ...DocumentContent
  }
  updateRemindersDay
  description
  targetDate
  updateReminderFrequency
  health
  updateRemindersHour
  icon
  color
  content
  slugId
  lastUpdate {
    id
  }
  updatedAt
  updateReminderFrequencyInWeeks
  name
  targetDateResolution
  frequencyResolution
  sortOrder
  status
  archivedAt
  createdAt
  healthUpdatedAt
  startedAt
  completedAt
  id
  creator {
    id
  }
  owner {
    id
  }
}
fragment InitiativeConnection on InitiativeConnection {
  __typename
  nodes {
    ...Initiative
  }
  pageInfo {
    ...PageInfo
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`);
const IntegrationDocument = new TypedDocumentString(`
    query integration($id: String!) {
  integration(id: $id) {
    ...Integration
  }
}
    fragment Integration on Integration {
  __typename
  service
  updatedAt
  team {
    id
  }
  archivedAt
  createdAt
  id
  creator {
    id
  }
}`);
const IntegrationHasScopesDocument = new TypedDocumentString(`
    query integrationHasScopes($integrationId: String!, $scopes: [String!]!) {
  integrationHasScopes(integrationId: $integrationId, scopes: $scopes) {
    ...IntegrationHasScopesPayload
  }
}
    fragment IntegrationHasScopesPayload on IntegrationHasScopesPayload {
  __typename
  missingScopes
  hasAllScopes
}`);
const IntegrationTemplateDocument = new TypedDocumentString(`
    query integrationTemplate($id: String!) {
  integrationTemplate(id: $id) {
    ...IntegrationTemplate
  }
}
    fragment IntegrationTemplate on IntegrationTemplate {
  __typename
  foreignEntityId
  integration {
    id
  }
  updatedAt
  template {
    id
  }
  archivedAt
  createdAt
  id
}`);
const IntegrationTemplatesDocument = new TypedDocumentString(`
    query integrationTemplates($after: String, $before: String, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  integrationTemplates(
    after: $after
    before: $before
    first: $first
    includeArchived: $includeArchived
    last: $last
    orderBy: $orderBy
  ) {
    ...IntegrationTemplateConnection
  }
}
    fragment IntegrationTemplate on IntegrationTemplate {
  __typename
  foreignEntityId
  integration {
    id
  }
  updatedAt
  template {
    id
  }
  archivedAt
  createdAt
  id
}
fragment IntegrationTemplateConnection on IntegrationTemplateConnection {
  __typename
  nodes {
    ...IntegrationTemplate
  }
  pageInfo {
    ...PageInfo
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`);
const IntegrationsDocument = new TypedDocumentString(`
    query integrations($after: String, $before: String, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  integrations(
    after: $after
    before: $before
    first: $first
    includeArchived: $includeArchived
    last: $last
    orderBy: $orderBy
  ) {
    ...IntegrationConnection
  }
}
    fragment Integration on Integration {
  __typename
  service
  updatedAt
  team {
    id
  }
  archivedAt
  createdAt
  id
  creator {
    id
  }
}
fragment IntegrationConnection on IntegrationConnection {
  __typename
  nodes {
    ...Integration
  }
  pageInfo {
    ...PageInfo
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`);
const IntegrationsSettingsDocument = new TypedDocumentString(`
    query integrationsSettings($id: String!) {
  integrationsSettings(id: $id) {
    ...IntegrationsSettings
  }
}
    fragment IntegrationsSettings on IntegrationsSettings {
  __typename
  initiative {
    id
  }
  project {
    id
  }
  team {
    id
  }
  updatedAt
  archivedAt
  createdAt
  contextViewType
  id
  slackIssueNewComment
  slackInitiativeUpdateCreated
  slackIssueAddedToTriage
  slackIssueCreated
  slackProjectUpdateCreated
  slackIssueSlaHighRisk
  slackIssueSlaBreached
  slackIssueAddedToView
  slackIssueStatusChangedDone
  slackIssueStatusChangedAll
  slackProjectUpdateCreatedToTeam
  slackProjectUpdateCreatedToWorkspace
}`);
const IssueDocument = new TypedDocumentString(`
    query issue($id: String!) {
  issue(id: $id) {
    ...Issue
  }
}
    fragment ActorBot on ActorBot {
  __typename
  avatarUrl
  name
  userDisplayName
  subType
  type
  id
}
fragment Reaction on Reaction {
  __typename
  emoji
  comment {
    id
  }
  externalUser {
    id
  }
  initiativeUpdate {
    id
  }
  issue {
    id
  }
  updatedAt
  projectUpdate {
    id
  }
  archivedAt
  createdAt
  id
  user {
    id
  }
}
fragment Issue on Issue {
  __typename
  trashed
  reactionData
  labelIds
  integrationSourceType
  url
  identifier
  priorityLabel
  previousIdentifiers
  reactions {
    ...Reaction
  }
  customerTicketCount
  branchName
  delegate {
    id
  }
  botActor {
    ...ActorBot
  }
  sourceComment {
    id
  }
  cycle {
    id
  }
  dueDate
  estimate
  syncedWith {
    ...ExternalEntityInfo
  }
  externalUserCreator {
    id
  }
  asksExternalUserRequester {
    id
  }
  asksRequester {
    id
  }
  description
  title
  number
  lastAppliedTemplate {
    id
  }
  updatedAt
  boardOrder
  prioritySortOrder
  sortOrder
  subIssueSortOrder
  parent {
    id
  }
  priority
  project {
    id
  }
  projectMilestone {
    id
  }
  recurringIssueTemplate {
    id
  }
  team {
    id
  }
  archivedAt
  createdAt
  startedTriageAt
  triagedAt
  addedToCycleAt
  addedToProjectAt
  addedToTeamAt
  autoArchivedAt
  autoClosedAt
  canceledAt
  completedAt
  startedAt
  slaStartedAt
  slaBreachesAt
  slaHighRiskAt
  slaMediumRiskAt
  snoozedUntilAt
  slaType
  id
  assignee {
    id
  }
  creator {
    id
  }
  snoozedBy {
    id
  }
  favorite {
    id
  }
  state {
    id
  }
}
fragment ExternalEntityInfo on ExternalEntityInfo {
  __typename
  metadata {
    ... on ExternalEntityInfoGithubMetadata {
      ...ExternalEntityInfoGithubMetadata
    }
    ... on ExternalEntityInfoJiraMetadata {
      ...ExternalEntityInfoJiraMetadata
    }
    ... on ExternalEntitySlackMetadata {
      ...ExternalEntitySlackMetadata
    }
  }
  id
  service
}
fragment ExternalEntityInfoGithubMetadata on ExternalEntityInfoGithubMetadata {
  __typename
  number
  owner
  repo
}
fragment ExternalEntityInfoJiraMetadata on ExternalEntityInfoJiraMetadata {
  __typename
  issueTypeId
  projectId
  issueKey
}
fragment ExternalEntitySlackMetadata on ExternalEntitySlackMetadata {
  __typename
  messageUrl
  channelId
  channelName
  isFromSlack
}`);
const Issue_AttachmentsDocument = new TypedDocumentString(`
    query issue_attachments($id: String!, $after: String, $before: String, $filter: AttachmentFilter, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  issue(id: $id) {
    attachments(
      after: $after
      before: $before
      filter: $filter
      first: $first
      includeArchived: $includeArchived
      last: $last
      orderBy: $orderBy
    ) {
      ...AttachmentConnection
    }
  }
}
    fragment Attachment on Attachment {
  __typename
  sourceType
  subtitle
  title
  metadata
  groupBySource
  source
  url
  bodyData
  creator {
    id
  }
  issue {
    id
  }
  originalIssue {
    id
  }
  updatedAt
  externalUserCreator {
    id
  }
  archivedAt
  createdAt
  id
}
fragment AttachmentConnection on AttachmentConnection {
  __typename
  nodes {
    ...Attachment
  }
  pageInfo {
    ...PageInfo
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`);
const Issue_BotActorDocument = new TypedDocumentString(`
    query issue_botActor($id: String!) {
  issue(id: $id) {
    botActor {
      ...ActorBot
    }
  }
}
    fragment ActorBot on ActorBot {
  __typename
  avatarUrl
  name
  userDisplayName
  subType
  type
  id
}`);
const Issue_ChildrenDocument = new TypedDocumentString(`
    query issue_children($id: String!, $after: String, $before: String, $filter: IssueFilter, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  issue(id: $id) {
    children(
      after: $after
      before: $before
      filter: $filter
      first: $first
      includeArchived: $includeArchived
      last: $last
      orderBy: $orderBy
    ) {
      ...IssueConnection
    }
  }
}
    fragment ActorBot on ActorBot {
  __typename
  avatarUrl
  name
  userDisplayName
  subType
  type
  id
}
fragment Reaction on Reaction {
  __typename
  emoji
  comment {
    id
  }
  externalUser {
    id
  }
  initiativeUpdate {
    id
  }
  issue {
    id
  }
  updatedAt
  projectUpdate {
    id
  }
  archivedAt
  createdAt
  id
  user {
    id
  }
}
fragment Issue on Issue {
  __typename
  trashed
  reactionData
  labelIds
  integrationSourceType
  url
  identifier
  priorityLabel
  previousIdentifiers
  reactions {
    ...Reaction
  }
  customerTicketCount
  branchName
  delegate {
    id
  }
  botActor {
    ...ActorBot
  }
  sourceComment {
    id
  }
  cycle {
    id
  }
  dueDate
  estimate
  syncedWith {
    ...ExternalEntityInfo
  }
  externalUserCreator {
    id
  }
  asksExternalUserRequester {
    id
  }
  asksRequester {
    id
  }
  description
  title
  number
  lastAppliedTemplate {
    id
  }
  updatedAt
  boardOrder
  prioritySortOrder
  sortOrder
  subIssueSortOrder
  parent {
    id
  }
  priority
  project {
    id
  }
  projectMilestone {
    id
  }
  recurringIssueTemplate {
    id
  }
  team {
    id
  }
  archivedAt
  createdAt
  startedTriageAt
  triagedAt
  addedToCycleAt
  addedToProjectAt
  addedToTeamAt
  autoArchivedAt
  autoClosedAt
  canceledAt
  completedAt
  startedAt
  slaStartedAt
  slaBreachesAt
  slaHighRiskAt
  slaMediumRiskAt
  snoozedUntilAt
  slaType
  id
  assignee {
    id
  }
  creator {
    id
  }
  snoozedBy {
    id
  }
  favorite {
    id
  }
  state {
    id
  }
}
fragment ExternalEntityInfo on ExternalEntityInfo {
  __typename
  metadata {
    ... on ExternalEntityInfoGithubMetadata {
      ...ExternalEntityInfoGithubMetadata
    }
    ... on ExternalEntityInfoJiraMetadata {
      ...ExternalEntityInfoJiraMetadata
    }
    ... on ExternalEntitySlackMetadata {
      ...ExternalEntitySlackMetadata
    }
  }
  id
  service
}
fragment ExternalEntityInfoGithubMetadata on ExternalEntityInfoGithubMetadata {
  __typename
  number
  owner
  repo
}
fragment ExternalEntityInfoJiraMetadata on ExternalEntityInfoJiraMetadata {
  __typename
  issueTypeId
  projectId
  issueKey
}
fragment ExternalEntitySlackMetadata on ExternalEntitySlackMetadata {
  __typename
  messageUrl
  channelId
  channelName
  isFromSlack
}
fragment IssueConnection on IssueConnection {
  __typename
  nodes {
    ...Issue
  }
  pageInfo {
    ...PageInfo
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`);
const Issue_CommentsDocument = new TypedDocumentString(`
    query issue_comments($id: String!, $after: String, $before: String, $filter: CommentFilter, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  issue(id: $id) {
    comments(
      after: $after
      before: $before
      filter: $filter
      first: $first
      includeArchived: $includeArchived
      last: $last
      orderBy: $orderBy
    ) {
      ...CommentConnection
    }
  }
}
    fragment ActorBot on ActorBot {
  __typename
  avatarUrl
  name
  userDisplayName
  subType
  type
  id
}
fragment Comment on Comment {
  __typename
  agentSession {
    id
  }
  url
  reactionData
  reactions {
    ...Reaction
  }
  resolvingCommentId
  documentContentId
  initiativeUpdateId
  issueId
  parentId
  projectUpdateId
  botActor {
    ...ActorBot
  }
  body
  resolvingComment {
    id
  }
  documentContent {
    ...DocumentContent
  }
  syncedWith {
    ...ExternalEntityInfo
  }
  externalThread {
    ...SyncedExternalThread
  }
  externalUser {
    id
  }
  initiativeUpdate {
    id
  }
  issue {
    id
  }
  updatedAt
  parent {
    id
  }
  projectUpdate {
    id
  }
  quotedText
  archivedAt
  createdAt
  resolvedAt
  editedAt
  id
  resolvingUser {
    id
  }
  user {
    id
  }
}
fragment SyncedExternalThread on SyncedExternalThread {
  __typename
  name
  displayName
  url
  subType
  type
  isPersonalIntegrationRequired
  isPersonalIntegrationConnected
  isConnected
  id
}
fragment DocumentContent on DocumentContent {
  __typename
  aiPromptRules {
    ...AiPromptRules
  }
  content
  contentState
  document {
    id
  }
  initiative {
    id
  }
  issue {
    id
  }
  updatedAt
  projectMilestone {
    id
  }
  project {
    id
  }
  restoredAt
  archivedAt
  createdAt
  id
}
fragment Reaction on Reaction {
  __typename
  emoji
  comment {
    id
  }
  externalUser {
    id
  }
  initiativeUpdate {
    id
  }
  issue {
    id
  }
  updatedAt
  projectUpdate {
    id
  }
  archivedAt
  createdAt
  id
  user {
    id
  }
}
fragment AiPromptRules on AiPromptRules {
  __typename
  updatedAt
  archivedAt
  createdAt
  id
  updatedBy {
    id
  }
}
fragment ExternalEntityInfo on ExternalEntityInfo {
  __typename
  metadata {
    ... on ExternalEntityInfoGithubMetadata {
      ...ExternalEntityInfoGithubMetadata
    }
    ... on ExternalEntityInfoJiraMetadata {
      ...ExternalEntityInfoJiraMetadata
    }
    ... on ExternalEntitySlackMetadata {
      ...ExternalEntitySlackMetadata
    }
  }
  id
  service
}
fragment ExternalEntityInfoGithubMetadata on ExternalEntityInfoGithubMetadata {
  __typename
  number
  owner
  repo
}
fragment ExternalEntityInfoJiraMetadata on ExternalEntityInfoJiraMetadata {
  __typename
  issueTypeId
  projectId
  issueKey
}
fragment ExternalEntitySlackMetadata on ExternalEntitySlackMetadata {
  __typename
  messageUrl
  channelId
  channelName
  isFromSlack
}
fragment CommentConnection on CommentConnection {
  __typename
  nodes {
    ...Comment
  }
  pageInfo {
    ...PageInfo
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`);
const Issue_DocumentsDocument = new TypedDocumentString(`
    query issue_documents($id: String!, $after: String, $before: String, $filter: DocumentFilter, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  issue(id: $id) {
    documents(
      after: $after
      before: $before
      filter: $filter
      first: $first
      includeArchived: $includeArchived
      last: $last
      orderBy: $orderBy
    ) {
      ...DocumentConnection
    }
  }
}
    fragment Document on Document {
  __typename
  trashed
  documentContentId
  url
  color
  title
  slugId
  content
  icon
  initiative {
    id
  }
  issue {
    id
  }
  lastAppliedTemplate {
    id
  }
  updatedAt
  sortOrder
  project {
    id
  }
  hiddenAt
  archivedAt
  createdAt
  id
  creator {
    id
  }
  updatedBy {
    id
  }
}
fragment DocumentConnection on DocumentConnection {
  __typename
  nodes {
    ...Document
  }
  pageInfo {
    ...PageInfo
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`);
const Issue_FormerAttachmentsDocument = new TypedDocumentString(`
    query issue_formerAttachments($id: String!, $after: String, $before: String, $filter: AttachmentFilter, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  issue(id: $id) {
    formerAttachments(
      after: $after
      before: $before
      filter: $filter
      first: $first
      includeArchived: $includeArchived
      last: $last
      orderBy: $orderBy
    ) {
      ...AttachmentConnection
    }
  }
}
    fragment Attachment on Attachment {
  __typename
  sourceType
  subtitle
  title
  metadata
  groupBySource
  source
  url
  bodyData
  creator {
    id
  }
  issue {
    id
  }
  originalIssue {
    id
  }
  updatedAt
  externalUserCreator {
    id
  }
  archivedAt
  createdAt
  id
}
fragment AttachmentConnection on AttachmentConnection {
  __typename
  nodes {
    ...Attachment
  }
  pageInfo {
    ...PageInfo
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`);
const Issue_FormerNeedsDocument = new TypedDocumentString(`
    query issue_formerNeeds($id: String!, $after: String, $before: String, $filter: CustomerNeedFilter, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  issue(id: $id) {
    formerNeeds(
      after: $after
      before: $before
      filter: $filter
      first: $first
      includeArchived: $includeArchived
      last: $last
      orderBy: $orderBy
    ) {
      ...CustomerNeedConnection
    }
  }
}
    fragment CustomerNeed on CustomerNeed {
  __typename
  url
  attachment {
    id
  }
  comment {
    id
  }
  creator {
    id
  }
  customer {
    id
  }
  originalIssue {
    id
  }
  issue {
    id
  }
  updatedAt
  body
  projectAttachment {
    ...ProjectAttachment
  }
  project {
    id
  }
  archivedAt
  createdAt
  id
  priority
}
fragment ProjectAttachment on ProjectAttachment {
  __typename
  sourceType
  metadata
  source
  subtitle
  creator {
    id
  }
  updatedAt
  archivedAt
  createdAt
  id
  title
  url
}
fragment CustomerNeedConnection on CustomerNeedConnection {
  __typename
  nodes {
    ...CustomerNeed
  }
  pageInfo {
    ...PageInfo
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`);
const Issue_HistoryDocument = new TypedDocumentString(`
    query issue_history($id: String!, $after: String, $before: String, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  issue(id: $id) {
    history(
      after: $after
      before: $before
      first: $first
      includeArchived: $includeArchived
      last: $last
      orderBy: $orderBy
    ) {
      ...IssueHistoryConnection
    }
  }
}
    fragment ActorBot on ActorBot {
  __typename
  avatarUrl
  name
  userDisplayName
  subType
  type
  id
}
fragment IssueHistory on IssueHistory {
  __typename
  triageResponsibilityAutoAssigned
  relationChanges {
    ...IssueRelationHistoryPayload
  }
  addedLabelIds
  removedLabelIds
  actor {
    id
  }
  descriptionUpdatedBy {
    ...User
  }
  actors {
    ...User
  }
  fromDelegate {
    id
  }
  toDelegate {
    id
  }
  botActor {
    ...ActorBot
  }
  fromCycle {
    id
  }
  toCycle {
    id
  }
  attachmentId
  customerNeedId
  toCycleId
  toParentId
  toConvertedProjectId
  toProjectId
  toStateId
  fromCycleId
  fromParentId
  fromProjectId
  fromStateId
  fromTeamId
  toTeamId
  fromAssigneeId
  toAssigneeId
  actorId
  issueImport {
    ...IssueImport
  }
  issue {
    id
  }
  addedLabels {
    ...IssueLabel
  }
  removedLabels {
    ...IssueLabel
  }
  updatedAt
  attachment {
    id
  }
  toConvertedProject {
    id
  }
  fromParent {
    id
  }
  toParent {
    id
  }
  fromProject {
    id
  }
  toProject {
    id
  }
  fromState {
    id
  }
  toState {
    id
  }
  fromTeam {
    id
  }
  toTeam {
    id
  }
  archivedAt
  createdAt
  id
  toAssignee {
    id
  }
  fromAssignee {
    id
  }
  triageResponsibilityNotifiedUsers {
    ...User
  }
  fromDueDate
  toDueDate
  fromEstimate
  toEstimate
  fromPriority
  toPriority
  fromTitle
  toTitle
  archived
  autoArchived
  autoClosed
  trashed
  updatedDescription
}
fragment User on User {
  __typename
  statusUntilAt
  description
  avatarUrl
  createdIssueCount
  disableReason
  avatarBackgroundColor
  statusEmoji
  initials
  statusLabel
  updatedAt
  lastSeen
  timezone
  archivedAt
  createdAt
  id
  gitHubUserId
  displayName
  email
  name
  url
  active
  guest
  app
  admin
  owner
  isAssignable
  isMentionable
  isMe
  supportsAgentSessions
  canAccessAnyPublicTeam
  calendarHash
  inviteHash
}
fragment IssueImport on IssueImport {
  __typename
  progress
  errorMetadata
  csvFileUrl
  serviceMetadata
  teamName
  mapping
  displayName
  creatorId
  updatedAt
  service
  status
  archivedAt
  createdAt
  id
  error
}
fragment IssueRelationHistoryPayload on IssueRelationHistoryPayload {
  __typename
  identifier
  type
}
fragment IssueLabel on IssueLabel {
  __typename
  lastAppliedAt
  color
  description
  name
  updatedAt
  inheritedFrom {
    id
  }
  parent {
    id
  }
  team {
    id
  }
  archivedAt
  createdAt
  id
  creator {
    id
  }
  retiredBy {
    id
  }
  isGroup
}
fragment IssueHistoryConnection on IssueHistoryConnection {
  __typename
  nodes {
    ...IssueHistory
  }
  pageInfo {
    ...PageInfo
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`);
const Issue_InverseRelationsDocument = new TypedDocumentString(`
    query issue_inverseRelations($id: String!, $after: String, $before: String, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  issue(id: $id) {
    inverseRelations(
      after: $after
      before: $before
      first: $first
      includeArchived: $includeArchived
      last: $last
      orderBy: $orderBy
    ) {
      ...IssueRelationConnection
    }
  }
}
    fragment IssueRelation on IssueRelation {
  __typename
  issue {
    id
  }
  updatedAt
  relatedIssue {
    id
  }
  type
  archivedAt
  createdAt
  id
}
fragment IssueRelationConnection on IssueRelationConnection {
  __typename
  nodes {
    ...IssueRelation
  }
  pageInfo {
    ...PageInfo
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`);
const Issue_LabelsDocument = new TypedDocumentString(`
    query issue_labels($id: String!, $after: String, $before: String, $filter: IssueLabelFilter, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  issue(id: $id) {
    labels(
      after: $after
      before: $before
      filter: $filter
      first: $first
      includeArchived: $includeArchived
      last: $last
      orderBy: $orderBy
    ) {
      ...IssueLabelConnection
    }
  }
}
    fragment IssueLabel on IssueLabel {
  __typename
  lastAppliedAt
  color
  description
  name
  updatedAt
  inheritedFrom {
    id
  }
  parent {
    id
  }
  team {
    id
  }
  archivedAt
  createdAt
  id
  creator {
    id
  }
  retiredBy {
    id
  }
  isGroup
}
fragment IssueLabelConnection on IssueLabelConnection {
  __typename
  nodes {
    ...IssueLabel
  }
  pageInfo {
    ...PageInfo
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`);
const Issue_NeedsDocument = new TypedDocumentString(`
    query issue_needs($id: String!, $after: String, $before: String, $filter: CustomerNeedFilter, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  issue(id: $id) {
    needs(
      after: $after
      before: $before
      filter: $filter
      first: $first
      includeArchived: $includeArchived
      last: $last
      orderBy: $orderBy
    ) {
      ...CustomerNeedConnection
    }
  }
}
    fragment CustomerNeed on CustomerNeed {
  __typename
  url
  attachment {
    id
  }
  comment {
    id
  }
  creator {
    id
  }
  customer {
    id
  }
  originalIssue {
    id
  }
  issue {
    id
  }
  updatedAt
  body
  projectAttachment {
    ...ProjectAttachment
  }
  project {
    id
  }
  archivedAt
  createdAt
  id
  priority
}
fragment ProjectAttachment on ProjectAttachment {
  __typename
  sourceType
  metadata
  source
  subtitle
  creator {
    id
  }
  updatedAt
  archivedAt
  createdAt
  id
  title
  url
}
fragment CustomerNeedConnection on CustomerNeedConnection {
  __typename
  nodes {
    ...CustomerNeed
  }
  pageInfo {
    ...PageInfo
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`);
const Issue_RelationsDocument = new TypedDocumentString(`
    query issue_relations($id: String!, $after: String, $before: String, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  issue(id: $id) {
    relations(
      after: $after
      before: $before
      first: $first
      includeArchived: $includeArchived
      last: $last
      orderBy: $orderBy
    ) {
      ...IssueRelationConnection
    }
  }
}
    fragment IssueRelation on IssueRelation {
  __typename
  issue {
    id
  }
  updatedAt
  relatedIssue {
    id
  }
  type
  archivedAt
  createdAt
  id
}
fragment IssueRelationConnection on IssueRelationConnection {
  __typename
  nodes {
    ...IssueRelation
  }
  pageInfo {
    ...PageInfo
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`);
const Issue_SubscribersDocument = new TypedDocumentString(`
    query issue_subscribers($id: String!, $after: String, $before: String, $filter: UserFilter, $first: Int, $includeArchived: Boolean, $includeDisabled: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  issue(id: $id) {
    subscribers(
      after: $after
      before: $before
      filter: $filter
      first: $first
      includeArchived: $includeArchived
      includeDisabled: $includeDisabled
      last: $last
      orderBy: $orderBy
    ) {
      ...UserConnection
    }
  }
}
    fragment User on User {
  __typename
  statusUntilAt
  description
  avatarUrl
  createdIssueCount
  disableReason
  avatarBackgroundColor
  statusEmoji
  initials
  statusLabel
  updatedAt
  lastSeen
  timezone
  archivedAt
  createdAt
  id
  gitHubUserId
  displayName
  email
  name
  url
  active
  guest
  app
  admin
  owner
  isAssignable
  isMentionable
  isMe
  supportsAgentSessions
  canAccessAnyPublicTeam
  calendarHash
  inviteHash
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}
fragment UserConnection on UserConnection {
  __typename
  nodes {
    ...User
  }
  pageInfo {
    ...PageInfo
  }
}`);
const IssueFigmaFileKeySearchDocument = new TypedDocumentString(`
    query issueFigmaFileKeySearch($after: String, $before: String, $fileKey: String!, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  issueFigmaFileKeySearch(
    after: $after
    before: $before
    fileKey: $fileKey
    first: $first
    includeArchived: $includeArchived
    last: $last
    orderBy: $orderBy
  ) {
    ...IssueConnection
  }
}
    fragment ActorBot on ActorBot {
  __typename
  avatarUrl
  name
  userDisplayName
  subType
  type
  id
}
fragment Reaction on Reaction {
  __typename
  emoji
  comment {
    id
  }
  externalUser {
    id
  }
  initiativeUpdate {
    id
  }
  issue {
    id
  }
  updatedAt
  projectUpdate {
    id
  }
  archivedAt
  createdAt
  id
  user {
    id
  }
}
fragment Issue on Issue {
  __typename
  trashed
  reactionData
  labelIds
  integrationSourceType
  url
  identifier
  priorityLabel
  previousIdentifiers
  reactions {
    ...Reaction
  }
  customerTicketCount
  branchName
  delegate {
    id
  }
  botActor {
    ...ActorBot
  }
  sourceComment {
    id
  }
  cycle {
    id
  }
  dueDate
  estimate
  syncedWith {
    ...ExternalEntityInfo
  }
  externalUserCreator {
    id
  }
  asksExternalUserRequester {
    id
  }
  asksRequester {
    id
  }
  description
  title
  number
  lastAppliedTemplate {
    id
  }
  updatedAt
  boardOrder
  prioritySortOrder
  sortOrder
  subIssueSortOrder
  parent {
    id
  }
  priority
  project {
    id
  }
  projectMilestone {
    id
  }
  recurringIssueTemplate {
    id
  }
  team {
    id
  }
  archivedAt
  createdAt
  startedTriageAt
  triagedAt
  addedToCycleAt
  addedToProjectAt
  addedToTeamAt
  autoArchivedAt
  autoClosedAt
  canceledAt
  completedAt
  startedAt
  slaStartedAt
  slaBreachesAt
  slaHighRiskAt
  slaMediumRiskAt
  snoozedUntilAt
  slaType
  id
  assignee {
    id
  }
  creator {
    id
  }
  snoozedBy {
    id
  }
  favorite {
    id
  }
  state {
    id
  }
}
fragment ExternalEntityInfo on ExternalEntityInfo {
  __typename
  metadata {
    ... on ExternalEntityInfoGithubMetadata {
      ...ExternalEntityInfoGithubMetadata
    }
    ... on ExternalEntityInfoJiraMetadata {
      ...ExternalEntityInfoJiraMetadata
    }
    ... on ExternalEntitySlackMetadata {
      ...ExternalEntitySlackMetadata
    }
  }
  id
  service
}
fragment ExternalEntityInfoGithubMetadata on ExternalEntityInfoGithubMetadata {
  __typename
  number
  owner
  repo
}
fragment ExternalEntityInfoJiraMetadata on ExternalEntityInfoJiraMetadata {
  __typename
  issueTypeId
  projectId
  issueKey
}
fragment ExternalEntitySlackMetadata on ExternalEntitySlackMetadata {
  __typename
  messageUrl
  channelId
  channelName
  isFromSlack
}
fragment IssueConnection on IssueConnection {
  __typename
  nodes {
    ...Issue
  }
  pageInfo {
    ...PageInfo
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`);
const IssueFilterSuggestionDocument = new TypedDocumentString(`
    query issueFilterSuggestion($projectId: String, $prompt: String!) {
  issueFilterSuggestion(projectId: $projectId, prompt: $prompt) {
    ...IssueFilterSuggestionPayload
  }
}
    fragment IssueFilterSuggestionPayload on IssueFilterSuggestionPayload {
  __typename
  filter
  logId
}`);
const IssueImportCheckCsvDocument = new TypedDocumentString(`
    query issueImportCheckCSV($csvUrl: String!, $service: String!) {
  issueImportCheckCSV(csvUrl: $csvUrl, service: $service) {
    ...IssueImportCheckPayload
  }
}
    fragment IssueImportCheckPayload on IssueImportCheckPayload {
  __typename
  success
}`);
const IssueImportCheckSyncDocument = new TypedDocumentString(`
    query issueImportCheckSync($issueImportId: String!) {
  issueImportCheckSync(issueImportId: $issueImportId) {
    ...IssueImportSyncCheckPayload
  }
}
    fragment IssueImportSyncCheckPayload on IssueImportSyncCheckPayload {
  __typename
  error
  canSync
}`);
const IssueImportJqlCheckDocument = new TypedDocumentString(`
    query issueImportJqlCheck($jiraEmail: String!, $jiraHostname: String!, $jiraProject: String!, $jiraToken: String!, $jql: String!) {
  issueImportJqlCheck(
    jiraEmail: $jiraEmail
    jiraHostname: $jiraHostname
    jiraProject: $jiraProject
    jiraToken: $jiraToken
    jql: $jql
  ) {
    ...IssueImportJqlCheckPayload
  }
}
    fragment IssueImportJqlCheckPayload on IssueImportJqlCheckPayload {
  __typename
  error
  count
  success
}`);
const IssueLabelDocument = new TypedDocumentString(`
    query issueLabel($id: String!) {
  issueLabel(id: $id) {
    ...IssueLabel
  }
}
    fragment IssueLabel on IssueLabel {
  __typename
  lastAppliedAt
  color
  description
  name
  updatedAt
  inheritedFrom {
    id
  }
  parent {
    id
  }
  team {
    id
  }
  archivedAt
  createdAt
  id
  creator {
    id
  }
  retiredBy {
    id
  }
  isGroup
}`);
const IssueLabel_ChildrenDocument = new TypedDocumentString(`
    query issueLabel_children($id: String!, $after: String, $before: String, $filter: IssueLabelFilter, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  issueLabel(id: $id) {
    children(
      after: $after
      before: $before
      filter: $filter
      first: $first
      includeArchived: $includeArchived
      last: $last
      orderBy: $orderBy
    ) {
      ...IssueLabelConnection
    }
  }
}
    fragment IssueLabel on IssueLabel {
  __typename
  lastAppliedAt
  color
  description
  name
  updatedAt
  inheritedFrom {
    id
  }
  parent {
    id
  }
  team {
    id
  }
  archivedAt
  createdAt
  id
  creator {
    id
  }
  retiredBy {
    id
  }
  isGroup
}
fragment IssueLabelConnection on IssueLabelConnection {
  __typename
  nodes {
    ...IssueLabel
  }
  pageInfo {
    ...PageInfo
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`);
const IssueLabel_IssuesDocument = new TypedDocumentString(`
    query issueLabel_issues($id: String!, $after: String, $before: String, $filter: IssueFilter, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  issueLabel(id: $id) {
    issues(
      after: $after
      before: $before
      filter: $filter
      first: $first
      includeArchived: $includeArchived
      last: $last
      orderBy: $orderBy
    ) {
      ...IssueConnection
    }
  }
}
    fragment ActorBot on ActorBot {
  __typename
  avatarUrl
  name
  userDisplayName
  subType
  type
  id
}
fragment Reaction on Reaction {
  __typename
  emoji
  comment {
    id
  }
  externalUser {
    id
  }
  initiativeUpdate {
    id
  }
  issue {
    id
  }
  updatedAt
  projectUpdate {
    id
  }
  archivedAt
  createdAt
  id
  user {
    id
  }
}
fragment Issue on Issue {
  __typename
  trashed
  reactionData
  labelIds
  integrationSourceType
  url
  identifier
  priorityLabel
  previousIdentifiers
  reactions {
    ...Reaction
  }
  customerTicketCount
  branchName
  delegate {
    id
  }
  botActor {
    ...ActorBot
  }
  sourceComment {
    id
  }
  cycle {
    id
  }
  dueDate
  estimate
  syncedWith {
    ...ExternalEntityInfo
  }
  externalUserCreator {
    id
  }
  asksExternalUserRequester {
    id
  }
  asksRequester {
    id
  }
  description
  title
  number
  lastAppliedTemplate {
    id
  }
  updatedAt
  boardOrder
  prioritySortOrder
  sortOrder
  subIssueSortOrder
  parent {
    id
  }
  priority
  project {
    id
  }
  projectMilestone {
    id
  }
  recurringIssueTemplate {
    id
  }
  team {
    id
  }
  archivedAt
  createdAt
  startedTriageAt
  triagedAt
  addedToCycleAt
  addedToProjectAt
  addedToTeamAt
  autoArchivedAt
  autoClosedAt
  canceledAt
  completedAt
  startedAt
  slaStartedAt
  slaBreachesAt
  slaHighRiskAt
  slaMediumRiskAt
  snoozedUntilAt
  slaType
  id
  assignee {
    id
  }
  creator {
    id
  }
  snoozedBy {
    id
  }
  favorite {
    id
  }
  state {
    id
  }
}
fragment ExternalEntityInfo on ExternalEntityInfo {
  __typename
  metadata {
    ... on ExternalEntityInfoGithubMetadata {
      ...ExternalEntityInfoGithubMetadata
    }
    ... on ExternalEntityInfoJiraMetadata {
      ...ExternalEntityInfoJiraMetadata
    }
    ... on ExternalEntitySlackMetadata {
      ...ExternalEntitySlackMetadata
    }
  }
  id
  service
}
fragment ExternalEntityInfoGithubMetadata on ExternalEntityInfoGithubMetadata {
  __typename
  number
  owner
  repo
}
fragment ExternalEntityInfoJiraMetadata on ExternalEntityInfoJiraMetadata {
  __typename
  issueTypeId
  projectId
  issueKey
}
fragment ExternalEntitySlackMetadata on ExternalEntitySlackMetadata {
  __typename
  messageUrl
  channelId
  channelName
  isFromSlack
}
fragment IssueConnection on IssueConnection {
  __typename
  nodes {
    ...Issue
  }
  pageInfo {
    ...PageInfo
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`);
const IssueLabelsDocument = new TypedDocumentString(`
    query issueLabels($after: String, $before: String, $filter: IssueLabelFilter, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  issueLabels(
    after: $after
    before: $before
    filter: $filter
    first: $first
    includeArchived: $includeArchived
    last: $last
    orderBy: $orderBy
  ) {
    ...IssueLabelConnection
  }
}
    fragment IssueLabel on IssueLabel {
  __typename
  lastAppliedAt
  color
  description
  name
  updatedAt
  inheritedFrom {
    id
  }
  parent {
    id
  }
  team {
    id
  }
  archivedAt
  createdAt
  id
  creator {
    id
  }
  retiredBy {
    id
  }
  isGroup
}
fragment IssueLabelConnection on IssueLabelConnection {
  __typename
  nodes {
    ...IssueLabel
  }
  pageInfo {
    ...PageInfo
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`);
const IssuePriorityValuesDocument = new TypedDocumentString(`
    query issuePriorityValues {
  issuePriorityValues {
    ...IssuePriorityValue
  }
}
    fragment IssuePriorityValue on IssuePriorityValue {
  __typename
  label
  priority
}`);
const IssueRelationDocument = new TypedDocumentString(`
    query issueRelation($id: String!) {
  issueRelation(id: $id) {
    ...IssueRelation
  }
}
    fragment IssueRelation on IssueRelation {
  __typename
  issue {
    id
  }
  updatedAt
  relatedIssue {
    id
  }
  type
  archivedAt
  createdAt
  id
}`);
const IssueRelationsDocument = new TypedDocumentString(`
    query issueRelations($after: String, $before: String, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  issueRelations(
    after: $after
    before: $before
    first: $first
    includeArchived: $includeArchived
    last: $last
    orderBy: $orderBy
  ) {
    ...IssueRelationConnection
  }
}
    fragment IssueRelation on IssueRelation {
  __typename
  issue {
    id
  }
  updatedAt
  relatedIssue {
    id
  }
  type
  archivedAt
  createdAt
  id
}
fragment IssueRelationConnection on IssueRelationConnection {
  __typename
  nodes {
    ...IssueRelation
  }
  pageInfo {
    ...PageInfo
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`);
const IssueSearchDocument = new TypedDocumentString(`
    query issueSearch($after: String, $before: String, $filter: IssueFilter, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy, $query: String) {
  issueSearch(
    after: $after
    before: $before
    filter: $filter
    first: $first
    includeArchived: $includeArchived
    last: $last
    orderBy: $orderBy
    query: $query
  ) {
    ...IssueConnection
  }
}
    fragment ActorBot on ActorBot {
  __typename
  avatarUrl
  name
  userDisplayName
  subType
  type
  id
}
fragment Reaction on Reaction {
  __typename
  emoji
  comment {
    id
  }
  externalUser {
    id
  }
  initiativeUpdate {
    id
  }
  issue {
    id
  }
  updatedAt
  projectUpdate {
    id
  }
  archivedAt
  createdAt
  id
  user {
    id
  }
}
fragment Issue on Issue {
  __typename
  trashed
  reactionData
  labelIds
  integrationSourceType
  url
  identifier
  priorityLabel
  previousIdentifiers
  reactions {
    ...Reaction
  }
  customerTicketCount
  branchName
  delegate {
    id
  }
  botActor {
    ...ActorBot
  }
  sourceComment {
    id
  }
  cycle {
    id
  }
  dueDate
  estimate
  syncedWith {
    ...ExternalEntityInfo
  }
  externalUserCreator {
    id
  }
  asksExternalUserRequester {
    id
  }
  asksRequester {
    id
  }
  description
  title
  number
  lastAppliedTemplate {
    id
  }
  updatedAt
  boardOrder
  prioritySortOrder
  sortOrder
  subIssueSortOrder
  parent {
    id
  }
  priority
  project {
    id
  }
  projectMilestone {
    id
  }
  recurringIssueTemplate {
    id
  }
  team {
    id
  }
  archivedAt
  createdAt
  startedTriageAt
  triagedAt
  addedToCycleAt
  addedToProjectAt
  addedToTeamAt
  autoArchivedAt
  autoClosedAt
  canceledAt
  completedAt
  startedAt
  slaStartedAt
  slaBreachesAt
  slaHighRiskAt
  slaMediumRiskAt
  snoozedUntilAt
  slaType
  id
  assignee {
    id
  }
  creator {
    id
  }
  snoozedBy {
    id
  }
  favorite {
    id
  }
  state {
    id
  }
}
fragment ExternalEntityInfo on ExternalEntityInfo {
  __typename
  metadata {
    ... on ExternalEntityInfoGithubMetadata {
      ...ExternalEntityInfoGithubMetadata
    }
    ... on ExternalEntityInfoJiraMetadata {
      ...ExternalEntityInfoJiraMetadata
    }
    ... on ExternalEntitySlackMetadata {
      ...ExternalEntitySlackMetadata
    }
  }
  id
  service
}
fragment ExternalEntityInfoGithubMetadata on ExternalEntityInfoGithubMetadata {
  __typename
  number
  owner
  repo
}
fragment ExternalEntityInfoJiraMetadata on ExternalEntityInfoJiraMetadata {
  __typename
  issueTypeId
  projectId
  issueKey
}
fragment ExternalEntitySlackMetadata on ExternalEntitySlackMetadata {
  __typename
  messageUrl
  channelId
  channelName
  isFromSlack
}
fragment IssueConnection on IssueConnection {
  __typename
  nodes {
    ...Issue
  }
  pageInfo {
    ...PageInfo
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`);
const IssueTitleSuggestionFromCustomerRequestDocument = new TypedDocumentString(`
    query issueTitleSuggestionFromCustomerRequest($request: String!) {
  issueTitleSuggestionFromCustomerRequest(request: $request) {
    ...IssueTitleSuggestionFromCustomerRequestPayload
  }
}
    fragment IssueTitleSuggestionFromCustomerRequestPayload on IssueTitleSuggestionFromCustomerRequestPayload {
  __typename
  lastSyncId
  title
}`);
const IssueVcsBranchSearchDocument = new TypedDocumentString(`
    query issueVcsBranchSearch($branchName: String!) {
  issueVcsBranchSearch(branchName: $branchName) {
    ...Issue
  }
}
    fragment ActorBot on ActorBot {
  __typename
  avatarUrl
  name
  userDisplayName
  subType
  type
  id
}
fragment Reaction on Reaction {
  __typename
  emoji
  comment {
    id
  }
  externalUser {
    id
  }
  initiativeUpdate {
    id
  }
  issue {
    id
  }
  updatedAt
  projectUpdate {
    id
  }
  archivedAt
  createdAt
  id
  user {
    id
  }
}
fragment Issue on Issue {
  __typename
  trashed
  reactionData
  labelIds
  integrationSourceType
  url
  identifier
  priorityLabel
  previousIdentifiers
  reactions {
    ...Reaction
  }
  customerTicketCount
  branchName
  delegate {
    id
  }
  botActor {
    ...ActorBot
  }
  sourceComment {
    id
  }
  cycle {
    id
  }
  dueDate
  estimate
  syncedWith {
    ...ExternalEntityInfo
  }
  externalUserCreator {
    id
  }
  asksExternalUserRequester {
    id
  }
  asksRequester {
    id
  }
  description
  title
  number
  lastAppliedTemplate {
    id
  }
  updatedAt
  boardOrder
  prioritySortOrder
  sortOrder
  subIssueSortOrder
  parent {
    id
  }
  priority
  project {
    id
  }
  projectMilestone {
    id
  }
  recurringIssueTemplate {
    id
  }
  team {
    id
  }
  archivedAt
  createdAt
  startedTriageAt
  triagedAt
  addedToCycleAt
  addedToProjectAt
  addedToTeamAt
  autoArchivedAt
  autoClosedAt
  canceledAt
  completedAt
  startedAt
  slaStartedAt
  slaBreachesAt
  slaHighRiskAt
  slaMediumRiskAt
  snoozedUntilAt
  slaType
  id
  assignee {
    id
  }
  creator {
    id
  }
  snoozedBy {
    id
  }
  favorite {
    id
  }
  state {
    id
  }
}
fragment ExternalEntityInfo on ExternalEntityInfo {
  __typename
  metadata {
    ... on ExternalEntityInfoGithubMetadata {
      ...ExternalEntityInfoGithubMetadata
    }
    ... on ExternalEntityInfoJiraMetadata {
      ...ExternalEntityInfoJiraMetadata
    }
    ... on ExternalEntitySlackMetadata {
      ...ExternalEntitySlackMetadata
    }
  }
  id
  service
}
fragment ExternalEntityInfoGithubMetadata on ExternalEntityInfoGithubMetadata {
  __typename
  number
  owner
  repo
}
fragment ExternalEntityInfoJiraMetadata on ExternalEntityInfoJiraMetadata {
  __typename
  issueTypeId
  projectId
  issueKey
}
fragment ExternalEntitySlackMetadata on ExternalEntitySlackMetadata {
  __typename
  messageUrl
  channelId
  channelName
  isFromSlack
}`);
const IssueVcsBranchSearch_AttachmentsDocument = new TypedDocumentString(`
    query issueVcsBranchSearch_attachments($branchName: String!, $after: String, $before: String, $filter: AttachmentFilter, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  issueVcsBranchSearch(branchName: $branchName) {
    attachments(
      after: $after
      before: $before
      filter: $filter
      first: $first
      includeArchived: $includeArchived
      last: $last
      orderBy: $orderBy
    ) {
      ...AttachmentConnection
    }
  }
}
    fragment Attachment on Attachment {
  __typename
  sourceType
  subtitle
  title
  metadata
  groupBySource
  source
  url
  bodyData
  creator {
    id
  }
  issue {
    id
  }
  originalIssue {
    id
  }
  updatedAt
  externalUserCreator {
    id
  }
  archivedAt
  createdAt
  id
}
fragment AttachmentConnection on AttachmentConnection {
  __typename
  nodes {
    ...Attachment
  }
  pageInfo {
    ...PageInfo
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`);
const IssueVcsBranchSearch_BotActorDocument = new TypedDocumentString(`
    query issueVcsBranchSearch_botActor($branchName: String!) {
  issueVcsBranchSearch(branchName: $branchName) {
    botActor {
      ...ActorBot
    }
  }
}
    fragment ActorBot on ActorBot {
  __typename
  avatarUrl
  name
  userDisplayName
  subType
  type
  id
}`);
const IssueVcsBranchSearch_ChildrenDocument = new TypedDocumentString(`
    query issueVcsBranchSearch_children($branchName: String!, $after: String, $before: String, $filter: IssueFilter, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  issueVcsBranchSearch(branchName: $branchName) {
    children(
      after: $after
      before: $before
      filter: $filter
      first: $first
      includeArchived: $includeArchived
      last: $last
      orderBy: $orderBy
    ) {
      ...IssueConnection
    }
  }
}
    fragment ActorBot on ActorBot {
  __typename
  avatarUrl
  name
  userDisplayName
  subType
  type
  id
}
fragment Reaction on Reaction {
  __typename
  emoji
  comment {
    id
  }
  externalUser {
    id
  }
  initiativeUpdate {
    id
  }
  issue {
    id
  }
  updatedAt
  projectUpdate {
    id
  }
  archivedAt
  createdAt
  id
  user {
    id
  }
}
fragment Issue on Issue {
  __typename
  trashed
  reactionData
  labelIds
  integrationSourceType
  url
  identifier
  priorityLabel
  previousIdentifiers
  reactions {
    ...Reaction
  }
  customerTicketCount
  branchName
  delegate {
    id
  }
  botActor {
    ...ActorBot
  }
  sourceComment {
    id
  }
  cycle {
    id
  }
  dueDate
  estimate
  syncedWith {
    ...ExternalEntityInfo
  }
  externalUserCreator {
    id
  }
  asksExternalUserRequester {
    id
  }
  asksRequester {
    id
  }
  description
  title
  number
  lastAppliedTemplate {
    id
  }
  updatedAt
  boardOrder
  prioritySortOrder
  sortOrder
  subIssueSortOrder
  parent {
    id
  }
  priority
  project {
    id
  }
  projectMilestone {
    id
  }
  recurringIssueTemplate {
    id
  }
  team {
    id
  }
  archivedAt
  createdAt
  startedTriageAt
  triagedAt
  addedToCycleAt
  addedToProjectAt
  addedToTeamAt
  autoArchivedAt
  autoClosedAt
  canceledAt
  completedAt
  startedAt
  slaStartedAt
  slaBreachesAt
  slaHighRiskAt
  slaMediumRiskAt
  snoozedUntilAt
  slaType
  id
  assignee {
    id
  }
  creator {
    id
  }
  snoozedBy {
    id
  }
  favorite {
    id
  }
  state {
    id
  }
}
fragment ExternalEntityInfo on ExternalEntityInfo {
  __typename
  metadata {
    ... on ExternalEntityInfoGithubMetadata {
      ...ExternalEntityInfoGithubMetadata
    }
    ... on ExternalEntityInfoJiraMetadata {
      ...ExternalEntityInfoJiraMetadata
    }
    ... on ExternalEntitySlackMetadata {
      ...ExternalEntitySlackMetadata
    }
  }
  id
  service
}
fragment ExternalEntityInfoGithubMetadata on ExternalEntityInfoGithubMetadata {
  __typename
  number
  owner
  repo
}
fragment ExternalEntityInfoJiraMetadata on ExternalEntityInfoJiraMetadata {
  __typename
  issueTypeId
  projectId
  issueKey
}
fragment ExternalEntitySlackMetadata on ExternalEntitySlackMetadata {
  __typename
  messageUrl
  channelId
  channelName
  isFromSlack
}
fragment IssueConnection on IssueConnection {
  __typename
  nodes {
    ...Issue
  }
  pageInfo {
    ...PageInfo
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`);
const IssueVcsBranchSearch_CommentsDocument = new TypedDocumentString(`
    query issueVcsBranchSearch_comments($branchName: String!, $after: String, $before: String, $filter: CommentFilter, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  issueVcsBranchSearch(branchName: $branchName) {
    comments(
      after: $after
      before: $before
      filter: $filter
      first: $first
      includeArchived: $includeArchived
      last: $last
      orderBy: $orderBy
    ) {
      ...CommentConnection
    }
  }
}
    fragment ActorBot on ActorBot {
  __typename
  avatarUrl
  name
  userDisplayName
  subType
  type
  id
}
fragment Comment on Comment {
  __typename
  agentSession {
    id
  }
  url
  reactionData
  reactions {
    ...Reaction
  }
  resolvingCommentId
  documentContentId
  initiativeUpdateId
  issueId
  parentId
  projectUpdateId
  botActor {
    ...ActorBot
  }
  body
  resolvingComment {
    id
  }
  documentContent {
    ...DocumentContent
  }
  syncedWith {
    ...ExternalEntityInfo
  }
  externalThread {
    ...SyncedExternalThread
  }
  externalUser {
    id
  }
  initiativeUpdate {
    id
  }
  issue {
    id
  }
  updatedAt
  parent {
    id
  }
  projectUpdate {
    id
  }
  quotedText
  archivedAt
  createdAt
  resolvedAt
  editedAt
  id
  resolvingUser {
    id
  }
  user {
    id
  }
}
fragment SyncedExternalThread on SyncedExternalThread {
  __typename
  name
  displayName
  url
  subType
  type
  isPersonalIntegrationRequired
  isPersonalIntegrationConnected
  isConnected
  id
}
fragment DocumentContent on DocumentContent {
  __typename
  aiPromptRules {
    ...AiPromptRules
  }
  content
  contentState
  document {
    id
  }
  initiative {
    id
  }
  issue {
    id
  }
  updatedAt
  projectMilestone {
    id
  }
  project {
    id
  }
  restoredAt
  archivedAt
  createdAt
  id
}
fragment Reaction on Reaction {
  __typename
  emoji
  comment {
    id
  }
  externalUser {
    id
  }
  initiativeUpdate {
    id
  }
  issue {
    id
  }
  updatedAt
  projectUpdate {
    id
  }
  archivedAt
  createdAt
  id
  user {
    id
  }
}
fragment AiPromptRules on AiPromptRules {
  __typename
  updatedAt
  archivedAt
  createdAt
  id
  updatedBy {
    id
  }
}
fragment ExternalEntityInfo on ExternalEntityInfo {
  __typename
  metadata {
    ... on ExternalEntityInfoGithubMetadata {
      ...ExternalEntityInfoGithubMetadata
    }
    ... on ExternalEntityInfoJiraMetadata {
      ...ExternalEntityInfoJiraMetadata
    }
    ... on ExternalEntitySlackMetadata {
      ...ExternalEntitySlackMetadata
    }
  }
  id
  service
}
fragment ExternalEntityInfoGithubMetadata on ExternalEntityInfoGithubMetadata {
  __typename
  number
  owner
  repo
}
fragment ExternalEntityInfoJiraMetadata on ExternalEntityInfoJiraMetadata {
  __typename
  issueTypeId
  projectId
  issueKey
}
fragment ExternalEntitySlackMetadata on ExternalEntitySlackMetadata {
  __typename
  messageUrl
  channelId
  channelName
  isFromSlack
}
fragment CommentConnection on CommentConnection {
  __typename
  nodes {
    ...Comment
  }
  pageInfo {
    ...PageInfo
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`);
const IssueVcsBranchSearch_DocumentsDocument = new TypedDocumentString(`
    query issueVcsBranchSearch_documents($branchName: String!, $after: String, $before: String, $filter: DocumentFilter, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  issueVcsBranchSearch(branchName: $branchName) {
    documents(
      after: $after
      before: $before
      filter: $filter
      first: $first
      includeArchived: $includeArchived
      last: $last
      orderBy: $orderBy
    ) {
      ...DocumentConnection
    }
  }
}
    fragment Document on Document {
  __typename
  trashed
  documentContentId
  url
  color
  title
  slugId
  content
  icon
  initiative {
    id
  }
  issue {
    id
  }
  lastAppliedTemplate {
    id
  }
  updatedAt
  sortOrder
  project {
    id
  }
  hiddenAt
  archivedAt
  createdAt
  id
  creator {
    id
  }
  updatedBy {
    id
  }
}
fragment DocumentConnection on DocumentConnection {
  __typename
  nodes {
    ...Document
  }
  pageInfo {
    ...PageInfo
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`);
const IssueVcsBranchSearch_FormerAttachmentsDocument = new TypedDocumentString(`
    query issueVcsBranchSearch_formerAttachments($branchName: String!, $after: String, $before: String, $filter: AttachmentFilter, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  issueVcsBranchSearch(branchName: $branchName) {
    formerAttachments(
      after: $after
      before: $before
      filter: $filter
      first: $first
      includeArchived: $includeArchived
      last: $last
      orderBy: $orderBy
    ) {
      ...AttachmentConnection
    }
  }
}
    fragment Attachment on Attachment {
  __typename
  sourceType
  subtitle
  title
  metadata
  groupBySource
  source
  url
  bodyData
  creator {
    id
  }
  issue {
    id
  }
  originalIssue {
    id
  }
  updatedAt
  externalUserCreator {
    id
  }
  archivedAt
  createdAt
  id
}
fragment AttachmentConnection on AttachmentConnection {
  __typename
  nodes {
    ...Attachment
  }
  pageInfo {
    ...PageInfo
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`);
const IssueVcsBranchSearch_FormerNeedsDocument = new TypedDocumentString(`
    query issueVcsBranchSearch_formerNeeds($branchName: String!, $after: String, $before: String, $filter: CustomerNeedFilter, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  issueVcsBranchSearch(branchName: $branchName) {
    formerNeeds(
      after: $after
      before: $before
      filter: $filter
      first: $first
      includeArchived: $includeArchived
      last: $last
      orderBy: $orderBy
    ) {
      ...CustomerNeedConnection
    }
  }
}
    fragment CustomerNeed on CustomerNeed {
  __typename
  url
  attachment {
    id
  }
  comment {
    id
  }
  creator {
    id
  }
  customer {
    id
  }
  originalIssue {
    id
  }
  issue {
    id
  }
  updatedAt
  body
  projectAttachment {
    ...ProjectAttachment
  }
  project {
    id
  }
  archivedAt
  createdAt
  id
  priority
}
fragment ProjectAttachment on ProjectAttachment {
  __typename
  sourceType
  metadata
  source
  subtitle
  creator {
    id
  }
  updatedAt
  archivedAt
  createdAt
  id
  title
  url
}
fragment CustomerNeedConnection on CustomerNeedConnection {
  __typename
  nodes {
    ...CustomerNeed
  }
  pageInfo {
    ...PageInfo
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`);
const IssueVcsBranchSearch_HistoryDocument = new TypedDocumentString(`
    query issueVcsBranchSearch_history($branchName: String!, $after: String, $before: String, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  issueVcsBranchSearch(branchName: $branchName) {
    history(
      after: $after
      before: $before
      first: $first
      includeArchived: $includeArchived
      last: $last
      orderBy: $orderBy
    ) {
      ...IssueHistoryConnection
    }
  }
}
    fragment ActorBot on ActorBot {
  __typename
  avatarUrl
  name
  userDisplayName
  subType
  type
  id
}
fragment IssueHistory on IssueHistory {
  __typename
  triageResponsibilityAutoAssigned
  relationChanges {
    ...IssueRelationHistoryPayload
  }
  addedLabelIds
  removedLabelIds
  actor {
    id
  }
  descriptionUpdatedBy {
    ...User
  }
  actors {
    ...User
  }
  fromDelegate {
    id
  }
  toDelegate {
    id
  }
  botActor {
    ...ActorBot
  }
  fromCycle {
    id
  }
  toCycle {
    id
  }
  attachmentId
  customerNeedId
  toCycleId
  toParentId
  toConvertedProjectId
  toProjectId
  toStateId
  fromCycleId
  fromParentId
  fromProjectId
  fromStateId
  fromTeamId
  toTeamId
  fromAssigneeId
  toAssigneeId
  actorId
  issueImport {
    ...IssueImport
  }
  issue {
    id
  }
  addedLabels {
    ...IssueLabel
  }
  removedLabels {
    ...IssueLabel
  }
  updatedAt
  attachment {
    id
  }
  toConvertedProject {
    id
  }
  fromParent {
    id
  }
  toParent {
    id
  }
  fromProject {
    id
  }
  toProject {
    id
  }
  fromState {
    id
  }
  toState {
    id
  }
  fromTeam {
    id
  }
  toTeam {
    id
  }
  archivedAt
  createdAt
  id
  toAssignee {
    id
  }
  fromAssignee {
    id
  }
  triageResponsibilityNotifiedUsers {
    ...User
  }
  fromDueDate
  toDueDate
  fromEstimate
  toEstimate
  fromPriority
  toPriority
  fromTitle
  toTitle
  archived
  autoArchived
  autoClosed
  trashed
  updatedDescription
}
fragment User on User {
  __typename
  statusUntilAt
  description
  avatarUrl
  createdIssueCount
  disableReason
  avatarBackgroundColor
  statusEmoji
  initials
  statusLabel
  updatedAt
  lastSeen
  timezone
  archivedAt
  createdAt
  id
  gitHubUserId
  displayName
  email
  name
  url
  active
  guest
  app
  admin
  owner
  isAssignable
  isMentionable
  isMe
  supportsAgentSessions
  canAccessAnyPublicTeam
  calendarHash
  inviteHash
}
fragment IssueImport on IssueImport {
  __typename
  progress
  errorMetadata
  csvFileUrl
  serviceMetadata
  teamName
  mapping
  displayName
  creatorId
  updatedAt
  service
  status
  archivedAt
  createdAt
  id
  error
}
fragment IssueRelationHistoryPayload on IssueRelationHistoryPayload {
  __typename
  identifier
  type
}
fragment IssueLabel on IssueLabel {
  __typename
  lastAppliedAt
  color
  description
  name
  updatedAt
  inheritedFrom {
    id
  }
  parent {
    id
  }
  team {
    id
  }
  archivedAt
  createdAt
  id
  creator {
    id
  }
  retiredBy {
    id
  }
  isGroup
}
fragment IssueHistoryConnection on IssueHistoryConnection {
  __typename
  nodes {
    ...IssueHistory
  }
  pageInfo {
    ...PageInfo
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`);
const IssueVcsBranchSearch_InverseRelationsDocument = new TypedDocumentString(`
    query issueVcsBranchSearch_inverseRelations($branchName: String!, $after: String, $before: String, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  issueVcsBranchSearch(branchName: $branchName) {
    inverseRelations(
      after: $after
      before: $before
      first: $first
      includeArchived: $includeArchived
      last: $last
      orderBy: $orderBy
    ) {
      ...IssueRelationConnection
    }
  }
}
    fragment IssueRelation on IssueRelation {
  __typename
  issue {
    id
  }
  updatedAt
  relatedIssue {
    id
  }
  type
  archivedAt
  createdAt
  id
}
fragment IssueRelationConnection on IssueRelationConnection {
  __typename
  nodes {
    ...IssueRelation
  }
  pageInfo {
    ...PageInfo
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`);
const IssueVcsBranchSearch_LabelsDocument = new TypedDocumentString(`
    query issueVcsBranchSearch_labels($branchName: String!, $after: String, $before: String, $filter: IssueLabelFilter, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  issueVcsBranchSearch(branchName: $branchName) {
    labels(
      after: $after
      before: $before
      filter: $filter
      first: $first
      includeArchived: $includeArchived
      last: $last
      orderBy: $orderBy
    ) {
      ...IssueLabelConnection
    }
  }
}
    fragment IssueLabel on IssueLabel {
  __typename
  lastAppliedAt
  color
  description
  name
  updatedAt
  inheritedFrom {
    id
  }
  parent {
    id
  }
  team {
    id
  }
  archivedAt
  createdAt
  id
  creator {
    id
  }
  retiredBy {
    id
  }
  isGroup
}
fragment IssueLabelConnection on IssueLabelConnection {
  __typename
  nodes {
    ...IssueLabel
  }
  pageInfo {
    ...PageInfo
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`);
const IssueVcsBranchSearch_NeedsDocument = new TypedDocumentString(`
    query issueVcsBranchSearch_needs($branchName: String!, $after: String, $before: String, $filter: CustomerNeedFilter, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  issueVcsBranchSearch(branchName: $branchName) {
    needs(
      after: $after
      before: $before
      filter: $filter
      first: $first
      includeArchived: $includeArchived
      last: $last
      orderBy: $orderBy
    ) {
      ...CustomerNeedConnection
    }
  }
}
    fragment CustomerNeed on CustomerNeed {
  __typename
  url
  attachment {
    id
  }
  comment {
    id
  }
  creator {
    id
  }
  customer {
    id
  }
  originalIssue {
    id
  }
  issue {
    id
  }
  updatedAt
  body
  projectAttachment {
    ...ProjectAttachment
  }
  project {
    id
  }
  archivedAt
  createdAt
  id
  priority
}
fragment ProjectAttachment on ProjectAttachment {
  __typename
  sourceType
  metadata
  source
  subtitle
  creator {
    id
  }
  updatedAt
  archivedAt
  createdAt
  id
  title
  url
}
fragment CustomerNeedConnection on CustomerNeedConnection {
  __typename
  nodes {
    ...CustomerNeed
  }
  pageInfo {
    ...PageInfo
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`);
const IssueVcsBranchSearch_RelationsDocument = new TypedDocumentString(`
    query issueVcsBranchSearch_relations($branchName: String!, $after: String, $before: String, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  issueVcsBranchSearch(branchName: $branchName) {
    relations(
      after: $after
      before: $before
      first: $first
      includeArchived: $includeArchived
      last: $last
      orderBy: $orderBy
    ) {
      ...IssueRelationConnection
    }
  }
}
    fragment IssueRelation on IssueRelation {
  __typename
  issue {
    id
  }
  updatedAt
  relatedIssue {
    id
  }
  type
  archivedAt
  createdAt
  id
}
fragment IssueRelationConnection on IssueRelationConnection {
  __typename
  nodes {
    ...IssueRelation
  }
  pageInfo {
    ...PageInfo
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`);
const IssueVcsBranchSearch_SubscribersDocument = new TypedDocumentString(`
    query issueVcsBranchSearch_subscribers($branchName: String!, $after: String, $before: String, $filter: UserFilter, $first: Int, $includeArchived: Boolean, $includeDisabled: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  issueVcsBranchSearch(branchName: $branchName) {
    subscribers(
      after: $after
      before: $before
      filter: $filter
      first: $first
      includeArchived: $includeArchived
      includeDisabled: $includeDisabled
      last: $last
      orderBy: $orderBy
    ) {
      ...UserConnection
    }
  }
}
    fragment User on User {
  __typename
  statusUntilAt
  description
  avatarUrl
  createdIssueCount
  disableReason
  avatarBackgroundColor
  statusEmoji
  initials
  statusLabel
  updatedAt
  lastSeen
  timezone
  archivedAt
  createdAt
  id
  gitHubUserId
  displayName
  email
  name
  url
  active
  guest
  app
  admin
  owner
  isAssignable
  isMentionable
  isMe
  supportsAgentSessions
  canAccessAnyPublicTeam
  calendarHash
  inviteHash
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}
fragment UserConnection on UserConnection {
  __typename
  nodes {
    ...User
  }
  pageInfo {
    ...PageInfo
  }
}`);
const IssuesDocument = new TypedDocumentString(`
    query issues($after: String, $before: String, $filter: IssueFilter, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy, $sort: [IssueSortInput!]) {
  issues(
    after: $after
    before: $before
    filter: $filter
    first: $first
    includeArchived: $includeArchived
    last: $last
    orderBy: $orderBy
    sort: $sort
  ) {
    ...IssueConnection
  }
}
    fragment ActorBot on ActorBot {
  __typename
  avatarUrl
  name
  userDisplayName
  subType
  type
  id
}
fragment Reaction on Reaction {
  __typename
  emoji
  comment {
    id
  }
  externalUser {
    id
  }
  initiativeUpdate {
    id
  }
  issue {
    id
  }
  updatedAt
  projectUpdate {
    id
  }
  archivedAt
  createdAt
  id
  user {
    id
  }
}
fragment Issue on Issue {
  __typename
  trashed
  reactionData
  labelIds
  integrationSourceType
  url
  identifier
  priorityLabel
  previousIdentifiers
  reactions {
    ...Reaction
  }
  customerTicketCount
  branchName
  delegate {
    id
  }
  botActor {
    ...ActorBot
  }
  sourceComment {
    id
  }
  cycle {
    id
  }
  dueDate
  estimate
  syncedWith {
    ...ExternalEntityInfo
  }
  externalUserCreator {
    id
  }
  asksExternalUserRequester {
    id
  }
  asksRequester {
    id
  }
  description
  title
  number
  lastAppliedTemplate {
    id
  }
  updatedAt
  boardOrder
  prioritySortOrder
  sortOrder
  subIssueSortOrder
  parent {
    id
  }
  priority
  project {
    id
  }
  projectMilestone {
    id
  }
  recurringIssueTemplate {
    id
  }
  team {
    id
  }
  archivedAt
  createdAt
  startedTriageAt
  triagedAt
  addedToCycleAt
  addedToProjectAt
  addedToTeamAt
  autoArchivedAt
  autoClosedAt
  canceledAt
  completedAt
  startedAt
  slaStartedAt
  slaBreachesAt
  slaHighRiskAt
  slaMediumRiskAt
  snoozedUntilAt
  slaType
  id
  assignee {
    id
  }
  creator {
    id
  }
  snoozedBy {
    id
  }
  favorite {
    id
  }
  state {
    id
  }
}
fragment ExternalEntityInfo on ExternalEntityInfo {
  __typename
  metadata {
    ... on ExternalEntityInfoGithubMetadata {
      ...ExternalEntityInfoGithubMetadata
    }
    ... on ExternalEntityInfoJiraMetadata {
      ...ExternalEntityInfoJiraMetadata
    }
    ... on ExternalEntitySlackMetadata {
      ...ExternalEntitySlackMetadata
    }
  }
  id
  service
}
fragment ExternalEntityInfoGithubMetadata on ExternalEntityInfoGithubMetadata {
  __typename
  number
  owner
  repo
}
fragment ExternalEntityInfoJiraMetadata on ExternalEntityInfoJiraMetadata {
  __typename
  issueTypeId
  projectId
  issueKey
}
fragment ExternalEntitySlackMetadata on ExternalEntitySlackMetadata {
  __typename
  messageUrl
  channelId
  channelName
  isFromSlack
}
fragment IssueConnection on IssueConnection {
  __typename
  nodes {
    ...Issue
  }
  pageInfo {
    ...PageInfo
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`);
const NotificationDocument = new TypedDocumentString(`
    query notification($id: String!) {
  notification(id: $id) {
    ...Notification
  }
}
    fragment ActorBot on ActorBot {
  __typename
  avatarUrl
  name
  userDisplayName
  subType
  type
  id
}
fragment CustomerNeedNotification on CustomerNeedNotification {
  __typename
  type
  customerNeedId
  botActor {
    ...ActorBot
  }
  category
  customerNeed {
    id
  }
  externalUserActor {
    id
  }
  relatedIssue {
    id
  }
  updatedAt
  relatedProject {
    id
  }
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment CustomerNotification on CustomerNotification {
  __typename
  type
  customerId
  botActor {
    ...ActorBot
  }
  category
  customer {
    id
  }
  externalUserActor {
    id
  }
  updatedAt
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment DocumentNotification on DocumentNotification {
  __typename
  reactionEmoji
  type
  commentId
  documentId
  parentCommentId
  botActor {
    ...ActorBot
  }
  category
  externalUserActor {
    id
  }
  updatedAt
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment Notification on Notification {
  __typename
  type
  botActor {
    ...ActorBot
  }
  category
  externalUserActor {
    id
  }
  updatedAt
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
  ... on CustomerNeedNotification {
    ...CustomerNeedNotification
  }
  ... on CustomerNotification {
    ...CustomerNotification
  }
  ... on DocumentNotification {
    ...DocumentNotification
  }
  ... on InitiativeNotification {
    ...InitiativeNotification
  }
  ... on IssueNotification {
    ...IssueNotification
  }
  ... on OauthClientApprovalNotification {
    ...OauthClientApprovalNotification
  }
  ... on PostNotification {
    ...PostNotification
  }
  ... on ProjectNotification {
    ...ProjectNotification
  }
  ... on PullRequestNotification {
    ...PullRequestNotification
  }
}
fragment PostNotification on PostNotification {
  __typename
  reactionEmoji
  type
  commentId
  parentCommentId
  postId
  botActor {
    ...ActorBot
  }
  category
  externalUserActor {
    id
  }
  updatedAt
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment ProjectNotification on ProjectNotification {
  __typename
  reactionEmoji
  type
  commentId
  parentCommentId
  projectId
  projectMilestoneId
  projectUpdateId
  botActor {
    ...ActorBot
  }
  category
  comment {
    id
  }
  document {
    id
  }
  externalUserActor {
    id
  }
  updatedAt
  parentComment {
    id
  }
  project {
    id
  }
  projectUpdate {
    id
  }
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment PullRequestNotification on PullRequestNotification {
  __typename
  type
  pullRequestCommentId
  pullRequestId
  botActor {
    ...ActorBot
  }
  category
  externalUserActor {
    id
  }
  updatedAt
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment InitiativeNotification on InitiativeNotification {
  __typename
  reactionEmoji
  type
  commentId
  initiativeId
  initiativeUpdateId
  parentCommentId
  botActor {
    ...ActorBot
  }
  category
  comment {
    id
  }
  document {
    id
  }
  externalUserActor {
    id
  }
  initiative {
    id
  }
  initiativeUpdate {
    id
  }
  updatedAt
  parentComment {
    id
  }
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment IssueNotification on IssueNotification {
  __typename
  reactionEmoji
  type
  commentId
  issueId
  parentCommentId
  botActor {
    ...ActorBot
  }
  category
  comment {
    id
  }
  externalUserActor {
    id
  }
  issue {
    id
  }
  updatedAt
  parentComment {
    id
  }
  subscriptions {
    ...NotificationSubscription
  }
  team {
    id
  }
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment OauthClientApprovalNotification on OauthClientApprovalNotification {
  __typename
  type
  oauthClientApprovalId
  oauthClientApproval {
    ...OauthClientApproval
  }
  botActor {
    ...ActorBot
  }
  category
  externalUserActor {
    id
  }
  updatedAt
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment NotificationSubscription on NotificationSubscription {
  __typename
  customView {
    id
  }
  cycle {
    id
  }
  initiative {
    id
  }
  label {
    id
  }
  project {
    id
  }
  customer {
    id
  }
  updatedAt
  team {
    id
  }
  archivedAt
  createdAt
  userContextViewType
  contextViewType
  id
  subscriber {
    id
  }
  user {
    id
  }
  active
}
fragment OauthClientApproval on OauthClientApproval {
  __typename
  newlyRequestedScopes
  updatedAt
  requesterId
  responderId
  requestReason
  denyReason
  scopes
  status
  archivedAt
  createdAt
  id
  oauthClientId
}`);
const NotificationSubscriptionDocument = new TypedDocumentString(`
    query notificationSubscription($id: String!) {
  notificationSubscription(id: $id) {
    ...NotificationSubscription
  }
}
    fragment NotificationSubscription on NotificationSubscription {
  __typename
  customView {
    id
  }
  cycle {
    id
  }
  initiative {
    id
  }
  label {
    id
  }
  project {
    id
  }
  customer {
    id
  }
  updatedAt
  team {
    id
  }
  archivedAt
  createdAt
  userContextViewType
  contextViewType
  id
  subscriber {
    id
  }
  user {
    id
  }
  active
}`);
const NotificationSubscriptionsDocument = new TypedDocumentString(`
    query notificationSubscriptions($after: String, $before: String, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  notificationSubscriptions(
    after: $after
    before: $before
    first: $first
    includeArchived: $includeArchived
    last: $last
    orderBy: $orderBy
  ) {
    ...NotificationSubscriptionConnection
  }
}
    fragment NotificationSubscription on NotificationSubscription {
  __typename
  customView {
    id
  }
  cycle {
    id
  }
  initiative {
    id
  }
  label {
    id
  }
  project {
    id
  }
  customer {
    id
  }
  updatedAt
  team {
    id
  }
  archivedAt
  createdAt
  userContextViewType
  contextViewType
  id
  subscriber {
    id
  }
  user {
    id
  }
  active
}
fragment NotificationSubscriptionConnection on NotificationSubscriptionConnection {
  __typename
  nodes {
    ...NotificationSubscription
  }
  pageInfo {
    ...PageInfo
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`);
const NotificationsDocument = new TypedDocumentString(`
    query notifications($after: String, $before: String, $filter: NotificationFilter, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  notifications(
    after: $after
    before: $before
    filter: $filter
    first: $first
    includeArchived: $includeArchived
    last: $last
    orderBy: $orderBy
  ) {
    ...NotificationConnection
  }
}
    fragment ActorBot on ActorBot {
  __typename
  avatarUrl
  name
  userDisplayName
  subType
  type
  id
}
fragment CustomerNeedNotification on CustomerNeedNotification {
  __typename
  type
  customerNeedId
  botActor {
    ...ActorBot
  }
  category
  customerNeed {
    id
  }
  externalUserActor {
    id
  }
  relatedIssue {
    id
  }
  updatedAt
  relatedProject {
    id
  }
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment CustomerNotification on CustomerNotification {
  __typename
  type
  customerId
  botActor {
    ...ActorBot
  }
  category
  customer {
    id
  }
  externalUserActor {
    id
  }
  updatedAt
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment DocumentNotification on DocumentNotification {
  __typename
  reactionEmoji
  type
  commentId
  documentId
  parentCommentId
  botActor {
    ...ActorBot
  }
  category
  externalUserActor {
    id
  }
  updatedAt
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment Notification on Notification {
  __typename
  type
  botActor {
    ...ActorBot
  }
  category
  externalUserActor {
    id
  }
  updatedAt
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
  ... on CustomerNeedNotification {
    ...CustomerNeedNotification
  }
  ... on CustomerNotification {
    ...CustomerNotification
  }
  ... on DocumentNotification {
    ...DocumentNotification
  }
  ... on InitiativeNotification {
    ...InitiativeNotification
  }
  ... on IssueNotification {
    ...IssueNotification
  }
  ... on OauthClientApprovalNotification {
    ...OauthClientApprovalNotification
  }
  ... on PostNotification {
    ...PostNotification
  }
  ... on ProjectNotification {
    ...ProjectNotification
  }
  ... on PullRequestNotification {
    ...PullRequestNotification
  }
}
fragment PostNotification on PostNotification {
  __typename
  reactionEmoji
  type
  commentId
  parentCommentId
  postId
  botActor {
    ...ActorBot
  }
  category
  externalUserActor {
    id
  }
  updatedAt
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment ProjectNotification on ProjectNotification {
  __typename
  reactionEmoji
  type
  commentId
  parentCommentId
  projectId
  projectMilestoneId
  projectUpdateId
  botActor {
    ...ActorBot
  }
  category
  comment {
    id
  }
  document {
    id
  }
  externalUserActor {
    id
  }
  updatedAt
  parentComment {
    id
  }
  project {
    id
  }
  projectUpdate {
    id
  }
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment PullRequestNotification on PullRequestNotification {
  __typename
  type
  pullRequestCommentId
  pullRequestId
  botActor {
    ...ActorBot
  }
  category
  externalUserActor {
    id
  }
  updatedAt
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment InitiativeNotification on InitiativeNotification {
  __typename
  reactionEmoji
  type
  commentId
  initiativeId
  initiativeUpdateId
  parentCommentId
  botActor {
    ...ActorBot
  }
  category
  comment {
    id
  }
  document {
    id
  }
  externalUserActor {
    id
  }
  initiative {
    id
  }
  initiativeUpdate {
    id
  }
  updatedAt
  parentComment {
    id
  }
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment IssueNotification on IssueNotification {
  __typename
  reactionEmoji
  type
  commentId
  issueId
  parentCommentId
  botActor {
    ...ActorBot
  }
  category
  comment {
    id
  }
  externalUserActor {
    id
  }
  issue {
    id
  }
  updatedAt
  parentComment {
    id
  }
  subscriptions {
    ...NotificationSubscription
  }
  team {
    id
  }
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment OauthClientApprovalNotification on OauthClientApprovalNotification {
  __typename
  type
  oauthClientApprovalId
  oauthClientApproval {
    ...OauthClientApproval
  }
  botActor {
    ...ActorBot
  }
  category
  externalUserActor {
    id
  }
  updatedAt
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment NotificationSubscription on NotificationSubscription {
  __typename
  customView {
    id
  }
  cycle {
    id
  }
  initiative {
    id
  }
  label {
    id
  }
  project {
    id
  }
  customer {
    id
  }
  updatedAt
  team {
    id
  }
  archivedAt
  createdAt
  userContextViewType
  contextViewType
  id
  subscriber {
    id
  }
  user {
    id
  }
  active
}
fragment OauthClientApproval on OauthClientApproval {
  __typename
  newlyRequestedScopes
  updatedAt
  requesterId
  responderId
  requestReason
  denyReason
  scopes
  status
  archivedAt
  createdAt
  id
  oauthClientId
}
fragment NotificationConnection on NotificationConnection {
  __typename
  nodes {
    ...Notification
  }
  pageInfo {
    ...PageInfo
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`);
const OrganizationDocument = new TypedDocumentString(`
    query organization {
  organization {
    ...Organization
  }
}
    fragment ProjectStatus on ProjectStatus {
  __typename
  description
  color
  updatedAt
  name
  position
  archivedAt
  createdAt
  type
  id
  indefinite
}
fragment Organization on Organization {
  __typename
  allowedAuthServices
  allowedFileUploadContentTypes
  createdIssueCount
  customersConfiguration
  defaultFeedSummarySchedule
  gitBranchFormat
  ipRestrictions {
    ...OrganizationIpRestriction
  }
  userCount
  customerCount
  previousUrlKeys
  periodUploadVolume
  securitySettings
  initiativeUpdateRemindersDay
  projectUpdateRemindersDay
  releaseChannel
  initiativeUpdateRemindersHour
  projectUpdateRemindersHour
  updatedAt
  fiscalYearStartMonth
  initiativeUpdateReminderFrequencyInWeeks
  projectUpdateReminderFrequencyInWeeks
  logoUrl
  name
  projectStatuses {
    ...ProjectStatus
  }
  subscription {
    ...PaidSubscription
  }
  urlKey
  deletionRequestedAt
  archivedAt
  createdAt
  trialStartsAt
  trialEndsAt
  id
  hipaaComplianceEnabled
  samlEnabled
  scimEnabled
  gitLinkbackDescriptionsEnabled
  gitLinkbackMessagesEnabled
  gitPublicLinkbackMessagesEnabled
  aiDiscussionSummariesEnabled
  aiThreadSummariesEnabled
  feedEnabled
  customersEnabled
  roadmapEnabled
  projectUpdatesReminderFrequency
  allowMembersToInvite
  restrictTeamCreationToAdmins
  restrictLabelManagementToAdmins
  slaDayCount
}
fragment PaidSubscription on PaidSubscription {
  __typename
  collectionMethod
  creator {
    id
  }
  cancelAt
  canceledAt
  nextBillingAt
  updatedAt
  seatsMaximum
  seatsMinimum
  seats
  pendingChangeType
  type
  archivedAt
  createdAt
  id
}
fragment OrganizationIpRestriction on OrganizationIpRestriction {
  __typename
  range
  description
  type
  enabled
}`);
const Organization_IntegrationsDocument = new TypedDocumentString(`
    query organization_integrations($after: String, $before: String, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  organization {
    integrations(
      after: $after
      before: $before
      first: $first
      includeArchived: $includeArchived
      last: $last
      orderBy: $orderBy
    ) {
      ...IntegrationConnection
    }
  }
}
    fragment Integration on Integration {
  __typename
  service
  updatedAt
  team {
    id
  }
  archivedAt
  createdAt
  id
  creator {
    id
  }
}
fragment IntegrationConnection on IntegrationConnection {
  __typename
  nodes {
    ...Integration
  }
  pageInfo {
    ...PageInfo
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`);
const Organization_LabelsDocument = new TypedDocumentString(`
    query organization_labels($after: String, $before: String, $filter: IssueLabelFilter, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  organization {
    labels(
      after: $after
      before: $before
      filter: $filter
      first: $first
      includeArchived: $includeArchived
      last: $last
      orderBy: $orderBy
    ) {
      ...IssueLabelConnection
    }
  }
}
    fragment IssueLabel on IssueLabel {
  __typename
  lastAppliedAt
  color
  description
  name
  updatedAt
  inheritedFrom {
    id
  }
  parent {
    id
  }
  team {
    id
  }
  archivedAt
  createdAt
  id
  creator {
    id
  }
  retiredBy {
    id
  }
  isGroup
}
fragment IssueLabelConnection on IssueLabelConnection {
  __typename
  nodes {
    ...IssueLabel
  }
  pageInfo {
    ...PageInfo
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`);
const Organization_ProjectLabelsDocument = new TypedDocumentString(`
    query organization_projectLabels($after: String, $before: String, $filter: ProjectLabelFilter, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  organization {
    projectLabels(
      after: $after
      before: $before
      filter: $filter
      first: $first
      includeArchived: $includeArchived
      last: $last
      orderBy: $orderBy
    ) {
      ...ProjectLabelConnection
    }
  }
}
    fragment ProjectLabel on ProjectLabel {
  __typename
  lastAppliedAt
  color
  description
  name
  updatedAt
  parent {
    id
  }
  archivedAt
  createdAt
  id
  creator {
    id
  }
  retiredBy {
    id
  }
  isGroup
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}
fragment ProjectLabelConnection on ProjectLabelConnection {
  __typename
  nodes {
    ...ProjectLabel
  }
  pageInfo {
    ...PageInfo
  }
}`);
const Organization_SubscriptionDocument = new TypedDocumentString(`
    query organization_subscription {
  organization {
    subscription {
      ...PaidSubscription
    }
  }
}
    fragment PaidSubscription on PaidSubscription {
  __typename
  collectionMethod
  creator {
    id
  }
  cancelAt
  canceledAt
  nextBillingAt
  updatedAt
  seatsMaximum
  seatsMinimum
  seats
  pendingChangeType
  type
  archivedAt
  createdAt
  id
}`);
const Organization_TeamsDocument = new TypedDocumentString(`
    query organization_teams($after: String, $before: String, $filter: TeamFilter, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  organization {
    teams(
      after: $after
      before: $before
      filter: $filter
      first: $first
      includeArchived: $includeArchived
      last: $last
      orderBy: $orderBy
    ) {
      ...TeamConnection
    }
  }
}
    fragment Team on Team {
  __typename
  cycleIssueAutoAssignCompleted
  cycleLockToActive
  cycleIssueAutoAssignStarted
  cycleCalenderUrl
  upcomingCycleCount
  issueCount
  autoArchivePeriod
  autoClosePeriod
  securitySettings
  integrationsSettings {
    id
  }
  activeCycle {
    id
  }
  triageResponsibility {
    id
  }
  scimGroupName
  autoCloseStateId
  cycleCooldownTime
  cycleStartDay
  defaultTemplateForMembers {
    id
  }
  defaultTemplateForNonMembers {
    id
  }
  defaultProjectTemplate {
    id
  }
  defaultIssueState {
    id
  }
  cycleDuration
  icon
  defaultTemplateForMembersId
  defaultTemplateForNonMembersId
  issueEstimationType
  updatedAt
  displayName
  color
  description
  name
  key
  archivedAt
  createdAt
  timezone
  id
  mergeWorkflowState {
    id
  }
  draftWorkflowState {
    id
  }
  startWorkflowState {
    id
  }
  mergeableWorkflowState {
    id
  }
  reviewWorkflowState {
    id
  }
  markedAsDuplicateWorkflowState {
    id
  }
  triageIssueState {
    id
  }
  defaultIssueEstimate
  setIssueSortOrderOnStateChange
  allMembersCanJoin
  requirePriorityToLeaveTriage
  autoCloseChildIssues
  autoCloseParentIssues
  scimManaged
  private
  inheritIssueEstimation
  inheritWorkflowStatuses
  cyclesEnabled
  issueEstimationExtended
  issueEstimationAllowZero
  aiDiscussionSummariesEnabled
  aiThreadSummariesEnabled
  groupIssueHistory
  slackIssueComments
  slackNewIssue
  slackIssueStatuses
  triageEnabled
  inviteHash
  issueOrderingNoPriorityFirst
  issueSortOrderDefaultToBottom
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}
fragment TeamConnection on TeamConnection {
  __typename
  nodes {
    ...Team
  }
  pageInfo {
    ...PageInfo
  }
}`);
const Organization_TemplatesDocument = new TypedDocumentString(`
    query organization_templates($after: String, $before: String, $filter: NullableTemplateFilter, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  organization {
    templates(
      after: $after
      before: $before
      filter: $filter
      first: $first
      includeArchived: $includeArchived
      last: $last
      orderBy: $orderBy
    ) {
      ...TemplateConnection
    }
  }
}
    fragment Template on Template {
  __typename
  templateData
  description
  lastAppliedAt
  type
  updatedAt
  name
  inheritedFrom {
    id
  }
  sortOrder
  team {
    id
  }
  archivedAt
  createdAt
  id
  creator {
    id
  }
  lastUpdatedBy {
    id
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}
fragment TemplateConnection on TemplateConnection {
  __typename
  nodes {
    ...Template
  }
  pageInfo {
    ...PageInfo
  }
}`);
const Organization_UsersDocument = new TypedDocumentString(`
    query organization_users($after: String, $before: String, $first: Int, $includeArchived: Boolean, $includeDisabled: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  organization {
    users(
      after: $after
      before: $before
      first: $first
      includeArchived: $includeArchived
      includeDisabled: $includeDisabled
      last: $last
      orderBy: $orderBy
    ) {
      ...UserConnection
    }
  }
}
    fragment User on User {
  __typename
  statusUntilAt
  description
  avatarUrl
  createdIssueCount
  disableReason
  avatarBackgroundColor
  statusEmoji
  initials
  statusLabel
  updatedAt
  lastSeen
  timezone
  archivedAt
  createdAt
  id
  gitHubUserId
  displayName
  email
  name
  url
  active
  guest
  app
  admin
  owner
  isAssignable
  isMentionable
  isMe
  supportsAgentSessions
  canAccessAnyPublicTeam
  calendarHash
  inviteHash
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}
fragment UserConnection on UserConnection {
  __typename
  nodes {
    ...User
  }
  pageInfo {
    ...PageInfo
  }
}`);
const OrganizationExistsDocument = new TypedDocumentString(`
    query organizationExists($urlKey: String!) {
  organizationExists(urlKey: $urlKey) {
    ...OrganizationExistsPayload
  }
}
    fragment OrganizationExistsPayload on OrganizationExistsPayload {
  __typename
  success
  exists
}`);
const OrganizationInviteDocument = new TypedDocumentString(`
    query organizationInvite($id: String!) {
  organizationInvite(id: $id) {
    ...OrganizationInvite
  }
}
    fragment OrganizationInvite on OrganizationInvite {
  __typename
  metadata
  external
  email
  updatedAt
  archivedAt
  createdAt
  acceptedAt
  expiresAt
  id
  role
  inviter {
    id
  }
  invitee {
    id
  }
}`);
const OrganizationInvitesDocument = new TypedDocumentString(`
    query organizationInvites($after: String, $before: String, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  organizationInvites(
    after: $after
    before: $before
    first: $first
    includeArchived: $includeArchived
    last: $last
    orderBy: $orderBy
  ) {
    ...OrganizationInviteConnection
  }
}
    fragment OrganizationInvite on OrganizationInvite {
  __typename
  metadata
  external
  email
  updatedAt
  archivedAt
  createdAt
  acceptedAt
  expiresAt
  id
  role
  inviter {
    id
  }
  invitee {
    id
  }
}
fragment OrganizationInviteConnection on OrganizationInviteConnection {
  __typename
  nodes {
    ...OrganizationInvite
  }
  pageInfo {
    ...PageInfo
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`);
const ProjectDocument = new TypedDocumentString(`
    query project($id: String!) {
  project(id: $id) {
    ...Project
  }
}
    fragment DocumentContent on DocumentContent {
  __typename
  aiPromptRules {
    ...AiPromptRules
  }
  content
  contentState
  document {
    id
  }
  initiative {
    id
  }
  issue {
    id
  }
  updatedAt
  projectMilestone {
    id
  }
  project {
    id
  }
  restoredAt
  archivedAt
  createdAt
  id
}
fragment Project on Project {
  __typename
  trashed
  labelIds
  url
  integrationsSettings {
    id
  }
  documentContent {
    ...DocumentContent
  }
  updateRemindersDay
  targetDate
  startDate
  updateReminderFrequency
  health
  updateRemindersHour
  icon
  lastUpdate {
    id
  }
  lastAppliedTemplate {
    id
  }
  updatedAt
  updateReminderFrequencyInWeeks
  completedScopeHistory
  completedIssueCountHistory
  inProgressScopeHistory
  progress
  scope
  priorityLabel
  priority
  lead {
    id
  }
  convertedFromIssue {
    id
  }
  color
  content
  description
  name
  slugId
  targetDateResolution
  startDateResolution
  frequencyResolution
  prioritySortOrder
  sortOrder
  status {
    id
  }
  archivedAt
  createdAt
  healthUpdatedAt
  autoArchivedAt
  canceledAt
  completedAt
  startedAt
  projectUpdateRemindersPausedUntilAt
  scopeHistory
  issueCountHistory
  id
  creator {
    id
  }
  favorite {
    id
  }
  slackIssueComments
  slackNewIssue
  slackIssueStatuses
  state
}
fragment AiPromptRules on AiPromptRules {
  __typename
  updatedAt
  archivedAt
  createdAt
  id
  updatedBy {
    id
  }
}`);
const Project_CommentsDocument = new TypedDocumentString(`
    query project_comments($id: String!, $after: String, $before: String, $filter: CommentFilter, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  project(id: $id) {
    comments(
      after: $after
      before: $before
      filter: $filter
      first: $first
      includeArchived: $includeArchived
      last: $last
      orderBy: $orderBy
    ) {
      ...CommentConnection
    }
  }
}
    fragment ActorBot on ActorBot {
  __typename
  avatarUrl
  name
  userDisplayName
  subType
  type
  id
}
fragment Comment on Comment {
  __typename
  agentSession {
    id
  }
  url
  reactionData
  reactions {
    ...Reaction
  }
  resolvingCommentId
  documentContentId
  initiativeUpdateId
  issueId
  parentId
  projectUpdateId
  botActor {
    ...ActorBot
  }
  body
  resolvingComment {
    id
  }
  documentContent {
    ...DocumentContent
  }
  syncedWith {
    ...ExternalEntityInfo
  }
  externalThread {
    ...SyncedExternalThread
  }
  externalUser {
    id
  }
  initiativeUpdate {
    id
  }
  issue {
    id
  }
  updatedAt
  parent {
    id
  }
  projectUpdate {
    id
  }
  quotedText
  archivedAt
  createdAt
  resolvedAt
  editedAt
  id
  resolvingUser {
    id
  }
  user {
    id
  }
}
fragment SyncedExternalThread on SyncedExternalThread {
  __typename
  name
  displayName
  url
  subType
  type
  isPersonalIntegrationRequired
  isPersonalIntegrationConnected
  isConnected
  id
}
fragment DocumentContent on DocumentContent {
  __typename
  aiPromptRules {
    ...AiPromptRules
  }
  content
  contentState
  document {
    id
  }
  initiative {
    id
  }
  issue {
    id
  }
  updatedAt
  projectMilestone {
    id
  }
  project {
    id
  }
  restoredAt
  archivedAt
  createdAt
  id
}
fragment Reaction on Reaction {
  __typename
  emoji
  comment {
    id
  }
  externalUser {
    id
  }
  initiativeUpdate {
    id
  }
  issue {
    id
  }
  updatedAt
  projectUpdate {
    id
  }
  archivedAt
  createdAt
  id
  user {
    id
  }
}
fragment AiPromptRules on AiPromptRules {
  __typename
  updatedAt
  archivedAt
  createdAt
  id
  updatedBy {
    id
  }
}
fragment ExternalEntityInfo on ExternalEntityInfo {
  __typename
  metadata {
    ... on ExternalEntityInfoGithubMetadata {
      ...ExternalEntityInfoGithubMetadata
    }
    ... on ExternalEntityInfoJiraMetadata {
      ...ExternalEntityInfoJiraMetadata
    }
    ... on ExternalEntitySlackMetadata {
      ...ExternalEntitySlackMetadata
    }
  }
  id
  service
}
fragment ExternalEntityInfoGithubMetadata on ExternalEntityInfoGithubMetadata {
  __typename
  number
  owner
  repo
}
fragment ExternalEntityInfoJiraMetadata on ExternalEntityInfoJiraMetadata {
  __typename
  issueTypeId
  projectId
  issueKey
}
fragment ExternalEntitySlackMetadata on ExternalEntitySlackMetadata {
  __typename
  messageUrl
  channelId
  channelName
  isFromSlack
}
fragment CommentConnection on CommentConnection {
  __typename
  nodes {
    ...Comment
  }
  pageInfo {
    ...PageInfo
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`);
const Project_DocumentContentDocument = new TypedDocumentString(`
    query project_documentContent($id: String!) {
  project(id: $id) {
    documentContent {
      ...DocumentContent
    }
  }
}
    fragment DocumentContent on DocumentContent {
  __typename
  aiPromptRules {
    ...AiPromptRules
  }
  content
  contentState
  document {
    id
  }
  initiative {
    id
  }
  issue {
    id
  }
  updatedAt
  projectMilestone {
    id
  }
  project {
    id
  }
  restoredAt
  archivedAt
  createdAt
  id
}
fragment AiPromptRules on AiPromptRules {
  __typename
  updatedAt
  archivedAt
  createdAt
  id
  updatedBy {
    id
  }
}`);
const Project_DocumentContent_AiPromptRulesDocument = new TypedDocumentString(`
    query project_documentContent_aiPromptRules($id: String!) {
  project(id: $id) {
    documentContent {
      aiPromptRules {
        ...AiPromptRules
      }
    }
  }
}
    fragment AiPromptRules on AiPromptRules {
  __typename
  updatedAt
  archivedAt
  createdAt
  id
  updatedBy {
    id
  }
}`);
const Project_DocumentsDocument = new TypedDocumentString(`
    query project_documents($id: String!, $after: String, $before: String, $filter: DocumentFilter, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  project(id: $id) {
    documents(
      after: $after
      before: $before
      filter: $filter
      first: $first
      includeArchived: $includeArchived
      last: $last
      orderBy: $orderBy
    ) {
      ...DocumentConnection
    }
  }
}
    fragment Document on Document {
  __typename
  trashed
  documentContentId
  url
  color
  title
  slugId
  content
  icon
  initiative {
    id
  }
  issue {
    id
  }
  lastAppliedTemplate {
    id
  }
  updatedAt
  sortOrder
  project {
    id
  }
  hiddenAt
  archivedAt
  createdAt
  id
  creator {
    id
  }
  updatedBy {
    id
  }
}
fragment DocumentConnection on DocumentConnection {
  __typename
  nodes {
    ...Document
  }
  pageInfo {
    ...PageInfo
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`);
const Project_ExternalLinksDocument = new TypedDocumentString(`
    query project_externalLinks($id: String!, $after: String, $before: String, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  project(id: $id) {
    externalLinks(
      after: $after
      before: $before
      first: $first
      includeArchived: $includeArchived
      last: $last
      orderBy: $orderBy
    ) {
      ...EntityExternalLinkConnection
    }
  }
}
    fragment EntityExternalLink on EntityExternalLink {
  __typename
  initiative {
    id
  }
  updatedAt
  url
  label
  sortOrder
  archivedAt
  createdAt
  id
  creator {
    id
  }
}
fragment EntityExternalLinkConnection on EntityExternalLinkConnection {
  __typename
  nodes {
    ...EntityExternalLink
  }
  pageInfo {
    ...PageInfo
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`);
const Project_HistoryDocument = new TypedDocumentString(`
    query project_history($id: String!, $after: String, $before: String, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  project(id: $id) {
    history(
      after: $after
      before: $before
      first: $first
      includeArchived: $includeArchived
      last: $last
      orderBy: $orderBy
    ) {
      ...ProjectHistoryConnection
    }
  }
}
    fragment ProjectHistory on ProjectHistory {
  __typename
  entries
  updatedAt
  project {
    id
  }
  archivedAt
  createdAt
  id
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}
fragment ProjectHistoryConnection on ProjectHistoryConnection {
  __typename
  nodes {
    ...ProjectHistory
  }
  pageInfo {
    ...PageInfo
  }
}`);
const Project_InitiativeToProjectsDocument = new TypedDocumentString(`
    query project_initiativeToProjects($id: String!, $after: String, $before: String, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  project(id: $id) {
    initiativeToProjects(
      after: $after
      before: $before
      first: $first
      includeArchived: $includeArchived
      last: $last
      orderBy: $orderBy
    ) {
      ...InitiativeToProjectConnection
    }
  }
}
    fragment InitiativeToProject on InitiativeToProject {
  __typename
  initiative {
    id
  }
  updatedAt
  project {
    id
  }
  sortOrder
  archivedAt
  createdAt
  id
}
fragment InitiativeToProjectConnection on InitiativeToProjectConnection {
  __typename
  nodes {
    ...InitiativeToProject
  }
  pageInfo {
    ...PageInfo
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`);
const Project_InitiativesDocument = new TypedDocumentString(`
    query project_initiatives($id: String!, $after: String, $before: String, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  project(id: $id) {
    initiatives(
      after: $after
      before: $before
      first: $first
      includeArchived: $includeArchived
      last: $last
      orderBy: $orderBy
    ) {
      ...InitiativeConnection
    }
  }
}
    fragment DocumentContent on DocumentContent {
  __typename
  aiPromptRules {
    ...AiPromptRules
  }
  content
  contentState
  document {
    id
  }
  initiative {
    id
  }
  issue {
    id
  }
  updatedAt
  projectMilestone {
    id
  }
  project {
    id
  }
  restoredAt
  archivedAt
  createdAt
  id
}
fragment AiPromptRules on AiPromptRules {
  __typename
  updatedAt
  archivedAt
  createdAt
  id
  updatedBy {
    id
  }
}
fragment Initiative on Initiative {
  __typename
  trashed
  url
  parentInitiative {
    id
  }
  integrationsSettings {
    id
  }
  documentContent {
    ...DocumentContent
  }
  updateRemindersDay
  description
  targetDate
  updateReminderFrequency
  health
  updateRemindersHour
  icon
  color
  content
  slugId
  lastUpdate {
    id
  }
  updatedAt
  updateReminderFrequencyInWeeks
  name
  targetDateResolution
  frequencyResolution
  sortOrder
  status
  archivedAt
  createdAt
  healthUpdatedAt
  startedAt
  completedAt
  id
  creator {
    id
  }
  owner {
    id
  }
}
fragment InitiativeConnection on InitiativeConnection {
  __typename
  nodes {
    ...Initiative
  }
  pageInfo {
    ...PageInfo
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`);
const Project_InverseRelationsDocument = new TypedDocumentString(`
    query project_inverseRelations($id: String!, $after: String, $before: String, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  project(id: $id) {
    inverseRelations(
      after: $after
      before: $before
      first: $first
      includeArchived: $includeArchived
      last: $last
      orderBy: $orderBy
    ) {
      ...ProjectRelationConnection
    }
  }
}
    fragment ProjectRelation on ProjectRelation {
  __typename
  updatedAt
  user {
    id
  }
  projectMilestone {
    id
  }
  relatedProjectMilestone {
    id
  }
  project {
    id
  }
  relatedProject {
    id
  }
  type
  archivedAt
  createdAt
  anchorType
  relatedAnchorType
  id
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}
fragment ProjectRelationConnection on ProjectRelationConnection {
  __typename
  nodes {
    ...ProjectRelation
  }
  pageInfo {
    ...PageInfo
  }
}`);
const Project_IssuesDocument = new TypedDocumentString(`
    query project_issues($id: String!, $after: String, $before: String, $filter: IssueFilter, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  project(id: $id) {
    issues(
      after: $after
      before: $before
      filter: $filter
      first: $first
      includeArchived: $includeArchived
      last: $last
      orderBy: $orderBy
    ) {
      ...IssueConnection
    }
  }
}
    fragment ActorBot on ActorBot {
  __typename
  avatarUrl
  name
  userDisplayName
  subType
  type
  id
}
fragment Reaction on Reaction {
  __typename
  emoji
  comment {
    id
  }
  externalUser {
    id
  }
  initiativeUpdate {
    id
  }
  issue {
    id
  }
  updatedAt
  projectUpdate {
    id
  }
  archivedAt
  createdAt
  id
  user {
    id
  }
}
fragment Issue on Issue {
  __typename
  trashed
  reactionData
  labelIds
  integrationSourceType
  url
  identifier
  priorityLabel
  previousIdentifiers
  reactions {
    ...Reaction
  }
  customerTicketCount
  branchName
  delegate {
    id
  }
  botActor {
    ...ActorBot
  }
  sourceComment {
    id
  }
  cycle {
    id
  }
  dueDate
  estimate
  syncedWith {
    ...ExternalEntityInfo
  }
  externalUserCreator {
    id
  }
  asksExternalUserRequester {
    id
  }
  asksRequester {
    id
  }
  description
  title
  number
  lastAppliedTemplate {
    id
  }
  updatedAt
  boardOrder
  prioritySortOrder
  sortOrder
  subIssueSortOrder
  parent {
    id
  }
  priority
  project {
    id
  }
  projectMilestone {
    id
  }
  recurringIssueTemplate {
    id
  }
  team {
    id
  }
  archivedAt
  createdAt
  startedTriageAt
  triagedAt
  addedToCycleAt
  addedToProjectAt
  addedToTeamAt
  autoArchivedAt
  autoClosedAt
  canceledAt
  completedAt
  startedAt
  slaStartedAt
  slaBreachesAt
  slaHighRiskAt
  slaMediumRiskAt
  snoozedUntilAt
  slaType
  id
  assignee {
    id
  }
  creator {
    id
  }
  snoozedBy {
    id
  }
  favorite {
    id
  }
  state {
    id
  }
}
fragment ExternalEntityInfo on ExternalEntityInfo {
  __typename
  metadata {
    ... on ExternalEntityInfoGithubMetadata {
      ...ExternalEntityInfoGithubMetadata
    }
    ... on ExternalEntityInfoJiraMetadata {
      ...ExternalEntityInfoJiraMetadata
    }
    ... on ExternalEntitySlackMetadata {
      ...ExternalEntitySlackMetadata
    }
  }
  id
  service
}
fragment ExternalEntityInfoGithubMetadata on ExternalEntityInfoGithubMetadata {
  __typename
  number
  owner
  repo
}
fragment ExternalEntityInfoJiraMetadata on ExternalEntityInfoJiraMetadata {
  __typename
  issueTypeId
  projectId
  issueKey
}
fragment ExternalEntitySlackMetadata on ExternalEntitySlackMetadata {
  __typename
  messageUrl
  channelId
  channelName
  isFromSlack
}
fragment IssueConnection on IssueConnection {
  __typename
  nodes {
    ...Issue
  }
  pageInfo {
    ...PageInfo
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`);
const Project_LabelsDocument = new TypedDocumentString(`
    query project_labels($id: String!, $after: String, $before: String, $filter: ProjectLabelFilter, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  project(id: $id) {
    labels(
      after: $after
      before: $before
      filter: $filter
      first: $first
      includeArchived: $includeArchived
      last: $last
      orderBy: $orderBy
    ) {
      ...ProjectLabelConnection
    }
  }
}
    fragment ProjectLabel on ProjectLabel {
  __typename
  lastAppliedAt
  color
  description
  name
  updatedAt
  parent {
    id
  }
  archivedAt
  createdAt
  id
  creator {
    id
  }
  retiredBy {
    id
  }
  isGroup
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}
fragment ProjectLabelConnection on ProjectLabelConnection {
  __typename
  nodes {
    ...ProjectLabel
  }
  pageInfo {
    ...PageInfo
  }
}`);
const Project_MembersDocument = new TypedDocumentString(`
    query project_members($id: String!, $after: String, $before: String, $filter: UserFilter, $first: Int, $includeArchived: Boolean, $includeDisabled: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  project(id: $id) {
    members(
      after: $after
      before: $before
      filter: $filter
      first: $first
      includeArchived: $includeArchived
      includeDisabled: $includeDisabled
      last: $last
      orderBy: $orderBy
    ) {
      ...UserConnection
    }
  }
}
    fragment User on User {
  __typename
  statusUntilAt
  description
  avatarUrl
  createdIssueCount
  disableReason
  avatarBackgroundColor
  statusEmoji
  initials
  statusLabel
  updatedAt
  lastSeen
  timezone
  archivedAt
  createdAt
  id
  gitHubUserId
  displayName
  email
  name
  url
  active
  guest
  app
  admin
  owner
  isAssignable
  isMentionable
  isMe
  supportsAgentSessions
  canAccessAnyPublicTeam
  calendarHash
  inviteHash
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}
fragment UserConnection on UserConnection {
  __typename
  nodes {
    ...User
  }
  pageInfo {
    ...PageInfo
  }
}`);
const Project_NeedsDocument = new TypedDocumentString(`
    query project_needs($id: String!, $after: String, $before: String, $filter: CustomerNeedFilter, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  project(id: $id) {
    needs(
      after: $after
      before: $before
      filter: $filter
      first: $first
      includeArchived: $includeArchived
      last: $last
      orderBy: $orderBy
    ) {
      ...CustomerNeedConnection
    }
  }
}
    fragment CustomerNeed on CustomerNeed {
  __typename
  url
  attachment {
    id
  }
  comment {
    id
  }
  creator {
    id
  }
  customer {
    id
  }
  originalIssue {
    id
  }
  issue {
    id
  }
  updatedAt
  body
  projectAttachment {
    ...ProjectAttachment
  }
  project {
    id
  }
  archivedAt
  createdAt
  id
  priority
}
fragment ProjectAttachment on ProjectAttachment {
  __typename
  sourceType
  metadata
  source
  subtitle
  creator {
    id
  }
  updatedAt
  archivedAt
  createdAt
  id
  title
  url
}
fragment CustomerNeedConnection on CustomerNeedConnection {
  __typename
  nodes {
    ...CustomerNeed
  }
  pageInfo {
    ...PageInfo
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`);
const Project_ProjectMilestonesDocument = new TypedDocumentString(`
    query project_projectMilestones($id: String!, $after: String, $before: String, $filter: ProjectMilestoneFilter, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  project(id: $id) {
    projectMilestones(
      after: $after
      before: $before
      filter: $filter
      first: $first
      includeArchived: $includeArchived
      last: $last
      orderBy: $orderBy
    ) {
      ...ProjectMilestoneConnection
    }
  }
}
    fragment DocumentContent on DocumentContent {
  __typename
  aiPromptRules {
    ...AiPromptRules
  }
  content
  contentState
  document {
    id
  }
  initiative {
    id
  }
  issue {
    id
  }
  updatedAt
  projectMilestone {
    id
  }
  project {
    id
  }
  restoredAt
  archivedAt
  createdAt
  id
}
fragment ProjectMilestone on ProjectMilestone {
  __typename
  documentContent {
    ...DocumentContent
  }
  updatedAt
  name
  sortOrder
  targetDate
  progress
  description
  project {
    id
  }
  status
  archivedAt
  createdAt
  id
}
fragment AiPromptRules on AiPromptRules {
  __typename
  updatedAt
  archivedAt
  createdAt
  id
  updatedBy {
    id
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}
fragment ProjectMilestoneConnection on ProjectMilestoneConnection {
  __typename
  nodes {
    ...ProjectMilestone
  }
  pageInfo {
    ...PageInfo
  }
}`);
const Project_ProjectUpdatesDocument = new TypedDocumentString(`
    query project_projectUpdates($id: String!, $after: String, $before: String, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  project(id: $id) {
    projectUpdates(
      after: $after
      before: $before
      first: $first
      includeArchived: $includeArchived
      last: $last
      orderBy: $orderBy
    ) {
      ...ProjectUpdateConnection
    }
  }
}
    fragment Reaction on Reaction {
  __typename
  emoji
  comment {
    id
  }
  externalUser {
    id
  }
  initiativeUpdate {
    id
  }
  issue {
    id
  }
  updatedAt
  projectUpdate {
    id
  }
  archivedAt
  createdAt
  id
  user {
    id
  }
}
fragment ProjectUpdate on ProjectUpdate {
  __typename
  reactionData
  commentCount
  reactions {
    ...Reaction
  }
  url
  diffMarkdown
  diff
  health
  updatedAt
  project {
    id
  }
  archivedAt
  createdAt
  editedAt
  id
  body
  slugId
  user {
    id
  }
  isDiffHidden
  isStale
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}
fragment ProjectUpdateConnection on ProjectUpdateConnection {
  __typename
  nodes {
    ...ProjectUpdate
  }
  pageInfo {
    ...PageInfo
  }
}`);
const Project_RelationsDocument = new TypedDocumentString(`
    query project_relations($id: String!, $after: String, $before: String, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  project(id: $id) {
    relations(
      after: $after
      before: $before
      first: $first
      includeArchived: $includeArchived
      last: $last
      orderBy: $orderBy
    ) {
      ...ProjectRelationConnection
    }
  }
}
    fragment ProjectRelation on ProjectRelation {
  __typename
  updatedAt
  user {
    id
  }
  projectMilestone {
    id
  }
  relatedProjectMilestone {
    id
  }
  project {
    id
  }
  relatedProject {
    id
  }
  type
  archivedAt
  createdAt
  anchorType
  relatedAnchorType
  id
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}
fragment ProjectRelationConnection on ProjectRelationConnection {
  __typename
  nodes {
    ...ProjectRelation
  }
  pageInfo {
    ...PageInfo
  }
}`);
const Project_TeamsDocument = new TypedDocumentString(`
    query project_teams($id: String!, $after: String, $before: String, $filter: TeamFilter, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  project(id: $id) {
    teams(
      after: $after
      before: $before
      filter: $filter
      first: $first
      includeArchived: $includeArchived
      last: $last
      orderBy: $orderBy
    ) {
      ...TeamConnection
    }
  }
}
    fragment Team on Team {
  __typename
  cycleIssueAutoAssignCompleted
  cycleLockToActive
  cycleIssueAutoAssignStarted
  cycleCalenderUrl
  upcomingCycleCount
  issueCount
  autoArchivePeriod
  autoClosePeriod
  securitySettings
  integrationsSettings {
    id
  }
  activeCycle {
    id
  }
  triageResponsibility {
    id
  }
  scimGroupName
  autoCloseStateId
  cycleCooldownTime
  cycleStartDay
  defaultTemplateForMembers {
    id
  }
  defaultTemplateForNonMembers {
    id
  }
  defaultProjectTemplate {
    id
  }
  defaultIssueState {
    id
  }
  cycleDuration
  icon
  defaultTemplateForMembersId
  defaultTemplateForNonMembersId
  issueEstimationType
  updatedAt
  displayName
  color
  description
  name
  key
  archivedAt
  createdAt
  timezone
  id
  mergeWorkflowState {
    id
  }
  draftWorkflowState {
    id
  }
  startWorkflowState {
    id
  }
  mergeableWorkflowState {
    id
  }
  reviewWorkflowState {
    id
  }
  markedAsDuplicateWorkflowState {
    id
  }
  triageIssueState {
    id
  }
  defaultIssueEstimate
  setIssueSortOrderOnStateChange
  allMembersCanJoin
  requirePriorityToLeaveTriage
  autoCloseChildIssues
  autoCloseParentIssues
  scimManaged
  private
  inheritIssueEstimation
  inheritWorkflowStatuses
  cyclesEnabled
  issueEstimationExtended
  issueEstimationAllowZero
  aiDiscussionSummariesEnabled
  aiThreadSummariesEnabled
  groupIssueHistory
  slackIssueComments
  slackNewIssue
  slackIssueStatuses
  triageEnabled
  inviteHash
  issueOrderingNoPriorityFirst
  issueSortOrderDefaultToBottom
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}
fragment TeamConnection on TeamConnection {
  __typename
  nodes {
    ...Team
  }
  pageInfo {
    ...PageInfo
  }
}`);
const ProjectFilterSuggestionDocument = new TypedDocumentString(`
    query projectFilterSuggestion($prompt: String!) {
  projectFilterSuggestion(prompt: $prompt) {
    ...ProjectFilterSuggestionPayload
  }
}
    fragment ProjectFilterSuggestionPayload on ProjectFilterSuggestionPayload {
  __typename
  filter
  logId
}`);
const ProjectLabelDocument = new TypedDocumentString(`
    query projectLabel($id: String!) {
  projectLabel(id: $id) {
    ...ProjectLabel
  }
}
    fragment ProjectLabel on ProjectLabel {
  __typename
  lastAppliedAt
  color
  description
  name
  updatedAt
  parent {
    id
  }
  archivedAt
  createdAt
  id
  creator {
    id
  }
  retiredBy {
    id
  }
  isGroup
}`);
const ProjectLabel_ChildrenDocument = new TypedDocumentString(`
    query projectLabel_children($id: String!, $after: String, $before: String, $filter: ProjectLabelFilter, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  projectLabel(id: $id) {
    children(
      after: $after
      before: $before
      filter: $filter
      first: $first
      includeArchived: $includeArchived
      last: $last
      orderBy: $orderBy
    ) {
      ...ProjectLabelConnection
    }
  }
}
    fragment ProjectLabel on ProjectLabel {
  __typename
  lastAppliedAt
  color
  description
  name
  updatedAt
  parent {
    id
  }
  archivedAt
  createdAt
  id
  creator {
    id
  }
  retiredBy {
    id
  }
  isGroup
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}
fragment ProjectLabelConnection on ProjectLabelConnection {
  __typename
  nodes {
    ...ProjectLabel
  }
  pageInfo {
    ...PageInfo
  }
}`);
const ProjectLabel_ProjectsDocument = new TypedDocumentString(`
    query projectLabel_projects($id: String!, $after: String, $before: String, $filter: ProjectFilter, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy, $sort: [ProjectSortInput!]) {
  projectLabel(id: $id) {
    projects(
      after: $after
      before: $before
      filter: $filter
      first: $first
      includeArchived: $includeArchived
      last: $last
      orderBy: $orderBy
      sort: $sort
    ) {
      ...ProjectConnection
    }
  }
}
    fragment DocumentContent on DocumentContent {
  __typename
  aiPromptRules {
    ...AiPromptRules
  }
  content
  contentState
  document {
    id
  }
  initiative {
    id
  }
  issue {
    id
  }
  updatedAt
  projectMilestone {
    id
  }
  project {
    id
  }
  restoredAt
  archivedAt
  createdAt
  id
}
fragment Project on Project {
  __typename
  trashed
  labelIds
  url
  integrationsSettings {
    id
  }
  documentContent {
    ...DocumentContent
  }
  updateRemindersDay
  targetDate
  startDate
  updateReminderFrequency
  health
  updateRemindersHour
  icon
  lastUpdate {
    id
  }
  lastAppliedTemplate {
    id
  }
  updatedAt
  updateReminderFrequencyInWeeks
  completedScopeHistory
  completedIssueCountHistory
  inProgressScopeHistory
  progress
  scope
  priorityLabel
  priority
  lead {
    id
  }
  convertedFromIssue {
    id
  }
  color
  content
  description
  name
  slugId
  targetDateResolution
  startDateResolution
  frequencyResolution
  prioritySortOrder
  sortOrder
  status {
    id
  }
  archivedAt
  createdAt
  healthUpdatedAt
  autoArchivedAt
  canceledAt
  completedAt
  startedAt
  projectUpdateRemindersPausedUntilAt
  scopeHistory
  issueCountHistory
  id
  creator {
    id
  }
  favorite {
    id
  }
  slackIssueComments
  slackNewIssue
  slackIssueStatuses
  state
}
fragment AiPromptRules on AiPromptRules {
  __typename
  updatedAt
  archivedAt
  createdAt
  id
  updatedBy {
    id
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}
fragment ProjectConnection on ProjectConnection {
  __typename
  nodes {
    ...Project
  }
  pageInfo {
    ...PageInfo
  }
}`);
const ProjectLabelsDocument = new TypedDocumentString(`
    query projectLabels($after: String, $before: String, $filter: ProjectLabelFilter, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  projectLabels(
    after: $after
    before: $before
    filter: $filter
    first: $first
    includeArchived: $includeArchived
    last: $last
    orderBy: $orderBy
  ) {
    ...ProjectLabelConnection
  }
}
    fragment ProjectLabel on ProjectLabel {
  __typename
  lastAppliedAt
  color
  description
  name
  updatedAt
  parent {
    id
  }
  archivedAt
  createdAt
  id
  creator {
    id
  }
  retiredBy {
    id
  }
  isGroup
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}
fragment ProjectLabelConnection on ProjectLabelConnection {
  __typename
  nodes {
    ...ProjectLabel
  }
  pageInfo {
    ...PageInfo
  }
}`);
const ProjectMilestoneDocument = new TypedDocumentString(`
    query projectMilestone($id: String!) {
  projectMilestone(id: $id) {
    ...ProjectMilestone
  }
}
    fragment DocumentContent on DocumentContent {
  __typename
  aiPromptRules {
    ...AiPromptRules
  }
  content
  contentState
  document {
    id
  }
  initiative {
    id
  }
  issue {
    id
  }
  updatedAt
  projectMilestone {
    id
  }
  project {
    id
  }
  restoredAt
  archivedAt
  createdAt
  id
}
fragment ProjectMilestone on ProjectMilestone {
  __typename
  documentContent {
    ...DocumentContent
  }
  updatedAt
  name
  sortOrder
  targetDate
  progress
  description
  project {
    id
  }
  status
  archivedAt
  createdAt
  id
}
fragment AiPromptRules on AiPromptRules {
  __typename
  updatedAt
  archivedAt
  createdAt
  id
  updatedBy {
    id
  }
}`);
const ProjectMilestone_DocumentContentDocument = new TypedDocumentString(`
    query projectMilestone_documentContent($id: String!) {
  projectMilestone(id: $id) {
    documentContent {
      ...DocumentContent
    }
  }
}
    fragment DocumentContent on DocumentContent {
  __typename
  aiPromptRules {
    ...AiPromptRules
  }
  content
  contentState
  document {
    id
  }
  initiative {
    id
  }
  issue {
    id
  }
  updatedAt
  projectMilestone {
    id
  }
  project {
    id
  }
  restoredAt
  archivedAt
  createdAt
  id
}
fragment AiPromptRules on AiPromptRules {
  __typename
  updatedAt
  archivedAt
  createdAt
  id
  updatedBy {
    id
  }
}`);
const ProjectMilestone_DocumentContent_AiPromptRulesDocument = new TypedDocumentString(`
    query projectMilestone_documentContent_aiPromptRules($id: String!) {
  projectMilestone(id: $id) {
    documentContent {
      aiPromptRules {
        ...AiPromptRules
      }
    }
  }
}
    fragment AiPromptRules on AiPromptRules {
  __typename
  updatedAt
  archivedAt
  createdAt
  id
  updatedBy {
    id
  }
}`);
const ProjectMilestone_IssuesDocument = new TypedDocumentString(`
    query projectMilestone_issues($id: String!, $after: String, $before: String, $filter: IssueFilter, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  projectMilestone(id: $id) {
    issues(
      after: $after
      before: $before
      filter: $filter
      first: $first
      includeArchived: $includeArchived
      last: $last
      orderBy: $orderBy
    ) {
      ...IssueConnection
    }
  }
}
    fragment ActorBot on ActorBot {
  __typename
  avatarUrl
  name
  userDisplayName
  subType
  type
  id
}
fragment Reaction on Reaction {
  __typename
  emoji
  comment {
    id
  }
  externalUser {
    id
  }
  initiativeUpdate {
    id
  }
  issue {
    id
  }
  updatedAt
  projectUpdate {
    id
  }
  archivedAt
  createdAt
  id
  user {
    id
  }
}
fragment Issue on Issue {
  __typename
  trashed
  reactionData
  labelIds
  integrationSourceType
  url
  identifier
  priorityLabel
  previousIdentifiers
  reactions {
    ...Reaction
  }
  customerTicketCount
  branchName
  delegate {
    id
  }
  botActor {
    ...ActorBot
  }
  sourceComment {
    id
  }
  cycle {
    id
  }
  dueDate
  estimate
  syncedWith {
    ...ExternalEntityInfo
  }
  externalUserCreator {
    id
  }
  asksExternalUserRequester {
    id
  }
  asksRequester {
    id
  }
  description
  title
  number
  lastAppliedTemplate {
    id
  }
  updatedAt
  boardOrder
  prioritySortOrder
  sortOrder
  subIssueSortOrder
  parent {
    id
  }
  priority
  project {
    id
  }
  projectMilestone {
    id
  }
  recurringIssueTemplate {
    id
  }
  team {
    id
  }
  archivedAt
  createdAt
  startedTriageAt
  triagedAt
  addedToCycleAt
  addedToProjectAt
  addedToTeamAt
  autoArchivedAt
  autoClosedAt
  canceledAt
  completedAt
  startedAt
  slaStartedAt
  slaBreachesAt
  slaHighRiskAt
  slaMediumRiskAt
  snoozedUntilAt
  slaType
  id
  assignee {
    id
  }
  creator {
    id
  }
  snoozedBy {
    id
  }
  favorite {
    id
  }
  state {
    id
  }
}
fragment ExternalEntityInfo on ExternalEntityInfo {
  __typename
  metadata {
    ... on ExternalEntityInfoGithubMetadata {
      ...ExternalEntityInfoGithubMetadata
    }
    ... on ExternalEntityInfoJiraMetadata {
      ...ExternalEntityInfoJiraMetadata
    }
    ... on ExternalEntitySlackMetadata {
      ...ExternalEntitySlackMetadata
    }
  }
  id
  service
}
fragment ExternalEntityInfoGithubMetadata on ExternalEntityInfoGithubMetadata {
  __typename
  number
  owner
  repo
}
fragment ExternalEntityInfoJiraMetadata on ExternalEntityInfoJiraMetadata {
  __typename
  issueTypeId
  projectId
  issueKey
}
fragment ExternalEntitySlackMetadata on ExternalEntitySlackMetadata {
  __typename
  messageUrl
  channelId
  channelName
  isFromSlack
}
fragment IssueConnection on IssueConnection {
  __typename
  nodes {
    ...Issue
  }
  pageInfo {
    ...PageInfo
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`);
const ProjectMilestonesDocument = new TypedDocumentString(`
    query projectMilestones($after: String, $before: String, $filter: ProjectMilestoneFilter, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  projectMilestones(
    after: $after
    before: $before
    filter: $filter
    first: $first
    includeArchived: $includeArchived
    last: $last
    orderBy: $orderBy
  ) {
    ...ProjectMilestoneConnection
  }
}
    fragment DocumentContent on DocumentContent {
  __typename
  aiPromptRules {
    ...AiPromptRules
  }
  content
  contentState
  document {
    id
  }
  initiative {
    id
  }
  issue {
    id
  }
  updatedAt
  projectMilestone {
    id
  }
  project {
    id
  }
  restoredAt
  archivedAt
  createdAt
  id
}
fragment ProjectMilestone on ProjectMilestone {
  __typename
  documentContent {
    ...DocumentContent
  }
  updatedAt
  name
  sortOrder
  targetDate
  progress
  description
  project {
    id
  }
  status
  archivedAt
  createdAt
  id
}
fragment AiPromptRules on AiPromptRules {
  __typename
  updatedAt
  archivedAt
  createdAt
  id
  updatedBy {
    id
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}
fragment ProjectMilestoneConnection on ProjectMilestoneConnection {
  __typename
  nodes {
    ...ProjectMilestone
  }
  pageInfo {
    ...PageInfo
  }
}`);
const ProjectRelationDocument = new TypedDocumentString(`
    query projectRelation($id: String!) {
  projectRelation(id: $id) {
    ...ProjectRelation
  }
}
    fragment ProjectRelation on ProjectRelation {
  __typename
  updatedAt
  user {
    id
  }
  projectMilestone {
    id
  }
  relatedProjectMilestone {
    id
  }
  project {
    id
  }
  relatedProject {
    id
  }
  type
  archivedAt
  createdAt
  anchorType
  relatedAnchorType
  id
}`);
const ProjectRelationsDocument = new TypedDocumentString(`
    query projectRelations($after: String, $before: String, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  projectRelations(
    after: $after
    before: $before
    first: $first
    includeArchived: $includeArchived
    last: $last
    orderBy: $orderBy
  ) {
    ...ProjectRelationConnection
  }
}
    fragment ProjectRelation on ProjectRelation {
  __typename
  updatedAt
  user {
    id
  }
  projectMilestone {
    id
  }
  relatedProjectMilestone {
    id
  }
  project {
    id
  }
  relatedProject {
    id
  }
  type
  archivedAt
  createdAt
  anchorType
  relatedAnchorType
  id
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}
fragment ProjectRelationConnection on ProjectRelationConnection {
  __typename
  nodes {
    ...ProjectRelation
  }
  pageInfo {
    ...PageInfo
  }
}`);
const ProjectStatusDocument = new TypedDocumentString(`
    query projectStatus($id: String!) {
  projectStatus(id: $id) {
    ...ProjectStatus
  }
}
    fragment ProjectStatus on ProjectStatus {
  __typename
  description
  color
  updatedAt
  name
  position
  archivedAt
  createdAt
  type
  id
  indefinite
}`);
const ProjectStatusesDocument = new TypedDocumentString(`
    query projectStatuses($after: String, $before: String, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  projectStatuses(
    after: $after
    before: $before
    first: $first
    includeArchived: $includeArchived
    last: $last
    orderBy: $orderBy
  ) {
    ...ProjectStatusConnection
  }
}
    fragment ProjectStatus on ProjectStatus {
  __typename
  description
  color
  updatedAt
  name
  position
  archivedAt
  createdAt
  type
  id
  indefinite
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}
fragment ProjectStatusConnection on ProjectStatusConnection {
  __typename
  nodes {
    ...ProjectStatus
  }
  pageInfo {
    ...PageInfo
  }
}`);
const ProjectUpdateDocument = new TypedDocumentString(`
    query projectUpdate($id: String!) {
  projectUpdate(id: $id) {
    ...ProjectUpdate
  }
}
    fragment Reaction on Reaction {
  __typename
  emoji
  comment {
    id
  }
  externalUser {
    id
  }
  initiativeUpdate {
    id
  }
  issue {
    id
  }
  updatedAt
  projectUpdate {
    id
  }
  archivedAt
  createdAt
  id
  user {
    id
  }
}
fragment ProjectUpdate on ProjectUpdate {
  __typename
  reactionData
  commentCount
  reactions {
    ...Reaction
  }
  url
  diffMarkdown
  diff
  health
  updatedAt
  project {
    id
  }
  archivedAt
  createdAt
  editedAt
  id
  body
  slugId
  user {
    id
  }
  isDiffHidden
  isStale
}`);
const ProjectUpdate_CommentsDocument = new TypedDocumentString(`
    query projectUpdate_comments($id: String!, $after: String, $before: String, $filter: CommentFilter, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  projectUpdate(id: $id) {
    comments(
      after: $after
      before: $before
      filter: $filter
      first: $first
      includeArchived: $includeArchived
      last: $last
      orderBy: $orderBy
    ) {
      ...CommentConnection
    }
  }
}
    fragment ActorBot on ActorBot {
  __typename
  avatarUrl
  name
  userDisplayName
  subType
  type
  id
}
fragment Comment on Comment {
  __typename
  agentSession {
    id
  }
  url
  reactionData
  reactions {
    ...Reaction
  }
  resolvingCommentId
  documentContentId
  initiativeUpdateId
  issueId
  parentId
  projectUpdateId
  botActor {
    ...ActorBot
  }
  body
  resolvingComment {
    id
  }
  documentContent {
    ...DocumentContent
  }
  syncedWith {
    ...ExternalEntityInfo
  }
  externalThread {
    ...SyncedExternalThread
  }
  externalUser {
    id
  }
  initiativeUpdate {
    id
  }
  issue {
    id
  }
  updatedAt
  parent {
    id
  }
  projectUpdate {
    id
  }
  quotedText
  archivedAt
  createdAt
  resolvedAt
  editedAt
  id
  resolvingUser {
    id
  }
  user {
    id
  }
}
fragment SyncedExternalThread on SyncedExternalThread {
  __typename
  name
  displayName
  url
  subType
  type
  isPersonalIntegrationRequired
  isPersonalIntegrationConnected
  isConnected
  id
}
fragment DocumentContent on DocumentContent {
  __typename
  aiPromptRules {
    ...AiPromptRules
  }
  content
  contentState
  document {
    id
  }
  initiative {
    id
  }
  issue {
    id
  }
  updatedAt
  projectMilestone {
    id
  }
  project {
    id
  }
  restoredAt
  archivedAt
  createdAt
  id
}
fragment Reaction on Reaction {
  __typename
  emoji
  comment {
    id
  }
  externalUser {
    id
  }
  initiativeUpdate {
    id
  }
  issue {
    id
  }
  updatedAt
  projectUpdate {
    id
  }
  archivedAt
  createdAt
  id
  user {
    id
  }
}
fragment AiPromptRules on AiPromptRules {
  __typename
  updatedAt
  archivedAt
  createdAt
  id
  updatedBy {
    id
  }
}
fragment ExternalEntityInfo on ExternalEntityInfo {
  __typename
  metadata {
    ... on ExternalEntityInfoGithubMetadata {
      ...ExternalEntityInfoGithubMetadata
    }
    ... on ExternalEntityInfoJiraMetadata {
      ...ExternalEntityInfoJiraMetadata
    }
    ... on ExternalEntitySlackMetadata {
      ...ExternalEntitySlackMetadata
    }
  }
  id
  service
}
fragment ExternalEntityInfoGithubMetadata on ExternalEntityInfoGithubMetadata {
  __typename
  number
  owner
  repo
}
fragment ExternalEntityInfoJiraMetadata on ExternalEntityInfoJiraMetadata {
  __typename
  issueTypeId
  projectId
  issueKey
}
fragment ExternalEntitySlackMetadata on ExternalEntitySlackMetadata {
  __typename
  messageUrl
  channelId
  channelName
  isFromSlack
}
fragment CommentConnection on CommentConnection {
  __typename
  nodes {
    ...Comment
  }
  pageInfo {
    ...PageInfo
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`);
const ProjectUpdatesDocument = new TypedDocumentString(`
    query projectUpdates($after: String, $before: String, $filter: ProjectUpdateFilter, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  projectUpdates(
    after: $after
    before: $before
    filter: $filter
    first: $first
    includeArchived: $includeArchived
    last: $last
    orderBy: $orderBy
  ) {
    ...ProjectUpdateConnection
  }
}
    fragment Reaction on Reaction {
  __typename
  emoji
  comment {
    id
  }
  externalUser {
    id
  }
  initiativeUpdate {
    id
  }
  issue {
    id
  }
  updatedAt
  projectUpdate {
    id
  }
  archivedAt
  createdAt
  id
  user {
    id
  }
}
fragment ProjectUpdate on ProjectUpdate {
  __typename
  reactionData
  commentCount
  reactions {
    ...Reaction
  }
  url
  diffMarkdown
  diff
  health
  updatedAt
  project {
    id
  }
  archivedAt
  createdAt
  editedAt
  id
  body
  slugId
  user {
    id
  }
  isDiffHidden
  isStale
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}
fragment ProjectUpdateConnection on ProjectUpdateConnection {
  __typename
  nodes {
    ...ProjectUpdate
  }
  pageInfo {
    ...PageInfo
  }
}`);
const ProjectsDocument = new TypedDocumentString(`
    query projects($after: String, $before: String, $filter: ProjectFilter, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy, $sort: [ProjectSortInput!]) {
  projects(
    after: $after
    before: $before
    filter: $filter
    first: $first
    includeArchived: $includeArchived
    last: $last
    orderBy: $orderBy
    sort: $sort
  ) {
    ...ProjectConnection
  }
}
    fragment DocumentContent on DocumentContent {
  __typename
  aiPromptRules {
    ...AiPromptRules
  }
  content
  contentState
  document {
    id
  }
  initiative {
    id
  }
  issue {
    id
  }
  updatedAt
  projectMilestone {
    id
  }
  project {
    id
  }
  restoredAt
  archivedAt
  createdAt
  id
}
fragment Project on Project {
  __typename
  trashed
  labelIds
  url
  integrationsSettings {
    id
  }
  documentContent {
    ...DocumentContent
  }
  updateRemindersDay
  targetDate
  startDate
  updateReminderFrequency
  health
  updateRemindersHour
  icon
  lastUpdate {
    id
  }
  lastAppliedTemplate {
    id
  }
  updatedAt
  updateReminderFrequencyInWeeks
  completedScopeHistory
  completedIssueCountHistory
  inProgressScopeHistory
  progress
  scope
  priorityLabel
  priority
  lead {
    id
  }
  convertedFromIssue {
    id
  }
  color
  content
  description
  name
  slugId
  targetDateResolution
  startDateResolution
  frequencyResolution
  prioritySortOrder
  sortOrder
  status {
    id
  }
  archivedAt
  createdAt
  healthUpdatedAt
  autoArchivedAt
  canceledAt
  completedAt
  startedAt
  projectUpdateRemindersPausedUntilAt
  scopeHistory
  issueCountHistory
  id
  creator {
    id
  }
  favorite {
    id
  }
  slackIssueComments
  slackNewIssue
  slackIssueStatuses
  state
}
fragment AiPromptRules on AiPromptRules {
  __typename
  updatedAt
  archivedAt
  createdAt
  id
  updatedBy {
    id
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}
fragment ProjectConnection on ProjectConnection {
  __typename
  nodes {
    ...Project
  }
  pageInfo {
    ...PageInfo
  }
}`);
const PushSubscriptionTestDocument = new TypedDocumentString(`
    query pushSubscriptionTest($sendStrategy: SendStrategy, $targetMobile: Boolean) {
  pushSubscriptionTest(sendStrategy: $sendStrategy, targetMobile: $targetMobile) {
    ...PushSubscriptionTestPayload
  }
}
    fragment PushSubscriptionTestPayload on PushSubscriptionTestPayload {
  __typename
  success
}`);
const RateLimitStatusDocument = new TypedDocumentString(`
    query rateLimitStatus {
  rateLimitStatus {
    ...RateLimitPayload
  }
}
    fragment RateLimitPayload on RateLimitPayload {
  __typename
  identifier
  kind
  limits {
    ...RateLimitResultPayload
  }
}
fragment RateLimitResultPayload on RateLimitResultPayload {
  __typename
  period
  remainingAmount
  requestedAmount
  reset
  allowedAmount
  type
}`);
const RoadmapDocument = new TypedDocumentString(`
    query roadmap($id: String!) {
  roadmap(id: $id) {
    ...Roadmap
  }
}
    fragment Roadmap on Roadmap {
  __typename
  url
  description
  updatedAt
  name
  color
  slugId
  sortOrder
  archivedAt
  createdAt
  id
  creator {
    id
  }
  owner {
    id
  }
}`);
const Roadmap_ProjectsDocument = new TypedDocumentString(`
    query roadmap_projects($id: String!, $after: String, $before: String, $filter: ProjectFilter, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  roadmap(id: $id) {
    projects(
      after: $after
      before: $before
      filter: $filter
      first: $first
      includeArchived: $includeArchived
      last: $last
      orderBy: $orderBy
    ) {
      ...ProjectConnection
    }
  }
}
    fragment DocumentContent on DocumentContent {
  __typename
  aiPromptRules {
    ...AiPromptRules
  }
  content
  contentState
  document {
    id
  }
  initiative {
    id
  }
  issue {
    id
  }
  updatedAt
  projectMilestone {
    id
  }
  project {
    id
  }
  restoredAt
  archivedAt
  createdAt
  id
}
fragment Project on Project {
  __typename
  trashed
  labelIds
  url
  integrationsSettings {
    id
  }
  documentContent {
    ...DocumentContent
  }
  updateRemindersDay
  targetDate
  startDate
  updateReminderFrequency
  health
  updateRemindersHour
  icon
  lastUpdate {
    id
  }
  lastAppliedTemplate {
    id
  }
  updatedAt
  updateReminderFrequencyInWeeks
  completedScopeHistory
  completedIssueCountHistory
  inProgressScopeHistory
  progress
  scope
  priorityLabel
  priority
  lead {
    id
  }
  convertedFromIssue {
    id
  }
  color
  content
  description
  name
  slugId
  targetDateResolution
  startDateResolution
  frequencyResolution
  prioritySortOrder
  sortOrder
  status {
    id
  }
  archivedAt
  createdAt
  healthUpdatedAt
  autoArchivedAt
  canceledAt
  completedAt
  startedAt
  projectUpdateRemindersPausedUntilAt
  scopeHistory
  issueCountHistory
  id
  creator {
    id
  }
  favorite {
    id
  }
  slackIssueComments
  slackNewIssue
  slackIssueStatuses
  state
}
fragment AiPromptRules on AiPromptRules {
  __typename
  updatedAt
  archivedAt
  createdAt
  id
  updatedBy {
    id
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}
fragment ProjectConnection on ProjectConnection {
  __typename
  nodes {
    ...Project
  }
  pageInfo {
    ...PageInfo
  }
}`);
const RoadmapToProjectDocument = new TypedDocumentString(`
    query roadmapToProject($id: String!) {
  roadmapToProject(id: $id) {
    ...RoadmapToProject
  }
}
    fragment RoadmapToProject on RoadmapToProject {
  __typename
  updatedAt
  project {
    id
  }
  roadmap {
    id
  }
  sortOrder
  archivedAt
  createdAt
  id
}`);
const RoadmapToProjectsDocument = new TypedDocumentString(`
    query roadmapToProjects($after: String, $before: String, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  roadmapToProjects(
    after: $after
    before: $before
    first: $first
    includeArchived: $includeArchived
    last: $last
    orderBy: $orderBy
  ) {
    ...RoadmapToProjectConnection
  }
}
    fragment RoadmapToProject on RoadmapToProject {
  __typename
  updatedAt
  project {
    id
  }
  roadmap {
    id
  }
  sortOrder
  archivedAt
  createdAt
  id
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}
fragment RoadmapToProjectConnection on RoadmapToProjectConnection {
  __typename
  nodes {
    ...RoadmapToProject
  }
  pageInfo {
    ...PageInfo
  }
}`);
const RoadmapsDocument = new TypedDocumentString(`
    query roadmaps($after: String, $before: String, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  roadmaps(
    after: $after
    before: $before
    first: $first
    includeArchived: $includeArchived
    last: $last
    orderBy: $orderBy
  ) {
    ...RoadmapConnection
  }
}
    fragment Roadmap on Roadmap {
  __typename
  url
  description
  updatedAt
  name
  color
  slugId
  sortOrder
  archivedAt
  createdAt
  id
  creator {
    id
  }
  owner {
    id
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}
fragment RoadmapConnection on RoadmapConnection {
  __typename
  nodes {
    ...Roadmap
  }
  pageInfo {
    ...PageInfo
  }
}`);
const SearchDocumentsDocument = new TypedDocumentString(`
    query searchDocuments($after: String, $before: String, $first: Int, $includeArchived: Boolean, $includeComments: Boolean, $last: Int, $orderBy: PaginationOrderBy, $teamId: String, $term: String!) {
  searchDocuments(
    after: $after
    before: $before
    first: $first
    includeArchived: $includeArchived
    includeComments: $includeComments
    last: $last
    orderBy: $orderBy
    teamId: $teamId
    term: $term
  ) {
    ...DocumentSearchPayload
  }
}
    fragment ArchiveResponse on ArchiveResponse {
  __typename
  archive
  totalCount
  databaseVersion
  includesDependencies
}
fragment DocumentSearchPayload on DocumentSearchPayload {
  __typename
  archivePayload {
    ...ArchiveResponse
  }
  totalCount
  nodes {
    ...DocumentSearchResult
  }
  pageInfo {
    ...PageInfo
  }
}
fragment DocumentSearchResult on DocumentSearchResult {
  __typename
  trashed
  metadata
  documentContentId
  url
  color
  title
  slugId
  content
  icon
  initiative {
    id
  }
  issue {
    id
  }
  lastAppliedTemplate {
    id
  }
  updatedAt
  sortOrder
  project {
    id
  }
  hiddenAt
  archivedAt
  createdAt
  id
  creator {
    id
  }
  updatedBy {
    id
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`);
const SearchDocuments_ArchivePayloadDocument = new TypedDocumentString(`
    query searchDocuments_archivePayload($after: String, $before: String, $first: Int, $includeArchived: Boolean, $includeComments: Boolean, $last: Int, $orderBy: PaginationOrderBy, $teamId: String, $term: String!) {
  searchDocuments(
    after: $after
    before: $before
    first: $first
    includeArchived: $includeArchived
    includeComments: $includeComments
    last: $last
    orderBy: $orderBy
    teamId: $teamId
    term: $term
  ) {
    archivePayload {
      ...ArchiveResponse
    }
  }
}
    fragment ArchiveResponse on ArchiveResponse {
  __typename
  archive
  totalCount
  databaseVersion
  includesDependencies
}`);
const SearchIssuesDocument = new TypedDocumentString(`
    query searchIssues($after: String, $before: String, $filter: IssueFilter, $first: Int, $includeArchived: Boolean, $includeComments: Boolean, $last: Int, $orderBy: PaginationOrderBy, $teamId: String, $term: String!) {
  searchIssues(
    after: $after
    before: $before
    filter: $filter
    first: $first
    includeArchived: $includeArchived
    includeComments: $includeComments
    last: $last
    orderBy: $orderBy
    teamId: $teamId
    term: $term
  ) {
    ...IssueSearchPayload
  }
}
    fragment ActorBot on ActorBot {
  __typename
  avatarUrl
  name
  userDisplayName
  subType
  type
  id
}
fragment Reaction on Reaction {
  __typename
  emoji
  comment {
    id
  }
  externalUser {
    id
  }
  initiativeUpdate {
    id
  }
  issue {
    id
  }
  updatedAt
  projectUpdate {
    id
  }
  archivedAt
  createdAt
  id
  user {
    id
  }
}
fragment ArchiveResponse on ArchiveResponse {
  __typename
  archive
  totalCount
  databaseVersion
  includesDependencies
}
fragment ExternalEntityInfo on ExternalEntityInfo {
  __typename
  metadata {
    ... on ExternalEntityInfoGithubMetadata {
      ...ExternalEntityInfoGithubMetadata
    }
    ... on ExternalEntityInfoJiraMetadata {
      ...ExternalEntityInfoJiraMetadata
    }
    ... on ExternalEntitySlackMetadata {
      ...ExternalEntitySlackMetadata
    }
  }
  id
  service
}
fragment ExternalEntityInfoGithubMetadata on ExternalEntityInfoGithubMetadata {
  __typename
  number
  owner
  repo
}
fragment ExternalEntityInfoJiraMetadata on ExternalEntityInfoJiraMetadata {
  __typename
  issueTypeId
  projectId
  issueKey
}
fragment ExternalEntitySlackMetadata on ExternalEntitySlackMetadata {
  __typename
  messageUrl
  channelId
  channelName
  isFromSlack
}
fragment IssueSearchPayload on IssueSearchPayload {
  __typename
  archivePayload {
    ...ArchiveResponse
  }
  totalCount
  nodes {
    ...IssueSearchResult
  }
  pageInfo {
    ...PageInfo
  }
}
fragment IssueSearchResult on IssueSearchResult {
  __typename
  trashed
  reactionData
  labelIds
  integrationSourceType
  url
  identifier
  priorityLabel
  metadata
  previousIdentifiers
  reactions {
    ...Reaction
  }
  customerTicketCount
  branchName
  delegate {
    id
  }
  botActor {
    ...ActorBot
  }
  sourceComment {
    id
  }
  cycle {
    id
  }
  dueDate
  estimate
  syncedWith {
    ...ExternalEntityInfo
  }
  externalUserCreator {
    id
  }
  asksExternalUserRequester {
    id
  }
  asksRequester {
    id
  }
  description
  title
  number
  lastAppliedTemplate {
    id
  }
  updatedAt
  boardOrder
  prioritySortOrder
  sortOrder
  subIssueSortOrder
  parent {
    id
  }
  priority
  project {
    id
  }
  projectMilestone {
    id
  }
  recurringIssueTemplate {
    id
  }
  team {
    id
  }
  archivedAt
  createdAt
  startedTriageAt
  triagedAt
  addedToCycleAt
  addedToProjectAt
  addedToTeamAt
  autoArchivedAt
  autoClosedAt
  canceledAt
  completedAt
  startedAt
  slaStartedAt
  slaBreachesAt
  slaHighRiskAt
  slaMediumRiskAt
  snoozedUntilAt
  slaType
  id
  assignee {
    id
  }
  creator {
    id
  }
  snoozedBy {
    id
  }
  favorite {
    id
  }
  state {
    id
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`);
const SearchIssues_ArchivePayloadDocument = new TypedDocumentString(`
    query searchIssues_archivePayload($after: String, $before: String, $filter: IssueFilter, $first: Int, $includeArchived: Boolean, $includeComments: Boolean, $last: Int, $orderBy: PaginationOrderBy, $teamId: String, $term: String!) {
  searchIssues(
    after: $after
    before: $before
    filter: $filter
    first: $first
    includeArchived: $includeArchived
    includeComments: $includeComments
    last: $last
    orderBy: $orderBy
    teamId: $teamId
    term: $term
  ) {
    archivePayload {
      ...ArchiveResponse
    }
  }
}
    fragment ArchiveResponse on ArchiveResponse {
  __typename
  archive
  totalCount
  databaseVersion
  includesDependencies
}`);
const SearchProjectsDocument = new TypedDocumentString(`
    query searchProjects($after: String, $before: String, $first: Int, $includeArchived: Boolean, $includeComments: Boolean, $last: Int, $orderBy: PaginationOrderBy, $teamId: String, $term: String!) {
  searchProjects(
    after: $after
    before: $before
    first: $first
    includeArchived: $includeArchived
    includeComments: $includeComments
    last: $last
    orderBy: $orderBy
    teamId: $teamId
    term: $term
  ) {
    ...ProjectSearchPayload
  }
}
    fragment DocumentContent on DocumentContent {
  __typename
  aiPromptRules {
    ...AiPromptRules
  }
  content
  contentState
  document {
    id
  }
  initiative {
    id
  }
  issue {
    id
  }
  updatedAt
  projectMilestone {
    id
  }
  project {
    id
  }
  restoredAt
  archivedAt
  createdAt
  id
}
fragment AiPromptRules on AiPromptRules {
  __typename
  updatedAt
  archivedAt
  createdAt
  id
  updatedBy {
    id
  }
}
fragment ArchiveResponse on ArchiveResponse {
  __typename
  archive
  totalCount
  databaseVersion
  includesDependencies
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}
fragment ProjectSearchPayload on ProjectSearchPayload {
  __typename
  archivePayload {
    ...ArchiveResponse
  }
  totalCount
  nodes {
    ...ProjectSearchResult
  }
  pageInfo {
    ...PageInfo
  }
}
fragment ProjectSearchResult on ProjectSearchResult {
  __typename
  trashed
  labelIds
  metadata
  url
  integrationsSettings {
    id
  }
  documentContent {
    ...DocumentContent
  }
  updateRemindersDay
  targetDate
  startDate
  updateReminderFrequency
  health
  updateRemindersHour
  icon
  lastUpdate {
    id
  }
  lastAppliedTemplate {
    id
  }
  updatedAt
  updateReminderFrequencyInWeeks
  completedScopeHistory
  completedIssueCountHistory
  inProgressScopeHistory
  progress
  scope
  priorityLabel
  priority
  lead {
    id
  }
  convertedFromIssue {
    id
  }
  color
  content
  description
  name
  slugId
  targetDateResolution
  startDateResolution
  frequencyResolution
  prioritySortOrder
  sortOrder
  status {
    id
  }
  archivedAt
  createdAt
  healthUpdatedAt
  autoArchivedAt
  canceledAt
  completedAt
  startedAt
  projectUpdateRemindersPausedUntilAt
  scopeHistory
  issueCountHistory
  id
  creator {
    id
  }
  favorite {
    id
  }
  slackIssueComments
  slackNewIssue
  slackIssueStatuses
  state
}`);
const SearchProjects_ArchivePayloadDocument = new TypedDocumentString(`
    query searchProjects_archivePayload($after: String, $before: String, $first: Int, $includeArchived: Boolean, $includeComments: Boolean, $last: Int, $orderBy: PaginationOrderBy, $teamId: String, $term: String!) {
  searchProjects(
    after: $after
    before: $before
    first: $first
    includeArchived: $includeArchived
    includeComments: $includeComments
    last: $last
    orderBy: $orderBy
    teamId: $teamId
    term: $term
  ) {
    archivePayload {
      ...ArchiveResponse
    }
  }
}
    fragment ArchiveResponse on ArchiveResponse {
  __typename
  archive
  totalCount
  databaseVersion
  includesDependencies
}`);
const SemanticSearchDocument = new TypedDocumentString(`
    query semanticSearch($filters: SemanticSearchFilters, $includeArchived: Boolean, $maxResults: Int, $query: String!, $types: [SemanticSearchResultType!]) {
  semanticSearch(
    filters: $filters
    includeArchived: $includeArchived
    maxResults: $maxResults
    query: $query
    types: $types
  ) {
    ...SemanticSearchPayload
  }
}
    fragment SemanticSearchResult on SemanticSearchResult {
  __typename
  document {
    id
  }
  initiative {
    id
  }
  issue {
    id
  }
  project {
    id
  }
  type
  id
}
fragment SemanticSearchPayload on SemanticSearchPayload {
  __typename
  enabled
  results {
    ...SemanticSearchResult
  }
}`);
const SsoUrlFromEmailDocument = new TypedDocumentString(`
    query ssoUrlFromEmail($email: String!, $isDesktop: Boolean, $type: IdentityProviderType!) {
  ssoUrlFromEmail(email: $email, isDesktop: $isDesktop, type: $type) {
    ...SsoUrlFromEmailResponse
  }
}
    fragment SsoUrlFromEmailResponse on SsoUrlFromEmailResponse {
  __typename
  samlSsoUrl
  success
}`);
const TeamDocument = new TypedDocumentString(`
    query team($id: String!) {
  team(id: $id) {
    ...Team
  }
}
    fragment Team on Team {
  __typename
  cycleIssueAutoAssignCompleted
  cycleLockToActive
  cycleIssueAutoAssignStarted
  cycleCalenderUrl
  upcomingCycleCount
  issueCount
  autoArchivePeriod
  autoClosePeriod
  securitySettings
  integrationsSettings {
    id
  }
  activeCycle {
    id
  }
  triageResponsibility {
    id
  }
  scimGroupName
  autoCloseStateId
  cycleCooldownTime
  cycleStartDay
  defaultTemplateForMembers {
    id
  }
  defaultTemplateForNonMembers {
    id
  }
  defaultProjectTemplate {
    id
  }
  defaultIssueState {
    id
  }
  cycleDuration
  icon
  defaultTemplateForMembersId
  defaultTemplateForNonMembersId
  issueEstimationType
  updatedAt
  displayName
  color
  description
  name
  key
  archivedAt
  createdAt
  timezone
  id
  mergeWorkflowState {
    id
  }
  draftWorkflowState {
    id
  }
  startWorkflowState {
    id
  }
  mergeableWorkflowState {
    id
  }
  reviewWorkflowState {
    id
  }
  markedAsDuplicateWorkflowState {
    id
  }
  triageIssueState {
    id
  }
  defaultIssueEstimate
  setIssueSortOrderOnStateChange
  allMembersCanJoin
  requirePriorityToLeaveTriage
  autoCloseChildIssues
  autoCloseParentIssues
  scimManaged
  private
  inheritIssueEstimation
  inheritWorkflowStatuses
  cyclesEnabled
  issueEstimationExtended
  issueEstimationAllowZero
  aiDiscussionSummariesEnabled
  aiThreadSummariesEnabled
  groupIssueHistory
  slackIssueComments
  slackNewIssue
  slackIssueStatuses
  triageEnabled
  inviteHash
  issueOrderingNoPriorityFirst
  issueSortOrderDefaultToBottom
}`);
const Team_CyclesDocument = new TypedDocumentString(`
    query team_cycles($id: String!, $after: String, $before: String, $filter: CycleFilter, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  team(id: $id) {
    cycles(
      after: $after
      before: $before
      filter: $filter
      first: $first
      includeArchived: $includeArchived
      last: $last
      orderBy: $orderBy
    ) {
      ...CycleConnection
    }
  }
}
    fragment Cycle on Cycle {
  __typename
  completedAt
  name
  inheritedFrom {
    id
  }
  description
  endsAt
  updatedAt
  completedScopeHistory
  completedIssueCountHistory
  inProgressScopeHistory
  number
  progress
  startsAt
  team {
    id
  }
  autoArchivedAt
  archivedAt
  createdAt
  scopeHistory
  issueCountHistory
  id
  isActive
  isFuture
  isPast
  isNext
  isPrevious
}
fragment CycleConnection on CycleConnection {
  __typename
  nodes {
    ...Cycle
  }
  pageInfo {
    ...PageInfo
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`);
const Team_GitAutomationStatesDocument = new TypedDocumentString(`
    query team_gitAutomationStates($id: String!, $after: String, $before: String, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  team(id: $id) {
    gitAutomationStates(
      after: $after
      before: $before
      first: $first
      includeArchived: $includeArchived
      last: $last
      orderBy: $orderBy
    ) {
      ...GitAutomationStateConnection
    }
  }
}
    fragment GitAutomationTargetBranch on GitAutomationTargetBranch {
  __typename
  updatedAt
  branchPattern
  team {
    id
  }
  archivedAt
  createdAt
  id
  isRegex
}
fragment GitAutomationState on GitAutomationState {
  __typename
  state {
    id
  }
  event
  updatedAt
  targetBranch {
    ...GitAutomationTargetBranch
  }
  team {
    id
  }
  archivedAt
  createdAt
  id
  branchPattern
}
fragment GitAutomationStateConnection on GitAutomationStateConnection {
  __typename
  nodes {
    ...GitAutomationState
  }
  pageInfo {
    ...PageInfo
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`);
const Team_IssuesDocument = new TypedDocumentString(`
    query team_issues($id: String!, $after: String, $before: String, $filter: IssueFilter, $first: Int, $includeArchived: Boolean, $includeSubTeams: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  team(id: $id) {
    issues(
      after: $after
      before: $before
      filter: $filter
      first: $first
      includeArchived: $includeArchived
      includeSubTeams: $includeSubTeams
      last: $last
      orderBy: $orderBy
    ) {
      ...IssueConnection
    }
  }
}
    fragment ActorBot on ActorBot {
  __typename
  avatarUrl
  name
  userDisplayName
  subType
  type
  id
}
fragment Reaction on Reaction {
  __typename
  emoji
  comment {
    id
  }
  externalUser {
    id
  }
  initiativeUpdate {
    id
  }
  issue {
    id
  }
  updatedAt
  projectUpdate {
    id
  }
  archivedAt
  createdAt
  id
  user {
    id
  }
}
fragment Issue on Issue {
  __typename
  trashed
  reactionData
  labelIds
  integrationSourceType
  url
  identifier
  priorityLabel
  previousIdentifiers
  reactions {
    ...Reaction
  }
  customerTicketCount
  branchName
  delegate {
    id
  }
  botActor {
    ...ActorBot
  }
  sourceComment {
    id
  }
  cycle {
    id
  }
  dueDate
  estimate
  syncedWith {
    ...ExternalEntityInfo
  }
  externalUserCreator {
    id
  }
  asksExternalUserRequester {
    id
  }
  asksRequester {
    id
  }
  description
  title
  number
  lastAppliedTemplate {
    id
  }
  updatedAt
  boardOrder
  prioritySortOrder
  sortOrder
  subIssueSortOrder
  parent {
    id
  }
  priority
  project {
    id
  }
  projectMilestone {
    id
  }
  recurringIssueTemplate {
    id
  }
  team {
    id
  }
  archivedAt
  createdAt
  startedTriageAt
  triagedAt
  addedToCycleAt
  addedToProjectAt
  addedToTeamAt
  autoArchivedAt
  autoClosedAt
  canceledAt
  completedAt
  startedAt
  slaStartedAt
  slaBreachesAt
  slaHighRiskAt
  slaMediumRiskAt
  snoozedUntilAt
  slaType
  id
  assignee {
    id
  }
  creator {
    id
  }
  snoozedBy {
    id
  }
  favorite {
    id
  }
  state {
    id
  }
}
fragment ExternalEntityInfo on ExternalEntityInfo {
  __typename
  metadata {
    ... on ExternalEntityInfoGithubMetadata {
      ...ExternalEntityInfoGithubMetadata
    }
    ... on ExternalEntityInfoJiraMetadata {
      ...ExternalEntityInfoJiraMetadata
    }
    ... on ExternalEntitySlackMetadata {
      ...ExternalEntitySlackMetadata
    }
  }
  id
  service
}
fragment ExternalEntityInfoGithubMetadata on ExternalEntityInfoGithubMetadata {
  __typename
  number
  owner
  repo
}
fragment ExternalEntityInfoJiraMetadata on ExternalEntityInfoJiraMetadata {
  __typename
  issueTypeId
  projectId
  issueKey
}
fragment ExternalEntitySlackMetadata on ExternalEntitySlackMetadata {
  __typename
  messageUrl
  channelId
  channelName
  isFromSlack
}
fragment IssueConnection on IssueConnection {
  __typename
  nodes {
    ...Issue
  }
  pageInfo {
    ...PageInfo
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`);
const Team_LabelsDocument = new TypedDocumentString(`
    query team_labels($id: String!, $after: String, $before: String, $filter: IssueLabelFilter, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  team(id: $id) {
    labels(
      after: $after
      before: $before
      filter: $filter
      first: $first
      includeArchived: $includeArchived
      last: $last
      orderBy: $orderBy
    ) {
      ...IssueLabelConnection
    }
  }
}
    fragment IssueLabel on IssueLabel {
  __typename
  lastAppliedAt
  color
  description
  name
  updatedAt
  inheritedFrom {
    id
  }
  parent {
    id
  }
  team {
    id
  }
  archivedAt
  createdAt
  id
  creator {
    id
  }
  retiredBy {
    id
  }
  isGroup
}
fragment IssueLabelConnection on IssueLabelConnection {
  __typename
  nodes {
    ...IssueLabel
  }
  pageInfo {
    ...PageInfo
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`);
const Team_MembersDocument = new TypedDocumentString(`
    query team_members($id: String!, $after: String, $before: String, $filter: UserFilter, $first: Int, $includeArchived: Boolean, $includeDisabled: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  team(id: $id) {
    members(
      after: $after
      before: $before
      filter: $filter
      first: $first
      includeArchived: $includeArchived
      includeDisabled: $includeDisabled
      last: $last
      orderBy: $orderBy
    ) {
      ...UserConnection
    }
  }
}
    fragment User on User {
  __typename
  statusUntilAt
  description
  avatarUrl
  createdIssueCount
  disableReason
  avatarBackgroundColor
  statusEmoji
  initials
  statusLabel
  updatedAt
  lastSeen
  timezone
  archivedAt
  createdAt
  id
  gitHubUserId
  displayName
  email
  name
  url
  active
  guest
  app
  admin
  owner
  isAssignable
  isMentionable
  isMe
  supportsAgentSessions
  canAccessAnyPublicTeam
  calendarHash
  inviteHash
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}
fragment UserConnection on UserConnection {
  __typename
  nodes {
    ...User
  }
  pageInfo {
    ...PageInfo
  }
}`);
const Team_MembershipsDocument = new TypedDocumentString(`
    query team_memberships($id: String!, $after: String, $before: String, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  team(id: $id) {
    memberships(
      after: $after
      before: $before
      first: $first
      includeArchived: $includeArchived
      last: $last
      orderBy: $orderBy
    ) {
      ...TeamMembershipConnection
    }
  }
}
    fragment TeamMembership on TeamMembership {
  __typename
  updatedAt
  sortOrder
  team {
    id
  }
  archivedAt
  createdAt
  id
  user {
    id
  }
  owner
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}
fragment TeamMembershipConnection on TeamMembershipConnection {
  __typename
  nodes {
    ...TeamMembership
  }
  pageInfo {
    ...PageInfo
  }
}`);
const Team_ProjectsDocument = new TypedDocumentString(`
    query team_projects($id: String!, $after: String, $before: String, $filter: ProjectFilter, $first: Int, $includeArchived: Boolean, $includeSubTeams: Boolean, $last: Int, $orderBy: PaginationOrderBy, $sort: [ProjectSortInput!]) {
  team(id: $id) {
    projects(
      after: $after
      before: $before
      filter: $filter
      first: $first
      includeArchived: $includeArchived
      includeSubTeams: $includeSubTeams
      last: $last
      orderBy: $orderBy
      sort: $sort
    ) {
      ...ProjectConnection
    }
  }
}
    fragment DocumentContent on DocumentContent {
  __typename
  aiPromptRules {
    ...AiPromptRules
  }
  content
  contentState
  document {
    id
  }
  initiative {
    id
  }
  issue {
    id
  }
  updatedAt
  projectMilestone {
    id
  }
  project {
    id
  }
  restoredAt
  archivedAt
  createdAt
  id
}
fragment Project on Project {
  __typename
  trashed
  labelIds
  url
  integrationsSettings {
    id
  }
  documentContent {
    ...DocumentContent
  }
  updateRemindersDay
  targetDate
  startDate
  updateReminderFrequency
  health
  updateRemindersHour
  icon
  lastUpdate {
    id
  }
  lastAppliedTemplate {
    id
  }
  updatedAt
  updateReminderFrequencyInWeeks
  completedScopeHistory
  completedIssueCountHistory
  inProgressScopeHistory
  progress
  scope
  priorityLabel
  priority
  lead {
    id
  }
  convertedFromIssue {
    id
  }
  color
  content
  description
  name
  slugId
  targetDateResolution
  startDateResolution
  frequencyResolution
  prioritySortOrder
  sortOrder
  status {
    id
  }
  archivedAt
  createdAt
  healthUpdatedAt
  autoArchivedAt
  canceledAt
  completedAt
  startedAt
  projectUpdateRemindersPausedUntilAt
  scopeHistory
  issueCountHistory
  id
  creator {
    id
  }
  favorite {
    id
  }
  slackIssueComments
  slackNewIssue
  slackIssueStatuses
  state
}
fragment AiPromptRules on AiPromptRules {
  __typename
  updatedAt
  archivedAt
  createdAt
  id
  updatedBy {
    id
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}
fragment ProjectConnection on ProjectConnection {
  __typename
  nodes {
    ...Project
  }
  pageInfo {
    ...PageInfo
  }
}`);
const Team_StatesDocument = new TypedDocumentString(`
    query team_states($id: String!, $after: String, $before: String, $filter: WorkflowStateFilter, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  team(id: $id) {
    states(
      after: $after
      before: $before
      filter: $filter
      first: $first
      includeArchived: $includeArchived
      last: $last
      orderBy: $orderBy
    ) {
      ...WorkflowStateConnection
    }
  }
}
    fragment WorkflowState on WorkflowState {
  __typename
  description
  updatedAt
  position
  inheritedFrom {
    id
  }
  color
  name
  team {
    id
  }
  archivedAt
  createdAt
  type
  id
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}
fragment WorkflowStateConnection on WorkflowStateConnection {
  __typename
  nodes {
    ...WorkflowState
  }
  pageInfo {
    ...PageInfo
  }
}`);
const Team_TemplatesDocument = new TypedDocumentString(`
    query team_templates($id: String!, $after: String, $before: String, $filter: NullableTemplateFilter, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  team(id: $id) {
    templates(
      after: $after
      before: $before
      filter: $filter
      first: $first
      includeArchived: $includeArchived
      last: $last
      orderBy: $orderBy
    ) {
      ...TemplateConnection
    }
  }
}
    fragment Template on Template {
  __typename
  templateData
  description
  lastAppliedAt
  type
  updatedAt
  name
  inheritedFrom {
    id
  }
  sortOrder
  team {
    id
  }
  archivedAt
  createdAt
  id
  creator {
    id
  }
  lastUpdatedBy {
    id
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}
fragment TemplateConnection on TemplateConnection {
  __typename
  nodes {
    ...Template
  }
  pageInfo {
    ...PageInfo
  }
}`);
const Team_WebhooksDocument = new TypedDocumentString(`
    query team_webhooks($id: String!, $after: String, $before: String, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  team(id: $id) {
    webhooks(
      after: $after
      before: $before
      first: $first
      includeArchived: $includeArchived
      last: $last
      orderBy: $orderBy
    ) {
      ...WebhookConnection
    }
  }
}
    fragment Webhook on Webhook {
  __typename
  secret
  updatedAt
  resourceTypes
  team {
    id
  }
  archivedAt
  createdAt
  id
  creator {
    id
  }
  url
  label
  allPublicTeams
  enabled
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}
fragment WebhookConnection on WebhookConnection {
  __typename
  nodes {
    ...Webhook
  }
  pageInfo {
    ...PageInfo
  }
}`);
const TeamMembershipDocument = new TypedDocumentString(`
    query teamMembership($id: String!) {
  teamMembership(id: $id) {
    ...TeamMembership
  }
}
    fragment TeamMembership on TeamMembership {
  __typename
  updatedAt
  sortOrder
  team {
    id
  }
  archivedAt
  createdAt
  id
  user {
    id
  }
  owner
}`);
const TeamMembershipsDocument = new TypedDocumentString(`
    query teamMemberships($after: String, $before: String, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  teamMemberships(
    after: $after
    before: $before
    first: $first
    includeArchived: $includeArchived
    last: $last
    orderBy: $orderBy
  ) {
    ...TeamMembershipConnection
  }
}
    fragment TeamMembership on TeamMembership {
  __typename
  updatedAt
  sortOrder
  team {
    id
  }
  archivedAt
  createdAt
  id
  user {
    id
  }
  owner
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}
fragment TeamMembershipConnection on TeamMembershipConnection {
  __typename
  nodes {
    ...TeamMembership
  }
  pageInfo {
    ...PageInfo
  }
}`);
const TeamsDocument = new TypedDocumentString(`
    query teams($after: String, $before: String, $filter: TeamFilter, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  teams(
    after: $after
    before: $before
    filter: $filter
    first: $first
    includeArchived: $includeArchived
    last: $last
    orderBy: $orderBy
  ) {
    ...TeamConnection
  }
}
    fragment Team on Team {
  __typename
  cycleIssueAutoAssignCompleted
  cycleLockToActive
  cycleIssueAutoAssignStarted
  cycleCalenderUrl
  upcomingCycleCount
  issueCount
  autoArchivePeriod
  autoClosePeriod
  securitySettings
  integrationsSettings {
    id
  }
  activeCycle {
    id
  }
  triageResponsibility {
    id
  }
  scimGroupName
  autoCloseStateId
  cycleCooldownTime
  cycleStartDay
  defaultTemplateForMembers {
    id
  }
  defaultTemplateForNonMembers {
    id
  }
  defaultProjectTemplate {
    id
  }
  defaultIssueState {
    id
  }
  cycleDuration
  icon
  defaultTemplateForMembersId
  defaultTemplateForNonMembersId
  issueEstimationType
  updatedAt
  displayName
  color
  description
  name
  key
  archivedAt
  createdAt
  timezone
  id
  mergeWorkflowState {
    id
  }
  draftWorkflowState {
    id
  }
  startWorkflowState {
    id
  }
  mergeableWorkflowState {
    id
  }
  reviewWorkflowState {
    id
  }
  markedAsDuplicateWorkflowState {
    id
  }
  triageIssueState {
    id
  }
  defaultIssueEstimate
  setIssueSortOrderOnStateChange
  allMembersCanJoin
  requirePriorityToLeaveTriage
  autoCloseChildIssues
  autoCloseParentIssues
  scimManaged
  private
  inheritIssueEstimation
  inheritWorkflowStatuses
  cyclesEnabled
  issueEstimationExtended
  issueEstimationAllowZero
  aiDiscussionSummariesEnabled
  aiThreadSummariesEnabled
  groupIssueHistory
  slackIssueComments
  slackNewIssue
  slackIssueStatuses
  triageEnabled
  inviteHash
  issueOrderingNoPriorityFirst
  issueSortOrderDefaultToBottom
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}
fragment TeamConnection on TeamConnection {
  __typename
  nodes {
    ...Team
  }
  pageInfo {
    ...PageInfo
  }
}`);
const TemplateDocument = new TypedDocumentString(`
    query template($id: String!) {
  template(id: $id) {
    ...Template
  }
}
    fragment Template on Template {
  __typename
  templateData
  description
  lastAppliedAt
  type
  updatedAt
  name
  inheritedFrom {
    id
  }
  sortOrder
  team {
    id
  }
  archivedAt
  createdAt
  id
  creator {
    id
  }
  lastUpdatedBy {
    id
  }
}`);
const TemplatesDocument = new TypedDocumentString(`
    query templates {
  templates {
    ...Template
  }
}
    fragment Template on Template {
  __typename
  templateData
  description
  lastAppliedAt
  type
  updatedAt
  name
  inheritedFrom {
    id
  }
  sortOrder
  team {
    id
  }
  archivedAt
  createdAt
  id
  creator {
    id
  }
  lastUpdatedBy {
    id
  }
}`);
const TemplatesForIntegrationDocument = new TypedDocumentString(`
    query templatesForIntegration($integrationType: String!) {
  templatesForIntegration(integrationType: $integrationType) {
    ...Template
  }
}
    fragment Template on Template {
  __typename
  templateData
  description
  lastAppliedAt
  type
  updatedAt
  name
  inheritedFrom {
    id
  }
  sortOrder
  team {
    id
  }
  archivedAt
  createdAt
  id
  creator {
    id
  }
  lastUpdatedBy {
    id
  }
}`);
const TimeScheduleDocument = new TypedDocumentString(`
    query timeSchedule($id: String!) {
  timeSchedule(id: $id) {
    ...TimeSchedule
  }
}
    fragment TimeSchedule on TimeSchedule {
  __typename
  externalUrl
  integration {
    id
  }
  externalId
  updatedAt
  name
  entries {
    ...TimeScheduleEntry
  }
  archivedAt
  createdAt
  id
}
fragment TimeScheduleEntry on TimeScheduleEntry {
  __typename
  userId
  userEmail
  endsAt
  startsAt
}`);
const TimeSchedulesDocument = new TypedDocumentString(`
    query timeSchedules($after: String, $before: String, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  timeSchedules(
    after: $after
    before: $before
    first: $first
    includeArchived: $includeArchived
    last: $last
    orderBy: $orderBy
  ) {
    ...TimeScheduleConnection
  }
}
    fragment TimeSchedule on TimeSchedule {
  __typename
  externalUrl
  integration {
    id
  }
  externalId
  updatedAt
  name
  entries {
    ...TimeScheduleEntry
  }
  archivedAt
  createdAt
  id
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}
fragment TimeScheduleConnection on TimeScheduleConnection {
  __typename
  nodes {
    ...TimeSchedule
  }
  pageInfo {
    ...PageInfo
  }
}
fragment TimeScheduleEntry on TimeScheduleEntry {
  __typename
  userId
  userEmail
  endsAt
  startsAt
}`);
const TriageResponsibilitiesDocument = new TypedDocumentString(`
    query triageResponsibilities($after: String, $before: String, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  triageResponsibilities(
    after: $after
    before: $before
    first: $first
    includeArchived: $includeArchived
    last: $last
    orderBy: $orderBy
  ) {
    ...TriageResponsibilityConnection
  }
}
    fragment TriageResponsibility on TriageResponsibility {
  __typename
  manualSelection {
    ...TriageResponsibilityManualSelection
  }
  action
  updatedAt
  team {
    id
  }
  archivedAt
  createdAt
  timeSchedule {
    id
  }
  id
  currentUser {
    id
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}
fragment TriageResponsibilityConnection on TriageResponsibilityConnection {
  __typename
  nodes {
    ...TriageResponsibility
  }
  pageInfo {
    ...PageInfo
  }
}
fragment TriageResponsibilityManualSelection on TriageResponsibilityManualSelection {
  __typename
  userIds
}`);
const TriageResponsibilityDocument = new TypedDocumentString(`
    query triageResponsibility($id: String!) {
  triageResponsibility(id: $id) {
    ...TriageResponsibility
  }
}
    fragment TriageResponsibility on TriageResponsibility {
  __typename
  manualSelection {
    ...TriageResponsibilityManualSelection
  }
  action
  updatedAt
  team {
    id
  }
  archivedAt
  createdAt
  timeSchedule {
    id
  }
  id
  currentUser {
    id
  }
}
fragment TriageResponsibilityManualSelection on TriageResponsibilityManualSelection {
  __typename
  userIds
}`);
const TriageResponsibility_ManualSelectionDocument = new TypedDocumentString(`
    query triageResponsibility_manualSelection($id: String!) {
  triageResponsibility(id: $id) {
    manualSelection {
      ...TriageResponsibilityManualSelection
    }
  }
}
    fragment TriageResponsibilityManualSelection on TriageResponsibilityManualSelection {
  __typename
  userIds
}`);
const UserDocument = new TypedDocumentString(`
    query user($id: String!) {
  user(id: $id) {
    ...User
  }
}
    fragment User on User {
  __typename
  statusUntilAt
  description
  avatarUrl
  createdIssueCount
  disableReason
  avatarBackgroundColor
  statusEmoji
  initials
  statusLabel
  updatedAt
  lastSeen
  timezone
  archivedAt
  createdAt
  id
  gitHubUserId
  displayName
  email
  name
  url
  active
  guest
  app
  admin
  owner
  isAssignable
  isMentionable
  isMe
  supportsAgentSessions
  canAccessAnyPublicTeam
  calendarHash
  inviteHash
}`);
const User_AssignedIssuesDocument = new TypedDocumentString(`
    query user_assignedIssues($id: String!, $after: String, $before: String, $filter: IssueFilter, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  user(id: $id) {
    assignedIssues(
      after: $after
      before: $before
      filter: $filter
      first: $first
      includeArchived: $includeArchived
      last: $last
      orderBy: $orderBy
    ) {
      ...IssueConnection
    }
  }
}
    fragment ActorBot on ActorBot {
  __typename
  avatarUrl
  name
  userDisplayName
  subType
  type
  id
}
fragment Reaction on Reaction {
  __typename
  emoji
  comment {
    id
  }
  externalUser {
    id
  }
  initiativeUpdate {
    id
  }
  issue {
    id
  }
  updatedAt
  projectUpdate {
    id
  }
  archivedAt
  createdAt
  id
  user {
    id
  }
}
fragment Issue on Issue {
  __typename
  trashed
  reactionData
  labelIds
  integrationSourceType
  url
  identifier
  priorityLabel
  previousIdentifiers
  reactions {
    ...Reaction
  }
  customerTicketCount
  branchName
  delegate {
    id
  }
  botActor {
    ...ActorBot
  }
  sourceComment {
    id
  }
  cycle {
    id
  }
  dueDate
  estimate
  syncedWith {
    ...ExternalEntityInfo
  }
  externalUserCreator {
    id
  }
  asksExternalUserRequester {
    id
  }
  asksRequester {
    id
  }
  description
  title
  number
  lastAppliedTemplate {
    id
  }
  updatedAt
  boardOrder
  prioritySortOrder
  sortOrder
  subIssueSortOrder
  parent {
    id
  }
  priority
  project {
    id
  }
  projectMilestone {
    id
  }
  recurringIssueTemplate {
    id
  }
  team {
    id
  }
  archivedAt
  createdAt
  startedTriageAt
  triagedAt
  addedToCycleAt
  addedToProjectAt
  addedToTeamAt
  autoArchivedAt
  autoClosedAt
  canceledAt
  completedAt
  startedAt
  slaStartedAt
  slaBreachesAt
  slaHighRiskAt
  slaMediumRiskAt
  snoozedUntilAt
  slaType
  id
  assignee {
    id
  }
  creator {
    id
  }
  snoozedBy {
    id
  }
  favorite {
    id
  }
  state {
    id
  }
}
fragment ExternalEntityInfo on ExternalEntityInfo {
  __typename
  metadata {
    ... on ExternalEntityInfoGithubMetadata {
      ...ExternalEntityInfoGithubMetadata
    }
    ... on ExternalEntityInfoJiraMetadata {
      ...ExternalEntityInfoJiraMetadata
    }
    ... on ExternalEntitySlackMetadata {
      ...ExternalEntitySlackMetadata
    }
  }
  id
  service
}
fragment ExternalEntityInfoGithubMetadata on ExternalEntityInfoGithubMetadata {
  __typename
  number
  owner
  repo
}
fragment ExternalEntityInfoJiraMetadata on ExternalEntityInfoJiraMetadata {
  __typename
  issueTypeId
  projectId
  issueKey
}
fragment ExternalEntitySlackMetadata on ExternalEntitySlackMetadata {
  __typename
  messageUrl
  channelId
  channelName
  isFromSlack
}
fragment IssueConnection on IssueConnection {
  __typename
  nodes {
    ...Issue
  }
  pageInfo {
    ...PageInfo
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`);
const User_CreatedIssuesDocument = new TypedDocumentString(`
    query user_createdIssues($id: String!, $after: String, $before: String, $filter: IssueFilter, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  user(id: $id) {
    createdIssues(
      after: $after
      before: $before
      filter: $filter
      first: $first
      includeArchived: $includeArchived
      last: $last
      orderBy: $orderBy
    ) {
      ...IssueConnection
    }
  }
}
    fragment ActorBot on ActorBot {
  __typename
  avatarUrl
  name
  userDisplayName
  subType
  type
  id
}
fragment Reaction on Reaction {
  __typename
  emoji
  comment {
    id
  }
  externalUser {
    id
  }
  initiativeUpdate {
    id
  }
  issue {
    id
  }
  updatedAt
  projectUpdate {
    id
  }
  archivedAt
  createdAt
  id
  user {
    id
  }
}
fragment Issue on Issue {
  __typename
  trashed
  reactionData
  labelIds
  integrationSourceType
  url
  identifier
  priorityLabel
  previousIdentifiers
  reactions {
    ...Reaction
  }
  customerTicketCount
  branchName
  delegate {
    id
  }
  botActor {
    ...ActorBot
  }
  sourceComment {
    id
  }
  cycle {
    id
  }
  dueDate
  estimate
  syncedWith {
    ...ExternalEntityInfo
  }
  externalUserCreator {
    id
  }
  asksExternalUserRequester {
    id
  }
  asksRequester {
    id
  }
  description
  title
  number
  lastAppliedTemplate {
    id
  }
  updatedAt
  boardOrder
  prioritySortOrder
  sortOrder
  subIssueSortOrder
  parent {
    id
  }
  priority
  project {
    id
  }
  projectMilestone {
    id
  }
  recurringIssueTemplate {
    id
  }
  team {
    id
  }
  archivedAt
  createdAt
  startedTriageAt
  triagedAt
  addedToCycleAt
  addedToProjectAt
  addedToTeamAt
  autoArchivedAt
  autoClosedAt
  canceledAt
  completedAt
  startedAt
  slaStartedAt
  slaBreachesAt
  slaHighRiskAt
  slaMediumRiskAt
  snoozedUntilAt
  slaType
  id
  assignee {
    id
  }
  creator {
    id
  }
  snoozedBy {
    id
  }
  favorite {
    id
  }
  state {
    id
  }
}
fragment ExternalEntityInfo on ExternalEntityInfo {
  __typename
  metadata {
    ... on ExternalEntityInfoGithubMetadata {
      ...ExternalEntityInfoGithubMetadata
    }
    ... on ExternalEntityInfoJiraMetadata {
      ...ExternalEntityInfoJiraMetadata
    }
    ... on ExternalEntitySlackMetadata {
      ...ExternalEntitySlackMetadata
    }
  }
  id
  service
}
fragment ExternalEntityInfoGithubMetadata on ExternalEntityInfoGithubMetadata {
  __typename
  number
  owner
  repo
}
fragment ExternalEntityInfoJiraMetadata on ExternalEntityInfoJiraMetadata {
  __typename
  issueTypeId
  projectId
  issueKey
}
fragment ExternalEntitySlackMetadata on ExternalEntitySlackMetadata {
  __typename
  messageUrl
  channelId
  channelName
  isFromSlack
}
fragment IssueConnection on IssueConnection {
  __typename
  nodes {
    ...Issue
  }
  pageInfo {
    ...PageInfo
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`);
const User_DelegatedIssuesDocument = new TypedDocumentString(`
    query user_delegatedIssues($id: String!, $after: String, $before: String, $filter: IssueFilter, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  user(id: $id) {
    delegatedIssues(
      after: $after
      before: $before
      filter: $filter
      first: $first
      includeArchived: $includeArchived
      last: $last
      orderBy: $orderBy
    ) {
      ...IssueConnection
    }
  }
}
    fragment ActorBot on ActorBot {
  __typename
  avatarUrl
  name
  userDisplayName
  subType
  type
  id
}
fragment Reaction on Reaction {
  __typename
  emoji
  comment {
    id
  }
  externalUser {
    id
  }
  initiativeUpdate {
    id
  }
  issue {
    id
  }
  updatedAt
  projectUpdate {
    id
  }
  archivedAt
  createdAt
  id
  user {
    id
  }
}
fragment Issue on Issue {
  __typename
  trashed
  reactionData
  labelIds
  integrationSourceType
  url
  identifier
  priorityLabel
  previousIdentifiers
  reactions {
    ...Reaction
  }
  customerTicketCount
  branchName
  delegate {
    id
  }
  botActor {
    ...ActorBot
  }
  sourceComment {
    id
  }
  cycle {
    id
  }
  dueDate
  estimate
  syncedWith {
    ...ExternalEntityInfo
  }
  externalUserCreator {
    id
  }
  asksExternalUserRequester {
    id
  }
  asksRequester {
    id
  }
  description
  title
  number
  lastAppliedTemplate {
    id
  }
  updatedAt
  boardOrder
  prioritySortOrder
  sortOrder
  subIssueSortOrder
  parent {
    id
  }
  priority
  project {
    id
  }
  projectMilestone {
    id
  }
  recurringIssueTemplate {
    id
  }
  team {
    id
  }
  archivedAt
  createdAt
  startedTriageAt
  triagedAt
  addedToCycleAt
  addedToProjectAt
  addedToTeamAt
  autoArchivedAt
  autoClosedAt
  canceledAt
  completedAt
  startedAt
  slaStartedAt
  slaBreachesAt
  slaHighRiskAt
  slaMediumRiskAt
  snoozedUntilAt
  slaType
  id
  assignee {
    id
  }
  creator {
    id
  }
  snoozedBy {
    id
  }
  favorite {
    id
  }
  state {
    id
  }
}
fragment ExternalEntityInfo on ExternalEntityInfo {
  __typename
  metadata {
    ... on ExternalEntityInfoGithubMetadata {
      ...ExternalEntityInfoGithubMetadata
    }
    ... on ExternalEntityInfoJiraMetadata {
      ...ExternalEntityInfoJiraMetadata
    }
    ... on ExternalEntitySlackMetadata {
      ...ExternalEntitySlackMetadata
    }
  }
  id
  service
}
fragment ExternalEntityInfoGithubMetadata on ExternalEntityInfoGithubMetadata {
  __typename
  number
  owner
  repo
}
fragment ExternalEntityInfoJiraMetadata on ExternalEntityInfoJiraMetadata {
  __typename
  issueTypeId
  projectId
  issueKey
}
fragment ExternalEntitySlackMetadata on ExternalEntitySlackMetadata {
  __typename
  messageUrl
  channelId
  channelName
  isFromSlack
}
fragment IssueConnection on IssueConnection {
  __typename
  nodes {
    ...Issue
  }
  pageInfo {
    ...PageInfo
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`);
const User_DraftsDocument = new TypedDocumentString(`
    query user_drafts($id: String!, $after: String, $before: String, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  user(id: $id) {
    drafts(
      after: $after
      before: $before
      first: $first
      includeArchived: $includeArchived
      last: $last
      orderBy: $orderBy
    ) {
      ...DraftConnection
    }
  }
}
    fragment Draft on Draft {
  __typename
  data
  parentComment {
    id
  }
  customerNeed {
    id
  }
  initiative {
    id
  }
  initiativeUpdate {
    id
  }
  issue {
    id
  }
  updatedAt
  project {
    id
  }
  projectUpdate {
    id
  }
  team {
    id
  }
  bodyData
  archivedAt
  createdAt
  id
  user {
    id
  }
  isAutogenerated
}
fragment DraftConnection on DraftConnection {
  __typename
  nodes {
    ...Draft
  }
  pageInfo {
    ...PageInfo
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`);
const User_TeamMembershipsDocument = new TypedDocumentString(`
    query user_teamMemberships($id: String!, $after: String, $before: String, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  user(id: $id) {
    teamMemberships(
      after: $after
      before: $before
      first: $first
      includeArchived: $includeArchived
      last: $last
      orderBy: $orderBy
    ) {
      ...TeamMembershipConnection
    }
  }
}
    fragment TeamMembership on TeamMembership {
  __typename
  updatedAt
  sortOrder
  team {
    id
  }
  archivedAt
  createdAt
  id
  user {
    id
  }
  owner
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}
fragment TeamMembershipConnection on TeamMembershipConnection {
  __typename
  nodes {
    ...TeamMembership
  }
  pageInfo {
    ...PageInfo
  }
}`);
const User_TeamsDocument = new TypedDocumentString(`
    query user_teams($id: String!, $after: String, $before: String, $filter: TeamFilter, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  user(id: $id) {
    teams(
      after: $after
      before: $before
      filter: $filter
      first: $first
      includeArchived: $includeArchived
      last: $last
      orderBy: $orderBy
    ) {
      ...TeamConnection
    }
  }
}
    fragment Team on Team {
  __typename
  cycleIssueAutoAssignCompleted
  cycleLockToActive
  cycleIssueAutoAssignStarted
  cycleCalenderUrl
  upcomingCycleCount
  issueCount
  autoArchivePeriod
  autoClosePeriod
  securitySettings
  integrationsSettings {
    id
  }
  activeCycle {
    id
  }
  triageResponsibility {
    id
  }
  scimGroupName
  autoCloseStateId
  cycleCooldownTime
  cycleStartDay
  defaultTemplateForMembers {
    id
  }
  defaultTemplateForNonMembers {
    id
  }
  defaultProjectTemplate {
    id
  }
  defaultIssueState {
    id
  }
  cycleDuration
  icon
  defaultTemplateForMembersId
  defaultTemplateForNonMembersId
  issueEstimationType
  updatedAt
  displayName
  color
  description
  name
  key
  archivedAt
  createdAt
  timezone
  id
  mergeWorkflowState {
    id
  }
  draftWorkflowState {
    id
  }
  startWorkflowState {
    id
  }
  mergeableWorkflowState {
    id
  }
  reviewWorkflowState {
    id
  }
  markedAsDuplicateWorkflowState {
    id
  }
  triageIssueState {
    id
  }
  defaultIssueEstimate
  setIssueSortOrderOnStateChange
  allMembersCanJoin
  requirePriorityToLeaveTriage
  autoCloseChildIssues
  autoCloseParentIssues
  scimManaged
  private
  inheritIssueEstimation
  inheritWorkflowStatuses
  cyclesEnabled
  issueEstimationExtended
  issueEstimationAllowZero
  aiDiscussionSummariesEnabled
  aiThreadSummariesEnabled
  groupIssueHistory
  slackIssueComments
  slackNewIssue
  slackIssueStatuses
  triageEnabled
  inviteHash
  issueOrderingNoPriorityFirst
  issueSortOrderDefaultToBottom
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}
fragment TeamConnection on TeamConnection {
  __typename
  nodes {
    ...Team
  }
  pageInfo {
    ...PageInfo
  }
}`);
const UserSettingsDocument = new TypedDocumentString(`
    query userSettings {
  userSettings {
    ...UserSettings
  }
}
    fragment NotificationCategoryPreferences on NotificationCategoryPreferences {
  __typename
  customers {
    ...NotificationChannelPreferences
  }
  feed {
    ...NotificationChannelPreferences
  }
  appsAndIntegrations {
    ...NotificationChannelPreferences
  }
  assignments {
    ...NotificationChannelPreferences
  }
  commentsAndReplies {
    ...NotificationChannelPreferences
  }
  documentChanges {
    ...NotificationChannelPreferences
  }
  mentions {
    ...NotificationChannelPreferences
  }
  postsAndUpdates {
    ...NotificationChannelPreferences
  }
  reactions {
    ...NotificationChannelPreferences
  }
  reminders {
    ...NotificationChannelPreferences
  }
  reviews {
    ...NotificationChannelPreferences
  }
  statusChanges {
    ...NotificationChannelPreferences
  }
  subscriptions {
    ...NotificationChannelPreferences
  }
  system {
    ...NotificationChannelPreferences
  }
  triage {
    ...NotificationChannelPreferences
  }
}
fragment NotificationChannelPreferences on NotificationChannelPreferences {
  __typename
  slack
  desktop
  email
  mobile
}
fragment NotificationDeliveryPreferences on NotificationDeliveryPreferences {
  __typename
  mobile {
    ...NotificationDeliveryPreferencesChannel
  }
}
fragment NotificationDeliveryPreferencesChannel on NotificationDeliveryPreferencesChannel {
  __typename
  schedule {
    ...NotificationDeliveryPreferencesSchedule
  }
  notificationsDisabled
}
fragment NotificationDeliveryPreferencesDay on NotificationDeliveryPreferencesDay {
  __typename
  end
  start
}
fragment NotificationDeliveryPreferencesSchedule on NotificationDeliveryPreferencesSchedule {
  __typename
  friday {
    ...NotificationDeliveryPreferencesDay
  }
  monday {
    ...NotificationDeliveryPreferencesDay
  }
  saturday {
    ...NotificationDeliveryPreferencesDay
  }
  sunday {
    ...NotificationDeliveryPreferencesDay
  }
  thursday {
    ...NotificationDeliveryPreferencesDay
  }
  tuesday {
    ...NotificationDeliveryPreferencesDay
  }
  wednesday {
    ...NotificationDeliveryPreferencesDay
  }
  disabled
}
fragment UserSettings on UserSettings {
  __typename
  calendarHash
  unsubscribedFrom
  updatedAt
  notificationDeliveryPreferences {
    ...NotificationDeliveryPreferences
  }
  archivedAt
  createdAt
  id
  user {
    id
  }
  feedSummarySchedule
  feedLastSeenTime
  notificationCategoryPreferences {
    ...NotificationCategoryPreferences
  }
  notificationChannelPreferences {
    ...NotificationChannelPreferences
  }
  theme {
    ...UserSettingsTheme
  }
  subscribedToDPA
  subscribedToChangelog
  subscribedToInviteAccepted
  subscribedToPrivacyLegalUpdates
  autoAssignToSelf
  showFullUserNames
}
fragment UserSettingsCustomSidebarTheme on UserSettingsCustomSidebarTheme {
  __typename
  accent
  base
  contrast
}
fragment UserSettingsCustomTheme on UserSettingsCustomTheme {
  __typename
  sidebar {
    ...UserSettingsCustomSidebarTheme
  }
  accent
  base
  contrast
}
fragment UserSettingsTheme on UserSettingsTheme {
  __typename
  custom {
    ...UserSettingsCustomTheme
  }
  preset
}`);
const UserSettings_NotificationCategoryPreferencesDocument = new TypedDocumentString(`
    query userSettings_notificationCategoryPreferences {
  userSettings {
    notificationCategoryPreferences {
      ...NotificationCategoryPreferences
    }
  }
}
    fragment NotificationCategoryPreferences on NotificationCategoryPreferences {
  __typename
  customers {
    ...NotificationChannelPreferences
  }
  feed {
    ...NotificationChannelPreferences
  }
  appsAndIntegrations {
    ...NotificationChannelPreferences
  }
  assignments {
    ...NotificationChannelPreferences
  }
  commentsAndReplies {
    ...NotificationChannelPreferences
  }
  documentChanges {
    ...NotificationChannelPreferences
  }
  mentions {
    ...NotificationChannelPreferences
  }
  postsAndUpdates {
    ...NotificationChannelPreferences
  }
  reactions {
    ...NotificationChannelPreferences
  }
  reminders {
    ...NotificationChannelPreferences
  }
  reviews {
    ...NotificationChannelPreferences
  }
  statusChanges {
    ...NotificationChannelPreferences
  }
  subscriptions {
    ...NotificationChannelPreferences
  }
  system {
    ...NotificationChannelPreferences
  }
  triage {
    ...NotificationChannelPreferences
  }
}
fragment NotificationChannelPreferences on NotificationChannelPreferences {
  __typename
  slack
  desktop
  email
  mobile
}`);
const UserSettings_NotificationCategoryPreferences_AppsAndIntegrationsDocument = new TypedDocumentString(`
    query userSettings_notificationCategoryPreferences_appsAndIntegrations {
  userSettings {
    notificationCategoryPreferences {
      appsAndIntegrations {
        ...NotificationChannelPreferences
      }
    }
  }
}
    fragment NotificationChannelPreferences on NotificationChannelPreferences {
  __typename
  slack
  desktop
  email
  mobile
}`);
const UserSettings_NotificationCategoryPreferences_AssignmentsDocument = new TypedDocumentString(`
    query userSettings_notificationCategoryPreferences_assignments {
  userSettings {
    notificationCategoryPreferences {
      assignments {
        ...NotificationChannelPreferences
      }
    }
  }
}
    fragment NotificationChannelPreferences on NotificationChannelPreferences {
  __typename
  slack
  desktop
  email
  mobile
}`);
const UserSettings_NotificationCategoryPreferences_CommentsAndRepliesDocument = new TypedDocumentString(`
    query userSettings_notificationCategoryPreferences_commentsAndReplies {
  userSettings {
    notificationCategoryPreferences {
      commentsAndReplies {
        ...NotificationChannelPreferences
      }
    }
  }
}
    fragment NotificationChannelPreferences on NotificationChannelPreferences {
  __typename
  slack
  desktop
  email
  mobile
}`);
const UserSettings_NotificationCategoryPreferences_CustomersDocument = new TypedDocumentString(`
    query userSettings_notificationCategoryPreferences_customers {
  userSettings {
    notificationCategoryPreferences {
      customers {
        ...NotificationChannelPreferences
      }
    }
  }
}
    fragment NotificationChannelPreferences on NotificationChannelPreferences {
  __typename
  slack
  desktop
  email
  mobile
}`);
const UserSettings_NotificationCategoryPreferences_DocumentChangesDocument = new TypedDocumentString(`
    query userSettings_notificationCategoryPreferences_documentChanges {
  userSettings {
    notificationCategoryPreferences {
      documentChanges {
        ...NotificationChannelPreferences
      }
    }
  }
}
    fragment NotificationChannelPreferences on NotificationChannelPreferences {
  __typename
  slack
  desktop
  email
  mobile
}`);
const UserSettings_NotificationCategoryPreferences_FeedDocument = new TypedDocumentString(`
    query userSettings_notificationCategoryPreferences_feed {
  userSettings {
    notificationCategoryPreferences {
      feed {
        ...NotificationChannelPreferences
      }
    }
  }
}
    fragment NotificationChannelPreferences on NotificationChannelPreferences {
  __typename
  slack
  desktop
  email
  mobile
}`);
const UserSettings_NotificationCategoryPreferences_MentionsDocument = new TypedDocumentString(`
    query userSettings_notificationCategoryPreferences_mentions {
  userSettings {
    notificationCategoryPreferences {
      mentions {
        ...NotificationChannelPreferences
      }
    }
  }
}
    fragment NotificationChannelPreferences on NotificationChannelPreferences {
  __typename
  slack
  desktop
  email
  mobile
}`);
const UserSettings_NotificationCategoryPreferences_PostsAndUpdatesDocument = new TypedDocumentString(`
    query userSettings_notificationCategoryPreferences_postsAndUpdates {
  userSettings {
    notificationCategoryPreferences {
      postsAndUpdates {
        ...NotificationChannelPreferences
      }
    }
  }
}
    fragment NotificationChannelPreferences on NotificationChannelPreferences {
  __typename
  slack
  desktop
  email
  mobile
}`);
const UserSettings_NotificationCategoryPreferences_ReactionsDocument = new TypedDocumentString(`
    query userSettings_notificationCategoryPreferences_reactions {
  userSettings {
    notificationCategoryPreferences {
      reactions {
        ...NotificationChannelPreferences
      }
    }
  }
}
    fragment NotificationChannelPreferences on NotificationChannelPreferences {
  __typename
  slack
  desktop
  email
  mobile
}`);
const UserSettings_NotificationCategoryPreferences_RemindersDocument = new TypedDocumentString(`
    query userSettings_notificationCategoryPreferences_reminders {
  userSettings {
    notificationCategoryPreferences {
      reminders {
        ...NotificationChannelPreferences
      }
    }
  }
}
    fragment NotificationChannelPreferences on NotificationChannelPreferences {
  __typename
  slack
  desktop
  email
  mobile
}`);
const UserSettings_NotificationCategoryPreferences_ReviewsDocument = new TypedDocumentString(`
    query userSettings_notificationCategoryPreferences_reviews {
  userSettings {
    notificationCategoryPreferences {
      reviews {
        ...NotificationChannelPreferences
      }
    }
  }
}
    fragment NotificationChannelPreferences on NotificationChannelPreferences {
  __typename
  slack
  desktop
  email
  mobile
}`);
const UserSettings_NotificationCategoryPreferences_StatusChangesDocument = new TypedDocumentString(`
    query userSettings_notificationCategoryPreferences_statusChanges {
  userSettings {
    notificationCategoryPreferences {
      statusChanges {
        ...NotificationChannelPreferences
      }
    }
  }
}
    fragment NotificationChannelPreferences on NotificationChannelPreferences {
  __typename
  slack
  desktop
  email
  mobile
}`);
const UserSettings_NotificationCategoryPreferences_SubscriptionsDocument = new TypedDocumentString(`
    query userSettings_notificationCategoryPreferences_subscriptions {
  userSettings {
    notificationCategoryPreferences {
      subscriptions {
        ...NotificationChannelPreferences
      }
    }
  }
}
    fragment NotificationChannelPreferences on NotificationChannelPreferences {
  __typename
  slack
  desktop
  email
  mobile
}`);
const UserSettings_NotificationCategoryPreferences_SystemDocument = new TypedDocumentString(`
    query userSettings_notificationCategoryPreferences_system {
  userSettings {
    notificationCategoryPreferences {
      system {
        ...NotificationChannelPreferences
      }
    }
  }
}
    fragment NotificationChannelPreferences on NotificationChannelPreferences {
  __typename
  slack
  desktop
  email
  mobile
}`);
const UserSettings_NotificationCategoryPreferences_TriageDocument = new TypedDocumentString(`
    query userSettings_notificationCategoryPreferences_triage {
  userSettings {
    notificationCategoryPreferences {
      triage {
        ...NotificationChannelPreferences
      }
    }
  }
}
    fragment NotificationChannelPreferences on NotificationChannelPreferences {
  __typename
  slack
  desktop
  email
  mobile
}`);
const UserSettings_NotificationChannelPreferencesDocument = new TypedDocumentString(`
    query userSettings_notificationChannelPreferences {
  userSettings {
    notificationChannelPreferences {
      ...NotificationChannelPreferences
    }
  }
}
    fragment NotificationChannelPreferences on NotificationChannelPreferences {
  __typename
  slack
  desktop
  email
  mobile
}`);
const UserSettings_NotificationDeliveryPreferencesDocument = new TypedDocumentString(`
    query userSettings_notificationDeliveryPreferences {
  userSettings {
    notificationDeliveryPreferences {
      ...NotificationDeliveryPreferences
    }
  }
}
    fragment NotificationDeliveryPreferences on NotificationDeliveryPreferences {
  __typename
  mobile {
    ...NotificationDeliveryPreferencesChannel
  }
}
fragment NotificationDeliveryPreferencesChannel on NotificationDeliveryPreferencesChannel {
  __typename
  schedule {
    ...NotificationDeliveryPreferencesSchedule
  }
  notificationsDisabled
}
fragment NotificationDeliveryPreferencesDay on NotificationDeliveryPreferencesDay {
  __typename
  end
  start
}
fragment NotificationDeliveryPreferencesSchedule on NotificationDeliveryPreferencesSchedule {
  __typename
  friday {
    ...NotificationDeliveryPreferencesDay
  }
  monday {
    ...NotificationDeliveryPreferencesDay
  }
  saturday {
    ...NotificationDeliveryPreferencesDay
  }
  sunday {
    ...NotificationDeliveryPreferencesDay
  }
  thursday {
    ...NotificationDeliveryPreferencesDay
  }
  tuesday {
    ...NotificationDeliveryPreferencesDay
  }
  wednesday {
    ...NotificationDeliveryPreferencesDay
  }
  disabled
}`);
const UserSettings_NotificationDeliveryPreferences_MobileDocument = new TypedDocumentString(`
    query userSettings_notificationDeliveryPreferences_mobile {
  userSettings {
    notificationDeliveryPreferences {
      mobile {
        ...NotificationDeliveryPreferencesChannel
      }
    }
  }
}
    fragment NotificationDeliveryPreferencesChannel on NotificationDeliveryPreferencesChannel {
  __typename
  schedule {
    ...NotificationDeliveryPreferencesSchedule
  }
  notificationsDisabled
}
fragment NotificationDeliveryPreferencesDay on NotificationDeliveryPreferencesDay {
  __typename
  end
  start
}
fragment NotificationDeliveryPreferencesSchedule on NotificationDeliveryPreferencesSchedule {
  __typename
  friday {
    ...NotificationDeliveryPreferencesDay
  }
  monday {
    ...NotificationDeliveryPreferencesDay
  }
  saturday {
    ...NotificationDeliveryPreferencesDay
  }
  sunday {
    ...NotificationDeliveryPreferencesDay
  }
  thursday {
    ...NotificationDeliveryPreferencesDay
  }
  tuesday {
    ...NotificationDeliveryPreferencesDay
  }
  wednesday {
    ...NotificationDeliveryPreferencesDay
  }
  disabled
}`);
const UserSettings_NotificationDeliveryPreferences_Mobile_ScheduleDocument = new TypedDocumentString(`
    query userSettings_notificationDeliveryPreferences_mobile_schedule {
  userSettings {
    notificationDeliveryPreferences {
      mobile {
        schedule {
          ...NotificationDeliveryPreferencesSchedule
        }
      }
    }
  }
}
    fragment NotificationDeliveryPreferencesDay on NotificationDeliveryPreferencesDay {
  __typename
  end
  start
}
fragment NotificationDeliveryPreferencesSchedule on NotificationDeliveryPreferencesSchedule {
  __typename
  friday {
    ...NotificationDeliveryPreferencesDay
  }
  monday {
    ...NotificationDeliveryPreferencesDay
  }
  saturday {
    ...NotificationDeliveryPreferencesDay
  }
  sunday {
    ...NotificationDeliveryPreferencesDay
  }
  thursday {
    ...NotificationDeliveryPreferencesDay
  }
  tuesday {
    ...NotificationDeliveryPreferencesDay
  }
  wednesday {
    ...NotificationDeliveryPreferencesDay
  }
  disabled
}`);
const UserSettings_NotificationDeliveryPreferences_Mobile_Schedule_FridayDocument = new TypedDocumentString(`
    query userSettings_notificationDeliveryPreferences_mobile_schedule_friday {
  userSettings {
    notificationDeliveryPreferences {
      mobile {
        schedule {
          friday {
            ...NotificationDeliveryPreferencesDay
          }
        }
      }
    }
  }
}
    fragment NotificationDeliveryPreferencesDay on NotificationDeliveryPreferencesDay {
  __typename
  end
  start
}`);
const UserSettings_NotificationDeliveryPreferences_Mobile_Schedule_MondayDocument = new TypedDocumentString(`
    query userSettings_notificationDeliveryPreferences_mobile_schedule_monday {
  userSettings {
    notificationDeliveryPreferences {
      mobile {
        schedule {
          monday {
            ...NotificationDeliveryPreferencesDay
          }
        }
      }
    }
  }
}
    fragment NotificationDeliveryPreferencesDay on NotificationDeliveryPreferencesDay {
  __typename
  end
  start
}`);
const UserSettings_NotificationDeliveryPreferences_Mobile_Schedule_SaturdayDocument = new TypedDocumentString(`
    query userSettings_notificationDeliveryPreferences_mobile_schedule_saturday {
  userSettings {
    notificationDeliveryPreferences {
      mobile {
        schedule {
          saturday {
            ...NotificationDeliveryPreferencesDay
          }
        }
      }
    }
  }
}
    fragment NotificationDeliveryPreferencesDay on NotificationDeliveryPreferencesDay {
  __typename
  end
  start
}`);
const UserSettings_NotificationDeliveryPreferences_Mobile_Schedule_SundayDocument = new TypedDocumentString(`
    query userSettings_notificationDeliveryPreferences_mobile_schedule_sunday {
  userSettings {
    notificationDeliveryPreferences {
      mobile {
        schedule {
          sunday {
            ...NotificationDeliveryPreferencesDay
          }
        }
      }
    }
  }
}
    fragment NotificationDeliveryPreferencesDay on NotificationDeliveryPreferencesDay {
  __typename
  end
  start
}`);
const UserSettings_NotificationDeliveryPreferences_Mobile_Schedule_ThursdayDocument = new TypedDocumentString(`
    query userSettings_notificationDeliveryPreferences_mobile_schedule_thursday {
  userSettings {
    notificationDeliveryPreferences {
      mobile {
        schedule {
          thursday {
            ...NotificationDeliveryPreferencesDay
          }
        }
      }
    }
  }
}
    fragment NotificationDeliveryPreferencesDay on NotificationDeliveryPreferencesDay {
  __typename
  end
  start
}`);
const UserSettings_NotificationDeliveryPreferences_Mobile_Schedule_TuesdayDocument = new TypedDocumentString(`
    query userSettings_notificationDeliveryPreferences_mobile_schedule_tuesday {
  userSettings {
    notificationDeliveryPreferences {
      mobile {
        schedule {
          tuesday {
            ...NotificationDeliveryPreferencesDay
          }
        }
      }
    }
  }
}
    fragment NotificationDeliveryPreferencesDay on NotificationDeliveryPreferencesDay {
  __typename
  end
  start
}`);
const UserSettings_NotificationDeliveryPreferences_Mobile_Schedule_WednesdayDocument = new TypedDocumentString(`
    query userSettings_notificationDeliveryPreferences_mobile_schedule_wednesday {
  userSettings {
    notificationDeliveryPreferences {
      mobile {
        schedule {
          wednesday {
            ...NotificationDeliveryPreferencesDay
          }
        }
      }
    }
  }
}
    fragment NotificationDeliveryPreferencesDay on NotificationDeliveryPreferencesDay {
  __typename
  end
  start
}`);
const UserSettings_ThemeDocument = new TypedDocumentString(`
    query userSettings_theme($deviceType: UserSettingsThemeDeviceType, $mode: UserSettingsThemeMode) {
  userSettings {
    theme(deviceType: $deviceType, mode: $mode) {
      ...UserSettingsTheme
    }
  }
}
    fragment UserSettingsCustomSidebarTheme on UserSettingsCustomSidebarTheme {
  __typename
  accent
  base
  contrast
}
fragment UserSettingsCustomTheme on UserSettingsCustomTheme {
  __typename
  sidebar {
    ...UserSettingsCustomSidebarTheme
  }
  accent
  base
  contrast
}
fragment UserSettingsTheme on UserSettingsTheme {
  __typename
  custom {
    ...UserSettingsCustomTheme
  }
  preset
}`);
const UserSettings_Theme_CustomDocument = new TypedDocumentString(`
    query userSettings_theme_custom($deviceType: UserSettingsThemeDeviceType, $mode: UserSettingsThemeMode) {
  userSettings {
    theme(deviceType: $deviceType, mode: $mode) {
      custom {
        ...UserSettingsCustomTheme
      }
    }
  }
}
    fragment UserSettingsCustomSidebarTheme on UserSettingsCustomSidebarTheme {
  __typename
  accent
  base
  contrast
}
fragment UserSettingsCustomTheme on UserSettingsCustomTheme {
  __typename
  sidebar {
    ...UserSettingsCustomSidebarTheme
  }
  accent
  base
  contrast
}`);
const UserSettings_Theme_Custom_SidebarDocument = new TypedDocumentString(`
    query userSettings_theme_custom_sidebar($deviceType: UserSettingsThemeDeviceType, $mode: UserSettingsThemeMode) {
  userSettings {
    theme(deviceType: $deviceType, mode: $mode) {
      custom {
        sidebar {
          ...UserSettingsCustomSidebarTheme
        }
      }
    }
  }
}
    fragment UserSettingsCustomSidebarTheme on UserSettingsCustomSidebarTheme {
  __typename
  accent
  base
  contrast
}`);
const UsersDocument = new TypedDocumentString(`
    query users($after: String, $before: String, $filter: UserFilter, $first: Int, $includeArchived: Boolean, $includeDisabled: Boolean, $last: Int, $orderBy: PaginationOrderBy, $sort: [UserSortInput!]) {
  users(
    after: $after
    before: $before
    filter: $filter
    first: $first
    includeArchived: $includeArchived
    includeDisabled: $includeDisabled
    last: $last
    orderBy: $orderBy
    sort: $sort
  ) {
    ...UserConnection
  }
}
    fragment User on User {
  __typename
  statusUntilAt
  description
  avatarUrl
  createdIssueCount
  disableReason
  avatarBackgroundColor
  statusEmoji
  initials
  statusLabel
  updatedAt
  lastSeen
  timezone
  archivedAt
  createdAt
  id
  gitHubUserId
  displayName
  email
  name
  url
  active
  guest
  app
  admin
  owner
  isAssignable
  isMentionable
  isMe
  supportsAgentSessions
  canAccessAnyPublicTeam
  calendarHash
  inviteHash
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}
fragment UserConnection on UserConnection {
  __typename
  nodes {
    ...User
  }
  pageInfo {
    ...PageInfo
  }
}`);
const VerifyGitHubEnterpriseServerInstallationDocument = new TypedDocumentString(`
    query verifyGitHubEnterpriseServerInstallation($integrationId: String!) {
  verifyGitHubEnterpriseServerInstallation(integrationId: $integrationId) {
    ...GitHubEnterpriseServerInstallVerificationPayload
  }
}
    fragment GitHubEnterpriseServerInstallVerificationPayload on GitHubEnterpriseServerInstallVerificationPayload {
  __typename
  success
}`);
const ViewerDocument = new TypedDocumentString(`
    query viewer {
  viewer {
    ...User
  }
}
    fragment User on User {
  __typename
  statusUntilAt
  description
  avatarUrl
  createdIssueCount
  disableReason
  avatarBackgroundColor
  statusEmoji
  initials
  statusLabel
  updatedAt
  lastSeen
  timezone
  archivedAt
  createdAt
  id
  gitHubUserId
  displayName
  email
  name
  url
  active
  guest
  app
  admin
  owner
  isAssignable
  isMentionable
  isMe
  supportsAgentSessions
  canAccessAnyPublicTeam
  calendarHash
  inviteHash
}`);
const Viewer_AssignedIssuesDocument = new TypedDocumentString(`
    query viewer_assignedIssues($after: String, $before: String, $filter: IssueFilter, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  viewer {
    assignedIssues(
      after: $after
      before: $before
      filter: $filter
      first: $first
      includeArchived: $includeArchived
      last: $last
      orderBy: $orderBy
    ) {
      ...IssueConnection
    }
  }
}
    fragment ActorBot on ActorBot {
  __typename
  avatarUrl
  name
  userDisplayName
  subType
  type
  id
}
fragment Reaction on Reaction {
  __typename
  emoji
  comment {
    id
  }
  externalUser {
    id
  }
  initiativeUpdate {
    id
  }
  issue {
    id
  }
  updatedAt
  projectUpdate {
    id
  }
  archivedAt
  createdAt
  id
  user {
    id
  }
}
fragment Issue on Issue {
  __typename
  trashed
  reactionData
  labelIds
  integrationSourceType
  url
  identifier
  priorityLabel
  previousIdentifiers
  reactions {
    ...Reaction
  }
  customerTicketCount
  branchName
  delegate {
    id
  }
  botActor {
    ...ActorBot
  }
  sourceComment {
    id
  }
  cycle {
    id
  }
  dueDate
  estimate
  syncedWith {
    ...ExternalEntityInfo
  }
  externalUserCreator {
    id
  }
  asksExternalUserRequester {
    id
  }
  asksRequester {
    id
  }
  description
  title
  number
  lastAppliedTemplate {
    id
  }
  updatedAt
  boardOrder
  prioritySortOrder
  sortOrder
  subIssueSortOrder
  parent {
    id
  }
  priority
  project {
    id
  }
  projectMilestone {
    id
  }
  recurringIssueTemplate {
    id
  }
  team {
    id
  }
  archivedAt
  createdAt
  startedTriageAt
  triagedAt
  addedToCycleAt
  addedToProjectAt
  addedToTeamAt
  autoArchivedAt
  autoClosedAt
  canceledAt
  completedAt
  startedAt
  slaStartedAt
  slaBreachesAt
  slaHighRiskAt
  slaMediumRiskAt
  snoozedUntilAt
  slaType
  id
  assignee {
    id
  }
  creator {
    id
  }
  snoozedBy {
    id
  }
  favorite {
    id
  }
  state {
    id
  }
}
fragment ExternalEntityInfo on ExternalEntityInfo {
  __typename
  metadata {
    ... on ExternalEntityInfoGithubMetadata {
      ...ExternalEntityInfoGithubMetadata
    }
    ... on ExternalEntityInfoJiraMetadata {
      ...ExternalEntityInfoJiraMetadata
    }
    ... on ExternalEntitySlackMetadata {
      ...ExternalEntitySlackMetadata
    }
  }
  id
  service
}
fragment ExternalEntityInfoGithubMetadata on ExternalEntityInfoGithubMetadata {
  __typename
  number
  owner
  repo
}
fragment ExternalEntityInfoJiraMetadata on ExternalEntityInfoJiraMetadata {
  __typename
  issueTypeId
  projectId
  issueKey
}
fragment ExternalEntitySlackMetadata on ExternalEntitySlackMetadata {
  __typename
  messageUrl
  channelId
  channelName
  isFromSlack
}
fragment IssueConnection on IssueConnection {
  __typename
  nodes {
    ...Issue
  }
  pageInfo {
    ...PageInfo
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`);
const Viewer_CreatedIssuesDocument = new TypedDocumentString(`
    query viewer_createdIssues($after: String, $before: String, $filter: IssueFilter, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  viewer {
    createdIssues(
      after: $after
      before: $before
      filter: $filter
      first: $first
      includeArchived: $includeArchived
      last: $last
      orderBy: $orderBy
    ) {
      ...IssueConnection
    }
  }
}
    fragment ActorBot on ActorBot {
  __typename
  avatarUrl
  name
  userDisplayName
  subType
  type
  id
}
fragment Reaction on Reaction {
  __typename
  emoji
  comment {
    id
  }
  externalUser {
    id
  }
  initiativeUpdate {
    id
  }
  issue {
    id
  }
  updatedAt
  projectUpdate {
    id
  }
  archivedAt
  createdAt
  id
  user {
    id
  }
}
fragment Issue on Issue {
  __typename
  trashed
  reactionData
  labelIds
  integrationSourceType
  url
  identifier
  priorityLabel
  previousIdentifiers
  reactions {
    ...Reaction
  }
  customerTicketCount
  branchName
  delegate {
    id
  }
  botActor {
    ...ActorBot
  }
  sourceComment {
    id
  }
  cycle {
    id
  }
  dueDate
  estimate
  syncedWith {
    ...ExternalEntityInfo
  }
  externalUserCreator {
    id
  }
  asksExternalUserRequester {
    id
  }
  asksRequester {
    id
  }
  description
  title
  number
  lastAppliedTemplate {
    id
  }
  updatedAt
  boardOrder
  prioritySortOrder
  sortOrder
  subIssueSortOrder
  parent {
    id
  }
  priority
  project {
    id
  }
  projectMilestone {
    id
  }
  recurringIssueTemplate {
    id
  }
  team {
    id
  }
  archivedAt
  createdAt
  startedTriageAt
  triagedAt
  addedToCycleAt
  addedToProjectAt
  addedToTeamAt
  autoArchivedAt
  autoClosedAt
  canceledAt
  completedAt
  startedAt
  slaStartedAt
  slaBreachesAt
  slaHighRiskAt
  slaMediumRiskAt
  snoozedUntilAt
  slaType
  id
  assignee {
    id
  }
  creator {
    id
  }
  snoozedBy {
    id
  }
  favorite {
    id
  }
  state {
    id
  }
}
fragment ExternalEntityInfo on ExternalEntityInfo {
  __typename
  metadata {
    ... on ExternalEntityInfoGithubMetadata {
      ...ExternalEntityInfoGithubMetadata
    }
    ... on ExternalEntityInfoJiraMetadata {
      ...ExternalEntityInfoJiraMetadata
    }
    ... on ExternalEntitySlackMetadata {
      ...ExternalEntitySlackMetadata
    }
  }
  id
  service
}
fragment ExternalEntityInfoGithubMetadata on ExternalEntityInfoGithubMetadata {
  __typename
  number
  owner
  repo
}
fragment ExternalEntityInfoJiraMetadata on ExternalEntityInfoJiraMetadata {
  __typename
  issueTypeId
  projectId
  issueKey
}
fragment ExternalEntitySlackMetadata on ExternalEntitySlackMetadata {
  __typename
  messageUrl
  channelId
  channelName
  isFromSlack
}
fragment IssueConnection on IssueConnection {
  __typename
  nodes {
    ...Issue
  }
  pageInfo {
    ...PageInfo
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`);
const Viewer_DelegatedIssuesDocument = new TypedDocumentString(`
    query viewer_delegatedIssues($after: String, $before: String, $filter: IssueFilter, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  viewer {
    delegatedIssues(
      after: $after
      before: $before
      filter: $filter
      first: $first
      includeArchived: $includeArchived
      last: $last
      orderBy: $orderBy
    ) {
      ...IssueConnection
    }
  }
}
    fragment ActorBot on ActorBot {
  __typename
  avatarUrl
  name
  userDisplayName
  subType
  type
  id
}
fragment Reaction on Reaction {
  __typename
  emoji
  comment {
    id
  }
  externalUser {
    id
  }
  initiativeUpdate {
    id
  }
  issue {
    id
  }
  updatedAt
  projectUpdate {
    id
  }
  archivedAt
  createdAt
  id
  user {
    id
  }
}
fragment Issue on Issue {
  __typename
  trashed
  reactionData
  labelIds
  integrationSourceType
  url
  identifier
  priorityLabel
  previousIdentifiers
  reactions {
    ...Reaction
  }
  customerTicketCount
  branchName
  delegate {
    id
  }
  botActor {
    ...ActorBot
  }
  sourceComment {
    id
  }
  cycle {
    id
  }
  dueDate
  estimate
  syncedWith {
    ...ExternalEntityInfo
  }
  externalUserCreator {
    id
  }
  asksExternalUserRequester {
    id
  }
  asksRequester {
    id
  }
  description
  title
  number
  lastAppliedTemplate {
    id
  }
  updatedAt
  boardOrder
  prioritySortOrder
  sortOrder
  subIssueSortOrder
  parent {
    id
  }
  priority
  project {
    id
  }
  projectMilestone {
    id
  }
  recurringIssueTemplate {
    id
  }
  team {
    id
  }
  archivedAt
  createdAt
  startedTriageAt
  triagedAt
  addedToCycleAt
  addedToProjectAt
  addedToTeamAt
  autoArchivedAt
  autoClosedAt
  canceledAt
  completedAt
  startedAt
  slaStartedAt
  slaBreachesAt
  slaHighRiskAt
  slaMediumRiskAt
  snoozedUntilAt
  slaType
  id
  assignee {
    id
  }
  creator {
    id
  }
  snoozedBy {
    id
  }
  favorite {
    id
  }
  state {
    id
  }
}
fragment ExternalEntityInfo on ExternalEntityInfo {
  __typename
  metadata {
    ... on ExternalEntityInfoGithubMetadata {
      ...ExternalEntityInfoGithubMetadata
    }
    ... on ExternalEntityInfoJiraMetadata {
      ...ExternalEntityInfoJiraMetadata
    }
    ... on ExternalEntitySlackMetadata {
      ...ExternalEntitySlackMetadata
    }
  }
  id
  service
}
fragment ExternalEntityInfoGithubMetadata on ExternalEntityInfoGithubMetadata {
  __typename
  number
  owner
  repo
}
fragment ExternalEntityInfoJiraMetadata on ExternalEntityInfoJiraMetadata {
  __typename
  issueTypeId
  projectId
  issueKey
}
fragment ExternalEntitySlackMetadata on ExternalEntitySlackMetadata {
  __typename
  messageUrl
  channelId
  channelName
  isFromSlack
}
fragment IssueConnection on IssueConnection {
  __typename
  nodes {
    ...Issue
  }
  pageInfo {
    ...PageInfo
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`);
const Viewer_DraftsDocument = new TypedDocumentString(`
    query viewer_drafts($after: String, $before: String, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  viewer {
    drafts(
      after: $after
      before: $before
      first: $first
      includeArchived: $includeArchived
      last: $last
      orderBy: $orderBy
    ) {
      ...DraftConnection
    }
  }
}
    fragment Draft on Draft {
  __typename
  data
  parentComment {
    id
  }
  customerNeed {
    id
  }
  initiative {
    id
  }
  initiativeUpdate {
    id
  }
  issue {
    id
  }
  updatedAt
  project {
    id
  }
  projectUpdate {
    id
  }
  team {
    id
  }
  bodyData
  archivedAt
  createdAt
  id
  user {
    id
  }
  isAutogenerated
}
fragment DraftConnection on DraftConnection {
  __typename
  nodes {
    ...Draft
  }
  pageInfo {
    ...PageInfo
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`);
const Viewer_TeamMembershipsDocument = new TypedDocumentString(`
    query viewer_teamMemberships($after: String, $before: String, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  viewer {
    teamMemberships(
      after: $after
      before: $before
      first: $first
      includeArchived: $includeArchived
      last: $last
      orderBy: $orderBy
    ) {
      ...TeamMembershipConnection
    }
  }
}
    fragment TeamMembership on TeamMembership {
  __typename
  updatedAt
  sortOrder
  team {
    id
  }
  archivedAt
  createdAt
  id
  user {
    id
  }
  owner
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}
fragment TeamMembershipConnection on TeamMembershipConnection {
  __typename
  nodes {
    ...TeamMembership
  }
  pageInfo {
    ...PageInfo
  }
}`);
const Viewer_TeamsDocument = new TypedDocumentString(`
    query viewer_teams($after: String, $before: String, $filter: TeamFilter, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  viewer {
    teams(
      after: $after
      before: $before
      filter: $filter
      first: $first
      includeArchived: $includeArchived
      last: $last
      orderBy: $orderBy
    ) {
      ...TeamConnection
    }
  }
}
    fragment Team on Team {
  __typename
  cycleIssueAutoAssignCompleted
  cycleLockToActive
  cycleIssueAutoAssignStarted
  cycleCalenderUrl
  upcomingCycleCount
  issueCount
  autoArchivePeriod
  autoClosePeriod
  securitySettings
  integrationsSettings {
    id
  }
  activeCycle {
    id
  }
  triageResponsibility {
    id
  }
  scimGroupName
  autoCloseStateId
  cycleCooldownTime
  cycleStartDay
  defaultTemplateForMembers {
    id
  }
  defaultTemplateForNonMembers {
    id
  }
  defaultProjectTemplate {
    id
  }
  defaultIssueState {
    id
  }
  cycleDuration
  icon
  defaultTemplateForMembersId
  defaultTemplateForNonMembersId
  issueEstimationType
  updatedAt
  displayName
  color
  description
  name
  key
  archivedAt
  createdAt
  timezone
  id
  mergeWorkflowState {
    id
  }
  draftWorkflowState {
    id
  }
  startWorkflowState {
    id
  }
  mergeableWorkflowState {
    id
  }
  reviewWorkflowState {
    id
  }
  markedAsDuplicateWorkflowState {
    id
  }
  triageIssueState {
    id
  }
  defaultIssueEstimate
  setIssueSortOrderOnStateChange
  allMembersCanJoin
  requirePriorityToLeaveTriage
  autoCloseChildIssues
  autoCloseParentIssues
  scimManaged
  private
  inheritIssueEstimation
  inheritWorkflowStatuses
  cyclesEnabled
  issueEstimationExtended
  issueEstimationAllowZero
  aiDiscussionSummariesEnabled
  aiThreadSummariesEnabled
  groupIssueHistory
  slackIssueComments
  slackNewIssue
  slackIssueStatuses
  triageEnabled
  inviteHash
  issueOrderingNoPriorityFirst
  issueSortOrderDefaultToBottom
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}
fragment TeamConnection on TeamConnection {
  __typename
  nodes {
    ...Team
  }
  pageInfo {
    ...PageInfo
  }
}`);
const WebhookDocument = new TypedDocumentString(`
    query webhook($id: String!) {
  webhook(id: $id) {
    ...Webhook
  }
}
    fragment Webhook on Webhook {
  __typename
  secret
  updatedAt
  resourceTypes
  team {
    id
  }
  archivedAt
  createdAt
  id
  creator {
    id
  }
  url
  label
  allPublicTeams
  enabled
}`);
const WebhooksDocument = new TypedDocumentString(`
    query webhooks($after: String, $before: String, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  webhooks(
    after: $after
    before: $before
    first: $first
    includeArchived: $includeArchived
    last: $last
    orderBy: $orderBy
  ) {
    ...WebhookConnection
  }
}
    fragment Webhook on Webhook {
  __typename
  secret
  updatedAt
  resourceTypes
  team {
    id
  }
  archivedAt
  createdAt
  id
  creator {
    id
  }
  url
  label
  allPublicTeams
  enabled
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}
fragment WebhookConnection on WebhookConnection {
  __typename
  nodes {
    ...Webhook
  }
  pageInfo {
    ...PageInfo
  }
}`);
const WorkflowStateDocument = new TypedDocumentString(`
    query workflowState($id: String!) {
  workflowState(id: $id) {
    ...WorkflowState
  }
}
    fragment WorkflowState on WorkflowState {
  __typename
  description
  updatedAt
  position
  inheritedFrom {
    id
  }
  color
  name
  team {
    id
  }
  archivedAt
  createdAt
  type
  id
}`);
const WorkflowState_IssuesDocument = new TypedDocumentString(`
    query workflowState_issues($id: String!, $after: String, $before: String, $filter: IssueFilter, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  workflowState(id: $id) {
    issues(
      after: $after
      before: $before
      filter: $filter
      first: $first
      includeArchived: $includeArchived
      last: $last
      orderBy: $orderBy
    ) {
      ...IssueConnection
    }
  }
}
    fragment ActorBot on ActorBot {
  __typename
  avatarUrl
  name
  userDisplayName
  subType
  type
  id
}
fragment Reaction on Reaction {
  __typename
  emoji
  comment {
    id
  }
  externalUser {
    id
  }
  initiativeUpdate {
    id
  }
  issue {
    id
  }
  updatedAt
  projectUpdate {
    id
  }
  archivedAt
  createdAt
  id
  user {
    id
  }
}
fragment Issue on Issue {
  __typename
  trashed
  reactionData
  labelIds
  integrationSourceType
  url
  identifier
  priorityLabel
  previousIdentifiers
  reactions {
    ...Reaction
  }
  customerTicketCount
  branchName
  delegate {
    id
  }
  botActor {
    ...ActorBot
  }
  sourceComment {
    id
  }
  cycle {
    id
  }
  dueDate
  estimate
  syncedWith {
    ...ExternalEntityInfo
  }
  externalUserCreator {
    id
  }
  asksExternalUserRequester {
    id
  }
  asksRequester {
    id
  }
  description
  title
  number
  lastAppliedTemplate {
    id
  }
  updatedAt
  boardOrder
  prioritySortOrder
  sortOrder
  subIssueSortOrder
  parent {
    id
  }
  priority
  project {
    id
  }
  projectMilestone {
    id
  }
  recurringIssueTemplate {
    id
  }
  team {
    id
  }
  archivedAt
  createdAt
  startedTriageAt
  triagedAt
  addedToCycleAt
  addedToProjectAt
  addedToTeamAt
  autoArchivedAt
  autoClosedAt
  canceledAt
  completedAt
  startedAt
  slaStartedAt
  slaBreachesAt
  slaHighRiskAt
  slaMediumRiskAt
  snoozedUntilAt
  slaType
  id
  assignee {
    id
  }
  creator {
    id
  }
  snoozedBy {
    id
  }
  favorite {
    id
  }
  state {
    id
  }
}
fragment ExternalEntityInfo on ExternalEntityInfo {
  __typename
  metadata {
    ... on ExternalEntityInfoGithubMetadata {
      ...ExternalEntityInfoGithubMetadata
    }
    ... on ExternalEntityInfoJiraMetadata {
      ...ExternalEntityInfoJiraMetadata
    }
    ... on ExternalEntitySlackMetadata {
      ...ExternalEntitySlackMetadata
    }
  }
  id
  service
}
fragment ExternalEntityInfoGithubMetadata on ExternalEntityInfoGithubMetadata {
  __typename
  number
  owner
  repo
}
fragment ExternalEntityInfoJiraMetadata on ExternalEntityInfoJiraMetadata {
  __typename
  issueTypeId
  projectId
  issueKey
}
fragment ExternalEntitySlackMetadata on ExternalEntitySlackMetadata {
  __typename
  messageUrl
  channelId
  channelName
  isFromSlack
}
fragment IssueConnection on IssueConnection {
  __typename
  nodes {
    ...Issue
  }
  pageInfo {
    ...PageInfo
  }
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}`);
const WorkflowStatesDocument = new TypedDocumentString(`
    query workflowStates($after: String, $before: String, $filter: WorkflowStateFilter, $first: Int, $includeArchived: Boolean, $last: Int, $orderBy: PaginationOrderBy) {
  workflowStates(
    after: $after
    before: $before
    filter: $filter
    first: $first
    includeArchived: $includeArchived
    last: $last
    orderBy: $orderBy
  ) {
    ...WorkflowStateConnection
  }
}
    fragment WorkflowState on WorkflowState {
  __typename
  description
  updatedAt
  position
  inheritedFrom {
    id
  }
  color
  name
  team {
    id
  }
  archivedAt
  createdAt
  type
  id
}
fragment PageInfo on PageInfo {
  __typename
  startCursor
  endCursor
  hasPreviousPage
  hasNextPage
}
fragment WorkflowStateConnection on WorkflowStateConnection {
  __typename
  nodes {
    ...WorkflowState
  }
  pageInfo {
    ...PageInfo
  }
}`);
const CreateAgentActivityDocument = new TypedDocumentString(`
    mutation createAgentActivity($input: AgentActivityCreateInput!) {
  agentActivityCreate(input: $input) {
    ...AgentActivityPayload
  }
}
    fragment AgentActivityPayload on AgentActivityPayload {
  __typename
  agentActivity {
    id
  }
  lastSyncId
  success
}`);
const AgentSessionCreateOnCommentDocument = new TypedDocumentString(`
    mutation agentSessionCreateOnComment($input: AgentSessionCreateOnComment!) {
  agentSessionCreateOnComment(input: $input) {
    ...AgentSessionPayload
  }
}
    fragment AgentSessionPayload on AgentSessionPayload {
  __typename
  agentSession {
    id
  }
  lastSyncId
  success
}`);
const AgentSessionCreateOnIssueDocument = new TypedDocumentString(`
    mutation agentSessionCreateOnIssue($input: AgentSessionCreateOnIssue!) {
  agentSessionCreateOnIssue(input: $input) {
    ...AgentSessionPayload
  }
}
    fragment AgentSessionPayload on AgentSessionPayload {
  __typename
  agentSession {
    id
  }
  lastSyncId
  success
}`);
const UpdateAgentSessionDocument = new TypedDocumentString(`
    mutation updateAgentSession($id: String!, $input: AgentSessionUpdateInput!) {
  agentSessionUpdate(id: $id, input: $input) {
    ...AgentSessionPayload
  }
}
    fragment AgentSessionPayload on AgentSessionPayload {
  __typename
  agentSession {
    id
  }
  lastSyncId
  success
}`);
const AgentSessionUpdateExternalUrlDocument = new TypedDocumentString(`
    mutation agentSessionUpdateExternalUrl($id: String!, $input: AgentSessionUpdateExternalUrlInput!) {
  agentSessionUpdateExternalUrl(id: $id, input: $input) {
    ...AgentSessionPayload
  }
}
    fragment AgentSessionPayload on AgentSessionPayload {
  __typename
  agentSession {
    id
  }
  lastSyncId
  success
}`);
const AirbyteIntegrationConnectDocument = new TypedDocumentString(`
    mutation airbyteIntegrationConnect($input: AirbyteConfigurationInput!) {
  airbyteIntegrationConnect(input: $input) {
    ...IntegrationPayload
  }
}
    fragment IntegrationPayload on IntegrationPayload {
  __typename
  lastSyncId
  integration {
    id
  }
  success
}`);
const AsksWebFormsAuthDocument = new TypedDocumentString(`
    mutation asksWebFormsAuth($token: String!) {
  asksWebFormsAuth(token: $token) {
    ...AsksWebFormsAuthResponse
  }
}
    fragment AsksWebFormsAuthResponse on AsksWebFormsAuthResponse {
  __typename
  name
  email
}`);
const CreateAttachmentDocument = new TypedDocumentString(`
    mutation createAttachment($input: AttachmentCreateInput!) {
  attachmentCreate(input: $input) {
    ...AttachmentPayload
  }
}
    fragment AttachmentPayload on AttachmentPayload {
  __typename
  lastSyncId
  attachment {
    id
  }
  success
}`);
const DeleteAttachmentDocument = new TypedDocumentString(`
    mutation deleteAttachment($id: String!) {
  attachmentDelete(id: $id) {
    ...DeletePayload
  }
}
    fragment DeletePayload on DeletePayload {
  __typename
  entityId
  lastSyncId
  success
}`);
const AttachmentLinkDiscordDocument = new TypedDocumentString(`
    mutation attachmentLinkDiscord($channelId: String!, $createAsUser: String, $displayIconUrl: String, $id: String, $issueId: String!, $messageId: String!, $title: String, $url: String!) {
  attachmentLinkDiscord(
    channelId: $channelId
    createAsUser: $createAsUser
    displayIconUrl: $displayIconUrl
    id: $id
    issueId: $issueId
    messageId: $messageId
    title: $title
    url: $url
  ) {
    ...AttachmentPayload
  }
}
    fragment AttachmentPayload on AttachmentPayload {
  __typename
  lastSyncId
  attachment {
    id
  }
  success
}`);
const AttachmentLinkFrontDocument = new TypedDocumentString(`
    mutation attachmentLinkFront($conversationId: String!, $createAsUser: String, $displayIconUrl: String, $id: String, $issueId: String!, $title: String) {
  attachmentLinkFront(
    conversationId: $conversationId
    createAsUser: $createAsUser
    displayIconUrl: $displayIconUrl
    id: $id
    issueId: $issueId
    title: $title
  ) {
    ...FrontAttachmentPayload
  }
}
    fragment FrontAttachmentPayload on FrontAttachmentPayload {
  __typename
  lastSyncId
  attachment {
    id
  }
  success
}`);
const AttachmentLinkGitHubIssueDocument = new TypedDocumentString(`
    mutation attachmentLinkGitHubIssue($createAsUser: String, $displayIconUrl: String, $id: String, $issueId: String!, $title: String, $url: String!) {
  attachmentLinkGitHubIssue(
    createAsUser: $createAsUser
    displayIconUrl: $displayIconUrl
    id: $id
    issueId: $issueId
    title: $title
    url: $url
  ) {
    ...AttachmentPayload
  }
}
    fragment AttachmentPayload on AttachmentPayload {
  __typename
  lastSyncId
  attachment {
    id
  }
  success
}`);
const AttachmentLinkGitHubPrDocument = new TypedDocumentString(`
    mutation attachmentLinkGitHubPR($createAsUser: String, $displayIconUrl: String, $id: String, $issueId: String!, $linkKind: GitLinkKind, $title: String, $url: String!) {
  attachmentLinkGitHubPR(
    createAsUser: $createAsUser
    displayIconUrl: $displayIconUrl
    id: $id
    issueId: $issueId
    linkKind: $linkKind
    title: $title
    url: $url
  ) {
    ...AttachmentPayload
  }
}
    fragment AttachmentPayload on AttachmentPayload {
  __typename
  lastSyncId
  attachment {
    id
  }
  success
}`);
const AttachmentLinkGitLabMrDocument = new TypedDocumentString(`
    mutation attachmentLinkGitLabMR($createAsUser: String, $displayIconUrl: String, $id: String, $issueId: String!, $number: Float!, $projectPathWithNamespace: String!, $title: String, $url: String!) {
  attachmentLinkGitLabMR(
    createAsUser: $createAsUser
    displayIconUrl: $displayIconUrl
    id: $id
    issueId: $issueId
    number: $number
    projectPathWithNamespace: $projectPathWithNamespace
    title: $title
    url: $url
  ) {
    ...AttachmentPayload
  }
}
    fragment AttachmentPayload on AttachmentPayload {
  __typename
  lastSyncId
  attachment {
    id
  }
  success
}`);
const AttachmentLinkIntercomDocument = new TypedDocumentString(`
    mutation attachmentLinkIntercom($conversationId: String!, $createAsUser: String, $displayIconUrl: String, $id: String, $issueId: String!, $partId: String, $title: String) {
  attachmentLinkIntercom(
    conversationId: $conversationId
    createAsUser: $createAsUser
    displayIconUrl: $displayIconUrl
    id: $id
    issueId: $issueId
    partId: $partId
    title: $title
  ) {
    ...AttachmentPayload
  }
}
    fragment AttachmentPayload on AttachmentPayload {
  __typename
  lastSyncId
  attachment {
    id
  }
  success
}`);
const AttachmentLinkJiraIssueDocument = new TypedDocumentString(`
    mutation attachmentLinkJiraIssue($createAsUser: String, $displayIconUrl: String, $id: String, $issueId: String!, $jiraIssueId: String!, $title: String, $url: String) {
  attachmentLinkJiraIssue(
    createAsUser: $createAsUser
    displayIconUrl: $displayIconUrl
    id: $id
    issueId: $issueId
    jiraIssueId: $jiraIssueId
    title: $title
    url: $url
  ) {
    ...AttachmentPayload
  }
}
    fragment AttachmentPayload on AttachmentPayload {
  __typename
  lastSyncId
  attachment {
    id
  }
  success
}`);
const AttachmentLinkSalesforceDocument = new TypedDocumentString(`
    mutation attachmentLinkSalesforce($createAsUser: String, $displayIconUrl: String, $id: String, $issueId: String!, $title: String, $url: String!) {
  attachmentLinkSalesforce(
    createAsUser: $createAsUser
    displayIconUrl: $displayIconUrl
    id: $id
    issueId: $issueId
    title: $title
    url: $url
  ) {
    ...AttachmentPayload
  }
}
    fragment AttachmentPayload on AttachmentPayload {
  __typename
  lastSyncId
  attachment {
    id
  }
  success
}`);
const AttachmentLinkSlackDocument = new TypedDocumentString(`
    mutation attachmentLinkSlack($createAsUser: String, $displayIconUrl: String, $id: String, $issueId: String!, $syncToCommentThread: Boolean, $title: String, $url: String!) {
  attachmentLinkSlack(
    createAsUser: $createAsUser
    displayIconUrl: $displayIconUrl
    id: $id
    issueId: $issueId
    syncToCommentThread: $syncToCommentThread
    title: $title
    url: $url
  ) {
    ...AttachmentPayload
  }
}
    fragment AttachmentPayload on AttachmentPayload {
  __typename
  lastSyncId
  attachment {
    id
  }
  success
}`);
const AttachmentLinkUrlDocument = new TypedDocumentString(`
    mutation attachmentLinkURL($createAsUser: String, $displayIconUrl: String, $id: String, $issueId: String!, $title: String, $url: String!) {
  attachmentLinkURL(
    createAsUser: $createAsUser
    displayIconUrl: $displayIconUrl
    id: $id
    issueId: $issueId
    title: $title
    url: $url
  ) {
    ...AttachmentPayload
  }
}
    fragment AttachmentPayload on AttachmentPayload {
  __typename
  lastSyncId
  attachment {
    id
  }
  success
}`);
const AttachmentLinkZendeskDocument = new TypedDocumentString(`
    mutation attachmentLinkZendesk($createAsUser: String, $displayIconUrl: String, $id: String, $issueId: String!, $ticketId: String!, $title: String, $url: String) {
  attachmentLinkZendesk(
    createAsUser: $createAsUser
    displayIconUrl: $displayIconUrl
    id: $id
    issueId: $issueId
    ticketId: $ticketId
    title: $title
    url: $url
  ) {
    ...AttachmentPayload
  }
}
    fragment AttachmentPayload on AttachmentPayload {
  __typename
  lastSyncId
  attachment {
    id
  }
  success
}`);
const AttachmentSyncToSlackDocument = new TypedDocumentString(`
    mutation attachmentSyncToSlack($id: String!) {
  attachmentSyncToSlack(id: $id) {
    ...AttachmentPayload
  }
}
    fragment AttachmentPayload on AttachmentPayload {
  __typename
  lastSyncId
  attachment {
    id
  }
  success
}`);
const UpdateAttachmentDocument = new TypedDocumentString(`
    mutation updateAttachment($id: String!, $input: AttachmentUpdateInput!) {
  attachmentUpdate(id: $id, input: $input) {
    ...AttachmentPayload
  }
}
    fragment AttachmentPayload on AttachmentPayload {
  __typename
  lastSyncId
  attachment {
    id
  }
  success
}`);
const CreateCommentDocument = new TypedDocumentString(`
    mutation createComment($input: CommentCreateInput!) {
  commentCreate(input: $input) {
    ...CommentPayload
  }
}
    fragment CommentPayload on CommentPayload {
  __typename
  comment {
    id
  }
  lastSyncId
  success
}`);
const DeleteCommentDocument = new TypedDocumentString(`
    mutation deleteComment($id: String!) {
  commentDelete(id: $id) {
    ...DeletePayload
  }
}
    fragment DeletePayload on DeletePayload {
  __typename
  entityId
  lastSyncId
  success
}`);
const CommentResolveDocument = new TypedDocumentString(`
    mutation commentResolve($id: String!, $resolvingCommentId: String) {
  commentResolve(id: $id, resolvingCommentId: $resolvingCommentId) {
    ...CommentPayload
  }
}
    fragment CommentPayload on CommentPayload {
  __typename
  comment {
    id
  }
  lastSyncId
  success
}`);
const CommentUnresolveDocument = new TypedDocumentString(`
    mutation commentUnresolve($id: String!) {
  commentUnresolve(id: $id) {
    ...CommentPayload
  }
}
    fragment CommentPayload on CommentPayload {
  __typename
  comment {
    id
  }
  lastSyncId
  success
}`);
const UpdateCommentDocument = new TypedDocumentString(`
    mutation updateComment($id: String!, $input: CommentUpdateInput!, $skipEditedAt: Boolean) {
  commentUpdate(id: $id, input: $input, skipEditedAt: $skipEditedAt) {
    ...CommentPayload
  }
}
    fragment CommentPayload on CommentPayload {
  __typename
  comment {
    id
  }
  lastSyncId
  success
}`);
const CreateContactDocument = new TypedDocumentString(`
    mutation createContact($input: ContactCreateInput!) {
  contactCreate(input: $input) {
    ...ContactPayload
  }
}
    fragment ContactPayload on ContactPayload {
  __typename
  success
}`);
const CreateCsvExportReportDocument = new TypedDocumentString(`
    mutation createCsvExportReport($includePrivateTeamIds: [String!]) {
  createCsvExportReport(includePrivateTeamIds: $includePrivateTeamIds) {
    ...CreateCsvExportReportPayload
  }
}
    fragment CreateCsvExportReportPayload on CreateCsvExportReportPayload {
  __typename
  success
}`);
const CreateInitiativeUpdateReminderDocument = new TypedDocumentString(`
    mutation createInitiativeUpdateReminder($initiativeId: String!, $userId: String) {
  createInitiativeUpdateReminder(initiativeId: $initiativeId, userId: $userId) {
    ...InitiativeUpdateReminderPayload
  }
}
    fragment InitiativeUpdateReminderPayload on InitiativeUpdateReminderPayload {
  __typename
  lastSyncId
  success
}`);
const CreateOrganizationFromOnboardingDocument = new TypedDocumentString(`
    mutation createOrganizationFromOnboarding($input: CreateOrganizationInput!, $survey: OnboardingCustomerSurvey) {
  createOrganizationFromOnboarding(input: $input, survey: $survey) {
    ...CreateOrJoinOrganizationResponse
  }
}
    fragment AuthUser on AuthUser {
  __typename
  avatarUrl
  organization {
    ...AuthOrganization
  }
  createdAt
  displayName
  email
  name
  userAccountId
  active
  role
  id
}
fragment AuthOrganization on AuthOrganization {
  __typename
  allowedAuthServices
  previousUrlKeys
  serviceId
  releaseChannel
  logoUrl
  name
  urlKey
  region
  deletionRequestedAt
  createdAt
  id
  samlEnabled
  scimEnabled
  enabled
  userCount
}
fragment CreateOrJoinOrganizationResponse on CreateOrJoinOrganizationResponse {
  __typename
  organization {
    ...AuthOrganization
  }
  user {
    ...AuthUser
  }
}`);
const CreateProjectUpdateReminderDocument = new TypedDocumentString(`
    mutation createProjectUpdateReminder($projectId: String!, $userId: String) {
  createProjectUpdateReminder(projectId: $projectId, userId: $userId) {
    ...ProjectUpdateReminderPayload
  }
}
    fragment ProjectUpdateReminderPayload on ProjectUpdateReminderPayload {
  __typename
  lastSyncId
  success
}`);
const CreateCustomViewDocument = new TypedDocumentString(`
    mutation createCustomView($input: CustomViewCreateInput!) {
  customViewCreate(input: $input) {
    ...CustomViewPayload
  }
}
    fragment CustomViewPayload on CustomViewPayload {
  __typename
  customView {
    id
  }
  lastSyncId
  success
}`);
const DeleteCustomViewDocument = new TypedDocumentString(`
    mutation deleteCustomView($id: String!) {
  customViewDelete(id: $id) {
    ...DeletePayload
  }
}
    fragment DeletePayload on DeletePayload {
  __typename
  entityId
  lastSyncId
  success
}`);
const UpdateCustomViewDocument = new TypedDocumentString(`
    mutation updateCustomView($id: String!, $input: CustomViewUpdateInput!) {
  customViewUpdate(id: $id, input: $input) {
    ...CustomViewPayload
  }
}
    fragment CustomViewPayload on CustomViewPayload {
  __typename
  customView {
    id
  }
  lastSyncId
  success
}`);
const CreateCustomerDocument = new TypedDocumentString(`
    mutation createCustomer($input: CustomerCreateInput!) {
  customerCreate(input: $input) {
    ...CustomerPayload
  }
}
    fragment CustomerPayload on CustomerPayload {
  __typename
  customer {
    id
  }
  lastSyncId
  success
}`);
const DeleteCustomerDocument = new TypedDocumentString(`
    mutation deleteCustomer($id: String!) {
  customerDelete(id: $id) {
    ...DeletePayload
  }
}
    fragment DeletePayload on DeletePayload {
  __typename
  entityId
  lastSyncId
  success
}`);
const CustomerMergeDocument = new TypedDocumentString(`
    mutation customerMerge($sourceCustomerId: String!, $targetCustomerId: String!) {
  customerMerge(
    sourceCustomerId: $sourceCustomerId
    targetCustomerId: $targetCustomerId
  ) {
    ...CustomerPayload
  }
}
    fragment CustomerPayload on CustomerPayload {
  __typename
  customer {
    id
  }
  lastSyncId
  success
}`);
const ArchiveCustomerNeedDocument = new TypedDocumentString(`
    mutation archiveCustomerNeed($id: String!) {
  customerNeedArchive(id: $id) {
    ...CustomerNeedArchivePayload
  }
}
    fragment CustomerNeedArchivePayload on CustomerNeedArchivePayload {
  __typename
  entity {
    id
  }
  lastSyncId
  success
}`);
const CreateCustomerNeedDocument = new TypedDocumentString(`
    mutation createCustomerNeed($input: CustomerNeedCreateInput!) {
  customerNeedCreate(input: $input) {
    ...CustomerNeedPayload
  }
}
    fragment CustomerNeedPayload on CustomerNeedPayload {
  __typename
  need {
    id
  }
  lastSyncId
  success
}`);
const CustomerNeedCreateFromAttachmentDocument = new TypedDocumentString(`
    mutation customerNeedCreateFromAttachment($input: CustomerNeedCreateFromAttachmentInput!) {
  customerNeedCreateFromAttachment(input: $input) {
    ...CustomerNeedPayload
  }
}
    fragment CustomerNeedPayload on CustomerNeedPayload {
  __typename
  need {
    id
  }
  lastSyncId
  success
}`);
const DeleteCustomerNeedDocument = new TypedDocumentString(`
    mutation deleteCustomerNeed($id: String!, $keepAttachment: Boolean) {
  customerNeedDelete(id: $id, keepAttachment: $keepAttachment) {
    ...DeletePayload
  }
}
    fragment DeletePayload on DeletePayload {
  __typename
  entityId
  lastSyncId
  success
}`);
const UnarchiveCustomerNeedDocument = new TypedDocumentString(`
    mutation unarchiveCustomerNeed($id: String!) {
  customerNeedUnarchive(id: $id) {
    ...CustomerNeedArchivePayload
  }
}
    fragment CustomerNeedArchivePayload on CustomerNeedArchivePayload {
  __typename
  entity {
    id
  }
  lastSyncId
  success
}`);
const UpdateCustomerNeedDocument = new TypedDocumentString(`
    mutation updateCustomerNeed($id: String!, $input: CustomerNeedUpdateInput!) {
  customerNeedUpdate(id: $id, input: $input) {
    ...CustomerNeedUpdatePayload
  }
}
    fragment CustomerNeed on CustomerNeed {
  __typename
  url
  attachment {
    id
  }
  comment {
    id
  }
  creator {
    id
  }
  customer {
    id
  }
  originalIssue {
    id
  }
  issue {
    id
  }
  updatedAt
  body
  projectAttachment {
    ...ProjectAttachment
  }
  project {
    id
  }
  archivedAt
  createdAt
  id
  priority
}
fragment ProjectAttachment on ProjectAttachment {
  __typename
  sourceType
  metadata
  source
  subtitle
  creator {
    id
  }
  updatedAt
  archivedAt
  createdAt
  id
  title
  url
}
fragment CustomerNeedUpdatePayload on CustomerNeedUpdatePayload {
  __typename
  need {
    id
  }
  lastSyncId
  updatedRelatedNeeds {
    ...CustomerNeed
  }
  success
}`);
const CreateCustomerStatusDocument = new TypedDocumentString(`
    mutation createCustomerStatus($input: CustomerStatusCreateInput!) {
  customerStatusCreate(input: $input) {
    ...CustomerStatusPayload
  }
}
    fragment CustomerStatusPayload on CustomerStatusPayload {
  __typename
  status {
    id
  }
  lastSyncId
  success
}`);
const DeleteCustomerStatusDocument = new TypedDocumentString(`
    mutation deleteCustomerStatus($id: String!) {
  customerStatusDelete(id: $id) {
    ...DeletePayload
  }
}
    fragment DeletePayload on DeletePayload {
  __typename
  entityId
  lastSyncId
  success
}`);
const UpdateCustomerStatusDocument = new TypedDocumentString(`
    mutation updateCustomerStatus($id: String!, $input: CustomerStatusUpdateInput!) {
  customerStatusUpdate(id: $id, input: $input) {
    ...CustomerStatusPayload
  }
}
    fragment CustomerStatusPayload on CustomerStatusPayload {
  __typename
  status {
    id
  }
  lastSyncId
  success
}`);
const CreateCustomerTierDocument = new TypedDocumentString(`
    mutation createCustomerTier($input: CustomerTierCreateInput!) {
  customerTierCreate(input: $input) {
    ...CustomerTierPayload
  }
}
    fragment CustomerTierPayload on CustomerTierPayload {
  __typename
  tier {
    id
  }
  lastSyncId
  success
}`);
const DeleteCustomerTierDocument = new TypedDocumentString(`
    mutation deleteCustomerTier($id: String!) {
  customerTierDelete(id: $id) {
    ...DeletePayload
  }
}
    fragment DeletePayload on DeletePayload {
  __typename
  entityId
  lastSyncId
  success
}`);
const UpdateCustomerTierDocument = new TypedDocumentString(`
    mutation updateCustomerTier($id: String!, $input: CustomerTierUpdateInput!) {
  customerTierUpdate(id: $id, input: $input) {
    ...CustomerTierPayload
  }
}
    fragment CustomerTierPayload on CustomerTierPayload {
  __typename
  tier {
    id
  }
  lastSyncId
  success
}`);
const CustomerUnsyncDocument = new TypedDocumentString(`
    mutation customerUnsync($id: String!) {
  customerUnsync(id: $id) {
    ...CustomerPayload
  }
}
    fragment CustomerPayload on CustomerPayload {
  __typename
  customer {
    id
  }
  lastSyncId
  success
}`);
const UpdateCustomerDocument = new TypedDocumentString(`
    mutation updateCustomer($id: String!, $input: CustomerUpdateInput!) {
  customerUpdate(id: $id, input: $input) {
    ...CustomerPayload
  }
}
    fragment CustomerPayload on CustomerPayload {
  __typename
  customer {
    id
  }
  lastSyncId
  success
}`);
const CustomerUpsertDocument = new TypedDocumentString(`
    mutation customerUpsert($input: CustomerUpsertInput!) {
  customerUpsert(input: $input) {
    ...CustomerPayload
  }
}
    fragment CustomerPayload on CustomerPayload {
  __typename
  customer {
    id
  }
  lastSyncId
  success
}`);
const ArchiveCycleDocument = new TypedDocumentString(`
    mutation archiveCycle($id: String!) {
  cycleArchive(id: $id) {
    ...CycleArchivePayload
  }
}
    fragment CycleArchivePayload on CycleArchivePayload {
  __typename
  entity {
    id
  }
  lastSyncId
  success
}`);
const CreateCycleDocument = new TypedDocumentString(`
    mutation createCycle($input: CycleCreateInput!) {
  cycleCreate(input: $input) {
    ...CyclePayload
  }
}
    fragment CyclePayload on CyclePayload {
  __typename
  cycle {
    id
  }
  lastSyncId
  success
}`);
const CycleShiftAllDocument = new TypedDocumentString(`
    mutation cycleShiftAll($input: CycleShiftAllInput!) {
  cycleShiftAll(input: $input) {
    ...CyclePayload
  }
}
    fragment CyclePayload on CyclePayload {
  __typename
  cycle {
    id
  }
  lastSyncId
  success
}`);
const CycleStartUpcomingCycleTodayDocument = new TypedDocumentString(`
    mutation cycleStartUpcomingCycleToday($id: String!) {
  cycleStartUpcomingCycleToday(id: $id) {
    ...CyclePayload
  }
}
    fragment CyclePayload on CyclePayload {
  __typename
  cycle {
    id
  }
  lastSyncId
  success
}`);
const UpdateCycleDocument = new TypedDocumentString(`
    mutation updateCycle($id: String!, $input: CycleUpdateInput!) {
  cycleUpdate(id: $id, input: $input) {
    ...CyclePayload
  }
}
    fragment CyclePayload on CyclePayload {
  __typename
  cycle {
    id
  }
  lastSyncId
  success
}`);
const CreateDocumentDocument = new TypedDocumentString(`
    mutation createDocument($input: DocumentCreateInput!) {
  documentCreate(input: $input) {
    ...DocumentPayload
  }
}
    fragment DocumentPayload on DocumentPayload {
  __typename
  document {
    id
  }
  lastSyncId
  success
}`);
const DeleteDocumentDocument = new TypedDocumentString(`
    mutation deleteDocument($id: String!) {
  documentDelete(id: $id) {
    ...DocumentArchivePayload
  }
}
    fragment DocumentArchivePayload on DocumentArchivePayload {
  __typename
  entity {
    id
  }
  lastSyncId
  success
}`);
const UnarchiveDocumentDocument = new TypedDocumentString(`
    mutation unarchiveDocument($id: String!) {
  documentUnarchive(id: $id) {
    ...DocumentArchivePayload
  }
}
    fragment DocumentArchivePayload on DocumentArchivePayload {
  __typename
  entity {
    id
  }
  lastSyncId
  success
}`);
const UpdateDocumentDocument = new TypedDocumentString(`
    mutation updateDocument($id: String!, $input: DocumentUpdateInput!) {
  documentUpdate(id: $id, input: $input) {
    ...DocumentPayload
  }
}
    fragment DocumentPayload on DocumentPayload {
  __typename
  document {
    id
  }
  lastSyncId
  success
}`);
const CreateEmailIntakeAddressDocument = new TypedDocumentString(`
    mutation createEmailIntakeAddress($input: EmailIntakeAddressCreateInput!) {
  emailIntakeAddressCreate(input: $input) {
    ...EmailIntakeAddressPayload
  }
}
    fragment EmailIntakeAddressPayload on EmailIntakeAddressPayload {
  __typename
  emailIntakeAddress {
    id
  }
  lastSyncId
  success
}`);
const DeleteEmailIntakeAddressDocument = new TypedDocumentString(`
    mutation deleteEmailIntakeAddress($id: String!) {
  emailIntakeAddressDelete(id: $id) {
    ...DeletePayload
  }
}
    fragment DeletePayload on DeletePayload {
  __typename
  entityId
  lastSyncId
  success
}`);
const EmailIntakeAddressRotateDocument = new TypedDocumentString(`
    mutation emailIntakeAddressRotate($id: String!) {
  emailIntakeAddressRotate(id: $id) {
    ...EmailIntakeAddressPayload
  }
}
    fragment EmailIntakeAddressPayload on EmailIntakeAddressPayload {
  __typename
  emailIntakeAddress {
    id
  }
  lastSyncId
  success
}`);
const UpdateEmailIntakeAddressDocument = new TypedDocumentString(`
    mutation updateEmailIntakeAddress($id: String!, $input: EmailIntakeAddressUpdateInput!) {
  emailIntakeAddressUpdate(id: $id, input: $input) {
    ...EmailIntakeAddressPayload
  }
}
    fragment EmailIntakeAddressPayload on EmailIntakeAddressPayload {
  __typename
  emailIntakeAddress {
    id
  }
  lastSyncId
  success
}`);
const EmailTokenUserAccountAuthDocument = new TypedDocumentString(`
    mutation emailTokenUserAccountAuth($input: TokenUserAccountAuthInput!) {
  emailTokenUserAccountAuth(input: $input) {
    ...AuthResolverResponse
  }
}
    fragment AuthUser on AuthUser {
  __typename
  avatarUrl
  organization {
    ...AuthOrganization
  }
  createdAt
  displayName
  email
  name
  userAccountId
  active
  role
  id
}
fragment AuthOrganization on AuthOrganization {
  __typename
  allowedAuthServices
  previousUrlKeys
  serviceId
  releaseChannel
  logoUrl
  name
  urlKey
  region
  deletionRequestedAt
  createdAt
  id
  samlEnabled
  scimEnabled
  enabled
  userCount
}
fragment AuthResolverResponse on AuthResolverResponse {
  __typename
  token
  email
  lastUsedOrganizationId
  users {
    ...AuthUser
  }
  lockedUsers {
    ...AuthUser
  }
  lockedOrganizations {
    ...AuthOrganization
  }
  availableOrganizations {
    ...AuthOrganization
  }
  allowDomainAccess
  id
}`);
const EmailUnsubscribeDocument = new TypedDocumentString(`
    mutation emailUnsubscribe($input: EmailUnsubscribeInput!) {
  emailUnsubscribe(input: $input) {
    ...EmailUnsubscribePayload
  }
}
    fragment EmailUnsubscribePayload on EmailUnsubscribePayload {
  __typename
  success
}`);
const EmailUserAccountAuthChallengeDocument = new TypedDocumentString(`
    mutation emailUserAccountAuthChallenge($input: EmailUserAccountAuthChallengeInput!) {
  emailUserAccountAuthChallenge(input: $input) {
    ...EmailUserAccountAuthChallengeResponse
  }
}
    fragment EmailUserAccountAuthChallengeResponse on EmailUserAccountAuthChallengeResponse {
  __typename
  authType
  success
}`);
const CreateEmojiDocument = new TypedDocumentString(`
    mutation createEmoji($input: EmojiCreateInput!) {
  emojiCreate(input: $input) {
    ...EmojiPayload
  }
}
    fragment EmojiPayload on EmojiPayload {
  __typename
  emoji {
    id
  }
  lastSyncId
  success
}`);
const DeleteEmojiDocument = new TypedDocumentString(`
    mutation deleteEmoji($id: String!) {
  emojiDelete(id: $id) {
    ...DeletePayload
  }
}
    fragment DeletePayload on DeletePayload {
  __typename
  entityId
  lastSyncId
  success
}`);
const CreateEntityExternalLinkDocument = new TypedDocumentString(`
    mutation createEntityExternalLink($input: EntityExternalLinkCreateInput!) {
  entityExternalLinkCreate(input: $input) {
    ...EntityExternalLinkPayload
  }
}
    fragment EntityExternalLinkPayload on EntityExternalLinkPayload {
  __typename
  lastSyncId
  entityExternalLink {
    id
  }
  success
}`);
const DeleteEntityExternalLinkDocument = new TypedDocumentString(`
    mutation deleteEntityExternalLink($id: String!) {
  entityExternalLinkDelete(id: $id) {
    ...DeletePayload
  }
}
    fragment DeletePayload on DeletePayload {
  __typename
  entityId
  lastSyncId
  success
}`);
const UpdateEntityExternalLinkDocument = new TypedDocumentString(`
    mutation updateEntityExternalLink($id: String!, $input: EntityExternalLinkUpdateInput!) {
  entityExternalLinkUpdate(id: $id, input: $input) {
    ...EntityExternalLinkPayload
  }
}
    fragment EntityExternalLinkPayload on EntityExternalLinkPayload {
  __typename
  lastSyncId
  entityExternalLink {
    id
  }
  success
}`);
const CreateFavoriteDocument = new TypedDocumentString(`
    mutation createFavorite($input: FavoriteCreateInput!) {
  favoriteCreate(input: $input) {
    ...FavoritePayload
  }
}
    fragment FavoritePayload on FavoritePayload {
  __typename
  lastSyncId
  favorite {
    id
  }
  success
}`);
const DeleteFavoriteDocument = new TypedDocumentString(`
    mutation deleteFavorite($id: String!) {
  favoriteDelete(id: $id) {
    ...DeletePayload
  }
}
    fragment DeletePayload on DeletePayload {
  __typename
  entityId
  lastSyncId
  success
}`);
const UpdateFavoriteDocument = new TypedDocumentString(`
    mutation updateFavorite($id: String!, $input: FavoriteUpdateInput!) {
  favoriteUpdate(id: $id, input: $input) {
    ...FavoritePayload
  }
}
    fragment FavoritePayload on FavoritePayload {
  __typename
  lastSyncId
  favorite {
    id
  }
  success
}`);
const FileUploadDocument = new TypedDocumentString(`
    mutation fileUpload($contentType: String!, $filename: String!, $makePublic: Boolean, $metaData: JSON, $size: Int!) {
  fileUpload(
    contentType: $contentType
    filename: $filename
    makePublic: $makePublic
    metaData: $metaData
    size: $size
  ) {
    ...UploadPayload
  }
}
    fragment UploadFile on UploadFile {
  __typename
  assetUrl
  contentType
  filename
  uploadUrl
  size
  headers {
    ...UploadFileHeader
  }
  metaData
}
fragment UploadFileHeader on UploadFileHeader {
  __typename
  key
  value
}
fragment UploadPayload on UploadPayload {
  __typename
  uploadFile {
    ...UploadFile
  }
  lastSyncId
  success
}`);
const CreateGitAutomationStateDocument = new TypedDocumentString(`
    mutation createGitAutomationState($input: GitAutomationStateCreateInput!) {
  gitAutomationStateCreate(input: $input) {
    ...GitAutomationStatePayload
  }
}
    fragment GitAutomationTargetBranch on GitAutomationTargetBranch {
  __typename
  updatedAt
  branchPattern
  team {
    id
  }
  archivedAt
  createdAt
  id
  isRegex
}
fragment GitAutomationState on GitAutomationState {
  __typename
  state {
    id
  }
  event
  updatedAt
  targetBranch {
    ...GitAutomationTargetBranch
  }
  team {
    id
  }
  archivedAt
  createdAt
  id
  branchPattern
}
fragment GitAutomationStatePayload on GitAutomationStatePayload {
  __typename
  gitAutomationState {
    ...GitAutomationState
  }
  lastSyncId
  success
}`);
const DeleteGitAutomationStateDocument = new TypedDocumentString(`
    mutation deleteGitAutomationState($id: String!) {
  gitAutomationStateDelete(id: $id) {
    ...DeletePayload
  }
}
    fragment DeletePayload on DeletePayload {
  __typename
  entityId
  lastSyncId
  success
}`);
const UpdateGitAutomationStateDocument = new TypedDocumentString(`
    mutation updateGitAutomationState($id: String!, $input: GitAutomationStateUpdateInput!) {
  gitAutomationStateUpdate(id: $id, input: $input) {
    ...GitAutomationStatePayload
  }
}
    fragment GitAutomationTargetBranch on GitAutomationTargetBranch {
  __typename
  updatedAt
  branchPattern
  team {
    id
  }
  archivedAt
  createdAt
  id
  isRegex
}
fragment GitAutomationState on GitAutomationState {
  __typename
  state {
    id
  }
  event
  updatedAt
  targetBranch {
    ...GitAutomationTargetBranch
  }
  team {
    id
  }
  archivedAt
  createdAt
  id
  branchPattern
}
fragment GitAutomationStatePayload on GitAutomationStatePayload {
  __typename
  gitAutomationState {
    ...GitAutomationState
  }
  lastSyncId
  success
}`);
const CreateGitAutomationTargetBranchDocument = new TypedDocumentString(`
    mutation createGitAutomationTargetBranch($input: GitAutomationTargetBranchCreateInput!) {
  gitAutomationTargetBranchCreate(input: $input) {
    ...GitAutomationTargetBranchPayload
  }
}
    fragment GitAutomationTargetBranch on GitAutomationTargetBranch {
  __typename
  updatedAt
  branchPattern
  team {
    id
  }
  archivedAt
  createdAt
  id
  isRegex
}
fragment GitAutomationTargetBranchPayload on GitAutomationTargetBranchPayload {
  __typename
  targetBranch {
    ...GitAutomationTargetBranch
  }
  lastSyncId
  success
}`);
const DeleteGitAutomationTargetBranchDocument = new TypedDocumentString(`
    mutation deleteGitAutomationTargetBranch($id: String!) {
  gitAutomationTargetBranchDelete(id: $id) {
    ...DeletePayload
  }
}
    fragment DeletePayload on DeletePayload {
  __typename
  entityId
  lastSyncId
  success
}`);
const UpdateGitAutomationTargetBranchDocument = new TypedDocumentString(`
    mutation updateGitAutomationTargetBranch($id: String!, $input: GitAutomationTargetBranchUpdateInput!) {
  gitAutomationTargetBranchUpdate(id: $id, input: $input) {
    ...GitAutomationTargetBranchPayload
  }
}
    fragment GitAutomationTargetBranch on GitAutomationTargetBranch {
  __typename
  updatedAt
  branchPattern
  team {
    id
  }
  archivedAt
  createdAt
  id
  isRegex
}
fragment GitAutomationTargetBranchPayload on GitAutomationTargetBranchPayload {
  __typename
  targetBranch {
    ...GitAutomationTargetBranch
  }
  lastSyncId
  success
}`);
const GoogleUserAccountAuthDocument = new TypedDocumentString(`
    mutation googleUserAccountAuth($input: GoogleUserAccountAuthInput!) {
  googleUserAccountAuth(input: $input) {
    ...AuthResolverResponse
  }
}
    fragment AuthUser on AuthUser {
  __typename
  avatarUrl
  organization {
    ...AuthOrganization
  }
  createdAt
  displayName
  email
  name
  userAccountId
  active
  role
  id
}
fragment AuthOrganization on AuthOrganization {
  __typename
  allowedAuthServices
  previousUrlKeys
  serviceId
  releaseChannel
  logoUrl
  name
  urlKey
  region
  deletionRequestedAt
  createdAt
  id
  samlEnabled
  scimEnabled
  enabled
  userCount
}
fragment AuthResolverResponse on AuthResolverResponse {
  __typename
  token
  email
  lastUsedOrganizationId
  users {
    ...AuthUser
  }
  lockedUsers {
    ...AuthUser
  }
  lockedOrganizations {
    ...AuthOrganization
  }
  availableOrganizations {
    ...AuthOrganization
  }
  allowDomainAccess
  id
}`);
const ImageUploadFromUrlDocument = new TypedDocumentString(`
    mutation imageUploadFromUrl($url: String!) {
  imageUploadFromUrl(url: $url) {
    ...ImageUploadFromUrlPayload
  }
}
    fragment ImageUploadFromUrlPayload on ImageUploadFromUrlPayload {
  __typename
  url
  lastSyncId
  success
}`);
const ImportFileUploadDocument = new TypedDocumentString(`
    mutation importFileUpload($contentType: String!, $filename: String!, $metaData: JSON, $size: Int!) {
  importFileUpload(
    contentType: $contentType
    filename: $filename
    metaData: $metaData
    size: $size
  ) {
    ...UploadPayload
  }
}
    fragment UploadFile on UploadFile {
  __typename
  assetUrl
  contentType
  filename
  uploadUrl
  size
  headers {
    ...UploadFileHeader
  }
  metaData
}
fragment UploadFileHeader on UploadFileHeader {
  __typename
  key
  value
}
fragment UploadPayload on UploadPayload {
  __typename
  uploadFile {
    ...UploadFile
  }
  lastSyncId
  success
}`);
const ArchiveInitiativeDocument = new TypedDocumentString(`
    mutation archiveInitiative($id: String!) {
  initiativeArchive(id: $id) {
    ...InitiativeArchivePayload
  }
}
    fragment InitiativeArchivePayload on InitiativeArchivePayload {
  __typename
  entity {
    id
  }
  lastSyncId
  success
}`);
const CreateInitiativeDocument = new TypedDocumentString(`
    mutation createInitiative($input: InitiativeCreateInput!) {
  initiativeCreate(input: $input) {
    ...InitiativePayload
  }
}
    fragment InitiativePayload on InitiativePayload {
  __typename
  lastSyncId
  initiative {
    id
  }
  success
}`);
const DeleteInitiativeDocument = new TypedDocumentString(`
    mutation deleteInitiative($id: String!) {
  initiativeDelete(id: $id) {
    ...DeletePayload
  }
}
    fragment DeletePayload on DeletePayload {
  __typename
  entityId
  lastSyncId
  success
}`);
const CreateInitiativeRelationDocument = new TypedDocumentString(`
    mutation createInitiativeRelation($input: InitiativeRelationCreateInput!) {
  initiativeRelationCreate(input: $input) {
    ...InitiativeRelationPayload
  }
}
    fragment InitiativeRelation on InitiativeRelation {
  __typename
  relatedInitiative {
    id
  }
  updatedAt
  user {
    id
  }
  initiative {
    id
  }
  sortOrder
  archivedAt
  createdAt
  id
}
fragment InitiativeRelationPayload on InitiativeRelationPayload {
  __typename
  lastSyncId
  initiativeRelation {
    ...InitiativeRelation
  }
  success
}`);
const DeleteInitiativeRelationDocument = new TypedDocumentString(`
    mutation deleteInitiativeRelation($id: String!) {
  initiativeRelationDelete(id: $id) {
    ...DeletePayload
  }
}
    fragment DeletePayload on DeletePayload {
  __typename
  entityId
  lastSyncId
  success
}`);
const UpdateInitiativeRelationDocument = new TypedDocumentString(`
    mutation updateInitiativeRelation($id: String!, $input: InitiativeRelationUpdateInput!) {
  initiativeRelationUpdate(id: $id, input: $input) {
    ...DeletePayload
  }
}
    fragment DeletePayload on DeletePayload {
  __typename
  entityId
  lastSyncId
  success
}`);
const CreateInitiativeToProjectDocument = new TypedDocumentString(`
    mutation createInitiativeToProject($input: InitiativeToProjectCreateInput!) {
  initiativeToProjectCreate(input: $input) {
    ...InitiativeToProjectPayload
  }
}
    fragment InitiativeToProjectPayload on InitiativeToProjectPayload {
  __typename
  lastSyncId
  initiativeToProject {
    id
  }
  success
}`);
const DeleteInitiativeToProjectDocument = new TypedDocumentString(`
    mutation deleteInitiativeToProject($id: String!) {
  initiativeToProjectDelete(id: $id) {
    ...DeletePayload
  }
}
    fragment DeletePayload on DeletePayload {
  __typename
  entityId
  lastSyncId
  success
}`);
const UpdateInitiativeToProjectDocument = new TypedDocumentString(`
    mutation updateInitiativeToProject($id: String!, $input: InitiativeToProjectUpdateInput!) {
  initiativeToProjectUpdate(id: $id, input: $input) {
    ...InitiativeToProjectPayload
  }
}
    fragment InitiativeToProjectPayload on InitiativeToProjectPayload {
  __typename
  lastSyncId
  initiativeToProject {
    id
  }
  success
}`);
const UnarchiveInitiativeDocument = new TypedDocumentString(`
    mutation unarchiveInitiative($id: String!) {
  initiativeUnarchive(id: $id) {
    ...InitiativeArchivePayload
  }
}
    fragment InitiativeArchivePayload on InitiativeArchivePayload {
  __typename
  entity {
    id
  }
  lastSyncId
  success
}`);
const UpdateInitiativeDocument = new TypedDocumentString(`
    mutation updateInitiative($id: String!, $input: InitiativeUpdateInput!) {
  initiativeUpdate(id: $id, input: $input) {
    ...InitiativePayload
  }
}
    fragment InitiativePayload on InitiativePayload {
  __typename
  lastSyncId
  initiative {
    id
  }
  success
}`);
const ArchiveInitiativeUpdateDocument = new TypedDocumentString(`
    mutation archiveInitiativeUpdate($id: String!) {
  initiativeUpdateArchive(id: $id) {
    ...InitiativeUpdateArchivePayload
  }
}
    fragment InitiativeUpdateArchivePayload on InitiativeUpdateArchivePayload {
  __typename
  entity {
    id
  }
  lastSyncId
  success
}`);
const CreateInitiativeUpdateDocument = new TypedDocumentString(`
    mutation createInitiativeUpdate($input: InitiativeUpdateCreateInput!) {
  initiativeUpdateCreate(input: $input) {
    ...InitiativeUpdatePayload
  }
}
    fragment InitiativeUpdatePayload on InitiativeUpdatePayload {
  __typename
  lastSyncId
  initiativeUpdate {
    id
  }
  success
}`);
const UnarchiveInitiativeUpdateDocument = new TypedDocumentString(`
    mutation unarchiveInitiativeUpdate($id: String!) {
  initiativeUpdateUnarchive(id: $id) {
    ...InitiativeUpdateArchivePayload
  }
}
    fragment InitiativeUpdateArchivePayload on InitiativeUpdateArchivePayload {
  __typename
  entity {
    id
  }
  lastSyncId
  success
}`);
const UpdateInitiativeUpdateDocument = new TypedDocumentString(`
    mutation updateInitiativeUpdate($id: String!, $input: InitiativeUpdateUpdateInput!) {
  initiativeUpdateUpdate(id: $id, input: $input) {
    ...InitiativeUpdatePayload
  }
}
    fragment InitiativeUpdatePayload on InitiativeUpdatePayload {
  __typename
  lastSyncId
  initiativeUpdate {
    id
  }
  success
}`);
const ArchiveIntegrationDocument = new TypedDocumentString(`
    mutation archiveIntegration($id: String!) {
  integrationArchive(id: $id) {
    ...DeletePayload
  }
}
    fragment DeletePayload on DeletePayload {
  __typename
  entityId
  lastSyncId
  success
}`);
const IntegrationAsksConnectChannelDocument = new TypedDocumentString(`
    mutation integrationAsksConnectChannel($code: String!, $redirectUri: String!) {
  integrationAsksConnectChannel(code: $code, redirectUri: $redirectUri) {
    ...AsksChannelConnectPayload
  }
}
    fragment SlackChannelNameMapping on SlackChannelNameMapping {
  __typename
  id
  name
  autoCreateTemplateId
  autoCreateOnBotMention
  postCancellationUpdates
  postCompletionUpdates
  postAcceptedFromTriageUpdates
  botAdded
  isPrivate
  isShared
  aiTitles
  autoCreateOnMessage
  autoCreateOnEmoji
  teams {
    ...SlackAsksTeamSettings
  }
}
fragment SlackAsksTeamSettings on SlackAsksTeamSettings {
  __typename
  id
  hasDefaultAsk
}
fragment AsksChannelConnectPayload on AsksChannelConnectPayload {
  __typename
  lastSyncId
  integration {
    id
  }
  mapping {
    ...SlackChannelNameMapping
  }
  addBot
  success
}`);
const DeleteIntegrationDocument = new TypedDocumentString(`
    mutation deleteIntegration($id: String!, $skipInstallationDeletion: Boolean) {
  integrationDelete(id: $id, skipInstallationDeletion: $skipInstallationDeletion) {
    ...DeletePayload
  }
}
    fragment DeletePayload on DeletePayload {
  __typename
  entityId
  lastSyncId
  success
}`);
const IntegrationDiscordDocument = new TypedDocumentString(`
    mutation integrationDiscord($code: String!, $redirectUri: String!) {
  integrationDiscord(code: $code, redirectUri: $redirectUri) {
    ...IntegrationPayload
  }
}
    fragment IntegrationPayload on IntegrationPayload {
  __typename
  lastSyncId
  integration {
    id
  }
  success
}`);
const IntegrationFigmaDocument = new TypedDocumentString(`
    mutation integrationFigma($code: String!, $redirectUri: String!) {
  integrationFigma(code: $code, redirectUri: $redirectUri) {
    ...IntegrationPayload
  }
}
    fragment IntegrationPayload on IntegrationPayload {
  __typename
  lastSyncId
  integration {
    id
  }
  success
}`);
const IntegrationFrontDocument = new TypedDocumentString(`
    mutation integrationFront($code: String!, $redirectUri: String!) {
  integrationFront(code: $code, redirectUri: $redirectUri) {
    ...IntegrationPayload
  }
}
    fragment IntegrationPayload on IntegrationPayload {
  __typename
  lastSyncId
  integration {
    id
  }
  success
}`);
const IntegrationGitHubEnterpriseServerConnectDocument = new TypedDocumentString(`
    mutation integrationGitHubEnterpriseServerConnect($githubUrl: String!, $organizationName: String!) {
  integrationGitHubEnterpriseServerConnect(
    githubUrl: $githubUrl
    organizationName: $organizationName
  ) {
    ...GitHubEnterpriseServerPayload
  }
}
    fragment GitHubEnterpriseServerPayload on GitHubEnterpriseServerPayload {
  __typename
  installUrl
  lastSyncId
  integration {
    id
  }
  setupUrl
  webhookSecret
  success
}`);
const IntegrationGitHubPersonalDocument = new TypedDocumentString(`
    mutation integrationGitHubPersonal($code: String!, $codeAccess: Boolean) {
  integrationGitHubPersonal(code: $code, codeAccess: $codeAccess) {
    ...IntegrationPayload
  }
}
    fragment IntegrationPayload on IntegrationPayload {
  __typename
  lastSyncId
  integration {
    id
  }
  success
}`);
const CreateIntegrationGithubCommitDocument = new TypedDocumentString(`
    mutation createIntegrationGithubCommit {
  integrationGithubCommitCreate {
    ...GitHubCommitIntegrationPayload
  }
}
    fragment GitHubCommitIntegrationPayload on GitHubCommitIntegrationPayload {
  __typename
  lastSyncId
  integration {
    id
  }
  webhookSecret
  success
}`);
const IntegrationGithubConnectDocument = new TypedDocumentString(`
    mutation integrationGithubConnect($code: String!, $codeAccess: Boolean, $installationId: String!) {
  integrationGithubConnect(
    code: $code
    codeAccess: $codeAccess
    installationId: $installationId
  ) {
    ...IntegrationPayload
  }
}
    fragment IntegrationPayload on IntegrationPayload {
  __typename
  lastSyncId
  integration {
    id
  }
  success
}`);
const IntegrationGithubImportConnectDocument = new TypedDocumentString(`
    mutation integrationGithubImportConnect($code: String!, $installationId: String!) {
  integrationGithubImportConnect(code: $code, installationId: $installationId) {
    ...IntegrationPayload
  }
}
    fragment IntegrationPayload on IntegrationPayload {
  __typename
  lastSyncId
  integration {
    id
  }
  success
}`);
const IntegrationGithubImportRefreshDocument = new TypedDocumentString(`
    mutation integrationGithubImportRefresh($id: String!) {
  integrationGithubImportRefresh(id: $id) {
    ...IntegrationPayload
  }
}
    fragment IntegrationPayload on IntegrationPayload {
  __typename
  lastSyncId
  integration {
    id
  }
  success
}`);
const IntegrationGitlabConnectDocument = new TypedDocumentString(`
    mutation integrationGitlabConnect($accessToken: String!, $gitlabUrl: String!) {
  integrationGitlabConnect(accessToken: $accessToken, gitlabUrl: $gitlabUrl) {
    ...GitLabIntegrationCreatePayload
  }
}
    fragment GitLabIntegrationCreatePayload on GitLabIntegrationCreatePayload {
  __typename
  lastSyncId
  integration {
    id
  }
  webhookSecret
  success
}`);
const IntegrationGongDocument = new TypedDocumentString(`
    mutation integrationGong($code: String!, $redirectUri: String!) {
  integrationGong(code: $code, redirectUri: $redirectUri) {
    ...IntegrationPayload
  }
}
    fragment IntegrationPayload on IntegrationPayload {
  __typename
  lastSyncId
  integration {
    id
  }
  success
}`);
const IntegrationGoogleSheetsDocument = new TypedDocumentString(`
    mutation integrationGoogleSheets($code: String!) {
  integrationGoogleSheets(code: $code) {
    ...IntegrationPayload
  }
}
    fragment IntegrationPayload on IntegrationPayload {
  __typename
  lastSyncId
  integration {
    id
  }
  success
}`);
const IntegrationIntercomDocument = new TypedDocumentString(`
    mutation integrationIntercom($code: String!, $domainUrl: String, $redirectUri: String!) {
  integrationIntercom(
    code: $code
    domainUrl: $domainUrl
    redirectUri: $redirectUri
  ) {
    ...IntegrationPayload
  }
}
    fragment IntegrationPayload on IntegrationPayload {
  __typename
  lastSyncId
  integration {
    id
  }
  success
}`);
const DeleteIntegrationIntercomDocument = new TypedDocumentString(`
    mutation deleteIntegrationIntercom {
  integrationIntercomDelete {
    ...IntegrationPayload
  }
}
    fragment IntegrationPayload on IntegrationPayload {
  __typename
  lastSyncId
  integration {
    id
  }
  success
}`);
const UpdateIntegrationIntercomSettingsDocument = new TypedDocumentString(`
    mutation updateIntegrationIntercomSettings($input: IntercomSettingsInput!) {
  integrationIntercomSettingsUpdate(input: $input) {
    ...IntegrationPayload
  }
}
    fragment IntegrationPayload on IntegrationPayload {
  __typename
  lastSyncId
  integration {
    id
  }
  success
}`);
const IntegrationJiraPersonalDocument = new TypedDocumentString(`
    mutation integrationJiraPersonal($accessToken: String, $code: String) {
  integrationJiraPersonal(accessToken: $accessToken, code: $code) {
    ...IntegrationPayload
  }
}
    fragment IntegrationPayload on IntegrationPayload {
  __typename
  lastSyncId
  integration {
    id
  }
  success
}`);
const IntegrationLoomDocument = new TypedDocumentString(`
    mutation integrationLoom {
  integrationLoom {
    ...IntegrationPayload
  }
}
    fragment IntegrationPayload on IntegrationPayload {
  __typename
  lastSyncId
  integration {
    id
  }
  success
}`);
const IntegrationRequestDocument = new TypedDocumentString(`
    mutation integrationRequest($input: IntegrationRequestInput!) {
  integrationRequest(input: $input) {
    ...IntegrationRequestPayload
  }
}
    fragment IntegrationRequestPayload on IntegrationRequestPayload {
  __typename
  success
}`);
const IntegrationSalesforceDocument = new TypedDocumentString(`
    mutation integrationSalesforce($code: String!, $redirectUri: String!, $subdomain: String!) {
  integrationSalesforce(
    code: $code
    redirectUri: $redirectUri
    subdomain: $subdomain
  ) {
    ...IntegrationPayload
  }
}
    fragment IntegrationPayload on IntegrationPayload {
  __typename
  lastSyncId
  integration {
    id
  }
  success
}`);
const IntegrationSentryConnectDocument = new TypedDocumentString(`
    mutation integrationSentryConnect($code: String!, $installationId: String!, $organizationSlug: String!) {
  integrationSentryConnect(
    code: $code
    installationId: $installationId
    organizationSlug: $organizationSlug
  ) {
    ...IntegrationPayload
  }
}
    fragment IntegrationPayload on IntegrationPayload {
  __typename
  lastSyncId
  integration {
    id
  }
  success
}`);
const IntegrationSlackDocument = new TypedDocumentString(`
    mutation integrationSlack($code: String!, $redirectUri: String!, $shouldUseV2Auth: Boolean) {
  integrationSlack(
    code: $code
    redirectUri: $redirectUri
    shouldUseV2Auth: $shouldUseV2Auth
  ) {
    ...IntegrationPayload
  }
}
    fragment IntegrationPayload on IntegrationPayload {
  __typename
  lastSyncId
  integration {
    id
  }
  success
}`);
const IntegrationSlackAsksDocument = new TypedDocumentString(`
    mutation integrationSlackAsks($code: String!, $redirectUri: String!) {
  integrationSlackAsks(code: $code, redirectUri: $redirectUri) {
    ...IntegrationPayload
  }
}
    fragment IntegrationPayload on IntegrationPayload {
  __typename
  lastSyncId
  integration {
    id
  }
  success
}`);
const IntegrationSlackCustomViewNotificationsDocument = new TypedDocumentString(`
    mutation integrationSlackCustomViewNotifications($code: String!, $customViewId: String!, $redirectUri: String!) {
  integrationSlackCustomViewNotifications(
    code: $code
    customViewId: $customViewId
    redirectUri: $redirectUri
  ) {
    ...SlackChannelConnectPayload
  }
}
    fragment SlackChannelConnectPayload on SlackChannelConnectPayload {
  __typename
  lastSyncId
  integration {
    id
  }
  nudgeToConnectMainSlackIntegration
  nudgeToUpdateMainSlackIntegration
  addBot
  success
}`);
const IntegrationSlackCustomerChannelLinkDocument = new TypedDocumentString(`
    mutation integrationSlackCustomerChannelLink($code: String!, $customerId: String!, $redirectUri: String!) {
  integrationSlackCustomerChannelLink(
    code: $code
    customerId: $customerId
    redirectUri: $redirectUri
  ) {
    ...SuccessPayload
  }
}
    fragment SuccessPayload on SuccessPayload {
  __typename
  lastSyncId
  success
}`);
const IntegrationSlackImportEmojisDocument = new TypedDocumentString(`
    mutation integrationSlackImportEmojis($code: String!, $redirectUri: String!) {
  integrationSlackImportEmojis(code: $code, redirectUri: $redirectUri) {
    ...IntegrationPayload
  }
}
    fragment IntegrationPayload on IntegrationPayload {
  __typename
  lastSyncId
  integration {
    id
  }
  success
}`);
const IntegrationSlackOrAsksUpdateSlackTeamNameDocument = new TypedDocumentString(`
    mutation integrationSlackOrAsksUpdateSlackTeamName($integrationId: String!) {
  integrationSlackOrAsksUpdateSlackTeamName(integrationId: $integrationId) {
    ...IntegrationSlackWorkspaceNamePayload
  }
}
    fragment IntegrationSlackWorkspaceNamePayload on IntegrationSlackWorkspaceNamePayload {
  __typename
  name
  success
}`);
const IntegrationSlackOrgProjectUpdatesPostDocument = new TypedDocumentString(`
    mutation integrationSlackOrgProjectUpdatesPost($code: String!, $redirectUri: String!) {
  integrationSlackOrgProjectUpdatesPost(code: $code, redirectUri: $redirectUri) {
    ...SlackChannelConnectPayload
  }
}
    fragment SlackChannelConnectPayload on SlackChannelConnectPayload {
  __typename
  lastSyncId
  integration {
    id
  }
  nudgeToConnectMainSlackIntegration
  nudgeToUpdateMainSlackIntegration
  addBot
  success
}`);
const IntegrationSlackPersonalDocument = new TypedDocumentString(`
    mutation integrationSlackPersonal($code: String!, $redirectUri: String!) {
  integrationSlackPersonal(code: $code, redirectUri: $redirectUri) {
    ...IntegrationPayload
  }
}
    fragment IntegrationPayload on IntegrationPayload {
  __typename
  lastSyncId
  integration {
    id
  }
  success
}`);
const IntegrationSlackPostDocument = new TypedDocumentString(`
    mutation integrationSlackPost($code: String!, $redirectUri: String!, $shouldUseV2Auth: Boolean, $teamId: String!) {
  integrationSlackPost(
    code: $code
    redirectUri: $redirectUri
    shouldUseV2Auth: $shouldUseV2Auth
    teamId: $teamId
  ) {
    ...SlackChannelConnectPayload
  }
}
    fragment SlackChannelConnectPayload on SlackChannelConnectPayload {
  __typename
  lastSyncId
  integration {
    id
  }
  nudgeToConnectMainSlackIntegration
  nudgeToUpdateMainSlackIntegration
  addBot
  success
}`);
const IntegrationSlackProjectPostDocument = new TypedDocumentString(`
    mutation integrationSlackProjectPost($code: String!, $projectId: String!, $redirectUri: String!, $service: String!) {
  integrationSlackProjectPost(
    code: $code
    projectId: $projectId
    redirectUri: $redirectUri
    service: $service
  ) {
    ...SlackChannelConnectPayload
  }
}
    fragment SlackChannelConnectPayload on SlackChannelConnectPayload {
  __typename
  lastSyncId
  integration {
    id
  }
  nudgeToConnectMainSlackIntegration
  nudgeToUpdateMainSlackIntegration
  addBot
  success
}`);
const CreateIntegrationTemplateDocument = new TypedDocumentString(`
    mutation createIntegrationTemplate($input: IntegrationTemplateCreateInput!) {
  integrationTemplateCreate(input: $input) {
    ...IntegrationTemplatePayload
  }
}
    fragment IntegrationTemplatePayload on IntegrationTemplatePayload {
  __typename
  integrationTemplate {
    id
  }
  lastSyncId
  success
}`);
const DeleteIntegrationTemplateDocument = new TypedDocumentString(`
    mutation deleteIntegrationTemplate($id: String!) {
  integrationTemplateDelete(id: $id) {
    ...DeletePayload
  }
}
    fragment DeletePayload on DeletePayload {
  __typename
  entityId
  lastSyncId
  success
}`);
const IntegrationZendeskDocument = new TypedDocumentString(`
    mutation integrationZendesk($code: String!, $redirectUri: String!, $scope: String!, $subdomain: String!) {
  integrationZendesk(
    code: $code
    redirectUri: $redirectUri
    scope: $scope
    subdomain: $subdomain
  ) {
    ...IntegrationPayload
  }
}
    fragment IntegrationPayload on IntegrationPayload {
  __typename
  lastSyncId
  integration {
    id
  }
  success
}`);
const CreateIntegrationsSettingsDocument = new TypedDocumentString(`
    mutation createIntegrationsSettings($input: IntegrationsSettingsCreateInput!) {
  integrationsSettingsCreate(input: $input) {
    ...IntegrationsSettingsPayload
  }
}
    fragment IntegrationsSettingsPayload on IntegrationsSettingsPayload {
  __typename
  lastSyncId
  integrationsSettings {
    id
  }
  success
}`);
const UpdateIntegrationsSettingsDocument = new TypedDocumentString(`
    mutation updateIntegrationsSettings($id: String!, $input: IntegrationsSettingsUpdateInput!) {
  integrationsSettingsUpdate(id: $id, input: $input) {
    ...IntegrationsSettingsPayload
  }
}
    fragment IntegrationsSettingsPayload on IntegrationsSettingsPayload {
  __typename
  lastSyncId
  integrationsSettings {
    id
  }
  success
}`);
const IssueAddLabelDocument = new TypedDocumentString(`
    mutation issueAddLabel($id: String!, $labelId: String!) {
  issueAddLabel(id: $id, labelId: $labelId) {
    ...IssuePayload
  }
}
    fragment IssuePayload on IssuePayload {
  __typename
  lastSyncId
  issue {
    id
  }
  success
}`);
const ArchiveIssueDocument = new TypedDocumentString(`
    mutation archiveIssue($id: String!, $trash: Boolean) {
  issueArchive(id: $id, trash: $trash) {
    ...IssueArchivePayload
  }
}
    fragment IssueArchivePayload on IssueArchivePayload {
  __typename
  entity {
    id
  }
  lastSyncId
  success
}`);
const CreateIssueBatchDocument = new TypedDocumentString(`
    mutation createIssueBatch($input: IssueBatchCreateInput!) {
  issueBatchCreate(input: $input) {
    ...IssueBatchPayload
  }
}
    fragment ActorBot on ActorBot {
  __typename
  avatarUrl
  name
  userDisplayName
  subType
  type
  id
}
fragment Reaction on Reaction {
  __typename
  emoji
  comment {
    id
  }
  externalUser {
    id
  }
  initiativeUpdate {
    id
  }
  issue {
    id
  }
  updatedAt
  projectUpdate {
    id
  }
  archivedAt
  createdAt
  id
  user {
    id
  }
}
fragment Issue on Issue {
  __typename
  trashed
  reactionData
  labelIds
  integrationSourceType
  url
  identifier
  priorityLabel
  previousIdentifiers
  reactions {
    ...Reaction
  }
  customerTicketCount
  branchName
  delegate {
    id
  }
  botActor {
    ...ActorBot
  }
  sourceComment {
    id
  }
  cycle {
    id
  }
  dueDate
  estimate
  syncedWith {
    ...ExternalEntityInfo
  }
  externalUserCreator {
    id
  }
  asksExternalUserRequester {
    id
  }
  asksRequester {
    id
  }
  description
  title
  number
  lastAppliedTemplate {
    id
  }
  updatedAt
  boardOrder
  prioritySortOrder
  sortOrder
  subIssueSortOrder
  parent {
    id
  }
  priority
  project {
    id
  }
  projectMilestone {
    id
  }
  recurringIssueTemplate {
    id
  }
  team {
    id
  }
  archivedAt
  createdAt
  startedTriageAt
  triagedAt
  addedToCycleAt
  addedToProjectAt
  addedToTeamAt
  autoArchivedAt
  autoClosedAt
  canceledAt
  completedAt
  startedAt
  slaStartedAt
  slaBreachesAt
  slaHighRiskAt
  slaMediumRiskAt
  snoozedUntilAt
  slaType
  id
  assignee {
    id
  }
  creator {
    id
  }
  snoozedBy {
    id
  }
  favorite {
    id
  }
  state {
    id
  }
}
fragment ExternalEntityInfo on ExternalEntityInfo {
  __typename
  metadata {
    ... on ExternalEntityInfoGithubMetadata {
      ...ExternalEntityInfoGithubMetadata
    }
    ... on ExternalEntityInfoJiraMetadata {
      ...ExternalEntityInfoJiraMetadata
    }
    ... on ExternalEntitySlackMetadata {
      ...ExternalEntitySlackMetadata
    }
  }
  id
  service
}
fragment ExternalEntityInfoGithubMetadata on ExternalEntityInfoGithubMetadata {
  __typename
  number
  owner
  repo
}
fragment ExternalEntityInfoJiraMetadata on ExternalEntityInfoJiraMetadata {
  __typename
  issueTypeId
  projectId
  issueKey
}
fragment ExternalEntitySlackMetadata on ExternalEntitySlackMetadata {
  __typename
  messageUrl
  channelId
  channelName
  isFromSlack
}
fragment IssueBatchPayload on IssueBatchPayload {
  __typename
  lastSyncId
  issues {
    ...Issue
  }
  success
}`);
const UpdateIssueBatchDocument = new TypedDocumentString(`
    mutation updateIssueBatch($ids: [UUID!]!, $input: IssueUpdateInput!) {
  issueBatchUpdate(ids: $ids, input: $input) {
    ...IssueBatchPayload
  }
}
    fragment ActorBot on ActorBot {
  __typename
  avatarUrl
  name
  userDisplayName
  subType
  type
  id
}
fragment Reaction on Reaction {
  __typename
  emoji
  comment {
    id
  }
  externalUser {
    id
  }
  initiativeUpdate {
    id
  }
  issue {
    id
  }
  updatedAt
  projectUpdate {
    id
  }
  archivedAt
  createdAt
  id
  user {
    id
  }
}
fragment Issue on Issue {
  __typename
  trashed
  reactionData
  labelIds
  integrationSourceType
  url
  identifier
  priorityLabel
  previousIdentifiers
  reactions {
    ...Reaction
  }
  customerTicketCount
  branchName
  delegate {
    id
  }
  botActor {
    ...ActorBot
  }
  sourceComment {
    id
  }
  cycle {
    id
  }
  dueDate
  estimate
  syncedWith {
    ...ExternalEntityInfo
  }
  externalUserCreator {
    id
  }
  asksExternalUserRequester {
    id
  }
  asksRequester {
    id
  }
  description
  title
  number
  lastAppliedTemplate {
    id
  }
  updatedAt
  boardOrder
  prioritySortOrder
  sortOrder
  subIssueSortOrder
  parent {
    id
  }
  priority
  project {
    id
  }
  projectMilestone {
    id
  }
  recurringIssueTemplate {
    id
  }
  team {
    id
  }
  archivedAt
  createdAt
  startedTriageAt
  triagedAt
  addedToCycleAt
  addedToProjectAt
  addedToTeamAt
  autoArchivedAt
  autoClosedAt
  canceledAt
  completedAt
  startedAt
  slaStartedAt
  slaBreachesAt
  slaHighRiskAt
  slaMediumRiskAt
  snoozedUntilAt
  slaType
  id
  assignee {
    id
  }
  creator {
    id
  }
  snoozedBy {
    id
  }
  favorite {
    id
  }
  state {
    id
  }
}
fragment ExternalEntityInfo on ExternalEntityInfo {
  __typename
  metadata {
    ... on ExternalEntityInfoGithubMetadata {
      ...ExternalEntityInfoGithubMetadata
    }
    ... on ExternalEntityInfoJiraMetadata {
      ...ExternalEntityInfoJiraMetadata
    }
    ... on ExternalEntitySlackMetadata {
      ...ExternalEntitySlackMetadata
    }
  }
  id
  service
}
fragment ExternalEntityInfoGithubMetadata on ExternalEntityInfoGithubMetadata {
  __typename
  number
  owner
  repo
}
fragment ExternalEntityInfoJiraMetadata on ExternalEntityInfoJiraMetadata {
  __typename
  issueTypeId
  projectId
  issueKey
}
fragment ExternalEntitySlackMetadata on ExternalEntitySlackMetadata {
  __typename
  messageUrl
  channelId
  channelName
  isFromSlack
}
fragment IssueBatchPayload on IssueBatchPayload {
  __typename
  lastSyncId
  issues {
    ...Issue
  }
  success
}`);
const CreateIssueDocument = new TypedDocumentString(`
    mutation createIssue($input: IssueCreateInput!) {
  issueCreate(input: $input) {
    ...IssuePayload
  }
}
    fragment IssuePayload on IssuePayload {
  __typename
  lastSyncId
  issue {
    id
  }
  success
}`);
const DeleteIssueDocument = new TypedDocumentString(`
    mutation deleteIssue($id: String!, $permanentlyDelete: Boolean) {
  issueDelete(id: $id, permanentlyDelete: $permanentlyDelete) {
    ...IssueArchivePayload
  }
}
    fragment IssueArchivePayload on IssueArchivePayload {
  __typename
  entity {
    id
  }
  lastSyncId
  success
}`);
const IssueExternalSyncDisableDocument = new TypedDocumentString(`
    mutation issueExternalSyncDisable($attachmentId: String!) {
  issueExternalSyncDisable(attachmentId: $attachmentId) {
    ...IssuePayload
  }
}
    fragment IssuePayload on IssuePayload {
  __typename
  lastSyncId
  issue {
    id
  }
  success
}`);
const IssueImportCreateAsanaDocument = new TypedDocumentString(`
    mutation issueImportCreateAsana($asanaTeamName: String!, $asanaToken: String!, $id: String, $includeClosedIssues: Boolean, $instantProcess: Boolean, $teamId: String, $teamName: String) {
  issueImportCreateAsana(
    asanaTeamName: $asanaTeamName
    asanaToken: $asanaToken
    id: $id
    includeClosedIssues: $includeClosedIssues
    instantProcess: $instantProcess
    teamId: $teamId
    teamName: $teamName
  ) {
    ...IssueImportPayload
  }
}
    fragment IssueImport on IssueImport {
  __typename
  progress
  errorMetadata
  csvFileUrl
  serviceMetadata
  teamName
  mapping
  displayName
  creatorId
  updatedAt
  service
  status
  archivedAt
  createdAt
  id
  error
}
fragment IssueImportPayload on IssueImportPayload {
  __typename
  lastSyncId
  issueImport {
    ...IssueImport
  }
  success
}`);
const IssueImportCreateCsvJiraDocument = new TypedDocumentString(`
    mutation issueImportCreateCSVJira($csvUrl: String!, $jiraEmail: String, $jiraHostname: String, $jiraToken: String, $teamId: String, $teamName: String) {
  issueImportCreateCSVJira(
    csvUrl: $csvUrl
    jiraEmail: $jiraEmail
    jiraHostname: $jiraHostname
    jiraToken: $jiraToken
    teamId: $teamId
    teamName: $teamName
  ) {
    ...IssueImportPayload
  }
}
    fragment IssueImport on IssueImport {
  __typename
  progress
  errorMetadata
  csvFileUrl
  serviceMetadata
  teamName
  mapping
  displayName
  creatorId
  updatedAt
  service
  status
  archivedAt
  createdAt
  id
  error
}
fragment IssueImportPayload on IssueImportPayload {
  __typename
  lastSyncId
  issueImport {
    ...IssueImport
  }
  success
}`);
const IssueImportCreateClubhouseDocument = new TypedDocumentString(`
    mutation issueImportCreateClubhouse($clubhouseGroupName: String!, $clubhouseToken: String!, $id: String, $includeClosedIssues: Boolean, $instantProcess: Boolean, $teamId: String, $teamName: String) {
  issueImportCreateClubhouse(
    clubhouseGroupName: $clubhouseGroupName
    clubhouseToken: $clubhouseToken
    id: $id
    includeClosedIssues: $includeClosedIssues
    instantProcess: $instantProcess
    teamId: $teamId
    teamName: $teamName
  ) {
    ...IssueImportPayload
  }
}
    fragment IssueImport on IssueImport {
  __typename
  progress
  errorMetadata
  csvFileUrl
  serviceMetadata
  teamName
  mapping
  displayName
  creatorId
  updatedAt
  service
  status
  archivedAt
  createdAt
  id
  error
}
fragment IssueImportPayload on IssueImportPayload {
  __typename
  lastSyncId
  issueImport {
    ...IssueImport
  }
  success
}`);
const IssueImportCreateGithubDocument = new TypedDocumentString(`
    mutation issueImportCreateGithub($githubLabels: [String!], $githubRepoIds: [Int!], $includeClosedIssues: Boolean, $instantProcess: Boolean, $teamId: String, $teamName: String) {
  issueImportCreateGithub(
    githubLabels: $githubLabels
    githubRepoIds: $githubRepoIds
    includeClosedIssues: $includeClosedIssues
    instantProcess: $instantProcess
    teamId: $teamId
    teamName: $teamName
  ) {
    ...IssueImportPayload
  }
}
    fragment IssueImport on IssueImport {
  __typename
  progress
  errorMetadata
  csvFileUrl
  serviceMetadata
  teamName
  mapping
  displayName
  creatorId
  updatedAt
  service
  status
  archivedAt
  createdAt
  id
  error
}
fragment IssueImportPayload on IssueImportPayload {
  __typename
  lastSyncId
  issueImport {
    ...IssueImport
  }
  success
}`);
const IssueImportCreateJiraDocument = new TypedDocumentString(`
    mutation issueImportCreateJira($id: String, $includeClosedIssues: Boolean, $instantProcess: Boolean, $jiraEmail: String!, $jiraHostname: String!, $jiraProject: String!, $jiraToken: String!, $jql: String, $teamId: String, $teamName: String) {
  issueImportCreateJira(
    id: $id
    includeClosedIssues: $includeClosedIssues
    instantProcess: $instantProcess
    jiraEmail: $jiraEmail
    jiraHostname: $jiraHostname
    jiraProject: $jiraProject
    jiraToken: $jiraToken
    jql: $jql
    teamId: $teamId
    teamName: $teamName
  ) {
    ...IssueImportPayload
  }
}
    fragment IssueImport on IssueImport {
  __typename
  progress
  errorMetadata
  csvFileUrl
  serviceMetadata
  teamName
  mapping
  displayName
  creatorId
  updatedAt
  service
  status
  archivedAt
  createdAt
  id
  error
}
fragment IssueImportPayload on IssueImportPayload {
  __typename
  lastSyncId
  issueImport {
    ...IssueImport
  }
  success
}`);
const DeleteIssueImportDocument = new TypedDocumentString(`
    mutation deleteIssueImport($issueImportId: String!) {
  issueImportDelete(issueImportId: $issueImportId) {
    ...IssueImportDeletePayload
  }
}
    fragment IssueImport on IssueImport {
  __typename
  progress
  errorMetadata
  csvFileUrl
  serviceMetadata
  teamName
  mapping
  displayName
  creatorId
  updatedAt
  service
  status
  archivedAt
  createdAt
  id
  error
}
fragment IssueImportDeletePayload on IssueImportDeletePayload {
  __typename
  lastSyncId
  issueImport {
    ...IssueImport
  }
  success
}`);
const IssueImportProcessDocument = new TypedDocumentString(`
    mutation issueImportProcess($issueImportId: String!, $mapping: JSONObject!) {
  issueImportProcess(issueImportId: $issueImportId, mapping: $mapping) {
    ...IssueImportPayload
  }
}
    fragment IssueImport on IssueImport {
  __typename
  progress
  errorMetadata
  csvFileUrl
  serviceMetadata
  teamName
  mapping
  displayName
  creatorId
  updatedAt
  service
  status
  archivedAt
  createdAt
  id
  error
}
fragment IssueImportPayload on IssueImportPayload {
  __typename
  lastSyncId
  issueImport {
    ...IssueImport
  }
  success
}`);
const UpdateIssueImportDocument = new TypedDocumentString(`
    mutation updateIssueImport($id: String!, $input: IssueImportUpdateInput!) {
  issueImportUpdate(id: $id, input: $input) {
    ...IssueImportPayload
  }
}
    fragment IssueImport on IssueImport {
  __typename
  progress
  errorMetadata
  csvFileUrl
  serviceMetadata
  teamName
  mapping
  displayName
  creatorId
  updatedAt
  service
  status
  archivedAt
  createdAt
  id
  error
}
fragment IssueImportPayload on IssueImportPayload {
  __typename
  lastSyncId
  issueImport {
    ...IssueImport
  }
  success
}`);
const CreateIssueLabelDocument = new TypedDocumentString(`
    mutation createIssueLabel($input: IssueLabelCreateInput!, $replaceTeamLabels: Boolean) {
  issueLabelCreate(input: $input, replaceTeamLabels: $replaceTeamLabels) {
    ...IssueLabelPayload
  }
}
    fragment IssueLabelPayload on IssueLabelPayload {
  __typename
  lastSyncId
  issueLabel {
    id
  }
  success
}`);
const DeleteIssueLabelDocument = new TypedDocumentString(`
    mutation deleteIssueLabel($id: String!) {
  issueLabelDelete(id: $id) {
    ...DeletePayload
  }
}
    fragment DeletePayload on DeletePayload {
  __typename
  entityId
  lastSyncId
  success
}`);
const IssueLabelRestoreDocument = new TypedDocumentString(`
    mutation issueLabelRestore($id: String!) {
  issueLabelRestore(id: $id) {
    ...IssueLabelPayload
  }
}
    fragment IssueLabelPayload on IssueLabelPayload {
  __typename
  lastSyncId
  issueLabel {
    id
  }
  success
}`);
const IssueLabelRetireDocument = new TypedDocumentString(`
    mutation issueLabelRetire($id: String!) {
  issueLabelRetire(id: $id) {
    ...IssueLabelPayload
  }
}
    fragment IssueLabelPayload on IssueLabelPayload {
  __typename
  lastSyncId
  issueLabel {
    id
  }
  success
}`);
const UpdateIssueLabelDocument = new TypedDocumentString(`
    mutation updateIssueLabel($id: String!, $input: IssueLabelUpdateInput!, $replaceTeamLabels: Boolean) {
  issueLabelUpdate(id: $id, input: $input, replaceTeamLabels: $replaceTeamLabels) {
    ...IssueLabelPayload
  }
}
    fragment IssueLabelPayload on IssueLabelPayload {
  __typename
  lastSyncId
  issueLabel {
    id
  }
  success
}`);
const CreateIssueRelationDocument = new TypedDocumentString(`
    mutation createIssueRelation($input: IssueRelationCreateInput!, $overrideCreatedAt: DateTime) {
  issueRelationCreate(input: $input, overrideCreatedAt: $overrideCreatedAt) {
    ...IssueRelationPayload
  }
}
    fragment IssueRelationPayload on IssueRelationPayload {
  __typename
  lastSyncId
  issueRelation {
    id
  }
  success
}`);
const DeleteIssueRelationDocument = new TypedDocumentString(`
    mutation deleteIssueRelation($id: String!) {
  issueRelationDelete(id: $id) {
    ...DeletePayload
  }
}
    fragment DeletePayload on DeletePayload {
  __typename
  entityId
  lastSyncId
  success
}`);
const UpdateIssueRelationDocument = new TypedDocumentString(`
    mutation updateIssueRelation($id: String!, $input: IssueRelationUpdateInput!) {
  issueRelationUpdate(id: $id, input: $input) {
    ...IssueRelationPayload
  }
}
    fragment IssueRelationPayload on IssueRelationPayload {
  __typename
  lastSyncId
  issueRelation {
    id
  }
  success
}`);
const IssueReminderDocument = new TypedDocumentString(`
    mutation issueReminder($id: String!, $reminderAt: DateTime!) {
  issueReminder(id: $id, reminderAt: $reminderAt) {
    ...IssuePayload
  }
}
    fragment IssuePayload on IssuePayload {
  __typename
  lastSyncId
  issue {
    id
  }
  success
}`);
const IssueRemoveLabelDocument = new TypedDocumentString(`
    mutation issueRemoveLabel($id: String!, $labelId: String!) {
  issueRemoveLabel(id: $id, labelId: $labelId) {
    ...IssuePayload
  }
}
    fragment IssuePayload on IssuePayload {
  __typename
  lastSyncId
  issue {
    id
  }
  success
}`);
const IssueSubscribeDocument = new TypedDocumentString(`
    mutation issueSubscribe($id: String!, $userEmail: String, $userId: String) {
  issueSubscribe(id: $id, userEmail: $userEmail, userId: $userId) {
    ...IssuePayload
  }
}
    fragment IssuePayload on IssuePayload {
  __typename
  lastSyncId
  issue {
    id
  }
  success
}`);
const UnarchiveIssueDocument = new TypedDocumentString(`
    mutation unarchiveIssue($id: String!) {
  issueUnarchive(id: $id) {
    ...IssueArchivePayload
  }
}
    fragment IssueArchivePayload on IssueArchivePayload {
  __typename
  entity {
    id
  }
  lastSyncId
  success
}`);
const IssueUnsubscribeDocument = new TypedDocumentString(`
    mutation issueUnsubscribe($id: String!, $userEmail: String, $userId: String) {
  issueUnsubscribe(id: $id, userEmail: $userEmail, userId: $userId) {
    ...IssuePayload
  }
}
    fragment IssuePayload on IssuePayload {
  __typename
  lastSyncId
  issue {
    id
  }
  success
}`);
const UpdateIssueDocument = new TypedDocumentString(`
    mutation updateIssue($id: String!, $input: IssueUpdateInput!) {
  issueUpdate(id: $id, input: $input) {
    ...IssuePayload
  }
}
    fragment IssuePayload on IssuePayload {
  __typename
  lastSyncId
  issue {
    id
  }
  success
}`);
const JoinOrganizationFromOnboardingDocument = new TypedDocumentString(`
    mutation joinOrganizationFromOnboarding($input: JoinOrganizationInput!) {
  joinOrganizationFromOnboarding(input: $input) {
    ...CreateOrJoinOrganizationResponse
  }
}
    fragment AuthUser on AuthUser {
  __typename
  avatarUrl
  organization {
    ...AuthOrganization
  }
  createdAt
  displayName
  email
  name
  userAccountId
  active
  role
  id
}
fragment AuthOrganization on AuthOrganization {
  __typename
  allowedAuthServices
  previousUrlKeys
  serviceId
  releaseChannel
  logoUrl
  name
  urlKey
  region
  deletionRequestedAt
  createdAt
  id
  samlEnabled
  scimEnabled
  enabled
  userCount
}
fragment CreateOrJoinOrganizationResponse on CreateOrJoinOrganizationResponse {
  __typename
  organization {
    ...AuthOrganization
  }
  user {
    ...AuthUser
  }
}`);
const LeaveOrganizationDocument = new TypedDocumentString(`
    mutation leaveOrganization($organizationId: String!) {
  leaveOrganization(organizationId: $organizationId) {
    ...CreateOrJoinOrganizationResponse
  }
}
    fragment AuthUser on AuthUser {
  __typename
  avatarUrl
  organization {
    ...AuthOrganization
  }
  createdAt
  displayName
  email
  name
  userAccountId
  active
  role
  id
}
fragment AuthOrganization on AuthOrganization {
  __typename
  allowedAuthServices
  previousUrlKeys
  serviceId
  releaseChannel
  logoUrl
  name
  urlKey
  region
  deletionRequestedAt
  createdAt
  id
  samlEnabled
  scimEnabled
  enabled
  userCount
}
fragment CreateOrJoinOrganizationResponse on CreateOrJoinOrganizationResponse {
  __typename
  organization {
    ...AuthOrganization
  }
  user {
    ...AuthUser
  }
}`);
const LogoutDocument = new TypedDocumentString(`
    mutation logout($reason: String) {
  logout(reason: $reason) {
    ...LogoutResponse
  }
}
    fragment LogoutResponse on LogoutResponse {
  __typename
  success
}`);
const LogoutAllSessionsDocument = new TypedDocumentString(`
    mutation logoutAllSessions($reason: String) {
  logoutAllSessions(reason: $reason) {
    ...LogoutResponse
  }
}
    fragment LogoutResponse on LogoutResponse {
  __typename
  success
}`);
const LogoutOtherSessionsDocument = new TypedDocumentString(`
    mutation logoutOtherSessions($reason: String) {
  logoutOtherSessions(reason: $reason) {
    ...LogoutResponse
  }
}
    fragment LogoutResponse on LogoutResponse {
  __typename
  success
}`);
const LogoutSessionDocument = new TypedDocumentString(`
    mutation logoutSession($sessionId: String!) {
  logoutSession(sessionId: $sessionId) {
    ...LogoutResponse
  }
}
    fragment LogoutResponse on LogoutResponse {
  __typename
  success
}`);
const ArchiveNotificationDocument = new TypedDocumentString(`
    mutation archiveNotification($id: String!) {
  notificationArchive(id: $id) {
    ...NotificationArchivePayload
  }
}
    fragment ActorBot on ActorBot {
  __typename
  avatarUrl
  name
  userDisplayName
  subType
  type
  id
}
fragment CustomerNeedNotification on CustomerNeedNotification {
  __typename
  type
  customerNeedId
  botActor {
    ...ActorBot
  }
  category
  customerNeed {
    id
  }
  externalUserActor {
    id
  }
  relatedIssue {
    id
  }
  updatedAt
  relatedProject {
    id
  }
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment CustomerNotification on CustomerNotification {
  __typename
  type
  customerId
  botActor {
    ...ActorBot
  }
  category
  customer {
    id
  }
  externalUserActor {
    id
  }
  updatedAt
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment DocumentNotification on DocumentNotification {
  __typename
  reactionEmoji
  type
  commentId
  documentId
  parentCommentId
  botActor {
    ...ActorBot
  }
  category
  externalUserActor {
    id
  }
  updatedAt
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment NotificationArchivePayload on NotificationArchivePayload {
  __typename
  entity {
    ...Notification
  }
  lastSyncId
  success
}
fragment Notification on Notification {
  __typename
  type
  botActor {
    ...ActorBot
  }
  category
  externalUserActor {
    id
  }
  updatedAt
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
  ... on CustomerNeedNotification {
    ...CustomerNeedNotification
  }
  ... on CustomerNotification {
    ...CustomerNotification
  }
  ... on DocumentNotification {
    ...DocumentNotification
  }
  ... on InitiativeNotification {
    ...InitiativeNotification
  }
  ... on IssueNotification {
    ...IssueNotification
  }
  ... on OauthClientApprovalNotification {
    ...OauthClientApprovalNotification
  }
  ... on PostNotification {
    ...PostNotification
  }
  ... on ProjectNotification {
    ...ProjectNotification
  }
  ... on PullRequestNotification {
    ...PullRequestNotification
  }
}
fragment PostNotification on PostNotification {
  __typename
  reactionEmoji
  type
  commentId
  parentCommentId
  postId
  botActor {
    ...ActorBot
  }
  category
  externalUserActor {
    id
  }
  updatedAt
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment ProjectNotification on ProjectNotification {
  __typename
  reactionEmoji
  type
  commentId
  parentCommentId
  projectId
  projectMilestoneId
  projectUpdateId
  botActor {
    ...ActorBot
  }
  category
  comment {
    id
  }
  document {
    id
  }
  externalUserActor {
    id
  }
  updatedAt
  parentComment {
    id
  }
  project {
    id
  }
  projectUpdate {
    id
  }
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment PullRequestNotification on PullRequestNotification {
  __typename
  type
  pullRequestCommentId
  pullRequestId
  botActor {
    ...ActorBot
  }
  category
  externalUserActor {
    id
  }
  updatedAt
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment InitiativeNotification on InitiativeNotification {
  __typename
  reactionEmoji
  type
  commentId
  initiativeId
  initiativeUpdateId
  parentCommentId
  botActor {
    ...ActorBot
  }
  category
  comment {
    id
  }
  document {
    id
  }
  externalUserActor {
    id
  }
  initiative {
    id
  }
  initiativeUpdate {
    id
  }
  updatedAt
  parentComment {
    id
  }
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment IssueNotification on IssueNotification {
  __typename
  reactionEmoji
  type
  commentId
  issueId
  parentCommentId
  botActor {
    ...ActorBot
  }
  category
  comment {
    id
  }
  externalUserActor {
    id
  }
  issue {
    id
  }
  updatedAt
  parentComment {
    id
  }
  subscriptions {
    ...NotificationSubscription
  }
  team {
    id
  }
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment OauthClientApprovalNotification on OauthClientApprovalNotification {
  __typename
  type
  oauthClientApprovalId
  oauthClientApproval {
    ...OauthClientApproval
  }
  botActor {
    ...ActorBot
  }
  category
  externalUserActor {
    id
  }
  updatedAt
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment NotificationSubscription on NotificationSubscription {
  __typename
  customView {
    id
  }
  cycle {
    id
  }
  initiative {
    id
  }
  label {
    id
  }
  project {
    id
  }
  customer {
    id
  }
  updatedAt
  team {
    id
  }
  archivedAt
  createdAt
  userContextViewType
  contextViewType
  id
  subscriber {
    id
  }
  user {
    id
  }
  active
}
fragment OauthClientApproval on OauthClientApproval {
  __typename
  newlyRequestedScopes
  updatedAt
  requesterId
  responderId
  requestReason
  denyReason
  scopes
  status
  archivedAt
  createdAt
  id
  oauthClientId
}`);
const NotificationArchiveAllDocument = new TypedDocumentString(`
    mutation notificationArchiveAll($input: NotificationEntityInput!) {
  notificationArchiveAll(input: $input) {
    ...NotificationBatchActionPayload
  }
}
    fragment ActorBot on ActorBot {
  __typename
  avatarUrl
  name
  userDisplayName
  subType
  type
  id
}
fragment CustomerNeedNotification on CustomerNeedNotification {
  __typename
  type
  customerNeedId
  botActor {
    ...ActorBot
  }
  category
  customerNeed {
    id
  }
  externalUserActor {
    id
  }
  relatedIssue {
    id
  }
  updatedAt
  relatedProject {
    id
  }
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment CustomerNotification on CustomerNotification {
  __typename
  type
  customerId
  botActor {
    ...ActorBot
  }
  category
  customer {
    id
  }
  externalUserActor {
    id
  }
  updatedAt
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment DocumentNotification on DocumentNotification {
  __typename
  reactionEmoji
  type
  commentId
  documentId
  parentCommentId
  botActor {
    ...ActorBot
  }
  category
  externalUserActor {
    id
  }
  updatedAt
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment Notification on Notification {
  __typename
  type
  botActor {
    ...ActorBot
  }
  category
  externalUserActor {
    id
  }
  updatedAt
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
  ... on CustomerNeedNotification {
    ...CustomerNeedNotification
  }
  ... on CustomerNotification {
    ...CustomerNotification
  }
  ... on DocumentNotification {
    ...DocumentNotification
  }
  ... on InitiativeNotification {
    ...InitiativeNotification
  }
  ... on IssueNotification {
    ...IssueNotification
  }
  ... on OauthClientApprovalNotification {
    ...OauthClientApprovalNotification
  }
  ... on PostNotification {
    ...PostNotification
  }
  ... on ProjectNotification {
    ...ProjectNotification
  }
  ... on PullRequestNotification {
    ...PullRequestNotification
  }
}
fragment PostNotification on PostNotification {
  __typename
  reactionEmoji
  type
  commentId
  parentCommentId
  postId
  botActor {
    ...ActorBot
  }
  category
  externalUserActor {
    id
  }
  updatedAt
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment ProjectNotification on ProjectNotification {
  __typename
  reactionEmoji
  type
  commentId
  parentCommentId
  projectId
  projectMilestoneId
  projectUpdateId
  botActor {
    ...ActorBot
  }
  category
  comment {
    id
  }
  document {
    id
  }
  externalUserActor {
    id
  }
  updatedAt
  parentComment {
    id
  }
  project {
    id
  }
  projectUpdate {
    id
  }
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment PullRequestNotification on PullRequestNotification {
  __typename
  type
  pullRequestCommentId
  pullRequestId
  botActor {
    ...ActorBot
  }
  category
  externalUserActor {
    id
  }
  updatedAt
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment InitiativeNotification on InitiativeNotification {
  __typename
  reactionEmoji
  type
  commentId
  initiativeId
  initiativeUpdateId
  parentCommentId
  botActor {
    ...ActorBot
  }
  category
  comment {
    id
  }
  document {
    id
  }
  externalUserActor {
    id
  }
  initiative {
    id
  }
  initiativeUpdate {
    id
  }
  updatedAt
  parentComment {
    id
  }
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment IssueNotification on IssueNotification {
  __typename
  reactionEmoji
  type
  commentId
  issueId
  parentCommentId
  botActor {
    ...ActorBot
  }
  category
  comment {
    id
  }
  externalUserActor {
    id
  }
  issue {
    id
  }
  updatedAt
  parentComment {
    id
  }
  subscriptions {
    ...NotificationSubscription
  }
  team {
    id
  }
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment OauthClientApprovalNotification on OauthClientApprovalNotification {
  __typename
  type
  oauthClientApprovalId
  oauthClientApproval {
    ...OauthClientApproval
  }
  botActor {
    ...ActorBot
  }
  category
  externalUserActor {
    id
  }
  updatedAt
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment NotificationSubscription on NotificationSubscription {
  __typename
  customView {
    id
  }
  cycle {
    id
  }
  initiative {
    id
  }
  label {
    id
  }
  project {
    id
  }
  customer {
    id
  }
  updatedAt
  team {
    id
  }
  archivedAt
  createdAt
  userContextViewType
  contextViewType
  id
  subscriber {
    id
  }
  user {
    id
  }
  active
}
fragment OauthClientApproval on OauthClientApproval {
  __typename
  newlyRequestedScopes
  updatedAt
  requesterId
  responderId
  requestReason
  denyReason
  scopes
  status
  archivedAt
  createdAt
  id
  oauthClientId
}
fragment NotificationBatchActionPayload on NotificationBatchActionPayload {
  __typename
  lastSyncId
  notifications {
    ...Notification
  }
  success
}`);
const UpdateNotificationCategoryChannelSubscriptionDocument = new TypedDocumentString(`
    mutation updateNotificationCategoryChannelSubscription($category: NotificationCategory!, $channel: NotificationChannel!, $subscribe: Boolean!) {
  notificationCategoryChannelSubscriptionUpdate(
    category: $category
    channel: $channel
    subscribe: $subscribe
  ) {
    ...UserSettingsPayload
  }
}
    fragment UserSettingsPayload on UserSettingsPayload {
  __typename
  lastSyncId
  success
}`);
const NotificationMarkReadAllDocument = new TypedDocumentString(`
    mutation notificationMarkReadAll($input: NotificationEntityInput!, $readAt: DateTime!) {
  notificationMarkReadAll(input: $input, readAt: $readAt) {
    ...NotificationBatchActionPayload
  }
}
    fragment ActorBot on ActorBot {
  __typename
  avatarUrl
  name
  userDisplayName
  subType
  type
  id
}
fragment CustomerNeedNotification on CustomerNeedNotification {
  __typename
  type
  customerNeedId
  botActor {
    ...ActorBot
  }
  category
  customerNeed {
    id
  }
  externalUserActor {
    id
  }
  relatedIssue {
    id
  }
  updatedAt
  relatedProject {
    id
  }
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment CustomerNotification on CustomerNotification {
  __typename
  type
  customerId
  botActor {
    ...ActorBot
  }
  category
  customer {
    id
  }
  externalUserActor {
    id
  }
  updatedAt
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment DocumentNotification on DocumentNotification {
  __typename
  reactionEmoji
  type
  commentId
  documentId
  parentCommentId
  botActor {
    ...ActorBot
  }
  category
  externalUserActor {
    id
  }
  updatedAt
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment Notification on Notification {
  __typename
  type
  botActor {
    ...ActorBot
  }
  category
  externalUserActor {
    id
  }
  updatedAt
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
  ... on CustomerNeedNotification {
    ...CustomerNeedNotification
  }
  ... on CustomerNotification {
    ...CustomerNotification
  }
  ... on DocumentNotification {
    ...DocumentNotification
  }
  ... on InitiativeNotification {
    ...InitiativeNotification
  }
  ... on IssueNotification {
    ...IssueNotification
  }
  ... on OauthClientApprovalNotification {
    ...OauthClientApprovalNotification
  }
  ... on PostNotification {
    ...PostNotification
  }
  ... on ProjectNotification {
    ...ProjectNotification
  }
  ... on PullRequestNotification {
    ...PullRequestNotification
  }
}
fragment PostNotification on PostNotification {
  __typename
  reactionEmoji
  type
  commentId
  parentCommentId
  postId
  botActor {
    ...ActorBot
  }
  category
  externalUserActor {
    id
  }
  updatedAt
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment ProjectNotification on ProjectNotification {
  __typename
  reactionEmoji
  type
  commentId
  parentCommentId
  projectId
  projectMilestoneId
  projectUpdateId
  botActor {
    ...ActorBot
  }
  category
  comment {
    id
  }
  document {
    id
  }
  externalUserActor {
    id
  }
  updatedAt
  parentComment {
    id
  }
  project {
    id
  }
  projectUpdate {
    id
  }
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment PullRequestNotification on PullRequestNotification {
  __typename
  type
  pullRequestCommentId
  pullRequestId
  botActor {
    ...ActorBot
  }
  category
  externalUserActor {
    id
  }
  updatedAt
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment InitiativeNotification on InitiativeNotification {
  __typename
  reactionEmoji
  type
  commentId
  initiativeId
  initiativeUpdateId
  parentCommentId
  botActor {
    ...ActorBot
  }
  category
  comment {
    id
  }
  document {
    id
  }
  externalUserActor {
    id
  }
  initiative {
    id
  }
  initiativeUpdate {
    id
  }
  updatedAt
  parentComment {
    id
  }
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment IssueNotification on IssueNotification {
  __typename
  reactionEmoji
  type
  commentId
  issueId
  parentCommentId
  botActor {
    ...ActorBot
  }
  category
  comment {
    id
  }
  externalUserActor {
    id
  }
  issue {
    id
  }
  updatedAt
  parentComment {
    id
  }
  subscriptions {
    ...NotificationSubscription
  }
  team {
    id
  }
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment OauthClientApprovalNotification on OauthClientApprovalNotification {
  __typename
  type
  oauthClientApprovalId
  oauthClientApproval {
    ...OauthClientApproval
  }
  botActor {
    ...ActorBot
  }
  category
  externalUserActor {
    id
  }
  updatedAt
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment NotificationSubscription on NotificationSubscription {
  __typename
  customView {
    id
  }
  cycle {
    id
  }
  initiative {
    id
  }
  label {
    id
  }
  project {
    id
  }
  customer {
    id
  }
  updatedAt
  team {
    id
  }
  archivedAt
  createdAt
  userContextViewType
  contextViewType
  id
  subscriber {
    id
  }
  user {
    id
  }
  active
}
fragment OauthClientApproval on OauthClientApproval {
  __typename
  newlyRequestedScopes
  updatedAt
  requesterId
  responderId
  requestReason
  denyReason
  scopes
  status
  archivedAt
  createdAt
  id
  oauthClientId
}
fragment NotificationBatchActionPayload on NotificationBatchActionPayload {
  __typename
  lastSyncId
  notifications {
    ...Notification
  }
  success
}`);
const NotificationMarkUnreadAllDocument = new TypedDocumentString(`
    mutation notificationMarkUnreadAll($input: NotificationEntityInput!) {
  notificationMarkUnreadAll(input: $input) {
    ...NotificationBatchActionPayload
  }
}
    fragment ActorBot on ActorBot {
  __typename
  avatarUrl
  name
  userDisplayName
  subType
  type
  id
}
fragment CustomerNeedNotification on CustomerNeedNotification {
  __typename
  type
  customerNeedId
  botActor {
    ...ActorBot
  }
  category
  customerNeed {
    id
  }
  externalUserActor {
    id
  }
  relatedIssue {
    id
  }
  updatedAt
  relatedProject {
    id
  }
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment CustomerNotification on CustomerNotification {
  __typename
  type
  customerId
  botActor {
    ...ActorBot
  }
  category
  customer {
    id
  }
  externalUserActor {
    id
  }
  updatedAt
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment DocumentNotification on DocumentNotification {
  __typename
  reactionEmoji
  type
  commentId
  documentId
  parentCommentId
  botActor {
    ...ActorBot
  }
  category
  externalUserActor {
    id
  }
  updatedAt
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment Notification on Notification {
  __typename
  type
  botActor {
    ...ActorBot
  }
  category
  externalUserActor {
    id
  }
  updatedAt
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
  ... on CustomerNeedNotification {
    ...CustomerNeedNotification
  }
  ... on CustomerNotification {
    ...CustomerNotification
  }
  ... on DocumentNotification {
    ...DocumentNotification
  }
  ... on InitiativeNotification {
    ...InitiativeNotification
  }
  ... on IssueNotification {
    ...IssueNotification
  }
  ... on OauthClientApprovalNotification {
    ...OauthClientApprovalNotification
  }
  ... on PostNotification {
    ...PostNotification
  }
  ... on ProjectNotification {
    ...ProjectNotification
  }
  ... on PullRequestNotification {
    ...PullRequestNotification
  }
}
fragment PostNotification on PostNotification {
  __typename
  reactionEmoji
  type
  commentId
  parentCommentId
  postId
  botActor {
    ...ActorBot
  }
  category
  externalUserActor {
    id
  }
  updatedAt
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment ProjectNotification on ProjectNotification {
  __typename
  reactionEmoji
  type
  commentId
  parentCommentId
  projectId
  projectMilestoneId
  projectUpdateId
  botActor {
    ...ActorBot
  }
  category
  comment {
    id
  }
  document {
    id
  }
  externalUserActor {
    id
  }
  updatedAt
  parentComment {
    id
  }
  project {
    id
  }
  projectUpdate {
    id
  }
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment PullRequestNotification on PullRequestNotification {
  __typename
  type
  pullRequestCommentId
  pullRequestId
  botActor {
    ...ActorBot
  }
  category
  externalUserActor {
    id
  }
  updatedAt
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment InitiativeNotification on InitiativeNotification {
  __typename
  reactionEmoji
  type
  commentId
  initiativeId
  initiativeUpdateId
  parentCommentId
  botActor {
    ...ActorBot
  }
  category
  comment {
    id
  }
  document {
    id
  }
  externalUserActor {
    id
  }
  initiative {
    id
  }
  initiativeUpdate {
    id
  }
  updatedAt
  parentComment {
    id
  }
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment IssueNotification on IssueNotification {
  __typename
  reactionEmoji
  type
  commentId
  issueId
  parentCommentId
  botActor {
    ...ActorBot
  }
  category
  comment {
    id
  }
  externalUserActor {
    id
  }
  issue {
    id
  }
  updatedAt
  parentComment {
    id
  }
  subscriptions {
    ...NotificationSubscription
  }
  team {
    id
  }
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment OauthClientApprovalNotification on OauthClientApprovalNotification {
  __typename
  type
  oauthClientApprovalId
  oauthClientApproval {
    ...OauthClientApproval
  }
  botActor {
    ...ActorBot
  }
  category
  externalUserActor {
    id
  }
  updatedAt
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment NotificationSubscription on NotificationSubscription {
  __typename
  customView {
    id
  }
  cycle {
    id
  }
  initiative {
    id
  }
  label {
    id
  }
  project {
    id
  }
  customer {
    id
  }
  updatedAt
  team {
    id
  }
  archivedAt
  createdAt
  userContextViewType
  contextViewType
  id
  subscriber {
    id
  }
  user {
    id
  }
  active
}
fragment OauthClientApproval on OauthClientApproval {
  __typename
  newlyRequestedScopes
  updatedAt
  requesterId
  responderId
  requestReason
  denyReason
  scopes
  status
  archivedAt
  createdAt
  id
  oauthClientId
}
fragment NotificationBatchActionPayload on NotificationBatchActionPayload {
  __typename
  lastSyncId
  notifications {
    ...Notification
  }
  success
}`);
const NotificationSnoozeAllDocument = new TypedDocumentString(`
    mutation notificationSnoozeAll($input: NotificationEntityInput!, $snoozedUntilAt: DateTime!) {
  notificationSnoozeAll(input: $input, snoozedUntilAt: $snoozedUntilAt) {
    ...NotificationBatchActionPayload
  }
}
    fragment ActorBot on ActorBot {
  __typename
  avatarUrl
  name
  userDisplayName
  subType
  type
  id
}
fragment CustomerNeedNotification on CustomerNeedNotification {
  __typename
  type
  customerNeedId
  botActor {
    ...ActorBot
  }
  category
  customerNeed {
    id
  }
  externalUserActor {
    id
  }
  relatedIssue {
    id
  }
  updatedAt
  relatedProject {
    id
  }
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment CustomerNotification on CustomerNotification {
  __typename
  type
  customerId
  botActor {
    ...ActorBot
  }
  category
  customer {
    id
  }
  externalUserActor {
    id
  }
  updatedAt
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment DocumentNotification on DocumentNotification {
  __typename
  reactionEmoji
  type
  commentId
  documentId
  parentCommentId
  botActor {
    ...ActorBot
  }
  category
  externalUserActor {
    id
  }
  updatedAt
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment Notification on Notification {
  __typename
  type
  botActor {
    ...ActorBot
  }
  category
  externalUserActor {
    id
  }
  updatedAt
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
  ... on CustomerNeedNotification {
    ...CustomerNeedNotification
  }
  ... on CustomerNotification {
    ...CustomerNotification
  }
  ... on DocumentNotification {
    ...DocumentNotification
  }
  ... on InitiativeNotification {
    ...InitiativeNotification
  }
  ... on IssueNotification {
    ...IssueNotification
  }
  ... on OauthClientApprovalNotification {
    ...OauthClientApprovalNotification
  }
  ... on PostNotification {
    ...PostNotification
  }
  ... on ProjectNotification {
    ...ProjectNotification
  }
  ... on PullRequestNotification {
    ...PullRequestNotification
  }
}
fragment PostNotification on PostNotification {
  __typename
  reactionEmoji
  type
  commentId
  parentCommentId
  postId
  botActor {
    ...ActorBot
  }
  category
  externalUserActor {
    id
  }
  updatedAt
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment ProjectNotification on ProjectNotification {
  __typename
  reactionEmoji
  type
  commentId
  parentCommentId
  projectId
  projectMilestoneId
  projectUpdateId
  botActor {
    ...ActorBot
  }
  category
  comment {
    id
  }
  document {
    id
  }
  externalUserActor {
    id
  }
  updatedAt
  parentComment {
    id
  }
  project {
    id
  }
  projectUpdate {
    id
  }
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment PullRequestNotification on PullRequestNotification {
  __typename
  type
  pullRequestCommentId
  pullRequestId
  botActor {
    ...ActorBot
  }
  category
  externalUserActor {
    id
  }
  updatedAt
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment InitiativeNotification on InitiativeNotification {
  __typename
  reactionEmoji
  type
  commentId
  initiativeId
  initiativeUpdateId
  parentCommentId
  botActor {
    ...ActorBot
  }
  category
  comment {
    id
  }
  document {
    id
  }
  externalUserActor {
    id
  }
  initiative {
    id
  }
  initiativeUpdate {
    id
  }
  updatedAt
  parentComment {
    id
  }
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment IssueNotification on IssueNotification {
  __typename
  reactionEmoji
  type
  commentId
  issueId
  parentCommentId
  botActor {
    ...ActorBot
  }
  category
  comment {
    id
  }
  externalUserActor {
    id
  }
  issue {
    id
  }
  updatedAt
  parentComment {
    id
  }
  subscriptions {
    ...NotificationSubscription
  }
  team {
    id
  }
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment OauthClientApprovalNotification on OauthClientApprovalNotification {
  __typename
  type
  oauthClientApprovalId
  oauthClientApproval {
    ...OauthClientApproval
  }
  botActor {
    ...ActorBot
  }
  category
  externalUserActor {
    id
  }
  updatedAt
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment NotificationSubscription on NotificationSubscription {
  __typename
  customView {
    id
  }
  cycle {
    id
  }
  initiative {
    id
  }
  label {
    id
  }
  project {
    id
  }
  customer {
    id
  }
  updatedAt
  team {
    id
  }
  archivedAt
  createdAt
  userContextViewType
  contextViewType
  id
  subscriber {
    id
  }
  user {
    id
  }
  active
}
fragment OauthClientApproval on OauthClientApproval {
  __typename
  newlyRequestedScopes
  updatedAt
  requesterId
  responderId
  requestReason
  denyReason
  scopes
  status
  archivedAt
  createdAt
  id
  oauthClientId
}
fragment NotificationBatchActionPayload on NotificationBatchActionPayload {
  __typename
  lastSyncId
  notifications {
    ...Notification
  }
  success
}`);
const CreateNotificationSubscriptionDocument = new TypedDocumentString(`
    mutation createNotificationSubscription($input: NotificationSubscriptionCreateInput!) {
  notificationSubscriptionCreate(input: $input) {
    ...NotificationSubscriptionPayload
  }
}
    fragment NotificationSubscription on NotificationSubscription {
  __typename
  customView {
    id
  }
  cycle {
    id
  }
  initiative {
    id
  }
  label {
    id
  }
  project {
    id
  }
  customer {
    id
  }
  updatedAt
  team {
    id
  }
  archivedAt
  createdAt
  userContextViewType
  contextViewType
  id
  subscriber {
    id
  }
  user {
    id
  }
  active
}
fragment NotificationSubscriptionPayload on NotificationSubscriptionPayload {
  __typename
  lastSyncId
  notificationSubscription {
    ...NotificationSubscription
  }
  success
}`);
const DeleteNotificationSubscriptionDocument = new TypedDocumentString(`
    mutation deleteNotificationSubscription($id: String!) {
  notificationSubscriptionDelete(id: $id) {
    ...DeletePayload
  }
}
    fragment DeletePayload on DeletePayload {
  __typename
  entityId
  lastSyncId
  success
}`);
const UpdateNotificationSubscriptionDocument = new TypedDocumentString(`
    mutation updateNotificationSubscription($id: String!, $input: NotificationSubscriptionUpdateInput!) {
  notificationSubscriptionUpdate(id: $id, input: $input) {
    ...NotificationSubscriptionPayload
  }
}
    fragment NotificationSubscription on NotificationSubscription {
  __typename
  customView {
    id
  }
  cycle {
    id
  }
  initiative {
    id
  }
  label {
    id
  }
  project {
    id
  }
  customer {
    id
  }
  updatedAt
  team {
    id
  }
  archivedAt
  createdAt
  userContextViewType
  contextViewType
  id
  subscriber {
    id
  }
  user {
    id
  }
  active
}
fragment NotificationSubscriptionPayload on NotificationSubscriptionPayload {
  __typename
  lastSyncId
  notificationSubscription {
    ...NotificationSubscription
  }
  success
}`);
const UnarchiveNotificationDocument = new TypedDocumentString(`
    mutation unarchiveNotification($id: String!) {
  notificationUnarchive(id: $id) {
    ...NotificationArchivePayload
  }
}
    fragment ActorBot on ActorBot {
  __typename
  avatarUrl
  name
  userDisplayName
  subType
  type
  id
}
fragment CustomerNeedNotification on CustomerNeedNotification {
  __typename
  type
  customerNeedId
  botActor {
    ...ActorBot
  }
  category
  customerNeed {
    id
  }
  externalUserActor {
    id
  }
  relatedIssue {
    id
  }
  updatedAt
  relatedProject {
    id
  }
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment CustomerNotification on CustomerNotification {
  __typename
  type
  customerId
  botActor {
    ...ActorBot
  }
  category
  customer {
    id
  }
  externalUserActor {
    id
  }
  updatedAt
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment DocumentNotification on DocumentNotification {
  __typename
  reactionEmoji
  type
  commentId
  documentId
  parentCommentId
  botActor {
    ...ActorBot
  }
  category
  externalUserActor {
    id
  }
  updatedAt
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment NotificationArchivePayload on NotificationArchivePayload {
  __typename
  entity {
    ...Notification
  }
  lastSyncId
  success
}
fragment Notification on Notification {
  __typename
  type
  botActor {
    ...ActorBot
  }
  category
  externalUserActor {
    id
  }
  updatedAt
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
  ... on CustomerNeedNotification {
    ...CustomerNeedNotification
  }
  ... on CustomerNotification {
    ...CustomerNotification
  }
  ... on DocumentNotification {
    ...DocumentNotification
  }
  ... on InitiativeNotification {
    ...InitiativeNotification
  }
  ... on IssueNotification {
    ...IssueNotification
  }
  ... on OauthClientApprovalNotification {
    ...OauthClientApprovalNotification
  }
  ... on PostNotification {
    ...PostNotification
  }
  ... on ProjectNotification {
    ...ProjectNotification
  }
  ... on PullRequestNotification {
    ...PullRequestNotification
  }
}
fragment PostNotification on PostNotification {
  __typename
  reactionEmoji
  type
  commentId
  parentCommentId
  postId
  botActor {
    ...ActorBot
  }
  category
  externalUserActor {
    id
  }
  updatedAt
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment ProjectNotification on ProjectNotification {
  __typename
  reactionEmoji
  type
  commentId
  parentCommentId
  projectId
  projectMilestoneId
  projectUpdateId
  botActor {
    ...ActorBot
  }
  category
  comment {
    id
  }
  document {
    id
  }
  externalUserActor {
    id
  }
  updatedAt
  parentComment {
    id
  }
  project {
    id
  }
  projectUpdate {
    id
  }
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment PullRequestNotification on PullRequestNotification {
  __typename
  type
  pullRequestCommentId
  pullRequestId
  botActor {
    ...ActorBot
  }
  category
  externalUserActor {
    id
  }
  updatedAt
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment InitiativeNotification on InitiativeNotification {
  __typename
  reactionEmoji
  type
  commentId
  initiativeId
  initiativeUpdateId
  parentCommentId
  botActor {
    ...ActorBot
  }
  category
  comment {
    id
  }
  document {
    id
  }
  externalUserActor {
    id
  }
  initiative {
    id
  }
  initiativeUpdate {
    id
  }
  updatedAt
  parentComment {
    id
  }
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment IssueNotification on IssueNotification {
  __typename
  reactionEmoji
  type
  commentId
  issueId
  parentCommentId
  botActor {
    ...ActorBot
  }
  category
  comment {
    id
  }
  externalUserActor {
    id
  }
  issue {
    id
  }
  updatedAt
  parentComment {
    id
  }
  subscriptions {
    ...NotificationSubscription
  }
  team {
    id
  }
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment OauthClientApprovalNotification on OauthClientApprovalNotification {
  __typename
  type
  oauthClientApprovalId
  oauthClientApproval {
    ...OauthClientApproval
  }
  botActor {
    ...ActorBot
  }
  category
  externalUserActor {
    id
  }
  updatedAt
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment NotificationSubscription on NotificationSubscription {
  __typename
  customView {
    id
  }
  cycle {
    id
  }
  initiative {
    id
  }
  label {
    id
  }
  project {
    id
  }
  customer {
    id
  }
  updatedAt
  team {
    id
  }
  archivedAt
  createdAt
  userContextViewType
  contextViewType
  id
  subscriber {
    id
  }
  user {
    id
  }
  active
}
fragment OauthClientApproval on OauthClientApproval {
  __typename
  newlyRequestedScopes
  updatedAt
  requesterId
  responderId
  requestReason
  denyReason
  scopes
  status
  archivedAt
  createdAt
  id
  oauthClientId
}`);
const NotificationUnsnoozeAllDocument = new TypedDocumentString(`
    mutation notificationUnsnoozeAll($input: NotificationEntityInput!, $unsnoozedAt: DateTime!) {
  notificationUnsnoozeAll(input: $input, unsnoozedAt: $unsnoozedAt) {
    ...NotificationBatchActionPayload
  }
}
    fragment ActorBot on ActorBot {
  __typename
  avatarUrl
  name
  userDisplayName
  subType
  type
  id
}
fragment CustomerNeedNotification on CustomerNeedNotification {
  __typename
  type
  customerNeedId
  botActor {
    ...ActorBot
  }
  category
  customerNeed {
    id
  }
  externalUserActor {
    id
  }
  relatedIssue {
    id
  }
  updatedAt
  relatedProject {
    id
  }
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment CustomerNotification on CustomerNotification {
  __typename
  type
  customerId
  botActor {
    ...ActorBot
  }
  category
  customer {
    id
  }
  externalUserActor {
    id
  }
  updatedAt
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment DocumentNotification on DocumentNotification {
  __typename
  reactionEmoji
  type
  commentId
  documentId
  parentCommentId
  botActor {
    ...ActorBot
  }
  category
  externalUserActor {
    id
  }
  updatedAt
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment Notification on Notification {
  __typename
  type
  botActor {
    ...ActorBot
  }
  category
  externalUserActor {
    id
  }
  updatedAt
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
  ... on CustomerNeedNotification {
    ...CustomerNeedNotification
  }
  ... on CustomerNotification {
    ...CustomerNotification
  }
  ... on DocumentNotification {
    ...DocumentNotification
  }
  ... on InitiativeNotification {
    ...InitiativeNotification
  }
  ... on IssueNotification {
    ...IssueNotification
  }
  ... on OauthClientApprovalNotification {
    ...OauthClientApprovalNotification
  }
  ... on PostNotification {
    ...PostNotification
  }
  ... on ProjectNotification {
    ...ProjectNotification
  }
  ... on PullRequestNotification {
    ...PullRequestNotification
  }
}
fragment PostNotification on PostNotification {
  __typename
  reactionEmoji
  type
  commentId
  parentCommentId
  postId
  botActor {
    ...ActorBot
  }
  category
  externalUserActor {
    id
  }
  updatedAt
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment ProjectNotification on ProjectNotification {
  __typename
  reactionEmoji
  type
  commentId
  parentCommentId
  projectId
  projectMilestoneId
  projectUpdateId
  botActor {
    ...ActorBot
  }
  category
  comment {
    id
  }
  document {
    id
  }
  externalUserActor {
    id
  }
  updatedAt
  parentComment {
    id
  }
  project {
    id
  }
  projectUpdate {
    id
  }
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment PullRequestNotification on PullRequestNotification {
  __typename
  type
  pullRequestCommentId
  pullRequestId
  botActor {
    ...ActorBot
  }
  category
  externalUserActor {
    id
  }
  updatedAt
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment InitiativeNotification on InitiativeNotification {
  __typename
  reactionEmoji
  type
  commentId
  initiativeId
  initiativeUpdateId
  parentCommentId
  botActor {
    ...ActorBot
  }
  category
  comment {
    id
  }
  document {
    id
  }
  externalUserActor {
    id
  }
  initiative {
    id
  }
  initiativeUpdate {
    id
  }
  updatedAt
  parentComment {
    id
  }
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment IssueNotification on IssueNotification {
  __typename
  reactionEmoji
  type
  commentId
  issueId
  parentCommentId
  botActor {
    ...ActorBot
  }
  category
  comment {
    id
  }
  externalUserActor {
    id
  }
  issue {
    id
  }
  updatedAt
  parentComment {
    id
  }
  subscriptions {
    ...NotificationSubscription
  }
  team {
    id
  }
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment OauthClientApprovalNotification on OauthClientApprovalNotification {
  __typename
  type
  oauthClientApprovalId
  oauthClientApproval {
    ...OauthClientApproval
  }
  botActor {
    ...ActorBot
  }
  category
  externalUserActor {
    id
  }
  updatedAt
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment NotificationSubscription on NotificationSubscription {
  __typename
  customView {
    id
  }
  cycle {
    id
  }
  initiative {
    id
  }
  label {
    id
  }
  project {
    id
  }
  customer {
    id
  }
  updatedAt
  team {
    id
  }
  archivedAt
  createdAt
  userContextViewType
  contextViewType
  id
  subscriber {
    id
  }
  user {
    id
  }
  active
}
fragment OauthClientApproval on OauthClientApproval {
  __typename
  newlyRequestedScopes
  updatedAt
  requesterId
  responderId
  requestReason
  denyReason
  scopes
  status
  archivedAt
  createdAt
  id
  oauthClientId
}
fragment NotificationBatchActionPayload on NotificationBatchActionPayload {
  __typename
  lastSyncId
  notifications {
    ...Notification
  }
  success
}`);
const UpdateNotificationDocument = new TypedDocumentString(`
    mutation updateNotification($id: String!, $input: NotificationUpdateInput!) {
  notificationUpdate(id: $id, input: $input) {
    ...NotificationPayload
  }
}
    fragment ActorBot on ActorBot {
  __typename
  avatarUrl
  name
  userDisplayName
  subType
  type
  id
}
fragment CustomerNeedNotification on CustomerNeedNotification {
  __typename
  type
  customerNeedId
  botActor {
    ...ActorBot
  }
  category
  customerNeed {
    id
  }
  externalUserActor {
    id
  }
  relatedIssue {
    id
  }
  updatedAt
  relatedProject {
    id
  }
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment CustomerNotification on CustomerNotification {
  __typename
  type
  customerId
  botActor {
    ...ActorBot
  }
  category
  customer {
    id
  }
  externalUserActor {
    id
  }
  updatedAt
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment DocumentNotification on DocumentNotification {
  __typename
  reactionEmoji
  type
  commentId
  documentId
  parentCommentId
  botActor {
    ...ActorBot
  }
  category
  externalUserActor {
    id
  }
  updatedAt
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment Notification on Notification {
  __typename
  type
  botActor {
    ...ActorBot
  }
  category
  externalUserActor {
    id
  }
  updatedAt
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
  ... on CustomerNeedNotification {
    ...CustomerNeedNotification
  }
  ... on CustomerNotification {
    ...CustomerNotification
  }
  ... on DocumentNotification {
    ...DocumentNotification
  }
  ... on InitiativeNotification {
    ...InitiativeNotification
  }
  ... on IssueNotification {
    ...IssueNotification
  }
  ... on OauthClientApprovalNotification {
    ...OauthClientApprovalNotification
  }
  ... on PostNotification {
    ...PostNotification
  }
  ... on ProjectNotification {
    ...ProjectNotification
  }
  ... on PullRequestNotification {
    ...PullRequestNotification
  }
}
fragment PostNotification on PostNotification {
  __typename
  reactionEmoji
  type
  commentId
  parentCommentId
  postId
  botActor {
    ...ActorBot
  }
  category
  externalUserActor {
    id
  }
  updatedAt
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment ProjectNotification on ProjectNotification {
  __typename
  reactionEmoji
  type
  commentId
  parentCommentId
  projectId
  projectMilestoneId
  projectUpdateId
  botActor {
    ...ActorBot
  }
  category
  comment {
    id
  }
  document {
    id
  }
  externalUserActor {
    id
  }
  updatedAt
  parentComment {
    id
  }
  project {
    id
  }
  projectUpdate {
    id
  }
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment PullRequestNotification on PullRequestNotification {
  __typename
  type
  pullRequestCommentId
  pullRequestId
  botActor {
    ...ActorBot
  }
  category
  externalUserActor {
    id
  }
  updatedAt
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment InitiativeNotification on InitiativeNotification {
  __typename
  reactionEmoji
  type
  commentId
  initiativeId
  initiativeUpdateId
  parentCommentId
  botActor {
    ...ActorBot
  }
  category
  comment {
    id
  }
  document {
    id
  }
  externalUserActor {
    id
  }
  initiative {
    id
  }
  initiativeUpdate {
    id
  }
  updatedAt
  parentComment {
    id
  }
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment IssueNotification on IssueNotification {
  __typename
  reactionEmoji
  type
  commentId
  issueId
  parentCommentId
  botActor {
    ...ActorBot
  }
  category
  comment {
    id
  }
  externalUserActor {
    id
  }
  issue {
    id
  }
  updatedAt
  parentComment {
    id
  }
  subscriptions {
    ...NotificationSubscription
  }
  team {
    id
  }
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment OauthClientApprovalNotification on OauthClientApprovalNotification {
  __typename
  type
  oauthClientApprovalId
  oauthClientApproval {
    ...OauthClientApproval
  }
  botActor {
    ...ActorBot
  }
  category
  externalUserActor {
    id
  }
  updatedAt
  emailedAt
  readAt
  unsnoozedAt
  archivedAt
  createdAt
  snoozedUntilAt
  id
  actor {
    id
  }
  user {
    id
  }
}
fragment NotificationSubscription on NotificationSubscription {
  __typename
  customView {
    id
  }
  cycle {
    id
  }
  initiative {
    id
  }
  label {
    id
  }
  project {
    id
  }
  customer {
    id
  }
  updatedAt
  team {
    id
  }
  archivedAt
  createdAt
  userContextViewType
  contextViewType
  id
  subscriber {
    id
  }
  user {
    id
  }
  active
}
fragment OauthClientApproval on OauthClientApproval {
  __typename
  newlyRequestedScopes
  updatedAt
  requesterId
  responderId
  requestReason
  denyReason
  scopes
  status
  archivedAt
  createdAt
  id
  oauthClientId
}
fragment NotificationPayload on NotificationPayload {
  __typename
  lastSyncId
  notification {
    ...Notification
  }
  success
}`);
const DeleteOrganizationCancelDocument = new TypedDocumentString(`
    mutation deleteOrganizationCancel {
  organizationCancelDelete {
    ...OrganizationCancelDeletePayload
  }
}
    fragment OrganizationCancelDeletePayload on OrganizationCancelDeletePayload {
  __typename
  success
}`);
const DeleteOrganizationDocument = new TypedDocumentString(`
    mutation deleteOrganization($input: DeleteOrganizationInput!) {
  organizationDelete(input: $input) {
    ...OrganizationDeletePayload
  }
}
    fragment OrganizationDeletePayload on OrganizationDeletePayload {
  __typename
  success
}`);
const OrganizationDeleteChallengeDocument = new TypedDocumentString(`
    mutation organizationDeleteChallenge {
  organizationDeleteChallenge {
    ...OrganizationDeletePayload
  }
}
    fragment OrganizationDeletePayload on OrganizationDeletePayload {
  __typename
  success
}`);
const DeleteOrganizationDomainDocument = new TypedDocumentString(`
    mutation deleteOrganizationDomain($id: String!) {
  organizationDomainDelete(id: $id) {
    ...DeletePayload
  }
}
    fragment DeletePayload on DeletePayload {
  __typename
  entityId
  lastSyncId
  success
}`);
const CreateOrganizationInviteDocument = new TypedDocumentString(`
    mutation createOrganizationInvite($input: OrganizationInviteCreateInput!) {
  organizationInviteCreate(input: $input) {
    ...OrganizationInvitePayload
  }
}
    fragment OrganizationInvitePayload on OrganizationInvitePayload {
  __typename
  lastSyncId
  organizationInvite {
    id
  }
  success
}`);
const DeleteOrganizationInviteDocument = new TypedDocumentString(`
    mutation deleteOrganizationInvite($id: String!) {
  organizationInviteDelete(id: $id) {
    ...DeletePayload
  }
}
    fragment DeletePayload on DeletePayload {
  __typename
  entityId
  lastSyncId
  success
}`);
const UpdateOrganizationInviteDocument = new TypedDocumentString(`
    mutation updateOrganizationInvite($id: String!, $input: OrganizationInviteUpdateInput!) {
  organizationInviteUpdate(id: $id, input: $input) {
    ...OrganizationInvitePayload
  }
}
    fragment OrganizationInvitePayload on OrganizationInvitePayload {
  __typename
  lastSyncId
  organizationInvite {
    id
  }
  success
}`);
const OrganizationStartTrialDocument = new TypedDocumentString(`
    mutation organizationStartTrial {
  organizationStartTrial {
    ...OrganizationStartTrialPayload
  }
}
    fragment OrganizationStartTrialPayload on OrganizationStartTrialPayload {
  __typename
  success
}`);
const OrganizationStartTrialForPlanDocument = new TypedDocumentString(`
    mutation organizationStartTrialForPlan($input: OrganizationStartTrialInput!) {
  organizationStartTrialForPlan(input: $input) {
    ...OrganizationStartTrialPayload
  }
}
    fragment OrganizationStartTrialPayload on OrganizationStartTrialPayload {
  __typename
  success
}`);
const UpdateOrganizationDocument = new TypedDocumentString(`
    mutation updateOrganization($input: OrganizationUpdateInput!) {
  organizationUpdate(input: $input) {
    ...OrganizationPayload
  }
}
    fragment OrganizationPayload on OrganizationPayload {
  __typename
  lastSyncId
  success
}`);
const ProjectAddLabelDocument = new TypedDocumentString(`
    mutation projectAddLabel($id: String!, $labelId: String!) {
  projectAddLabel(id: $id, labelId: $labelId) {
    ...ProjectPayload
  }
}
    fragment ProjectPayload on ProjectPayload {
  __typename
  lastSyncId
  project {
    id
  }
  success
}`);
const ArchiveProjectDocument = new TypedDocumentString(`
    mutation archiveProject($id: String!, $trash: Boolean) {
  projectArchive(id: $id, trash: $trash) {
    ...ProjectArchivePayload
  }
}
    fragment ProjectArchivePayload on ProjectArchivePayload {
  __typename
  entity {
    id
  }
  lastSyncId
  success
}`);
const CreateProjectDocument = new TypedDocumentString(`
    mutation createProject($connectSlackChannel: Boolean, $input: ProjectCreateInput!) {
  projectCreate(connectSlackChannel: $connectSlackChannel, input: $input) {
    ...ProjectPayload
  }
}
    fragment ProjectPayload on ProjectPayload {
  __typename
  lastSyncId
  project {
    id
  }
  success
}`);
const DeleteProjectDocument = new TypedDocumentString(`
    mutation deleteProject($id: String!) {
  projectDelete(id: $id) {
    ...ProjectArchivePayload
  }
}
    fragment ProjectArchivePayload on ProjectArchivePayload {
  __typename
  entity {
    id
  }
  lastSyncId
  success
}`);
const ProjectExternalSyncDisableDocument = new TypedDocumentString(`
    mutation projectExternalSyncDisable($projectId: String!, $syncSource: ExternalSyncService!) {
  projectExternalSyncDisable(projectId: $projectId, syncSource: $syncSource) {
    ...ProjectPayload
  }
}
    fragment ProjectPayload on ProjectPayload {
  __typename
  lastSyncId
  project {
    id
  }
  success
}`);
const CreateProjectLabelDocument = new TypedDocumentString(`
    mutation createProjectLabel($input: ProjectLabelCreateInput!) {
  projectLabelCreate(input: $input) {
    ...ProjectLabelPayload
  }
}
    fragment ProjectLabelPayload on ProjectLabelPayload {
  __typename
  lastSyncId
  projectLabel {
    id
  }
  success
}`);
const DeleteProjectLabelDocument = new TypedDocumentString(`
    mutation deleteProjectLabel($id: String!) {
  projectLabelDelete(id: $id) {
    ...DeletePayload
  }
}
    fragment DeletePayload on DeletePayload {
  __typename
  entityId
  lastSyncId
  success
}`);
const ProjectLabelRestoreDocument = new TypedDocumentString(`
    mutation projectLabelRestore($id: String!) {
  projectLabelRestore(id: $id) {
    ...ProjectLabelPayload
  }
}
    fragment ProjectLabelPayload on ProjectLabelPayload {
  __typename
  lastSyncId
  projectLabel {
    id
  }
  success
}`);
const ProjectLabelRetireDocument = new TypedDocumentString(`
    mutation projectLabelRetire($id: String!) {
  projectLabelRetire(id: $id) {
    ...ProjectLabelPayload
  }
}
    fragment ProjectLabelPayload on ProjectLabelPayload {
  __typename
  lastSyncId
  projectLabel {
    id
  }
  success
}`);
const UpdateProjectLabelDocument = new TypedDocumentString(`
    mutation updateProjectLabel($id: String!, $input: ProjectLabelUpdateInput!) {
  projectLabelUpdate(id: $id, input: $input) {
    ...ProjectLabelPayload
  }
}
    fragment ProjectLabelPayload on ProjectLabelPayload {
  __typename
  lastSyncId
  projectLabel {
    id
  }
  success
}`);
const CreateProjectMilestoneDocument = new TypedDocumentString(`
    mutation createProjectMilestone($input: ProjectMilestoneCreateInput!) {
  projectMilestoneCreate(input: $input) {
    ...ProjectMilestonePayload
  }
}
    fragment ProjectMilestonePayload on ProjectMilestonePayload {
  __typename
  lastSyncId
  projectMilestone {
    id
  }
  success
}`);
const DeleteProjectMilestoneDocument = new TypedDocumentString(`
    mutation deleteProjectMilestone($id: String!) {
  projectMilestoneDelete(id: $id) {
    ...DeletePayload
  }
}
    fragment DeletePayload on DeletePayload {
  __typename
  entityId
  lastSyncId
  success
}`);
const UpdateProjectMilestoneDocument = new TypedDocumentString(`
    mutation updateProjectMilestone($id: String!, $input: ProjectMilestoneUpdateInput!) {
  projectMilestoneUpdate(id: $id, input: $input) {
    ...ProjectMilestonePayload
  }
}
    fragment ProjectMilestonePayload on ProjectMilestonePayload {
  __typename
  lastSyncId
  projectMilestone {
    id
  }
  success
}`);
const CreateProjectRelationDocument = new TypedDocumentString(`
    mutation createProjectRelation($input: ProjectRelationCreateInput!) {
  projectRelationCreate(input: $input) {
    ...ProjectRelationPayload
  }
}
    fragment ProjectRelationPayload on ProjectRelationPayload {
  __typename
  lastSyncId
  projectRelation {
    id
  }
  success
}`);
const DeleteProjectRelationDocument = new TypedDocumentString(`
    mutation deleteProjectRelation($id: String!) {
  projectRelationDelete(id: $id) {
    ...DeletePayload
  }
}
    fragment DeletePayload on DeletePayload {
  __typename
  entityId
  lastSyncId
  success
}`);
const UpdateProjectRelationDocument = new TypedDocumentString(`
    mutation updateProjectRelation($id: String!, $input: ProjectRelationUpdateInput!) {
  projectRelationUpdate(id: $id, input: $input) {
    ...ProjectRelationPayload
  }
}
    fragment ProjectRelationPayload on ProjectRelationPayload {
  __typename
  lastSyncId
  projectRelation {
    id
  }
  success
}`);
const ProjectRemoveLabelDocument = new TypedDocumentString(`
    mutation projectRemoveLabel($id: String!, $labelId: String!) {
  projectRemoveLabel(id: $id, labelId: $labelId) {
    ...ProjectPayload
  }
}
    fragment ProjectPayload on ProjectPayload {
  __typename
  lastSyncId
  project {
    id
  }
  success
}`);
const ArchiveProjectStatusDocument = new TypedDocumentString(`
    mutation archiveProjectStatus($id: String!) {
  projectStatusArchive(id: $id) {
    ...ProjectStatusArchivePayload
  }
}
    fragment ProjectStatusArchivePayload on ProjectStatusArchivePayload {
  __typename
  entity {
    id
  }
  lastSyncId
  success
}`);
const CreateProjectStatusDocument = new TypedDocumentString(`
    mutation createProjectStatus($input: ProjectStatusCreateInput!) {
  projectStatusCreate(input: $input) {
    ...ProjectStatusPayload
  }
}
    fragment ProjectStatusPayload on ProjectStatusPayload {
  __typename
  lastSyncId
  status {
    id
  }
  success
}`);
const UnarchiveProjectStatusDocument = new TypedDocumentString(`
    mutation unarchiveProjectStatus($id: String!) {
  projectStatusUnarchive(id: $id) {
    ...ProjectStatusArchivePayload
  }
}
    fragment ProjectStatusArchivePayload on ProjectStatusArchivePayload {
  __typename
  entity {
    id
  }
  lastSyncId
  success
}`);
const UpdateProjectStatusDocument = new TypedDocumentString(`
    mutation updateProjectStatus($id: String!, $input: ProjectStatusUpdateInput!) {
  projectStatusUpdate(id: $id, input: $input) {
    ...ProjectStatusPayload
  }
}
    fragment ProjectStatusPayload on ProjectStatusPayload {
  __typename
  lastSyncId
  status {
    id
  }
  success
}`);
const UnarchiveProjectDocument = new TypedDocumentString(`
    mutation unarchiveProject($id: String!) {
  projectUnarchive(id: $id) {
    ...ProjectArchivePayload
  }
}
    fragment ProjectArchivePayload on ProjectArchivePayload {
  __typename
  entity {
    id
  }
  lastSyncId
  success
}`);
const UpdateProjectDocument = new TypedDocumentString(`
    mutation updateProject($id: String!, $input: ProjectUpdateInput!) {
  projectUpdate(id: $id, input: $input) {
    ...ProjectPayload
  }
}
    fragment ProjectPayload on ProjectPayload {
  __typename
  lastSyncId
  project {
    id
  }
  success
}`);
const ArchiveProjectUpdateDocument = new TypedDocumentString(`
    mutation archiveProjectUpdate($id: String!) {
  projectUpdateArchive(id: $id) {
    ...ProjectUpdateArchivePayload
  }
}
    fragment ProjectUpdateArchivePayload on ProjectUpdateArchivePayload {
  __typename
  entity {
    id
  }
  lastSyncId
  success
}`);
const CreateProjectUpdateDocument = new TypedDocumentString(`
    mutation createProjectUpdate($input: ProjectUpdateCreateInput!) {
  projectUpdateCreate(input: $input) {
    ...ProjectUpdatePayload
  }
}
    fragment ProjectUpdatePayload on ProjectUpdatePayload {
  __typename
  lastSyncId
  projectUpdate {
    id
  }
  success
}`);
const DeleteProjectUpdateDocument = new TypedDocumentString(`
    mutation deleteProjectUpdate($id: String!) {
  projectUpdateDelete(id: $id) {
    ...DeletePayload
  }
}
    fragment DeletePayload on DeletePayload {
  __typename
  entityId
  lastSyncId
  success
}`);
const UnarchiveProjectUpdateDocument = new TypedDocumentString(`
    mutation unarchiveProjectUpdate($id: String!) {
  projectUpdateUnarchive(id: $id) {
    ...ProjectUpdateArchivePayload
  }
}
    fragment ProjectUpdateArchivePayload on ProjectUpdateArchivePayload {
  __typename
  entity {
    id
  }
  lastSyncId
  success
}`);
const UpdateProjectUpdateDocument = new TypedDocumentString(`
    mutation updateProjectUpdate($id: String!, $input: ProjectUpdateUpdateInput!) {
  projectUpdateUpdate(id: $id, input: $input) {
    ...ProjectUpdatePayload
  }
}
    fragment ProjectUpdatePayload on ProjectUpdatePayload {
  __typename
  lastSyncId
  projectUpdate {
    id
  }
  success
}`);
const CreatePushSubscriptionDocument = new TypedDocumentString(`
    mutation createPushSubscription($input: PushSubscriptionCreateInput!) {
  pushSubscriptionCreate(input: $input) {
    ...PushSubscriptionPayload
  }
}
    fragment PushSubscription on PushSubscription {
  __typename
  updatedAt
  archivedAt
  createdAt
  id
}
fragment PushSubscriptionPayload on PushSubscriptionPayload {
  __typename
  lastSyncId
  entity {
    ...PushSubscription
  }
  success
}`);
const DeletePushSubscriptionDocument = new TypedDocumentString(`
    mutation deletePushSubscription($id: String!) {
  pushSubscriptionDelete(id: $id) {
    ...PushSubscriptionPayload
  }
}
    fragment PushSubscription on PushSubscription {
  __typename
  updatedAt
  archivedAt
  createdAt
  id
}
fragment PushSubscriptionPayload on PushSubscriptionPayload {
  __typename
  lastSyncId
  entity {
    ...PushSubscription
  }
  success
}`);
const CreateReactionDocument = new TypedDocumentString(`
    mutation createReaction($input: ReactionCreateInput!) {
  reactionCreate(input: $input) {
    ...ReactionPayload
  }
}
    fragment Reaction on Reaction {
  __typename
  emoji
  comment {
    id
  }
  externalUser {
    id
  }
  initiativeUpdate {
    id
  }
  issue {
    id
  }
  updatedAt
  projectUpdate {
    id
  }
  archivedAt
  createdAt
  id
  user {
    id
  }
}
fragment ReactionPayload on ReactionPayload {
  __typename
  lastSyncId
  reaction {
    ...Reaction
  }
  success
}`);
const DeleteReactionDocument = new TypedDocumentString(`
    mutation deleteReaction($id: String!) {
  reactionDelete(id: $id) {
    ...DeletePayload
  }
}
    fragment DeletePayload on DeletePayload {
  __typename
  entityId
  lastSyncId
  success
}`);
const RefreshGoogleSheetsDataDocument = new TypedDocumentString(`
    mutation refreshGoogleSheetsData($id: String!, $type: String) {
  refreshGoogleSheetsData(id: $id, type: $type) {
    ...IntegrationPayload
  }
}
    fragment IntegrationPayload on IntegrationPayload {
  __typename
  lastSyncId
  integration {
    id
  }
  success
}`);
const ResendOrganizationInviteDocument = new TypedDocumentString(`
    mutation resendOrganizationInvite($id: String!) {
  resendOrganizationInvite(id: $id) {
    ...DeletePayload
  }
}
    fragment DeletePayload on DeletePayload {
  __typename
  entityId
  lastSyncId
  success
}`);
const ResendOrganizationInviteByEmailDocument = new TypedDocumentString(`
    mutation resendOrganizationInviteByEmail($email: String!) {
  resendOrganizationInviteByEmail(email: $email) {
    ...DeletePayload
  }
}
    fragment DeletePayload on DeletePayload {
  __typename
  entityId
  lastSyncId
  success
}`);
const ArchiveRoadmapDocument = new TypedDocumentString(`
    mutation archiveRoadmap($id: String!) {
  roadmapArchive(id: $id) {
    ...RoadmapArchivePayload
  }
}
    fragment RoadmapArchivePayload on RoadmapArchivePayload {
  __typename
  entity {
    id
  }
  lastSyncId
  success
}`);
const CreateRoadmapDocument = new TypedDocumentString(`
    mutation createRoadmap($input: RoadmapCreateInput!) {
  roadmapCreate(input: $input) {
    ...RoadmapPayload
  }
}
    fragment RoadmapPayload on RoadmapPayload {
  __typename
  lastSyncId
  roadmap {
    id
  }
  success
}`);
const DeleteRoadmapDocument = new TypedDocumentString(`
    mutation deleteRoadmap($id: String!) {
  roadmapDelete(id: $id) {
    ...DeletePayload
  }
}
    fragment DeletePayload on DeletePayload {
  __typename
  entityId
  lastSyncId
  success
}`);
const CreateRoadmapToProjectDocument = new TypedDocumentString(`
    mutation createRoadmapToProject($input: RoadmapToProjectCreateInput!) {
  roadmapToProjectCreate(input: $input) {
    ...RoadmapToProjectPayload
  }
}
    fragment RoadmapToProjectPayload on RoadmapToProjectPayload {
  __typename
  lastSyncId
  roadmapToProject {
    id
  }
  success
}`);
const DeleteRoadmapToProjectDocument = new TypedDocumentString(`
    mutation deleteRoadmapToProject($id: String!) {
  roadmapToProjectDelete(id: $id) {
    ...DeletePayload
  }
}
    fragment DeletePayload on DeletePayload {
  __typename
  entityId
  lastSyncId
  success
}`);
const UpdateRoadmapToProjectDocument = new TypedDocumentString(`
    mutation updateRoadmapToProject($id: String!, $input: RoadmapToProjectUpdateInput!) {
  roadmapToProjectUpdate(id: $id, input: $input) {
    ...RoadmapToProjectPayload
  }
}
    fragment RoadmapToProjectPayload on RoadmapToProjectPayload {
  __typename
  lastSyncId
  roadmapToProject {
    id
  }
  success
}`);
const UnarchiveRoadmapDocument = new TypedDocumentString(`
    mutation unarchiveRoadmap($id: String!) {
  roadmapUnarchive(id: $id) {
    ...RoadmapArchivePayload
  }
}
    fragment RoadmapArchivePayload on RoadmapArchivePayload {
  __typename
  entity {
    id
  }
  lastSyncId
  success
}`);
const UpdateRoadmapDocument = new TypedDocumentString(`
    mutation updateRoadmap($id: String!, $input: RoadmapUpdateInput!) {
  roadmapUpdate(id: $id, input: $input) {
    ...RoadmapPayload
  }
}
    fragment RoadmapPayload on RoadmapPayload {
  __typename
  lastSyncId
  roadmap {
    id
  }
  success
}`);
const SamlTokenUserAccountAuthDocument = new TypedDocumentString(`
    mutation samlTokenUserAccountAuth($input: TokenUserAccountAuthInput!) {
  samlTokenUserAccountAuth(input: $input) {
    ...AuthResolverResponse
  }
}
    fragment AuthUser on AuthUser {
  __typename
  avatarUrl
  organization {
    ...AuthOrganization
  }
  createdAt
  displayName
  email
  name
  userAccountId
  active
  role
  id
}
fragment AuthOrganization on AuthOrganization {
  __typename
  allowedAuthServices
  previousUrlKeys
  serviceId
  releaseChannel
  logoUrl
  name
  urlKey
  region
  deletionRequestedAt
  createdAt
  id
  samlEnabled
  scimEnabled
  enabled
  userCount
}
fragment AuthResolverResponse on AuthResolverResponse {
  __typename
  token
  email
  lastUsedOrganizationId
  users {
    ...AuthUser
  }
  lockedUsers {
    ...AuthUser
  }
  lockedOrganizations {
    ...AuthOrganization
  }
  availableOrganizations {
    ...AuthOrganization
  }
  allowDomainAccess
  id
}`);
const CreateTeamDocument = new TypedDocumentString(`
    mutation createTeam($copySettingsFromTeamId: String, $input: TeamCreateInput!) {
  teamCreate(copySettingsFromTeamId: $copySettingsFromTeamId, input: $input) {
    ...TeamPayload
  }
}
    fragment TeamPayload on TeamPayload {
  __typename
  lastSyncId
  team {
    id
  }
  success
}`);
const DeleteTeamCyclesDocument = new TypedDocumentString(`
    mutation deleteTeamCycles($id: String!) {
  teamCyclesDelete(id: $id) {
    ...TeamPayload
  }
}
    fragment TeamPayload on TeamPayload {
  __typename
  lastSyncId
  team {
    id
  }
  success
}`);
const DeleteTeamDocument = new TypedDocumentString(`
    mutation deleteTeam($id: String!) {
  teamDelete(id: $id) {
    ...DeletePayload
  }
}
    fragment DeletePayload on DeletePayload {
  __typename
  entityId
  lastSyncId
  success
}`);
const DeleteTeamKeyDocument = new TypedDocumentString(`
    mutation deleteTeamKey($id: String!) {
  teamKeyDelete(id: $id) {
    ...DeletePayload
  }
}
    fragment DeletePayload on DeletePayload {
  __typename
  entityId
  lastSyncId
  success
}`);
const CreateTeamMembershipDocument = new TypedDocumentString(`
    mutation createTeamMembership($input: TeamMembershipCreateInput!) {
  teamMembershipCreate(input: $input) {
    ...TeamMembershipPayload
  }
}
    fragment TeamMembershipPayload on TeamMembershipPayload {
  __typename
  lastSyncId
  teamMembership {
    id
  }
  success
}`);
const DeleteTeamMembershipDocument = new TypedDocumentString(`
    mutation deleteTeamMembership($alsoLeaveParentTeams: Boolean, $id: String!) {
  teamMembershipDelete(alsoLeaveParentTeams: $alsoLeaveParentTeams, id: $id) {
    ...DeletePayload
  }
}
    fragment DeletePayload on DeletePayload {
  __typename
  entityId
  lastSyncId
  success
}`);
const UpdateTeamMembershipDocument = new TypedDocumentString(`
    mutation updateTeamMembership($id: String!, $input: TeamMembershipUpdateInput!) {
  teamMembershipUpdate(id: $id, input: $input) {
    ...TeamMembershipPayload
  }
}
    fragment TeamMembershipPayload on TeamMembershipPayload {
  __typename
  lastSyncId
  teamMembership {
    id
  }
  success
}`);
const UnarchiveTeamDocument = new TypedDocumentString(`
    mutation unarchiveTeam($id: String!) {
  teamUnarchive(id: $id) {
    ...TeamArchivePayload
  }
}
    fragment TeamArchivePayload on TeamArchivePayload {
  __typename
  entity {
    id
  }
  lastSyncId
  success
}`);
const UpdateTeamDocument = new TypedDocumentString(`
    mutation updateTeam($id: String!, $input: TeamUpdateInput!, $mapping: InheritanceEntityMapping) {
  teamUpdate(id: $id, input: $input, mapping: $mapping) {
    ...TeamPayload
  }
}
    fragment TeamPayload on TeamPayload {
  __typename
  lastSyncId
  team {
    id
  }
  success
}`);
const CreateTemplateDocument = new TypedDocumentString(`
    mutation createTemplate($input: TemplateCreateInput!) {
  templateCreate(input: $input) {
    ...TemplatePayload
  }
}
    fragment TemplatePayload on TemplatePayload {
  __typename
  lastSyncId
  template {
    id
  }
  success
}`);
const DeleteTemplateDocument = new TypedDocumentString(`
    mutation deleteTemplate($id: String!) {
  templateDelete(id: $id) {
    ...DeletePayload
  }
}
    fragment DeletePayload on DeletePayload {
  __typename
  entityId
  lastSyncId
  success
}`);
const UpdateTemplateDocument = new TypedDocumentString(`
    mutation updateTemplate($id: String!, $input: TemplateUpdateInput!) {
  templateUpdate(id: $id, input: $input) {
    ...TemplatePayload
  }
}
    fragment TemplatePayload on TemplatePayload {
  __typename
  lastSyncId
  template {
    id
  }
  success
}`);
const CreateTimeScheduleDocument = new TypedDocumentString(`
    mutation createTimeSchedule($input: TimeScheduleCreateInput!) {
  timeScheduleCreate(input: $input) {
    ...TimeSchedulePayload
  }
}
    fragment TimeSchedulePayload on TimeSchedulePayload {
  __typename
  lastSyncId
  success
  timeSchedule {
    id
  }
}`);
const DeleteTimeScheduleDocument = new TypedDocumentString(`
    mutation deleteTimeSchedule($id: String!) {
  timeScheduleDelete(id: $id) {
    ...DeletePayload
  }
}
    fragment DeletePayload on DeletePayload {
  __typename
  entityId
  lastSyncId
  success
}`);
const TimeScheduleRefreshIntegrationScheduleDocument = new TypedDocumentString(`
    mutation timeScheduleRefreshIntegrationSchedule($id: String!) {
  timeScheduleRefreshIntegrationSchedule(id: $id) {
    ...TimeSchedulePayload
  }
}
    fragment TimeSchedulePayload on TimeSchedulePayload {
  __typename
  lastSyncId
  success
  timeSchedule {
    id
  }
}`);
const UpdateTimeScheduleDocument = new TypedDocumentString(`
    mutation updateTimeSchedule($id: String!, $input: TimeScheduleUpdateInput!) {
  timeScheduleUpdate(id: $id, input: $input) {
    ...TimeSchedulePayload
  }
}
    fragment TimeSchedulePayload on TimeSchedulePayload {
  __typename
  lastSyncId
  success
  timeSchedule {
    id
  }
}`);
const TimeScheduleUpsertExternalDocument = new TypedDocumentString(`
    mutation timeScheduleUpsertExternal($externalId: String!, $input: TimeScheduleUpdateInput!) {
  timeScheduleUpsertExternal(externalId: $externalId, input: $input) {
    ...TimeSchedulePayload
  }
}
    fragment TimeSchedulePayload on TimeSchedulePayload {
  __typename
  lastSyncId
  success
  timeSchedule {
    id
  }
}`);
const CreateTriageResponsibilityDocument = new TypedDocumentString(`
    mutation createTriageResponsibility($input: TriageResponsibilityCreateInput!) {
  triageResponsibilityCreate(input: $input) {
    ...TriageResponsibilityPayload
  }
}
    fragment TriageResponsibilityPayload on TriageResponsibilityPayload {
  __typename
  lastSyncId
  success
  triageResponsibility {
    id
  }
}`);
const DeleteTriageResponsibilityDocument = new TypedDocumentString(`
    mutation deleteTriageResponsibility($id: String!) {
  triageResponsibilityDelete(id: $id) {
    ...DeletePayload
  }
}
    fragment DeletePayload on DeletePayload {
  __typename
  entityId
  lastSyncId
  success
}`);
const UpdateTriageResponsibilityDocument = new TypedDocumentString(`
    mutation updateTriageResponsibility($id: String!, $input: TriageResponsibilityUpdateInput!) {
  triageResponsibilityUpdate(id: $id, input: $input) {
    ...TriageResponsibilityPayload
  }
}
    fragment TriageResponsibilityPayload on TriageResponsibilityPayload {
  __typename
  lastSyncId
  success
  triageResponsibility {
    id
  }
}`);
const UserChangeRoleDocument = new TypedDocumentString(`
    mutation userChangeRole($id: String!, $role: UserRoleType!) {
  userChangeRole(id: $id, role: $role) {
    ...UserAdminPayload
  }
}
    fragment UserAdminPayload on UserAdminPayload {
  __typename
  success
}`);
const UserDemoteAdminDocument = new TypedDocumentString(`
    mutation userDemoteAdmin($id: String!) {
  userDemoteAdmin(id: $id) {
    ...UserAdminPayload
  }
}
    fragment UserAdminPayload on UserAdminPayload {
  __typename
  success
}`);
const UserDemoteMemberDocument = new TypedDocumentString(`
    mutation userDemoteMember($id: String!) {
  userDemoteMember(id: $id) {
    ...UserAdminPayload
  }
}
    fragment UserAdminPayload on UserAdminPayload {
  __typename
  success
}`);
const UserDiscordConnectDocument = new TypedDocumentString(`
    mutation userDiscordConnect($code: String!, $redirectUri: String!) {
  userDiscordConnect(code: $code, redirectUri: $redirectUri) {
    ...UserPayload
  }
}
    fragment UserPayload on UserPayload {
  __typename
  lastSyncId
  user {
    id
  }
  success
}`);
const UserExternalUserDisconnectDocument = new TypedDocumentString(`
    mutation userExternalUserDisconnect($service: String!) {
  userExternalUserDisconnect(service: $service) {
    ...UserPayload
  }
}
    fragment UserPayload on UserPayload {
  __typename
  lastSyncId
  user {
    id
  }
  success
}`);
const UpdateUserFlagDocument = new TypedDocumentString(`
    mutation updateUserFlag($flag: UserFlagType!, $operation: UserFlagUpdateOperation!) {
  userFlagUpdate(flag: $flag, operation: $operation) {
    ...UserSettingsFlagPayload
  }
}
    fragment UserSettingsFlagPayload on UserSettingsFlagPayload {
  __typename
  flag
  value
  lastSyncId
  success
}`);
const UserPromoteAdminDocument = new TypedDocumentString(`
    mutation userPromoteAdmin($id: String!) {
  userPromoteAdmin(id: $id) {
    ...UserAdminPayload
  }
}
    fragment UserAdminPayload on UserAdminPayload {
  __typename
  success
}`);
const UserPromoteMemberDocument = new TypedDocumentString(`
    mutation userPromoteMember($id: String!) {
  userPromoteMember(id: $id) {
    ...UserAdminPayload
  }
}
    fragment UserAdminPayload on UserAdminPayload {
  __typename
  success
}`);
const UserSettingsFlagsResetDocument = new TypedDocumentString(`
    mutation userSettingsFlagsReset($flags: [UserFlagType!]) {
  userSettingsFlagsReset(flags: $flags) {
    ...UserSettingsFlagsResetPayload
  }
}
    fragment UserSettingsFlagsResetPayload on UserSettingsFlagsResetPayload {
  __typename
  lastSyncId
  success
}`);
const UpdateUserSettingsDocument = new TypedDocumentString(`
    mutation updateUserSettings($id: String!, $input: UserSettingsUpdateInput!) {
  userSettingsUpdate(id: $id, input: $input) {
    ...UserSettingsPayload
  }
}
    fragment UserSettingsPayload on UserSettingsPayload {
  __typename
  lastSyncId
  success
}`);
const SuspendUserDocument = new TypedDocumentString(`
    mutation suspendUser($id: String!) {
  userSuspend(id: $id) {
    ...UserAdminPayload
  }
}
    fragment UserAdminPayload on UserAdminPayload {
  __typename
  success
}`);
const UserUnlinkFromIdentityProviderDocument = new TypedDocumentString(`
    mutation userUnlinkFromIdentityProvider($id: String!) {
  userUnlinkFromIdentityProvider(id: $id) {
    ...UserAdminPayload
  }
}
    fragment UserAdminPayload on UserAdminPayload {
  __typename
  success
}`);
const UnsuspendUserDocument = new TypedDocumentString(`
    mutation unsuspendUser($id: String!) {
  userUnsuspend(id: $id) {
    ...UserAdminPayload
  }
}
    fragment UserAdminPayload on UserAdminPayload {
  __typename
  success
}`);
const UpdateUserDocument = new TypedDocumentString(`
    mutation updateUser($id: String!, $input: UserUpdateInput!) {
  userUpdate(id: $id, input: $input) {
    ...UserPayload
  }
}
    fragment UserPayload on UserPayload {
  __typename
  lastSyncId
  user {
    id
  }
  success
}`);
const CreateViewPreferencesDocument = new TypedDocumentString(`
    mutation createViewPreferences($input: ViewPreferencesCreateInput!) {
  viewPreferencesCreate(input: $input) {
    ...ViewPreferencesPayload
  }
}
    fragment ViewPreferences on ViewPreferences {
  __typename
  updatedAt
  archivedAt
  createdAt
  id
  type
  preferences {
    ...ViewPreferencesValues
  }
  viewType
}
fragment ViewPreferencesPayload on ViewPreferencesPayload {
  __typename
  lastSyncId
  viewPreferences {
    ...ViewPreferences
  }
  success
}
fragment ViewPreferencesValues on ViewPreferencesValues {
  __typename
  issueGrouping
  viewOrdering
  issueSubGrouping
  showCompletedIssues
}`);
const DeleteViewPreferencesDocument = new TypedDocumentString(`
    mutation deleteViewPreferences($id: String!) {
  viewPreferencesDelete(id: $id) {
    ...DeletePayload
  }
}
    fragment DeletePayload on DeletePayload {
  __typename
  entityId
  lastSyncId
  success
}`);
const UpdateViewPreferencesDocument = new TypedDocumentString(`
    mutation updateViewPreferences($id: String!, $input: ViewPreferencesUpdateInput!) {
  viewPreferencesUpdate(id: $id, input: $input) {
    ...ViewPreferencesPayload
  }
}
    fragment ViewPreferences on ViewPreferences {
  __typename
  updatedAt
  archivedAt
  createdAt
  id
  type
  preferences {
    ...ViewPreferencesValues
  }
  viewType
}
fragment ViewPreferencesPayload on ViewPreferencesPayload {
  __typename
  lastSyncId
  viewPreferences {
    ...ViewPreferences
  }
  success
}
fragment ViewPreferencesValues on ViewPreferencesValues {
  __typename
  issueGrouping
  viewOrdering
  issueSubGrouping
  showCompletedIssues
}`);
const CreateWebhookDocument = new TypedDocumentString(`
    mutation createWebhook($input: WebhookCreateInput!) {
  webhookCreate(input: $input) {
    ...WebhookPayload
  }
}
    fragment WebhookPayload on WebhookPayload {
  __typename
  lastSyncId
  webhook {
    id
  }
  success
}`);
const DeleteWebhookDocument = new TypedDocumentString(`
    mutation deleteWebhook($id: String!) {
  webhookDelete(id: $id) {
    ...DeletePayload
  }
}
    fragment DeletePayload on DeletePayload {
  __typename
  entityId
  lastSyncId
  success
}`);
const UpdateWebhookDocument = new TypedDocumentString(`
    mutation updateWebhook($id: String!, $input: WebhookUpdateInput!) {
  webhookUpdate(id: $id, input: $input) {
    ...WebhookPayload
  }
}
    fragment WebhookPayload on WebhookPayload {
  __typename
  lastSyncId
  webhook {
    id
  }
  success
}`);
const ArchiveWorkflowStateDocument = new TypedDocumentString(`
    mutation archiveWorkflowState($id: String!) {
  workflowStateArchive(id: $id) {
    ...WorkflowStateArchivePayload
  }
}
    fragment WorkflowStateArchivePayload on WorkflowStateArchivePayload {
  __typename
  entity {
    id
  }
  lastSyncId
  success
}`);
const CreateWorkflowStateDocument = new TypedDocumentString(`
    mutation createWorkflowState($input: WorkflowStateCreateInput!) {
  workflowStateCreate(input: $input) {
    ...WorkflowStatePayload
  }
}
    fragment WorkflowStatePayload on WorkflowStatePayload {
  __typename
  lastSyncId
  workflowState {
    id
  }
  success
}`);
const UpdateWorkflowStateDocument = new TypedDocumentString(`
    mutation updateWorkflowState($id: String!, $input: WorkflowStateUpdateInput!) {
  workflowStateUpdate(id: $id, input: $input) {
    ...WorkflowStatePayload
  }
}
    fragment WorkflowStatePayload on WorkflowStatePayload {
  __typename
  lastSyncId
  workflowState {
    id
  }
  success
}`);

//#endregion
//#region src/_generated_sdk.ts
/**
* Base class to provide a request function
*
* @param request - function to call the graphql client
*/
var Request = class {
	_request;
	constructor(request) {
		this._request = request;
	}
	/**
	* Helper to paginate over all pages of a given connection query.
	* @param fn The query to paginate
	* @param args The arguments to pass to the query
	*/
	async paginate(fn, args) {
		const boundFn = fn.bind(this);
		let connection = await boundFn(args);
		const nodes = connection.nodes;
		while (connection.pageInfo.hasNextPage) {
			connection = await boundFn({
				first: 50,
				...args,
				after: connection.pageInfo.endCursor
			});
			nodes.push(...connection.nodes);
		}
		return nodes;
	}
};
/**
* Default connection variables required for pagination
* Defaults to 50 as per the Linear API
*/
function defaultConnection(variables) {
	return {
		...variables,
		first: variables.first ?? (variables.after ? 50 : void 0),
		last: variables.last ?? (variables.before ? 50 : void 0)
	};
}
/**
* Connection models containing a list of nodes and pagination information
* Follows the Relay spec
*/
var LinearConnection = class extends Request {
	pageInfo;
	nodes;
	constructor(request) {
		super(request);
		this.pageInfo = new PageInfo(request, {
			hasNextPage: false,
			hasPreviousPage: false,
			__typename: "PageInfo"
		});
		this.nodes = [];
	}
};
/**
* The base connection class to provide pagination
* Follows the Relay spec
*
* @param request - function to call the graphql client
* @param fetch - Function to refetch the connection given different pagination variables
* @param nodes - The list of models to initialize the connection
* @param pageInfo - The pagination information to initialize the connection
*/
var Connection = class extends LinearConnection {
	_fetch;
	constructor(request, fetch$1, nodes, pageInfo) {
		super(request);
		this._fetch = fetch$1;
		this.nodes = nodes;
		this.pageInfo = pageInfo;
	}
	/** Add nodes to the end of the existing nodes */
	_appendNodes(nodes) {
		this.nodes = nodes ? [...this.nodes ?? [], ...nodes] : this.nodes;
	}
	/** Add nodes to the start of the existing nodes */
	_prependNodes(nodes) {
		this.nodes = nodes ? [...nodes, ...this.nodes ?? []] : this.nodes;
	}
	/** Update the pagination end cursor */
	_appendPageInfo(pageInfo) {
		if (this.pageInfo) {
			this.pageInfo.endCursor = pageInfo?.endCursor ?? this.pageInfo.startCursor;
			this.pageInfo.hasNextPage = pageInfo?.hasNextPage ?? this.pageInfo.hasNextPage;
		}
	}
	/** Update the pagination start cursor */
	_prependPageInfo(pageInfo) {
		if (this.pageInfo) {
			this.pageInfo.startCursor = pageInfo?.startCursor ?? this.pageInfo.startCursor;
			this.pageInfo.hasPreviousPage = pageInfo?.hasPreviousPage ?? this.pageInfo.hasPreviousPage;
		}
	}
	/** Fetch the next page of results and append to nodes */
	async fetchNext() {
		if (this.pageInfo?.hasNextPage) {
			const response = await this._fetch({ after: this.pageInfo?.endCursor });
			this._appendNodes(response?.nodes);
			this._appendPageInfo(response?.pageInfo);
		}
		return Promise.resolve(this);
	}
	/** Fetch the previous page of results and prepend to nodes */
	async fetchPrevious() {
		if (this.pageInfo?.hasPreviousPage) {
			const response = await this._fetch({ before: this.pageInfo?.startCursor });
			this._prependNodes(response?.nodes);
			this._prependPageInfo(response?.pageInfo);
		}
		return Promise.resolve(this);
	}
};
/**
* Function to parse custom scalars into Date types
*
* @param value - value to parse
*/
function parseDate(value) {
	try {
		return value ? new Date(value) : void 0;
	} catch (e) {
		return;
	}
}
/**
* Function to parse custom scalars into JSON objects
*
* @param value - value to parse
*/
function parseJson(value) {
	try {
		return value ? JSON.parse(value) : void 0;
	} catch (e) {
		return;
	}
}
/**
* A bot actor is an actor that is not a user, but an application or integration.
*
* @param request - function to call the graphql client
* @param data - L.ActorBotFragment response data
*/
var ActorBot = class extends Request {
	constructor(request, data) {
		super(request);
		this.avatarUrl = data.avatarUrl ?? void 0;
		this.id = data.id ?? void 0;
		this.name = data.name ?? void 0;
		this.subType = data.subType ?? void 0;
		this.type = data.type;
		this.userDisplayName = data.userDisplayName ?? void 0;
	}
	/** A url pointing to the avatar representing this bot. */
	avatarUrl;
	id;
	/** The display name of the bot. */
	name;
	/** The sub type of the bot. */
	subType;
	/** The type of bot. */
	type;
	/** The display name of the external user on behalf of which the bot acted. */
	userDisplayName;
};
/**
* An activity within an agent context.
*
* @param request - function to call the graphql client
* @param data - L.AgentActivityFragment response data
*/
var AgentActivity = class extends Request {
	_agentSession;
	_sourceComment;
	_user;
	constructor(request, data) {
		super(request);
		this.archivedAt = parseDate(data.archivedAt) ?? void 0;
		this.createdAt = parseDate(data.createdAt) ?? /* @__PURE__ */ new Date();
		this.ephemeral = data.ephemeral;
		this.id = data.id;
		this.signalMetadata = parseJson(data.signalMetadata) ?? void 0;
		this.sourceMetadata = parseJson(data.sourceMetadata) ?? void 0;
		this.updatedAt = parseDate(data.updatedAt) ?? /* @__PURE__ */ new Date();
		this.signal = data.signal ?? void 0;
		this.content = data.content;
		this._agentSession = data.agentSession;
		this._sourceComment = data.sourceComment ?? void 0;
		this._user = data.user;
	}
	/** The time at which the entity was archived. Null if the entity has not been archived. */
	archivedAt;
	/** The time at which the entity was created. */
	createdAt;
	/** Whether the activity is ephemeral, and should disappear after the next agent activity. */
	ephemeral;
	/** The unique identifier of the entity. */
	id;
	/** Metadata about this agent activity's signal. */
	signalMetadata;
	/** Metadata about the external source that created this agent activity. */
	sourceMetadata;
	/**
	* The last time at which the entity was meaningfully updated. This is the same as the creation time if the entity hasn't
	*     been updated after creation.
	*/
	updatedAt;
	/** An optional modifier that provides additional instructions on how the activity should be interpreted. */
	signal;
	/** The content of the activity */
	content;
	/** The agent session this activity belongs to. */
	get agentSession() {
		return new AgentSessionQuery(this._request).fetch(this._agentSession.id);
	}
	/** The ID of agent session this activity belongs to. */
	get agentSessionId() {
		return this._agentSession?.id;
	}
	/** The comment this activity is linked to. */
	get sourceComment() {
		return this._sourceComment?.id ? new CommentQuery(this._request).fetch({ id: this._sourceComment?.id }) : void 0;
	}
	/** The ID of comment this activity is linked to. */
	get sourceCommentId() {
		return this._sourceComment?.id;
	}
	/** The user who created this agent activity. */
	get user() {
		return new UserQuery(this._request).fetch(this._user.id);
	}
	/** The ID of user who created this agent activity. */
	get userId() {
		return this._user?.id;
	}
	/** Creates an agent activity. */
	create(input) {
		return new CreateAgentActivityMutation(this._request).fetch(input);
	}
};
/**
* Content for an action activity (tool call or action).
*
* @param request - function to call the graphql client
* @param data - L.AgentActivityActionContentFragment response data
*/
var AgentActivityActionContent = class extends Request {
	constructor(request, data) {
		super(request);
		this.action = data.action;
		this.parameter = data.parameter;
		this.result = data.result ?? void 0;
		this.type = data.type;
	}
	/** The action being performed. */
	action;
	/** The parameters for the action, e.g. a file path, a keyword, etc. */
	parameter;
	/** The result of the action in Markdown format. */
	result;
	/** The type of activity. */
	type;
};
/**
* AgentActivityConnection model
*
* @param request - function to call the graphql client
* @param fetch - function to trigger a refetch of this AgentActivityConnection model
* @param data - AgentActivityConnection response data
*/
var AgentActivityConnection = class extends Connection {
	constructor(request, fetch$1, data) {
		super(request, fetch$1, data.nodes.map((node) => new AgentActivity(request, node)), new PageInfo(request, data.pageInfo));
	}
};
/**
* Content for an elicitation activity.
*
* @param request - function to call the graphql client
* @param data - L.AgentActivityElicitationContentFragment response data
*/
var AgentActivityElicitationContent = class extends Request {
	constructor(request, data) {
		super(request);
		this.body = data.body;
		this.type = data.type;
	}
	/** The elicitation message in Markdown format. */
	body;
	/** The type of activity. */
	type;
};
/**
* Content for an error activity.
*
* @param request - function to call the graphql client
* @param data - L.AgentActivityErrorContentFragment response data
*/
var AgentActivityErrorContent = class extends Request {
	constructor(request, data) {
		super(request);
		this.body = data.body;
		this.type = data.type;
	}
	/** The error message in Markdown format. */
	body;
	/** The type of activity. */
	type;
};
/**
* AgentActivityPayload model
*
* @param request - function to call the graphql client
* @param data - L.AgentActivityPayloadFragment response data
*/
var AgentActivityPayload = class extends Request {
	_agentActivity;
	constructor(request, data) {
		super(request);
		this.lastSyncId = data.lastSyncId;
		this.success = data.success;
		this._agentActivity = data.agentActivity;
	}
	/** The identifier of the last sync operation. */
	lastSyncId;
	/** Whether the operation was successful. */
	success;
	/** The agent activity that was created or updated. */
	get agentActivity() {
		return new AgentActivityQuery(this._request).fetch(this._agentActivity.id);
	}
	/** The ID of agent activity that was created or updated. */
	get agentActivityId() {
		return this._agentActivity?.id;
	}
};
/**
* Content for a prompt activity.
*
* @param request - function to call the graphql client
* @param data - L.AgentActivityPromptContentFragment response data
*/
var AgentActivityPromptContent = class extends Request {
	constructor(request, data) {
		super(request);
		this.body = data.body;
		this.type = data.type;
	}
	/** A message requesting additional information or action from user. */
	body;
	/** The type of activity. */
	type;
};
/**
* Content for a response activity.
*
* @param request - function to call the graphql client
* @param data - L.AgentActivityResponseContentFragment response data
*/
var AgentActivityResponseContent = class extends Request {
	constructor(request, data) {
		super(request);
		this.body = data.body;
		this.type = data.type;
	}
	/** The response content in Markdown format. */
	body;
	/** The type of activity. */
	type;
};
/**
* Content for a thought activity.
*
* @param request - function to call the graphql client
* @param data - L.AgentActivityThoughtContentFragment response data
*/
var AgentActivityThoughtContent = class extends Request {
	constructor(request, data) {
		super(request);
		this.body = data.body;
		this.type = data.type;
	}
	/** The thought content in Markdown format. */
	body;
	/** The type of activity. */
	type;
};
/**
* Payload for an agent activity webhook.
*
* @param data - L.AgentActivityWebhookPayloadFragment response data
*/
var AgentActivityWebhookPayload = class {
	constructor(data) {
		this.agentSessionId = data.agentSessionId;
		this.archivedAt = data.archivedAt ?? void 0;
		this.content = data.content;
		this.createdAt = data.createdAt;
		this.id = data.id;
		this.signal = data.signal ?? void 0;
		this.signalMetadata = data.signalMetadata ?? void 0;
		this.sourceCommentId = data.sourceCommentId ?? void 0;
		this.updatedAt = data.updatedAt;
		this.userId = data.userId ?? void 0;
	}
	/** The ID of the agent session that this activity belongs to. */
	agentSessionId;
	/** The time at which the entity was archived. */
	archivedAt;
	/** The content of the agent activity. */
	content;
	/** The time at which the entity was created. */
	createdAt;
	/** The ID of the entity. */
	id;
	/** An optional modifier that provides additional instructions on how the activity should be interpreted. */
	signal;
	/** Metadata about this agent activity's signal. */
	signalMetadata;
	/** The ID of the comment this activity is linked to. */
	sourceCommentId;
	/** The time at which the entity was updated. */
	updatedAt;
	/** The ID of the user who created this agent activity. */
	userId;
};
/**
* A session for agent activities and state management.
*
* @param request - function to call the graphql client
* @param data - L.AgentSessionFragment response data
*/
var AgentSession = class extends Request {
	_appUser;
	_comment;
	_creator;
	_dismissedBy;
	_issue;
	_sourceComment;
	constructor(request, data) {
		super(request);
		this.archivedAt = parseDate(data.archivedAt) ?? void 0;
		this.context = parseJson(data.context) ?? {};
		this.createdAt = parseDate(data.createdAt) ?? /* @__PURE__ */ new Date();
		this.dismissedAt = parseDate(data.dismissedAt) ?? void 0;
		this.endedAt = parseDate(data.endedAt) ?? void 0;
		this.externalLink = data.externalLink ?? void 0;
		this.externalUrls = parseJson(data.externalUrls) ?? {};
		this.id = data.id;
		this.plan = parseJson(data.plan) ?? void 0;
		this.sourceMetadata = parseJson(data.sourceMetadata) ?? void 0;
		this.startedAt = parseDate(data.startedAt) ?? void 0;
		this.summary = data.summary ?? void 0;
		this.updatedAt = parseDate(data.updatedAt) ?? /* @__PURE__ */ new Date();
		this.status = data.status;
		this.type = data.type ?? void 0;
		this._appUser = data.appUser;
		this._comment = data.comment ?? void 0;
		this._creator = data.creator ?? void 0;
		this._dismissedBy = data.dismissedBy ?? void 0;
		this._issue = data.issue ?? void 0;
		this._sourceComment = data.sourceComment ?? void 0;
	}
	/** The time at which the entity was archived. Null if the entity has not been archived. */
	archivedAt;
	/** Serialized JSON representing the contexts this session is related to, for direct chat sessions. */
	context;
	/** The time at which the entity was created. */
	createdAt;
	/** The time the agent session was dismissed. */
	dismissedAt;
	/** The time the agent session ended. */
	endedAt;
	/** The URL of an external agent-hosted page associated with this session. */
	externalLink;
	/** URLs of external resources associated with this session. */
	externalUrls;
	/** The unique identifier of the entity. */
	id;
	/** A dynamically updated list of the agent's execution strategy. */
	plan;
	/** Metadata about the external source that created this agent session. */
	sourceMetadata;
	/** The time the agent session started. */
	startedAt;
	/** A summary of the activities in this session. */
	summary;
	/**
	* The last time at which the entity was meaningfully updated. This is the same as the creation time if the entity hasn't
	*     been updated after creation.
	*/
	updatedAt;
	/** The current status of the agent session. */
	status;
	/** [DEPRECATED] The type of the agent session. */
	type;
	/** The agent user that is associated with this agent session. */
	get appUser() {
		return new UserQuery(this._request).fetch(this._appUser.id);
	}
	/** The ID of agent user that is associated with this agent session. */
	get appUserId() {
		return this._appUser?.id;
	}
	/** The comment this agent session is associated with. */
	get comment() {
		return this._comment?.id ? new CommentQuery(this._request).fetch({ id: this._comment?.id }) : void 0;
	}
	/** The ID of comment this agent session is associated with. */
	get commentId() {
		return this._comment?.id;
	}
	/** The human user responsible for the agent session. Null if the session was initiated via automation or by an agent user, with no responsible human user. */
	get creator() {
		return this._creator?.id ? new UserQuery(this._request).fetch(this._creator?.id) : void 0;
	}
	/** The ID of human user responsible for the agent session. null if the session was initiated via automation or by an agent user, with no responsible human user. */
	get creatorId() {
		return this._creator?.id;
	}
	/** The user who dismissed the agent session. */
	get dismissedBy() {
		return this._dismissedBy?.id ? new UserQuery(this._request).fetch(this._dismissedBy?.id) : void 0;
	}
	/** The ID of user who dismissed the agent session. */
	get dismissedById() {
		return this._dismissedBy?.id;
	}
	/** The issue this agent session is associated with. */
	get issue() {
		return this._issue?.id ? new IssueQuery(this._request).fetch(this._issue?.id) : void 0;
	}
	/** The ID of issue this agent session is associated with. */
	get issueId() {
		return this._issue?.id;
	}
	/** The comment that this agent session was spawned from, if from a different thread. */
	get sourceComment() {
		return this._sourceComment?.id ? new CommentQuery(this._request).fetch({ id: this._sourceComment?.id }) : void 0;
	}
	/** The ID of comment that this agent session was spawned from, if from a different thread. */
	get sourceCommentId() {
		return this._sourceComment?.id;
	}
	/** Activities associated with this agent session. */
	activities(variables) {
		return new AgentSession_ActivitiesQuery(this._request, this.id, variables).fetch(variables);
	}
	/** Updates an agent session. */
	update(input) {
		return new UpdateAgentSessionMutation(this._request).fetch(this.id, input);
	}
};
/**
* AgentSessionConnection model
*
* @param request - function to call the graphql client
* @param fetch - function to trigger a refetch of this AgentSessionConnection model
* @param data - AgentSessionConnection response data
*/
var AgentSessionConnection = class extends Connection {
	constructor(request, fetch$1, data) {
		super(request, fetch$1, data.nodes.map((node) => new AgentSession(request, node)), new PageInfo(request, data.pageInfo));
	}
};
/**
* Payload for agent session webhook events.
*
* @param data - L.AgentSessionEventWebhookPayloadFragment response data
*/
var AgentSessionEventWebhookPayload = class {
	constructor(data) {
		this.action = data.action;
		this.appUserId = data.appUserId;
		this.createdAt = parseDate(data.createdAt) ?? /* @__PURE__ */ new Date();
		this.oauthClientId = data.oauthClientId;
		this.organizationId = data.organizationId;
		this.promptContext = data.promptContext ?? void 0;
		this.type = data.type;
		this.webhookId = data.webhookId;
		this.webhookTimestamp = data.webhookTimestamp;
		this.agentActivity = data.agentActivity ? new AgentActivityWebhookPayload(data.agentActivity) : void 0;
		this.agentSession = new AgentSessionWebhookPayload(data.agentSession);
		this.guidance = data.guidance ? data.guidance.map((node) => new GuidanceRuleWebhookPayload(node)) : void 0;
		this.previousComments = data.previousComments ? data.previousComments.map((node) => new CommentChildWebhookPayload(node)) : void 0;
	}
	/** The type of action that triggered the webhook. */
	action;
	/** ID of the app user the agent session belongs to. */
	appUserId;
	/** The time the payload was created. */
	createdAt;
	/** ID of the OAuth client the app user is tied to. */
	oauthClientId;
	/** ID of the organization for which the webhook belongs to. */
	organizationId;
	/** A formatted prompt string containing the relevant context for the agent session, including issue details, comments, and guidance. Present only for `created` events. */
	promptContext;
	/** The type of resource. */
	type;
	/** The ID of the webhook that sent this event. */
	webhookId;
	/** Unix timestamp in milliseconds when the webhook was sent. */
	webhookTimestamp;
	/** Guidance to inform the agent's behavior, which comes from configuration at the level of the workspace, parent teams, and/or current team for this session. The nearest team-specific guidance should take highest precendence. */
	guidance;
	/** The previous comments in the thread before this agent was mentioned and the session was initiated, if any. Present only for `created` events where the session was initiated by mentioning the agent in a child comment of a thread. */
	previousComments;
	/** The agent activity that was created. */
	agentActivity;
	/** The agent session that the event belongs to. */
	agentSession;
};
/**
* AgentSessionPayload model
*
* @param request - function to call the graphql client
* @param data - L.AgentSessionPayloadFragment response data
*/
var AgentSessionPayload = class extends Request {
	_agentSession;
	constructor(request, data) {
		super(request);
		this.lastSyncId = data.lastSyncId;
		this.success = data.success;
		this._agentSession = data.agentSession;
	}
	/** The identifier of the last sync operation. */
	lastSyncId;
	/** Whether the operation was successful. */
	success;
	/** The agent session that was created or updated. */
	get agentSession() {
		return new AgentSessionQuery(this._request).fetch(this._agentSession.id);
	}
	/** The ID of agent session that was created or updated. */
	get agentSessionId() {
		return this._agentSession?.id;
	}
};
/**
* Join table between agent sessions and pull requests.
*
* @param request - function to call the graphql client
* @param data - L.AgentSessionToPullRequestFragment response data
*/
var AgentSessionToPullRequest = class extends Request {
	_agentSession;
	constructor(request, data) {
		super(request);
		this.archivedAt = parseDate(data.archivedAt) ?? void 0;
		this.createdAt = parseDate(data.createdAt) ?? /* @__PURE__ */ new Date();
		this.id = data.id;
		this.updatedAt = parseDate(data.updatedAt) ?? /* @__PURE__ */ new Date();
		this._agentSession = data.agentSession;
	}
	/** The time at which the entity was archived. Null if the entity has not been archived. */
	archivedAt;
	/** The time at which the entity was created. */
	createdAt;
	/** The unique identifier of the entity. */
	id;
	/**
	* The last time at which the entity was meaningfully updated. This is the same as the creation time if the entity hasn't
	*     been updated after creation.
	*/
	updatedAt;
	/** The agent session that the pull request is associated with. */
	get agentSession() {
		return new AgentSessionQuery(this._request).fetch(this._agentSession.id);
	}
	/** The ID of agent session that the pull request is associated with. */
	get agentSessionId() {
		return this._agentSession?.id;
	}
};
/**
* AgentSessionToPullRequestConnection model
*
* @param request - function to call the graphql client
* @param fetch - function to trigger a refetch of this AgentSessionToPullRequestConnection model
* @param data - AgentSessionToPullRequestConnection response data
*/
var AgentSessionToPullRequestConnection = class extends Connection {
	constructor(request, fetch$1, data) {
		super(request, fetch$1, data.nodes.map((node) => new AgentSessionToPullRequest(request, node)), new PageInfo(request, data.pageInfo));
	}
};
/**
* Payload for an agent session webhook.
*
* @param data - L.AgentSessionWebhookPayloadFragment response data
*/
var AgentSessionWebhookPayload = class {
	constructor(data) {
		this.appUserId = data.appUserId;
		this.archivedAt = data.archivedAt ?? void 0;
		this.commentId = data.commentId ?? void 0;
		this.createdAt = data.createdAt;
		this.creatorId = data.creatorId ?? void 0;
		this.endedAt = data.endedAt ?? void 0;
		this.id = data.id;
		this.issueId = data.issueId ?? void 0;
		this.organizationId = data.organizationId;
		this.sourceCommentId = data.sourceCommentId ?? void 0;
		this.sourceMetadata = data.sourceMetadata ?? void 0;
		this.startedAt = data.startedAt ?? void 0;
		this.status = data.status;
		this.summary = data.summary ?? void 0;
		this.type = data.type;
		this.updatedAt = data.updatedAt;
		this.comment = data.comment ? new CommentChildWebhookPayload(data.comment) : void 0;
		this.creator = data.creator ? new UserChildWebhookPayload(data.creator) : void 0;
		this.issue = data.issue ? new IssueWithDescriptionChildWebhookPayload(data.issue) : void 0;
	}
	/** The ID of the agent that the agent session belongs to. */
	appUserId;
	/** The time at which the entity was archived. */
	archivedAt;
	/** The ID of the root comment of the thread this agent session is attached to. */
	commentId;
	/** The time at which the entity was created. */
	createdAt;
	/** The ID of the human user responsible for the agent session. Unset if the session was initiated via automation or by an agent user, with no responsible human user. */
	creatorId;
	/** The time the agent session ended. */
	endedAt;
	/** The ID of the entity. */
	id;
	/** The ID of the issue this agent session is associated with. */
	issueId;
	/** The ID of the organization that the agent session belongs to. */
	organizationId;
	/** The ID of the comment that this agent session was spawned from, if from a different thread. */
	sourceCommentId;
	/** Metadata about the external source that created this agent session. */
	sourceMetadata;
	/** The time the agent session started working. */
	startedAt;
	/** The current status of the agent session. */
	status;
	/** A summary of the activities in this session. */
	summary;
	/** The type of the agent session. */
	type;
	/** The time at which the entity was updated. */
	updatedAt;
	/** The root comment of the thread this agent session is attached to. */
	comment;
	/** The human user responsible for the agent session. Unset if the session was initiated via automation or by an agent user, with no responsible human user. */
	creator;
	/** The issue this agent session is associated with. */
	issue;
};
/**
* AI prompt rules for a team.
*
* @param request - function to call the graphql client
* @param data - L.AiPromptRulesFragment response data
*/
var AiPromptRules = class extends Request {
	_updatedBy;
	constructor(request, data) {
		super(request);
		this.archivedAt = parseDate(data.archivedAt) ?? void 0;
		this.createdAt = parseDate(data.createdAt) ?? /* @__PURE__ */ new Date();
		this.id = data.id;
		this.updatedAt = parseDate(data.updatedAt) ?? /* @__PURE__ */ new Date();
		this._updatedBy = data.updatedBy ?? void 0;
	}
	/** The time at which the entity was archived. Null if the entity has not been archived. */
	archivedAt;
	/** The time at which the entity was created. */
	createdAt;
	/** The unique identifier of the entity. */
	id;
	/**
	* The last time at which the entity was meaningfully updated. This is the same as the creation time if the entity hasn't
	*     been updated after creation.
	*/
	updatedAt;
	/** The user who last updated the AI prompt rules. */
	get updatedBy() {
		return this._updatedBy?.id ? new UserQuery(this._request).fetch(this._updatedBy?.id) : void 0;
	}
	/** The ID of user who last updated the ai prompt rules. */
	get updatedById() {
		return this._updatedBy?.id;
	}
};
/**
* Payload for app user notification webhook events.
*
* @param data - L.AppUserNotificationWebhookPayloadFragment response data
*/
var AppUserNotificationWebhookPayload = class {
	constructor(data) {
		this.action = data.action;
		this.appUserId = data.appUserId;
		this.createdAt = parseDate(data.createdAt) ?? /* @__PURE__ */ new Date();
		this.oauthClientId = data.oauthClientId;
		this.organizationId = data.organizationId;
		this.type = data.type;
		this.webhookId = data.webhookId;
		this.webhookTimestamp = data.webhookTimestamp;
	}
	/** The type of action that triggered the webhook. */
	action;
	/** ID of the app user the notification is for. */
	appUserId;
	/** The time the payload was created. */
	createdAt;
	/** ID of the OAuth client the app user is tied to. */
	oauthClientId;
	/** ID of the organization for which the webhook belongs to. */
	organizationId;
	/** The type of resource. */
	type;
	/** The ID of the webhook that sent this event. */
	webhookId;
	/** Unix timestamp in milliseconds when the webhook was sent. */
	webhookTimestamp;
};
/**
* Payload for app user team access change webhook events.
*
* @param data - L.AppUserTeamAccessChangedWebhookPayloadFragment response data
*/
var AppUserTeamAccessChangedWebhookPayload = class {
	constructor(data) {
		this.action = data.action;
		this.addedTeamIds = data.addedTeamIds;
		this.appUserId = data.appUserId;
		this.canAccessAllPublicTeams = data.canAccessAllPublicTeams;
		this.createdAt = parseDate(data.createdAt) ?? /* @__PURE__ */ new Date();
		this.oauthClientId = data.oauthClientId;
		this.organizationId = data.organizationId;
		this.removedTeamIds = data.removedTeamIds;
		this.type = data.type;
		this.webhookId = data.webhookId;
		this.webhookTimestamp = data.webhookTimestamp;
	}
	/** The type of action that triggered the webhook. */
	action;
	/** IDs of the teams the app user was added to. */
	addedTeamIds;
	/** ID of the app user the notification is for. */
	appUserId;
	/** Whether the app user can access all public teams. */
	canAccessAllPublicTeams;
	/** The time the payload was created. */
	createdAt;
	/** ID of the OAuth client the app user is tied to. */
	oauthClientId;
	/** ID of the organization for which the webhook belongs to. */
	organizationId;
	/** IDs of the teams the app user was removed from. */
	removedTeamIds;
	/** The type of resource. */
	type;
	/** The ID of the webhook that sent this event. */
	webhookId;
	/** Unix timestamp in milliseconds when the webhook was sent. */
	webhookTimestamp;
};
/**
* Public information of the OAuth application.
*
* @param request - function to call the graphql client
* @param data - L.ApplicationFragment response data
*/
var Application = class extends Request {
	constructor(request, data) {
		super(request);
		this.clientId = data.clientId;
		this.description = data.description ?? void 0;
		this.developer = data.developer;
		this.developerUrl = data.developerUrl;
		this.id = data.id;
		this.imageUrl = data.imageUrl ?? void 0;
		this.name = data.name;
	}
	/** OAuth application's client ID. */
	clientId;
	/** Information about the application. */
	description;
	/** Name of the developer. */
	developer;
	/** Url of the developer (homepage or docs). */
	developerUrl;
	/** OAuth application's ID. */
	id;
	/** Image of the application. */
	imageUrl;
	/** Application name. */
	name;
};
/**
* A generic payload return from entity archive or deletion mutations.
*
* @param request - function to call the graphql client
* @param data - L.ArchivePayloadFragment response data
*/
var ArchivePayload = class extends Request {
	constructor(request, data) {
		super(request);
		this.lastSyncId = data.lastSyncId;
		this.success = data.success;
	}
	/** The identifier of the last sync operation. */
	lastSyncId;
	/** Whether the operation was successful. */
	success;
};
/**
* Contains requested archived model objects.
*
* @param request - function to call the graphql client
* @param data - L.ArchiveResponseFragment response data
*/
var ArchiveResponse = class extends Request {
	constructor(request, data) {
		super(request);
		this.archive = data.archive;
		this.databaseVersion = data.databaseVersion;
		this.includesDependencies = data.includesDependencies;
		this.totalCount = data.totalCount;
	}
	/** A JSON serialized collection of model objects loaded from the archive */
	archive;
	/** The version of the remote database. Incremented by 1 for each migration run on the database. */
	databaseVersion;
	/** Whether the dependencies for the model objects are included in the archive. */
	includesDependencies;
	/** The total number of entities in the archive. */
	totalCount;
};
/**
* AsksChannelConnectPayload model
*
* @param request - function to call the graphql client
* @param data - L.AsksChannelConnectPayloadFragment response data
*/
var AsksChannelConnectPayload = class extends Request {
	_integration;
	constructor(request, data) {
		super(request);
		this.addBot = data.addBot;
		this.lastSyncId = data.lastSyncId;
		this.success = data.success;
		this.mapping = new SlackChannelNameMapping(request, data.mapping);
		this._integration = data.integration ?? void 0;
	}
	/** Whether the bot needs to be manually added to the channel. */
	addBot;
	/** The identifier of the last sync operation. */
	lastSyncId;
	/** Whether the operation was successful. */
	success;
	/** The new Asks Slack channel mapping for the connected channel. */
	mapping;
	/** The integration that was created or updated. */
	get integration() {
		return this._integration?.id ? new IntegrationQuery(this._request).fetch(this._integration?.id) : void 0;
	}
	/** The ID of integration that was created or updated. */
	get integrationId() {
		return this._integration?.id;
	}
};
/**
* AsksWebFormsAuthResponse model
*
* @param request - function to call the graphql client
* @param data - L.AsksWebFormsAuthResponseFragment response data
*/
var AsksWebFormsAuthResponse = class extends Request {
	constructor(request, data) {
		super(request);
		this.email = data.email;
		this.name = data.name;
	}
	/** User email. */
	email;
	/** User display name. */
	name;
};
/**
* Issue attachment (e.g. support ticket, pull request).
*
* @param request - function to call the graphql client
* @param data - L.AttachmentFragment response data
*/
var Attachment = class extends Request {
	_creator;
	_externalUserCreator;
	_issue;
	_originalIssue;
	constructor(request, data) {
		super(request);
		this.archivedAt = parseDate(data.archivedAt) ?? void 0;
		this.bodyData = data.bodyData ?? void 0;
		this.createdAt = parseDate(data.createdAt) ?? /* @__PURE__ */ new Date();
		this.groupBySource = data.groupBySource;
		this.id = data.id;
		this.metadata = data.metadata;
		this.source = data.source ?? void 0;
		this.sourceType = data.sourceType ?? void 0;
		this.subtitle = data.subtitle ?? void 0;
		this.title = data.title;
		this.updatedAt = parseDate(data.updatedAt) ?? /* @__PURE__ */ new Date();
		this.url = data.url;
		this._creator = data.creator ?? void 0;
		this._externalUserCreator = data.externalUserCreator ?? void 0;
		this._issue = data.issue;
		this._originalIssue = data.originalIssue ?? void 0;
	}
	/** The time at which the entity was archived. Null if the entity has not been archived. */
	archivedAt;
	/** The body data of the attachment, if any. */
	bodyData;
	/** The time at which the entity was created. */
	createdAt;
	/** Indicates if attachments for the same source application should be grouped in the Linear UI. */
	groupBySource;
	/** The unique identifier of the entity. */
	id;
	/** Custom metadata related to the attachment. */
	metadata;
	/** Information about the source which created the attachment. */
	source;
	/** An accessor helper to source.type, defines the source type of the attachment. */
	sourceType;
	/** Content for the subtitle line in the Linear attachment widget. */
	subtitle;
	/** Content for the title line in the Linear attachment widget. */
	title;
	/**
	* The last time at which the entity was meaningfully updated. This is the same as the creation time if the entity hasn't
	*     been updated after creation.
	*/
	updatedAt;
	/** Location of the attachment which is also used as an identifier. */
	url;
	/** The creator of the attachment. */
	get creator() {
		return this._creator?.id ? new UserQuery(this._request).fetch(this._creator?.id) : void 0;
	}
	/** The ID of creator of the attachment. */
	get creatorId() {
		return this._creator?.id;
	}
	/** The non-Linear user who created the attachment. */
	get externalUserCreator() {
		return this._externalUserCreator?.id ? new ExternalUserQuery(this._request).fetch(this._externalUserCreator?.id) : void 0;
	}
	/** The ID of non-linear user who created the attachment. */
	get externalUserCreatorId() {
		return this._externalUserCreator?.id;
	}
	/** The issue this attachment belongs to. */
	get issue() {
		return new IssueQuery(this._request).fetch(this._issue.id);
	}
	/** The ID of issue this attachment belongs to. */
	get issueId() {
		return this._issue?.id;
	}
	/** The issue this attachment was originally created on. Will be undefined if the attachment hasn't been moved. */
	get originalIssue() {
		return this._originalIssue?.id ? new IssueQuery(this._request).fetch(this._originalIssue?.id) : void 0;
	}
	/** The ID of issue this attachment was originally created on. will be undefined if the attachment hasn't been moved. */
	get originalIssueId() {
		return this._originalIssue?.id;
	}
	/** Creates a new attachment, or updates existing if the same `url` and `issueId` is used. */
	create(input) {
		return new CreateAttachmentMutation(this._request).fetch(input);
	}
	/** Deletes an issue attachment. */
	delete() {
		return new DeleteAttachmentMutation(this._request).fetch(this.id);
	}
	/** Updates an existing issue attachment. */
	update(input) {
		return new UpdateAttachmentMutation(this._request).fetch(this.id, input);
	}
};
/**
* AttachmentConnection model
*
* @param request - function to call the graphql client
* @param fetch - function to trigger a refetch of this AttachmentConnection model
* @param data - AttachmentConnection response data
*/
var AttachmentConnection = class extends Connection {
	constructor(request, fetch$1, data) {
		super(request, fetch$1, data.nodes.map((node) => new Attachment(request, node)), new PageInfo(request, data.pageInfo));
	}
};
/**
* AttachmentPayload model
*
* @param request - function to call the graphql client
* @param data - L.AttachmentPayloadFragment response data
*/
var AttachmentPayload = class extends Request {
	_attachment;
	constructor(request, data) {
		super(request);
		this.lastSyncId = data.lastSyncId;
		this.success = data.success;
		this._attachment = data.attachment;
	}
	/** The identifier of the last sync operation. */
	lastSyncId;
	/** Whether the operation was successful. */
	success;
	/** The issue attachment that was created. */
	get attachment() {
		return new AttachmentQuery(this._request).fetch(this._attachment.id);
	}
	/** The ID of issue attachment that was created. */
	get attachmentId() {
		return this._attachment?.id;
	}
};
/**
* AttachmentSourcesPayload model
*
* @param request - function to call the graphql client
* @param data - L.AttachmentSourcesPayloadFragment response data
*/
var AttachmentSourcesPayload = class extends Request {
	constructor(request, data) {
		super(request);
		this.sources = data.sources;
	}
	/** A unique list of all source types used in this workspace. */
	sources;
};
/**
* Payload for an attachment webhook.
*
* @param data - L.AttachmentWebhookPayloadFragment response data
*/
var AttachmentWebhookPayload = class {
	constructor(data) {
		this.archivedAt = data.archivedAt ?? void 0;
		this.createdAt = data.createdAt;
		this.creatorId = data.creatorId ?? void 0;
		this.externalUserCreatorId = data.externalUserCreatorId ?? void 0;
		this.groupBySource = data.groupBySource;
		this.id = data.id;
		this.issueId = data.issueId;
		this.metadata = data.metadata;
		this.originalIssueId = data.originalIssueId ?? void 0;
		this.source = data.source ?? void 0;
		this.sourceType = data.sourceType ?? void 0;
		this.subtitle = data.subtitle ?? void 0;
		this.title = data.title;
		this.updatedAt = data.updatedAt;
		this.url = data.url;
	}
	/** The time at which the entity was archived. */
	archivedAt;
	/** The time at which the entity was created. */
	createdAt;
	/** The ID of the creator of the attachment. */
	creatorId;
	/** The ID of the non-Linear user who created the attachment. */
	externalUserCreatorId;
	/** Whether attachments for the same source application should be grouped in the Linear UI. */
	groupBySource;
	/** The ID of the entity. */
	id;
	/** The ID of the issue this attachment belongs to. */
	issueId;
	/** Custom metadata related to the attachment. */
	metadata;
	/** The ID of the issue this attachment belonged to originally. */
	originalIssueId;
	/** Information about the source which created the attachment. */
	source;
	/** The source type of the attachment. */
	sourceType;
	/** Optional subtitle of the attachment. */
	subtitle;
	/** The title of the attachment. */
	title;
	/** The time at which the entity was updated. */
	updatedAt;
	/** The URL of the attachment. */
	url;
};
/**
* Workspace audit log entry object.
*
* @param request - function to call the graphql client
* @param data - L.AuditEntryFragment response data
*/
var AuditEntry = class extends Request {
	_actor;
	constructor(request, data) {
		super(request);
		this.actorId = data.actorId ?? void 0;
		this.archivedAt = parseDate(data.archivedAt) ?? void 0;
		this.countryCode = data.countryCode ?? void 0;
		this.createdAt = parseDate(data.createdAt) ?? /* @__PURE__ */ new Date();
		this.id = data.id;
		this.ip = data.ip ?? void 0;
		this.metadata = data.metadata ?? void 0;
		this.requestInformation = data.requestInformation ?? void 0;
		this.type = data.type;
		this.updatedAt = parseDate(data.updatedAt) ?? /* @__PURE__ */ new Date();
		this._actor = data.actor ?? void 0;
	}
	/** The ID of the user that caused the audit entry to be created. */
	actorId;
	/** The time at which the entity was archived. Null if the entity has not been archived. */
	archivedAt;
	/** Country code of request resulting to audit entry. */
	countryCode;
	/** The time at which the entity was created. */
	createdAt;
	/** The unique identifier of the entity. */
	id;
	/** IP from actor when entry was recorded. */
	ip;
	/** Additional metadata related to the audit entry. */
	metadata;
	/** Additional information related to the request which performed the action. */
	requestInformation;
	type;
	/**
	* The last time at which the entity was meaningfully updated. This is the same as the creation time if the entity hasn't
	*     been updated after creation.
	*/
	updatedAt;
	/** The user that caused the audit entry to be created. */
	get actor() {
		return this._actor?.id ? new UserQuery(this._request).fetch(this._actor?.id) : void 0;
	}
	/** The organization the audit log belongs to. */
	get organization() {
		return new OrganizationQuery(this._request).fetch();
	}
};
/**
* AuditEntryConnection model
*
* @param request - function to call the graphql client
* @param fetch - function to trigger a refetch of this AuditEntryConnection model
* @param data - AuditEntryConnection response data
*/
var AuditEntryConnection = class extends Connection {
	constructor(request, fetch$1, data) {
		super(request, fetch$1, data.nodes.map((node) => new AuditEntry(request, node)), new PageInfo(request, data.pageInfo));
	}
};
/**
* AuditEntryType model
*
* @param request - function to call the graphql client
* @param data - L.AuditEntryTypeFragment response data
*/
var AuditEntryType = class extends Request {
	constructor(request, data) {
		super(request);
		this.description = data.description;
		this.type = data.type;
	}
	/** Description of the audit entry type. */
	description;
	/** The audit entry type. */
	type;
};
/**
* Payload for an audit entry webhook.
*
* @param data - L.AuditEntryWebhookPayloadFragment response data
*/
var AuditEntryWebhookPayload = class {
	constructor(data) {
		this.actorId = data.actorId ?? void 0;
		this.archivedAt = data.archivedAt ?? void 0;
		this.countryCode = data.countryCode ?? void 0;
		this.createdAt = data.createdAt;
		this.id = data.id;
		this.ip = data.ip ?? void 0;
		this.metadata = data.metadata ?? void 0;
		this.organizationId = data.organizationId;
		this.requestInformation = data.requestInformation ?? void 0;
		this.type = data.type;
		this.updatedAt = data.updatedAt;
	}
	/** The ID of the user that caused the audit entry to be created. */
	actorId;
	/** The time at which the entity was archived. */
	archivedAt;
	/** Country code of request resulting to audit entry. */
	countryCode;
	/** The time at which the entity was created. */
	createdAt;
	/** The ID of the entity. */
	id;
	/** IP from actor when entry was recorded. */
	ip;
	/** Additional metadata related to the audit entry. */
	metadata;
	/** The ID of the organization that the audit entry belongs to. */
	organizationId;
	/** Additional information related to the request which performed the action. */
	requestInformation;
	/** The type of the audit entry. */
	type;
	/** The time at which the entity was updated. */
	updatedAt;
};
/**
* An identity provider.
*
* @param request - function to call the graphql client
* @param data - L.AuthIdentityProviderFragment response data
*/
var AuthIdentityProvider = class extends Request {
	constructor(request, data) {
		super(request);
		this.createdAt = parseDate(data.createdAt) ?? /* @__PURE__ */ new Date();
		this.defaultMigrated = data.defaultMigrated;
		this.id = data.id;
		this.issuerEntityId = data.issuerEntityId ?? void 0;
		this.priority = data.priority ?? void 0;
		this.samlEnabled = data.samlEnabled;
		this.scimEnabled = data.scimEnabled;
		this.spEntityId = data.spEntityId ?? void 0;
		this.ssoBinding = data.ssoBinding ?? void 0;
		this.ssoEndpoint = data.ssoEndpoint ?? void 0;
		this.ssoSignAlgo = data.ssoSignAlgo ?? void 0;
		this.ssoSigningCert = data.ssoSigningCert ?? void 0;
		this.type = data.type;
	}
	/** The time at which the entity was created. */
	createdAt;
	/** Whether the identity provider is the default identity provider migrated from organization level settings. */
	defaultMigrated;
	/** The unique identifier of the entity. */
	id;
	/** The issuer's custom entity ID. */
	issuerEntityId;
	/** The SAML priority used to pick default workspace in SAML SP initiated flow, when same domain is claimed for SAML by multiple workspaces. Lower priority value means higher preference. */
	priority;
	/** Whether SAML authentication is enabled for organization. */
	samlEnabled;
	/** Whether SCIM provisioning is enabled for organization. */
	scimEnabled;
	/** The service provider (Linear) custom entity ID. Defaults to https://auth.linear.app/sso */
	spEntityId;
	/** Binding method for authentication call. Can be either `post` (default) or `redirect`. */
	ssoBinding;
	/** Sign in endpoint URL for the identity provider. */
	ssoEndpoint;
	/** The algorithm of the Signing Certificate. Can be one of `sha1`, `sha256` (default), or `sha512`. */
	ssoSignAlgo;
	/** X.509 Signing Certificate in string form. */
	ssoSigningCert;
	/** The type of identity provider. */
	type;
};
/**
* An organization. Organizations are root-level objects that contain users and teams.
*
* @param request - function to call the graphql client
* @param data - L.AuthOrganizationFragment response data
*/
var AuthOrganization = class extends Request {
	constructor(request, data) {
		super(request);
		this.allowedAuthServices = data.allowedAuthServices;
		this.createdAt = parseDate(data.createdAt) ?? /* @__PURE__ */ new Date();
		this.deletionRequestedAt = parseDate(data.deletionRequestedAt) ?? void 0;
		this.enabled = data.enabled;
		this.id = data.id;
		this.logoUrl = data.logoUrl ?? void 0;
		this.name = data.name;
		this.previousUrlKeys = data.previousUrlKeys;
		this.region = data.region;
		this.samlEnabled = data.samlEnabled;
		this.scimEnabled = data.scimEnabled;
		this.serviceId = data.serviceId;
		this.urlKey = data.urlKey;
		this.userCount = data.userCount ?? void 0;
		this.releaseChannel = data.releaseChannel;
	}
	/** Allowed authentication providers, empty array means all are allowed */
	allowedAuthServices;
	/** The time at which the entity was created. */
	createdAt;
	/** The time at which deletion of the organization was requested. */
	deletionRequestedAt;
	/** Whether the organization is enabled. Used as a superuser tool to lock down the org. */
	enabled;
	/** The unique identifier of the entity. */
	id;
	/** The organization's logo URL. */
	logoUrl;
	/** The organization's name. */
	name;
	/** Previously used URL keys for the organization (last 3 are kept and redirected). */
	previousUrlKeys;
	/** The region the organization is hosted in. */
	region;
	/** Whether SAML authentication is enabled for organization. */
	samlEnabled;
	/** Whether SCIM provisioning is enabled for organization. */
	scimEnabled;
	/** The email domain or URL key for the organization. */
	serviceId;
	/** The organization's unique URL key. */
	urlKey;
	userCount;
	/** The feature release channel the organization belongs to. */
	releaseChannel;
};
/**
* AuthResolverResponse model
*
* @param request - function to call the graphql client
* @param data - L.AuthResolverResponseFragment response data
*/
var AuthResolverResponse = class extends Request {
	constructor(request, data) {
		super(request);
		this.allowDomainAccess = data.allowDomainAccess ?? void 0;
		this.email = data.email;
		this.id = data.id;
		this.lastUsedOrganizationId = data.lastUsedOrganizationId ?? void 0;
		this.token = data.token ?? void 0;
		this.availableOrganizations = data.availableOrganizations ? data.availableOrganizations.map((node) => new AuthOrganization(request, node)) : void 0;
		this.lockedOrganizations = data.lockedOrganizations ? data.lockedOrganizations.map((node) => new AuthOrganization(request, node)) : void 0;
		this.lockedUsers = data.lockedUsers.map((node) => new AuthUser(request, node));
		this.users = data.users.map((node) => new AuthUser(request, node));
	}
	/** Should the signup flow allow access for the domain. */
	allowDomainAccess;
	/** Email for the authenticated account. */
	email;
	/** User account ID. */
	id;
	/** ID of the organization last accessed by the user. */
	lastUsedOrganizationId;
	/** Application token. */
	token;
	/** List of organizations allowing this user account to join automatically. */
	availableOrganizations;
	/** List of organization available to this user account but locked due to the current auth method. */
	lockedOrganizations;
	/** List of locked users that are locked by login restrictions */
	lockedUsers;
	/** List of active users that belong to the user account. */
	users;
};
/**
* A user that has access to the the resources of an organization.
*
* @param request - function to call the graphql client
* @param data - L.AuthUserFragment response data
*/
var AuthUser = class extends Request {
	constructor(request, data) {
		super(request);
		this.active = data.active;
		this.avatarUrl = data.avatarUrl ?? void 0;
		this.createdAt = parseDate(data.createdAt) ?? /* @__PURE__ */ new Date();
		this.displayName = data.displayName;
		this.email = data.email;
		this.id = data.id;
		this.name = data.name;
		this.userAccountId = data.userAccountId;
		this.organization = new AuthOrganization(request, data.organization);
		this.role = data.role;
	}
	/** Whether the user is active. */
	active;
	/** An URL to the user's avatar image. */
	avatarUrl;
	/** The time at which the entity was created. */
	createdAt;
	/** The user's display (nick) name. Unique within each organization. */
	displayName;
	/** The user's email address. */
	email;
	id;
	/** The user's full name. */
	name;
	/** User account ID the user belongs to. */
	userAccountId;
	/** Organization the user belongs to. */
	organization;
	/** Whether the user is an organization admin or guest on a database level. */
	role;
};
/**
* Authentication session information.
*
* @param request - function to call the graphql client
* @param data - L.AuthenticationSessionResponseFragment response data
*/
var AuthenticationSessionResponse = class extends Request {
	constructor(request, data) {
		super(request);
		this.browserType = data.browserType ?? void 0;
		this.client = data.client ?? void 0;
		this.countryCodes = data.countryCodes;
		this.createdAt = parseDate(data.createdAt) ?? /* @__PURE__ */ new Date();
		this.id = data.id;
		this.ip = data.ip ?? void 0;
		this.isCurrentSession = data.isCurrentSession;
		this.lastActiveAt = parseDate(data.lastActiveAt) ?? void 0;
		this.location = data.location ?? void 0;
		this.locationCity = data.locationCity ?? void 0;
		this.locationCountry = data.locationCountry ?? void 0;
		this.locationCountryCode = data.locationCountryCode ?? void 0;
		this.locationRegionCode = data.locationRegionCode ?? void 0;
		this.name = data.name;
		this.operatingSystem = data.operatingSystem ?? void 0;
		this.service = data.service ?? void 0;
		this.updatedAt = parseDate(data.updatedAt) ?? /* @__PURE__ */ new Date();
		this.userAgent = data.userAgent ?? void 0;
		this.type = data.type;
	}
	/** Used web browser. */
	browserType;
	/** Client used for the session */
	client;
	/** Country codes of all seen locations. */
	countryCodes;
	/** The time at which the entity was created. */
	createdAt;
	id;
	/** IP address. */
	ip;
	/** Identifies the session used to make the request. */
	isCurrentSession;
	/** When was the session last seen */
	lastActiveAt;
	/** Human readable location */
	location;
	/** Location city name. */
	locationCity;
	/** Location country name. */
	locationCountry;
	/** Location country code. */
	locationCountryCode;
	/** Location region code. */
	locationRegionCode;
	/** Name of the session, derived from the client and operating system */
	name;
	/** Operating system used for the session */
	operatingSystem;
	/** Service used for logging in. */
	service;
	/** Date when the session was last updated. */
	updatedAt;
	/** Session's user-agent. */
	userAgent;
	/** Type of application used to authenticate. */
	type;
};
/**
* Base fields for all webhook payloads.
*
* @param data - L.BaseWebhookPayloadFragment response data
*/
var BaseWebhookPayload = class {
	constructor(data) {
		this.createdAt = parseDate(data.createdAt) ?? /* @__PURE__ */ new Date();
		this.organizationId = data.organizationId;
		this.webhookId = data.webhookId;
		this.webhookTimestamp = data.webhookTimestamp;
	}
	/** The time the payload was created. */
	createdAt;
	/** ID of the organization for which the webhook belongs to. */
	organizationId;
	/** The ID of the webhook that sent this event. */
	webhookId;
	/** Unix timestamp in milliseconds when the webhook was sent. */
	webhookTimestamp;
};
/**
* A comment associated with an issue.
*
* @param request - function to call the graphql client
* @param data - L.CommentFragment response data
*/
var Comment = class extends Request {
	_agentSession;
	_externalUser;
	_initiativeUpdate;
	_issue;
	_parent;
	_projectUpdate;
	_resolvingComment;
	_resolvingUser;
	_user;
	constructor(request, data) {
		super(request);
		this.archivedAt = parseDate(data.archivedAt) ?? void 0;
		this.body = data.body;
		this.createdAt = parseDate(data.createdAt) ?? /* @__PURE__ */ new Date();
		this.documentContentId = data.documentContentId ?? void 0;
		this.editedAt = parseDate(data.editedAt) ?? void 0;
		this.id = data.id;
		this.initiativeUpdateId = data.initiativeUpdateId ?? void 0;
		this.issueId = data.issueId ?? void 0;
		this.parentId = data.parentId ?? void 0;
		this.projectUpdateId = data.projectUpdateId ?? void 0;
		this.quotedText = data.quotedText ?? void 0;
		this.reactionData = data.reactionData;
		this.resolvedAt = parseDate(data.resolvedAt) ?? void 0;
		this.resolvingCommentId = data.resolvingCommentId ?? void 0;
		this.updatedAt = parseDate(data.updatedAt) ?? /* @__PURE__ */ new Date();
		this.url = data.url;
		this.botActor = data.botActor ? new ActorBot(request, data.botActor) : void 0;
		this.documentContent = data.documentContent ? new DocumentContent(request, data.documentContent) : void 0;
		this.externalThread = data.externalThread ? new SyncedExternalThread(request, data.externalThread) : void 0;
		this.reactions = data.reactions.map((node) => new Reaction(request, node));
		this.syncedWith = data.syncedWith ? data.syncedWith.map((node) => new ExternalEntityInfo(request, node)) : void 0;
		this._agentSession = data.agentSession ?? void 0;
		this._externalUser = data.externalUser ?? void 0;
		this._initiativeUpdate = data.initiativeUpdate ?? void 0;
		this._issue = data.issue ?? void 0;
		this._parent = data.parent ?? void 0;
		this._projectUpdate = data.projectUpdate ?? void 0;
		this._resolvingComment = data.resolvingComment ?? void 0;
		this._resolvingUser = data.resolvingUser ?? void 0;
		this._user = data.user ?? void 0;
	}
	/** The time at which the entity was archived. Null if the entity has not been archived. */
	archivedAt;
	/** The comment content in markdown format. */
	body;
	/** The time at which the entity was created. */
	createdAt;
	/** The ID of the document content that the comment is associated with. */
	documentContentId;
	/** The time user edited the comment. */
	editedAt;
	/** The unique identifier of the entity. */
	id;
	/** The ID of the initiative update that the comment is associated with. */
	initiativeUpdateId;
	/** The ID of the issue that the comment is associated with. */
	issueId;
	/** The ID of the parent comment under which the current comment is nested. */
	parentId;
	/** The ID of the project update that the comment is associated with. */
	projectUpdateId;
	/** The text that this comment references. Only defined for inline comments. */
	quotedText;
	/** Emoji reaction summary, grouped by emoji type. */
	reactionData;
	/** The time the resolvingUser resolved the thread. */
	resolvedAt;
	/** The ID of the comment that resolved the thread. */
	resolvingCommentId;
	/**
	* The last time at which the entity was meaningfully updated. This is the same as the creation time if the entity hasn't
	*     been updated after creation.
	*/
	updatedAt;
	/** Comment's URL. */
	url;
	/** Reactions associated with the comment. */
	reactions;
	/** The external services the comment is synced with. */
	syncedWith;
	/** The bot that created the comment. */
	botActor;
	/** The document content that the comment is associated with. */
	documentContent;
	/** The external thread that the comment is synced with. */
	externalThread;
	/** Agent session associated with this comment. */
	get agentSession() {
		return this._agentSession?.id ? new AgentSessionQuery(this._request).fetch(this._agentSession?.id) : void 0;
	}
	/** The ID of agent session associated with this comment. */
	get agentSessionId() {
		return this._agentSession?.id;
	}
	/** The external user who wrote the comment. */
	get externalUser() {
		return this._externalUser?.id ? new ExternalUserQuery(this._request).fetch(this._externalUser?.id) : void 0;
	}
	/** The ID of external user who wrote the comment. */
	get externalUserId() {
		return this._externalUser?.id;
	}
	/** The initiative update that the comment is associated with. */
	get initiativeUpdate() {
		return this._initiativeUpdate?.id ? new InitiativeUpdateQuery(this._request).fetch(this._initiativeUpdate?.id) : void 0;
	}
	/** The issue that the comment is associated with. */
	get issue() {
		return this._issue?.id ? new IssueQuery(this._request).fetch(this._issue?.id) : void 0;
	}
	/** The parent comment under which the current comment is nested. */
	get parent() {
		return this._parent?.id ? new CommentQuery(this._request).fetch({ id: this._parent?.id }) : void 0;
	}
	/** The project update that the comment is associated with. */
	get projectUpdate() {
		return this._projectUpdate?.id ? new ProjectUpdateQuery(this._request).fetch(this._projectUpdate?.id) : void 0;
	}
	/** The comment that resolved the thread. */
	get resolvingComment() {
		return this._resolvingComment?.id ? new CommentQuery(this._request).fetch({ id: this._resolvingComment?.id }) : void 0;
	}
	/** The user that resolved the thread. */
	get resolvingUser() {
		return this._resolvingUser?.id ? new UserQuery(this._request).fetch(this._resolvingUser?.id) : void 0;
	}
	/** The ID of user that resolved the thread. */
	get resolvingUserId() {
		return this._resolvingUser?.id;
	}
	/** The user who wrote the comment. */
	get user() {
		return this._user?.id ? new UserQuery(this._request).fetch(this._user?.id) : void 0;
	}
	/** The ID of user who wrote the comment. */
	get userId() {
		return this._user?.id;
	}
	/** The children of the comment. */
	children(variables) {
		return new Comment_ChildrenQuery(this._request, variables).fetch(variables);
	}
	/** Issues created from this comment. */
	createdIssues(variables) {
		return new Comment_CreatedIssuesQuery(this._request, variables).fetch(variables);
	}
	/** Creates a new comment. */
	create(input) {
		return new CreateCommentMutation(this._request).fetch(input);
	}
	/** Deletes a comment. */
	delete() {
		return new DeleteCommentMutation(this._request).fetch(this.id);
	}
	/** Updates a comment. */
	update(input, variables) {
		return new UpdateCommentMutation(this._request).fetch(this.id, input, variables);
	}
};
/**
* Certain properties of a comment.
*
* @param data - L.CommentChildWebhookPayloadFragment response data
*/
var CommentChildWebhookPayload = class {
	constructor(data) {
		this.body = data.body;
		this.documentContentId = data.documentContentId ?? void 0;
		this.id = data.id;
		this.initiativeUpdateId = data.initiativeUpdateId ?? void 0;
		this.issueId = data.issueId ?? void 0;
		this.projectUpdateId = data.projectUpdateId ?? void 0;
		this.userId = data.userId ?? void 0;
	}
	/** The body of the comment. */
	body;
	/** The ID of the document content this comment belongs to. */
	documentContentId;
	/** The ID of the comment. */
	id;
	/** The ID of the initiative update this comment belongs to. */
	initiativeUpdateId;
	/** The ID of the issue this comment belongs to. */
	issueId;
	/** The ID of the project update this comment belongs to. */
	projectUpdateId;
	/** The ID of the user who created this comment. */
	userId;
};
/**
* CommentConnection model
*
* @param request - function to call the graphql client
* @param fetch - function to trigger a refetch of this CommentConnection model
* @param data - CommentConnection response data
*/
var CommentConnection = class extends Connection {
	constructor(request, fetch$1, data) {
		super(request, fetch$1, data.nodes.map((node) => new Comment(request, node)), new PageInfo(request, data.pageInfo));
	}
};
/**
* CommentPayload model
*
* @param request - function to call the graphql client
* @param data - L.CommentPayloadFragment response data
*/
var CommentPayload = class extends Request {
	_comment;
	constructor(request, data) {
		super(request);
		this.lastSyncId = data.lastSyncId;
		this.success = data.success;
		this._comment = data.comment;
	}
	/** The identifier of the last sync operation. */
	lastSyncId;
	/** Whether the operation was successful. */
	success;
	/** The comment that was created or updated. */
	get comment() {
		return new CommentQuery(this._request).fetch({ id: this._comment.id });
	}
	/** The ID of comment that was created or updated. */
	get commentId() {
		return this._comment?.id;
	}
};
/**
* Payload for a comment webhook.
*
* @param data - L.CommentWebhookPayloadFragment response data
*/
var CommentWebhookPayload = class {
	constructor(data) {
		this.archivedAt = data.archivedAt ?? void 0;
		this.body = data.body;
		this.botActor = data.botActor ?? void 0;
		this.createdAt = data.createdAt;
		this.documentContentId = data.documentContentId ?? void 0;
		this.editedAt = data.editedAt ?? void 0;
		this.externalUserId = data.externalUserId ?? void 0;
		this.id = data.id;
		this.initiativeUpdateId = data.initiativeUpdateId ?? void 0;
		this.issueId = data.issueId ?? void 0;
		this.parentId = data.parentId ?? void 0;
		this.postId = data.postId ?? void 0;
		this.projectUpdateId = data.projectUpdateId ?? void 0;
		this.quotedText = data.quotedText ?? void 0;
		this.reactionData = data.reactionData;
		this.resolvedAt = data.resolvedAt ?? void 0;
		this.resolvingCommentId = data.resolvingCommentId ?? void 0;
		this.resolvingUserId = data.resolvingUserId ?? void 0;
		this.syncedWith = data.syncedWith ?? void 0;
		this.updatedAt = data.updatedAt;
		this.userId = data.userId ?? void 0;
		this.documentContent = data.documentContent ? new DocumentContentChildWebhookPayload(data.documentContent) : void 0;
		this.externalUser = data.externalUser ? new ExternalUserChildWebhookPayload(data.externalUser) : void 0;
		this.initiativeUpdate = data.initiativeUpdate ? new InitiativeUpdateChildWebhookPayload(data.initiativeUpdate) : void 0;
		this.issue = data.issue ? new IssueChildWebhookPayload(data.issue) : void 0;
		this.parent = data.parent ? new CommentChildWebhookPayload(data.parent) : void 0;
		this.projectUpdate = data.projectUpdate ? new ProjectUpdateChildWebhookPayload(data.projectUpdate) : void 0;
		this.user = data.user ? new UserChildWebhookPayload(data.user) : void 0;
	}
	/** The time at which the entity was archived. */
	archivedAt;
	/** The body of the comment. */
	body;
	/** The bot actor data for this comment. */
	botActor;
	/** The time at which the entity was created. */
	createdAt;
	/** The ID of the document content this comment belongs to. */
	documentContentId;
	/** When the comment was last edited. */
	editedAt;
	/** The ID of the external user who created this comment. */
	externalUserId;
	/** The ID of the entity. */
	id;
	/** The ID of the initiative update this comment belongs to. */
	initiativeUpdateId;
	/** The ID of the issue this comment belongs to. */
	issueId;
	/** The ID of the parent comment. */
	parentId;
	/** The ID of the post this comment belongs to. */
	postId;
	/** The ID of the project update this comment belongs to. */
	projectUpdateId;
	/** The quoted text in this comment. */
	quotedText;
	/** The reaction data for this comment. */
	reactionData;
	/** When the comment was resolved. */
	resolvedAt;
	/** The ID of the comment that resolved this comment. */
	resolvingCommentId;
	/** The ID of the user who resolved this comment. */
	resolvingUserId;
	/** The entity this comment is synced with. */
	syncedWith;
	/** The time at which the entity was updated. */
	updatedAt;
	/** The ID of the user who created this comment. */
	userId;
	/** The document content for this comment. */
	documentContent;
	/** The external user who created this comment. */
	externalUser;
	/** The initiative update this comment belongs to. */
	initiativeUpdate;
	/** The issue this comment belongs to. */
	issue;
	/** The parent comment. */
	parent;
	/** The project update this comment belongs to. */
	projectUpdate;
	/** The user who created this comment. */
	user;
};
/**
* ContactPayload model
*
* @param request - function to call the graphql client
* @param data - L.ContactPayloadFragment response data
*/
var ContactPayload = class extends Request {
	constructor(request, data) {
		super(request);
		this.success = data.success;
	}
	/** Whether the operation was successful. */
	success;
};
/**
* CreateCsvExportReportPayload model
*
* @param request - function to call the graphql client
* @param data - L.CreateCsvExportReportPayloadFragment response data
*/
var CreateCsvExportReportPayload = class extends Request {
	constructor(request, data) {
		super(request);
		this.success = data.success;
	}
	/** Whether the operation was successful. */
	success;
};
/**
* CreateOrJoinOrganizationResponse model
*
* @param request - function to call the graphql client
* @param data - L.CreateOrJoinOrganizationResponseFragment response data
*/
var CreateOrJoinOrganizationResponse = class extends Request {
	constructor(request, data) {
		super(request);
		this.organization = new AuthOrganization(request, data.organization);
		this.user = new AuthUser(request, data.user);
	}
	organization;
	user;
};
/**
* Payload for custom webhook resource events.
*
* @param data - L.CustomResourceWebhookPayloadFragment response data
*/
var CustomResourceWebhookPayload = class {
	constructor(data) {
		this.action = data.action;
		this.createdAt = parseDate(data.createdAt) ?? /* @__PURE__ */ new Date();
		this.organizationId = data.organizationId;
		this.type = data.type;
		this.webhookId = data.webhookId;
		this.webhookTimestamp = data.webhookTimestamp;
	}
	/** The type of action that triggered the webhook. */
	action;
	/** The time the payload was created. */
	createdAt;
	/** ID of the organization for which the webhook belongs to. */
	organizationId;
	/** The type of resource. */
	type;
	/** The ID of the webhook that sent this event. */
	webhookId;
	/** Unix timestamp in milliseconds when the webhook was sent. */
	webhookTimestamp;
};
/**
* A custom view that has been saved by a user.
*
* @param request - function to call the graphql client
* @param data - L.CustomViewFragment response data
*/
var CustomView = class extends Request {
	_creator;
	_owner;
	_team;
	_updatedBy;
	constructor(request, data) {
		super(request);
		this.archivedAt = parseDate(data.archivedAt) ?? void 0;
		this.color = data.color ?? void 0;
		this.createdAt = parseDate(data.createdAt) ?? /* @__PURE__ */ new Date();
		this.description = data.description ?? void 0;
		this.feedItemFilterData = data.feedItemFilterData ?? void 0;
		this.filterData = data.filterData;
		this.filters = data.filters;
		this.icon = data.icon ?? void 0;
		this.id = data.id;
		this.initiativeFilterData = data.initiativeFilterData ?? void 0;
		this.modelName = data.modelName;
		this.name = data.name;
		this.projectFilterData = data.projectFilterData ?? void 0;
		this.shared = data.shared;
		this.slugId = data.slugId;
		this.updatedAt = parseDate(data.updatedAt) ?? /* @__PURE__ */ new Date();
		this.organizationViewPreferences = data.organizationViewPreferences ? new ViewPreferences(request, data.organizationViewPreferences) : void 0;
		this.userViewPreferences = data.userViewPreferences ? new ViewPreferences(request, data.userViewPreferences) : void 0;
		this.viewPreferencesValues = data.viewPreferencesValues ? new ViewPreferencesValues(request, data.viewPreferencesValues) : void 0;
		this._creator = data.creator;
		this._owner = data.owner;
		this._team = data.team ?? void 0;
		this._updatedBy = data.updatedBy ?? void 0;
	}
	/** The time at which the entity was archived. Null if the entity has not been archived. */
	archivedAt;
	/** The color of the icon of the custom view. */
	color;
	/** The time at which the entity was created. */
	createdAt;
	/** The description of the custom view. */
	description;
	/** The filter applied to feed items in the custom view. */
	feedItemFilterData;
	/** The filter applied to issues in the custom view. */
	filterData;
	/** The filters applied to issues in the custom view. */
	filters;
	/** The icon of the custom view. */
	icon;
	/** The unique identifier of the entity. */
	id;
	/** The filter applied to initiatives in the custom view. */
	initiativeFilterData;
	/** The model name of the custom view. */
	modelName;
	/** The name of the custom view. */
	name;
	/** The filter applied to projects in the custom view. */
	projectFilterData;
	/** Whether the custom view is shared with everyone in the organization. */
	shared;
	/** The custom view's unique URL slug. */
	slugId;
	/**
	* The last time at which the entity was meaningfully updated. This is the same as the creation time if the entity hasn't
	*     been updated after creation.
	*/
	updatedAt;
	/** The organizations default view preferences for this custom view. */
	organizationViewPreferences;
	/** The current users view preferences for this custom view. */
	userViewPreferences;
	/** The calculated view preferences values for this custom view. */
	viewPreferencesValues;
	/** The user who created the custom view. */
	get creator() {
		return new UserQuery(this._request).fetch(this._creator.id);
	}
	/** The ID of user who created the custom view. */
	get creatorId() {
		return this._creator?.id;
	}
	/** The organization of the custom view. */
	get organization() {
		return new OrganizationQuery(this._request).fetch();
	}
	/** The user who owns the custom view. */
	get owner() {
		return new UserQuery(this._request).fetch(this._owner.id);
	}
	/** The ID of user who owns the custom view. */
	get ownerId() {
		return this._owner?.id;
	}
	/** The team associated with the custom view. */
	get team() {
		return this._team?.id ? new TeamQuery(this._request).fetch(this._team?.id) : void 0;
	}
	/** The ID of team associated with the custom view. */
	get teamId() {
		return this._team?.id;
	}
	/** The user who last updated the custom view. */
	get updatedBy() {
		return this._updatedBy?.id ? new UserQuery(this._request).fetch(this._updatedBy?.id) : void 0;
	}
	/** The ID of user who last updated the custom view. */
	get updatedById() {
		return this._updatedBy?.id;
	}
	/** Initiatives associated with the custom view. */
	initiatives(variables) {
		return new CustomView_InitiativesQuery(this._request, this.id, variables).fetch(variables);
	}
	/** Issues associated with the custom view. */
	issues(variables) {
		return new CustomView_IssuesQuery(this._request, this.id, variables).fetch(variables);
	}
	/** Projects associated with the custom view. */
	projects(variables) {
		return new CustomView_ProjectsQuery(this._request, this.id, variables).fetch(variables);
	}
	/** Creates a new custom view. */
	create(input) {
		return new CreateCustomViewMutation(this._request).fetch(input);
	}
	/** Deletes a custom view. */
	delete() {
		return new DeleteCustomViewMutation(this._request).fetch(this.id);
	}
	/** Updates a custom view. */
	update(input) {
		return new UpdateCustomViewMutation(this._request).fetch(this.id, input);
	}
};
/**
* CustomViewConnection model
*
* @param request - function to call the graphql client
* @param fetch - function to trigger a refetch of this CustomViewConnection model
* @param data - CustomViewConnection response data
*/
var CustomViewConnection = class extends Connection {
	constructor(request, fetch$1, data) {
		super(request, fetch$1, data.nodes.map((node) => new CustomView(request, node)), new PageInfo(request, data.pageInfo));
	}
};
/**
* CustomViewHasSubscribersPayload model
*
* @param request - function to call the graphql client
* @param data - L.CustomViewHasSubscribersPayloadFragment response data
*/
var CustomViewHasSubscribersPayload = class extends Request {
	constructor(request, data) {
		super(request);
		this.hasSubscribers = data.hasSubscribers;
	}
	/** Whether the custom view has subscribers. */
	hasSubscribers;
};
/**
* A custom view notification subscription.
*
* @param request - function to call the graphql client
* @param data - L.CustomViewNotificationSubscriptionFragment response data
*/
var CustomViewNotificationSubscription = class extends Request {
	_customView;
	_customer;
	_cycle;
	_initiative;
	_label;
	_project;
	_subscriber;
	_team;
	_user;
	constructor(request, data) {
		super(request);
		this.active = data.active;
		this.archivedAt = parseDate(data.archivedAt) ?? void 0;
		this.createdAt = parseDate(data.createdAt) ?? /* @__PURE__ */ new Date();
		this.id = data.id;
		this.notificationSubscriptionTypes = data.notificationSubscriptionTypes;
		this.updatedAt = parseDate(data.updatedAt) ?? /* @__PURE__ */ new Date();
		this.contextViewType = data.contextViewType ?? void 0;
		this.userContextViewType = data.userContextViewType ?? void 0;
		this._customView = data.customView;
		this._customer = data.customer ?? void 0;
		this._cycle = data.cycle ?? void 0;
		this._initiative = data.initiative ?? void 0;
		this._label = data.label ?? void 0;
		this._project = data.project ?? void 0;
		this._subscriber = data.subscriber;
		this._team = data.team ?? void 0;
		this._user = data.user ?? void 0;
	}
	/** Whether the subscription is active or not. */
	active;
	/** The time at which the entity was archived. Null if the entity has not been archived. */
	archivedAt;
	/** The time at which the entity was created. */
	createdAt;
	/** The unique identifier of the entity. */
	id;
	/** The type of subscription. */
	notificationSubscriptionTypes;
	/**
	* The last time at which the entity was meaningfully updated. This is the same as the creation time if the entity hasn't
	*     been updated after creation.
	*/
	updatedAt;
	/** The type of view to which the notification subscription context is associated with. */
	contextViewType;
	/** The type of user view to which the notification subscription context is associated with. */
	userContextViewType;
	/** The custom view subscribed to. */
	get customView() {
		return new CustomViewQuery(this._request).fetch(this._customView.id);
	}
	/** The ID of custom view subscribed to. */
	get customViewId() {
		return this._customView?.id;
	}
	/** The customer associated with the notification subscription. */
	get customer() {
		return this._customer?.id ? new CustomerQuery(this._request).fetch(this._customer?.id) : void 0;
	}
	/** The ID of customer associated with the notification subscription. */
	get customerId() {
		return this._customer?.id;
	}
	/** The contextual cycle view associated with the notification subscription. */
	get cycle() {
		return this._cycle?.id ? new CycleQuery(this._request).fetch(this._cycle?.id) : void 0;
	}
	/** The ID of contextual cycle view associated with the notification subscription. */
	get cycleId() {
		return this._cycle?.id;
	}
	/** The contextual initiative view associated with the notification subscription. */
	get initiative() {
		return this._initiative?.id ? new InitiativeQuery(this._request).fetch(this._initiative?.id) : void 0;
	}
	/** The ID of contextual initiative view associated with the notification subscription. */
	get initiativeId() {
		return this._initiative?.id;
	}
	/** The contextual label view associated with the notification subscription. */
	get label() {
		return this._label?.id ? new IssueLabelQuery(this._request).fetch(this._label?.id) : void 0;
	}
	/** The ID of contextual label view associated with the notification subscription. */
	get labelId() {
		return this._label?.id;
	}
	/** The contextual project view associated with the notification subscription. */
	get project() {
		return this._project?.id ? new ProjectQuery(this._request).fetch(this._project?.id) : void 0;
	}
	/** The ID of contextual project view associated with the notification subscription. */
	get projectId() {
		return this._project?.id;
	}
	/** The user that subscribed to receive notifications. */
	get subscriber() {
		return new UserQuery(this._request).fetch(this._subscriber.id);
	}
	/** The ID of user that subscribed to receive notifications. */
	get subscriberId() {
		return this._subscriber?.id;
	}
	/** The team associated with the notification subscription. */
	get team() {
		return this._team?.id ? new TeamQuery(this._request).fetch(this._team?.id) : void 0;
	}
	/** The ID of team associated with the notification subscription. */
	get teamId() {
		return this._team?.id;
	}
	/** The user view associated with the notification subscription. */
	get user() {
		return this._user?.id ? new UserQuery(this._request).fetch(this._user?.id) : void 0;
	}
	/** The ID of user view associated with the notification subscription. */
	get userId() {
		return this._user?.id;
	}
};
/**
* CustomViewPayload model
*
* @param request - function to call the graphql client
* @param data - L.CustomViewPayloadFragment response data
*/
var CustomViewPayload = class extends Request {
	_customView;
	constructor(request, data) {
		super(request);
		this.lastSyncId = data.lastSyncId;
		this.success = data.success;
		this._customView = data.customView;
	}
	/** The identifier of the last sync operation. */
	lastSyncId;
	/** Whether the operation was successful. */
	success;
	/** The custom view that was created or updated. */
	get customView() {
		return new CustomViewQuery(this._request).fetch(this._customView.id);
	}
	/** The ID of custom view that was created or updated. */
	get customViewId() {
		return this._customView?.id;
	}
};
/**
* CustomViewSuggestionPayload model
*
* @param request - function to call the graphql client
* @param data - L.CustomViewSuggestionPayloadFragment response data
*/
var CustomViewSuggestionPayload = class extends Request {
	constructor(request, data) {
		super(request);
		this.description = data.description ?? void 0;
		this.icon = data.icon ?? void 0;
		this.name = data.name ?? void 0;
	}
	/** The suggested view description. */
	description;
	/** The suggested view icon. */
	icon;
	/** The suggested view name. */
	name;
};
/**
* A customer whose needs will be tied to issues or projects.
*
* @param request - function to call the graphql client
* @param data - L.CustomerFragment response data
*/
var Customer = class extends Request {
	_integration;
	_owner;
	_status;
	_tier;
	constructor(request, data) {
		super(request);
		this.approximateNeedCount = data.approximateNeedCount;
		this.archivedAt = parseDate(data.archivedAt) ?? void 0;
		this.createdAt = parseDate(data.createdAt) ?? /* @__PURE__ */ new Date();
		this.domains = data.domains;
		this.externalIds = data.externalIds;
		this.id = data.id;
		this.logoUrl = data.logoUrl ?? void 0;
		this.mainSourceId = data.mainSourceId ?? void 0;
		this.name = data.name;
		this.revenue = data.revenue ?? void 0;
		this.size = data.size ?? void 0;
		this.slackChannelId = data.slackChannelId ?? void 0;
		this.slugId = data.slugId;
		this.updatedAt = parseDate(data.updatedAt) ?? /* @__PURE__ */ new Date();
		this.url = data.url;
		this._integration = data.integration ?? void 0;
		this._owner = data.owner ?? void 0;
		this._status = data.status;
		this._tier = data.tier ?? void 0;
	}
	/** The approximate number of needs of the customer. */
	approximateNeedCount;
	/** The time at which the entity was archived. Null if the entity has not been archived. */
	archivedAt;
	/** The time at which the entity was created. */
	createdAt;
	/** The domains associated with this customer. */
	domains;
	/** The ids of the customers in external systems. */
	externalIds;
	/** The unique identifier of the entity. */
	id;
	/** The customer's logo URL. */
	logoUrl;
	/** The ID of the main source, when a customer has multiple sources. Must be one of externalIds. */
	mainSourceId;
	/** The customer's name. */
	name;
	/** The annual revenue generated by the customer. */
	revenue;
	/** The size of the customer. */
	size;
	/** The ID of the Slack channel used to interact with the customer. */
	slackChannelId;
	/** The customer's unique URL slug. */
	slugId;
	/**
	* The last time at which the entity was meaningfully updated. This is the same as the creation time if the entity hasn't
	*     been updated after creation.
	*/
	updatedAt;
	/** URL of the customer in Linear. */
	url;
	/** The integration that manages the Customer. */
	get integration() {
		return this._integration?.id ? new IntegrationQuery(this._request).fetch(this._integration?.id) : void 0;
	}
	/** The ID of integration that manages the customer. */
	get integrationId() {
		return this._integration?.id;
	}
	/** The user who owns the customer. */
	get owner() {
		return this._owner?.id ? new UserQuery(this._request).fetch(this._owner?.id) : void 0;
	}
	/** The ID of user who owns the customer. */
	get ownerId() {
		return this._owner?.id;
	}
	/** The current status of the customer. */
	get status() {
		return new CustomerStatusQuery(this._request).fetch(this._status.id);
	}
	/** The ID of current status of the customer. */
	get statusId() {
		return this._status?.id;
	}
	/** The tier of the customer. */
	get tier() {
		return this._tier?.id ? new CustomerTierQuery(this._request).fetch(this._tier?.id) : void 0;
	}
	/** The ID of tier of the customer. */
	get tierId() {
		return this._tier?.id;
	}
	/** Creates a new customer. */
	create(input) {
		return new CreateCustomerMutation(this._request).fetch(input);
	}
	/** Deletes a customer. */
	delete() {
		return new DeleteCustomerMutation(this._request).fetch(this.id);
	}
	/** Updates a customer */
	update(input) {
		return new UpdateCustomerMutation(this._request).fetch(this.id, input);
	}
};
/**
* Certain properties of a customer.
*
* @param data - L.CustomerChildWebhookPayloadFragment response data
*/
var CustomerChildWebhookPayload = class {
	constructor(data) {
		this.domains = data.domains;
		this.externalIds = data.externalIds;
		this.id = data.id;
		this.name = data.name;
	}
	/** The domains associated with this customer. */
	domains;
	/** The ids of the customers in external systems. */
	externalIds;
	/** The ID of the customer. */
	id;
	/** The name of the customer. */
	name;
};
/**
* CustomerConnection model
*
* @param request - function to call the graphql client
* @param fetch - function to trigger a refetch of this CustomerConnection model
* @param data - CustomerConnection response data
*/
var CustomerConnection = class extends Connection {
	constructor(request, fetch$1, data) {
		super(request, fetch$1, data.nodes.map((node) => new Customer(request, node)), new PageInfo(request, data.pageInfo));
	}
};
/**
* A customer need, expressed through a reference to an issue, project, or comment.
*
* @param request - function to call the graphql client
* @param data - L.CustomerNeedFragment response data
*/
var CustomerNeed = class extends Request {
	_attachment;
	_comment;
	_creator;
	_customer;
	_issue;
	_originalIssue;
	_project;
	constructor(request, data) {
		super(request);
		this.archivedAt = parseDate(data.archivedAt) ?? void 0;
		this.body = data.body ?? void 0;
		this.createdAt = parseDate(data.createdAt) ?? /* @__PURE__ */ new Date();
		this.id = data.id;
		this.priority = data.priority;
		this.updatedAt = parseDate(data.updatedAt) ?? /* @__PURE__ */ new Date();
		this.url = data.url ?? void 0;
		this.projectAttachment = data.projectAttachment ? new ProjectAttachment(request, data.projectAttachment) : void 0;
		this._attachment = data.attachment ?? void 0;
		this._comment = data.comment ?? void 0;
		this._creator = data.creator ?? void 0;
		this._customer = data.customer ?? void 0;
		this._issue = data.issue ?? void 0;
		this._originalIssue = data.originalIssue ?? void 0;
		this._project = data.project ?? void 0;
	}
	/** The time at which the entity was archived. Null if the entity has not been archived. */
	archivedAt;
	/** The need content in markdown format. */
	body;
	/** The time at which the entity was created. */
	createdAt;
	/** The unique identifier of the entity. */
	id;
	/** Whether the customer need is important or not. 0 = Not important, 1 = Important. */
	priority;
	/**
	* The last time at which the entity was meaningfully updated. This is the same as the creation time if the entity hasn't
	*     been updated after creation.
	*/
	updatedAt;
	/** The URL of the underlying attachment, if any */
	url;
	/** The project attachment this need is referencing. */
	projectAttachment;
	/** The attachment this need is referencing. */
	get attachment() {
		return this._attachment?.id ? new AttachmentQuery(this._request).fetch(this._attachment?.id) : void 0;
	}
	/** The ID of attachment this need is referencing. */
	get attachmentId() {
		return this._attachment?.id;
	}
	/** The comment this need is referencing. */
	get comment() {
		return this._comment?.id ? new CommentQuery(this._request).fetch({ id: this._comment?.id }) : void 0;
	}
	/** The ID of comment this need is referencing. */
	get commentId() {
		return this._comment?.id;
	}
	/** The creator of the customer need. */
	get creator() {
		return this._creator?.id ? new UserQuery(this._request).fetch(this._creator?.id) : void 0;
	}
	/** The ID of creator of the customer need. */
	get creatorId() {
		return this._creator?.id;
	}
	/** The customer that this need is attached to. */
	get customer() {
		return this._customer?.id ? new CustomerQuery(this._request).fetch(this._customer?.id) : void 0;
	}
	/** The ID of customer that this need is attached to. */
	get customerId() {
		return this._customer?.id;
	}
	/** The issue this need is referencing. */
	get issue() {
		return this._issue?.id ? new IssueQuery(this._request).fetch(this._issue?.id) : void 0;
	}
	/** The ID of issue this need is referencing. */
	get issueId() {
		return this._issue?.id;
	}
	/** The issue this customer need was originally created on. Will be undefined if the customer need hasn't been moved. */
	get originalIssue() {
		return this._originalIssue?.id ? new IssueQuery(this._request).fetch(this._originalIssue?.id) : void 0;
	}
	/** The ID of issue this customer need was originally created on. will be undefined if the customer need hasn't been moved. */
	get originalIssueId() {
		return this._originalIssue?.id;
	}
	/** The project this need is referencing. */
	get project() {
		return this._project?.id ? new ProjectQuery(this._request).fetch(this._project?.id) : void 0;
	}
	/** The ID of project this need is referencing. */
	get projectId() {
		return this._project?.id;
	}
	/** Archives a customer need. */
	archive() {
		return new ArchiveCustomerNeedMutation(this._request).fetch(this.id);
	}
	/** Creates a new customer need. */
	create(input) {
		return new CreateCustomerNeedMutation(this._request).fetch(input);
	}
	/** Deletes a customer need. */
	delete(variables) {
		return new DeleteCustomerNeedMutation(this._request).fetch(this.id, variables);
	}
	/** Unarchives a customer need. */
	unarchive() {
		return new UnarchiveCustomerNeedMutation(this._request).fetch(this.id);
	}
	/** Updates a customer need */
	update(input) {
		return new UpdateCustomerNeedMutation(this._request).fetch(this.id, input);
	}
};
/**
* A generic payload return from entity archive mutations.
*
* @param request - function to call the graphql client
* @param data - L.CustomerNeedArchivePayloadFragment response data
*/
var CustomerNeedArchivePayload = class extends Request {
	_entity;
	constructor(request, data) {
		super(request);
		this.lastSyncId = data.lastSyncId;
		this.success = data.success;
		this._entity = data.entity ?? void 0;
	}
	/** The identifier of the last sync operation. */
	lastSyncId;
	/** Whether the operation was successful. */
	success;
	/** The archived/unarchived entity. Null if entity was deleted. */
	get entity() {
		return this._entity?.id ? new CustomerNeedQuery(this._request).fetch({ id: this._entity?.id }) : void 0;
	}
	/** The ID of archived/unarchived entity. null if entity was deleted. */
	get entityId() {
		return this._entity?.id;
	}
};
/**
* Certain properties of a customer need.
*
* @param data - L.CustomerNeedChildWebhookPayloadFragment response data
*/
var CustomerNeedChildWebhookPayload = class {
	constructor(data) {
		this.attachmentId = data.attachmentId ?? void 0;
		this.customerId = data.customerId ?? void 0;
		this.id = data.id;
		this.issueId = data.issueId ?? void 0;
		this.projectId = data.projectId ?? void 0;
	}
	/** The ID of the attachment this need is referencing. */
	attachmentId;
	/** The ID of the customer that this need is attached to. */
	customerId;
	/** The ID of the customer need. */
	id;
	/** The ID of the issue this need is referencing. */
	issueId;
	/** The ID of the project this need is referencing. */
	projectId;
};
/**
* CustomerNeedConnection model
*
* @param request - function to call the graphql client
* @param fetch - function to trigger a refetch of this CustomerNeedConnection model
* @param data - CustomerNeedConnection response data
*/
var CustomerNeedConnection = class extends Connection {
	constructor(request, fetch$1, data) {
		super(request, fetch$1, data.nodes.map((node) => new CustomerNeed(request, node)), new PageInfo(request, data.pageInfo));
	}
};
/**
* A customer need related notification.
*
* @param request - function to call the graphql client
* @param data - L.CustomerNeedNotificationFragment response data
*/
var CustomerNeedNotification = class extends Request {
	_actor;
	_customerNeed;
	_externalUserActor;
	_relatedIssue;
	_relatedProject;
	_user;
	constructor(request, data) {
		super(request);
		this.archivedAt = parseDate(data.archivedAt) ?? void 0;
		this.createdAt = parseDate(data.createdAt) ?? /* @__PURE__ */ new Date();
		this.customerNeedId = data.customerNeedId;
		this.emailedAt = parseDate(data.emailedAt) ?? void 0;
		this.id = data.id;
		this.readAt = parseDate(data.readAt) ?? void 0;
		this.snoozedUntilAt = parseDate(data.snoozedUntilAt) ?? void 0;
		this.type = data.type;
		this.unsnoozedAt = parseDate(data.unsnoozedAt) ?? void 0;
		this.updatedAt = parseDate(data.updatedAt) ?? /* @__PURE__ */ new Date();
		this.botActor = data.botActor ? new ActorBot(request, data.botActor) : void 0;
		this.category = data.category;
		this._actor = data.actor ?? void 0;
		this._customerNeed = data.customerNeed;
		this._externalUserActor = data.externalUserActor ?? void 0;
		this._relatedIssue = data.relatedIssue ?? void 0;
		this._relatedProject = data.relatedProject ?? void 0;
		this._user = data.user;
	}
	/** The time at which the entity was archived. Null if the entity has not been archived. */
	archivedAt;
	/** The time at which the entity was created. */
	createdAt;
	/** Related customer need. */
	customerNeedId;
	/**
	* The time at when an email reminder for this notification was sent to the user. Null, if no email
	*     reminder has been sent.
	*/
	emailedAt;
	/** The unique identifier of the entity. */
	id;
	/** The time at when the user marked the notification as read. Null, if the the user hasn't read the notification */
	readAt;
	/** The time until a notification will be snoozed. After that it will appear in the inbox again. */
	snoozedUntilAt;
	/** Notification type. */
	type;
	/** The time at which a notification was unsnoozed.. */
	unsnoozedAt;
	/**
	* The last time at which the entity was meaningfully updated. This is the same as the creation time if the entity hasn't
	*     been updated after creation.
	*/
	updatedAt;
	/** The bot that caused the notification. */
	botActor;
	/** The category of the notification. */
	category;
	/** The user that caused the notification. */
	get actor() {
		return this._actor?.id ? new UserQuery(this._request).fetch(this._actor?.id) : void 0;
	}
	/** The ID of user that caused the notification. */
	get actorId() {
		return this._actor?.id;
	}
	/** The customer need related to the notification. */
	get customerNeed() {
		return new CustomerNeedQuery(this._request).fetch({ id: this._customerNeed.id });
	}
	/** The external user that caused the notification. */
	get externalUserActor() {
		return this._externalUserActor?.id ? new ExternalUserQuery(this._request).fetch(this._externalUserActor?.id) : void 0;
	}
	/** The ID of external user that caused the notification. */
	get externalUserActorId() {
		return this._externalUserActor?.id;
	}
	/** The issue related to the notification. */
	get relatedIssue() {
		return this._relatedIssue?.id ? new IssueQuery(this._request).fetch(this._relatedIssue?.id) : void 0;
	}
	/** The ID of issue related to the notification. */
	get relatedIssueId() {
		return this._relatedIssue?.id;
	}
	/** The project related to the notification. */
	get relatedProject() {
		return this._relatedProject?.id ? new ProjectQuery(this._request).fetch(this._relatedProject?.id) : void 0;
	}
	/** The ID of project related to the notification. */
	get relatedProjectId() {
		return this._relatedProject?.id;
	}
	/** The user that received the notification. */
	get user() {
		return new UserQuery(this._request).fetch(this._user.id);
	}
	/** The ID of user that received the notification. */
	get userId() {
		return this._user?.id;
	}
};
/**
* CustomerNeedPayload model
*
* @param request - function to call the graphql client
* @param data - L.CustomerNeedPayloadFragment response data
*/
var CustomerNeedPayload = class extends Request {
	_need;
	constructor(request, data) {
		super(request);
		this.lastSyncId = data.lastSyncId;
		this.success = data.success;
		this._need = data.need;
	}
	/** The identifier of the last sync operation. */
	lastSyncId;
	/** Whether the operation was successful. */
	success;
	/** The customer need that was created or updated. */
	get need() {
		return new CustomerNeedQuery(this._request).fetch({ id: this._need.id });
	}
	/** The ID of customer need that was created or updated. */
	get needId() {
		return this._need?.id;
	}
};
/**
* CustomerNeedUpdatePayload model
*
* @param request - function to call the graphql client
* @param data - L.CustomerNeedUpdatePayloadFragment response data
*/
var CustomerNeedUpdatePayload = class extends Request {
	_need;
	constructor(request, data) {
		super(request);
		this.lastSyncId = data.lastSyncId;
		this.success = data.success;
		this.updatedRelatedNeeds = data.updatedRelatedNeeds.map((node) => new CustomerNeed(request, node));
		this._need = data.need;
	}
	/** The identifier of the last sync operation. */
	lastSyncId;
	/** Whether the operation was successful. */
	success;
	/** The related customer needs that were updated. */
	updatedRelatedNeeds;
	/** The customer need that was created or updated. */
	get need() {
		return new CustomerNeedQuery(this._request).fetch({ id: this._need.id });
	}
	/** The ID of customer need that was created or updated. */
	get needId() {
		return this._need?.id;
	}
};
/**
* Payload for a customer need webhook.
*
* @param data - L.CustomerNeedWebhookPayloadFragment response data
*/
var CustomerNeedWebhookPayload = class {
	constructor(data) {
		this.archivedAt = data.archivedAt ?? void 0;
		this.attachmentId = data.attachmentId ?? void 0;
		this.body = data.body ?? void 0;
		this.commentId = data.commentId ?? void 0;
		this.createdAt = data.createdAt;
		this.creatorId = data.creatorId ?? void 0;
		this.customerId = data.customerId ?? void 0;
		this.id = data.id;
		this.issueId = data.issueId ?? void 0;
		this.originalIssueId = data.originalIssueId ?? void 0;
		this.priority = data.priority;
		this.projectAttachmentId = data.projectAttachmentId ?? void 0;
		this.projectId = data.projectId ?? void 0;
		this.updatedAt = data.updatedAt;
		this.attachment = data.attachment ? new AttachmentWebhookPayload(data.attachment) : void 0;
		this.customer = data.customer ? new CustomerChildWebhookPayload(data.customer) : void 0;
		this.issue = data.issue ? new IssueChildWebhookPayload(data.issue) : void 0;
		this.project = data.project ? new ProjectChildWebhookPayload(data.project) : void 0;
	}
	/** The time at which the entity was archived. */
	archivedAt;
	/** The ID of the attachment this need is referencing. */
	attachmentId;
	/** The body of the need in Markdown format. */
	body;
	/** The ID of the comment this need is referencing. */
	commentId;
	/** The time at which the entity was created. */
	createdAt;
	/** The ID of the creator of the customer need. */
	creatorId;
	/** The ID of the customer that this need is attached to. */
	customerId;
	/** The ID of the entity. */
	id;
	/** The ID of the issue this need is referencing. */
	issueId;
	/** The issue ID this customer need was originally created on. Will be undefined if the customer need hasn't been moved. */
	originalIssueId;
	/** The priority of the need. */
	priority;
	/** The ID of the project attachment this need is referencing. */
	projectAttachmentId;
	/** The ID of the project this need is referencing. */
	projectId;
	/** The time at which the entity was updated. */
	updatedAt;
	/** The attachment this need is referencing. */
	attachment;
	/** The customer that this need is attached to. */
	customer;
	/** The issue this need is referencing. */
	issue;
	/** The project this need is referencing. */
	project;
};
/**
* A customer related notification.
*
* @param request - function to call the graphql client
* @param data - L.CustomerNotificationFragment response data
*/
var CustomerNotification = class extends Request {
	_actor;
	_customer;
	_externalUserActor;
	_user;
	constructor(request, data) {
		super(request);
		this.archivedAt = parseDate(data.archivedAt) ?? void 0;
		this.createdAt = parseDate(data.createdAt) ?? /* @__PURE__ */ new Date();
		this.customerId = data.customerId;
		this.emailedAt = parseDate(data.emailedAt) ?? void 0;
		this.id = data.id;
		this.readAt = parseDate(data.readAt) ?? void 0;
		this.snoozedUntilAt = parseDate(data.snoozedUntilAt) ?? void 0;
		this.type = data.type;
		this.unsnoozedAt = parseDate(data.unsnoozedAt) ?? void 0;
		this.updatedAt = parseDate(data.updatedAt) ?? /* @__PURE__ */ new Date();
		this.botActor = data.botActor ? new ActorBot(request, data.botActor) : void 0;
		this.category = data.category;
		this._actor = data.actor ?? void 0;
		this._customer = data.customer;
		this._externalUserActor = data.externalUserActor ?? void 0;
		this._user = data.user;
	}
	/** The time at which the entity was archived. Null if the entity has not been archived. */
	archivedAt;
	/** The time at which the entity was created. */
	createdAt;
	/** Related customer. */
	customerId;
	/**
	* The time at when an email reminder for this notification was sent to the user. Null, if no email
	*     reminder has been sent.
	*/
	emailedAt;
	/** The unique identifier of the entity. */
	id;
	/** The time at when the user marked the notification as read. Null, if the the user hasn't read the notification */
	readAt;
	/** The time until a notification will be snoozed. After that it will appear in the inbox again. */
	snoozedUntilAt;
	/** Notification type. */
	type;
	/** The time at which a notification was unsnoozed.. */
	unsnoozedAt;
	/**
	* The last time at which the entity was meaningfully updated. This is the same as the creation time if the entity hasn't
	*     been updated after creation.
	*/
	updatedAt;
	/** The bot that caused the notification. */
	botActor;
	/** The category of the notification. */
	category;
	/** The user that caused the notification. */
	get actor() {
		return this._actor?.id ? new UserQuery(this._request).fetch(this._actor?.id) : void 0;
	}
	/** The ID of user that caused the notification. */
	get actorId() {
		return this._actor?.id;
	}
	/** The customer related to the notification. */
	get customer() {
		return new CustomerQuery(this._request).fetch(this._customer.id);
	}
	/** The external user that caused the notification. */
	get externalUserActor() {
		return this._externalUserActor?.id ? new ExternalUserQuery(this._request).fetch(this._externalUserActor?.id) : void 0;
	}
	/** The ID of external user that caused the notification. */
	get externalUserActorId() {
		return this._externalUserActor?.id;
	}
	/** The user that received the notification. */
	get user() {
		return new UserQuery(this._request).fetch(this._user.id);
	}
	/** The ID of user that received the notification. */
	get userId() {
		return this._user?.id;
	}
};
/**
* A customer notification subscription.
*
* @param request - function to call the graphql client
* @param data - L.CustomerNotificationSubscriptionFragment response data
*/
var CustomerNotificationSubscription = class extends Request {
	_customView;
	_customer;
	_cycle;
	_initiative;
	_label;
	_project;
	_subscriber;
	_team;
	_user;
	constructor(request, data) {
		super(request);
		this.active = data.active;
		this.archivedAt = parseDate(data.archivedAt) ?? void 0;
		this.createdAt = parseDate(data.createdAt) ?? /* @__PURE__ */ new Date();
		this.id = data.id;
		this.notificationSubscriptionTypes = data.notificationSubscriptionTypes;
		this.updatedAt = parseDate(data.updatedAt) ?? /* @__PURE__ */ new Date();
		this.contextViewType = data.contextViewType ?? void 0;
		this.userContextViewType = data.userContextViewType ?? void 0;
		this._customView = data.customView ?? void 0;
		this._customer = data.customer;
		this._cycle = data.cycle ?? void 0;
		this._initiative = data.initiative ?? void 0;
		this._label = data.label ?? void 0;
		this._project = data.project ?? void 0;
		this._subscriber = data.subscriber;
		this._team = data.team ?? void 0;
		this._user = data.user ?? void 0;
	}
	/** Whether the subscription is active or not. */
	active;
	/** The time at which the entity was archived. Null if the entity has not been archived. */
	archivedAt;
	/** The time at which the entity was created. */
	createdAt;
	/** The unique identifier of the entity. */
	id;
	/** The type of subscription. */
	notificationSubscriptionTypes;
	/**
	* The last time at which the entity was meaningfully updated. This is the same as the creation time if the entity hasn't
	*     been updated after creation.
	*/
	updatedAt;
	/** The type of view to which the notification subscription context is associated with. */
	contextViewType;
	/** The type of user view to which the notification subscription context is associated with. */
	userContextViewType;
	/** The contextual custom view associated with the notification subscription. */
	get customView() {
		return this._customView?.id ? new CustomViewQuery(this._request).fetch(this._customView?.id) : void 0;
	}
	/** The ID of contextual custom view associated with the notification subscription. */
	get customViewId() {
		return this._customView?.id;
	}
	/** The customer subscribed to. */
	get customer() {
		return new CustomerQuery(this._request).fetch(this._customer.id);
	}
	/** The ID of customer subscribed to. */
	get customerId() {
		return this._customer?.id;
	}
	/** The contextual cycle view associated with the notification subscription. */
	get cycle() {
		return this._cycle?.id ? new CycleQuery(this._request).fetch(this._cycle?.id) : void 0;
	}
	/** The ID of contextual cycle view associated with the notification subscription. */
	get cycleId() {
		return this._cycle?.id;
	}
	/** The contextual initiative view associated with the notification subscription. */
	get initiative() {
		return this._initiative?.id ? new InitiativeQuery(this._request).fetch(this._initiative?.id) : void 0;
	}
	/** The ID of contextual initiative view associated with the notification subscription. */
	get initiativeId() {
		return this._initiative?.id;
	}
	/** The contextual label view associated with the notification subscription. */
	get label() {
		return this._label?.id ? new IssueLabelQuery(this._request).fetch(this._label?.id) : void 0;
	}
	/** The ID of contextual label view associated with the notification subscription. */
	get labelId() {
		return this._label?.id;
	}
	/** The contextual project view associated with the notification subscription. */
	get project() {
		return this._project?.id ? new ProjectQuery(this._request).fetch(this._project?.id) : void 0;
	}
	/** The ID of contextual project view associated with the notification subscription. */
	get projectId() {
		return this._project?.id;
	}
	/** The user that subscribed to receive notifications. */
	get subscriber() {
		return new UserQuery(this._request).fetch(this._subscriber.id);
	}
	/** The ID of user that subscribed to receive notifications. */
	get subscriberId() {
		return this._subscriber?.id;
	}
	/** The team associated with the notification subscription. */
	get team() {
		return this._team?.id ? new TeamQuery(this._request).fetch(this._team?.id) : void 0;
	}
	/** The ID of team associated with the notification subscription. */
	get teamId() {
		return this._team?.id;
	}
	/** The user view associated with the notification subscription. */
	get user() {
		return this._user?.id ? new UserQuery(this._request).fetch(this._user?.id) : void 0;
	}
	/** The ID of user view associated with the notification subscription. */
	get userId() {
		return this._user?.id;
	}
};
/**
* CustomerPayload model
*
* @param request - function to call the graphql client
* @param data - L.CustomerPayloadFragment response data
*/
var CustomerPayload = class extends Request {
	_customer;
	constructor(request, data) {
		super(request);
		this.lastSyncId = data.lastSyncId;
		this.success = data.success;
		this._customer = data.customer;
	}
	/** The identifier of the last sync operation. */
	lastSyncId;
	/** Whether the operation was successful. */
	success;
	/** The customer that was created or updated. */
	get customer() {
		return new CustomerQuery(this._request).fetch(this._customer.id);
	}
	/** The ID of customer that was created or updated. */
	get customerId() {
		return this._customer?.id;
	}
};
/**
* A customer status.
*
* @param request - function to call the graphql client
* @param data - L.CustomerStatusFragment response data
*/
var CustomerStatus = class extends Request {
	constructor(request, data) {
		super(request);
		this.archivedAt = parseDate(data.archivedAt) ?? void 0;
		this.color = data.color;
		this.createdAt = parseDate(data.createdAt) ?? /* @__PURE__ */ new Date();
		this.description = data.description ?? void 0;
		this.displayName = data.displayName;
		this.id = data.id;
		this.name = data.name;
		this.position = data.position;
		this.updatedAt = parseDate(data.updatedAt) ?? /* @__PURE__ */ new Date();
		this.type = data.type ?? void 0;
	}
	/** The time at which the entity was archived. Null if the entity has not been archived. */
	archivedAt;
	/** The UI color of the status as a HEX string. */
	color;
	/** The time at which the entity was created. */
	createdAt;
	/** Description of the status. */
	description;
	/** The display name of the status. */
	displayName;
	/** The unique identifier of the entity. */
	id;
	/** The name of the status. */
	name;
	/** The position of the status in the workspace's customers flow. */
	position;
	/**
	* The last time at which the entity was meaningfully updated. This is the same as the creation time if the entity hasn't
	*     been updated after creation.
	*/
	updatedAt;
	/** The type of the customer status. */
	type;
	/** Creates a new customer status. */
	create(input) {
		return new CreateCustomerStatusMutation(this._request).fetch(input);
	}
	/** Deletes a customer status. */
	delete() {
		return new DeleteCustomerStatusMutation(this._request).fetch(this.id);
	}
	/** Updates a customer status. */
	update(input) {
		return new UpdateCustomerStatusMutation(this._request).fetch(this.id, input);
	}
};
/**
* Certain properties of a customer status.
*
* @param data - L.CustomerStatusChildWebhookPayloadFragment response data
*/
var CustomerStatusChildWebhookPayload = class {
	constructor(data) {
		this.color = data.color;
		this.description = data.description ?? void 0;
		this.displayName = data.displayName;
		this.id = data.id;
		this.name = data.name;
		this.type = data.type ?? void 0;
	}
	/** The color of the customer status. */
	color;
	/** The description of the customer status. */
	description;
	/** The display name of the customer status. */
	displayName;
	/** The ID of the customer status. */
	id;
	/** The name of the customer status. */
	name;
	/** The type of the customer status. */
	type;
};
/**
* CustomerStatusConnection model
*
* @param request - function to call the graphql client
* @param fetch - function to trigger a refetch of this CustomerStatusConnection model
* @param data - CustomerStatusConnection response data
*/
var CustomerStatusConnection = class extends Connection {
	constructor(request, fetch$1, data) {
		super(request, fetch$1, data.nodes.map((node) => new CustomerStatus(request, node)), new PageInfo(request, data.pageInfo));
	}
};
/**
* CustomerStatusPayload model
*
* @param request - function to call the graphql client
* @param data - L.CustomerStatusPayloadFragment response data
*/
var CustomerStatusPayload = class extends Request {
	_status;
	constructor(request, data) {
		super(request);
		this.lastSyncId = data.lastSyncId;
		this.success = data.success;
		this._status = data.status;
	}
	/** The identifier of the last sync operation. */
	lastSyncId;
	/** Whether the operation was successful. */
	success;
	/** The customer status that was created or updated. */
	get status() {
		return new CustomerStatusQuery(this._request).fetch(this._status.id);
	}
	/** The ID of customer status that was created or updated. */
	get statusId() {
		return this._status?.id;
	}
};
/**
* A customer tier.
*
* @param request - function to call the graphql client
* @param data - L.CustomerTierFragment response data
*/
var CustomerTier = class extends Request {
	constructor(request, data) {
		super(request);
		this.archivedAt = parseDate(data.archivedAt) ?? void 0;
		this.color = data.color;
		this.createdAt = parseDate(data.createdAt) ?? /* @__PURE__ */ new Date();
		this.description = data.description ?? void 0;
		this.displayName = data.displayName;
		this.id = data.id;
		this.name = data.name;
		this.position = data.position;
		this.updatedAt = parseDate(data.updatedAt) ?? /* @__PURE__ */ new Date();
	}
	/** The time at which the entity was archived. Null if the entity has not been archived. */
	archivedAt;
	/** The UI color of the tier as a HEX string. */
	color;
	/** The time at which the entity was created. */
	createdAt;
	/** Description of the tier. */
	description;
	/** The display name of the tier. */
	displayName;
	/** The unique identifier of the entity. */
	id;
	/** The name of the tier. */
	name;
	/** The position of the tier in the workspace's customers flow. */
	position;
	/**
	* The last time at which the entity was meaningfully updated. This is the same as the creation time if the entity hasn't
	*     been updated after creation.
	*/
	updatedAt;
	/** Creates a new customer tier. */
	create(input) {
		return new CreateCustomerTierMutation(this._request).fetch(input);
	}
	/** Deletes a customer tier. */
	delete() {
		return new DeleteCustomerTierMutation(this._request).fetch(this.id);
	}
	/** Updates a customer tier. */
	update(input) {
		return new UpdateCustomerTierMutation(this._request).fetch(this.id, input);
	}
};
/**
* Certain properties of a customer tier.
*
* @param data - L.CustomerTierChildWebhookPayloadFragment response data
*/
var CustomerTierChildWebhookPayload = class {
	constructor(data) {
		this.color = data.color;
		this.description = data.description ?? void 0;
		this.displayName = data.displayName;
		this.id = data.id;
		this.name = data.name;
	}
	/** The color of the customer tier. */
	color;
	/** The description of the customer tier. */
	description;
	/** The display name of the customer tier. */
	displayName;
	/** The ID of the customer tier. */
	id;
	/** The name of the customer tier. */
	name;
};
/**
* CustomerTierConnection model
*
* @param request - function to call the graphql client
* @param fetch - function to trigger a refetch of this CustomerTierConnection model
* @param data - CustomerTierConnection response data
*/
var CustomerTierConnection = class extends Connection {
	constructor(request, fetch$1, data) {
		super(request, fetch$1, data.nodes.map((node) => new CustomerTier(request, node)), new PageInfo(request, data.pageInfo));
	}
};
/**
* CustomerTierPayload model
*
* @param request - function to call the graphql client
* @param data - L.CustomerTierPayloadFragment response data
*/
var CustomerTierPayload = class extends Request {
	_tier;
	constructor(request, data) {
		super(request);
		this.lastSyncId = data.lastSyncId;
		this.success = data.success;
		this._tier = data.tier;
	}
	/** The identifier of the last sync operation. */
	lastSyncId;
	/** Whether the operation was successful. */
	success;
	/** The customer tier that was created or updated. */
	get tier() {
		return new CustomerTierQuery(this._request).fetch(this._tier.id);
	}
	/** The ID of customer tier that was created or updated. */
	get tierId() {
		return this._tier?.id;
	}
};
/**
* Payload for a customer webhook.
*
* @param data - L.CustomerWebhookPayloadFragment response data
*/
var CustomerWebhookPayload = class {
	constructor(data) {
		this.approximateNeedCount = data.approximateNeedCount;
		this.archivedAt = data.archivedAt ?? void 0;
		this.createdAt = data.createdAt;
		this.domains = data.domains;
		this.externalIds = data.externalIds;
		this.id = data.id;
		this.logoUrl = data.logoUrl ?? void 0;
		this.mainSourceId = data.mainSourceId ?? void 0;
		this.name = data.name;
		this.ownerId = data.ownerId ?? void 0;
		this.revenue = data.revenue ?? void 0;
		this.size = data.size ?? void 0;
		this.slackChannelId = data.slackChannelId ?? void 0;
		this.slugId = data.slugId;
		this.statusId = data.statusId ?? void 0;
		this.tierId = data.tierId ?? void 0;
		this.updatedAt = data.updatedAt;
		this.url = data.url;
		this.status = data.status ? new CustomerStatusChildWebhookPayload(data.status) : void 0;
		this.tier = data.tier ? new CustomerTierChildWebhookPayload(data.tier) : void 0;
	}
	/** The approximate number of needs of the customer. */
	approximateNeedCount;
	/** The time at which the entity was archived. */
	archivedAt;
	/** The time at which the entity was created. */
	createdAt;
	/** The domains associated with this customer. */
	domains;
	/** The ids of the customers in external systems. */
	externalIds;
	/** The ID of the entity. */
	id;
	/** The customer's logo URL. */
	logoUrl;
	/** The ID of the main source, when a customer has multiple sources. Must be one of externalIds. */
	mainSourceId;
	/** The name of the customer. */
	name;
	/** The ID of the user who owns the customer. */
	ownerId;
	/** The annual revenue generated by the customer. */
	revenue;
	/** The size of the customer. */
	size;
	/** The ID of the Slack channel used to interact with the customer. */
	slackChannelId;
	/** The customer's unique URL slug. */
	slugId;
	/** The ID of the customer status. */
	statusId;
	/** The ID of the customer tier. */
	tierId;
	/** The time at which the entity was updated. */
	updatedAt;
	/** The URL of the customer. */
	url;
	/** The customer status. */
	status;
	/** The customer tier. */
	tier;
};
/**
* A set of issues to be resolved in a specified amount of time.
*
* @param request - function to call the graphql client
* @param data - L.CycleFragment response data
*/
var Cycle = class extends Request {
	_inheritedFrom;
	_team;
	constructor(request, data) {
		super(request);
		this.archivedAt = parseDate(data.archivedAt) ?? void 0;
		this.autoArchivedAt = parseDate(data.autoArchivedAt) ?? void 0;
		this.completedAt = parseDate(data.completedAt) ?? void 0;
		this.completedIssueCountHistory = data.completedIssueCountHistory;
		this.completedScopeHistory = data.completedScopeHistory;
		this.createdAt = parseDate(data.createdAt) ?? /* @__PURE__ */ new Date();
		this.description = data.description ?? void 0;
		this.endsAt = parseDate(data.endsAt) ?? /* @__PURE__ */ new Date();
		this.id = data.id;
		this.inProgressScopeHistory = data.inProgressScopeHistory;
		this.isActive = data.isActive;
		this.isFuture = data.isFuture;
		this.isNext = data.isNext;
		this.isPast = data.isPast;
		this.isPrevious = data.isPrevious;
		this.issueCountHistory = data.issueCountHistory;
		this.name = data.name ?? void 0;
		this.number = data.number;
		this.progress = data.progress;
		this.scopeHistory = data.scopeHistory;
		this.startsAt = parseDate(data.startsAt) ?? /* @__PURE__ */ new Date();
		this.updatedAt = parseDate(data.updatedAt) ?? /* @__PURE__ */ new Date();
		this._inheritedFrom = data.inheritedFrom ?? void 0;
		this._team = data.team;
	}
	/** The time at which the entity was archived. Null if the entity has not been archived. */
	archivedAt;
	/** The time at which the cycle was automatically archived by the auto pruning process. */
	autoArchivedAt;
	/** The completion time of the cycle. If null, the cycle hasn't been completed. */
	completedAt;
	/** The number of completed issues in the cycle after each day. */
	completedIssueCountHistory;
	/** The number of completed estimation points after each day. */
	completedScopeHistory;
	/** The time at which the entity was created. */
	createdAt;
	/** The cycle's description. */
	description;
	/** The end time of the cycle. */
	endsAt;
	/** The unique identifier of the entity. */
	id;
	/** The number of in progress estimation points after each day. */
	inProgressScopeHistory;
	/** Whether the cycle is currently active. */
	isActive;
	/** Whether the cycle is in the future. */
	isFuture;
	/** Whether the cycle is the next cycle for the team. */
	isNext;
	/** Whether the cycle is in the past. */
	isPast;
	/** Whether the cycle is the previous cycle for the team. */
	isPrevious;
	/** The total number of issues in the cycle after each day. */
	issueCountHistory;
	/** The custom name of the cycle. */
	name;
	/** The number of the cycle. */
	number;
	/** The overall progress of the cycle. This is the (completed estimate points + 0.25 * in progress estimate points) / total estimate points. */
	progress;
	/** The total number of estimation points after each day. */
	scopeHistory;
	/** The start time of the cycle. */
	startsAt;
	/**
	* The last time at which the entity was meaningfully updated. This is the same as the creation time if the entity hasn't
	*     been updated after creation.
	*/
	updatedAt;
	/** The cycle inherited from. */
	get inheritedFrom() {
		return this._inheritedFrom?.id ? new CycleQuery(this._request).fetch(this._inheritedFrom?.id) : void 0;
	}
	/** The ID of cycle inherited from. */
	get inheritedFromId() {
		return this._inheritedFrom?.id;
	}
	/** The team that the cycle is associated with. */
	get team() {
		return new TeamQuery(this._request).fetch(this._team.id);
	}
	/** The ID of team that the cycle is associated with. */
	get teamId() {
		return this._team?.id;
	}
	/** Issues associated with the cycle. */
	issues(variables) {
		return new Cycle_IssuesQuery(this._request, this.id, variables).fetch(variables);
	}
	/** Issues that weren't completed when the cycle was closed. */
	uncompletedIssuesUponClose(variables) {
		return new Cycle_UncompletedIssuesUponCloseQuery(this._request, this.id, variables).fetch(variables);
	}
	/** Archives a cycle. */
	archive() {
		return new ArchiveCycleMutation(this._request).fetch(this.id);
	}
	/** Creates a new cycle. */
	create(input) {
		return new CreateCycleMutation(this._request).fetch(input);
	}
	/** Updates a cycle. */
	update(input) {
		return new UpdateCycleMutation(this._request).fetch(this.id, input);
	}
};
/**
* A generic payload return from entity archive mutations.
*
* @param request - function to call the graphql client
* @param data - L.CycleArchivePayloadFragment response data
*/
var CycleArchivePayload = class extends Request {
	_entity;
	constructor(request, data) {
		super(request);
		this.lastSyncId = data.lastSyncId;
		this.success = data.success;
		this._entity = data.entity ?? void 0;
	}
	/** The identifier of the last sync operation. */
	lastSyncId;
	/** Whether the operation was successful. */
	success;
	/** The archived/unarchived entity. Null if entity was deleted. */
	get entity() {
		return this._entity?.id ? new CycleQuery(this._request).fetch(this._entity?.id) : void 0;
	}
	/** The ID of archived/unarchived entity. null if entity was deleted. */
	get entityId() {
		return this._entity?.id;
	}
};
/**
* Certain properties of a cycle.
*
* @param data - L.CycleChildWebhookPayloadFragment response data
*/
var CycleChildWebhookPayload = class {
	constructor(data) {
		this.endsAt = data.endsAt;
		this.id = data.id;
		this.name = data.name ?? void 0;
		this.number = data.number;
		this.startsAt = data.startsAt;
	}
	/** The end date of the cycle. */
	endsAt;
	/** The ID of the cycle. */
	id;
	/** The name of the cycle. */
	name;
	/** The number of the cycle. */
	number;
	/** The start date of the cycle. */
	startsAt;
};
/**
* CycleConnection model
*
* @param request - function to call the graphql client
* @param fetch - function to trigger a refetch of this CycleConnection model
* @param data - CycleConnection response data
*/
var CycleConnection = class extends Connection {
	constructor(request, fetch$1, data) {
		super(request, fetch$1, data.nodes.map((node) => new Cycle(request, node)), new PageInfo(request, data.pageInfo));
	}
};
/**
* A cycle notification subscription.
*
* @param request - function to call the graphql client
* @param data - L.CycleNotificationSubscriptionFragment response data
*/
var CycleNotificationSubscription = class extends Request {
	_customView;
	_customer;
	_cycle;
	_initiative;
	_label;
	_project;
	_subscriber;
	_team;
	_user;
	constructor(request, data) {
		super(request);
		this.active = data.active;
		this.archivedAt = parseDate(data.archivedAt) ?? void 0;
		this.createdAt = parseDate(data.createdAt) ?? /* @__PURE__ */ new Date();
		this.id = data.id;
		this.notificationSubscriptionTypes = data.notificationSubscriptionTypes;
		this.updatedAt = parseDate(data.updatedAt) ?? /* @__PURE__ */ new Date();
		this.contextViewType = data.contextViewType ?? void 0;
		this.userContextViewType = data.userContextViewType ?? void 0;
		this._customView = data.customView ?? void 0;
		this._customer = data.customer ?? void 0;
		this._cycle = data.cycle;
		this._initiative = data.initiative ?? void 0;
		this._label = data.label ?? void 0;
		this._project = data.project ?? void 0;
		this._subscriber = data.subscriber;
		this._team = data.team ?? void 0;
		this._user = data.user ?? void 0;
	}
	/** Whether the subscription is active or not. */
	active;
	/** The time at which the entity was archived. Null if the entity has not been archived. */
	archivedAt;
	/** The time at which the entity was created. */
	createdAt;
	/** The unique identifier of the entity. */
	id;
	/** The type of subscription. */
	notificationSubscriptionTypes;
	/**
	* The last time at which the entity was meaningfully updated. This is the same as the creation time if the entity hasn't
	*     been updated after creation.
	*/
	updatedAt;
	/** The type of view to which the notification subscription context is associated with. */
	contextViewType;
	/** The type of user view to which the notification subscription context is associated with. */
	userContextViewType;
	/** The contextual custom view associated with the notification subscription. */
	get customView() {
		return this._customView?.id ? new CustomViewQuery(this._request).fetch(this._customView?.id) : void 0;
	}
	/** The ID of contextual custom view associated with the notification subscription. */
	get customViewId() {
		return this._customView?.id;
	}
	/** The customer associated with the notification subscription. */
	get customer() {
		return this._customer?.id ? new CustomerQuery(this._request).fetch(this._customer?.id) : void 0;
	}
	/** The ID of customer associated with the notification subscription. */
	get customerId() {
		return this._customer?.id;
	}
	/** The cycle subscribed to. */
	get cycle() {
		return new CycleQuery(this._request).fetch(this._cycle.id);
	}
	/** The ID of cycle subscribed to. */
	get cycleId() {
		return this._cycle?.id;
	}
	/** The contextual initiative view associated with the notification subscription. */
	get initiative() {
		return this._initiative?.id ? new InitiativeQuery(this._request).fetch(this._initiative?.id) : void 0;
	}
	/** The ID of contextual initiative view associated with the notification subscription. */
	get initiativeId() {
		return this._initiative?.id;
	}
	/** The contextual label view associated with the notification subscription. */
	get label() {
		return this._label?.id ? new IssueLabelQuery(this._request).fetch(this._label?.id) : void 0;
	}
	/** The ID of contextual label view associated with the notification subscription. */
	get labelId() {
		return this._label?.id;
	}
	/** The contextual project view associated with the notification subscription. */
	get project() {
		return this._project?.id ? new ProjectQuery(this._request).fetch(this._project?.id) : void 0;
	}
	/** The ID of contextual project view associated with the notification subscription. */
	get projectId() {
		return this._project?.id;
	}
	/** The user that subscribed to receive notifications. */
	get subscriber() {
		return new UserQuery(this._request).fetch(this._subscriber.id);
	}
	/** The ID of user that subscribed to receive notifications. */
	get subscriberId() {
		return this._subscriber?.id;
	}
	/** The team associated with the notification subscription. */
	get team() {
		return this._team?.id ? new TeamQuery(this._request).fetch(this._team?.id) : void 0;
	}
	/** The ID of team associated with the notification subscription. */
	get teamId() {
		return this._team?.id;
	}
	/** The user view associated with the notification subscription. */
	get user() {
		return this._user?.id ? new UserQuery(this._request).fetch(this._user?.id) : void 0;
	}
	/** The ID of user view associated with the notification subscription. */
	get userId() {
		return this._user?.id;
	}
};
/**
* CyclePayload model
*
* @param request - function to call the graphql client
* @param data - L.CyclePayloadFragment response data
*/
var CyclePayload = class extends Request {
	_cycle;
	constructor(request, data) {
		super(request);
		this.lastSyncId = data.lastSyncId;
		this.success = data.success;
		this._cycle = data.cycle ?? void 0;
	}
	/** The identifier of the last sync operation. */
	lastSyncId;
	/** Whether the operation was successful. */
	success;
	/** The Cycle that was created or updated. */
	get cycle() {
		return this._cycle?.id ? new CycleQuery(this._request).fetch(this._cycle?.id) : void 0;
	}
	/** The ID of cycle that was created or updated. */
	get cycleId() {
		return this._cycle?.id;
	}
};
/**
* Payload for a cycle webhook.
*
* @param data - L.CycleWebhookPayloadFragment response data
*/
var CycleWebhookPayload = class {
	constructor(data) {
		this.archivedAt = data.archivedAt ?? void 0;
		this.autoArchivedAt = data.autoArchivedAt ?? void 0;
		this.completedAt = data.completedAt ?? void 0;
		this.completedIssueCountHistory = data.completedIssueCountHistory;
		this.completedScopeHistory = data.completedScopeHistory;
		this.createdAt = data.createdAt;
		this.description = data.description ?? void 0;
		this.endsAt = data.endsAt;
		this.id = data.id;
		this.inProgressScopeHistory = data.inProgressScopeHistory;
		this.inheritedFromId = data.inheritedFromId ?? void 0;
		this.issueCountHistory = data.issueCountHistory;
		this.name = data.name ?? void 0;
		this.number = data.number;
		this.scopeHistory = data.scopeHistory;
		this.startsAt = data.startsAt;
		this.teamId = data.teamId;
		this.uncompletedIssuesUponCloseIds = data.uncompletedIssuesUponCloseIds;
		this.updatedAt = data.updatedAt;
	}
	/** The time at which the entity was archived. */
	archivedAt;
	/** The time at which the cycle was automatically archived by the auto pruning process. */
	autoArchivedAt;
	/** The completion time of the cycle. If null, the cycle hasn't been completed. */
	completedAt;
	/** The number of completed issues in the cycle after each day. */
	completedIssueCountHistory;
	/** The number of completed estimation points after each day. */
	completedScopeHistory;
	/** The time at which the entity was created. */
	createdAt;
	/** The cycle's description. */
	description;
	/** The end date of the cycle. */
	endsAt;
	/** The ID of the entity. */
	id;
	/** The number of in progress estimation points after each day. */
	inProgressScopeHistory;
	/** The ID of the cycle inherited from. */
	inheritedFromId;
	/** The total number of issues in the cycle after each day. */
	issueCountHistory;
	/** The name of the cycle. */
	name;
	/** The number of the cycle. */
	number;
	/** The total number of estimation points after each day. */
	scopeHistory;
	/** The start date of the cycle. */
	startsAt;
	/** The team ID of the cycle. */
	teamId;
	/** The IDs of the uncompleted issues upon close. */
	uncompletedIssuesUponCloseIds;
	/** The time at which the entity was updated. */
	updatedAt;
};
/**
* A generic payload return from entity deletion mutations.
*
* @param request - function to call the graphql client
* @param data - L.DeletePayloadFragment response data
*/
var DeletePayload = class extends Request {
	constructor(request, data) {
		super(request);
		this.entityId = data.entityId;
		this.lastSyncId = data.lastSyncId;
		this.success = data.success;
	}
	/** The identifier of the deleted entity. */
	entityId;
	/** The identifier of the last sync operation. */
	lastSyncId;
	/** Whether the operation was successful. */
	success;
};
/**
* A document that can be attached to different entities.
*
* @param request - function to call the graphql client
* @param data - L.DocumentFragment response data
*/
var Document = class extends Request {
	_creator;
	_initiative;
	_issue;
	_lastAppliedTemplate;
	_project;
	_updatedBy;
	constructor(request, data) {
		super(request);
		this.archivedAt = parseDate(data.archivedAt) ?? void 0;
		this.color = data.color ?? void 0;
		this.content = data.content ?? void 0;
		this.createdAt = parseDate(data.createdAt) ?? /* @__PURE__ */ new Date();
		this.documentContentId = data.documentContentId ?? void 0;
		this.hiddenAt = parseDate(data.hiddenAt) ?? void 0;
		this.icon = data.icon ?? void 0;
		this.id = data.id;
		this.slugId = data.slugId;
		this.sortOrder = data.sortOrder;
		this.title = data.title;
		this.trashed = data.trashed ?? void 0;
		this.updatedAt = parseDate(data.updatedAt) ?? /* @__PURE__ */ new Date();
		this.url = data.url;
		this._creator = data.creator ?? void 0;
		this._initiative = data.initiative ?? void 0;
		this._issue = data.issue ?? void 0;
		this._lastAppliedTemplate = data.lastAppliedTemplate ?? void 0;
		this._project = data.project ?? void 0;
		this._updatedBy = data.updatedBy ?? void 0;
	}
	/** The time at which the entity was archived. Null if the entity has not been archived. */
	archivedAt;
	/** The color of the icon. */
	color;
	/** The documents content in markdown format. */
	content;
	/** The time at which the entity was created. */
	createdAt;
	/** The ID of the document content associated with the document. */
	documentContentId;
	/** The time at which the document was hidden. Null if the entity has not been hidden. */
	hiddenAt;
	/** The icon of the document. */
	icon;
	/** The unique identifier of the entity. */
	id;
	/** The document's unique URL slug. */
	slugId;
	/** The order of the item in the resources list. */
	sortOrder;
	/** The document title. */
	title;
	/** A flag that indicates whether the document is in the trash bin. */
	trashed;
	/**
	* The last time at which the entity was meaningfully updated. This is the same as the creation time if the entity hasn't
	*     been updated after creation.
	*/
	updatedAt;
	/** The canonical url for the document. */
	url;
	/** The user who created the document. */
	get creator() {
		return this._creator?.id ? new UserQuery(this._request).fetch(this._creator?.id) : void 0;
	}
	/** The ID of user who created the document. */
	get creatorId() {
		return this._creator?.id;
	}
	/** The initiative that the document is associated with. */
	get initiative() {
		return this._initiative?.id ? new InitiativeQuery(this._request).fetch(this._initiative?.id) : void 0;
	}
	/** The ID of initiative that the document is associated with. */
	get initiativeId() {
		return this._initiative?.id;
	}
	/** The issue that the document is associated with. */
	get issue() {
		return this._issue?.id ? new IssueQuery(this._request).fetch(this._issue?.id) : void 0;
	}
	/** The ID of issue that the document is associated with. */
	get issueId() {
		return this._issue?.id;
	}
	/** The last template that was applied to this document. */
	get lastAppliedTemplate() {
		return this._lastAppliedTemplate?.id ? new TemplateQuery(this._request).fetch(this._lastAppliedTemplate?.id) : void 0;
	}
	/** The ID of last template that was applied to this document. */
	get lastAppliedTemplateId() {
		return this._lastAppliedTemplate?.id;
	}
	/** The project that the document is associated with. */
	get project() {
		return this._project?.id ? new ProjectQuery(this._request).fetch(this._project?.id) : void 0;
	}
	/** The ID of project that the document is associated with. */
	get projectId() {
		return this._project?.id;
	}
	/** The user who last updated the document. */
	get updatedBy() {
		return this._updatedBy?.id ? new UserQuery(this._request).fetch(this._updatedBy?.id) : void 0;
	}
	/** The ID of user who last updated the document. */
	get updatedById() {
		return this._updatedBy?.id;
	}
	/** Comments associated with the document. */
	comments(variables) {
		return new Document_CommentsQuery(this._request, this.id, variables).fetch(variables);
	}
	/** Creates a new document. */
	create(input) {
		return new CreateDocumentMutation(this._request).fetch(input);
	}
	/** Deletes (trashes) a document. */
	delete() {
		return new DeleteDocumentMutation(this._request).fetch(this.id);
	}
	/** Restores a document. */
	unarchive() {
		return new UnarchiveDocumentMutation(this._request).fetch(this.id);
	}
	/** Updates a document. */
	update(input) {
		return new UpdateDocumentMutation(this._request).fetch(this.id, input);
	}
};
/**
* A generic payload return from entity archive mutations.
*
* @param request - function to call the graphql client
* @param data - L.DocumentArchivePayloadFragment response data
*/
var DocumentArchivePayload = class extends Request {
	_entity;
	constructor(request, data) {
		super(request);
		this.lastSyncId = data.lastSyncId;
		this.success = data.success;
		this._entity = data.entity ?? void 0;
	}
	/** The identifier of the last sync operation. */
	lastSyncId;
	/** Whether the operation was successful. */
	success;
	/** The archived/unarchived entity. Null if entity was deleted. */
	get entity() {
		return this._entity?.id ? new DocumentQuery(this._request).fetch(this._entity?.id) : void 0;
	}
	/** The ID of archived/unarchived entity. null if entity was deleted. */
	get entityId() {
		return this._entity?.id;
	}
};
/**
* Certain properties of a document.
*
* @param data - L.DocumentChildWebhookPayloadFragment response data
*/
var DocumentChildWebhookPayload = class {
	constructor(data) {
		this.id = data.id;
		this.initiativeId = data.initiativeId ?? void 0;
		this.projectId = data.projectId ?? void 0;
		this.title = data.title;
		this.initiative = data.initiative ? new InitiativeChildWebhookPayload(data.initiative) : void 0;
		this.project = data.project ? new ProjectChildWebhookPayload(data.project) : void 0;
	}
	/** The ID of the document. */
	id;
	/** The ID of the initiative this document belongs to. */
	initiativeId;
	/** The ID of the project this document belongs to. */
	projectId;
	/** The title of the document. */
	title;
	/** The initiative this document belongs to. */
	initiative;
	/** The project this document belongs to. */
	project;
};
/**
* DocumentConnection model
*
* @param request - function to call the graphql client
* @param fetch - function to trigger a refetch of this DocumentConnection model
* @param data - DocumentConnection response data
*/
var DocumentConnection = class extends Connection {
	constructor(request, fetch$1, data) {
		super(request, fetch$1, data.nodes.map((node) => new Document(request, node)), new PageInfo(request, data.pageInfo));
	}
};
/**
* A document content for a project.
*
* @param request - function to call the graphql client
* @param data - L.DocumentContentFragment response data
*/
var DocumentContent = class extends Request {
	_document;
	_initiative;
	_issue;
	_project;
	_projectMilestone;
	constructor(request, data) {
		super(request);
		this.archivedAt = parseDate(data.archivedAt) ?? void 0;
		this.content = data.content ?? void 0;
		this.contentState = data.contentState ?? void 0;
		this.createdAt = parseDate(data.createdAt) ?? /* @__PURE__ */ new Date();
		this.id = data.id;
		this.restoredAt = parseDate(data.restoredAt) ?? void 0;
		this.updatedAt = parseDate(data.updatedAt) ?? /* @__PURE__ */ new Date();
		this.aiPromptRules = data.aiPromptRules ? new AiPromptRules(request, data.aiPromptRules) : void 0;
		this._document = data.document ?? void 0;
		this._initiative = data.initiative ?? void 0;
		this._issue = data.issue ?? void 0;
		this._project = data.project ?? void 0;
		this._projectMilestone = data.projectMilestone ?? void 0;
	}
	/** The time at which the entity was archived. Null if the entity has not been archived. */
	archivedAt;
	/** The document content in markdown format. */
	content;
	/** The document content state as a base64 encoded string. */
	contentState;
	/** The time at which the entity was created. */
	createdAt;
	/** The unique identifier of the entity. */
	id;
	/** The time at which the document content was restored from a previous version. */
	restoredAt;
	/**
	* The last time at which the entity was meaningfully updated. This is the same as the creation time if the entity hasn't
	*     been updated after creation.
	*/
	updatedAt;
	/** The AI prompt rules that the content is associated with. */
	aiPromptRules;
	/** The document that the content is associated with. */
	get document() {
		return this._document?.id ? new DocumentQuery(this._request).fetch(this._document?.id) : void 0;
	}
	/** The ID of document that the content is associated with. */
	get documentId() {
		return this._document?.id;
	}
	/** The initiative that the content is associated with. */
	get initiative() {
		return this._initiative?.id ? new InitiativeQuery(this._request).fetch(this._initiative?.id) : void 0;
	}
	/** The ID of initiative that the content is associated with. */
	get initiativeId() {
		return this._initiative?.id;
	}
	/** The issue that the content is associated with. */
	get issue() {
		return this._issue?.id ? new IssueQuery(this._request).fetch(this._issue?.id) : void 0;
	}
	/** The ID of issue that the content is associated with. */
	get issueId() {
		return this._issue?.id;
	}
	/** The project that the content is associated with. */
	get project() {
		return this._project?.id ? new ProjectQuery(this._request).fetch(this._project?.id) : void 0;
	}
	/** The ID of project that the content is associated with. */
	get projectId() {
		return this._project?.id;
	}
	/** The project milestone that the content is associated with. */
	get projectMilestone() {
		return this._projectMilestone?.id ? new ProjectMilestoneQuery(this._request).fetch(this._projectMilestone?.id) : void 0;
	}
	/** The ID of project milestone that the content is associated with. */
	get projectMilestoneId() {
		return this._projectMilestone?.id;
	}
};
/**
* Certain properties of a document content.
*
* @param data - L.DocumentContentChildWebhookPayloadFragment response data
*/
var DocumentContentChildWebhookPayload = class {
	constructor(data) {
		this.document = data.document ? new DocumentChildWebhookPayload(data.document) : void 0;
		this.project = data.project ? new ProjectChildWebhookPayload(data.project) : void 0;
	}
	/** The document this document content belongs to. */
	document;
	/** The project this document belongs to. */
	project;
};
/**
* DocumentContentHistoryPayload model
*
* @param request - function to call the graphql client
* @param data - L.DocumentContentHistoryPayloadFragment response data
*/
var DocumentContentHistoryPayload = class extends Request {
	constructor(request, data) {
		super(request);
		this.success = data.success;
		this.history = data.history.map((node) => new DocumentContentHistoryType(request, node));
	}
	/** Whether the operation was successful. */
	success;
	/** The document content history entries. */
	history;
};
/**
* DocumentContentHistoryType model
*
* @param request - function to call the graphql client
* @param data - L.DocumentContentHistoryTypeFragment response data
*/
var DocumentContentHistoryType = class extends Request {
	constructor(request, data) {
		super(request);
		this.actorIds = data.actorIds ?? void 0;
		this.contentDataSnapshotAt = parseDate(data.contentDataSnapshotAt) ?? /* @__PURE__ */ new Date();
		this.createdAt = parseDate(data.createdAt) ?? /* @__PURE__ */ new Date();
		this.id = data.id;
	}
	/** The ID of the author of the change. */
	actorIds;
	/** The date when the document content history snapshot was taken. This can be different than createdAt since the content is captured from its state at the previously known updatedAt timestamp in the case of an update. On document create, these timestamps can be the same. */
	contentDataSnapshotAt;
	/** The date when the document content history entry was created. */
	createdAt;
	/** The UUID of the document content history entry. */
	id;
};
/**
* A document related notification.
*
* @param request - function to call the graphql client
* @param data - L.DocumentNotificationFragment response data
*/
var DocumentNotification = class extends Request {
	_actor;
	_externalUserActor;
	_user;
	constructor(request, data) {
		super(request);
		this.archivedAt = parseDate(data.archivedAt) ?? void 0;
		this.commentId = data.commentId ?? void 0;
		this.createdAt = parseDate(data.createdAt) ?? /* @__PURE__ */ new Date();
		this.documentId = data.documentId;
		this.emailedAt = parseDate(data.emailedAt) ?? void 0;
		this.id = data.id;
		this.parentCommentId = data.parentCommentId ?? void 0;
		this.reactionEmoji = data.reactionEmoji ?? void 0;
		this.readAt = parseDate(data.readAt) ?? void 0;
		this.snoozedUntilAt = parseDate(data.snoozedUntilAt) ?? void 0;
		this.type = data.type;
		this.unsnoozedAt = parseDate(data.unsnoozedAt) ?? void 0;
		this.updatedAt = parseDate(data.updatedAt) ?? /* @__PURE__ */ new Date();
		this.botActor = data.botActor ? new ActorBot(request, data.botActor) : void 0;
		this.category = data.category;
		this._actor = data.actor ?? void 0;
		this._externalUserActor = data.externalUserActor ?? void 0;
		this._user = data.user;
	}
	/** The time at which the entity was archived. Null if the entity has not been archived. */
	archivedAt;
	/** Related comment ID. Null if the notification is not related to a comment. */
	commentId;
	/** The time at which the entity was created. */
	createdAt;
	/** Related document ID. */
	documentId;
	/**
	* The time at when an email reminder for this notification was sent to the user. Null, if no email
	*     reminder has been sent.
	*/
	emailedAt;
	/** The unique identifier of the entity. */
	id;
	/** Related parent comment ID. Null if the notification is not related to a comment. */
	parentCommentId;
	/** Name of the reaction emoji related to the notification. */
	reactionEmoji;
	/** The time at when the user marked the notification as read. Null, if the the user hasn't read the notification */
	readAt;
	/** The time until a notification will be snoozed. After that it will appear in the inbox again. */
	snoozedUntilAt;
	/** Notification type. */
	type;
	/** The time at which a notification was unsnoozed.. */
	unsnoozedAt;
	/**
	* The last time at which the entity was meaningfully updated. This is the same as the creation time if the entity hasn't
	*     been updated after creation.
	*/
	updatedAt;
	/** The bot that caused the notification. */
	botActor;
	/** The category of the notification. */
	category;
	/** The user that caused the notification. */
	get actor() {
		return this._actor?.id ? new UserQuery(this._request).fetch(this._actor?.id) : void 0;
	}
	/** The ID of user that caused the notification. */
	get actorId() {
		return this._actor?.id;
	}
	/** The external user that caused the notification. */
	get externalUserActor() {
		return this._externalUserActor?.id ? new ExternalUserQuery(this._request).fetch(this._externalUserActor?.id) : void 0;
	}
	/** The ID of external user that caused the notification. */
	get externalUserActorId() {
		return this._externalUserActor?.id;
	}
	/** The user that received the notification. */
	get user() {
		return new UserQuery(this._request).fetch(this._user.id);
	}
	/** The ID of user that received the notification. */
	get userId() {
		return this._user?.id;
	}
};
/**
* DocumentPayload model
*
* @param request - function to call the graphql client
* @param data - L.DocumentPayloadFragment response data
*/
var DocumentPayload = class extends Request {
	_document;
	constructor(request, data) {
		super(request);
		this.lastSyncId = data.lastSyncId;
		this.success = data.success;
		this._document = data.document;
	}
	/** The identifier of the last sync operation. */
	lastSyncId;
	/** Whether the operation was successful. */
	success;
	/** The document that was created or updated. */
	get document() {
		return new DocumentQuery(this._request).fetch(this._document.id);
	}
	/** The ID of document that was created or updated. */
	get documentId() {
		return this._document?.id;
	}
};
/**
* DocumentSearchPayload model
*
* @param request - function to call the graphql client
* @param data - L.DocumentSearchPayloadFragment response data
*/
var DocumentSearchPayload = class extends Request {
	constructor(request, data) {
		super(request);
		this.totalCount = data.totalCount;
		this.archivePayload = new ArchiveResponse(request, data.archivePayload);
		this.pageInfo = new PageInfo(request, data.pageInfo);
		this.nodes = data.nodes.map((node) => new DocumentSearchResult(request, node));
	}
	/** Total number of results for query without filters applied. */
	totalCount;
	nodes;
	/** Archived entities matching the search term along with all their dependencies. */
	archivePayload;
	pageInfo;
};
/**
* DocumentSearchResult model
*
* @param request - function to call the graphql client
* @param data - L.DocumentSearchResultFragment response data
*/
var DocumentSearchResult = class extends Request {
	_creator;
	_initiative;
	_issue;
	_lastAppliedTemplate;
	_project;
	_updatedBy;
	constructor(request, data) {
		super(request);
		this.archivedAt = parseDate(data.archivedAt) ?? void 0;
		this.color = data.color ?? void 0;
		this.content = data.content ?? void 0;
		this.createdAt = parseDate(data.createdAt) ?? /* @__PURE__ */ new Date();
		this.documentContentId = data.documentContentId ?? void 0;
		this.hiddenAt = parseDate(data.hiddenAt) ?? void 0;
		this.icon = data.icon ?? void 0;
		this.id = data.id;
		this.metadata = data.metadata;
		this.slugId = data.slugId;
		this.sortOrder = data.sortOrder;
		this.title = data.title;
		this.trashed = data.trashed ?? void 0;
		this.updatedAt = parseDate(data.updatedAt) ?? /* @__PURE__ */ new Date();
		this.url = data.url;
		this._creator = data.creator ?? void 0;
		this._initiative = data.initiative ?? void 0;
		this._issue = data.issue ?? void 0;
		this._lastAppliedTemplate = data.lastAppliedTemplate ?? void 0;
		this._project = data.project ?? void 0;
		this._updatedBy = data.updatedBy ?? void 0;
	}
	/** The time at which the entity was archived. Null if the entity has not been archived. */
	archivedAt;
	/** The color of the icon. */
	color;
	/** The documents content in markdown format. */
	content;
	/** The time at which the entity was created. */
	createdAt;
	/** The ID of the document content associated with the document. */
	documentContentId;
	/** The time at which the document was hidden. Null if the entity has not been hidden. */
	hiddenAt;
	/** The icon of the document. */
	icon;
	/** The unique identifier of the entity. */
	id;
	/** Metadata related to search result. */
	metadata;
	/** The document's unique URL slug. */
	slugId;
	/** The order of the item in the resources list. */
	sortOrder;
	/** The document title. */
	title;
	/** A flag that indicates whether the document is in the trash bin. */
	trashed;
	/**
	* The last time at which the entity was meaningfully updated. This is the same as the creation time if the entity hasn't
	*     been updated after creation.
	*/
	updatedAt;
	/** The canonical url for the document. */
	url;
	/** The user who created the document. */
	get creator() {
		return this._creator?.id ? new UserQuery(this._request).fetch(this._creator?.id) : void 0;
	}
	/** The ID of user who created the document. */
	get creatorId() {
		return this._creator?.id;
	}
	/** The initiative that the document is associated with. */
	get initiative() {
		return this._initiative?.id ? new InitiativeQuery(this._request).fetch(this._initiative?.id) : void 0;
	}
	/** The ID of initiative that the document is associated with. */
	get initiativeId() {
		return this._initiative?.id;
	}
	/** The issue that the document is associated with. */
	get issue() {
		return this._issue?.id ? new IssueQuery(this._request).fetch(this._issue?.id) : void 0;
	}
	/** The ID of issue that the document is associated with. */
	get issueId() {
		return this._issue?.id;
	}
	/** The last template that was applied to this document. */
	get lastAppliedTemplate() {
		return this._lastAppliedTemplate?.id ? new TemplateQuery(this._request).fetch(this._lastAppliedTemplate?.id) : void 0;
	}
	/** The ID of last template that was applied to this document. */
	get lastAppliedTemplateId() {
		return this._lastAppliedTemplate?.id;
	}
	/** The project that the document is associated with. */
	get project() {
		return this._project?.id ? new ProjectQuery(this._request).fetch(this._project?.id) : void 0;
	}
	/** The ID of project that the document is associated with. */
	get projectId() {
		return this._project?.id;
	}
	/** The user who last updated the document. */
	get updatedBy() {
		return this._updatedBy?.id ? new UserQuery(this._request).fetch(this._updatedBy?.id) : void 0;
	}
	/** The ID of user who last updated the document. */
	get updatedById() {
		return this._updatedBy?.id;
	}
};
/**
* Payload for a document webhook.
*
* @param data - L.DocumentWebhookPayloadFragment response data
*/
var DocumentWebhookPayload = class {
	constructor(data) {
		this.archivedAt = data.archivedAt ?? void 0;
		this.color = data.color ?? void 0;
		this.content = data.content ?? void 0;
		this.createdAt = data.createdAt;
		this.creatorId = data.creatorId ?? void 0;
		this.description = data.description ?? void 0;
		this.hiddenAt = data.hiddenAt ?? void 0;
		this.icon = data.icon ?? void 0;
		this.id = data.id;
		this.initiativeId = data.initiativeId ?? void 0;
		this.lastAppliedTemplateId = data.lastAppliedTemplateId ?? void 0;
		this.projectId = data.projectId ?? void 0;
		this.resourceFolderId = data.resourceFolderId ?? void 0;
		this.slugId = data.slugId;
		this.sortOrder = data.sortOrder;
		this.subscriberIds = data.subscriberIds ?? void 0;
		this.title = data.title;
		this.trashed = data.trashed ?? void 0;
		this.updatedAt = data.updatedAt;
		this.updatedById = data.updatedById ?? void 0;
	}
	/** The time at which the entity was archived. */
	archivedAt;
	/** The color of the document. */
	color;
	/** The content of the document. */
	content;
	/** The time at which the entity was created. */
	createdAt;
	/** The ID of the user who created the document. */
	creatorId;
	/** The description of the document. */
	description;
	/** The time at which the document was hidden. */
	hiddenAt;
	/** The icon of the document. */
	icon;
	/** The ID of the entity. */
	id;
	/** The ID of the initiative this document belongs to. */
	initiativeId;
	/** The ID of the last template that was applied to this document. */
	lastAppliedTemplateId;
	/** The ID of the project this document belongs to. */
	projectId;
	/** The ID of the resource folder this document belongs to. */
	resourceFolderId;
	/** The document's unique URL slug. */
	slugId;
	/** The order of the item in the resources list. */
	sortOrder;
	/** The IDs of the users who are subscribed to this document. */
	subscriberIds;
	/** The title of the document. */
	title;
	/** A flag that indicates whether the document is in the trash bin. */
	trashed;
	/** The time at which the entity was updated. */
	updatedAt;
	/** The ID of the user who last updated the document. */
	updatedById;
};
/**
* A general purpose draft. Used for comments, project updates, etc.
*
* @param request - function to call the graphql client
* @param data - L.DraftFragment response data
*/
var Draft = class extends Request {
	_customerNeed;
	_initiative;
	_initiativeUpdate;
	_issue;
	_parentComment;
	_project;
	_projectUpdate;
	_team;
	_user;
	constructor(request, data) {
		super(request);
		this.archivedAt = parseDate(data.archivedAt) ?? void 0;
		this.bodyData = parseJson(data.bodyData) ?? {};
		this.createdAt = parseDate(data.createdAt) ?? /* @__PURE__ */ new Date();
		this.data = data.data ?? void 0;
		this.id = data.id;
		this.isAutogenerated = data.isAutogenerated;
		this.updatedAt = parseDate(data.updatedAt) ?? /* @__PURE__ */ new Date();
		this._customerNeed = data.customerNeed ?? void 0;
		this._initiative = data.initiative ?? void 0;
		this._initiativeUpdate = data.initiativeUpdate ?? void 0;
		this._issue = data.issue ?? void 0;
		this._parentComment = data.parentComment ?? void 0;
		this._project = data.project ?? void 0;
		this._projectUpdate = data.projectUpdate ?? void 0;
		this._team = data.team ?? void 0;
		this._user = data.user;
	}
	/** The time at which the entity was archived. Null if the entity has not been archived. */
	archivedAt;
	/** The text content as a Prosemirror document. */
	bodyData;
	/** The time at which the entity was created. */
	createdAt;
	/** Additional properties for the draft. */
	data;
	/** The unique identifier of the entity. */
	id;
	/** Whether the draft was autogenerated for the user. */
	isAutogenerated;
	/**
	* The last time at which the entity was meaningfully updated. This is the same as the creation time if the entity hasn't
	*     been updated after creation.
	*/
	updatedAt;
	/** The customer need that this draft is referencing. */
	get customerNeed() {
		return this._customerNeed?.id ? new CustomerNeedQuery(this._request).fetch({ id: this._customerNeed?.id }) : void 0;
	}
	/** The ID of customer need that this draft is referencing. */
	get customerNeedId() {
		return this._customerNeed?.id;
	}
	/** The initiative for which this is a draft initiative update. */
	get initiative() {
		return this._initiative?.id ? new InitiativeQuery(this._request).fetch(this._initiative?.id) : void 0;
	}
	/** The ID of initiative for which this is a draft initiative update. */
	get initiativeId() {
		return this._initiative?.id;
	}
	/** The initiative update for which this is a draft comment. */
	get initiativeUpdate() {
		return this._initiativeUpdate?.id ? new InitiativeUpdateQuery(this._request).fetch(this._initiativeUpdate?.id) : void 0;
	}
	/** The ID of initiative update for which this is a draft comment. */
	get initiativeUpdateId() {
		return this._initiativeUpdate?.id;
	}
	/** The issue for which this is a draft comment. */
	get issue() {
		return this._issue?.id ? new IssueQuery(this._request).fetch(this._issue?.id) : void 0;
	}
	/** The ID of issue for which this is a draft comment. */
	get issueId() {
		return this._issue?.id;
	}
	/** The comment for which this is a draft comment reply. */
	get parentComment() {
		return this._parentComment?.id ? new CommentQuery(this._request).fetch({ id: this._parentComment?.id }) : void 0;
	}
	/** The ID of comment for which this is a draft comment reply. */
	get parentCommentId() {
		return this._parentComment?.id;
	}
	/** The project for which this is a draft project update. */
	get project() {
		return this._project?.id ? new ProjectQuery(this._request).fetch(this._project?.id) : void 0;
	}
	/** The ID of project for which this is a draft project update. */
	get projectId() {
		return this._project?.id;
	}
	/** The project update for which this is a draft comment. */
	get projectUpdate() {
		return this._projectUpdate?.id ? new ProjectUpdateQuery(this._request).fetch(this._projectUpdate?.id) : void 0;
	}
	/** The ID of project update for which this is a draft comment. */
	get projectUpdateId() {
		return this._projectUpdate?.id;
	}
	/** The team for which this is a draft post. */
	get team() {
		return this._team?.id ? new TeamQuery(this._request).fetch(this._team?.id) : void 0;
	}
	/** The ID of team for which this is a draft post. */
	get teamId() {
		return this._team?.id;
	}
	/** The user who created the draft. */
	get user() {
		return new UserQuery(this._request).fetch(this._user.id);
	}
	/** The ID of user who created the draft. */
	get userId() {
		return this._user?.id;
	}
};
/**
* DraftConnection model
*
* @param request - function to call the graphql client
* @param fetch - function to trigger a refetch of this DraftConnection model
* @param data - DraftConnection response data
*/
var DraftConnection = class extends Connection {
	constructor(request, fetch$1, data) {
		super(request, fetch$1, data.nodes.map((node) => new Draft(request, node)), new PageInfo(request, data.pageInfo));
	}
};
/**
* An email address that can be used for submitting issues.
*
* @param request - function to call the graphql client
* @param data - L.EmailIntakeAddressFragment response data
*/
var EmailIntakeAddress = class extends Request {
	_creator;
	_team;
	_template;
	constructor(request, data) {
		super(request);
		this.address = data.address;
		this.archivedAt = parseDate(data.archivedAt) ?? void 0;
		this.createdAt = parseDate(data.createdAt) ?? /* @__PURE__ */ new Date();
		this.customerRequestsEnabled = data.customerRequestsEnabled;
		this.enabled = data.enabled;
		this.forwardingEmailAddress = data.forwardingEmailAddress ?? void 0;
		this.id = data.id;
		this.issueCanceledAutoReply = data.issueCanceledAutoReply ?? void 0;
		this.issueCanceledAutoReplyEnabled = data.issueCanceledAutoReplyEnabled;
		this.issueCompletedAutoReply = data.issueCompletedAutoReply ?? void 0;
		this.issueCompletedAutoReplyEnabled = data.issueCompletedAutoReplyEnabled;
		this.issueCreatedAutoReply = data.issueCreatedAutoReply ?? void 0;
		this.issueCreatedAutoReplyEnabled = data.issueCreatedAutoReplyEnabled;
		this.repliesEnabled = data.repliesEnabled;
		this.senderName = data.senderName ?? void 0;
		this.updatedAt = parseDate(data.updatedAt) ?? /* @__PURE__ */ new Date();
		this.useUserNamesInReplies = data.useUserNamesInReplies;
		this.sesDomainIdentity = data.sesDomainIdentity ? new SesDomainIdentity(request, data.sesDomainIdentity) : void 0;
		this.type = data.type;
		this._creator = data.creator ?? void 0;
		this._team = data.team ?? void 0;
		this._template = data.template ?? void 0;
	}
	/** Unique email address user name (before @) used for incoming email. */
	address;
	/** The time at which the entity was archived. Null if the entity has not been archived. */
	archivedAt;
	/** The time at which the entity was created. */
	createdAt;
	/** Whether issues created from that email address will be turned into customer requests. */
	customerRequestsEnabled;
	/** Whether the email address is enabled. */
	enabled;
	/** The email address used to forward emails to the intake address. */
	forwardingEmailAddress;
	/** The unique identifier of the entity. */
	id;
	/** The auto-reply message for issue canceled. If not set, the default reply will be used. */
	issueCanceledAutoReply;
	/** Whether the auto-reply for issue canceled is enabled. */
	issueCanceledAutoReplyEnabled;
	/** The auto-reply message for issue completed. If not set, the default reply will be used. */
	issueCompletedAutoReply;
	/** Whether the auto-reply for issue completed is enabled. */
	issueCompletedAutoReplyEnabled;
	/** The auto-reply message for issue created. If not set, the default reply will be used. */
	issueCreatedAutoReply;
	/** Whether the auto-reply for issue created is enabled. */
	issueCreatedAutoReplyEnabled;
	/** Whether email replies are enabled. */
	repliesEnabled;
	/** The name to be used for outgoing emails. */
	senderName;
	/**
	* The last time at which the entity was meaningfully updated. This is the same as the creation time if the entity hasn't
	*     been updated after creation.
	*/
	updatedAt;
	/** Whether the commenter's name is included in the email replies. */
	useUserNamesInReplies;
	/** The SES domain identity that the email address is associated with. */
	sesDomainIdentity;
	/** The type of the email address. */
	type;
	/** The user who created the email intake address. */
	get creator() {
		return this._creator?.id ? new UserQuery(this._request).fetch(this._creator?.id) : void 0;
	}
	/** The ID of user who created the email intake address. */
	get creatorId() {
		return this._creator?.id;
	}
	/** The organization that the email address is associated with. */
	get organization() {
		return new OrganizationQuery(this._request).fetch();
	}
	/** The team that the email address is associated with. */
	get team() {
		return this._team?.id ? new TeamQuery(this._request).fetch(this._team?.id) : void 0;
	}
	/** The ID of team that the email address is associated with. */
	get teamId() {
		return this._team?.id;
	}
	/** The template that the email address is associated with. */
	get template() {
		return this._template?.id ? new TemplateQuery(this._request).fetch(this._template?.id) : void 0;
	}
	/** The ID of template that the email address is associated with. */
	get templateId() {
		return this._template?.id;
	}
	/** Creates a new email intake address. */
	create(input) {
		return new CreateEmailIntakeAddressMutation(this._request).fetch(input);
	}
	/** Deletes an email intake address object. */
	delete() {
		return new DeleteEmailIntakeAddressMutation(this._request).fetch(this.id);
	}
	/** Updates an existing email intake address. */
	update(input) {
		return new UpdateEmailIntakeAddressMutation(this._request).fetch(this.id, input);
	}
};
/**
* EmailIntakeAddressPayload model
*
* @param request - function to call the graphql client
* @param data - L.EmailIntakeAddressPayloadFragment response data
*/
var EmailIntakeAddressPayload = class extends Request {
	_emailIntakeAddress;
	constructor(request, data) {
		super(request);
		this.lastSyncId = data.lastSyncId;
		this.success = data.success;
		this._emailIntakeAddress = data.emailIntakeAddress;
	}
	/** The identifier of the last sync operation. */
	lastSyncId;
	/** Whether the operation was successful. */
	success;
	/** The email address that was created or updated. */
	get emailIntakeAddress() {
		return new EmailIntakeAddressQuery(this._request).fetch(this._emailIntakeAddress.id);
	}
	/** The ID of email address that was created or updated. */
	get emailIntakeAddressId() {
		return this._emailIntakeAddress?.id;
	}
};
/**
* EmailUnsubscribePayload model
*
* @param request - function to call the graphql client
* @param data - L.EmailUnsubscribePayloadFragment response data
*/
var EmailUnsubscribePayload = class extends Request {
	constructor(request, data) {
		super(request);
		this.success = data.success;
	}
	/** Whether the operation was successful. */
	success;
};
/**
* EmailUserAccountAuthChallengeResponse model
*
* @param request - function to call the graphql client
* @param data - L.EmailUserAccountAuthChallengeResponseFragment response data
*/
var EmailUserAccountAuthChallengeResponse = class extends Request {
	constructor(request, data) {
		super(request);
		this.authType = data.authType;
		this.success = data.success;
	}
	/** Supported challenge for this user account. Can be either verificationCode or password. */
	authType;
	/** Whether the operation was successful. */
	success;
};
/**
* A custom emoji.
*
* @param request - function to call the graphql client
* @param data - L.EmojiFragment response data
*/
var Emoji = class extends Request {
	_creator;
	constructor(request, data) {
		super(request);
		this.archivedAt = parseDate(data.archivedAt) ?? void 0;
		this.createdAt = parseDate(data.createdAt) ?? /* @__PURE__ */ new Date();
		this.id = data.id;
		this.name = data.name;
		this.source = data.source;
		this.updatedAt = parseDate(data.updatedAt) ?? /* @__PURE__ */ new Date();
		this.url = data.url;
		this._creator = data.creator ?? void 0;
	}
	/** The time at which the entity was archived. Null if the entity has not been archived. */
	archivedAt;
	/** The time at which the entity was created. */
	createdAt;
	/** The unique identifier of the entity. */
	id;
	/** The emoji's name. */
	name;
	/** The source of the emoji. */
	source;
	/**
	* The last time at which the entity was meaningfully updated. This is the same as the creation time if the entity hasn't
	*     been updated after creation.
	*/
	updatedAt;
	/** The emoji image URL. */
	url;
	/** The user who created the emoji. */
	get creator() {
		return this._creator?.id ? new UserQuery(this._request).fetch(this._creator?.id) : void 0;
	}
	/** The ID of user who created the emoji. */
	get creatorId() {
		return this._creator?.id;
	}
	/** The organization that the emoji belongs to. */
	get organization() {
		return new OrganizationQuery(this._request).fetch();
	}
	/** Creates a custom emoji. */
	create(input) {
		return new CreateEmojiMutation(this._request).fetch(input);
	}
	/** Deletes an emoji. */
	delete() {
		return new DeleteEmojiMutation(this._request).fetch(this.id);
	}
};
/**
* EmojiConnection model
*
* @param request - function to call the graphql client
* @param fetch - function to trigger a refetch of this EmojiConnection model
* @param data - EmojiConnection response data
*/
var EmojiConnection = class extends Connection {
	constructor(request, fetch$1, data) {
		super(request, fetch$1, data.nodes.map((node) => new Emoji(request, node)), new PageInfo(request, data.pageInfo));
	}
};
/**
* EmojiPayload model
*
* @param request - function to call the graphql client
* @param data - L.EmojiPayloadFragment response data
*/
var EmojiPayload = class extends Request {
	_emoji;
	constructor(request, data) {
		super(request);
		this.lastSyncId = data.lastSyncId;
		this.success = data.success;
		this._emoji = data.emoji;
	}
	/** The identifier of the last sync operation. */
	lastSyncId;
	/** Whether the operation was successful. */
	success;
	/** The emoji that was created. */
	get emoji() {
		return new EmojiQuery(this._request).fetch(this._emoji.id);
	}
	/** The ID of emoji that was created. */
	get emojiId() {
		return this._emoji?.id;
	}
};
/**
* A basic entity.
*
* @param request - function to call the graphql client
* @param data - L.EntityFragment response data
*/
var Entity = class extends Request {
	constructor(request, data) {
		super(request);
		this.archivedAt = parseDate(data.archivedAt) ?? void 0;
		this.createdAt = parseDate(data.createdAt) ?? /* @__PURE__ */ new Date();
		this.id = data.id;
		this.updatedAt = parseDate(data.updatedAt) ?? /* @__PURE__ */ new Date();
	}
	/** The time at which the entity was archived. Null if the entity has not been archived. */
	archivedAt;
	/** The time at which the entity was created. */
	createdAt;
	/** The unique identifier of the entity. */
	id;
	/**
	* The last time at which the entity was meaningfully updated. This is the same as the creation time if the entity hasn't
	*     been updated after creation.
	*/
	updatedAt;
};
/**
* An external link for an entity like initiative, etc...
*
* @param request - function to call the graphql client
* @param data - L.EntityExternalLinkFragment response data
*/
var EntityExternalLink = class extends Request {
	_creator;
	_initiative;
	constructor(request, data) {
		super(request);
		this.archivedAt = parseDate(data.archivedAt) ?? void 0;
		this.createdAt = parseDate(data.createdAt) ?? /* @__PURE__ */ new Date();
		this.id = data.id;
		this.label = data.label;
		this.sortOrder = data.sortOrder;
		this.updatedAt = parseDate(data.updatedAt) ?? /* @__PURE__ */ new Date();
		this.url = data.url;
		this._creator = data.creator;
		this._initiative = data.initiative ?? void 0;
	}
	/** The time at which the entity was archived. Null if the entity has not been archived. */
	archivedAt;
	/** The time at which the entity was created. */
	createdAt;
	/** The unique identifier of the entity. */
	id;
	/** The link's label. */
	label;
	/** The order of the item in the resources list. */
	sortOrder;
	/**
	* The last time at which the entity was meaningfully updated. This is the same as the creation time if the entity hasn't
	*     been updated after creation.
	*/
	updatedAt;
	/** The link's URL. */
	url;
	/** The user who created the link. */
	get creator() {
		return new UserQuery(this._request).fetch(this._creator.id);
	}
	/** The ID of user who created the link. */
	get creatorId() {
		return this._creator?.id;
	}
	/** The initiative that the link is associated with. */
	get initiative() {
		return this._initiative?.id ? new InitiativeQuery(this._request).fetch(this._initiative?.id) : void 0;
	}
	/** The ID of initiative that the link is associated with. */
	get initiativeId() {
		return this._initiative?.id;
	}
	/** Creates a new entity link. */
	create(input) {
		return new CreateEntityExternalLinkMutation(this._request).fetch(input);
	}
	/** Deletes an entity link. */
	delete() {
		return new DeleteEntityExternalLinkMutation(this._request).fetch(this.id);
	}
	/** Updates an entity link. */
	update(input) {
		return new UpdateEntityExternalLinkMutation(this._request).fetch(this.id, input);
	}
};
/**
* EntityExternalLinkConnection model
*
* @param request - function to call the graphql client
* @param fetch - function to trigger a refetch of this EntityExternalLinkConnection model
* @param data - EntityExternalLinkConnection response data
*/
var EntityExternalLinkConnection = class extends Connection {
	constructor(request, fetch$1, data) {
		super(request, fetch$1, data.nodes.map((node) => new EntityExternalLink(request, node)), new PageInfo(request, data.pageInfo));
	}
};
/**
* EntityExternalLinkPayload model
*
* @param request - function to call the graphql client
* @param data - L.EntityExternalLinkPayloadFragment response data
*/
var EntityExternalLinkPayload = class extends Request {
	_entityExternalLink;
	constructor(request, data) {
		super(request);
		this.lastSyncId = data.lastSyncId;
		this.success = data.success;
		this._entityExternalLink = data.entityExternalLink;
	}
	/** The identifier of the last sync operation. */
	lastSyncId;
	/** Whether the operation was successful. */
	success;
	/** The link that was created or updated. */
	get entityExternalLink() {
		return new EntityExternalLinkQuery(this._request).fetch(this._entityExternalLink.id);
	}
	/** The ID of link that was created or updated. */
	get entityExternalLinkId() {
		return this._entityExternalLink?.id;
	}
};
/**
* Payload for entity-related webhook events.
*
* @param data - L.EntityWebhookPayloadFragment response data
*/
var EntityWebhookPayload = class {
	constructor(data) {
		this.action = data.action;
		this.createdAt = parseDate(data.createdAt) ?? /* @__PURE__ */ new Date();
		this.organizationId = data.organizationId;
		this.type = data.type;
		this.updatedFrom = data.updatedFrom ?? void 0;
		this.url = data.url ?? void 0;
		this.webhookId = data.webhookId;
		this.webhookTimestamp = data.webhookTimestamp;
	}
	/** The type of action that triggered the webhook. */
	action;
	/** The time the payload was created. */
	createdAt;
	/** ID of the organization for which the webhook belongs to. */
	organizationId;
	/** The type of resource, i.e., the name of the entity. */
	type;
	/** In case of an update event, previous values of all updated properties. */
	updatedFrom;
	/** URL for the entity. */
	url;
	/** The ID of the webhook that sent this event. */
	webhookId;
	/** Unix timestamp in milliseconds when the webhook was sent. */
	webhookTimestamp;
};
/**
* Information about an external entity.
*
* @param request - function to call the graphql client
* @param data - L.ExternalEntityInfoFragment response data
*/
var ExternalEntityInfo = class extends Request {
	constructor(request, data) {
		super(request);
		this.id = data.id;
		this.service = data.service;
		this.metadata = data.metadata ?? void 0;
	}
	/** The id of the external entity. */
	id;
	/** The name of the service this entity is synced with. */
	service;
	/** Metadata about the external entity. */
	metadata;
};
/**
* Metadata about the external GitHub entity.
*
* @param request - function to call the graphql client
* @param data - L.ExternalEntityInfoGithubMetadataFragment response data
*/
var ExternalEntityInfoGithubMetadata = class extends Request {
	constructor(request, data) {
		super(request);
		this.number = data.number ?? void 0;
		this.owner = data.owner ?? void 0;
		this.repo = data.repo ?? void 0;
	}
	/** The number of the issue. */
	number;
	/** The owner of the repository. */
	owner;
	/** The repository name. */
	repo;
};
/**
* Metadata about the external Jira entity.
*
* @param request - function to call the graphql client
* @param data - L.ExternalEntityInfoJiraMetadataFragment response data
*/
var ExternalEntityInfoJiraMetadata = class extends Request {
	constructor(request, data) {
		super(request);
		this.issueKey = data.issueKey ?? void 0;
		this.issueTypeId = data.issueTypeId ?? void 0;
		this.projectId = data.projectId ?? void 0;
	}
	/** The key of the Jira issue. */
	issueKey;
	/** The id of the Jira issue type. */
	issueTypeId;
	/** The id of the Jira project. */
	projectId;
};
/**
* Metadata about the external Slack entity.
*
* @param request - function to call the graphql client
* @param data - L.ExternalEntitySlackMetadataFragment response data
*/
var ExternalEntitySlackMetadata = class extends Request {
	constructor(request, data) {
		super(request);
		this.channelId = data.channelId ?? void 0;
		this.channelName = data.channelName ?? void 0;
		this.isFromSlack = data.isFromSlack;
		this.messageUrl = data.messageUrl ?? void 0;
	}
	/** The id of the Slack channel. */
	channelId;
	/** The name of the Slack channel. */
	channelName;
	/** Whether the entity originated from Slack (not Linear). */
	isFromSlack;
	/** The URL of the Slack message. */
	messageUrl;
};
/**
* An external authenticated (e.g., through Slack) user which doesn't have a Linear account, but can create and update entities in Linear from the external system that authenticated them.
*
* @param request - function to call the graphql client
* @param data - L.ExternalUserFragment response data
*/
var ExternalUser = class extends Request {
	constructor(request, data) {
		super(request);
		this.archivedAt = parseDate(data.archivedAt) ?? void 0;
		this.avatarUrl = data.avatarUrl ?? void 0;
		this.createdAt = parseDate(data.createdAt) ?? /* @__PURE__ */ new Date();
		this.displayName = data.displayName;
		this.email = data.email ?? void 0;
		this.id = data.id;
		this.lastSeen = parseDate(data.lastSeen) ?? void 0;
		this.name = data.name;
		this.updatedAt = parseDate(data.updatedAt) ?? /* @__PURE__ */ new Date();
	}
	/** The time at which the entity was archived. Null if the entity has not been archived. */
	archivedAt;
	/** An URL to the external user's avatar image. */
	avatarUrl;
	/** The time at which the entity was created. */
	createdAt;
	/** The external user's display name. Unique within each organization. Can match the display name of an actual user. */
	displayName;
	/** The external user's email address. */
	email;
	/** The unique identifier of the entity. */
	id;
	/** The last time the external user was seen interacting with Linear. */
	lastSeen;
	/** The external user's full name. */
	name;
	/**
	* The last time at which the entity was meaningfully updated. This is the same as the creation time if the entity hasn't
	*     been updated after creation.
	*/
	updatedAt;
	/** Organization the external user belongs to. */
	get organization() {
		return new OrganizationQuery(this._request).fetch();
	}
};
/**
* Certain properties of an external user.
*
* @param data - L.ExternalUserChildWebhookPayloadFragment response data
*/
var ExternalUserChildWebhookPayload = class {
	constructor(data) {
		this.email = data.email;
		this.id = data.id;
		this.name = data.name;
	}
	/** The email of the external user. */
	email;
	/** The ID of the external user. */
	id;
	/** The name of the external user. */
	name;
};
/**
* ExternalUserConnection model
*
* @param request - function to call the graphql client
* @param fetch - function to trigger a refetch of this ExternalUserConnection model
* @param data - ExternalUserConnection response data
*/
var ExternalUserConnection = class extends Connection {
	constructor(request, fetch$1, data) {
		super(request, fetch$1, data.nodes.map((node) => new ExternalUser(request, node)), new PageInfo(request, data.pageInfo));
	}
};
/**
* A facet. Facets are joins between entities. A facet can tie a custom view to a project, or a a project to a roadmap for example.
*
* @param request - function to call the graphql client
* @param data - L.FacetFragment response data
*/
var Facet = class extends Request {
	_sourceFeedUser;
	_sourceInitiative;
	_sourceProject;
	_sourceTeam;
	_targetCustomView;
	constructor(request, data) {
		super(request);
		this.archivedAt = parseDate(data.archivedAt) ?? void 0;
		this.createdAt = parseDate(data.createdAt) ?? /* @__PURE__ */ new Date();
		this.id = data.id;
		this.sortOrder = data.sortOrder;
		this.updatedAt = parseDate(data.updatedAt) ?? /* @__PURE__ */ new Date();
		this.sourcePage = data.sourcePage ?? void 0;
		this._sourceFeedUser = data.sourceFeedUser ?? void 0;
		this._sourceInitiative = data.sourceInitiative ?? void 0;
		this._sourceProject = data.sourceProject ?? void 0;
		this._sourceTeam = data.sourceTeam ?? void 0;
		this._targetCustomView = data.targetCustomView ?? void 0;
	}
	/** The time at which the entity was archived. Null if the entity has not been archived. */
	archivedAt;
	/** The time at which the entity was created. */
	createdAt;
	/** The unique identifier of the entity. */
	id;
	/** The sort order of the facet. */
	sortOrder;
	/**
	* The last time at which the entity was meaningfully updated. This is the same as the creation time if the entity hasn't
	*     been updated after creation.
	*/
	updatedAt;
	/** The owning page. */
	sourcePage;
	/** The owning feed user. */
	get sourceFeedUser() {
		return this._sourceFeedUser?.id ? new UserQuery(this._request).fetch(this._sourceFeedUser?.id) : void 0;
	}
	/** The ID of owning feed user. */
	get sourceFeedUserId() {
		return this._sourceFeedUser?.id;
	}
	/** The owning initiative. */
	get sourceInitiative() {
		return this._sourceInitiative?.id ? new InitiativeQuery(this._request).fetch(this._sourceInitiative?.id) : void 0;
	}
	/** The ID of owning initiative. */
	get sourceInitiativeId() {
		return this._sourceInitiative?.id;
	}
	/** The owning organization. */
	get sourceOrganization() {
		return new OrganizationQuery(this._request).fetch();
	}
	/** The owning project. */
	get sourceProject() {
		return this._sourceProject?.id ? new ProjectQuery(this._request).fetch(this._sourceProject?.id) : void 0;
	}
	/** The ID of owning project. */
	get sourceProjectId() {
		return this._sourceProject?.id;
	}
	/** The owning team. */
	get sourceTeam() {
		return this._sourceTeam?.id ? new TeamQuery(this._request).fetch(this._sourceTeam?.id) : void 0;
	}
	/** The ID of owning team. */
	get sourceTeamId() {
		return this._sourceTeam?.id;
	}
	/** The targeted custom view. */
	get targetCustomView() {
		return this._targetCustomView?.id ? new CustomViewQuery(this._request).fetch(this._targetCustomView?.id) : void 0;
	}
	/** The ID of targeted custom view. */
	get targetCustomViewId() {
		return this._targetCustomView?.id;
	}
};
/**
* FacetConnection model
*
* @param request - function to call the graphql client
* @param fetch - function to trigger a refetch of this FacetConnection model
* @param data - FacetConnection response data
*/
var FacetConnection = class extends Connection {
	constructor(request, fetch$1, data) {
		super(request, fetch$1, data.nodes.map((node) => new Facet(request, node)), new PageInfo(request, data.pageInfo));
	}
};
/**
* User favorites presented in the sidebar.
*
* @param request - function to call the graphql client
* @param data - L.FavoriteFragment response data
*/
var Favorite = class extends Request {
	_customView;
	_customer;
	_cycle;
	_document;
	_initiative;
	_issue;
	_label;
	_owner;
	_parent;
	_predefinedViewTeam;
	_project;
	_projectLabel;
	_projectTeam;
	_user;
	constructor(request, data) {
		super(request);
		this.archivedAt = parseDate(data.archivedAt) ?? void 0;
		this.createdAt = parseDate(data.createdAt) ?? /* @__PURE__ */ new Date();
		this.folderName = data.folderName ?? void 0;
		this.id = data.id;
		this.predefinedViewType = data.predefinedViewType ?? void 0;
		this.sortOrder = data.sortOrder;
		this.type = data.type;
		this.updatedAt = parseDate(data.updatedAt) ?? /* @__PURE__ */ new Date();
		this.url = data.url ?? void 0;
		this.initiativeTab = data.initiativeTab ?? void 0;
		this.projectTab = data.projectTab ?? void 0;
		this._customView = data.customView ?? void 0;
		this._customer = data.customer ?? void 0;
		this._cycle = data.cycle ?? void 0;
		this._document = data.document ?? void 0;
		this._initiative = data.initiative ?? void 0;
		this._issue = data.issue ?? void 0;
		this._label = data.label ?? void 0;
		this._owner = data.owner;
		this._parent = data.parent ?? void 0;
		this._predefinedViewTeam = data.predefinedViewTeam ?? void 0;
		this._project = data.project ?? void 0;
		this._projectLabel = data.projectLabel ?? void 0;
		this._projectTeam = data.projectTeam ?? void 0;
		this._user = data.user ?? void 0;
	}
	/** The time at which the entity was archived. Null if the entity has not been archived. */
	archivedAt;
	/** The time at which the entity was created. */
	createdAt;
	/** The name of the folder. Only applies to favorites of type folder. */
	folderName;
	/** The unique identifier of the entity. */
	id;
	/** The type of favorited predefined view. */
	predefinedViewType;
	/** The order of the item in the favorites list. */
	sortOrder;
	/** The type of the favorite. */
	type;
	/**
	* The last time at which the entity was meaningfully updated. This is the same as the creation time if the entity hasn't
	*     been updated after creation.
	*/
	updatedAt;
	/** URL of the favorited entity. Folders return 'null' value. */
	url;
	/** The targeted tab of the initiative. */
	initiativeTab;
	/** The targeted tab of the project. */
	projectTab;
	/** The favorited custom view. */
	get customView() {
		return this._customView?.id ? new CustomViewQuery(this._request).fetch(this._customView?.id) : void 0;
	}
	/** The ID of favorited custom view. */
	get customViewId() {
		return this._customView?.id;
	}
	/** The favorited customer. */
	get customer() {
		return this._customer?.id ? new CustomerQuery(this._request).fetch(this._customer?.id) : void 0;
	}
	/** The ID of favorited customer. */
	get customerId() {
		return this._customer?.id;
	}
	/** The favorited cycle. */
	get cycle() {
		return this._cycle?.id ? new CycleQuery(this._request).fetch(this._cycle?.id) : void 0;
	}
	/** The ID of favorited cycle. */
	get cycleId() {
		return this._cycle?.id;
	}
	/** The favorited document. */
	get document() {
		return this._document?.id ? new DocumentQuery(this._request).fetch(this._document?.id) : void 0;
	}
	/** The ID of favorited document. */
	get documentId() {
		return this._document?.id;
	}
	/** The favorited initiative. */
	get initiative() {
		return this._initiative?.id ? new InitiativeQuery(this._request).fetch(this._initiative?.id) : void 0;
	}
	/** The ID of favorited initiative. */
	get initiativeId() {
		return this._initiative?.id;
	}
	/** The favorited issue. */
	get issue() {
		return this._issue?.id ? new IssueQuery(this._request).fetch(this._issue?.id) : void 0;
	}
	/** The ID of favorited issue. */
	get issueId() {
		return this._issue?.id;
	}
	/** The favorited label. */
	get label() {
		return this._label?.id ? new IssueLabelQuery(this._request).fetch(this._label?.id) : void 0;
	}
	/** The ID of favorited label. */
	get labelId() {
		return this._label?.id;
	}
	/** The owner of the favorite. */
	get owner() {
		return new UserQuery(this._request).fetch(this._owner.id);
	}
	/** The ID of owner of the favorite. */
	get ownerId() {
		return this._owner?.id;
	}
	/** The parent folder of the favorite. */
	get parent() {
		return this._parent?.id ? new FavoriteQuery(this._request).fetch(this._parent?.id) : void 0;
	}
	/** The ID of parent folder of the favorite. */
	get parentId() {
		return this._parent?.id;
	}
	/** The team of the favorited predefined view. */
	get predefinedViewTeam() {
		return this._predefinedViewTeam?.id ? new TeamQuery(this._request).fetch(this._predefinedViewTeam?.id) : void 0;
	}
	/** The ID of team of the favorited predefined view. */
	get predefinedViewTeamId() {
		return this._predefinedViewTeam?.id;
	}
	/** The favorited project. */
	get project() {
		return this._project?.id ? new ProjectQuery(this._request).fetch(this._project?.id) : void 0;
	}
	/** The ID of favorited project. */
	get projectId() {
		return this._project?.id;
	}
	/** The favorited project label. */
	get projectLabel() {
		return this._projectLabel?.id ? new ProjectLabelQuery(this._request).fetch(this._projectLabel?.id) : void 0;
	}
	/** The ID of favorited project label. */
	get projectLabelId() {
		return this._projectLabel?.id;
	}
	/** [DEPRECATED] The favorited team of the project. */
	get projectTeam() {
		return this._projectTeam?.id ? new TeamQuery(this._request).fetch(this._projectTeam?.id) : void 0;
	}
	/** The ID of [deprecated] the favorited team of the project. */
	get projectTeamId() {
		return this._projectTeam?.id;
	}
	/** The favorited user. */
	get user() {
		return this._user?.id ? new UserQuery(this._request).fetch(this._user?.id) : void 0;
	}
	/** The ID of favorited user. */
	get userId() {
		return this._user?.id;
	}
	/** Children of the favorite. Only applies to favorites of type folder. */
	children(variables) {
		return new Favorite_ChildrenQuery(this._request, this.id, variables).fetch(variables);
	}
	/** Creates a new favorite (project, cycle etc). */
	create(input) {
		return new CreateFavoriteMutation(this._request).fetch(input);
	}
	/** Deletes a favorite reference. */
	delete() {
		return new DeleteFavoriteMutation(this._request).fetch(this.id);
	}
	/** Updates a favorite. */
	update(input) {
		return new UpdateFavoriteMutation(this._request).fetch(this.id, input);
	}
};
/**
* FavoriteConnection model
*
* @param request - function to call the graphql client
* @param fetch - function to trigger a refetch of this FavoriteConnection model
* @param data - FavoriteConnection response data
*/
var FavoriteConnection = class extends Connection {
	constructor(request, fetch$1, data) {
		super(request, fetch$1, data.nodes.map((node) => new Favorite(request, node)), new PageInfo(request, data.pageInfo));
	}
};
/**
* FavoritePayload model
*
* @param request - function to call the graphql client
* @param data - L.FavoritePayloadFragment response data
*/
var FavoritePayload = class extends Request {
	_favorite;
	constructor(request, data) {
		super(request);
		this.lastSyncId = data.lastSyncId;
		this.success = data.success;
		this._favorite = data.favorite;
	}
	/** The identifier of the last sync operation. */
	lastSyncId;
	/** Whether the operation was successful. */
	success;
	/** The object that was added as a favorite. */
	get favorite() {
		return new FavoriteQuery(this._request).fetch(this._favorite.id);
	}
	/** The ID of object that was added as a favorite. */
	get favoriteId() {
		return this._favorite?.id;
	}
};
/**
* FetchDataPayload model
*
* @param request - function to call the graphql client
* @param data - L.FetchDataPayloadFragment response data
*/
var FetchDataPayload = class extends Request {
	constructor(request, data) {
		super(request);
		this.data = data.data ?? void 0;
		this.filters = data.filters ?? void 0;
		this.query = data.query ?? void 0;
		this.success = data.success;
	}
	/** The fetched data based on the natural language query. */
	data;
	/** The filters used to fetch the data. */
	filters;
	/** The GraphQL query used to fetch the data. */
	query;
	/** Whether the fetch operation was successful. */
	success;
};
/**
* FileUploadDeletePayload model
*
* @param request - function to call the graphql client
* @param data - L.FileUploadDeletePayloadFragment response data
*/
var FileUploadDeletePayload = class extends Request {
	constructor(request, data) {
		super(request);
		this.success = data.success;
	}
	/** Whether the operation was successful. */
	success;
};
/**
* FrontAttachmentPayload model
*
* @param request - function to call the graphql client
* @param data - L.FrontAttachmentPayloadFragment response data
*/
var FrontAttachmentPayload = class extends Request {
	_attachment;
	constructor(request, data) {
		super(request);
		this.lastSyncId = data.lastSyncId;
		this.success = data.success;
		this._attachment = data.attachment;
	}
	/** The identifier of the last sync operation. */
	lastSyncId;
	/** Whether the operation was successful. */
	success;
	/** The issue attachment that was created. */
	get attachment() {
		return new AttachmentQuery(this._request).fetch(this._attachment.id);
	}
	/** The ID of issue attachment that was created. */
	get attachmentId() {
		return this._attachment?.id;
	}
};
/**
* A trigger that updates the issue status according to Git automations.
*
* @param request - function to call the graphql client
* @param data - L.GitAutomationStateFragment response data
*/
var GitAutomationState = class extends Request {
	_state;
	_team;
	constructor(request, data) {
		super(request);
		this.archivedAt = parseDate(data.archivedAt) ?? void 0;
		this.branchPattern = data.branchPattern ?? void 0;
		this.createdAt = parseDate(data.createdAt) ?? /* @__PURE__ */ new Date();
		this.id = data.id;
		this.updatedAt = parseDate(data.updatedAt) ?? /* @__PURE__ */ new Date();
		this.targetBranch = data.targetBranch ? new GitAutomationTargetBranch(request, data.targetBranch) : void 0;
		this.event = data.event;
		this._state = data.state ?? void 0;
		this._team = data.team;
	}
	/** The time at which the entity was archived. Null if the entity has not been archived. */
	archivedAt;
	/** [DEPRECATED] The target branch, if null, the automation will be triggered on any branch. */
	branchPattern;
	/** The time at which the entity was created. */
	createdAt;
	/** The unique identifier of the entity. */
	id;
	/**
	* The last time at which the entity was meaningfully updated. This is the same as the creation time if the entity hasn't
	*     been updated after creation.
	*/
	updatedAt;
	/** The target branch associated to this automation state. */
	targetBranch;
	/** The event that triggers the automation. */
	event;
	/** The associated workflow state. */
	get state() {
		return this._state?.id ? new WorkflowStateQuery(this._request).fetch(this._state?.id) : void 0;
	}
	/** The ID of associated workflow state. */
	get stateId() {
		return this._state?.id;
	}
	/** The team to which this automation state belongs. */
	get team() {
		return new TeamQuery(this._request).fetch(this._team.id);
	}
	/** The ID of team to which this automation state belongs. */
	get teamId() {
		return this._team?.id;
	}
	/** Creates a new automation state. */
	create(input) {
		return new CreateGitAutomationStateMutation(this._request).fetch(input);
	}
	/** Archives an automation state. */
	delete() {
		return new DeleteGitAutomationStateMutation(this._request).fetch(this.id);
	}
	/** Updates an existing state. */
	update(input) {
		return new UpdateGitAutomationStateMutation(this._request).fetch(this.id, input);
	}
};
/**
* GitAutomationStateConnection model
*
* @param request - function to call the graphql client
* @param fetch - function to trigger a refetch of this GitAutomationStateConnection model
* @param data - GitAutomationStateConnection response data
*/
var GitAutomationStateConnection = class extends Connection {
	constructor(request, fetch$1, data) {
		super(request, fetch$1, data.nodes.map((node) => new GitAutomationState(request, node)), new PageInfo(request, data.pageInfo));
	}
};
/**
* GitAutomationStatePayload model
*
* @param request - function to call the graphql client
* @param data - L.GitAutomationStatePayloadFragment response data
*/
var GitAutomationStatePayload = class extends Request {
	constructor(request, data) {
		super(request);
		this.lastSyncId = data.lastSyncId;
		this.success = data.success;
		this.gitAutomationState = new GitAutomationState(request, data.gitAutomationState);
	}
	/** The identifier of the last sync operation. */
	lastSyncId;
	/** Whether the operation was successful. */
	success;
	/** The automation state that was created or updated. */
	gitAutomationState;
};
/**
* A Git target branch for which there are automations (GitAutomationState).
*
* @param request - function to call the graphql client
* @param data - L.GitAutomationTargetBranchFragment response data
*/
var GitAutomationTargetBranch = class extends Request {
	_team;
	constructor(request, data) {
		super(request);
		this.archivedAt = parseDate(data.archivedAt) ?? void 0;
		this.branchPattern = data.branchPattern;
		this.createdAt = parseDate(data.createdAt) ?? /* @__PURE__ */ new Date();
		this.id = data.id;
		this.isRegex = data.isRegex;
		this.updatedAt = parseDate(data.updatedAt) ?? /* @__PURE__ */ new Date();
		this._team = data.team;
	}
	/** The time at which the entity was archived. Null if the entity has not been archived. */
	archivedAt;
	/** The target branch pattern. */
	branchPattern;
	/** The time at which the entity was created. */
	createdAt;
	/** The unique identifier of the entity. */
	id;
	/** Whether the branch pattern is a regular expression. */
	isRegex;
	/**
	* The last time at which the entity was meaningfully updated. This is the same as the creation time if the entity hasn't
	*     been updated after creation.
	*/
	updatedAt;
	/** The team to which this Git target branch automation belongs. */
	get team() {
		return new TeamQuery(this._request).fetch(this._team.id);
	}
	/** The ID of team to which this git target branch automation belongs. */
	get teamId() {
		return this._team?.id;
	}
	/** Creates a Git target branch automation. */
	create(input) {
		return new CreateGitAutomationTargetBranchMutation(this._request).fetch(input);
	}
	/** Archives a Git target branch automation. */
	delete() {
		return new DeleteGitAutomationTargetBranchMutation(this._request).fetch(this.id);
	}
	/** Updates an existing Git target branch automation. */
	update(input) {
		return new UpdateGitAutomationTargetBranchMutation(this._request).fetch(this.id, input);
	}
};
/**
* GitAutomationTargetBranchPayload model
*
* @param request - function to call the graphql client
* @param data - L.GitAutomationTargetBranchPayloadFragment response data
*/
var GitAutomationTargetBranchPayload = class extends Request {
	constructor(request, data) {
		super(request);
		this.lastSyncId = data.lastSyncId;
		this.success = data.success;
		this.targetBranch = new GitAutomationTargetBranch(request, data.targetBranch);
	}
	/** The identifier of the last sync operation. */
	lastSyncId;
	/** Whether the operation was successful. */
	success;
	/** The Git target branch automation that was created or updated. */
	targetBranch;
};
/**
* GitHubCommitIntegrationPayload model
*
* @param request - function to call the graphql client
* @param data - L.GitHubCommitIntegrationPayloadFragment response data
*/
var GitHubCommitIntegrationPayload = class extends Request {
	_integration;
	constructor(request, data) {
		super(request);
		this.lastSyncId = data.lastSyncId;
		this.success = data.success;
		this.webhookSecret = data.webhookSecret;
		this._integration = data.integration ?? void 0;
	}
	/** The identifier of the last sync operation. */
	lastSyncId;
	/** Whether the operation was successful. */
	success;
	/** The webhook secret to provide to GitHub. */
	webhookSecret;
	/** The integration that was created or updated. */
	get integration() {
		return this._integration?.id ? new IntegrationQuery(this._request).fetch(this._integration?.id) : void 0;
	}
	/** The ID of integration that was created or updated. */
	get integrationId() {
		return this._integration?.id;
	}
};
/**
* GitHubEnterpriseServerInstallVerificationPayload model
*
* @param request - function to call the graphql client
* @param data - L.GitHubEnterpriseServerInstallVerificationPayloadFragment response data
*/
var GitHubEnterpriseServerInstallVerificationPayload = class extends Request {
	constructor(request, data) {
		super(request);
		this.success = data.success;
	}
	/** Has the install been successful. */
	success;
};
/**
* GitHubEnterpriseServerPayload model
*
* @param request - function to call the graphql client
* @param data - L.GitHubEnterpriseServerPayloadFragment response data
*/
var GitHubEnterpriseServerPayload = class extends Request {
	_integration;
	constructor(request, data) {
		super(request);
		this.installUrl = data.installUrl;
		this.lastSyncId = data.lastSyncId;
		this.setupUrl = data.setupUrl;
		this.success = data.success;
		this.webhookSecret = data.webhookSecret;
		this._integration = data.integration ?? void 0;
	}
	/** The app install address. */
	installUrl;
	/** The identifier of the last sync operation. */
	lastSyncId;
	/** The setup address. */
	setupUrl;
	/** Whether the operation was successful. */
	success;
	/** The webhook secret to provide to GitHub. */
	webhookSecret;
	/** The integration that was created or updated. */
	get integration() {
		return this._integration?.id ? new IntegrationQuery(this._request).fetch(this._integration?.id) : void 0;
	}
	/** The ID of integration that was created or updated. */
	get integrationId() {
		return this._integration?.id;
	}
};
/**
* GitLabIntegrationCreatePayload model
*
* @param request - function to call the graphql client
* @param data - L.GitLabIntegrationCreatePayloadFragment response data
*/
var GitLabIntegrationCreatePayload = class extends Request {
	_integration;
	constructor(request, data) {
		super(request);
		this.lastSyncId = data.lastSyncId;
		this.success = data.success;
		this.webhookSecret = data.webhookSecret;
		this._integration = data.integration ?? void 0;
	}
	/** The identifier of the last sync operation. */
	lastSyncId;
	/** Whether the operation was successful. */
	success;
	/** The webhook secret to provide to GitLab. */
	webhookSecret;
	/** The integration that was created or updated. */
	get integration() {
		return this._integration?.id ? new IntegrationQuery(this._request).fetch(this._integration?.id) : void 0;
	}
	/** The ID of integration that was created or updated. */
	get integrationId() {
		return this._integration?.id;
	}
};
/**
* Metadata for guidance that should be provided to an AI agent.
*
* @param data - L.GuidanceRuleWebhookPayloadFragment response data
*/
var GuidanceRuleWebhookPayload = class {
	constructor(data) {
		this.body = data.body;
	}
	/** The content of the guidance as markdown. */
	body;
};
/**
* An identity provider.
*
* @param request - function to call the graphql client
* @param data - L.IdentityProviderFragment response data
*/
var IdentityProvider = class extends Request {
	constructor(request, data) {
		super(request);
		this.allowNameChange = data.allowNameChange;
		this.archivedAt = parseDate(data.archivedAt) ?? void 0;
		this.createdAt = parseDate(data.createdAt) ?? /* @__PURE__ */ new Date();
		this.defaultMigrated = data.defaultMigrated;
		this.id = data.id;
		this.issuerEntityId = data.issuerEntityId ?? void 0;
		this.priority = data.priority ?? void 0;
		this.samlEnabled = data.samlEnabled;
		this.scimEnabled = data.scimEnabled;
		this.spEntityId = data.spEntityId ?? void 0;
		this.ssoBinding = data.ssoBinding ?? void 0;
		this.ssoEndpoint = data.ssoEndpoint ?? void 0;
		this.ssoSignAlgo = data.ssoSignAlgo ?? void 0;
		this.ssoSigningCert = data.ssoSigningCert ?? void 0;
		this.updatedAt = parseDate(data.updatedAt) ?? /* @__PURE__ */ new Date();
		this.type = data.type;
	}
	/** Whether users are allowed to change their name and display name even if SCIM is enabled. */
	allowNameChange;
	/** The time at which the entity was archived. Null if the entity has not been archived. */
	archivedAt;
	/** The time at which the entity was created. */
	createdAt;
	/** Whether the identity provider is the default identity provider migrated from organization level settings. */
	defaultMigrated;
	/** The unique identifier of the entity. */
	id;
	/** The issuer's custom entity ID. */
	issuerEntityId;
	/** The SAML priority used to pick default workspace in SAML SP initiated flow, when same domain is claimed for SAML by multiple workspaces. Lower priority value means higher preference. */
	priority;
	/** Whether SAML authentication is enabled for organization. */
	samlEnabled;
	/** Whether SCIM provisioning is enabled for organization. */
	scimEnabled;
	/** The service provider (Linear) custom entity ID. Defaults to https://auth.linear.app/sso */
	spEntityId;
	/** Binding method for authentication call. Can be either `post` (default) or `redirect`. */
	ssoBinding;
	/** Sign in endpoint URL for the identity provider. */
	ssoEndpoint;
	/** The algorithm of the Signing Certificate. Can be one of `sha1`, `sha256` (default), or `sha512`. */
	ssoSignAlgo;
	/** X.509 Signing Certificate in string form. */
	ssoSigningCert;
	/**
	* The last time at which the entity was meaningfully updated. This is the same as the creation time if the entity hasn't
	*     been updated after creation.
	*/
	updatedAt;
	/** The type of identity provider. */
	type;
};
/**
* ImageUploadFromUrlPayload model
*
* @param request - function to call the graphql client
* @param data - L.ImageUploadFromUrlPayloadFragment response data
*/
var ImageUploadFromUrlPayload = class extends Request {
	constructor(request, data) {
		super(request);
		this.lastSyncId = data.lastSyncId;
		this.success = data.success;
		this.url = data.url ?? void 0;
	}
	/** The identifier of the last sync operation. */
	lastSyncId;
	/** Whether the operation was successful. */
	success;
	/** The URL containing the image. */
	url;
};
/**
* An initiative to group projects.
*
* @param request - function to call the graphql client
* @param data - L.InitiativeFragment response data
*/
var Initiative = class extends Request {
	_creator;
	_integrationsSettings;
	_lastUpdate;
	_owner;
	_parentInitiative;
	constructor(request, data) {
		super(request);
		this.archivedAt = parseDate(data.archivedAt) ?? void 0;
		this.color = data.color ?? void 0;
		this.completedAt = parseDate(data.completedAt) ?? void 0;
		this.content = data.content ?? void 0;
		this.createdAt = parseDate(data.createdAt) ?? /* @__PURE__ */ new Date();
		this.description = data.description ?? void 0;
		this.healthUpdatedAt = parseDate(data.healthUpdatedAt) ?? void 0;
		this.icon = data.icon ?? void 0;
		this.id = data.id;
		this.name = data.name;
		this.slugId = data.slugId;
		this.sortOrder = data.sortOrder;
		this.startedAt = parseDate(data.startedAt) ?? void 0;
		this.targetDate = data.targetDate ?? void 0;
		this.trashed = data.trashed ?? void 0;
		this.updateReminderFrequency = data.updateReminderFrequency ?? void 0;
		this.updateReminderFrequencyInWeeks = data.updateReminderFrequencyInWeeks ?? void 0;
		this.updateRemindersHour = data.updateRemindersHour ?? void 0;
		this.updatedAt = parseDate(data.updatedAt) ?? /* @__PURE__ */ new Date();
		this.url = data.url;
		this.documentContent = data.documentContent ? new DocumentContent(request, data.documentContent) : void 0;
		this.frequencyResolution = data.frequencyResolution;
		this.health = data.health ?? void 0;
		this.status = data.status;
		this.targetDateResolution = data.targetDateResolution ?? void 0;
		this.updateRemindersDay = data.updateRemindersDay ?? void 0;
		this._creator = data.creator ?? void 0;
		this._integrationsSettings = data.integrationsSettings ?? void 0;
		this._lastUpdate = data.lastUpdate ?? void 0;
		this._owner = data.owner ?? void 0;
		this._parentInitiative = data.parentInitiative ?? void 0;
	}
	/** The time at which the entity was archived. Null if the entity has not been archived. */
	archivedAt;
	/** The initiative's color. */
	color;
	/** The time at which the initiative was moved into completed status. */
	completedAt;
	/** The initiative's content in markdown format. */
	content;
	/** The time at which the entity was created. */
	createdAt;
	/** The description of the initiative. */
	description;
	/** The time at which the initiative health was updated. */
	healthUpdatedAt;
	/** The icon of the initiative. */
	icon;
	/** The unique identifier of the entity. */
	id;
	/** The name of the initiative. */
	name;
	/** The initiative's unique URL slug. */
	slugId;
	/** The sort order of the initiative within the organization. */
	sortOrder;
	/** The time at which the initiative was moved into active status. */
	startedAt;
	/** The estimated completion date of the initiative. */
	targetDate;
	/** A flag that indicates whether the initiative is in the trash bin. */
	trashed;
	/** The frequency at which to prompt for updates. When not set, reminders are inherited from workspace. */
	updateReminderFrequency;
	/** The n-weekly frequency at which to prompt for updates. When not set, reminders are inherited from workspace. */
	updateReminderFrequencyInWeeks;
	/** The hour at which to prompt for updates. */
	updateRemindersHour;
	/**
	* The last time at which the entity was meaningfully updated. This is the same as the creation time if the entity hasn't
	*     been updated after creation.
	*/
	updatedAt;
	/** Initiative URL. */
	url;
	/** The content of the initiative description. */
	documentContent;
	/** The resolution of the reminder frequency. */
	frequencyResolution;
	/** The health of the initiative. */
	health;
	/** The status of the initiative. One of Planned, Active, Completed */
	status;
	/** The resolution of the initiative's estimated completion date. */
	targetDateResolution;
	/** The day at which to prompt for updates. */
	updateRemindersDay;
	/** The user who created the initiative. */
	get creator() {
		return this._creator?.id ? new UserQuery(this._request).fetch(this._creator?.id) : void 0;
	}
	/** The ID of user who created the initiative. */
	get creatorId() {
		return this._creator?.id;
	}
	/** Settings for all integrations associated with that initiative. */
	get integrationsSettings() {
		return this._integrationsSettings?.id ? new IntegrationsSettingsQuery(this._request).fetch(this._integrationsSettings?.id) : void 0;
	}
	/** The ID of settings for all integrations associated with that initiative. */
	get integrationsSettingsId() {
		return this._integrationsSettings?.id;
	}
	/** The last initiative update posted for this initiative. */
	get lastUpdate() {
		return this._lastUpdate?.id ? new InitiativeUpdateQuery(this._request).fetch(this._lastUpdate?.id) : void 0;
	}
	/** The ID of last initiative update posted for this initiative. */
	get lastUpdateId() {
		return this._lastUpdate?.id;
	}
	/** The organization of the initiative. */
	get organization() {
		return new OrganizationQuery(this._request).fetch();
	}
	/** The user who owns the initiative. */
	get owner() {
		return this._owner?.id ? new UserQuery(this._request).fetch(this._owner?.id) : void 0;
	}
	/** The ID of user who owns the initiative. */
	get ownerId() {
		return this._owner?.id;
	}
	/** Parent initiative associated with the initiative. */
	get parentInitiative() {
		return this._parentInitiative?.id ? new InitiativeQuery(this._request).fetch(this._parentInitiative?.id) : void 0;
	}
	/** The ID of parent initiative associated with the initiative. */
	get parentInitiativeId() {
		return this._parentInitiative?.id;
	}
	/** Documents associated with the initiative. */
	documents(variables) {
		return new Initiative_DocumentsQuery(this._request, this.id, variables).fetch(variables);
	}
	/** History entries associated with the initiative. */
	history(variables) {
		return new Initiative_HistoryQuery(this._request, this.id, variables).fetch(variables);
	}
	/** Initiative updates associated with the initiative. */
	initiativeUpdates(variables) {
		return new Initiative_InitiativeUpdatesQuery(this._request, this.id, variables).fetch(variables);
	}
	/** Links associated with the initiative. */
	links(variables) {
		return new Initiative_LinksQuery(this._request, this.id, variables).fetch(variables);
	}
	/** Projects associated with the initiative. */
	projects(variables) {
		return new Initiative_ProjectsQuery(this._request, this.id, variables).fetch(variables);
	}
	/** Sub-initiatives associated with the initiative. */
	subInitiatives(variables) {
		return new Initiative_SubInitiativesQuery(this._request, this.id, variables).fetch(variables);
	}
	/** Archives a initiative. */
	archive() {
		return new ArchiveInitiativeMutation(this._request).fetch(this.id);
	}
	/** Creates a new initiative. */
	create(input) {
		return new CreateInitiativeMutation(this._request).fetch(input);
	}
	/** Deletes (trashes) an initiative. */
	delete() {
		return new DeleteInitiativeMutation(this._request).fetch(this.id);
	}
	/** Unarchives a initiative. */
	unarchive() {
		return new UnarchiveInitiativeMutation(this._request).fetch(this.id);
	}
	/** Updates a initiative. */
	update() {
		return new InitiativeUpdateQuery(this._request).fetch(this.id);
	}
};
/**
* A generic payload return from entity archive mutations.
*
* @param request - function to call the graphql client
* @param data - L.InitiativeArchivePayloadFragment response data
*/
var InitiativeArchivePayload = class extends Request {
	_entity;
	constructor(request, data) {
		super(request);
		this.lastSyncId = data.lastSyncId;
		this.success = data.success;
		this._entity = data.entity ?? void 0;
	}
	/** The identifier of the last sync operation. */
	lastSyncId;
	/** Whether the operation was successful. */
	success;
	/** The archived/unarchived entity. Null if entity was deleted. */
	get entity() {
		return this._entity?.id ? new InitiativeQuery(this._request).fetch(this._entity?.id) : void 0;
	}
	/** The ID of archived/unarchived entity. null if entity was deleted. */
	get entityId() {
		return this._entity?.id;
	}
};
/**
* Certain properties of an initiative.
*
* @param data - L.InitiativeChildWebhookPayloadFragment response data
*/
var InitiativeChildWebhookPayload = class {
	constructor(data) {
		this.id = data.id;
		this.name = data.name;
		this.url = data.url;
	}
	/** The ID of the initiative. */
	id;
	/** The name of the initiative. */
	name;
	/** The URL of the initiative. */
	url;
};
/**
* InitiativeConnection model
*
* @param request - function to call the graphql client
* @param fetch - function to trigger a refetch of this InitiativeConnection model
* @param data - InitiativeConnection response data
*/
var InitiativeConnection = class extends Connection {
	constructor(request, fetch$1, data) {
		super(request, fetch$1, data.nodes.map((node) => new Initiative(request, node)), new PageInfo(request, data.pageInfo));
	}
};
/**
* A initiative history containing relevant change events.
*
* @param request - function to call the graphql client
* @param data - L.InitiativeHistoryFragment response data
*/
var InitiativeHistory = class extends Request {
	_initiative;
	constructor(request, data) {
		super(request);
		this.archivedAt = parseDate(data.archivedAt) ?? void 0;
		this.createdAt = parseDate(data.createdAt) ?? /* @__PURE__ */ new Date();
		this.entries = data.entries;
		this.id = data.id;
		this.updatedAt = parseDate(data.updatedAt) ?? /* @__PURE__ */ new Date();
		this._initiative = data.initiative;
	}
	/** The time at which the entity was archived. Null if the entity has not been archived. */
	archivedAt;
	/** The time at which the entity was created. */
	createdAt;
	/** The events that happened while recording that history. */
	entries;
	/** The unique identifier of the entity. */
	id;
	/**
	* The last time at which the entity was meaningfully updated. This is the same as the creation time if the entity hasn't
	*     been updated after creation.
	*/
	updatedAt;
	/** The initiative that the history is associated with. */
	get initiative() {
		return new InitiativeQuery(this._request).fetch(this._initiative.id);
	}
	/** The ID of initiative that the history is associated with. */
	get initiativeId() {
		return this._initiative?.id;
	}
};
/**
* InitiativeHistoryConnection model
*
* @param request - function to call the graphql client
* @param fetch - function to trigger a refetch of this InitiativeHistoryConnection model
* @param data - InitiativeHistoryConnection response data
*/
var InitiativeHistoryConnection = class extends Connection {
	constructor(request, fetch$1, data) {
		super(request, fetch$1, data.nodes.map((node) => new InitiativeHistory(request, node)), new PageInfo(request, data.pageInfo));
	}
};
/**
* An initiative related notification.
*
* @param request - function to call the graphql client
* @param data - L.InitiativeNotificationFragment response data
*/
var InitiativeNotification = class extends Request {
	_actor;
	_comment;
	_document;
	_externalUserActor;
	_initiative;
	_initiativeUpdate;
	_parentComment;
	_user;
	constructor(request, data) {
		super(request);
		this.archivedAt = parseDate(data.archivedAt) ?? void 0;
		this.commentId = data.commentId ?? void 0;
		this.createdAt = parseDate(data.createdAt) ?? /* @__PURE__ */ new Date();
		this.emailedAt = parseDate(data.emailedAt) ?? void 0;
		this.id = data.id;
		this.initiativeId = data.initiativeId;
		this.initiativeUpdateId = data.initiativeUpdateId ?? void 0;
		this.parentCommentId = data.parentCommentId ?? void 0;
		this.reactionEmoji = data.reactionEmoji ?? void 0;
		this.readAt = parseDate(data.readAt) ?? void 0;
		this.snoozedUntilAt = parseDate(data.snoozedUntilAt) ?? void 0;
		this.type = data.type;
		this.unsnoozedAt = parseDate(data.unsnoozedAt) ?? void 0;
		this.updatedAt = parseDate(data.updatedAt) ?? /* @__PURE__ */ new Date();
		this.botActor = data.botActor ? new ActorBot(request, data.botActor) : void 0;
		this.category = data.category;
		this._actor = data.actor ?? void 0;
		this._comment = data.comment ?? void 0;
		this._document = data.document ?? void 0;
		this._externalUserActor = data.externalUserActor ?? void 0;
		this._initiative = data.initiative ?? void 0;
		this._initiativeUpdate = data.initiativeUpdate ?? void 0;
		this._parentComment = data.parentComment ?? void 0;
		this._user = data.user;
	}
	/** The time at which the entity was archived. Null if the entity has not been archived. */
	archivedAt;
	/** Related comment ID. Null if the notification is not related to a comment. */
	commentId;
	/** The time at which the entity was created. */
	createdAt;
	/**
	* The time at when an email reminder for this notification was sent to the user. Null, if no email
	*     reminder has been sent.
	*/
	emailedAt;
	/** The unique identifier of the entity. */
	id;
	/** Related initiative ID. */
	initiativeId;
	/** Related initiative update ID. */
	initiativeUpdateId;
	/** Related parent comment ID. Null if the notification is not related to a comment. */
	parentCommentId;
	/** Name of the reaction emoji related to the notification. */
	reactionEmoji;
	/** The time at when the user marked the notification as read. Null, if the the user hasn't read the notification */
	readAt;
	/** The time until a notification will be snoozed. After that it will appear in the inbox again. */
	snoozedUntilAt;
	/** Notification type. */
	type;
	/** The time at which a notification was unsnoozed.. */
	unsnoozedAt;
	/**
	* The last time at which the entity was meaningfully updated. This is the same as the creation time if the entity hasn't
	*     been updated after creation.
	*/
	updatedAt;
	/** The bot that caused the notification. */
	botActor;
	/** The category of the notification. */
	category;
	/** The user that caused the notification. */
	get actor() {
		return this._actor?.id ? new UserQuery(this._request).fetch(this._actor?.id) : void 0;
	}
	/** The ID of user that caused the notification. */
	get actorId() {
		return this._actor?.id;
	}
	/** The comment related to the notification. */
	get comment() {
		return this._comment?.id ? new CommentQuery(this._request).fetch({ id: this._comment?.id }) : void 0;
	}
	/** The document related to the notification. */
	get document() {
		return this._document?.id ? new DocumentQuery(this._request).fetch(this._document?.id) : void 0;
	}
	/** The ID of document related to the notification. */
	get documentId() {
		return this._document?.id;
	}
	/** The external user that caused the notification. */
	get externalUserActor() {
		return this._externalUserActor?.id ? new ExternalUserQuery(this._request).fetch(this._externalUserActor?.id) : void 0;
	}
	/** The ID of external user that caused the notification. */
	get externalUserActorId() {
		return this._externalUserActor?.id;
	}
	/** The initiative related to the notification. */
	get initiative() {
		return this._initiative?.id ? new InitiativeQuery(this._request).fetch(this._initiative?.id) : void 0;
	}
	/** The initiative update related to the notification. */
	get initiativeUpdate() {
		return this._initiativeUpdate?.id ? new InitiativeUpdateQuery(this._request).fetch(this._initiativeUpdate?.id) : void 0;
	}
	/** The parent comment related to the notification, if a notification is a reply comment notification. */
	get parentComment() {
		return this._parentComment?.id ? new CommentQuery(this._request).fetch({ id: this._parentComment?.id }) : void 0;
	}
	/** The user that received the notification. */
	get user() {
		return new UserQuery(this._request).fetch(this._user.id);
	}
	/** The ID of user that received the notification. */
	get userId() {
		return this._user?.id;
	}
};
/**
* An initiative notification subscription.
*
* @param request - function to call the graphql client
* @param data - L.InitiativeNotificationSubscriptionFragment response data
*/
var InitiativeNotificationSubscription = class extends Request {
	_customView;
	_customer;
	_cycle;
	_initiative;
	_label;
	_project;
	_subscriber;
	_team;
	_user;
	constructor(request, data) {
		super(request);
		this.active = data.active;
		this.archivedAt = parseDate(data.archivedAt) ?? void 0;
		this.createdAt = parseDate(data.createdAt) ?? /* @__PURE__ */ new Date();
		this.id = data.id;
		this.notificationSubscriptionTypes = data.notificationSubscriptionTypes;
		this.updatedAt = parseDate(data.updatedAt) ?? /* @__PURE__ */ new Date();
		this.contextViewType = data.contextViewType ?? void 0;
		this.userContextViewType = data.userContextViewType ?? void 0;
		this._customView = data.customView ?? void 0;
		this._customer = data.customer ?? void 0;
		this._cycle = data.cycle ?? void 0;
		this._initiative = data.initiative;
		this._label = data.label ?? void 0;
		this._project = data.project ?? void 0;
		this._subscriber = data.subscriber;
		this._team = data.team ?? void 0;
		this._user = data.user ?? void 0;
	}
	/** Whether the subscription is active or not. */
	active;
	/** The time at which the entity was archived. Null if the entity has not been archived. */
	archivedAt;
	/** The time at which the entity was created. */
	createdAt;
	/** The unique identifier of the entity. */
	id;
	/** The type of subscription. */
	notificationSubscriptionTypes;
	/**
	* The last time at which the entity was meaningfully updated. This is the same as the creation time if the entity hasn't
	*     been updated after creation.
	*/
	updatedAt;
	/** The type of view to which the notification subscription context is associated with. */
	contextViewType;
	/** The type of user view to which the notification subscription context is associated with. */
	userContextViewType;
	/** The contextual custom view associated with the notification subscription. */
	get customView() {
		return this._customView?.id ? new CustomViewQuery(this._request).fetch(this._customView?.id) : void 0;
	}
	/** The ID of contextual custom view associated with the notification subscription. */
	get customViewId() {
		return this._customView?.id;
	}
	/** The customer associated with the notification subscription. */
	get customer() {
		return this._customer?.id ? new CustomerQuery(this._request).fetch(this._customer?.id) : void 0;
	}
	/** The ID of customer associated with the notification subscription. */
	get customerId() {
		return this._customer?.id;
	}
	/** The contextual cycle view associated with the notification subscription. */
	get cycle() {
		return this._cycle?.id ? new CycleQuery(this._request).fetch(this._cycle?.id) : void 0;
	}
	/** The ID of contextual cycle view associated with the notification subscription. */
	get cycleId() {
		return this._cycle?.id;
	}
	/** The initiative subscribed to. */
	get initiative() {
		return new InitiativeQuery(this._request).fetch(this._initiative.id);
	}
	/** The ID of initiative subscribed to. */
	get initiativeId() {
		return this._initiative?.id;
	}
	/** The contextual label view associated with the notification subscription. */
	get label() {
		return this._label?.id ? new IssueLabelQuery(this._request).fetch(this._label?.id) : void 0;
	}
	/** The ID of contextual label view associated with the notification subscription. */
	get labelId() {
		return this._label?.id;
	}
	/** The contextual project view associated with the notification subscription. */
	get project() {
		return this._project?.id ? new ProjectQuery(this._request).fetch(this._project?.id) : void 0;
	}
	/** The ID of contextual project view associated with the notification subscription. */
	get projectId() {
		return this._project?.id;
	}
	/** The user that subscribed to receive notifications. */
	get subscriber() {
		return new UserQuery(this._request).fetch(this._subscriber.id);
	}
	/** The ID of user that subscribed to receive notifications. */
	get subscriberId() {
		return this._subscriber?.id;
	}
	/** The team associated with the notification subscription. */
	get team() {
		return this._team?.id ? new TeamQuery(this._request).fetch(this._team?.id) : void 0;
	}
	/** The ID of team associated with the notification subscription. */
	get teamId() {
		return this._team?.id;
	}
	/** The user view associated with the notification subscription. */
	get user() {
		return this._user?.id ? new UserQuery(this._request).fetch(this._user?.id) : void 0;
	}
	/** The ID of user view associated with the notification subscription. */
	get userId() {
		return this._user?.id;
	}
};
/**
* The payload returned by the initiative mutations.
*
* @param request - function to call the graphql client
* @param data - L.InitiativePayloadFragment response data
*/
var InitiativePayload = class extends Request {
	_initiative;
	constructor(request, data) {
		super(request);
		this.lastSyncId = data.lastSyncId;
		this.success = data.success;
		this._initiative = data.initiative;
	}
	/** The identifier of the last sync operation. */
	lastSyncId;
	/** Whether the operation was successful. */
	success;
	/** The initiative that was created or updated. */
	get initiative() {
		return new InitiativeQuery(this._request).fetch(this._initiative.id);
	}
	/** The ID of initiative that was created or updated. */
	get initiativeId() {
		return this._initiative?.id;
	}
};
/**
* A relation representing the dependency between two initiatives.
*
* @param request - function to call the graphql client
* @param data - L.InitiativeRelationFragment response data
*/
var InitiativeRelation = class extends Request {
	_initiative;
	_relatedInitiative;
	_user;
	constructor(request, data) {
		super(request);
		this.archivedAt = parseDate(data.archivedAt) ?? void 0;
		this.createdAt = parseDate(data.createdAt) ?? /* @__PURE__ */ new Date();
		this.id = data.id;
		this.sortOrder = data.sortOrder;
		this.updatedAt = parseDate(data.updatedAt) ?? /* @__PURE__ */ new Date();
		this._initiative = data.initiative;
		this._relatedInitiative = data.relatedInitiative;
		this._user = data.user ?? void 0;
	}
	/** The time at which the entity was archived. Null if the entity has not been archived. */
	archivedAt;
	/** The time at which the entity was created. */
	createdAt;
	/** The unique identifier of the entity. */
	id;
	/** The sort order of the relation within the initiative. */
	sortOrder;
	/**
	* The last time at which the entity was meaningfully updated. This is the same as the creation time if the entity hasn't
	*     been updated after creation.
	*/
	updatedAt;
	/** The parent initiative. */
	get initiative() {
		return new InitiativeQuery(this._request).fetch(this._initiative.id);
	}
	/** The ID of parent initiative. */
	get initiativeId() {
		return this._initiative?.id;
	}
	/** The child initiative. */
	get relatedInitiative() {
		return new InitiativeQuery(this._request).fetch(this._relatedInitiative.id);
	}
	/** The ID of child initiative. */
	get relatedInitiativeId() {
		return this._relatedInitiative?.id;
	}
	/** The last user who created or modified the relation. */
	get user() {
		return this._user?.id ? new UserQuery(this._request).fetch(this._user?.id) : void 0;
	}
	/** The ID of last user who created or modified the relation. */
	get userId() {
		return this._user?.id;
	}
	/** Creates a new initiative relation. */
	create(input) {
		return new CreateInitiativeRelationMutation(this._request).fetch(input);
	}
	/** Deletes an initiative relation. */
	delete() {
		return new DeleteInitiativeRelationMutation(this._request).fetch(this.id);
	}
	/** Updates an initiative relation. */
	update(input) {
		return new UpdateInitiativeRelationMutation(this._request).fetch(this.id, input);
	}
};
/**
* InitiativeRelationConnection model
*
* @param request - function to call the graphql client
* @param fetch - function to trigger a refetch of this InitiativeRelationConnection model
* @param data - InitiativeRelationConnection response data
*/
var InitiativeRelationConnection = class extends Connection {
	constructor(request, fetch$1, data) {
		super(request, fetch$1, data.nodes.map((node) => new InitiativeRelation(request, node)), new PageInfo(request, data.pageInfo));
	}
};
/**
* InitiativeRelationPayload model
*
* @param request - function to call the graphql client
* @param data - L.InitiativeRelationPayloadFragment response data
*/
var InitiativeRelationPayload = class extends Request {
	constructor(request, data) {
		super(request);
		this.lastSyncId = data.lastSyncId;
		this.success = data.success;
		this.initiativeRelation = new InitiativeRelation(request, data.initiativeRelation);
	}
	/** The identifier of the last sync operation. */
	lastSyncId;
	/** Whether the operation was successful. */
	success;
	/** The initiative relation that was created or updated. */
	initiativeRelation;
};
/**
* Join table between projects and initiatives.
*
* @param request - function to call the graphql client
* @param data - L.InitiativeToProjectFragment response data
*/
var InitiativeToProject = class extends Request {
	_initiative;
	_project;
	constructor(request, data) {
		super(request);
		this.archivedAt = parseDate(data.archivedAt) ?? void 0;
		this.createdAt = parseDate(data.createdAt) ?? /* @__PURE__ */ new Date();
		this.id = data.id;
		this.sortOrder = data.sortOrder;
		this.updatedAt = parseDate(data.updatedAt) ?? /* @__PURE__ */ new Date();
		this._initiative = data.initiative;
		this._project = data.project;
	}
	/** The time at which the entity was archived. Null if the entity has not been archived. */
	archivedAt;
	/** The time at which the entity was created. */
	createdAt;
	/** The unique identifier of the entity. */
	id;
	/** The sort order of the project within the initiative. */
	sortOrder;
	/**
	* The last time at which the entity was meaningfully updated. This is the same as the creation time if the entity hasn't
	*     been updated after creation.
	*/
	updatedAt;
	/** The initiative that the project is associated with. */
	get initiative() {
		return new InitiativeQuery(this._request).fetch(this._initiative.id);
	}
	/** The ID of initiative that the project is associated with. */
	get initiativeId() {
		return this._initiative?.id;
	}
	/** The project that the initiative is associated with. */
	get project() {
		return new ProjectQuery(this._request).fetch(this._project.id);
	}
	/** The ID of project that the initiative is associated with. */
	get projectId() {
		return this._project?.id;
	}
	/** Creates a new initiativeToProject join. */
	create(input) {
		return new CreateInitiativeToProjectMutation(this._request).fetch(input);
	}
	/** Deletes a initiativeToProject. */
	delete() {
		return new DeleteInitiativeToProjectMutation(this._request).fetch(this.id);
	}
	/** Updates a initiativeToProject. */
	update(input) {
		return new UpdateInitiativeToProjectMutation(this._request).fetch(this.id, input);
	}
};
/**
* InitiativeToProjectConnection model
*
* @param request - function to call the graphql client
* @param fetch - function to trigger a refetch of this InitiativeToProjectConnection model
* @param data - InitiativeToProjectConnection response data
*/
var InitiativeToProjectConnection = class extends Connection {
	constructor(request, fetch$1, data) {
		super(request, fetch$1, data.nodes.map((node) => new InitiativeToProject(request, node)), new PageInfo(request, data.pageInfo));
	}
};
/**
* The result of a initiativeToProject mutation.
*
* @param request - function to call the graphql client
* @param data - L.InitiativeToProjectPayloadFragment response data
*/
var InitiativeToProjectPayload = class extends Request {
	_initiativeToProject;
	constructor(request, data) {
		super(request);
		this.lastSyncId = data.lastSyncId;
		this.success = data.success;
		this._initiativeToProject = data.initiativeToProject;
	}
	/** The identifier of the last sync operation. */
	lastSyncId;
	/** Whether the operation was successful. */
	success;
	/** The initiativeToProject that was created or updated. */
	get initiativeToProject() {
		return new InitiativeToProjectQuery(this._request).fetch(this._initiativeToProject.id);
	}
	/** The ID of initiativetoproject that was created or updated. */
	get initiativeToProjectId() {
		return this._initiativeToProject?.id;
	}
};
/**
* An initiative update.
*
* @param request - function to call the graphql client
* @param data - L.InitiativeUpdateFragment response data
*/
var InitiativeUpdate = class extends Request {
	_initiative;
	_user;
	constructor(request, data) {
		super(request);
		this.archivedAt = parseDate(data.archivedAt) ?? void 0;
		this.body = data.body;
		this.commentCount = data.commentCount;
		this.createdAt = parseDate(data.createdAt) ?? /* @__PURE__ */ new Date();
		this.diff = data.diff ?? void 0;
		this.diffMarkdown = data.diffMarkdown ?? void 0;
		this.editedAt = parseDate(data.editedAt) ?? void 0;
		this.id = data.id;
		this.isDiffHidden = data.isDiffHidden;
		this.isStale = data.isStale;
		this.reactionData = data.reactionData;
		this.slugId = data.slugId;
		this.updatedAt = parseDate(data.updatedAt) ?? /* @__PURE__ */ new Date();
		this.url = data.url;
		this.reactions = data.reactions.map((node) => new Reaction(request, node));
		this.health = data.health;
		this._initiative = data.initiative;
		this._user = data.user;
	}
	/** The time at which the entity was archived. Null if the entity has not been archived. */
	archivedAt;
	/** The update content in markdown format. */
	body;
	/** Number of comments associated with the initiative update. */
	commentCount;
	/** The time at which the entity was created. */
	createdAt;
	/** The diff between the current update and the previous one. */
	diff;
	/** The diff between the current update and the previous one, formatted as markdown. */
	diffMarkdown;
	/** The time the update was edited. */
	editedAt;
	/** The unique identifier of the entity. */
	id;
	/** Whether initiative update diff should be hidden. */
	isDiffHidden;
	/** Whether the initiative update is stale. */
	isStale;
	/** Emoji reaction summary, grouped by emoji type. */
	reactionData;
	/** The update's unique URL slug. */
	slugId;
	/**
	* The last time at which the entity was meaningfully updated. This is the same as the creation time if the entity hasn't
	*     been updated after creation.
	*/
	updatedAt;
	/** The URL to the initiative update. */
	url;
	/** Reactions associated with the initiative update. */
	reactions;
	/** The health at the time of the update. */
	health;
	/** The initiative that the update is associated with. */
	get initiative() {
		return new InitiativeQuery(this._request).fetch(this._initiative.id);
	}
	/** The ID of initiative that the update is associated with. */
	get initiativeId() {
		return this._initiative?.id;
	}
	/** The user who wrote the update. */
	get user() {
		return new UserQuery(this._request).fetch(this._user.id);
	}
	/** The ID of user who wrote the update. */
	get userId() {
		return this._user?.id;
	}
	/** Comments associated with the initiative update. */
	comments(variables) {
		return new InitiativeUpdate_CommentsQuery(this._request, this.id, variables).fetch(variables);
	}
	/** Archives an initiative update. */
	archive() {
		return new ArchiveInitiativeUpdateMutation(this._request).fetch(this.id);
	}
	/** Creates a initiative update. */
	create(input) {
		return new CreateInitiativeUpdateMutation(this._request).fetch(input);
	}
	/** Unarchives an initiative update. */
	unarchive() {
		return new UnarchiveInitiativeUpdateMutation(this._request).fetch(this.id);
	}
	/** Updates an update. */
	update(input) {
		return new UpdateInitiativeUpdateMutation(this._request).fetch(this.id, input);
	}
};
/**
* A generic payload return from entity archive mutations.
*
* @param request - function to call the graphql client
* @param data - L.InitiativeUpdateArchivePayloadFragment response data
*/
var InitiativeUpdateArchivePayload = class extends Request {
	_entity;
	constructor(request, data) {
		super(request);
		this.lastSyncId = data.lastSyncId;
		this.success = data.success;
		this._entity = data.entity ?? void 0;
	}
	/** The identifier of the last sync operation. */
	lastSyncId;
	/** Whether the operation was successful. */
	success;
	/** The archived/unarchived entity. Null if entity was deleted. */
	get entity() {
		return this._entity?.id ? new InitiativeUpdateQuery(this._request).fetch(this._entity?.id) : void 0;
	}
	/** The ID of archived/unarchived entity. null if entity was deleted. */
	get entityId() {
		return this._entity?.id;
	}
};
/**
* Certain properties of an initiative update.
*
* @param data - L.InitiativeUpdateChildWebhookPayloadFragment response data
*/
var InitiativeUpdateChildWebhookPayload = class {
	constructor(data) {
		this.bodyData = data.bodyData;
		this.editedAt = data.editedAt;
		this.health = data.health;
		this.id = data.id;
	}
	/** The body of the initiative update. */
	bodyData;
	/** The edited at timestamp of the initiative update. */
	editedAt;
	/** The health of the initiative update. */
	health;
	/** The ID of the initiative update. */
	id;
};
/**
* InitiativeUpdateConnection model
*
* @param request - function to call the graphql client
* @param fetch - function to trigger a refetch of this InitiativeUpdateConnection model
* @param data - InitiativeUpdateConnection response data
*/
var InitiativeUpdateConnection = class extends Connection {
	constructor(request, fetch$1, data) {
		super(request, fetch$1, data.nodes.map((node) => new InitiativeUpdate(request, node)), new PageInfo(request, data.pageInfo));
	}
};
/**
* InitiativeUpdatePayload model
*
* @param request - function to call the graphql client
* @param data - L.InitiativeUpdatePayloadFragment response data
*/
var InitiativeUpdatePayload = class extends Request {
	_initiativeUpdate;
	constructor(request, data) {
		super(request);
		this.lastSyncId = data.lastSyncId;
		this.success = data.success;
		this._initiativeUpdate = data.initiativeUpdate;
	}
	/** The identifier of the last sync operation. */
	lastSyncId;
	/** Whether the operation was successful. */
	success;
	/** The initiative update that was created. */
	get initiativeUpdate() {
		return new InitiativeUpdateQuery(this._request).fetch(this._initiativeUpdate.id);
	}
	/** The ID of initiative update that was created. */
	get initiativeUpdateId() {
		return this._initiativeUpdate?.id;
	}
};
/**
* InitiativeUpdateReminderPayload model
*
* @param request - function to call the graphql client
* @param data - L.InitiativeUpdateReminderPayloadFragment response data
*/
var InitiativeUpdateReminderPayload = class extends Request {
	constructor(request, data) {
		super(request);
		this.lastSyncId = data.lastSyncId;
		this.success = data.success;
	}
	/** The identifier of the last sync operation. */
	lastSyncId;
	/** Whether the operation was successful. */
	success;
};
/**
* Payload for an initiative update webhook.
*
* @param data - L.InitiativeUpdateWebhookPayloadFragment response data
*/
var InitiativeUpdateWebhookPayload = class {
	constructor(data) {
		this.archivedAt = data.archivedAt ?? void 0;
		this.body = data.body;
		this.bodyData = data.bodyData;
		this.createdAt = data.createdAt;
		this.editedAt = data.editedAt;
		this.health = data.health;
		this.id = data.id;
		this.initiativeId = data.initiativeId;
		this.reactionData = data.reactionData;
		this.slugId = data.slugId;
		this.updatedAt = data.updatedAt;
		this.url = data.url ?? void 0;
		this.userId = data.userId;
		this.initiative = new InitiativeChildWebhookPayload(data.initiative);
		this.user = new UserChildWebhookPayload(data.user);
	}
	/** The time at which the entity was archived. */
	archivedAt;
	/** The body of the initiative update. */
	body;
	/** The body data of the initiative update. */
	bodyData;
	/** The time at which the entity was created. */
	createdAt;
	/** The edited at timestamp of the initiative update. */
	editedAt;
	/** The health of the initiative update. */
	health;
	/** The ID of the entity. */
	id;
	/** The initiative id of the initiative update. */
	initiativeId;
	/** The reaction data for this initiative update. */
	reactionData;
	/** The slug id of the initiative update. */
	slugId;
	/** The time at which the entity was updated. */
	updatedAt;
	/** The URL of the initiative update. */
	url;
	/** The user id of the initiative update. */
	userId;
	/** The initiative that the initiative update belongs to. */
	initiative;
	/** The user that created the initiative update. */
	user;
};
/**
* Payload for an initiative webhook.
*
* @param data - L.InitiativeWebhookPayloadFragment response data
*/
var InitiativeWebhookPayload = class {
	constructor(data) {
		this.archivedAt = data.archivedAt ?? void 0;
		this.color = data.color ?? void 0;
		this.completedAt = data.completedAt ?? void 0;
		this.createdAt = data.createdAt;
		this.creatorId = data.creatorId ?? void 0;
		this.description = data.description;
		this.frequencyResolution = data.frequencyResolution;
		this.health = data.health ?? void 0;
		this.healthUpdatedAt = data.healthUpdatedAt ?? void 0;
		this.icon = data.icon ?? void 0;
		this.id = data.id;
		this.lastUpdateId = data.lastUpdateId ?? void 0;
		this.name = data.name;
		this.organizationId = data.organizationId;
		this.ownerId = data.ownerId ?? void 0;
		this.slugId = data.slugId;
		this.sortOrder = data.sortOrder;
		this.startedAt = data.startedAt ?? void 0;
		this.status = data.status;
		this.targetDate = data.targetDate ?? void 0;
		this.targetDateResolution = data.targetDateResolution ?? void 0;
		this.trashed = data.trashed ?? void 0;
		this.updateReminderFrequency = data.updateReminderFrequency ?? void 0;
		this.updateReminderFrequencyInWeeks = data.updateReminderFrequencyInWeeks ?? void 0;
		this.updateRemindersDay = data.updateRemindersDay ?? void 0;
		this.updateRemindersHour = data.updateRemindersHour ?? void 0;
		this.updatedAt = data.updatedAt;
		this.url = data.url;
		this.creator = data.creator ? new UserChildWebhookPayload(data.creator) : void 0;
		this.lastUpdate = data.lastUpdate ? new InitiativeUpdateChildWebhookPayload(data.lastUpdate) : void 0;
		this.owner = data.owner ? new UserChildWebhookPayload(data.owner) : void 0;
		this.parentInitiative = data.parentInitiative ? new InitiativeChildWebhookPayload(data.parentInitiative) : void 0;
		this.projects = data.projects ? data.projects.map((node) => new ProjectChildWebhookPayload(node)) : void 0;
		this.subInitiatives = data.subInitiatives ? data.subInitiatives.map((node) => new InitiativeChildWebhookPayload(node)) : void 0;
	}
	/** The time at which the entity was archived. */
	archivedAt;
	/** The color of the initiative. */
	color;
	/** When the initiative was completed. */
	completedAt;
	/** The time at which the entity was created. */
	createdAt;
	/** The ID of the user who created the initiative. */
	creatorId;
	/** The description of the initiative. */
	description;
	/** The resolution of the update reminder frequency. */
	frequencyResolution;
	/** The health status of the initiative. */
	health;
	/** When the health status was last updated. */
	healthUpdatedAt;
	/** The icon of the initiative. */
	icon;
	/** The ID of the entity. */
	id;
	/** The ID of the last update for this initiative. */
	lastUpdateId;
	/** The name of the initiative. */
	name;
	/** The ID of the organization this initiative belongs to. */
	organizationId;
	/** The ID of the user who owns the initiative. */
	ownerId;
	/** The unique slug identifier of the initiative. */
	slugId;
	/** The sort order of the initiative within the organization. */
	sortOrder;
	/** When the initiative was started. */
	startedAt;
	/** The current status of the initiative. */
	status;
	/** The target date of the initiative. */
	targetDate;
	/** The resolution of the target date. */
	targetDateResolution;
	/** Whether the initiative is trashed. */
	trashed;
	/** The frequency of update reminders. */
	updateReminderFrequency;
	/** The frequency of update reminders in weeks. */
	updateReminderFrequencyInWeeks;
	/** The day of the week for update reminders. */
	updateRemindersDay;
	/** The hour of the day for update reminders. */
	updateRemindersHour;
	/** The time at which the entity was updated. */
	updatedAt;
	/** The URL of the initiative. */
	url;
	/** The projects associated with the initiative. */
	projects;
	/** The sub-initiatives associated with the initiative. */
	subInitiatives;
	/** The user who created the initiative. */
	creator;
	/** The last update for this initiative. */
	lastUpdate;
	/** The user who owns the initiative. */
	owner;
	/** The parent initiative associated with the initiative. */
	parentInitiative;
};
/**
* An integration with an external service.
*
* @param request - function to call the graphql client
* @param data - L.IntegrationFragment response data
*/
var Integration = class extends Request {
	_creator;
	_team;
	constructor(request, data) {
		super(request);
		this.archivedAt = parseDate(data.archivedAt) ?? void 0;
		this.createdAt = parseDate(data.createdAt) ?? /* @__PURE__ */ new Date();
		this.id = data.id;
		this.service = data.service;
		this.updatedAt = parseDate(data.updatedAt) ?? /* @__PURE__ */ new Date();
		this._creator = data.creator;
		this._team = data.team ?? void 0;
	}
	/** The time at which the entity was archived. Null if the entity has not been archived. */
	archivedAt;
	/** The time at which the entity was created. */
	createdAt;
	/** The unique identifier of the entity. */
	id;
	/** The integration's type. */
	service;
	/**
	* The last time at which the entity was meaningfully updated. This is the same as the creation time if the entity hasn't
	*     been updated after creation.
	*/
	updatedAt;
	/** The user that added the integration. */
	get creator() {
		return new UserQuery(this._request).fetch(this._creator.id);
	}
	/** The ID of user that added the integration. */
	get creatorId() {
		return this._creator?.id;
	}
	/** The organization that the integration is associated with. */
	get organization() {
		return new OrganizationQuery(this._request).fetch();
	}
	/** The team that the integration is associated with. */
	get team() {
		return this._team?.id ? new TeamQuery(this._request).fetch(this._team?.id) : void 0;
	}
	/** The ID of team that the integration is associated with. */
	get teamId() {
		return this._team?.id;
	}
	/** Archives an integration. */
	archive() {
		return new ArchiveIntegrationMutation(this._request).fetch(this.id);
	}
	/** Deletes an integration. */
	delete(variables) {
		return new DeleteIntegrationMutation(this._request).fetch(this.id, variables);
	}
};
/**
* Integration actor payload for webhooks.
*
* @param data - L.IntegrationActorWebhookPayloadFragment response data
*/
var IntegrationActorWebhookPayload = class {
	constructor(data) {
		this.id = data.id;
		this.service = data.service;
		this.type = data.type;
	}
	/** The ID of the integration. */
	id;
	/** The service of the integration. */
	service;
	/** The type of actor. */
	type;
};
/**
* Certain properties of an integration.
*
* @param data - L.IntegrationChildWebhookPayloadFragment response data
*/
var IntegrationChildWebhookPayload = class {
	constructor(data) {
		this.id = data.id;
		this.service = data.service;
	}
	/** The ID of the integration. */
	id;
	/** The service of the integration. */
	service;
};
/**
* IntegrationConnection model
*
* @param request - function to call the graphql client
* @param fetch - function to trigger a refetch of this IntegrationConnection model
* @param data - IntegrationConnection response data
*/
var IntegrationConnection = class extends Connection {
	constructor(request, fetch$1, data) {
		super(request, fetch$1, data.nodes.map((node) => new Integration(request, node)), new PageInfo(request, data.pageInfo));
	}
};
/**
* IntegrationHasScopesPayload model
*
* @param request - function to call the graphql client
* @param data - L.IntegrationHasScopesPayloadFragment response data
*/
var IntegrationHasScopesPayload = class extends Request {
	constructor(request, data) {
		super(request);
		this.hasAllScopes = data.hasAllScopes;
		this.missingScopes = data.missingScopes ?? void 0;
	}
	/** Whether the integration has the required scopes. */
	hasAllScopes;
	/** The missing scopes. */
	missingScopes;
};
/**
* IntegrationPayload model
*
* @param request - function to call the graphql client
* @param data - L.IntegrationPayloadFragment response data
*/
var IntegrationPayload = class extends Request {
	_integration;
	constructor(request, data) {
		super(request);
		this.lastSyncId = data.lastSyncId;
		this.success = data.success;
		this._integration = data.integration ?? void 0;
	}
	/** The identifier of the last sync operation. */
	lastSyncId;
	/** Whether the operation was successful. */
	success;
	/** The integration that was created or updated. */
	get integration() {
		return this._integration?.id ? new IntegrationQuery(this._request).fetch(this._integration?.id) : void 0;
	}
	/** The ID of integration that was created or updated. */
	get integrationId() {
		return this._integration?.id;
	}
};
/**
* IntegrationRequestPayload model
*
* @param request - function to call the graphql client
* @param data - L.IntegrationRequestPayloadFragment response data
*/
var IntegrationRequestPayload = class extends Request {
	constructor(request, data) {
		super(request);
		this.success = data.success;
	}
	/** Whether the operation was successful. */
	success;
};
/**
* IntegrationSlackWorkspaceNamePayload model
*
* @param request - function to call the graphql client
* @param data - L.IntegrationSlackWorkspaceNamePayloadFragment response data
*/
var IntegrationSlackWorkspaceNamePayload = class extends Request {
	constructor(request, data) {
		super(request);
		this.name = data.name;
		this.success = data.success;
	}
	/** The current name of the Slack workspace. */
	name;
	/** Whether the operation was successful. */
	success;
};
/**
* Join table between templates and integrations.
*
* @param request - function to call the graphql client
* @param data - L.IntegrationTemplateFragment response data
*/
var IntegrationTemplate = class extends Request {
	_integration;
	_template;
	constructor(request, data) {
		super(request);
		this.archivedAt = parseDate(data.archivedAt) ?? void 0;
		this.createdAt = parseDate(data.createdAt) ?? /* @__PURE__ */ new Date();
		this.foreignEntityId = data.foreignEntityId ?? void 0;
		this.id = data.id;
		this.updatedAt = parseDate(data.updatedAt) ?? /* @__PURE__ */ new Date();
		this._integration = data.integration;
		this._template = data.template;
	}
	/** The time at which the entity was archived. Null if the entity has not been archived. */
	archivedAt;
	/** The time at which the entity was created. */
	createdAt;
	/** ID of the foreign entity in the external integration this template is for, e.g., Slack channel ID. */
	foreignEntityId;
	/** The unique identifier of the entity. */
	id;
	/**
	* The last time at which the entity was meaningfully updated. This is the same as the creation time if the entity hasn't
	*     been updated after creation.
	*/
	updatedAt;
	/** The integration that the template is associated with. */
	get integration() {
		return new IntegrationQuery(this._request).fetch(this._integration.id);
	}
	/** The ID of integration that the template is associated with. */
	get integrationId() {
		return this._integration?.id;
	}
	/** The template that the integration is associated with. */
	get template() {
		return new TemplateQuery(this._request).fetch(this._template.id);
	}
	/** The ID of template that the integration is associated with. */
	get templateId() {
		return this._template?.id;
	}
	/** Creates a new integrationTemplate join. */
	create(input) {
		return new CreateIntegrationTemplateMutation(this._request).fetch(input);
	}
	/** Deletes a integrationTemplate. */
	delete() {
		return new DeleteIntegrationTemplateMutation(this._request).fetch(this.id);
	}
};
/**
* IntegrationTemplateConnection model
*
* @param request - function to call the graphql client
* @param fetch - function to trigger a refetch of this IntegrationTemplateConnection model
* @param data - IntegrationTemplateConnection response data
*/
var IntegrationTemplateConnection = class extends Connection {
	constructor(request, fetch$1, data) {
		super(request, fetch$1, data.nodes.map((node) => new IntegrationTemplate(request, node)), new PageInfo(request, data.pageInfo));
	}
};
/**
* IntegrationTemplatePayload model
*
* @param request - function to call the graphql client
* @param data - L.IntegrationTemplatePayloadFragment response data
*/
var IntegrationTemplatePayload = class extends Request {
	_integrationTemplate;
	constructor(request, data) {
		super(request);
		this.lastSyncId = data.lastSyncId;
		this.success = data.success;
		this._integrationTemplate = data.integrationTemplate;
	}
	/** The identifier of the last sync operation. */
	lastSyncId;
	/** Whether the operation was successful. */
	success;
	/** The IntegrationTemplate that was created or updated. */
	get integrationTemplate() {
		return new IntegrationTemplateQuery(this._request).fetch(this._integrationTemplate.id);
	}
	/** The ID of integrationtemplate that was created or updated. */
	get integrationTemplateId() {
		return this._integrationTemplate?.id;
	}
};
/**
* The configuration of all integrations for different entities.
*
* @param request - function to call the graphql client
* @param data - L.IntegrationsSettingsFragment response data
*/
var IntegrationsSettings = class extends Request {
	_initiative;
	_project;
	_team;
	constructor(request, data) {
		super(request);
		this.archivedAt = parseDate(data.archivedAt) ?? void 0;
		this.createdAt = parseDate(data.createdAt) ?? /* @__PURE__ */ new Date();
		this.id = data.id;
		this.slackInitiativeUpdateCreated = data.slackInitiativeUpdateCreated ?? void 0;
		this.slackIssueAddedToTriage = data.slackIssueAddedToTriage ?? void 0;
		this.slackIssueAddedToView = data.slackIssueAddedToView ?? void 0;
		this.slackIssueCreated = data.slackIssueCreated ?? void 0;
		this.slackIssueNewComment = data.slackIssueNewComment ?? void 0;
		this.slackIssueSlaBreached = data.slackIssueSlaBreached ?? void 0;
		this.slackIssueSlaHighRisk = data.slackIssueSlaHighRisk ?? void 0;
		this.slackIssueStatusChangedAll = data.slackIssueStatusChangedAll ?? void 0;
		this.slackIssueStatusChangedDone = data.slackIssueStatusChangedDone ?? void 0;
		this.slackProjectUpdateCreated = data.slackProjectUpdateCreated ?? void 0;
		this.slackProjectUpdateCreatedToTeam = data.slackProjectUpdateCreatedToTeam ?? void 0;
		this.slackProjectUpdateCreatedToWorkspace = data.slackProjectUpdateCreatedToWorkspace ?? void 0;
		this.updatedAt = parseDate(data.updatedAt) ?? /* @__PURE__ */ new Date();
		this.contextViewType = data.contextViewType ?? void 0;
		this._initiative = data.initiative ?? void 0;
		this._project = data.project ?? void 0;
		this._team = data.team ?? void 0;
	}
	/** The time at which the entity was archived. Null if the entity has not been archived. */
	archivedAt;
	/** The time at which the entity was created. */
	createdAt;
	/** The unique identifier of the entity. */
	id;
	/** Whether to send a Slack message when a initiate update is created. */
	slackInitiativeUpdateCreated;
	/** Whether to send a Slack message when a new issue is added to triage. */
	slackIssueAddedToTriage;
	/** Whether to send a Slack message when an issue is added to the custom view. */
	slackIssueAddedToView;
	/** Whether to send a Slack message when a new issue is created for the project or the team. */
	slackIssueCreated;
	/** Whether to send a Slack message when a comment is created on any of the project or team's issues. */
	slackIssueNewComment;
	/** Whether to send a Slack message when an SLA is breached. */
	slackIssueSlaBreached;
	/** Whether to send a Slack message when an SLA is at high risk. */
	slackIssueSlaHighRisk;
	/** Whether to send a Slack message when any of the project or team's issues has a change in status. */
	slackIssueStatusChangedAll;
	/** Whether to send a Slack message when any of the project or team's issues change to completed or cancelled. */
	slackIssueStatusChangedDone;
	/** Whether to send a Slack message when a project update is created. */
	slackProjectUpdateCreated;
	/** Whether to send a new project update to team Slack channels. */
	slackProjectUpdateCreatedToTeam;
	/** Whether to send a new project update to workspace Slack channel. */
	slackProjectUpdateCreatedToWorkspace;
	/**
	* The last time at which the entity was meaningfully updated. This is the same as the creation time if the entity hasn't
	*     been updated after creation.
	*/
	updatedAt;
	/** The type of view to which the integration settings context is associated with. */
	contextViewType;
	/** Initiative which those settings apply to. */
	get initiative() {
		return this._initiative?.id ? new InitiativeQuery(this._request).fetch(this._initiative?.id) : void 0;
	}
	/** The ID of initiative which those settings apply to. */
	get initiativeId() {
		return this._initiative?.id;
	}
	/** Project which those settings apply to. */
	get project() {
		return this._project?.id ? new ProjectQuery(this._request).fetch(this._project?.id) : void 0;
	}
	/** The ID of project which those settings apply to. */
	get projectId() {
		return this._project?.id;
	}
	/** Team which those settings apply to. */
	get team() {
		return this._team?.id ? new TeamQuery(this._request).fetch(this._team?.id) : void 0;
	}
	/** The ID of team which those settings apply to. */
	get teamId() {
		return this._team?.id;
	}
	/** Creates new settings for one or more integrations. */
	create(input) {
		return new CreateIntegrationsSettingsMutation(this._request).fetch(input);
	}
	/** Updates settings related to integrations for a project or a team. */
	update(input) {
		return new UpdateIntegrationsSettingsMutation(this._request).fetch(this.id, input);
	}
};
/**
* IntegrationsSettingsPayload model
*
* @param request - function to call the graphql client
* @param data - L.IntegrationsSettingsPayloadFragment response data
*/
var IntegrationsSettingsPayload = class extends Request {
	_integrationsSettings;
	constructor(request, data) {
		super(request);
		this.lastSyncId = data.lastSyncId;
		this.success = data.success;
		this._integrationsSettings = data.integrationsSettings;
	}
	/** The identifier of the last sync operation. */
	lastSyncId;
	/** Whether the operation was successful. */
	success;
	/** The settings that were created or updated. */
	get integrationsSettings() {
		return new IntegrationsSettingsQuery(this._request).fetch(this._integrationsSettings.id);
	}
	/** The ID of settings that were created or updated. */
	get integrationsSettingsId() {
		return this._integrationsSettings?.id;
	}
};
/**
* An issue.
*
* @param request - function to call the graphql client
* @param data - L.IssueFragment response data
*/
var Issue = class extends Request {
	_asksExternalUserRequester;
	_asksRequester;
	_assignee;
	_creator;
	_cycle;
	_delegate;
	_externalUserCreator;
	_favorite;
	_lastAppliedTemplate;
	_parent;
	_project;
	_projectMilestone;
	_recurringIssueTemplate;
	_snoozedBy;
	_sourceComment;
	_state;
	_team;
	constructor(request, data) {
		super(request);
		this.addedToCycleAt = parseDate(data.addedToCycleAt) ?? void 0;
		this.addedToProjectAt = parseDate(data.addedToProjectAt) ?? void 0;
		this.addedToTeamAt = parseDate(data.addedToTeamAt) ?? void 0;
		this.archivedAt = parseDate(data.archivedAt) ?? void 0;
		this.autoArchivedAt = parseDate(data.autoArchivedAt) ?? void 0;
		this.autoClosedAt = parseDate(data.autoClosedAt) ?? void 0;
		this.boardOrder = data.boardOrder;
		this.branchName = data.branchName;
		this.canceledAt = parseDate(data.canceledAt) ?? void 0;
		this.completedAt = parseDate(data.completedAt) ?? void 0;
		this.createdAt = parseDate(data.createdAt) ?? /* @__PURE__ */ new Date();
		this.customerTicketCount = data.customerTicketCount;
		this.description = data.description ?? void 0;
		this.dueDate = data.dueDate ?? void 0;
		this.estimate = data.estimate ?? void 0;
		this.id = data.id;
		this.identifier = data.identifier;
		this.labelIds = data.labelIds;
		this.number = data.number;
		this.previousIdentifiers = data.previousIdentifiers;
		this.priority = data.priority;
		this.priorityLabel = data.priorityLabel;
		this.prioritySortOrder = data.prioritySortOrder;
		this.reactionData = data.reactionData;
		this.slaBreachesAt = parseDate(data.slaBreachesAt) ?? void 0;
		this.slaHighRiskAt = parseDate(data.slaHighRiskAt) ?? void 0;
		this.slaMediumRiskAt = parseDate(data.slaMediumRiskAt) ?? void 0;
		this.slaStartedAt = parseDate(data.slaStartedAt) ?? void 0;
		this.slaType = data.slaType ?? void 0;
		this.snoozedUntilAt = parseDate(data.snoozedUntilAt) ?? void 0;
		this.sortOrder = data.sortOrder;
		this.startedAt = parseDate(data.startedAt) ?? void 0;
		this.startedTriageAt = parseDate(data.startedTriageAt) ?? void 0;
		this.subIssueSortOrder = data.subIssueSortOrder ?? void 0;
		this.title = data.title;
		this.trashed = data.trashed ?? void 0;
		this.triagedAt = parseDate(data.triagedAt) ?? void 0;
		this.updatedAt = parseDate(data.updatedAt) ?? /* @__PURE__ */ new Date();
		this.url = data.url;
		this.botActor = data.botActor ? new ActorBot(request, data.botActor) : void 0;
		this.reactions = data.reactions.map((node) => new Reaction(request, node));
		this.syncedWith = data.syncedWith ? data.syncedWith.map((node) => new ExternalEntityInfo(request, node)) : void 0;
		this.integrationSourceType = data.integrationSourceType ?? void 0;
		this._asksExternalUserRequester = data.asksExternalUserRequester ?? void 0;
		this._asksRequester = data.asksRequester ?? void 0;
		this._assignee = data.assignee ?? void 0;
		this._creator = data.creator ?? void 0;
		this._cycle = data.cycle ?? void 0;
		this._delegate = data.delegate ?? void 0;
		this._externalUserCreator = data.externalUserCreator ?? void 0;
		this._favorite = data.favorite ?? void 0;
		this._lastAppliedTemplate = data.lastAppliedTemplate ?? void 0;
		this._parent = data.parent ?? void 0;
		this._project = data.project ?? void 0;
		this._projectMilestone = data.projectMilestone ?? void 0;
		this._recurringIssueTemplate = data.recurringIssueTemplate ?? void 0;
		this._snoozedBy = data.snoozedBy ?? void 0;
		this._sourceComment = data.sourceComment ?? void 0;
		this._state = data.state;
		this._team = data.team;
	}
	/** The time at which the issue was added to a cycle. */
	addedToCycleAt;
	/** The time at which the issue was added to a project. */
	addedToProjectAt;
	/** The time at which the issue was added to a team. */
	addedToTeamAt;
	/** The time at which the entity was archived. Null if the entity has not been archived. */
	archivedAt;
	/** The time at which the issue was automatically archived by the auto pruning process. */
	autoArchivedAt;
	/** The time at which the issue was automatically closed by the auto pruning process. */
	autoClosedAt;
	/** The order of the item in its column on the board. */
	boardOrder;
	/** Suggested branch name for the issue. */
	branchName;
	/** The time at which the issue was moved into canceled state. */
	canceledAt;
	/** The time at which the issue was moved into completed state. */
	completedAt;
	/** The time at which the entity was created. */
	createdAt;
	/** Returns the number of Attachment resources which are created by customer support ticketing systems (e.g. Zendesk). */
	customerTicketCount;
	/** The issue's description in markdown format. */
	description;
	/** The date at which the issue is due. */
	dueDate;
	/** The estimate of the complexity of the issue.. */
	estimate;
	/** The unique identifier of the entity. */
	id;
	/** Issue's human readable identifier (e.g. ENG-123). */
	identifier;
	/** Id of the labels associated with this issue. */
	labelIds;
	/** The issue's unique number. */
	number;
	/** Previous identifiers of the issue if it has been moved between teams. */
	previousIdentifiers;
	/** The priority of the issue. 0 = No priority, 1 = Urgent, 2 = High, 3 = Normal, 4 = Low. */
	priority;
	/** Label for the priority. */
	priorityLabel;
	/** The order of the item in relation to other items in the organization, when ordered by priority. */
	prioritySortOrder;
	/** Emoji reaction summary, grouped by emoji type. */
	reactionData;
	/** The time at which the issue's SLA will breach. */
	slaBreachesAt;
	/** The time at which the issue's SLA will enter high risk state. */
	slaHighRiskAt;
	/** The time at which the issue's SLA will enter medium risk state. */
	slaMediumRiskAt;
	/** The time at which the issue's SLA began. */
	slaStartedAt;
	/** The type of SLA set on the issue. Calendar days or business days. */
	slaType;
	/** The time until an issue will be snoozed in Triage view. */
	snoozedUntilAt;
	/** The order of the item in relation to other items in the organization. */
	sortOrder;
	/** The time at which the issue was moved into started state. */
	startedAt;
	/** The time at which the issue entered triage. */
	startedTriageAt;
	/** The order of the item in the sub-issue list. Only set if the issue has a parent. */
	subIssueSortOrder;
	/** The issue's title. */
	title;
	/** A flag that indicates whether the issue is in the trash bin. */
	trashed;
	/** The time at which the issue left triage. */
	triagedAt;
	/**
	* The last time at which the entity was meaningfully updated. This is the same as the creation time if the entity hasn't
	*     been updated after creation.
	*/
	updatedAt;
	/** Issue URL. */
	url;
	/** Reactions associated with the issue. */
	reactions;
	/** The external services the issue is synced with. */
	syncedWith;
	/** The bot that created the issue, if applicable. */
	botActor;
	/** Integration type that created this issue, if applicable. */
	integrationSourceType;
	/** The external user who requested creation of the Asks issue on behalf of the creator. */
	get asksExternalUserRequester() {
		return this._asksExternalUserRequester?.id ? new ExternalUserQuery(this._request).fetch(this._asksExternalUserRequester?.id) : void 0;
	}
	/** The ID of external user who requested creation of the asks issue on behalf of the creator. */
	get asksExternalUserRequesterId() {
		return this._asksExternalUserRequester?.id;
	}
	/** The internal user who requested creation of the Asks issue on behalf of the creator. */
	get asksRequester() {
		return this._asksRequester?.id ? new UserQuery(this._request).fetch(this._asksRequester?.id) : void 0;
	}
	/** The ID of internal user who requested creation of the asks issue on behalf of the creator. */
	get asksRequesterId() {
		return this._asksRequester?.id;
	}
	/** The user to whom the issue is assigned to. */
	get assignee() {
		return this._assignee?.id ? new UserQuery(this._request).fetch(this._assignee?.id) : void 0;
	}
	/** The ID of user to whom the issue is assigned to. */
	get assigneeId() {
		return this._assignee?.id;
	}
	/** The user who created the issue. */
	get creator() {
		return this._creator?.id ? new UserQuery(this._request).fetch(this._creator?.id) : void 0;
	}
	/** The ID of user who created the issue. */
	get creatorId() {
		return this._creator?.id;
	}
	/** The cycle that the issue is associated with. */
	get cycle() {
		return this._cycle?.id ? new CycleQuery(this._request).fetch(this._cycle?.id) : void 0;
	}
	/** The ID of cycle that the issue is associated with. */
	get cycleId() {
		return this._cycle?.id;
	}
	/** The agent user that is delegated to work on this issue. */
	get delegate() {
		return this._delegate?.id ? new UserQuery(this._request).fetch(this._delegate?.id) : void 0;
	}
	/** The ID of agent user that is delegated to work on this issue. */
	get delegateId() {
		return this._delegate?.id;
	}
	/** The external user who created the issue. */
	get externalUserCreator() {
		return this._externalUserCreator?.id ? new ExternalUserQuery(this._request).fetch(this._externalUserCreator?.id) : void 0;
	}
	/** The ID of external user who created the issue. */
	get externalUserCreatorId() {
		return this._externalUserCreator?.id;
	}
	/** The users favorite associated with this issue. */
	get favorite() {
		return this._favorite?.id ? new FavoriteQuery(this._request).fetch(this._favorite?.id) : void 0;
	}
	/** The ID of users favorite associated with this issue. */
	get favoriteId() {
		return this._favorite?.id;
	}
	/** The last template that was applied to this issue. */
	get lastAppliedTemplate() {
		return this._lastAppliedTemplate?.id ? new TemplateQuery(this._request).fetch(this._lastAppliedTemplate?.id) : void 0;
	}
	/** The ID of last template that was applied to this issue. */
	get lastAppliedTemplateId() {
		return this._lastAppliedTemplate?.id;
	}
	/** The parent of the issue. */
	get parent() {
		return this._parent?.id ? new IssueQuery(this._request).fetch(this._parent?.id) : void 0;
	}
	/** The ID of parent of the issue. */
	get parentId() {
		return this._parent?.id;
	}
	/** The project that the issue is associated with. */
	get project() {
		return this._project?.id ? new ProjectQuery(this._request).fetch(this._project?.id) : void 0;
	}
	/** The ID of project that the issue is associated with. */
	get projectId() {
		return this._project?.id;
	}
	/** The projectMilestone that the issue is associated with. */
	get projectMilestone() {
		return this._projectMilestone?.id ? new ProjectMilestoneQuery(this._request).fetch(this._projectMilestone?.id) : void 0;
	}
	/** The ID of projectmilestone that the issue is associated with. */
	get projectMilestoneId() {
		return this._projectMilestone?.id;
	}
	/** The recurring issue template that created this issue. */
	get recurringIssueTemplate() {
		return this._recurringIssueTemplate?.id ? new TemplateQuery(this._request).fetch(this._recurringIssueTemplate?.id) : void 0;
	}
	/** The ID of recurring issue template that created this issue. */
	get recurringIssueTemplateId() {
		return this._recurringIssueTemplate?.id;
	}
	/** The user who snoozed the issue. */
	get snoozedBy() {
		return this._snoozedBy?.id ? new UserQuery(this._request).fetch(this._snoozedBy?.id) : void 0;
	}
	/** The ID of user who snoozed the issue. */
	get snoozedById() {
		return this._snoozedBy?.id;
	}
	/** The comment that this issue was created from. */
	get sourceComment() {
		return this._sourceComment?.id ? new CommentQuery(this._request).fetch({ id: this._sourceComment?.id }) : void 0;
	}
	/** The ID of comment that this issue was created from. */
	get sourceCommentId() {
		return this._sourceComment?.id;
	}
	/** The workflow state that the issue is associated with. */
	get state() {
		return new WorkflowStateQuery(this._request).fetch(this._state.id);
	}
	/** The ID of workflow state that the issue is associated with. */
	get stateId() {
		return this._state?.id;
	}
	/** The team that the issue is associated with. */
	get team() {
		return new TeamQuery(this._request).fetch(this._team.id);
	}
	/** The ID of team that the issue is associated with. */
	get teamId() {
		return this._team?.id;
	}
	/** Attachments associated with the issue. */
	attachments(variables) {
		return new Issue_AttachmentsQuery(this._request, this.id, variables).fetch(variables);
	}
	/** Children of the issue. */
	children(variables) {
		return new Issue_ChildrenQuery(this._request, this.id, variables).fetch(variables);
	}
	/** Comments associated with the issue. */
	comments(variables) {
		return new Issue_CommentsQuery(this._request, this.id, variables).fetch(variables);
	}
	/** Documents associated with the issue. */
	documents(variables) {
		return new Issue_DocumentsQuery(this._request, this.id, variables).fetch(variables);
	}
	/** Attachments previously associated with the issue before being moved to another issue. */
	formerAttachments(variables) {
		return new Issue_FormerAttachmentsQuery(this._request, this.id, variables).fetch(variables);
	}
	/** Customer needs previously associated with the issue before being moved to another issue. */
	formerNeeds(variables) {
		return new Issue_FormerNeedsQuery(this._request, this.id, variables).fetch(variables);
	}
	/** History entries associated with the issue. */
	history(variables) {
		return new Issue_HistoryQuery(this._request, this.id, variables).fetch(variables);
	}
	/** Inverse relations associated with this issue. */
	inverseRelations(variables) {
		return new Issue_InverseRelationsQuery(this._request, this.id, variables).fetch(variables);
	}
	/** Labels associated with this issue. */
	labels(variables) {
		return new Issue_LabelsQuery(this._request, this.id, variables).fetch(variables);
	}
	/** Customer needs associated with the issue. */
	needs(variables) {
		return new Issue_NeedsQuery(this._request, this.id, variables).fetch(variables);
	}
	/** Relations associated with this issue. */
	relations(variables) {
		return new Issue_RelationsQuery(this._request, this.id, variables).fetch(variables);
	}
	/** Users who are subscribed to the issue. */
	subscribers(variables) {
		return new Issue_SubscribersQuery(this._request, this.id, variables).fetch(variables);
	}
	/** Archives an issue. */
	archive(variables) {
		return new ArchiveIssueMutation(this._request).fetch(this.id, variables);
	}
	/** Creates a new issue. */
	create(input) {
		return new CreateIssueMutation(this._request).fetch(input);
	}
	/** Deletes (trashes) an issue. */
	delete(variables) {
		return new DeleteIssueMutation(this._request).fetch(this.id, variables);
	}
	/** Unarchives an issue. */
	unarchive() {
		return new UnarchiveIssueMutation(this._request).fetch(this.id);
	}
	/** Updates an issue. */
	update(input) {
		return new UpdateIssueMutation(this._request).fetch(this.id, input);
	}
};
/**
* A generic payload return from entity archive mutations.
*
* @param request - function to call the graphql client
* @param data - L.IssueArchivePayloadFragment response data
*/
var IssueArchivePayload = class extends Request {
	_entity;
	constructor(request, data) {
		super(request);
		this.lastSyncId = data.lastSyncId;
		this.success = data.success;
		this._entity = data.entity ?? void 0;
	}
	/** The identifier of the last sync operation. */
	lastSyncId;
	/** Whether the operation was successful. */
	success;
	/** The archived/unarchived entity. Null if entity was deleted. */
	get entity() {
		return this._entity?.id ? new IssueQuery(this._request).fetch(this._entity?.id) : void 0;
	}
	/** The ID of archived/unarchived entity. null if entity was deleted. */
	get entityId() {
		return this._entity?.id;
	}
};
/**
* Payload for an issue assigned to you notification.
*
* @param data - L.IssueAssignedToYouNotificationWebhookPayloadFragment response data
*/
var IssueAssignedToYouNotificationWebhookPayload = class {
	constructor(data) {
		this.actorId = data.actorId ?? void 0;
		this.archivedAt = data.archivedAt ?? void 0;
		this.createdAt = data.createdAt;
		this.externalUserActorId = data.externalUserActorId ?? void 0;
		this.id = data.id;
		this.issueId = data.issueId;
		this.type = data.type;
		this.updatedAt = data.updatedAt;
		this.userId = data.userId;
		this.actor = data.actor ? new UserChildWebhookPayload(data.actor) : void 0;
		this.issue = new IssueWithDescriptionChildWebhookPayload(data.issue);
	}
	/** The ID of the actor who caused the notification. */
	actorId;
	/** The time at which the entity was archived. */
	archivedAt;
	/** The time at which the entity was created. */
	createdAt;
	/** The ID of the external user who caused the notification. */
	externalUserActorId;
	/** The ID of the entity. */
	id;
	/** The ID of the issue this notification belongs to. */
	issueId;
	/** An issue assigned to you notification type. */
	type;
	/** The time at which the entity was updated. */
	updatedAt;
	/** The ID of the user who received the notification. */
	userId;
	/** The actor who caused the notification. */
	actor;
	/** The issue this notification belongs to. */
	issue;
};
/**
* IssueBatchPayload model
*
* @param request - function to call the graphql client
* @param data - L.IssueBatchPayloadFragment response data
*/
var IssueBatchPayload = class extends Request {
	constructor(request, data) {
		super(request);
		this.lastSyncId = data.lastSyncId;
		this.success = data.success;
		this.issues = data.issues.map((node) => new Issue(request, node));
	}
	/** The identifier of the last sync operation. */
	lastSyncId;
	/** Whether the operation was successful. */
	success;
	/** The issues that were updated. */
	issues;
};
/**
* Certain properties of an issue.
*
* @param data - L.IssueChildWebhookPayloadFragment response data
*/
var IssueChildWebhookPayload = class {
	constructor(data) {
		this.id = data.id;
		this.identifier = data.identifier;
		this.teamId = data.teamId;
		this.title = data.title;
		this.url = data.url;
		this.team = new TeamChildWebhookPayload(data.team);
	}
	/** The ID of the issue. */
	id;
	/** The identifier of the issue. */
	identifier;
	/** The ID of the team that the issue belongs to. */
	teamId;
	/** The title of the issue. */
	title;
	/** The URL of the issue. */
	url;
	/** The ID of the team that the issue belongs to. */
	team;
};
/**
* Payload for an issue comment mention notification.
*
* @param data - L.IssueCommentMentionNotificationWebhookPayloadFragment response data
*/
var IssueCommentMentionNotificationWebhookPayload = class {
	constructor(data) {
		this.actorId = data.actorId ?? void 0;
		this.archivedAt = data.archivedAt ?? void 0;
		this.commentId = data.commentId;
		this.createdAt = data.createdAt;
		this.externalUserActorId = data.externalUserActorId ?? void 0;
		this.id = data.id;
		this.issueId = data.issueId;
		this.parentCommentId = data.parentCommentId ?? void 0;
		this.type = data.type;
		this.updatedAt = data.updatedAt;
		this.userId = data.userId;
		this.actor = data.actor ? new UserChildWebhookPayload(data.actor) : void 0;
		this.comment = new CommentChildWebhookPayload(data.comment);
		this.issue = new IssueWithDescriptionChildWebhookPayload(data.issue);
		this.parentComment = data.parentComment ? new CommentChildWebhookPayload(data.parentComment) : void 0;
	}
	/** The ID of the actor who caused the notification. */
	actorId;
	/** The time at which the entity was archived. */
	archivedAt;
	/** The ID of the comment this notification belongs to. */
	commentId;
	/** The time at which the entity was created. */
	createdAt;
	/** The ID of the external user who caused the notification. */
	externalUserActorId;
	/** The ID of the entity. */
	id;
	/** The ID of the issue this notification belongs to. */
	issueId;
	/** The ID of the parent comment for the comment this notification belongs to. */
	parentCommentId;
	/** An issue comment mention notification type. */
	type;
	/** The time at which the entity was updated. */
	updatedAt;
	/** The ID of the user who received the notification. */
	userId;
	/** The actor who caused the notification. */
	actor;
	/** The comment this notification belongs to. */
	comment;
	/** The issue this notification belongs to. */
	issue;
	/** The parent comment for the comment this notification belongs to. */
	parentComment;
};
/**
* Payload for an issue comment reaction notification.
*
* @param data - L.IssueCommentReactionNotificationWebhookPayloadFragment response data
*/
var IssueCommentReactionNotificationWebhookPayload = class {
	constructor(data) {
		this.actorId = data.actorId ?? void 0;
		this.archivedAt = data.archivedAt ?? void 0;
		this.commentId = data.commentId;
		this.createdAt = data.createdAt;
		this.externalUserActorId = data.externalUserActorId ?? void 0;
		this.id = data.id;
		this.issueId = data.issueId;
		this.parentCommentId = data.parentCommentId ?? void 0;
		this.reactionEmoji = data.reactionEmoji;
		this.type = data.type;
		this.updatedAt = data.updatedAt;
		this.userId = data.userId;
		this.actor = data.actor ? new UserChildWebhookPayload(data.actor) : void 0;
		this.comment = new CommentChildWebhookPayload(data.comment);
		this.issue = new IssueWithDescriptionChildWebhookPayload(data.issue);
		this.parentComment = data.parentComment ? new CommentChildWebhookPayload(data.parentComment) : void 0;
	}
	/** The ID of the actor who caused the notification. */
	actorId;
	/** The time at which the entity was archived. */
	archivedAt;
	/** The ID of the comment this notification belongs to. */
	commentId;
	/** The time at which the entity was created. */
	createdAt;
	/** The ID of the external user who caused the notification. */
	externalUserActorId;
	/** The ID of the entity. */
	id;
	/** The ID of the issue this notification belongs to. */
	issueId;
	/** The ID of the parent comment for the comment this notification belongs to. */
	parentCommentId;
	/** The emoji of the reaction this notification is for. */
	reactionEmoji;
	/** An issue comment reaction notification type. */
	type;
	/** The time at which the entity was updated. */
	updatedAt;
	/** The ID of the user who received the notification. */
	userId;
	/** The actor who caused the notification. */
	actor;
	/** The comment this notification belongs to. */
	comment;
	/** The issue this notification belongs to. */
	issue;
	/** The parent comment for the comment this notification belongs to. */
	parentComment;
};
/**
* IssueConnection model
*
* @param request - function to call the graphql client
* @param fetch - function to trigger a refetch of this IssueConnection model
* @param data - IssueConnection response data
*/
var IssueConnection = class extends Connection {
	constructor(request, fetch$1, data) {
		super(request, fetch$1, data.nodes.map((node) => new Issue(request, node)), new PageInfo(request, data.pageInfo));
	}
};
/**
* Payload for an issue emoji reaction notification.
*
* @param data - L.IssueEmojiReactionNotificationWebhookPayloadFragment response data
*/
var IssueEmojiReactionNotificationWebhookPayload = class {
	constructor(data) {
		this.actorId = data.actorId ?? void 0;
		this.archivedAt = data.archivedAt ?? void 0;
		this.createdAt = data.createdAt;
		this.externalUserActorId = data.externalUserActorId ?? void 0;
		this.id = data.id;
		this.issueId = data.issueId;
		this.reactionEmoji = data.reactionEmoji;
		this.type = data.type;
		this.updatedAt = data.updatedAt;
		this.userId = data.userId;
		this.actor = data.actor ? new UserChildWebhookPayload(data.actor) : void 0;
		this.issue = new IssueWithDescriptionChildWebhookPayload(data.issue);
	}
	/** The ID of the actor who caused the notification. */
	actorId;
	/** The time at which the entity was archived. */
	archivedAt;
	/** The time at which the entity was created. */
	createdAt;
	/** The ID of the external user who caused the notification. */
	externalUserActorId;
	/** The ID of the entity. */
	id;
	/** The ID of the issue this notification belongs to. */
	issueId;
	/** The emoji of the reaction this notification is for. */
	reactionEmoji;
	/** An issue emoji reaction notification type. */
	type;
	/** The time at which the entity was updated. */
	updatedAt;
	/** The ID of the user who received the notification. */
	userId;
	/** The actor who caused the notification. */
	actor;
	/** The issue this notification belongs to. */
	issue;
};
/**
* IssueFilterSuggestionPayload model
*
* @param request - function to call the graphql client
* @param data - L.IssueFilterSuggestionPayloadFragment response data
*/
var IssueFilterSuggestionPayload = class extends Request {
	constructor(request, data) {
		super(request);
		this.filter = data.filter ?? void 0;
		this.logId = data.logId ?? void 0;
	}
	/** The json filter that is suggested. */
	filter;
	/** The log id of the prompt, that created this filter. */
	logId;
};
/**
* A record of changes to an issue.
*
* @param request - function to call the graphql client
* @param data - L.IssueHistoryFragment response data
*/
var IssueHistory = class extends Request {
	_actor;
	_attachment;
	_fromAssignee;
	_fromCycle;
	_fromDelegate;
	_fromParent;
	_fromProject;
	_fromState;
	_fromTeam;
	_issue;
	_toAssignee;
	_toConvertedProject;
	_toCycle;
	_toDelegate;
	_toParent;
	_toProject;
	_toState;
	_toTeam;
	constructor(request, data) {
		super(request);
		this.actorId = data.actorId ?? void 0;
		this.addedLabelIds = data.addedLabelIds ?? void 0;
		this.archived = data.archived ?? void 0;
		this.archivedAt = parseDate(data.archivedAt) ?? void 0;
		this.attachmentId = data.attachmentId ?? void 0;
		this.autoArchived = data.autoArchived ?? void 0;
		this.autoClosed = data.autoClosed ?? void 0;
		this.createdAt = parseDate(data.createdAt) ?? /* @__PURE__ */ new Date();
		this.customerNeedId = data.customerNeedId ?? void 0;
		this.fromAssigneeId = data.fromAssigneeId ?? void 0;
		this.fromCycleId = data.fromCycleId ?? void 0;
		this.fromDueDate = data.fromDueDate ?? void 0;
		this.fromEstimate = data.fromEstimate ?? void 0;
		this.fromParentId = data.fromParentId ?? void 0;
		this.fromPriority = data.fromPriority ?? void 0;
		this.fromProjectId = data.fromProjectId ?? void 0;
		this.fromStateId = data.fromStateId ?? void 0;
		this.fromTeamId = data.fromTeamId ?? void 0;
		this.fromTitle = data.fromTitle ?? void 0;
		this.id = data.id;
		this.removedLabelIds = data.removedLabelIds ?? void 0;
		this.toAssigneeId = data.toAssigneeId ?? void 0;
		this.toConvertedProjectId = data.toConvertedProjectId ?? void 0;
		this.toCycleId = data.toCycleId ?? void 0;
		this.toDueDate = data.toDueDate ?? void 0;
		this.toEstimate = data.toEstimate ?? void 0;
		this.toParentId = data.toParentId ?? void 0;
		this.toPriority = data.toPriority ?? void 0;
		this.toProjectId = data.toProjectId ?? void 0;
		this.toStateId = data.toStateId ?? void 0;
		this.toTeamId = data.toTeamId ?? void 0;
		this.toTitle = data.toTitle ?? void 0;
		this.trashed = data.trashed ?? void 0;
		this.triageResponsibilityAutoAssigned = data.triageResponsibilityAutoAssigned ?? void 0;
		this.updatedAt = parseDate(data.updatedAt) ?? /* @__PURE__ */ new Date();
		this.updatedDescription = data.updatedDescription ?? void 0;
		this.botActor = data.botActor ? new ActorBot(request, data.botActor) : void 0;
		this.issueImport = data.issueImport ? new IssueImport(request, data.issueImport) : void 0;
		this.actors = data.actors ? data.actors.map((node) => new User(request, node)) : void 0;
		this.addedLabels = data.addedLabels ? data.addedLabels.map((node) => new IssueLabel(request, node)) : void 0;
		this.descriptionUpdatedBy = data.descriptionUpdatedBy ? data.descriptionUpdatedBy.map((node) => new User(request, node)) : void 0;
		this.relationChanges = data.relationChanges ? data.relationChanges.map((node) => new IssueRelationHistoryPayload(request, node)) : void 0;
		this.removedLabels = data.removedLabels ? data.removedLabels.map((node) => new IssueLabel(request, node)) : void 0;
		this.triageResponsibilityNotifiedUsers = data.triageResponsibilityNotifiedUsers ? data.triageResponsibilityNotifiedUsers.map((node) => new User(request, node)) : void 0;
		this._actor = data.actor ?? void 0;
		this._attachment = data.attachment ?? void 0;
		this._fromAssignee = data.fromAssignee ?? void 0;
		this._fromCycle = data.fromCycle ?? void 0;
		this._fromDelegate = data.fromDelegate ?? void 0;
		this._fromParent = data.fromParent ?? void 0;
		this._fromProject = data.fromProject ?? void 0;
		this._fromState = data.fromState ?? void 0;
		this._fromTeam = data.fromTeam ?? void 0;
		this._issue = data.issue;
		this._toAssignee = data.toAssignee ?? void 0;
		this._toConvertedProject = data.toConvertedProject ?? void 0;
		this._toCycle = data.toCycle ?? void 0;
		this._toDelegate = data.toDelegate ?? void 0;
		this._toParent = data.toParent ?? void 0;
		this._toProject = data.toProject ?? void 0;
		this._toState = data.toState ?? void 0;
		this._toTeam = data.toTeam ?? void 0;
	}
	/** The id of user who made these changes. If null, possibly means that the change made by an integration. */
	actorId;
	/** ID's of labels that were added. */
	addedLabelIds;
	/** Whether the issue is archived at the time of this history entry. */
	archived;
	/** The time at which the entity was archived. Null if the entity has not been archived. */
	archivedAt;
	/** The id of linked attachment. */
	attachmentId;
	/** Whether the issue was auto-archived. */
	autoArchived;
	/** Whether the issue was auto-closed. */
	autoClosed;
	/** The time at which the entity was created. */
	createdAt;
	/** The id of linked customer need. */
	customerNeedId;
	/** The id of user from whom the issue was re-assigned from. */
	fromAssigneeId;
	/** The id of previous cycle of the issue. */
	fromCycleId;
	/** What the due date was changed from. */
	fromDueDate;
	/** What the estimate was changed from. */
	fromEstimate;
	/** The id of previous parent of the issue. */
	fromParentId;
	/** What the priority was changed from. */
	fromPriority;
	/** The id of previous project of the issue. */
	fromProjectId;
	/** The id of previous workflow state of the issue. */
	fromStateId;
	/** The id of team from which the issue was moved from. */
	fromTeamId;
	/** What the title was changed from. */
	fromTitle;
	/** The unique identifier of the entity. */
	id;
	/** ID's of labels that were removed. */
	removedLabelIds;
	/** The id of user to whom the issue was assigned to. */
	toAssigneeId;
	/** The id of new project created from the issue. */
	toConvertedProjectId;
	/** The id of new cycle of the issue. */
	toCycleId;
	/** What the due date was changed to. */
	toDueDate;
	/** What the estimate was changed to. */
	toEstimate;
	/** The id of new parent of the issue. */
	toParentId;
	/** What the priority was changed to. */
	toPriority;
	/** The id of new project of the issue. */
	toProjectId;
	/** The id of new workflow state of the issue. */
	toStateId;
	/** The id of team to which the issue was moved to. */
	toTeamId;
	/** What the title was changed to. */
	toTitle;
	/** Whether the issue was trashed or un-trashed. */
	trashed;
	/** Boolean indicating if the issue was auto-assigned using the triage responsibility feature. */
	triageResponsibilityAutoAssigned;
	/**
	* The last time at which the entity was meaningfully updated. This is the same as the creation time if the entity hasn't
	*     been updated after creation.
	*/
	updatedAt;
	/** Whether the issue's description was updated. */
	updatedDescription;
	/** The actors that performed the actions. This field may be empty in the case of integrations or automations. */
	actors;
	/** The labels that were added to the issue. */
	addedLabels;
	/** The actors that edited the description of the issue, if any. */
	descriptionUpdatedBy;
	/** Changed issue relationships. */
	relationChanges;
	/** The labels that were removed from the issue. */
	removedLabels;
	/** The users that were notified of the issue. */
	triageResponsibilityNotifiedUsers;
	/** The bot that performed the action. */
	botActor;
	/** The import record. */
	issueImport;
	/** The actor that performed the actions. This field may be empty in the case of integrations or automations. */
	get actor() {
		return this._actor?.id ? new UserQuery(this._request).fetch(this._actor?.id) : void 0;
	}
	/** The linked attachment. */
	get attachment() {
		return this._attachment?.id ? new AttachmentQuery(this._request).fetch(this._attachment?.id) : void 0;
	}
	/** The user that was unassigned from the issue. */
	get fromAssignee() {
		return this._fromAssignee?.id ? new UserQuery(this._request).fetch(this._fromAssignee?.id) : void 0;
	}
	/** The cycle that the issue was moved from. */
	get fromCycle() {
		return this._fromCycle?.id ? new CycleQuery(this._request).fetch(this._fromCycle?.id) : void 0;
	}
	/** The app user from whom the issue delegation was transferred. */
	get fromDelegate() {
		return this._fromDelegate?.id ? new UserQuery(this._request).fetch(this._fromDelegate?.id) : void 0;
	}
	/** The ID of app user from whom the issue delegation was transferred. */
	get fromDelegateId() {
		return this._fromDelegate?.id;
	}
	/** The parent issue that the issue was moved from. */
	get fromParent() {
		return this._fromParent?.id ? new IssueQuery(this._request).fetch(this._fromParent?.id) : void 0;
	}
	/** The project that the issue was moved from. */
	get fromProject() {
		return this._fromProject?.id ? new ProjectQuery(this._request).fetch(this._fromProject?.id) : void 0;
	}
	/** The state that the issue was moved from. */
	get fromState() {
		return this._fromState?.id ? new WorkflowStateQuery(this._request).fetch(this._fromState?.id) : void 0;
	}
	/** The team that the issue was moved from. */
	get fromTeam() {
		return this._fromTeam?.id ? new TeamQuery(this._request).fetch(this._fromTeam?.id) : void 0;
	}
	/** The issue that was changed. */
	get issue() {
		return new IssueQuery(this._request).fetch(this._issue.id);
	}
	/** The ID of issue that was changed. */
	get issueId() {
		return this._issue?.id;
	}
	/** The user that was assigned to the issue. */
	get toAssignee() {
		return this._toAssignee?.id ? new UserQuery(this._request).fetch(this._toAssignee?.id) : void 0;
	}
	/** The new project created from the issue. */
	get toConvertedProject() {
		return this._toConvertedProject?.id ? new ProjectQuery(this._request).fetch(this._toConvertedProject?.id) : void 0;
	}
	/** The cycle that the issue was moved to. */
	get toCycle() {
		return this._toCycle?.id ? new CycleQuery(this._request).fetch(this._toCycle?.id) : void 0;
	}
	/** The app user to whom the issue delegation was transferred. */
	get toDelegate() {
		return this._toDelegate?.id ? new UserQuery(this._request).fetch(this._toDelegate?.id) : void 0;
	}
	/** The ID of app user to whom the issue delegation was transferred. */
	get toDelegateId() {
		return this._toDelegate?.id;
	}
	/** The parent issue that the issue was moved to. */
	get toParent() {
		return this._toParent?.id ? new IssueQuery(this._request).fetch(this._toParent?.id) : void 0;
	}
	/** The project that the issue was moved to. */
	get toProject() {
		return this._toProject?.id ? new ProjectQuery(this._request).fetch(this._toProject?.id) : void 0;
	}
	/** The state that the issue was moved to. */
	get toState() {
		return this._toState?.id ? new WorkflowStateQuery(this._request).fetch(this._toState?.id) : void 0;
	}
	/** The team that the issue was moved to. */
	get toTeam() {
		return this._toTeam?.id ? new TeamQuery(this._request).fetch(this._toTeam?.id) : void 0;
	}
};
/**
* IssueHistoryConnection model
*
* @param request - function to call the graphql client
* @param fetch - function to trigger a refetch of this IssueHistoryConnection model
* @param data - IssueHistoryConnection response data
*/
var IssueHistoryConnection = class extends Connection {
	constructor(request, fetch$1, data) {
		super(request, fetch$1, data.nodes.map((node) => new IssueHistory(request, node)), new PageInfo(request, data.pageInfo));
	}
};
/**
* An import job for data from an external service.
*
* @param request - function to call the graphql client
* @param data - L.IssueImportFragment response data
*/
var IssueImport = class extends Request {
	constructor(request, data) {
		super(request);
		this.archivedAt = parseDate(data.archivedAt) ?? void 0;
		this.createdAt = parseDate(data.createdAt) ?? /* @__PURE__ */ new Date();
		this.creatorId = data.creatorId ?? void 0;
		this.csvFileUrl = data.csvFileUrl ?? void 0;
		this.displayName = data.displayName;
		this.error = data.error ?? void 0;
		this.errorMetadata = data.errorMetadata ?? void 0;
		this.id = data.id;
		this.mapping = data.mapping ?? void 0;
		this.progress = data.progress ?? void 0;
		this.service = data.service;
		this.serviceMetadata = data.serviceMetadata ?? void 0;
		this.status = data.status;
		this.teamName = data.teamName ?? void 0;
		this.updatedAt = parseDate(data.updatedAt) ?? /* @__PURE__ */ new Date();
	}
	/** The time at which the entity was archived. Null if the entity has not been archived. */
	archivedAt;
	/** The time at which the entity was created. */
	createdAt;
	/** The id for the user that started the job. */
	creatorId;
	/** File URL for the uploaded CSV for the import, if there is one. */
	csvFileUrl;
	/** The display name of the import service. */
	displayName;
	/** User readable error message, if one has occurred during the import. */
	error;
	/** Error code and metadata, if one has occurred during the import. */
	errorMetadata;
	/** The unique identifier of the entity. */
	id;
	/** The data mapping configuration for the import job. */
	mapping;
	/** Current step progress in % (0-100). */
	progress;
	/** The service from which data will be imported. */
	service;
	/** Metadata related to import service. */
	serviceMetadata;
	/** The status for the import job. */
	status;
	/** New team's name in cases when teamId not set. */
	teamName;
	/**
	* The last time at which the entity was meaningfully updated. This is the same as the creation time if the entity hasn't
	*     been updated after creation.
	*/
	updatedAt;
	/** Deletes an import job. */
	delete(issueImportId) {
		return new DeleteIssueImportMutation(this._request).fetch(issueImportId);
	}
	/** Updates the mapping for the issue import. */
	update(input) {
		return new UpdateIssueImportMutation(this._request).fetch(this.id, input);
	}
};
/**
* IssueImportCheckPayload model
*
* @param request - function to call the graphql client
* @param data - L.IssueImportCheckPayloadFragment response data
*/
var IssueImportCheckPayload = class extends Request {
	constructor(request, data) {
		super(request);
		this.success = data.success;
	}
	/** Whether the operation was successful. */
	success;
};
/**
* IssueImportDeletePayload model
*
* @param request - function to call the graphql client
* @param data - L.IssueImportDeletePayloadFragment response data
*/
var IssueImportDeletePayload = class extends Request {
	constructor(request, data) {
		super(request);
		this.lastSyncId = data.lastSyncId;
		this.success = data.success;
		this.issueImport = data.issueImport ? new IssueImport(request, data.issueImport) : void 0;
	}
	/** The identifier of the last sync operation. */
	lastSyncId;
	/** Whether the operation was successful. */
	success;
	/** The import job that was deleted. */
	issueImport;
};
/**
* Whether a custom JQL query is valid or not
*
* @param request - function to call the graphql client
* @param data - L.IssueImportJqlCheckPayloadFragment response data
*/
var IssueImportJqlCheckPayload = class extends Request {
	constructor(request, data) {
		super(request);
		this.count = data.count ?? void 0;
		this.error = data.error ?? void 0;
		this.success = data.success;
	}
	/** Returns an approximate number of issues matching the JQL query, if available */
	count;
	/** An error message returned by Jira when validating the JQL query. */
	error;
	/** Returns true if the JQL query has been validated successfully, false otherwise */
	success;
};
/**
* IssueImportPayload model
*
* @param request - function to call the graphql client
* @param data - L.IssueImportPayloadFragment response data
*/
var IssueImportPayload = class extends Request {
	constructor(request, data) {
		super(request);
		this.lastSyncId = data.lastSyncId;
		this.success = data.success;
		this.issueImport = data.issueImport ? new IssueImport(request, data.issueImport) : void 0;
	}
	/** The identifier of the last sync operation. */
	lastSyncId;
	/** Whether the operation was successful. */
	success;
	/** The import job that was created or updated. */
	issueImport;
};
/**
* Whether an issue import can be synced at the end of an import or not
*
* @param request - function to call the graphql client
* @param data - L.IssueImportSyncCheckPayloadFragment response data
*/
var IssueImportSyncCheckPayload = class extends Request {
	constructor(request, data) {
		super(request);
		this.canSync = data.canSync;
		this.error = data.error ?? void 0;
	}
	/** Returns true if the import can be synced, false otherwise */
	canSync;
	/** An error message with a root cause of why the import cannot be synced */
	error;
};
/**
* Labels that can be associated with issues.
*
* @param request - function to call the graphql client
* @param data - L.IssueLabelFragment response data
*/
var IssueLabel = class extends Request {
	_creator;
	_inheritedFrom;
	_parent;
	_retiredBy;
	_team;
	constructor(request, data) {
		super(request);
		this.archivedAt = parseDate(data.archivedAt) ?? void 0;
		this.color = data.color;
		this.createdAt = parseDate(data.createdAt) ?? /* @__PURE__ */ new Date();
		this.description = data.description ?? void 0;
		this.id = data.id;
		this.isGroup = data.isGroup;
		this.lastAppliedAt = parseDate(data.lastAppliedAt) ?? void 0;
		this.name = data.name;
		this.updatedAt = parseDate(data.updatedAt) ?? /* @__PURE__ */ new Date();
		this._creator = data.creator ?? void 0;
		this._inheritedFrom = data.inheritedFrom ?? void 0;
		this._parent = data.parent ?? void 0;
		this._retiredBy = data.retiredBy ?? void 0;
		this._team = data.team ?? void 0;
	}
	/** The time at which the entity was archived. Null if the entity has not been archived. */
	archivedAt;
	/** The label's color as a HEX string. */
	color;
	/** The time at which the entity was created. */
	createdAt;
	/** The label's description. */
	description;
	/** The unique identifier of the entity. */
	id;
	/** Whether the label is a group. */
	isGroup;
	/** The date when the label was last applied to an issue or project. */
	lastAppliedAt;
	/** The label's name. */
	name;
	/**
	* The last time at which the entity was meaningfully updated. This is the same as the creation time if the entity hasn't
	*     been updated after creation.
	*/
	updatedAt;
	/** The user who created the label. */
	get creator() {
		return this._creator?.id ? new UserQuery(this._request).fetch(this._creator?.id) : void 0;
	}
	/** The ID of user who created the label. */
	get creatorId() {
		return this._creator?.id;
	}
	/** The original label inherited from. */
	get inheritedFrom() {
		return this._inheritedFrom?.id ? new IssueLabelQuery(this._request).fetch(this._inheritedFrom?.id) : void 0;
	}
	/** The ID of original label inherited from. */
	get inheritedFromId() {
		return this._inheritedFrom?.id;
	}
	get organization() {
		return new OrganizationQuery(this._request).fetch();
	}
	/** The parent label. */
	get parent() {
		return this._parent?.id ? new IssueLabelQuery(this._request).fetch(this._parent?.id) : void 0;
	}
	/** The ID of parent label. */
	get parentId() {
		return this._parent?.id;
	}
	/** The user who retired the label. */
	get retiredBy() {
		return this._retiredBy?.id ? new UserQuery(this._request).fetch(this._retiredBy?.id) : void 0;
	}
	/** The ID of user who retired the label. */
	get retiredById() {
		return this._retiredBy?.id;
	}
	/** The team that the label is associated with. If null, the label is associated with the global workspace. */
	get team() {
		return this._team?.id ? new TeamQuery(this._request).fetch(this._team?.id) : void 0;
	}
	/** The ID of team that the label is associated with. if null, the label is associated with the global workspace. */
	get teamId() {
		return this._team?.id;
	}
	/** Children of the label. */
	children(variables) {
		return new IssueLabel_ChildrenQuery(this._request, this.id, variables).fetch(variables);
	}
	/** Issues associated with the label. */
	issues(variables) {
		return new IssueLabel_IssuesQuery(this._request, this.id, variables).fetch(variables);
	}
	/** Creates a new label. */
	create(input, variables) {
		return new CreateIssueLabelMutation(this._request).fetch(input, variables);
	}
	/** Deletes an issue label. */
	delete() {
		return new DeleteIssueLabelMutation(this._request).fetch(this.id);
	}
	/** Updates a label. */
	update(input, variables) {
		return new UpdateIssueLabelMutation(this._request).fetch(this.id, input, variables);
	}
};
/**
* Certain properties of an issue label.
*
* @param data - L.IssueLabelChildWebhookPayloadFragment response data
*/
var IssueLabelChildWebhookPayload = class {
	constructor(data) {
		this.color = data.color;
		this.id = data.id;
		this.name = data.name;
		this.parentId = data.parentId ?? void 0;
	}
	/** The color of the issue label. */
	color;
	/** The ID of the issue label. */
	id;
	/** The name of the issue label. */
	name;
	/** The parent ID of the issue label. */
	parentId;
};
/**
* IssueLabelConnection model
*
* @param request - function to call the graphql client
* @param fetch - function to trigger a refetch of this IssueLabelConnection model
* @param data - IssueLabelConnection response data
*/
var IssueLabelConnection = class extends Connection {
	constructor(request, fetch$1, data) {
		super(request, fetch$1, data.nodes.map((node) => new IssueLabel(request, node)), new PageInfo(request, data.pageInfo));
	}
};
/**
* IssueLabelPayload model
*
* @param request - function to call the graphql client
* @param data - L.IssueLabelPayloadFragment response data
*/
var IssueLabelPayload = class extends Request {
	_issueLabel;
	constructor(request, data) {
		super(request);
		this.lastSyncId = data.lastSyncId;
		this.success = data.success;
		this._issueLabel = data.issueLabel;
	}
	/** The identifier of the last sync operation. */
	lastSyncId;
	/** Whether the operation was successful. */
	success;
	/** The label that was created or updated. */
	get issueLabel() {
		return new IssueLabelQuery(this._request).fetch(this._issueLabel.id);
	}
	/** The ID of label that was created or updated. */
	get issueLabelId() {
		return this._issueLabel?.id;
	}
};
/**
* Payload for an issue label webhook.
*
* @param data - L.IssueLabelWebhookPayloadFragment response data
*/
var IssueLabelWebhookPayload = class {
	constructor(data) {
		this.archivedAt = data.archivedAt ?? void 0;
		this.color = data.color;
		this.createdAt = data.createdAt;
		this.creatorId = data.creatorId ?? void 0;
		this.description = data.description ?? void 0;
		this.id = data.id;
		this.inheritedFromId = data.inheritedFromId ?? void 0;
		this.isGroup = data.isGroup;
		this.name = data.name;
		this.parentId = data.parentId ?? void 0;
		this.teamId = data.teamId ?? void 0;
		this.updatedAt = data.updatedAt;
	}
	/** The time at which the entity was archived. */
	archivedAt;
	/** The color of the issue label. */
	color;
	/** The time at which the entity was created. */
	createdAt;
	/** The creator ID of the issue label. */
	creatorId;
	/** The label's description. */
	description;
	/** The ID of the entity. */
	id;
	/** The original label inherited from. */
	inheritedFromId;
	/** Whether the label is a group. */
	isGroup;
	/** The name of the issue label. */
	name;
	/** The parent ID of the issue label. */
	parentId;
	/** The team ID of the issue label. */
	teamId;
	/** The time at which the entity was updated. */
	updatedAt;
};
/**
* Payload for an issue mention notification.
*
* @param data - L.IssueMentionNotificationWebhookPayloadFragment response data
*/
var IssueMentionNotificationWebhookPayload = class {
	constructor(data) {
		this.actorId = data.actorId ?? void 0;
		this.archivedAt = data.archivedAt ?? void 0;
		this.createdAt = data.createdAt;
		this.externalUserActorId = data.externalUserActorId ?? void 0;
		this.id = data.id;
		this.issueId = data.issueId;
		this.type = data.type;
		this.updatedAt = data.updatedAt;
		this.userId = data.userId;
		this.actor = data.actor ? new UserChildWebhookPayload(data.actor) : void 0;
		this.issue = new IssueWithDescriptionChildWebhookPayload(data.issue);
	}
	/** The ID of the actor who caused the notification. */
	actorId;
	/** The time at which the entity was archived. */
	archivedAt;
	/** The time at which the entity was created. */
	createdAt;
	/** The ID of the external user who caused the notification. */
	externalUserActorId;
	/** The ID of the entity. */
	id;
	/** The ID of the issue this notification belongs to. */
	issueId;
	/** An issue mention notification type. */
	type;
	/** The time at which the entity was updated. */
	updatedAt;
	/** The ID of the user who received the notification. */
	userId;
	/** The actor who caused the notification. */
	actor;
	/** The issue this notification belongs to. */
	issue;
};
/**
* Payload for an issue new comment notification.
*
* @param data - L.IssueNewCommentNotificationWebhookPayloadFragment response data
*/
var IssueNewCommentNotificationWebhookPayload = class {
	constructor(data) {
		this.actorId = data.actorId ?? void 0;
		this.archivedAt = data.archivedAt ?? void 0;
		this.commentId = data.commentId;
		this.createdAt = data.createdAt;
		this.externalUserActorId = data.externalUserActorId ?? void 0;
		this.id = data.id;
		this.issueId = data.issueId;
		this.parentCommentId = data.parentCommentId ?? void 0;
		this.type = data.type;
		this.updatedAt = data.updatedAt;
		this.userId = data.userId;
		this.actor = data.actor ? new UserChildWebhookPayload(data.actor) : void 0;
		this.comment = new CommentChildWebhookPayload(data.comment);
		this.issue = new IssueWithDescriptionChildWebhookPayload(data.issue);
		this.parentComment = data.parentComment ? new CommentChildWebhookPayload(data.parentComment) : void 0;
	}
	/** The ID of the actor who caused the notification. */
	actorId;
	/** The time at which the entity was archived. */
	archivedAt;
	/** The ID of the comment this notification belongs to. */
	commentId;
	/** The time at which the entity was created. */
	createdAt;
	/** The ID of the external user who caused the notification. */
	externalUserActorId;
	/** The ID of the entity. */
	id;
	/** The ID of the issue this notification belongs to. */
	issueId;
	/** The ID of the parent comment for the comment this notification belongs to. */
	parentCommentId;
	/** An issue new comment notification type. */
	type;
	/** The time at which the entity was updated. */
	updatedAt;
	/** The ID of the user who received the notification. */
	userId;
	/** The actor who caused the notification. */
	actor;
	/** The comment this notification belongs to. */
	comment;
	/** The issue this notification belongs to. */
	issue;
	/** The parent comment for the comment this notification belongs to. */
	parentComment;
};
/**
* An issue related notification.
*
* @param request - function to call the graphql client
* @param data - L.IssueNotificationFragment response data
*/
var IssueNotification = class extends Request {
	_actor;
	_comment;
	_externalUserActor;
	_issue;
	_parentComment;
	_team;
	_user;
	constructor(request, data) {
		super(request);
		this.archivedAt = parseDate(data.archivedAt) ?? void 0;
		this.commentId = data.commentId ?? void 0;
		this.createdAt = parseDate(data.createdAt) ?? /* @__PURE__ */ new Date();
		this.emailedAt = parseDate(data.emailedAt) ?? void 0;
		this.id = data.id;
		this.issueId = data.issueId;
		this.parentCommentId = data.parentCommentId ?? void 0;
		this.reactionEmoji = data.reactionEmoji ?? void 0;
		this.readAt = parseDate(data.readAt) ?? void 0;
		this.snoozedUntilAt = parseDate(data.snoozedUntilAt) ?? void 0;
		this.type = data.type;
		this.unsnoozedAt = parseDate(data.unsnoozedAt) ?? void 0;
		this.updatedAt = parseDate(data.updatedAt) ?? /* @__PURE__ */ new Date();
		this.botActor = data.botActor ? new ActorBot(request, data.botActor) : void 0;
		this.subscriptions = data.subscriptions ? data.subscriptions.map((node) => new NotificationSubscription(request, node)) : void 0;
		this.category = data.category;
		this._actor = data.actor ?? void 0;
		this._comment = data.comment ?? void 0;
		this._externalUserActor = data.externalUserActor ?? void 0;
		this._issue = data.issue;
		this._parentComment = data.parentComment ?? void 0;
		this._team = data.team;
		this._user = data.user;
	}
	/** The time at which the entity was archived. Null if the entity has not been archived. */
	archivedAt;
	/** Related comment ID. Null if the notification is not related to a comment. */
	commentId;
	/** The time at which the entity was created. */
	createdAt;
	/**
	* The time at when an email reminder for this notification was sent to the user. Null, if no email
	*     reminder has been sent.
	*/
	emailedAt;
	/** The unique identifier of the entity. */
	id;
	/** Related issue ID. */
	issueId;
	/** Related parent comment ID. Null if the notification is not related to a comment. */
	parentCommentId;
	/** Name of the reaction emoji related to the notification. */
	reactionEmoji;
	/** The time at when the user marked the notification as read. Null, if the the user hasn't read the notification */
	readAt;
	/** The time until a notification will be snoozed. After that it will appear in the inbox again. */
	snoozedUntilAt;
	/** Notification type. */
	type;
	/** The time at which a notification was unsnoozed.. */
	unsnoozedAt;
	/**
	* The last time at which the entity was meaningfully updated. This is the same as the creation time if the entity hasn't
	*     been updated after creation.
	*/
	updatedAt;
	/** The subscriptions related to the notification. */
	subscriptions;
	/** The bot that caused the notification. */
	botActor;
	/** The category of the notification. */
	category;
	/** The user that caused the notification. */
	get actor() {
		return this._actor?.id ? new UserQuery(this._request).fetch(this._actor?.id) : void 0;
	}
	/** The ID of user that caused the notification. */
	get actorId() {
		return this._actor?.id;
	}
	/** The comment related to the notification. */
	get comment() {
		return this._comment?.id ? new CommentQuery(this._request).fetch({ id: this._comment?.id }) : void 0;
	}
	/** The external user that caused the notification. */
	get externalUserActor() {
		return this._externalUserActor?.id ? new ExternalUserQuery(this._request).fetch(this._externalUserActor?.id) : void 0;
	}
	/** The ID of external user that caused the notification. */
	get externalUserActorId() {
		return this._externalUserActor?.id;
	}
	/** The issue related to the notification. */
	get issue() {
		return new IssueQuery(this._request).fetch(this._issue.id);
	}
	/** The parent comment related to the notification, if a notification is a reply comment notification. */
	get parentComment() {
		return this._parentComment?.id ? new CommentQuery(this._request).fetch({ id: this._parentComment?.id }) : void 0;
	}
	/** The team related to the issue notification. */
	get team() {
		return new TeamQuery(this._request).fetch(this._team.id);
	}
	/** The ID of team related to the issue notification. */
	get teamId() {
		return this._team?.id;
	}
	/** The user that received the notification. */
	get user() {
		return new UserQuery(this._request).fetch(this._user.id);
	}
	/** The ID of user that received the notification. */
	get userId() {
		return this._user?.id;
	}
};
/**
* IssuePayload model
*
* @param request - function to call the graphql client
* @param data - L.IssuePayloadFragment response data
*/
var IssuePayload = class extends Request {
	_issue;
	constructor(request, data) {
		super(request);
		this.lastSyncId = data.lastSyncId;
		this.success = data.success;
		this._issue = data.issue ?? void 0;
	}
	/** The identifier of the last sync operation. */
	lastSyncId;
	/** Whether the operation was successful. */
	success;
	/** The issue that was created or updated. */
	get issue() {
		return this._issue?.id ? new IssueQuery(this._request).fetch(this._issue?.id) : void 0;
	}
	/** The ID of issue that was created or updated. */
	get issueId() {
		return this._issue?.id;
	}
};
/**
* IssuePriorityValue model
*
* @param request - function to call the graphql client
* @param data - L.IssuePriorityValueFragment response data
*/
var IssuePriorityValue = class extends Request {
	constructor(request, data) {
		super(request);
		this.label = data.label;
		this.priority = data.priority;
	}
	/** Priority's label. */
	label;
	/** Priority's number value. */
	priority;
};
/**
* A relation between two issues.
*
* @param request - function to call the graphql client
* @param data - L.IssueRelationFragment response data
*/
var IssueRelation = class extends Request {
	_issue;
	_relatedIssue;
	constructor(request, data) {
		super(request);
		this.archivedAt = parseDate(data.archivedAt) ?? void 0;
		this.createdAt = parseDate(data.createdAt) ?? /* @__PURE__ */ new Date();
		this.id = data.id;
		this.type = data.type;
		this.updatedAt = parseDate(data.updatedAt) ?? /* @__PURE__ */ new Date();
		this._issue = data.issue;
		this._relatedIssue = data.relatedIssue;
	}
	/** The time at which the entity was archived. Null if the entity has not been archived. */
	archivedAt;
	/** The time at which the entity was created. */
	createdAt;
	/** The unique identifier of the entity. */
	id;
	/** The relationship of the issue with the related issue. */
	type;
	/**
	* The last time at which the entity was meaningfully updated. This is the same as the creation time if the entity hasn't
	*     been updated after creation.
	*/
	updatedAt;
	/** The issue whose relationship is being described. */
	get issue() {
		return new IssueQuery(this._request).fetch(this._issue.id);
	}
	/** The ID of issue whose relationship is being described. */
	get issueId() {
		return this._issue?.id;
	}
	/** The related issue. */
	get relatedIssue() {
		return new IssueQuery(this._request).fetch(this._relatedIssue.id);
	}
	/** The ID of related issue. */
	get relatedIssueId() {
		return this._relatedIssue?.id;
	}
	/** Creates a new issue relation. */
	create(input, variables) {
		return new CreateIssueRelationMutation(this._request).fetch(input, variables);
	}
	/** Deletes an issue relation. */
	delete() {
		return new DeleteIssueRelationMutation(this._request).fetch(this.id);
	}
	/** Updates an issue relation. */
	update(input) {
		return new UpdateIssueRelationMutation(this._request).fetch(this.id, input);
	}
};
/**
* IssueRelationConnection model
*
* @param request - function to call the graphql client
* @param fetch - function to trigger a refetch of this IssueRelationConnection model
* @param data - IssueRelationConnection response data
*/
var IssueRelationConnection = class extends Connection {
	constructor(request, fetch$1, data) {
		super(request, fetch$1, data.nodes.map((node) => new IssueRelation(request, node)), new PageInfo(request, data.pageInfo));
	}
};
/**
* Issue relation history's payload.
*
* @param request - function to call the graphql client
* @param data - L.IssueRelationHistoryPayloadFragment response data
*/
var IssueRelationHistoryPayload = class extends Request {
	constructor(request, data) {
		super(request);
		this.identifier = data.identifier;
		this.type = data.type;
	}
	/** The identifier of the related issue. */
	identifier;
	/** The type of the change. */
	type;
};
/**
* IssueRelationPayload model
*
* @param request - function to call the graphql client
* @param data - L.IssueRelationPayloadFragment response data
*/
var IssueRelationPayload = class extends Request {
	_issueRelation;
	constructor(request, data) {
		super(request);
		this.lastSyncId = data.lastSyncId;
		this.success = data.success;
		this._issueRelation = data.issueRelation;
	}
	/** The identifier of the last sync operation. */
	lastSyncId;
	/** Whether the operation was successful. */
	success;
	/** The issue relation that was created or updated. */
	get issueRelation() {
		return new IssueRelationQuery(this._request).fetch(this._issueRelation.id);
	}
	/** The ID of issue relation that was created or updated. */
	get issueRelationId() {
		return this._issueRelation?.id;
	}
};
/**
* IssueSearchPayload model
*
* @param request - function to call the graphql client
* @param data - L.IssueSearchPayloadFragment response data
*/
var IssueSearchPayload = class extends Request {
	constructor(request, data) {
		super(request);
		this.totalCount = data.totalCount;
		this.archivePayload = new ArchiveResponse(request, data.archivePayload);
		this.pageInfo = new PageInfo(request, data.pageInfo);
		this.nodes = data.nodes.map((node) => new IssueSearchResult(request, node));
	}
	/** Total number of results for query without filters applied. */
	totalCount;
	nodes;
	/** Archived entities matching the search term along with all their dependencies. */
	archivePayload;
	pageInfo;
};
/**
* IssueSearchResult model
*
* @param request - function to call the graphql client
* @param data - L.IssueSearchResultFragment response data
*/
var IssueSearchResult = class extends Request {
	_asksExternalUserRequester;
	_asksRequester;
	_assignee;
	_creator;
	_cycle;
	_delegate;
	_externalUserCreator;
	_favorite;
	_lastAppliedTemplate;
	_parent;
	_project;
	_projectMilestone;
	_recurringIssueTemplate;
	_snoozedBy;
	_sourceComment;
	_state;
	_team;
	constructor(request, data) {
		super(request);
		this.addedToCycleAt = parseDate(data.addedToCycleAt) ?? void 0;
		this.addedToProjectAt = parseDate(data.addedToProjectAt) ?? void 0;
		this.addedToTeamAt = parseDate(data.addedToTeamAt) ?? void 0;
		this.archivedAt = parseDate(data.archivedAt) ?? void 0;
		this.autoArchivedAt = parseDate(data.autoArchivedAt) ?? void 0;
		this.autoClosedAt = parseDate(data.autoClosedAt) ?? void 0;
		this.boardOrder = data.boardOrder;
		this.branchName = data.branchName;
		this.canceledAt = parseDate(data.canceledAt) ?? void 0;
		this.completedAt = parseDate(data.completedAt) ?? void 0;
		this.createdAt = parseDate(data.createdAt) ?? /* @__PURE__ */ new Date();
		this.customerTicketCount = data.customerTicketCount;
		this.description = data.description ?? void 0;
		this.dueDate = data.dueDate ?? void 0;
		this.estimate = data.estimate ?? void 0;
		this.id = data.id;
		this.identifier = data.identifier;
		this.labelIds = data.labelIds;
		this.metadata = data.metadata;
		this.number = data.number;
		this.previousIdentifiers = data.previousIdentifiers;
		this.priority = data.priority;
		this.priorityLabel = data.priorityLabel;
		this.prioritySortOrder = data.prioritySortOrder;
		this.reactionData = data.reactionData;
		this.slaBreachesAt = parseDate(data.slaBreachesAt) ?? void 0;
		this.slaHighRiskAt = parseDate(data.slaHighRiskAt) ?? void 0;
		this.slaMediumRiskAt = parseDate(data.slaMediumRiskAt) ?? void 0;
		this.slaStartedAt = parseDate(data.slaStartedAt) ?? void 0;
		this.slaType = data.slaType ?? void 0;
		this.snoozedUntilAt = parseDate(data.snoozedUntilAt) ?? void 0;
		this.sortOrder = data.sortOrder;
		this.startedAt = parseDate(data.startedAt) ?? void 0;
		this.startedTriageAt = parseDate(data.startedTriageAt) ?? void 0;
		this.subIssueSortOrder = data.subIssueSortOrder ?? void 0;
		this.title = data.title;
		this.trashed = data.trashed ?? void 0;
		this.triagedAt = parseDate(data.triagedAt) ?? void 0;
		this.updatedAt = parseDate(data.updatedAt) ?? /* @__PURE__ */ new Date();
		this.url = data.url;
		this.botActor = data.botActor ? new ActorBot(request, data.botActor) : void 0;
		this.reactions = data.reactions.map((node) => new Reaction(request, node));
		this.syncedWith = data.syncedWith ? data.syncedWith.map((node) => new ExternalEntityInfo(request, node)) : void 0;
		this.integrationSourceType = data.integrationSourceType ?? void 0;
		this._asksExternalUserRequester = data.asksExternalUserRequester ?? void 0;
		this._asksRequester = data.asksRequester ?? void 0;
		this._assignee = data.assignee ?? void 0;
		this._creator = data.creator ?? void 0;
		this._cycle = data.cycle ?? void 0;
		this._delegate = data.delegate ?? void 0;
		this._externalUserCreator = data.externalUserCreator ?? void 0;
		this._favorite = data.favorite ?? void 0;
		this._lastAppliedTemplate = data.lastAppliedTemplate ?? void 0;
		this._parent = data.parent ?? void 0;
		this._project = data.project ?? void 0;
		this._projectMilestone = data.projectMilestone ?? void 0;
		this._recurringIssueTemplate = data.recurringIssueTemplate ?? void 0;
		this._snoozedBy = data.snoozedBy ?? void 0;
		this._sourceComment = data.sourceComment ?? void 0;
		this._state = data.state;
		this._team = data.team;
	}
	/** The time at which the issue was added to a cycle. */
	addedToCycleAt;
	/** The time at which the issue was added to a project. */
	addedToProjectAt;
	/** The time at which the issue was added to a team. */
	addedToTeamAt;
	/** The time at which the entity was archived. Null if the entity has not been archived. */
	archivedAt;
	/** The time at which the issue was automatically archived by the auto pruning process. */
	autoArchivedAt;
	/** The time at which the issue was automatically closed by the auto pruning process. */
	autoClosedAt;
	/** The order of the item in its column on the board. */
	boardOrder;
	/** Suggested branch name for the issue. */
	branchName;
	/** The time at which the issue was moved into canceled state. */
	canceledAt;
	/** The time at which the issue was moved into completed state. */
	completedAt;
	/** The time at which the entity was created. */
	createdAt;
	/** Returns the number of Attachment resources which are created by customer support ticketing systems (e.g. Zendesk). */
	customerTicketCount;
	/** The issue's description in markdown format. */
	description;
	/** The date at which the issue is due. */
	dueDate;
	/** The estimate of the complexity of the issue.. */
	estimate;
	/** The unique identifier of the entity. */
	id;
	/** Issue's human readable identifier (e.g. ENG-123). */
	identifier;
	/** Id of the labels associated with this issue. */
	labelIds;
	/** Metadata related to search result. */
	metadata;
	/** The issue's unique number. */
	number;
	/** Previous identifiers of the issue if it has been moved between teams. */
	previousIdentifiers;
	/** The priority of the issue. 0 = No priority, 1 = Urgent, 2 = High, 3 = Normal, 4 = Low. */
	priority;
	/** Label for the priority. */
	priorityLabel;
	/** The order of the item in relation to other items in the organization, when ordered by priority. */
	prioritySortOrder;
	/** Emoji reaction summary, grouped by emoji type. */
	reactionData;
	/** The time at which the issue's SLA will breach. */
	slaBreachesAt;
	/** The time at which the issue's SLA will enter high risk state. */
	slaHighRiskAt;
	/** The time at which the issue's SLA will enter medium risk state. */
	slaMediumRiskAt;
	/** The time at which the issue's SLA began. */
	slaStartedAt;
	/** The type of SLA set on the issue. Calendar days or business days. */
	slaType;
	/** The time until an issue will be snoozed in Triage view. */
	snoozedUntilAt;
	/** The order of the item in relation to other items in the organization. */
	sortOrder;
	/** The time at which the issue was moved into started state. */
	startedAt;
	/** The time at which the issue entered triage. */
	startedTriageAt;
	/** The order of the item in the sub-issue list. Only set if the issue has a parent. */
	subIssueSortOrder;
	/** The issue's title. */
	title;
	/** A flag that indicates whether the issue is in the trash bin. */
	trashed;
	/** The time at which the issue left triage. */
	triagedAt;
	/**
	* The last time at which the entity was meaningfully updated. This is the same as the creation time if the entity hasn't
	*     been updated after creation.
	*/
	updatedAt;
	/** Issue URL. */
	url;
	/** Reactions associated with the issue. */
	reactions;
	/** The external services the issue is synced with. */
	syncedWith;
	/** The bot that created the issue, if applicable. */
	botActor;
	/** Integration type that created this issue, if applicable. */
	integrationSourceType;
	/** The external user who requested creation of the Asks issue on behalf of the creator. */
	get asksExternalUserRequester() {
		return this._asksExternalUserRequester?.id ? new ExternalUserQuery(this._request).fetch(this._asksExternalUserRequester?.id) : void 0;
	}
	/** The ID of external user who requested creation of the asks issue on behalf of the creator. */
	get asksExternalUserRequesterId() {
		return this._asksExternalUserRequester?.id;
	}
	/** The internal user who requested creation of the Asks issue on behalf of the creator. */
	get asksRequester() {
		return this._asksRequester?.id ? new UserQuery(this._request).fetch(this._asksRequester?.id) : void 0;
	}
	/** The ID of internal user who requested creation of the asks issue on behalf of the creator. */
	get asksRequesterId() {
		return this._asksRequester?.id;
	}
	/** The user to whom the issue is assigned to. */
	get assignee() {
		return this._assignee?.id ? new UserQuery(this._request).fetch(this._assignee?.id) : void 0;
	}
	/** The ID of user to whom the issue is assigned to. */
	get assigneeId() {
		return this._assignee?.id;
	}
	/** The user who created the issue. */
	get creator() {
		return this._creator?.id ? new UserQuery(this._request).fetch(this._creator?.id) : void 0;
	}
	/** The ID of user who created the issue. */
	get creatorId() {
		return this._creator?.id;
	}
	/** The cycle that the issue is associated with. */
	get cycle() {
		return this._cycle?.id ? new CycleQuery(this._request).fetch(this._cycle?.id) : void 0;
	}
	/** The ID of cycle that the issue is associated with. */
	get cycleId() {
		return this._cycle?.id;
	}
	/** The agent user that is delegated to work on this issue. */
	get delegate() {
		return this._delegate?.id ? new UserQuery(this._request).fetch(this._delegate?.id) : void 0;
	}
	/** The ID of agent user that is delegated to work on this issue. */
	get delegateId() {
		return this._delegate?.id;
	}
	/** The external user who created the issue. */
	get externalUserCreator() {
		return this._externalUserCreator?.id ? new ExternalUserQuery(this._request).fetch(this._externalUserCreator?.id) : void 0;
	}
	/** The ID of external user who created the issue. */
	get externalUserCreatorId() {
		return this._externalUserCreator?.id;
	}
	/** The users favorite associated with this issue. */
	get favorite() {
		return this._favorite?.id ? new FavoriteQuery(this._request).fetch(this._favorite?.id) : void 0;
	}
	/** The ID of users favorite associated with this issue. */
	get favoriteId() {
		return this._favorite?.id;
	}
	/** The last template that was applied to this issue. */
	get lastAppliedTemplate() {
		return this._lastAppliedTemplate?.id ? new TemplateQuery(this._request).fetch(this._lastAppliedTemplate?.id) : void 0;
	}
	/** The ID of last template that was applied to this issue. */
	get lastAppliedTemplateId() {
		return this._lastAppliedTemplate?.id;
	}
	/** The parent of the issue. */
	get parent() {
		return this._parent?.id ? new IssueQuery(this._request).fetch(this._parent?.id) : void 0;
	}
	/** The ID of parent of the issue. */
	get parentId() {
		return this._parent?.id;
	}
	/** The project that the issue is associated with. */
	get project() {
		return this._project?.id ? new ProjectQuery(this._request).fetch(this._project?.id) : void 0;
	}
	/** The ID of project that the issue is associated with. */
	get projectId() {
		return this._project?.id;
	}
	/** The projectMilestone that the issue is associated with. */
	get projectMilestone() {
		return this._projectMilestone?.id ? new ProjectMilestoneQuery(this._request).fetch(this._projectMilestone?.id) : void 0;
	}
	/** The ID of projectmilestone that the issue is associated with. */
	get projectMilestoneId() {
		return this._projectMilestone?.id;
	}
	/** The recurring issue template that created this issue. */
	get recurringIssueTemplate() {
		return this._recurringIssueTemplate?.id ? new TemplateQuery(this._request).fetch(this._recurringIssueTemplate?.id) : void 0;
	}
	/** The ID of recurring issue template that created this issue. */
	get recurringIssueTemplateId() {
		return this._recurringIssueTemplate?.id;
	}
	/** The user who snoozed the issue. */
	get snoozedBy() {
		return this._snoozedBy?.id ? new UserQuery(this._request).fetch(this._snoozedBy?.id) : void 0;
	}
	/** The ID of user who snoozed the issue. */
	get snoozedById() {
		return this._snoozedBy?.id;
	}
	/** The comment that this issue was created from. */
	get sourceComment() {
		return this._sourceComment?.id ? new CommentQuery(this._request).fetch({ id: this._sourceComment?.id }) : void 0;
	}
	/** The ID of comment that this issue was created from. */
	get sourceCommentId() {
		return this._sourceComment?.id;
	}
	/** The workflow state that the issue is associated with. */
	get state() {
		return new WorkflowStateQuery(this._request).fetch(this._state.id);
	}
	/** The ID of workflow state that the issue is associated with. */
	get stateId() {
		return this._state?.id;
	}
	/** The team that the issue is associated with. */
	get team() {
		return new TeamQuery(this._request).fetch(this._team.id);
	}
	/** The ID of team that the issue is associated with. */
	get teamId() {
		return this._team?.id;
	}
};
/**
* Payload for issue SLA webhook events.
*
* @param data - L.IssueSlaWebhookPayloadFragment response data
*/
var IssueSlaWebhookPayload = class {
	constructor(data) {
		this.action = data.action;
		this.createdAt = parseDate(data.createdAt) ?? /* @__PURE__ */ new Date();
		this.organizationId = data.organizationId;
		this.type = data.type;
		this.url = data.url ?? void 0;
		this.webhookId = data.webhookId;
		this.webhookTimestamp = data.webhookTimestamp;
		this.issueData = new IssueWebhookPayload(data.issueData);
	}
	/** The type of action that triggered the webhook. */
	action;
	/** The time the payload was created. */
	createdAt;
	/** ID of the organization for which the webhook belongs to. */
	organizationId;
	/** The type of resource. */
	type;
	/** URL for the issue. */
	url;
	/** The ID of the webhook that sent this event. */
	webhookId;
	/** Unix timestamp in milliseconds when the webhook was sent. */
	webhookTimestamp;
	/** The issue that the SLA event is about. */
	issueData;
};
/**
* A continuous period of time during which an issue remained in a specific workflow state.
*
* @param request - function to call the graphql client
* @param data - L.IssueStateSpanFragment response data
*/
var IssueStateSpan = class extends Request {
	_state;
	constructor(request, data) {
		super(request);
		this.endedAt = parseDate(data.endedAt) ?? void 0;
		this.id = data.id;
		this.startedAt = parseDate(data.startedAt) ?? /* @__PURE__ */ new Date();
		this.stateId = data.stateId;
		this._state = data.state ?? void 0;
	}
	/** The timestamp when the issue left this state. Null if the issue is currently in this state. */
	endedAt;
	/** The unique identifier of the state span. */
	id;
	/** The timestamp when the issue entered this state. */
	startedAt;
	/** The workflow state identifier for this span. */
	stateId;
	/** The workflow state for this span. */
	get state() {
		return this._state?.id ? new WorkflowStateQuery(this._request).fetch(this._state?.id) : void 0;
	}
};
/**
* IssueStateSpanConnection model
*
* @param request - function to call the graphql client
* @param fetch - function to trigger a refetch of this IssueStateSpanConnection model
* @param data - IssueStateSpanConnection response data
*/
var IssueStateSpanConnection = class extends Connection {
	constructor(request, fetch$1, data) {
		super(request, fetch$1, data.nodes.map((node) => new IssueStateSpan(request, node)), new PageInfo(request, data.pageInfo));
	}
};
/**
* Payload for a terminal issue status change notification.
*
* @param data - L.IssueStatusChangedNotificationWebhookPayloadFragment response data
*/
var IssueStatusChangedNotificationWebhookPayload = class {
	constructor(data) {
		this.actorId = data.actorId ?? void 0;
		this.archivedAt = data.archivedAt ?? void 0;
		this.createdAt = data.createdAt;
		this.externalUserActorId = data.externalUserActorId ?? void 0;
		this.id = data.id;
		this.issueId = data.issueId;
		this.type = data.type;
		this.updatedAt = data.updatedAt;
		this.userId = data.userId;
		this.actor = data.actor ? new UserChildWebhookPayload(data.actor) : void 0;
		this.issue = new IssueWithDescriptionChildWebhookPayload(data.issue);
	}
	/** The ID of the actor who caused the notification. */
	actorId;
	/** The time at which the entity was archived. */
	archivedAt;
	/** The time at which the entity was created. */
	createdAt;
	/** The ID of the external user who caused the notification. */
	externalUserActorId;
	/** The ID of the entity. */
	id;
	/** The ID of the issue this notification belongs to. */
	issueId;
	/** A terminal issue status change notification type. */
	type;
	/** The time at which the entity was updated. */
	updatedAt;
	/** The ID of the user who received the notification. */
	userId;
	/** The actor who caused the notification. */
	actor;
	/** The issue this notification belongs to. */
	issue;
};
/**
* IssueSuggestion model
*
* @param request - function to call the graphql client
* @param data - L.IssueSuggestionFragment response data
*/
var IssueSuggestion = class extends Request {
	_issue;
	_suggestedIssue;
	_suggestedLabel;
	_suggestedProject;
	_suggestedTeam;
	_suggestedUser;
	constructor(request, data) {
		super(request);
		this.archivedAt = parseDate(data.archivedAt) ?? void 0;
		this.createdAt = parseDate(data.createdAt) ?? /* @__PURE__ */ new Date();
		this.dismissalReason = data.dismissalReason ?? void 0;
		this.id = data.id;
		this.issueId = data.issueId;
		this.stateChangedAt = parseDate(data.stateChangedAt) ?? /* @__PURE__ */ new Date();
		this.suggestedIssueId = data.suggestedIssueId ?? void 0;
		this.suggestedLabelId = data.suggestedLabelId ?? void 0;
		this.suggestedUserId = data.suggestedUserId ?? void 0;
		this.updatedAt = parseDate(data.updatedAt) ?? /* @__PURE__ */ new Date();
		this.metadata = data.metadata ? new IssueSuggestionMetadata(request, data.metadata) : void 0;
		this.state = data.state;
		this.type = data.type;
		this._issue = data.issue;
		this._suggestedIssue = data.suggestedIssue ?? void 0;
		this._suggestedLabel = data.suggestedLabel ?? void 0;
		this._suggestedProject = data.suggestedProject ?? void 0;
		this._suggestedTeam = data.suggestedTeam ?? void 0;
		this._suggestedUser = data.suggestedUser ?? void 0;
	}
	/** The time at which the entity was archived. Null if the entity has not been archived. */
	archivedAt;
	/** The time at which the entity was created. */
	createdAt;
	dismissalReason;
	/** The unique identifier of the entity. */
	id;
	issueId;
	stateChangedAt;
	suggestedIssueId;
	suggestedLabelId;
	suggestedUserId;
	/**
	* The last time at which the entity was meaningfully updated. This is the same as the creation time if the entity hasn't
	*     been updated after creation.
	*/
	updatedAt;
	metadata;
	state;
	type;
	get issue() {
		return new IssueQuery(this._request).fetch(this._issue.id);
	}
	get suggestedIssue() {
		return this._suggestedIssue?.id ? new IssueQuery(this._request).fetch(this._suggestedIssue?.id) : void 0;
	}
	get suggestedLabel() {
		return this._suggestedLabel?.id ? new IssueLabelQuery(this._request).fetch(this._suggestedLabel?.id) : void 0;
	}
	get suggestedProject() {
		return this._suggestedProject?.id ? new ProjectQuery(this._request).fetch(this._suggestedProject?.id) : void 0;
	}
	/** The ID of suggestedProject */
	get suggestedProjectId() {
		return this._suggestedProject?.id;
	}
	get suggestedTeam() {
		return this._suggestedTeam?.id ? new TeamQuery(this._request).fetch(this._suggestedTeam?.id) : void 0;
	}
	/** The ID of suggestedTeam */
	get suggestedTeamId() {
		return this._suggestedTeam?.id;
	}
	get suggestedUser() {
		return this._suggestedUser?.id ? new UserQuery(this._request).fetch(this._suggestedUser?.id) : void 0;
	}
};
/**
* IssueSuggestionConnection model
*
* @param request - function to call the graphql client
* @param fetch - function to trigger a refetch of this IssueSuggestionConnection model
* @param data - IssueSuggestionConnection response data
*/
var IssueSuggestionConnection = class extends Connection {
	constructor(request, fetch$1, data) {
		super(request, fetch$1, data.nodes.map((node) => new IssueSuggestion(request, node)), new PageInfo(request, data.pageInfo));
	}
};
/**
* IssueSuggestionMetadata model
*
* @param request - function to call the graphql client
* @param data - L.IssueSuggestionMetadataFragment response data
*/
var IssueSuggestionMetadata = class extends Request {
	constructor(request, data) {
		super(request);
		this.appliedAutomationRuleId = data.appliedAutomationRuleId ?? void 0;
		this.classification = data.classification ?? void 0;
		this.evalLogId = data.evalLogId ?? void 0;
		this.rank = data.rank ?? void 0;
		this.reasons = data.reasons ?? void 0;
		this.score = data.score ?? void 0;
		this.variant = data.variant ?? void 0;
	}
	appliedAutomationRuleId;
	classification;
	evalLogId;
	rank;
	reasons;
	score;
	variant;
};
/**
* IssueTitleSuggestionFromCustomerRequestPayload model
*
* @param request - function to call the graphql client
* @param data - L.IssueTitleSuggestionFromCustomerRequestPayloadFragment response data
*/
var IssueTitleSuggestionFromCustomerRequestPayload = class extends Request {
	constructor(request, data) {
		super(request);
		this.lastSyncId = data.lastSyncId;
		this.title = data.title;
	}
	/** The identifier of the last sync operation. */
	lastSyncId;
	/** The suggested issue title. */
	title;
};
/**
* Payload for an issue unassignment notification.
*
* @param data - L.IssueUnassignedFromYouNotificationWebhookPayloadFragment response data
*/
var IssueUnassignedFromYouNotificationWebhookPayload = class {
	constructor(data) {
		this.actorId = data.actorId ?? void 0;
		this.archivedAt = data.archivedAt ?? void 0;
		this.createdAt = data.createdAt;
		this.externalUserActorId = data.externalUserActorId ?? void 0;
		this.id = data.id;
		this.issueId = data.issueId;
		this.type = data.type;
		this.updatedAt = data.updatedAt;
		this.userId = data.userId;
		this.actor = data.actor ? new UserChildWebhookPayload(data.actor) : void 0;
		this.issue = new IssueWithDescriptionChildWebhookPayload(data.issue);
	}
	/** The ID of the actor who caused the notification. */
	actorId;
	/** The time at which the entity was archived. */
	archivedAt;
	/** The time at which the entity was created. */
	createdAt;
	/** The ID of the external user who caused the notification. */
	externalUserActorId;
	/** The ID of the entity. */
	id;
	/** The ID of the issue this notification belongs to. */
	issueId;
	/** An issue unassignment notification type. */
	type;
	/** The time at which the entity was updated. */
	updatedAt;
	/** The ID of the user who received the notification. */
	userId;
	/** The actor who caused the notification. */
	actor;
	/** The issue this notification belongs to. */
	issue;
};
/**
* Payload for an issue webhook.
*
* @param data - L.IssueWebhookPayloadFragment response data
*/
var IssueWebhookPayload = class {
	constructor(data) {
		this.addedToCycleAt = data.addedToCycleAt ?? void 0;
		this.addedToProjectAt = data.addedToProjectAt ?? void 0;
		this.addedToTeamAt = data.addedToTeamAt ?? void 0;
		this.archivedAt = data.archivedAt ?? void 0;
		this.assigneeId = data.assigneeId ?? void 0;
		this.autoArchivedAt = data.autoArchivedAt ?? void 0;
		this.autoClosedAt = data.autoClosedAt ?? void 0;
		this.botActor = data.botActor ?? void 0;
		this.canceledAt = data.canceledAt ?? void 0;
		this.completedAt = data.completedAt ?? void 0;
		this.createdAt = data.createdAt;
		this.creatorId = data.creatorId ?? void 0;
		this.cycleId = data.cycleId ?? void 0;
		this.delegateId = data.delegateId ?? void 0;
		this.description = data.description ?? void 0;
		this.descriptionData = data.descriptionData ?? void 0;
		this.dueDate = data.dueDate ?? void 0;
		this.estimate = data.estimate ?? void 0;
		this.externalUserCreatorId = data.externalUserCreatorId ?? void 0;
		this.id = data.id;
		this.identifier = data.identifier;
		this.integrationSourceType = data.integrationSourceType ?? void 0;
		this.labelIds = data.labelIds;
		this.lastAppliedTemplateId = data.lastAppliedTemplateId ?? void 0;
		this.number = data.number;
		this.parentId = data.parentId ?? void 0;
		this.previousIdentifiers = data.previousIdentifiers;
		this.priority = data.priority;
		this.priorityLabel = data.priorityLabel;
		this.prioritySortOrder = data.prioritySortOrder;
		this.projectId = data.projectId ?? void 0;
		this.projectMilestoneId = data.projectMilestoneId ?? void 0;
		this.reactionData = data.reactionData;
		this.recurringIssueTemplateId = data.recurringIssueTemplateId ?? void 0;
		this.slaBreachesAt = data.slaBreachesAt ?? void 0;
		this.slaHighRiskAt = data.slaHighRiskAt ?? void 0;
		this.slaMediumRiskAt = data.slaMediumRiskAt ?? void 0;
		this.slaStartedAt = data.slaStartedAt ?? void 0;
		this.slaType = data.slaType ?? void 0;
		this.snoozedUntilAt = data.snoozedUntilAt ?? void 0;
		this.sortOrder = data.sortOrder;
		this.sourceCommentId = data.sourceCommentId ?? void 0;
		this.startedAt = data.startedAt ?? void 0;
		this.startedTriageAt = data.startedTriageAt ?? void 0;
		this.stateId = data.stateId;
		this.subIssueSortOrder = data.subIssueSortOrder ?? void 0;
		this.subscriberIds = data.subscriberIds;
		this.syncedWith = data.syncedWith ?? void 0;
		this.teamId = data.teamId;
		this.title = data.title;
		this.trashed = data.trashed ?? void 0;
		this.triagedAt = data.triagedAt ?? void 0;
		this.updatedAt = data.updatedAt;
		this.url = data.url;
		this.assignee = data.assignee ? new UserChildWebhookPayload(data.assignee) : void 0;
		this.creator = data.creator ? new UserChildWebhookPayload(data.creator) : void 0;
		this.cycle = data.cycle ? new CycleChildWebhookPayload(data.cycle) : void 0;
		this.delegate = data.delegate ? new UserChildWebhookPayload(data.delegate) : void 0;
		this.externalUserCreator = data.externalUserCreator ? new ExternalUserChildWebhookPayload(data.externalUserCreator) : void 0;
		this.project = data.project ? new ProjectChildWebhookPayload(data.project) : void 0;
		this.projectMilestone = data.projectMilestone ? new ProjectMilestoneChildWebhookPayload(data.projectMilestone) : void 0;
		this.state = new WorkflowStateChildWebhookPayload(data.state);
		this.team = data.team ? new TeamChildWebhookPayload(data.team) : void 0;
		this.labels = data.labels.map((node) => new IssueLabelChildWebhookPayload(node));
	}
	/** The time at which the issue was added to a cycle. */
	addedToCycleAt;
	/** The time at which the issue was added to a project. */
	addedToProjectAt;
	/** The time at which the issue was added to a team. */
	addedToTeamAt;
	/** The time at which the entity was archived. */
	archivedAt;
	/** The ID of the user that is assigned to the issue. */
	assigneeId;
	/** The time at which the issue was auto-archived. */
	autoArchivedAt;
	/** The time at which the issue was auto-closed. */
	autoClosedAt;
	/** The bot actor data for this issue. */
	botActor;
	/** The time at which the issue was canceled. */
	canceledAt;
	/** The time at which the issue was completed. */
	completedAt;
	/** The time at which the entity was created. */
	createdAt;
	/** The ID of the user that created the issue. */
	creatorId;
	/** The ID of the cycle that the issue belongs to. */
	cycleId;
	/** The ID of the agent user that the issue is delegated to. */
	delegateId;
	/** The description of the issue. */
	description;
	/** The description data of the issue. */
	descriptionData;
	/** The due date of the issue. */
	dueDate;
	/** The estimate of the complexity of the issue.. */
	estimate;
	/** The ID of the external user that created the issue. */
	externalUserCreatorId;
	/** The ID of the entity. */
	id;
	/** The identifier of the issue. */
	identifier;
	/** Integration type that created this issue, if applicable. */
	integrationSourceType;
	/** Id of the labels associated with this issue. */
	labelIds;
	/** The ID of the last template that was applied to the issue. */
	lastAppliedTemplateId;
	/** The issue's unique number. */
	number;
	/** The ID of the parent issue. */
	parentId;
	/** Previous identifiers of the issue if it has been moved between teams. */
	previousIdentifiers;
	/** The priority of the issue. 0 = No priority, 1 = Urgent, 2 = High, 3 = Normal, 4 = Low. */
	priority;
	/** The label of the issue's priority. */
	priorityLabel;
	/** The order of the item in relation to other items in the organization, when ordered by priority. */
	prioritySortOrder;
	/** The ID of the project that the issue belongs to. */
	projectId;
	/** The ID of the project milestone that the issue belongs to. */
	projectMilestoneId;
	/** The reaction data for this issue. */
	reactionData;
	/** The ID of the recurring issue template that created the issue. */
	recurringIssueTemplateId;
	/** The time at which the issue would breach its SLA. */
	slaBreachesAt;
	/** The time at which the issue would enter SLA high risk. */
	slaHighRiskAt;
	/** The time at which the issue would enter SLA medium risk. */
	slaMediumRiskAt;
	/** The time at which the issue's SLA started. */
	slaStartedAt;
	/** The type of SLA the issue is under. */
	slaType;
	/** The time until an issue will be snoozed in Triage view. */
	snoozedUntilAt;
	/** The order of the item in relation to other items in the organization. */
	sortOrder;
	/** The ID of the source comment that the issue was created from. */
	sourceCommentId;
	/** The time at which the issue was moved into started state. */
	startedAt;
	/** The time at which the issue entered triage. */
	startedTriageAt;
	/** The ID of the issue's current workflow state. */
	stateId;
	/** The order of the item in the sub-issue list. Only set if the issue has a parent. */
	subIssueSortOrder;
	/** The IDs of the users that are subscribed to the issue. */
	subscriberIds;
	/** The entity this issue is synced with. */
	syncedWith;
	/** The ID of the team that the issue belongs to. */
	teamId;
	/** The issue's title. */
	title;
	/** A flag that indicates whether the issue is in the trash bin. */
	trashed;
	/** The time at which the issue was triaged. */
	triagedAt;
	/** The time at which the entity was updated. */
	updatedAt;
	/** The URL of the issue. */
	url;
	/** The labels associated with this issue. */
	labels;
	/** The user that is assigned to the issue. */
	assignee;
	/** The user that created the issue. */
	creator;
	/** The cycle that the issue belongs to. */
	cycle;
	/** The agent user that the issue is delegated to. */
	delegate;
	/** The external user that created the issue. */
	externalUserCreator;
	/** The project that the issue belongs to. */
	project;
	/** The project milestone that the issue belongs to. */
	projectMilestone;
	/** The issue's current workflow state. */
	state;
	/** The team that the issue belongs to. */
	team;
};
/**
* Certain properties of an issue, including its description.
*
* @param data - L.IssueWithDescriptionChildWebhookPayloadFragment response data
*/
var IssueWithDescriptionChildWebhookPayload = class {
	constructor(data) {
		this.description = data.description ?? void 0;
		this.id = data.id;
		this.identifier = data.identifier;
		this.teamId = data.teamId;
		this.title = data.title;
		this.url = data.url;
		this.team = new TeamChildWebhookPayload(data.team);
	}
	/** The description of the issue. */
	description;
	/** The ID of the issue. */
	id;
	/** The identifier of the issue. */
	identifier;
	/** The ID of the team that the issue belongs to. */
	teamId;
	/** The title of the issue. */
	title;
	/** The URL of the issue. */
	url;
	/** The ID of the team that the issue belongs to. */
	team;
};
/**
* A label notification subscription.
*
* @param request - function to call the graphql client
* @param data - L.LabelNotificationSubscriptionFragment response data
*/
var LabelNotificationSubscription = class extends Request {
	_customView;
	_customer;
	_cycle;
	_initiative;
	_label;
	_project;
	_subscriber;
	_team;
	_user;
	constructor(request, data) {
		super(request);
		this.active = data.active;
		this.archivedAt = parseDate(data.archivedAt) ?? void 0;
		this.createdAt = parseDate(data.createdAt) ?? /* @__PURE__ */ new Date();
		this.id = data.id;
		this.notificationSubscriptionTypes = data.notificationSubscriptionTypes;
		this.updatedAt = parseDate(data.updatedAt) ?? /* @__PURE__ */ new Date();
		this.contextViewType = data.contextViewType ?? void 0;
		this.userContextViewType = data.userContextViewType ?? void 0;
		this._customView = data.customView ?? void 0;
		this._customer = data.customer ?? void 0;
		this._cycle = data.cycle ?? void 0;
		this._initiative = data.initiative ?? void 0;
		this._label = data.label;
		this._project = data.project ?? void 0;
		this._subscriber = data.subscriber;
		this._team = data.team ?? void 0;
		this._user = data.user ?? void 0;
	}
	/** Whether the subscription is active or not. */
	active;
	/** The time at which the entity was archived. Null if the entity has not been archived. */
	archivedAt;
	/** The time at which the entity was created. */
	createdAt;
	/** The unique identifier of the entity. */
	id;
	/** The type of subscription. */
	notificationSubscriptionTypes;
	/**
	* The last time at which the entity was meaningfully updated. This is the same as the creation time if the entity hasn't
	*     been updated after creation.
	*/
	updatedAt;
	/** The type of view to which the notification subscription context is associated with. */
	contextViewType;
	/** The type of user view to which the notification subscription context is associated with. */
	userContextViewType;
	/** The contextual custom view associated with the notification subscription. */
	get customView() {
		return this._customView?.id ? new CustomViewQuery(this._request).fetch(this._customView?.id) : void 0;
	}
	/** The ID of contextual custom view associated with the notification subscription. */
	get customViewId() {
		return this._customView?.id;
	}
	/** The customer associated with the notification subscription. */
	get customer() {
		return this._customer?.id ? new CustomerQuery(this._request).fetch(this._customer?.id) : void 0;
	}
	/** The ID of customer associated with the notification subscription. */
	get customerId() {
		return this._customer?.id;
	}
	/** The contextual cycle view associated with the notification subscription. */
	get cycle() {
		return this._cycle?.id ? new CycleQuery(this._request).fetch(this._cycle?.id) : void 0;
	}
	/** The ID of contextual cycle view associated with the notification subscription. */
	get cycleId() {
		return this._cycle?.id;
	}
	/** The contextual initiative view associated with the notification subscription. */
	get initiative() {
		return this._initiative?.id ? new InitiativeQuery(this._request).fetch(this._initiative?.id) : void 0;
	}
	/** The ID of contextual initiative view associated with the notification subscription. */
	get initiativeId() {
		return this._initiative?.id;
	}
	/** The label subscribed to. */
	get label() {
		return new IssueLabelQuery(this._request).fetch(this._label.id);
	}
	/** The ID of label subscribed to. */
	get labelId() {
		return this._label?.id;
	}
	/** The contextual project view associated with the notification subscription. */
	get project() {
		return this._project?.id ? new ProjectQuery(this._request).fetch(this._project?.id) : void 0;
	}
	/** The ID of contextual project view associated with the notification subscription. */
	get projectId() {
		return this._project?.id;
	}
	/** The user that subscribed to receive notifications. */
	get subscriber() {
		return new UserQuery(this._request).fetch(this._subscriber.id);
	}
	/** The ID of user that subscribed to receive notifications. */
	get subscriberId() {
		return this._subscriber?.id;
	}
	/** The team associated with the notification subscription. */
	get team() {
		return this._team?.id ? new TeamQuery(this._request).fetch(this._team?.id) : void 0;
	}
	/** The ID of team associated with the notification subscription. */
	get teamId() {
		return this._team?.id;
	}
	/** The user view associated with the notification subscription. */
	get user() {
		return this._user?.id ? new UserQuery(this._request).fetch(this._user?.id) : void 0;
	}
	/** The ID of user view associated with the notification subscription. */
	get userId() {
		return this._user?.id;
	}
};
/**
* LogoutResponse model
*
* @param request - function to call the graphql client
* @param data - L.LogoutResponseFragment response data
*/
var LogoutResponse = class extends Request {
	constructor(request, data) {
		super(request);
		this.success = data.success;
	}
	/** Whether the operation was successful. */
	success;
};
/**
* Node model
*
* @param request - function to call the graphql client
* @param data - L.NodeFragment response data
*/
var Node = class extends Request {
	constructor(request, data) {
		super(request);
		this.id = data.id;
	}
	/** The unique identifier of the entity. */
	id;
};
/**
* A notification sent to a user.
*
* @param request - function to call the graphql client
* @param data - L.NotificationFragment response data
*/
var Notification = class extends Request {
	_actor;
	_externalUserActor;
	_user;
	constructor(request, data) {
		super(request);
		this.archivedAt = parseDate(data.archivedAt) ?? void 0;
		this.createdAt = parseDate(data.createdAt) ?? /* @__PURE__ */ new Date();
		this.emailedAt = parseDate(data.emailedAt) ?? void 0;
		this.id = data.id;
		this.readAt = parseDate(data.readAt) ?? void 0;
		this.snoozedUntilAt = parseDate(data.snoozedUntilAt) ?? void 0;
		this.type = data.type;
		this.unsnoozedAt = parseDate(data.unsnoozedAt) ?? void 0;
		this.updatedAt = parseDate(data.updatedAt) ?? /* @__PURE__ */ new Date();
		this.botActor = data.botActor ? new ActorBot(request, data.botActor) : void 0;
		this.category = data.category;
		this._actor = data.actor ?? void 0;
		this._externalUserActor = data.externalUserActor ?? void 0;
		this._user = data.user;
	}
	/** The time at which the entity was archived. Null if the entity has not been archived. */
	archivedAt;
	/** The time at which the entity was created. */
	createdAt;
	/**
	* The time at when an email reminder for this notification was sent to the user. Null, if no email
	*     reminder has been sent.
	*/
	emailedAt;
	/** The unique identifier of the entity. */
	id;
	/** The time at when the user marked the notification as read. Null, if the the user hasn't read the notification */
	readAt;
	/** The time until a notification will be snoozed. After that it will appear in the inbox again. */
	snoozedUntilAt;
	/** Notification type. */
	type;
	/** The time at which a notification was unsnoozed.. */
	unsnoozedAt;
	/**
	* The last time at which the entity was meaningfully updated. This is the same as the creation time if the entity hasn't
	*     been updated after creation.
	*/
	updatedAt;
	/** The bot that caused the notification. */
	botActor;
	/** The category of the notification. */
	category;
	/** The user that caused the notification. */
	get actor() {
		return this._actor?.id ? new UserQuery(this._request).fetch(this._actor?.id) : void 0;
	}
	/** The ID of user that caused the notification. */
	get actorId() {
		return this._actor?.id;
	}
	/** The external user that caused the notification. */
	get externalUserActor() {
		return this._externalUserActor?.id ? new ExternalUserQuery(this._request).fetch(this._externalUserActor?.id) : void 0;
	}
	/** The ID of external user that caused the notification. */
	get externalUserActorId() {
		return this._externalUserActor?.id;
	}
	/** The user that received the notification. */
	get user() {
		return new UserQuery(this._request).fetch(this._user.id);
	}
	/** The ID of user that received the notification. */
	get userId() {
		return this._user?.id;
	}
	/** Archives a notification. */
	archive() {
		return new ArchiveNotificationMutation(this._request).fetch(this.id);
	}
	/** Unarchives a notification. */
	unarchive() {
		return new UnarchiveNotificationMutation(this._request).fetch(this.id);
	}
	/** Updates a notification. */
	update(input) {
		return new UpdateNotificationMutation(this._request).fetch(this.id, input);
	}
};
/**
* A generic payload return from entity archive mutations.
*
* @param request - function to call the graphql client
* @param data - L.NotificationArchivePayloadFragment response data
*/
var NotificationArchivePayload = class extends Request {
	constructor(request, data) {
		super(request);
		this.lastSyncId = data.lastSyncId;
		this.success = data.success;
	}
	/** The identifier of the last sync operation. */
	lastSyncId;
	/** Whether the operation was successful. */
	success;
};
/**
* NotificationBatchActionPayload model
*
* @param request - function to call the graphql client
* @param data - L.NotificationBatchActionPayloadFragment response data
*/
var NotificationBatchActionPayload = class extends Request {
	constructor(request, data) {
		super(request);
		this.lastSyncId = data.lastSyncId;
		this.success = data.success;
		this.notifications = data.notifications.map((node) => new Notification(request, node));
	}
	/** The identifier of the last sync operation. */
	lastSyncId;
	/** Whether the operation was successful. */
	success;
	/** The notifications that were updated. */
	notifications;
};
/**
* A user's notification category preferences.
*
* @param request - function to call the graphql client
* @param data - L.NotificationCategoryPreferencesFragment response data
*/
var NotificationCategoryPreferences = class extends Request {
	constructor(request, data) {
		super(request);
		this.appsAndIntegrations = new NotificationChannelPreferences(request, data.appsAndIntegrations);
		this.assignments = new NotificationChannelPreferences(request, data.assignments);
		this.commentsAndReplies = new NotificationChannelPreferences(request, data.commentsAndReplies);
		this.customers = new NotificationChannelPreferences(request, data.customers);
		this.documentChanges = new NotificationChannelPreferences(request, data.documentChanges);
		this.feed = new NotificationChannelPreferences(request, data.feed);
		this.mentions = new NotificationChannelPreferences(request, data.mentions);
		this.postsAndUpdates = new NotificationChannelPreferences(request, data.postsAndUpdates);
		this.reactions = new NotificationChannelPreferences(request, data.reactions);
		this.reminders = new NotificationChannelPreferences(request, data.reminders);
		this.reviews = new NotificationChannelPreferences(request, data.reviews);
		this.statusChanges = new NotificationChannelPreferences(request, data.statusChanges);
		this.subscriptions = new NotificationChannelPreferences(request, data.subscriptions);
		this.system = new NotificationChannelPreferences(request, data.system);
		this.triage = new NotificationChannelPreferences(request, data.triage);
	}
	/** The preferences for notifications about apps and integrations. */
	appsAndIntegrations;
	/** The preferences for notifications about assignments. */
	assignments;
	/** The preferences for notifications about comments and replies. */
	commentsAndReplies;
	/** The preferences for customer notifications. */
	customers;
	/** The preferences for notifications about document changes. */
	documentChanges;
	/** The preferences for feed summary notifications. */
	feed;
	/** The preferences for notifications about mentions. */
	mentions;
	/** The preferences for notifications about posts and updates. */
	postsAndUpdates;
	/** The preferences for notifications about reactions. */
	reactions;
	/** The preferences for notifications about reminders. */
	reminders;
	/** The preferences for notifications about reviews. */
	reviews;
	/** The preferences for notifications about status changes. */
	statusChanges;
	/** The preferences for notifications about subscriptions. */
	subscriptions;
	/** The preferences for system notifications. */
	system;
	/** The preferences for triage notifications. */
	triage;
};
/**
* A user's notification channel preferences, indicating if a channel is enabled or not
*
* @param request - function to call the graphql client
* @param data - L.NotificationChannelPreferencesFragment response data
*/
var NotificationChannelPreferences = class extends Request {
	constructor(request, data) {
		super(request);
		this.desktop = data.desktop;
		this.email = data.email;
		this.mobile = data.mobile;
		this.slack = data.slack;
	}
	/** Whether notifications are currently enabled for desktop. */
	desktop;
	/** Whether notifications are currently enabled for email. */
	email;
	/** Whether notifications are currently enabled for mobile. */
	mobile;
	/** Whether notifications are currently enabled for Slack. */
	slack;
};
/**
* NotificationConnection model
*
* @param request - function to call the graphql client
* @param fetch - function to trigger a refetch of this NotificationConnection model
* @param data - NotificationConnection response data
*/
var NotificationConnection = class extends Connection {
	constructor(request, fetch$1, data) {
		super(request, fetch$1, data.nodes.map((node) => {
			switch (node.__typename) {
				case "CustomerNeedNotification": return new CustomerNeedNotification(request, node);
				case "CustomerNotification": return new CustomerNotification(request, node);
				case "DocumentNotification": return new DocumentNotification(request, node);
				case "InitiativeNotification": return new InitiativeNotification(request, node);
				case "IssueNotification": return new IssueNotification(request, node);
				case "OauthClientApprovalNotification": return new OauthClientApprovalNotification(request, node);
				case "PostNotification": return new PostNotification(request, node);
				case "ProjectNotification": return new ProjectNotification(request, node);
				case "PullRequestNotification": return new PullRequestNotification(request, node);
				default: return new Notification(request, node);
			}
		}), new PageInfo(request, data.pageInfo));
	}
};
/**
* A user's notification delivery preferences.
*
* @param request - function to call the graphql client
* @param data - L.NotificationDeliveryPreferencesFragment response data
*/
var NotificationDeliveryPreferences = class extends Request {
	constructor(request, data) {
		super(request);
		this.mobile = data.mobile ? new NotificationDeliveryPreferencesChannel(request, data.mobile) : void 0;
	}
	/** The delivery preferences for the mobile channel. */
	mobile;
};
/**
* A user's notification delivery preferences.
*
* @param request - function to call the graphql client
* @param data - L.NotificationDeliveryPreferencesChannelFragment response data
*/
var NotificationDeliveryPreferencesChannel = class extends Request {
	constructor(request, data) {
		super(request);
		this.notificationsDisabled = data.notificationsDisabled ?? void 0;
		this.schedule = data.schedule ? new NotificationDeliveryPreferencesSchedule(request, data.schedule) : void 0;
	}
	/** [DEPRECATED] Whether notifications are enabled for this channel. Use notificationChannelPreferences instead. */
	notificationsDisabled;
	/** The schedule for notifications on this channel. */
	schedule;
};
/**
* A user's notification delivery schedule for a particular day.
*
* @param request - function to call the graphql client
* @param data - L.NotificationDeliveryPreferencesDayFragment response data
*/
var NotificationDeliveryPreferencesDay = class extends Request {
	constructor(request, data) {
		super(request);
		this.end = data.end ?? void 0;
		this.start = data.start ?? void 0;
	}
	/** The time notifications end. */
	end;
	/** The time notifications start. */
	start;
};
/**
* A user's notification delivery schedule for a particular day.
*
* @param request - function to call the graphql client
* @param data - L.NotificationDeliveryPreferencesScheduleFragment response data
*/
var NotificationDeliveryPreferencesSchedule = class extends Request {
	constructor(request, data) {
		super(request);
		this.disabled = data.disabled ?? void 0;
		this.friday = new NotificationDeliveryPreferencesDay(request, data.friday);
		this.monday = new NotificationDeliveryPreferencesDay(request, data.monday);
		this.saturday = new NotificationDeliveryPreferencesDay(request, data.saturday);
		this.sunday = new NotificationDeliveryPreferencesDay(request, data.sunday);
		this.thursday = new NotificationDeliveryPreferencesDay(request, data.thursday);
		this.tuesday = new NotificationDeliveryPreferencesDay(request, data.tuesday);
		this.wednesday = new NotificationDeliveryPreferencesDay(request, data.wednesday);
	}
	/** Whether the schedule is disabled. */
	disabled;
	/** Delivery preferences for Friday. */
	friday;
	/** Delivery preferences for Monday. */
	monday;
	/** Delivery preferences for Saturday. */
	saturday;
	/** Delivery preferences for Sunday. */
	sunday;
	/** Delivery preferences for Thursday. */
	thursday;
	/** Delivery preferences for Tuesday. */
	tuesday;
	/** Delivery preferences for Wednesday. */
	wednesday;
};
/**
* NotificationPayload model
*
* @param request - function to call the graphql client
* @param data - L.NotificationPayloadFragment response data
*/
var NotificationPayload = class extends Request {
	constructor(request, data) {
		super(request);
		this.lastSyncId = data.lastSyncId;
		this.success = data.success;
	}
	/** The identifier of the last sync operation. */
	lastSyncId;
	/** Whether the operation was successful. */
	success;
};
/**
* Notification subscriptions for models.
*
* @param request - function to call the graphql client
* @param data - L.NotificationSubscriptionFragment response data
*/
var NotificationSubscription = class extends Request {
	_customView;
	_customer;
	_cycle;
	_initiative;
	_label;
	_project;
	_subscriber;
	_team;
	_user;
	constructor(request, data) {
		super(request);
		this.active = data.active;
		this.archivedAt = parseDate(data.archivedAt) ?? void 0;
		this.createdAt = parseDate(data.createdAt) ?? /* @__PURE__ */ new Date();
		this.id = data.id;
		this.updatedAt = parseDate(data.updatedAt) ?? /* @__PURE__ */ new Date();
		this.contextViewType = data.contextViewType ?? void 0;
		this.userContextViewType = data.userContextViewType ?? void 0;
		this._customView = data.customView ?? void 0;
		this._customer = data.customer ?? void 0;
		this._cycle = data.cycle ?? void 0;
		this._initiative = data.initiative ?? void 0;
		this._label = data.label ?? void 0;
		this._project = data.project ?? void 0;
		this._subscriber = data.subscriber;
		this._team = data.team ?? void 0;
		this._user = data.user ?? void 0;
	}
	/** Whether the subscription is active or not. */
	active;
	/** The time at which the entity was archived. Null if the entity has not been archived. */
	archivedAt;
	/** The time at which the entity was created. */
	createdAt;
	/** The unique identifier of the entity. */
	id;
	/**
	* The last time at which the entity was meaningfully updated. This is the same as the creation time if the entity hasn't
	*     been updated after creation.
	*/
	updatedAt;
	/** The type of view to which the notification subscription context is associated with. */
	contextViewType;
	/** The type of user view to which the notification subscription context is associated with. */
	userContextViewType;
	/** The contextual custom view associated with the notification subscription. */
	get customView() {
		return this._customView?.id ? new CustomViewQuery(this._request).fetch(this._customView?.id) : void 0;
	}
	/** The ID of contextual custom view associated with the notification subscription. */
	get customViewId() {
		return this._customView?.id;
	}
	/** The customer associated with the notification subscription. */
	get customer() {
		return this._customer?.id ? new CustomerQuery(this._request).fetch(this._customer?.id) : void 0;
	}
	/** The ID of customer associated with the notification subscription. */
	get customerId() {
		return this._customer?.id;
	}
	/** The contextual cycle view associated with the notification subscription. */
	get cycle() {
		return this._cycle?.id ? new CycleQuery(this._request).fetch(this._cycle?.id) : void 0;
	}
	/** The ID of contextual cycle view associated with the notification subscription. */
	get cycleId() {
		return this._cycle?.id;
	}
	/** The contextual initiative view associated with the notification subscription. */
	get initiative() {
		return this._initiative?.id ? new InitiativeQuery(this._request).fetch(this._initiative?.id) : void 0;
	}
	/** The ID of contextual initiative view associated with the notification subscription. */
	get initiativeId() {
		return this._initiative?.id;
	}
	/** The contextual label view associated with the notification subscription. */
	get label() {
		return this._label?.id ? new IssueLabelQuery(this._request).fetch(this._label?.id) : void 0;
	}
	/** The ID of contextual label view associated with the notification subscription. */
	get labelId() {
		return this._label?.id;
	}
	/** The contextual project view associated with the notification subscription. */
	get project() {
		return this._project?.id ? new ProjectQuery(this._request).fetch(this._project?.id) : void 0;
	}
	/** The ID of contextual project view associated with the notification subscription. */
	get projectId() {
		return this._project?.id;
	}
	/** The user that subscribed to receive notifications. */
	get subscriber() {
		return new UserQuery(this._request).fetch(this._subscriber.id);
	}
	/** The ID of user that subscribed to receive notifications. */
	get subscriberId() {
		return this._subscriber?.id;
	}
	/** The team associated with the notification subscription. */
	get team() {
		return this._team?.id ? new TeamQuery(this._request).fetch(this._team?.id) : void 0;
	}
	/** The ID of team associated with the notification subscription. */
	get teamId() {
		return this._team?.id;
	}
	/** The user view associated with the notification subscription. */
	get user() {
		return this._user?.id ? new UserQuery(this._request).fetch(this._user?.id) : void 0;
	}
	/** The ID of user view associated with the notification subscription. */
	get userId() {
		return this._user?.id;
	}
	/** Creates a new notification subscription for a cycle, custom view, label, project or team. */
	create(input) {
		return new CreateNotificationSubscriptionMutation(this._request).fetch(input);
	}
	/** Deletes a notification subscription reference. */
	delete() {
		return new DeleteNotificationSubscriptionMutation(this._request).fetch(this.id);
	}
	/** Updates a notification subscription. */
	update(input) {
		return new UpdateNotificationSubscriptionMutation(this._request).fetch(this.id, input);
	}
};
/**
* NotificationSubscriptionConnection model
*
* @param request - function to call the graphql client
* @param fetch - function to trigger a refetch of this NotificationSubscriptionConnection model
* @param data - NotificationSubscriptionConnection response data
*/
var NotificationSubscriptionConnection = class extends Connection {
	constructor(request, fetch$1, data) {
		super(request, fetch$1, data.nodes.map((node) => {
			switch (node.__typename) {
				case "CustomViewNotificationSubscription": return new CustomViewNotificationSubscription(request, node);
				case "CustomerNotificationSubscription": return new CustomerNotificationSubscription(request, node);
				case "CycleNotificationSubscription": return new CycleNotificationSubscription(request, node);
				case "InitiativeNotificationSubscription": return new InitiativeNotificationSubscription(request, node);
				case "LabelNotificationSubscription": return new LabelNotificationSubscription(request, node);
				case "ProjectNotificationSubscription": return new ProjectNotificationSubscription(request, node);
				case "TeamNotificationSubscription": return new TeamNotificationSubscription(request, node);
				case "UserNotificationSubscription": return new UserNotificationSubscription(request, node);
				default: return new NotificationSubscription(request, node);
			}
		}), new PageInfo(request, data.pageInfo));
	}
};
/**
* NotificationSubscriptionPayload model
*
* @param request - function to call the graphql client
* @param data - L.NotificationSubscriptionPayloadFragment response data
*/
var NotificationSubscriptionPayload = class extends Request {
	constructor(request, data) {
		super(request);
		this.lastSyncId = data.lastSyncId;
		this.success = data.success;
	}
	/** The identifier of the last sync operation. */
	lastSyncId;
	/** Whether the operation was successful. */
	success;
};
/**
* Payload for OAuth app webhook events.
*
* @param data - L.OAuthAppWebhookPayloadFragment response data
*/
var OAuthAppWebhookPayload = class {
	constructor(data) {
		this.action = data.action;
		this.createdAt = parseDate(data.createdAt) ?? /* @__PURE__ */ new Date();
		this.oauthClientId = data.oauthClientId;
		this.organizationId = data.organizationId;
		this.type = data.type;
		this.webhookId = data.webhookId;
		this.webhookTimestamp = data.webhookTimestamp;
	}
	/** The type of action that triggered the webhook. */
	action;
	/** The time the payload was created. */
	createdAt;
	/** Id of the OAuth client that was revoked. */
	oauthClientId;
	/** ID of the organization for which the webhook belongs to. */
	organizationId;
	/** The type of resource. */
	type;
	/** The ID of the webhook that sent this event. */
	webhookId;
	/** Unix timestamp in milliseconds when the webhook was sent. */
	webhookTimestamp;
};
/**
* Payload for OAuth authorization webhook events.
*
* @param data - L.OAuthAuthorizationWebhookPayloadFragment response data
*/
var OAuthAuthorizationWebhookPayload = class {
	constructor(data) {
		this.action = data.action;
		this.activeTokensForUser = data.activeTokensForUser;
		this.createdAt = parseDate(data.createdAt) ?? /* @__PURE__ */ new Date();
		this.oauthClientId = data.oauthClientId;
		this.organizationId = data.organizationId;
		this.type = data.type;
		this.userId = data.userId;
		this.webhookId = data.webhookId;
		this.webhookTimestamp = data.webhookTimestamp;
		this.oauthClient = new OauthClientChildWebhookPayload(data.oauthClient);
		this.user = new UserChildWebhookPayload(data.user);
	}
	/** The type of action that triggered the webhook. */
	action;
	/** The number of currently active tokens for the user for this client. */
	activeTokensForUser;
	/** The time the payload was created. */
	createdAt;
	/** ID of the OAuth client the authorization belongs to. */
	oauthClientId;
	/** ID of the organization for which the webhook belongs to. */
	organizationId;
	/** The type of resource. */
	type;
	/** ID of the user that the authorization belongs to. */
	userId;
	/** The ID of the webhook that sent this event. */
	webhookId;
	/** Unix timestamp in milliseconds when the webhook was sent. */
	webhookTimestamp;
	/** Details of the OAuth client the authorization belongs to. */
	oauthClient;
	/** Details of the user that the authorization belongs to. */
	user;
};
/**
* OAuth client actor payload for webhooks.
*
* @param data - L.OauthClientActorWebhookPayloadFragment response data
*/
var OauthClientActorWebhookPayload = class {
	constructor(data) {
		this.id = data.id;
		this.name = data.name;
		this.type = data.type;
	}
	/** The ID of the OAuth client. */
	id;
	/** The name of the OAuth client. */
	name;
	/** The type of actor. */
	type;
};
/**
* Request to install OAuth clients on organizations and the response to the request.
*
* @param request - function to call the graphql client
* @param data - L.OauthClientApprovalFragment response data
*/
var OauthClientApproval = class extends Request {
	constructor(request, data) {
		super(request);
		this.archivedAt = parseDate(data.archivedAt) ?? void 0;
		this.createdAt = parseDate(data.createdAt) ?? /* @__PURE__ */ new Date();
		this.denyReason = data.denyReason ?? void 0;
		this.id = data.id;
		this.newlyRequestedScopes = data.newlyRequestedScopes ?? void 0;
		this.oauthClientId = data.oauthClientId;
		this.requestReason = data.requestReason ?? void 0;
		this.requesterId = data.requesterId;
		this.responderId = data.responderId ?? void 0;
		this.scopes = data.scopes;
		this.updatedAt = parseDate(data.updatedAt) ?? /* @__PURE__ */ new Date();
		this.status = data.status;
	}
	/** The time at which the entity was archived. Null if the entity has not been archived. */
	archivedAt;
	/** The time at which the entity was created. */
	createdAt;
	/** The reason the request for the OAuth client approval was denied. */
	denyReason;
	/** The unique identifier of the entity. */
	id;
	/** New scopes that were requested for approval after the initial request. */
	newlyRequestedScopes;
	/** The uuid of the OAuth client being requested for installation. */
	oauthClientId;
	/** The reason the person wants to install this OAuth client. */
	requestReason;
	/** The person who requested installing the OAuth client. */
	requesterId;
	/** The person who responded to the request to install the OAuth client. */
	responderId;
	/** The scopes the app has been approved for. */
	scopes;
	/**
	* The last time at which the entity was meaningfully updated. This is the same as the creation time if the entity hasn't
	*     been updated after creation.
	*/
	updatedAt;
	/** The status for the OAuth client approval request. */
	status;
};
/**
* An oauth client approval related notification.
*
* @param request - function to call the graphql client
* @param data - L.OauthClientApprovalNotificationFragment response data
*/
var OauthClientApprovalNotification = class extends Request {
	_actor;
	_externalUserActor;
	_user;
	constructor(request, data) {
		super(request);
		this.archivedAt = parseDate(data.archivedAt) ?? void 0;
		this.createdAt = parseDate(data.createdAt) ?? /* @__PURE__ */ new Date();
		this.emailedAt = parseDate(data.emailedAt) ?? void 0;
		this.id = data.id;
		this.oauthClientApprovalId = data.oauthClientApprovalId;
		this.readAt = parseDate(data.readAt) ?? void 0;
		this.snoozedUntilAt = parseDate(data.snoozedUntilAt) ?? void 0;
		this.type = data.type;
		this.unsnoozedAt = parseDate(data.unsnoozedAt) ?? void 0;
		this.updatedAt = parseDate(data.updatedAt) ?? /* @__PURE__ */ new Date();
		this.botActor = data.botActor ? new ActorBot(request, data.botActor) : void 0;
		this.oauthClientApproval = new OauthClientApproval(request, data.oauthClientApproval);
		this.category = data.category;
		this._actor = data.actor ?? void 0;
		this._externalUserActor = data.externalUserActor ?? void 0;
		this._user = data.user;
	}
	/** The time at which the entity was archived. Null if the entity has not been archived. */
	archivedAt;
	/** The time at which the entity was created. */
	createdAt;
	/**
	* The time at when an email reminder for this notification was sent to the user. Null, if no email
	*     reminder has been sent.
	*/
	emailedAt;
	/** The unique identifier of the entity. */
	id;
	/** Related OAuth client approval request ID. */
	oauthClientApprovalId;
	/** The time at when the user marked the notification as read. Null, if the the user hasn't read the notification */
	readAt;
	/** The time until a notification will be snoozed. After that it will appear in the inbox again. */
	snoozedUntilAt;
	/** Notification type. */
	type;
	/** The time at which a notification was unsnoozed.. */
	unsnoozedAt;
	/**
	* The last time at which the entity was meaningfully updated. This is the same as the creation time if the entity hasn't
	*     been updated after creation.
	*/
	updatedAt;
	/** The bot that caused the notification. */
	botActor;
	/** The OAuth client approval request related to the notification. */
	oauthClientApproval;
	/** The category of the notification. */
	category;
	/** The user that caused the notification. */
	get actor() {
		return this._actor?.id ? new UserQuery(this._request).fetch(this._actor?.id) : void 0;
	}
	/** The ID of user that caused the notification. */
	get actorId() {
		return this._actor?.id;
	}
	/** The external user that caused the notification. */
	get externalUserActor() {
		return this._externalUserActor?.id ? new ExternalUserQuery(this._request).fetch(this._externalUserActor?.id) : void 0;
	}
	/** The ID of external user that caused the notification. */
	get externalUserActorId() {
		return this._externalUserActor?.id;
	}
	/** The user that received the notification. */
	get user() {
		return new UserQuery(this._request).fetch(this._user.id);
	}
	/** The ID of user that received the notification. */
	get userId() {
		return this._user?.id;
	}
};
/**
* Certain properties of an OAuth client.
*
* @param data - L.OauthClientChildWebhookPayloadFragment response data
*/
var OauthClientChildWebhookPayload = class {
	constructor(data) {
		this.id = data.id;
		this.name = data.name;
	}
	/** The ID of the OAuth client. */
	id;
	/** The name of the OAuth client. */
	name;
};
/**
* An organization. Organizations are root-level objects that contain user accounts and teams.
*
* @param request - function to call the graphql client
* @param data - L.OrganizationFragment response data
*/
var Organization = class extends Request {
	constructor(request, data) {
		super(request);
		this.aiDiscussionSummariesEnabled = data.aiDiscussionSummariesEnabled;
		this.aiThreadSummariesEnabled = data.aiThreadSummariesEnabled;
		this.allowMembersToInvite = data.allowMembersToInvite ?? void 0;
		this.allowedAuthServices = data.allowedAuthServices;
		this.allowedFileUploadContentTypes = data.allowedFileUploadContentTypes ?? void 0;
		this.archivedAt = parseDate(data.archivedAt) ?? void 0;
		this.createdAt = parseDate(data.createdAt) ?? /* @__PURE__ */ new Date();
		this.createdIssueCount = data.createdIssueCount;
		this.customerCount = data.customerCount;
		this.customersConfiguration = data.customersConfiguration;
		this.customersEnabled = data.customersEnabled;
		this.deletionRequestedAt = parseDate(data.deletionRequestedAt) ?? void 0;
		this.feedEnabled = data.feedEnabled;
		this.fiscalYearStartMonth = data.fiscalYearStartMonth;
		this.gitBranchFormat = data.gitBranchFormat ?? void 0;
		this.gitLinkbackDescriptionsEnabled = data.gitLinkbackDescriptionsEnabled;
		this.gitLinkbackMessagesEnabled = data.gitLinkbackMessagesEnabled;
		this.gitPublicLinkbackMessagesEnabled = data.gitPublicLinkbackMessagesEnabled;
		this.hipaaComplianceEnabled = data.hipaaComplianceEnabled;
		this.id = data.id;
		this.initiativeUpdateReminderFrequencyInWeeks = data.initiativeUpdateReminderFrequencyInWeeks ?? void 0;
		this.initiativeUpdateRemindersHour = data.initiativeUpdateRemindersHour;
		this.logoUrl = data.logoUrl ?? void 0;
		this.name = data.name;
		this.periodUploadVolume = data.periodUploadVolume;
		this.previousUrlKeys = data.previousUrlKeys;
		this.projectUpdateReminderFrequencyInWeeks = data.projectUpdateReminderFrequencyInWeeks ?? void 0;
		this.projectUpdateRemindersHour = data.projectUpdateRemindersHour;
		this.restrictLabelManagementToAdmins = data.restrictLabelManagementToAdmins ?? void 0;
		this.restrictTeamCreationToAdmins = data.restrictTeamCreationToAdmins ?? void 0;
		this.roadmapEnabled = data.roadmapEnabled;
		this.samlEnabled = data.samlEnabled;
		this.scimEnabled = data.scimEnabled;
		this.securitySettings = data.securitySettings;
		this.trialEndsAt = parseDate(data.trialEndsAt) ?? void 0;
		this.trialStartsAt = parseDate(data.trialStartsAt) ?? void 0;
		this.updatedAt = parseDate(data.updatedAt) ?? /* @__PURE__ */ new Date();
		this.urlKey = data.urlKey;
		this.userCount = data.userCount;
		this.subscription = data.subscription ? new PaidSubscription(request, data.subscription) : void 0;
		this.ipRestrictions = data.ipRestrictions ? data.ipRestrictions.map((node) => new OrganizationIpRestriction(request, node)) : void 0;
		this.projectStatuses = data.projectStatuses.map((node) => new ProjectStatus(request, node));
		this.defaultFeedSummarySchedule = data.defaultFeedSummarySchedule ?? void 0;
		this.initiativeUpdateRemindersDay = data.initiativeUpdateRemindersDay;
		this.projectUpdateRemindersDay = data.projectUpdateRemindersDay;
		this.projectUpdatesReminderFrequency = data.projectUpdatesReminderFrequency;
		this.releaseChannel = data.releaseChannel;
		this.slaDayCount = data.slaDayCount;
	}
	/** Whether the organization has enabled AI discussion summaries for issues. */
	aiDiscussionSummariesEnabled;
	/** Whether the organization has enabled resolved thread AI summaries. */
	aiThreadSummariesEnabled;
	/** [DEPRECATED] Whether member users are allowed to send invites. */
	allowMembersToInvite;
	/** Allowed authentication providers, empty array means all are allowed. */
	allowedAuthServices;
	/** Allowed file upload content types */
	allowedFileUploadContentTypes;
	/** The time at which the entity was archived. Null if the entity has not been archived. */
	archivedAt;
	/** The time at which the entity was created. */
	createdAt;
	/** Aproximate number of issues in the organization, including archived ones. */
	createdIssueCount;
	/** Number of customers in the organization. */
	customerCount;
	/** Configuration settings for the Customers feature. */
	customersConfiguration;
	/** Whether the organization is using Customers. */
	customersEnabled;
	/** The time at which deletion of the organization was requested. */
	deletionRequestedAt;
	/** Whether the organization has enabled the feed feature. */
	feedEnabled;
	/** The month at which the fiscal year starts. Defaults to January (0). */
	fiscalYearStartMonth;
	/** How git branches are formatted. If null, default formatting will be used. */
	gitBranchFormat;
	/** Whether issue descriptions should be included in Git integration linkback messages. */
	gitLinkbackDescriptionsEnabled;
	/** Whether the Git integration linkback messages should be sent to private repositories. */
	gitLinkbackMessagesEnabled;
	/** Whether the Git integration linkback messages should be sent to public repositories. */
	gitPublicLinkbackMessagesEnabled;
	/** Whether HIPAA compliance is enabled for organization. */
	hipaaComplianceEnabled;
	/** The unique identifier of the entity. */
	id;
	/** The n-weekly frequency at which to prompt for initiative updates. When not set, reminders are off. */
	initiativeUpdateReminderFrequencyInWeeks;
	/** The hour at which to prompt for initiative updates. */
	initiativeUpdateRemindersHour;
	/** The organization's logo URL. */
	logoUrl;
	/** The organization's name. */
	name;
	/** Rolling 30-day total upload volume for the organization, in megabytes. */
	periodUploadVolume;
	/** Previously used URL keys for the organization (last 3 are kept and redirected). */
	previousUrlKeys;
	/** The n-weekly frequency at which to prompt for project updates. When not set, reminders are off. */
	projectUpdateReminderFrequencyInWeeks;
	/** The hour at which to prompt for project updates. */
	projectUpdateRemindersHour;
	/** [DEPRECATED] Whether workspace label creation, update, and deletion is restricted to admins. */
	restrictLabelManagementToAdmins;
	/** [DEPRECATED] Whether team creation is restricted to admins. */
	restrictTeamCreationToAdmins;
	/** Whether the organization is using a roadmap. */
	roadmapEnabled;
	/** Whether SAML authentication is enabled for organization. */
	samlEnabled;
	/** Whether SCIM provisioning is enabled for organization. */
	scimEnabled;
	/** Security settings for the organization. */
	securitySettings;
	/** The time at which the trial will end. */
	trialEndsAt;
	/** The time at which the trial started. */
	trialStartsAt;
	/**
	* The last time at which the entity was meaningfully updated. This is the same as the creation time if the entity hasn't
	*     been updated after creation.
	*/
	updatedAt;
	/** The organization's unique URL key. */
	urlKey;
	/** Number of active users in the organization. */
	userCount;
	/** IP restriction configurations. */
	ipRestrictions;
	/** The organization's project statuses. */
	projectStatuses;
	/** The organization's subscription to a paid plan. */
	subscription;
	/** Default schedule for how often feed summaries are generated. */
	defaultFeedSummarySchedule;
	/** The day at which to prompt for initiative updates. */
	initiativeUpdateRemindersDay;
	/** The day at which to prompt for project updates. */
	projectUpdateRemindersDay;
	/** [DEPRECATED] The frequency at which to prompt for project updates. */
	projectUpdatesReminderFrequency;
	/** The feature release channel the organization belongs to. */
	releaseChannel;
	/** [DEPRECATED] Which day count to use for SLA calculations. */
	slaDayCount;
	/** Integrations associated with the organization. */
	integrations(variables) {
		return new Organization_IntegrationsQuery(this._request, variables).fetch(variables);
	}
	/** Labels associated with the organization. */
	labels(variables) {
		return new Organization_LabelsQuery(this._request, variables).fetch(variables);
	}
	/** Project labels associated with the organization. */
	projectLabels(variables) {
		return new Organization_ProjectLabelsQuery(this._request, variables).fetch(variables);
	}
	/** Teams associated with the organization. */
	teams(variables) {
		return new Organization_TeamsQuery(this._request, variables).fetch(variables);
	}
	/** Templates associated with the organization. */
	templates(variables) {
		return new Organization_TemplatesQuery(this._request, variables).fetch(variables);
	}
	/** Users associated with the organization. */
	users(variables) {
		return new Organization_UsersQuery(this._request, variables).fetch(variables);
	}
	/** Deletes an organization. */
	delete(input) {
		return new DeleteOrganizationMutation(this._request).fetch(input);
	}
	/** Updates the user's organization. */
	update(input) {
		return new UpdateOrganizationMutation(this._request).fetch(input);
	}
};
/**
* OrganizationAcceptedOrExpiredInviteDetailsPayload model
*
* @param request - function to call the graphql client
* @param data - L.OrganizationAcceptedOrExpiredInviteDetailsPayloadFragment response data
*/
var OrganizationAcceptedOrExpiredInviteDetailsPayload = class extends Request {
	constructor(request, data) {
		super(request);
		this.status = data.status;
	}
	/** The status of the invite. */
	status;
};
/**
* OrganizationCancelDeletePayload model
*
* @param request - function to call the graphql client
* @param data - L.OrganizationCancelDeletePayloadFragment response data
*/
var OrganizationCancelDeletePayload = class extends Request {
	constructor(request, data) {
		super(request);
		this.success = data.success;
	}
	/** Whether the operation was successful. */
	success;
};
/**
* OrganizationDeletePayload model
*
* @param request - function to call the graphql client
* @param data - L.OrganizationDeletePayloadFragment response data
*/
var OrganizationDeletePayload = class extends Request {
	constructor(request, data) {
		super(request);
		this.success = data.success;
	}
	/** Whether the operation was successful. */
	success;
};
/**
* Defines the use of a domain by an organization.
*
* @param request - function to call the graphql client
* @param data - L.OrganizationDomainFragment response data
*/
var OrganizationDomain = class extends Request {
	_creator;
	constructor(request, data) {
		super(request);
		this.archivedAt = parseDate(data.archivedAt) ?? void 0;
		this.claimed = data.claimed ?? void 0;
		this.createdAt = parseDate(data.createdAt) ?? /* @__PURE__ */ new Date();
		this.disableOrganizationCreation = data.disableOrganizationCreation ?? void 0;
		this.id = data.id;
		this.name = data.name;
		this.updatedAt = parseDate(data.updatedAt) ?? /* @__PURE__ */ new Date();
		this.verificationEmail = data.verificationEmail ?? void 0;
		this.verified = data.verified;
		this.identityProvider = data.identityProvider ? new IdentityProvider(request, data.identityProvider) : void 0;
		this.authType = data.authType;
		this._creator = data.creator ?? void 0;
	}
	/** The time at which the entity was archived. Null if the entity has not been archived. */
	archivedAt;
	/** Whether the domains was claimed by the organization through DNS verification. */
	claimed;
	/** The time at which the entity was created. */
	createdAt;
	/** Prevent users with this domain to create new workspaces. */
	disableOrganizationCreation;
	/** The unique identifier of the entity. */
	id;
	/** Domain name. */
	name;
	/**
	* The last time at which the entity was meaningfully updated. This is the same as the creation time if the entity hasn't
	*     been updated after creation.
	*/
	updatedAt;
	/** E-mail used to verify this domain. */
	verificationEmail;
	/** Is this domain verified. */
	verified;
	/** The identity provider the domain belongs to. */
	identityProvider;
	/** What type of auth is the domain used for. */
	authType;
	/** The user who added the domain. */
	get creator() {
		return this._creator?.id ? new UserQuery(this._request).fetch(this._creator?.id) : void 0;
	}
	/** The ID of user who added the domain. */
	get creatorId() {
		return this._creator?.id;
	}
	/** Deletes a domain. */
	delete() {
		return new DeleteOrganizationDomainMutation(this._request).fetch(this.id);
	}
};
/**
* OrganizationExistsPayload model
*
* @param request - function to call the graphql client
* @param data - L.OrganizationExistsPayloadFragment response data
*/
var OrganizationExistsPayload = class extends Request {
	constructor(request, data) {
		super(request);
		this.exists = data.exists;
		this.success = data.success;
	}
	/** Whether the organization exists. */
	exists;
	/** Whether the operation was successful. */
	success;
};
/**
* An invitation to the organization that has been sent via email.
*
* @param request - function to call the graphql client
* @param data - L.OrganizationInviteFragment response data
*/
var OrganizationInvite = class extends Request {
	_invitee;
	_inviter;
	constructor(request, data) {
		super(request);
		this.acceptedAt = parseDate(data.acceptedAt) ?? void 0;
		this.archivedAt = parseDate(data.archivedAt) ?? void 0;
		this.createdAt = parseDate(data.createdAt) ?? /* @__PURE__ */ new Date();
		this.email = data.email;
		this.expiresAt = parseDate(data.expiresAt) ?? void 0;
		this.external = data.external;
		this.id = data.id;
		this.metadata = data.metadata ?? void 0;
		this.updatedAt = parseDate(data.updatedAt) ?? /* @__PURE__ */ new Date();
		this.role = data.role;
		this._invitee = data.invitee ?? void 0;
		this._inviter = data.inviter;
	}
	/** The time at which the invite was accepted. Null, if the invite hasn't been accepted. */
	acceptedAt;
	/** The time at which the entity was archived. Null if the entity has not been archived. */
	archivedAt;
	/** The time at which the entity was created. */
	createdAt;
	/** The invitees email address. */
	email;
	/** The time at which the invite will be expiring. Null, if the invite shouldn't expire. */
	expiresAt;
	/** The invite was sent to external address. */
	external;
	/** The unique identifier of the entity. */
	id;
	/** Extra metadata associated with the organization invite. */
	metadata;
	/**
	* The last time at which the entity was meaningfully updated. This is the same as the creation time if the entity hasn't
	*     been updated after creation.
	*/
	updatedAt;
	/** The user role that the invitee will receive upon accepting the invite. */
	role;
	/** The user who has accepted the invite. Null, if the invite hasn't been accepted. */
	get invitee() {
		return this._invitee?.id ? new UserQuery(this._request).fetch(this._invitee?.id) : void 0;
	}
	/** The ID of user who has accepted the invite. null, if the invite hasn't been accepted. */
	get inviteeId() {
		return this._invitee?.id;
	}
	/** The user who created the invitation. */
	get inviter() {
		return new UserQuery(this._request).fetch(this._inviter.id);
	}
	/** The ID of user who created the invitation. */
	get inviterId() {
		return this._inviter?.id;
	}
	/** The organization that the invite is associated with. */
	get organization() {
		return new OrganizationQuery(this._request).fetch();
	}
	/** Creates a new organization invite. */
	create(input) {
		return new CreateOrganizationInviteMutation(this._request).fetch(input);
	}
	/** Deletes an organization invite. */
	delete() {
		return new DeleteOrganizationInviteMutation(this._request).fetch(this.id);
	}
	/** Updates an organization invite. */
	update(input) {
		return new UpdateOrganizationInviteMutation(this._request).fetch(this.id, input);
	}
};
/**
* OrganizationInviteConnection model
*
* @param request - function to call the graphql client
* @param fetch - function to trigger a refetch of this OrganizationInviteConnection model
* @param data - OrganizationInviteConnection response data
*/
var OrganizationInviteConnection = class extends Connection {
	constructor(request, fetch$1, data) {
		super(request, fetch$1, data.nodes.map((node) => new OrganizationInvite(request, node)), new PageInfo(request, data.pageInfo));
	}
};
/**
* OrganizationInviteFullDetailsPayload model
*
* @param request - function to call the graphql client
* @param data - L.OrganizationInviteFullDetailsPayloadFragment response data
*/
var OrganizationInviteFullDetailsPayload = class extends Request {
	constructor(request, data) {
		super(request);
		this.accepted = data.accepted;
		this.allowedAuthServices = data.allowedAuthServices;
		this.createdAt = parseDate(data.createdAt) ?? /* @__PURE__ */ new Date();
		this.email = data.email;
		this.expired = data.expired;
		this.inviter = data.inviter;
		this.organizationId = data.organizationId;
		this.organizationLogoUrl = data.organizationLogoUrl ?? void 0;
		this.organizationName = data.organizationName;
		this.role = data.role;
		this.status = data.status;
	}
	/** Whether the invite has already been accepted. */
	accepted;
	/** Allowed authentication providers, empty array means all are allowed. */
	allowedAuthServices;
	/** When the invite was created. */
	createdAt;
	/** The email of the invitee. */
	email;
	/** Whether the invite has expired. */
	expired;
	/** The name of the inviter. */
	inviter;
	/** ID of the workspace the invite is for. */
	organizationId;
	/** URL of the workspace logo the invite is for. */
	organizationLogoUrl;
	/** Name of the workspace the invite is for. */
	organizationName;
	/** What user role the invite should grant. */
	role;
	/** The status of the invite. */
	status;
};
/**
* OrganizationInvitePayload model
*
* @param request - function to call the graphql client
* @param data - L.OrganizationInvitePayloadFragment response data
*/
var OrganizationInvitePayload = class extends Request {
	_organizationInvite;
	constructor(request, data) {
		super(request);
		this.lastSyncId = data.lastSyncId;
		this.success = data.success;
		this._organizationInvite = data.organizationInvite;
	}
	/** The identifier of the last sync operation. */
	lastSyncId;
	/** Whether the operation was successful. */
	success;
	/** The organization invite that was created or updated. */
	get organizationInvite() {
		return new OrganizationInviteQuery(this._request).fetch(this._organizationInvite.id);
	}
	/** The ID of organization invite that was created or updated. */
	get organizationInviteId() {
		return this._organizationInvite?.id;
	}
};
/**
* OrganizationIpRestriction model
*
* @param request - function to call the graphql client
* @param data - L.OrganizationIpRestrictionFragment response data
*/
var OrganizationIpRestriction = class extends Request {
	constructor(request, data) {
		super(request);
		this.description = data.description ?? void 0;
		this.enabled = data.enabled;
		this.range = data.range;
		this.type = data.type;
	}
	/** Optional restriction description. */
	description;
	/** Whether the restriction is enabled. */
	enabled;
	/** IP range in CIDR format. */
	range;
	/** Restriction type. */
	type;
};
/**
* OrganizationMeta model
*
* @param request - function to call the graphql client
* @param data - L.OrganizationMetaFragment response data
*/
var OrganizationMeta = class extends Request {
	constructor(request, data) {
		super(request);
		this.allowedAuthServices = data.allowedAuthServices;
		this.region = data.region;
	}
	/** Allowed authentication providers, empty array means all are allowed. */
	allowedAuthServices;
	/** The region the organization is hosted in. */
	region;
};
/**
* Organization origin for guidance rules.
*
* @param data - L.OrganizationOriginWebhookPayloadFragment response data
*/
var OrganizationOriginWebhookPayload = class {
	constructor(data) {
		this.type = data.type;
	}
	/** The type of origin, always 'Organization'. */
	type;
};
/**
* OrganizationPayload model
*
* @param request - function to call the graphql client
* @param data - L.OrganizationPayloadFragment response data
*/
var OrganizationPayload = class extends Request {
	constructor(request, data) {
		super(request);
		this.lastSyncId = data.lastSyncId;
		this.success = data.success;
	}
	/** The identifier of the last sync operation. */
	lastSyncId;
	/** Whether the operation was successful. */
	success;
	/** The organization that was created or updated. */
	get organization() {
		return new OrganizationQuery(this._request).fetch();
	}
};
/**
* OrganizationStartTrialPayload model
*
* @param request - function to call the graphql client
* @param data - L.OrganizationStartTrialPayloadFragment response data
*/
var OrganizationStartTrialPayload = class extends Request {
	constructor(request, data) {
		super(request);
		this.success = data.success;
	}
	/** Whether the operation was successful. */
	success;
};
/**
* Generic notification payload.
*
* @param data - L.OtherNotificationWebhookPayloadFragment response data
*/
var OtherNotificationWebhookPayload = class {
	constructor(data) {
		this.actorId = data.actorId ?? void 0;
		this.archivedAt = data.archivedAt ?? void 0;
		this.commentId = data.commentId ?? void 0;
		this.createdAt = data.createdAt;
		this.documentId = data.documentId ?? void 0;
		this.externalUserActorId = data.externalUserActorId ?? void 0;
		this.id = data.id;
		this.issueId = data.issueId ?? void 0;
		this.parentCommentId = data.parentCommentId ?? void 0;
		this.projectId = data.projectId ?? void 0;
		this.projectUpdateId = data.projectUpdateId ?? void 0;
		this.reactionEmoji = data.reactionEmoji ?? void 0;
		this.updatedAt = data.updatedAt;
		this.userId = data.userId;
		this.actor = data.actor ? new UserChildWebhookPayload(data.actor) : void 0;
		this.comment = data.comment ? new CommentChildWebhookPayload(data.comment) : void 0;
		this.document = data.document ? new DocumentChildWebhookPayload(data.document) : void 0;
		this.issue = data.issue ? new IssueWithDescriptionChildWebhookPayload(data.issue) : void 0;
		this.parentComment = data.parentComment ? new CommentChildWebhookPayload(data.parentComment) : void 0;
		this.project = data.project ? new ProjectChildWebhookPayload(data.project) : void 0;
		this.projectUpdate = data.projectUpdate ? new ProjectUpdateChildWebhookPayload(data.projectUpdate) : void 0;
		this.type = data.type;
	}
	/** The ID of the actor who caused the notification. */
	actorId;
	/** The time at which the entity was archived. */
	archivedAt;
	/** The ID of the comment this notification belongs to. */
	commentId;
	/** The time at which the entity was created. */
	createdAt;
	/** The ID of the document this notification belongs to. */
	documentId;
	/** The ID of the external user who caused the notification. */
	externalUserActorId;
	/** The ID of the entity. */
	id;
	/** The ID of the issue this notification belongs to. */
	issueId;
	/** The ID of the parent comment this notification belongs to. */
	parentCommentId;
	/** The ID of the project this notification belongs to. */
	projectId;
	/** The ID of the project update this notification belongs to. */
	projectUpdateId;
	/** The emoji of the reaction this notification is for. */
	reactionEmoji;
	/** The time at which the entity was updated. */
	updatedAt;
	/** The ID of the user who received the notification. */
	userId;
	/** The actor who caused the notification. */
	actor;
	/** The comment this notification belongs to. */
	comment;
	/** The document this notification belongs to. */
	document;
	/** The issue this notification belongs to. */
	issue;
	/** The parent comment this notification belongs to. */
	parentComment;
	/** The project this notification belongs to. */
	project;
	/** The project update this notification belongs to. */
	projectUpdate;
	/** The type of the notification. */
	type;
};
/**
* PageInfo model
*
* @param request - function to call the graphql client
* @param data - L.PageInfoFragment response data
*/
var PageInfo = class extends Request {
	constructor(request, data) {
		super(request);
		this.endCursor = data.endCursor ?? void 0;
		this.hasNextPage = data.hasNextPage;
		this.hasPreviousPage = data.hasPreviousPage;
		this.startCursor = data.startCursor ?? void 0;
	}
	/** Cursor representing the last result in the paginated results. */
	endCursor;
	/** Indicates if there are more results when paginating forward. */
	hasNextPage;
	/** Indicates if there are more results when paginating backward. */
	hasPreviousPage;
	/** Cursor representing the first result in the paginated results. */
	startCursor;
};
/**
* The paid subscription of an organization.
*
* @param request - function to call the graphql client
* @param data - L.PaidSubscriptionFragment response data
*/
var PaidSubscription = class extends Request {
	_creator;
	constructor(request, data) {
		super(request);
		this.archivedAt = parseDate(data.archivedAt) ?? void 0;
		this.cancelAt = parseDate(data.cancelAt) ?? void 0;
		this.canceledAt = parseDate(data.canceledAt) ?? void 0;
		this.collectionMethod = data.collectionMethod;
		this.createdAt = parseDate(data.createdAt) ?? /* @__PURE__ */ new Date();
		this.id = data.id;
		this.nextBillingAt = parseDate(data.nextBillingAt) ?? void 0;
		this.pendingChangeType = data.pendingChangeType ?? void 0;
		this.seats = data.seats;
		this.seatsMaximum = data.seatsMaximum ?? void 0;
		this.seatsMinimum = data.seatsMinimum ?? void 0;
		this.type = data.type;
		this.updatedAt = parseDate(data.updatedAt) ?? /* @__PURE__ */ new Date();
		this._creator = data.creator ?? void 0;
	}
	/** The time at which the entity was archived. Null if the entity has not been archived. */
	archivedAt;
	/** The date the subscription is scheduled to be canceled, if any. */
	cancelAt;
	/** The date the subscription was canceled, if any. */
	canceledAt;
	/** The collection method for this subscription, either automatically charged or invoiced. */
	collectionMethod;
	/** The time at which the entity was created. */
	createdAt;
	/** The unique identifier of the entity. */
	id;
	/** The date the subscription will be billed next. */
	nextBillingAt;
	/** The subscription type of a pending change. Null if no change pending. */
	pendingChangeType;
	/** The number of seats in the subscription. */
	seats;
	/** The maximum number of seats that will be billed in the subscription. */
	seatsMaximum;
	/** The minimum number of seats that will be billed in the subscription. */
	seatsMinimum;
	/** The subscription type. */
	type;
	/**
	* The last time at which the entity was meaningfully updated. This is the same as the creation time if the entity hasn't
	*     been updated after creation.
	*/
	updatedAt;
	/** The creator of the subscription. */
	get creator() {
		return this._creator?.id ? new UserQuery(this._request).fetch(this._creator?.id) : void 0;
	}
	/** The ID of creator of the subscription. */
	get creatorId() {
		return this._creator?.id;
	}
	/** The organization that the subscription is associated with. */
	get organization() {
		return new OrganizationQuery(this._request).fetch();
	}
};
/**
* PasskeyLoginStartResponse model
*
* @param request - function to call the graphql client
* @param data - L.PasskeyLoginStartResponseFragment response data
*/
var PasskeyLoginStartResponse = class extends Request {
	constructor(request, data) {
		super(request);
		this.options = data.options;
		this.success = data.success;
	}
	options;
	success;
};
/**
* A post related notification.
*
* @param request - function to call the graphql client
* @param data - L.PostNotificationFragment response data
*/
var PostNotification = class extends Request {
	_actor;
	_externalUserActor;
	_user;
	constructor(request, data) {
		super(request);
		this.archivedAt = parseDate(data.archivedAt) ?? void 0;
		this.commentId = data.commentId ?? void 0;
		this.createdAt = parseDate(data.createdAt) ?? /* @__PURE__ */ new Date();
		this.emailedAt = parseDate(data.emailedAt) ?? void 0;
		this.id = data.id;
		this.parentCommentId = data.parentCommentId ?? void 0;
		this.postId = data.postId;
		this.reactionEmoji = data.reactionEmoji ?? void 0;
		this.readAt = parseDate(data.readAt) ?? void 0;
		this.snoozedUntilAt = parseDate(data.snoozedUntilAt) ?? void 0;
		this.type = data.type;
		this.unsnoozedAt = parseDate(data.unsnoozedAt) ?? void 0;
		this.updatedAt = parseDate(data.updatedAt) ?? /* @__PURE__ */ new Date();
		this.botActor = data.botActor ? new ActorBot(request, data.botActor) : void 0;
		this.category = data.category;
		this._actor = data.actor ?? void 0;
		this._externalUserActor = data.externalUserActor ?? void 0;
		this._user = data.user;
	}
	/** The time at which the entity was archived. Null if the entity has not been archived. */
	archivedAt;
	/** Related comment ID. Null if the notification is not related to a comment. */
	commentId;
	/** The time at which the entity was created. */
	createdAt;
	/**
	* The time at when an email reminder for this notification was sent to the user. Null, if no email
	*     reminder has been sent.
	*/
	emailedAt;
	/** The unique identifier of the entity. */
	id;
	/** Related parent comment ID. Null if the notification is not related to a comment. */
	parentCommentId;
	/** Related post ID. */
	postId;
	/** Name of the reaction emoji related to the notification. */
	reactionEmoji;
	/** The time at when the user marked the notification as read. Null, if the the user hasn't read the notification */
	readAt;
	/** The time until a notification will be snoozed. After that it will appear in the inbox again. */
	snoozedUntilAt;
	/** Notification type. */
	type;
	/** The time at which a notification was unsnoozed.. */
	unsnoozedAt;
	/**
	* The last time at which the entity was meaningfully updated. This is the same as the creation time if the entity hasn't
	*     been updated after creation.
	*/
	updatedAt;
	/** The bot that caused the notification. */
	botActor;
	/** The category of the notification. */
	category;
	/** The user that caused the notification. */
	get actor() {
		return this._actor?.id ? new UserQuery(this._request).fetch(this._actor?.id) : void 0;
	}
	/** The ID of user that caused the notification. */
	get actorId() {
		return this._actor?.id;
	}
	/** The external user that caused the notification. */
	get externalUserActor() {
		return this._externalUserActor?.id ? new ExternalUserQuery(this._request).fetch(this._externalUserActor?.id) : void 0;
	}
	/** The ID of external user that caused the notification. */
	get externalUserActorId() {
		return this._externalUserActor?.id;
	}
	/** The user that received the notification. */
	get user() {
		return new UserQuery(this._request).fetch(this._user.id);
	}
	/** The ID of user that received the notification. */
	get userId() {
		return this._user?.id;
	}
};
/**
* A project.
*
* @param request - function to call the graphql client
* @param data - L.ProjectFragment response data
*/
var Project = class extends Request {
	_convertedFromIssue;
	_creator;
	_favorite;
	_integrationsSettings;
	_lastAppliedTemplate;
	_lastUpdate;
	_lead;
	_status;
	constructor(request, data) {
		super(request);
		this.archivedAt = parseDate(data.archivedAt) ?? void 0;
		this.autoArchivedAt = parseDate(data.autoArchivedAt) ?? void 0;
		this.canceledAt = parseDate(data.canceledAt) ?? void 0;
		this.color = data.color;
		this.completedAt = parseDate(data.completedAt) ?? void 0;
		this.completedIssueCountHistory = data.completedIssueCountHistory;
		this.completedScopeHistory = data.completedScopeHistory;
		this.content = data.content ?? void 0;
		this.createdAt = parseDate(data.createdAt) ?? /* @__PURE__ */ new Date();
		this.description = data.description;
		this.healthUpdatedAt = parseDate(data.healthUpdatedAt) ?? void 0;
		this.icon = data.icon ?? void 0;
		this.id = data.id;
		this.inProgressScopeHistory = data.inProgressScopeHistory;
		this.issueCountHistory = data.issueCountHistory;
		this.labelIds = data.labelIds;
		this.name = data.name;
		this.priority = data.priority;
		this.priorityLabel = data.priorityLabel;
		this.prioritySortOrder = data.prioritySortOrder;
		this.progress = data.progress;
		this.projectUpdateRemindersPausedUntilAt = parseDate(data.projectUpdateRemindersPausedUntilAt) ?? void 0;
		this.scope = data.scope;
		this.scopeHistory = data.scopeHistory;
		this.slackIssueComments = data.slackIssueComments;
		this.slackIssueStatuses = data.slackIssueStatuses;
		this.slackNewIssue = data.slackNewIssue;
		this.slugId = data.slugId;
		this.sortOrder = data.sortOrder;
		this.startDate = data.startDate ?? void 0;
		this.startedAt = parseDate(data.startedAt) ?? void 0;
		this.state = data.state;
		this.targetDate = data.targetDate ?? void 0;
		this.trashed = data.trashed ?? void 0;
		this.updateReminderFrequency = data.updateReminderFrequency ?? void 0;
		this.updateReminderFrequencyInWeeks = data.updateReminderFrequencyInWeeks ?? void 0;
		this.updateRemindersHour = data.updateRemindersHour ?? void 0;
		this.updatedAt = parseDate(data.updatedAt) ?? /* @__PURE__ */ new Date();
		this.url = data.url;
		this.documentContent = data.documentContent ? new DocumentContent(request, data.documentContent) : void 0;
		this.frequencyResolution = data.frequencyResolution;
		this.health = data.health ?? void 0;
		this.startDateResolution = data.startDateResolution ?? void 0;
		this.targetDateResolution = data.targetDateResolution ?? void 0;
		this.updateRemindersDay = data.updateRemindersDay ?? void 0;
		this._convertedFromIssue = data.convertedFromIssue ?? void 0;
		this._creator = data.creator ?? void 0;
		this._favorite = data.favorite ?? void 0;
		this._integrationsSettings = data.integrationsSettings ?? void 0;
		this._lastAppliedTemplate = data.lastAppliedTemplate ?? void 0;
		this._lastUpdate = data.lastUpdate ?? void 0;
		this._lead = data.lead ?? void 0;
		this._status = data.status;
	}
	/** The time at which the entity was archived. Null if the entity has not been archived. */
	archivedAt;
	/** The time at which the project was automatically archived by the auto pruning process. */
	autoArchivedAt;
	/** The time at which the project was moved into canceled state. */
	canceledAt;
	/** The project's color. */
	color;
	/** The time at which the project was moved into completed state. */
	completedAt;
	/** The number of completed issues in the project after each week. */
	completedIssueCountHistory;
	/** The number of completed estimation points after each week. */
	completedScopeHistory;
	/** The project's content in markdown format. */
	content;
	/** The time at which the entity was created. */
	createdAt;
	/** The project's description. */
	description;
	/** The time at which the project health was updated. */
	healthUpdatedAt;
	/** The icon of the project. */
	icon;
	/** The unique identifier of the entity. */
	id;
	/** The number of in progress estimation points after each week. */
	inProgressScopeHistory;
	/** The total number of issues in the project after each week. */
	issueCountHistory;
	/** Id of the labels associated with this project. */
	labelIds;
	/** The project's name. */
	name;
	/** The priority of the project. 0 = No priority, 1 = Urgent, 2 = High, 3 = Normal, 4 = Low. */
	priority;
	/** The priority of the project as a label. */
	priorityLabel;
	/** The sort order for the project within the organization, when ordered by priority. */
	prioritySortOrder;
	/** The overall progress of the project. This is the (completed estimate points + 0.25 * in progress estimate points) / total estimate points. */
	progress;
	/** The time until which project update reminders are paused. */
	projectUpdateRemindersPausedUntilAt;
	/** The overall scope (total estimate points) of the project. */
	scope;
	/** The total number of estimation points after each week. */
	scopeHistory;
	/** Whether to send new issue comment notifications to Slack. */
	slackIssueComments;
	/** Whether to send new issue status updates to Slack. */
	slackIssueStatuses;
	/** Whether to send new issue notifications to Slack. */
	slackNewIssue;
	/** The project's unique URL slug. */
	slugId;
	/** The sort order for the project within the organization. */
	sortOrder;
	/** The estimated start date of the project. */
	startDate;
	/** The time at which the project was moved into started state. */
	startedAt;
	/** [DEPRECATED] The type of the state. */
	state;
	/** The estimated completion date of the project. */
	targetDate;
	/** A flag that indicates whether the project is in the trash bin. */
	trashed;
	/** The frequency at which to prompt for updates. When not set, reminders are inherited from workspace. */
	updateReminderFrequency;
	/** The n-weekly frequency at which to prompt for updates. When not set, reminders are inherited from workspace. */
	updateReminderFrequencyInWeeks;
	/** The hour at which to prompt for updates. */
	updateRemindersHour;
	/**
	* The last time at which the entity was meaningfully updated. This is the same as the creation time if the entity hasn't
	*     been updated after creation.
	*/
	updatedAt;
	/** Project URL. */
	url;
	/** The content of the project description. */
	documentContent;
	/** The resolution of the reminder frequency. */
	frequencyResolution;
	/** The health of the project. */
	health;
	/** The resolution of the project's start date. */
	startDateResolution;
	/** The resolution of the project's estimated completion date. */
	targetDateResolution;
	/** The day at which to prompt for updates. */
	updateRemindersDay;
	/** The project was created based on this issue. */
	get convertedFromIssue() {
		return this._convertedFromIssue?.id ? new IssueQuery(this._request).fetch(this._convertedFromIssue?.id) : void 0;
	}
	/** The ID of project was created based on this issue. */
	get convertedFromIssueId() {
		return this._convertedFromIssue?.id;
	}
	/** The user who created the project. */
	get creator() {
		return this._creator?.id ? new UserQuery(this._request).fetch(this._creator?.id) : void 0;
	}
	/** The ID of user who created the project. */
	get creatorId() {
		return this._creator?.id;
	}
	/** The user's favorite associated with this project. */
	get favorite() {
		return this._favorite?.id ? new FavoriteQuery(this._request).fetch(this._favorite?.id) : void 0;
	}
	/** The ID of user's favorite associated with this project. */
	get favoriteId() {
		return this._favorite?.id;
	}
	/** Settings for all integrations associated with that project. */
	get integrationsSettings() {
		return this._integrationsSettings?.id ? new IntegrationsSettingsQuery(this._request).fetch(this._integrationsSettings?.id) : void 0;
	}
	/** The ID of settings for all integrations associated with that project. */
	get integrationsSettingsId() {
		return this._integrationsSettings?.id;
	}
	/** The last template that was applied to this project. */
	get lastAppliedTemplate() {
		return this._lastAppliedTemplate?.id ? new TemplateQuery(this._request).fetch(this._lastAppliedTemplate?.id) : void 0;
	}
	/** The ID of last template that was applied to this project. */
	get lastAppliedTemplateId() {
		return this._lastAppliedTemplate?.id;
	}
	/** The last project update posted for this project. */
	get lastUpdate() {
		return this._lastUpdate?.id ? new ProjectUpdateQuery(this._request).fetch(this._lastUpdate?.id) : void 0;
	}
	/** The ID of last project update posted for this project. */
	get lastUpdateId() {
		return this._lastUpdate?.id;
	}
	/** The project lead. */
	get lead() {
		return this._lead?.id ? new UserQuery(this._request).fetch(this._lead?.id) : void 0;
	}
	/** The ID of project lead. */
	get leadId() {
		return this._lead?.id;
	}
	/** The status that the project is associated with. */
	get status() {
		return new ProjectStatusQuery(this._request).fetch(this._status.id);
	}
	/** The ID of status that the project is associated with. */
	get statusId() {
		return this._status?.id;
	}
	/** Comments associated with the project overview. */
	comments(variables) {
		return new Project_CommentsQuery(this._request, this.id, variables).fetch(variables);
	}
	/** Documents associated with the project. */
	documents(variables) {
		return new Project_DocumentsQuery(this._request, this.id, variables).fetch(variables);
	}
	/** External links associated with the project. */
	externalLinks(variables) {
		return new Project_ExternalLinksQuery(this._request, this.id, variables).fetch(variables);
	}
	/** History entries associated with the project. */
	history(variables) {
		return new Project_HistoryQuery(this._request, this.id, variables).fetch(variables);
	}
	/** Associations of this project to parent initiatives. */
	initiativeToProjects(variables) {
		return new Project_InitiativeToProjectsQuery(this._request, this.id, variables).fetch(variables);
	}
	/** Initiatives that this project belongs to. */
	initiatives(variables) {
		return new Project_InitiativesQuery(this._request, this.id, variables).fetch(variables);
	}
	/** Inverse relations associated with this project. */
	inverseRelations(variables) {
		return new Project_InverseRelationsQuery(this._request, this.id, variables).fetch(variables);
	}
	/** Issues associated with the project. */
	issues(variables) {
		return new Project_IssuesQuery(this._request, this.id, variables).fetch(variables);
	}
	/** Labels associated with this project. */
	labels(variables) {
		return new Project_LabelsQuery(this._request, this.id, variables).fetch(variables);
	}
	/** Users that are members of the project. */
	members(variables) {
		return new Project_MembersQuery(this._request, this.id, variables).fetch(variables);
	}
	/** Customer needs associated with the project. */
	needs(variables) {
		return new Project_NeedsQuery(this._request, this.id, variables).fetch(variables);
	}
	/** Milestones associated with the project. */
	projectMilestones(variables) {
		return new Project_ProjectMilestonesQuery(this._request, this.id, variables).fetch(variables);
	}
	/** Project updates associated with the project. */
	projectUpdates(variables) {
		return new Project_ProjectUpdatesQuery(this._request, this.id, variables).fetch(variables);
	}
	/** Relations associated with this project. */
	relations(variables) {
		return new Project_RelationsQuery(this._request, this.id, variables).fetch(variables);
	}
	/** Teams associated with this project. */
	teams(variables) {
		return new Project_TeamsQuery(this._request, this.id, variables).fetch(variables);
	}
	/** Archives a project. */
	archive(variables) {
		return new ArchiveProjectMutation(this._request).fetch(this.id, variables);
	}
	/** Creates a new project. */
	create(input, variables) {
		return new CreateProjectMutation(this._request).fetch(input, variables);
	}
	/** Deletes (trashes) a project. */
	delete() {
		return new DeleteProjectMutation(this._request).fetch(this.id);
	}
	/** Unarchives a project. */
	unarchive() {
		return new UnarchiveProjectMutation(this._request).fetch(this.id);
	}
	/** Updates a project. */
	update() {
		return new ProjectUpdateQuery(this._request).fetch(this.id);
	}
};
/**
* A generic payload return from entity archive mutations.
*
* @param request - function to call the graphql client
* @param data - L.ProjectArchivePayloadFragment response data
*/
var ProjectArchivePayload = class extends Request {
	_entity;
	constructor(request, data) {
		super(request);
		this.lastSyncId = data.lastSyncId;
		this.success = data.success;
		this._entity = data.entity ?? void 0;
	}
	/** The identifier of the last sync operation. */
	lastSyncId;
	/** Whether the operation was successful. */
	success;
	/** The archived/unarchived entity. Null if entity was deleted. */
	get entity() {
		return this._entity?.id ? new ProjectQuery(this._request).fetch(this._entity?.id) : void 0;
	}
	/** The ID of archived/unarchived entity. null if entity was deleted. */
	get entityId() {
		return this._entity?.id;
	}
};
/**
* Project attachment
*
* @param request - function to call the graphql client
* @param data - L.ProjectAttachmentFragment response data
*/
var ProjectAttachment = class extends Request {
	_creator;
	constructor(request, data) {
		super(request);
		this.archivedAt = parseDate(data.archivedAt) ?? void 0;
		this.createdAt = parseDate(data.createdAt) ?? /* @__PURE__ */ new Date();
		this.id = data.id;
		this.metadata = data.metadata;
		this.source = data.source ?? void 0;
		this.sourceType = data.sourceType ?? void 0;
		this.subtitle = data.subtitle ?? void 0;
		this.title = data.title;
		this.updatedAt = parseDate(data.updatedAt) ?? /* @__PURE__ */ new Date();
		this.url = data.url;
		this._creator = data.creator ?? void 0;
	}
	/** The time at which the entity was archived. Null if the entity has not been archived. */
	archivedAt;
	/** The time at which the entity was created. */
	createdAt;
	/** The unique identifier of the entity. */
	id;
	/** Custom metadata related to the attachment. */
	metadata;
	/** Information about the external source which created the attachment. */
	source;
	/** An accessor helper to source.type, defines the source type of the attachment. */
	sourceType;
	/** Optional subtitle of the attachment */
	subtitle;
	/** Title of the attachment. */
	title;
	/**
	* The last time at which the entity was meaningfully updated. This is the same as the creation time if the entity hasn't
	*     been updated after creation.
	*/
	updatedAt;
	/** URL of the attachment. */
	url;
	/** The creator of the attachment. */
	get creator() {
		return this._creator?.id ? new UserQuery(this._request).fetch(this._creator?.id) : void 0;
	}
	/** The ID of creator of the attachment. */
	get creatorId() {
		return this._creator?.id;
	}
};
/**
* Certain properties of a project.
*
* @param data - L.ProjectChildWebhookPayloadFragment response data
*/
var ProjectChildWebhookPayload = class {
	constructor(data) {
		this.id = data.id;
		this.name = data.name;
		this.url = data.url;
	}
	/** The ID of the project. */
	id;
	/** The name of the project. */
	name;
	/** The URL of the project. */
	url;
};
/**
* ProjectConnection model
*
* @param request - function to call the graphql client
* @param fetch - function to trigger a refetch of this ProjectConnection model
* @param data - ProjectConnection response data
*/
var ProjectConnection = class extends Connection {
	constructor(request, fetch$1, data) {
		super(request, fetch$1, data.nodes.map((node) => new Project(request, node)), new PageInfo(request, data.pageInfo));
	}
};
/**
* ProjectFilterSuggestionPayload model
*
* @param request - function to call the graphql client
* @param data - L.ProjectFilterSuggestionPayloadFragment response data
*/
var ProjectFilterSuggestionPayload = class extends Request {
	constructor(request, data) {
		super(request);
		this.filter = data.filter ?? void 0;
		this.logId = data.logId ?? void 0;
	}
	/** The json filter that is suggested. */
	filter;
	/** The log id of the prompt, that created this filter. */
	logId;
};
/**
* An history associated with a project.
*
* @param request - function to call the graphql client
* @param data - L.ProjectHistoryFragment response data
*/
var ProjectHistory = class extends Request {
	_project;
	constructor(request, data) {
		super(request);
		this.archivedAt = parseDate(data.archivedAt) ?? void 0;
		this.createdAt = parseDate(data.createdAt) ?? /* @__PURE__ */ new Date();
		this.entries = data.entries;
		this.id = data.id;
		this.updatedAt = parseDate(data.updatedAt) ?? /* @__PURE__ */ new Date();
		this._project = data.project;
	}
	/** The time at which the entity was archived. Null if the entity has not been archived. */
	archivedAt;
	/** The time at which the entity was created. */
	createdAt;
	/** The events that happened while recording that history. */
	entries;
	/** The unique identifier of the entity. */
	id;
	/**
	* The last time at which the entity was meaningfully updated. This is the same as the creation time if the entity hasn't
	*     been updated after creation.
	*/
	updatedAt;
	/** The project that the history is associated with. */
	get project() {
		return new ProjectQuery(this._request).fetch(this._project.id);
	}
	/** The ID of project that the history is associated with. */
	get projectId() {
		return this._project?.id;
	}
};
/**
* ProjectHistoryConnection model
*
* @param request - function to call the graphql client
* @param fetch - function to trigger a refetch of this ProjectHistoryConnection model
* @param data - ProjectHistoryConnection response data
*/
var ProjectHistoryConnection = class extends Connection {
	constructor(request, fetch$1, data) {
		super(request, fetch$1, data.nodes.map((node) => new ProjectHistory(request, node)), new PageInfo(request, data.pageInfo));
	}
};
/**
* Labels that can be associated with projects.
*
* @param request - function to call the graphql client
* @param data - L.ProjectLabelFragment response data
*/
var ProjectLabel = class extends Request {
	_creator;
	_parent;
	_retiredBy;
	constructor(request, data) {
		super(request);
		this.archivedAt = parseDate(data.archivedAt) ?? void 0;
		this.color = data.color;
		this.createdAt = parseDate(data.createdAt) ?? /* @__PURE__ */ new Date();
		this.description = data.description ?? void 0;
		this.id = data.id;
		this.isGroup = data.isGroup;
		this.lastAppliedAt = parseDate(data.lastAppliedAt) ?? void 0;
		this.name = data.name;
		this.updatedAt = parseDate(data.updatedAt) ?? /* @__PURE__ */ new Date();
		this._creator = data.creator ?? void 0;
		this._parent = data.parent ?? void 0;
		this._retiredBy = data.retiredBy ?? void 0;
	}
	/** The time at which the entity was archived. Null if the entity has not been archived. */
	archivedAt;
	/** The label's color as a HEX string. */
	color;
	/** The time at which the entity was created. */
	createdAt;
	/** The label's description. */
	description;
	/** The unique identifier of the entity. */
	id;
	/** Whether the label is a group. */
	isGroup;
	/** The date when the label was last applied to an issue or project. */
	lastAppliedAt;
	/** The label's name. */
	name;
	/**
	* The last time at which the entity was meaningfully updated. This is the same as the creation time if the entity hasn't
	*     been updated after creation.
	*/
	updatedAt;
	/** The user who created the label. */
	get creator() {
		return this._creator?.id ? new UserQuery(this._request).fetch(this._creator?.id) : void 0;
	}
	/** The ID of user who created the label. */
	get creatorId() {
		return this._creator?.id;
	}
	get organization() {
		return new OrganizationQuery(this._request).fetch();
	}
	/** The parent label. */
	get parent() {
		return this._parent?.id ? new ProjectLabelQuery(this._request).fetch(this._parent?.id) : void 0;
	}
	/** The ID of parent label. */
	get parentId() {
		return this._parent?.id;
	}
	/** The user who retired the label. */
	get retiredBy() {
		return this._retiredBy?.id ? new UserQuery(this._request).fetch(this._retiredBy?.id) : void 0;
	}
	/** The ID of user who retired the label. */
	get retiredById() {
		return this._retiredBy?.id;
	}
	/** Children of the label. */
	children(variables) {
		return new ProjectLabel_ChildrenQuery(this._request, this.id, variables).fetch(variables);
	}
	/** Projects associated with the label. */
	projects(variables) {
		return new ProjectLabel_ProjectsQuery(this._request, this.id, variables).fetch(variables);
	}
	/** Creates a new project label. */
	create(input) {
		return new CreateProjectLabelMutation(this._request).fetch(input);
	}
	/** Deletes a project label. */
	delete() {
		return new DeleteProjectLabelMutation(this._request).fetch(this.id);
	}
	/** Updates a project label. */
	update(input) {
		return new UpdateProjectLabelMutation(this._request).fetch(this.id, input);
	}
};
/**
* Certain properties of a project label.
*
* @param data - L.ProjectLabelChildWebhookPayloadFragment response data
*/
var ProjectLabelChildWebhookPayload = class {
	constructor(data) {
		this.color = data.color;
		this.id = data.id;
		this.name = data.name;
		this.parentId = data.parentId ?? void 0;
	}
	/** The color of the project label. */
	color;
	/** The ID of the project label. */
	id;
	/** The name of the project label. */
	name;
	/** The parent ID of the project label. */
	parentId;
};
/**
* ProjectLabelConnection model
*
* @param request - function to call the graphql client
* @param fetch - function to trigger a refetch of this ProjectLabelConnection model
* @param data - ProjectLabelConnection response data
*/
var ProjectLabelConnection = class extends Connection {
	constructor(request, fetch$1, data) {
		super(request, fetch$1, data.nodes.map((node) => new ProjectLabel(request, node)), new PageInfo(request, data.pageInfo));
	}
};
/**
* ProjectLabelPayload model
*
* @param request - function to call the graphql client
* @param data - L.ProjectLabelPayloadFragment response data
*/
var ProjectLabelPayload = class extends Request {
	_projectLabel;
	constructor(request, data) {
		super(request);
		this.lastSyncId = data.lastSyncId;
		this.success = data.success;
		this._projectLabel = data.projectLabel;
	}
	/** The identifier of the last sync operation. */
	lastSyncId;
	/** Whether the operation was successful. */
	success;
	/** The label that was created or updated. */
	get projectLabel() {
		return new ProjectLabelQuery(this._request).fetch(this._projectLabel.id);
	}
	/** The ID of label that was created or updated. */
	get projectLabelId() {
		return this._projectLabel?.id;
	}
};
/**
* Payload for a project label webhook.
*
* @param data - L.ProjectLabelWebhookPayloadFragment response data
*/
var ProjectLabelWebhookPayload = class {
	constructor(data) {
		this.archivedAt = data.archivedAt ?? void 0;
		this.color = data.color;
		this.createdAt = data.createdAt;
		this.creatorId = data.creatorId ?? void 0;
		this.description = data.description ?? void 0;
		this.id = data.id;
		this.isGroup = data.isGroup;
		this.name = data.name;
		this.parentId = data.parentId ?? void 0;
		this.updatedAt = data.updatedAt;
	}
	/** The time at which the entity was archived. */
	archivedAt;
	/** The color of the project label. */
	color;
	/** The time at which the entity was created. */
	createdAt;
	/** The creator ID of the project label. */
	creatorId;
	/** The label's description. */
	description;
	/** The ID of the entity. */
	id;
	/** Whether the label is a group. */
	isGroup;
	/** The name of the project label. */
	name;
	/** The parent ID of the project label. */
	parentId;
	/** The time at which the entity was updated. */
	updatedAt;
};
/**
* A milestone for a project.
*
* @param request - function to call the graphql client
* @param data - L.ProjectMilestoneFragment response data
*/
var ProjectMilestone = class extends Request {
	_project;
	constructor(request, data) {
		super(request);
		this.archivedAt = parseDate(data.archivedAt) ?? void 0;
		this.createdAt = parseDate(data.createdAt) ?? /* @__PURE__ */ new Date();
		this.description = data.description ?? void 0;
		this.id = data.id;
		this.name = data.name;
		this.progress = data.progress;
		this.sortOrder = data.sortOrder;
		this.targetDate = data.targetDate ?? void 0;
		this.updatedAt = parseDate(data.updatedAt) ?? /* @__PURE__ */ new Date();
		this.documentContent = data.documentContent ? new DocumentContent(request, data.documentContent) : void 0;
		this.status = data.status;
		this._project = data.project;
	}
	/** The time at which the entity was archived. Null if the entity has not been archived. */
	archivedAt;
	/** The time at which the entity was created. */
	createdAt;
	/** The project milestone's description in markdown format. */
	description;
	/** The unique identifier of the entity. */
	id;
	/** The name of the project milestone. */
	name;
	/** The progress % of the project milestone. */
	progress;
	/** The order of the milestone in relation to other milestones within a project. */
	sortOrder;
	/** The planned completion date of the milestone. */
	targetDate;
	/**
	* The last time at which the entity was meaningfully updated. This is the same as the creation time if the entity hasn't
	*     been updated after creation.
	*/
	updatedAt;
	/** The content of the project milestone description. */
	documentContent;
	/** The status of the project milestone. */
	status;
	/** The project of the milestone. */
	get project() {
		return new ProjectQuery(this._request).fetch(this._project.id);
	}
	/** The ID of project of the milestone. */
	get projectId() {
		return this._project?.id;
	}
	/** Issues associated with the project milestone. */
	issues(variables) {
		return new ProjectMilestone_IssuesQuery(this._request, this.id, variables).fetch(variables);
	}
	/** Creates a new project milestone. */
	create(input) {
		return new CreateProjectMilestoneMutation(this._request).fetch(input);
	}
	/** Deletes a project milestone. */
	delete() {
		return new DeleteProjectMilestoneMutation(this._request).fetch(this.id);
	}
	/** Updates a project milestone. */
	update(input) {
		return new UpdateProjectMilestoneMutation(this._request).fetch(this.id, input);
	}
};
/**
* Certain properties of a project milestone.
*
* @param data - L.ProjectMilestoneChildWebhookPayloadFragment response data
*/
var ProjectMilestoneChildWebhookPayload = class {
	constructor(data) {
		this.id = data.id;
		this.name = data.name;
		this.targetDate = data.targetDate;
	}
	/** The ID of the project milestone. */
	id;
	/** The name of the project milestone. */
	name;
	/** The target date of the project milestone. */
	targetDate;
};
/**
* ProjectMilestoneConnection model
*
* @param request - function to call the graphql client
* @param fetch - function to trigger a refetch of this ProjectMilestoneConnection model
* @param data - ProjectMilestoneConnection response data
*/
var ProjectMilestoneConnection = class extends Connection {
	constructor(request, fetch$1, data) {
		super(request, fetch$1, data.nodes.map((node) => new ProjectMilestone(request, node)), new PageInfo(request, data.pageInfo));
	}
};
/**
* ProjectMilestoneMoveIssueToTeam model
*
* @param request - function to call the graphql client
* @param data - L.ProjectMilestoneMoveIssueToTeamFragment response data
*/
var ProjectMilestoneMoveIssueToTeam = class extends Request {
	constructor(request, data) {
		super(request);
		this.issueId = data.issueId;
		this.teamId = data.teamId;
	}
	/** The issue id in this relationship, you can use * as wildcard if all issues are being moved to the same team */
	issueId;
	/** The team id in this relationship */
	teamId;
};
/**
* ProjectMilestoneMovePayload model
*
* @param request - function to call the graphql client
* @param data - L.ProjectMilestoneMovePayloadFragment response data
*/
var ProjectMilestoneMovePayload = class extends Request {
	_projectMilestone;
	constructor(request, data) {
		super(request);
		this.lastSyncId = data.lastSyncId;
		this.success = data.success;
		this.previousProjectTeamIds = data.previousProjectTeamIds ? new ProjectMilestoneMoveProjectTeams(request, data.previousProjectTeamIds) : void 0;
		this.previousIssueTeamIds = data.previousIssueTeamIds ? data.previousIssueTeamIds.map((node) => new ProjectMilestoneMoveIssueToTeam(request, node)) : void 0;
		this._projectMilestone = data.projectMilestone;
	}
	/** The identifier of the last sync operation. */
	lastSyncId;
	/** Whether the operation was successful. */
	success;
	/** A snapshot of the issues that were moved to new teams, if the user selected to do it, containing an array of mappings between an issue and its previous team. Store on the client to use for undoing a previous milestone move. */
	previousIssueTeamIds;
	/** A snapshot of the project that had new teams added to it, if the user selected to do it, containing an array of mappings between a project and its previous teams. Store on the client to use for undoing a previous milestone move. */
	previousProjectTeamIds;
	/** The project milestone that was created or updated. */
	get projectMilestone() {
		return new ProjectMilestoneQuery(this._request).fetch(this._projectMilestone.id);
	}
	/** The ID of project milestone that was created or updated. */
	get projectMilestoneId() {
		return this._projectMilestone?.id;
	}
};
/**
* ProjectMilestoneMoveProjectTeams model
*
* @param request - function to call the graphql client
* @param data - L.ProjectMilestoneMoveProjectTeamsFragment response data
*/
var ProjectMilestoneMoveProjectTeams = class extends Request {
	constructor(request, data) {
		super(request);
		this.projectId = data.projectId;
		this.teamIds = data.teamIds;
	}
	/** The project id */
	projectId;
	/** The team ids for the project */
	teamIds;
};
/**
* ProjectMilestonePayload model
*
* @param request - function to call the graphql client
* @param data - L.ProjectMilestonePayloadFragment response data
*/
var ProjectMilestonePayload = class extends Request {
	_projectMilestone;
	constructor(request, data) {
		super(request);
		this.lastSyncId = data.lastSyncId;
		this.success = data.success;
		this._projectMilestone = data.projectMilestone;
	}
	/** The identifier of the last sync operation. */
	lastSyncId;
	/** Whether the operation was successful. */
	success;
	/** The project milestone that was created or updated. */
	get projectMilestone() {
		return new ProjectMilestoneQuery(this._request).fetch(this._projectMilestone.id);
	}
	/** The ID of project milestone that was created or updated. */
	get projectMilestoneId() {
		return this._projectMilestone?.id;
	}
};
/**
* A project related notification.
*
* @param request - function to call the graphql client
* @param data - L.ProjectNotificationFragment response data
*/
var ProjectNotification = class extends Request {
	_actor;
	_comment;
	_document;
	_externalUserActor;
	_parentComment;
	_project;
	_projectUpdate;
	_user;
	constructor(request, data) {
		super(request);
		this.archivedAt = parseDate(data.archivedAt) ?? void 0;
		this.commentId = data.commentId ?? void 0;
		this.createdAt = parseDate(data.createdAt) ?? /* @__PURE__ */ new Date();
		this.emailedAt = parseDate(data.emailedAt) ?? void 0;
		this.id = data.id;
		this.parentCommentId = data.parentCommentId ?? void 0;
		this.projectId = data.projectId;
		this.projectMilestoneId = data.projectMilestoneId ?? void 0;
		this.projectUpdateId = data.projectUpdateId ?? void 0;
		this.reactionEmoji = data.reactionEmoji ?? void 0;
		this.readAt = parseDate(data.readAt) ?? void 0;
		this.snoozedUntilAt = parseDate(data.snoozedUntilAt) ?? void 0;
		this.type = data.type;
		this.unsnoozedAt = parseDate(data.unsnoozedAt) ?? void 0;
		this.updatedAt = parseDate(data.updatedAt) ?? /* @__PURE__ */ new Date();
		this.botActor = data.botActor ? new ActorBot(request, data.botActor) : void 0;
		this.category = data.category;
		this._actor = data.actor ?? void 0;
		this._comment = data.comment ?? void 0;
		this._document = data.document ?? void 0;
		this._externalUserActor = data.externalUserActor ?? void 0;
		this._parentComment = data.parentComment ?? void 0;
		this._project = data.project;
		this._projectUpdate = data.projectUpdate ?? void 0;
		this._user = data.user;
	}
	/** The time at which the entity was archived. Null if the entity has not been archived. */
	archivedAt;
	/** Related comment ID. Null if the notification is not related to a comment. */
	commentId;
	/** The time at which the entity was created. */
	createdAt;
	/**
	* The time at when an email reminder for this notification was sent to the user. Null, if no email
	*     reminder has been sent.
	*/
	emailedAt;
	/** The unique identifier of the entity. */
	id;
	/** Related parent comment ID. Null if the notification is not related to a comment. */
	parentCommentId;
	/** Related project ID. */
	projectId;
	/** Related project milestone ID. */
	projectMilestoneId;
	/** Related project update ID. */
	projectUpdateId;
	/** Name of the reaction emoji related to the notification. */
	reactionEmoji;
	/** The time at when the user marked the notification as read. Null, if the the user hasn't read the notification */
	readAt;
	/** The time until a notification will be snoozed. After that it will appear in the inbox again. */
	snoozedUntilAt;
	/** Notification type. */
	type;
	/** The time at which a notification was unsnoozed.. */
	unsnoozedAt;
	/**
	* The last time at which the entity was meaningfully updated. This is the same as the creation time if the entity hasn't
	*     been updated after creation.
	*/
	updatedAt;
	/** The bot that caused the notification. */
	botActor;
	/** The category of the notification. */
	category;
	/** The user that caused the notification. */
	get actor() {
		return this._actor?.id ? new UserQuery(this._request).fetch(this._actor?.id) : void 0;
	}
	/** The ID of user that caused the notification. */
	get actorId() {
		return this._actor?.id;
	}
	/** The comment related to the notification. */
	get comment() {
		return this._comment?.id ? new CommentQuery(this._request).fetch({ id: this._comment?.id }) : void 0;
	}
	/** The document related to the notification. */
	get document() {
		return this._document?.id ? new DocumentQuery(this._request).fetch(this._document?.id) : void 0;
	}
	/** The ID of document related to the notification. */
	get documentId() {
		return this._document?.id;
	}
	/** The external user that caused the notification. */
	get externalUserActor() {
		return this._externalUserActor?.id ? new ExternalUserQuery(this._request).fetch(this._externalUserActor?.id) : void 0;
	}
	/** The ID of external user that caused the notification. */
	get externalUserActorId() {
		return this._externalUserActor?.id;
	}
	/** The parent comment related to the notification, if a notification is a reply comment notification. */
	get parentComment() {
		return this._parentComment?.id ? new CommentQuery(this._request).fetch({ id: this._parentComment?.id }) : void 0;
	}
	/** The project related to the notification. */
	get project() {
		return new ProjectQuery(this._request).fetch(this._project.id);
	}
	/** The project update related to the notification. */
	get projectUpdate() {
		return this._projectUpdate?.id ? new ProjectUpdateQuery(this._request).fetch(this._projectUpdate?.id) : void 0;
	}
	/** The user that received the notification. */
	get user() {
		return new UserQuery(this._request).fetch(this._user.id);
	}
	/** The ID of user that received the notification. */
	get userId() {
		return this._user?.id;
	}
};
/**
* A project notification subscription.
*
* @param request - function to call the graphql client
* @param data - L.ProjectNotificationSubscriptionFragment response data
*/
var ProjectNotificationSubscription = class extends Request {
	_customView;
	_customer;
	_cycle;
	_initiative;
	_label;
	_project;
	_subscriber;
	_team;
	_user;
	constructor(request, data) {
		super(request);
		this.active = data.active;
		this.archivedAt = parseDate(data.archivedAt) ?? void 0;
		this.createdAt = parseDate(data.createdAt) ?? /* @__PURE__ */ new Date();
		this.id = data.id;
		this.notificationSubscriptionTypes = data.notificationSubscriptionTypes;
		this.updatedAt = parseDate(data.updatedAt) ?? /* @__PURE__ */ new Date();
		this.contextViewType = data.contextViewType ?? void 0;
		this.userContextViewType = data.userContextViewType ?? void 0;
		this._customView = data.customView ?? void 0;
		this._customer = data.customer ?? void 0;
		this._cycle = data.cycle ?? void 0;
		this._initiative = data.initiative ?? void 0;
		this._label = data.label ?? void 0;
		this._project = data.project;
		this._subscriber = data.subscriber;
		this._team = data.team ?? void 0;
		this._user = data.user ?? void 0;
	}
	/** Whether the subscription is active or not. */
	active;
	/** The time at which the entity was archived. Null if the entity has not been archived. */
	archivedAt;
	/** The time at which the entity was created. */
	createdAt;
	/** The unique identifier of the entity. */
	id;
	/** The type of subscription. */
	notificationSubscriptionTypes;
	/**
	* The last time at which the entity was meaningfully updated. This is the same as the creation time if the entity hasn't
	*     been updated after creation.
	*/
	updatedAt;
	/** The type of view to which the notification subscription context is associated with. */
	contextViewType;
	/** The type of user view to which the notification subscription context is associated with. */
	userContextViewType;
	/** The contextual custom view associated with the notification subscription. */
	get customView() {
		return this._customView?.id ? new CustomViewQuery(this._request).fetch(this._customView?.id) : void 0;
	}
	/** The ID of contextual custom view associated with the notification subscription. */
	get customViewId() {
		return this._customView?.id;
	}
	/** The customer associated with the notification subscription. */
	get customer() {
		return this._customer?.id ? new CustomerQuery(this._request).fetch(this._customer?.id) : void 0;
	}
	/** The ID of customer associated with the notification subscription. */
	get customerId() {
		return this._customer?.id;
	}
	/** The contextual cycle view associated with the notification subscription. */
	get cycle() {
		return this._cycle?.id ? new CycleQuery(this._request).fetch(this._cycle?.id) : void 0;
	}
	/** The ID of contextual cycle view associated with the notification subscription. */
	get cycleId() {
		return this._cycle?.id;
	}
	/** The contextual initiative view associated with the notification subscription. */
	get initiative() {
		return this._initiative?.id ? new InitiativeQuery(this._request).fetch(this._initiative?.id) : void 0;
	}
	/** The ID of contextual initiative view associated with the notification subscription. */
	get initiativeId() {
		return this._initiative?.id;
	}
	/** The contextual label view associated with the notification subscription. */
	get label() {
		return this._label?.id ? new IssueLabelQuery(this._request).fetch(this._label?.id) : void 0;
	}
	/** The ID of contextual label view associated with the notification subscription. */
	get labelId() {
		return this._label?.id;
	}
	/** The project subscribed to. */
	get project() {
		return new ProjectQuery(this._request).fetch(this._project.id);
	}
	/** The ID of project subscribed to. */
	get projectId() {
		return this._project?.id;
	}
	/** The user that subscribed to receive notifications. */
	get subscriber() {
		return new UserQuery(this._request).fetch(this._subscriber.id);
	}
	/** The ID of user that subscribed to receive notifications. */
	get subscriberId() {
		return this._subscriber?.id;
	}
	/** The team associated with the notification subscription. */
	get team() {
		return this._team?.id ? new TeamQuery(this._request).fetch(this._team?.id) : void 0;
	}
	/** The ID of team associated with the notification subscription. */
	get teamId() {
		return this._team?.id;
	}
	/** The user view associated with the notification subscription. */
	get user() {
		return this._user?.id ? new UserQuery(this._request).fetch(this._user?.id) : void 0;
	}
	/** The ID of user view associated with the notification subscription. */
	get userId() {
		return this._user?.id;
	}
};
/**
* ProjectPayload model
*
* @param request - function to call the graphql client
* @param data - L.ProjectPayloadFragment response data
*/
var ProjectPayload = class extends Request {
	_project;
	constructor(request, data) {
		super(request);
		this.lastSyncId = data.lastSyncId;
		this.success = data.success;
		this._project = data.project ?? void 0;
	}
	/** The identifier of the last sync operation. */
	lastSyncId;
	/** Whether the operation was successful. */
	success;
	/** The project that was created or updated. */
	get project() {
		return this._project?.id ? new ProjectQuery(this._request).fetch(this._project?.id) : void 0;
	}
	/** The ID of project that was created or updated. */
	get projectId() {
		return this._project?.id;
	}
};
/**
* A relation between two projects.
*
* @param request - function to call the graphql client
* @param data - L.ProjectRelationFragment response data
*/
var ProjectRelation = class extends Request {
	_project;
	_projectMilestone;
	_relatedProject;
	_relatedProjectMilestone;
	_user;
	constructor(request, data) {
		super(request);
		this.anchorType = data.anchorType;
		this.archivedAt = parseDate(data.archivedAt) ?? void 0;
		this.createdAt = parseDate(data.createdAt) ?? /* @__PURE__ */ new Date();
		this.id = data.id;
		this.relatedAnchorType = data.relatedAnchorType;
		this.type = data.type;
		this.updatedAt = parseDate(data.updatedAt) ?? /* @__PURE__ */ new Date();
		this._project = data.project;
		this._projectMilestone = data.projectMilestone ?? void 0;
		this._relatedProject = data.relatedProject;
		this._relatedProjectMilestone = data.relatedProjectMilestone ?? void 0;
		this._user = data.user ?? void 0;
	}
	/** The type of anchor on the project end of the relation. */
	anchorType;
	/** The time at which the entity was archived. Null if the entity has not been archived. */
	archivedAt;
	/** The time at which the entity was created. */
	createdAt;
	/** The unique identifier of the entity. */
	id;
	/** The type of anchor on the relatedProject end of the relation. */
	relatedAnchorType;
	/** The relationship of the project with the related project. */
	type;
	/**
	* The last time at which the entity was meaningfully updated. This is the same as the creation time if the entity hasn't
	*     been updated after creation.
	*/
	updatedAt;
	/** The project whose relationship is being described. */
	get project() {
		return new ProjectQuery(this._request).fetch(this._project.id);
	}
	/** The ID of project whose relationship is being described. */
	get projectId() {
		return this._project?.id;
	}
	/** The milestone within the project whose relationship is being described. */
	get projectMilestone() {
		return this._projectMilestone?.id ? new ProjectMilestoneQuery(this._request).fetch(this._projectMilestone?.id) : void 0;
	}
	/** The ID of milestone within the project whose relationship is being described. */
	get projectMilestoneId() {
		return this._projectMilestone?.id;
	}
	/** The related project. */
	get relatedProject() {
		return new ProjectQuery(this._request).fetch(this._relatedProject.id);
	}
	/** The ID of related project. */
	get relatedProjectId() {
		return this._relatedProject?.id;
	}
	/** The milestone within the related project whose relationship is being described. */
	get relatedProjectMilestone() {
		return this._relatedProjectMilestone?.id ? new ProjectMilestoneQuery(this._request).fetch(this._relatedProjectMilestone?.id) : void 0;
	}
	/** The ID of milestone within the related project whose relationship is being described. */
	get relatedProjectMilestoneId() {
		return this._relatedProjectMilestone?.id;
	}
	/** The last user who created or modified the relation. */
	get user() {
		return this._user?.id ? new UserQuery(this._request).fetch(this._user?.id) : void 0;
	}
	/** The ID of last user who created or modified the relation. */
	get userId() {
		return this._user?.id;
	}
	/** Creates a new project relation. */
	create(input) {
		return new CreateProjectRelationMutation(this._request).fetch(input);
	}
	/** Deletes a project relation. */
	delete() {
		return new DeleteProjectRelationMutation(this._request).fetch(this.id);
	}
	/** Updates a project relation. */
	update(input) {
		return new UpdateProjectRelationMutation(this._request).fetch(this.id, input);
	}
};
/**
* ProjectRelationConnection model
*
* @param request - function to call the graphql client
* @param fetch - function to trigger a refetch of this ProjectRelationConnection model
* @param data - ProjectRelationConnection response data
*/
var ProjectRelationConnection = class extends Connection {
	constructor(request, fetch$1, data) {
		super(request, fetch$1, data.nodes.map((node) => new ProjectRelation(request, node)), new PageInfo(request, data.pageInfo));
	}
};
/**
* ProjectRelationPayload model
*
* @param request - function to call the graphql client
* @param data - L.ProjectRelationPayloadFragment response data
*/
var ProjectRelationPayload = class extends Request {
	_projectRelation;
	constructor(request, data) {
		super(request);
		this.lastSyncId = data.lastSyncId;
		this.success = data.success;
		this._projectRelation = data.projectRelation;
	}
	/** The identifier of the last sync operation. */
	lastSyncId;
	/** Whether the operation was successful. */
	success;
	/** The project relation that was created or updated. */
	get projectRelation() {
		return new ProjectRelationQuery(this._request).fetch(this._projectRelation.id);
	}
	/** The ID of project relation that was created or updated. */
	get projectRelationId() {
		return this._projectRelation?.id;
	}
};
/**
* ProjectSearchPayload model
*
* @param request - function to call the graphql client
* @param data - L.ProjectSearchPayloadFragment response data
*/
var ProjectSearchPayload = class extends Request {
	constructor(request, data) {
		super(request);
		this.totalCount = data.totalCount;
		this.archivePayload = new ArchiveResponse(request, data.archivePayload);
		this.pageInfo = new PageInfo(request, data.pageInfo);
		this.nodes = data.nodes.map((node) => new ProjectSearchResult(request, node));
	}
	/** Total number of results for query without filters applied. */
	totalCount;
	nodes;
	/** Archived entities matching the search term along with all their dependencies. */
	archivePayload;
	pageInfo;
};
/**
* ProjectSearchResult model
*
* @param request - function to call the graphql client
* @param data - L.ProjectSearchResultFragment response data
*/
var ProjectSearchResult = class extends Request {
	_convertedFromIssue;
	_creator;
	_favorite;
	_integrationsSettings;
	_lastAppliedTemplate;
	_lastUpdate;
	_lead;
	_status;
	constructor(request, data) {
		super(request);
		this.archivedAt = parseDate(data.archivedAt) ?? void 0;
		this.autoArchivedAt = parseDate(data.autoArchivedAt) ?? void 0;
		this.canceledAt = parseDate(data.canceledAt) ?? void 0;
		this.color = data.color;
		this.completedAt = parseDate(data.completedAt) ?? void 0;
		this.completedIssueCountHistory = data.completedIssueCountHistory;
		this.completedScopeHistory = data.completedScopeHistory;
		this.content = data.content ?? void 0;
		this.createdAt = parseDate(data.createdAt) ?? /* @__PURE__ */ new Date();
		this.description = data.description;
		this.healthUpdatedAt = parseDate(data.healthUpdatedAt) ?? void 0;
		this.icon = data.icon ?? void 0;
		this.id = data.id;
		this.inProgressScopeHistory = data.inProgressScopeHistory;
		this.issueCountHistory = data.issueCountHistory;
		this.labelIds = data.labelIds;
		this.metadata = data.metadata;
		this.name = data.name;
		this.priority = data.priority;
		this.priorityLabel = data.priorityLabel;
		this.prioritySortOrder = data.prioritySortOrder;
		this.progress = data.progress;
		this.projectUpdateRemindersPausedUntilAt = parseDate(data.projectUpdateRemindersPausedUntilAt) ?? void 0;
		this.scope = data.scope;
		this.scopeHistory = data.scopeHistory;
		this.slackIssueComments = data.slackIssueComments;
		this.slackIssueStatuses = data.slackIssueStatuses;
		this.slackNewIssue = data.slackNewIssue;
		this.slugId = data.slugId;
		this.sortOrder = data.sortOrder;
		this.startDate = data.startDate ?? void 0;
		this.startedAt = parseDate(data.startedAt) ?? void 0;
		this.state = data.state;
		this.targetDate = data.targetDate ?? void 0;
		this.trashed = data.trashed ?? void 0;
		this.updateReminderFrequency = data.updateReminderFrequency ?? void 0;
		this.updateReminderFrequencyInWeeks = data.updateReminderFrequencyInWeeks ?? void 0;
		this.updateRemindersHour = data.updateRemindersHour ?? void 0;
		this.updatedAt = parseDate(data.updatedAt) ?? /* @__PURE__ */ new Date();
		this.url = data.url;
		this.documentContent = data.documentContent ? new DocumentContent(request, data.documentContent) : void 0;
		this.frequencyResolution = data.frequencyResolution;
		this.health = data.health ?? void 0;
		this.startDateResolution = data.startDateResolution ?? void 0;
		this.targetDateResolution = data.targetDateResolution ?? void 0;
		this.updateRemindersDay = data.updateRemindersDay ?? void 0;
		this._convertedFromIssue = data.convertedFromIssue ?? void 0;
		this._creator = data.creator ?? void 0;
		this._favorite = data.favorite ?? void 0;
		this._integrationsSettings = data.integrationsSettings ?? void 0;
		this._lastAppliedTemplate = data.lastAppliedTemplate ?? void 0;
		this._lastUpdate = data.lastUpdate ?? void 0;
		this._lead = data.lead ?? void 0;
		this._status = data.status;
	}
	/** The time at which the entity was archived. Null if the entity has not been archived. */
	archivedAt;
	/** The time at which the project was automatically archived by the auto pruning process. */
	autoArchivedAt;
	/** The time at which the project was moved into canceled state. */
	canceledAt;
	/** The project's color. */
	color;
	/** The time at which the project was moved into completed state. */
	completedAt;
	/** The number of completed issues in the project after each week. */
	completedIssueCountHistory;
	/** The number of completed estimation points after each week. */
	completedScopeHistory;
	/** The project's content in markdown format. */
	content;
	/** The time at which the entity was created. */
	createdAt;
	/** The project's description. */
	description;
	/** The time at which the project health was updated. */
	healthUpdatedAt;
	/** The icon of the project. */
	icon;
	/** The unique identifier of the entity. */
	id;
	/** The number of in progress estimation points after each week. */
	inProgressScopeHistory;
	/** The total number of issues in the project after each week. */
	issueCountHistory;
	/** Id of the labels associated with this project. */
	labelIds;
	/** Metadata related to search result. */
	metadata;
	/** The project's name. */
	name;
	/** The priority of the project. 0 = No priority, 1 = Urgent, 2 = High, 3 = Normal, 4 = Low. */
	priority;
	/** The priority of the project as a label. */
	priorityLabel;
	/** The sort order for the project within the organization, when ordered by priority. */
	prioritySortOrder;
	/** The overall progress of the project. This is the (completed estimate points + 0.25 * in progress estimate points) / total estimate points. */
	progress;
	/** The time until which project update reminders are paused. */
	projectUpdateRemindersPausedUntilAt;
	/** The overall scope (total estimate points) of the project. */
	scope;
	/** The total number of estimation points after each week. */
	scopeHistory;
	/** Whether to send new issue comment notifications to Slack. */
	slackIssueComments;
	/** Whether to send new issue status updates to Slack. */
	slackIssueStatuses;
	/** Whether to send new issue notifications to Slack. */
	slackNewIssue;
	/** The project's unique URL slug. */
	slugId;
	/** The sort order for the project within the organization. */
	sortOrder;
	/** The estimated start date of the project. */
	startDate;
	/** The time at which the project was moved into started state. */
	startedAt;
	/** [DEPRECATED] The type of the state. */
	state;
	/** The estimated completion date of the project. */
	targetDate;
	/** A flag that indicates whether the project is in the trash bin. */
	trashed;
	/** The frequency at which to prompt for updates. When not set, reminders are inherited from workspace. */
	updateReminderFrequency;
	/** The n-weekly frequency at which to prompt for updates. When not set, reminders are inherited from workspace. */
	updateReminderFrequencyInWeeks;
	/** The hour at which to prompt for updates. */
	updateRemindersHour;
	/**
	* The last time at which the entity was meaningfully updated. This is the same as the creation time if the entity hasn't
	*     been updated after creation.
	*/
	updatedAt;
	/** Project URL. */
	url;
	/** The content of the project description. */
	documentContent;
	/** The resolution of the reminder frequency. */
	frequencyResolution;
	/** The health of the project. */
	health;
	/** The resolution of the project's start date. */
	startDateResolution;
	/** The resolution of the project's estimated completion date. */
	targetDateResolution;
	/** The day at which to prompt for updates. */
	updateRemindersDay;
	/** The project was created based on this issue. */
	get convertedFromIssue() {
		return this._convertedFromIssue?.id ? new IssueQuery(this._request).fetch(this._convertedFromIssue?.id) : void 0;
	}
	/** The ID of project was created based on this issue. */
	get convertedFromIssueId() {
		return this._convertedFromIssue?.id;
	}
	/** The user who created the project. */
	get creator() {
		return this._creator?.id ? new UserQuery(this._request).fetch(this._creator?.id) : void 0;
	}
	/** The ID of user who created the project. */
	get creatorId() {
		return this._creator?.id;
	}
	/** The user's favorite associated with this project. */
	get favorite() {
		return this._favorite?.id ? new FavoriteQuery(this._request).fetch(this._favorite?.id) : void 0;
	}
	/** The ID of user's favorite associated with this project. */
	get favoriteId() {
		return this._favorite?.id;
	}
	/** Settings for all integrations associated with that project. */
	get integrationsSettings() {
		return this._integrationsSettings?.id ? new IntegrationsSettingsQuery(this._request).fetch(this._integrationsSettings?.id) : void 0;
	}
	/** The ID of settings for all integrations associated with that project. */
	get integrationsSettingsId() {
		return this._integrationsSettings?.id;
	}
	/** The last template that was applied to this project. */
	get lastAppliedTemplate() {
		return this._lastAppliedTemplate?.id ? new TemplateQuery(this._request).fetch(this._lastAppliedTemplate?.id) : void 0;
	}
	/** The ID of last template that was applied to this project. */
	get lastAppliedTemplateId() {
		return this._lastAppliedTemplate?.id;
	}
	/** The last project update posted for this project. */
	get lastUpdate() {
		return this._lastUpdate?.id ? new ProjectUpdateQuery(this._request).fetch(this._lastUpdate?.id) : void 0;
	}
	/** The ID of last project update posted for this project. */
	get lastUpdateId() {
		return this._lastUpdate?.id;
	}
	/** The project lead. */
	get lead() {
		return this._lead?.id ? new UserQuery(this._request).fetch(this._lead?.id) : void 0;
	}
	/** The ID of project lead. */
	get leadId() {
		return this._lead?.id;
	}
	/** The status that the project is associated with. */
	get status() {
		return new ProjectStatusQuery(this._request).fetch(this._status.id);
	}
	/** The ID of status that the project is associated with. */
	get statusId() {
		return this._status?.id;
	}
};
/**
* A project status.
*
* @param request - function to call the graphql client
* @param data - L.ProjectStatusFragment response data
*/
var ProjectStatus = class extends Request {
	constructor(request, data) {
		super(request);
		this.archivedAt = parseDate(data.archivedAt) ?? void 0;
		this.color = data.color;
		this.createdAt = parseDate(data.createdAt) ?? /* @__PURE__ */ new Date();
		this.description = data.description ?? void 0;
		this.id = data.id;
		this.indefinite = data.indefinite;
		this.name = data.name;
		this.position = data.position;
		this.updatedAt = parseDate(data.updatedAt) ?? /* @__PURE__ */ new Date();
		this.type = data.type;
	}
	/** The time at which the entity was archived. Null if the entity has not been archived. */
	archivedAt;
	/** The UI color of the status as a HEX string. */
	color;
	/** The time at which the entity was created. */
	createdAt;
	/** Description of the status. */
	description;
	/** The unique identifier of the entity. */
	id;
	/** Whether or not a project can be in this status indefinitely. */
	indefinite;
	/** The name of the status. */
	name;
	/** The position of the status in the workspace's project flow. */
	position;
	/**
	* The last time at which the entity was meaningfully updated. This is the same as the creation time if the entity hasn't
	*     been updated after creation.
	*/
	updatedAt;
	/** The type of the project status. */
	type;
	/** Archives a project status. */
	archive() {
		return new ArchiveProjectStatusMutation(this._request).fetch(this.id);
	}
	/** Creates a new project status. */
	create(input) {
		return new CreateProjectStatusMutation(this._request).fetch(input);
	}
	/** Unarchives a project status. */
	unarchive() {
		return new UnarchiveProjectStatusMutation(this._request).fetch(this.id);
	}
	/** Updates a project status. */
	update(input) {
		return new UpdateProjectStatusMutation(this._request).fetch(this.id, input);
	}
};
/**
* A generic payload return from entity archive mutations.
*
* @param request - function to call the graphql client
* @param data - L.ProjectStatusArchivePayloadFragment response data
*/
var ProjectStatusArchivePayload = class extends Request {
	_entity;
	constructor(request, data) {
		super(request);
		this.lastSyncId = data.lastSyncId;
		this.success = data.success;
		this._entity = data.entity ?? void 0;
	}
	/** The identifier of the last sync operation. */
	lastSyncId;
	/** Whether the operation was successful. */
	success;
	/** The archived/unarchived entity. Null if entity was deleted. */
	get entity() {
		return this._entity?.id ? new ProjectStatusQuery(this._request).fetch(this._entity?.id) : void 0;
	}
	/** The ID of archived/unarchived entity. null if entity was deleted. */
	get entityId() {
		return this._entity?.id;
	}
};
/**
* Certain properties of a project status.
*
* @param data - L.ProjectStatusChildWebhookPayloadFragment response data
*/
var ProjectStatusChildWebhookPayload = class {
	constructor(data) {
		this.color = data.color;
		this.id = data.id;
		this.name = data.name;
		this.type = data.type;
	}
	/** The color of the project status. */
	color;
	/** The ID of the project status. */
	id;
	/** The name of the project status. */
	name;
	/** The type of the project status. */
	type;
};
/**
* ProjectStatusConnection model
*
* @param request - function to call the graphql client
* @param fetch - function to trigger a refetch of this ProjectStatusConnection model
* @param data - ProjectStatusConnection response data
*/
var ProjectStatusConnection = class extends Connection {
	constructor(request, fetch$1, data) {
		super(request, fetch$1, data.nodes.map((node) => new ProjectStatus(request, node)), new PageInfo(request, data.pageInfo));
	}
};
/**
* ProjectStatusCountPayload model
*
* @param request - function to call the graphql client
* @param data - L.ProjectStatusCountPayloadFragment response data
*/
var ProjectStatusCountPayload = class extends Request {
	constructor(request, data) {
		super(request);
		this.archivedTeamCount = data.archivedTeamCount;
		this.count = data.count;
		this.privateCount = data.privateCount;
	}
	/** Total number of projects using this project status that are not visible to the user because they are in an archived team. */
	archivedTeamCount;
	/** Total number of projects using this project status. */
	count;
	/** Total number of projects using this project status that are not visible to the user because they are in a private team. */
	privateCount;
};
/**
* ProjectStatusPayload model
*
* @param request - function to call the graphql client
* @param data - L.ProjectStatusPayloadFragment response data
*/
var ProjectStatusPayload = class extends Request {
	_status;
	constructor(request, data) {
		super(request);
		this.lastSyncId = data.lastSyncId;
		this.success = data.success;
		this._status = data.status;
	}
	/** The identifier of the last sync operation. */
	lastSyncId;
	/** Whether the operation was successful. */
	success;
	/** The project status that was created or updated. */
	get status() {
		return new ProjectStatusQuery(this._request).fetch(this._status.id);
	}
	/** The ID of project status that was created or updated. */
	get statusId() {
		return this._status?.id;
	}
};
/**
* An update associated with a project.
*
* @param request - function to call the graphql client
* @param data - L.ProjectUpdateFragment response data
*/
var ProjectUpdate = class extends Request {
	_project;
	_user;
	constructor(request, data) {
		super(request);
		this.archivedAt = parseDate(data.archivedAt) ?? void 0;
		this.body = data.body;
		this.commentCount = data.commentCount;
		this.createdAt = parseDate(data.createdAt) ?? /* @__PURE__ */ new Date();
		this.diff = data.diff ?? void 0;
		this.diffMarkdown = data.diffMarkdown ?? void 0;
		this.editedAt = parseDate(data.editedAt) ?? void 0;
		this.id = data.id;
		this.isDiffHidden = data.isDiffHidden;
		this.isStale = data.isStale;
		this.reactionData = data.reactionData;
		this.slugId = data.slugId;
		this.updatedAt = parseDate(data.updatedAt) ?? /* @__PURE__ */ new Date();
		this.url = data.url;
		this.reactions = data.reactions.map((node) => new Reaction(request, node));
		this.health = data.health;
		this._project = data.project;
		this._user = data.user;
	}
	/** The time at which the entity was archived. Null if the entity has not been archived. */
	archivedAt;
	/** The update content in markdown format. */
	body;
	/** Number of comments associated with the project update. */
	commentCount;
	/** The time at which the entity was created. */
	createdAt;
	/** The diff between the current update and the previous one. */
	diff;
	/** The diff between the current update and the previous one, formatted as markdown. */
	diffMarkdown;
	/** The time the update was edited. */
	editedAt;
	/** The unique identifier of the entity. */
	id;
	/** Whether project update diff should be hidden. */
	isDiffHidden;
	/** Whether the project update is stale. */
	isStale;
	/** Emoji reaction summary, grouped by emoji type. */
	reactionData;
	/** The update's unique URL slug. */
	slugId;
	/**
	* The last time at which the entity was meaningfully updated. This is the same as the creation time if the entity hasn't
	*     been updated after creation.
	*/
	updatedAt;
	/** The URL to the project update. */
	url;
	/** Reactions associated with the project update. */
	reactions;
	/** The health of the project at the time of the update. */
	health;
	/** The project that the update is associated with. */
	get project() {
		return new ProjectQuery(this._request).fetch(this._project.id);
	}
	/** The ID of project that the update is associated with. */
	get projectId() {
		return this._project?.id;
	}
	/** The user who wrote the update. */
	get user() {
		return new UserQuery(this._request).fetch(this._user.id);
	}
	/** The ID of user who wrote the update. */
	get userId() {
		return this._user?.id;
	}
	/** Comments associated with the project update. */
	comments(variables) {
		return new ProjectUpdate_CommentsQuery(this._request, this.id, variables).fetch(variables);
	}
	/** Archives a project update. */
	archive() {
		return new ArchiveProjectUpdateMutation(this._request).fetch(this.id);
	}
	/** Creates a new project update. */
	create(input) {
		return new CreateProjectUpdateMutation(this._request).fetch(input);
	}
	/** Deletes a project update. */
	delete() {
		return new DeleteProjectUpdateMutation(this._request).fetch(this.id);
	}
	/** Unarchives a project update. */
	unarchive() {
		return new UnarchiveProjectUpdateMutation(this._request).fetch(this.id);
	}
	/** Updates a project update. */
	update(input) {
		return new UpdateProjectUpdateMutation(this._request).fetch(this.id, input);
	}
};
/**
* A generic payload return from entity archive mutations.
*
* @param request - function to call the graphql client
* @param data - L.ProjectUpdateArchivePayloadFragment response data
*/
var ProjectUpdateArchivePayload = class extends Request {
	_entity;
	constructor(request, data) {
		super(request);
		this.lastSyncId = data.lastSyncId;
		this.success = data.success;
		this._entity = data.entity ?? void 0;
	}
	/** The identifier of the last sync operation. */
	lastSyncId;
	/** Whether the operation was successful. */
	success;
	/** The archived/unarchived entity. Null if entity was deleted. */
	get entity() {
		return this._entity?.id ? new ProjectUpdateQuery(this._request).fetch(this._entity?.id) : void 0;
	}
	/** The ID of archived/unarchived entity. null if entity was deleted. */
	get entityId() {
		return this._entity?.id;
	}
};
/**
* Certain properties of a project update.
*
* @param data - L.ProjectUpdateChildWebhookPayloadFragment response data
*/
var ProjectUpdateChildWebhookPayload = class {
	constructor(data) {
		this.body = data.body;
		this.id = data.id;
		this.userId = data.userId;
		this.project = new ProjectChildWebhookPayload(data.project);
	}
	/** The body of the project update. */
	body;
	/** The ID of the project update. */
	id;
	/** The ID of the user who wrote the project update. */
	userId;
	/** The project that the project update belongs to. */
	project;
};
/**
* ProjectUpdateConnection model
*
* @param request - function to call the graphql client
* @param fetch - function to trigger a refetch of this ProjectUpdateConnection model
* @param data - ProjectUpdateConnection response data
*/
var ProjectUpdateConnection = class extends Connection {
	constructor(request, fetch$1, data) {
		super(request, fetch$1, data.nodes.map((node) => new ProjectUpdate(request, node)), new PageInfo(request, data.pageInfo));
	}
};
/**
* ProjectUpdatePayload model
*
* @param request - function to call the graphql client
* @param data - L.ProjectUpdatePayloadFragment response data
*/
var ProjectUpdatePayload = class extends Request {
	_projectUpdate;
	constructor(request, data) {
		super(request);
		this.lastSyncId = data.lastSyncId;
		this.success = data.success;
		this._projectUpdate = data.projectUpdate;
	}
	/** The identifier of the last sync operation. */
	lastSyncId;
	/** Whether the operation was successful. */
	success;
	/** The project update that was created or updated. */
	get projectUpdate() {
		return new ProjectUpdateQuery(this._request).fetch(this._projectUpdate.id);
	}
	/** The ID of project update that was created or updated. */
	get projectUpdateId() {
		return this._projectUpdate?.id;
	}
};
/**
* ProjectUpdateReminderPayload model
*
* @param request - function to call the graphql client
* @param data - L.ProjectUpdateReminderPayloadFragment response data
*/
var ProjectUpdateReminderPayload = class extends Request {
	constructor(request, data) {
		super(request);
		this.lastSyncId = data.lastSyncId;
		this.success = data.success;
	}
	/** The identifier of the last sync operation. */
	lastSyncId;
	/** Whether the operation was successful. */
	success;
};
/**
* Payload for a project update webhook.
*
* @param data - L.ProjectUpdateWebhookPayloadFragment response data
*/
var ProjectUpdateWebhookPayload = class {
	constructor(data) {
		this.archivedAt = data.archivedAt ?? void 0;
		this.body = data.body;
		this.bodyData = data.bodyData;
		this.createdAt = data.createdAt;
		this.editedAt = data.editedAt;
		this.health = data.health;
		this.id = data.id;
		this.projectId = data.projectId;
		this.reactionData = data.reactionData;
		this.slugId = data.slugId;
		this.updatedAt = data.updatedAt;
		this.url = data.url ?? void 0;
		this.userId = data.userId;
		this.project = new ProjectChildWebhookPayload(data.project);
		this.user = new UserChildWebhookPayload(data.user);
	}
	/** The time at which the entity was archived. */
	archivedAt;
	/** The body of the project update. */
	body;
	/** The body data of the project update. */
	bodyData;
	/** The time at which the entity was created. */
	createdAt;
	/** The edited at timestamp of the project update. */
	editedAt;
	/** The health of the project update. */
	health;
	/** The ID of the entity. */
	id;
	/** The project id of the project update. */
	projectId;
	/** The reaction data for this project update. */
	reactionData;
	/** The slug id of the project update. */
	slugId;
	/** The time at which the entity was updated. */
	updatedAt;
	/** The URL of the project update. */
	url;
	/** The user id of the project update. */
	userId;
	/** The project that the project update belongs to. */
	project;
	/** The user who wrote the project update. */
	user;
};
/**
* Payload for a project webhook.
*
* @param data - L.ProjectWebhookPayloadFragment response data
*/
var ProjectWebhookPayload = class {
	constructor(data) {
		this.archivedAt = data.archivedAt ?? void 0;
		this.autoArchivedAt = data.autoArchivedAt ?? void 0;
		this.canceledAt = data.canceledAt ?? void 0;
		this.color = data.color;
		this.completedAt = data.completedAt ?? void 0;
		this.completedIssueCountHistory = data.completedIssueCountHistory;
		this.completedScopeHistory = data.completedScopeHistory;
		this.content = data.content ?? void 0;
		this.convertedFromIssueId = data.convertedFromIssueId ?? void 0;
		this.createdAt = data.createdAt;
		this.creatorId = data.creatorId ?? void 0;
		this.description = data.description;
		this.documentContentId = data.documentContentId ?? void 0;
		this.health = data.health ?? void 0;
		this.healthUpdatedAt = data.healthUpdatedAt ?? void 0;
		this.icon = data.icon ?? void 0;
		this.id = data.id;
		this.inProgressScopeHistory = data.inProgressScopeHistory;
		this.issueCountHistory = data.issueCountHistory;
		this.labelIds = data.labelIds;
		this.lastAppliedTemplateId = data.lastAppliedTemplateId ?? void 0;
		this.lastUpdateId = data.lastUpdateId ?? void 0;
		this.leadId = data.leadId ?? void 0;
		this.memberIds = data.memberIds;
		this.name = data.name;
		this.priority = data.priority;
		this.prioritySortOrder = data.prioritySortOrder;
		this.projectUpdateRemindersPausedUntilAt = data.projectUpdateRemindersPausedUntilAt ?? void 0;
		this.scopeHistory = data.scopeHistory;
		this.slugId = data.slugId;
		this.sortOrder = data.sortOrder;
		this.startDate = data.startDate ?? void 0;
		this.startDateResolution = data.startDateResolution ?? void 0;
		this.startedAt = data.startedAt ?? void 0;
		this.statusId = data.statusId;
		this.targetDate = data.targetDate ?? void 0;
		this.targetDateResolution = data.targetDateResolution ?? void 0;
		this.teamIds = data.teamIds;
		this.trashed = data.trashed ?? void 0;
		this.updatedAt = data.updatedAt;
		this.url = data.url;
		this.lead = data.lead ? new UserChildWebhookPayload(data.lead) : void 0;
		this.status = data.status ? new ProjectStatusChildWebhookPayload(data.status) : void 0;
		this.initiatives = data.initiatives ? data.initiatives.map((node) => new InitiativeChildWebhookPayload(node)) : void 0;
		this.milestones = data.milestones ? data.milestones.map((node) => new ProjectMilestoneChildWebhookPayload(node)) : void 0;
	}
	/** The time at which the entity was archived. */
	archivedAt;
	/** The auto archived at timestamp of the project. */
	autoArchivedAt;
	/** The canceled at timestamp of the project. */
	canceledAt;
	/** The project's color. */
	color;
	/** The completed at timestamp of the project. */
	completedAt;
	/** The number of completed issues in the project after each week. */
	completedIssueCountHistory;
	/** The number of completed estimation points after each week. */
	completedScopeHistory;
	/** The content of the project. */
	content;
	/** The ID of the issue that was converted to the project. */
	convertedFromIssueId;
	/** The time at which the entity was created. */
	createdAt;
	/** The ID of the user who created the project. */
	creatorId;
	/** The project's description. */
	description;
	/** The document content ID of the project. */
	documentContentId;
	/** The health of the project. */
	health;
	/** The time at which the project health was updated. */
	healthUpdatedAt;
	/** The icon of the project. */
	icon;
	/** The ID of the entity. */
	id;
	/** The number of in progress estimation points after each week. */
	inProgressScopeHistory;
	/** The total number of issues in the project after each week. */
	issueCountHistory;
	/** IDs of the labels associated with this project. */
	labelIds;
	/** The ID of the last template that was applied to the project. */
	lastAppliedTemplateId;
	/** The ID of the last update posted for this project. */
	lastUpdateId;
	/** The ID of the project lead. */
	leadId;
	/** IDs of the members of the project. */
	memberIds;
	/** The project's name. */
	name;
	/** The priority of the project. 0 = No priority, 1 = Urgent, 2 = High, 3 = Normal, 4 = Low. */
	priority;
	/** The sort order for the project within the organization, when ordered by priority. */
	prioritySortOrder;
	/** The time at which the project update reminders were paused until. */
	projectUpdateRemindersPausedUntilAt;
	/** The total number of estimation points after each week. */
	scopeHistory;
	/** The project's unique URL slug. */
	slugId;
	/** The sort order for the project within the organization. */
	sortOrder;
	/** The estimated start date of the project. */
	startDate;
	/** The resolution of the project's estimated start date. */
	startDateResolution;
	/** The time at which the project was moved into started state. */
	startedAt;
	/** The ID of the project status. */
	statusId;
	/** The target date of the project. */
	targetDate;
	/** The resolution of the project's target date. */
	targetDateResolution;
	/** IDs of the teams associated with this project. */
	teamIds;
	/** The trashed status of the project. */
	trashed;
	/** The time at which the entity was updated. */
	updatedAt;
	/** The URL of the project. */
	url;
	/** The initiatives associated with the project. */
	initiatives;
	/** The milestones associated with the project. */
	milestones;
	/** The project lead. */
	lead;
	/** The project status. */
	status;
};
/**
* A pull request related notification.
*
* @param request - function to call the graphql client
* @param data - L.PullRequestNotificationFragment response data
*/
var PullRequestNotification = class extends Request {
	_actor;
	_externalUserActor;
	_user;
	constructor(request, data) {
		super(request);
		this.archivedAt = parseDate(data.archivedAt) ?? void 0;
		this.createdAt = parseDate(data.createdAt) ?? /* @__PURE__ */ new Date();
		this.emailedAt = parseDate(data.emailedAt) ?? void 0;
		this.id = data.id;
		this.pullRequestCommentId = data.pullRequestCommentId ?? void 0;
		this.pullRequestId = data.pullRequestId;
		this.readAt = parseDate(data.readAt) ?? void 0;
		this.snoozedUntilAt = parseDate(data.snoozedUntilAt) ?? void 0;
		this.type = data.type;
		this.unsnoozedAt = parseDate(data.unsnoozedAt) ?? void 0;
		this.updatedAt = parseDate(data.updatedAt) ?? /* @__PURE__ */ new Date();
		this.botActor = data.botActor ? new ActorBot(request, data.botActor) : void 0;
		this.category = data.category;
		this._actor = data.actor ?? void 0;
		this._externalUserActor = data.externalUserActor ?? void 0;
		this._user = data.user;
	}
	/** The time at which the entity was archived. Null if the entity has not been archived. */
	archivedAt;
	/** The time at which the entity was created. */
	createdAt;
	/**
	* The time at when an email reminder for this notification was sent to the user. Null, if no email
	*     reminder has been sent.
	*/
	emailedAt;
	/** The unique identifier of the entity. */
	id;
	/** Related pull request comment ID. Null if the notification is not related to a pull request comment. */
	pullRequestCommentId;
	/** Related pull request. */
	pullRequestId;
	/** The time at when the user marked the notification as read. Null, if the the user hasn't read the notification */
	readAt;
	/** The time until a notification will be snoozed. After that it will appear in the inbox again. */
	snoozedUntilAt;
	/** Notification type. */
	type;
	/** The time at which a notification was unsnoozed.. */
	unsnoozedAt;
	/**
	* The last time at which the entity was meaningfully updated. This is the same as the creation time if the entity hasn't
	*     been updated after creation.
	*/
	updatedAt;
	/** The bot that caused the notification. */
	botActor;
	/** The category of the notification. */
	category;
	/** The user that caused the notification. */
	get actor() {
		return this._actor?.id ? new UserQuery(this._request).fetch(this._actor?.id) : void 0;
	}
	/** The ID of user that caused the notification. */
	get actorId() {
		return this._actor?.id;
	}
	/** The external user that caused the notification. */
	get externalUserActor() {
		return this._externalUserActor?.id ? new ExternalUserQuery(this._request).fetch(this._externalUserActor?.id) : void 0;
	}
	/** The ID of external user that caused the notification. */
	get externalUserActorId() {
		return this._externalUserActor?.id;
	}
	/** The user that received the notification. */
	get user() {
		return new UserQuery(this._request).fetch(this._user.id);
	}
	/** The ID of user that received the notification. */
	get userId() {
		return this._user?.id;
	}
};
/**
* A user's web or mobile push notification subscription.
*
* @param request - function to call the graphql client
* @param data - L.PushSubscriptionFragment response data
*/
var PushSubscription = class extends Request {
	constructor(request, data) {
		super(request);
		this.archivedAt = parseDate(data.archivedAt) ?? void 0;
		this.createdAt = parseDate(data.createdAt) ?? /* @__PURE__ */ new Date();
		this.id = data.id;
		this.updatedAt = parseDate(data.updatedAt) ?? /* @__PURE__ */ new Date();
	}
	/** The time at which the entity was archived. Null if the entity has not been archived. */
	archivedAt;
	/** The time at which the entity was created. */
	createdAt;
	/** The unique identifier of the entity. */
	id;
	/**
	* The last time at which the entity was meaningfully updated. This is the same as the creation time if the entity hasn't
	*     been updated after creation.
	*/
	updatedAt;
	/** Creates a push subscription. */
	create(input) {
		return new CreatePushSubscriptionMutation(this._request).fetch(input);
	}
	/** Deletes a push subscription. */
	delete() {
		return new DeletePushSubscriptionMutation(this._request).fetch(this.id);
	}
};
/**
* PushSubscriptionPayload model
*
* @param request - function to call the graphql client
* @param data - L.PushSubscriptionPayloadFragment response data
*/
var PushSubscriptionPayload = class extends Request {
	constructor(request, data) {
		super(request);
		this.lastSyncId = data.lastSyncId;
		this.success = data.success;
		this.entity = new PushSubscription(request, data.entity);
	}
	/** The identifier of the last sync operation. */
	lastSyncId;
	/** Whether the operation was successful. */
	success;
	/** The push subscription that was created or updated. */
	entity;
};
/**
* PushSubscriptionTestPayload model
*
* @param request - function to call the graphql client
* @param data - L.PushSubscriptionTestPayloadFragment response data
*/
var PushSubscriptionTestPayload = class extends Request {
	constructor(request, data) {
		super(request);
		this.success = data.success;
	}
	/** Whether the operation was successful. */
	success;
};
/**
* RateLimitPayload model
*
* @param request - function to call the graphql client
* @param data - L.RateLimitPayloadFragment response data
*/
var RateLimitPayload = class extends Request {
	constructor(request, data) {
		super(request);
		this.identifier = data.identifier ?? void 0;
		this.kind = data.kind;
		this.limits = data.limits.map((node) => new RateLimitResultPayload(request, node));
	}
	/** The identifier we rate limit on. */
	identifier;
	/** The kind of rate limit selected for this request. */
	kind;
	/** The state of the rate limit. */
	limits;
};
/**
* RateLimitResultPayload model
*
* @param request - function to call the graphql client
* @param data - L.RateLimitResultPayloadFragment response data
*/
var RateLimitResultPayload = class extends Request {
	constructor(request, data) {
		super(request);
		this.allowedAmount = data.allowedAmount;
		this.period = data.period;
		this.remainingAmount = data.remainingAmount;
		this.requestedAmount = data.requestedAmount;
		this.reset = data.reset;
		this.type = data.type;
	}
	/** The total allowed quantity for this type of limit. */
	allowedAmount;
	/** The period in which the rate limit is fully replenished in ms. */
	period;
	/** The remaining quantity for this type of limit after this request. */
	remainingAmount;
	/** The requested quantity for this type of limit. */
	requestedAmount;
	/** The timestamp after the rate limit is fully replenished as a UNIX timestamp. */
	reset;
	/** What is being rate limited. */
	type;
};
/**
* A reaction associated with a comment or a project update.
*
* @param request - function to call the graphql client
* @param data - L.ReactionFragment response data
*/
var Reaction = class extends Request {
	_comment;
	_externalUser;
	_initiativeUpdate;
	_issue;
	_projectUpdate;
	_user;
	constructor(request, data) {
		super(request);
		this.archivedAt = parseDate(data.archivedAt) ?? void 0;
		this.createdAt = parseDate(data.createdAt) ?? /* @__PURE__ */ new Date();
		this.emoji = data.emoji;
		this.id = data.id;
		this.updatedAt = parseDate(data.updatedAt) ?? /* @__PURE__ */ new Date();
		this._comment = data.comment ?? void 0;
		this._externalUser = data.externalUser ?? void 0;
		this._initiativeUpdate = data.initiativeUpdate ?? void 0;
		this._issue = data.issue ?? void 0;
		this._projectUpdate = data.projectUpdate ?? void 0;
		this._user = data.user ?? void 0;
	}
	/** The time at which the entity was archived. Null if the entity has not been archived. */
	archivedAt;
	/** The time at which the entity was created. */
	createdAt;
	/** Name of the reaction's emoji. */
	emoji;
	/** The unique identifier of the entity. */
	id;
	/**
	* The last time at which the entity was meaningfully updated. This is the same as the creation time if the entity hasn't
	*     been updated after creation.
	*/
	updatedAt;
	/** The comment that the reaction is associated with. */
	get comment() {
		return this._comment?.id ? new CommentQuery(this._request).fetch({ id: this._comment?.id }) : void 0;
	}
	/** The ID of comment that the reaction is associated with. */
	get commentId() {
		return this._comment?.id;
	}
	/** The external user that created the reaction. */
	get externalUser() {
		return this._externalUser?.id ? new ExternalUserQuery(this._request).fetch(this._externalUser?.id) : void 0;
	}
	/** The ID of external user that created the reaction. */
	get externalUserId() {
		return this._externalUser?.id;
	}
	/** The initiative update that the reaction is associated with. */
	get initiativeUpdate() {
		return this._initiativeUpdate?.id ? new InitiativeUpdateQuery(this._request).fetch(this._initiativeUpdate?.id) : void 0;
	}
	/** The ID of initiative update that the reaction is associated with. */
	get initiativeUpdateId() {
		return this._initiativeUpdate?.id;
	}
	/** The issue that the reaction is associated with. */
	get issue() {
		return this._issue?.id ? new IssueQuery(this._request).fetch(this._issue?.id) : void 0;
	}
	/** The ID of issue that the reaction is associated with. */
	get issueId() {
		return this._issue?.id;
	}
	/** The project update that the reaction is associated with. */
	get projectUpdate() {
		return this._projectUpdate?.id ? new ProjectUpdateQuery(this._request).fetch(this._projectUpdate?.id) : void 0;
	}
	/** The ID of project update that the reaction is associated with. */
	get projectUpdateId() {
		return this._projectUpdate?.id;
	}
	/** The user that created the reaction. */
	get user() {
		return this._user?.id ? new UserQuery(this._request).fetch(this._user?.id) : void 0;
	}
	/** The ID of user that created the reaction. */
	get userId() {
		return this._user?.id;
	}
	/** Creates a new reaction. */
	create(input) {
		return new CreateReactionMutation(this._request).fetch(input);
	}
	/** Deletes a reaction. */
	delete() {
		return new DeleteReactionMutation(this._request).fetch(this.id);
	}
};
/**
* ReactionPayload model
*
* @param request - function to call the graphql client
* @param data - L.ReactionPayloadFragment response data
*/
var ReactionPayload = class extends Request {
	constructor(request, data) {
		super(request);
		this.lastSyncId = data.lastSyncId;
		this.success = data.success;
		this.reaction = new Reaction(request, data.reaction);
	}
	/** The identifier of the last sync operation. */
	lastSyncId;
	success;
	reaction;
};
/**
* Payload for a reaction webhook.
*
* @param data - L.ReactionWebhookPayloadFragment response data
*/
var ReactionWebhookPayload = class {
	constructor(data) {
		this.archivedAt = data.archivedAt ?? void 0;
		this.commentId = data.commentId ?? void 0;
		this.createdAt = data.createdAt;
		this.emoji = data.emoji;
		this.externalUserId = data.externalUserId ?? void 0;
		this.id = data.id;
		this.initiativeUpdateId = data.initiativeUpdateId ?? void 0;
		this.issueId = data.issueId ?? void 0;
		this.postId = data.postId ?? void 0;
		this.projectUpdateId = data.projectUpdateId ?? void 0;
		this.updatedAt = data.updatedAt;
		this.userId = data.userId ?? void 0;
		this.comment = data.comment ? new CommentChildWebhookPayload(data.comment) : void 0;
		this.issue = data.issue ? new IssueChildWebhookPayload(data.issue) : void 0;
		this.projectUpdate = data.projectUpdate ? new ProjectUpdateChildWebhookPayload(data.projectUpdate) : void 0;
		this.user = data.user ? new UserChildWebhookPayload(data.user) : void 0;
	}
	/** The time at which the entity was archived. */
	archivedAt;
	/** The ID of the comment that the reaction is associated with. */
	commentId;
	/** The time at which the entity was created. */
	createdAt;
	/** Name of the reaction's emoji. */
	emoji;
	/** The ID of the external user that created the reaction. */
	externalUserId;
	/** The ID of the entity. */
	id;
	/** The ID of the initiative update that the reaction is associated with. */
	initiativeUpdateId;
	/** The ID of the issue that the reaction is associated with. */
	issueId;
	/** The ID of the post that the reaction is associated with. */
	postId;
	/** The ID of the project update that the reaction is associated with. */
	projectUpdateId;
	/** The time at which the entity was updated. */
	updatedAt;
	/** The ID of the user that created the reaction. */
	userId;
	/** The comment the reaction is associated with. */
	comment;
	/** The issue the reaction is associated with. */
	issue;
	/** The project update the reaction is associated with. */
	projectUpdate;
	/** The user that created the reaction. */
	user;
};
/**
* A generic payload return from entity archive mutations.
*
* @param request - function to call the graphql client
* @param data - L.ReleaseArchivePayloadFragment response data
*/
var ReleaseArchivePayload = class extends Request {
	constructor(request, data) {
		super(request);
		this.lastSyncId = data.lastSyncId;
		this.success = data.success;
	}
	/** The identifier of the last sync operation. */
	lastSyncId;
	/** Whether the operation was successful. */
	success;
};
/**
* ReleasePayload model
*
* @param request - function to call the graphql client
* @param data - L.ReleasePayloadFragment response data
*/
var ReleasePayload = class extends Request {
	constructor(request, data) {
		super(request);
		this.lastSyncId = data.lastSyncId;
		this.success = data.success;
	}
	/** The identifier of the last sync operation. */
	lastSyncId;
	/** Whether the operation was successful. */
	success;
};
/**
* A generic payload return from entity archive mutations.
*
* @param request - function to call the graphql client
* @param data - L.ReleasePipelineArchivePayloadFragment response data
*/
var ReleasePipelineArchivePayload = class extends Request {
	constructor(request, data) {
		super(request);
		this.lastSyncId = data.lastSyncId;
		this.success = data.success;
	}
	/** The identifier of the last sync operation. */
	lastSyncId;
	/** Whether the operation was successful. */
	success;
};
/**
* ReleasePipelinePayload model
*
* @param request - function to call the graphql client
* @param data - L.ReleasePipelinePayloadFragment response data
*/
var ReleasePipelinePayload = class extends Request {
	constructor(request, data) {
		super(request);
		this.lastSyncId = data.lastSyncId;
		this.success = data.success;
	}
	/** The identifier of the last sync operation. */
	lastSyncId;
	/** Whether the operation was successful. */
	success;
};
/**
* A generic payload return from entity archive mutations.
*
* @param request - function to call the graphql client
* @param data - L.ReleaseStageArchivePayloadFragment response data
*/
var ReleaseStageArchivePayload = class extends Request {
	constructor(request, data) {
		super(request);
		this.lastSyncId = data.lastSyncId;
		this.success = data.success;
	}
	/** The identifier of the last sync operation. */
	lastSyncId;
	/** Whether the operation was successful. */
	success;
};
/**
* ReleaseStagePayload model
*
* @param request - function to call the graphql client
* @param data - L.ReleaseStagePayloadFragment response data
*/
var ReleaseStagePayload = class extends Request {
	constructor(request, data) {
		super(request);
		this.lastSyncId = data.lastSyncId;
		this.success = data.success;
	}
	/** The identifier of the last sync operation. */
	lastSyncId;
	/** Whether the operation was successful. */
	success;
};
/**
* RepositorySuggestion model
*
* @param request - function to call the graphql client
* @param data - L.RepositorySuggestionFragment response data
*/
var RepositorySuggestion = class extends Request {
	constructor(request, data) {
		super(request);
		this.confidence = data.confidence;
		this.hostname = data.hostname ?? void 0;
		this.repositoryFullName = data.repositoryFullName;
	}
	/** Confidence score from 0.0 to 1.0. */
	confidence;
	/** Hostname of the Git service (e.g., 'github.com', 'github.company.com'). */
	hostname;
	/** The full name of the repository in owner/name format (e.g., 'acme/backend'). */
	repositoryFullName;
};
/**
* RepositorySuggestionsPayload model
*
* @param request - function to call the graphql client
* @param data - L.RepositorySuggestionsPayloadFragment response data
*/
var RepositorySuggestionsPayload = class extends Request {
	constructor(request, data) {
		super(request);
		this.suggestions = data.suggestions.map((node) => new RepositorySuggestion(request, node));
	}
	/** The suggested repositories. */
	suggestions;
};
/**
* [Deprecated] A roadmap for projects.
*
* @param request - function to call the graphql client
* @param data - L.RoadmapFragment response data
*/
var Roadmap = class extends Request {
	_creator;
	_owner;
	constructor(request, data) {
		super(request);
		this.archivedAt = parseDate(data.archivedAt) ?? void 0;
		this.color = data.color ?? void 0;
		this.createdAt = parseDate(data.createdAt) ?? /* @__PURE__ */ new Date();
		this.description = data.description ?? void 0;
		this.id = data.id;
		this.name = data.name;
		this.slugId = data.slugId;
		this.sortOrder = data.sortOrder;
		this.updatedAt = parseDate(data.updatedAt) ?? /* @__PURE__ */ new Date();
		this.url = data.url;
		this._creator = data.creator;
		this._owner = data.owner ?? void 0;
	}
	/** The time at which the entity was archived. Null if the entity has not been archived. */
	archivedAt;
	/** The roadmap's color. */
	color;
	/** The time at which the entity was created. */
	createdAt;
	/** The description of the roadmap. */
	description;
	/** The unique identifier of the entity. */
	id;
	/** The name of the roadmap. */
	name;
	/** The roadmap's unique URL slug. */
	slugId;
	/** The sort order of the roadmap within the organization. */
	sortOrder;
	/**
	* The last time at which the entity was meaningfully updated. This is the same as the creation time if the entity hasn't
	*     been updated after creation.
	*/
	updatedAt;
	/** The canonical url for the roadmap. */
	url;
	/** The user who created the roadmap. */
	get creator() {
		return new UserQuery(this._request).fetch(this._creator.id);
	}
	/** The ID of user who created the roadmap. */
	get creatorId() {
		return this._creator?.id;
	}
	/** The organization of the roadmap. */
	get organization() {
		return new OrganizationQuery(this._request).fetch();
	}
	/** The user who owns the roadmap. */
	get owner() {
		return this._owner?.id ? new UserQuery(this._request).fetch(this._owner?.id) : void 0;
	}
	/** The ID of user who owns the roadmap. */
	get ownerId() {
		return this._owner?.id;
	}
	/** Projects associated with the roadmap. */
	projects(variables) {
		return new Roadmap_ProjectsQuery(this._request, this.id, variables).fetch(variables);
	}
	/** Archives a roadmap. */
	archive() {
		return new ArchiveRoadmapMutation(this._request).fetch(this.id);
	}
	/** Creates a new roadmap. */
	create(input) {
		return new CreateRoadmapMutation(this._request).fetch(input);
	}
	/** Deletes a roadmap. */
	delete() {
		return new DeleteRoadmapMutation(this._request).fetch(this.id);
	}
	/** Unarchives a roadmap. */
	unarchive() {
		return new UnarchiveRoadmapMutation(this._request).fetch(this.id);
	}
	/** Updates a roadmap. */
	update(input) {
		return new UpdateRoadmapMutation(this._request).fetch(this.id, input);
	}
};
/**
* A generic payload return from entity archive mutations.
*
* @param request - function to call the graphql client
* @param data - L.RoadmapArchivePayloadFragment response data
*/
var RoadmapArchivePayload = class extends Request {
	_entity;
	constructor(request, data) {
		super(request);
		this.lastSyncId = data.lastSyncId;
		this.success = data.success;
		this._entity = data.entity ?? void 0;
	}
	/** The identifier of the last sync operation. */
	lastSyncId;
	/** Whether the operation was successful. */
	success;
	/** The archived/unarchived entity. Null if entity was deleted. */
	get entity() {
		return this._entity?.id ? new RoadmapQuery(this._request).fetch(this._entity?.id) : void 0;
	}
	/** The ID of archived/unarchived entity. null if entity was deleted. */
	get entityId() {
		return this._entity?.id;
	}
};
/**
* RoadmapConnection model
*
* @param request - function to call the graphql client
* @param fetch - function to trigger a refetch of this RoadmapConnection model
* @param data - RoadmapConnection response data
*/
var RoadmapConnection = class extends Connection {
	constructor(request, fetch$1, data) {
		super(request, fetch$1, data.nodes.map((node) => new Roadmap(request, node)), new PageInfo(request, data.pageInfo));
	}
};
/**
* RoadmapPayload model
*
* @param request - function to call the graphql client
* @param data - L.RoadmapPayloadFragment response data
*/
var RoadmapPayload = class extends Request {
	_roadmap;
	constructor(request, data) {
		super(request);
		this.lastSyncId = data.lastSyncId;
		this.success = data.success;
		this._roadmap = data.roadmap;
	}
	/** The identifier of the last sync operation. */
	lastSyncId;
	/** Whether the operation was successful. */
	success;
	/** The roadmap that was created or updated. */
	get roadmap() {
		return new RoadmapQuery(this._request).fetch(this._roadmap.id);
	}
	/** The ID of roadmap that was created or updated. */
	get roadmapId() {
		return this._roadmap?.id;
	}
};
/**
* [Deprecated] Join table between projects and roadmaps.
*
* @param request - function to call the graphql client
* @param data - L.RoadmapToProjectFragment response data
*/
var RoadmapToProject = class extends Request {
	_project;
	_roadmap;
	constructor(request, data) {
		super(request);
		this.archivedAt = parseDate(data.archivedAt) ?? void 0;
		this.createdAt = parseDate(data.createdAt) ?? /* @__PURE__ */ new Date();
		this.id = data.id;
		this.sortOrder = data.sortOrder;
		this.updatedAt = parseDate(data.updatedAt) ?? /* @__PURE__ */ new Date();
		this._project = data.project;
		this._roadmap = data.roadmap;
	}
	/** The time at which the entity was archived. Null if the entity has not been archived. */
	archivedAt;
	/** The time at which the entity was created. */
	createdAt;
	/** The unique identifier of the entity. */
	id;
	/** The sort order of the project within the roadmap. */
	sortOrder;
	/**
	* The last time at which the entity was meaningfully updated. This is the same as the creation time if the entity hasn't
	*     been updated after creation.
	*/
	updatedAt;
	/** The project that the roadmap is associated with. */
	get project() {
		return new ProjectQuery(this._request).fetch(this._project.id);
	}
	/** The ID of project that the roadmap is associated with. */
	get projectId() {
		return this._project?.id;
	}
	/** The roadmap that the project is associated with. */
	get roadmap() {
		return new RoadmapQuery(this._request).fetch(this._roadmap.id);
	}
	/** The ID of roadmap that the project is associated with. */
	get roadmapId() {
		return this._roadmap?.id;
	}
	/** Creates a new roadmapToProject join. */
	create(input) {
		return new CreateRoadmapToProjectMutation(this._request).fetch(input);
	}
	/** Deletes a roadmapToProject. */
	delete() {
		return new DeleteRoadmapToProjectMutation(this._request).fetch(this.id);
	}
	/** Updates a roadmapToProject. */
	update(input) {
		return new UpdateRoadmapToProjectMutation(this._request).fetch(this.id, input);
	}
};
/**
* RoadmapToProjectConnection model
*
* @param request - function to call the graphql client
* @param fetch - function to trigger a refetch of this RoadmapToProjectConnection model
* @param data - RoadmapToProjectConnection response data
*/
var RoadmapToProjectConnection = class extends Connection {
	constructor(request, fetch$1, data) {
		super(request, fetch$1, data.nodes.map((node) => new RoadmapToProject(request, node)), new PageInfo(request, data.pageInfo));
	}
};
/**
* RoadmapToProjectPayload model
*
* @param request - function to call the graphql client
* @param data - L.RoadmapToProjectPayloadFragment response data
*/
var RoadmapToProjectPayload = class extends Request {
	_roadmapToProject;
	constructor(request, data) {
		super(request);
		this.lastSyncId = data.lastSyncId;
		this.success = data.success;
		this._roadmapToProject = data.roadmapToProject;
	}
	/** The identifier of the last sync operation. */
	lastSyncId;
	/** Whether the operation was successful. */
	success;
	/** The roadmapToProject that was created or updated. */
	get roadmapToProject() {
		return new RoadmapToProjectQuery(this._request).fetch(this._roadmapToProject.id);
	}
	/** The ID of roadmaptoproject that was created or updated. */
	get roadmapToProjectId() {
		return this._roadmapToProject?.id;
	}
};
/**
* Payload returned by semantic search.
*
* @param request - function to call the graphql client
* @param data - L.SemanticSearchPayloadFragment response data
*/
var SemanticSearchPayload = class extends Request {
	constructor(request, data) {
		super(request);
		this.enabled = data.enabled;
		this.results = data.results.map((node) => new SemanticSearchResult(request, node));
	}
	/** Whether the semantic search is enabled. */
	enabled;
	results;
};
/**
* A semantic search result reference.
*
* @param request - function to call the graphql client
* @param data - L.SemanticSearchResultFragment response data
*/
var SemanticSearchResult = class extends Request {
	_document;
	_initiative;
	_issue;
	_project;
	constructor(request, data) {
		super(request);
		this.id = data.id;
		this.type = data.type;
		this._document = data.document ?? void 0;
		this._initiative = data.initiative ?? void 0;
		this._issue = data.issue ?? void 0;
		this._project = data.project ?? void 0;
	}
	/** The unique identifier of the entity. */
	id;
	/** The type of the semantic search result. */
	type;
	/** The document related to the semantic search result. */
	get document() {
		return this._document?.id ? new DocumentQuery(this._request).fetch(this._document?.id) : void 0;
	}
	/** The ID of document related to the semantic search result. */
	get documentId() {
		return this._document?.id;
	}
	/** The initiative related to the semantic search result. */
	get initiative() {
		return this._initiative?.id ? new InitiativeQuery(this._request).fetch(this._initiative?.id) : void 0;
	}
	/** The ID of initiative related to the semantic search result. */
	get initiativeId() {
		return this._initiative?.id;
	}
	/** The issue related to the semantic search result. */
	get issue() {
		return this._issue?.id ? new IssueQuery(this._request).fetch(this._issue?.id) : void 0;
	}
	/** The ID of issue related to the semantic search result. */
	get issueId() {
		return this._issue?.id;
	}
	/** The project related to the semantic search result. */
	get project() {
		return this._project?.id ? new ProjectQuery(this._request).fetch(this._project?.id) : void 0;
	}
	/** The ID of project related to the semantic search result. */
	get projectId() {
		return this._project?.id;
	}
};
/**
* SES domain identity used for sending emails from a custom domain.
*
* @param request - function to call the graphql client
* @param data - L.SesDomainIdentityFragment response data
*/
var SesDomainIdentity = class extends Request {
	_creator;
	constructor(request, data) {
		super(request);
		this.archivedAt = parseDate(data.archivedAt) ?? void 0;
		this.canSendFromCustomDomain = data.canSendFromCustomDomain;
		this.createdAt = parseDate(data.createdAt) ?? /* @__PURE__ */ new Date();
		this.domain = data.domain;
		this.id = data.id;
		this.region = data.region;
		this.updatedAt = parseDate(data.updatedAt) ?? /* @__PURE__ */ new Date();
		this.dnsRecords = data.dnsRecords.map((node) => new SesDomainIdentityDnsRecord(request, node));
		this._creator = data.creator ?? void 0;
	}
	/** The time at which the entity was archived. Null if the entity has not been archived. */
	archivedAt;
	/** Whether the domain is fully verified and can be used for sending emails. */
	canSendFromCustomDomain;
	/** The time at which the entity was created. */
	createdAt;
	/** The domain of the SES domain identity. */
	domain;
	/** The unique identifier of the entity. */
	id;
	/** The AWS region of the SES domain identity. */
	region;
	/**
	* The last time at which the entity was meaningfully updated. This is the same as the creation time if the entity hasn't
	*     been updated after creation.
	*/
	updatedAt;
	/** The DNS records for the SES domain identity. */
	dnsRecords;
	/** The user who created the SES domain identity. */
	get creator() {
		return this._creator?.id ? new UserQuery(this._request).fetch(this._creator?.id) : void 0;
	}
	/** The ID of user who created the ses domain identity. */
	get creatorId() {
		return this._creator?.id;
	}
	/** The organization of the SES domain identity. */
	get organization() {
		return new OrganizationQuery(this._request).fetch();
	}
};
/**
* A DNS record for a SES domain identity.
*
* @param request - function to call the graphql client
* @param data - L.SesDomainIdentityDnsRecordFragment response data
*/
var SesDomainIdentityDnsRecord = class extends Request {
	constructor(request, data) {
		super(request);
		this.content = data.content;
		this.isVerified = data.isVerified;
		this.name = data.name;
		this.type = data.type;
	}
	/** The content of the DNS record. */
	content;
	/** Whether the DNS record is verified in the domain's DNS configuration. */
	isVerified;
	/** The name of the DNS record. */
	name;
	/** The type of the DNS record. */
	type;
};
/**
* Tuple for mapping Slack channel IDs to names.
*
* @param request - function to call the graphql client
* @param data - L.SlackAsksTeamSettingsFragment response data
*/
var SlackAsksTeamSettings = class extends Request {
	constructor(request, data) {
		super(request);
		this.hasDefaultAsk = data.hasDefaultAsk;
		this.id = data.id;
	}
	/** Whether the default Asks template is enabled in the given channel for this team. */
	hasDefaultAsk;
	/** The Linear team ID. */
	id;
};
/**
* SlackChannelConnectPayload model
*
* @param request - function to call the graphql client
* @param data - L.SlackChannelConnectPayloadFragment response data
*/
var SlackChannelConnectPayload = class extends Request {
	_integration;
	constructor(request, data) {
		super(request);
		this.addBot = data.addBot;
		this.lastSyncId = data.lastSyncId;
		this.nudgeToConnectMainSlackIntegration = data.nudgeToConnectMainSlackIntegration ?? void 0;
		this.nudgeToUpdateMainSlackIntegration = data.nudgeToUpdateMainSlackIntegration ?? void 0;
		this.success = data.success;
		this._integration = data.integration ?? void 0;
	}
	/** Whether the bot needs to be manually added to the channel. */
	addBot;
	/** The identifier of the last sync operation. */
	lastSyncId;
	/** Whether it's recommended to connect main Slack integration. */
	nudgeToConnectMainSlackIntegration;
	/** Whether it's recommended to update main Slack integration. */
	nudgeToUpdateMainSlackIntegration;
	/** Whether the operation was successful. */
	success;
	/** The integration that was created or updated. */
	get integration() {
		return this._integration?.id ? new IntegrationQuery(this._request).fetch(this._integration?.id) : void 0;
	}
	/** The ID of integration that was created or updated. */
	get integrationId() {
		return this._integration?.id;
	}
};
/**
* Object for mapping Slack channel IDs to names and other settings.
*
* @param request - function to call the graphql client
* @param data - L.SlackChannelNameMappingFragment response data
*/
var SlackChannelNameMapping = class extends Request {
	constructor(request, data) {
		super(request);
		this.aiTitles = data.aiTitles ?? void 0;
		this.autoCreateOnBotMention = data.autoCreateOnBotMention ?? void 0;
		this.autoCreateOnEmoji = data.autoCreateOnEmoji ?? void 0;
		this.autoCreateOnMessage = data.autoCreateOnMessage ?? void 0;
		this.autoCreateTemplateId = data.autoCreateTemplateId ?? void 0;
		this.botAdded = data.botAdded ?? void 0;
		this.id = data.id;
		this.isPrivate = data.isPrivate ?? void 0;
		this.isShared = data.isShared ?? void 0;
		this.name = data.name;
		this.postAcceptedFromTriageUpdates = data.postAcceptedFromTriageUpdates ?? void 0;
		this.postCancellationUpdates = data.postCancellationUpdates ?? void 0;
		this.postCompletionUpdates = data.postCompletionUpdates ?? void 0;
		this.teams = data.teams.map((node) => new SlackAsksTeamSettings(request, node));
	}
	/** Whether or not to use AI to generate titles for Asks created in this channel. */
	aiTitles;
	/** Whether or not @-mentioning the bot should automatically create an Ask with the message. */
	autoCreateOnBotMention;
	/** Whether or not using the :ticket: emoji in this channel should automatically create Asks. */
	autoCreateOnEmoji;
	/** Whether or not top-level messages in this channel should automatically create Asks. */
	autoCreateOnMessage;
	/** The optional template ID to use for Asks auto-created in this channel. If not set, auto-created Asks won't use any template. */
	autoCreateTemplateId;
	/** Whether or not the Linear Asks bot has been added to this Slack channel. */
	botAdded;
	/** The Slack channel ID. */
	id;
	/** Whether or not the Slack channel is private. */
	isPrivate;
	/** Whether or not the Slack channel is shared with an external org. */
	isShared;
	/** The Slack channel name. */
	name;
	/** Whether or not synced Slack threads should be updated with a message when their Ask is accepted from triage. */
	postAcceptedFromTriageUpdates;
	/** Whether or not synced Slack threads should be updated with a message and emoji when their Ask is canceled. */
	postCancellationUpdates;
	/** Whether or not synced Slack threads should be updated with a message and emoji when their Ask is completed. */
	postCompletionUpdates;
	/** Which teams are connected to the channel and settings for those teams. */
	teams;
};
/**
* SsoUrlFromEmailResponse model
*
* @param request - function to call the graphql client
* @param data - L.SsoUrlFromEmailResponseFragment response data
*/
var SsoUrlFromEmailResponse = class extends Request {
	constructor(request, data) {
		super(request);
		this.samlSsoUrl = data.samlSsoUrl;
		this.success = data.success;
	}
	/** SAML SSO sign-in URL. */
	samlSsoUrl;
	/** Whether the operation was successful. */
	success;
};
/**
* SuccessPayload model
*
* @param request - function to call the graphql client
* @param data - L.SuccessPayloadFragment response data
*/
var SuccessPayload = class extends Request {
	constructor(request, data) {
		super(request);
		this.lastSyncId = data.lastSyncId;
		this.success = data.success;
	}
	/** The identifier of the last sync operation. */
	lastSyncId;
	/** Whether the operation was successful. */
	success;
};
/**
* A comment thread that is synced with an external source.
*
* @param request - function to call the graphql client
* @param data - L.SyncedExternalThreadFragment response data
*/
var SyncedExternalThread = class extends Request {
	constructor(request, data) {
		super(request);
		this.displayName = data.displayName ?? void 0;
		this.id = data.id ?? void 0;
		this.isConnected = data.isConnected;
		this.isPersonalIntegrationConnected = data.isPersonalIntegrationConnected;
		this.isPersonalIntegrationRequired = data.isPersonalIntegrationRequired;
		this.name = data.name ?? void 0;
		this.subType = data.subType ?? void 0;
		this.type = data.type;
		this.url = data.url ?? void 0;
	}
	/** The display name of the thread. */
	displayName;
	id;
	/** Whether this thread is syncing with the external service. */
	isConnected;
	/** Whether the current user has the corresponding personal integration connected for the external service. */
	isPersonalIntegrationConnected;
	/** Whether a connected personal integration is required to comment in this thread. */
	isPersonalIntegrationRequired;
	/** The display name of the source. */
	name;
	/** The sub type of the external source. */
	subType;
	/** The type of the external source. */
	type;
	/** The external url of the thread. */
	url;
};
/**
* An organizational unit that contains issues.
*
* @param request - function to call the graphql client
* @param data - L.TeamFragment response data
*/
var Team = class extends Request {
	_activeCycle;
	_defaultIssueState;
	_defaultProjectTemplate;
	_defaultTemplateForMembers;
	_defaultTemplateForNonMembers;
	_draftWorkflowState;
	_integrationsSettings;
	_markedAsDuplicateWorkflowState;
	_mergeWorkflowState;
	_mergeableWorkflowState;
	_reviewWorkflowState;
	_startWorkflowState;
	_triageIssueState;
	_triageResponsibility;
	constructor(request, data) {
		super(request);
		this.aiDiscussionSummariesEnabled = data.aiDiscussionSummariesEnabled;
		this.aiThreadSummariesEnabled = data.aiThreadSummariesEnabled;
		this.allMembersCanJoin = data.allMembersCanJoin ?? void 0;
		this.archivedAt = parseDate(data.archivedAt) ?? void 0;
		this.autoArchivePeriod = data.autoArchivePeriod;
		this.autoCloseChildIssues = data.autoCloseChildIssues ?? void 0;
		this.autoCloseParentIssues = data.autoCloseParentIssues ?? void 0;
		this.autoClosePeriod = data.autoClosePeriod ?? void 0;
		this.autoCloseStateId = data.autoCloseStateId ?? void 0;
		this.color = data.color ?? void 0;
		this.createdAt = parseDate(data.createdAt) ?? /* @__PURE__ */ new Date();
		this.cycleCalenderUrl = data.cycleCalenderUrl;
		this.cycleCooldownTime = data.cycleCooldownTime;
		this.cycleDuration = data.cycleDuration;
		this.cycleIssueAutoAssignCompleted = data.cycleIssueAutoAssignCompleted;
		this.cycleIssueAutoAssignStarted = data.cycleIssueAutoAssignStarted;
		this.cycleLockToActive = data.cycleLockToActive;
		this.cycleStartDay = data.cycleStartDay;
		this.cyclesEnabled = data.cyclesEnabled;
		this.defaultIssueEstimate = data.defaultIssueEstimate;
		this.defaultTemplateForMembersId = data.defaultTemplateForMembersId ?? void 0;
		this.defaultTemplateForNonMembersId = data.defaultTemplateForNonMembersId ?? void 0;
		this.description = data.description ?? void 0;
		this.displayName = data.displayName;
		this.groupIssueHistory = data.groupIssueHistory;
		this.icon = data.icon ?? void 0;
		this.id = data.id;
		this.inheritIssueEstimation = data.inheritIssueEstimation;
		this.inheritWorkflowStatuses = data.inheritWorkflowStatuses;
		this.inviteHash = data.inviteHash;
		this.issueCount = data.issueCount;
		this.issueEstimationAllowZero = data.issueEstimationAllowZero;
		this.issueEstimationExtended = data.issueEstimationExtended;
		this.issueEstimationType = data.issueEstimationType;
		this.issueOrderingNoPriorityFirst = data.issueOrderingNoPriorityFirst;
		this.issueSortOrderDefaultToBottom = data.issueSortOrderDefaultToBottom;
		this.key = data.key;
		this.name = data.name;
		this.private = data.private;
		this.requirePriorityToLeaveTriage = data.requirePriorityToLeaveTriage;
		this.scimGroupName = data.scimGroupName ?? void 0;
		this.scimManaged = data.scimManaged;
		this.securitySettings = data.securitySettings;
		this.setIssueSortOrderOnStateChange = data.setIssueSortOrderOnStateChange;
		this.slackIssueComments = data.slackIssueComments;
		this.slackIssueStatuses = data.slackIssueStatuses;
		this.slackNewIssue = data.slackNewIssue;
		this.timezone = data.timezone;
		this.triageEnabled = data.triageEnabled;
		this.upcomingCycleCount = data.upcomingCycleCount;
		this.updatedAt = parseDate(data.updatedAt) ?? /* @__PURE__ */ new Date();
		this._activeCycle = data.activeCycle ?? void 0;
		this._defaultIssueState = data.defaultIssueState ?? void 0;
		this._defaultProjectTemplate = data.defaultProjectTemplate ?? void 0;
		this._defaultTemplateForMembers = data.defaultTemplateForMembers ?? void 0;
		this._defaultTemplateForNonMembers = data.defaultTemplateForNonMembers ?? void 0;
		this._draftWorkflowState = data.draftWorkflowState ?? void 0;
		this._integrationsSettings = data.integrationsSettings ?? void 0;
		this._markedAsDuplicateWorkflowState = data.markedAsDuplicateWorkflowState ?? void 0;
		this._mergeWorkflowState = data.mergeWorkflowState ?? void 0;
		this._mergeableWorkflowState = data.mergeableWorkflowState ?? void 0;
		this._reviewWorkflowState = data.reviewWorkflowState ?? void 0;
		this._startWorkflowState = data.startWorkflowState ?? void 0;
		this._triageIssueState = data.triageIssueState ?? void 0;
		this._triageResponsibility = data.triageResponsibility ?? void 0;
	}
	/** Whether to enable AI discussion summaries for issues in this team. */
	aiDiscussionSummariesEnabled;
	/** Whether to enable resolved thread AI summaries. */
	aiThreadSummariesEnabled;
	/** Whether all members in the workspace can join the team. Only used for public teams. */
	allMembersCanJoin;
	/** The time at which the entity was archived. Null if the entity has not been archived. */
	archivedAt;
	/** Period after which automatically closed and completed issues are automatically archived in months. */
	autoArchivePeriod;
	/** Whether child issues should automatically close when their parent issue is closed */
	autoCloseChildIssues;
	/** Whether parent issues should automatically close when all child issues are closed */
	autoCloseParentIssues;
	/** Period after which issues are automatically closed in months. Null/undefined means disabled. */
	autoClosePeriod;
	/** The canceled workflow state which auto closed issues will be set to. Defaults to the first canceled state. */
	autoCloseStateId;
	/** The team's color. */
	color;
	/** The time at which the entity was created. */
	createdAt;
	/** Calendar feed URL (iCal) for cycles. */
	cycleCalenderUrl;
	/** The cooldown time after each cycle in weeks. */
	cycleCooldownTime;
	/** The duration of a cycle in weeks. */
	cycleDuration;
	/** Auto assign completed issues to current cycle. */
	cycleIssueAutoAssignCompleted;
	/** Auto assign started issues to current cycle. */
	cycleIssueAutoAssignStarted;
	/** Auto assign issues to current cycle if in active status. */
	cycleLockToActive;
	/** The day of the week that a new cycle starts. */
	cycleStartDay;
	/** Whether the team uses cycles. */
	cyclesEnabled;
	/** What to use as a default estimate for unestimated issues. */
	defaultIssueEstimate;
	/** The id of the default template to use for new issues created by members of the team. */
	defaultTemplateForMembersId;
	/** The id of the default template to use for new issues created by non-members of the team. */
	defaultTemplateForNonMembersId;
	/** The team's description. */
	description;
	/** The name of the team including its parent team name if it has one. */
	displayName;
	/** Whether to group recent issue history entries. */
	groupIssueHistory;
	/** The icon of the team. */
	icon;
	/** The unique identifier of the entity. */
	id;
	/** Whether the team should inherit its estimation settings from its parent. Only applies to sub-teams. */
	inheritIssueEstimation;
	/** Whether the team should inherit its workflow statuses from its parent. Only applies to sub-teams. */
	inheritWorkflowStatuses;
	/** [DEPRECATED] Unique hash for the team to be used in invite URLs. */
	inviteHash;
	/** Number of issues in the team. */
	issueCount;
	/** Whether to allow zeros in issues estimates. */
	issueEstimationAllowZero;
	/** Whether to add additional points to the estimate scale. */
	issueEstimationExtended;
	/** The issue estimation type to use. Must be one of "notUsed", "exponential", "fibonacci", "linear", "tShirt". */
	issueEstimationType;
	/** [DEPRECATED] Whether issues without priority should be sorted first. */
	issueOrderingNoPriorityFirst;
	/** [DEPRECATED] Whether to move issues to bottom of the column when changing state. */
	issueSortOrderDefaultToBottom;
	/** The team's unique key. The key is used in URLs. */
	key;
	/** The team's name. */
	name;
	/** Whether the team is private or not. */
	private;
	/** Whether an issue needs to have a priority set before leaving triage. */
	requirePriorityToLeaveTriage;
	/** The SCIM group name for the team. */
	scimGroupName;
	/** Whether the team is managed by SCIM integration. */
	scimManaged;
	/** Security settings for the team. */
	securitySettings;
	/** Where to move issues when changing state. */
	setIssueSortOrderOnStateChange;
	/** Whether to send new issue comment notifications to Slack. */
	slackIssueComments;
	/** Whether to send new issue status updates to Slack. */
	slackIssueStatuses;
	/** Whether to send new issue notifications to Slack. */
	slackNewIssue;
	/** The timezone of the team. Defaults to "America/Los_Angeles" */
	timezone;
	/** Whether triage mode is enabled for the team or not. */
	triageEnabled;
	/** How many upcoming cycles to create. */
	upcomingCycleCount;
	/**
	* The last time at which the entity was meaningfully updated. This is the same as the creation time if the entity hasn't
	*     been updated after creation.
	*/
	updatedAt;
	/** Team's currently active cycle. */
	get activeCycle() {
		return this._activeCycle?.id ? new CycleQuery(this._request).fetch(this._activeCycle?.id) : void 0;
	}
	/** The ID of team's currently active cycle. */
	get activeCycleId() {
		return this._activeCycle?.id;
	}
	/** The default workflow state into which issues are set when they are opened by team members. */
	get defaultIssueState() {
		return this._defaultIssueState?.id ? new WorkflowStateQuery(this._request).fetch(this._defaultIssueState?.id) : void 0;
	}
	/** The ID of default workflow state into which issues are set when they are opened by team members. */
	get defaultIssueStateId() {
		return this._defaultIssueState?.id;
	}
	/** The default template to use for new projects created for the team. */
	get defaultProjectTemplate() {
		return this._defaultProjectTemplate?.id ? new TemplateQuery(this._request).fetch(this._defaultProjectTemplate?.id) : void 0;
	}
	/** The ID of default template to use for new projects created for the team. */
	get defaultProjectTemplateId() {
		return this._defaultProjectTemplate?.id;
	}
	/** The default template to use for new issues created by members of the team. */
	get defaultTemplateForMembers() {
		return this._defaultTemplateForMembers?.id ? new TemplateQuery(this._request).fetch(this._defaultTemplateForMembers?.id) : void 0;
	}
	/** The default template to use for new issues created by non-members of the team. */
	get defaultTemplateForNonMembers() {
		return this._defaultTemplateForNonMembers?.id ? new TemplateQuery(this._request).fetch(this._defaultTemplateForNonMembers?.id) : void 0;
	}
	/** The workflow state into which issues are moved when a PR has been opened as draft. */
	get draftWorkflowState() {
		return this._draftWorkflowState?.id ? new WorkflowStateQuery(this._request).fetch(this._draftWorkflowState?.id) : void 0;
	}
	/** The ID of workflow state into which issues are moved when a pr has been opened as draft. */
	get draftWorkflowStateId() {
		return this._draftWorkflowState?.id;
	}
	/** Settings for all integrations associated with that team. */
	get integrationsSettings() {
		return this._integrationsSettings?.id ? new IntegrationsSettingsQuery(this._request).fetch(this._integrationsSettings?.id) : void 0;
	}
	/** The ID of settings for all integrations associated with that team. */
	get integrationsSettingsId() {
		return this._integrationsSettings?.id;
	}
	/** The workflow state into which issues are moved when they are marked as a duplicate of another issue. Defaults to the first canceled state. */
	get markedAsDuplicateWorkflowState() {
		return this._markedAsDuplicateWorkflowState?.id ? new WorkflowStateQuery(this._request).fetch(this._markedAsDuplicateWorkflowState?.id) : void 0;
	}
	/** The ID of workflow state into which issues are moved when they are marked as a duplicate of another issue. defaults to the first canceled state. */
	get markedAsDuplicateWorkflowStateId() {
		return this._markedAsDuplicateWorkflowState?.id;
	}
	/** The workflow state into which issues are moved when a PR has been merged. */
	get mergeWorkflowState() {
		return this._mergeWorkflowState?.id ? new WorkflowStateQuery(this._request).fetch(this._mergeWorkflowState?.id) : void 0;
	}
	/** The ID of workflow state into which issues are moved when a pr has been merged. */
	get mergeWorkflowStateId() {
		return this._mergeWorkflowState?.id;
	}
	/** The workflow state into which issues are moved when a PR is ready to be merged. */
	get mergeableWorkflowState() {
		return this._mergeableWorkflowState?.id ? new WorkflowStateQuery(this._request).fetch(this._mergeableWorkflowState?.id) : void 0;
	}
	/** The ID of workflow state into which issues are moved when a pr is ready to be merged. */
	get mergeableWorkflowStateId() {
		return this._mergeableWorkflowState?.id;
	}
	/** The organization that the team is associated with. */
	get organization() {
		return new OrganizationQuery(this._request).fetch();
	}
	/** The workflow state into which issues are moved when a review has been requested for the PR. */
	get reviewWorkflowState() {
		return this._reviewWorkflowState?.id ? new WorkflowStateQuery(this._request).fetch(this._reviewWorkflowState?.id) : void 0;
	}
	/** The ID of workflow state into which issues are moved when a review has been requested for the pr. */
	get reviewWorkflowStateId() {
		return this._reviewWorkflowState?.id;
	}
	/** The workflow state into which issues are moved when a PR has been opened. */
	get startWorkflowState() {
		return this._startWorkflowState?.id ? new WorkflowStateQuery(this._request).fetch(this._startWorkflowState?.id) : void 0;
	}
	/** The ID of workflow state into which issues are moved when a pr has been opened. */
	get startWorkflowStateId() {
		return this._startWorkflowState?.id;
	}
	/** The workflow state into which issues are set when they are opened by non-team members or integrations if triage is enabled. */
	get triageIssueState() {
		return this._triageIssueState?.id ? new WorkflowStateQuery(this._request).fetch(this._triageIssueState?.id) : void 0;
	}
	/** The ID of workflow state into which issues are set when they are opened by non-team members or integrations if triage is enabled. */
	get triageIssueStateId() {
		return this._triageIssueState?.id;
	}
	/** Team's triage responsibility. */
	get triageResponsibility() {
		return this._triageResponsibility?.id ? new TriageResponsibilityQuery(this._request).fetch(this._triageResponsibility?.id) : void 0;
	}
	/** The ID of team's triage responsibility. */
	get triageResponsibilityId() {
		return this._triageResponsibility?.id;
	}
	/** Cycles associated with the team. */
	cycles(variables) {
		return new Team_CyclesQuery(this._request, this.id, variables).fetch(variables);
	}
	/** The Git automation states for the team. */
	gitAutomationStates(variables) {
		return new Team_GitAutomationStatesQuery(this._request, this.id, variables).fetch(variables);
	}
	/** Issues associated with the team. */
	issues(variables) {
		return new Team_IssuesQuery(this._request, this.id, variables).fetch(variables);
	}
	/** Labels associated with the team. */
	labels(variables) {
		return new Team_LabelsQuery(this._request, this.id, variables).fetch(variables);
	}
	/** Users who are members of this team. */
	members(variables) {
		return new Team_MembersQuery(this._request, this.id, variables).fetch(variables);
	}
	/** Memberships associated with the team. For easier access of the same data, use `members` query. */
	memberships(variables) {
		return new Team_MembershipsQuery(this._request, this.id, variables).fetch(variables);
	}
	/** Projects associated with the team. */
	projects(variables) {
		return new Team_ProjectsQuery(this._request, this.id, variables).fetch(variables);
	}
	/** The states that define the workflow associated with the team. */
	states(variables) {
		return new Team_StatesQuery(this._request, this.id, variables).fetch(variables);
	}
	/** Templates associated with the team. */
	templates(variables) {
		return new Team_TemplatesQuery(this._request, this.id, variables).fetch(variables);
	}
	/** Webhooks associated with the team. */
	webhooks(variables) {
		return new Team_WebhooksQuery(this._request, this.id, variables).fetch(variables);
	}
	/** Creates a new team. The user who creates the team will automatically be added as a member to the newly created team. */
	create(input, variables) {
		return new CreateTeamMutation(this._request).fetch(input, variables);
	}
	/** Deletes a team. */
	delete() {
		return new DeleteTeamMutation(this._request).fetch(this.id);
	}
	/** Unarchives a team and cancels deletion. */
	unarchive() {
		return new UnarchiveTeamMutation(this._request).fetch(this.id);
	}
	/** Updates a team. */
	update(input, variables) {
		return new UpdateTeamMutation(this._request).fetch(this.id, input, variables);
	}
};
/**
* A generic payload return from entity archive mutations.
*
* @param request - function to call the graphql client
* @param data - L.TeamArchivePayloadFragment response data
*/
var TeamArchivePayload = class extends Request {
	_entity;
	constructor(request, data) {
		super(request);
		this.lastSyncId = data.lastSyncId;
		this.success = data.success;
		this._entity = data.entity ?? void 0;
	}
	/** The identifier of the last sync operation. */
	lastSyncId;
	/** Whether the operation was successful. */
	success;
	/** The archived/unarchived entity. Null if entity was deleted. */
	get entity() {
		return this._entity?.id ? new TeamQuery(this._request).fetch(this._entity?.id) : void 0;
	}
	/** The ID of archived/unarchived entity. null if entity was deleted. */
	get entityId() {
		return this._entity?.id;
	}
};
/**
* Certain properties of a team.
*
* @param data - L.TeamChildWebhookPayloadFragment response data
*/
var TeamChildWebhookPayload = class {
	constructor(data) {
		this.id = data.id;
		this.key = data.key;
		this.name = data.name;
	}
	/** The ID of the team. */
	id;
	/** The key of the team. */
	key;
	/** The name of the team. */
	name;
};
/**
* TeamConnection model
*
* @param request - function to call the graphql client
* @param fetch - function to trigger a refetch of this TeamConnection model
* @param data - TeamConnection response data
*/
var TeamConnection = class extends Connection {
	constructor(request, fetch$1, data) {
		super(request, fetch$1, data.nodes.map((node) => new Team(request, node)), new PageInfo(request, data.pageInfo));
	}
};
/**
* Defines the membership of a user to a team.
*
* @param request - function to call the graphql client
* @param data - L.TeamMembershipFragment response data
*/
var TeamMembership = class extends Request {
	_team;
	_user;
	constructor(request, data) {
		super(request);
		this.archivedAt = parseDate(data.archivedAt) ?? void 0;
		this.createdAt = parseDate(data.createdAt) ?? /* @__PURE__ */ new Date();
		this.id = data.id;
		this.owner = data.owner;
		this.sortOrder = data.sortOrder;
		this.updatedAt = parseDate(data.updatedAt) ?? /* @__PURE__ */ new Date();
		this._team = data.team;
		this._user = data.user;
	}
	/** The time at which the entity was archived. Null if the entity has not been archived. */
	archivedAt;
	/** The time at which the entity was created. */
	createdAt;
	/** The unique identifier of the entity. */
	id;
	/** Whether the user is an owner of the team. */
	owner;
	/** The order of the item in the users team list. */
	sortOrder;
	/**
	* The last time at which the entity was meaningfully updated. This is the same as the creation time if the entity hasn't
	*     been updated after creation.
	*/
	updatedAt;
	/** The team that the membership is associated with. */
	get team() {
		return new TeamQuery(this._request).fetch(this._team.id);
	}
	/** The ID of team that the membership is associated with. */
	get teamId() {
		return this._team?.id;
	}
	/** The user that the membership is associated with. */
	get user() {
		return new UserQuery(this._request).fetch(this._user.id);
	}
	/** The ID of user that the membership is associated with. */
	get userId() {
		return this._user?.id;
	}
	/** Creates a new team membership. */
	create(input) {
		return new CreateTeamMembershipMutation(this._request).fetch(input);
	}
	/** Deletes a team membership. */
	delete(variables) {
		return new DeleteTeamMembershipMutation(this._request).fetch(this.id, variables);
	}
	/** Updates a team membership. */
	update(input) {
		return new UpdateTeamMembershipMutation(this._request).fetch(this.id, input);
	}
};
/**
* TeamMembershipConnection model
*
* @param request - function to call the graphql client
* @param fetch - function to trigger a refetch of this TeamMembershipConnection model
* @param data - TeamMembershipConnection response data
*/
var TeamMembershipConnection = class extends Connection {
	constructor(request, fetch$1, data) {
		super(request, fetch$1, data.nodes.map((node) => new TeamMembership(request, node)), new PageInfo(request, data.pageInfo));
	}
};
/**
* TeamMembershipPayload model
*
* @param request - function to call the graphql client
* @param data - L.TeamMembershipPayloadFragment response data
*/
var TeamMembershipPayload = class extends Request {
	_teamMembership;
	constructor(request, data) {
		super(request);
		this.lastSyncId = data.lastSyncId;
		this.success = data.success;
		this._teamMembership = data.teamMembership ?? void 0;
	}
	/** The identifier of the last sync operation. */
	lastSyncId;
	/** Whether the operation was successful. */
	success;
	/** The team membership that was created or updated. */
	get teamMembership() {
		return this._teamMembership?.id ? new TeamMembershipQuery(this._request).fetch(this._teamMembership?.id) : void 0;
	}
	/** The ID of team membership that was created or updated. */
	get teamMembershipId() {
		return this._teamMembership?.id;
	}
};
/**
* A team notification subscription.
*
* @param request - function to call the graphql client
* @param data - L.TeamNotificationSubscriptionFragment response data
*/
var TeamNotificationSubscription = class extends Request {
	_customView;
	_customer;
	_cycle;
	_initiative;
	_label;
	_project;
	_subscriber;
	_team;
	_user;
	constructor(request, data) {
		super(request);
		this.active = data.active;
		this.archivedAt = parseDate(data.archivedAt) ?? void 0;
		this.createdAt = parseDate(data.createdAt) ?? /* @__PURE__ */ new Date();
		this.id = data.id;
		this.notificationSubscriptionTypes = data.notificationSubscriptionTypes;
		this.updatedAt = parseDate(data.updatedAt) ?? /* @__PURE__ */ new Date();
		this.contextViewType = data.contextViewType ?? void 0;
		this.userContextViewType = data.userContextViewType ?? void 0;
		this._customView = data.customView ?? void 0;
		this._customer = data.customer ?? void 0;
		this._cycle = data.cycle ?? void 0;
		this._initiative = data.initiative ?? void 0;
		this._label = data.label ?? void 0;
		this._project = data.project ?? void 0;
		this._subscriber = data.subscriber;
		this._team = data.team;
		this._user = data.user ?? void 0;
	}
	/** Whether the subscription is active or not. */
	active;
	/** The time at which the entity was archived. Null if the entity has not been archived. */
	archivedAt;
	/** The time at which the entity was created. */
	createdAt;
	/** The unique identifier of the entity. */
	id;
	/** The type of subscription. */
	notificationSubscriptionTypes;
	/**
	* The last time at which the entity was meaningfully updated. This is the same as the creation time if the entity hasn't
	*     been updated after creation.
	*/
	updatedAt;
	/** The type of view to which the notification subscription context is associated with. */
	contextViewType;
	/** The type of user view to which the notification subscription context is associated with. */
	userContextViewType;
	/** The contextual custom view associated with the notification subscription. */
	get customView() {
		return this._customView?.id ? new CustomViewQuery(this._request).fetch(this._customView?.id) : void 0;
	}
	/** The ID of contextual custom view associated with the notification subscription. */
	get customViewId() {
		return this._customView?.id;
	}
	/** The customer associated with the notification subscription. */
	get customer() {
		return this._customer?.id ? new CustomerQuery(this._request).fetch(this._customer?.id) : void 0;
	}
	/** The ID of customer associated with the notification subscription. */
	get customerId() {
		return this._customer?.id;
	}
	/** The contextual cycle view associated with the notification subscription. */
	get cycle() {
		return this._cycle?.id ? new CycleQuery(this._request).fetch(this._cycle?.id) : void 0;
	}
	/** The ID of contextual cycle view associated with the notification subscription. */
	get cycleId() {
		return this._cycle?.id;
	}
	/** The contextual initiative view associated with the notification subscription. */
	get initiative() {
		return this._initiative?.id ? new InitiativeQuery(this._request).fetch(this._initiative?.id) : void 0;
	}
	/** The ID of contextual initiative view associated with the notification subscription. */
	get initiativeId() {
		return this._initiative?.id;
	}
	/** The contextual label view associated with the notification subscription. */
	get label() {
		return this._label?.id ? new IssueLabelQuery(this._request).fetch(this._label?.id) : void 0;
	}
	/** The ID of contextual label view associated with the notification subscription. */
	get labelId() {
		return this._label?.id;
	}
	/** The contextual project view associated with the notification subscription. */
	get project() {
		return this._project?.id ? new ProjectQuery(this._request).fetch(this._project?.id) : void 0;
	}
	/** The ID of contextual project view associated with the notification subscription. */
	get projectId() {
		return this._project?.id;
	}
	/** The user that subscribed to receive notifications. */
	get subscriber() {
		return new UserQuery(this._request).fetch(this._subscriber.id);
	}
	/** The ID of user that subscribed to receive notifications. */
	get subscriberId() {
		return this._subscriber?.id;
	}
	/** The team subscribed to. */
	get team() {
		return new TeamQuery(this._request).fetch(this._team.id);
	}
	/** The ID of team subscribed to. */
	get teamId() {
		return this._team?.id;
	}
	/** The user view associated with the notification subscription. */
	get user() {
		return this._user?.id ? new UserQuery(this._request).fetch(this._user?.id) : void 0;
	}
	/** The ID of user view associated with the notification subscription. */
	get userId() {
		return this._user?.id;
	}
};
/**
* Team origin for guidance rules.
*
* @param data - L.TeamOriginWebhookPayloadFragment response data
*/
var TeamOriginWebhookPayload = class {
	constructor(data) {
		this.type = data.type;
		this.team = new TeamWithParentWebhookPayload(data.team);
	}
	/** The type of origin, always 'Team'. */
	type;
	/** The team that the guidance was defined in. */
	team;
};
/**
* TeamPayload model
*
* @param request - function to call the graphql client
* @param data - L.TeamPayloadFragment response data
*/
var TeamPayload = class extends Request {
	_team;
	constructor(request, data) {
		super(request);
		this.lastSyncId = data.lastSyncId;
		this.success = data.success;
		this._team = data.team ?? void 0;
	}
	/** The identifier of the last sync operation. */
	lastSyncId;
	/** Whether the operation was successful. */
	success;
	/** The team that was created or updated. */
	get team() {
		return this._team?.id ? new TeamQuery(this._request).fetch(this._team?.id) : void 0;
	}
	/** The ID of team that was created or updated. */
	get teamId() {
		return this._team?.id;
	}
};
/**
* Team properties including parent information for guidance rules.
*
* @param data - L.TeamWithParentWebhookPayloadFragment response data
*/
var TeamWithParentWebhookPayload = class {
	constructor(data) {
		this.displayName = data.displayName;
		this.id = data.id;
		this.key = data.key;
		this.name = data.name;
		this.parentId = data.parentId ?? void 0;
	}
	/** The team's display name including parent team names if applicable. */
	displayName;
	/** The ID of the team. */
	id;
	/** The key of the team. */
	key;
	/** The name of the team. */
	name;
	/** The parent team's unique identifier, if any. */
	parentId;
};
/**
* A template object used for creating entities faster.
*
* @param request - function to call the graphql client
* @param data - L.TemplateFragment response data
*/
var Template = class extends Request {
	_creator;
	_inheritedFrom;
	_lastUpdatedBy;
	_team;
	constructor(request, data) {
		super(request);
		this.archivedAt = parseDate(data.archivedAt) ?? void 0;
		this.createdAt = parseDate(data.createdAt) ?? /* @__PURE__ */ new Date();
		this.description = data.description ?? void 0;
		this.id = data.id;
		this.lastAppliedAt = parseDate(data.lastAppliedAt) ?? void 0;
		this.name = data.name;
		this.sortOrder = data.sortOrder;
		this.templateData = parseJson(data.templateData) ?? {};
		this.type = data.type;
		this.updatedAt = parseDate(data.updatedAt) ?? /* @__PURE__ */ new Date();
		this._creator = data.creator ?? void 0;
		this._inheritedFrom = data.inheritedFrom ?? void 0;
		this._lastUpdatedBy = data.lastUpdatedBy ?? void 0;
		this._team = data.team ?? void 0;
	}
	/** The time at which the entity was archived. Null if the entity has not been archived. */
	archivedAt;
	/** The time at which the entity was created. */
	createdAt;
	/** Template description. */
	description;
	/** The unique identifier of the entity. */
	id;
	/** The date when the template was last applied. */
	lastAppliedAt;
	/** The name of the template. */
	name;
	/** The sort order of the template. */
	sortOrder;
	/** Template data. */
	templateData;
	/** The entity type this template is for. */
	type;
	/**
	* The last time at which the entity was meaningfully updated. This is the same as the creation time if the entity hasn't
	*     been updated after creation.
	*/
	updatedAt;
	/** The user who created the template. */
	get creator() {
		return this._creator?.id ? new UserQuery(this._request).fetch(this._creator?.id) : void 0;
	}
	/** The ID of user who created the template. */
	get creatorId() {
		return this._creator?.id;
	}
	/** The original template inherited from. */
	get inheritedFrom() {
		return this._inheritedFrom?.id ? new TemplateQuery(this._request).fetch(this._inheritedFrom?.id) : void 0;
	}
	/** The ID of original template inherited from. */
	get inheritedFromId() {
		return this._inheritedFrom?.id;
	}
	/** The user who last updated the template. */
	get lastUpdatedBy() {
		return this._lastUpdatedBy?.id ? new UserQuery(this._request).fetch(this._lastUpdatedBy?.id) : void 0;
	}
	/** The ID of user who last updated the template. */
	get lastUpdatedById() {
		return this._lastUpdatedBy?.id;
	}
	/** The organization that the template is associated with. If null, the template is associated with a particular team. */
	get organization() {
		return new OrganizationQuery(this._request).fetch();
	}
	/** The team that the template is associated with. If null, the template is global to the workspace. */
	get team() {
		return this._team?.id ? new TeamQuery(this._request).fetch(this._team?.id) : void 0;
	}
	/** The ID of team that the template is associated with. if null, the template is global to the workspace. */
	get teamId() {
		return this._team?.id;
	}
	/** Creates a new template. */
	create(input) {
		return new CreateTemplateMutation(this._request).fetch(input);
	}
	/** Deletes a template. */
	delete() {
		return new DeleteTemplateMutation(this._request).fetch(this.id);
	}
	/** Updates an existing template. */
	update(input) {
		return new UpdateTemplateMutation(this._request).fetch(this.id, input);
	}
};
/**
* TemplateConnection model
*
* @param request - function to call the graphql client
* @param fetch - function to trigger a refetch of this TemplateConnection model
* @param data - TemplateConnection response data
*/
var TemplateConnection = class extends Connection {
	constructor(request, fetch$1, data) {
		super(request, fetch$1, data.nodes.map((node) => new Template(request, node)), new PageInfo(request, data.pageInfo));
	}
};
/**
* TemplatePayload model
*
* @param request - function to call the graphql client
* @param data - L.TemplatePayloadFragment response data
*/
var TemplatePayload = class extends Request {
	_template;
	constructor(request, data) {
		super(request);
		this.lastSyncId = data.lastSyncId;
		this.success = data.success;
		this._template = data.template;
	}
	/** The identifier of the last sync operation. */
	lastSyncId;
	/** Whether the operation was successful. */
	success;
	/** The template that was created or updated. */
	get template() {
		return new TemplateQuery(this._request).fetch(this._template.id);
	}
	/** The ID of template that was created or updated. */
	get templateId() {
		return this._template?.id;
	}
};
/**
* A time schedule.
*
* @param request - function to call the graphql client
* @param data - L.TimeScheduleFragment response data
*/
var TimeSchedule = class extends Request {
	_integration;
	constructor(request, data) {
		super(request);
		this.archivedAt = parseDate(data.archivedAt) ?? void 0;
		this.createdAt = parseDate(data.createdAt) ?? /* @__PURE__ */ new Date();
		this.externalId = data.externalId ?? void 0;
		this.externalUrl = data.externalUrl ?? void 0;
		this.id = data.id;
		this.name = data.name;
		this.updatedAt = parseDate(data.updatedAt) ?? /* @__PURE__ */ new Date();
		this.entries = data.entries ? data.entries.map((node) => new TimeScheduleEntry(request, node)) : void 0;
		this._integration = data.integration ?? void 0;
	}
	/** The time at which the entity was archived. Null if the entity has not been archived. */
	archivedAt;
	/** The time at which the entity was created. */
	createdAt;
	/** The identifier of the external schedule. */
	externalId;
	/** The URL to the external schedule. */
	externalUrl;
	/** The unique identifier of the entity. */
	id;
	/** The name of the schedule. */
	name;
	/**
	* The last time at which the entity was meaningfully updated. This is the same as the creation time if the entity hasn't
	*     been updated after creation.
	*/
	updatedAt;
	/** The schedule entries. */
	entries;
	/** The identifier of the Linear integration populating the schedule. */
	get integration() {
		return this._integration?.id ? new IntegrationQuery(this._request).fetch(this._integration?.id) : void 0;
	}
	/** The ID of identifier of the linear integration populating the schedule. */
	get integrationId() {
		return this._integration?.id;
	}
	/** The organization of the schedule. */
	get organization() {
		return new OrganizationQuery(this._request).fetch();
	}
	/** Creates a new time schedule. */
	create(input) {
		return new CreateTimeScheduleMutation(this._request).fetch(input);
	}
	/** Deletes a time schedule. */
	delete() {
		return new DeleteTimeScheduleMutation(this._request).fetch(this.id);
	}
	/** Updates a time schedule. */
	update(input) {
		return new UpdateTimeScheduleMutation(this._request).fetch(this.id, input);
	}
};
/**
* TimeScheduleConnection model
*
* @param request - function to call the graphql client
* @param fetch - function to trigger a refetch of this TimeScheduleConnection model
* @param data - TimeScheduleConnection response data
*/
var TimeScheduleConnection = class extends Connection {
	constructor(request, fetch$1, data) {
		super(request, fetch$1, data.nodes.map((node) => new TimeSchedule(request, node)), new PageInfo(request, data.pageInfo));
	}
};
/**
* TimeScheduleEntry model
*
* @param request - function to call the graphql client
* @param data - L.TimeScheduleEntryFragment response data
*/
var TimeScheduleEntry = class extends Request {
	constructor(request, data) {
		super(request);
		this.endsAt = parseDate(data.endsAt) ?? /* @__PURE__ */ new Date();
		this.startsAt = parseDate(data.startsAt) ?? /* @__PURE__ */ new Date();
		this.userEmail = data.userEmail ?? void 0;
		this.userId = data.userId ?? void 0;
	}
	/** The end date of the schedule in ISO 8601 date-time format. */
	endsAt;
	/** The start date of the schedule in ISO 8601 date-time format. */
	startsAt;
	/** The email, name or reference to the user on schedule. This is used in case the external user could not be mapped to a Linear user id. */
	userEmail;
	/** The Linear user id of the user on schedule. If the user cannot be mapped to a Linear user then `userEmail` can be used as a reference. */
	userId;
};
/**
* TimeSchedulePayload model
*
* @param request - function to call the graphql client
* @param data - L.TimeSchedulePayloadFragment response data
*/
var TimeSchedulePayload = class extends Request {
	_timeSchedule;
	constructor(request, data) {
		super(request);
		this.lastSyncId = data.lastSyncId;
		this.success = data.success;
		this._timeSchedule = data.timeSchedule;
	}
	/** The identifier of the last sync operation. */
	lastSyncId;
	/** Whether the operation was successful. */
	success;
	get timeSchedule() {
		return new TimeScheduleQuery(this._request).fetch(this._timeSchedule.id);
	}
	/** The ID of timeSchedule */
	get timeScheduleId() {
		return this._timeSchedule?.id;
	}
};
/**
* A team's triage responsibility.
*
* @param request - function to call the graphql client
* @param data - L.TriageResponsibilityFragment response data
*/
var TriageResponsibility = class extends Request {
	_currentUser;
	_team;
	_timeSchedule;
	constructor(request, data) {
		super(request);
		this.archivedAt = parseDate(data.archivedAt) ?? void 0;
		this.createdAt = parseDate(data.createdAt) ?? /* @__PURE__ */ new Date();
		this.id = data.id;
		this.updatedAt = parseDate(data.updatedAt) ?? /* @__PURE__ */ new Date();
		this.manualSelection = data.manualSelection ? new TriageResponsibilityManualSelection(request, data.manualSelection) : void 0;
		this.action = data.action;
		this._currentUser = data.currentUser ?? void 0;
		this._team = data.team;
		this._timeSchedule = data.timeSchedule ?? void 0;
	}
	/** The time at which the entity was archived. Null if the entity has not been archived. */
	archivedAt;
	/** The time at which the entity was created. */
	createdAt;
	/** The unique identifier of the entity. */
	id;
	/**
	* The last time at which the entity was meaningfully updated. This is the same as the creation time if the entity hasn't
	*     been updated after creation.
	*/
	updatedAt;
	/** Set of users used for triage responsibility. */
	manualSelection;
	/** The action to take when an issue is added to triage. */
	action;
	/** The user currently responsible for triage. */
	get currentUser() {
		return this._currentUser?.id ? new UserQuery(this._request).fetch(this._currentUser?.id) : void 0;
	}
	/** The ID of user currently responsible for triage. */
	get currentUserId() {
		return this._currentUser?.id;
	}
	/** The team to which the triage responsibility belongs to. */
	get team() {
		return new TeamQuery(this._request).fetch(this._team.id);
	}
	/** The ID of team to which the triage responsibility belongs to. */
	get teamId() {
		return this._team?.id;
	}
	/** The time schedule used for scheduling. */
	get timeSchedule() {
		return this._timeSchedule?.id ? new TimeScheduleQuery(this._request).fetch(this._timeSchedule?.id) : void 0;
	}
	/** The ID of time schedule used for scheduling. */
	get timeScheduleId() {
		return this._timeSchedule?.id;
	}
	/** Creates a new triage responsibility. */
	create(input) {
		return new CreateTriageResponsibilityMutation(this._request).fetch(input);
	}
	/** Deletes a triage responsibility. */
	delete() {
		return new DeleteTriageResponsibilityMutation(this._request).fetch(this.id);
	}
	/** Updates an existing triage responsibility. */
	update(input) {
		return new UpdateTriageResponsibilityMutation(this._request).fetch(this.id, input);
	}
};
/**
* TriageResponsibilityConnection model
*
* @param request - function to call the graphql client
* @param fetch - function to trigger a refetch of this TriageResponsibilityConnection model
* @param data - TriageResponsibilityConnection response data
*/
var TriageResponsibilityConnection = class extends Connection {
	constructor(request, fetch$1, data) {
		super(request, fetch$1, data.nodes.map((node) => new TriageResponsibility(request, node)), new PageInfo(request, data.pageInfo));
	}
};
/**
* TriageResponsibilityManualSelection model
*
* @param request - function to call the graphql client
* @param data - L.TriageResponsibilityManualSelectionFragment response data
*/
var TriageResponsibilityManualSelection = class extends Request {
	constructor(request, data) {
		super(request);
		this.userIds = data.userIds;
	}
	/** The set of users responsible for triage. */
	userIds;
};
/**
* TriageResponsibilityPayload model
*
* @param request - function to call the graphql client
* @param data - L.TriageResponsibilityPayloadFragment response data
*/
var TriageResponsibilityPayload = class extends Request {
	_triageResponsibility;
	constructor(request, data) {
		super(request);
		this.lastSyncId = data.lastSyncId;
		this.success = data.success;
		this._triageResponsibility = data.triageResponsibility;
	}
	/** The identifier of the last sync operation. */
	lastSyncId;
	/** Whether the operation was successful. */
	success;
	get triageResponsibility() {
		return new TriageResponsibilityQuery(this._request).fetch(this._triageResponsibility.id);
	}
	/** The ID of triageResponsibility */
	get triageResponsibilityId() {
		return this._triageResponsibility?.id;
	}
};
/**
* Object representing Google Cloud upload policy, plus additional data.
*
* @param request - function to call the graphql client
* @param data - L.UploadFileFragment response data
*/
var UploadFile = class extends Request {
	constructor(request, data) {
		super(request);
		this.assetUrl = data.assetUrl;
		this.contentType = data.contentType;
		this.filename = data.filename;
		this.metaData = data.metaData ?? void 0;
		this.size = data.size;
		this.uploadUrl = data.uploadUrl;
		this.headers = data.headers.map((node) => new UploadFileHeader(request, node));
	}
	/** The asset URL for the uploaded file. (assigned automatically). */
	assetUrl;
	/** The content type. */
	contentType;
	/** The filename. */
	filename;
	metaData;
	/** The size of the uploaded file. */
	size;
	/** The signed URL the for the uploaded file. (assigned automatically). */
	uploadUrl;
	headers;
};
/**
* UploadFileHeader model
*
* @param request - function to call the graphql client
* @param data - L.UploadFileHeaderFragment response data
*/
var UploadFileHeader = class extends Request {
	constructor(request, data) {
		super(request);
		this.key = data.key;
		this.value = data.value;
	}
	/** Upload file header key. */
	key;
	/** Upload file header value. */
	value;
};
/**
* UploadPayload model
*
* @param request - function to call the graphql client
* @param data - L.UploadPayloadFragment response data
*/
var UploadPayload = class extends Request {
	constructor(request, data) {
		super(request);
		this.lastSyncId = data.lastSyncId;
		this.success = data.success;
		this.uploadFile = data.uploadFile ? new UploadFile(request, data.uploadFile) : void 0;
	}
	/** The identifier of the last sync operation. */
	lastSyncId;
	/** Whether the operation was successful. */
	success;
	/** Object describing the file to be uploaded. */
	uploadFile;
};
/**
* A user that has access to the the resources of an organization.
*
* @param request - function to call the graphql client
* @param data - L.UserFragment response data
*/
var User = class extends Request {
	constructor(request, data) {
		super(request);
		this.active = data.active;
		this.admin = data.admin;
		this.app = data.app;
		this.archivedAt = parseDate(data.archivedAt) ?? void 0;
		this.avatarBackgroundColor = data.avatarBackgroundColor;
		this.avatarUrl = data.avatarUrl ?? void 0;
		this.calendarHash = data.calendarHash ?? void 0;
		this.canAccessAnyPublicTeam = data.canAccessAnyPublicTeam;
		this.createdAt = parseDate(data.createdAt) ?? /* @__PURE__ */ new Date();
		this.createdIssueCount = data.createdIssueCount;
		this.description = data.description ?? void 0;
		this.disableReason = data.disableReason ?? void 0;
		this.displayName = data.displayName;
		this.email = data.email;
		this.gitHubUserId = data.gitHubUserId ?? void 0;
		this.guest = data.guest;
		this.id = data.id;
		this.initials = data.initials;
		this.inviteHash = data.inviteHash;
		this.isAssignable = data.isAssignable;
		this.isMe = data.isMe;
		this.isMentionable = data.isMentionable;
		this.lastSeen = parseDate(data.lastSeen) ?? void 0;
		this.name = data.name;
		this.owner = data.owner;
		this.statusEmoji = data.statusEmoji ?? void 0;
		this.statusLabel = data.statusLabel ?? void 0;
		this.statusUntilAt = parseDate(data.statusUntilAt) ?? void 0;
		this.supportsAgentSessions = data.supportsAgentSessions;
		this.timezone = data.timezone ?? void 0;
		this.updatedAt = parseDate(data.updatedAt) ?? /* @__PURE__ */ new Date();
		this.url = data.url;
	}
	/** Whether the user account is active or disabled (suspended). */
	active;
	/** Whether the user is an organization administrator. */
	admin;
	/** Whether the user is an app. */
	app;
	/** The time at which the entity was archived. Null if the entity has not been archived. */
	archivedAt;
	/** The background color of the avatar for users without set avatar. */
	avatarBackgroundColor;
	/** An URL to the user's avatar image. */
	avatarUrl;
	/** [DEPRECATED] Hash for the user to be used in calendar URLs. */
	calendarHash;
	/** Whether this user can access any public team in the organization. */
	canAccessAnyPublicTeam;
	/** The time at which the entity was created. */
	createdAt;
	/** Number of issues created. */
	createdIssueCount;
	/** A short description of the user, either its title or bio. */
	description;
	/** Reason why is the account disabled. */
	disableReason;
	/** The user's display (nick) name. Unique within each organization. */
	displayName;
	/** The user's email address. */
	email;
	/** The user's GitHub user ID. */
	gitHubUserId;
	/** Whether the user is a guest in the workspace and limited to accessing a subset of teams. */
	guest;
	/** The unique identifier of the entity. */
	id;
	/** The initials of the user. */
	initials;
	/** [DEPRECATED] Unique hash for the user to be used in invite URLs. */
	inviteHash;
	/** Whether the user is assignable. */
	isAssignable;
	/** Whether the user is the currently authenticated user. */
	isMe;
	/** Whether the user is mentionable. */
	isMentionable;
	/** The last time the user was seen online. */
	lastSeen;
	/** The user's full name. */
	name;
	/** Whether the user is an organization owner. */
	owner;
	/** The emoji to represent the user current status. */
	statusEmoji;
	/** The label of the user current status. */
	statusLabel;
	/** A date at which the user current status should be cleared. */
	statusUntilAt;
	/** Whether this agent user supports agent sessions. */
	supportsAgentSessions;
	/** The local timezone of the user. */
	timezone;
	/**
	* The last time at which the entity was meaningfully updated. This is the same as the creation time if the entity hasn't
	*     been updated after creation.
	*/
	updatedAt;
	/** User's profile URL. */
	url;
	/** Organization the user belongs to. */
	get organization() {
		return new OrganizationQuery(this._request).fetch();
	}
	/** Issues assigned to the user. */
	assignedIssues(variables) {
		return new User_AssignedIssuesQuery(this._request, this.id, variables).fetch(variables);
	}
	/** Issues created by the user. */
	createdIssues(variables) {
		return new User_CreatedIssuesQuery(this._request, this.id, variables).fetch(variables);
	}
	/** Issues delegated to this user. */
	delegatedIssues(variables) {
		return new User_DelegatedIssuesQuery(this._request, this.id, variables).fetch(variables);
	}
	/** The user's drafts */
	drafts(variables) {
		return new User_DraftsQuery(this._request, this.id, variables).fetch(variables);
	}
	/** Memberships associated with the user. For easier access of the same data, use `teams` query. */
	teamMemberships(variables) {
		return new User_TeamMembershipsQuery(this._request, this.id, variables).fetch(variables);
	}
	/** Teams the user is part of. */
	teams(variables) {
		return new User_TeamsQuery(this._request, this.id, variables).fetch(variables);
	}
	/** Suspends a user. Can only be called by an admin or owner. */
	suspend() {
		return new SuspendUserMutation(this._request).fetch(this.id);
	}
	/** Un-suspends a user. Can only be called by an admin or owner. */
	unsuspend() {
		return new UnsuspendUserMutation(this._request).fetch(this.id);
	}
	/** Updates a user. Only available to organization admins and the user themselves. */
	update(input) {
		return new UpdateUserMutation(this._request).fetch(this.id, input);
	}
};
/**
* User actor payload for webhooks.
*
* @param data - L.UserActorWebhookPayloadFragment response data
*/
var UserActorWebhookPayload = class {
	constructor(data) {
		this.avatarUrl = data.avatarUrl ?? void 0;
		this.email = data.email;
		this.id = data.id;
		this.name = data.name;
		this.type = data.type;
		this.url = data.url;
	}
	/** The avatar URL of the user. */
	avatarUrl;
	/** The email of the user. */
	email;
	/** The ID of the user. */
	id;
	/** The name of the user. */
	name;
	/** The type of actor. */
	type;
	/** The URL of the user. */
	url;
};
/**
* UserAdminPayload model
*
* @param request - function to call the graphql client
* @param data - L.UserAdminPayloadFragment response data
*/
var UserAdminPayload = class extends Request {
	constructor(request, data) {
		super(request);
		this.success = data.success;
	}
	/** Whether the operation was successful. */
	success;
};
/**
* Certain properties of a user.
*
* @param data - L.UserChildWebhookPayloadFragment response data
*/
var UserChildWebhookPayload = class {
	constructor(data) {
		this.avatarUrl = data.avatarUrl ?? void 0;
		this.email = data.email;
		this.id = data.id;
		this.name = data.name;
		this.url = data.url;
	}
	/** The avatar URL of the user. */
	avatarUrl;
	/** The email of the user. */
	email;
	/** The ID of the user. */
	id;
	/** The name of the user. */
	name;
	/** The URL of the user. */
	url;
};
/**
* UserConnection model
*
* @param request - function to call the graphql client
* @param fetch - function to trigger a refetch of this UserConnection model
* @param data - UserConnection response data
*/
var UserConnection = class extends Connection {
	constructor(request, fetch$1, data) {
		super(request, fetch$1, data.nodes.map((node) => new User(request, node)), new PageInfo(request, data.pageInfo));
	}
};
/**
* A user notification subscription.
*
* @param request - function to call the graphql client
* @param data - L.UserNotificationSubscriptionFragment response data
*/
var UserNotificationSubscription = class extends Request {
	_customView;
	_customer;
	_cycle;
	_initiative;
	_label;
	_project;
	_subscriber;
	_team;
	_user;
	constructor(request, data) {
		super(request);
		this.active = data.active;
		this.archivedAt = parseDate(data.archivedAt) ?? void 0;
		this.createdAt = parseDate(data.createdAt) ?? /* @__PURE__ */ new Date();
		this.id = data.id;
		this.notificationSubscriptionTypes = data.notificationSubscriptionTypes;
		this.updatedAt = parseDate(data.updatedAt) ?? /* @__PURE__ */ new Date();
		this.contextViewType = data.contextViewType ?? void 0;
		this.userContextViewType = data.userContextViewType ?? void 0;
		this._customView = data.customView ?? void 0;
		this._customer = data.customer ?? void 0;
		this._cycle = data.cycle ?? void 0;
		this._initiative = data.initiative ?? void 0;
		this._label = data.label ?? void 0;
		this._project = data.project ?? void 0;
		this._subscriber = data.subscriber;
		this._team = data.team ?? void 0;
		this._user = data.user;
	}
	/** Whether the subscription is active or not. */
	active;
	/** The time at which the entity was archived. Null if the entity has not been archived. */
	archivedAt;
	/** The time at which the entity was created. */
	createdAt;
	/** The unique identifier of the entity. */
	id;
	/** The type of subscription. */
	notificationSubscriptionTypes;
	/**
	* The last time at which the entity was meaningfully updated. This is the same as the creation time if the entity hasn't
	*     been updated after creation.
	*/
	updatedAt;
	/** The type of view to which the notification subscription context is associated with. */
	contextViewType;
	/** The type of user view to which the notification subscription context is associated with. */
	userContextViewType;
	/** The contextual custom view associated with the notification subscription. */
	get customView() {
		return this._customView?.id ? new CustomViewQuery(this._request).fetch(this._customView?.id) : void 0;
	}
	/** The ID of contextual custom view associated with the notification subscription. */
	get customViewId() {
		return this._customView?.id;
	}
	/** The customer associated with the notification subscription. */
	get customer() {
		return this._customer?.id ? new CustomerQuery(this._request).fetch(this._customer?.id) : void 0;
	}
	/** The ID of customer associated with the notification subscription. */
	get customerId() {
		return this._customer?.id;
	}
	/** The contextual cycle view associated with the notification subscription. */
	get cycle() {
		return this._cycle?.id ? new CycleQuery(this._request).fetch(this._cycle?.id) : void 0;
	}
	/** The ID of contextual cycle view associated with the notification subscription. */
	get cycleId() {
		return this._cycle?.id;
	}
	/** The contextual initiative view associated with the notification subscription. */
	get initiative() {
		return this._initiative?.id ? new InitiativeQuery(this._request).fetch(this._initiative?.id) : void 0;
	}
	/** The ID of contextual initiative view associated with the notification subscription. */
	get initiativeId() {
		return this._initiative?.id;
	}
	/** The contextual label view associated with the notification subscription. */
	get label() {
		return this._label?.id ? new IssueLabelQuery(this._request).fetch(this._label?.id) : void 0;
	}
	/** The ID of contextual label view associated with the notification subscription. */
	get labelId() {
		return this._label?.id;
	}
	/** The contextual project view associated with the notification subscription. */
	get project() {
		return this._project?.id ? new ProjectQuery(this._request).fetch(this._project?.id) : void 0;
	}
	/** The ID of contextual project view associated with the notification subscription. */
	get projectId() {
		return this._project?.id;
	}
	/** The user that subscribed to receive notifications. */
	get subscriber() {
		return new UserQuery(this._request).fetch(this._subscriber.id);
	}
	/** The ID of user that subscribed to receive notifications. */
	get subscriberId() {
		return this._subscriber?.id;
	}
	/** The team associated with the notification subscription. */
	get team() {
		return this._team?.id ? new TeamQuery(this._request).fetch(this._team?.id) : void 0;
	}
	/** The ID of team associated with the notification subscription. */
	get teamId() {
		return this._team?.id;
	}
	/** The user subscribed to. */
	get user() {
		return new UserQuery(this._request).fetch(this._user.id);
	}
	/** The ID of user subscribed to. */
	get userId() {
		return this._user?.id;
	}
};
/**
* UserPayload model
*
* @param request - function to call the graphql client
* @param data - L.UserPayloadFragment response data
*/
var UserPayload = class extends Request {
	_user;
	constructor(request, data) {
		super(request);
		this.lastSyncId = data.lastSyncId;
		this.success = data.success;
		this._user = data.user ?? void 0;
	}
	/** The identifier of the last sync operation. */
	lastSyncId;
	/** Whether the operation was successful. */
	success;
	/** The user that was created or updated. */
	get user() {
		return this._user?.id ? new UserQuery(this._request).fetch(this._user?.id) : void 0;
	}
	/** The ID of user that was created or updated. */
	get userId() {
		return this._user?.id;
	}
};
/**
* The settings of a user as a JSON object.
*
* @param request - function to call the graphql client
* @param data - L.UserSettingsFragment response data
*/
var UserSettings = class extends Request {
	_user;
	constructor(request, data) {
		super(request);
		this.archivedAt = parseDate(data.archivedAt) ?? void 0;
		this.autoAssignToSelf = data.autoAssignToSelf;
		this.calendarHash = data.calendarHash ?? void 0;
		this.createdAt = parseDate(data.createdAt) ?? /* @__PURE__ */ new Date();
		this.feedLastSeenTime = parseDate(data.feedLastSeenTime) ?? void 0;
		this.id = data.id;
		this.showFullUserNames = data.showFullUserNames;
		this.subscribedToChangelog = data.subscribedToChangelog;
		this.subscribedToDPA = data.subscribedToDPA;
		this.subscribedToInviteAccepted = data.subscribedToInviteAccepted;
		this.subscribedToPrivacyLegalUpdates = data.subscribedToPrivacyLegalUpdates;
		this.unsubscribedFrom = data.unsubscribedFrom;
		this.updatedAt = parseDate(data.updatedAt) ?? /* @__PURE__ */ new Date();
		this.notificationCategoryPreferences = new NotificationCategoryPreferences(request, data.notificationCategoryPreferences);
		this.notificationChannelPreferences = new NotificationChannelPreferences(request, data.notificationChannelPreferences);
		this.notificationDeliveryPreferences = new NotificationDeliveryPreferences(request, data.notificationDeliveryPreferences);
		this.theme = data.theme ? new UserSettingsTheme(request, data.theme) : void 0;
		this.feedSummarySchedule = data.feedSummarySchedule ?? void 0;
		this._user = data.user;
	}
	/** The time at which the entity was archived. Null if the entity has not been archived. */
	archivedAt;
	/** Whether to auto-assign newly created issues to the current user by default. */
	autoAssignToSelf;
	/** Hash for the user to be used in calendar URLs. */
	calendarHash;
	/** The time at which the entity was created. */
	createdAt;
	/** The user's last seen time for the pulse feed. */
	feedLastSeenTime;
	/** The unique identifier of the entity. */
	id;
	/** Whether to show full user names instead of display names. */
	showFullUserNames;
	/** Whether this user is subscribed to changelog email or not. */
	subscribedToChangelog;
	/** Whether this user is subscribed to DPA emails or not. */
	subscribedToDPA;
	/** Whether this user is subscribed to invite accepted emails or not. */
	subscribedToInviteAccepted;
	/** Whether this user is subscribed to privacy and legal update emails or not. */
	subscribedToPrivacyLegalUpdates;
	/** The email types the user has unsubscribed from. */
	unsubscribedFrom;
	/**
	* The last time at which the entity was meaningfully updated. This is the same as the creation time if the entity hasn't
	*     been updated after creation.
	*/
	updatedAt;
	/** The user's notification category preferences. */
	notificationCategoryPreferences;
	/** The user's notification channel preferences. */
	notificationChannelPreferences;
	/** The notification delivery preferences for the user. Note: notificationDisabled field is deprecated in favor of notificationChannelPreferences. */
	notificationDeliveryPreferences;
	/** The user's theme for a given mode and device type. */
	theme;
	/** The user's feed summary schedule preference. */
	feedSummarySchedule;
	/** The user associated with these settings. */
	get user() {
		return new UserQuery(this._request).fetch(this._user.id);
	}
	/** The ID of user associated with these settings. */
	get userId() {
		return this._user?.id;
	}
	/** Updates the user's settings. */
	update(input) {
		return new UpdateUserSettingsMutation(this._request).fetch(this.id, input);
	}
};
/**
* UserSettingsCustomSidebarTheme model
*
* @param request - function to call the graphql client
* @param data - L.UserSettingsCustomSidebarThemeFragment response data
*/
var UserSettingsCustomSidebarTheme = class extends Request {
	constructor(request, data) {
		super(request);
		this.accent = data.accent;
		this.base = data.base;
		this.contrast = data.contrast;
	}
	/** The accent color in LCH format. */
	accent;
	/** The base color in LCH format. */
	base;
	/** The contrast value. */
	contrast;
};
/**
* UserSettingsCustomTheme model
*
* @param request - function to call the graphql client
* @param data - L.UserSettingsCustomThemeFragment response data
*/
var UserSettingsCustomTheme = class extends Request {
	constructor(request, data) {
		super(request);
		this.accent = data.accent;
		this.base = data.base;
		this.contrast = data.contrast;
		this.sidebar = data.sidebar ? new UserSettingsCustomSidebarTheme(request, data.sidebar) : void 0;
	}
	/** The accent color in LCH format. */
	accent;
	/** The base color in LCH format. */
	base;
	/** The contrast value. */
	contrast;
	/** Optional sidebar theme colors. */
	sidebar;
};
/**
* UserSettingsFlagPayload model
*
* @param request - function to call the graphql client
* @param data - L.UserSettingsFlagPayloadFragment response data
*/
var UserSettingsFlagPayload = class extends Request {
	constructor(request, data) {
		super(request);
		this.flag = data.flag ?? void 0;
		this.lastSyncId = data.lastSyncId;
		this.success = data.success;
		this.value = data.value ?? void 0;
	}
	/** The flag key which was updated. */
	flag;
	/** The identifier of the last sync operation. */
	lastSyncId;
	/** Whether the operation was successful. */
	success;
	/** The flag value after update. */
	value;
};
/**
* UserSettingsFlagsResetPayload model
*
* @param request - function to call the graphql client
* @param data - L.UserSettingsFlagsResetPayloadFragment response data
*/
var UserSettingsFlagsResetPayload = class extends Request {
	constructor(request, data) {
		super(request);
		this.lastSyncId = data.lastSyncId;
		this.success = data.success;
	}
	/** The identifier of the last sync operation. */
	lastSyncId;
	/** Whether the operation was successful. */
	success;
};
/**
* UserSettingsPayload model
*
* @param request - function to call the graphql client
* @param data - L.UserSettingsPayloadFragment response data
*/
var UserSettingsPayload = class extends Request {
	constructor(request, data) {
		super(request);
		this.lastSyncId = data.lastSyncId;
		this.success = data.success;
	}
	/** The identifier of the last sync operation. */
	lastSyncId;
	/** Whether the operation was successful. */
	success;
	/** The user's settings. */
	get userSettings() {
		return new UserSettingsQuery(this._request).fetch();
	}
};
/**
* UserSettingsTheme model
*
* @param request - function to call the graphql client
* @param data - L.UserSettingsThemeFragment response data
*/
var UserSettingsTheme = class extends Request {
	constructor(request, data) {
		super(request);
		this.custom = data.custom ? new UserSettingsCustomTheme(request, data.custom) : void 0;
		this.preset = data.preset;
	}
	/** The custom theme definition, only present when preset is 'custom'. */
	custom;
	/** The theme preset. */
	preset;
};
/**
* Payload for a user webhook.
*
* @param data - L.UserWebhookPayloadFragment response data
*/
var UserWebhookPayload = class {
	constructor(data) {
		this.active = data.active;
		this.admin = data.admin;
		this.app = data.app;
		this.archivedAt = data.archivedAt ?? void 0;
		this.avatarUrl = data.avatarUrl ?? void 0;
		this.createdAt = data.createdAt;
		this.description = data.description ?? void 0;
		this.disableReason = data.disableReason ?? void 0;
		this.displayName = data.displayName;
		this.email = data.email;
		this.guest = data.guest;
		this.id = data.id;
		this.name = data.name;
		this.owner = data.owner ?? void 0;
		this.timezone = data.timezone ?? void 0;
		this.updatedAt = data.updatedAt;
		this.url = data.url;
	}
	/** Whether the user is active. */
	active;
	/** Whether the user is an admin. */
	admin;
	/** Whether the user is an app. */
	app;
	/** The time at which the entity was archived. */
	archivedAt;
	/** The avatar URL of the user. */
	avatarUrl;
	/** The time at which the entity was created. */
	createdAt;
	/** The description of the user. */
	description;
	/** The reason the user is disabled. */
	disableReason;
	/** The display name of the user. */
	displayName;
	/** The email of the user. */
	email;
	/** Whether the user is a guest. */
	guest;
	/** The ID of the entity. */
	id;
	/** The name of the user. */
	name;
	/** Whether the user is an owner. */
	owner;
	/** The local timezone of the user. */
	timezone;
	/** The time at which the entity was updated. */
	updatedAt;
	/** The URL of the user. */
	url;
};
/**
* View preferences.
*
* @param request - function to call the graphql client
* @param data - L.ViewPreferencesFragment response data
*/
var ViewPreferences = class extends Request {
	constructor(request, data) {
		super(request);
		this.archivedAt = parseDate(data.archivedAt) ?? void 0;
		this.createdAt = parseDate(data.createdAt) ?? /* @__PURE__ */ new Date();
		this.id = data.id;
		this.type = data.type;
		this.updatedAt = parseDate(data.updatedAt) ?? /* @__PURE__ */ new Date();
		this.viewType = data.viewType;
		this.preferences = new ViewPreferencesValues(request, data.preferences);
	}
	/** The time at which the entity was archived. Null if the entity has not been archived. */
	archivedAt;
	/** The time at which the entity was created. */
	createdAt;
	/** The unique identifier of the entity. */
	id;
	/** The view preference type. */
	type;
	/**
	* The last time at which the entity was meaningfully updated. This is the same as the creation time if the entity hasn't
	*     been updated after creation.
	*/
	updatedAt;
	/** The view type. */
	viewType;
	/** The view preferences */
	preferences;
	/** Creates a new ViewPreferences object. */
	create(input) {
		return new CreateViewPreferencesMutation(this._request).fetch(input);
	}
	/** Deletes a ViewPreferences. */
	delete() {
		return new DeleteViewPreferencesMutation(this._request).fetch(this.id);
	}
	/** Updates an existing ViewPreferences object. */
	update(input) {
		return new UpdateViewPreferencesMutation(this._request).fetch(this.id, input);
	}
};
/**
* ViewPreferencesPayload model
*
* @param request - function to call the graphql client
* @param data - L.ViewPreferencesPayloadFragment response data
*/
var ViewPreferencesPayload = class extends Request {
	constructor(request, data) {
		super(request);
		this.lastSyncId = data.lastSyncId;
		this.success = data.success;
		this.viewPreferences = new ViewPreferences(request, data.viewPreferences);
	}
	/** The identifier of the last sync operation. */
	lastSyncId;
	/** Whether the operation was successful. */
	success;
	/** The view preferences entity being mutated. */
	viewPreferences;
};
/**
* ViewPreferencesValues model
*
* @param request - function to call the graphql client
* @param data - L.ViewPreferencesValuesFragment response data
*/
var ViewPreferencesValues = class extends Request {
	constructor(request, data) {
		super(request);
		this.issueGrouping = data.issueGrouping ?? void 0;
		this.issueSubGrouping = data.issueSubGrouping ?? void 0;
		this.showCompletedIssues = data.showCompletedIssues ?? void 0;
		this.viewOrdering = data.viewOrdering ?? void 0;
	}
	/** The issue grouping. */
	issueGrouping;
	/** The issue sub grouping. */
	issueSubGrouping;
	/** Whether to show completed issues. */
	showCompletedIssues;
	/** The issue ordering. */
	viewOrdering;
};
/**
* A webhook used to send HTTP notifications over data updates.
*
* @param request - function to call the graphql client
* @param data - L.WebhookFragment response data
*/
var Webhook = class extends Request {
	_creator;
	_team;
	constructor(request, data) {
		super(request);
		this.allPublicTeams = data.allPublicTeams;
		this.archivedAt = parseDate(data.archivedAt) ?? void 0;
		this.createdAt = parseDate(data.createdAt) ?? /* @__PURE__ */ new Date();
		this.enabled = data.enabled;
		this.id = data.id;
		this.label = data.label ?? void 0;
		this.resourceTypes = data.resourceTypes;
		this.secret = data.secret ?? void 0;
		this.updatedAt = parseDate(data.updatedAt) ?? /* @__PURE__ */ new Date();
		this.url = data.url ?? void 0;
		this._creator = data.creator ?? void 0;
		this._team = data.team ?? void 0;
	}
	/** Whether the Webhook is enabled for all public teams, including teams created after the webhook was created. */
	allPublicTeams;
	/** The time at which the entity was archived. Null if the entity has not been archived. */
	archivedAt;
	/** The time at which the entity was created. */
	createdAt;
	/** Whether the Webhook is enabled. */
	enabled;
	/** The unique identifier of the entity. */
	id;
	/** Webhook label. */
	label;
	/** The resource types this webhook is subscribed to. */
	resourceTypes;
	/** Secret token for verifying the origin on the recipient side. */
	secret;
	/**
	* The last time at which the entity was meaningfully updated. This is the same as the creation time if the entity hasn't
	*     been updated after creation.
	*/
	updatedAt;
	/** Webhook URL. */
	url;
	/** The user who created the webhook. */
	get creator() {
		return this._creator?.id ? new UserQuery(this._request).fetch(this._creator?.id) : void 0;
	}
	/** The ID of user who created the webhook. */
	get creatorId() {
		return this._creator?.id;
	}
	/** The team that the webhook is associated with. If null, the webhook is associated with all public teams of the organization or multiple teams. */
	get team() {
		return this._team?.id ? new TeamQuery(this._request).fetch(this._team?.id) : void 0;
	}
	/** The ID of team that the webhook is associated with. if null, the webhook is associated with all public teams of the organization or multiple teams. */
	get teamId() {
		return this._team?.id;
	}
	/** Creates a new webhook. */
	create(input) {
		return new CreateWebhookMutation(this._request).fetch(input);
	}
	/** Deletes a Webhook. */
	delete() {
		return new DeleteWebhookMutation(this._request).fetch(this.id);
	}
	/** Updates an existing Webhook. */
	update(input) {
		return new UpdateWebhookMutation(this._request).fetch(this.id, input);
	}
};
/**
* WebhookConnection model
*
* @param request - function to call the graphql client
* @param fetch - function to trigger a refetch of this WebhookConnection model
* @param data - WebhookConnection response data
*/
var WebhookConnection = class extends Connection {
	constructor(request, fetch$1, data) {
		super(request, fetch$1, data.nodes.map((node) => new Webhook(request, node)), new PageInfo(request, data.pageInfo));
	}
};
/**
* Entity representing a webhook execution failure.
*
* @param request - function to call the graphql client
* @param data - L.WebhookFailureEventFragment response data
*/
var WebhookFailureEvent = class extends Request {
	_webhook;
	constructor(request, data) {
		super(request);
		this.createdAt = parseDate(data.createdAt) ?? /* @__PURE__ */ new Date();
		this.executionId = data.executionId;
		this.httpStatus = data.httpStatus ?? void 0;
		this.id = data.id;
		this.responseOrError = data.responseOrError ?? void 0;
		this.url = data.url;
		this._webhook = data.webhook;
	}
	/** The time at which the entity was created. */
	createdAt;
	/** The unique execution ID of the webhook push. This is retained between retries of the same push. */
	executionId;
	/** The HTTP status code returned by the recipient. */
	httpStatus;
	/** The unique identifier of the entity. */
	id;
	/** The HTTP response body returned by the recipient or error occured. */
	responseOrError;
	/** The URL that the webhook was trying to push to. */
	url;
	/** The webhook that this failure event is associated with. */
	get webhook() {
		return new WebhookQuery(this._request).fetch(this._webhook.id);
	}
	/** The ID of webhook that this failure event is associated with. */
	get webhookId() {
		return this._webhook?.id;
	}
};
/**
* WebhookPayload model
*
* @param request - function to call the graphql client
* @param data - L.WebhookPayloadFragment response data
*/
var WebhookPayload = class extends Request {
	_webhook;
	constructor(request, data) {
		super(request);
		this.lastSyncId = data.lastSyncId;
		this.success = data.success;
		this._webhook = data.webhook;
	}
	/** The identifier of the last sync operation. */
	lastSyncId;
	/** Whether the operation was successful. */
	success;
	/** The webhook entity being mutated. */
	get webhook() {
		return new WebhookQuery(this._request).fetch(this._webhook.id);
	}
	/** The ID of webhook entity being mutated. */
	get webhookId() {
		return this._webhook?.id;
	}
};
/**
* A state in a team workflow.
*
* @param request - function to call the graphql client
* @param data - L.WorkflowStateFragment response data
*/
var WorkflowState = class extends Request {
	_inheritedFrom;
	_team;
	constructor(request, data) {
		super(request);
		this.archivedAt = parseDate(data.archivedAt) ?? void 0;
		this.color = data.color;
		this.createdAt = parseDate(data.createdAt) ?? /* @__PURE__ */ new Date();
		this.description = data.description ?? void 0;
		this.id = data.id;
		this.name = data.name;
		this.position = data.position;
		this.type = data.type;
		this.updatedAt = parseDate(data.updatedAt) ?? /* @__PURE__ */ new Date();
		this._inheritedFrom = data.inheritedFrom ?? void 0;
		this._team = data.team;
	}
	/** The time at which the entity was archived. Null if the entity has not been archived. */
	archivedAt;
	/** The state's UI color as a HEX string. */
	color;
	/** The time at which the entity was created. */
	createdAt;
	/** Description of the state. */
	description;
	/** The unique identifier of the entity. */
	id;
	/** The state's name. */
	name;
	/** The position of the state in the team flow. */
	position;
	/** The type of the state. One of "triage", "backlog", "unstarted", "started", "completed", "canceled". */
	type;
	/**
	* The last time at which the entity was meaningfully updated. This is the same as the creation time if the entity hasn't
	*     been updated after creation.
	*/
	updatedAt;
	/** The state inherited from */
	get inheritedFrom() {
		return this._inheritedFrom?.id ? new WorkflowStateQuery(this._request).fetch(this._inheritedFrom?.id) : void 0;
	}
	/** The ID of state inherited from */
	get inheritedFromId() {
		return this._inheritedFrom?.id;
	}
	/** The team to which this state belongs to. */
	get team() {
		return new TeamQuery(this._request).fetch(this._team.id);
	}
	/** The ID of team to which this state belongs to. */
	get teamId() {
		return this._team?.id;
	}
	/** Issues belonging in this state. */
	issues(variables) {
		return new WorkflowState_IssuesQuery(this._request, this.id, variables).fetch(variables);
	}
	/** Archives a state. Only states with issues that have all been archived can be archived. */
	archive() {
		return new ArchiveWorkflowStateMutation(this._request).fetch(this.id);
	}
	/** Creates a new state, adding it to the workflow of a team. */
	create(input) {
		return new CreateWorkflowStateMutation(this._request).fetch(input);
	}
	/** Updates a state. */
	update(input) {
		return new UpdateWorkflowStateMutation(this._request).fetch(this.id, input);
	}
};
/**
* A generic payload return from entity archive mutations.
*
* @param request - function to call the graphql client
* @param data - L.WorkflowStateArchivePayloadFragment response data
*/
var WorkflowStateArchivePayload = class extends Request {
	_entity;
	constructor(request, data) {
		super(request);
		this.lastSyncId = data.lastSyncId;
		this.success = data.success;
		this._entity = data.entity ?? void 0;
	}
	/** The identifier of the last sync operation. */
	lastSyncId;
	/** Whether the operation was successful. */
	success;
	/** The archived/unarchived entity. Null if entity was deleted. */
	get entity() {
		return this._entity?.id ? new WorkflowStateQuery(this._request).fetch(this._entity?.id) : void 0;
	}
	/** The ID of archived/unarchived entity. null if entity was deleted. */
	get entityId() {
		return this._entity?.id;
	}
};
/**
* Certain properties of a workflow state.
*
* @param data - L.WorkflowStateChildWebhookPayloadFragment response data
*/
var WorkflowStateChildWebhookPayload = class {
	constructor(data) {
		this.color = data.color;
		this.id = data.id;
		this.name = data.name;
		this.type = data.type;
	}
	/** The color of the workflow state. */
	color;
	/** The ID of the workflow state. */
	id;
	/** The name of the workflow state. */
	name;
	/** The type of the workflow state. */
	type;
};
/**
* WorkflowStateConnection model
*
* @param request - function to call the graphql client
* @param fetch - function to trigger a refetch of this WorkflowStateConnection model
* @param data - WorkflowStateConnection response data
*/
var WorkflowStateConnection = class extends Connection {
	constructor(request, fetch$1, data) {
		super(request, fetch$1, data.nodes.map((node) => new WorkflowState(request, node)), new PageInfo(request, data.pageInfo));
	}
};
/**
* WorkflowStatePayload model
*
* @param request - function to call the graphql client
* @param data - L.WorkflowStatePayloadFragment response data
*/
var WorkflowStatePayload = class extends Request {
	_workflowState;
	constructor(request, data) {
		super(request);
		this.lastSyncId = data.lastSyncId;
		this.success = data.success;
		this._workflowState = data.workflowState;
	}
	/** The identifier of the last sync operation. */
	lastSyncId;
	/** Whether the operation was successful. */
	success;
	/** The state that was created or updated. */
	get workflowState() {
		return new WorkflowStateQuery(this._request).fetch(this._workflowState.id);
	}
	/** The ID of state that was created or updated. */
	get workflowStateId() {
		return this._workflowState?.id;
	}
};
/**
* A fetchable AdministrableTeams Query
*
* @param request - function to call the graphql client
*/
var AdministrableTeamsQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the AdministrableTeams query and return a TeamConnection
	*
	* @param variables - variables to pass into the AdministrableTeamsQuery
	* @returns parsed response from AdministrableTeamsQuery
	*/
	async fetch(variables) {
		const data = (await this._request(AdministrableTeamsDocument.toString(), variables)).administrableTeams;
		return new TeamConnection(this._request, (connection) => this.fetch(defaultConnection({
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable AgentActivities Query
*
* @param request - function to call the graphql client
*/
var AgentActivitiesQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the AgentActivities query and return a AgentActivityConnection
	*
	* @param variables - variables to pass into the AgentActivitiesQuery
	* @returns parsed response from AgentActivitiesQuery
	*/
	async fetch(variables) {
		const data = (await this._request(AgentActivitiesDocument.toString(), variables)).agentActivities;
		return new AgentActivityConnection(this._request, (connection) => this.fetch(defaultConnection({
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable AgentActivity Query
*
* @param request - function to call the graphql client
*/
var AgentActivityQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the AgentActivity query and return a AgentActivity
	*
	* @param id - required id to pass to agentActivity
	* @returns parsed response from AgentActivityQuery
	*/
	async fetch(id) {
		const data = (await this._request(AgentActivityDocument.toString(), { id })).agentActivity;
		return new AgentActivity(this._request, data);
	}
};
/**
* A fetchable AgentSession Query
*
* @param request - function to call the graphql client
*/
var AgentSessionQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the AgentSession query and return a AgentSession
	*
	* @param id - required id to pass to agentSession
	* @returns parsed response from AgentSessionQuery
	*/
	async fetch(id) {
		const data = (await this._request(AgentSessionDocument.toString(), { id })).agentSession;
		return new AgentSession(this._request, data);
	}
};
/**
* A fetchable AgentSessions Query
*
* @param request - function to call the graphql client
*/
var AgentSessionsQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the AgentSessions query and return a AgentSessionConnection
	*
	* @param variables - variables to pass into the AgentSessionsQuery
	* @returns parsed response from AgentSessionsQuery
	*/
	async fetch(variables) {
		const data = (await this._request(AgentSessionsDocument.toString(), variables)).agentSessions;
		return new AgentSessionConnection(this._request, (connection) => this.fetch(defaultConnection({
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable ApplicationInfo Query
*
* @param request - function to call the graphql client
*/
var ApplicationInfoQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the ApplicationInfo query and return a Application
	*
	* @param clientId - required clientId to pass to applicationInfo
	* @returns parsed response from ApplicationInfoQuery
	*/
	async fetch(clientId) {
		const data = (await this._request(ApplicationInfoDocument.toString(), { clientId })).applicationInfo;
		return new Application(this._request, data);
	}
};
/**
* A fetchable Attachment Query
*
* @param request - function to call the graphql client
*/
var AttachmentQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the Attachment query and return a Attachment
	*
	* @param id - required id to pass to attachment
	* @returns parsed response from AttachmentQuery
	*/
	async fetch(id) {
		const data = (await this._request(AttachmentDocument.toString(), { id })).attachment;
		return new Attachment(this._request, data);
	}
};
/**
* A fetchable AttachmentIssue Query
*
* @param request - function to call the graphql client
*/
var AttachmentIssueQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the AttachmentIssue query and return a Issue
	*
	* @param id - required id to pass to attachmentIssue
	* @returns parsed response from AttachmentIssueQuery
	*/
	async fetch(id) {
		const data = (await this._request(AttachmentIssueDocument.toString(), { id })).attachmentIssue;
		return new Issue(this._request, data);
	}
};
/**
* A fetchable Attachments Query
*
* @param request - function to call the graphql client
*/
var AttachmentsQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the Attachments query and return a AttachmentConnection
	*
	* @param variables - variables to pass into the AttachmentsQuery
	* @returns parsed response from AttachmentsQuery
	*/
	async fetch(variables) {
		const data = (await this._request(AttachmentsDocument.toString(), variables)).attachments;
		return new AttachmentConnection(this._request, (connection) => this.fetch(defaultConnection({
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable AttachmentsForUrl Query
*
* @param request - function to call the graphql client
*/
var AttachmentsForUrlQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the AttachmentsForUrl query and return a AttachmentConnection
	*
	* @param url - required url to pass to attachmentsForURL
	* @param variables - variables without 'url' to pass into the AttachmentsForUrlQuery
	* @returns parsed response from AttachmentsForUrlQuery
	*/
	async fetch(url, variables) {
		const data = (await this._request(AttachmentsForUrlDocument.toString(), {
			url,
			...variables
		})).attachmentsForURL;
		return new AttachmentConnection(this._request, (connection) => this.fetch(url, defaultConnection({
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable AuditEntries Query
*
* @param request - function to call the graphql client
*/
var AuditEntriesQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the AuditEntries query and return a AuditEntryConnection
	*
	* @param variables - variables to pass into the AuditEntriesQuery
	* @returns parsed response from AuditEntriesQuery
	*/
	async fetch(variables) {
		const data = (await this._request(AuditEntriesDocument.toString(), variables)).auditEntries;
		return new AuditEntryConnection(this._request, (connection) => this.fetch(defaultConnection({
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable AuditEntryTypes Query
*
* @param request - function to call the graphql client
*/
var AuditEntryTypesQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the AuditEntryTypes query and return a AuditEntryType list
	*
	* @returns parsed response from AuditEntryTypesQuery
	*/
	async fetch() {
		return (await this._request(AuditEntryTypesDocument.toString(), {})).auditEntryTypes.map((node) => {
			return new AuditEntryType(this._request, node);
		});
	}
};
/**
* A fetchable AuthenticationSessions Query
*
* @param request - function to call the graphql client
*/
var AuthenticationSessionsQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the AuthenticationSessions query and return a AuthenticationSessionResponse list
	*
	* @returns parsed response from AuthenticationSessionsQuery
	*/
	async fetch() {
		return (await this._request(AuthenticationSessionsDocument.toString(), {})).authenticationSessions.map((node) => {
			return new AuthenticationSessionResponse(this._request, node);
		});
	}
};
/**
* A fetchable AvailableUsers Query
*
* @param request - function to call the graphql client
*/
var AvailableUsersQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the AvailableUsers query and return a AuthResolverResponse
	*
	* @returns parsed response from AvailableUsersQuery
	*/
	async fetch() {
		const data = (await this._request(AvailableUsersDocument.toString(), {})).availableUsers;
		return new AuthResolverResponse(this._request, data);
	}
};
/**
* A fetchable Comment Query
*
* @param request - function to call the graphql client
*/
var CommentQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the Comment query and return a Comment
	*
	* @param variables - variables to pass into the CommentQuery
	* @returns parsed response from CommentQuery
	*/
	async fetch(variables) {
		const data = (await this._request(CommentDocument.toString(), variables)).comment;
		return new Comment(this._request, data);
	}
};
/**
* A fetchable Comments Query
*
* @param request - function to call the graphql client
*/
var CommentsQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the Comments query and return a CommentConnection
	*
	* @param variables - variables to pass into the CommentsQuery
	* @returns parsed response from CommentsQuery
	*/
	async fetch(variables) {
		const data = (await this._request(CommentsDocument.toString(), variables)).comments;
		return new CommentConnection(this._request, (connection) => this.fetch(defaultConnection({
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable CustomView Query
*
* @param request - function to call the graphql client
*/
var CustomViewQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the CustomView query and return a CustomView
	*
	* @param id - required id to pass to customView
	* @returns parsed response from CustomViewQuery
	*/
	async fetch(id) {
		const data = (await this._request(CustomViewDocument.toString(), { id })).customView;
		return new CustomView(this._request, data);
	}
};
/**
* A fetchable CustomViewHasSubscribers Query
*
* @param request - function to call the graphql client
*/
var CustomViewHasSubscribersQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the CustomViewHasSubscribers query and return a CustomViewHasSubscribersPayload
	*
	* @param id - required id to pass to customViewHasSubscribers
	* @returns parsed response from CustomViewHasSubscribersQuery
	*/
	async fetch(id) {
		const data = (await this._request(CustomViewHasSubscribersDocument.toString(), { id })).customViewHasSubscribers;
		return new CustomViewHasSubscribersPayload(this._request, data);
	}
};
/**
* A fetchable CustomViews Query
*
* @param request - function to call the graphql client
*/
var CustomViewsQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the CustomViews query and return a CustomViewConnection
	*
	* @param variables - variables to pass into the CustomViewsQuery
	* @returns parsed response from CustomViewsQuery
	*/
	async fetch(variables) {
		const data = (await this._request(CustomViewsDocument.toString(), variables)).customViews;
		return new CustomViewConnection(this._request, (connection) => this.fetch(defaultConnection({
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable Customer Query
*
* @param request - function to call the graphql client
*/
var CustomerQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the Customer query and return a Customer
	*
	* @param id - required id to pass to customer
	* @returns parsed response from CustomerQuery
	*/
	async fetch(id) {
		const data = (await this._request(CustomerDocument.toString(), { id })).customer;
		return new Customer(this._request, data);
	}
};
/**
* A fetchable CustomerNeed Query
*
* @param request - function to call the graphql client
*/
var CustomerNeedQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the CustomerNeed query and return a CustomerNeed
	*
	* @param variables - variables to pass into the CustomerNeedQuery
	* @returns parsed response from CustomerNeedQuery
	*/
	async fetch(variables) {
		const data = (await this._request(CustomerNeedDocument.toString(), variables)).customerNeed;
		return new CustomerNeed(this._request, data);
	}
};
/**
* A fetchable CustomerNeeds Query
*
* @param request - function to call the graphql client
*/
var CustomerNeedsQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the CustomerNeeds query and return a CustomerNeedConnection
	*
	* @param variables - variables to pass into the CustomerNeedsQuery
	* @returns parsed response from CustomerNeedsQuery
	*/
	async fetch(variables) {
		const data = (await this._request(CustomerNeedsDocument.toString(), variables)).customerNeeds;
		return new CustomerNeedConnection(this._request, (connection) => this.fetch(defaultConnection({
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable CustomerStatus Query
*
* @param request - function to call the graphql client
*/
var CustomerStatusQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the CustomerStatus query and return a CustomerStatus
	*
	* @param id - required id to pass to customerStatus
	* @returns parsed response from CustomerStatusQuery
	*/
	async fetch(id) {
		const data = (await this._request(CustomerStatusDocument.toString(), { id })).customerStatus;
		return new CustomerStatus(this._request, data);
	}
};
/**
* A fetchable CustomerStatuses Query
*
* @param request - function to call the graphql client
*/
var CustomerStatusesQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the CustomerStatuses query and return a CustomerStatusConnection
	*
	* @param variables - variables to pass into the CustomerStatusesQuery
	* @returns parsed response from CustomerStatusesQuery
	*/
	async fetch(variables) {
		const data = (await this._request(CustomerStatusesDocument.toString(), variables)).customerStatuses;
		return new CustomerStatusConnection(this._request, (connection) => this.fetch(defaultConnection({
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable CustomerTier Query
*
* @param request - function to call the graphql client
*/
var CustomerTierQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the CustomerTier query and return a CustomerTier
	*
	* @param id - required id to pass to customerTier
	* @returns parsed response from CustomerTierQuery
	*/
	async fetch(id) {
		const data = (await this._request(CustomerTierDocument.toString(), { id })).customerTier;
		return new CustomerTier(this._request, data);
	}
};
/**
* A fetchable CustomerTiers Query
*
* @param request - function to call the graphql client
*/
var CustomerTiersQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the CustomerTiers query and return a CustomerTierConnection
	*
	* @param variables - variables to pass into the CustomerTiersQuery
	* @returns parsed response from CustomerTiersQuery
	*/
	async fetch(variables) {
		const data = (await this._request(CustomerTiersDocument.toString(), variables)).customerTiers;
		return new CustomerTierConnection(this._request, (connection) => this.fetch(defaultConnection({
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable Customers Query
*
* @param request - function to call the graphql client
*/
var CustomersQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the Customers query and return a CustomerConnection
	*
	* @param variables - variables to pass into the CustomersQuery
	* @returns parsed response from CustomersQuery
	*/
	async fetch(variables) {
		const data = (await this._request(CustomersDocument.toString(), variables)).customers;
		return new CustomerConnection(this._request, (connection) => this.fetch(defaultConnection({
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable Cycle Query
*
* @param request - function to call the graphql client
*/
var CycleQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the Cycle query and return a Cycle
	*
	* @param id - required id to pass to cycle
	* @returns parsed response from CycleQuery
	*/
	async fetch(id) {
		const data = (await this._request(CycleDocument.toString(), { id })).cycle;
		return new Cycle(this._request, data);
	}
};
/**
* A fetchable Cycles Query
*
* @param request - function to call the graphql client
*/
var CyclesQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the Cycles query and return a CycleConnection
	*
	* @param variables - variables to pass into the CyclesQuery
	* @returns parsed response from CyclesQuery
	*/
	async fetch(variables) {
		const data = (await this._request(CyclesDocument.toString(), variables)).cycles;
		return new CycleConnection(this._request, (connection) => this.fetch(defaultConnection({
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable Document Query
*
* @param request - function to call the graphql client
*/
var DocumentQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the Document query and return a Document
	*
	* @param id - required id to pass to document
	* @returns parsed response from DocumentQuery
	*/
	async fetch(id) {
		const data = (await this._request(DocumentDocument.toString(), { id })).document;
		return new Document(this._request, data);
	}
};
/**
* A fetchable DocumentContentHistory Query
*
* @param request - function to call the graphql client
*/
var DocumentContentHistoryQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the DocumentContentHistory query and return a DocumentContentHistoryPayload
	*
	* @param id - required id to pass to documentContentHistory
	* @returns parsed response from DocumentContentHistoryQuery
	*/
	async fetch(id) {
		const data = (await this._request(DocumentContentHistoryDocument.toString(), { id })).documentContentHistory;
		return new DocumentContentHistoryPayload(this._request, data);
	}
};
/**
* A fetchable Documents Query
*
* @param request - function to call the graphql client
*/
var DocumentsQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the Documents query and return a DocumentConnection
	*
	* @param variables - variables to pass into the DocumentsQuery
	* @returns parsed response from DocumentsQuery
	*/
	async fetch(variables) {
		const data = (await this._request(DocumentsDocument.toString(), variables)).documents;
		return new DocumentConnection(this._request, (connection) => this.fetch(defaultConnection({
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable EmailIntakeAddress Query
*
* @param request - function to call the graphql client
*/
var EmailIntakeAddressQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the EmailIntakeAddress query and return a EmailIntakeAddress
	*
	* @param id - required id to pass to emailIntakeAddress
	* @returns parsed response from EmailIntakeAddressQuery
	*/
	async fetch(id) {
		const data = (await this._request(EmailIntakeAddressDocument.toString(), { id })).emailIntakeAddress;
		return new EmailIntakeAddress(this._request, data);
	}
};
/**
* A fetchable Emoji Query
*
* @param request - function to call the graphql client
*/
var EmojiQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the Emoji query and return a Emoji
	*
	* @param id - required id to pass to emoji
	* @returns parsed response from EmojiQuery
	*/
	async fetch(id) {
		const data = (await this._request(EmojiDocument.toString(), { id })).emoji;
		return new Emoji(this._request, data);
	}
};
/**
* A fetchable Emojis Query
*
* @param request - function to call the graphql client
*/
var EmojisQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the Emojis query and return a EmojiConnection
	*
	* @param variables - variables to pass into the EmojisQuery
	* @returns parsed response from EmojisQuery
	*/
	async fetch(variables) {
		const data = (await this._request(EmojisDocument.toString(), variables)).emojis;
		return new EmojiConnection(this._request, (connection) => this.fetch(defaultConnection({
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable EntityExternalLink Query
*
* @param request - function to call the graphql client
*/
var EntityExternalLinkQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the EntityExternalLink query and return a EntityExternalLink
	*
	* @param id - required id to pass to entityExternalLink
	* @returns parsed response from EntityExternalLinkQuery
	*/
	async fetch(id) {
		const data = (await this._request(EntityExternalLinkDocument.toString(), { id })).entityExternalLink;
		return new EntityExternalLink(this._request, data);
	}
};
/**
* A fetchable ExternalUser Query
*
* @param request - function to call the graphql client
*/
var ExternalUserQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the ExternalUser query and return a ExternalUser
	*
	* @param id - required id to pass to externalUser
	* @returns parsed response from ExternalUserQuery
	*/
	async fetch(id) {
		const data = (await this._request(ExternalUserDocument.toString(), { id })).externalUser;
		return new ExternalUser(this._request, data);
	}
};
/**
* A fetchable ExternalUsers Query
*
* @param request - function to call the graphql client
*/
var ExternalUsersQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the ExternalUsers query and return a ExternalUserConnection
	*
	* @param variables - variables to pass into the ExternalUsersQuery
	* @returns parsed response from ExternalUsersQuery
	*/
	async fetch(variables) {
		const data = (await this._request(ExternalUsersDocument.toString(), variables)).externalUsers;
		return new ExternalUserConnection(this._request, (connection) => this.fetch(defaultConnection({
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable Favorite Query
*
* @param request - function to call the graphql client
*/
var FavoriteQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the Favorite query and return a Favorite
	*
	* @param id - required id to pass to favorite
	* @returns parsed response from FavoriteQuery
	*/
	async fetch(id) {
		const data = (await this._request(FavoriteDocument.toString(), { id })).favorite;
		return new Favorite(this._request, data);
	}
};
/**
* A fetchable Favorites Query
*
* @param request - function to call the graphql client
*/
var FavoritesQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the Favorites query and return a FavoriteConnection
	*
	* @param variables - variables to pass into the FavoritesQuery
	* @returns parsed response from FavoritesQuery
	*/
	async fetch(variables) {
		const data = (await this._request(FavoritesDocument.toString(), variables)).favorites;
		return new FavoriteConnection(this._request, (connection) => this.fetch(defaultConnection({
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable Initiative Query
*
* @param request - function to call the graphql client
*/
var InitiativeQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the Initiative query and return a Initiative
	*
	* @param id - required id to pass to initiative
	* @returns parsed response from InitiativeQuery
	*/
	async fetch(id) {
		const data = (await this._request(InitiativeDocument.toString(), { id })).initiative;
		return new Initiative(this._request, data);
	}
};
/**
* A fetchable InitiativeRelation Query
*
* @param request - function to call the graphql client
*/
var InitiativeRelationQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the InitiativeRelation query and return a ProjectRelation
	*
	* @param id - required id to pass to initiativeRelation
	* @returns parsed response from InitiativeRelationQuery
	*/
	async fetch(id) {
		const data = (await this._request(InitiativeRelationDocument.toString(), { id })).initiativeRelation;
		return new ProjectRelation(this._request, data);
	}
};
/**
* A fetchable InitiativeRelations Query
*
* @param request - function to call the graphql client
*/
var InitiativeRelationsQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the InitiativeRelations query and return a InitiativeRelationConnection
	*
	* @param variables - variables to pass into the InitiativeRelationsQuery
	* @returns parsed response from InitiativeRelationsQuery
	*/
	async fetch(variables) {
		const data = (await this._request(InitiativeRelationsDocument.toString(), variables)).initiativeRelations;
		return new InitiativeRelationConnection(this._request, (connection) => this.fetch(defaultConnection({
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable InitiativeToProject Query
*
* @param request - function to call the graphql client
*/
var InitiativeToProjectQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the InitiativeToProject query and return a InitiativeToProject
	*
	* @param id - required id to pass to initiativeToProject
	* @returns parsed response from InitiativeToProjectQuery
	*/
	async fetch(id) {
		const data = (await this._request(InitiativeToProjectDocument.toString(), { id })).initiativeToProject;
		return new InitiativeToProject(this._request, data);
	}
};
/**
* A fetchable InitiativeToProjects Query
*
* @param request - function to call the graphql client
*/
var InitiativeToProjectsQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the InitiativeToProjects query and return a InitiativeToProjectConnection
	*
	* @param variables - variables to pass into the InitiativeToProjectsQuery
	* @returns parsed response from InitiativeToProjectsQuery
	*/
	async fetch(variables) {
		const data = (await this._request(InitiativeToProjectsDocument.toString(), variables)).initiativeToProjects;
		return new InitiativeToProjectConnection(this._request, (connection) => this.fetch(defaultConnection({
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable InitiativeUpdate Query
*
* @param request - function to call the graphql client
*/
var InitiativeUpdateQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the InitiativeUpdate query and return a InitiativeUpdate
	*
	* @param id - required id to pass to initiativeUpdate
	* @returns parsed response from InitiativeUpdateQuery
	*/
	async fetch(id) {
		const data = (await this._request(InitiativeUpdateDocument.toString(), { id })).initiativeUpdate;
		return new InitiativeUpdate(this._request, data);
	}
};
/**
* A fetchable InitiativeUpdates Query
*
* @param request - function to call the graphql client
*/
var InitiativeUpdatesQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the InitiativeUpdates query and return a InitiativeUpdateConnection
	*
	* @param variables - variables to pass into the InitiativeUpdatesQuery
	* @returns parsed response from InitiativeUpdatesQuery
	*/
	async fetch(variables) {
		const data = (await this._request(InitiativeUpdatesDocument.toString(), variables)).initiativeUpdates;
		return new InitiativeUpdateConnection(this._request, (connection) => this.fetch(defaultConnection({
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable Initiatives Query
*
* @param request - function to call the graphql client
*/
var InitiativesQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the Initiatives query and return a InitiativeConnection
	*
	* @param variables - variables to pass into the InitiativesQuery
	* @returns parsed response from InitiativesQuery
	*/
	async fetch(variables) {
		const data = (await this._request(InitiativesDocument.toString(), variables)).initiatives;
		return new InitiativeConnection(this._request, (connection) => this.fetch(defaultConnection({
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable Integration Query
*
* @param request - function to call the graphql client
*/
var IntegrationQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the Integration query and return a Integration
	*
	* @param id - required id to pass to integration
	* @returns parsed response from IntegrationQuery
	*/
	async fetch(id) {
		const data = (await this._request(IntegrationDocument.toString(), { id })).integration;
		return new Integration(this._request, data);
	}
};
/**
* A fetchable IntegrationHasScopes Query
*
* @param request - function to call the graphql client
*/
var IntegrationHasScopesQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the IntegrationHasScopes query and return a IntegrationHasScopesPayload
	*
	* @param integrationId - required integrationId to pass to integrationHasScopes
	* @param scopes - required scopes to pass to integrationHasScopes
	* @returns parsed response from IntegrationHasScopesQuery
	*/
	async fetch(integrationId, scopes) {
		const data = (await this._request(IntegrationHasScopesDocument.toString(), {
			integrationId,
			scopes
		})).integrationHasScopes;
		return new IntegrationHasScopesPayload(this._request, data);
	}
};
/**
* A fetchable IntegrationTemplate Query
*
* @param request - function to call the graphql client
*/
var IntegrationTemplateQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the IntegrationTemplate query and return a IntegrationTemplate
	*
	* @param id - required id to pass to integrationTemplate
	* @returns parsed response from IntegrationTemplateQuery
	*/
	async fetch(id) {
		const data = (await this._request(IntegrationTemplateDocument.toString(), { id })).integrationTemplate;
		return new IntegrationTemplate(this._request, data);
	}
};
/**
* A fetchable IntegrationTemplates Query
*
* @param request - function to call the graphql client
*/
var IntegrationTemplatesQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the IntegrationTemplates query and return a IntegrationTemplateConnection
	*
	* @param variables - variables to pass into the IntegrationTemplatesQuery
	* @returns parsed response from IntegrationTemplatesQuery
	*/
	async fetch(variables) {
		const data = (await this._request(IntegrationTemplatesDocument.toString(), variables)).integrationTemplates;
		return new IntegrationTemplateConnection(this._request, (connection) => this.fetch(defaultConnection({
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable Integrations Query
*
* @param request - function to call the graphql client
*/
var IntegrationsQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the Integrations query and return a IntegrationConnection
	*
	* @param variables - variables to pass into the IntegrationsQuery
	* @returns parsed response from IntegrationsQuery
	*/
	async fetch(variables) {
		const data = (await this._request(IntegrationsDocument.toString(), variables)).integrations;
		return new IntegrationConnection(this._request, (connection) => this.fetch(defaultConnection({
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable IntegrationsSettings Query
*
* @param request - function to call the graphql client
*/
var IntegrationsSettingsQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the IntegrationsSettings query and return a IntegrationsSettings
	*
	* @param id - required id to pass to integrationsSettings
	* @returns parsed response from IntegrationsSettingsQuery
	*/
	async fetch(id) {
		const data = (await this._request(IntegrationsSettingsDocument.toString(), { id })).integrationsSettings;
		return new IntegrationsSettings(this._request, data);
	}
};
/**
* A fetchable Issue Query
*
* @param request - function to call the graphql client
*/
var IssueQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the Issue query and return a Issue
	*
	* @param id - required id to pass to issue
	* @returns parsed response from IssueQuery
	*/
	async fetch(id) {
		const data = (await this._request(IssueDocument.toString(), { id })).issue;
		return new Issue(this._request, data);
	}
};
/**
* A fetchable IssueFigmaFileKeySearch Query
*
* @param request - function to call the graphql client
*/
var IssueFigmaFileKeySearchQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the IssueFigmaFileKeySearch query and return a IssueConnection
	*
	* @param fileKey - required fileKey to pass to issueFigmaFileKeySearch
	* @param variables - variables without 'fileKey' to pass into the IssueFigmaFileKeySearchQuery
	* @returns parsed response from IssueFigmaFileKeySearchQuery
	*/
	async fetch(fileKey, variables) {
		const data = (await this._request(IssueFigmaFileKeySearchDocument.toString(), {
			fileKey,
			...variables
		})).issueFigmaFileKeySearch;
		return new IssueConnection(this._request, (connection) => this.fetch(fileKey, defaultConnection({
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable IssueFilterSuggestion Query
*
* @param request - function to call the graphql client
*/
var IssueFilterSuggestionQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the IssueFilterSuggestion query and return a IssueFilterSuggestionPayload
	*
	* @param prompt - required prompt to pass to issueFilterSuggestion
	* @param variables - variables without 'prompt' to pass into the IssueFilterSuggestionQuery
	* @returns parsed response from IssueFilterSuggestionQuery
	*/
	async fetch(prompt, variables) {
		const data = (await this._request(IssueFilterSuggestionDocument.toString(), {
			prompt,
			...variables
		})).issueFilterSuggestion;
		return new IssueFilterSuggestionPayload(this._request, data);
	}
};
/**
* A fetchable IssueImportCheckCsv Query
*
* @param request - function to call the graphql client
*/
var IssueImportCheckCsvQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the IssueImportCheckCsv query and return a IssueImportCheckPayload
	*
	* @param csvUrl - required csvUrl to pass to issueImportCheckCSV
	* @param service - required service to pass to issueImportCheckCSV
	* @returns parsed response from IssueImportCheckCsvQuery
	*/
	async fetch(csvUrl, service) {
		const data = (await this._request(IssueImportCheckCsvDocument.toString(), {
			csvUrl,
			service
		})).issueImportCheckCSV;
		return new IssueImportCheckPayload(this._request, data);
	}
};
/**
* A fetchable IssueImportCheckSync Query
*
* @param request - function to call the graphql client
*/
var IssueImportCheckSyncQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the IssueImportCheckSync query and return a IssueImportSyncCheckPayload
	*
	* @param issueImportId - required issueImportId to pass to issueImportCheckSync
	* @returns parsed response from IssueImportCheckSyncQuery
	*/
	async fetch(issueImportId) {
		const data = (await this._request(IssueImportCheckSyncDocument.toString(), { issueImportId })).issueImportCheckSync;
		return new IssueImportSyncCheckPayload(this._request, data);
	}
};
/**
* A fetchable IssueImportJqlCheck Query
*
* @param request - function to call the graphql client
*/
var IssueImportJqlCheckQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the IssueImportJqlCheck query and return a IssueImportJqlCheckPayload
	*
	* @param jiraEmail - required jiraEmail to pass to issueImportJqlCheck
	* @param jiraHostname - required jiraHostname to pass to issueImportJqlCheck
	* @param jiraProject - required jiraProject to pass to issueImportJqlCheck
	* @param jiraToken - required jiraToken to pass to issueImportJqlCheck
	* @param jql - required jql to pass to issueImportJqlCheck
	* @returns parsed response from IssueImportJqlCheckQuery
	*/
	async fetch(jiraEmail, jiraHostname, jiraProject, jiraToken, jql) {
		const data = (await this._request(IssueImportJqlCheckDocument.toString(), {
			jiraEmail,
			jiraHostname,
			jiraProject,
			jiraToken,
			jql
		})).issueImportJqlCheck;
		return new IssueImportJqlCheckPayload(this._request, data);
	}
};
/**
* A fetchable IssueLabel Query
*
* @param request - function to call the graphql client
*/
var IssueLabelQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the IssueLabel query and return a IssueLabel
	*
	* @param id - required id to pass to issueLabel
	* @returns parsed response from IssueLabelQuery
	*/
	async fetch(id) {
		const data = (await this._request(IssueLabelDocument.toString(), { id })).issueLabel;
		return new IssueLabel(this._request, data);
	}
};
/**
* A fetchable IssueLabels Query
*
* @param request - function to call the graphql client
*/
var IssueLabelsQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the IssueLabels query and return a IssueLabelConnection
	*
	* @param variables - variables to pass into the IssueLabelsQuery
	* @returns parsed response from IssueLabelsQuery
	*/
	async fetch(variables) {
		const data = (await this._request(IssueLabelsDocument.toString(), variables)).issueLabels;
		return new IssueLabelConnection(this._request, (connection) => this.fetch(defaultConnection({
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable IssuePriorityValues Query
*
* @param request - function to call the graphql client
*/
var IssuePriorityValuesQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the IssuePriorityValues query and return a IssuePriorityValue list
	*
	* @returns parsed response from IssuePriorityValuesQuery
	*/
	async fetch() {
		return (await this._request(IssuePriorityValuesDocument.toString(), {})).issuePriorityValues.map((node) => {
			return new IssuePriorityValue(this._request, node);
		});
	}
};
/**
* A fetchable IssueRelation Query
*
* @param request - function to call the graphql client
*/
var IssueRelationQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the IssueRelation query and return a IssueRelation
	*
	* @param id - required id to pass to issueRelation
	* @returns parsed response from IssueRelationQuery
	*/
	async fetch(id) {
		const data = (await this._request(IssueRelationDocument.toString(), { id })).issueRelation;
		return new IssueRelation(this._request, data);
	}
};
/**
* A fetchable IssueRelations Query
*
* @param request - function to call the graphql client
*/
var IssueRelationsQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the IssueRelations query and return a IssueRelationConnection
	*
	* @param variables - variables to pass into the IssueRelationsQuery
	* @returns parsed response from IssueRelationsQuery
	*/
	async fetch(variables) {
		const data = (await this._request(IssueRelationsDocument.toString(), variables)).issueRelations;
		return new IssueRelationConnection(this._request, (connection) => this.fetch(defaultConnection({
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable IssueSearch Query
*
* @param request - function to call the graphql client
*/
var IssueSearchQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the IssueSearch query and return a IssueConnection
	*
	* @param variables - variables to pass into the IssueSearchQuery
	* @returns parsed response from IssueSearchQuery
	*/
	async fetch(variables) {
		const data = (await this._request(IssueSearchDocument.toString(), variables)).issueSearch;
		return new IssueConnection(this._request, (connection) => this.fetch(defaultConnection({
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable IssueTitleSuggestionFromCustomerRequest Query
*
* @param request - function to call the graphql client
*/
var IssueTitleSuggestionFromCustomerRequestQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the IssueTitleSuggestionFromCustomerRequest query and return a IssueTitleSuggestionFromCustomerRequestPayload
	*
	* @param request - required request to pass to issueTitleSuggestionFromCustomerRequest
	* @returns parsed response from IssueTitleSuggestionFromCustomerRequestQuery
	*/
	async fetch(request) {
		const data = (await this._request(IssueTitleSuggestionFromCustomerRequestDocument.toString(), { request })).issueTitleSuggestionFromCustomerRequest;
		return new IssueTitleSuggestionFromCustomerRequestPayload(this._request, data);
	}
};
/**
* A fetchable IssueVcsBranchSearch Query
*
* @param request - function to call the graphql client
*/
var IssueVcsBranchSearchQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the IssueVcsBranchSearch query and return a Issue
	*
	* @param branchName - required branchName to pass to issueVcsBranchSearch
	* @returns parsed response from IssueVcsBranchSearchQuery
	*/
	async fetch(branchName) {
		const data = (await this._request(IssueVcsBranchSearchDocument.toString(), { branchName })).issueVcsBranchSearch;
		return data ? new Issue(this._request, data) : void 0;
	}
};
/**
* A fetchable Issues Query
*
* @param request - function to call the graphql client
*/
var IssuesQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the Issues query and return a IssueConnection
	*
	* @param variables - variables to pass into the IssuesQuery
	* @returns parsed response from IssuesQuery
	*/
	async fetch(variables) {
		const data = (await this._request(IssuesDocument.toString(), variables)).issues;
		return new IssueConnection(this._request, (connection) => this.fetch(defaultConnection({
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable Notification Query
*
* @param request - function to call the graphql client
*/
var NotificationQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the Notification query and return a Notification
	*
	* @param id - required id to pass to notification
	* @returns parsed response from NotificationQuery
	*/
	async fetch(id) {
		const data = (await this._request(NotificationDocument.toString(), { id })).notification;
		switch (data.__typename) {
			case "CustomerNeedNotification": return new CustomerNeedNotification(this._request, data);
			case "CustomerNotification": return new CustomerNotification(this._request, data);
			case "DocumentNotification": return new DocumentNotification(this._request, data);
			case "InitiativeNotification": return new InitiativeNotification(this._request, data);
			case "IssueNotification": return new IssueNotification(this._request, data);
			case "OauthClientApprovalNotification": return new OauthClientApprovalNotification(this._request, data);
			case "PostNotification": return new PostNotification(this._request, data);
			case "ProjectNotification": return new ProjectNotification(this._request, data);
			case "PullRequestNotification": return new PullRequestNotification(this._request, data);
			default: return new Notification(this._request, data);
		}
	}
};
/**
* A fetchable NotificationSubscription Query
*
* @param request - function to call the graphql client
*/
var NotificationSubscriptionQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the NotificationSubscription query and return a NotificationSubscription
	*
	* @param id - required id to pass to notificationSubscription
	* @returns parsed response from NotificationSubscriptionQuery
	*/
	async fetch(id) {
		const data = (await this._request(NotificationSubscriptionDocument.toString(), { id })).notificationSubscription;
		switch (data.__typename) {
			case "CustomViewNotificationSubscription": return new CustomViewNotificationSubscription(this._request, data);
			case "CustomerNotificationSubscription": return new CustomerNotificationSubscription(this._request, data);
			case "CycleNotificationSubscription": return new CycleNotificationSubscription(this._request, data);
			case "InitiativeNotificationSubscription": return new InitiativeNotificationSubscription(this._request, data);
			case "LabelNotificationSubscription": return new LabelNotificationSubscription(this._request, data);
			case "ProjectNotificationSubscription": return new ProjectNotificationSubscription(this._request, data);
			case "TeamNotificationSubscription": return new TeamNotificationSubscription(this._request, data);
			case "UserNotificationSubscription": return new UserNotificationSubscription(this._request, data);
			default: return new NotificationSubscription(this._request, data);
		}
	}
};
/**
* A fetchable NotificationSubscriptions Query
*
* @param request - function to call the graphql client
*/
var NotificationSubscriptionsQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the NotificationSubscriptions query and return a NotificationSubscriptionConnection
	*
	* @param variables - variables to pass into the NotificationSubscriptionsQuery
	* @returns parsed response from NotificationSubscriptionsQuery
	*/
	async fetch(variables) {
		const data = (await this._request(NotificationSubscriptionsDocument.toString(), variables)).notificationSubscriptions;
		return new NotificationSubscriptionConnection(this._request, (connection) => this.fetch(defaultConnection({
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable Notifications Query
*
* @param request - function to call the graphql client
*/
var NotificationsQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the Notifications query and return a NotificationConnection
	*
	* @param variables - variables to pass into the NotificationsQuery
	* @returns parsed response from NotificationsQuery
	*/
	async fetch(variables) {
		const data = (await this._request(NotificationsDocument.toString(), variables)).notifications;
		return new NotificationConnection(this._request, (connection) => this.fetch(defaultConnection({
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable Organization Query
*
* @param request - function to call the graphql client
*/
var OrganizationQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the Organization query and return a Organization
	*
	* @returns parsed response from OrganizationQuery
	*/
	async fetch() {
		const data = (await this._request(OrganizationDocument.toString(), {})).organization;
		return new Organization(this._request, data);
	}
};
/**
* A fetchable OrganizationExists Query
*
* @param request - function to call the graphql client
*/
var OrganizationExistsQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the OrganizationExists query and return a OrganizationExistsPayload
	*
	* @param urlKey - required urlKey to pass to organizationExists
	* @returns parsed response from OrganizationExistsQuery
	*/
	async fetch(urlKey) {
		const data = (await this._request(OrganizationExistsDocument.toString(), { urlKey })).organizationExists;
		return new OrganizationExistsPayload(this._request, data);
	}
};
/**
* A fetchable OrganizationInvite Query
*
* @param request - function to call the graphql client
*/
var OrganizationInviteQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the OrganizationInvite query and return a OrganizationInvite
	*
	* @param id - required id to pass to organizationInvite
	* @returns parsed response from OrganizationInviteQuery
	*/
	async fetch(id) {
		const data = (await this._request(OrganizationInviteDocument.toString(), { id })).organizationInvite;
		return new OrganizationInvite(this._request, data);
	}
};
/**
* A fetchable OrganizationInvites Query
*
* @param request - function to call the graphql client
*/
var OrganizationInvitesQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the OrganizationInvites query and return a OrganizationInviteConnection
	*
	* @param variables - variables to pass into the OrganizationInvitesQuery
	* @returns parsed response from OrganizationInvitesQuery
	*/
	async fetch(variables) {
		const data = (await this._request(OrganizationInvitesDocument.toString(), variables)).organizationInvites;
		return new OrganizationInviteConnection(this._request, (connection) => this.fetch(defaultConnection({
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable Project Query
*
* @param request - function to call the graphql client
*/
var ProjectQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the Project query and return a Project
	*
	* @param id - required id to pass to project
	* @returns parsed response from ProjectQuery
	*/
	async fetch(id) {
		const data = (await this._request(ProjectDocument.toString(), { id })).project;
		return new Project(this._request, data);
	}
};
/**
* A fetchable ProjectFilterSuggestion Query
*
* @param request - function to call the graphql client
*/
var ProjectFilterSuggestionQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the ProjectFilterSuggestion query and return a ProjectFilterSuggestionPayload
	*
	* @param prompt - required prompt to pass to projectFilterSuggestion
	* @returns parsed response from ProjectFilterSuggestionQuery
	*/
	async fetch(prompt) {
		const data = (await this._request(ProjectFilterSuggestionDocument.toString(), { prompt })).projectFilterSuggestion;
		return new ProjectFilterSuggestionPayload(this._request, data);
	}
};
/**
* A fetchable ProjectLabel Query
*
* @param request - function to call the graphql client
*/
var ProjectLabelQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the ProjectLabel query and return a ProjectLabel
	*
	* @param id - required id to pass to projectLabel
	* @returns parsed response from ProjectLabelQuery
	*/
	async fetch(id) {
		const data = (await this._request(ProjectLabelDocument.toString(), { id })).projectLabel;
		return new ProjectLabel(this._request, data);
	}
};
/**
* A fetchable ProjectLabels Query
*
* @param request - function to call the graphql client
*/
var ProjectLabelsQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the ProjectLabels query and return a ProjectLabelConnection
	*
	* @param variables - variables to pass into the ProjectLabelsQuery
	* @returns parsed response from ProjectLabelsQuery
	*/
	async fetch(variables) {
		const data = (await this._request(ProjectLabelsDocument.toString(), variables)).projectLabels;
		return new ProjectLabelConnection(this._request, (connection) => this.fetch(defaultConnection({
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable ProjectMilestone Query
*
* @param request - function to call the graphql client
*/
var ProjectMilestoneQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the ProjectMilestone query and return a ProjectMilestone
	*
	* @param id - required id to pass to projectMilestone
	* @returns parsed response from ProjectMilestoneQuery
	*/
	async fetch(id) {
		const data = (await this._request(ProjectMilestoneDocument.toString(), { id })).projectMilestone;
		return new ProjectMilestone(this._request, data);
	}
};
/**
* A fetchable ProjectMilestones Query
*
* @param request - function to call the graphql client
*/
var ProjectMilestonesQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the ProjectMilestones query and return a ProjectMilestoneConnection
	*
	* @param variables - variables to pass into the ProjectMilestonesQuery
	* @returns parsed response from ProjectMilestonesQuery
	*/
	async fetch(variables) {
		const data = (await this._request(ProjectMilestonesDocument.toString(), variables)).projectMilestones;
		return new ProjectMilestoneConnection(this._request, (connection) => this.fetch(defaultConnection({
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable ProjectRelation Query
*
* @param request - function to call the graphql client
*/
var ProjectRelationQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the ProjectRelation query and return a ProjectRelation
	*
	* @param id - required id to pass to projectRelation
	* @returns parsed response from ProjectRelationQuery
	*/
	async fetch(id) {
		const data = (await this._request(ProjectRelationDocument.toString(), { id })).projectRelation;
		return new ProjectRelation(this._request, data);
	}
};
/**
* A fetchable ProjectRelations Query
*
* @param request - function to call the graphql client
*/
var ProjectRelationsQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the ProjectRelations query and return a ProjectRelationConnection
	*
	* @param variables - variables to pass into the ProjectRelationsQuery
	* @returns parsed response from ProjectRelationsQuery
	*/
	async fetch(variables) {
		const data = (await this._request(ProjectRelationsDocument.toString(), variables)).projectRelations;
		return new ProjectRelationConnection(this._request, (connection) => this.fetch(defaultConnection({
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable ProjectStatus Query
*
* @param request - function to call the graphql client
*/
var ProjectStatusQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the ProjectStatus query and return a ProjectStatus
	*
	* @param id - required id to pass to projectStatus
	* @returns parsed response from ProjectStatusQuery
	*/
	async fetch(id) {
		const data = (await this._request(ProjectStatusDocument.toString(), { id })).projectStatus;
		return new ProjectStatus(this._request, data);
	}
};
/**
* A fetchable ProjectStatuses Query
*
* @param request - function to call the graphql client
*/
var ProjectStatusesQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the ProjectStatuses query and return a ProjectStatusConnection
	*
	* @param variables - variables to pass into the ProjectStatusesQuery
	* @returns parsed response from ProjectStatusesQuery
	*/
	async fetch(variables) {
		const data = (await this._request(ProjectStatusesDocument.toString(), variables)).projectStatuses;
		return new ProjectStatusConnection(this._request, (connection) => this.fetch(defaultConnection({
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable ProjectUpdate Query
*
* @param request - function to call the graphql client
*/
var ProjectUpdateQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the ProjectUpdate query and return a ProjectUpdate
	*
	* @param id - required id to pass to projectUpdate
	* @returns parsed response from ProjectUpdateQuery
	*/
	async fetch(id) {
		const data = (await this._request(ProjectUpdateDocument.toString(), { id })).projectUpdate;
		return new ProjectUpdate(this._request, data);
	}
};
/**
* A fetchable ProjectUpdates Query
*
* @param request - function to call the graphql client
*/
var ProjectUpdatesQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the ProjectUpdates query and return a ProjectUpdateConnection
	*
	* @param variables - variables to pass into the ProjectUpdatesQuery
	* @returns parsed response from ProjectUpdatesQuery
	*/
	async fetch(variables) {
		const data = (await this._request(ProjectUpdatesDocument.toString(), variables)).projectUpdates;
		return new ProjectUpdateConnection(this._request, (connection) => this.fetch(defaultConnection({
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable Projects Query
*
* @param request - function to call the graphql client
*/
var ProjectsQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the Projects query and return a ProjectConnection
	*
	* @param variables - variables to pass into the ProjectsQuery
	* @returns parsed response from ProjectsQuery
	*/
	async fetch(variables) {
		const data = (await this._request(ProjectsDocument.toString(), variables)).projects;
		return new ProjectConnection(this._request, (connection) => this.fetch(defaultConnection({
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable PushSubscriptionTest Query
*
* @param request - function to call the graphql client
*/
var PushSubscriptionTestQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the PushSubscriptionTest query and return a PushSubscriptionTestPayload
	*
	* @param variables - variables to pass into the PushSubscriptionTestQuery
	* @returns parsed response from PushSubscriptionTestQuery
	*/
	async fetch(variables) {
		const data = (await this._request(PushSubscriptionTestDocument.toString(), variables)).pushSubscriptionTest;
		return new PushSubscriptionTestPayload(this._request, data);
	}
};
/**
* A fetchable RateLimitStatus Query
*
* @param request - function to call the graphql client
*/
var RateLimitStatusQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the RateLimitStatus query and return a RateLimitPayload
	*
	* @returns parsed response from RateLimitStatusQuery
	*/
	async fetch() {
		const data = (await this._request(RateLimitStatusDocument.toString(), {})).rateLimitStatus;
		return new RateLimitPayload(this._request, data);
	}
};
/**
* A fetchable Roadmap Query
*
* @param request - function to call the graphql client
*/
var RoadmapQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the Roadmap query and return a Roadmap
	*
	* @param id - required id to pass to roadmap
	* @returns parsed response from RoadmapQuery
	*/
	async fetch(id) {
		const data = (await this._request(RoadmapDocument.toString(), { id })).roadmap;
		return new Roadmap(this._request, data);
	}
};
/**
* A fetchable RoadmapToProject Query
*
* @param request - function to call the graphql client
*/
var RoadmapToProjectQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the RoadmapToProject query and return a RoadmapToProject
	*
	* @param id - required id to pass to roadmapToProject
	* @returns parsed response from RoadmapToProjectQuery
	*/
	async fetch(id) {
		const data = (await this._request(RoadmapToProjectDocument.toString(), { id })).roadmapToProject;
		return new RoadmapToProject(this._request, data);
	}
};
/**
* A fetchable RoadmapToProjects Query
*
* @param request - function to call the graphql client
*/
var RoadmapToProjectsQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the RoadmapToProjects query and return a RoadmapToProjectConnection
	*
	* @param variables - variables to pass into the RoadmapToProjectsQuery
	* @returns parsed response from RoadmapToProjectsQuery
	*/
	async fetch(variables) {
		const data = (await this._request(RoadmapToProjectsDocument.toString(), variables)).roadmapToProjects;
		return new RoadmapToProjectConnection(this._request, (connection) => this.fetch(defaultConnection({
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable Roadmaps Query
*
* @param request - function to call the graphql client
*/
var RoadmapsQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the Roadmaps query and return a RoadmapConnection
	*
	* @param variables - variables to pass into the RoadmapsQuery
	* @returns parsed response from RoadmapsQuery
	*/
	async fetch(variables) {
		const data = (await this._request(RoadmapsDocument.toString(), variables)).roadmaps;
		return new RoadmapConnection(this._request, (connection) => this.fetch(defaultConnection({
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable SearchDocuments Query
*
* @param request - function to call the graphql client
*/
var SearchDocumentsQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the SearchDocuments query and return a DocumentSearchPayload
	*
	* @param term - required term to pass to searchDocuments
	* @param variables - variables without 'term' to pass into the SearchDocumentsQuery
	* @returns parsed response from SearchDocumentsQuery
	*/
	async fetch(term, variables) {
		const data = (await this._request(SearchDocumentsDocument.toString(), {
			term,
			...variables
		})).searchDocuments;
		return new DocumentSearchPayload(this._request, data);
	}
};
/**
* A fetchable SearchIssues Query
*
* @param request - function to call the graphql client
*/
var SearchIssuesQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the SearchIssues query and return a IssueSearchPayload
	*
	* @param term - required term to pass to searchIssues
	* @param variables - variables without 'term' to pass into the SearchIssuesQuery
	* @returns parsed response from SearchIssuesQuery
	*/
	async fetch(term, variables) {
		const data = (await this._request(SearchIssuesDocument.toString(), {
			term,
			...variables
		})).searchIssues;
		return new IssueSearchPayload(this._request, data);
	}
};
/**
* A fetchable SearchProjects Query
*
* @param request - function to call the graphql client
*/
var SearchProjectsQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the SearchProjects query and return a ProjectSearchPayload
	*
	* @param term - required term to pass to searchProjects
	* @param variables - variables without 'term' to pass into the SearchProjectsQuery
	* @returns parsed response from SearchProjectsQuery
	*/
	async fetch(term, variables) {
		const data = (await this._request(SearchProjectsDocument.toString(), {
			term,
			...variables
		})).searchProjects;
		return new ProjectSearchPayload(this._request, data);
	}
};
/**
* A fetchable SemanticSearch Query
*
* @param request - function to call the graphql client
*/
var SemanticSearchQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the SemanticSearch query and return a SemanticSearchPayload
	*
	* @param query - required query to pass to semanticSearch
	* @param variables - variables without 'query' to pass into the SemanticSearchQuery
	* @returns parsed response from SemanticSearchQuery
	*/
	async fetch(query, variables) {
		const data = (await this._request(SemanticSearchDocument.toString(), {
			query,
			...variables
		})).semanticSearch;
		return new SemanticSearchPayload(this._request, data);
	}
};
/**
* A fetchable SsoUrlFromEmail Query
*
* @param request - function to call the graphql client
*/
var SsoUrlFromEmailQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the SsoUrlFromEmail query and return a SsoUrlFromEmailResponse
	*
	* @param email - required email to pass to ssoUrlFromEmail
	* @param type - required type to pass to ssoUrlFromEmail
	* @param variables - variables without 'email', 'type' to pass into the SsoUrlFromEmailQuery
	* @returns parsed response from SsoUrlFromEmailQuery
	*/
	async fetch(email, type, variables) {
		const data = (await this._request(SsoUrlFromEmailDocument.toString(), {
			email,
			type,
			...variables
		})).ssoUrlFromEmail;
		return new SsoUrlFromEmailResponse(this._request, data);
	}
};
/**
* A fetchable Team Query
*
* @param request - function to call the graphql client
*/
var TeamQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the Team query and return a Team
	*
	* @param id - required id to pass to team
	* @returns parsed response from TeamQuery
	*/
	async fetch(id) {
		const data = (await this._request(TeamDocument.toString(), { id })).team;
		return new Team(this._request, data);
	}
};
/**
* A fetchable TeamMembership Query
*
* @param request - function to call the graphql client
*/
var TeamMembershipQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the TeamMembership query and return a TeamMembership
	*
	* @param id - required id to pass to teamMembership
	* @returns parsed response from TeamMembershipQuery
	*/
	async fetch(id) {
		const data = (await this._request(TeamMembershipDocument.toString(), { id })).teamMembership;
		return new TeamMembership(this._request, data);
	}
};
/**
* A fetchable TeamMemberships Query
*
* @param request - function to call the graphql client
*/
var TeamMembershipsQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the TeamMemberships query and return a TeamMembershipConnection
	*
	* @param variables - variables to pass into the TeamMembershipsQuery
	* @returns parsed response from TeamMembershipsQuery
	*/
	async fetch(variables) {
		const data = (await this._request(TeamMembershipsDocument.toString(), variables)).teamMemberships;
		return new TeamMembershipConnection(this._request, (connection) => this.fetch(defaultConnection({
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable Teams Query
*
* @param request - function to call the graphql client
*/
var TeamsQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the Teams query and return a TeamConnection
	*
	* @param variables - variables to pass into the TeamsQuery
	* @returns parsed response from TeamsQuery
	*/
	async fetch(variables) {
		const data = (await this._request(TeamsDocument.toString(), variables)).teams;
		return new TeamConnection(this._request, (connection) => this.fetch(defaultConnection({
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable Template Query
*
* @param request - function to call the graphql client
*/
var TemplateQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the Template query and return a Template
	*
	* @param id - required id to pass to template
	* @returns parsed response from TemplateQuery
	*/
	async fetch(id) {
		const data = (await this._request(TemplateDocument.toString(), { id })).template;
		return new Template(this._request, data);
	}
};
/**
* A fetchable Templates Query
*
* @param request - function to call the graphql client
*/
var TemplatesQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the Templates query and return a Template list
	*
	* @returns parsed response from TemplatesQuery
	*/
	async fetch() {
		return (await this._request(TemplatesDocument.toString(), {})).templates.map((node) => {
			return new Template(this._request, node);
		});
	}
};
/**
* A fetchable TemplatesForIntegration Query
*
* @param request - function to call the graphql client
*/
var TemplatesForIntegrationQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the TemplatesForIntegration query and return a Template list
	*
	* @param integrationType - required integrationType to pass to templatesForIntegration
	* @returns parsed response from TemplatesForIntegrationQuery
	*/
	async fetch(integrationType) {
		return (await this._request(TemplatesForIntegrationDocument.toString(), { integrationType })).templatesForIntegration.map((node) => {
			return new Template(this._request, node);
		});
	}
};
/**
* A fetchable TimeSchedule Query
*
* @param request - function to call the graphql client
*/
var TimeScheduleQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the TimeSchedule query and return a TimeSchedule
	*
	* @param id - required id to pass to timeSchedule
	* @returns parsed response from TimeScheduleQuery
	*/
	async fetch(id) {
		const data = (await this._request(TimeScheduleDocument.toString(), { id })).timeSchedule;
		return new TimeSchedule(this._request, data);
	}
};
/**
* A fetchable TimeSchedules Query
*
* @param request - function to call the graphql client
*/
var TimeSchedulesQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the TimeSchedules query and return a TimeScheduleConnection
	*
	* @param variables - variables to pass into the TimeSchedulesQuery
	* @returns parsed response from TimeSchedulesQuery
	*/
	async fetch(variables) {
		const data = (await this._request(TimeSchedulesDocument.toString(), variables)).timeSchedules;
		return new TimeScheduleConnection(this._request, (connection) => this.fetch(defaultConnection({
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable TriageResponsibilities Query
*
* @param request - function to call the graphql client
*/
var TriageResponsibilitiesQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the TriageResponsibilities query and return a TriageResponsibilityConnection
	*
	* @param variables - variables to pass into the TriageResponsibilitiesQuery
	* @returns parsed response from TriageResponsibilitiesQuery
	*/
	async fetch(variables) {
		const data = (await this._request(TriageResponsibilitiesDocument.toString(), variables)).triageResponsibilities;
		return new TriageResponsibilityConnection(this._request, (connection) => this.fetch(defaultConnection({
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable TriageResponsibility Query
*
* @param request - function to call the graphql client
*/
var TriageResponsibilityQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the TriageResponsibility query and return a TriageResponsibility
	*
	* @param id - required id to pass to triageResponsibility
	* @returns parsed response from TriageResponsibilityQuery
	*/
	async fetch(id) {
		const data = (await this._request(TriageResponsibilityDocument.toString(), { id })).triageResponsibility;
		return new TriageResponsibility(this._request, data);
	}
};
/**
* A fetchable User Query
*
* @param request - function to call the graphql client
*/
var UserQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the User query and return a User
	*
	* @param id - required id to pass to user
	* @returns parsed response from UserQuery
	*/
	async fetch(id) {
		const data = (await this._request(UserDocument.toString(), { id })).user;
		return new User(this._request, data);
	}
};
/**
* A fetchable UserSettings Query
*
* @param request - function to call the graphql client
*/
var UserSettingsQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the UserSettings query and return a UserSettings
	*
	* @returns parsed response from UserSettingsQuery
	*/
	async fetch() {
		const data = (await this._request(UserSettingsDocument.toString(), {})).userSettings;
		return new UserSettings(this._request, data);
	}
};
/**
* A fetchable Users Query
*
* @param request - function to call the graphql client
*/
var UsersQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the Users query and return a UserConnection
	*
	* @param variables - variables to pass into the UsersQuery
	* @returns parsed response from UsersQuery
	*/
	async fetch(variables) {
		const data = (await this._request(UsersDocument.toString(), variables)).users;
		return new UserConnection(this._request, (connection) => this.fetch(defaultConnection({
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable VerifyGitHubEnterpriseServerInstallation Query
*
* @param request - function to call the graphql client
*/
var VerifyGitHubEnterpriseServerInstallationQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the VerifyGitHubEnterpriseServerInstallation query and return a GitHubEnterpriseServerInstallVerificationPayload
	*
	* @param integrationId - required integrationId to pass to verifyGitHubEnterpriseServerInstallation
	* @returns parsed response from VerifyGitHubEnterpriseServerInstallationQuery
	*/
	async fetch(integrationId) {
		const data = (await this._request(VerifyGitHubEnterpriseServerInstallationDocument.toString(), { integrationId })).verifyGitHubEnterpriseServerInstallation;
		return new GitHubEnterpriseServerInstallVerificationPayload(this._request, data);
	}
};
/**
* A fetchable Viewer Query
*
* @param request - function to call the graphql client
*/
var ViewerQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the Viewer query and return a User
	*
	* @returns parsed response from ViewerQuery
	*/
	async fetch() {
		const data = (await this._request(ViewerDocument.toString(), {})).viewer;
		return new User(this._request, data);
	}
};
/**
* A fetchable Webhook Query
*
* @param request - function to call the graphql client
*/
var WebhookQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the Webhook query and return a Webhook
	*
	* @param id - required id to pass to webhook
	* @returns parsed response from WebhookQuery
	*/
	async fetch(id) {
		const data = (await this._request(WebhookDocument.toString(), { id })).webhook;
		return new Webhook(this._request, data);
	}
};
/**
* A fetchable Webhooks Query
*
* @param request - function to call the graphql client
*/
var WebhooksQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the Webhooks query and return a WebhookConnection
	*
	* @param variables - variables to pass into the WebhooksQuery
	* @returns parsed response from WebhooksQuery
	*/
	async fetch(variables) {
		const data = (await this._request(WebhooksDocument.toString(), variables)).webhooks;
		return new WebhookConnection(this._request, (connection) => this.fetch(defaultConnection({
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable WorkflowState Query
*
* @param request - function to call the graphql client
*/
var WorkflowStateQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the WorkflowState query and return a WorkflowState
	*
	* @param id - required id to pass to workflowState
	* @returns parsed response from WorkflowStateQuery
	*/
	async fetch(id) {
		const data = (await this._request(WorkflowStateDocument.toString(), { id })).workflowState;
		return new WorkflowState(this._request, data);
	}
};
/**
* A fetchable WorkflowStates Query
*
* @param request - function to call the graphql client
*/
var WorkflowStatesQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the WorkflowStates query and return a WorkflowStateConnection
	*
	* @param variables - variables to pass into the WorkflowStatesQuery
	* @returns parsed response from WorkflowStatesQuery
	*/
	async fetch(variables) {
		const data = (await this._request(WorkflowStatesDocument.toString(), variables)).workflowStates;
		return new WorkflowStateConnection(this._request, (connection) => this.fetch(defaultConnection({
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable CreateAgentActivity Mutation
*
* @param request - function to call the graphql client
*/
var CreateAgentActivityMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the CreateAgentActivity mutation and return a AgentActivityPayload
	*
	* @param input - required input to pass to createAgentActivity
	* @returns parsed response from CreateAgentActivityMutation
	*/
	async fetch(input) {
		const data = (await this._request(CreateAgentActivityDocument.toString(), { input })).agentActivityCreate;
		return new AgentActivityPayload(this._request, data);
	}
};
/**
* A fetchable AgentSessionCreateOnComment Mutation
*
* @param request - function to call the graphql client
*/
var AgentSessionCreateOnCommentMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the AgentSessionCreateOnComment mutation and return a AgentSessionPayload
	*
	* @param input - required input to pass to agentSessionCreateOnComment
	* @returns parsed response from AgentSessionCreateOnCommentMutation
	*/
	async fetch(input) {
		const data = (await this._request(AgentSessionCreateOnCommentDocument.toString(), { input })).agentSessionCreateOnComment;
		return new AgentSessionPayload(this._request, data);
	}
};
/**
* A fetchable AgentSessionCreateOnIssue Mutation
*
* @param request - function to call the graphql client
*/
var AgentSessionCreateOnIssueMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the AgentSessionCreateOnIssue mutation and return a AgentSessionPayload
	*
	* @param input - required input to pass to agentSessionCreateOnIssue
	* @returns parsed response from AgentSessionCreateOnIssueMutation
	*/
	async fetch(input) {
		const data = (await this._request(AgentSessionCreateOnIssueDocument.toString(), { input })).agentSessionCreateOnIssue;
		return new AgentSessionPayload(this._request, data);
	}
};
/**
* A fetchable UpdateAgentSession Mutation
*
* @param request - function to call the graphql client
*/
var UpdateAgentSessionMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the UpdateAgentSession mutation and return a AgentSessionPayload
	*
	* @param id - required id to pass to updateAgentSession
	* @param input - required input to pass to updateAgentSession
	* @returns parsed response from UpdateAgentSessionMutation
	*/
	async fetch(id, input) {
		const data = (await this._request(UpdateAgentSessionDocument.toString(), {
			id,
			input
		})).agentSessionUpdate;
		return new AgentSessionPayload(this._request, data);
	}
};
/**
* A fetchable AgentSessionUpdateExternalUrl Mutation
*
* @param request - function to call the graphql client
*/
var AgentSessionUpdateExternalUrlMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the AgentSessionUpdateExternalUrl mutation and return a AgentSessionPayload
	*
	* @param id - required id to pass to agentSessionUpdateExternalUrl
	* @param input - required input to pass to agentSessionUpdateExternalUrl
	* @returns parsed response from AgentSessionUpdateExternalUrlMutation
	*/
	async fetch(id, input) {
		const data = (await this._request(AgentSessionUpdateExternalUrlDocument.toString(), {
			id,
			input
		})).agentSessionUpdateExternalUrl;
		return new AgentSessionPayload(this._request, data);
	}
};
/**
* A fetchable AirbyteIntegrationConnect Mutation
*
* @param request - function to call the graphql client
*/
var AirbyteIntegrationConnectMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the AirbyteIntegrationConnect mutation and return a IntegrationPayload
	*
	* @param input - required input to pass to airbyteIntegrationConnect
	* @returns parsed response from AirbyteIntegrationConnectMutation
	*/
	async fetch(input) {
		const data = (await this._request(AirbyteIntegrationConnectDocument.toString(), { input })).airbyteIntegrationConnect;
		return new IntegrationPayload(this._request, data);
	}
};
/**
* A fetchable AsksWebFormsAuth Mutation
*
* @param request - function to call the graphql client
*/
var AsksWebFormsAuthMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the AsksWebFormsAuth mutation and return a AsksWebFormsAuthResponse
	*
	* @param token - required token to pass to asksWebFormsAuth
	* @returns parsed response from AsksWebFormsAuthMutation
	*/
	async fetch(token) {
		const data = (await this._request(AsksWebFormsAuthDocument.toString(), { token })).asksWebFormsAuth;
		return new AsksWebFormsAuthResponse(this._request, data);
	}
};
/**
* A fetchable CreateAttachment Mutation
*
* @param request - function to call the graphql client
*/
var CreateAttachmentMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the CreateAttachment mutation and return a AttachmentPayload
	*
	* @param input - required input to pass to createAttachment
	* @returns parsed response from CreateAttachmentMutation
	*/
	async fetch(input) {
		const data = (await this._request(CreateAttachmentDocument.toString(), { input })).attachmentCreate;
		return new AttachmentPayload(this._request, data);
	}
};
/**
* A fetchable DeleteAttachment Mutation
*
* @param request - function to call the graphql client
*/
var DeleteAttachmentMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the DeleteAttachment mutation and return a DeletePayload
	*
	* @param id - required id to pass to deleteAttachment
	* @returns parsed response from DeleteAttachmentMutation
	*/
	async fetch(id) {
		const data = (await this._request(DeleteAttachmentDocument.toString(), { id })).attachmentDelete;
		return new DeletePayload(this._request, data);
	}
};
/**
* A fetchable AttachmentLinkDiscord Mutation
*
* @param request - function to call the graphql client
*/
var AttachmentLinkDiscordMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the AttachmentLinkDiscord mutation and return a AttachmentPayload
	*
	* @param channelId - required channelId to pass to attachmentLinkDiscord
	* @param issueId - required issueId to pass to attachmentLinkDiscord
	* @param messageId - required messageId to pass to attachmentLinkDiscord
	* @param url - required url to pass to attachmentLinkDiscord
	* @param variables - variables without 'channelId', 'issueId', 'messageId', 'url' to pass into the AttachmentLinkDiscordMutation
	* @returns parsed response from AttachmentLinkDiscordMutation
	*/
	async fetch(channelId, issueId, messageId, url, variables) {
		const data = (await this._request(AttachmentLinkDiscordDocument.toString(), {
			channelId,
			issueId,
			messageId,
			url,
			...variables
		})).attachmentLinkDiscord;
		return new AttachmentPayload(this._request, data);
	}
};
/**
* A fetchable AttachmentLinkFront Mutation
*
* @param request - function to call the graphql client
*/
var AttachmentLinkFrontMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the AttachmentLinkFront mutation and return a FrontAttachmentPayload
	*
	* @param conversationId - required conversationId to pass to attachmentLinkFront
	* @param issueId - required issueId to pass to attachmentLinkFront
	* @param variables - variables without 'conversationId', 'issueId' to pass into the AttachmentLinkFrontMutation
	* @returns parsed response from AttachmentLinkFrontMutation
	*/
	async fetch(conversationId, issueId, variables) {
		const data = (await this._request(AttachmentLinkFrontDocument.toString(), {
			conversationId,
			issueId,
			...variables
		})).attachmentLinkFront;
		return new FrontAttachmentPayload(this._request, data);
	}
};
/**
* A fetchable AttachmentLinkGitHubIssue Mutation
*
* @param request - function to call the graphql client
*/
var AttachmentLinkGitHubIssueMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the AttachmentLinkGitHubIssue mutation and return a AttachmentPayload
	*
	* @param issueId - required issueId to pass to attachmentLinkGitHubIssue
	* @param url - required url to pass to attachmentLinkGitHubIssue
	* @param variables - variables without 'issueId', 'url' to pass into the AttachmentLinkGitHubIssueMutation
	* @returns parsed response from AttachmentLinkGitHubIssueMutation
	*/
	async fetch(issueId, url, variables) {
		const data = (await this._request(AttachmentLinkGitHubIssueDocument.toString(), {
			issueId,
			url,
			...variables
		})).attachmentLinkGitHubIssue;
		return new AttachmentPayload(this._request, data);
	}
};
/**
* A fetchable AttachmentLinkGitHubPr Mutation
*
* @param request - function to call the graphql client
*/
var AttachmentLinkGitHubPrMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the AttachmentLinkGitHubPr mutation and return a AttachmentPayload
	*
	* @param issueId - required issueId to pass to attachmentLinkGitHubPR
	* @param url - required url to pass to attachmentLinkGitHubPR
	* @param variables - variables without 'issueId', 'url' to pass into the AttachmentLinkGitHubPrMutation
	* @returns parsed response from AttachmentLinkGitHubPrMutation
	*/
	async fetch(issueId, url, variables) {
		const data = (await this._request(AttachmentLinkGitHubPrDocument.toString(), {
			issueId,
			url,
			...variables
		})).attachmentLinkGitHubPR;
		return new AttachmentPayload(this._request, data);
	}
};
/**
* A fetchable AttachmentLinkGitLabMr Mutation
*
* @param request - function to call the graphql client
*/
var AttachmentLinkGitLabMrMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the AttachmentLinkGitLabMr mutation and return a AttachmentPayload
	*
	* @param issueId - required issueId to pass to attachmentLinkGitLabMR
	* @param number - required number to pass to attachmentLinkGitLabMR
	* @param projectPathWithNamespace - required projectPathWithNamespace to pass to attachmentLinkGitLabMR
	* @param url - required url to pass to attachmentLinkGitLabMR
	* @param variables - variables without 'issueId', 'number', 'projectPathWithNamespace', 'url' to pass into the AttachmentLinkGitLabMrMutation
	* @returns parsed response from AttachmentLinkGitLabMrMutation
	*/
	async fetch(issueId, number, projectPathWithNamespace, url, variables) {
		const data = (await this._request(AttachmentLinkGitLabMrDocument.toString(), {
			issueId,
			number,
			projectPathWithNamespace,
			url,
			...variables
		})).attachmentLinkGitLabMR;
		return new AttachmentPayload(this._request, data);
	}
};
/**
* A fetchable AttachmentLinkIntercom Mutation
*
* @param request - function to call the graphql client
*/
var AttachmentLinkIntercomMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the AttachmentLinkIntercom mutation and return a AttachmentPayload
	*
	* @param conversationId - required conversationId to pass to attachmentLinkIntercom
	* @param issueId - required issueId to pass to attachmentLinkIntercom
	* @param variables - variables without 'conversationId', 'issueId' to pass into the AttachmentLinkIntercomMutation
	* @returns parsed response from AttachmentLinkIntercomMutation
	*/
	async fetch(conversationId, issueId, variables) {
		const data = (await this._request(AttachmentLinkIntercomDocument.toString(), {
			conversationId,
			issueId,
			...variables
		})).attachmentLinkIntercom;
		return new AttachmentPayload(this._request, data);
	}
};
/**
* A fetchable AttachmentLinkJiraIssue Mutation
*
* @param request - function to call the graphql client
*/
var AttachmentLinkJiraIssueMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the AttachmentLinkJiraIssue mutation and return a AttachmentPayload
	*
	* @param issueId - required issueId to pass to attachmentLinkJiraIssue
	* @param jiraIssueId - required jiraIssueId to pass to attachmentLinkJiraIssue
	* @param variables - variables without 'issueId', 'jiraIssueId' to pass into the AttachmentLinkJiraIssueMutation
	* @returns parsed response from AttachmentLinkJiraIssueMutation
	*/
	async fetch(issueId, jiraIssueId, variables) {
		const data = (await this._request(AttachmentLinkJiraIssueDocument.toString(), {
			issueId,
			jiraIssueId,
			...variables
		})).attachmentLinkJiraIssue;
		return new AttachmentPayload(this._request, data);
	}
};
/**
* A fetchable AttachmentLinkSalesforce Mutation
*
* @param request - function to call the graphql client
*/
var AttachmentLinkSalesforceMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the AttachmentLinkSalesforce mutation and return a AttachmentPayload
	*
	* @param issueId - required issueId to pass to attachmentLinkSalesforce
	* @param url - required url to pass to attachmentLinkSalesforce
	* @param variables - variables without 'issueId', 'url' to pass into the AttachmentLinkSalesforceMutation
	* @returns parsed response from AttachmentLinkSalesforceMutation
	*/
	async fetch(issueId, url, variables) {
		const data = (await this._request(AttachmentLinkSalesforceDocument.toString(), {
			issueId,
			url,
			...variables
		})).attachmentLinkSalesforce;
		return new AttachmentPayload(this._request, data);
	}
};
/**
* A fetchable AttachmentLinkSlack Mutation
*
* @param request - function to call the graphql client
*/
var AttachmentLinkSlackMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the AttachmentLinkSlack mutation and return a AttachmentPayload
	*
	* @param issueId - required issueId to pass to attachmentLinkSlack
	* @param url - required url to pass to attachmentLinkSlack
	* @param variables - variables without 'issueId', 'url' to pass into the AttachmentLinkSlackMutation
	* @returns parsed response from AttachmentLinkSlackMutation
	*/
	async fetch(issueId, url, variables) {
		const data = (await this._request(AttachmentLinkSlackDocument.toString(), {
			issueId,
			url,
			...variables
		})).attachmentLinkSlack;
		return new AttachmentPayload(this._request, data);
	}
};
/**
* A fetchable AttachmentLinkUrl Mutation
*
* @param request - function to call the graphql client
*/
var AttachmentLinkUrlMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the AttachmentLinkUrl mutation and return a AttachmentPayload
	*
	* @param issueId - required issueId to pass to attachmentLinkURL
	* @param url - required url to pass to attachmentLinkURL
	* @param variables - variables without 'issueId', 'url' to pass into the AttachmentLinkUrlMutation
	* @returns parsed response from AttachmentLinkUrlMutation
	*/
	async fetch(issueId, url, variables) {
		const data = (await this._request(AttachmentLinkUrlDocument.toString(), {
			issueId,
			url,
			...variables
		})).attachmentLinkURL;
		return new AttachmentPayload(this._request, data);
	}
};
/**
* A fetchable AttachmentLinkZendesk Mutation
*
* @param request - function to call the graphql client
*/
var AttachmentLinkZendeskMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the AttachmentLinkZendesk mutation and return a AttachmentPayload
	*
	* @param issueId - required issueId to pass to attachmentLinkZendesk
	* @param ticketId - required ticketId to pass to attachmentLinkZendesk
	* @param variables - variables without 'issueId', 'ticketId' to pass into the AttachmentLinkZendeskMutation
	* @returns parsed response from AttachmentLinkZendeskMutation
	*/
	async fetch(issueId, ticketId, variables) {
		const data = (await this._request(AttachmentLinkZendeskDocument.toString(), {
			issueId,
			ticketId,
			...variables
		})).attachmentLinkZendesk;
		return new AttachmentPayload(this._request, data);
	}
};
/**
* A fetchable AttachmentSyncToSlack Mutation
*
* @param request - function to call the graphql client
*/
var AttachmentSyncToSlackMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the AttachmentSyncToSlack mutation and return a AttachmentPayload
	*
	* @param id - required id to pass to attachmentSyncToSlack
	* @returns parsed response from AttachmentSyncToSlackMutation
	*/
	async fetch(id) {
		const data = (await this._request(AttachmentSyncToSlackDocument.toString(), { id })).attachmentSyncToSlack;
		return new AttachmentPayload(this._request, data);
	}
};
/**
* A fetchable UpdateAttachment Mutation
*
* @param request - function to call the graphql client
*/
var UpdateAttachmentMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the UpdateAttachment mutation and return a AttachmentPayload
	*
	* @param id - required id to pass to updateAttachment
	* @param input - required input to pass to updateAttachment
	* @returns parsed response from UpdateAttachmentMutation
	*/
	async fetch(id, input) {
		const data = (await this._request(UpdateAttachmentDocument.toString(), {
			id,
			input
		})).attachmentUpdate;
		return new AttachmentPayload(this._request, data);
	}
};
/**
* A fetchable CreateComment Mutation
*
* @param request - function to call the graphql client
*/
var CreateCommentMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the CreateComment mutation and return a CommentPayload
	*
	* @param input - required input to pass to createComment
	* @returns parsed response from CreateCommentMutation
	*/
	async fetch(input) {
		const data = (await this._request(CreateCommentDocument.toString(), { input })).commentCreate;
		return new CommentPayload(this._request, data);
	}
};
/**
* A fetchable DeleteComment Mutation
*
* @param request - function to call the graphql client
*/
var DeleteCommentMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the DeleteComment mutation and return a DeletePayload
	*
	* @param id - required id to pass to deleteComment
	* @returns parsed response from DeleteCommentMutation
	*/
	async fetch(id) {
		const data = (await this._request(DeleteCommentDocument.toString(), { id })).commentDelete;
		return new DeletePayload(this._request, data);
	}
};
/**
* A fetchable CommentResolve Mutation
*
* @param request - function to call the graphql client
*/
var CommentResolveMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the CommentResolve mutation and return a CommentPayload
	*
	* @param id - required id to pass to commentResolve
	* @param variables - variables without 'id' to pass into the CommentResolveMutation
	* @returns parsed response from CommentResolveMutation
	*/
	async fetch(id, variables) {
		const data = (await this._request(CommentResolveDocument.toString(), {
			id,
			...variables
		})).commentResolve;
		return new CommentPayload(this._request, data);
	}
};
/**
* A fetchable CommentUnresolve Mutation
*
* @param request - function to call the graphql client
*/
var CommentUnresolveMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the CommentUnresolve mutation and return a CommentPayload
	*
	* @param id - required id to pass to commentUnresolve
	* @returns parsed response from CommentUnresolveMutation
	*/
	async fetch(id) {
		const data = (await this._request(CommentUnresolveDocument.toString(), { id })).commentUnresolve;
		return new CommentPayload(this._request, data);
	}
};
/**
* A fetchable UpdateComment Mutation
*
* @param request - function to call the graphql client
*/
var UpdateCommentMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the UpdateComment mutation and return a CommentPayload
	*
	* @param id - required id to pass to updateComment
	* @param input - required input to pass to updateComment
	* @param variables - variables without 'id', 'input' to pass into the UpdateCommentMutation
	* @returns parsed response from UpdateCommentMutation
	*/
	async fetch(id, input, variables) {
		const data = (await this._request(UpdateCommentDocument.toString(), {
			id,
			input,
			...variables
		})).commentUpdate;
		return new CommentPayload(this._request, data);
	}
};
/**
* A fetchable CreateContact Mutation
*
* @param request - function to call the graphql client
*/
var CreateContactMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the CreateContact mutation and return a ContactPayload
	*
	* @param input - required input to pass to createContact
	* @returns parsed response from CreateContactMutation
	*/
	async fetch(input) {
		const data = (await this._request(CreateContactDocument.toString(), { input })).contactCreate;
		return new ContactPayload(this._request, data);
	}
};
/**
* A fetchable CreateCsvExportReport Mutation
*
* @param request - function to call the graphql client
*/
var CreateCsvExportReportMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the CreateCsvExportReport mutation and return a CreateCsvExportReportPayload
	*
	* @param variables - variables to pass into the CreateCsvExportReportMutation
	* @returns parsed response from CreateCsvExportReportMutation
	*/
	async fetch(variables) {
		const data = (await this._request(CreateCsvExportReportDocument.toString(), variables)).createCsvExportReport;
		return new CreateCsvExportReportPayload(this._request, data);
	}
};
/**
* A fetchable CreateInitiativeUpdateReminder Mutation
*
* @param request - function to call the graphql client
*/
var CreateInitiativeUpdateReminderMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the CreateInitiativeUpdateReminder mutation and return a InitiativeUpdateReminderPayload
	*
	* @param initiativeId - required initiativeId to pass to createInitiativeUpdateReminder
	* @param variables - variables without 'initiativeId' to pass into the CreateInitiativeUpdateReminderMutation
	* @returns parsed response from CreateInitiativeUpdateReminderMutation
	*/
	async fetch(initiativeId, variables) {
		const data = (await this._request(CreateInitiativeUpdateReminderDocument.toString(), {
			initiativeId,
			...variables
		})).createInitiativeUpdateReminder;
		return new InitiativeUpdateReminderPayload(this._request, data);
	}
};
/**
* A fetchable CreateOrganizationFromOnboarding Mutation
*
* @param request - function to call the graphql client
*/
var CreateOrganizationFromOnboardingMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the CreateOrganizationFromOnboarding mutation and return a CreateOrJoinOrganizationResponse
	*
	* @param input - required input to pass to createOrganizationFromOnboarding
	* @param variables - variables without 'input' to pass into the CreateOrganizationFromOnboardingMutation
	* @returns parsed response from CreateOrganizationFromOnboardingMutation
	*/
	async fetch(input, variables) {
		const data = (await this._request(CreateOrganizationFromOnboardingDocument.toString(), {
			input,
			...variables
		})).createOrganizationFromOnboarding;
		return new CreateOrJoinOrganizationResponse(this._request, data);
	}
};
/**
* A fetchable CreateProjectUpdateReminder Mutation
*
* @param request - function to call the graphql client
*/
var CreateProjectUpdateReminderMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the CreateProjectUpdateReminder mutation and return a ProjectUpdateReminderPayload
	*
	* @param projectId - required projectId to pass to createProjectUpdateReminder
	* @param variables - variables without 'projectId' to pass into the CreateProjectUpdateReminderMutation
	* @returns parsed response from CreateProjectUpdateReminderMutation
	*/
	async fetch(projectId, variables) {
		const data = (await this._request(CreateProjectUpdateReminderDocument.toString(), {
			projectId,
			...variables
		})).createProjectUpdateReminder;
		return new ProjectUpdateReminderPayload(this._request, data);
	}
};
/**
* A fetchable CreateCustomView Mutation
*
* @param request - function to call the graphql client
*/
var CreateCustomViewMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the CreateCustomView mutation and return a CustomViewPayload
	*
	* @param input - required input to pass to createCustomView
	* @returns parsed response from CreateCustomViewMutation
	*/
	async fetch(input) {
		const data = (await this._request(CreateCustomViewDocument.toString(), { input })).customViewCreate;
		return new CustomViewPayload(this._request, data);
	}
};
/**
* A fetchable DeleteCustomView Mutation
*
* @param request - function to call the graphql client
*/
var DeleteCustomViewMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the DeleteCustomView mutation and return a DeletePayload
	*
	* @param id - required id to pass to deleteCustomView
	* @returns parsed response from DeleteCustomViewMutation
	*/
	async fetch(id) {
		const data = (await this._request(DeleteCustomViewDocument.toString(), { id })).customViewDelete;
		return new DeletePayload(this._request, data);
	}
};
/**
* A fetchable UpdateCustomView Mutation
*
* @param request - function to call the graphql client
*/
var UpdateCustomViewMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the UpdateCustomView mutation and return a CustomViewPayload
	*
	* @param id - required id to pass to updateCustomView
	* @param input - required input to pass to updateCustomView
	* @returns parsed response from UpdateCustomViewMutation
	*/
	async fetch(id, input) {
		const data = (await this._request(UpdateCustomViewDocument.toString(), {
			id,
			input
		})).customViewUpdate;
		return new CustomViewPayload(this._request, data);
	}
};
/**
* A fetchable CreateCustomer Mutation
*
* @param request - function to call the graphql client
*/
var CreateCustomerMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the CreateCustomer mutation and return a CustomerPayload
	*
	* @param input - required input to pass to createCustomer
	* @returns parsed response from CreateCustomerMutation
	*/
	async fetch(input) {
		const data = (await this._request(CreateCustomerDocument.toString(), { input })).customerCreate;
		return new CustomerPayload(this._request, data);
	}
};
/**
* A fetchable DeleteCustomer Mutation
*
* @param request - function to call the graphql client
*/
var DeleteCustomerMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the DeleteCustomer mutation and return a DeletePayload
	*
	* @param id - required id to pass to deleteCustomer
	* @returns parsed response from DeleteCustomerMutation
	*/
	async fetch(id) {
		const data = (await this._request(DeleteCustomerDocument.toString(), { id })).customerDelete;
		return new DeletePayload(this._request, data);
	}
};
/**
* A fetchable CustomerMerge Mutation
*
* @param request - function to call the graphql client
*/
var CustomerMergeMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the CustomerMerge mutation and return a CustomerPayload
	*
	* @param sourceCustomerId - required sourceCustomerId to pass to customerMerge
	* @param targetCustomerId - required targetCustomerId to pass to customerMerge
	* @returns parsed response from CustomerMergeMutation
	*/
	async fetch(sourceCustomerId, targetCustomerId) {
		const data = (await this._request(CustomerMergeDocument.toString(), {
			sourceCustomerId,
			targetCustomerId
		})).customerMerge;
		return new CustomerPayload(this._request, data);
	}
};
/**
* A fetchable ArchiveCustomerNeed Mutation
*
* @param request - function to call the graphql client
*/
var ArchiveCustomerNeedMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the ArchiveCustomerNeed mutation and return a CustomerNeedArchivePayload
	*
	* @param id - required id to pass to archiveCustomerNeed
	* @returns parsed response from ArchiveCustomerNeedMutation
	*/
	async fetch(id) {
		const data = (await this._request(ArchiveCustomerNeedDocument.toString(), { id })).customerNeedArchive;
		return new CustomerNeedArchivePayload(this._request, data);
	}
};
/**
* A fetchable CreateCustomerNeed Mutation
*
* @param request - function to call the graphql client
*/
var CreateCustomerNeedMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the CreateCustomerNeed mutation and return a CustomerNeedPayload
	*
	* @param input - required input to pass to createCustomerNeed
	* @returns parsed response from CreateCustomerNeedMutation
	*/
	async fetch(input) {
		const data = (await this._request(CreateCustomerNeedDocument.toString(), { input })).customerNeedCreate;
		return new CustomerNeedPayload(this._request, data);
	}
};
/**
* A fetchable CustomerNeedCreateFromAttachment Mutation
*
* @param request - function to call the graphql client
*/
var CustomerNeedCreateFromAttachmentMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the CustomerNeedCreateFromAttachment mutation and return a CustomerNeedPayload
	*
	* @param input - required input to pass to customerNeedCreateFromAttachment
	* @returns parsed response from CustomerNeedCreateFromAttachmentMutation
	*/
	async fetch(input) {
		const data = (await this._request(CustomerNeedCreateFromAttachmentDocument.toString(), { input })).customerNeedCreateFromAttachment;
		return new CustomerNeedPayload(this._request, data);
	}
};
/**
* A fetchable DeleteCustomerNeed Mutation
*
* @param request - function to call the graphql client
*/
var DeleteCustomerNeedMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the DeleteCustomerNeed mutation and return a DeletePayload
	*
	* @param id - required id to pass to deleteCustomerNeed
	* @param variables - variables without 'id' to pass into the DeleteCustomerNeedMutation
	* @returns parsed response from DeleteCustomerNeedMutation
	*/
	async fetch(id, variables) {
		const data = (await this._request(DeleteCustomerNeedDocument.toString(), {
			id,
			...variables
		})).customerNeedDelete;
		return new DeletePayload(this._request, data);
	}
};
/**
* A fetchable UnarchiveCustomerNeed Mutation
*
* @param request - function to call the graphql client
*/
var UnarchiveCustomerNeedMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the UnarchiveCustomerNeed mutation and return a CustomerNeedArchivePayload
	*
	* @param id - required id to pass to unarchiveCustomerNeed
	* @returns parsed response from UnarchiveCustomerNeedMutation
	*/
	async fetch(id) {
		const data = (await this._request(UnarchiveCustomerNeedDocument.toString(), { id })).customerNeedUnarchive;
		return new CustomerNeedArchivePayload(this._request, data);
	}
};
/**
* A fetchable UpdateCustomerNeed Mutation
*
* @param request - function to call the graphql client
*/
var UpdateCustomerNeedMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the UpdateCustomerNeed mutation and return a CustomerNeedUpdatePayload
	*
	* @param id - required id to pass to updateCustomerNeed
	* @param input - required input to pass to updateCustomerNeed
	* @returns parsed response from UpdateCustomerNeedMutation
	*/
	async fetch(id, input) {
		const data = (await this._request(UpdateCustomerNeedDocument.toString(), {
			id,
			input
		})).customerNeedUpdate;
		return new CustomerNeedUpdatePayload(this._request, data);
	}
};
/**
* A fetchable CreateCustomerStatus Mutation
*
* @param request - function to call the graphql client
*/
var CreateCustomerStatusMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the CreateCustomerStatus mutation and return a CustomerStatusPayload
	*
	* @param input - required input to pass to createCustomerStatus
	* @returns parsed response from CreateCustomerStatusMutation
	*/
	async fetch(input) {
		const data = (await this._request(CreateCustomerStatusDocument.toString(), { input })).customerStatusCreate;
		return new CustomerStatusPayload(this._request, data);
	}
};
/**
* A fetchable DeleteCustomerStatus Mutation
*
* @param request - function to call the graphql client
*/
var DeleteCustomerStatusMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the DeleteCustomerStatus mutation and return a DeletePayload
	*
	* @param id - required id to pass to deleteCustomerStatus
	* @returns parsed response from DeleteCustomerStatusMutation
	*/
	async fetch(id) {
		const data = (await this._request(DeleteCustomerStatusDocument.toString(), { id })).customerStatusDelete;
		return new DeletePayload(this._request, data);
	}
};
/**
* A fetchable UpdateCustomerStatus Mutation
*
* @param request - function to call the graphql client
*/
var UpdateCustomerStatusMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the UpdateCustomerStatus mutation and return a CustomerStatusPayload
	*
	* @param id - required id to pass to updateCustomerStatus
	* @param input - required input to pass to updateCustomerStatus
	* @returns parsed response from UpdateCustomerStatusMutation
	*/
	async fetch(id, input) {
		const data = (await this._request(UpdateCustomerStatusDocument.toString(), {
			id,
			input
		})).customerStatusUpdate;
		return new CustomerStatusPayload(this._request, data);
	}
};
/**
* A fetchable CreateCustomerTier Mutation
*
* @param request - function to call the graphql client
*/
var CreateCustomerTierMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the CreateCustomerTier mutation and return a CustomerTierPayload
	*
	* @param input - required input to pass to createCustomerTier
	* @returns parsed response from CreateCustomerTierMutation
	*/
	async fetch(input) {
		const data = (await this._request(CreateCustomerTierDocument.toString(), { input })).customerTierCreate;
		return new CustomerTierPayload(this._request, data);
	}
};
/**
* A fetchable DeleteCustomerTier Mutation
*
* @param request - function to call the graphql client
*/
var DeleteCustomerTierMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the DeleteCustomerTier mutation and return a DeletePayload
	*
	* @param id - required id to pass to deleteCustomerTier
	* @returns parsed response from DeleteCustomerTierMutation
	*/
	async fetch(id) {
		const data = (await this._request(DeleteCustomerTierDocument.toString(), { id })).customerTierDelete;
		return new DeletePayload(this._request, data);
	}
};
/**
* A fetchable UpdateCustomerTier Mutation
*
* @param request - function to call the graphql client
*/
var UpdateCustomerTierMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the UpdateCustomerTier mutation and return a CustomerTierPayload
	*
	* @param id - required id to pass to updateCustomerTier
	* @param input - required input to pass to updateCustomerTier
	* @returns parsed response from UpdateCustomerTierMutation
	*/
	async fetch(id, input) {
		const data = (await this._request(UpdateCustomerTierDocument.toString(), {
			id,
			input
		})).customerTierUpdate;
		return new CustomerTierPayload(this._request, data);
	}
};
/**
* A fetchable CustomerUnsync Mutation
*
* @param request - function to call the graphql client
*/
var CustomerUnsyncMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the CustomerUnsync mutation and return a CustomerPayload
	*
	* @param id - required id to pass to customerUnsync
	* @returns parsed response from CustomerUnsyncMutation
	*/
	async fetch(id) {
		const data = (await this._request(CustomerUnsyncDocument.toString(), { id })).customerUnsync;
		return new CustomerPayload(this._request, data);
	}
};
/**
* A fetchable UpdateCustomer Mutation
*
* @param request - function to call the graphql client
*/
var UpdateCustomerMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the UpdateCustomer mutation and return a CustomerPayload
	*
	* @param id - required id to pass to updateCustomer
	* @param input - required input to pass to updateCustomer
	* @returns parsed response from UpdateCustomerMutation
	*/
	async fetch(id, input) {
		const data = (await this._request(UpdateCustomerDocument.toString(), {
			id,
			input
		})).customerUpdate;
		return new CustomerPayload(this._request, data);
	}
};
/**
* A fetchable CustomerUpsert Mutation
*
* @param request - function to call the graphql client
*/
var CustomerUpsertMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the CustomerUpsert mutation and return a CustomerPayload
	*
	* @param input - required input to pass to customerUpsert
	* @returns parsed response from CustomerUpsertMutation
	*/
	async fetch(input) {
		const data = (await this._request(CustomerUpsertDocument.toString(), { input })).customerUpsert;
		return new CustomerPayload(this._request, data);
	}
};
/**
* A fetchable ArchiveCycle Mutation
*
* @param request - function to call the graphql client
*/
var ArchiveCycleMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the ArchiveCycle mutation and return a CycleArchivePayload
	*
	* @param id - required id to pass to archiveCycle
	* @returns parsed response from ArchiveCycleMutation
	*/
	async fetch(id) {
		const data = (await this._request(ArchiveCycleDocument.toString(), { id })).cycleArchive;
		return new CycleArchivePayload(this._request, data);
	}
};
/**
* A fetchable CreateCycle Mutation
*
* @param request - function to call the graphql client
*/
var CreateCycleMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the CreateCycle mutation and return a CyclePayload
	*
	* @param input - required input to pass to createCycle
	* @returns parsed response from CreateCycleMutation
	*/
	async fetch(input) {
		const data = (await this._request(CreateCycleDocument.toString(), { input })).cycleCreate;
		return new CyclePayload(this._request, data);
	}
};
/**
* A fetchable CycleShiftAll Mutation
*
* @param request - function to call the graphql client
*/
var CycleShiftAllMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the CycleShiftAll mutation and return a CyclePayload
	*
	* @param input - required input to pass to cycleShiftAll
	* @returns parsed response from CycleShiftAllMutation
	*/
	async fetch(input) {
		const data = (await this._request(CycleShiftAllDocument.toString(), { input })).cycleShiftAll;
		return new CyclePayload(this._request, data);
	}
};
/**
* A fetchable CycleStartUpcomingCycleToday Mutation
*
* @param request - function to call the graphql client
*/
var CycleStartUpcomingCycleTodayMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the CycleStartUpcomingCycleToday mutation and return a CyclePayload
	*
	* @param id - required id to pass to cycleStartUpcomingCycleToday
	* @returns parsed response from CycleStartUpcomingCycleTodayMutation
	*/
	async fetch(id) {
		const data = (await this._request(CycleStartUpcomingCycleTodayDocument.toString(), { id })).cycleStartUpcomingCycleToday;
		return new CyclePayload(this._request, data);
	}
};
/**
* A fetchable UpdateCycle Mutation
*
* @param request - function to call the graphql client
*/
var UpdateCycleMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the UpdateCycle mutation and return a CyclePayload
	*
	* @param id - required id to pass to updateCycle
	* @param input - required input to pass to updateCycle
	* @returns parsed response from UpdateCycleMutation
	*/
	async fetch(id, input) {
		const data = (await this._request(UpdateCycleDocument.toString(), {
			id,
			input
		})).cycleUpdate;
		return new CyclePayload(this._request, data);
	}
};
/**
* A fetchable CreateDocument Mutation
*
* @param request - function to call the graphql client
*/
var CreateDocumentMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the CreateDocument mutation and return a DocumentPayload
	*
	* @param input - required input to pass to createDocument
	* @returns parsed response from CreateDocumentMutation
	*/
	async fetch(input) {
		const data = (await this._request(CreateDocumentDocument.toString(), { input })).documentCreate;
		return new DocumentPayload(this._request, data);
	}
};
/**
* A fetchable DeleteDocument Mutation
*
* @param request - function to call the graphql client
*/
var DeleteDocumentMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the DeleteDocument mutation and return a DocumentArchivePayload
	*
	* @param id - required id to pass to deleteDocument
	* @returns parsed response from DeleteDocumentMutation
	*/
	async fetch(id) {
		const data = (await this._request(DeleteDocumentDocument.toString(), { id })).documentDelete;
		return new DocumentArchivePayload(this._request, data);
	}
};
/**
* A fetchable UnarchiveDocument Mutation
*
* @param request - function to call the graphql client
*/
var UnarchiveDocumentMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the UnarchiveDocument mutation and return a DocumentArchivePayload
	*
	* @param id - required id to pass to unarchiveDocument
	* @returns parsed response from UnarchiveDocumentMutation
	*/
	async fetch(id) {
		const data = (await this._request(UnarchiveDocumentDocument.toString(), { id })).documentUnarchive;
		return new DocumentArchivePayload(this._request, data);
	}
};
/**
* A fetchable UpdateDocument Mutation
*
* @param request - function to call the graphql client
*/
var UpdateDocumentMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the UpdateDocument mutation and return a DocumentPayload
	*
	* @param id - required id to pass to updateDocument
	* @param input - required input to pass to updateDocument
	* @returns parsed response from UpdateDocumentMutation
	*/
	async fetch(id, input) {
		const data = (await this._request(UpdateDocumentDocument.toString(), {
			id,
			input
		})).documentUpdate;
		return new DocumentPayload(this._request, data);
	}
};
/**
* A fetchable CreateEmailIntakeAddress Mutation
*
* @param request - function to call the graphql client
*/
var CreateEmailIntakeAddressMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the CreateEmailIntakeAddress mutation and return a EmailIntakeAddressPayload
	*
	* @param input - required input to pass to createEmailIntakeAddress
	* @returns parsed response from CreateEmailIntakeAddressMutation
	*/
	async fetch(input) {
		const data = (await this._request(CreateEmailIntakeAddressDocument.toString(), { input })).emailIntakeAddressCreate;
		return new EmailIntakeAddressPayload(this._request, data);
	}
};
/**
* A fetchable DeleteEmailIntakeAddress Mutation
*
* @param request - function to call the graphql client
*/
var DeleteEmailIntakeAddressMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the DeleteEmailIntakeAddress mutation and return a DeletePayload
	*
	* @param id - required id to pass to deleteEmailIntakeAddress
	* @returns parsed response from DeleteEmailIntakeAddressMutation
	*/
	async fetch(id) {
		const data = (await this._request(DeleteEmailIntakeAddressDocument.toString(), { id })).emailIntakeAddressDelete;
		return new DeletePayload(this._request, data);
	}
};
/**
* A fetchable EmailIntakeAddressRotate Mutation
*
* @param request - function to call the graphql client
*/
var EmailIntakeAddressRotateMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the EmailIntakeAddressRotate mutation and return a EmailIntakeAddressPayload
	*
	* @param id - required id to pass to emailIntakeAddressRotate
	* @returns parsed response from EmailIntakeAddressRotateMutation
	*/
	async fetch(id) {
		const data = (await this._request(EmailIntakeAddressRotateDocument.toString(), { id })).emailIntakeAddressRotate;
		return new EmailIntakeAddressPayload(this._request, data);
	}
};
/**
* A fetchable UpdateEmailIntakeAddress Mutation
*
* @param request - function to call the graphql client
*/
var UpdateEmailIntakeAddressMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the UpdateEmailIntakeAddress mutation and return a EmailIntakeAddressPayload
	*
	* @param id - required id to pass to updateEmailIntakeAddress
	* @param input - required input to pass to updateEmailIntakeAddress
	* @returns parsed response from UpdateEmailIntakeAddressMutation
	*/
	async fetch(id, input) {
		const data = (await this._request(UpdateEmailIntakeAddressDocument.toString(), {
			id,
			input
		})).emailIntakeAddressUpdate;
		return new EmailIntakeAddressPayload(this._request, data);
	}
};
/**
* A fetchable EmailTokenUserAccountAuth Mutation
*
* @param request - function to call the graphql client
*/
var EmailTokenUserAccountAuthMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the EmailTokenUserAccountAuth mutation and return a AuthResolverResponse
	*
	* @param input - required input to pass to emailTokenUserAccountAuth
	* @returns parsed response from EmailTokenUserAccountAuthMutation
	*/
	async fetch(input) {
		const data = (await this._request(EmailTokenUserAccountAuthDocument.toString(), { input })).emailTokenUserAccountAuth;
		return new AuthResolverResponse(this._request, data);
	}
};
/**
* A fetchable EmailUnsubscribe Mutation
*
* @param request - function to call the graphql client
*/
var EmailUnsubscribeMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the EmailUnsubscribe mutation and return a EmailUnsubscribePayload
	*
	* @param input - required input to pass to emailUnsubscribe
	* @returns parsed response from EmailUnsubscribeMutation
	*/
	async fetch(input) {
		const data = (await this._request(EmailUnsubscribeDocument.toString(), { input })).emailUnsubscribe;
		return new EmailUnsubscribePayload(this._request, data);
	}
};
/**
* A fetchable EmailUserAccountAuthChallenge Mutation
*
* @param request - function to call the graphql client
*/
var EmailUserAccountAuthChallengeMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the EmailUserAccountAuthChallenge mutation and return a EmailUserAccountAuthChallengeResponse
	*
	* @param input - required input to pass to emailUserAccountAuthChallenge
	* @returns parsed response from EmailUserAccountAuthChallengeMutation
	*/
	async fetch(input) {
		const data = (await this._request(EmailUserAccountAuthChallengeDocument.toString(), { input })).emailUserAccountAuthChallenge;
		return new EmailUserAccountAuthChallengeResponse(this._request, data);
	}
};
/**
* A fetchable CreateEmoji Mutation
*
* @param request - function to call the graphql client
*/
var CreateEmojiMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the CreateEmoji mutation and return a EmojiPayload
	*
	* @param input - required input to pass to createEmoji
	* @returns parsed response from CreateEmojiMutation
	*/
	async fetch(input) {
		const data = (await this._request(CreateEmojiDocument.toString(), { input })).emojiCreate;
		return new EmojiPayload(this._request, data);
	}
};
/**
* A fetchable DeleteEmoji Mutation
*
* @param request - function to call the graphql client
*/
var DeleteEmojiMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the DeleteEmoji mutation and return a DeletePayload
	*
	* @param id - required id to pass to deleteEmoji
	* @returns parsed response from DeleteEmojiMutation
	*/
	async fetch(id) {
		const data = (await this._request(DeleteEmojiDocument.toString(), { id })).emojiDelete;
		return new DeletePayload(this._request, data);
	}
};
/**
* A fetchable CreateEntityExternalLink Mutation
*
* @param request - function to call the graphql client
*/
var CreateEntityExternalLinkMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the CreateEntityExternalLink mutation and return a EntityExternalLinkPayload
	*
	* @param input - required input to pass to createEntityExternalLink
	* @returns parsed response from CreateEntityExternalLinkMutation
	*/
	async fetch(input) {
		const data = (await this._request(CreateEntityExternalLinkDocument.toString(), { input })).entityExternalLinkCreate;
		return new EntityExternalLinkPayload(this._request, data);
	}
};
/**
* A fetchable DeleteEntityExternalLink Mutation
*
* @param request - function to call the graphql client
*/
var DeleteEntityExternalLinkMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the DeleteEntityExternalLink mutation and return a DeletePayload
	*
	* @param id - required id to pass to deleteEntityExternalLink
	* @returns parsed response from DeleteEntityExternalLinkMutation
	*/
	async fetch(id) {
		const data = (await this._request(DeleteEntityExternalLinkDocument.toString(), { id })).entityExternalLinkDelete;
		return new DeletePayload(this._request, data);
	}
};
/**
* A fetchable UpdateEntityExternalLink Mutation
*
* @param request - function to call the graphql client
*/
var UpdateEntityExternalLinkMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the UpdateEntityExternalLink mutation and return a EntityExternalLinkPayload
	*
	* @param id - required id to pass to updateEntityExternalLink
	* @param input - required input to pass to updateEntityExternalLink
	* @returns parsed response from UpdateEntityExternalLinkMutation
	*/
	async fetch(id, input) {
		const data = (await this._request(UpdateEntityExternalLinkDocument.toString(), {
			id,
			input
		})).entityExternalLinkUpdate;
		return new EntityExternalLinkPayload(this._request, data);
	}
};
/**
* A fetchable CreateFavorite Mutation
*
* @param request - function to call the graphql client
*/
var CreateFavoriteMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the CreateFavorite mutation and return a FavoritePayload
	*
	* @param input - required input to pass to createFavorite
	* @returns parsed response from CreateFavoriteMutation
	*/
	async fetch(input) {
		const data = (await this._request(CreateFavoriteDocument.toString(), { input })).favoriteCreate;
		return new FavoritePayload(this._request, data);
	}
};
/**
* A fetchable DeleteFavorite Mutation
*
* @param request - function to call the graphql client
*/
var DeleteFavoriteMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the DeleteFavorite mutation and return a DeletePayload
	*
	* @param id - required id to pass to deleteFavorite
	* @returns parsed response from DeleteFavoriteMutation
	*/
	async fetch(id) {
		const data = (await this._request(DeleteFavoriteDocument.toString(), { id })).favoriteDelete;
		return new DeletePayload(this._request, data);
	}
};
/**
* A fetchable UpdateFavorite Mutation
*
* @param request - function to call the graphql client
*/
var UpdateFavoriteMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the UpdateFavorite mutation and return a FavoritePayload
	*
	* @param id - required id to pass to updateFavorite
	* @param input - required input to pass to updateFavorite
	* @returns parsed response from UpdateFavoriteMutation
	*/
	async fetch(id, input) {
		const data = (await this._request(UpdateFavoriteDocument.toString(), {
			id,
			input
		})).favoriteUpdate;
		return new FavoritePayload(this._request, data);
	}
};
/**
* A fetchable FileUpload Mutation
*
* @param request - function to call the graphql client
*/
var FileUploadMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the FileUpload mutation and return a UploadPayload
	*
	* @param contentType - required contentType to pass to fileUpload
	* @param filename - required filename to pass to fileUpload
	* @param size - required size to pass to fileUpload
	* @param variables - variables without 'contentType', 'filename', 'size' to pass into the FileUploadMutation
	* @returns parsed response from FileUploadMutation
	*/
	async fetch(contentType, filename, size, variables) {
		const data = (await this._request(FileUploadDocument.toString(), {
			contentType,
			filename,
			size,
			...variables
		})).fileUpload;
		return new UploadPayload(this._request, data);
	}
};
/**
* A fetchable CreateGitAutomationState Mutation
*
* @param request - function to call the graphql client
*/
var CreateGitAutomationStateMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the CreateGitAutomationState mutation and return a GitAutomationStatePayload
	*
	* @param input - required input to pass to createGitAutomationState
	* @returns parsed response from CreateGitAutomationStateMutation
	*/
	async fetch(input) {
		const data = (await this._request(CreateGitAutomationStateDocument.toString(), { input })).gitAutomationStateCreate;
		return new GitAutomationStatePayload(this._request, data);
	}
};
/**
* A fetchable DeleteGitAutomationState Mutation
*
* @param request - function to call the graphql client
*/
var DeleteGitAutomationStateMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the DeleteGitAutomationState mutation and return a DeletePayload
	*
	* @param id - required id to pass to deleteGitAutomationState
	* @returns parsed response from DeleteGitAutomationStateMutation
	*/
	async fetch(id) {
		const data = (await this._request(DeleteGitAutomationStateDocument.toString(), { id })).gitAutomationStateDelete;
		return new DeletePayload(this._request, data);
	}
};
/**
* A fetchable UpdateGitAutomationState Mutation
*
* @param request - function to call the graphql client
*/
var UpdateGitAutomationStateMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the UpdateGitAutomationState mutation and return a GitAutomationStatePayload
	*
	* @param id - required id to pass to updateGitAutomationState
	* @param input - required input to pass to updateGitAutomationState
	* @returns parsed response from UpdateGitAutomationStateMutation
	*/
	async fetch(id, input) {
		const data = (await this._request(UpdateGitAutomationStateDocument.toString(), {
			id,
			input
		})).gitAutomationStateUpdate;
		return new GitAutomationStatePayload(this._request, data);
	}
};
/**
* A fetchable CreateGitAutomationTargetBranch Mutation
*
* @param request - function to call the graphql client
*/
var CreateGitAutomationTargetBranchMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the CreateGitAutomationTargetBranch mutation and return a GitAutomationTargetBranchPayload
	*
	* @param input - required input to pass to createGitAutomationTargetBranch
	* @returns parsed response from CreateGitAutomationTargetBranchMutation
	*/
	async fetch(input) {
		const data = (await this._request(CreateGitAutomationTargetBranchDocument.toString(), { input })).gitAutomationTargetBranchCreate;
		return new GitAutomationTargetBranchPayload(this._request, data);
	}
};
/**
* A fetchable DeleteGitAutomationTargetBranch Mutation
*
* @param request - function to call the graphql client
*/
var DeleteGitAutomationTargetBranchMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the DeleteGitAutomationTargetBranch mutation and return a DeletePayload
	*
	* @param id - required id to pass to deleteGitAutomationTargetBranch
	* @returns parsed response from DeleteGitAutomationTargetBranchMutation
	*/
	async fetch(id) {
		const data = (await this._request(DeleteGitAutomationTargetBranchDocument.toString(), { id })).gitAutomationTargetBranchDelete;
		return new DeletePayload(this._request, data);
	}
};
/**
* A fetchable UpdateGitAutomationTargetBranch Mutation
*
* @param request - function to call the graphql client
*/
var UpdateGitAutomationTargetBranchMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the UpdateGitAutomationTargetBranch mutation and return a GitAutomationTargetBranchPayload
	*
	* @param id - required id to pass to updateGitAutomationTargetBranch
	* @param input - required input to pass to updateGitAutomationTargetBranch
	* @returns parsed response from UpdateGitAutomationTargetBranchMutation
	*/
	async fetch(id, input) {
		const data = (await this._request(UpdateGitAutomationTargetBranchDocument.toString(), {
			id,
			input
		})).gitAutomationTargetBranchUpdate;
		return new GitAutomationTargetBranchPayload(this._request, data);
	}
};
/**
* A fetchable GoogleUserAccountAuth Mutation
*
* @param request - function to call the graphql client
*/
var GoogleUserAccountAuthMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the GoogleUserAccountAuth mutation and return a AuthResolverResponse
	*
	* @param input - required input to pass to googleUserAccountAuth
	* @returns parsed response from GoogleUserAccountAuthMutation
	*/
	async fetch(input) {
		const data = (await this._request(GoogleUserAccountAuthDocument.toString(), { input })).googleUserAccountAuth;
		return new AuthResolverResponse(this._request, data);
	}
};
/**
* A fetchable ImageUploadFromUrl Mutation
*
* @param request - function to call the graphql client
*/
var ImageUploadFromUrlMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the ImageUploadFromUrl mutation and return a ImageUploadFromUrlPayload
	*
	* @param url - required url to pass to imageUploadFromUrl
	* @returns parsed response from ImageUploadFromUrlMutation
	*/
	async fetch(url) {
		const data = (await this._request(ImageUploadFromUrlDocument.toString(), { url })).imageUploadFromUrl;
		return new ImageUploadFromUrlPayload(this._request, data);
	}
};
/**
* A fetchable ImportFileUpload Mutation
*
* @param request - function to call the graphql client
*/
var ImportFileUploadMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the ImportFileUpload mutation and return a UploadPayload
	*
	* @param contentType - required contentType to pass to importFileUpload
	* @param filename - required filename to pass to importFileUpload
	* @param size - required size to pass to importFileUpload
	* @param variables - variables without 'contentType', 'filename', 'size' to pass into the ImportFileUploadMutation
	* @returns parsed response from ImportFileUploadMutation
	*/
	async fetch(contentType, filename, size, variables) {
		const data = (await this._request(ImportFileUploadDocument.toString(), {
			contentType,
			filename,
			size,
			...variables
		})).importFileUpload;
		return new UploadPayload(this._request, data);
	}
};
/**
* A fetchable ArchiveInitiative Mutation
*
* @param request - function to call the graphql client
*/
var ArchiveInitiativeMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the ArchiveInitiative mutation and return a InitiativeArchivePayload
	*
	* @param id - required id to pass to archiveInitiative
	* @returns parsed response from ArchiveInitiativeMutation
	*/
	async fetch(id) {
		const data = (await this._request(ArchiveInitiativeDocument.toString(), { id })).initiativeArchive;
		return new InitiativeArchivePayload(this._request, data);
	}
};
/**
* A fetchable CreateInitiative Mutation
*
* @param request - function to call the graphql client
*/
var CreateInitiativeMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the CreateInitiative mutation and return a InitiativePayload
	*
	* @param input - required input to pass to createInitiative
	* @returns parsed response from CreateInitiativeMutation
	*/
	async fetch(input) {
		const data = (await this._request(CreateInitiativeDocument.toString(), { input })).initiativeCreate;
		return new InitiativePayload(this._request, data);
	}
};
/**
* A fetchable DeleteInitiative Mutation
*
* @param request - function to call the graphql client
*/
var DeleteInitiativeMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the DeleteInitiative mutation and return a DeletePayload
	*
	* @param id - required id to pass to deleteInitiative
	* @returns parsed response from DeleteInitiativeMutation
	*/
	async fetch(id) {
		const data = (await this._request(DeleteInitiativeDocument.toString(), { id })).initiativeDelete;
		return new DeletePayload(this._request, data);
	}
};
/**
* A fetchable CreateInitiativeRelation Mutation
*
* @param request - function to call the graphql client
*/
var CreateInitiativeRelationMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the CreateInitiativeRelation mutation and return a InitiativeRelationPayload
	*
	* @param input - required input to pass to createInitiativeRelation
	* @returns parsed response from CreateInitiativeRelationMutation
	*/
	async fetch(input) {
		const data = (await this._request(CreateInitiativeRelationDocument.toString(), { input })).initiativeRelationCreate;
		return new InitiativeRelationPayload(this._request, data);
	}
};
/**
* A fetchable DeleteInitiativeRelation Mutation
*
* @param request - function to call the graphql client
*/
var DeleteInitiativeRelationMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the DeleteInitiativeRelation mutation and return a DeletePayload
	*
	* @param id - required id to pass to deleteInitiativeRelation
	* @returns parsed response from DeleteInitiativeRelationMutation
	*/
	async fetch(id) {
		const data = (await this._request(DeleteInitiativeRelationDocument.toString(), { id })).initiativeRelationDelete;
		return new DeletePayload(this._request, data);
	}
};
/**
* A fetchable UpdateInitiativeRelation Mutation
*
* @param request - function to call the graphql client
*/
var UpdateInitiativeRelationMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the UpdateInitiativeRelation mutation and return a DeletePayload
	*
	* @param id - required id to pass to updateInitiativeRelation
	* @param input - required input to pass to updateInitiativeRelation
	* @returns parsed response from UpdateInitiativeRelationMutation
	*/
	async fetch(id, input) {
		const data = (await this._request(UpdateInitiativeRelationDocument.toString(), {
			id,
			input
		})).initiativeRelationUpdate;
		return new DeletePayload(this._request, data);
	}
};
/**
* A fetchable CreateInitiativeToProject Mutation
*
* @param request - function to call the graphql client
*/
var CreateInitiativeToProjectMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the CreateInitiativeToProject mutation and return a InitiativeToProjectPayload
	*
	* @param input - required input to pass to createInitiativeToProject
	* @returns parsed response from CreateInitiativeToProjectMutation
	*/
	async fetch(input) {
		const data = (await this._request(CreateInitiativeToProjectDocument.toString(), { input })).initiativeToProjectCreate;
		return new InitiativeToProjectPayload(this._request, data);
	}
};
/**
* A fetchable DeleteInitiativeToProject Mutation
*
* @param request - function to call the graphql client
*/
var DeleteInitiativeToProjectMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the DeleteInitiativeToProject mutation and return a DeletePayload
	*
	* @param id - required id to pass to deleteInitiativeToProject
	* @returns parsed response from DeleteInitiativeToProjectMutation
	*/
	async fetch(id) {
		const data = (await this._request(DeleteInitiativeToProjectDocument.toString(), { id })).initiativeToProjectDelete;
		return new DeletePayload(this._request, data);
	}
};
/**
* A fetchable UpdateInitiativeToProject Mutation
*
* @param request - function to call the graphql client
*/
var UpdateInitiativeToProjectMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the UpdateInitiativeToProject mutation and return a InitiativeToProjectPayload
	*
	* @param id - required id to pass to updateInitiativeToProject
	* @param input - required input to pass to updateInitiativeToProject
	* @returns parsed response from UpdateInitiativeToProjectMutation
	*/
	async fetch(id, input) {
		const data = (await this._request(UpdateInitiativeToProjectDocument.toString(), {
			id,
			input
		})).initiativeToProjectUpdate;
		return new InitiativeToProjectPayload(this._request, data);
	}
};
/**
* A fetchable UnarchiveInitiative Mutation
*
* @param request - function to call the graphql client
*/
var UnarchiveInitiativeMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the UnarchiveInitiative mutation and return a InitiativeArchivePayload
	*
	* @param id - required id to pass to unarchiveInitiative
	* @returns parsed response from UnarchiveInitiativeMutation
	*/
	async fetch(id) {
		const data = (await this._request(UnarchiveInitiativeDocument.toString(), { id })).initiativeUnarchive;
		return new InitiativeArchivePayload(this._request, data);
	}
};
/**
* A fetchable UpdateInitiative Mutation
*
* @param request - function to call the graphql client
*/
var UpdateInitiativeMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the UpdateInitiative mutation and return a InitiativePayload
	*
	* @param id - required id to pass to updateInitiative
	* @param input - required input to pass to updateInitiative
	* @returns parsed response from UpdateInitiativeMutation
	*/
	async fetch(id, input) {
		const data = (await this._request(UpdateInitiativeDocument.toString(), {
			id,
			input
		})).initiativeUpdate;
		return new InitiativePayload(this._request, data);
	}
};
/**
* A fetchable ArchiveInitiativeUpdate Mutation
*
* @param request - function to call the graphql client
*/
var ArchiveInitiativeUpdateMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the ArchiveInitiativeUpdate mutation and return a InitiativeUpdateArchivePayload
	*
	* @param id - required id to pass to archiveInitiativeUpdate
	* @returns parsed response from ArchiveInitiativeUpdateMutation
	*/
	async fetch(id) {
		const data = (await this._request(ArchiveInitiativeUpdateDocument.toString(), { id })).initiativeUpdateArchive;
		return new InitiativeUpdateArchivePayload(this._request, data);
	}
};
/**
* A fetchable CreateInitiativeUpdate Mutation
*
* @param request - function to call the graphql client
*/
var CreateInitiativeUpdateMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the CreateInitiativeUpdate mutation and return a InitiativeUpdatePayload
	*
	* @param input - required input to pass to createInitiativeUpdate
	* @returns parsed response from CreateInitiativeUpdateMutation
	*/
	async fetch(input) {
		const data = (await this._request(CreateInitiativeUpdateDocument.toString(), { input })).initiativeUpdateCreate;
		return new InitiativeUpdatePayload(this._request, data);
	}
};
/**
* A fetchable UnarchiveInitiativeUpdate Mutation
*
* @param request - function to call the graphql client
*/
var UnarchiveInitiativeUpdateMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the UnarchiveInitiativeUpdate mutation and return a InitiativeUpdateArchivePayload
	*
	* @param id - required id to pass to unarchiveInitiativeUpdate
	* @returns parsed response from UnarchiveInitiativeUpdateMutation
	*/
	async fetch(id) {
		const data = (await this._request(UnarchiveInitiativeUpdateDocument.toString(), { id })).initiativeUpdateUnarchive;
		return new InitiativeUpdateArchivePayload(this._request, data);
	}
};
/**
* A fetchable UpdateInitiativeUpdate Mutation
*
* @param request - function to call the graphql client
*/
var UpdateInitiativeUpdateMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the UpdateInitiativeUpdate mutation and return a InitiativeUpdatePayload
	*
	* @param id - required id to pass to updateInitiativeUpdate
	* @param input - required input to pass to updateInitiativeUpdate
	* @returns parsed response from UpdateInitiativeUpdateMutation
	*/
	async fetch(id, input) {
		const data = (await this._request(UpdateInitiativeUpdateDocument.toString(), {
			id,
			input
		})).initiativeUpdateUpdate;
		return new InitiativeUpdatePayload(this._request, data);
	}
};
/**
* A fetchable ArchiveIntegration Mutation
*
* @param request - function to call the graphql client
*/
var ArchiveIntegrationMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the ArchiveIntegration mutation and return a DeletePayload
	*
	* @param id - required id to pass to archiveIntegration
	* @returns parsed response from ArchiveIntegrationMutation
	*/
	async fetch(id) {
		const data = (await this._request(ArchiveIntegrationDocument.toString(), { id })).integrationArchive;
		return new DeletePayload(this._request, data);
	}
};
/**
* A fetchable IntegrationAsksConnectChannel Mutation
*
* @param request - function to call the graphql client
*/
var IntegrationAsksConnectChannelMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the IntegrationAsksConnectChannel mutation and return a AsksChannelConnectPayload
	*
	* @param code - required code to pass to integrationAsksConnectChannel
	* @param redirectUri - required redirectUri to pass to integrationAsksConnectChannel
	* @returns parsed response from IntegrationAsksConnectChannelMutation
	*/
	async fetch(code, redirectUri) {
		const data = (await this._request(IntegrationAsksConnectChannelDocument.toString(), {
			code,
			redirectUri
		})).integrationAsksConnectChannel;
		return new AsksChannelConnectPayload(this._request, data);
	}
};
/**
* A fetchable DeleteIntegration Mutation
*
* @param request - function to call the graphql client
*/
var DeleteIntegrationMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the DeleteIntegration mutation and return a DeletePayload
	*
	* @param id - required id to pass to deleteIntegration
	* @param variables - variables without 'id' to pass into the DeleteIntegrationMutation
	* @returns parsed response from DeleteIntegrationMutation
	*/
	async fetch(id, variables) {
		const data = (await this._request(DeleteIntegrationDocument.toString(), {
			id,
			...variables
		})).integrationDelete;
		return new DeletePayload(this._request, data);
	}
};
/**
* A fetchable IntegrationDiscord Mutation
*
* @param request - function to call the graphql client
*/
var IntegrationDiscordMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the IntegrationDiscord mutation and return a IntegrationPayload
	*
	* @param code - required code to pass to integrationDiscord
	* @param redirectUri - required redirectUri to pass to integrationDiscord
	* @returns parsed response from IntegrationDiscordMutation
	*/
	async fetch(code, redirectUri) {
		const data = (await this._request(IntegrationDiscordDocument.toString(), {
			code,
			redirectUri
		})).integrationDiscord;
		return new IntegrationPayload(this._request, data);
	}
};
/**
* A fetchable IntegrationFigma Mutation
*
* @param request - function to call the graphql client
*/
var IntegrationFigmaMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the IntegrationFigma mutation and return a IntegrationPayload
	*
	* @param code - required code to pass to integrationFigma
	* @param redirectUri - required redirectUri to pass to integrationFigma
	* @returns parsed response from IntegrationFigmaMutation
	*/
	async fetch(code, redirectUri) {
		const data = (await this._request(IntegrationFigmaDocument.toString(), {
			code,
			redirectUri
		})).integrationFigma;
		return new IntegrationPayload(this._request, data);
	}
};
/**
* A fetchable IntegrationFront Mutation
*
* @param request - function to call the graphql client
*/
var IntegrationFrontMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the IntegrationFront mutation and return a IntegrationPayload
	*
	* @param code - required code to pass to integrationFront
	* @param redirectUri - required redirectUri to pass to integrationFront
	* @returns parsed response from IntegrationFrontMutation
	*/
	async fetch(code, redirectUri) {
		const data = (await this._request(IntegrationFrontDocument.toString(), {
			code,
			redirectUri
		})).integrationFront;
		return new IntegrationPayload(this._request, data);
	}
};
/**
* A fetchable IntegrationGitHubEnterpriseServerConnect Mutation
*
* @param request - function to call the graphql client
*/
var IntegrationGitHubEnterpriseServerConnectMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the IntegrationGitHubEnterpriseServerConnect mutation and return a GitHubEnterpriseServerPayload
	*
	* @param githubUrl - required githubUrl to pass to integrationGitHubEnterpriseServerConnect
	* @param organizationName - required organizationName to pass to integrationGitHubEnterpriseServerConnect
	* @returns parsed response from IntegrationGitHubEnterpriseServerConnectMutation
	*/
	async fetch(githubUrl, organizationName) {
		const data = (await this._request(IntegrationGitHubEnterpriseServerConnectDocument.toString(), {
			githubUrl,
			organizationName
		})).integrationGitHubEnterpriseServerConnect;
		return new GitHubEnterpriseServerPayload(this._request, data);
	}
};
/**
* A fetchable IntegrationGitHubPersonal Mutation
*
* @param request - function to call the graphql client
*/
var IntegrationGitHubPersonalMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the IntegrationGitHubPersonal mutation and return a IntegrationPayload
	*
	* @param code - required code to pass to integrationGitHubPersonal
	* @param variables - variables without 'code' to pass into the IntegrationGitHubPersonalMutation
	* @returns parsed response from IntegrationGitHubPersonalMutation
	*/
	async fetch(code, variables) {
		const data = (await this._request(IntegrationGitHubPersonalDocument.toString(), {
			code,
			...variables
		})).integrationGitHubPersonal;
		return new IntegrationPayload(this._request, data);
	}
};
/**
* A fetchable CreateIntegrationGithubCommit Mutation
*
* @param request - function to call the graphql client
*/
var CreateIntegrationGithubCommitMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the CreateIntegrationGithubCommit mutation and return a GitHubCommitIntegrationPayload
	*
	* @returns parsed response from CreateIntegrationGithubCommitMutation
	*/
	async fetch() {
		const data = (await this._request(CreateIntegrationGithubCommitDocument.toString(), {})).integrationGithubCommitCreate;
		return new GitHubCommitIntegrationPayload(this._request, data);
	}
};
/**
* A fetchable IntegrationGithubConnect Mutation
*
* @param request - function to call the graphql client
*/
var IntegrationGithubConnectMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the IntegrationGithubConnect mutation and return a IntegrationPayload
	*
	* @param code - required code to pass to integrationGithubConnect
	* @param installationId - required installationId to pass to integrationGithubConnect
	* @param variables - variables without 'code', 'installationId' to pass into the IntegrationGithubConnectMutation
	* @returns parsed response from IntegrationGithubConnectMutation
	*/
	async fetch(code, installationId, variables) {
		const data = (await this._request(IntegrationGithubConnectDocument.toString(), {
			code,
			installationId,
			...variables
		})).integrationGithubConnect;
		return new IntegrationPayload(this._request, data);
	}
};
/**
* A fetchable IntegrationGithubImportConnect Mutation
*
* @param request - function to call the graphql client
*/
var IntegrationGithubImportConnectMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the IntegrationGithubImportConnect mutation and return a IntegrationPayload
	*
	* @param code - required code to pass to integrationGithubImportConnect
	* @param installationId - required installationId to pass to integrationGithubImportConnect
	* @returns parsed response from IntegrationGithubImportConnectMutation
	*/
	async fetch(code, installationId) {
		const data = (await this._request(IntegrationGithubImportConnectDocument.toString(), {
			code,
			installationId
		})).integrationGithubImportConnect;
		return new IntegrationPayload(this._request, data);
	}
};
/**
* A fetchable IntegrationGithubImportRefresh Mutation
*
* @param request - function to call the graphql client
*/
var IntegrationGithubImportRefreshMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the IntegrationGithubImportRefresh mutation and return a IntegrationPayload
	*
	* @param id - required id to pass to integrationGithubImportRefresh
	* @returns parsed response from IntegrationGithubImportRefreshMutation
	*/
	async fetch(id) {
		const data = (await this._request(IntegrationGithubImportRefreshDocument.toString(), { id })).integrationGithubImportRefresh;
		return new IntegrationPayload(this._request, data);
	}
};
/**
* A fetchable IntegrationGitlabConnect Mutation
*
* @param request - function to call the graphql client
*/
var IntegrationGitlabConnectMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the IntegrationGitlabConnect mutation and return a GitLabIntegrationCreatePayload
	*
	* @param accessToken - required accessToken to pass to integrationGitlabConnect
	* @param gitlabUrl - required gitlabUrl to pass to integrationGitlabConnect
	* @returns parsed response from IntegrationGitlabConnectMutation
	*/
	async fetch(accessToken, gitlabUrl) {
		const data = (await this._request(IntegrationGitlabConnectDocument.toString(), {
			accessToken,
			gitlabUrl
		})).integrationGitlabConnect;
		return new GitLabIntegrationCreatePayload(this._request, data);
	}
};
/**
* A fetchable IntegrationGong Mutation
*
* @param request - function to call the graphql client
*/
var IntegrationGongMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the IntegrationGong mutation and return a IntegrationPayload
	*
	* @param code - required code to pass to integrationGong
	* @param redirectUri - required redirectUri to pass to integrationGong
	* @returns parsed response from IntegrationGongMutation
	*/
	async fetch(code, redirectUri) {
		const data = (await this._request(IntegrationGongDocument.toString(), {
			code,
			redirectUri
		})).integrationGong;
		return new IntegrationPayload(this._request, data);
	}
};
/**
* A fetchable IntegrationGoogleSheets Mutation
*
* @param request - function to call the graphql client
*/
var IntegrationGoogleSheetsMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the IntegrationGoogleSheets mutation and return a IntegrationPayload
	*
	* @param code - required code to pass to integrationGoogleSheets
	* @returns parsed response from IntegrationGoogleSheetsMutation
	*/
	async fetch(code) {
		const data = (await this._request(IntegrationGoogleSheetsDocument.toString(), { code })).integrationGoogleSheets;
		return new IntegrationPayload(this._request, data);
	}
};
/**
* A fetchable IntegrationIntercom Mutation
*
* @param request - function to call the graphql client
*/
var IntegrationIntercomMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the IntegrationIntercom mutation and return a IntegrationPayload
	*
	* @param code - required code to pass to integrationIntercom
	* @param redirectUri - required redirectUri to pass to integrationIntercom
	* @param variables - variables without 'code', 'redirectUri' to pass into the IntegrationIntercomMutation
	* @returns parsed response from IntegrationIntercomMutation
	*/
	async fetch(code, redirectUri, variables) {
		const data = (await this._request(IntegrationIntercomDocument.toString(), {
			code,
			redirectUri,
			...variables
		})).integrationIntercom;
		return new IntegrationPayload(this._request, data);
	}
};
/**
* A fetchable DeleteIntegrationIntercom Mutation
*
* @param request - function to call the graphql client
*/
var DeleteIntegrationIntercomMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the DeleteIntegrationIntercom mutation and return a IntegrationPayload
	*
	* @returns parsed response from DeleteIntegrationIntercomMutation
	*/
	async fetch() {
		const data = (await this._request(DeleteIntegrationIntercomDocument.toString(), {})).integrationIntercomDelete;
		return new IntegrationPayload(this._request, data);
	}
};
/**
* A fetchable UpdateIntegrationIntercomSettings Mutation
*
* @param request - function to call the graphql client
*/
var UpdateIntegrationIntercomSettingsMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the UpdateIntegrationIntercomSettings mutation and return a IntegrationPayload
	*
	* @param input - required input to pass to updateIntegrationIntercomSettings
	* @returns parsed response from UpdateIntegrationIntercomSettingsMutation
	*/
	async fetch(input) {
		const data = (await this._request(UpdateIntegrationIntercomSettingsDocument.toString(), { input })).integrationIntercomSettingsUpdate;
		return new IntegrationPayload(this._request, data);
	}
};
/**
* A fetchable IntegrationJiraPersonal Mutation
*
* @param request - function to call the graphql client
*/
var IntegrationJiraPersonalMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the IntegrationJiraPersonal mutation and return a IntegrationPayload
	*
	* @param variables - variables to pass into the IntegrationJiraPersonalMutation
	* @returns parsed response from IntegrationJiraPersonalMutation
	*/
	async fetch(variables) {
		const data = (await this._request(IntegrationJiraPersonalDocument.toString(), variables)).integrationJiraPersonal;
		return new IntegrationPayload(this._request, data);
	}
};
/**
* A fetchable IntegrationLoom Mutation
*
* @param request - function to call the graphql client
*/
var IntegrationLoomMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the IntegrationLoom mutation and return a IntegrationPayload
	*
	* @returns parsed response from IntegrationLoomMutation
	*/
	async fetch() {
		const data = (await this._request(IntegrationLoomDocument.toString(), {})).integrationLoom;
		return new IntegrationPayload(this._request, data);
	}
};
/**
* A fetchable IntegrationRequest Mutation
*
* @param request - function to call the graphql client
*/
var IntegrationRequestMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the IntegrationRequest mutation and return a IntegrationRequestPayload
	*
	* @param input - required input to pass to integrationRequest
	* @returns parsed response from IntegrationRequestMutation
	*/
	async fetch(input) {
		const data = (await this._request(IntegrationRequestDocument.toString(), { input })).integrationRequest;
		return new IntegrationRequestPayload(this._request, data);
	}
};
/**
* A fetchable IntegrationSalesforce Mutation
*
* @param request - function to call the graphql client
*/
var IntegrationSalesforceMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the IntegrationSalesforce mutation and return a IntegrationPayload
	*
	* @param code - required code to pass to integrationSalesforce
	* @param redirectUri - required redirectUri to pass to integrationSalesforce
	* @param subdomain - required subdomain to pass to integrationSalesforce
	* @returns parsed response from IntegrationSalesforceMutation
	*/
	async fetch(code, redirectUri, subdomain) {
		const data = (await this._request(IntegrationSalesforceDocument.toString(), {
			code,
			redirectUri,
			subdomain
		})).integrationSalesforce;
		return new IntegrationPayload(this._request, data);
	}
};
/**
* A fetchable IntegrationSentryConnect Mutation
*
* @param request - function to call the graphql client
*/
var IntegrationSentryConnectMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the IntegrationSentryConnect mutation and return a IntegrationPayload
	*
	* @param code - required code to pass to integrationSentryConnect
	* @param installationId - required installationId to pass to integrationSentryConnect
	* @param organizationSlug - required organizationSlug to pass to integrationSentryConnect
	* @returns parsed response from IntegrationSentryConnectMutation
	*/
	async fetch(code, installationId, organizationSlug) {
		const data = (await this._request(IntegrationSentryConnectDocument.toString(), {
			code,
			installationId,
			organizationSlug
		})).integrationSentryConnect;
		return new IntegrationPayload(this._request, data);
	}
};
/**
* A fetchable IntegrationSlack Mutation
*
* @param request - function to call the graphql client
*/
var IntegrationSlackMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the IntegrationSlack mutation and return a IntegrationPayload
	*
	* @param code - required code to pass to integrationSlack
	* @param redirectUri - required redirectUri to pass to integrationSlack
	* @param variables - variables without 'code', 'redirectUri' to pass into the IntegrationSlackMutation
	* @returns parsed response from IntegrationSlackMutation
	*/
	async fetch(code, redirectUri, variables) {
		const data = (await this._request(IntegrationSlackDocument.toString(), {
			code,
			redirectUri,
			...variables
		})).integrationSlack;
		return new IntegrationPayload(this._request, data);
	}
};
/**
* A fetchable IntegrationSlackAsks Mutation
*
* @param request - function to call the graphql client
*/
var IntegrationSlackAsksMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the IntegrationSlackAsks mutation and return a IntegrationPayload
	*
	* @param code - required code to pass to integrationSlackAsks
	* @param redirectUri - required redirectUri to pass to integrationSlackAsks
	* @returns parsed response from IntegrationSlackAsksMutation
	*/
	async fetch(code, redirectUri) {
		const data = (await this._request(IntegrationSlackAsksDocument.toString(), {
			code,
			redirectUri
		})).integrationSlackAsks;
		return new IntegrationPayload(this._request, data);
	}
};
/**
* A fetchable IntegrationSlackCustomViewNotifications Mutation
*
* @param request - function to call the graphql client
*/
var IntegrationSlackCustomViewNotificationsMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the IntegrationSlackCustomViewNotifications mutation and return a SlackChannelConnectPayload
	*
	* @param code - required code to pass to integrationSlackCustomViewNotifications
	* @param customViewId - required customViewId to pass to integrationSlackCustomViewNotifications
	* @param redirectUri - required redirectUri to pass to integrationSlackCustomViewNotifications
	* @returns parsed response from IntegrationSlackCustomViewNotificationsMutation
	*/
	async fetch(code, customViewId, redirectUri) {
		const data = (await this._request(IntegrationSlackCustomViewNotificationsDocument.toString(), {
			code,
			customViewId,
			redirectUri
		})).integrationSlackCustomViewNotifications;
		return new SlackChannelConnectPayload(this._request, data);
	}
};
/**
* A fetchable IntegrationSlackCustomerChannelLink Mutation
*
* @param request - function to call the graphql client
*/
var IntegrationSlackCustomerChannelLinkMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the IntegrationSlackCustomerChannelLink mutation and return a SuccessPayload
	*
	* @param code - required code to pass to integrationSlackCustomerChannelLink
	* @param customerId - required customerId to pass to integrationSlackCustomerChannelLink
	* @param redirectUri - required redirectUri to pass to integrationSlackCustomerChannelLink
	* @returns parsed response from IntegrationSlackCustomerChannelLinkMutation
	*/
	async fetch(code, customerId, redirectUri) {
		const data = (await this._request(IntegrationSlackCustomerChannelLinkDocument.toString(), {
			code,
			customerId,
			redirectUri
		})).integrationSlackCustomerChannelLink;
		return new SuccessPayload(this._request, data);
	}
};
/**
* A fetchable IntegrationSlackImportEmojis Mutation
*
* @param request - function to call the graphql client
*/
var IntegrationSlackImportEmojisMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the IntegrationSlackImportEmojis mutation and return a IntegrationPayload
	*
	* @param code - required code to pass to integrationSlackImportEmojis
	* @param redirectUri - required redirectUri to pass to integrationSlackImportEmojis
	* @returns parsed response from IntegrationSlackImportEmojisMutation
	*/
	async fetch(code, redirectUri) {
		const data = (await this._request(IntegrationSlackImportEmojisDocument.toString(), {
			code,
			redirectUri
		})).integrationSlackImportEmojis;
		return new IntegrationPayload(this._request, data);
	}
};
/**
* A fetchable IntegrationSlackOrAsksUpdateSlackTeamName Mutation
*
* @param request - function to call the graphql client
*/
var IntegrationSlackOrAsksUpdateSlackTeamNameMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the IntegrationSlackOrAsksUpdateSlackTeamName mutation and return a IntegrationSlackWorkspaceNamePayload
	*
	* @param integrationId - required integrationId to pass to integrationSlackOrAsksUpdateSlackTeamName
	* @returns parsed response from IntegrationSlackOrAsksUpdateSlackTeamNameMutation
	*/
	async fetch(integrationId) {
		const data = (await this._request(IntegrationSlackOrAsksUpdateSlackTeamNameDocument.toString(), { integrationId })).integrationSlackOrAsksUpdateSlackTeamName;
		return new IntegrationSlackWorkspaceNamePayload(this._request, data);
	}
};
/**
* A fetchable IntegrationSlackOrgProjectUpdatesPost Mutation
*
* @param request - function to call the graphql client
*/
var IntegrationSlackOrgProjectUpdatesPostMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the IntegrationSlackOrgProjectUpdatesPost mutation and return a SlackChannelConnectPayload
	*
	* @param code - required code to pass to integrationSlackOrgProjectUpdatesPost
	* @param redirectUri - required redirectUri to pass to integrationSlackOrgProjectUpdatesPost
	* @returns parsed response from IntegrationSlackOrgProjectUpdatesPostMutation
	*/
	async fetch(code, redirectUri) {
		const data = (await this._request(IntegrationSlackOrgProjectUpdatesPostDocument.toString(), {
			code,
			redirectUri
		})).integrationSlackOrgProjectUpdatesPost;
		return new SlackChannelConnectPayload(this._request, data);
	}
};
/**
* A fetchable IntegrationSlackPersonal Mutation
*
* @param request - function to call the graphql client
*/
var IntegrationSlackPersonalMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the IntegrationSlackPersonal mutation and return a IntegrationPayload
	*
	* @param code - required code to pass to integrationSlackPersonal
	* @param redirectUri - required redirectUri to pass to integrationSlackPersonal
	* @returns parsed response from IntegrationSlackPersonalMutation
	*/
	async fetch(code, redirectUri) {
		const data = (await this._request(IntegrationSlackPersonalDocument.toString(), {
			code,
			redirectUri
		})).integrationSlackPersonal;
		return new IntegrationPayload(this._request, data);
	}
};
/**
* A fetchable IntegrationSlackPost Mutation
*
* @param request - function to call the graphql client
*/
var IntegrationSlackPostMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the IntegrationSlackPost mutation and return a SlackChannelConnectPayload
	*
	* @param code - required code to pass to integrationSlackPost
	* @param redirectUri - required redirectUri to pass to integrationSlackPost
	* @param teamId - required teamId to pass to integrationSlackPost
	* @param variables - variables without 'code', 'redirectUri', 'teamId' to pass into the IntegrationSlackPostMutation
	* @returns parsed response from IntegrationSlackPostMutation
	*/
	async fetch(code, redirectUri, teamId, variables) {
		const data = (await this._request(IntegrationSlackPostDocument.toString(), {
			code,
			redirectUri,
			teamId,
			...variables
		})).integrationSlackPost;
		return new SlackChannelConnectPayload(this._request, data);
	}
};
/**
* A fetchable IntegrationSlackProjectPost Mutation
*
* @param request - function to call the graphql client
*/
var IntegrationSlackProjectPostMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the IntegrationSlackProjectPost mutation and return a SlackChannelConnectPayload
	*
	* @param code - required code to pass to integrationSlackProjectPost
	* @param projectId - required projectId to pass to integrationSlackProjectPost
	* @param redirectUri - required redirectUri to pass to integrationSlackProjectPost
	* @param service - required service to pass to integrationSlackProjectPost
	* @returns parsed response from IntegrationSlackProjectPostMutation
	*/
	async fetch(code, projectId, redirectUri, service) {
		const data = (await this._request(IntegrationSlackProjectPostDocument.toString(), {
			code,
			projectId,
			redirectUri,
			service
		})).integrationSlackProjectPost;
		return new SlackChannelConnectPayload(this._request, data);
	}
};
/**
* A fetchable CreateIntegrationTemplate Mutation
*
* @param request - function to call the graphql client
*/
var CreateIntegrationTemplateMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the CreateIntegrationTemplate mutation and return a IntegrationTemplatePayload
	*
	* @param input - required input to pass to createIntegrationTemplate
	* @returns parsed response from CreateIntegrationTemplateMutation
	*/
	async fetch(input) {
		const data = (await this._request(CreateIntegrationTemplateDocument.toString(), { input })).integrationTemplateCreate;
		return new IntegrationTemplatePayload(this._request, data);
	}
};
/**
* A fetchable DeleteIntegrationTemplate Mutation
*
* @param request - function to call the graphql client
*/
var DeleteIntegrationTemplateMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the DeleteIntegrationTemplate mutation and return a DeletePayload
	*
	* @param id - required id to pass to deleteIntegrationTemplate
	* @returns parsed response from DeleteIntegrationTemplateMutation
	*/
	async fetch(id) {
		const data = (await this._request(DeleteIntegrationTemplateDocument.toString(), { id })).integrationTemplateDelete;
		return new DeletePayload(this._request, data);
	}
};
/**
* A fetchable IntegrationZendesk Mutation
*
* @param request - function to call the graphql client
*/
var IntegrationZendeskMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the IntegrationZendesk mutation and return a IntegrationPayload
	*
	* @param code - required code to pass to integrationZendesk
	* @param redirectUri - required redirectUri to pass to integrationZendesk
	* @param scope - required scope to pass to integrationZendesk
	* @param subdomain - required subdomain to pass to integrationZendesk
	* @returns parsed response from IntegrationZendeskMutation
	*/
	async fetch(code, redirectUri, scope, subdomain) {
		const data = (await this._request(IntegrationZendeskDocument.toString(), {
			code,
			redirectUri,
			scope,
			subdomain
		})).integrationZendesk;
		return new IntegrationPayload(this._request, data);
	}
};
/**
* A fetchable CreateIntegrationsSettings Mutation
*
* @param request - function to call the graphql client
*/
var CreateIntegrationsSettingsMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the CreateIntegrationsSettings mutation and return a IntegrationsSettingsPayload
	*
	* @param input - required input to pass to createIntegrationsSettings
	* @returns parsed response from CreateIntegrationsSettingsMutation
	*/
	async fetch(input) {
		const data = (await this._request(CreateIntegrationsSettingsDocument.toString(), { input })).integrationsSettingsCreate;
		return new IntegrationsSettingsPayload(this._request, data);
	}
};
/**
* A fetchable UpdateIntegrationsSettings Mutation
*
* @param request - function to call the graphql client
*/
var UpdateIntegrationsSettingsMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the UpdateIntegrationsSettings mutation and return a IntegrationsSettingsPayload
	*
	* @param id - required id to pass to updateIntegrationsSettings
	* @param input - required input to pass to updateIntegrationsSettings
	* @returns parsed response from UpdateIntegrationsSettingsMutation
	*/
	async fetch(id, input) {
		const data = (await this._request(UpdateIntegrationsSettingsDocument.toString(), {
			id,
			input
		})).integrationsSettingsUpdate;
		return new IntegrationsSettingsPayload(this._request, data);
	}
};
/**
* A fetchable IssueAddLabel Mutation
*
* @param request - function to call the graphql client
*/
var IssueAddLabelMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the IssueAddLabel mutation and return a IssuePayload
	*
	* @param id - required id to pass to issueAddLabel
	* @param labelId - required labelId to pass to issueAddLabel
	* @returns parsed response from IssueAddLabelMutation
	*/
	async fetch(id, labelId) {
		const data = (await this._request(IssueAddLabelDocument.toString(), {
			id,
			labelId
		})).issueAddLabel;
		return new IssuePayload(this._request, data);
	}
};
/**
* A fetchable ArchiveIssue Mutation
*
* @param request - function to call the graphql client
*/
var ArchiveIssueMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the ArchiveIssue mutation and return a IssueArchivePayload
	*
	* @param id - required id to pass to archiveIssue
	* @param variables - variables without 'id' to pass into the ArchiveIssueMutation
	* @returns parsed response from ArchiveIssueMutation
	*/
	async fetch(id, variables) {
		const data = (await this._request(ArchiveIssueDocument.toString(), {
			id,
			...variables
		})).issueArchive;
		return new IssueArchivePayload(this._request, data);
	}
};
/**
* A fetchable CreateIssueBatch Mutation
*
* @param request - function to call the graphql client
*/
var CreateIssueBatchMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the CreateIssueBatch mutation and return a IssueBatchPayload
	*
	* @param input - required input to pass to createIssueBatch
	* @returns parsed response from CreateIssueBatchMutation
	*/
	async fetch(input) {
		const data = (await this._request(CreateIssueBatchDocument.toString(), { input })).issueBatchCreate;
		return new IssueBatchPayload(this._request, data);
	}
};
/**
* A fetchable UpdateIssueBatch Mutation
*
* @param request - function to call the graphql client
*/
var UpdateIssueBatchMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the UpdateIssueBatch mutation and return a IssueBatchPayload
	*
	* @param ids - required ids to pass to updateIssueBatch
	* @param input - required input to pass to updateIssueBatch
	* @returns parsed response from UpdateIssueBatchMutation
	*/
	async fetch(ids, input) {
		const data = (await this._request(UpdateIssueBatchDocument.toString(), {
			ids,
			input
		})).issueBatchUpdate;
		return new IssueBatchPayload(this._request, data);
	}
};
/**
* A fetchable CreateIssue Mutation
*
* @param request - function to call the graphql client
*/
var CreateIssueMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the CreateIssue mutation and return a IssuePayload
	*
	* @param input - required input to pass to createIssue
	* @returns parsed response from CreateIssueMutation
	*/
	async fetch(input) {
		const data = (await this._request(CreateIssueDocument.toString(), { input })).issueCreate;
		return new IssuePayload(this._request, data);
	}
};
/**
* A fetchable DeleteIssue Mutation
*
* @param request - function to call the graphql client
*/
var DeleteIssueMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the DeleteIssue mutation and return a IssueArchivePayload
	*
	* @param id - required id to pass to deleteIssue
	* @param variables - variables without 'id' to pass into the DeleteIssueMutation
	* @returns parsed response from DeleteIssueMutation
	*/
	async fetch(id, variables) {
		const data = (await this._request(DeleteIssueDocument.toString(), {
			id,
			...variables
		})).issueDelete;
		return new IssueArchivePayload(this._request, data);
	}
};
/**
* A fetchable IssueExternalSyncDisable Mutation
*
* @param request - function to call the graphql client
*/
var IssueExternalSyncDisableMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the IssueExternalSyncDisable mutation and return a IssuePayload
	*
	* @param attachmentId - required attachmentId to pass to issueExternalSyncDisable
	* @returns parsed response from IssueExternalSyncDisableMutation
	*/
	async fetch(attachmentId) {
		const data = (await this._request(IssueExternalSyncDisableDocument.toString(), { attachmentId })).issueExternalSyncDisable;
		return new IssuePayload(this._request, data);
	}
};
/**
* A fetchable IssueImportCreateAsana Mutation
*
* @param request - function to call the graphql client
*/
var IssueImportCreateAsanaMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the IssueImportCreateAsana mutation and return a IssueImportPayload
	*
	* @param asanaTeamName - required asanaTeamName to pass to issueImportCreateAsana
	* @param asanaToken - required asanaToken to pass to issueImportCreateAsana
	* @param variables - variables without 'asanaTeamName', 'asanaToken' to pass into the IssueImportCreateAsanaMutation
	* @returns parsed response from IssueImportCreateAsanaMutation
	*/
	async fetch(asanaTeamName, asanaToken, variables) {
		const data = (await this._request(IssueImportCreateAsanaDocument.toString(), {
			asanaTeamName,
			asanaToken,
			...variables
		})).issueImportCreateAsana;
		return new IssueImportPayload(this._request, data);
	}
};
/**
* A fetchable IssueImportCreateCsvJira Mutation
*
* @param request - function to call the graphql client
*/
var IssueImportCreateCsvJiraMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the IssueImportCreateCsvJira mutation and return a IssueImportPayload
	*
	* @param csvUrl - required csvUrl to pass to issueImportCreateCSVJira
	* @param variables - variables without 'csvUrl' to pass into the IssueImportCreateCsvJiraMutation
	* @returns parsed response from IssueImportCreateCsvJiraMutation
	*/
	async fetch(csvUrl, variables) {
		const data = (await this._request(IssueImportCreateCsvJiraDocument.toString(), {
			csvUrl,
			...variables
		})).issueImportCreateCSVJira;
		return new IssueImportPayload(this._request, data);
	}
};
/**
* A fetchable IssueImportCreateClubhouse Mutation
*
* @param request - function to call the graphql client
*/
var IssueImportCreateClubhouseMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the IssueImportCreateClubhouse mutation and return a IssueImportPayload
	*
	* @param clubhouseGroupName - required clubhouseGroupName to pass to issueImportCreateClubhouse
	* @param clubhouseToken - required clubhouseToken to pass to issueImportCreateClubhouse
	* @param variables - variables without 'clubhouseGroupName', 'clubhouseToken' to pass into the IssueImportCreateClubhouseMutation
	* @returns parsed response from IssueImportCreateClubhouseMutation
	*/
	async fetch(clubhouseGroupName, clubhouseToken, variables) {
		const data = (await this._request(IssueImportCreateClubhouseDocument.toString(), {
			clubhouseGroupName,
			clubhouseToken,
			...variables
		})).issueImportCreateClubhouse;
		return new IssueImportPayload(this._request, data);
	}
};
/**
* A fetchable IssueImportCreateGithub Mutation
*
* @param request - function to call the graphql client
*/
var IssueImportCreateGithubMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the IssueImportCreateGithub mutation and return a IssueImportPayload
	*
	* @param variables - variables to pass into the IssueImportCreateGithubMutation
	* @returns parsed response from IssueImportCreateGithubMutation
	*/
	async fetch(variables) {
		const data = (await this._request(IssueImportCreateGithubDocument.toString(), variables)).issueImportCreateGithub;
		return new IssueImportPayload(this._request, data);
	}
};
/**
* A fetchable IssueImportCreateJira Mutation
*
* @param request - function to call the graphql client
*/
var IssueImportCreateJiraMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the IssueImportCreateJira mutation and return a IssueImportPayload
	*
	* @param jiraEmail - required jiraEmail to pass to issueImportCreateJira
	* @param jiraHostname - required jiraHostname to pass to issueImportCreateJira
	* @param jiraProject - required jiraProject to pass to issueImportCreateJira
	* @param jiraToken - required jiraToken to pass to issueImportCreateJira
	* @param variables - variables without 'jiraEmail', 'jiraHostname', 'jiraProject', 'jiraToken' to pass into the IssueImportCreateJiraMutation
	* @returns parsed response from IssueImportCreateJiraMutation
	*/
	async fetch(jiraEmail, jiraHostname, jiraProject, jiraToken, variables) {
		const data = (await this._request(IssueImportCreateJiraDocument.toString(), {
			jiraEmail,
			jiraHostname,
			jiraProject,
			jiraToken,
			...variables
		})).issueImportCreateJira;
		return new IssueImportPayload(this._request, data);
	}
};
/**
* A fetchable DeleteIssueImport Mutation
*
* @param request - function to call the graphql client
*/
var DeleteIssueImportMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the DeleteIssueImport mutation and return a IssueImportDeletePayload
	*
	* @param issueImportId - required issueImportId to pass to deleteIssueImport
	* @returns parsed response from DeleteIssueImportMutation
	*/
	async fetch(issueImportId) {
		const data = (await this._request(DeleteIssueImportDocument.toString(), { issueImportId })).issueImportDelete;
		return new IssueImportDeletePayload(this._request, data);
	}
};
/**
* A fetchable IssueImportProcess Mutation
*
* @param request - function to call the graphql client
*/
var IssueImportProcessMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the IssueImportProcess mutation and return a IssueImportPayload
	*
	* @param issueImportId - required issueImportId to pass to issueImportProcess
	* @param mapping - required mapping to pass to issueImportProcess
	* @returns parsed response from IssueImportProcessMutation
	*/
	async fetch(issueImportId, mapping) {
		const data = (await this._request(IssueImportProcessDocument.toString(), {
			issueImportId,
			mapping
		})).issueImportProcess;
		return new IssueImportPayload(this._request, data);
	}
};
/**
* A fetchable UpdateIssueImport Mutation
*
* @param request - function to call the graphql client
*/
var UpdateIssueImportMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the UpdateIssueImport mutation and return a IssueImportPayload
	*
	* @param id - required id to pass to updateIssueImport
	* @param input - required input to pass to updateIssueImport
	* @returns parsed response from UpdateIssueImportMutation
	*/
	async fetch(id, input) {
		const data = (await this._request(UpdateIssueImportDocument.toString(), {
			id,
			input
		})).issueImportUpdate;
		return new IssueImportPayload(this._request, data);
	}
};
/**
* A fetchable CreateIssueLabel Mutation
*
* @param request - function to call the graphql client
*/
var CreateIssueLabelMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the CreateIssueLabel mutation and return a IssueLabelPayload
	*
	* @param input - required input to pass to createIssueLabel
	* @param variables - variables without 'input' to pass into the CreateIssueLabelMutation
	* @returns parsed response from CreateIssueLabelMutation
	*/
	async fetch(input, variables) {
		const data = (await this._request(CreateIssueLabelDocument.toString(), {
			input,
			...variables
		})).issueLabelCreate;
		return new IssueLabelPayload(this._request, data);
	}
};
/**
* A fetchable DeleteIssueLabel Mutation
*
* @param request - function to call the graphql client
*/
var DeleteIssueLabelMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the DeleteIssueLabel mutation and return a DeletePayload
	*
	* @param id - required id to pass to deleteIssueLabel
	* @returns parsed response from DeleteIssueLabelMutation
	*/
	async fetch(id) {
		const data = (await this._request(DeleteIssueLabelDocument.toString(), { id })).issueLabelDelete;
		return new DeletePayload(this._request, data);
	}
};
/**
* A fetchable IssueLabelRestore Mutation
*
* @param request - function to call the graphql client
*/
var IssueLabelRestoreMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the IssueLabelRestore mutation and return a IssueLabelPayload
	*
	* @param id - required id to pass to issueLabelRestore
	* @returns parsed response from IssueLabelRestoreMutation
	*/
	async fetch(id) {
		const data = (await this._request(IssueLabelRestoreDocument.toString(), { id })).issueLabelRestore;
		return new IssueLabelPayload(this._request, data);
	}
};
/**
* A fetchable IssueLabelRetire Mutation
*
* @param request - function to call the graphql client
*/
var IssueLabelRetireMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the IssueLabelRetire mutation and return a IssueLabelPayload
	*
	* @param id - required id to pass to issueLabelRetire
	* @returns parsed response from IssueLabelRetireMutation
	*/
	async fetch(id) {
		const data = (await this._request(IssueLabelRetireDocument.toString(), { id })).issueLabelRetire;
		return new IssueLabelPayload(this._request, data);
	}
};
/**
* A fetchable UpdateIssueLabel Mutation
*
* @param request - function to call the graphql client
*/
var UpdateIssueLabelMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the UpdateIssueLabel mutation and return a IssueLabelPayload
	*
	* @param id - required id to pass to updateIssueLabel
	* @param input - required input to pass to updateIssueLabel
	* @param variables - variables without 'id', 'input' to pass into the UpdateIssueLabelMutation
	* @returns parsed response from UpdateIssueLabelMutation
	*/
	async fetch(id, input, variables) {
		const data = (await this._request(UpdateIssueLabelDocument.toString(), {
			id,
			input,
			...variables
		})).issueLabelUpdate;
		return new IssueLabelPayload(this._request, data);
	}
};
/**
* A fetchable CreateIssueRelation Mutation
*
* @param request - function to call the graphql client
*/
var CreateIssueRelationMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the CreateIssueRelation mutation and return a IssueRelationPayload
	*
	* @param input - required input to pass to createIssueRelation
	* @param variables - variables without 'input' to pass into the CreateIssueRelationMutation
	* @returns parsed response from CreateIssueRelationMutation
	*/
	async fetch(input, variables) {
		const data = (await this._request(CreateIssueRelationDocument.toString(), {
			input,
			...variables
		})).issueRelationCreate;
		return new IssueRelationPayload(this._request, data);
	}
};
/**
* A fetchable DeleteIssueRelation Mutation
*
* @param request - function to call the graphql client
*/
var DeleteIssueRelationMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the DeleteIssueRelation mutation and return a DeletePayload
	*
	* @param id - required id to pass to deleteIssueRelation
	* @returns parsed response from DeleteIssueRelationMutation
	*/
	async fetch(id) {
		const data = (await this._request(DeleteIssueRelationDocument.toString(), { id })).issueRelationDelete;
		return new DeletePayload(this._request, data);
	}
};
/**
* A fetchable UpdateIssueRelation Mutation
*
* @param request - function to call the graphql client
*/
var UpdateIssueRelationMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the UpdateIssueRelation mutation and return a IssueRelationPayload
	*
	* @param id - required id to pass to updateIssueRelation
	* @param input - required input to pass to updateIssueRelation
	* @returns parsed response from UpdateIssueRelationMutation
	*/
	async fetch(id, input) {
		const data = (await this._request(UpdateIssueRelationDocument.toString(), {
			id,
			input
		})).issueRelationUpdate;
		return new IssueRelationPayload(this._request, data);
	}
};
/**
* A fetchable IssueReminder Mutation
*
* @param request - function to call the graphql client
*/
var IssueReminderMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the IssueReminder mutation and return a IssuePayload
	*
	* @param id - required id to pass to issueReminder
	* @param reminderAt - required reminderAt to pass to issueReminder
	* @returns parsed response from IssueReminderMutation
	*/
	async fetch(id, reminderAt) {
		const data = (await this._request(IssueReminderDocument.toString(), {
			id,
			reminderAt
		})).issueReminder;
		return new IssuePayload(this._request, data);
	}
};
/**
* A fetchable IssueRemoveLabel Mutation
*
* @param request - function to call the graphql client
*/
var IssueRemoveLabelMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the IssueRemoveLabel mutation and return a IssuePayload
	*
	* @param id - required id to pass to issueRemoveLabel
	* @param labelId - required labelId to pass to issueRemoveLabel
	* @returns parsed response from IssueRemoveLabelMutation
	*/
	async fetch(id, labelId) {
		const data = (await this._request(IssueRemoveLabelDocument.toString(), {
			id,
			labelId
		})).issueRemoveLabel;
		return new IssuePayload(this._request, data);
	}
};
/**
* A fetchable IssueSubscribe Mutation
*
* @param request - function to call the graphql client
*/
var IssueSubscribeMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the IssueSubscribe mutation and return a IssuePayload
	*
	* @param id - required id to pass to issueSubscribe
	* @param variables - variables without 'id' to pass into the IssueSubscribeMutation
	* @returns parsed response from IssueSubscribeMutation
	*/
	async fetch(id, variables) {
		const data = (await this._request(IssueSubscribeDocument.toString(), {
			id,
			...variables
		})).issueSubscribe;
		return new IssuePayload(this._request, data);
	}
};
/**
* A fetchable UnarchiveIssue Mutation
*
* @param request - function to call the graphql client
*/
var UnarchiveIssueMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the UnarchiveIssue mutation and return a IssueArchivePayload
	*
	* @param id - required id to pass to unarchiveIssue
	* @returns parsed response from UnarchiveIssueMutation
	*/
	async fetch(id) {
		const data = (await this._request(UnarchiveIssueDocument.toString(), { id })).issueUnarchive;
		return new IssueArchivePayload(this._request, data);
	}
};
/**
* A fetchable IssueUnsubscribe Mutation
*
* @param request - function to call the graphql client
*/
var IssueUnsubscribeMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the IssueUnsubscribe mutation and return a IssuePayload
	*
	* @param id - required id to pass to issueUnsubscribe
	* @param variables - variables without 'id' to pass into the IssueUnsubscribeMutation
	* @returns parsed response from IssueUnsubscribeMutation
	*/
	async fetch(id, variables) {
		const data = (await this._request(IssueUnsubscribeDocument.toString(), {
			id,
			...variables
		})).issueUnsubscribe;
		return new IssuePayload(this._request, data);
	}
};
/**
* A fetchable UpdateIssue Mutation
*
* @param request - function to call the graphql client
*/
var UpdateIssueMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the UpdateIssue mutation and return a IssuePayload
	*
	* @param id - required id to pass to updateIssue
	* @param input - required input to pass to updateIssue
	* @returns parsed response from UpdateIssueMutation
	*/
	async fetch(id, input) {
		const data = (await this._request(UpdateIssueDocument.toString(), {
			id,
			input
		})).issueUpdate;
		return new IssuePayload(this._request, data);
	}
};
/**
* A fetchable JoinOrganizationFromOnboarding Mutation
*
* @param request - function to call the graphql client
*/
var JoinOrganizationFromOnboardingMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the JoinOrganizationFromOnboarding mutation and return a CreateOrJoinOrganizationResponse
	*
	* @param input - required input to pass to joinOrganizationFromOnboarding
	* @returns parsed response from JoinOrganizationFromOnboardingMutation
	*/
	async fetch(input) {
		const data = (await this._request(JoinOrganizationFromOnboardingDocument.toString(), { input })).joinOrganizationFromOnboarding;
		return new CreateOrJoinOrganizationResponse(this._request, data);
	}
};
/**
* A fetchable LeaveOrganization Mutation
*
* @param request - function to call the graphql client
*/
var LeaveOrganizationMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the LeaveOrganization mutation and return a CreateOrJoinOrganizationResponse
	*
	* @param organizationId - required organizationId to pass to leaveOrganization
	* @returns parsed response from LeaveOrganizationMutation
	*/
	async fetch(organizationId) {
		const data = (await this._request(LeaveOrganizationDocument.toString(), { organizationId })).leaveOrganization;
		return new CreateOrJoinOrganizationResponse(this._request, data);
	}
};
/**
* A fetchable Logout Mutation
*
* @param request - function to call the graphql client
*/
var LogoutMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the Logout mutation and return a LogoutResponse
	*
	* @param variables - variables to pass into the LogoutMutation
	* @returns parsed response from LogoutMutation
	*/
	async fetch(variables) {
		const data = (await this._request(LogoutDocument.toString(), variables)).logout;
		return new LogoutResponse(this._request, data);
	}
};
/**
* A fetchable LogoutAllSessions Mutation
*
* @param request - function to call the graphql client
*/
var LogoutAllSessionsMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the LogoutAllSessions mutation and return a LogoutResponse
	*
	* @param variables - variables to pass into the LogoutAllSessionsMutation
	* @returns parsed response from LogoutAllSessionsMutation
	*/
	async fetch(variables) {
		const data = (await this._request(LogoutAllSessionsDocument.toString(), variables)).logoutAllSessions;
		return new LogoutResponse(this._request, data);
	}
};
/**
* A fetchable LogoutOtherSessions Mutation
*
* @param request - function to call the graphql client
*/
var LogoutOtherSessionsMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the LogoutOtherSessions mutation and return a LogoutResponse
	*
	* @param variables - variables to pass into the LogoutOtherSessionsMutation
	* @returns parsed response from LogoutOtherSessionsMutation
	*/
	async fetch(variables) {
		const data = (await this._request(LogoutOtherSessionsDocument.toString(), variables)).logoutOtherSessions;
		return new LogoutResponse(this._request, data);
	}
};
/**
* A fetchable LogoutSession Mutation
*
* @param request - function to call the graphql client
*/
var LogoutSessionMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the LogoutSession mutation and return a LogoutResponse
	*
	* @param sessionId - required sessionId to pass to logoutSession
	* @returns parsed response from LogoutSessionMutation
	*/
	async fetch(sessionId) {
		const data = (await this._request(LogoutSessionDocument.toString(), { sessionId })).logoutSession;
		return new LogoutResponse(this._request, data);
	}
};
/**
* A fetchable ArchiveNotification Mutation
*
* @param request - function to call the graphql client
*/
var ArchiveNotificationMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the ArchiveNotification mutation and return a NotificationArchivePayload
	*
	* @param id - required id to pass to archiveNotification
	* @returns parsed response from ArchiveNotificationMutation
	*/
	async fetch(id) {
		const data = (await this._request(ArchiveNotificationDocument.toString(), { id })).notificationArchive;
		return new NotificationArchivePayload(this._request, data);
	}
};
/**
* A fetchable NotificationArchiveAll Mutation
*
* @param request - function to call the graphql client
*/
var NotificationArchiveAllMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the NotificationArchiveAll mutation and return a NotificationBatchActionPayload
	*
	* @param input - required input to pass to notificationArchiveAll
	* @returns parsed response from NotificationArchiveAllMutation
	*/
	async fetch(input) {
		const data = (await this._request(NotificationArchiveAllDocument.toString(), { input })).notificationArchiveAll;
		return new NotificationBatchActionPayload(this._request, data);
	}
};
/**
* A fetchable UpdateNotificationCategoryChannelSubscription Mutation
*
* @param request - function to call the graphql client
*/
var UpdateNotificationCategoryChannelSubscriptionMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the UpdateNotificationCategoryChannelSubscription mutation and return a UserSettingsPayload
	*
	* @param category - required category to pass to updateNotificationCategoryChannelSubscription
	* @param channel - required channel to pass to updateNotificationCategoryChannelSubscription
	* @param subscribe - required subscribe to pass to updateNotificationCategoryChannelSubscription
	* @returns parsed response from UpdateNotificationCategoryChannelSubscriptionMutation
	*/
	async fetch(category, channel, subscribe) {
		const data = (await this._request(UpdateNotificationCategoryChannelSubscriptionDocument.toString(), {
			category,
			channel,
			subscribe
		})).notificationCategoryChannelSubscriptionUpdate;
		return new UserSettingsPayload(this._request, data);
	}
};
/**
* A fetchable NotificationMarkReadAll Mutation
*
* @param request - function to call the graphql client
*/
var NotificationMarkReadAllMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the NotificationMarkReadAll mutation and return a NotificationBatchActionPayload
	*
	* @param input - required input to pass to notificationMarkReadAll
	* @param readAt - required readAt to pass to notificationMarkReadAll
	* @returns parsed response from NotificationMarkReadAllMutation
	*/
	async fetch(input, readAt) {
		const data = (await this._request(NotificationMarkReadAllDocument.toString(), {
			input,
			readAt
		})).notificationMarkReadAll;
		return new NotificationBatchActionPayload(this._request, data);
	}
};
/**
* A fetchable NotificationMarkUnreadAll Mutation
*
* @param request - function to call the graphql client
*/
var NotificationMarkUnreadAllMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the NotificationMarkUnreadAll mutation and return a NotificationBatchActionPayload
	*
	* @param input - required input to pass to notificationMarkUnreadAll
	* @returns parsed response from NotificationMarkUnreadAllMutation
	*/
	async fetch(input) {
		const data = (await this._request(NotificationMarkUnreadAllDocument.toString(), { input })).notificationMarkUnreadAll;
		return new NotificationBatchActionPayload(this._request, data);
	}
};
/**
* A fetchable NotificationSnoozeAll Mutation
*
* @param request - function to call the graphql client
*/
var NotificationSnoozeAllMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the NotificationSnoozeAll mutation and return a NotificationBatchActionPayload
	*
	* @param input - required input to pass to notificationSnoozeAll
	* @param snoozedUntilAt - required snoozedUntilAt to pass to notificationSnoozeAll
	* @returns parsed response from NotificationSnoozeAllMutation
	*/
	async fetch(input, snoozedUntilAt) {
		const data = (await this._request(NotificationSnoozeAllDocument.toString(), {
			input,
			snoozedUntilAt
		})).notificationSnoozeAll;
		return new NotificationBatchActionPayload(this._request, data);
	}
};
/**
* A fetchable CreateNotificationSubscription Mutation
*
* @param request - function to call the graphql client
*/
var CreateNotificationSubscriptionMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the CreateNotificationSubscription mutation and return a NotificationSubscriptionPayload
	*
	* @param input - required input to pass to createNotificationSubscription
	* @returns parsed response from CreateNotificationSubscriptionMutation
	*/
	async fetch(input) {
		const data = (await this._request(CreateNotificationSubscriptionDocument.toString(), { input })).notificationSubscriptionCreate;
		return new NotificationSubscriptionPayload(this._request, data);
	}
};
/**
* A fetchable DeleteNotificationSubscription Mutation
*
* @param request - function to call the graphql client
*/
var DeleteNotificationSubscriptionMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the DeleteNotificationSubscription mutation and return a DeletePayload
	*
	* @param id - required id to pass to deleteNotificationSubscription
	* @returns parsed response from DeleteNotificationSubscriptionMutation
	*/
	async fetch(id) {
		const data = (await this._request(DeleteNotificationSubscriptionDocument.toString(), { id })).notificationSubscriptionDelete;
		return new DeletePayload(this._request, data);
	}
};
/**
* A fetchable UpdateNotificationSubscription Mutation
*
* @param request - function to call the graphql client
*/
var UpdateNotificationSubscriptionMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the UpdateNotificationSubscription mutation and return a NotificationSubscriptionPayload
	*
	* @param id - required id to pass to updateNotificationSubscription
	* @param input - required input to pass to updateNotificationSubscription
	* @returns parsed response from UpdateNotificationSubscriptionMutation
	*/
	async fetch(id, input) {
		const data = (await this._request(UpdateNotificationSubscriptionDocument.toString(), {
			id,
			input
		})).notificationSubscriptionUpdate;
		return new NotificationSubscriptionPayload(this._request, data);
	}
};
/**
* A fetchable UnarchiveNotification Mutation
*
* @param request - function to call the graphql client
*/
var UnarchiveNotificationMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the UnarchiveNotification mutation and return a NotificationArchivePayload
	*
	* @param id - required id to pass to unarchiveNotification
	* @returns parsed response from UnarchiveNotificationMutation
	*/
	async fetch(id) {
		const data = (await this._request(UnarchiveNotificationDocument.toString(), { id })).notificationUnarchive;
		return new NotificationArchivePayload(this._request, data);
	}
};
/**
* A fetchable NotificationUnsnoozeAll Mutation
*
* @param request - function to call the graphql client
*/
var NotificationUnsnoozeAllMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the NotificationUnsnoozeAll mutation and return a NotificationBatchActionPayload
	*
	* @param input - required input to pass to notificationUnsnoozeAll
	* @param unsnoozedAt - required unsnoozedAt to pass to notificationUnsnoozeAll
	* @returns parsed response from NotificationUnsnoozeAllMutation
	*/
	async fetch(input, unsnoozedAt) {
		const data = (await this._request(NotificationUnsnoozeAllDocument.toString(), {
			input,
			unsnoozedAt
		})).notificationUnsnoozeAll;
		return new NotificationBatchActionPayload(this._request, data);
	}
};
/**
* A fetchable UpdateNotification Mutation
*
* @param request - function to call the graphql client
*/
var UpdateNotificationMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the UpdateNotification mutation and return a NotificationPayload
	*
	* @param id - required id to pass to updateNotification
	* @param input - required input to pass to updateNotification
	* @returns parsed response from UpdateNotificationMutation
	*/
	async fetch(id, input) {
		const data = (await this._request(UpdateNotificationDocument.toString(), {
			id,
			input
		})).notificationUpdate;
		return new NotificationPayload(this._request, data);
	}
};
/**
* A fetchable DeleteOrganizationCancel Mutation
*
* @param request - function to call the graphql client
*/
var DeleteOrganizationCancelMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the DeleteOrganizationCancel mutation and return a OrganizationCancelDeletePayload
	*
	* @returns parsed response from DeleteOrganizationCancelMutation
	*/
	async fetch() {
		const data = (await this._request(DeleteOrganizationCancelDocument.toString(), {})).organizationCancelDelete;
		return new OrganizationCancelDeletePayload(this._request, data);
	}
};
/**
* A fetchable DeleteOrganization Mutation
*
* @param request - function to call the graphql client
*/
var DeleteOrganizationMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the DeleteOrganization mutation and return a OrganizationDeletePayload
	*
	* @param input - required input to pass to deleteOrganization
	* @returns parsed response from DeleteOrganizationMutation
	*/
	async fetch(input) {
		const data = (await this._request(DeleteOrganizationDocument.toString(), { input })).organizationDelete;
		return new OrganizationDeletePayload(this._request, data);
	}
};
/**
* A fetchable OrganizationDeleteChallenge Mutation
*
* @param request - function to call the graphql client
*/
var OrganizationDeleteChallengeMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the OrganizationDeleteChallenge mutation and return a OrganizationDeletePayload
	*
	* @returns parsed response from OrganizationDeleteChallengeMutation
	*/
	async fetch() {
		const data = (await this._request(OrganizationDeleteChallengeDocument.toString(), {})).organizationDeleteChallenge;
		return new OrganizationDeletePayload(this._request, data);
	}
};
/**
* A fetchable DeleteOrganizationDomain Mutation
*
* @param request - function to call the graphql client
*/
var DeleteOrganizationDomainMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the DeleteOrganizationDomain mutation and return a DeletePayload
	*
	* @param id - required id to pass to deleteOrganizationDomain
	* @returns parsed response from DeleteOrganizationDomainMutation
	*/
	async fetch(id) {
		const data = (await this._request(DeleteOrganizationDomainDocument.toString(), { id })).organizationDomainDelete;
		return new DeletePayload(this._request, data);
	}
};
/**
* A fetchable CreateOrganizationInvite Mutation
*
* @param request - function to call the graphql client
*/
var CreateOrganizationInviteMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the CreateOrganizationInvite mutation and return a OrganizationInvitePayload
	*
	* @param input - required input to pass to createOrganizationInvite
	* @returns parsed response from CreateOrganizationInviteMutation
	*/
	async fetch(input) {
		const data = (await this._request(CreateOrganizationInviteDocument.toString(), { input })).organizationInviteCreate;
		return new OrganizationInvitePayload(this._request, data);
	}
};
/**
* A fetchable DeleteOrganizationInvite Mutation
*
* @param request - function to call the graphql client
*/
var DeleteOrganizationInviteMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the DeleteOrganizationInvite mutation and return a DeletePayload
	*
	* @param id - required id to pass to deleteOrganizationInvite
	* @returns parsed response from DeleteOrganizationInviteMutation
	*/
	async fetch(id) {
		const data = (await this._request(DeleteOrganizationInviteDocument.toString(), { id })).organizationInviteDelete;
		return new DeletePayload(this._request, data);
	}
};
/**
* A fetchable UpdateOrganizationInvite Mutation
*
* @param request - function to call the graphql client
*/
var UpdateOrganizationInviteMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the UpdateOrganizationInvite mutation and return a OrganizationInvitePayload
	*
	* @param id - required id to pass to updateOrganizationInvite
	* @param input - required input to pass to updateOrganizationInvite
	* @returns parsed response from UpdateOrganizationInviteMutation
	*/
	async fetch(id, input) {
		const data = (await this._request(UpdateOrganizationInviteDocument.toString(), {
			id,
			input
		})).organizationInviteUpdate;
		return new OrganizationInvitePayload(this._request, data);
	}
};
/**
* A fetchable OrganizationStartTrial Mutation
*
* @param request - function to call the graphql client
*/
var OrganizationStartTrialMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the OrganizationStartTrial mutation and return a OrganizationStartTrialPayload
	*
	* @returns parsed response from OrganizationStartTrialMutation
	*/
	async fetch() {
		const data = (await this._request(OrganizationStartTrialDocument.toString(), {})).organizationStartTrial;
		return new OrganizationStartTrialPayload(this._request, data);
	}
};
/**
* A fetchable OrganizationStartTrialForPlan Mutation
*
* @param request - function to call the graphql client
*/
var OrganizationStartTrialForPlanMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the OrganizationStartTrialForPlan mutation and return a OrganizationStartTrialPayload
	*
	* @param input - required input to pass to organizationStartTrialForPlan
	* @returns parsed response from OrganizationStartTrialForPlanMutation
	*/
	async fetch(input) {
		const data = (await this._request(OrganizationStartTrialForPlanDocument.toString(), { input })).organizationStartTrialForPlan;
		return new OrganizationStartTrialPayload(this._request, data);
	}
};
/**
* A fetchable UpdateOrganization Mutation
*
* @param request - function to call the graphql client
*/
var UpdateOrganizationMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the UpdateOrganization mutation and return a OrganizationPayload
	*
	* @param input - required input to pass to updateOrganization
	* @returns parsed response from UpdateOrganizationMutation
	*/
	async fetch(input) {
		const data = (await this._request(UpdateOrganizationDocument.toString(), { input })).organizationUpdate;
		return new OrganizationPayload(this._request, data);
	}
};
/**
* A fetchable ProjectAddLabel Mutation
*
* @param request - function to call the graphql client
*/
var ProjectAddLabelMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the ProjectAddLabel mutation and return a ProjectPayload
	*
	* @param id - required id to pass to projectAddLabel
	* @param labelId - required labelId to pass to projectAddLabel
	* @returns parsed response from ProjectAddLabelMutation
	*/
	async fetch(id, labelId) {
		const data = (await this._request(ProjectAddLabelDocument.toString(), {
			id,
			labelId
		})).projectAddLabel;
		return new ProjectPayload(this._request, data);
	}
};
/**
* A fetchable ArchiveProject Mutation
*
* @param request - function to call the graphql client
*/
var ArchiveProjectMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the ArchiveProject mutation and return a ProjectArchivePayload
	*
	* @param id - required id to pass to archiveProject
	* @param variables - variables without 'id' to pass into the ArchiveProjectMutation
	* @returns parsed response from ArchiveProjectMutation
	*/
	async fetch(id, variables) {
		const data = (await this._request(ArchiveProjectDocument.toString(), {
			id,
			...variables
		})).projectArchive;
		return new ProjectArchivePayload(this._request, data);
	}
};
/**
* A fetchable CreateProject Mutation
*
* @param request - function to call the graphql client
*/
var CreateProjectMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the CreateProject mutation and return a ProjectPayload
	*
	* @param input - required input to pass to createProject
	* @param variables - variables without 'input' to pass into the CreateProjectMutation
	* @returns parsed response from CreateProjectMutation
	*/
	async fetch(input, variables) {
		const data = (await this._request(CreateProjectDocument.toString(), {
			input,
			...variables
		})).projectCreate;
		return new ProjectPayload(this._request, data);
	}
};
/**
* A fetchable DeleteProject Mutation
*
* @param request - function to call the graphql client
*/
var DeleteProjectMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the DeleteProject mutation and return a ProjectArchivePayload
	*
	* @param id - required id to pass to deleteProject
	* @returns parsed response from DeleteProjectMutation
	*/
	async fetch(id) {
		const data = (await this._request(DeleteProjectDocument.toString(), { id })).projectDelete;
		return new ProjectArchivePayload(this._request, data);
	}
};
/**
* A fetchable ProjectExternalSyncDisable Mutation
*
* @param request - function to call the graphql client
*/
var ProjectExternalSyncDisableMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the ProjectExternalSyncDisable mutation and return a ProjectPayload
	*
	* @param projectId - required projectId to pass to projectExternalSyncDisable
	* @param syncSource - required syncSource to pass to projectExternalSyncDisable
	* @returns parsed response from ProjectExternalSyncDisableMutation
	*/
	async fetch(projectId, syncSource) {
		const data = (await this._request(ProjectExternalSyncDisableDocument.toString(), {
			projectId,
			syncSource
		})).projectExternalSyncDisable;
		return new ProjectPayload(this._request, data);
	}
};
/**
* A fetchable CreateProjectLabel Mutation
*
* @param request - function to call the graphql client
*/
var CreateProjectLabelMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the CreateProjectLabel mutation and return a ProjectLabelPayload
	*
	* @param input - required input to pass to createProjectLabel
	* @returns parsed response from CreateProjectLabelMutation
	*/
	async fetch(input) {
		const data = (await this._request(CreateProjectLabelDocument.toString(), { input })).projectLabelCreate;
		return new ProjectLabelPayload(this._request, data);
	}
};
/**
* A fetchable DeleteProjectLabel Mutation
*
* @param request - function to call the graphql client
*/
var DeleteProjectLabelMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the DeleteProjectLabel mutation and return a DeletePayload
	*
	* @param id - required id to pass to deleteProjectLabel
	* @returns parsed response from DeleteProjectLabelMutation
	*/
	async fetch(id) {
		const data = (await this._request(DeleteProjectLabelDocument.toString(), { id })).projectLabelDelete;
		return new DeletePayload(this._request, data);
	}
};
/**
* A fetchable ProjectLabelRestore Mutation
*
* @param request - function to call the graphql client
*/
var ProjectLabelRestoreMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the ProjectLabelRestore mutation and return a ProjectLabelPayload
	*
	* @param id - required id to pass to projectLabelRestore
	* @returns parsed response from ProjectLabelRestoreMutation
	*/
	async fetch(id) {
		const data = (await this._request(ProjectLabelRestoreDocument.toString(), { id })).projectLabelRestore;
		return new ProjectLabelPayload(this._request, data);
	}
};
/**
* A fetchable ProjectLabelRetire Mutation
*
* @param request - function to call the graphql client
*/
var ProjectLabelRetireMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the ProjectLabelRetire mutation and return a ProjectLabelPayload
	*
	* @param id - required id to pass to projectLabelRetire
	* @returns parsed response from ProjectLabelRetireMutation
	*/
	async fetch(id) {
		const data = (await this._request(ProjectLabelRetireDocument.toString(), { id })).projectLabelRetire;
		return new ProjectLabelPayload(this._request, data);
	}
};
/**
* A fetchable UpdateProjectLabel Mutation
*
* @param request - function to call the graphql client
*/
var UpdateProjectLabelMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the UpdateProjectLabel mutation and return a ProjectLabelPayload
	*
	* @param id - required id to pass to updateProjectLabel
	* @param input - required input to pass to updateProjectLabel
	* @returns parsed response from UpdateProjectLabelMutation
	*/
	async fetch(id, input) {
		const data = (await this._request(UpdateProjectLabelDocument.toString(), {
			id,
			input
		})).projectLabelUpdate;
		return new ProjectLabelPayload(this._request, data);
	}
};
/**
* A fetchable CreateProjectMilestone Mutation
*
* @param request - function to call the graphql client
*/
var CreateProjectMilestoneMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the CreateProjectMilestone mutation and return a ProjectMilestonePayload
	*
	* @param input - required input to pass to createProjectMilestone
	* @returns parsed response from CreateProjectMilestoneMutation
	*/
	async fetch(input) {
		const data = (await this._request(CreateProjectMilestoneDocument.toString(), { input })).projectMilestoneCreate;
		return new ProjectMilestonePayload(this._request, data);
	}
};
/**
* A fetchable DeleteProjectMilestone Mutation
*
* @param request - function to call the graphql client
*/
var DeleteProjectMilestoneMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the DeleteProjectMilestone mutation and return a DeletePayload
	*
	* @param id - required id to pass to deleteProjectMilestone
	* @returns parsed response from DeleteProjectMilestoneMutation
	*/
	async fetch(id) {
		const data = (await this._request(DeleteProjectMilestoneDocument.toString(), { id })).projectMilestoneDelete;
		return new DeletePayload(this._request, data);
	}
};
/**
* A fetchable UpdateProjectMilestone Mutation
*
* @param request - function to call the graphql client
*/
var UpdateProjectMilestoneMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the UpdateProjectMilestone mutation and return a ProjectMilestonePayload
	*
	* @param id - required id to pass to updateProjectMilestone
	* @param input - required input to pass to updateProjectMilestone
	* @returns parsed response from UpdateProjectMilestoneMutation
	*/
	async fetch(id, input) {
		const data = (await this._request(UpdateProjectMilestoneDocument.toString(), {
			id,
			input
		})).projectMilestoneUpdate;
		return new ProjectMilestonePayload(this._request, data);
	}
};
/**
* A fetchable CreateProjectRelation Mutation
*
* @param request - function to call the graphql client
*/
var CreateProjectRelationMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the CreateProjectRelation mutation and return a ProjectRelationPayload
	*
	* @param input - required input to pass to createProjectRelation
	* @returns parsed response from CreateProjectRelationMutation
	*/
	async fetch(input) {
		const data = (await this._request(CreateProjectRelationDocument.toString(), { input })).projectRelationCreate;
		return new ProjectRelationPayload(this._request, data);
	}
};
/**
* A fetchable DeleteProjectRelation Mutation
*
* @param request - function to call the graphql client
*/
var DeleteProjectRelationMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the DeleteProjectRelation mutation and return a DeletePayload
	*
	* @param id - required id to pass to deleteProjectRelation
	* @returns parsed response from DeleteProjectRelationMutation
	*/
	async fetch(id) {
		const data = (await this._request(DeleteProjectRelationDocument.toString(), { id })).projectRelationDelete;
		return new DeletePayload(this._request, data);
	}
};
/**
* A fetchable UpdateProjectRelation Mutation
*
* @param request - function to call the graphql client
*/
var UpdateProjectRelationMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the UpdateProjectRelation mutation and return a ProjectRelationPayload
	*
	* @param id - required id to pass to updateProjectRelation
	* @param input - required input to pass to updateProjectRelation
	* @returns parsed response from UpdateProjectRelationMutation
	*/
	async fetch(id, input) {
		const data = (await this._request(UpdateProjectRelationDocument.toString(), {
			id,
			input
		})).projectRelationUpdate;
		return new ProjectRelationPayload(this._request, data);
	}
};
/**
* A fetchable ProjectRemoveLabel Mutation
*
* @param request - function to call the graphql client
*/
var ProjectRemoveLabelMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the ProjectRemoveLabel mutation and return a ProjectPayload
	*
	* @param id - required id to pass to projectRemoveLabel
	* @param labelId - required labelId to pass to projectRemoveLabel
	* @returns parsed response from ProjectRemoveLabelMutation
	*/
	async fetch(id, labelId) {
		const data = (await this._request(ProjectRemoveLabelDocument.toString(), {
			id,
			labelId
		})).projectRemoveLabel;
		return new ProjectPayload(this._request, data);
	}
};
/**
* A fetchable ArchiveProjectStatus Mutation
*
* @param request - function to call the graphql client
*/
var ArchiveProjectStatusMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the ArchiveProjectStatus mutation and return a ProjectStatusArchivePayload
	*
	* @param id - required id to pass to archiveProjectStatus
	* @returns parsed response from ArchiveProjectStatusMutation
	*/
	async fetch(id) {
		const data = (await this._request(ArchiveProjectStatusDocument.toString(), { id })).projectStatusArchive;
		return new ProjectStatusArchivePayload(this._request, data);
	}
};
/**
* A fetchable CreateProjectStatus Mutation
*
* @param request - function to call the graphql client
*/
var CreateProjectStatusMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the CreateProjectStatus mutation and return a ProjectStatusPayload
	*
	* @param input - required input to pass to createProjectStatus
	* @returns parsed response from CreateProjectStatusMutation
	*/
	async fetch(input) {
		const data = (await this._request(CreateProjectStatusDocument.toString(), { input })).projectStatusCreate;
		return new ProjectStatusPayload(this._request, data);
	}
};
/**
* A fetchable UnarchiveProjectStatus Mutation
*
* @param request - function to call the graphql client
*/
var UnarchiveProjectStatusMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the UnarchiveProjectStatus mutation and return a ProjectStatusArchivePayload
	*
	* @param id - required id to pass to unarchiveProjectStatus
	* @returns parsed response from UnarchiveProjectStatusMutation
	*/
	async fetch(id) {
		const data = (await this._request(UnarchiveProjectStatusDocument.toString(), { id })).projectStatusUnarchive;
		return new ProjectStatusArchivePayload(this._request, data);
	}
};
/**
* A fetchable UpdateProjectStatus Mutation
*
* @param request - function to call the graphql client
*/
var UpdateProjectStatusMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the UpdateProjectStatus mutation and return a ProjectStatusPayload
	*
	* @param id - required id to pass to updateProjectStatus
	* @param input - required input to pass to updateProjectStatus
	* @returns parsed response from UpdateProjectStatusMutation
	*/
	async fetch(id, input) {
		const data = (await this._request(UpdateProjectStatusDocument.toString(), {
			id,
			input
		})).projectStatusUpdate;
		return new ProjectStatusPayload(this._request, data);
	}
};
/**
* A fetchable UnarchiveProject Mutation
*
* @param request - function to call the graphql client
*/
var UnarchiveProjectMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the UnarchiveProject mutation and return a ProjectArchivePayload
	*
	* @param id - required id to pass to unarchiveProject
	* @returns parsed response from UnarchiveProjectMutation
	*/
	async fetch(id) {
		const data = (await this._request(UnarchiveProjectDocument.toString(), { id })).projectUnarchive;
		return new ProjectArchivePayload(this._request, data);
	}
};
/**
* A fetchable UpdateProject Mutation
*
* @param request - function to call the graphql client
*/
var UpdateProjectMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the UpdateProject mutation and return a ProjectPayload
	*
	* @param id - required id to pass to updateProject
	* @param input - required input to pass to updateProject
	* @returns parsed response from UpdateProjectMutation
	*/
	async fetch(id, input) {
		const data = (await this._request(UpdateProjectDocument.toString(), {
			id,
			input
		})).projectUpdate;
		return new ProjectPayload(this._request, data);
	}
};
/**
* A fetchable ArchiveProjectUpdate Mutation
*
* @param request - function to call the graphql client
*/
var ArchiveProjectUpdateMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the ArchiveProjectUpdate mutation and return a ProjectUpdateArchivePayload
	*
	* @param id - required id to pass to archiveProjectUpdate
	* @returns parsed response from ArchiveProjectUpdateMutation
	*/
	async fetch(id) {
		const data = (await this._request(ArchiveProjectUpdateDocument.toString(), { id })).projectUpdateArchive;
		return new ProjectUpdateArchivePayload(this._request, data);
	}
};
/**
* A fetchable CreateProjectUpdate Mutation
*
* @param request - function to call the graphql client
*/
var CreateProjectUpdateMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the CreateProjectUpdate mutation and return a ProjectUpdatePayload
	*
	* @param input - required input to pass to createProjectUpdate
	* @returns parsed response from CreateProjectUpdateMutation
	*/
	async fetch(input) {
		const data = (await this._request(CreateProjectUpdateDocument.toString(), { input })).projectUpdateCreate;
		return new ProjectUpdatePayload(this._request, data);
	}
};
/**
* A fetchable DeleteProjectUpdate Mutation
*
* @param request - function to call the graphql client
*/
var DeleteProjectUpdateMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the DeleteProjectUpdate mutation and return a DeletePayload
	*
	* @param id - required id to pass to deleteProjectUpdate
	* @returns parsed response from DeleteProjectUpdateMutation
	*/
	async fetch(id) {
		const data = (await this._request(DeleteProjectUpdateDocument.toString(), { id })).projectUpdateDelete;
		return new DeletePayload(this._request, data);
	}
};
/**
* A fetchable UnarchiveProjectUpdate Mutation
*
* @param request - function to call the graphql client
*/
var UnarchiveProjectUpdateMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the UnarchiveProjectUpdate mutation and return a ProjectUpdateArchivePayload
	*
	* @param id - required id to pass to unarchiveProjectUpdate
	* @returns parsed response from UnarchiveProjectUpdateMutation
	*/
	async fetch(id) {
		const data = (await this._request(UnarchiveProjectUpdateDocument.toString(), { id })).projectUpdateUnarchive;
		return new ProjectUpdateArchivePayload(this._request, data);
	}
};
/**
* A fetchable UpdateProjectUpdate Mutation
*
* @param request - function to call the graphql client
*/
var UpdateProjectUpdateMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the UpdateProjectUpdate mutation and return a ProjectUpdatePayload
	*
	* @param id - required id to pass to updateProjectUpdate
	* @param input - required input to pass to updateProjectUpdate
	* @returns parsed response from UpdateProjectUpdateMutation
	*/
	async fetch(id, input) {
		const data = (await this._request(UpdateProjectUpdateDocument.toString(), {
			id,
			input
		})).projectUpdateUpdate;
		return new ProjectUpdatePayload(this._request, data);
	}
};
/**
* A fetchable CreatePushSubscription Mutation
*
* @param request - function to call the graphql client
*/
var CreatePushSubscriptionMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the CreatePushSubscription mutation and return a PushSubscriptionPayload
	*
	* @param input - required input to pass to createPushSubscription
	* @returns parsed response from CreatePushSubscriptionMutation
	*/
	async fetch(input) {
		const data = (await this._request(CreatePushSubscriptionDocument.toString(), { input })).pushSubscriptionCreate;
		return new PushSubscriptionPayload(this._request, data);
	}
};
/**
* A fetchable DeletePushSubscription Mutation
*
* @param request - function to call the graphql client
*/
var DeletePushSubscriptionMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the DeletePushSubscription mutation and return a PushSubscriptionPayload
	*
	* @param id - required id to pass to deletePushSubscription
	* @returns parsed response from DeletePushSubscriptionMutation
	*/
	async fetch(id) {
		const data = (await this._request(DeletePushSubscriptionDocument.toString(), { id })).pushSubscriptionDelete;
		return new PushSubscriptionPayload(this._request, data);
	}
};
/**
* A fetchable CreateReaction Mutation
*
* @param request - function to call the graphql client
*/
var CreateReactionMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the CreateReaction mutation and return a ReactionPayload
	*
	* @param input - required input to pass to createReaction
	* @returns parsed response from CreateReactionMutation
	*/
	async fetch(input) {
		const data = (await this._request(CreateReactionDocument.toString(), { input })).reactionCreate;
		return new ReactionPayload(this._request, data);
	}
};
/**
* A fetchable DeleteReaction Mutation
*
* @param request - function to call the graphql client
*/
var DeleteReactionMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the DeleteReaction mutation and return a DeletePayload
	*
	* @param id - required id to pass to deleteReaction
	* @returns parsed response from DeleteReactionMutation
	*/
	async fetch(id) {
		const data = (await this._request(DeleteReactionDocument.toString(), { id })).reactionDelete;
		return new DeletePayload(this._request, data);
	}
};
/**
* A fetchable RefreshGoogleSheetsData Mutation
*
* @param request - function to call the graphql client
*/
var RefreshGoogleSheetsDataMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the RefreshGoogleSheetsData mutation and return a IntegrationPayload
	*
	* @param id - required id to pass to refreshGoogleSheetsData
	* @param variables - variables without 'id' to pass into the RefreshGoogleSheetsDataMutation
	* @returns parsed response from RefreshGoogleSheetsDataMutation
	*/
	async fetch(id, variables) {
		const data = (await this._request(RefreshGoogleSheetsDataDocument.toString(), {
			id,
			...variables
		})).refreshGoogleSheetsData;
		return new IntegrationPayload(this._request, data);
	}
};
/**
* A fetchable ResendOrganizationInvite Mutation
*
* @param request - function to call the graphql client
*/
var ResendOrganizationInviteMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the ResendOrganizationInvite mutation and return a DeletePayload
	*
	* @param id - required id to pass to resendOrganizationInvite
	* @returns parsed response from ResendOrganizationInviteMutation
	*/
	async fetch(id) {
		const data = (await this._request(ResendOrganizationInviteDocument.toString(), { id })).resendOrganizationInvite;
		return new DeletePayload(this._request, data);
	}
};
/**
* A fetchable ResendOrganizationInviteByEmail Mutation
*
* @param request - function to call the graphql client
*/
var ResendOrganizationInviteByEmailMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the ResendOrganizationInviteByEmail mutation and return a DeletePayload
	*
	* @param email - required email to pass to resendOrganizationInviteByEmail
	* @returns parsed response from ResendOrganizationInviteByEmailMutation
	*/
	async fetch(email) {
		const data = (await this._request(ResendOrganizationInviteByEmailDocument.toString(), { email })).resendOrganizationInviteByEmail;
		return new DeletePayload(this._request, data);
	}
};
/**
* A fetchable ArchiveRoadmap Mutation
*
* @param request - function to call the graphql client
*/
var ArchiveRoadmapMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the ArchiveRoadmap mutation and return a RoadmapArchivePayload
	*
	* @param id - required id to pass to archiveRoadmap
	* @returns parsed response from ArchiveRoadmapMutation
	*/
	async fetch(id) {
		const data = (await this._request(ArchiveRoadmapDocument.toString(), { id })).roadmapArchive;
		return new RoadmapArchivePayload(this._request, data);
	}
};
/**
* A fetchable CreateRoadmap Mutation
*
* @param request - function to call the graphql client
*/
var CreateRoadmapMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the CreateRoadmap mutation and return a RoadmapPayload
	*
	* @param input - required input to pass to createRoadmap
	* @returns parsed response from CreateRoadmapMutation
	*/
	async fetch(input) {
		const data = (await this._request(CreateRoadmapDocument.toString(), { input })).roadmapCreate;
		return new RoadmapPayload(this._request, data);
	}
};
/**
* A fetchable DeleteRoadmap Mutation
*
* @param request - function to call the graphql client
*/
var DeleteRoadmapMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the DeleteRoadmap mutation and return a DeletePayload
	*
	* @param id - required id to pass to deleteRoadmap
	* @returns parsed response from DeleteRoadmapMutation
	*/
	async fetch(id) {
		const data = (await this._request(DeleteRoadmapDocument.toString(), { id })).roadmapDelete;
		return new DeletePayload(this._request, data);
	}
};
/**
* A fetchable CreateRoadmapToProject Mutation
*
* @param request - function to call the graphql client
*/
var CreateRoadmapToProjectMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the CreateRoadmapToProject mutation and return a RoadmapToProjectPayload
	*
	* @param input - required input to pass to createRoadmapToProject
	* @returns parsed response from CreateRoadmapToProjectMutation
	*/
	async fetch(input) {
		const data = (await this._request(CreateRoadmapToProjectDocument.toString(), { input })).roadmapToProjectCreate;
		return new RoadmapToProjectPayload(this._request, data);
	}
};
/**
* A fetchable DeleteRoadmapToProject Mutation
*
* @param request - function to call the graphql client
*/
var DeleteRoadmapToProjectMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the DeleteRoadmapToProject mutation and return a DeletePayload
	*
	* @param id - required id to pass to deleteRoadmapToProject
	* @returns parsed response from DeleteRoadmapToProjectMutation
	*/
	async fetch(id) {
		const data = (await this._request(DeleteRoadmapToProjectDocument.toString(), { id })).roadmapToProjectDelete;
		return new DeletePayload(this._request, data);
	}
};
/**
* A fetchable UpdateRoadmapToProject Mutation
*
* @param request - function to call the graphql client
*/
var UpdateRoadmapToProjectMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the UpdateRoadmapToProject mutation and return a RoadmapToProjectPayload
	*
	* @param id - required id to pass to updateRoadmapToProject
	* @param input - required input to pass to updateRoadmapToProject
	* @returns parsed response from UpdateRoadmapToProjectMutation
	*/
	async fetch(id, input) {
		const data = (await this._request(UpdateRoadmapToProjectDocument.toString(), {
			id,
			input
		})).roadmapToProjectUpdate;
		return new RoadmapToProjectPayload(this._request, data);
	}
};
/**
* A fetchable UnarchiveRoadmap Mutation
*
* @param request - function to call the graphql client
*/
var UnarchiveRoadmapMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the UnarchiveRoadmap mutation and return a RoadmapArchivePayload
	*
	* @param id - required id to pass to unarchiveRoadmap
	* @returns parsed response from UnarchiveRoadmapMutation
	*/
	async fetch(id) {
		const data = (await this._request(UnarchiveRoadmapDocument.toString(), { id })).roadmapUnarchive;
		return new RoadmapArchivePayload(this._request, data);
	}
};
/**
* A fetchable UpdateRoadmap Mutation
*
* @param request - function to call the graphql client
*/
var UpdateRoadmapMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the UpdateRoadmap mutation and return a RoadmapPayload
	*
	* @param id - required id to pass to updateRoadmap
	* @param input - required input to pass to updateRoadmap
	* @returns parsed response from UpdateRoadmapMutation
	*/
	async fetch(id, input) {
		const data = (await this._request(UpdateRoadmapDocument.toString(), {
			id,
			input
		})).roadmapUpdate;
		return new RoadmapPayload(this._request, data);
	}
};
/**
* A fetchable SamlTokenUserAccountAuth Mutation
*
* @param request - function to call the graphql client
*/
var SamlTokenUserAccountAuthMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the SamlTokenUserAccountAuth mutation and return a AuthResolverResponse
	*
	* @param input - required input to pass to samlTokenUserAccountAuth
	* @returns parsed response from SamlTokenUserAccountAuthMutation
	*/
	async fetch(input) {
		const data = (await this._request(SamlTokenUserAccountAuthDocument.toString(), { input })).samlTokenUserAccountAuth;
		return new AuthResolverResponse(this._request, data);
	}
};
/**
* A fetchable CreateTeam Mutation
*
* @param request - function to call the graphql client
*/
var CreateTeamMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the CreateTeam mutation and return a TeamPayload
	*
	* @param input - required input to pass to createTeam
	* @param variables - variables without 'input' to pass into the CreateTeamMutation
	* @returns parsed response from CreateTeamMutation
	*/
	async fetch(input, variables) {
		const data = (await this._request(CreateTeamDocument.toString(), {
			input,
			...variables
		})).teamCreate;
		return new TeamPayload(this._request, data);
	}
};
/**
* A fetchable DeleteTeamCycles Mutation
*
* @param request - function to call the graphql client
*/
var DeleteTeamCyclesMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the DeleteTeamCycles mutation and return a TeamPayload
	*
	* @param id - required id to pass to deleteTeamCycles
	* @returns parsed response from DeleteTeamCyclesMutation
	*/
	async fetch(id) {
		const data = (await this._request(DeleteTeamCyclesDocument.toString(), { id })).teamCyclesDelete;
		return new TeamPayload(this._request, data);
	}
};
/**
* A fetchable DeleteTeam Mutation
*
* @param request - function to call the graphql client
*/
var DeleteTeamMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the DeleteTeam mutation and return a DeletePayload
	*
	* @param id - required id to pass to deleteTeam
	* @returns parsed response from DeleteTeamMutation
	*/
	async fetch(id) {
		const data = (await this._request(DeleteTeamDocument.toString(), { id })).teamDelete;
		return new DeletePayload(this._request, data);
	}
};
/**
* A fetchable DeleteTeamKey Mutation
*
* @param request - function to call the graphql client
*/
var DeleteTeamKeyMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the DeleteTeamKey mutation and return a DeletePayload
	*
	* @param id - required id to pass to deleteTeamKey
	* @returns parsed response from DeleteTeamKeyMutation
	*/
	async fetch(id) {
		const data = (await this._request(DeleteTeamKeyDocument.toString(), { id })).teamKeyDelete;
		return new DeletePayload(this._request, data);
	}
};
/**
* A fetchable CreateTeamMembership Mutation
*
* @param request - function to call the graphql client
*/
var CreateTeamMembershipMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the CreateTeamMembership mutation and return a TeamMembershipPayload
	*
	* @param input - required input to pass to createTeamMembership
	* @returns parsed response from CreateTeamMembershipMutation
	*/
	async fetch(input) {
		const data = (await this._request(CreateTeamMembershipDocument.toString(), { input })).teamMembershipCreate;
		return new TeamMembershipPayload(this._request, data);
	}
};
/**
* A fetchable DeleteTeamMembership Mutation
*
* @param request - function to call the graphql client
*/
var DeleteTeamMembershipMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the DeleteTeamMembership mutation and return a DeletePayload
	*
	* @param id - required id to pass to deleteTeamMembership
	* @param variables - variables without 'id' to pass into the DeleteTeamMembershipMutation
	* @returns parsed response from DeleteTeamMembershipMutation
	*/
	async fetch(id, variables) {
		const data = (await this._request(DeleteTeamMembershipDocument.toString(), {
			id,
			...variables
		})).teamMembershipDelete;
		return new DeletePayload(this._request, data);
	}
};
/**
* A fetchable UpdateTeamMembership Mutation
*
* @param request - function to call the graphql client
*/
var UpdateTeamMembershipMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the UpdateTeamMembership mutation and return a TeamMembershipPayload
	*
	* @param id - required id to pass to updateTeamMembership
	* @param input - required input to pass to updateTeamMembership
	* @returns parsed response from UpdateTeamMembershipMutation
	*/
	async fetch(id, input) {
		const data = (await this._request(UpdateTeamMembershipDocument.toString(), {
			id,
			input
		})).teamMembershipUpdate;
		return new TeamMembershipPayload(this._request, data);
	}
};
/**
* A fetchable UnarchiveTeam Mutation
*
* @param request - function to call the graphql client
*/
var UnarchiveTeamMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the UnarchiveTeam mutation and return a TeamArchivePayload
	*
	* @param id - required id to pass to unarchiveTeam
	* @returns parsed response from UnarchiveTeamMutation
	*/
	async fetch(id) {
		const data = (await this._request(UnarchiveTeamDocument.toString(), { id })).teamUnarchive;
		return new TeamArchivePayload(this._request, data);
	}
};
/**
* A fetchable UpdateTeam Mutation
*
* @param request - function to call the graphql client
*/
var UpdateTeamMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the UpdateTeam mutation and return a TeamPayload
	*
	* @param id - required id to pass to updateTeam
	* @param input - required input to pass to updateTeam
	* @param variables - variables without 'id', 'input' to pass into the UpdateTeamMutation
	* @returns parsed response from UpdateTeamMutation
	*/
	async fetch(id, input, variables) {
		const data = (await this._request(UpdateTeamDocument.toString(), {
			id,
			input,
			...variables
		})).teamUpdate;
		return new TeamPayload(this._request, data);
	}
};
/**
* A fetchable CreateTemplate Mutation
*
* @param request - function to call the graphql client
*/
var CreateTemplateMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the CreateTemplate mutation and return a TemplatePayload
	*
	* @param input - required input to pass to createTemplate
	* @returns parsed response from CreateTemplateMutation
	*/
	async fetch(input) {
		const data = (await this._request(CreateTemplateDocument.toString(), { input })).templateCreate;
		return new TemplatePayload(this._request, data);
	}
};
/**
* A fetchable DeleteTemplate Mutation
*
* @param request - function to call the graphql client
*/
var DeleteTemplateMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the DeleteTemplate mutation and return a DeletePayload
	*
	* @param id - required id to pass to deleteTemplate
	* @returns parsed response from DeleteTemplateMutation
	*/
	async fetch(id) {
		const data = (await this._request(DeleteTemplateDocument.toString(), { id })).templateDelete;
		return new DeletePayload(this._request, data);
	}
};
/**
* A fetchable UpdateTemplate Mutation
*
* @param request - function to call the graphql client
*/
var UpdateTemplateMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the UpdateTemplate mutation and return a TemplatePayload
	*
	* @param id - required id to pass to updateTemplate
	* @param input - required input to pass to updateTemplate
	* @returns parsed response from UpdateTemplateMutation
	*/
	async fetch(id, input) {
		const data = (await this._request(UpdateTemplateDocument.toString(), {
			id,
			input
		})).templateUpdate;
		return new TemplatePayload(this._request, data);
	}
};
/**
* A fetchable CreateTimeSchedule Mutation
*
* @param request - function to call the graphql client
*/
var CreateTimeScheduleMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the CreateTimeSchedule mutation and return a TimeSchedulePayload
	*
	* @param input - required input to pass to createTimeSchedule
	* @returns parsed response from CreateTimeScheduleMutation
	*/
	async fetch(input) {
		const data = (await this._request(CreateTimeScheduleDocument.toString(), { input })).timeScheduleCreate;
		return new TimeSchedulePayload(this._request, data);
	}
};
/**
* A fetchable DeleteTimeSchedule Mutation
*
* @param request - function to call the graphql client
*/
var DeleteTimeScheduleMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the DeleteTimeSchedule mutation and return a DeletePayload
	*
	* @param id - required id to pass to deleteTimeSchedule
	* @returns parsed response from DeleteTimeScheduleMutation
	*/
	async fetch(id) {
		const data = (await this._request(DeleteTimeScheduleDocument.toString(), { id })).timeScheduleDelete;
		return new DeletePayload(this._request, data);
	}
};
/**
* A fetchable TimeScheduleRefreshIntegrationSchedule Mutation
*
* @param request - function to call the graphql client
*/
var TimeScheduleRefreshIntegrationScheduleMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the TimeScheduleRefreshIntegrationSchedule mutation and return a TimeSchedulePayload
	*
	* @param id - required id to pass to timeScheduleRefreshIntegrationSchedule
	* @returns parsed response from TimeScheduleRefreshIntegrationScheduleMutation
	*/
	async fetch(id) {
		const data = (await this._request(TimeScheduleRefreshIntegrationScheduleDocument.toString(), { id })).timeScheduleRefreshIntegrationSchedule;
		return new TimeSchedulePayload(this._request, data);
	}
};
/**
* A fetchable UpdateTimeSchedule Mutation
*
* @param request - function to call the graphql client
*/
var UpdateTimeScheduleMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the UpdateTimeSchedule mutation and return a TimeSchedulePayload
	*
	* @param id - required id to pass to updateTimeSchedule
	* @param input - required input to pass to updateTimeSchedule
	* @returns parsed response from UpdateTimeScheduleMutation
	*/
	async fetch(id, input) {
		const data = (await this._request(UpdateTimeScheduleDocument.toString(), {
			id,
			input
		})).timeScheduleUpdate;
		return new TimeSchedulePayload(this._request, data);
	}
};
/**
* A fetchable TimeScheduleUpsertExternal Mutation
*
* @param request - function to call the graphql client
*/
var TimeScheduleUpsertExternalMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the TimeScheduleUpsertExternal mutation and return a TimeSchedulePayload
	*
	* @param externalId - required externalId to pass to timeScheduleUpsertExternal
	* @param input - required input to pass to timeScheduleUpsertExternal
	* @returns parsed response from TimeScheduleUpsertExternalMutation
	*/
	async fetch(externalId, input) {
		const data = (await this._request(TimeScheduleUpsertExternalDocument.toString(), {
			externalId,
			input
		})).timeScheduleUpsertExternal;
		return new TimeSchedulePayload(this._request, data);
	}
};
/**
* A fetchable CreateTriageResponsibility Mutation
*
* @param request - function to call the graphql client
*/
var CreateTriageResponsibilityMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the CreateTriageResponsibility mutation and return a TriageResponsibilityPayload
	*
	* @param input - required input to pass to createTriageResponsibility
	* @returns parsed response from CreateTriageResponsibilityMutation
	*/
	async fetch(input) {
		const data = (await this._request(CreateTriageResponsibilityDocument.toString(), { input })).triageResponsibilityCreate;
		return new TriageResponsibilityPayload(this._request, data);
	}
};
/**
* A fetchable DeleteTriageResponsibility Mutation
*
* @param request - function to call the graphql client
*/
var DeleteTriageResponsibilityMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the DeleteTriageResponsibility mutation and return a DeletePayload
	*
	* @param id - required id to pass to deleteTriageResponsibility
	* @returns parsed response from DeleteTriageResponsibilityMutation
	*/
	async fetch(id) {
		const data = (await this._request(DeleteTriageResponsibilityDocument.toString(), { id })).triageResponsibilityDelete;
		return new DeletePayload(this._request, data);
	}
};
/**
* A fetchable UpdateTriageResponsibility Mutation
*
* @param request - function to call the graphql client
*/
var UpdateTriageResponsibilityMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the UpdateTriageResponsibility mutation and return a TriageResponsibilityPayload
	*
	* @param id - required id to pass to updateTriageResponsibility
	* @param input - required input to pass to updateTriageResponsibility
	* @returns parsed response from UpdateTriageResponsibilityMutation
	*/
	async fetch(id, input) {
		const data = (await this._request(UpdateTriageResponsibilityDocument.toString(), {
			id,
			input
		})).triageResponsibilityUpdate;
		return new TriageResponsibilityPayload(this._request, data);
	}
};
/**
* A fetchable UserChangeRole Mutation
*
* @param request - function to call the graphql client
*/
var UserChangeRoleMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the UserChangeRole mutation and return a UserAdminPayload
	*
	* @param id - required id to pass to userChangeRole
	* @param role - required role to pass to userChangeRole
	* @returns parsed response from UserChangeRoleMutation
	*/
	async fetch(id, role) {
		const data = (await this._request(UserChangeRoleDocument.toString(), {
			id,
			role
		})).userChangeRole;
		return new UserAdminPayload(this._request, data);
	}
};
/**
* A fetchable UserDemoteAdmin Mutation
*
* @param request - function to call the graphql client
*/
var UserDemoteAdminMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the UserDemoteAdmin mutation and return a UserAdminPayload
	*
	* @param id - required id to pass to userDemoteAdmin
	* @returns parsed response from UserDemoteAdminMutation
	*/
	async fetch(id) {
		const data = (await this._request(UserDemoteAdminDocument.toString(), { id })).userDemoteAdmin;
		return new UserAdminPayload(this._request, data);
	}
};
/**
* A fetchable UserDemoteMember Mutation
*
* @param request - function to call the graphql client
*/
var UserDemoteMemberMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the UserDemoteMember mutation and return a UserAdminPayload
	*
	* @param id - required id to pass to userDemoteMember
	* @returns parsed response from UserDemoteMemberMutation
	*/
	async fetch(id) {
		const data = (await this._request(UserDemoteMemberDocument.toString(), { id })).userDemoteMember;
		return new UserAdminPayload(this._request, data);
	}
};
/**
* A fetchable UserDiscordConnect Mutation
*
* @param request - function to call the graphql client
*/
var UserDiscordConnectMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the UserDiscordConnect mutation and return a UserPayload
	*
	* @param code - required code to pass to userDiscordConnect
	* @param redirectUri - required redirectUri to pass to userDiscordConnect
	* @returns parsed response from UserDiscordConnectMutation
	*/
	async fetch(code, redirectUri) {
		const data = (await this._request(UserDiscordConnectDocument.toString(), {
			code,
			redirectUri
		})).userDiscordConnect;
		return new UserPayload(this._request, data);
	}
};
/**
* A fetchable UserExternalUserDisconnect Mutation
*
* @param request - function to call the graphql client
*/
var UserExternalUserDisconnectMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the UserExternalUserDisconnect mutation and return a UserPayload
	*
	* @param service - required service to pass to userExternalUserDisconnect
	* @returns parsed response from UserExternalUserDisconnectMutation
	*/
	async fetch(service) {
		const data = (await this._request(UserExternalUserDisconnectDocument.toString(), { service })).userExternalUserDisconnect;
		return new UserPayload(this._request, data);
	}
};
/**
* A fetchable UpdateUserFlag Mutation
*
* @param request - function to call the graphql client
*/
var UpdateUserFlagMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the UpdateUserFlag mutation and return a UserSettingsFlagPayload
	*
	* @param flag - required flag to pass to updateUserFlag
	* @param operation - required operation to pass to updateUserFlag
	* @returns parsed response from UpdateUserFlagMutation
	*/
	async fetch(flag, operation) {
		const data = (await this._request(UpdateUserFlagDocument.toString(), {
			flag,
			operation
		})).userFlagUpdate;
		return new UserSettingsFlagPayload(this._request, data);
	}
};
/**
* A fetchable UserPromoteAdmin Mutation
*
* @param request - function to call the graphql client
*/
var UserPromoteAdminMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the UserPromoteAdmin mutation and return a UserAdminPayload
	*
	* @param id - required id to pass to userPromoteAdmin
	* @returns parsed response from UserPromoteAdminMutation
	*/
	async fetch(id) {
		const data = (await this._request(UserPromoteAdminDocument.toString(), { id })).userPromoteAdmin;
		return new UserAdminPayload(this._request, data);
	}
};
/**
* A fetchable UserPromoteMember Mutation
*
* @param request - function to call the graphql client
*/
var UserPromoteMemberMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the UserPromoteMember mutation and return a UserAdminPayload
	*
	* @param id - required id to pass to userPromoteMember
	* @returns parsed response from UserPromoteMemberMutation
	*/
	async fetch(id) {
		const data = (await this._request(UserPromoteMemberDocument.toString(), { id })).userPromoteMember;
		return new UserAdminPayload(this._request, data);
	}
};
/**
* A fetchable UserSettingsFlagsReset Mutation
*
* @param request - function to call the graphql client
*/
var UserSettingsFlagsResetMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the UserSettingsFlagsReset mutation and return a UserSettingsFlagsResetPayload
	*
	* @param variables - variables to pass into the UserSettingsFlagsResetMutation
	* @returns parsed response from UserSettingsFlagsResetMutation
	*/
	async fetch(variables) {
		const data = (await this._request(UserSettingsFlagsResetDocument.toString(), variables)).userSettingsFlagsReset;
		return new UserSettingsFlagsResetPayload(this._request, data);
	}
};
/**
* A fetchable UpdateUserSettings Mutation
*
* @param request - function to call the graphql client
*/
var UpdateUserSettingsMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the UpdateUserSettings mutation and return a UserSettingsPayload
	*
	* @param id - required id to pass to updateUserSettings
	* @param input - required input to pass to updateUserSettings
	* @returns parsed response from UpdateUserSettingsMutation
	*/
	async fetch(id, input) {
		const data = (await this._request(UpdateUserSettingsDocument.toString(), {
			id,
			input
		})).userSettingsUpdate;
		return new UserSettingsPayload(this._request, data);
	}
};
/**
* A fetchable SuspendUser Mutation
*
* @param request - function to call the graphql client
*/
var SuspendUserMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the SuspendUser mutation and return a UserAdminPayload
	*
	* @param id - required id to pass to suspendUser
	* @returns parsed response from SuspendUserMutation
	*/
	async fetch(id) {
		const data = (await this._request(SuspendUserDocument.toString(), { id })).userSuspend;
		return new UserAdminPayload(this._request, data);
	}
};
/**
* A fetchable UserUnlinkFromIdentityProvider Mutation
*
* @param request - function to call the graphql client
*/
var UserUnlinkFromIdentityProviderMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the UserUnlinkFromIdentityProvider mutation and return a UserAdminPayload
	*
	* @param id - required id to pass to userUnlinkFromIdentityProvider
	* @returns parsed response from UserUnlinkFromIdentityProviderMutation
	*/
	async fetch(id) {
		const data = (await this._request(UserUnlinkFromIdentityProviderDocument.toString(), { id })).userUnlinkFromIdentityProvider;
		return new UserAdminPayload(this._request, data);
	}
};
/**
* A fetchable UnsuspendUser Mutation
*
* @param request - function to call the graphql client
*/
var UnsuspendUserMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the UnsuspendUser mutation and return a UserAdminPayload
	*
	* @param id - required id to pass to unsuspendUser
	* @returns parsed response from UnsuspendUserMutation
	*/
	async fetch(id) {
		const data = (await this._request(UnsuspendUserDocument.toString(), { id })).userUnsuspend;
		return new UserAdminPayload(this._request, data);
	}
};
/**
* A fetchable UpdateUser Mutation
*
* @param request - function to call the graphql client
*/
var UpdateUserMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the UpdateUser mutation and return a UserPayload
	*
	* @param id - required id to pass to updateUser
	* @param input - required input to pass to updateUser
	* @returns parsed response from UpdateUserMutation
	*/
	async fetch(id, input) {
		const data = (await this._request(UpdateUserDocument.toString(), {
			id,
			input
		})).userUpdate;
		return new UserPayload(this._request, data);
	}
};
/**
* A fetchable CreateViewPreferences Mutation
*
* @param request - function to call the graphql client
*/
var CreateViewPreferencesMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the CreateViewPreferences mutation and return a ViewPreferencesPayload
	*
	* @param input - required input to pass to createViewPreferences
	* @returns parsed response from CreateViewPreferencesMutation
	*/
	async fetch(input) {
		const data = (await this._request(CreateViewPreferencesDocument.toString(), { input })).viewPreferencesCreate;
		return new ViewPreferencesPayload(this._request, data);
	}
};
/**
* A fetchable DeleteViewPreferences Mutation
*
* @param request - function to call the graphql client
*/
var DeleteViewPreferencesMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the DeleteViewPreferences mutation and return a DeletePayload
	*
	* @param id - required id to pass to deleteViewPreferences
	* @returns parsed response from DeleteViewPreferencesMutation
	*/
	async fetch(id) {
		const data = (await this._request(DeleteViewPreferencesDocument.toString(), { id })).viewPreferencesDelete;
		return new DeletePayload(this._request, data);
	}
};
/**
* A fetchable UpdateViewPreferences Mutation
*
* @param request - function to call the graphql client
*/
var UpdateViewPreferencesMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the UpdateViewPreferences mutation and return a ViewPreferencesPayload
	*
	* @param id - required id to pass to updateViewPreferences
	* @param input - required input to pass to updateViewPreferences
	* @returns parsed response from UpdateViewPreferencesMutation
	*/
	async fetch(id, input) {
		const data = (await this._request(UpdateViewPreferencesDocument.toString(), {
			id,
			input
		})).viewPreferencesUpdate;
		return new ViewPreferencesPayload(this._request, data);
	}
};
/**
* A fetchable CreateWebhook Mutation
*
* @param request - function to call the graphql client
*/
var CreateWebhookMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the CreateWebhook mutation and return a WebhookPayload
	*
	* @param input - required input to pass to createWebhook
	* @returns parsed response from CreateWebhookMutation
	*/
	async fetch(input) {
		const data = (await this._request(CreateWebhookDocument.toString(), { input })).webhookCreate;
		return new WebhookPayload(this._request, data);
	}
};
/**
* A fetchable DeleteWebhook Mutation
*
* @param request - function to call the graphql client
*/
var DeleteWebhookMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the DeleteWebhook mutation and return a DeletePayload
	*
	* @param id - required id to pass to deleteWebhook
	* @returns parsed response from DeleteWebhookMutation
	*/
	async fetch(id) {
		const data = (await this._request(DeleteWebhookDocument.toString(), { id })).webhookDelete;
		return new DeletePayload(this._request, data);
	}
};
/**
* A fetchable UpdateWebhook Mutation
*
* @param request - function to call the graphql client
*/
var UpdateWebhookMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the UpdateWebhook mutation and return a WebhookPayload
	*
	* @param id - required id to pass to updateWebhook
	* @param input - required input to pass to updateWebhook
	* @returns parsed response from UpdateWebhookMutation
	*/
	async fetch(id, input) {
		const data = (await this._request(UpdateWebhookDocument.toString(), {
			id,
			input
		})).webhookUpdate;
		return new WebhookPayload(this._request, data);
	}
};
/**
* A fetchable ArchiveWorkflowState Mutation
*
* @param request - function to call the graphql client
*/
var ArchiveWorkflowStateMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the ArchiveWorkflowState mutation and return a WorkflowStateArchivePayload
	*
	* @param id - required id to pass to archiveWorkflowState
	* @returns parsed response from ArchiveWorkflowStateMutation
	*/
	async fetch(id) {
		const data = (await this._request(ArchiveWorkflowStateDocument.toString(), { id })).workflowStateArchive;
		return new WorkflowStateArchivePayload(this._request, data);
	}
};
/**
* A fetchable CreateWorkflowState Mutation
*
* @param request - function to call the graphql client
*/
var CreateWorkflowStateMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the CreateWorkflowState mutation and return a WorkflowStatePayload
	*
	* @param input - required input to pass to createWorkflowState
	* @returns parsed response from CreateWorkflowStateMutation
	*/
	async fetch(input) {
		const data = (await this._request(CreateWorkflowStateDocument.toString(), { input })).workflowStateCreate;
		return new WorkflowStatePayload(this._request, data);
	}
};
/**
* A fetchable UpdateWorkflowState Mutation
*
* @param request - function to call the graphql client
*/
var UpdateWorkflowStateMutation = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the UpdateWorkflowState mutation and return a WorkflowStatePayload
	*
	* @param id - required id to pass to updateWorkflowState
	* @param input - required input to pass to updateWorkflowState
	* @returns parsed response from UpdateWorkflowStateMutation
	*/
	async fetch(id, input) {
		const data = (await this._request(UpdateWorkflowStateDocument.toString(), {
			id,
			input
		})).workflowStateUpdate;
		return new WorkflowStatePayload(this._request, data);
	}
};
/**
* A fetchable AgentSession_Activities Query
*
* @param request - function to call the graphql client
* @param id - required id to pass to agentSession
* @param variables - variables without 'id' to pass into the AgentSession_ActivitiesQuery
*/
var AgentSession_ActivitiesQuery = class extends Request {
	_id;
	_variables;
	constructor(request, id, variables) {
		super(request);
		this._id = id;
		this._variables = variables;
	}
	/**
	* Call the AgentSession_Activities query and return a AgentActivityConnection
	*
	* @param variables - variables without 'id' to pass into the AgentSession_ActivitiesQuery
	* @returns parsed response from AgentSession_ActivitiesQuery
	*/
	async fetch(variables) {
		const data = (await this._request(AgentSession_ActivitiesDocument.toString(), {
			id: this._id,
			...this._variables,
			...variables
		})).agentSession.activities;
		return new AgentActivityConnection(this._request, (connection) => this.fetch(defaultConnection({
			...this._variables,
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable AttachmentIssue_Attachments Query
*
* @param request - function to call the graphql client
* @param id - required id to pass to attachmentIssue
* @param variables - variables without 'id' to pass into the AttachmentIssue_AttachmentsQuery
*/
var AttachmentIssue_AttachmentsQuery = class extends Request {
	_id;
	_variables;
	constructor(request, id, variables) {
		super(request);
		this._id = id;
		this._variables = variables;
	}
	/**
	* Call the AttachmentIssue_Attachments query and return a AttachmentConnection
	*
	* @param variables - variables without 'id' to pass into the AttachmentIssue_AttachmentsQuery
	* @returns parsed response from AttachmentIssue_AttachmentsQuery
	*/
	async fetch(variables) {
		const data = (await this._request(AttachmentIssue_AttachmentsDocument.toString(), {
			id: this._id,
			...this._variables,
			...variables
		})).attachmentIssue.attachments;
		return new AttachmentConnection(this._request, (connection) => this.fetch(defaultConnection({
			...this._variables,
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable AttachmentIssue_BotActor Query
*
* @param request - function to call the graphql client
* @param id - required id to pass to attachmentIssue
*/
var AttachmentIssue_BotActorQuery = class extends Request {
	_id;
	constructor(request, id) {
		super(request);
		this._id = id;
	}
	/**
	* Call the AttachmentIssue_BotActor query and return a ActorBot
	*
	* @returns parsed response from AttachmentIssue_BotActorQuery
	*/
	async fetch() {
		const data = (await this._request(AttachmentIssue_BotActorDocument.toString(), { id: this._id })).attachmentIssue.botActor;
		return data ? new ActorBot(this._request, data) : void 0;
	}
};
/**
* A fetchable AttachmentIssue_Children Query
*
* @param request - function to call the graphql client
* @param id - required id to pass to attachmentIssue
* @param variables - variables without 'id' to pass into the AttachmentIssue_ChildrenQuery
*/
var AttachmentIssue_ChildrenQuery = class extends Request {
	_id;
	_variables;
	constructor(request, id, variables) {
		super(request);
		this._id = id;
		this._variables = variables;
	}
	/**
	* Call the AttachmentIssue_Children query and return a IssueConnection
	*
	* @param variables - variables without 'id' to pass into the AttachmentIssue_ChildrenQuery
	* @returns parsed response from AttachmentIssue_ChildrenQuery
	*/
	async fetch(variables) {
		const data = (await this._request(AttachmentIssue_ChildrenDocument.toString(), {
			id: this._id,
			...this._variables,
			...variables
		})).attachmentIssue.children;
		return new IssueConnection(this._request, (connection) => this.fetch(defaultConnection({
			...this._variables,
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable AttachmentIssue_Comments Query
*
* @param request - function to call the graphql client
* @param id - required id to pass to attachmentIssue
* @param variables - variables without 'id' to pass into the AttachmentIssue_CommentsQuery
*/
var AttachmentIssue_CommentsQuery = class extends Request {
	_id;
	_variables;
	constructor(request, id, variables) {
		super(request);
		this._id = id;
		this._variables = variables;
	}
	/**
	* Call the AttachmentIssue_Comments query and return a CommentConnection
	*
	* @param variables - variables without 'id' to pass into the AttachmentIssue_CommentsQuery
	* @returns parsed response from AttachmentIssue_CommentsQuery
	*/
	async fetch(variables) {
		const data = (await this._request(AttachmentIssue_CommentsDocument.toString(), {
			id: this._id,
			...this._variables,
			...variables
		})).attachmentIssue.comments;
		return new CommentConnection(this._request, (connection) => this.fetch(defaultConnection({
			...this._variables,
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable AttachmentIssue_Documents Query
*
* @param request - function to call the graphql client
* @param id - required id to pass to attachmentIssue
* @param variables - variables without 'id' to pass into the AttachmentIssue_DocumentsQuery
*/
var AttachmentIssue_DocumentsQuery = class extends Request {
	_id;
	_variables;
	constructor(request, id, variables) {
		super(request);
		this._id = id;
		this._variables = variables;
	}
	/**
	* Call the AttachmentIssue_Documents query and return a DocumentConnection
	*
	* @param variables - variables without 'id' to pass into the AttachmentIssue_DocumentsQuery
	* @returns parsed response from AttachmentIssue_DocumentsQuery
	*/
	async fetch(variables) {
		const data = (await this._request(AttachmentIssue_DocumentsDocument.toString(), {
			id: this._id,
			...this._variables,
			...variables
		})).attachmentIssue.documents;
		return new DocumentConnection(this._request, (connection) => this.fetch(defaultConnection({
			...this._variables,
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable AttachmentIssue_FormerAttachments Query
*
* @param request - function to call the graphql client
* @param id - required id to pass to attachmentIssue
* @param variables - variables without 'id' to pass into the AttachmentIssue_FormerAttachmentsQuery
*/
var AttachmentIssue_FormerAttachmentsQuery = class extends Request {
	_id;
	_variables;
	constructor(request, id, variables) {
		super(request);
		this._id = id;
		this._variables = variables;
	}
	/**
	* Call the AttachmentIssue_FormerAttachments query and return a AttachmentConnection
	*
	* @param variables - variables without 'id' to pass into the AttachmentIssue_FormerAttachmentsQuery
	* @returns parsed response from AttachmentIssue_FormerAttachmentsQuery
	*/
	async fetch(variables) {
		const data = (await this._request(AttachmentIssue_FormerAttachmentsDocument.toString(), {
			id: this._id,
			...this._variables,
			...variables
		})).attachmentIssue.formerAttachments;
		return new AttachmentConnection(this._request, (connection) => this.fetch(defaultConnection({
			...this._variables,
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable AttachmentIssue_FormerNeeds Query
*
* @param request - function to call the graphql client
* @param id - required id to pass to attachmentIssue
* @param variables - variables without 'id' to pass into the AttachmentIssue_FormerNeedsQuery
*/
var AttachmentIssue_FormerNeedsQuery = class extends Request {
	_id;
	_variables;
	constructor(request, id, variables) {
		super(request);
		this._id = id;
		this._variables = variables;
	}
	/**
	* Call the AttachmentIssue_FormerNeeds query and return a CustomerNeedConnection
	*
	* @param variables - variables without 'id' to pass into the AttachmentIssue_FormerNeedsQuery
	* @returns parsed response from AttachmentIssue_FormerNeedsQuery
	*/
	async fetch(variables) {
		const data = (await this._request(AttachmentIssue_FormerNeedsDocument.toString(), {
			id: this._id,
			...this._variables,
			...variables
		})).attachmentIssue.formerNeeds;
		return new CustomerNeedConnection(this._request, (connection) => this.fetch(defaultConnection({
			...this._variables,
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable AttachmentIssue_History Query
*
* @param request - function to call the graphql client
* @param id - required id to pass to attachmentIssue
* @param variables - variables without 'id' to pass into the AttachmentIssue_HistoryQuery
*/
var AttachmentIssue_HistoryQuery = class extends Request {
	_id;
	_variables;
	constructor(request, id, variables) {
		super(request);
		this._id = id;
		this._variables = variables;
	}
	/**
	* Call the AttachmentIssue_History query and return a IssueHistoryConnection
	*
	* @param variables - variables without 'id' to pass into the AttachmentIssue_HistoryQuery
	* @returns parsed response from AttachmentIssue_HistoryQuery
	*/
	async fetch(variables) {
		const data = (await this._request(AttachmentIssue_HistoryDocument.toString(), {
			id: this._id,
			...this._variables,
			...variables
		})).attachmentIssue.history;
		return new IssueHistoryConnection(this._request, (connection) => this.fetch(defaultConnection({
			...this._variables,
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable AttachmentIssue_InverseRelations Query
*
* @param request - function to call the graphql client
* @param id - required id to pass to attachmentIssue
* @param variables - variables without 'id' to pass into the AttachmentIssue_InverseRelationsQuery
*/
var AttachmentIssue_InverseRelationsQuery = class extends Request {
	_id;
	_variables;
	constructor(request, id, variables) {
		super(request);
		this._id = id;
		this._variables = variables;
	}
	/**
	* Call the AttachmentIssue_InverseRelations query and return a IssueRelationConnection
	*
	* @param variables - variables without 'id' to pass into the AttachmentIssue_InverseRelationsQuery
	* @returns parsed response from AttachmentIssue_InverseRelationsQuery
	*/
	async fetch(variables) {
		const data = (await this._request(AttachmentIssue_InverseRelationsDocument.toString(), {
			id: this._id,
			...this._variables,
			...variables
		})).attachmentIssue.inverseRelations;
		return new IssueRelationConnection(this._request, (connection) => this.fetch(defaultConnection({
			...this._variables,
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable AttachmentIssue_Labels Query
*
* @param request - function to call the graphql client
* @param id - required id to pass to attachmentIssue
* @param variables - variables without 'id' to pass into the AttachmentIssue_LabelsQuery
*/
var AttachmentIssue_LabelsQuery = class extends Request {
	_id;
	_variables;
	constructor(request, id, variables) {
		super(request);
		this._id = id;
		this._variables = variables;
	}
	/**
	* Call the AttachmentIssue_Labels query and return a IssueLabelConnection
	*
	* @param variables - variables without 'id' to pass into the AttachmentIssue_LabelsQuery
	* @returns parsed response from AttachmentIssue_LabelsQuery
	*/
	async fetch(variables) {
		const data = (await this._request(AttachmentIssue_LabelsDocument.toString(), {
			id: this._id,
			...this._variables,
			...variables
		})).attachmentIssue.labels;
		return new IssueLabelConnection(this._request, (connection) => this.fetch(defaultConnection({
			...this._variables,
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable AttachmentIssue_Needs Query
*
* @param request - function to call the graphql client
* @param id - required id to pass to attachmentIssue
* @param variables - variables without 'id' to pass into the AttachmentIssue_NeedsQuery
*/
var AttachmentIssue_NeedsQuery = class extends Request {
	_id;
	_variables;
	constructor(request, id, variables) {
		super(request);
		this._id = id;
		this._variables = variables;
	}
	/**
	* Call the AttachmentIssue_Needs query and return a CustomerNeedConnection
	*
	* @param variables - variables without 'id' to pass into the AttachmentIssue_NeedsQuery
	* @returns parsed response from AttachmentIssue_NeedsQuery
	*/
	async fetch(variables) {
		const data = (await this._request(AttachmentIssue_NeedsDocument.toString(), {
			id: this._id,
			...this._variables,
			...variables
		})).attachmentIssue.needs;
		return new CustomerNeedConnection(this._request, (connection) => this.fetch(defaultConnection({
			...this._variables,
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable AttachmentIssue_Relations Query
*
* @param request - function to call the graphql client
* @param id - required id to pass to attachmentIssue
* @param variables - variables without 'id' to pass into the AttachmentIssue_RelationsQuery
*/
var AttachmentIssue_RelationsQuery = class extends Request {
	_id;
	_variables;
	constructor(request, id, variables) {
		super(request);
		this._id = id;
		this._variables = variables;
	}
	/**
	* Call the AttachmentIssue_Relations query and return a IssueRelationConnection
	*
	* @param variables - variables without 'id' to pass into the AttachmentIssue_RelationsQuery
	* @returns parsed response from AttachmentIssue_RelationsQuery
	*/
	async fetch(variables) {
		const data = (await this._request(AttachmentIssue_RelationsDocument.toString(), {
			id: this._id,
			...this._variables,
			...variables
		})).attachmentIssue.relations;
		return new IssueRelationConnection(this._request, (connection) => this.fetch(defaultConnection({
			...this._variables,
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable AttachmentIssue_Subscribers Query
*
* @param request - function to call the graphql client
* @param id - required id to pass to attachmentIssue
* @param variables - variables without 'id' to pass into the AttachmentIssue_SubscribersQuery
*/
var AttachmentIssue_SubscribersQuery = class extends Request {
	_id;
	_variables;
	constructor(request, id, variables) {
		super(request);
		this._id = id;
		this._variables = variables;
	}
	/**
	* Call the AttachmentIssue_Subscribers query and return a UserConnection
	*
	* @param variables - variables without 'id' to pass into the AttachmentIssue_SubscribersQuery
	* @returns parsed response from AttachmentIssue_SubscribersQuery
	*/
	async fetch(variables) {
		const data = (await this._request(AttachmentIssue_SubscribersDocument.toString(), {
			id: this._id,
			...this._variables,
			...variables
		})).attachmentIssue.subscribers;
		return new UserConnection(this._request, (connection) => this.fetch(defaultConnection({
			...this._variables,
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable Comment_BotActor Query
*
* @param request - function to call the graphql client
* @param variables - variables to pass into the Comment_BotActorQuery
*/
var Comment_BotActorQuery = class extends Request {
	_variables;
	constructor(request, variables) {
		super(request);
		this._variables = variables;
	}
	/**
	* Call the Comment_BotActor query and return a ActorBot
	*
	* @param variables - variables to pass into the Comment_BotActorQuery
	* @returns parsed response from Comment_BotActorQuery
	*/
	async fetch(variables) {
		const data = (await this._request(Comment_BotActorDocument.toString(), variables)).comment.botActor;
		return data ? new ActorBot(this._request, data) : void 0;
	}
};
/**
* A fetchable Comment_Children Query
*
* @param request - function to call the graphql client
* @param variables - variables to pass into the Comment_ChildrenQuery
*/
var Comment_ChildrenQuery = class extends Request {
	_variables;
	constructor(request, variables) {
		super(request);
		this._variables = variables;
	}
	/**
	* Call the Comment_Children query and return a CommentConnection
	*
	* @param variables - variables to pass into the Comment_ChildrenQuery
	* @returns parsed response from Comment_ChildrenQuery
	*/
	async fetch(variables) {
		const data = (await this._request(Comment_ChildrenDocument.toString(), variables)).comment.children;
		return new CommentConnection(this._request, (connection) => this.fetch(defaultConnection({
			...this._variables,
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable Comment_CreatedIssues Query
*
* @param request - function to call the graphql client
* @param variables - variables to pass into the Comment_CreatedIssuesQuery
*/
var Comment_CreatedIssuesQuery = class extends Request {
	_variables;
	constructor(request, variables) {
		super(request);
		this._variables = variables;
	}
	/**
	* Call the Comment_CreatedIssues query and return a IssueConnection
	*
	* @param variables - variables to pass into the Comment_CreatedIssuesQuery
	* @returns parsed response from Comment_CreatedIssuesQuery
	*/
	async fetch(variables) {
		const data = (await this._request(Comment_CreatedIssuesDocument.toString(), variables)).comment.createdIssues;
		return new IssueConnection(this._request, (connection) => this.fetch(defaultConnection({
			...this._variables,
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable Comment_DocumentContent Query
*
* @param request - function to call the graphql client
* @param variables - variables to pass into the Comment_DocumentContentQuery
*/
var Comment_DocumentContentQuery = class extends Request {
	_variables;
	constructor(request, variables) {
		super(request);
		this._variables = variables;
	}
	/**
	* Call the Comment_DocumentContent query and return a DocumentContent
	*
	* @param variables - variables to pass into the Comment_DocumentContentQuery
	* @returns parsed response from Comment_DocumentContentQuery
	*/
	async fetch(variables) {
		const data = (await this._request(Comment_DocumentContentDocument.toString(), variables)).comment.documentContent;
		return data ? new DocumentContent(this._request, data) : void 0;
	}
};
/**
* A fetchable Comment_ExternalThread Query
*
* @param request - function to call the graphql client
* @param variables - variables to pass into the Comment_ExternalThreadQuery
*/
var Comment_ExternalThreadQuery = class extends Request {
	_variables;
	constructor(request, variables) {
		super(request);
		this._variables = variables;
	}
	/**
	* Call the Comment_ExternalThread query and return a SyncedExternalThread
	*
	* @param variables - variables to pass into the Comment_ExternalThreadQuery
	* @returns parsed response from Comment_ExternalThreadQuery
	*/
	async fetch(variables) {
		const data = (await this._request(Comment_ExternalThreadDocument.toString(), variables)).comment.externalThread;
		return data ? new SyncedExternalThread(this._request, data) : void 0;
	}
};
/**
* A fetchable Comment_DocumentContent_AiPromptRules Query
*
* @param request - function to call the graphql client
* @param variables - variables to pass into the Comment_DocumentContent_AiPromptRulesQuery
*/
var Comment_DocumentContent_AiPromptRulesQuery = class extends Request {
	_variables;
	constructor(request, variables) {
		super(request);
		this._variables = variables;
	}
	/**
	* Call the Comment_DocumentContent_AiPromptRules query and return a AiPromptRules
	*
	* @param variables - variables to pass into the Comment_DocumentContent_AiPromptRulesQuery
	* @returns parsed response from Comment_DocumentContent_AiPromptRulesQuery
	*/
	async fetch(variables) {
		const data = (await this._request(Comment_DocumentContent_AiPromptRulesDocument.toString(), variables)).comment.documentContent?.aiPromptRules;
		return data ? new AiPromptRules(this._request, data) : void 0;
	}
};
/**
* A fetchable CustomView_Initiatives Query
*
* @param request - function to call the graphql client
* @param id - required id to pass to customView
* @param variables - variables without 'id' to pass into the CustomView_InitiativesQuery
*/
var CustomView_InitiativesQuery = class extends Request {
	_id;
	_variables;
	constructor(request, id, variables) {
		super(request);
		this._id = id;
		this._variables = variables;
	}
	/**
	* Call the CustomView_Initiatives query and return a InitiativeConnection
	*
	* @param variables - variables without 'id' to pass into the CustomView_InitiativesQuery
	* @returns parsed response from CustomView_InitiativesQuery
	*/
	async fetch(variables) {
		const data = (await this._request(CustomView_InitiativesDocument.toString(), {
			id: this._id,
			...this._variables,
			...variables
		})).customView.initiatives;
		return new InitiativeConnection(this._request, (connection) => this.fetch(defaultConnection({
			...this._variables,
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable CustomView_Issues Query
*
* @param request - function to call the graphql client
* @param id - required id to pass to customView
* @param variables - variables without 'id' to pass into the CustomView_IssuesQuery
*/
var CustomView_IssuesQuery = class extends Request {
	_id;
	_variables;
	constructor(request, id, variables) {
		super(request);
		this._id = id;
		this._variables = variables;
	}
	/**
	* Call the CustomView_Issues query and return a IssueConnection
	*
	* @param variables - variables without 'id' to pass into the CustomView_IssuesQuery
	* @returns parsed response from CustomView_IssuesQuery
	*/
	async fetch(variables) {
		const data = (await this._request(CustomView_IssuesDocument.toString(), {
			id: this._id,
			...this._variables,
			...variables
		})).customView.issues;
		return new IssueConnection(this._request, (connection) => this.fetch(defaultConnection({
			...this._variables,
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable CustomView_OrganizationViewPreferences Query
*
* @param request - function to call the graphql client
* @param id - required id to pass to customView
*/
var CustomView_OrganizationViewPreferencesQuery = class extends Request {
	_id;
	constructor(request, id) {
		super(request);
		this._id = id;
	}
	/**
	* Call the CustomView_OrganizationViewPreferences query and return a ViewPreferences
	*
	* @returns parsed response from CustomView_OrganizationViewPreferencesQuery
	*/
	async fetch() {
		const data = (await this._request(CustomView_OrganizationViewPreferencesDocument.toString(), { id: this._id })).customView.organizationViewPreferences;
		return data ? new ViewPreferences(this._request, data) : void 0;
	}
};
/**
* A fetchable CustomView_Projects Query
*
* @param request - function to call the graphql client
* @param id - required id to pass to customView
* @param variables - variables without 'id' to pass into the CustomView_ProjectsQuery
*/
var CustomView_ProjectsQuery = class extends Request {
	_id;
	_variables;
	constructor(request, id, variables) {
		super(request);
		this._id = id;
		this._variables = variables;
	}
	/**
	* Call the CustomView_Projects query and return a ProjectConnection
	*
	* @param variables - variables without 'id' to pass into the CustomView_ProjectsQuery
	* @returns parsed response from CustomView_ProjectsQuery
	*/
	async fetch(variables) {
		const data = (await this._request(CustomView_ProjectsDocument.toString(), {
			id: this._id,
			...this._variables,
			...variables
		})).customView.projects;
		return new ProjectConnection(this._request, (connection) => this.fetch(defaultConnection({
			...this._variables,
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable CustomView_UserViewPreferences Query
*
* @param request - function to call the graphql client
* @param id - required id to pass to customView
*/
var CustomView_UserViewPreferencesQuery = class extends Request {
	_id;
	constructor(request, id) {
		super(request);
		this._id = id;
	}
	/**
	* Call the CustomView_UserViewPreferences query and return a ViewPreferences
	*
	* @returns parsed response from CustomView_UserViewPreferencesQuery
	*/
	async fetch() {
		const data = (await this._request(CustomView_UserViewPreferencesDocument.toString(), { id: this._id })).customView.userViewPreferences;
		return data ? new ViewPreferences(this._request, data) : void 0;
	}
};
/**
* A fetchable CustomView_ViewPreferencesValues Query
*
* @param request - function to call the graphql client
* @param id - required id to pass to customView
*/
var CustomView_ViewPreferencesValuesQuery = class extends Request {
	_id;
	constructor(request, id) {
		super(request);
		this._id = id;
	}
	/**
	* Call the CustomView_ViewPreferencesValues query and return a ViewPreferencesValues
	*
	* @returns parsed response from CustomView_ViewPreferencesValuesQuery
	*/
	async fetch() {
		const data = (await this._request(CustomView_ViewPreferencesValuesDocument.toString(), { id: this._id })).customView.viewPreferencesValues;
		return data ? new ViewPreferencesValues(this._request, data) : void 0;
	}
};
/**
* A fetchable CustomView_OrganizationViewPreferences_Preferences Query
*
* @param request - function to call the graphql client
* @param id - required id to pass to customView_organizationViewPreferences
*/
var CustomView_OrganizationViewPreferences_PreferencesQuery = class extends Request {
	_id;
	constructor(request, id) {
		super(request);
		this._id = id;
	}
	/**
	* Call the CustomView_OrganizationViewPreferences_Preferences query and return a ViewPreferencesValues
	*
	* @returns parsed response from CustomView_OrganizationViewPreferences_PreferencesQuery
	*/
	async fetch() {
		const data = (await this._request(CustomView_OrganizationViewPreferences_PreferencesDocument.toString(), { id: this._id })).customView.organizationViewPreferences?.preferences;
		return data ? new ViewPreferencesValues(this._request, data) : void 0;
	}
};
/**
* A fetchable CustomView_UserViewPreferences_Preferences Query
*
* @param request - function to call the graphql client
* @param id - required id to pass to customView_userViewPreferences
*/
var CustomView_UserViewPreferences_PreferencesQuery = class extends Request {
	_id;
	constructor(request, id) {
		super(request);
		this._id = id;
	}
	/**
	* Call the CustomView_UserViewPreferences_Preferences query and return a ViewPreferencesValues
	*
	* @returns parsed response from CustomView_UserViewPreferences_PreferencesQuery
	*/
	async fetch() {
		const data = (await this._request(CustomView_UserViewPreferences_PreferencesDocument.toString(), { id: this._id })).customView.userViewPreferences?.preferences;
		return data ? new ViewPreferencesValues(this._request, data) : void 0;
	}
};
/**
* A fetchable CustomerNeed_ProjectAttachment Query
*
* @param request - function to call the graphql client
* @param variables - variables to pass into the CustomerNeed_ProjectAttachmentQuery
*/
var CustomerNeed_ProjectAttachmentQuery = class extends Request {
	_variables;
	constructor(request, variables) {
		super(request);
		this._variables = variables;
	}
	/**
	* Call the CustomerNeed_ProjectAttachment query and return a ProjectAttachment
	*
	* @param variables - variables to pass into the CustomerNeed_ProjectAttachmentQuery
	* @returns parsed response from CustomerNeed_ProjectAttachmentQuery
	*/
	async fetch(variables) {
		const data = (await this._request(CustomerNeed_ProjectAttachmentDocument.toString(), variables)).customerNeed.projectAttachment;
		return data ? new ProjectAttachment(this._request, data) : void 0;
	}
};
/**
* A fetchable Cycle_Issues Query
*
* @param request - function to call the graphql client
* @param id - required id to pass to cycle
* @param variables - variables without 'id' to pass into the Cycle_IssuesQuery
*/
var Cycle_IssuesQuery = class extends Request {
	_id;
	_variables;
	constructor(request, id, variables) {
		super(request);
		this._id = id;
		this._variables = variables;
	}
	/**
	* Call the Cycle_Issues query and return a IssueConnection
	*
	* @param variables - variables without 'id' to pass into the Cycle_IssuesQuery
	* @returns parsed response from Cycle_IssuesQuery
	*/
	async fetch(variables) {
		const data = (await this._request(Cycle_IssuesDocument.toString(), {
			id: this._id,
			...this._variables,
			...variables
		})).cycle.issues;
		return new IssueConnection(this._request, (connection) => this.fetch(defaultConnection({
			...this._variables,
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable Cycle_UncompletedIssuesUponClose Query
*
* @param request - function to call the graphql client
* @param id - required id to pass to cycle
* @param variables - variables without 'id' to pass into the Cycle_UncompletedIssuesUponCloseQuery
*/
var Cycle_UncompletedIssuesUponCloseQuery = class extends Request {
	_id;
	_variables;
	constructor(request, id, variables) {
		super(request);
		this._id = id;
		this._variables = variables;
	}
	/**
	* Call the Cycle_UncompletedIssuesUponClose query and return a IssueConnection
	*
	* @param variables - variables without 'id' to pass into the Cycle_UncompletedIssuesUponCloseQuery
	* @returns parsed response from Cycle_UncompletedIssuesUponCloseQuery
	*/
	async fetch(variables) {
		const data = (await this._request(Cycle_UncompletedIssuesUponCloseDocument.toString(), {
			id: this._id,
			...this._variables,
			...variables
		})).cycle.uncompletedIssuesUponClose;
		return new IssueConnection(this._request, (connection) => this.fetch(defaultConnection({
			...this._variables,
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable Document_Comments Query
*
* @param request - function to call the graphql client
* @param id - required id to pass to document
* @param variables - variables without 'id' to pass into the Document_CommentsQuery
*/
var Document_CommentsQuery = class extends Request {
	_id;
	_variables;
	constructor(request, id, variables) {
		super(request);
		this._id = id;
		this._variables = variables;
	}
	/**
	* Call the Document_Comments query and return a CommentConnection
	*
	* @param variables - variables without 'id' to pass into the Document_CommentsQuery
	* @returns parsed response from Document_CommentsQuery
	*/
	async fetch(variables) {
		const data = (await this._request(Document_CommentsDocument.toString(), {
			id: this._id,
			...this._variables,
			...variables
		})).document.comments;
		return new CommentConnection(this._request, (connection) => this.fetch(defaultConnection({
			...this._variables,
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable EmailIntakeAddress_SesDomainIdentity Query
*
* @param request - function to call the graphql client
* @param id - required id to pass to emailIntakeAddress
*/
var EmailIntakeAddress_SesDomainIdentityQuery = class extends Request {
	_id;
	constructor(request, id) {
		super(request);
		this._id = id;
	}
	/**
	* Call the EmailIntakeAddress_SesDomainIdentity query and return a SesDomainIdentity
	*
	* @returns parsed response from EmailIntakeAddress_SesDomainIdentityQuery
	*/
	async fetch() {
		const data = (await this._request(EmailIntakeAddress_SesDomainIdentityDocument.toString(), { id: this._id })).emailIntakeAddress.sesDomainIdentity;
		return data ? new SesDomainIdentity(this._request, data) : void 0;
	}
};
/**
* A fetchable Favorite_Children Query
*
* @param request - function to call the graphql client
* @param id - required id to pass to favorite
* @param variables - variables without 'id' to pass into the Favorite_ChildrenQuery
*/
var Favorite_ChildrenQuery = class extends Request {
	_id;
	_variables;
	constructor(request, id, variables) {
		super(request);
		this._id = id;
		this._variables = variables;
	}
	/**
	* Call the Favorite_Children query and return a FavoriteConnection
	*
	* @param variables - variables without 'id' to pass into the Favorite_ChildrenQuery
	* @returns parsed response from Favorite_ChildrenQuery
	*/
	async fetch(variables) {
		const data = (await this._request(Favorite_ChildrenDocument.toString(), {
			id: this._id,
			...this._variables,
			...variables
		})).favorite.children;
		return new FavoriteConnection(this._request, (connection) => this.fetch(defaultConnection({
			...this._variables,
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable Initiative_DocumentContent Query
*
* @param request - function to call the graphql client
* @param id - required id to pass to initiative
*/
var Initiative_DocumentContentQuery = class extends Request {
	_id;
	constructor(request, id) {
		super(request);
		this._id = id;
	}
	/**
	* Call the Initiative_DocumentContent query and return a DocumentContent
	*
	* @returns parsed response from Initiative_DocumentContentQuery
	*/
	async fetch() {
		const data = (await this._request(Initiative_DocumentContentDocument.toString(), { id: this._id })).initiative.documentContent;
		return data ? new DocumentContent(this._request, data) : void 0;
	}
};
/**
* A fetchable Initiative_Documents Query
*
* @param request - function to call the graphql client
* @param id - required id to pass to initiative
* @param variables - variables without 'id' to pass into the Initiative_DocumentsQuery
*/
var Initiative_DocumentsQuery = class extends Request {
	_id;
	_variables;
	constructor(request, id, variables) {
		super(request);
		this._id = id;
		this._variables = variables;
	}
	/**
	* Call the Initiative_Documents query and return a DocumentConnection
	*
	* @param variables - variables without 'id' to pass into the Initiative_DocumentsQuery
	* @returns parsed response from Initiative_DocumentsQuery
	*/
	async fetch(variables) {
		const data = (await this._request(Initiative_DocumentsDocument.toString(), {
			id: this._id,
			...this._variables,
			...variables
		})).initiative.documents;
		return new DocumentConnection(this._request, (connection) => this.fetch(defaultConnection({
			...this._variables,
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable Initiative_History Query
*
* @param request - function to call the graphql client
* @param id - required id to pass to initiative
* @param variables - variables without 'id' to pass into the Initiative_HistoryQuery
*/
var Initiative_HistoryQuery = class extends Request {
	_id;
	_variables;
	constructor(request, id, variables) {
		super(request);
		this._id = id;
		this._variables = variables;
	}
	/**
	* Call the Initiative_History query and return a InitiativeHistoryConnection
	*
	* @param variables - variables without 'id' to pass into the Initiative_HistoryQuery
	* @returns parsed response from Initiative_HistoryQuery
	*/
	async fetch(variables) {
		const data = (await this._request(Initiative_HistoryDocument.toString(), {
			id: this._id,
			...this._variables,
			...variables
		})).initiative.history;
		return new InitiativeHistoryConnection(this._request, (connection) => this.fetch(defaultConnection({
			...this._variables,
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable Initiative_InitiativeUpdates Query
*
* @param request - function to call the graphql client
* @param id - required id to pass to initiative
* @param variables - variables without 'id' to pass into the Initiative_InitiativeUpdatesQuery
*/
var Initiative_InitiativeUpdatesQuery = class extends Request {
	_id;
	_variables;
	constructor(request, id, variables) {
		super(request);
		this._id = id;
		this._variables = variables;
	}
	/**
	* Call the Initiative_InitiativeUpdates query and return a InitiativeUpdateConnection
	*
	* @param variables - variables without 'id' to pass into the Initiative_InitiativeUpdatesQuery
	* @returns parsed response from Initiative_InitiativeUpdatesQuery
	*/
	async fetch(variables) {
		const data = (await this._request(Initiative_InitiativeUpdatesDocument.toString(), {
			id: this._id,
			...this._variables,
			...variables
		})).initiative.initiativeUpdates;
		return new InitiativeUpdateConnection(this._request, (connection) => this.fetch(defaultConnection({
			...this._variables,
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable Initiative_Links Query
*
* @param request - function to call the graphql client
* @param id - required id to pass to initiative
* @param variables - variables without 'id' to pass into the Initiative_LinksQuery
*/
var Initiative_LinksQuery = class extends Request {
	_id;
	_variables;
	constructor(request, id, variables) {
		super(request);
		this._id = id;
		this._variables = variables;
	}
	/**
	* Call the Initiative_Links query and return a EntityExternalLinkConnection
	*
	* @param variables - variables without 'id' to pass into the Initiative_LinksQuery
	* @returns parsed response from Initiative_LinksQuery
	*/
	async fetch(variables) {
		const data = (await this._request(Initiative_LinksDocument.toString(), {
			id: this._id,
			...this._variables,
			...variables
		})).initiative.links;
		return new EntityExternalLinkConnection(this._request, (connection) => this.fetch(defaultConnection({
			...this._variables,
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable Initiative_Projects Query
*
* @param request - function to call the graphql client
* @param id - required id to pass to initiative
* @param variables - variables without 'id' to pass into the Initiative_ProjectsQuery
*/
var Initiative_ProjectsQuery = class extends Request {
	_id;
	_variables;
	constructor(request, id, variables) {
		super(request);
		this._id = id;
		this._variables = variables;
	}
	/**
	* Call the Initiative_Projects query and return a ProjectConnection
	*
	* @param variables - variables without 'id' to pass into the Initiative_ProjectsQuery
	* @returns parsed response from Initiative_ProjectsQuery
	*/
	async fetch(variables) {
		const data = (await this._request(Initiative_ProjectsDocument.toString(), {
			id: this._id,
			...this._variables,
			...variables
		})).initiative.projects;
		return new ProjectConnection(this._request, (connection) => this.fetch(defaultConnection({
			...this._variables,
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable Initiative_SubInitiatives Query
*
* @param request - function to call the graphql client
* @param id - required id to pass to initiative
* @param variables - variables without 'id' to pass into the Initiative_SubInitiativesQuery
*/
var Initiative_SubInitiativesQuery = class extends Request {
	_id;
	_variables;
	constructor(request, id, variables) {
		super(request);
		this._id = id;
		this._variables = variables;
	}
	/**
	* Call the Initiative_SubInitiatives query and return a InitiativeConnection
	*
	* @param variables - variables without 'id' to pass into the Initiative_SubInitiativesQuery
	* @returns parsed response from Initiative_SubInitiativesQuery
	*/
	async fetch(variables) {
		const data = (await this._request(Initiative_SubInitiativesDocument.toString(), {
			id: this._id,
			...this._variables,
			...variables
		})).initiative.subInitiatives;
		return new InitiativeConnection(this._request, (connection) => this.fetch(defaultConnection({
			...this._variables,
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable Initiative_DocumentContent_AiPromptRules Query
*
* @param request - function to call the graphql client
* @param id - required id to pass to initiative_documentContent
*/
var Initiative_DocumentContent_AiPromptRulesQuery = class extends Request {
	_id;
	constructor(request, id) {
		super(request);
		this._id = id;
	}
	/**
	* Call the Initiative_DocumentContent_AiPromptRules query and return a AiPromptRules
	*
	* @returns parsed response from Initiative_DocumentContent_AiPromptRulesQuery
	*/
	async fetch() {
		const data = (await this._request(Initiative_DocumentContent_AiPromptRulesDocument.toString(), { id: this._id })).initiative.documentContent?.aiPromptRules;
		return data ? new AiPromptRules(this._request, data) : void 0;
	}
};
/**
* A fetchable InitiativeUpdate_Comments Query
*
* @param request - function to call the graphql client
* @param id - required id to pass to initiativeUpdate
* @param variables - variables without 'id' to pass into the InitiativeUpdate_CommentsQuery
*/
var InitiativeUpdate_CommentsQuery = class extends Request {
	_id;
	_variables;
	constructor(request, id, variables) {
		super(request);
		this._id = id;
		this._variables = variables;
	}
	/**
	* Call the InitiativeUpdate_Comments query and return a CommentConnection
	*
	* @param variables - variables without 'id' to pass into the InitiativeUpdate_CommentsQuery
	* @returns parsed response from InitiativeUpdate_CommentsQuery
	*/
	async fetch(variables) {
		const data = (await this._request(InitiativeUpdate_CommentsDocument.toString(), {
			id: this._id,
			...this._variables,
			...variables
		})).initiativeUpdate.comments;
		return new CommentConnection(this._request, (connection) => this.fetch(defaultConnection({
			...this._variables,
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable Issue_Attachments Query
*
* @param request - function to call the graphql client
* @param id - required id to pass to issue
* @param variables - variables without 'id' to pass into the Issue_AttachmentsQuery
*/
var Issue_AttachmentsQuery = class extends Request {
	_id;
	_variables;
	constructor(request, id, variables) {
		super(request);
		this._id = id;
		this._variables = variables;
	}
	/**
	* Call the Issue_Attachments query and return a AttachmentConnection
	*
	* @param variables - variables without 'id' to pass into the Issue_AttachmentsQuery
	* @returns parsed response from Issue_AttachmentsQuery
	*/
	async fetch(variables) {
		const data = (await this._request(Issue_AttachmentsDocument.toString(), {
			id: this._id,
			...this._variables,
			...variables
		})).issue.attachments;
		return new AttachmentConnection(this._request, (connection) => this.fetch(defaultConnection({
			...this._variables,
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable Issue_BotActor Query
*
* @param request - function to call the graphql client
* @param id - required id to pass to issue
*/
var Issue_BotActorQuery = class extends Request {
	_id;
	constructor(request, id) {
		super(request);
		this._id = id;
	}
	/**
	* Call the Issue_BotActor query and return a ActorBot
	*
	* @returns parsed response from Issue_BotActorQuery
	*/
	async fetch() {
		const data = (await this._request(Issue_BotActorDocument.toString(), { id: this._id })).issue.botActor;
		return data ? new ActorBot(this._request, data) : void 0;
	}
};
/**
* A fetchable Issue_Children Query
*
* @param request - function to call the graphql client
* @param id - required id to pass to issue
* @param variables - variables without 'id' to pass into the Issue_ChildrenQuery
*/
var Issue_ChildrenQuery = class extends Request {
	_id;
	_variables;
	constructor(request, id, variables) {
		super(request);
		this._id = id;
		this._variables = variables;
	}
	/**
	* Call the Issue_Children query and return a IssueConnection
	*
	* @param variables - variables without 'id' to pass into the Issue_ChildrenQuery
	* @returns parsed response from Issue_ChildrenQuery
	*/
	async fetch(variables) {
		const data = (await this._request(Issue_ChildrenDocument.toString(), {
			id: this._id,
			...this._variables,
			...variables
		})).issue.children;
		return new IssueConnection(this._request, (connection) => this.fetch(defaultConnection({
			...this._variables,
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable Issue_Comments Query
*
* @param request - function to call the graphql client
* @param id - required id to pass to issue
* @param variables - variables without 'id' to pass into the Issue_CommentsQuery
*/
var Issue_CommentsQuery = class extends Request {
	_id;
	_variables;
	constructor(request, id, variables) {
		super(request);
		this._id = id;
		this._variables = variables;
	}
	/**
	* Call the Issue_Comments query and return a CommentConnection
	*
	* @param variables - variables without 'id' to pass into the Issue_CommentsQuery
	* @returns parsed response from Issue_CommentsQuery
	*/
	async fetch(variables) {
		const data = (await this._request(Issue_CommentsDocument.toString(), {
			id: this._id,
			...this._variables,
			...variables
		})).issue.comments;
		return new CommentConnection(this._request, (connection) => this.fetch(defaultConnection({
			...this._variables,
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable Issue_Documents Query
*
* @param request - function to call the graphql client
* @param id - required id to pass to issue
* @param variables - variables without 'id' to pass into the Issue_DocumentsQuery
*/
var Issue_DocumentsQuery = class extends Request {
	_id;
	_variables;
	constructor(request, id, variables) {
		super(request);
		this._id = id;
		this._variables = variables;
	}
	/**
	* Call the Issue_Documents query and return a DocumentConnection
	*
	* @param variables - variables without 'id' to pass into the Issue_DocumentsQuery
	* @returns parsed response from Issue_DocumentsQuery
	*/
	async fetch(variables) {
		const data = (await this._request(Issue_DocumentsDocument.toString(), {
			id: this._id,
			...this._variables,
			...variables
		})).issue.documents;
		return new DocumentConnection(this._request, (connection) => this.fetch(defaultConnection({
			...this._variables,
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable Issue_FormerAttachments Query
*
* @param request - function to call the graphql client
* @param id - required id to pass to issue
* @param variables - variables without 'id' to pass into the Issue_FormerAttachmentsQuery
*/
var Issue_FormerAttachmentsQuery = class extends Request {
	_id;
	_variables;
	constructor(request, id, variables) {
		super(request);
		this._id = id;
		this._variables = variables;
	}
	/**
	* Call the Issue_FormerAttachments query and return a AttachmentConnection
	*
	* @param variables - variables without 'id' to pass into the Issue_FormerAttachmentsQuery
	* @returns parsed response from Issue_FormerAttachmentsQuery
	*/
	async fetch(variables) {
		const data = (await this._request(Issue_FormerAttachmentsDocument.toString(), {
			id: this._id,
			...this._variables,
			...variables
		})).issue.formerAttachments;
		return new AttachmentConnection(this._request, (connection) => this.fetch(defaultConnection({
			...this._variables,
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable Issue_FormerNeeds Query
*
* @param request - function to call the graphql client
* @param id - required id to pass to issue
* @param variables - variables without 'id' to pass into the Issue_FormerNeedsQuery
*/
var Issue_FormerNeedsQuery = class extends Request {
	_id;
	_variables;
	constructor(request, id, variables) {
		super(request);
		this._id = id;
		this._variables = variables;
	}
	/**
	* Call the Issue_FormerNeeds query and return a CustomerNeedConnection
	*
	* @param variables - variables without 'id' to pass into the Issue_FormerNeedsQuery
	* @returns parsed response from Issue_FormerNeedsQuery
	*/
	async fetch(variables) {
		const data = (await this._request(Issue_FormerNeedsDocument.toString(), {
			id: this._id,
			...this._variables,
			...variables
		})).issue.formerNeeds;
		return new CustomerNeedConnection(this._request, (connection) => this.fetch(defaultConnection({
			...this._variables,
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable Issue_History Query
*
* @param request - function to call the graphql client
* @param id - required id to pass to issue
* @param variables - variables without 'id' to pass into the Issue_HistoryQuery
*/
var Issue_HistoryQuery = class extends Request {
	_id;
	_variables;
	constructor(request, id, variables) {
		super(request);
		this._id = id;
		this._variables = variables;
	}
	/**
	* Call the Issue_History query and return a IssueHistoryConnection
	*
	* @param variables - variables without 'id' to pass into the Issue_HistoryQuery
	* @returns parsed response from Issue_HistoryQuery
	*/
	async fetch(variables) {
		const data = (await this._request(Issue_HistoryDocument.toString(), {
			id: this._id,
			...this._variables,
			...variables
		})).issue.history;
		return new IssueHistoryConnection(this._request, (connection) => this.fetch(defaultConnection({
			...this._variables,
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable Issue_InverseRelations Query
*
* @param request - function to call the graphql client
* @param id - required id to pass to issue
* @param variables - variables without 'id' to pass into the Issue_InverseRelationsQuery
*/
var Issue_InverseRelationsQuery = class extends Request {
	_id;
	_variables;
	constructor(request, id, variables) {
		super(request);
		this._id = id;
		this._variables = variables;
	}
	/**
	* Call the Issue_InverseRelations query and return a IssueRelationConnection
	*
	* @param variables - variables without 'id' to pass into the Issue_InverseRelationsQuery
	* @returns parsed response from Issue_InverseRelationsQuery
	*/
	async fetch(variables) {
		const data = (await this._request(Issue_InverseRelationsDocument.toString(), {
			id: this._id,
			...this._variables,
			...variables
		})).issue.inverseRelations;
		return new IssueRelationConnection(this._request, (connection) => this.fetch(defaultConnection({
			...this._variables,
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable Issue_Labels Query
*
* @param request - function to call the graphql client
* @param id - required id to pass to issue
* @param variables - variables without 'id' to pass into the Issue_LabelsQuery
*/
var Issue_LabelsQuery = class extends Request {
	_id;
	_variables;
	constructor(request, id, variables) {
		super(request);
		this._id = id;
		this._variables = variables;
	}
	/**
	* Call the Issue_Labels query and return a IssueLabelConnection
	*
	* @param variables - variables without 'id' to pass into the Issue_LabelsQuery
	* @returns parsed response from Issue_LabelsQuery
	*/
	async fetch(variables) {
		const data = (await this._request(Issue_LabelsDocument.toString(), {
			id: this._id,
			...this._variables,
			...variables
		})).issue.labels;
		return new IssueLabelConnection(this._request, (connection) => this.fetch(defaultConnection({
			...this._variables,
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable Issue_Needs Query
*
* @param request - function to call the graphql client
* @param id - required id to pass to issue
* @param variables - variables without 'id' to pass into the Issue_NeedsQuery
*/
var Issue_NeedsQuery = class extends Request {
	_id;
	_variables;
	constructor(request, id, variables) {
		super(request);
		this._id = id;
		this._variables = variables;
	}
	/**
	* Call the Issue_Needs query and return a CustomerNeedConnection
	*
	* @param variables - variables without 'id' to pass into the Issue_NeedsQuery
	* @returns parsed response from Issue_NeedsQuery
	*/
	async fetch(variables) {
		const data = (await this._request(Issue_NeedsDocument.toString(), {
			id: this._id,
			...this._variables,
			...variables
		})).issue.needs;
		return new CustomerNeedConnection(this._request, (connection) => this.fetch(defaultConnection({
			...this._variables,
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable Issue_Relations Query
*
* @param request - function to call the graphql client
* @param id - required id to pass to issue
* @param variables - variables without 'id' to pass into the Issue_RelationsQuery
*/
var Issue_RelationsQuery = class extends Request {
	_id;
	_variables;
	constructor(request, id, variables) {
		super(request);
		this._id = id;
		this._variables = variables;
	}
	/**
	* Call the Issue_Relations query and return a IssueRelationConnection
	*
	* @param variables - variables without 'id' to pass into the Issue_RelationsQuery
	* @returns parsed response from Issue_RelationsQuery
	*/
	async fetch(variables) {
		const data = (await this._request(Issue_RelationsDocument.toString(), {
			id: this._id,
			...this._variables,
			...variables
		})).issue.relations;
		return new IssueRelationConnection(this._request, (connection) => this.fetch(defaultConnection({
			...this._variables,
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable Issue_Subscribers Query
*
* @param request - function to call the graphql client
* @param id - required id to pass to issue
* @param variables - variables without 'id' to pass into the Issue_SubscribersQuery
*/
var Issue_SubscribersQuery = class extends Request {
	_id;
	_variables;
	constructor(request, id, variables) {
		super(request);
		this._id = id;
		this._variables = variables;
	}
	/**
	* Call the Issue_Subscribers query and return a UserConnection
	*
	* @param variables - variables without 'id' to pass into the Issue_SubscribersQuery
	* @returns parsed response from Issue_SubscribersQuery
	*/
	async fetch(variables) {
		const data = (await this._request(Issue_SubscribersDocument.toString(), {
			id: this._id,
			...this._variables,
			...variables
		})).issue.subscribers;
		return new UserConnection(this._request, (connection) => this.fetch(defaultConnection({
			...this._variables,
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable IssueLabel_Children Query
*
* @param request - function to call the graphql client
* @param id - required id to pass to issueLabel
* @param variables - variables without 'id' to pass into the IssueLabel_ChildrenQuery
*/
var IssueLabel_ChildrenQuery = class extends Request {
	_id;
	_variables;
	constructor(request, id, variables) {
		super(request);
		this._id = id;
		this._variables = variables;
	}
	/**
	* Call the IssueLabel_Children query and return a IssueLabelConnection
	*
	* @param variables - variables without 'id' to pass into the IssueLabel_ChildrenQuery
	* @returns parsed response from IssueLabel_ChildrenQuery
	*/
	async fetch(variables) {
		const data = (await this._request(IssueLabel_ChildrenDocument.toString(), {
			id: this._id,
			...this._variables,
			...variables
		})).issueLabel.children;
		return new IssueLabelConnection(this._request, (connection) => this.fetch(defaultConnection({
			...this._variables,
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable IssueLabel_Issues Query
*
* @param request - function to call the graphql client
* @param id - required id to pass to issueLabel
* @param variables - variables without 'id' to pass into the IssueLabel_IssuesQuery
*/
var IssueLabel_IssuesQuery = class extends Request {
	_id;
	_variables;
	constructor(request, id, variables) {
		super(request);
		this._id = id;
		this._variables = variables;
	}
	/**
	* Call the IssueLabel_Issues query and return a IssueConnection
	*
	* @param variables - variables without 'id' to pass into the IssueLabel_IssuesQuery
	* @returns parsed response from IssueLabel_IssuesQuery
	*/
	async fetch(variables) {
		const data = (await this._request(IssueLabel_IssuesDocument.toString(), {
			id: this._id,
			...this._variables,
			...variables
		})).issueLabel.issues;
		return new IssueConnection(this._request, (connection) => this.fetch(defaultConnection({
			...this._variables,
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable IssueVcsBranchSearch_Attachments Query
*
* @param request - function to call the graphql client
* @param branchName - required branchName to pass to issueVcsBranchSearch
* @param variables - variables without 'branchName' to pass into the IssueVcsBranchSearch_AttachmentsQuery
*/
var IssueVcsBranchSearch_AttachmentsQuery = class extends Request {
	_branchName;
	_variables;
	constructor(request, branchName, variables) {
		super(request);
		this._branchName = branchName;
		this._variables = variables;
	}
	/**
	* Call the IssueVcsBranchSearch_Attachments query and return a AttachmentConnection
	*
	* @param variables - variables without 'branchName' to pass into the IssueVcsBranchSearch_AttachmentsQuery
	* @returns parsed response from IssueVcsBranchSearch_AttachmentsQuery
	*/
	async fetch(variables) {
		const data = (await this._request(IssueVcsBranchSearch_AttachmentsDocument.toString(), {
			branchName: this._branchName,
			...this._variables,
			...variables
		})).issueVcsBranchSearch?.attachments;
		if (data) return new AttachmentConnection(this._request, (connection) => this.fetch(defaultConnection({
			...this._variables,
			...variables,
			...connection
		})), data);
		else return;
	}
};
/**
* A fetchable IssueVcsBranchSearch_BotActor Query
*
* @param request - function to call the graphql client
* @param branchName - required branchName to pass to issueVcsBranchSearch
*/
var IssueVcsBranchSearch_BotActorQuery = class extends Request {
	_branchName;
	constructor(request, branchName) {
		super(request);
		this._branchName = branchName;
	}
	/**
	* Call the IssueVcsBranchSearch_BotActor query and return a ActorBot
	*
	* @returns parsed response from IssueVcsBranchSearch_BotActorQuery
	*/
	async fetch() {
		const data = (await this._request(IssueVcsBranchSearch_BotActorDocument.toString(), { branchName: this._branchName })).issueVcsBranchSearch?.botActor;
		return data ? new ActorBot(this._request, data) : void 0;
	}
};
/**
* A fetchable IssueVcsBranchSearch_Children Query
*
* @param request - function to call the graphql client
* @param branchName - required branchName to pass to issueVcsBranchSearch
* @param variables - variables without 'branchName' to pass into the IssueVcsBranchSearch_ChildrenQuery
*/
var IssueVcsBranchSearch_ChildrenQuery = class extends Request {
	_branchName;
	_variables;
	constructor(request, branchName, variables) {
		super(request);
		this._branchName = branchName;
		this._variables = variables;
	}
	/**
	* Call the IssueVcsBranchSearch_Children query and return a IssueConnection
	*
	* @param variables - variables without 'branchName' to pass into the IssueVcsBranchSearch_ChildrenQuery
	* @returns parsed response from IssueVcsBranchSearch_ChildrenQuery
	*/
	async fetch(variables) {
		const data = (await this._request(IssueVcsBranchSearch_ChildrenDocument.toString(), {
			branchName: this._branchName,
			...this._variables,
			...variables
		})).issueVcsBranchSearch?.children;
		if (data) return new IssueConnection(this._request, (connection) => this.fetch(defaultConnection({
			...this._variables,
			...variables,
			...connection
		})), data);
		else return;
	}
};
/**
* A fetchable IssueVcsBranchSearch_Comments Query
*
* @param request - function to call the graphql client
* @param branchName - required branchName to pass to issueVcsBranchSearch
* @param variables - variables without 'branchName' to pass into the IssueVcsBranchSearch_CommentsQuery
*/
var IssueVcsBranchSearch_CommentsQuery = class extends Request {
	_branchName;
	_variables;
	constructor(request, branchName, variables) {
		super(request);
		this._branchName = branchName;
		this._variables = variables;
	}
	/**
	* Call the IssueVcsBranchSearch_Comments query and return a CommentConnection
	*
	* @param variables - variables without 'branchName' to pass into the IssueVcsBranchSearch_CommentsQuery
	* @returns parsed response from IssueVcsBranchSearch_CommentsQuery
	*/
	async fetch(variables) {
		const data = (await this._request(IssueVcsBranchSearch_CommentsDocument.toString(), {
			branchName: this._branchName,
			...this._variables,
			...variables
		})).issueVcsBranchSearch?.comments;
		if (data) return new CommentConnection(this._request, (connection) => this.fetch(defaultConnection({
			...this._variables,
			...variables,
			...connection
		})), data);
		else return;
	}
};
/**
* A fetchable IssueVcsBranchSearch_Documents Query
*
* @param request - function to call the graphql client
* @param branchName - required branchName to pass to issueVcsBranchSearch
* @param variables - variables without 'branchName' to pass into the IssueVcsBranchSearch_DocumentsQuery
*/
var IssueVcsBranchSearch_DocumentsQuery = class extends Request {
	_branchName;
	_variables;
	constructor(request, branchName, variables) {
		super(request);
		this._branchName = branchName;
		this._variables = variables;
	}
	/**
	* Call the IssueVcsBranchSearch_Documents query and return a DocumentConnection
	*
	* @param variables - variables without 'branchName' to pass into the IssueVcsBranchSearch_DocumentsQuery
	* @returns parsed response from IssueVcsBranchSearch_DocumentsQuery
	*/
	async fetch(variables) {
		const data = (await this._request(IssueVcsBranchSearch_DocumentsDocument.toString(), {
			branchName: this._branchName,
			...this._variables,
			...variables
		})).issueVcsBranchSearch?.documents;
		if (data) return new DocumentConnection(this._request, (connection) => this.fetch(defaultConnection({
			...this._variables,
			...variables,
			...connection
		})), data);
		else return;
	}
};
/**
* A fetchable IssueVcsBranchSearch_FormerAttachments Query
*
* @param request - function to call the graphql client
* @param branchName - required branchName to pass to issueVcsBranchSearch
* @param variables - variables without 'branchName' to pass into the IssueVcsBranchSearch_FormerAttachmentsQuery
*/
var IssueVcsBranchSearch_FormerAttachmentsQuery = class extends Request {
	_branchName;
	_variables;
	constructor(request, branchName, variables) {
		super(request);
		this._branchName = branchName;
		this._variables = variables;
	}
	/**
	* Call the IssueVcsBranchSearch_FormerAttachments query and return a AttachmentConnection
	*
	* @param variables - variables without 'branchName' to pass into the IssueVcsBranchSearch_FormerAttachmentsQuery
	* @returns parsed response from IssueVcsBranchSearch_FormerAttachmentsQuery
	*/
	async fetch(variables) {
		const data = (await this._request(IssueVcsBranchSearch_FormerAttachmentsDocument.toString(), {
			branchName: this._branchName,
			...this._variables,
			...variables
		})).issueVcsBranchSearch?.formerAttachments;
		if (data) return new AttachmentConnection(this._request, (connection) => this.fetch(defaultConnection({
			...this._variables,
			...variables,
			...connection
		})), data);
		else return;
	}
};
/**
* A fetchable IssueVcsBranchSearch_FormerNeeds Query
*
* @param request - function to call the graphql client
* @param branchName - required branchName to pass to issueVcsBranchSearch
* @param variables - variables without 'branchName' to pass into the IssueVcsBranchSearch_FormerNeedsQuery
*/
var IssueVcsBranchSearch_FormerNeedsQuery = class extends Request {
	_branchName;
	_variables;
	constructor(request, branchName, variables) {
		super(request);
		this._branchName = branchName;
		this._variables = variables;
	}
	/**
	* Call the IssueVcsBranchSearch_FormerNeeds query and return a CustomerNeedConnection
	*
	* @param variables - variables without 'branchName' to pass into the IssueVcsBranchSearch_FormerNeedsQuery
	* @returns parsed response from IssueVcsBranchSearch_FormerNeedsQuery
	*/
	async fetch(variables) {
		const data = (await this._request(IssueVcsBranchSearch_FormerNeedsDocument.toString(), {
			branchName: this._branchName,
			...this._variables,
			...variables
		})).issueVcsBranchSearch?.formerNeeds;
		if (data) return new CustomerNeedConnection(this._request, (connection) => this.fetch(defaultConnection({
			...this._variables,
			...variables,
			...connection
		})), data);
		else return;
	}
};
/**
* A fetchable IssueVcsBranchSearch_History Query
*
* @param request - function to call the graphql client
* @param branchName - required branchName to pass to issueVcsBranchSearch
* @param variables - variables without 'branchName' to pass into the IssueVcsBranchSearch_HistoryQuery
*/
var IssueVcsBranchSearch_HistoryQuery = class extends Request {
	_branchName;
	_variables;
	constructor(request, branchName, variables) {
		super(request);
		this._branchName = branchName;
		this._variables = variables;
	}
	/**
	* Call the IssueVcsBranchSearch_History query and return a IssueHistoryConnection
	*
	* @param variables - variables without 'branchName' to pass into the IssueVcsBranchSearch_HistoryQuery
	* @returns parsed response from IssueVcsBranchSearch_HistoryQuery
	*/
	async fetch(variables) {
		const data = (await this._request(IssueVcsBranchSearch_HistoryDocument.toString(), {
			branchName: this._branchName,
			...this._variables,
			...variables
		})).issueVcsBranchSearch?.history;
		if (data) return new IssueHistoryConnection(this._request, (connection) => this.fetch(defaultConnection({
			...this._variables,
			...variables,
			...connection
		})), data);
		else return;
	}
};
/**
* A fetchable IssueVcsBranchSearch_InverseRelations Query
*
* @param request - function to call the graphql client
* @param branchName - required branchName to pass to issueVcsBranchSearch
* @param variables - variables without 'branchName' to pass into the IssueVcsBranchSearch_InverseRelationsQuery
*/
var IssueVcsBranchSearch_InverseRelationsQuery = class extends Request {
	_branchName;
	_variables;
	constructor(request, branchName, variables) {
		super(request);
		this._branchName = branchName;
		this._variables = variables;
	}
	/**
	* Call the IssueVcsBranchSearch_InverseRelations query and return a IssueRelationConnection
	*
	* @param variables - variables without 'branchName' to pass into the IssueVcsBranchSearch_InverseRelationsQuery
	* @returns parsed response from IssueVcsBranchSearch_InverseRelationsQuery
	*/
	async fetch(variables) {
		const data = (await this._request(IssueVcsBranchSearch_InverseRelationsDocument.toString(), {
			branchName: this._branchName,
			...this._variables,
			...variables
		})).issueVcsBranchSearch?.inverseRelations;
		if (data) return new IssueRelationConnection(this._request, (connection) => this.fetch(defaultConnection({
			...this._variables,
			...variables,
			...connection
		})), data);
		else return;
	}
};
/**
* A fetchable IssueVcsBranchSearch_Labels Query
*
* @param request - function to call the graphql client
* @param branchName - required branchName to pass to issueVcsBranchSearch
* @param variables - variables without 'branchName' to pass into the IssueVcsBranchSearch_LabelsQuery
*/
var IssueVcsBranchSearch_LabelsQuery = class extends Request {
	_branchName;
	_variables;
	constructor(request, branchName, variables) {
		super(request);
		this._branchName = branchName;
		this._variables = variables;
	}
	/**
	* Call the IssueVcsBranchSearch_Labels query and return a IssueLabelConnection
	*
	* @param variables - variables without 'branchName' to pass into the IssueVcsBranchSearch_LabelsQuery
	* @returns parsed response from IssueVcsBranchSearch_LabelsQuery
	*/
	async fetch(variables) {
		const data = (await this._request(IssueVcsBranchSearch_LabelsDocument.toString(), {
			branchName: this._branchName,
			...this._variables,
			...variables
		})).issueVcsBranchSearch?.labels;
		if (data) return new IssueLabelConnection(this._request, (connection) => this.fetch(defaultConnection({
			...this._variables,
			...variables,
			...connection
		})), data);
		else return;
	}
};
/**
* A fetchable IssueVcsBranchSearch_Needs Query
*
* @param request - function to call the graphql client
* @param branchName - required branchName to pass to issueVcsBranchSearch
* @param variables - variables without 'branchName' to pass into the IssueVcsBranchSearch_NeedsQuery
*/
var IssueVcsBranchSearch_NeedsQuery = class extends Request {
	_branchName;
	_variables;
	constructor(request, branchName, variables) {
		super(request);
		this._branchName = branchName;
		this._variables = variables;
	}
	/**
	* Call the IssueVcsBranchSearch_Needs query and return a CustomerNeedConnection
	*
	* @param variables - variables without 'branchName' to pass into the IssueVcsBranchSearch_NeedsQuery
	* @returns parsed response from IssueVcsBranchSearch_NeedsQuery
	*/
	async fetch(variables) {
		const data = (await this._request(IssueVcsBranchSearch_NeedsDocument.toString(), {
			branchName: this._branchName,
			...this._variables,
			...variables
		})).issueVcsBranchSearch?.needs;
		if (data) return new CustomerNeedConnection(this._request, (connection) => this.fetch(defaultConnection({
			...this._variables,
			...variables,
			...connection
		})), data);
		else return;
	}
};
/**
* A fetchable IssueVcsBranchSearch_Relations Query
*
* @param request - function to call the graphql client
* @param branchName - required branchName to pass to issueVcsBranchSearch
* @param variables - variables without 'branchName' to pass into the IssueVcsBranchSearch_RelationsQuery
*/
var IssueVcsBranchSearch_RelationsQuery = class extends Request {
	_branchName;
	_variables;
	constructor(request, branchName, variables) {
		super(request);
		this._branchName = branchName;
		this._variables = variables;
	}
	/**
	* Call the IssueVcsBranchSearch_Relations query and return a IssueRelationConnection
	*
	* @param variables - variables without 'branchName' to pass into the IssueVcsBranchSearch_RelationsQuery
	* @returns parsed response from IssueVcsBranchSearch_RelationsQuery
	*/
	async fetch(variables) {
		const data = (await this._request(IssueVcsBranchSearch_RelationsDocument.toString(), {
			branchName: this._branchName,
			...this._variables,
			...variables
		})).issueVcsBranchSearch?.relations;
		if (data) return new IssueRelationConnection(this._request, (connection) => this.fetch(defaultConnection({
			...this._variables,
			...variables,
			...connection
		})), data);
		else return;
	}
};
/**
* A fetchable IssueVcsBranchSearch_Subscribers Query
*
* @param request - function to call the graphql client
* @param branchName - required branchName to pass to issueVcsBranchSearch
* @param variables - variables without 'branchName' to pass into the IssueVcsBranchSearch_SubscribersQuery
*/
var IssueVcsBranchSearch_SubscribersQuery = class extends Request {
	_branchName;
	_variables;
	constructor(request, branchName, variables) {
		super(request);
		this._branchName = branchName;
		this._variables = variables;
	}
	/**
	* Call the IssueVcsBranchSearch_Subscribers query and return a UserConnection
	*
	* @param variables - variables without 'branchName' to pass into the IssueVcsBranchSearch_SubscribersQuery
	* @returns parsed response from IssueVcsBranchSearch_SubscribersQuery
	*/
	async fetch(variables) {
		const data = (await this._request(IssueVcsBranchSearch_SubscribersDocument.toString(), {
			branchName: this._branchName,
			...this._variables,
			...variables
		})).issueVcsBranchSearch?.subscribers;
		if (data) return new UserConnection(this._request, (connection) => this.fetch(defaultConnection({
			...this._variables,
			...variables,
			...connection
		})), data);
		else return;
	}
};
/**
* A fetchable Organization_Integrations Query
*
* @param request - function to call the graphql client
* @param variables - variables to pass into the Organization_IntegrationsQuery
*/
var Organization_IntegrationsQuery = class extends Request {
	_variables;
	constructor(request, variables) {
		super(request);
		this._variables = variables;
	}
	/**
	* Call the Organization_Integrations query and return a IntegrationConnection
	*
	* @param variables - variables to pass into the Organization_IntegrationsQuery
	* @returns parsed response from Organization_IntegrationsQuery
	*/
	async fetch(variables) {
		const data = (await this._request(Organization_IntegrationsDocument.toString(), variables)).organization.integrations;
		return new IntegrationConnection(this._request, (connection) => this.fetch(defaultConnection({
			...this._variables,
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable Organization_Labels Query
*
* @param request - function to call the graphql client
* @param variables - variables to pass into the Organization_LabelsQuery
*/
var Organization_LabelsQuery = class extends Request {
	_variables;
	constructor(request, variables) {
		super(request);
		this._variables = variables;
	}
	/**
	* Call the Organization_Labels query and return a IssueLabelConnection
	*
	* @param variables - variables to pass into the Organization_LabelsQuery
	* @returns parsed response from Organization_LabelsQuery
	*/
	async fetch(variables) {
		const data = (await this._request(Organization_LabelsDocument.toString(), variables)).organization.labels;
		return new IssueLabelConnection(this._request, (connection) => this.fetch(defaultConnection({
			...this._variables,
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable Organization_ProjectLabels Query
*
* @param request - function to call the graphql client
* @param variables - variables to pass into the Organization_ProjectLabelsQuery
*/
var Organization_ProjectLabelsQuery = class extends Request {
	_variables;
	constructor(request, variables) {
		super(request);
		this._variables = variables;
	}
	/**
	* Call the Organization_ProjectLabels query and return a ProjectLabelConnection
	*
	* @param variables - variables to pass into the Organization_ProjectLabelsQuery
	* @returns parsed response from Organization_ProjectLabelsQuery
	*/
	async fetch(variables) {
		const data = (await this._request(Organization_ProjectLabelsDocument.toString(), variables)).organization.projectLabels;
		return new ProjectLabelConnection(this._request, (connection) => this.fetch(defaultConnection({
			...this._variables,
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable Organization_Subscription Query
*
* @param request - function to call the graphql client
*/
var Organization_SubscriptionQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the Organization_Subscription query and return a PaidSubscription
	*
	* @returns parsed response from Organization_SubscriptionQuery
	*/
	async fetch() {
		const data = (await this._request(Organization_SubscriptionDocument.toString(), {})).organization.subscription;
		return data ? new PaidSubscription(this._request, data) : void 0;
	}
};
/**
* A fetchable Organization_Teams Query
*
* @param request - function to call the graphql client
* @param variables - variables to pass into the Organization_TeamsQuery
*/
var Organization_TeamsQuery = class extends Request {
	_variables;
	constructor(request, variables) {
		super(request);
		this._variables = variables;
	}
	/**
	* Call the Organization_Teams query and return a TeamConnection
	*
	* @param variables - variables to pass into the Organization_TeamsQuery
	* @returns parsed response from Organization_TeamsQuery
	*/
	async fetch(variables) {
		const data = (await this._request(Organization_TeamsDocument.toString(), variables)).organization.teams;
		return new TeamConnection(this._request, (connection) => this.fetch(defaultConnection({
			...this._variables,
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable Organization_Templates Query
*
* @param request - function to call the graphql client
* @param variables - variables to pass into the Organization_TemplatesQuery
*/
var Organization_TemplatesQuery = class extends Request {
	_variables;
	constructor(request, variables) {
		super(request);
		this._variables = variables;
	}
	/**
	* Call the Organization_Templates query and return a TemplateConnection
	*
	* @param variables - variables to pass into the Organization_TemplatesQuery
	* @returns parsed response from Organization_TemplatesQuery
	*/
	async fetch(variables) {
		const data = (await this._request(Organization_TemplatesDocument.toString(), variables)).organization.templates;
		return new TemplateConnection(this._request, (connection) => this.fetch(defaultConnection({
			...this._variables,
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable Organization_Users Query
*
* @param request - function to call the graphql client
* @param variables - variables to pass into the Organization_UsersQuery
*/
var Organization_UsersQuery = class extends Request {
	_variables;
	constructor(request, variables) {
		super(request);
		this._variables = variables;
	}
	/**
	* Call the Organization_Users query and return a UserConnection
	*
	* @param variables - variables to pass into the Organization_UsersQuery
	* @returns parsed response from Organization_UsersQuery
	*/
	async fetch(variables) {
		const data = (await this._request(Organization_UsersDocument.toString(), variables)).organization.users;
		return new UserConnection(this._request, (connection) => this.fetch(defaultConnection({
			...this._variables,
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable Project_Comments Query
*
* @param request - function to call the graphql client
* @param id - required id to pass to project
* @param variables - variables without 'id' to pass into the Project_CommentsQuery
*/
var Project_CommentsQuery = class extends Request {
	_id;
	_variables;
	constructor(request, id, variables) {
		super(request);
		this._id = id;
		this._variables = variables;
	}
	/**
	* Call the Project_Comments query and return a CommentConnection
	*
	* @param variables - variables without 'id' to pass into the Project_CommentsQuery
	* @returns parsed response from Project_CommentsQuery
	*/
	async fetch(variables) {
		const data = (await this._request(Project_CommentsDocument.toString(), {
			id: this._id,
			...this._variables,
			...variables
		})).project.comments;
		return new CommentConnection(this._request, (connection) => this.fetch(defaultConnection({
			...this._variables,
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable Project_DocumentContent Query
*
* @param request - function to call the graphql client
* @param id - required id to pass to project
*/
var Project_DocumentContentQuery = class extends Request {
	_id;
	constructor(request, id) {
		super(request);
		this._id = id;
	}
	/**
	* Call the Project_DocumentContent query and return a DocumentContent
	*
	* @returns parsed response from Project_DocumentContentQuery
	*/
	async fetch() {
		const data = (await this._request(Project_DocumentContentDocument.toString(), { id: this._id })).project.documentContent;
		return data ? new DocumentContent(this._request, data) : void 0;
	}
};
/**
* A fetchable Project_Documents Query
*
* @param request - function to call the graphql client
* @param id - required id to pass to project
* @param variables - variables without 'id' to pass into the Project_DocumentsQuery
*/
var Project_DocumentsQuery = class extends Request {
	_id;
	_variables;
	constructor(request, id, variables) {
		super(request);
		this._id = id;
		this._variables = variables;
	}
	/**
	* Call the Project_Documents query and return a DocumentConnection
	*
	* @param variables - variables without 'id' to pass into the Project_DocumentsQuery
	* @returns parsed response from Project_DocumentsQuery
	*/
	async fetch(variables) {
		const data = (await this._request(Project_DocumentsDocument.toString(), {
			id: this._id,
			...this._variables,
			...variables
		})).project.documents;
		return new DocumentConnection(this._request, (connection) => this.fetch(defaultConnection({
			...this._variables,
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable Project_ExternalLinks Query
*
* @param request - function to call the graphql client
* @param id - required id to pass to project
* @param variables - variables without 'id' to pass into the Project_ExternalLinksQuery
*/
var Project_ExternalLinksQuery = class extends Request {
	_id;
	_variables;
	constructor(request, id, variables) {
		super(request);
		this._id = id;
		this._variables = variables;
	}
	/**
	* Call the Project_ExternalLinks query and return a EntityExternalLinkConnection
	*
	* @param variables - variables without 'id' to pass into the Project_ExternalLinksQuery
	* @returns parsed response from Project_ExternalLinksQuery
	*/
	async fetch(variables) {
		const data = (await this._request(Project_ExternalLinksDocument.toString(), {
			id: this._id,
			...this._variables,
			...variables
		})).project.externalLinks;
		return new EntityExternalLinkConnection(this._request, (connection) => this.fetch(defaultConnection({
			...this._variables,
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable Project_History Query
*
* @param request - function to call the graphql client
* @param id - required id to pass to project
* @param variables - variables without 'id' to pass into the Project_HistoryQuery
*/
var Project_HistoryQuery = class extends Request {
	_id;
	_variables;
	constructor(request, id, variables) {
		super(request);
		this._id = id;
		this._variables = variables;
	}
	/**
	* Call the Project_History query and return a ProjectHistoryConnection
	*
	* @param variables - variables without 'id' to pass into the Project_HistoryQuery
	* @returns parsed response from Project_HistoryQuery
	*/
	async fetch(variables) {
		const data = (await this._request(Project_HistoryDocument.toString(), {
			id: this._id,
			...this._variables,
			...variables
		})).project.history;
		return new ProjectHistoryConnection(this._request, (connection) => this.fetch(defaultConnection({
			...this._variables,
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable Project_InitiativeToProjects Query
*
* @param request - function to call the graphql client
* @param id - required id to pass to project
* @param variables - variables without 'id' to pass into the Project_InitiativeToProjectsQuery
*/
var Project_InitiativeToProjectsQuery = class extends Request {
	_id;
	_variables;
	constructor(request, id, variables) {
		super(request);
		this._id = id;
		this._variables = variables;
	}
	/**
	* Call the Project_InitiativeToProjects query and return a InitiativeToProjectConnection
	*
	* @param variables - variables without 'id' to pass into the Project_InitiativeToProjectsQuery
	* @returns parsed response from Project_InitiativeToProjectsQuery
	*/
	async fetch(variables) {
		const data = (await this._request(Project_InitiativeToProjectsDocument.toString(), {
			id: this._id,
			...this._variables,
			...variables
		})).project.initiativeToProjects;
		return new InitiativeToProjectConnection(this._request, (connection) => this.fetch(defaultConnection({
			...this._variables,
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable Project_Initiatives Query
*
* @param request - function to call the graphql client
* @param id - required id to pass to project
* @param variables - variables without 'id' to pass into the Project_InitiativesQuery
*/
var Project_InitiativesQuery = class extends Request {
	_id;
	_variables;
	constructor(request, id, variables) {
		super(request);
		this._id = id;
		this._variables = variables;
	}
	/**
	* Call the Project_Initiatives query and return a InitiativeConnection
	*
	* @param variables - variables without 'id' to pass into the Project_InitiativesQuery
	* @returns parsed response from Project_InitiativesQuery
	*/
	async fetch(variables) {
		const data = (await this._request(Project_InitiativesDocument.toString(), {
			id: this._id,
			...this._variables,
			...variables
		})).project.initiatives;
		return new InitiativeConnection(this._request, (connection) => this.fetch(defaultConnection({
			...this._variables,
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable Project_InverseRelations Query
*
* @param request - function to call the graphql client
* @param id - required id to pass to project
* @param variables - variables without 'id' to pass into the Project_InverseRelationsQuery
*/
var Project_InverseRelationsQuery = class extends Request {
	_id;
	_variables;
	constructor(request, id, variables) {
		super(request);
		this._id = id;
		this._variables = variables;
	}
	/**
	* Call the Project_InverseRelations query and return a ProjectRelationConnection
	*
	* @param variables - variables without 'id' to pass into the Project_InverseRelationsQuery
	* @returns parsed response from Project_InverseRelationsQuery
	*/
	async fetch(variables) {
		const data = (await this._request(Project_InverseRelationsDocument.toString(), {
			id: this._id,
			...this._variables,
			...variables
		})).project.inverseRelations;
		return new ProjectRelationConnection(this._request, (connection) => this.fetch(defaultConnection({
			...this._variables,
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable Project_Issues Query
*
* @param request - function to call the graphql client
* @param id - required id to pass to project
* @param variables - variables without 'id' to pass into the Project_IssuesQuery
*/
var Project_IssuesQuery = class extends Request {
	_id;
	_variables;
	constructor(request, id, variables) {
		super(request);
		this._id = id;
		this._variables = variables;
	}
	/**
	* Call the Project_Issues query and return a IssueConnection
	*
	* @param variables - variables without 'id' to pass into the Project_IssuesQuery
	* @returns parsed response from Project_IssuesQuery
	*/
	async fetch(variables) {
		const data = (await this._request(Project_IssuesDocument.toString(), {
			id: this._id,
			...this._variables,
			...variables
		})).project.issues;
		return new IssueConnection(this._request, (connection) => this.fetch(defaultConnection({
			...this._variables,
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable Project_Labels Query
*
* @param request - function to call the graphql client
* @param id - required id to pass to project
* @param variables - variables without 'id' to pass into the Project_LabelsQuery
*/
var Project_LabelsQuery = class extends Request {
	_id;
	_variables;
	constructor(request, id, variables) {
		super(request);
		this._id = id;
		this._variables = variables;
	}
	/**
	* Call the Project_Labels query and return a ProjectLabelConnection
	*
	* @param variables - variables without 'id' to pass into the Project_LabelsQuery
	* @returns parsed response from Project_LabelsQuery
	*/
	async fetch(variables) {
		const data = (await this._request(Project_LabelsDocument.toString(), {
			id: this._id,
			...this._variables,
			...variables
		})).project.labels;
		return new ProjectLabelConnection(this._request, (connection) => this.fetch(defaultConnection({
			...this._variables,
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable Project_Members Query
*
* @param request - function to call the graphql client
* @param id - required id to pass to project
* @param variables - variables without 'id' to pass into the Project_MembersQuery
*/
var Project_MembersQuery = class extends Request {
	_id;
	_variables;
	constructor(request, id, variables) {
		super(request);
		this._id = id;
		this._variables = variables;
	}
	/**
	* Call the Project_Members query and return a UserConnection
	*
	* @param variables - variables without 'id' to pass into the Project_MembersQuery
	* @returns parsed response from Project_MembersQuery
	*/
	async fetch(variables) {
		const data = (await this._request(Project_MembersDocument.toString(), {
			id: this._id,
			...this._variables,
			...variables
		})).project.members;
		return new UserConnection(this._request, (connection) => this.fetch(defaultConnection({
			...this._variables,
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable Project_Needs Query
*
* @param request - function to call the graphql client
* @param id - required id to pass to project
* @param variables - variables without 'id' to pass into the Project_NeedsQuery
*/
var Project_NeedsQuery = class extends Request {
	_id;
	_variables;
	constructor(request, id, variables) {
		super(request);
		this._id = id;
		this._variables = variables;
	}
	/**
	* Call the Project_Needs query and return a CustomerNeedConnection
	*
	* @param variables - variables without 'id' to pass into the Project_NeedsQuery
	* @returns parsed response from Project_NeedsQuery
	*/
	async fetch(variables) {
		const data = (await this._request(Project_NeedsDocument.toString(), {
			id: this._id,
			...this._variables,
			...variables
		})).project.needs;
		return new CustomerNeedConnection(this._request, (connection) => this.fetch(defaultConnection({
			...this._variables,
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable Project_ProjectMilestones Query
*
* @param request - function to call the graphql client
* @param id - required id to pass to project
* @param variables - variables without 'id' to pass into the Project_ProjectMilestonesQuery
*/
var Project_ProjectMilestonesQuery = class extends Request {
	_id;
	_variables;
	constructor(request, id, variables) {
		super(request);
		this._id = id;
		this._variables = variables;
	}
	/**
	* Call the Project_ProjectMilestones query and return a ProjectMilestoneConnection
	*
	* @param variables - variables without 'id' to pass into the Project_ProjectMilestonesQuery
	* @returns parsed response from Project_ProjectMilestonesQuery
	*/
	async fetch(variables) {
		const data = (await this._request(Project_ProjectMilestonesDocument.toString(), {
			id: this._id,
			...this._variables,
			...variables
		})).project.projectMilestones;
		return new ProjectMilestoneConnection(this._request, (connection) => this.fetch(defaultConnection({
			...this._variables,
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable Project_ProjectUpdates Query
*
* @param request - function to call the graphql client
* @param id - required id to pass to project
* @param variables - variables without 'id' to pass into the Project_ProjectUpdatesQuery
*/
var Project_ProjectUpdatesQuery = class extends Request {
	_id;
	_variables;
	constructor(request, id, variables) {
		super(request);
		this._id = id;
		this._variables = variables;
	}
	/**
	* Call the Project_ProjectUpdates query and return a ProjectUpdateConnection
	*
	* @param variables - variables without 'id' to pass into the Project_ProjectUpdatesQuery
	* @returns parsed response from Project_ProjectUpdatesQuery
	*/
	async fetch(variables) {
		const data = (await this._request(Project_ProjectUpdatesDocument.toString(), {
			id: this._id,
			...this._variables,
			...variables
		})).project.projectUpdates;
		return new ProjectUpdateConnection(this._request, (connection) => this.fetch(defaultConnection({
			...this._variables,
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable Project_Relations Query
*
* @param request - function to call the graphql client
* @param id - required id to pass to project
* @param variables - variables without 'id' to pass into the Project_RelationsQuery
*/
var Project_RelationsQuery = class extends Request {
	_id;
	_variables;
	constructor(request, id, variables) {
		super(request);
		this._id = id;
		this._variables = variables;
	}
	/**
	* Call the Project_Relations query and return a ProjectRelationConnection
	*
	* @param variables - variables without 'id' to pass into the Project_RelationsQuery
	* @returns parsed response from Project_RelationsQuery
	*/
	async fetch(variables) {
		const data = (await this._request(Project_RelationsDocument.toString(), {
			id: this._id,
			...this._variables,
			...variables
		})).project.relations;
		return new ProjectRelationConnection(this._request, (connection) => this.fetch(defaultConnection({
			...this._variables,
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable Project_Teams Query
*
* @param request - function to call the graphql client
* @param id - required id to pass to project
* @param variables - variables without 'id' to pass into the Project_TeamsQuery
*/
var Project_TeamsQuery = class extends Request {
	_id;
	_variables;
	constructor(request, id, variables) {
		super(request);
		this._id = id;
		this._variables = variables;
	}
	/**
	* Call the Project_Teams query and return a TeamConnection
	*
	* @param variables - variables without 'id' to pass into the Project_TeamsQuery
	* @returns parsed response from Project_TeamsQuery
	*/
	async fetch(variables) {
		const data = (await this._request(Project_TeamsDocument.toString(), {
			id: this._id,
			...this._variables,
			...variables
		})).project.teams;
		return new TeamConnection(this._request, (connection) => this.fetch(defaultConnection({
			...this._variables,
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable Project_DocumentContent_AiPromptRules Query
*
* @param request - function to call the graphql client
* @param id - required id to pass to project_documentContent
*/
var Project_DocumentContent_AiPromptRulesQuery = class extends Request {
	_id;
	constructor(request, id) {
		super(request);
		this._id = id;
	}
	/**
	* Call the Project_DocumentContent_AiPromptRules query and return a AiPromptRules
	*
	* @returns parsed response from Project_DocumentContent_AiPromptRulesQuery
	*/
	async fetch() {
		const data = (await this._request(Project_DocumentContent_AiPromptRulesDocument.toString(), { id: this._id })).project.documentContent?.aiPromptRules;
		return data ? new AiPromptRules(this._request, data) : void 0;
	}
};
/**
* A fetchable ProjectLabel_Children Query
*
* @param request - function to call the graphql client
* @param id - required id to pass to projectLabel
* @param variables - variables without 'id' to pass into the ProjectLabel_ChildrenQuery
*/
var ProjectLabel_ChildrenQuery = class extends Request {
	_id;
	_variables;
	constructor(request, id, variables) {
		super(request);
		this._id = id;
		this._variables = variables;
	}
	/**
	* Call the ProjectLabel_Children query and return a ProjectLabelConnection
	*
	* @param variables - variables without 'id' to pass into the ProjectLabel_ChildrenQuery
	* @returns parsed response from ProjectLabel_ChildrenQuery
	*/
	async fetch(variables) {
		const data = (await this._request(ProjectLabel_ChildrenDocument.toString(), {
			id: this._id,
			...this._variables,
			...variables
		})).projectLabel.children;
		return new ProjectLabelConnection(this._request, (connection) => this.fetch(defaultConnection({
			...this._variables,
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable ProjectLabel_Projects Query
*
* @param request - function to call the graphql client
* @param id - required id to pass to projectLabel
* @param variables - variables without 'id' to pass into the ProjectLabel_ProjectsQuery
*/
var ProjectLabel_ProjectsQuery = class extends Request {
	_id;
	_variables;
	constructor(request, id, variables) {
		super(request);
		this._id = id;
		this._variables = variables;
	}
	/**
	* Call the ProjectLabel_Projects query and return a ProjectConnection
	*
	* @param variables - variables without 'id' to pass into the ProjectLabel_ProjectsQuery
	* @returns parsed response from ProjectLabel_ProjectsQuery
	*/
	async fetch(variables) {
		const data = (await this._request(ProjectLabel_ProjectsDocument.toString(), {
			id: this._id,
			...this._variables,
			...variables
		})).projectLabel.projects;
		return new ProjectConnection(this._request, (connection) => this.fetch(defaultConnection({
			...this._variables,
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable ProjectMilestone_DocumentContent Query
*
* @param request - function to call the graphql client
* @param id - required id to pass to projectMilestone
*/
var ProjectMilestone_DocumentContentQuery = class extends Request {
	_id;
	constructor(request, id) {
		super(request);
		this._id = id;
	}
	/**
	* Call the ProjectMilestone_DocumentContent query and return a DocumentContent
	*
	* @returns parsed response from ProjectMilestone_DocumentContentQuery
	*/
	async fetch() {
		const data = (await this._request(ProjectMilestone_DocumentContentDocument.toString(), { id: this._id })).projectMilestone.documentContent;
		return data ? new DocumentContent(this._request, data) : void 0;
	}
};
/**
* A fetchable ProjectMilestone_Issues Query
*
* @param request - function to call the graphql client
* @param id - required id to pass to projectMilestone
* @param variables - variables without 'id' to pass into the ProjectMilestone_IssuesQuery
*/
var ProjectMilestone_IssuesQuery = class extends Request {
	_id;
	_variables;
	constructor(request, id, variables) {
		super(request);
		this._id = id;
		this._variables = variables;
	}
	/**
	* Call the ProjectMilestone_Issues query and return a IssueConnection
	*
	* @param variables - variables without 'id' to pass into the ProjectMilestone_IssuesQuery
	* @returns parsed response from ProjectMilestone_IssuesQuery
	*/
	async fetch(variables) {
		const data = (await this._request(ProjectMilestone_IssuesDocument.toString(), {
			id: this._id,
			...this._variables,
			...variables
		})).projectMilestone.issues;
		return new IssueConnection(this._request, (connection) => this.fetch(defaultConnection({
			...this._variables,
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable ProjectMilestone_DocumentContent_AiPromptRules Query
*
* @param request - function to call the graphql client
* @param id - required id to pass to projectMilestone_documentContent
*/
var ProjectMilestone_DocumentContent_AiPromptRulesQuery = class extends Request {
	_id;
	constructor(request, id) {
		super(request);
		this._id = id;
	}
	/**
	* Call the ProjectMilestone_DocumentContent_AiPromptRules query and return a AiPromptRules
	*
	* @returns parsed response from ProjectMilestone_DocumentContent_AiPromptRulesQuery
	*/
	async fetch() {
		const data = (await this._request(ProjectMilestone_DocumentContent_AiPromptRulesDocument.toString(), { id: this._id })).projectMilestone.documentContent?.aiPromptRules;
		return data ? new AiPromptRules(this._request, data) : void 0;
	}
};
/**
* A fetchable ProjectUpdate_Comments Query
*
* @param request - function to call the graphql client
* @param id - required id to pass to projectUpdate
* @param variables - variables without 'id' to pass into the ProjectUpdate_CommentsQuery
*/
var ProjectUpdate_CommentsQuery = class extends Request {
	_id;
	_variables;
	constructor(request, id, variables) {
		super(request);
		this._id = id;
		this._variables = variables;
	}
	/**
	* Call the ProjectUpdate_Comments query and return a CommentConnection
	*
	* @param variables - variables without 'id' to pass into the ProjectUpdate_CommentsQuery
	* @returns parsed response from ProjectUpdate_CommentsQuery
	*/
	async fetch(variables) {
		const data = (await this._request(ProjectUpdate_CommentsDocument.toString(), {
			id: this._id,
			...this._variables,
			...variables
		})).projectUpdate.comments;
		return new CommentConnection(this._request, (connection) => this.fetch(defaultConnection({
			...this._variables,
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable Roadmap_Projects Query
*
* @param request - function to call the graphql client
* @param id - required id to pass to roadmap
* @param variables - variables without 'id' to pass into the Roadmap_ProjectsQuery
*/
var Roadmap_ProjectsQuery = class extends Request {
	_id;
	_variables;
	constructor(request, id, variables) {
		super(request);
		this._id = id;
		this._variables = variables;
	}
	/**
	* Call the Roadmap_Projects query and return a ProjectConnection
	*
	* @param variables - variables without 'id' to pass into the Roadmap_ProjectsQuery
	* @returns parsed response from Roadmap_ProjectsQuery
	*/
	async fetch(variables) {
		const data = (await this._request(Roadmap_ProjectsDocument.toString(), {
			id: this._id,
			...this._variables,
			...variables
		})).roadmap.projects;
		return new ProjectConnection(this._request, (connection) => this.fetch(defaultConnection({
			...this._variables,
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable SearchDocuments_ArchivePayload Query
*
* @param request - function to call the graphql client
* @param term - required term to pass to searchDocuments
* @param variables - variables without 'term' to pass into the SearchDocuments_ArchivePayloadQuery
*/
var SearchDocuments_ArchivePayloadQuery = class extends Request {
	_term;
	_variables;
	constructor(request, term, variables) {
		super(request);
		this._term = term;
		this._variables = variables;
	}
	/**
	* Call the SearchDocuments_ArchivePayload query and return a ArchiveResponse
	*
	* @param variables - variables without 'term' to pass into the SearchDocuments_ArchivePayloadQuery
	* @returns parsed response from SearchDocuments_ArchivePayloadQuery
	*/
	async fetch(variables) {
		const data = (await this._request(SearchDocuments_ArchivePayloadDocument.toString(), {
			term: this._term,
			...this._variables,
			...variables
		})).searchDocuments.archivePayload;
		return new ArchiveResponse(this._request, data);
	}
};
/**
* A fetchable SearchIssues_ArchivePayload Query
*
* @param request - function to call the graphql client
* @param term - required term to pass to searchIssues
* @param variables - variables without 'term' to pass into the SearchIssues_ArchivePayloadQuery
*/
var SearchIssues_ArchivePayloadQuery = class extends Request {
	_term;
	_variables;
	constructor(request, term, variables) {
		super(request);
		this._term = term;
		this._variables = variables;
	}
	/**
	* Call the SearchIssues_ArchivePayload query and return a ArchiveResponse
	*
	* @param variables - variables without 'term' to pass into the SearchIssues_ArchivePayloadQuery
	* @returns parsed response from SearchIssues_ArchivePayloadQuery
	*/
	async fetch(variables) {
		const data = (await this._request(SearchIssues_ArchivePayloadDocument.toString(), {
			term: this._term,
			...this._variables,
			...variables
		})).searchIssues.archivePayload;
		return new ArchiveResponse(this._request, data);
	}
};
/**
* A fetchable SearchProjects_ArchivePayload Query
*
* @param request - function to call the graphql client
* @param term - required term to pass to searchProjects
* @param variables - variables without 'term' to pass into the SearchProjects_ArchivePayloadQuery
*/
var SearchProjects_ArchivePayloadQuery = class extends Request {
	_term;
	_variables;
	constructor(request, term, variables) {
		super(request);
		this._term = term;
		this._variables = variables;
	}
	/**
	* Call the SearchProjects_ArchivePayload query and return a ArchiveResponse
	*
	* @param variables - variables without 'term' to pass into the SearchProjects_ArchivePayloadQuery
	* @returns parsed response from SearchProjects_ArchivePayloadQuery
	*/
	async fetch(variables) {
		const data = (await this._request(SearchProjects_ArchivePayloadDocument.toString(), {
			term: this._term,
			...this._variables,
			...variables
		})).searchProjects.archivePayload;
		return new ArchiveResponse(this._request, data);
	}
};
/**
* A fetchable Team_Cycles Query
*
* @param request - function to call the graphql client
* @param id - required id to pass to team
* @param variables - variables without 'id' to pass into the Team_CyclesQuery
*/
var Team_CyclesQuery = class extends Request {
	_id;
	_variables;
	constructor(request, id, variables) {
		super(request);
		this._id = id;
		this._variables = variables;
	}
	/**
	* Call the Team_Cycles query and return a CycleConnection
	*
	* @param variables - variables without 'id' to pass into the Team_CyclesQuery
	* @returns parsed response from Team_CyclesQuery
	*/
	async fetch(variables) {
		const data = (await this._request(Team_CyclesDocument.toString(), {
			id: this._id,
			...this._variables,
			...variables
		})).team.cycles;
		return new CycleConnection(this._request, (connection) => this.fetch(defaultConnection({
			...this._variables,
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable Team_GitAutomationStates Query
*
* @param request - function to call the graphql client
* @param id - required id to pass to team
* @param variables - variables without 'id' to pass into the Team_GitAutomationStatesQuery
*/
var Team_GitAutomationStatesQuery = class extends Request {
	_id;
	_variables;
	constructor(request, id, variables) {
		super(request);
		this._id = id;
		this._variables = variables;
	}
	/**
	* Call the Team_GitAutomationStates query and return a GitAutomationStateConnection
	*
	* @param variables - variables without 'id' to pass into the Team_GitAutomationStatesQuery
	* @returns parsed response from Team_GitAutomationStatesQuery
	*/
	async fetch(variables) {
		const data = (await this._request(Team_GitAutomationStatesDocument.toString(), {
			id: this._id,
			...this._variables,
			...variables
		})).team.gitAutomationStates;
		return new GitAutomationStateConnection(this._request, (connection) => this.fetch(defaultConnection({
			...this._variables,
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable Team_Issues Query
*
* @param request - function to call the graphql client
* @param id - required id to pass to team
* @param variables - variables without 'id' to pass into the Team_IssuesQuery
*/
var Team_IssuesQuery = class extends Request {
	_id;
	_variables;
	constructor(request, id, variables) {
		super(request);
		this._id = id;
		this._variables = variables;
	}
	/**
	* Call the Team_Issues query and return a IssueConnection
	*
	* @param variables - variables without 'id' to pass into the Team_IssuesQuery
	* @returns parsed response from Team_IssuesQuery
	*/
	async fetch(variables) {
		const data = (await this._request(Team_IssuesDocument.toString(), {
			id: this._id,
			...this._variables,
			...variables
		})).team.issues;
		return new IssueConnection(this._request, (connection) => this.fetch(defaultConnection({
			...this._variables,
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable Team_Labels Query
*
* @param request - function to call the graphql client
* @param id - required id to pass to team
* @param variables - variables without 'id' to pass into the Team_LabelsQuery
*/
var Team_LabelsQuery = class extends Request {
	_id;
	_variables;
	constructor(request, id, variables) {
		super(request);
		this._id = id;
		this._variables = variables;
	}
	/**
	* Call the Team_Labels query and return a IssueLabelConnection
	*
	* @param variables - variables without 'id' to pass into the Team_LabelsQuery
	* @returns parsed response from Team_LabelsQuery
	*/
	async fetch(variables) {
		const data = (await this._request(Team_LabelsDocument.toString(), {
			id: this._id,
			...this._variables,
			...variables
		})).team.labels;
		return new IssueLabelConnection(this._request, (connection) => this.fetch(defaultConnection({
			...this._variables,
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable Team_Members Query
*
* @param request - function to call the graphql client
* @param id - required id to pass to team
* @param variables - variables without 'id' to pass into the Team_MembersQuery
*/
var Team_MembersQuery = class extends Request {
	_id;
	_variables;
	constructor(request, id, variables) {
		super(request);
		this._id = id;
		this._variables = variables;
	}
	/**
	* Call the Team_Members query and return a UserConnection
	*
	* @param variables - variables without 'id' to pass into the Team_MembersQuery
	* @returns parsed response from Team_MembersQuery
	*/
	async fetch(variables) {
		const data = (await this._request(Team_MembersDocument.toString(), {
			id: this._id,
			...this._variables,
			...variables
		})).team.members;
		return new UserConnection(this._request, (connection) => this.fetch(defaultConnection({
			...this._variables,
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable Team_Memberships Query
*
* @param request - function to call the graphql client
* @param id - required id to pass to team
* @param variables - variables without 'id' to pass into the Team_MembershipsQuery
*/
var Team_MembershipsQuery = class extends Request {
	_id;
	_variables;
	constructor(request, id, variables) {
		super(request);
		this._id = id;
		this._variables = variables;
	}
	/**
	* Call the Team_Memberships query and return a TeamMembershipConnection
	*
	* @param variables - variables without 'id' to pass into the Team_MembershipsQuery
	* @returns parsed response from Team_MembershipsQuery
	*/
	async fetch(variables) {
		const data = (await this._request(Team_MembershipsDocument.toString(), {
			id: this._id,
			...this._variables,
			...variables
		})).team.memberships;
		return new TeamMembershipConnection(this._request, (connection) => this.fetch(defaultConnection({
			...this._variables,
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable Team_Projects Query
*
* @param request - function to call the graphql client
* @param id - required id to pass to team
* @param variables - variables without 'id' to pass into the Team_ProjectsQuery
*/
var Team_ProjectsQuery = class extends Request {
	_id;
	_variables;
	constructor(request, id, variables) {
		super(request);
		this._id = id;
		this._variables = variables;
	}
	/**
	* Call the Team_Projects query and return a ProjectConnection
	*
	* @param variables - variables without 'id' to pass into the Team_ProjectsQuery
	* @returns parsed response from Team_ProjectsQuery
	*/
	async fetch(variables) {
		const data = (await this._request(Team_ProjectsDocument.toString(), {
			id: this._id,
			...this._variables,
			...variables
		})).team.projects;
		return new ProjectConnection(this._request, (connection) => this.fetch(defaultConnection({
			...this._variables,
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable Team_States Query
*
* @param request - function to call the graphql client
* @param id - required id to pass to team
* @param variables - variables without 'id' to pass into the Team_StatesQuery
*/
var Team_StatesQuery = class extends Request {
	_id;
	_variables;
	constructor(request, id, variables) {
		super(request);
		this._id = id;
		this._variables = variables;
	}
	/**
	* Call the Team_States query and return a WorkflowStateConnection
	*
	* @param variables - variables without 'id' to pass into the Team_StatesQuery
	* @returns parsed response from Team_StatesQuery
	*/
	async fetch(variables) {
		const data = (await this._request(Team_StatesDocument.toString(), {
			id: this._id,
			...this._variables,
			...variables
		})).team.states;
		return new WorkflowStateConnection(this._request, (connection) => this.fetch(defaultConnection({
			...this._variables,
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable Team_Templates Query
*
* @param request - function to call the graphql client
* @param id - required id to pass to team
* @param variables - variables without 'id' to pass into the Team_TemplatesQuery
*/
var Team_TemplatesQuery = class extends Request {
	_id;
	_variables;
	constructor(request, id, variables) {
		super(request);
		this._id = id;
		this._variables = variables;
	}
	/**
	* Call the Team_Templates query and return a TemplateConnection
	*
	* @param variables - variables without 'id' to pass into the Team_TemplatesQuery
	* @returns parsed response from Team_TemplatesQuery
	*/
	async fetch(variables) {
		const data = (await this._request(Team_TemplatesDocument.toString(), {
			id: this._id,
			...this._variables,
			...variables
		})).team.templates;
		return new TemplateConnection(this._request, (connection) => this.fetch(defaultConnection({
			...this._variables,
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable Team_Webhooks Query
*
* @param request - function to call the graphql client
* @param id - required id to pass to team
* @param variables - variables without 'id' to pass into the Team_WebhooksQuery
*/
var Team_WebhooksQuery = class extends Request {
	_id;
	_variables;
	constructor(request, id, variables) {
		super(request);
		this._id = id;
		this._variables = variables;
	}
	/**
	* Call the Team_Webhooks query and return a WebhookConnection
	*
	* @param variables - variables without 'id' to pass into the Team_WebhooksQuery
	* @returns parsed response from Team_WebhooksQuery
	*/
	async fetch(variables) {
		const data = (await this._request(Team_WebhooksDocument.toString(), {
			id: this._id,
			...this._variables,
			...variables
		})).team.webhooks;
		return new WebhookConnection(this._request, (connection) => this.fetch(defaultConnection({
			...this._variables,
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable TriageResponsibility_ManualSelection Query
*
* @param request - function to call the graphql client
* @param id - required id to pass to triageResponsibility
*/
var TriageResponsibility_ManualSelectionQuery = class extends Request {
	_id;
	constructor(request, id) {
		super(request);
		this._id = id;
	}
	/**
	* Call the TriageResponsibility_ManualSelection query and return a TriageResponsibilityManualSelection
	*
	* @returns parsed response from TriageResponsibility_ManualSelectionQuery
	*/
	async fetch() {
		const data = (await this._request(TriageResponsibility_ManualSelectionDocument.toString(), { id: this._id })).triageResponsibility.manualSelection;
		return data ? new TriageResponsibilityManualSelection(this._request, data) : void 0;
	}
};
/**
* A fetchable User_AssignedIssues Query
*
* @param request - function to call the graphql client
* @param id - required id to pass to user
* @param variables - variables without 'id' to pass into the User_AssignedIssuesQuery
*/
var User_AssignedIssuesQuery = class extends Request {
	_id;
	_variables;
	constructor(request, id, variables) {
		super(request);
		this._id = id;
		this._variables = variables;
	}
	/**
	* Call the User_AssignedIssues query and return a IssueConnection
	*
	* @param variables - variables without 'id' to pass into the User_AssignedIssuesQuery
	* @returns parsed response from User_AssignedIssuesQuery
	*/
	async fetch(variables) {
		const data = (await this._request(User_AssignedIssuesDocument.toString(), {
			id: this._id,
			...this._variables,
			...variables
		})).user.assignedIssues;
		return new IssueConnection(this._request, (connection) => this.fetch(defaultConnection({
			...this._variables,
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable User_CreatedIssues Query
*
* @param request - function to call the graphql client
* @param id - required id to pass to user
* @param variables - variables without 'id' to pass into the User_CreatedIssuesQuery
*/
var User_CreatedIssuesQuery = class extends Request {
	_id;
	_variables;
	constructor(request, id, variables) {
		super(request);
		this._id = id;
		this._variables = variables;
	}
	/**
	* Call the User_CreatedIssues query and return a IssueConnection
	*
	* @param variables - variables without 'id' to pass into the User_CreatedIssuesQuery
	* @returns parsed response from User_CreatedIssuesQuery
	*/
	async fetch(variables) {
		const data = (await this._request(User_CreatedIssuesDocument.toString(), {
			id: this._id,
			...this._variables,
			...variables
		})).user.createdIssues;
		return new IssueConnection(this._request, (connection) => this.fetch(defaultConnection({
			...this._variables,
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable User_DelegatedIssues Query
*
* @param request - function to call the graphql client
* @param id - required id to pass to user
* @param variables - variables without 'id' to pass into the User_DelegatedIssuesQuery
*/
var User_DelegatedIssuesQuery = class extends Request {
	_id;
	_variables;
	constructor(request, id, variables) {
		super(request);
		this._id = id;
		this._variables = variables;
	}
	/**
	* Call the User_DelegatedIssues query and return a IssueConnection
	*
	* @param variables - variables without 'id' to pass into the User_DelegatedIssuesQuery
	* @returns parsed response from User_DelegatedIssuesQuery
	*/
	async fetch(variables) {
		const data = (await this._request(User_DelegatedIssuesDocument.toString(), {
			id: this._id,
			...this._variables,
			...variables
		})).user.delegatedIssues;
		return new IssueConnection(this._request, (connection) => this.fetch(defaultConnection({
			...this._variables,
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable User_Drafts Query
*
* @param request - function to call the graphql client
* @param id - required id to pass to user
* @param variables - variables without 'id' to pass into the User_DraftsQuery
*/
var User_DraftsQuery = class extends Request {
	_id;
	_variables;
	constructor(request, id, variables) {
		super(request);
		this._id = id;
		this._variables = variables;
	}
	/**
	* Call the User_Drafts query and return a DraftConnection
	*
	* @param variables - variables without 'id' to pass into the User_DraftsQuery
	* @returns parsed response from User_DraftsQuery
	*/
	async fetch(variables) {
		const data = (await this._request(User_DraftsDocument.toString(), {
			id: this._id,
			...this._variables,
			...variables
		})).user.drafts;
		return new DraftConnection(this._request, (connection) => this.fetch(defaultConnection({
			...this._variables,
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable User_TeamMemberships Query
*
* @param request - function to call the graphql client
* @param id - required id to pass to user
* @param variables - variables without 'id' to pass into the User_TeamMembershipsQuery
*/
var User_TeamMembershipsQuery = class extends Request {
	_id;
	_variables;
	constructor(request, id, variables) {
		super(request);
		this._id = id;
		this._variables = variables;
	}
	/**
	* Call the User_TeamMemberships query and return a TeamMembershipConnection
	*
	* @param variables - variables without 'id' to pass into the User_TeamMembershipsQuery
	* @returns parsed response from User_TeamMembershipsQuery
	*/
	async fetch(variables) {
		const data = (await this._request(User_TeamMembershipsDocument.toString(), {
			id: this._id,
			...this._variables,
			...variables
		})).user.teamMemberships;
		return new TeamMembershipConnection(this._request, (connection) => this.fetch(defaultConnection({
			...this._variables,
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable User_Teams Query
*
* @param request - function to call the graphql client
* @param id - required id to pass to user
* @param variables - variables without 'id' to pass into the User_TeamsQuery
*/
var User_TeamsQuery = class extends Request {
	_id;
	_variables;
	constructor(request, id, variables) {
		super(request);
		this._id = id;
		this._variables = variables;
	}
	/**
	* Call the User_Teams query and return a TeamConnection
	*
	* @param variables - variables without 'id' to pass into the User_TeamsQuery
	* @returns parsed response from User_TeamsQuery
	*/
	async fetch(variables) {
		const data = (await this._request(User_TeamsDocument.toString(), {
			id: this._id,
			...this._variables,
			...variables
		})).user.teams;
		return new TeamConnection(this._request, (connection) => this.fetch(defaultConnection({
			...this._variables,
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable UserSettings_NotificationCategoryPreferences Query
*
* @param request - function to call the graphql client
*/
var UserSettings_NotificationCategoryPreferencesQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the UserSettings_NotificationCategoryPreferences query and return a NotificationCategoryPreferences
	*
	* @returns parsed response from UserSettings_NotificationCategoryPreferencesQuery
	*/
	async fetch() {
		const data = (await this._request(UserSettings_NotificationCategoryPreferencesDocument.toString(), {})).userSettings.notificationCategoryPreferences;
		return new NotificationCategoryPreferences(this._request, data);
	}
};
/**
* A fetchable UserSettings_NotificationChannelPreferences Query
*
* @param request - function to call the graphql client
*/
var UserSettings_NotificationChannelPreferencesQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the UserSettings_NotificationChannelPreferences query and return a NotificationChannelPreferences
	*
	* @returns parsed response from UserSettings_NotificationChannelPreferencesQuery
	*/
	async fetch() {
		const data = (await this._request(UserSettings_NotificationChannelPreferencesDocument.toString(), {})).userSettings.notificationChannelPreferences;
		return new NotificationChannelPreferences(this._request, data);
	}
};
/**
* A fetchable UserSettings_NotificationDeliveryPreferences Query
*
* @param request - function to call the graphql client
*/
var UserSettings_NotificationDeliveryPreferencesQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the UserSettings_NotificationDeliveryPreferences query and return a NotificationDeliveryPreferences
	*
	* @returns parsed response from UserSettings_NotificationDeliveryPreferencesQuery
	*/
	async fetch() {
		const data = (await this._request(UserSettings_NotificationDeliveryPreferencesDocument.toString(), {})).userSettings.notificationDeliveryPreferences;
		return new NotificationDeliveryPreferences(this._request, data);
	}
};
/**
* A fetchable UserSettings_Theme Query
*
* @param request - function to call the graphql client
* @param variables - variables to pass into the UserSettings_ThemeQuery
*/
var UserSettings_ThemeQuery = class extends Request {
	_variables;
	constructor(request, variables) {
		super(request);
		this._variables = variables;
	}
	/**
	* Call the UserSettings_Theme query and return a UserSettingsTheme
	*
	* @param variables - variables to pass into the UserSettings_ThemeQuery
	* @returns parsed response from UserSettings_ThemeQuery
	*/
	async fetch(variables) {
		const data = (await this._request(UserSettings_ThemeDocument.toString(), variables)).userSettings.theme;
		return data ? new UserSettingsTheme(this._request, data) : void 0;
	}
};
/**
* A fetchable UserSettings_NotificationCategoryPreferences_AppsAndIntegrations Query
*
* @param request - function to call the graphql client
*/
var UserSettings_NotificationCategoryPreferences_AppsAndIntegrationsQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the UserSettings_NotificationCategoryPreferences_AppsAndIntegrations query and return a NotificationChannelPreferences
	*
	* @returns parsed response from UserSettings_NotificationCategoryPreferences_AppsAndIntegrationsQuery
	*/
	async fetch() {
		const data = (await this._request(UserSettings_NotificationCategoryPreferences_AppsAndIntegrationsDocument.toString(), {})).userSettings.notificationCategoryPreferences.appsAndIntegrations;
		return new NotificationChannelPreferences(this._request, data);
	}
};
/**
* A fetchable UserSettings_NotificationCategoryPreferences_Assignments Query
*
* @param request - function to call the graphql client
*/
var UserSettings_NotificationCategoryPreferences_AssignmentsQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the UserSettings_NotificationCategoryPreferences_Assignments query and return a NotificationChannelPreferences
	*
	* @returns parsed response from UserSettings_NotificationCategoryPreferences_AssignmentsQuery
	*/
	async fetch() {
		const data = (await this._request(UserSettings_NotificationCategoryPreferences_AssignmentsDocument.toString(), {})).userSettings.notificationCategoryPreferences.assignments;
		return new NotificationChannelPreferences(this._request, data);
	}
};
/**
* A fetchable UserSettings_NotificationCategoryPreferences_CommentsAndReplies Query
*
* @param request - function to call the graphql client
*/
var UserSettings_NotificationCategoryPreferences_CommentsAndRepliesQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the UserSettings_NotificationCategoryPreferences_CommentsAndReplies query and return a NotificationChannelPreferences
	*
	* @returns parsed response from UserSettings_NotificationCategoryPreferences_CommentsAndRepliesQuery
	*/
	async fetch() {
		const data = (await this._request(UserSettings_NotificationCategoryPreferences_CommentsAndRepliesDocument.toString(), {})).userSettings.notificationCategoryPreferences.commentsAndReplies;
		return new NotificationChannelPreferences(this._request, data);
	}
};
/**
* A fetchable UserSettings_NotificationCategoryPreferences_Customers Query
*
* @param request - function to call the graphql client
*/
var UserSettings_NotificationCategoryPreferences_CustomersQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the UserSettings_NotificationCategoryPreferences_Customers query and return a NotificationChannelPreferences
	*
	* @returns parsed response from UserSettings_NotificationCategoryPreferences_CustomersQuery
	*/
	async fetch() {
		const data = (await this._request(UserSettings_NotificationCategoryPreferences_CustomersDocument.toString(), {})).userSettings.notificationCategoryPreferences.customers;
		return new NotificationChannelPreferences(this._request, data);
	}
};
/**
* A fetchable UserSettings_NotificationCategoryPreferences_DocumentChanges Query
*
* @param request - function to call the graphql client
*/
var UserSettings_NotificationCategoryPreferences_DocumentChangesQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the UserSettings_NotificationCategoryPreferences_DocumentChanges query and return a NotificationChannelPreferences
	*
	* @returns parsed response from UserSettings_NotificationCategoryPreferences_DocumentChangesQuery
	*/
	async fetch() {
		const data = (await this._request(UserSettings_NotificationCategoryPreferences_DocumentChangesDocument.toString(), {})).userSettings.notificationCategoryPreferences.documentChanges;
		return new NotificationChannelPreferences(this._request, data);
	}
};
/**
* A fetchable UserSettings_NotificationCategoryPreferences_Feed Query
*
* @param request - function to call the graphql client
*/
var UserSettings_NotificationCategoryPreferences_FeedQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the UserSettings_NotificationCategoryPreferences_Feed query and return a NotificationChannelPreferences
	*
	* @returns parsed response from UserSettings_NotificationCategoryPreferences_FeedQuery
	*/
	async fetch() {
		const data = (await this._request(UserSettings_NotificationCategoryPreferences_FeedDocument.toString(), {})).userSettings.notificationCategoryPreferences.feed;
		return new NotificationChannelPreferences(this._request, data);
	}
};
/**
* A fetchable UserSettings_NotificationCategoryPreferences_Mentions Query
*
* @param request - function to call the graphql client
*/
var UserSettings_NotificationCategoryPreferences_MentionsQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the UserSettings_NotificationCategoryPreferences_Mentions query and return a NotificationChannelPreferences
	*
	* @returns parsed response from UserSettings_NotificationCategoryPreferences_MentionsQuery
	*/
	async fetch() {
		const data = (await this._request(UserSettings_NotificationCategoryPreferences_MentionsDocument.toString(), {})).userSettings.notificationCategoryPreferences.mentions;
		return new NotificationChannelPreferences(this._request, data);
	}
};
/**
* A fetchable UserSettings_NotificationCategoryPreferences_PostsAndUpdates Query
*
* @param request - function to call the graphql client
*/
var UserSettings_NotificationCategoryPreferences_PostsAndUpdatesQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the UserSettings_NotificationCategoryPreferences_PostsAndUpdates query and return a NotificationChannelPreferences
	*
	* @returns parsed response from UserSettings_NotificationCategoryPreferences_PostsAndUpdatesQuery
	*/
	async fetch() {
		const data = (await this._request(UserSettings_NotificationCategoryPreferences_PostsAndUpdatesDocument.toString(), {})).userSettings.notificationCategoryPreferences.postsAndUpdates;
		return new NotificationChannelPreferences(this._request, data);
	}
};
/**
* A fetchable UserSettings_NotificationCategoryPreferences_Reactions Query
*
* @param request - function to call the graphql client
*/
var UserSettings_NotificationCategoryPreferences_ReactionsQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the UserSettings_NotificationCategoryPreferences_Reactions query and return a NotificationChannelPreferences
	*
	* @returns parsed response from UserSettings_NotificationCategoryPreferences_ReactionsQuery
	*/
	async fetch() {
		const data = (await this._request(UserSettings_NotificationCategoryPreferences_ReactionsDocument.toString(), {})).userSettings.notificationCategoryPreferences.reactions;
		return new NotificationChannelPreferences(this._request, data);
	}
};
/**
* A fetchable UserSettings_NotificationCategoryPreferences_Reminders Query
*
* @param request - function to call the graphql client
*/
var UserSettings_NotificationCategoryPreferences_RemindersQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the UserSettings_NotificationCategoryPreferences_Reminders query and return a NotificationChannelPreferences
	*
	* @returns parsed response from UserSettings_NotificationCategoryPreferences_RemindersQuery
	*/
	async fetch() {
		const data = (await this._request(UserSettings_NotificationCategoryPreferences_RemindersDocument.toString(), {})).userSettings.notificationCategoryPreferences.reminders;
		return new NotificationChannelPreferences(this._request, data);
	}
};
/**
* A fetchable UserSettings_NotificationCategoryPreferences_Reviews Query
*
* @param request - function to call the graphql client
*/
var UserSettings_NotificationCategoryPreferences_ReviewsQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the UserSettings_NotificationCategoryPreferences_Reviews query and return a NotificationChannelPreferences
	*
	* @returns parsed response from UserSettings_NotificationCategoryPreferences_ReviewsQuery
	*/
	async fetch() {
		const data = (await this._request(UserSettings_NotificationCategoryPreferences_ReviewsDocument.toString(), {})).userSettings.notificationCategoryPreferences.reviews;
		return new NotificationChannelPreferences(this._request, data);
	}
};
/**
* A fetchable UserSettings_NotificationCategoryPreferences_StatusChanges Query
*
* @param request - function to call the graphql client
*/
var UserSettings_NotificationCategoryPreferences_StatusChangesQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the UserSettings_NotificationCategoryPreferences_StatusChanges query and return a NotificationChannelPreferences
	*
	* @returns parsed response from UserSettings_NotificationCategoryPreferences_StatusChangesQuery
	*/
	async fetch() {
		const data = (await this._request(UserSettings_NotificationCategoryPreferences_StatusChangesDocument.toString(), {})).userSettings.notificationCategoryPreferences.statusChanges;
		return new NotificationChannelPreferences(this._request, data);
	}
};
/**
* A fetchable UserSettings_NotificationCategoryPreferences_Subscriptions Query
*
* @param request - function to call the graphql client
*/
var UserSettings_NotificationCategoryPreferences_SubscriptionsQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the UserSettings_NotificationCategoryPreferences_Subscriptions query and return a NotificationChannelPreferences
	*
	* @returns parsed response from UserSettings_NotificationCategoryPreferences_SubscriptionsQuery
	*/
	async fetch() {
		const data = (await this._request(UserSettings_NotificationCategoryPreferences_SubscriptionsDocument.toString(), {})).userSettings.notificationCategoryPreferences.subscriptions;
		return new NotificationChannelPreferences(this._request, data);
	}
};
/**
* A fetchable UserSettings_NotificationCategoryPreferences_System Query
*
* @param request - function to call the graphql client
*/
var UserSettings_NotificationCategoryPreferences_SystemQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the UserSettings_NotificationCategoryPreferences_System query and return a NotificationChannelPreferences
	*
	* @returns parsed response from UserSettings_NotificationCategoryPreferences_SystemQuery
	*/
	async fetch() {
		const data = (await this._request(UserSettings_NotificationCategoryPreferences_SystemDocument.toString(), {})).userSettings.notificationCategoryPreferences.system;
		return new NotificationChannelPreferences(this._request, data);
	}
};
/**
* A fetchable UserSettings_NotificationCategoryPreferences_Triage Query
*
* @param request - function to call the graphql client
*/
var UserSettings_NotificationCategoryPreferences_TriageQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the UserSettings_NotificationCategoryPreferences_Triage query and return a NotificationChannelPreferences
	*
	* @returns parsed response from UserSettings_NotificationCategoryPreferences_TriageQuery
	*/
	async fetch() {
		const data = (await this._request(UserSettings_NotificationCategoryPreferences_TriageDocument.toString(), {})).userSettings.notificationCategoryPreferences.triage;
		return new NotificationChannelPreferences(this._request, data);
	}
};
/**
* A fetchable UserSettings_NotificationDeliveryPreferences_Mobile Query
*
* @param request - function to call the graphql client
*/
var UserSettings_NotificationDeliveryPreferences_MobileQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the UserSettings_NotificationDeliveryPreferences_Mobile query and return a NotificationDeliveryPreferencesChannel
	*
	* @returns parsed response from UserSettings_NotificationDeliveryPreferences_MobileQuery
	*/
	async fetch() {
		const data = (await this._request(UserSettings_NotificationDeliveryPreferences_MobileDocument.toString(), {})).userSettings.notificationDeliveryPreferences.mobile;
		return data ? new NotificationDeliveryPreferencesChannel(this._request, data) : void 0;
	}
};
/**
* A fetchable UserSettings_NotificationDeliveryPreferences_Mobile_Schedule Query
*
* @param request - function to call the graphql client
*/
var UserSettings_NotificationDeliveryPreferences_Mobile_ScheduleQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the UserSettings_NotificationDeliveryPreferences_Mobile_Schedule query and return a NotificationDeliveryPreferencesSchedule
	*
	* @returns parsed response from UserSettings_NotificationDeliveryPreferences_Mobile_ScheduleQuery
	*/
	async fetch() {
		const data = (await this._request(UserSettings_NotificationDeliveryPreferences_Mobile_ScheduleDocument.toString(), {})).userSettings.notificationDeliveryPreferences.mobile?.schedule;
		return data ? new NotificationDeliveryPreferencesSchedule(this._request, data) : void 0;
	}
};
/**
* A fetchable UserSettings_NotificationDeliveryPreferences_Mobile_Schedule_Friday Query
*
* @param request - function to call the graphql client
*/
var UserSettings_NotificationDeliveryPreferences_Mobile_Schedule_FridayQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the UserSettings_NotificationDeliveryPreferences_Mobile_Schedule_Friday query and return a NotificationDeliveryPreferencesDay
	*
	* @returns parsed response from UserSettings_NotificationDeliveryPreferences_Mobile_Schedule_FridayQuery
	*/
	async fetch() {
		const data = (await this._request(UserSettings_NotificationDeliveryPreferences_Mobile_Schedule_FridayDocument.toString(), {})).userSettings.notificationDeliveryPreferences.mobile?.schedule?.friday;
		return data ? new NotificationDeliveryPreferencesDay(this._request, data) : void 0;
	}
};
/**
* A fetchable UserSettings_NotificationDeliveryPreferences_Mobile_Schedule_Monday Query
*
* @param request - function to call the graphql client
*/
var UserSettings_NotificationDeliveryPreferences_Mobile_Schedule_MondayQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the UserSettings_NotificationDeliveryPreferences_Mobile_Schedule_Monday query and return a NotificationDeliveryPreferencesDay
	*
	* @returns parsed response from UserSettings_NotificationDeliveryPreferences_Mobile_Schedule_MondayQuery
	*/
	async fetch() {
		const data = (await this._request(UserSettings_NotificationDeliveryPreferences_Mobile_Schedule_MondayDocument.toString(), {})).userSettings.notificationDeliveryPreferences.mobile?.schedule?.monday;
		return data ? new NotificationDeliveryPreferencesDay(this._request, data) : void 0;
	}
};
/**
* A fetchable UserSettings_NotificationDeliveryPreferences_Mobile_Schedule_Saturday Query
*
* @param request - function to call the graphql client
*/
var UserSettings_NotificationDeliveryPreferences_Mobile_Schedule_SaturdayQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the UserSettings_NotificationDeliveryPreferences_Mobile_Schedule_Saturday query and return a NotificationDeliveryPreferencesDay
	*
	* @returns parsed response from UserSettings_NotificationDeliveryPreferences_Mobile_Schedule_SaturdayQuery
	*/
	async fetch() {
		const data = (await this._request(UserSettings_NotificationDeliveryPreferences_Mobile_Schedule_SaturdayDocument.toString(), {})).userSettings.notificationDeliveryPreferences.mobile?.schedule?.saturday;
		return data ? new NotificationDeliveryPreferencesDay(this._request, data) : void 0;
	}
};
/**
* A fetchable UserSettings_NotificationDeliveryPreferences_Mobile_Schedule_Sunday Query
*
* @param request - function to call the graphql client
*/
var UserSettings_NotificationDeliveryPreferences_Mobile_Schedule_SundayQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the UserSettings_NotificationDeliveryPreferences_Mobile_Schedule_Sunday query and return a NotificationDeliveryPreferencesDay
	*
	* @returns parsed response from UserSettings_NotificationDeliveryPreferences_Mobile_Schedule_SundayQuery
	*/
	async fetch() {
		const data = (await this._request(UserSettings_NotificationDeliveryPreferences_Mobile_Schedule_SundayDocument.toString(), {})).userSettings.notificationDeliveryPreferences.mobile?.schedule?.sunday;
		return data ? new NotificationDeliveryPreferencesDay(this._request, data) : void 0;
	}
};
/**
* A fetchable UserSettings_NotificationDeliveryPreferences_Mobile_Schedule_Thursday Query
*
* @param request - function to call the graphql client
*/
var UserSettings_NotificationDeliveryPreferences_Mobile_Schedule_ThursdayQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the UserSettings_NotificationDeliveryPreferences_Mobile_Schedule_Thursday query and return a NotificationDeliveryPreferencesDay
	*
	* @returns parsed response from UserSettings_NotificationDeliveryPreferences_Mobile_Schedule_ThursdayQuery
	*/
	async fetch() {
		const data = (await this._request(UserSettings_NotificationDeliveryPreferences_Mobile_Schedule_ThursdayDocument.toString(), {})).userSettings.notificationDeliveryPreferences.mobile?.schedule?.thursday;
		return data ? new NotificationDeliveryPreferencesDay(this._request, data) : void 0;
	}
};
/**
* A fetchable UserSettings_NotificationDeliveryPreferences_Mobile_Schedule_Tuesday Query
*
* @param request - function to call the graphql client
*/
var UserSettings_NotificationDeliveryPreferences_Mobile_Schedule_TuesdayQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the UserSettings_NotificationDeliveryPreferences_Mobile_Schedule_Tuesday query and return a NotificationDeliveryPreferencesDay
	*
	* @returns parsed response from UserSettings_NotificationDeliveryPreferences_Mobile_Schedule_TuesdayQuery
	*/
	async fetch() {
		const data = (await this._request(UserSettings_NotificationDeliveryPreferences_Mobile_Schedule_TuesdayDocument.toString(), {})).userSettings.notificationDeliveryPreferences.mobile?.schedule?.tuesday;
		return data ? new NotificationDeliveryPreferencesDay(this._request, data) : void 0;
	}
};
/**
* A fetchable UserSettings_NotificationDeliveryPreferences_Mobile_Schedule_Wednesday Query
*
* @param request - function to call the graphql client
*/
var UserSettings_NotificationDeliveryPreferences_Mobile_Schedule_WednesdayQuery = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* Call the UserSettings_NotificationDeliveryPreferences_Mobile_Schedule_Wednesday query and return a NotificationDeliveryPreferencesDay
	*
	* @returns parsed response from UserSettings_NotificationDeliveryPreferences_Mobile_Schedule_WednesdayQuery
	*/
	async fetch() {
		const data = (await this._request(UserSettings_NotificationDeliveryPreferences_Mobile_Schedule_WednesdayDocument.toString(), {})).userSettings.notificationDeliveryPreferences.mobile?.schedule?.wednesday;
		return data ? new NotificationDeliveryPreferencesDay(this._request, data) : void 0;
	}
};
/**
* A fetchable UserSettings_Theme_Custom Query
*
* @param request - function to call the graphql client
* @param variables - variables to pass into the UserSettings_Theme_CustomQuery
*/
var UserSettings_Theme_CustomQuery = class extends Request {
	_variables;
	constructor(request, variables) {
		super(request);
		this._variables = variables;
	}
	/**
	* Call the UserSettings_Theme_Custom query and return a UserSettingsCustomTheme
	*
	* @param variables - variables to pass into the UserSettings_Theme_CustomQuery
	* @returns parsed response from UserSettings_Theme_CustomQuery
	*/
	async fetch(variables) {
		const data = (await this._request(UserSettings_Theme_CustomDocument.toString(), variables)).userSettings.theme?.custom;
		return data ? new UserSettingsCustomTheme(this._request, data) : void 0;
	}
};
/**
* A fetchable UserSettings_Theme_Custom_Sidebar Query
*
* @param request - function to call the graphql client
* @param variables - variables to pass into the UserSettings_Theme_Custom_SidebarQuery
*/
var UserSettings_Theme_Custom_SidebarQuery = class extends Request {
	_variables;
	constructor(request, variables) {
		super(request);
		this._variables = variables;
	}
	/**
	* Call the UserSettings_Theme_Custom_Sidebar query and return a UserSettingsCustomSidebarTheme
	*
	* @param variables - variables to pass into the UserSettings_Theme_Custom_SidebarQuery
	* @returns parsed response from UserSettings_Theme_Custom_SidebarQuery
	*/
	async fetch(variables) {
		const data = (await this._request(UserSettings_Theme_Custom_SidebarDocument.toString(), variables)).userSettings.theme?.custom?.sidebar;
		return data ? new UserSettingsCustomSidebarTheme(this._request, data) : void 0;
	}
};
/**
* A fetchable Viewer_AssignedIssues Query
*
* @param request - function to call the graphql client
* @param variables - variables to pass into the Viewer_AssignedIssuesQuery
*/
var Viewer_AssignedIssuesQuery = class extends Request {
	_variables;
	constructor(request, variables) {
		super(request);
		this._variables = variables;
	}
	/**
	* Call the Viewer_AssignedIssues query and return a IssueConnection
	*
	* @param variables - variables to pass into the Viewer_AssignedIssuesQuery
	* @returns parsed response from Viewer_AssignedIssuesQuery
	*/
	async fetch(variables) {
		const data = (await this._request(Viewer_AssignedIssuesDocument.toString(), variables)).viewer.assignedIssues;
		return new IssueConnection(this._request, (connection) => this.fetch(defaultConnection({
			...this._variables,
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable Viewer_CreatedIssues Query
*
* @param request - function to call the graphql client
* @param variables - variables to pass into the Viewer_CreatedIssuesQuery
*/
var Viewer_CreatedIssuesQuery = class extends Request {
	_variables;
	constructor(request, variables) {
		super(request);
		this._variables = variables;
	}
	/**
	* Call the Viewer_CreatedIssues query and return a IssueConnection
	*
	* @param variables - variables to pass into the Viewer_CreatedIssuesQuery
	* @returns parsed response from Viewer_CreatedIssuesQuery
	*/
	async fetch(variables) {
		const data = (await this._request(Viewer_CreatedIssuesDocument.toString(), variables)).viewer.createdIssues;
		return new IssueConnection(this._request, (connection) => this.fetch(defaultConnection({
			...this._variables,
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable Viewer_DelegatedIssues Query
*
* @param request - function to call the graphql client
* @param variables - variables to pass into the Viewer_DelegatedIssuesQuery
*/
var Viewer_DelegatedIssuesQuery = class extends Request {
	_variables;
	constructor(request, variables) {
		super(request);
		this._variables = variables;
	}
	/**
	* Call the Viewer_DelegatedIssues query and return a IssueConnection
	*
	* @param variables - variables to pass into the Viewer_DelegatedIssuesQuery
	* @returns parsed response from Viewer_DelegatedIssuesQuery
	*/
	async fetch(variables) {
		const data = (await this._request(Viewer_DelegatedIssuesDocument.toString(), variables)).viewer.delegatedIssues;
		return new IssueConnection(this._request, (connection) => this.fetch(defaultConnection({
			...this._variables,
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable Viewer_Drafts Query
*
* @param request - function to call the graphql client
* @param variables - variables to pass into the Viewer_DraftsQuery
*/
var Viewer_DraftsQuery = class extends Request {
	_variables;
	constructor(request, variables) {
		super(request);
		this._variables = variables;
	}
	/**
	* Call the Viewer_Drafts query and return a DraftConnection
	*
	* @param variables - variables to pass into the Viewer_DraftsQuery
	* @returns parsed response from Viewer_DraftsQuery
	*/
	async fetch(variables) {
		const data = (await this._request(Viewer_DraftsDocument.toString(), variables)).viewer.drafts;
		return new DraftConnection(this._request, (connection) => this.fetch(defaultConnection({
			...this._variables,
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable Viewer_TeamMemberships Query
*
* @param request - function to call the graphql client
* @param variables - variables to pass into the Viewer_TeamMembershipsQuery
*/
var Viewer_TeamMembershipsQuery = class extends Request {
	_variables;
	constructor(request, variables) {
		super(request);
		this._variables = variables;
	}
	/**
	* Call the Viewer_TeamMemberships query and return a TeamMembershipConnection
	*
	* @param variables - variables to pass into the Viewer_TeamMembershipsQuery
	* @returns parsed response from Viewer_TeamMembershipsQuery
	*/
	async fetch(variables) {
		const data = (await this._request(Viewer_TeamMembershipsDocument.toString(), variables)).viewer.teamMemberships;
		return new TeamMembershipConnection(this._request, (connection) => this.fetch(defaultConnection({
			...this._variables,
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable Viewer_Teams Query
*
* @param request - function to call the graphql client
* @param variables - variables to pass into the Viewer_TeamsQuery
*/
var Viewer_TeamsQuery = class extends Request {
	_variables;
	constructor(request, variables) {
		super(request);
		this._variables = variables;
	}
	/**
	* Call the Viewer_Teams query and return a TeamConnection
	*
	* @param variables - variables to pass into the Viewer_TeamsQuery
	* @returns parsed response from Viewer_TeamsQuery
	*/
	async fetch(variables) {
		const data = (await this._request(Viewer_TeamsDocument.toString(), variables)).viewer.teams;
		return new TeamConnection(this._request, (connection) => this.fetch(defaultConnection({
			...this._variables,
			...variables,
			...connection
		})), data);
	}
};
/**
* A fetchable WorkflowState_Issues Query
*
* @param request - function to call the graphql client
* @param id - required id to pass to workflowState
* @param variables - variables without 'id' to pass into the WorkflowState_IssuesQuery
*/
var WorkflowState_IssuesQuery = class extends Request {
	_id;
	_variables;
	constructor(request, id, variables) {
		super(request);
		this._id = id;
		this._variables = variables;
	}
	/**
	* Call the WorkflowState_Issues query and return a IssueConnection
	*
	* @param variables - variables without 'id' to pass into the WorkflowState_IssuesQuery
	* @returns parsed response from WorkflowState_IssuesQuery
	*/
	async fetch(variables) {
		const data = (await this._request(WorkflowState_IssuesDocument.toString(), {
			id: this._id,
			...this._variables,
			...variables
		})).workflowState.issues;
		return new IssueConnection(this._request, (connection) => this.fetch(defaultConnection({
			...this._variables,
			...variables,
			...connection
		})), data);
	}
};
/**
* The SDK class containing all root operations
*
* @param request - function to call the graphql client
*/
var LinearSdk = class extends Request {
	constructor(request) {
		super(request);
	}
	/**
	* All teams you the user can administrate. Administrable teams are teams whose settings the user can change, but to whose issues the user doesn't necessarily have access to.
	*
	* @param variables - variables to pass into the AdministrableTeamsQuery
	* @returns TeamConnection
	*/
	administrableTeams(variables) {
		return new AdministrableTeamsQuery(this._request).fetch(variables);
	}
	/**
	* All agent activities.
	*
	* @param variables - variables to pass into the AgentActivitiesQuery
	* @returns AgentActivityConnection
	*/
	agentActivities(variables) {
		return new AgentActivitiesQuery(this._request).fetch(variables);
	}
	/**
	* A specific agent activity.
	*
	* @param id - required id to pass to agentActivity
	* @returns AgentActivity
	*/
	agentActivity(id) {
		return new AgentActivityQuery(this._request).fetch(id);
	}
	/**
	* A specific agent session.
	*
	* @param id - required id to pass to agentSession
	* @returns AgentSession
	*/
	agentSession(id) {
		return new AgentSessionQuery(this._request).fetch(id);
	}
	/**
	* All agent sessions.
	*
	* @param variables - variables to pass into the AgentSessionsQuery
	* @returns AgentSessionConnection
	*/
	agentSessions(variables) {
		return new AgentSessionsQuery(this._request).fetch(variables);
	}
	/**
	* Get basic information for an application.
	*
	* @param clientId - required clientId to pass to applicationInfo
	* @returns Application
	*/
	applicationInfo(clientId) {
		return new ApplicationInfoQuery(this._request).fetch(clientId);
	}
	/**
	* One specific issue attachment.
	* [Deprecated] 'url' can no longer be used as the 'id' parameter. Use 'attachmentsForUrl' instead
	*
	* @param id - required id to pass to attachment
	* @returns Attachment
	*/
	attachment(id) {
		return new AttachmentQuery(this._request).fetch(id);
	}
	/**
	* Query an issue by its associated attachment, and its id.
	*
	* @param id - required id to pass to attachmentIssue
	* @returns Issue
	*/
	attachmentIssue(id) {
		return new AttachmentIssueQuery(this._request).fetch(id);
	}
	/**
	* All issue attachments.
	*
	* To get attachments for a given URL, use `attachmentsForURL` query.
	*
	* @param variables - variables to pass into the AttachmentsQuery
	* @returns AttachmentConnection
	*/
	attachments(variables) {
		return new AttachmentsQuery(this._request).fetch(variables);
	}
	/**
	* Returns issue attachments for a given `url`.
	*
	* @param url - required url to pass to attachmentsForURL
	* @param variables - variables without 'url' to pass into the AttachmentsForUrlQuery
	* @returns AttachmentConnection
	*/
	attachmentsForURL(url, variables) {
		return new AttachmentsForUrlQuery(this._request).fetch(url, variables);
	}
	/**
	* All audit log entries.
	*
	* @param variables - variables to pass into the AuditEntriesQuery
	* @returns AuditEntryConnection
	*/
	auditEntries(variables) {
		return new AuditEntriesQuery(this._request).fetch(variables);
	}
	/**
	* List of audit entry types.
	*
	* @returns AuditEntryType[]
	*/
	get auditEntryTypes() {
		return new AuditEntryTypesQuery(this._request).fetch();
	}
	/**
	* User's active sessions.
	*
	* @returns AuthenticationSessionResponse[]
	*/
	get authenticationSessions() {
		return new AuthenticationSessionsQuery(this._request).fetch();
	}
	/**
	* Fetch users belonging to this user account.
	*
	* @returns AuthResolverResponse
	*/
	get availableUsers() {
		return new AvailableUsersQuery(this._request).fetch();
	}
	/**
	* A specific comment.
	*
	* @param variables - variables to pass into the CommentQuery
	* @returns Comment
	*/
	comment(variables) {
		return new CommentQuery(this._request).fetch(variables);
	}
	/**
	* All comments.
	*
	* @param variables - variables to pass into the CommentsQuery
	* @returns CommentConnection
	*/
	comments(variables) {
		return new CommentsQuery(this._request).fetch(variables);
	}
	/**
	* One specific custom view.
	*
	* @param id - required id to pass to customView
	* @returns CustomView
	*/
	customView(id) {
		return new CustomViewQuery(this._request).fetch(id);
	}
	/**
	* Whether a custom view has other subscribers than the current user in the organization.
	*
	* @param id - required id to pass to customViewHasSubscribers
	* @returns CustomViewHasSubscribersPayload
	*/
	customViewHasSubscribers(id) {
		return new CustomViewHasSubscribersQuery(this._request).fetch(id);
	}
	/**
	* Custom views for the user.
	*
	* @param variables - variables to pass into the CustomViewsQuery
	* @returns CustomViewConnection
	*/
	customViews(variables) {
		return new CustomViewsQuery(this._request).fetch(variables);
	}
	/**
	* One specific customer.
	*
	* @param id - required id to pass to customer
	* @returns Customer
	*/
	customer(id) {
		return new CustomerQuery(this._request).fetch(id);
	}
	/**
	* One specific customer need
	*
	* @param variables - variables to pass into the CustomerNeedQuery
	* @returns CustomerNeed
	*/
	customerNeed(variables) {
		return new CustomerNeedQuery(this._request).fetch(variables);
	}
	/**
	* All customer needs.
	*
	* @param variables - variables to pass into the CustomerNeedsQuery
	* @returns CustomerNeedConnection
	*/
	customerNeeds(variables) {
		return new CustomerNeedsQuery(this._request).fetch(variables);
	}
	/**
	* One specific customer status.
	*
	* @param id - required id to pass to customerStatus
	* @returns CustomerStatus
	*/
	customerStatus(id) {
		return new CustomerStatusQuery(this._request).fetch(id);
	}
	/**
	* All customer statuses.
	*
	* @param variables - variables to pass into the CustomerStatusesQuery
	* @returns CustomerStatusConnection
	*/
	customerStatuses(variables) {
		return new CustomerStatusesQuery(this._request).fetch(variables);
	}
	/**
	* One specific customer tier.
	*
	* @param id - required id to pass to customerTier
	* @returns CustomerTier
	*/
	customerTier(id) {
		return new CustomerTierQuery(this._request).fetch(id);
	}
	/**
	* All customer tiers.
	*
	* @param variables - variables to pass into the CustomerTiersQuery
	* @returns CustomerTierConnection
	*/
	customerTiers(variables) {
		return new CustomerTiersQuery(this._request).fetch(variables);
	}
	/**
	* All customers.
	*
	* @param variables - variables to pass into the CustomersQuery
	* @returns CustomerConnection
	*/
	customers(variables) {
		return new CustomersQuery(this._request).fetch(variables);
	}
	/**
	* One specific cycle.
	*
	* @param id - required id to pass to cycle
	* @returns Cycle
	*/
	cycle(id) {
		return new CycleQuery(this._request).fetch(id);
	}
	/**
	* All cycles.
	*
	* @param variables - variables to pass into the CyclesQuery
	* @returns CycleConnection
	*/
	cycles(variables) {
		return new CyclesQuery(this._request).fetch(variables);
	}
	/**
	* One specific document.
	*
	* @param id - required id to pass to document
	* @returns Document
	*/
	document(id) {
		return new DocumentQuery(this._request).fetch(id);
	}
	/**
	* A collection of document content history entries.
	*
	* @param id - required id to pass to documentContentHistory
	* @returns DocumentContentHistoryPayload
	*/
	documentContentHistory(id) {
		return new DocumentContentHistoryQuery(this._request).fetch(id);
	}
	/**
	* All documents in the workspace.
	*
	* @param variables - variables to pass into the DocumentsQuery
	* @returns DocumentConnection
	*/
	documents(variables) {
		return new DocumentsQuery(this._request).fetch(variables);
	}
	/**
	* One specific email intake address.
	*
	* @param id - required id to pass to emailIntakeAddress
	* @returns EmailIntakeAddress
	*/
	emailIntakeAddress(id) {
		return new EmailIntakeAddressQuery(this._request).fetch(id);
	}
	/**
	* A specific emoji.
	*
	* @param id - required id to pass to emoji
	* @returns Emoji
	*/
	emoji(id) {
		return new EmojiQuery(this._request).fetch(id);
	}
	/**
	* All custom emojis.
	*
	* @param variables - variables to pass into the EmojisQuery
	* @returns EmojiConnection
	*/
	emojis(variables) {
		return new EmojisQuery(this._request).fetch(variables);
	}
	/**
	* One specific entity link.
	*
	* @param id - required id to pass to entityExternalLink
	* @returns EntityExternalLink
	*/
	entityExternalLink(id) {
		return new EntityExternalLinkQuery(this._request).fetch(id);
	}
	/**
	* One specific external user.
	*
	* @param id - required id to pass to externalUser
	* @returns ExternalUser
	*/
	externalUser(id) {
		return new ExternalUserQuery(this._request).fetch(id);
	}
	/**
	* All external users for the organization.
	*
	* @param variables - variables to pass into the ExternalUsersQuery
	* @returns ExternalUserConnection
	*/
	externalUsers(variables) {
		return new ExternalUsersQuery(this._request).fetch(variables);
	}
	/**
	* One specific favorite.
	*
	* @param id - required id to pass to favorite
	* @returns Favorite
	*/
	favorite(id) {
		return new FavoriteQuery(this._request).fetch(id);
	}
	/**
	* The user's favorites.
	*
	* @param variables - variables to pass into the FavoritesQuery
	* @returns FavoriteConnection
	*/
	favorites(variables) {
		return new FavoritesQuery(this._request).fetch(variables);
	}
	/**
	* One specific initiative.
	*
	* @param id - required id to pass to initiative
	* @returns Initiative
	*/
	initiative(id) {
		return new InitiativeQuery(this._request).fetch(id);
	}
	/**
	* One specific initiative relation.
	*
	* @param id - required id to pass to initiativeRelation
	* @returns ProjectRelation
	*/
	initiativeRelation(id) {
		return new InitiativeRelationQuery(this._request).fetch(id);
	}
	/**
	* All initiative relationships.
	*
	* @param variables - variables to pass into the InitiativeRelationsQuery
	* @returns InitiativeRelationConnection
	*/
	initiativeRelations(variables) {
		return new InitiativeRelationsQuery(this._request).fetch(variables);
	}
	/**
	* One specific initiativeToProject.
	*
	* @param id - required id to pass to initiativeToProject
	* @returns InitiativeToProject
	*/
	initiativeToProject(id) {
		return new InitiativeToProjectQuery(this._request).fetch(id);
	}
	/**
	* returns a list of initiative to project entities.
	*
	* @param variables - variables to pass into the InitiativeToProjectsQuery
	* @returns InitiativeToProjectConnection
	*/
	initiativeToProjects(variables) {
		return new InitiativeToProjectsQuery(this._request).fetch(variables);
	}
	/**
	* A specific  initiative update.
	*
	* @param id - required id to pass to initiativeUpdate
	* @returns InitiativeUpdate
	*/
	initiativeUpdate(id) {
		return new InitiativeUpdateQuery(this._request).fetch(id);
	}
	/**
	* All  InitiativeUpdates.
	*
	* @param variables - variables to pass into the InitiativeUpdatesQuery
	* @returns InitiativeUpdateConnection
	*/
	initiativeUpdates(variables) {
		return new InitiativeUpdatesQuery(this._request).fetch(variables);
	}
	/**
	* All initiatives in the workspace.
	*
	* @param variables - variables to pass into the InitiativesQuery
	* @returns InitiativeConnection
	*/
	initiatives(variables) {
		return new InitiativesQuery(this._request).fetch(variables);
	}
	/**
	* One specific integration.
	*
	* @param id - required id to pass to integration
	* @returns Integration
	*/
	integration(id) {
		return new IntegrationQuery(this._request).fetch(id);
	}
	/**
	* Checks if the integration has all required scopes.
	*
	* @param integrationId - required integrationId to pass to integrationHasScopes
	* @param scopes - required scopes to pass to integrationHasScopes
	* @returns IntegrationHasScopesPayload
	*/
	integrationHasScopes(integrationId, scopes) {
		return new IntegrationHasScopesQuery(this._request).fetch(integrationId, scopes);
	}
	/**
	* One specific integrationTemplate.
	*
	* @param id - required id to pass to integrationTemplate
	* @returns IntegrationTemplate
	*/
	integrationTemplate(id) {
		return new IntegrationTemplateQuery(this._request).fetch(id);
	}
	/**
	* Template and integration connections.
	*
	* @param variables - variables to pass into the IntegrationTemplatesQuery
	* @returns IntegrationTemplateConnection
	*/
	integrationTemplates(variables) {
		return new IntegrationTemplatesQuery(this._request).fetch(variables);
	}
	/**
	* All integrations.
	*
	* @param variables - variables to pass into the IntegrationsQuery
	* @returns IntegrationConnection
	*/
	integrations(variables) {
		return new IntegrationsQuery(this._request).fetch(variables);
	}
	/**
	* One specific set of settings.
	*
	* @param id - required id to pass to integrationsSettings
	* @returns IntegrationsSettings
	*/
	integrationsSettings(id) {
		return new IntegrationsSettingsQuery(this._request).fetch(id);
	}
	/**
	* One specific issue.
	*
	* @param id - required id to pass to issue
	* @returns Issue
	*/
	issue(id) {
		return new IssueQuery(this._request).fetch(id);
	}
	/**
	* Find issues that are related to a given Figma file key.
	*
	* @param fileKey - required fileKey to pass to issueFigmaFileKeySearch
	* @param variables - variables without 'fileKey' to pass into the IssueFigmaFileKeySearchQuery
	* @returns IssueConnection
	*/
	issueFigmaFileKeySearch(fileKey, variables) {
		return new IssueFigmaFileKeySearchQuery(this._request).fetch(fileKey, variables);
	}
	/**
	* Suggests filters for an issue view based on a text prompt.
	*
	* @param prompt - required prompt to pass to issueFilterSuggestion
	* @param variables - variables without 'prompt' to pass into the IssueFilterSuggestionQuery
	* @returns IssueFilterSuggestionPayload
	*/
	issueFilterSuggestion(prompt, variables) {
		return new IssueFilterSuggestionQuery(this._request).fetch(prompt, variables);
	}
	/**
	* Checks a CSV file validity against a specific import service.
	*
	* @param csvUrl - required csvUrl to pass to issueImportCheckCSV
	* @param service - required service to pass to issueImportCheckCSV
	* @returns IssueImportCheckPayload
	*/
	issueImportCheckCSV(csvUrl, service) {
		return new IssueImportCheckCsvQuery(this._request).fetch(csvUrl, service);
	}
	/**
	* Checks whether it will be possible to setup sync for this project or repository at the end of import
	*
	* @param issueImportId - required issueImportId to pass to issueImportCheckSync
	* @returns IssueImportSyncCheckPayload
	*/
	issueImportCheckSync(issueImportId) {
		return new IssueImportCheckSyncQuery(this._request).fetch(issueImportId);
	}
	/**
	* Checks whether a custom JQL query is valid and can be used to filter issues of a Jira import
	*
	* @param jiraEmail - required jiraEmail to pass to issueImportJqlCheck
	* @param jiraHostname - required jiraHostname to pass to issueImportJqlCheck
	* @param jiraProject - required jiraProject to pass to issueImportJqlCheck
	* @param jiraToken - required jiraToken to pass to issueImportJqlCheck
	* @param jql - required jql to pass to issueImportJqlCheck
	* @returns IssueImportJqlCheckPayload
	*/
	issueImportJqlCheck(jiraEmail, jiraHostname, jiraProject, jiraToken, jql) {
		return new IssueImportJqlCheckQuery(this._request).fetch(jiraEmail, jiraHostname, jiraProject, jiraToken, jql);
	}
	/**
	* One specific label.
	*
	* @param id - required id to pass to issueLabel
	* @returns IssueLabel
	*/
	issueLabel(id) {
		return new IssueLabelQuery(this._request).fetch(id);
	}
	/**
	* All issue labels.
	*
	* @param variables - variables to pass into the IssueLabelsQuery
	* @returns IssueLabelConnection
	*/
	issueLabels(variables) {
		return new IssueLabelsQuery(this._request).fetch(variables);
	}
	/**
	* Issue priority values and corresponding labels.
	*
	* @returns IssuePriorityValue[]
	*/
	get issuePriorityValues() {
		return new IssuePriorityValuesQuery(this._request).fetch();
	}
	/**
	* One specific issue relation.
	*
	* @param id - required id to pass to issueRelation
	* @returns IssueRelation
	*/
	issueRelation(id) {
		return new IssueRelationQuery(this._request).fetch(id);
	}
	/**
	* All issue relationships.
	*
	* @param variables - variables to pass into the IssueRelationsQuery
	* @returns IssueRelationConnection
	*/
	issueRelations(variables) {
		return new IssueRelationsQuery(this._request).fetch(variables);
	}
	/**
	* [DEPRECATED] Search issues. This endpoint is deprecated and will be removed in the future – use `searchIssues` instead.
	*
	* @param variables - variables to pass into the IssueSearchQuery
	* @returns IssueConnection
	*/
	issueSearch(variables) {
		return new IssueSearchQuery(this._request).fetch(variables);
	}
	/**
	* Suggests issue title based on a customer request.
	*
	* @param request - required request to pass to issueTitleSuggestionFromCustomerRequest
	* @returns IssueTitleSuggestionFromCustomerRequestPayload
	*/
	issueTitleSuggestionFromCustomerRequest(request) {
		return new IssueTitleSuggestionFromCustomerRequestQuery(this._request).fetch(request);
	}
	/**
	* Find issue based on the VCS branch name.
	*
	* @param branchName - required branchName to pass to issueVcsBranchSearch
	* @returns Issue
	*/
	issueVcsBranchSearch(branchName) {
		return new IssueVcsBranchSearchQuery(this._request).fetch(branchName);
	}
	/**
	* All issues.
	*
	* @param variables - variables to pass into the IssuesQuery
	* @returns IssueConnection
	*/
	issues(variables) {
		return new IssuesQuery(this._request).fetch(variables);
	}
	/**
	* One specific notification.
	*
	* @param id - required id to pass to notification
	* @returns Notification
	*/
	notification(id) {
		return new NotificationQuery(this._request).fetch(id);
	}
	/**
	* One specific notification subscription.
	*
	* @param id - required id to pass to notificationSubscription
	* @returns NotificationSubscription
	*/
	notificationSubscription(id) {
		return new NotificationSubscriptionQuery(this._request).fetch(id);
	}
	/**
	* The user's notification subscriptions.
	*
	* @param variables - variables to pass into the NotificationSubscriptionsQuery
	* @returns NotificationSubscriptionConnection
	*/
	notificationSubscriptions(variables) {
		return new NotificationSubscriptionsQuery(this._request).fetch(variables);
	}
	/**
	* All notifications.
	*
	* @param variables - variables to pass into the NotificationsQuery
	* @returns NotificationConnection
	*/
	notifications(variables) {
		return new NotificationsQuery(this._request).fetch(variables);
	}
	/**
	* The user's organization.
	*
	* @returns Organization
	*/
	get organization() {
		return new OrganizationQuery(this._request).fetch();
	}
	/**
	* Does the organization exist.
	*
	* @param urlKey - required urlKey to pass to organizationExists
	* @returns OrganizationExistsPayload
	*/
	organizationExists(urlKey) {
		return new OrganizationExistsQuery(this._request).fetch(urlKey);
	}
	/**
	* One specific organization invite.
	*
	* @param id - required id to pass to organizationInvite
	* @returns OrganizationInvite
	*/
	organizationInvite(id) {
		return new OrganizationInviteQuery(this._request).fetch(id);
	}
	/**
	* All invites for the organization.
	*
	* @param variables - variables to pass into the OrganizationInvitesQuery
	* @returns OrganizationInviteConnection
	*/
	organizationInvites(variables) {
		return new OrganizationInvitesQuery(this._request).fetch(variables);
	}
	/**
	* One specific project.
	*
	* @param id - required id to pass to project
	* @returns Project
	*/
	project(id) {
		return new ProjectQuery(this._request).fetch(id);
	}
	/**
	* Suggests filters for a project view based on a text prompt.
	*
	* @param prompt - required prompt to pass to projectFilterSuggestion
	* @returns ProjectFilterSuggestionPayload
	*/
	projectFilterSuggestion(prompt) {
		return new ProjectFilterSuggestionQuery(this._request).fetch(prompt);
	}
	/**
	* One specific label.
	*
	* @param id - required id to pass to projectLabel
	* @returns ProjectLabel
	*/
	projectLabel(id) {
		return new ProjectLabelQuery(this._request).fetch(id);
	}
	/**
	* All project labels.
	*
	* @param variables - variables to pass into the ProjectLabelsQuery
	* @returns ProjectLabelConnection
	*/
	projectLabels(variables) {
		return new ProjectLabelsQuery(this._request).fetch(variables);
	}
	/**
	* One specific project milestone.
	*
	* @param id - required id to pass to projectMilestone
	* @returns ProjectMilestone
	*/
	projectMilestone(id) {
		return new ProjectMilestoneQuery(this._request).fetch(id);
	}
	/**
	* All milestones for the project.
	*
	* @param variables - variables to pass into the ProjectMilestonesQuery
	* @returns ProjectMilestoneConnection
	*/
	projectMilestones(variables) {
		return new ProjectMilestonesQuery(this._request).fetch(variables);
	}
	/**
	* One specific project relation.
	*
	* @param id - required id to pass to projectRelation
	* @returns ProjectRelation
	*/
	projectRelation(id) {
		return new ProjectRelationQuery(this._request).fetch(id);
	}
	/**
	* All project relationships.
	*
	* @param variables - variables to pass into the ProjectRelationsQuery
	* @returns ProjectRelationConnection
	*/
	projectRelations(variables) {
		return new ProjectRelationsQuery(this._request).fetch(variables);
	}
	/**
	* One specific project status.
	*
	* @param id - required id to pass to projectStatus
	* @returns ProjectStatus
	*/
	projectStatus(id) {
		return new ProjectStatusQuery(this._request).fetch(id);
	}
	/**
	* All project statuses.
	*
	* @param variables - variables to pass into the ProjectStatusesQuery
	* @returns ProjectStatusConnection
	*/
	projectStatuses(variables) {
		return new ProjectStatusesQuery(this._request).fetch(variables);
	}
	/**
	* A specific project update.
	*
	* @param id - required id to pass to projectUpdate
	* @returns ProjectUpdate
	*/
	projectUpdate(id) {
		return new ProjectUpdateQuery(this._request).fetch(id);
	}
	/**
	* All project updates.
	*
	* @param variables - variables to pass into the ProjectUpdatesQuery
	* @returns ProjectUpdateConnection
	*/
	projectUpdates(variables) {
		return new ProjectUpdatesQuery(this._request).fetch(variables);
	}
	/**
	* All projects.
	*
	* @param variables - variables to pass into the ProjectsQuery
	* @returns ProjectConnection
	*/
	projects(variables) {
		return new ProjectsQuery(this._request).fetch(variables);
	}
	/**
	* Sends a test push message.
	*
	* @param variables - variables to pass into the PushSubscriptionTestQuery
	* @returns PushSubscriptionTestPayload
	*/
	pushSubscriptionTest(variables) {
		return new PushSubscriptionTestQuery(this._request).fetch(variables);
	}
	/**
	* The status of the rate limiter.
	*
	* @returns RateLimitPayload
	*/
	get rateLimitStatus() {
		return new RateLimitStatusQuery(this._request).fetch();
	}
	/**
	* One specific roadmap.
	*
	* @param id - required id to pass to roadmap
	* @returns Roadmap
	*/
	roadmap(id) {
		return new RoadmapQuery(this._request).fetch(id);
	}
	/**
	* One specific roadmapToProject.
	*
	* @param id - required id to pass to roadmapToProject
	* @returns RoadmapToProject
	*/
	roadmapToProject(id) {
		return new RoadmapToProjectQuery(this._request).fetch(id);
	}
	/**
	* Query roadmapToProjects for RoadmapToProjectConnection
	*
	* @param variables - variables to pass into the RoadmapToProjectsQuery
	* @returns RoadmapToProjectConnection
	*/
	roadmapToProjects(variables) {
		return new RoadmapToProjectsQuery(this._request).fetch(variables);
	}
	/**
	* All roadmaps in the workspace.
	*
	* @param variables - variables to pass into the RoadmapsQuery
	* @returns RoadmapConnection
	*/
	roadmaps(variables) {
		return new RoadmapsQuery(this._request).fetch(variables);
	}
	/**
	* Search documents.
	*
	* @param term - required term to pass to searchDocuments
	* @param variables - variables without 'term' to pass into the SearchDocumentsQuery
	* @returns DocumentSearchPayload
	*/
	searchDocuments(term, variables) {
		return new SearchDocumentsQuery(this._request).fetch(term, variables);
	}
	/**
	* Search issues.
	*
	* @param term - required term to pass to searchIssues
	* @param variables - variables without 'term' to pass into the SearchIssuesQuery
	* @returns IssueSearchPayload
	*/
	searchIssues(term, variables) {
		return new SearchIssuesQuery(this._request).fetch(term, variables);
	}
	/**
	* Search projects.
	*
	* @param term - required term to pass to searchProjects
	* @param variables - variables without 'term' to pass into the SearchProjectsQuery
	* @returns ProjectSearchPayload
	*/
	searchProjects(term, variables) {
		return new SearchProjectsQuery(this._request).fetch(term, variables);
	}
	/**
	* Search for various resources using natural language.
	*
	* @param query - required query to pass to semanticSearch
	* @param variables - variables without 'query' to pass into the SemanticSearchQuery
	* @returns SemanticSearchPayload
	*/
	semanticSearch(query, variables) {
		return new SemanticSearchQuery(this._request).fetch(query, variables);
	}
	/**
	* Fetch SSO login URL for the email provided.
	*
	* @param email - required email to pass to ssoUrlFromEmail
	* @param type - required type to pass to ssoUrlFromEmail
	* @param variables - variables without 'email', 'type' to pass into the SsoUrlFromEmailQuery
	* @returns SsoUrlFromEmailResponse
	*/
	ssoUrlFromEmail(email, type, variables) {
		return new SsoUrlFromEmailQuery(this._request).fetch(email, type, variables);
	}
	/**
	* One specific team.
	*
	* @param id - required id to pass to team
	* @returns Team
	*/
	team(id) {
		return new TeamQuery(this._request).fetch(id);
	}
	/**
	* One specific team membership.
	*
	* @param id - required id to pass to teamMembership
	* @returns TeamMembership
	*/
	teamMembership(id) {
		return new TeamMembershipQuery(this._request).fetch(id);
	}
	/**
	* All team memberships.
	*
	* @param variables - variables to pass into the TeamMembershipsQuery
	* @returns TeamMembershipConnection
	*/
	teamMemberships(variables) {
		return new TeamMembershipsQuery(this._request).fetch(variables);
	}
	/**
	* All teams whose issues can be accessed by the user. This might be different from `administrableTeams`, which also includes teams whose settings can be changed by the user.
	*
	* @param variables - variables to pass into the TeamsQuery
	* @returns TeamConnection
	*/
	teams(variables) {
		return new TeamsQuery(this._request).fetch(variables);
	}
	/**
	* A specific template.
	*
	* @param id - required id to pass to template
	* @returns Template
	*/
	template(id) {
		return new TemplateQuery(this._request).fetch(id);
	}
	/**
	* All templates from all users.
	*
	* @returns Template[]
	*/
	get templates() {
		return new TemplatesQuery(this._request).fetch();
	}
	/**
	* Returns all templates that are associated with the integration type.
	*
	* @param integrationType - required integrationType to pass to templatesForIntegration
	* @returns Template[]
	*/
	templatesForIntegration(integrationType) {
		return new TemplatesForIntegrationQuery(this._request).fetch(integrationType);
	}
	/**
	* A specific time schedule.
	*
	* @param id - required id to pass to timeSchedule
	* @returns TimeSchedule
	*/
	timeSchedule(id) {
		return new TimeScheduleQuery(this._request).fetch(id);
	}
	/**
	* All time schedules.
	*
	* @param variables - variables to pass into the TimeSchedulesQuery
	* @returns TimeScheduleConnection
	*/
	timeSchedules(variables) {
		return new TimeSchedulesQuery(this._request).fetch(variables);
	}
	/**
	* All triage responsibilities.
	*
	* @param variables - variables to pass into the TriageResponsibilitiesQuery
	* @returns TriageResponsibilityConnection
	*/
	triageResponsibilities(variables) {
		return new TriageResponsibilitiesQuery(this._request).fetch(variables);
	}
	/**
	* A specific triage responsibility.
	*
	* @param id - required id to pass to triageResponsibility
	* @returns TriageResponsibility
	*/
	triageResponsibility(id) {
		return new TriageResponsibilityQuery(this._request).fetch(id);
	}
	/**
	* One specific user.
	*
	* @param id - required id to pass to user
	* @returns User
	*/
	user(id) {
		return new UserQuery(this._request).fetch(id);
	}
	/**
	* The user's settings.
	*
	* @returns UserSettings
	*/
	get userSettings() {
		return new UserSettingsQuery(this._request).fetch();
	}
	/**
	* All users for the organization.
	*
	* @param variables - variables to pass into the UsersQuery
	* @returns UserConnection
	*/
	users(variables) {
		return new UsersQuery(this._request).fetch(variables);
	}
	/**
	* Verify that we received the correct response from the GitHub Enterprise Server.
	*
	* @param integrationId - required integrationId to pass to verifyGitHubEnterpriseServerInstallation
	* @returns GitHubEnterpriseServerInstallVerificationPayload
	*/
	verifyGitHubEnterpriseServerInstallation(integrationId) {
		return new VerifyGitHubEnterpriseServerInstallationQuery(this._request).fetch(integrationId);
	}
	/**
	* The currently authenticated user.
	*
	* @returns User
	*/
	get viewer() {
		return new ViewerQuery(this._request).fetch();
	}
	/**
	* A specific webhook.
	*
	* @param id - required id to pass to webhook
	* @returns Webhook
	*/
	webhook(id) {
		return new WebhookQuery(this._request).fetch(id);
	}
	/**
	* All webhooks.
	*
	* @param variables - variables to pass into the WebhooksQuery
	* @returns WebhookConnection
	*/
	webhooks(variables) {
		return new WebhooksQuery(this._request).fetch(variables);
	}
	/**
	* One specific state.
	*
	* @param id - required id to pass to workflowState
	* @returns WorkflowState
	*/
	workflowState(id) {
		return new WorkflowStateQuery(this._request).fetch(id);
	}
	/**
	* All issue workflow states.
	*
	* @param variables - variables to pass into the WorkflowStatesQuery
	* @returns WorkflowStateConnection
	*/
	workflowStates(variables) {
		return new WorkflowStatesQuery(this._request).fetch(variables);
	}
	/**
	* Creates an agent activity.
	*
	* @param input - required input to pass to createAgentActivity
	* @returns AgentActivityPayload
	*/
	createAgentActivity(input) {
		return new CreateAgentActivityMutation(this._request).fetch(input);
	}
	/**
	* Creates a new agent session on a rootcomment.
	*
	* @param input - required input to pass to agentSessionCreateOnComment
	* @returns AgentSessionPayload
	*/
	agentSessionCreateOnComment(input) {
		return new AgentSessionCreateOnCommentMutation(this._request).fetch(input);
	}
	/**
	* Creates a new agent session on an issue.
	*
	* @param input - required input to pass to agentSessionCreateOnIssue
	* @returns AgentSessionPayload
	*/
	agentSessionCreateOnIssue(input) {
		return new AgentSessionCreateOnIssueMutation(this._request).fetch(input);
	}
	/**
	* Updates an agent session.
	*
	* @param id - required id to pass to updateAgentSession
	* @param input - required input to pass to updateAgentSession
	* @returns AgentSessionPayload
	*/
	updateAgentSession(id, input) {
		return new UpdateAgentSessionMutation(this._request).fetch(id, input);
	}
	/**
	* Updates the externalUrl of an agent session, which is an agent-hosted page associated with this session.
	*
	* @param id - required id to pass to agentSessionUpdateExternalUrl
	* @param input - required input to pass to agentSessionUpdateExternalUrl
	* @returns AgentSessionPayload
	*/
	agentSessionUpdateExternalUrl(id, input) {
		return new AgentSessionUpdateExternalUrlMutation(this._request).fetch(id, input);
	}
	/**
	* Creates an integration api key for Airbyte to connect with Linear.
	*
	* @param input - required input to pass to airbyteIntegrationConnect
	* @returns IntegrationPayload
	*/
	airbyteIntegrationConnect(input) {
		return new AirbyteIntegrationConnectMutation(this._request).fetch(input);
	}
	/**
	* Authenticate a user to the Asks web forms app.
	*
	* @param token - required token to pass to asksWebFormsAuth
	* @returns AsksWebFormsAuthResponse
	*/
	asksWebFormsAuth(token) {
		return new AsksWebFormsAuthMutation(this._request).fetch(token);
	}
	/**
	* Creates a new attachment, or updates existing if the same `url` and `issueId` is used.
	*
	* @param input - required input to pass to createAttachment
	* @returns AttachmentPayload
	*/
	createAttachment(input) {
		return new CreateAttachmentMutation(this._request).fetch(input);
	}
	/**
	* Deletes an issue attachment.
	*
	* @param id - required id to pass to deleteAttachment
	* @returns DeletePayload
	*/
	deleteAttachment(id) {
		return new DeleteAttachmentMutation(this._request).fetch(id);
	}
	/**
	* Link an existing Discord message to an issue.
	*
	* @param channelId - required channelId to pass to attachmentLinkDiscord
	* @param issueId - required issueId to pass to attachmentLinkDiscord
	* @param messageId - required messageId to pass to attachmentLinkDiscord
	* @param url - required url to pass to attachmentLinkDiscord
	* @param variables - variables without 'channelId', 'issueId', 'messageId', 'url' to pass into the AttachmentLinkDiscordMutation
	* @returns AttachmentPayload
	*/
	attachmentLinkDiscord(channelId, issueId, messageId, url, variables) {
		return new AttachmentLinkDiscordMutation(this._request).fetch(channelId, issueId, messageId, url, variables);
	}
	/**
	* Link an existing Front conversation to an issue.
	*
	* @param conversationId - required conversationId to pass to attachmentLinkFront
	* @param issueId - required issueId to pass to attachmentLinkFront
	* @param variables - variables without 'conversationId', 'issueId' to pass into the AttachmentLinkFrontMutation
	* @returns FrontAttachmentPayload
	*/
	attachmentLinkFront(conversationId, issueId, variables) {
		return new AttachmentLinkFrontMutation(this._request).fetch(conversationId, issueId, variables);
	}
	/**
	* Link a GitHub issue to a Linear issue.
	*
	* @param issueId - required issueId to pass to attachmentLinkGitHubIssue
	* @param url - required url to pass to attachmentLinkGitHubIssue
	* @param variables - variables without 'issueId', 'url' to pass into the AttachmentLinkGitHubIssueMutation
	* @returns AttachmentPayload
	*/
	attachmentLinkGitHubIssue(issueId, url, variables) {
		return new AttachmentLinkGitHubIssueMutation(this._request).fetch(issueId, url, variables);
	}
	/**
	* Link a GitHub pull request to an issue.
	*
	* @param issueId - required issueId to pass to attachmentLinkGitHubPR
	* @param url - required url to pass to attachmentLinkGitHubPR
	* @param variables - variables without 'issueId', 'url' to pass into the AttachmentLinkGitHubPrMutation
	* @returns AttachmentPayload
	*/
	attachmentLinkGitHubPR(issueId, url, variables) {
		return new AttachmentLinkGitHubPrMutation(this._request).fetch(issueId, url, variables);
	}
	/**
	* Link an existing GitLab MR to an issue.
	*
	* @param issueId - required issueId to pass to attachmentLinkGitLabMR
	* @param number - required number to pass to attachmentLinkGitLabMR
	* @param projectPathWithNamespace - required projectPathWithNamespace to pass to attachmentLinkGitLabMR
	* @param url - required url to pass to attachmentLinkGitLabMR
	* @param variables - variables without 'issueId', 'number', 'projectPathWithNamespace', 'url' to pass into the AttachmentLinkGitLabMrMutation
	* @returns AttachmentPayload
	*/
	attachmentLinkGitLabMR(issueId, number, projectPathWithNamespace, url, variables) {
		return new AttachmentLinkGitLabMrMutation(this._request).fetch(issueId, number, projectPathWithNamespace, url, variables);
	}
	/**
	* Link an existing Intercom conversation to an issue.
	*
	* @param conversationId - required conversationId to pass to attachmentLinkIntercom
	* @param issueId - required issueId to pass to attachmentLinkIntercom
	* @param variables - variables without 'conversationId', 'issueId' to pass into the AttachmentLinkIntercomMutation
	* @returns AttachmentPayload
	*/
	attachmentLinkIntercom(conversationId, issueId, variables) {
		return new AttachmentLinkIntercomMutation(this._request).fetch(conversationId, issueId, variables);
	}
	/**
	* Link an existing Jira issue to an issue.
	*
	* @param issueId - required issueId to pass to attachmentLinkJiraIssue
	* @param jiraIssueId - required jiraIssueId to pass to attachmentLinkJiraIssue
	* @param variables - variables without 'issueId', 'jiraIssueId' to pass into the AttachmentLinkJiraIssueMutation
	* @returns AttachmentPayload
	*/
	attachmentLinkJiraIssue(issueId, jiraIssueId, variables) {
		return new AttachmentLinkJiraIssueMutation(this._request).fetch(issueId, jiraIssueId, variables);
	}
	/**
	* Link an existing Salesforce case to an issue.
	*
	* @param issueId - required issueId to pass to attachmentLinkSalesforce
	* @param url - required url to pass to attachmentLinkSalesforce
	* @param variables - variables without 'issueId', 'url' to pass into the AttachmentLinkSalesforceMutation
	* @returns AttachmentPayload
	*/
	attachmentLinkSalesforce(issueId, url, variables) {
		return new AttachmentLinkSalesforceMutation(this._request).fetch(issueId, url, variables);
	}
	/**
	* Link an existing Slack message to an issue.
	*
	* @param issueId - required issueId to pass to attachmentLinkSlack
	* @param url - required url to pass to attachmentLinkSlack
	* @param variables - variables without 'issueId', 'url' to pass into the AttachmentLinkSlackMutation
	* @returns AttachmentPayload
	*/
	attachmentLinkSlack(issueId, url, variables) {
		return new AttachmentLinkSlackMutation(this._request).fetch(issueId, url, variables);
	}
	/**
	* Link any url to an issue.
	*
	* @param issueId - required issueId to pass to attachmentLinkURL
	* @param url - required url to pass to attachmentLinkURL
	* @param variables - variables without 'issueId', 'url' to pass into the AttachmentLinkUrlMutation
	* @returns AttachmentPayload
	*/
	attachmentLinkURL(issueId, url, variables) {
		return new AttachmentLinkUrlMutation(this._request).fetch(issueId, url, variables);
	}
	/**
	* Link an existing Zendesk ticket to an issue.
	*
	* @param issueId - required issueId to pass to attachmentLinkZendesk
	* @param ticketId - required ticketId to pass to attachmentLinkZendesk
	* @param variables - variables without 'issueId', 'ticketId' to pass into the AttachmentLinkZendeskMutation
	* @returns AttachmentPayload
	*/
	attachmentLinkZendesk(issueId, ticketId, variables) {
		return new AttachmentLinkZendeskMutation(this._request).fetch(issueId, ticketId, variables);
	}
	/**
	* Begin syncing the thread for an existing Slack message attachment with a comment thread on its issue.
	*
	* @param id - required id to pass to attachmentSyncToSlack
	* @returns AttachmentPayload
	*/
	attachmentSyncToSlack(id) {
		return new AttachmentSyncToSlackMutation(this._request).fetch(id);
	}
	/**
	* Updates an existing issue attachment.
	*
	* @param id - required id to pass to updateAttachment
	* @param input - required input to pass to updateAttachment
	* @returns AttachmentPayload
	*/
	updateAttachment(id, input) {
		return new UpdateAttachmentMutation(this._request).fetch(id, input);
	}
	/**
	* Creates a new comment.
	*
	* @param input - required input to pass to createComment
	* @returns CommentPayload
	*/
	createComment(input) {
		return new CreateCommentMutation(this._request).fetch(input);
	}
	/**
	* Deletes a comment.
	*
	* @param id - required id to pass to deleteComment
	* @returns DeletePayload
	*/
	deleteComment(id) {
		return new DeleteCommentMutation(this._request).fetch(id);
	}
	/**
	* Resolves a comment.
	*
	* @param id - required id to pass to commentResolve
	* @param variables - variables without 'id' to pass into the CommentResolveMutation
	* @returns CommentPayload
	*/
	commentResolve(id, variables) {
		return new CommentResolveMutation(this._request).fetch(id, variables);
	}
	/**
	* Unresolves a comment.
	*
	* @param id - required id to pass to commentUnresolve
	* @returns CommentPayload
	*/
	commentUnresolve(id) {
		return new CommentUnresolveMutation(this._request).fetch(id);
	}
	/**
	* Updates a comment.
	*
	* @param id - required id to pass to updateComment
	* @param input - required input to pass to updateComment
	* @param variables - variables without 'id', 'input' to pass into the UpdateCommentMutation
	* @returns CommentPayload
	*/
	updateComment(id, input, variables) {
		return new UpdateCommentMutation(this._request).fetch(id, input, variables);
	}
	/**
	* Saves user message.
	*
	* @param input - required input to pass to createContact
	* @returns ContactPayload
	*/
	createContact(input) {
		return new CreateContactMutation(this._request).fetch(input);
	}
	/**
	* Create CSV export report for the organization.
	*
	* @param variables - variables to pass into the CreateCsvExportReportMutation
	* @returns CreateCsvExportReportPayload
	*/
	createCsvExportReport(variables) {
		return new CreateCsvExportReportMutation(this._request).fetch(variables);
	}
	/**
	* Create a notification to remind a user about an initiative update.
	*
	* @param initiativeId - required initiativeId to pass to createInitiativeUpdateReminder
	* @param variables - variables without 'initiativeId' to pass into the CreateInitiativeUpdateReminderMutation
	* @returns InitiativeUpdateReminderPayload
	*/
	createInitiativeUpdateReminder(initiativeId, variables) {
		return new CreateInitiativeUpdateReminderMutation(this._request).fetch(initiativeId, variables);
	}
	/**
	* Creates an organization from onboarding.
	*
	* @param input - required input to pass to createOrganizationFromOnboarding
	* @param variables - variables without 'input' to pass into the CreateOrganizationFromOnboardingMutation
	* @returns CreateOrJoinOrganizationResponse
	*/
	createOrganizationFromOnboarding(input, variables) {
		return new CreateOrganizationFromOnboardingMutation(this._request).fetch(input, variables);
	}
	/**
	* Create a notification to remind a user about a project update.
	*
	* @param projectId - required projectId to pass to createProjectUpdateReminder
	* @param variables - variables without 'projectId' to pass into the CreateProjectUpdateReminderMutation
	* @returns ProjectUpdateReminderPayload
	*/
	createProjectUpdateReminder(projectId, variables) {
		return new CreateProjectUpdateReminderMutation(this._request).fetch(projectId, variables);
	}
	/**
	* Creates a new custom view.
	*
	* @param input - required input to pass to createCustomView
	* @returns CustomViewPayload
	*/
	createCustomView(input) {
		return new CreateCustomViewMutation(this._request).fetch(input);
	}
	/**
	* Deletes a custom view.
	*
	* @param id - required id to pass to deleteCustomView
	* @returns DeletePayload
	*/
	deleteCustomView(id) {
		return new DeleteCustomViewMutation(this._request).fetch(id);
	}
	/**
	* Updates a custom view.
	*
	* @param id - required id to pass to updateCustomView
	* @param input - required input to pass to updateCustomView
	* @returns CustomViewPayload
	*/
	updateCustomView(id, input) {
		return new UpdateCustomViewMutation(this._request).fetch(id, input);
	}
	/**
	* Creates a new customer.
	*
	* @param input - required input to pass to createCustomer
	* @returns CustomerPayload
	*/
	createCustomer(input) {
		return new CreateCustomerMutation(this._request).fetch(input);
	}
	/**
	* Deletes a customer.
	*
	* @param id - required id to pass to deleteCustomer
	* @returns DeletePayload
	*/
	deleteCustomer(id) {
		return new DeleteCustomerMutation(this._request).fetch(id);
	}
	/**
	* Merges two customers.
	*
	* @param sourceCustomerId - required sourceCustomerId to pass to customerMerge
	* @param targetCustomerId - required targetCustomerId to pass to customerMerge
	* @returns CustomerPayload
	*/
	customerMerge(sourceCustomerId, targetCustomerId) {
		return new CustomerMergeMutation(this._request).fetch(sourceCustomerId, targetCustomerId);
	}
	/**
	* Archives a customer need.
	*
	* @param id - required id to pass to archiveCustomerNeed
	* @returns CustomerNeedArchivePayload
	*/
	archiveCustomerNeed(id) {
		return new ArchiveCustomerNeedMutation(this._request).fetch(id);
	}
	/**
	* Creates a new customer need.
	*
	* @param input - required input to pass to createCustomerNeed
	* @returns CustomerNeedPayload
	*/
	createCustomerNeed(input) {
		return new CreateCustomerNeedMutation(this._request).fetch(input);
	}
	/**
	* Creates a new customer need out of an attachment
	*
	* @param input - required input to pass to customerNeedCreateFromAttachment
	* @returns CustomerNeedPayload
	*/
	customerNeedCreateFromAttachment(input) {
		return new CustomerNeedCreateFromAttachmentMutation(this._request).fetch(input);
	}
	/**
	* Deletes a customer need.
	*
	* @param id - required id to pass to deleteCustomerNeed
	* @param variables - variables without 'id' to pass into the DeleteCustomerNeedMutation
	* @returns DeletePayload
	*/
	deleteCustomerNeed(id, variables) {
		return new DeleteCustomerNeedMutation(this._request).fetch(id, variables);
	}
	/**
	* Unarchives a customer need.
	*
	* @param id - required id to pass to unarchiveCustomerNeed
	* @returns CustomerNeedArchivePayload
	*/
	unarchiveCustomerNeed(id) {
		return new UnarchiveCustomerNeedMutation(this._request).fetch(id);
	}
	/**
	* Updates a customer need
	*
	* @param id - required id to pass to updateCustomerNeed
	* @param input - required input to pass to updateCustomerNeed
	* @returns CustomerNeedUpdatePayload
	*/
	updateCustomerNeed(id, input) {
		return new UpdateCustomerNeedMutation(this._request).fetch(id, input);
	}
	/**
	* Creates a new customer status.
	*
	* @param input - required input to pass to createCustomerStatus
	* @returns CustomerStatusPayload
	*/
	createCustomerStatus(input) {
		return new CreateCustomerStatusMutation(this._request).fetch(input);
	}
	/**
	* Deletes a customer status.
	*
	* @param id - required id to pass to deleteCustomerStatus
	* @returns DeletePayload
	*/
	deleteCustomerStatus(id) {
		return new DeleteCustomerStatusMutation(this._request).fetch(id);
	}
	/**
	* Updates a customer status.
	*
	* @param id - required id to pass to updateCustomerStatus
	* @param input - required input to pass to updateCustomerStatus
	* @returns CustomerStatusPayload
	*/
	updateCustomerStatus(id, input) {
		return new UpdateCustomerStatusMutation(this._request).fetch(id, input);
	}
	/**
	* Creates a new customer tier.
	*
	* @param input - required input to pass to createCustomerTier
	* @returns CustomerTierPayload
	*/
	createCustomerTier(input) {
		return new CreateCustomerTierMutation(this._request).fetch(input);
	}
	/**
	* Deletes a customer tier.
	*
	* @param id - required id to pass to deleteCustomerTier
	* @returns DeletePayload
	*/
	deleteCustomerTier(id) {
		return new DeleteCustomerTierMutation(this._request).fetch(id);
	}
	/**
	* Updates a customer tier.
	*
	* @param id - required id to pass to updateCustomerTier
	* @param input - required input to pass to updateCustomerTier
	* @returns CustomerTierPayload
	*/
	updateCustomerTier(id, input) {
		return new UpdateCustomerTierMutation(this._request).fetch(id, input);
	}
	/**
	* Unsyncs a managed customer from the its current data source. External IDs mapping to the external source will be cleared.
	*
	* @param id - required id to pass to customerUnsync
	* @returns CustomerPayload
	*/
	customerUnsync(id) {
		return new CustomerUnsyncMutation(this._request).fetch(id);
	}
	/**
	* Updates a customer
	*
	* @param id - required id to pass to updateCustomer
	* @param input - required input to pass to updateCustomer
	* @returns CustomerPayload
	*/
	updateCustomer(id, input) {
		return new UpdateCustomerMutation(this._request).fetch(id, input);
	}
	/**
	* Upserts a customer, creating it if it doesn't exists, updating it otherwise. Matches against an existing customer with `id` or `externalId`
	*
	* @param input - required input to pass to customerUpsert
	* @returns CustomerPayload
	*/
	customerUpsert(input) {
		return new CustomerUpsertMutation(this._request).fetch(input);
	}
	/**
	* Archives a cycle.
	*
	* @param id - required id to pass to archiveCycle
	* @returns CycleArchivePayload
	*/
	archiveCycle(id) {
		return new ArchiveCycleMutation(this._request).fetch(id);
	}
	/**
	* Creates a new cycle.
	*
	* @param input - required input to pass to createCycle
	* @returns CyclePayload
	*/
	createCycle(input) {
		return new CreateCycleMutation(this._request).fetch(input);
	}
	/**
	* Shifts all cycles starts and ends by a certain number of days, starting from the provided cycle onwards.
	*
	* @param input - required input to pass to cycleShiftAll
	* @returns CyclePayload
	*/
	cycleShiftAll(input) {
		return new CycleShiftAllMutation(this._request).fetch(input);
	}
	/**
	* Shifts all cycles starts and ends by a certain number of days, starting from the provided cycle onwards.
	*
	* @param id - required id to pass to cycleStartUpcomingCycleToday
	* @returns CyclePayload
	*/
	cycleStartUpcomingCycleToday(id) {
		return new CycleStartUpcomingCycleTodayMutation(this._request).fetch(id);
	}
	/**
	* Updates a cycle.
	*
	* @param id - required id to pass to updateCycle
	* @param input - required input to pass to updateCycle
	* @returns CyclePayload
	*/
	updateCycle(id, input) {
		return new UpdateCycleMutation(this._request).fetch(id, input);
	}
	/**
	* Creates a new document.
	*
	* @param input - required input to pass to createDocument
	* @returns DocumentPayload
	*/
	createDocument(input) {
		return new CreateDocumentMutation(this._request).fetch(input);
	}
	/**
	* Deletes (trashes) a document.
	*
	* @param id - required id to pass to deleteDocument
	* @returns DocumentArchivePayload
	*/
	deleteDocument(id) {
		return new DeleteDocumentMutation(this._request).fetch(id);
	}
	/**
	* Restores a document.
	*
	* @param id - required id to pass to unarchiveDocument
	* @returns DocumentArchivePayload
	*/
	unarchiveDocument(id) {
		return new UnarchiveDocumentMutation(this._request).fetch(id);
	}
	/**
	* Updates a document.
	*
	* @param id - required id to pass to updateDocument
	* @param input - required input to pass to updateDocument
	* @returns DocumentPayload
	*/
	updateDocument(id, input) {
		return new UpdateDocumentMutation(this._request).fetch(id, input);
	}
	/**
	* Creates a new email intake address.
	*
	* @param input - required input to pass to createEmailIntakeAddress
	* @returns EmailIntakeAddressPayload
	*/
	createEmailIntakeAddress(input) {
		return new CreateEmailIntakeAddressMutation(this._request).fetch(input);
	}
	/**
	* Deletes an email intake address object.
	*
	* @param id - required id to pass to deleteEmailIntakeAddress
	* @returns DeletePayload
	*/
	deleteEmailIntakeAddress(id) {
		return new DeleteEmailIntakeAddressMutation(this._request).fetch(id);
	}
	/**
	* Rotates an existing email intake address.
	*
	* @param id - required id to pass to emailIntakeAddressRotate
	* @returns EmailIntakeAddressPayload
	*/
	emailIntakeAddressRotate(id) {
		return new EmailIntakeAddressRotateMutation(this._request).fetch(id);
	}
	/**
	* Updates an existing email intake address.
	*
	* @param id - required id to pass to updateEmailIntakeAddress
	* @param input - required input to pass to updateEmailIntakeAddress
	* @returns EmailIntakeAddressPayload
	*/
	updateEmailIntakeAddress(id, input) {
		return new UpdateEmailIntakeAddressMutation(this._request).fetch(id, input);
	}
	/**
	* Authenticates a user account via email and authentication token.
	*
	* @param input - required input to pass to emailTokenUserAccountAuth
	* @returns AuthResolverResponse
	*/
	emailTokenUserAccountAuth(input) {
		return new EmailTokenUserAccountAuthMutation(this._request).fetch(input);
	}
	/**
	* Unsubscribes the user from one type of email.
	*
	* @param input - required input to pass to emailUnsubscribe
	* @returns EmailUnsubscribePayload
	*/
	emailUnsubscribe(input) {
		return new EmailUnsubscribeMutation(this._request).fetch(input);
	}
	/**
	* Finds or creates a new user account by email and sends an email with token.
	*
	* @param input - required input to pass to emailUserAccountAuthChallenge
	* @returns EmailUserAccountAuthChallengeResponse
	*/
	emailUserAccountAuthChallenge(input) {
		return new EmailUserAccountAuthChallengeMutation(this._request).fetch(input);
	}
	/**
	* Creates a custom emoji.
	*
	* @param input - required input to pass to createEmoji
	* @returns EmojiPayload
	*/
	createEmoji(input) {
		return new CreateEmojiMutation(this._request).fetch(input);
	}
	/**
	* Deletes an emoji.
	*
	* @param id - required id to pass to deleteEmoji
	* @returns DeletePayload
	*/
	deleteEmoji(id) {
		return new DeleteEmojiMutation(this._request).fetch(id);
	}
	/**
	* Creates a new entity link.
	*
	* @param input - required input to pass to createEntityExternalLink
	* @returns EntityExternalLinkPayload
	*/
	createEntityExternalLink(input) {
		return new CreateEntityExternalLinkMutation(this._request).fetch(input);
	}
	/**
	* Deletes an entity link.
	*
	* @param id - required id to pass to deleteEntityExternalLink
	* @returns DeletePayload
	*/
	deleteEntityExternalLink(id) {
		return new DeleteEntityExternalLinkMutation(this._request).fetch(id);
	}
	/**
	* Updates an entity link.
	*
	* @param id - required id to pass to updateEntityExternalLink
	* @param input - required input to pass to updateEntityExternalLink
	* @returns EntityExternalLinkPayload
	*/
	updateEntityExternalLink(id, input) {
		return new UpdateEntityExternalLinkMutation(this._request).fetch(id, input);
	}
	/**
	* Creates a new favorite (project, cycle etc).
	*
	* @param input - required input to pass to createFavorite
	* @returns FavoritePayload
	*/
	createFavorite(input) {
		return new CreateFavoriteMutation(this._request).fetch(input);
	}
	/**
	* Deletes a favorite reference.
	*
	* @param id - required id to pass to deleteFavorite
	* @returns DeletePayload
	*/
	deleteFavorite(id) {
		return new DeleteFavoriteMutation(this._request).fetch(id);
	}
	/**
	* Updates a favorite.
	*
	* @param id - required id to pass to updateFavorite
	* @param input - required input to pass to updateFavorite
	* @returns FavoritePayload
	*/
	updateFavorite(id, input) {
		return new UpdateFavoriteMutation(this._request).fetch(id, input);
	}
	/**
	* XHR request payload to upload an images, video and other attachments directly to Linear's cloud storage.
	*
	* @param contentType - required contentType to pass to fileUpload
	* @param filename - required filename to pass to fileUpload
	* @param size - required size to pass to fileUpload
	* @param variables - variables without 'contentType', 'filename', 'size' to pass into the FileUploadMutation
	* @returns UploadPayload
	*/
	fileUpload(contentType, filename, size, variables) {
		return new FileUploadMutation(this._request).fetch(contentType, filename, size, variables);
	}
	/**
	* Creates a new automation state.
	*
	* @param input - required input to pass to createGitAutomationState
	* @returns GitAutomationStatePayload
	*/
	createGitAutomationState(input) {
		return new CreateGitAutomationStateMutation(this._request).fetch(input);
	}
	/**
	* Archives an automation state.
	*
	* @param id - required id to pass to deleteGitAutomationState
	* @returns DeletePayload
	*/
	deleteGitAutomationState(id) {
		return new DeleteGitAutomationStateMutation(this._request).fetch(id);
	}
	/**
	* Updates an existing state.
	*
	* @param id - required id to pass to updateGitAutomationState
	* @param input - required input to pass to updateGitAutomationState
	* @returns GitAutomationStatePayload
	*/
	updateGitAutomationState(id, input) {
		return new UpdateGitAutomationStateMutation(this._request).fetch(id, input);
	}
	/**
	* Creates a Git target branch automation.
	*
	* @param input - required input to pass to createGitAutomationTargetBranch
	* @returns GitAutomationTargetBranchPayload
	*/
	createGitAutomationTargetBranch(input) {
		return new CreateGitAutomationTargetBranchMutation(this._request).fetch(input);
	}
	/**
	* Archives a Git target branch automation.
	*
	* @param id - required id to pass to deleteGitAutomationTargetBranch
	* @returns DeletePayload
	*/
	deleteGitAutomationTargetBranch(id) {
		return new DeleteGitAutomationTargetBranchMutation(this._request).fetch(id);
	}
	/**
	* Updates an existing Git target branch automation.
	*
	* @param id - required id to pass to updateGitAutomationTargetBranch
	* @param input - required input to pass to updateGitAutomationTargetBranch
	* @returns GitAutomationTargetBranchPayload
	*/
	updateGitAutomationTargetBranch(id, input) {
		return new UpdateGitAutomationTargetBranchMutation(this._request).fetch(id, input);
	}
	/**
	* Authenticate user account through Google OAuth. This is the 2nd step of OAuth flow.
	*
	* @param input - required input to pass to googleUserAccountAuth
	* @returns AuthResolverResponse
	*/
	googleUserAccountAuth(input) {
		return new GoogleUserAccountAuthMutation(this._request).fetch(input);
	}
	/**
	* Upload an image from an URL to Linear.
	*
	* @param url - required url to pass to imageUploadFromUrl
	* @returns ImageUploadFromUrlPayload
	*/
	imageUploadFromUrl(url) {
		return new ImageUploadFromUrlMutation(this._request).fetch(url);
	}
	/**
	* XHR request payload to upload a file for import, directly to Linear's cloud storage.
	*
	* @param contentType - required contentType to pass to importFileUpload
	* @param filename - required filename to pass to importFileUpload
	* @param size - required size to pass to importFileUpload
	* @param variables - variables without 'contentType', 'filename', 'size' to pass into the ImportFileUploadMutation
	* @returns UploadPayload
	*/
	importFileUpload(contentType, filename, size, variables) {
		return new ImportFileUploadMutation(this._request).fetch(contentType, filename, size, variables);
	}
	/**
	* Archives a initiative.
	*
	* @param id - required id to pass to archiveInitiative
	* @returns InitiativeArchivePayload
	*/
	archiveInitiative(id) {
		return new ArchiveInitiativeMutation(this._request).fetch(id);
	}
	/**
	* Creates a new initiative.
	*
	* @param input - required input to pass to createInitiative
	* @returns InitiativePayload
	*/
	createInitiative(input) {
		return new CreateInitiativeMutation(this._request).fetch(input);
	}
	/**
	* Deletes (trashes) an initiative.
	*
	* @param id - required id to pass to deleteInitiative
	* @returns DeletePayload
	*/
	deleteInitiative(id) {
		return new DeleteInitiativeMutation(this._request).fetch(id);
	}
	/**
	* Creates a new initiative relation.
	*
	* @param input - required input to pass to createInitiativeRelation
	* @returns InitiativeRelationPayload
	*/
	createInitiativeRelation(input) {
		return new CreateInitiativeRelationMutation(this._request).fetch(input);
	}
	/**
	* Deletes an initiative relation.
	*
	* @param id - required id to pass to deleteInitiativeRelation
	* @returns DeletePayload
	*/
	deleteInitiativeRelation(id) {
		return new DeleteInitiativeRelationMutation(this._request).fetch(id);
	}
	/**
	* Updates an initiative relation.
	*
	* @param id - required id to pass to updateInitiativeRelation
	* @param input - required input to pass to updateInitiativeRelation
	* @returns DeletePayload
	*/
	updateInitiativeRelation(id, input) {
		return new UpdateInitiativeRelationMutation(this._request).fetch(id, input);
	}
	/**
	* Creates a new initiativeToProject join.
	*
	* @param input - required input to pass to createInitiativeToProject
	* @returns InitiativeToProjectPayload
	*/
	createInitiativeToProject(input) {
		return new CreateInitiativeToProjectMutation(this._request).fetch(input);
	}
	/**
	* Deletes a initiativeToProject.
	*
	* @param id - required id to pass to deleteInitiativeToProject
	* @returns DeletePayload
	*/
	deleteInitiativeToProject(id) {
		return new DeleteInitiativeToProjectMutation(this._request).fetch(id);
	}
	/**
	* Updates a initiativeToProject.
	*
	* @param id - required id to pass to updateInitiativeToProject
	* @param input - required input to pass to updateInitiativeToProject
	* @returns InitiativeToProjectPayload
	*/
	updateInitiativeToProject(id, input) {
		return new UpdateInitiativeToProjectMutation(this._request).fetch(id, input);
	}
	/**
	* Unarchives a initiative.
	*
	* @param id - required id to pass to unarchiveInitiative
	* @returns InitiativeArchivePayload
	*/
	unarchiveInitiative(id) {
		return new UnarchiveInitiativeMutation(this._request).fetch(id);
	}
	/**
	* Updates a initiative.
	*
	* @param id - required id to pass to updateInitiative
	* @param input - required input to pass to updateInitiative
	* @returns InitiativePayload
	*/
	updateInitiative(id, input) {
		return new UpdateInitiativeMutation(this._request).fetch(id, input);
	}
	/**
	* Archives an initiative update.
	*
	* @param id - required id to pass to archiveInitiativeUpdate
	* @returns InitiativeUpdateArchivePayload
	*/
	archiveInitiativeUpdate(id) {
		return new ArchiveInitiativeUpdateMutation(this._request).fetch(id);
	}
	/**
	* Creates a initiative update.
	*
	* @param input - required input to pass to createInitiativeUpdate
	* @returns InitiativeUpdatePayload
	*/
	createInitiativeUpdate(input) {
		return new CreateInitiativeUpdateMutation(this._request).fetch(input);
	}
	/**
	* Unarchives an initiative update.
	*
	* @param id - required id to pass to unarchiveInitiativeUpdate
	* @returns InitiativeUpdateArchivePayload
	*/
	unarchiveInitiativeUpdate(id) {
		return new UnarchiveInitiativeUpdateMutation(this._request).fetch(id);
	}
	/**
	* Updates an update.
	*
	* @param id - required id to pass to updateInitiativeUpdate
	* @param input - required input to pass to updateInitiativeUpdate
	* @returns InitiativeUpdatePayload
	*/
	updateInitiativeUpdate(id, input) {
		return new UpdateInitiativeUpdateMutation(this._request).fetch(id, input);
	}
	/**
	* Archives an integration.
	*
	* @param id - required id to pass to archiveIntegration
	* @returns DeletePayload
	*/
	archiveIntegration(id) {
		return new ArchiveIntegrationMutation(this._request).fetch(id);
	}
	/**
	* Connect a Slack channel to Asks.
	*
	* @param code - required code to pass to integrationAsksConnectChannel
	* @param redirectUri - required redirectUri to pass to integrationAsksConnectChannel
	* @returns AsksChannelConnectPayload
	*/
	integrationAsksConnectChannel(code, redirectUri) {
		return new IntegrationAsksConnectChannelMutation(this._request).fetch(code, redirectUri);
	}
	/**
	* Deletes an integration.
	*
	* @param id - required id to pass to deleteIntegration
	* @param variables - variables without 'id' to pass into the DeleteIntegrationMutation
	* @returns DeletePayload
	*/
	deleteIntegration(id, variables) {
		return new DeleteIntegrationMutation(this._request).fetch(id, variables);
	}
	/**
	* Integrates the organization with Discord.
	*
	* @param code - required code to pass to integrationDiscord
	* @param redirectUri - required redirectUri to pass to integrationDiscord
	* @returns IntegrationPayload
	*/
	integrationDiscord(code, redirectUri) {
		return new IntegrationDiscordMutation(this._request).fetch(code, redirectUri);
	}
	/**
	* Integrates the organization with Figma.
	*
	* @param code - required code to pass to integrationFigma
	* @param redirectUri - required redirectUri to pass to integrationFigma
	* @returns IntegrationPayload
	*/
	integrationFigma(code, redirectUri) {
		return new IntegrationFigmaMutation(this._request).fetch(code, redirectUri);
	}
	/**
	* Integrates the organization with Front.
	*
	* @param code - required code to pass to integrationFront
	* @param redirectUri - required redirectUri to pass to integrationFront
	* @returns IntegrationPayload
	*/
	integrationFront(code, redirectUri) {
		return new IntegrationFrontMutation(this._request).fetch(code, redirectUri);
	}
	/**
	* Connects the organization with a GitHub Enterprise Server.
	*
	* @param githubUrl - required githubUrl to pass to integrationGitHubEnterpriseServerConnect
	* @param organizationName - required organizationName to pass to integrationGitHubEnterpriseServerConnect
	* @returns GitHubEnterpriseServerPayload
	*/
	integrationGitHubEnterpriseServerConnect(githubUrl, organizationName) {
		return new IntegrationGitHubEnterpriseServerConnectMutation(this._request).fetch(githubUrl, organizationName);
	}
	/**
	* Connect your GitHub account to Linear.
	*
	* @param code - required code to pass to integrationGitHubPersonal
	* @param variables - variables without 'code' to pass into the IntegrationGitHubPersonalMutation
	* @returns IntegrationPayload
	*/
	integrationGitHubPersonal(code, variables) {
		return new IntegrationGitHubPersonalMutation(this._request).fetch(code, variables);
	}
	/**
	* Generates a webhook for the GitHub commit integration.
	*
	* @returns GitHubCommitIntegrationPayload
	*/
	get createIntegrationGithubCommit() {
		return new CreateIntegrationGithubCommitMutation(this._request).fetch();
	}
	/**
	* Connects the organization with the GitHub App.
	*
	* @param code - required code to pass to integrationGithubConnect
	* @param installationId - required installationId to pass to integrationGithubConnect
	* @param variables - variables without 'code', 'installationId' to pass into the IntegrationGithubConnectMutation
	* @returns IntegrationPayload
	*/
	integrationGithubConnect(code, installationId, variables) {
		return new IntegrationGithubConnectMutation(this._request).fetch(code, installationId, variables);
	}
	/**
	* Connects the organization with the GitHub Import App.
	*
	* @param code - required code to pass to integrationGithubImportConnect
	* @param installationId - required installationId to pass to integrationGithubImportConnect
	* @returns IntegrationPayload
	*/
	integrationGithubImportConnect(code, installationId) {
		return new IntegrationGithubImportConnectMutation(this._request).fetch(code, installationId);
	}
	/**
	* Refreshes the data for a GitHub import integration.
	*
	* @param id - required id to pass to integrationGithubImportRefresh
	* @returns IntegrationPayload
	*/
	integrationGithubImportRefresh(id) {
		return new IntegrationGithubImportRefreshMutation(this._request).fetch(id);
	}
	/**
	* Connects the organization with a GitLab Access Token.
	*
	* @param accessToken - required accessToken to pass to integrationGitlabConnect
	* @param gitlabUrl - required gitlabUrl to pass to integrationGitlabConnect
	* @returns GitLabIntegrationCreatePayload
	*/
	integrationGitlabConnect(accessToken, gitlabUrl) {
		return new IntegrationGitlabConnectMutation(this._request).fetch(accessToken, gitlabUrl);
	}
	/**
	* Integrates the organization with Gong.
	*
	* @param code - required code to pass to integrationGong
	* @param redirectUri - required redirectUri to pass to integrationGong
	* @returns IntegrationPayload
	*/
	integrationGong(code, redirectUri) {
		return new IntegrationGongMutation(this._request).fetch(code, redirectUri);
	}
	/**
	* Integrates the organization with Google Sheets.
	*
	* @param code - required code to pass to integrationGoogleSheets
	* @returns IntegrationPayload
	*/
	integrationGoogleSheets(code) {
		return new IntegrationGoogleSheetsMutation(this._request).fetch(code);
	}
	/**
	* Integrates the organization with Intercom.
	*
	* @param code - required code to pass to integrationIntercom
	* @param redirectUri - required redirectUri to pass to integrationIntercom
	* @param variables - variables without 'code', 'redirectUri' to pass into the IntegrationIntercomMutation
	* @returns IntegrationPayload
	*/
	integrationIntercom(code, redirectUri, variables) {
		return new IntegrationIntercomMutation(this._request).fetch(code, redirectUri, variables);
	}
	/**
	* Disconnects the organization from Intercom.
	*
	* @returns IntegrationPayload
	*/
	get deleteIntegrationIntercom() {
		return new DeleteIntegrationIntercomMutation(this._request).fetch();
	}
	/**
	* [DEPRECATED] Updates settings on the Intercom integration.
	*
	* @param input - required input to pass to updateIntegrationIntercomSettings
	* @returns IntegrationPayload
	*/
	updateIntegrationIntercomSettings(input) {
		return new UpdateIntegrationIntercomSettingsMutation(this._request).fetch(input);
	}
	/**
	* Connect your Jira account to Linear.
	*
	* @param variables - variables to pass into the IntegrationJiraPersonalMutation
	* @returns IntegrationPayload
	*/
	integrationJiraPersonal(variables) {
		return new IntegrationJiraPersonalMutation(this._request).fetch(variables);
	}
	/**
	* Enables Loom integration for the organization.
	*
	* @returns IntegrationPayload
	*/
	get integrationLoom() {
		return new IntegrationLoomMutation(this._request).fetch();
	}
	/**
	* Requests a currently unavailable integration.
	*
	* @param input - required input to pass to integrationRequest
	* @returns IntegrationRequestPayload
	*/
	integrationRequest(input) {
		return new IntegrationRequestMutation(this._request).fetch(input);
	}
	/**
	* Integrates the organization with Salesforce.
	*
	* @param code - required code to pass to integrationSalesforce
	* @param redirectUri - required redirectUri to pass to integrationSalesforce
	* @param subdomain - required subdomain to pass to integrationSalesforce
	* @returns IntegrationPayload
	*/
	integrationSalesforce(code, redirectUri, subdomain) {
		return new IntegrationSalesforceMutation(this._request).fetch(code, redirectUri, subdomain);
	}
	/**
	* Integrates the organization with Sentry.
	*
	* @param code - required code to pass to integrationSentryConnect
	* @param installationId - required installationId to pass to integrationSentryConnect
	* @param organizationSlug - required organizationSlug to pass to integrationSentryConnect
	* @returns IntegrationPayload
	*/
	integrationSentryConnect(code, installationId, organizationSlug) {
		return new IntegrationSentryConnectMutation(this._request).fetch(code, installationId, organizationSlug);
	}
	/**
	* Integrates the organization with Slack.
	*
	* @param code - required code to pass to integrationSlack
	* @param redirectUri - required redirectUri to pass to integrationSlack
	* @param variables - variables without 'code', 'redirectUri' to pass into the IntegrationSlackMutation
	* @returns IntegrationPayload
	*/
	integrationSlack(code, redirectUri, variables) {
		return new IntegrationSlackMutation(this._request).fetch(code, redirectUri, variables);
	}
	/**
	* Integrates the organization with the Slack Asks app.
	*
	* @param code - required code to pass to integrationSlackAsks
	* @param redirectUri - required redirectUri to pass to integrationSlackAsks
	* @returns IntegrationPayload
	*/
	integrationSlackAsks(code, redirectUri) {
		return new IntegrationSlackAsksMutation(this._request).fetch(code, redirectUri);
	}
	/**
	* Slack integration for custom view notifications.
	*
	* @param code - required code to pass to integrationSlackCustomViewNotifications
	* @param customViewId - required customViewId to pass to integrationSlackCustomViewNotifications
	* @param redirectUri - required redirectUri to pass to integrationSlackCustomViewNotifications
	* @returns SlackChannelConnectPayload
	*/
	integrationSlackCustomViewNotifications(code, customViewId, redirectUri) {
		return new IntegrationSlackCustomViewNotificationsMutation(this._request).fetch(code, customViewId, redirectUri);
	}
	/**
	* Integrates a Slack Asks channel with a Customer.
	*
	* @param code - required code to pass to integrationSlackCustomerChannelLink
	* @param customerId - required customerId to pass to integrationSlackCustomerChannelLink
	* @param redirectUri - required redirectUri to pass to integrationSlackCustomerChannelLink
	* @returns SuccessPayload
	*/
	integrationSlackCustomerChannelLink(code, customerId, redirectUri) {
		return new IntegrationSlackCustomerChannelLinkMutation(this._request).fetch(code, customerId, redirectUri);
	}
	/**
	* Imports custom emojis from your Slack workspace.
	*
	* @param code - required code to pass to integrationSlackImportEmojis
	* @param redirectUri - required redirectUri to pass to integrationSlackImportEmojis
	* @returns IntegrationPayload
	*/
	integrationSlackImportEmojis(code, redirectUri) {
		return new IntegrationSlackImportEmojisMutation(this._request).fetch(code, redirectUri);
	}
	/**
	* Updates the Slack team's name in Linear for an existing Slack or Asks integration.
	*
	* @param integrationId - required integrationId to pass to integrationSlackOrAsksUpdateSlackTeamName
	* @returns IntegrationSlackWorkspaceNamePayload
	*/
	integrationSlackOrAsksUpdateSlackTeamName(integrationId) {
		return new IntegrationSlackOrAsksUpdateSlackTeamNameMutation(this._request).fetch(integrationId);
	}
	/**
	* Slack integration for organization level project update notifications.
	*
	* @param code - required code to pass to integrationSlackOrgProjectUpdatesPost
	* @param redirectUri - required redirectUri to pass to integrationSlackOrgProjectUpdatesPost
	* @returns SlackChannelConnectPayload
	*/
	integrationSlackOrgProjectUpdatesPost(code, redirectUri) {
		return new IntegrationSlackOrgProjectUpdatesPostMutation(this._request).fetch(code, redirectUri);
	}
	/**
	* Integrates your personal notifications with Slack.
	*
	* @param code - required code to pass to integrationSlackPersonal
	* @param redirectUri - required redirectUri to pass to integrationSlackPersonal
	* @returns IntegrationPayload
	*/
	integrationSlackPersonal(code, redirectUri) {
		return new IntegrationSlackPersonalMutation(this._request).fetch(code, redirectUri);
	}
	/**
	* Slack integration for team notifications.
	*
	* @param code - required code to pass to integrationSlackPost
	* @param redirectUri - required redirectUri to pass to integrationSlackPost
	* @param teamId - required teamId to pass to integrationSlackPost
	* @param variables - variables without 'code', 'redirectUri', 'teamId' to pass into the IntegrationSlackPostMutation
	* @returns SlackChannelConnectPayload
	*/
	integrationSlackPost(code, redirectUri, teamId, variables) {
		return new IntegrationSlackPostMutation(this._request).fetch(code, redirectUri, teamId, variables);
	}
	/**
	* Slack integration for project notifications.
	*
	* @param code - required code to pass to integrationSlackProjectPost
	* @param projectId - required projectId to pass to integrationSlackProjectPost
	* @param redirectUri - required redirectUri to pass to integrationSlackProjectPost
	* @param service - required service to pass to integrationSlackProjectPost
	* @returns SlackChannelConnectPayload
	*/
	integrationSlackProjectPost(code, projectId, redirectUri, service) {
		return new IntegrationSlackProjectPostMutation(this._request).fetch(code, projectId, redirectUri, service);
	}
	/**
	* Creates a new integrationTemplate join.
	*
	* @param input - required input to pass to createIntegrationTemplate
	* @returns IntegrationTemplatePayload
	*/
	createIntegrationTemplate(input) {
		return new CreateIntegrationTemplateMutation(this._request).fetch(input);
	}
	/**
	* Deletes a integrationTemplate.
	*
	* @param id - required id to pass to deleteIntegrationTemplate
	* @returns DeletePayload
	*/
	deleteIntegrationTemplate(id) {
		return new DeleteIntegrationTemplateMutation(this._request).fetch(id);
	}
	/**
	* Integrates the organization with Zendesk.
	*
	* @param code - required code to pass to integrationZendesk
	* @param redirectUri - required redirectUri to pass to integrationZendesk
	* @param scope - required scope to pass to integrationZendesk
	* @param subdomain - required subdomain to pass to integrationZendesk
	* @returns IntegrationPayload
	*/
	integrationZendesk(code, redirectUri, scope, subdomain) {
		return new IntegrationZendeskMutation(this._request).fetch(code, redirectUri, scope, subdomain);
	}
	/**
	* Creates new settings for one or more integrations.
	*
	* @param input - required input to pass to createIntegrationsSettings
	* @returns IntegrationsSettingsPayload
	*/
	createIntegrationsSettings(input) {
		return new CreateIntegrationsSettingsMutation(this._request).fetch(input);
	}
	/**
	* Updates settings related to integrations for a project or a team.
	*
	* @param id - required id to pass to updateIntegrationsSettings
	* @param input - required input to pass to updateIntegrationsSettings
	* @returns IntegrationsSettingsPayload
	*/
	updateIntegrationsSettings(id, input) {
		return new UpdateIntegrationsSettingsMutation(this._request).fetch(id, input);
	}
	/**
	* Adds a label to an issue.
	*
	* @param id - required id to pass to issueAddLabel
	* @param labelId - required labelId to pass to issueAddLabel
	* @returns IssuePayload
	*/
	issueAddLabel(id, labelId) {
		return new IssueAddLabelMutation(this._request).fetch(id, labelId);
	}
	/**
	* Archives an issue.
	*
	* @param id - required id to pass to archiveIssue
	* @param variables - variables without 'id' to pass into the ArchiveIssueMutation
	* @returns IssueArchivePayload
	*/
	archiveIssue(id, variables) {
		return new ArchiveIssueMutation(this._request).fetch(id, variables);
	}
	/**
	* Creates a list of issues in one transaction.
	*
	* @param input - required input to pass to createIssueBatch
	* @returns IssueBatchPayload
	*/
	createIssueBatch(input) {
		return new CreateIssueBatchMutation(this._request).fetch(input);
	}
	/**
	* Updates multiple issues at once.
	*
	* @param ids - required ids to pass to updateIssueBatch
	* @param input - required input to pass to updateIssueBatch
	* @returns IssueBatchPayload
	*/
	updateIssueBatch(ids, input) {
		return new UpdateIssueBatchMutation(this._request).fetch(ids, input);
	}
	/**
	* Creates a new issue.
	*
	* @param input - required input to pass to createIssue
	* @returns IssuePayload
	*/
	createIssue(input) {
		return new CreateIssueMutation(this._request).fetch(input);
	}
	/**
	* Deletes (trashes) an issue.
	*
	* @param id - required id to pass to deleteIssue
	* @param variables - variables without 'id' to pass into the DeleteIssueMutation
	* @returns IssueArchivePayload
	*/
	deleteIssue(id, variables) {
		return new DeleteIssueMutation(this._request).fetch(id, variables);
	}
	/**
	* Disables external sync on an issue.
	*
	* @param attachmentId - required attachmentId to pass to issueExternalSyncDisable
	* @returns IssuePayload
	*/
	issueExternalSyncDisable(attachmentId) {
		return new IssueExternalSyncDisableMutation(this._request).fetch(attachmentId);
	}
	/**
	* Kicks off an Asana import job.
	*
	* @param asanaTeamName - required asanaTeamName to pass to issueImportCreateAsana
	* @param asanaToken - required asanaToken to pass to issueImportCreateAsana
	* @param variables - variables without 'asanaTeamName', 'asanaToken' to pass into the IssueImportCreateAsanaMutation
	* @returns IssueImportPayload
	*/
	issueImportCreateAsana(asanaTeamName, asanaToken, variables) {
		return new IssueImportCreateAsanaMutation(this._request).fetch(asanaTeamName, asanaToken, variables);
	}
	/**
	* Kicks off a Jira import job from a CSV.
	*
	* @param csvUrl - required csvUrl to pass to issueImportCreateCSVJira
	* @param variables - variables without 'csvUrl' to pass into the IssueImportCreateCsvJiraMutation
	* @returns IssueImportPayload
	*/
	issueImportCreateCSVJira(csvUrl, variables) {
		return new IssueImportCreateCsvJiraMutation(this._request).fetch(csvUrl, variables);
	}
	/**
	* Kicks off a Shortcut (formerly Clubhouse) import job.
	*
	* @param clubhouseGroupName - required clubhouseGroupName to pass to issueImportCreateClubhouse
	* @param clubhouseToken - required clubhouseToken to pass to issueImportCreateClubhouse
	* @param variables - variables without 'clubhouseGroupName', 'clubhouseToken' to pass into the IssueImportCreateClubhouseMutation
	* @returns IssueImportPayload
	*/
	issueImportCreateClubhouse(clubhouseGroupName, clubhouseToken, variables) {
		return new IssueImportCreateClubhouseMutation(this._request).fetch(clubhouseGroupName, clubhouseToken, variables);
	}
	/**
	* Kicks off a GitHub import job.
	*
	* @param variables - variables to pass into the IssueImportCreateGithubMutation
	* @returns IssueImportPayload
	*/
	issueImportCreateGithub(variables) {
		return new IssueImportCreateGithubMutation(this._request).fetch(variables);
	}
	/**
	* Kicks off a Jira import job.
	*
	* @param jiraEmail - required jiraEmail to pass to issueImportCreateJira
	* @param jiraHostname - required jiraHostname to pass to issueImportCreateJira
	* @param jiraProject - required jiraProject to pass to issueImportCreateJira
	* @param jiraToken - required jiraToken to pass to issueImportCreateJira
	* @param variables - variables without 'jiraEmail', 'jiraHostname', 'jiraProject', 'jiraToken' to pass into the IssueImportCreateJiraMutation
	* @returns IssueImportPayload
	*/
	issueImportCreateJira(jiraEmail, jiraHostname, jiraProject, jiraToken, variables) {
		return new IssueImportCreateJiraMutation(this._request).fetch(jiraEmail, jiraHostname, jiraProject, jiraToken, variables);
	}
	/**
	* Deletes an import job.
	*
	* @param issueImportId - required issueImportId to pass to deleteIssueImport
	* @returns IssueImportDeletePayload
	*/
	deleteIssueImport(issueImportId) {
		return new DeleteIssueImportMutation(this._request).fetch(issueImportId);
	}
	/**
	* Kicks off import processing.
	*
	* @param issueImportId - required issueImportId to pass to issueImportProcess
	* @param mapping - required mapping to pass to issueImportProcess
	* @returns IssueImportPayload
	*/
	issueImportProcess(issueImportId, mapping) {
		return new IssueImportProcessMutation(this._request).fetch(issueImportId, mapping);
	}
	/**
	* Updates the mapping for the issue import.
	*
	* @param id - required id to pass to updateIssueImport
	* @param input - required input to pass to updateIssueImport
	* @returns IssueImportPayload
	*/
	updateIssueImport(id, input) {
		return new UpdateIssueImportMutation(this._request).fetch(id, input);
	}
	/**
	* Creates a new label.
	*
	* @param input - required input to pass to createIssueLabel
	* @param variables - variables without 'input' to pass into the CreateIssueLabelMutation
	* @returns IssueLabelPayload
	*/
	createIssueLabel(input, variables) {
		return new CreateIssueLabelMutation(this._request).fetch(input, variables);
	}
	/**
	* Deletes an issue label.
	*
	* @param id - required id to pass to deleteIssueLabel
	* @returns DeletePayload
	*/
	deleteIssueLabel(id) {
		return new DeleteIssueLabelMutation(this._request).fetch(id);
	}
	/**
	* Restores a label.
	*
	* @param id - required id to pass to issueLabelRestore
	* @returns IssueLabelPayload
	*/
	issueLabelRestore(id) {
		return new IssueLabelRestoreMutation(this._request).fetch(id);
	}
	/**
	* Retires a label.
	*
	* @param id - required id to pass to issueLabelRetire
	* @returns IssueLabelPayload
	*/
	issueLabelRetire(id) {
		return new IssueLabelRetireMutation(this._request).fetch(id);
	}
	/**
	* Updates a label.
	*
	* @param id - required id to pass to updateIssueLabel
	* @param input - required input to pass to updateIssueLabel
	* @param variables - variables without 'id', 'input' to pass into the UpdateIssueLabelMutation
	* @returns IssueLabelPayload
	*/
	updateIssueLabel(id, input, variables) {
		return new UpdateIssueLabelMutation(this._request).fetch(id, input, variables);
	}
	/**
	* Creates a new issue relation.
	*
	* @param input - required input to pass to createIssueRelation
	* @param variables - variables without 'input' to pass into the CreateIssueRelationMutation
	* @returns IssueRelationPayload
	*/
	createIssueRelation(input, variables) {
		return new CreateIssueRelationMutation(this._request).fetch(input, variables);
	}
	/**
	* Deletes an issue relation.
	*
	* @param id - required id to pass to deleteIssueRelation
	* @returns DeletePayload
	*/
	deleteIssueRelation(id) {
		return new DeleteIssueRelationMutation(this._request).fetch(id);
	}
	/**
	* Updates an issue relation.
	*
	* @param id - required id to pass to updateIssueRelation
	* @param input - required input to pass to updateIssueRelation
	* @returns IssueRelationPayload
	*/
	updateIssueRelation(id, input) {
		return new UpdateIssueRelationMutation(this._request).fetch(id, input);
	}
	/**
	* Adds an issue reminder. Will cause a notification to be sent when the issue reminder time is reached.
	*
	* @param id - required id to pass to issueReminder
	* @param reminderAt - required reminderAt to pass to issueReminder
	* @returns IssuePayload
	*/
	issueReminder(id, reminderAt) {
		return new IssueReminderMutation(this._request).fetch(id, reminderAt);
	}
	/**
	* Removes a label from an issue.
	*
	* @param id - required id to pass to issueRemoveLabel
	* @param labelId - required labelId to pass to issueRemoveLabel
	* @returns IssuePayload
	*/
	issueRemoveLabel(id, labelId) {
		return new IssueRemoveLabelMutation(this._request).fetch(id, labelId);
	}
	/**
	* Subscribes a user to an issue.
	*
	* @param id - required id to pass to issueSubscribe
	* @param variables - variables without 'id' to pass into the IssueSubscribeMutation
	* @returns IssuePayload
	*/
	issueSubscribe(id, variables) {
		return new IssueSubscribeMutation(this._request).fetch(id, variables);
	}
	/**
	* Unarchives an issue.
	*
	* @param id - required id to pass to unarchiveIssue
	* @returns IssueArchivePayload
	*/
	unarchiveIssue(id) {
		return new UnarchiveIssueMutation(this._request).fetch(id);
	}
	/**
	* Unsubscribes a user from an issue.
	*
	* @param id - required id to pass to issueUnsubscribe
	* @param variables - variables without 'id' to pass into the IssueUnsubscribeMutation
	* @returns IssuePayload
	*/
	issueUnsubscribe(id, variables) {
		return new IssueUnsubscribeMutation(this._request).fetch(id, variables);
	}
	/**
	* Updates an issue.
	*
	* @param id - required id to pass to updateIssue
	* @param input - required input to pass to updateIssue
	* @returns IssuePayload
	*/
	updateIssue(id, input) {
		return new UpdateIssueMutation(this._request).fetch(id, input);
	}
	/**
	* Join an organization from onboarding.
	*
	* @param input - required input to pass to joinOrganizationFromOnboarding
	* @returns CreateOrJoinOrganizationResponse
	*/
	joinOrganizationFromOnboarding(input) {
		return new JoinOrganizationFromOnboardingMutation(this._request).fetch(input);
	}
	/**
	* Leave an organization.
	*
	* @param organizationId - required organizationId to pass to leaveOrganization
	* @returns CreateOrJoinOrganizationResponse
	*/
	leaveOrganization(organizationId) {
		return new LeaveOrganizationMutation(this._request).fetch(organizationId);
	}
	/**
	* Logout the client.
	*
	* @param variables - variables to pass into the LogoutMutation
	* @returns LogoutResponse
	*/
	logout(variables) {
		return new LogoutMutation(this._request).fetch(variables);
	}
	/**
	* Logout all of user's sessions including the active one.
	*
	* @param variables - variables to pass into the LogoutAllSessionsMutation
	* @returns LogoutResponse
	*/
	logoutAllSessions(variables) {
		return new LogoutAllSessionsMutation(this._request).fetch(variables);
	}
	/**
	* Logout all of user's sessions excluding the current one.
	*
	* @param variables - variables to pass into the LogoutOtherSessionsMutation
	* @returns LogoutResponse
	*/
	logoutOtherSessions(variables) {
		return new LogoutOtherSessionsMutation(this._request).fetch(variables);
	}
	/**
	* Logout an individual session with its ID.
	*
	* @param sessionId - required sessionId to pass to logoutSession
	* @returns LogoutResponse
	*/
	logoutSession(sessionId) {
		return new LogoutSessionMutation(this._request).fetch(sessionId);
	}
	/**
	* Archives a notification.
	*
	* @param id - required id to pass to archiveNotification
	* @returns NotificationArchivePayload
	*/
	archiveNotification(id) {
		return new ArchiveNotificationMutation(this._request).fetch(id);
	}
	/**
	* Archives a notification and all related notifications.
	*
	* @param input - required input to pass to notificationArchiveAll
	* @returns NotificationBatchActionPayload
	*/
	notificationArchiveAll(input) {
		return new NotificationArchiveAllMutation(this._request).fetch(input);
	}
	/**
	* Subscribes to or unsubscribes from a notification category for a given notification channel for the user
	*
	* @param category - required category to pass to updateNotificationCategoryChannelSubscription
	* @param channel - required channel to pass to updateNotificationCategoryChannelSubscription
	* @param subscribe - required subscribe to pass to updateNotificationCategoryChannelSubscription
	* @returns UserSettingsPayload
	*/
	updateNotificationCategoryChannelSubscription(category, channel, subscribe) {
		return new UpdateNotificationCategoryChannelSubscriptionMutation(this._request).fetch(category, channel, subscribe);
	}
	/**
	* Marks notification and all related notifications as read.
	*
	* @param input - required input to pass to notificationMarkReadAll
	* @param readAt - required readAt to pass to notificationMarkReadAll
	* @returns NotificationBatchActionPayload
	*/
	notificationMarkReadAll(input, readAt) {
		return new NotificationMarkReadAllMutation(this._request).fetch(input, readAt);
	}
	/**
	* Marks notification and all related notifications as unread.
	*
	* @param input - required input to pass to notificationMarkUnreadAll
	* @returns NotificationBatchActionPayload
	*/
	notificationMarkUnreadAll(input) {
		return new NotificationMarkUnreadAllMutation(this._request).fetch(input);
	}
	/**
	* Snoozes a notification and all related notifications.
	*
	* @param input - required input to pass to notificationSnoozeAll
	* @param snoozedUntilAt - required snoozedUntilAt to pass to notificationSnoozeAll
	* @returns NotificationBatchActionPayload
	*/
	notificationSnoozeAll(input, snoozedUntilAt) {
		return new NotificationSnoozeAllMutation(this._request).fetch(input, snoozedUntilAt);
	}
	/**
	* Creates a new notification subscription for a cycle, custom view, label, project or team.
	*
	* @param input - required input to pass to createNotificationSubscription
	* @returns NotificationSubscriptionPayload
	*/
	createNotificationSubscription(input) {
		return new CreateNotificationSubscriptionMutation(this._request).fetch(input);
	}
	/**
	* Deletes a notification subscription reference.
	*
	* @param id - required id to pass to deleteNotificationSubscription
	* @returns DeletePayload
	*/
	deleteNotificationSubscription(id) {
		return new DeleteNotificationSubscriptionMutation(this._request).fetch(id);
	}
	/**
	* Updates a notification subscription.
	*
	* @param id - required id to pass to updateNotificationSubscription
	* @param input - required input to pass to updateNotificationSubscription
	* @returns NotificationSubscriptionPayload
	*/
	updateNotificationSubscription(id, input) {
		return new UpdateNotificationSubscriptionMutation(this._request).fetch(id, input);
	}
	/**
	* Unarchives a notification.
	*
	* @param id - required id to pass to unarchiveNotification
	* @returns NotificationArchivePayload
	*/
	unarchiveNotification(id) {
		return new UnarchiveNotificationMutation(this._request).fetch(id);
	}
	/**
	* Unsnoozes a notification and all related notifications.
	*
	* @param input - required input to pass to notificationUnsnoozeAll
	* @param unsnoozedAt - required unsnoozedAt to pass to notificationUnsnoozeAll
	* @returns NotificationBatchActionPayload
	*/
	notificationUnsnoozeAll(input, unsnoozedAt) {
		return new NotificationUnsnoozeAllMutation(this._request).fetch(input, unsnoozedAt);
	}
	/**
	* Updates a notification.
	*
	* @param id - required id to pass to updateNotification
	* @param input - required input to pass to updateNotification
	* @returns NotificationPayload
	*/
	updateNotification(id, input) {
		return new UpdateNotificationMutation(this._request).fetch(id, input);
	}
	/**
	* Cancels the deletion of an organization.
	*
	* @returns OrganizationCancelDeletePayload
	*/
	get deleteOrganizationCancel() {
		return new DeleteOrganizationCancelMutation(this._request).fetch();
	}
	/**
	* Deletes an organization.
	*
	* @param input - required input to pass to deleteOrganization
	* @returns OrganizationDeletePayload
	*/
	deleteOrganization(input) {
		return new DeleteOrganizationMutation(this._request).fetch(input);
	}
	/**
	* Get an organization's delete confirmation token.
	*
	* @returns OrganizationDeletePayload
	*/
	get organizationDeleteChallenge() {
		return new OrganizationDeleteChallengeMutation(this._request).fetch();
	}
	/**
	* Deletes a domain.
	*
	* @param id - required id to pass to deleteOrganizationDomain
	* @returns DeletePayload
	*/
	deleteOrganizationDomain(id) {
		return new DeleteOrganizationDomainMutation(this._request).fetch(id);
	}
	/**
	* Creates a new organization invite.
	*
	* @param input - required input to pass to createOrganizationInvite
	* @returns OrganizationInvitePayload
	*/
	createOrganizationInvite(input) {
		return new CreateOrganizationInviteMutation(this._request).fetch(input);
	}
	/**
	* Deletes an organization invite.
	*
	* @param id - required id to pass to deleteOrganizationInvite
	* @returns DeletePayload
	*/
	deleteOrganizationInvite(id) {
		return new DeleteOrganizationInviteMutation(this._request).fetch(id);
	}
	/**
	* Updates an organization invite.
	*
	* @param id - required id to pass to updateOrganizationInvite
	* @param input - required input to pass to updateOrganizationInvite
	* @returns OrganizationInvitePayload
	*/
	updateOrganizationInvite(id, input) {
		return new UpdateOrganizationInviteMutation(this._request).fetch(id, input);
	}
	/**
	* [DEPRECATED] Starts a trial for the organization.
	*
	* @returns OrganizationStartTrialPayload
	*/
	get organizationStartTrial() {
		return new OrganizationStartTrialMutation(this._request).fetch();
	}
	/**
	* Starts a trial for the organization on the specified plan type.
	*
	* @param input - required input to pass to organizationStartTrialForPlan
	* @returns OrganizationStartTrialPayload
	*/
	organizationStartTrialForPlan(input) {
		return new OrganizationStartTrialForPlanMutation(this._request).fetch(input);
	}
	/**
	* Updates the user's organization.
	*
	* @param input - required input to pass to updateOrganization
	* @returns OrganizationPayload
	*/
	updateOrganization(input) {
		return new UpdateOrganizationMutation(this._request).fetch(input);
	}
	/**
	* Adds a label to a project.
	*
	* @param id - required id to pass to projectAddLabel
	* @param labelId - required labelId to pass to projectAddLabel
	* @returns ProjectPayload
	*/
	projectAddLabel(id, labelId) {
		return new ProjectAddLabelMutation(this._request).fetch(id, labelId);
	}
	/**
	* Archives a project.
	*
	* @param id - required id to pass to archiveProject
	* @param variables - variables without 'id' to pass into the ArchiveProjectMutation
	* @returns ProjectArchivePayload
	*/
	archiveProject(id, variables) {
		return new ArchiveProjectMutation(this._request).fetch(id, variables);
	}
	/**
	* Creates a new project.
	*
	* @param input - required input to pass to createProject
	* @param variables - variables without 'input' to pass into the CreateProjectMutation
	* @returns ProjectPayload
	*/
	createProject(input, variables) {
		return new CreateProjectMutation(this._request).fetch(input, variables);
	}
	/**
	* Deletes (trashes) a project.
	*
	* @param id - required id to pass to deleteProject
	* @returns ProjectArchivePayload
	*/
	deleteProject(id) {
		return new DeleteProjectMutation(this._request).fetch(id);
	}
	/**
	* Disables external sync on a project.
	*
	* @param projectId - required projectId to pass to projectExternalSyncDisable
	* @param syncSource - required syncSource to pass to projectExternalSyncDisable
	* @returns ProjectPayload
	*/
	projectExternalSyncDisable(projectId, syncSource) {
		return new ProjectExternalSyncDisableMutation(this._request).fetch(projectId, syncSource);
	}
	/**
	* Creates a new project label.
	*
	* @param input - required input to pass to createProjectLabel
	* @returns ProjectLabelPayload
	*/
	createProjectLabel(input) {
		return new CreateProjectLabelMutation(this._request).fetch(input);
	}
	/**
	* Deletes a project label.
	*
	* @param id - required id to pass to deleteProjectLabel
	* @returns DeletePayload
	*/
	deleteProjectLabel(id) {
		return new DeleteProjectLabelMutation(this._request).fetch(id);
	}
	/**
	* Restores a project label.
	*
	* @param id - required id to pass to projectLabelRestore
	* @returns ProjectLabelPayload
	*/
	projectLabelRestore(id) {
		return new ProjectLabelRestoreMutation(this._request).fetch(id);
	}
	/**
	* Retires a project label.
	*
	* @param id - required id to pass to projectLabelRetire
	* @returns ProjectLabelPayload
	*/
	projectLabelRetire(id) {
		return new ProjectLabelRetireMutation(this._request).fetch(id);
	}
	/**
	* Updates a project label.
	*
	* @param id - required id to pass to updateProjectLabel
	* @param input - required input to pass to updateProjectLabel
	* @returns ProjectLabelPayload
	*/
	updateProjectLabel(id, input) {
		return new UpdateProjectLabelMutation(this._request).fetch(id, input);
	}
	/**
	* Creates a new project milestone.
	*
	* @param input - required input to pass to createProjectMilestone
	* @returns ProjectMilestonePayload
	*/
	createProjectMilestone(input) {
		return new CreateProjectMilestoneMutation(this._request).fetch(input);
	}
	/**
	* Deletes a project milestone.
	*
	* @param id - required id to pass to deleteProjectMilestone
	* @returns DeletePayload
	*/
	deleteProjectMilestone(id) {
		return new DeleteProjectMilestoneMutation(this._request).fetch(id);
	}
	/**
	* Updates a project milestone.
	*
	* @param id - required id to pass to updateProjectMilestone
	* @param input - required input to pass to updateProjectMilestone
	* @returns ProjectMilestonePayload
	*/
	updateProjectMilestone(id, input) {
		return new UpdateProjectMilestoneMutation(this._request).fetch(id, input);
	}
	/**
	* Creates a new project relation.
	*
	* @param input - required input to pass to createProjectRelation
	* @returns ProjectRelationPayload
	*/
	createProjectRelation(input) {
		return new CreateProjectRelationMutation(this._request).fetch(input);
	}
	/**
	* Deletes a project relation.
	*
	* @param id - required id to pass to deleteProjectRelation
	* @returns DeletePayload
	*/
	deleteProjectRelation(id) {
		return new DeleteProjectRelationMutation(this._request).fetch(id);
	}
	/**
	* Updates a project relation.
	*
	* @param id - required id to pass to updateProjectRelation
	* @param input - required input to pass to updateProjectRelation
	* @returns ProjectRelationPayload
	*/
	updateProjectRelation(id, input) {
		return new UpdateProjectRelationMutation(this._request).fetch(id, input);
	}
	/**
	* Removes a label from a project.
	*
	* @param id - required id to pass to projectRemoveLabel
	* @param labelId - required labelId to pass to projectRemoveLabel
	* @returns ProjectPayload
	*/
	projectRemoveLabel(id, labelId) {
		return new ProjectRemoveLabelMutation(this._request).fetch(id, labelId);
	}
	/**
	* Archives a project status.
	*
	* @param id - required id to pass to archiveProjectStatus
	* @returns ProjectStatusArchivePayload
	*/
	archiveProjectStatus(id) {
		return new ArchiveProjectStatusMutation(this._request).fetch(id);
	}
	/**
	* Creates a new project status.
	*
	* @param input - required input to pass to createProjectStatus
	* @returns ProjectStatusPayload
	*/
	createProjectStatus(input) {
		return new CreateProjectStatusMutation(this._request).fetch(input);
	}
	/**
	* Unarchives a project status.
	*
	* @param id - required id to pass to unarchiveProjectStatus
	* @returns ProjectStatusArchivePayload
	*/
	unarchiveProjectStatus(id) {
		return new UnarchiveProjectStatusMutation(this._request).fetch(id);
	}
	/**
	* Updates a project status.
	*
	* @param id - required id to pass to updateProjectStatus
	* @param input - required input to pass to updateProjectStatus
	* @returns ProjectStatusPayload
	*/
	updateProjectStatus(id, input) {
		return new UpdateProjectStatusMutation(this._request).fetch(id, input);
	}
	/**
	* Unarchives a project.
	*
	* @param id - required id to pass to unarchiveProject
	* @returns ProjectArchivePayload
	*/
	unarchiveProject(id) {
		return new UnarchiveProjectMutation(this._request).fetch(id);
	}
	/**
	* Updates a project.
	*
	* @param id - required id to pass to updateProject
	* @param input - required input to pass to updateProject
	* @returns ProjectPayload
	*/
	updateProject(id, input) {
		return new UpdateProjectMutation(this._request).fetch(id, input);
	}
	/**
	* Archives a project update.
	*
	* @param id - required id to pass to archiveProjectUpdate
	* @returns ProjectUpdateArchivePayload
	*/
	archiveProjectUpdate(id) {
		return new ArchiveProjectUpdateMutation(this._request).fetch(id);
	}
	/**
	* Creates a new project update.
	*
	* @param input - required input to pass to createProjectUpdate
	* @returns ProjectUpdatePayload
	*/
	createProjectUpdate(input) {
		return new CreateProjectUpdateMutation(this._request).fetch(input);
	}
	/**
	* Deletes a project update.
	*
	* @param id - required id to pass to deleteProjectUpdate
	* @returns DeletePayload
	*/
	deleteProjectUpdate(id) {
		return new DeleteProjectUpdateMutation(this._request).fetch(id);
	}
	/**
	* Unarchives a project update.
	*
	* @param id - required id to pass to unarchiveProjectUpdate
	* @returns ProjectUpdateArchivePayload
	*/
	unarchiveProjectUpdate(id) {
		return new UnarchiveProjectUpdateMutation(this._request).fetch(id);
	}
	/**
	* Updates a project update.
	*
	* @param id - required id to pass to updateProjectUpdate
	* @param input - required input to pass to updateProjectUpdate
	* @returns ProjectUpdatePayload
	*/
	updateProjectUpdate(id, input) {
		return new UpdateProjectUpdateMutation(this._request).fetch(id, input);
	}
	/**
	* Creates a push subscription.
	*
	* @param input - required input to pass to createPushSubscription
	* @returns PushSubscriptionPayload
	*/
	createPushSubscription(input) {
		return new CreatePushSubscriptionMutation(this._request).fetch(input);
	}
	/**
	* Deletes a push subscription.
	*
	* @param id - required id to pass to deletePushSubscription
	* @returns PushSubscriptionPayload
	*/
	deletePushSubscription(id) {
		return new DeletePushSubscriptionMutation(this._request).fetch(id);
	}
	/**
	* Creates a new reaction.
	*
	* @param input - required input to pass to createReaction
	* @returns ReactionPayload
	*/
	createReaction(input) {
		return new CreateReactionMutation(this._request).fetch(input);
	}
	/**
	* Deletes a reaction.
	*
	* @param id - required id to pass to deleteReaction
	* @returns DeletePayload
	*/
	deleteReaction(id) {
		return new DeleteReactionMutation(this._request).fetch(id);
	}
	/**
	* Manually update Google Sheets data.
	*
	* @param id - required id to pass to refreshGoogleSheetsData
	* @param variables - variables without 'id' to pass into the RefreshGoogleSheetsDataMutation
	* @returns IntegrationPayload
	*/
	refreshGoogleSheetsData(id, variables) {
		return new RefreshGoogleSheetsDataMutation(this._request).fetch(id, variables);
	}
	/**
	* Re-send an organization invite.
	*
	* @param id - required id to pass to resendOrganizationInvite
	* @returns DeletePayload
	*/
	resendOrganizationInvite(id) {
		return new ResendOrganizationInviteMutation(this._request).fetch(id);
	}
	/**
	* Re-send an organization invite tied to an email address.
	*
	* @param email - required email to pass to resendOrganizationInviteByEmail
	* @returns DeletePayload
	*/
	resendOrganizationInviteByEmail(email) {
		return new ResendOrganizationInviteByEmailMutation(this._request).fetch(email);
	}
	/**
	* Archives a roadmap.
	*
	* @param id - required id to pass to archiveRoadmap
	* @returns RoadmapArchivePayload
	*/
	archiveRoadmap(id) {
		return new ArchiveRoadmapMutation(this._request).fetch(id);
	}
	/**
	* Creates a new roadmap.
	*
	* @param input - required input to pass to createRoadmap
	* @returns RoadmapPayload
	*/
	createRoadmap(input) {
		return new CreateRoadmapMutation(this._request).fetch(input);
	}
	/**
	* Deletes a roadmap.
	*
	* @param id - required id to pass to deleteRoadmap
	* @returns DeletePayload
	*/
	deleteRoadmap(id) {
		return new DeleteRoadmapMutation(this._request).fetch(id);
	}
	/**
	* Creates a new roadmapToProject join.
	*
	* @param input - required input to pass to createRoadmapToProject
	* @returns RoadmapToProjectPayload
	*/
	createRoadmapToProject(input) {
		return new CreateRoadmapToProjectMutation(this._request).fetch(input);
	}
	/**
	* Deletes a roadmapToProject.
	*
	* @param id - required id to pass to deleteRoadmapToProject
	* @returns DeletePayload
	*/
	deleteRoadmapToProject(id) {
		return new DeleteRoadmapToProjectMutation(this._request).fetch(id);
	}
	/**
	* Updates a roadmapToProject.
	*
	* @param id - required id to pass to updateRoadmapToProject
	* @param input - required input to pass to updateRoadmapToProject
	* @returns RoadmapToProjectPayload
	*/
	updateRoadmapToProject(id, input) {
		return new UpdateRoadmapToProjectMutation(this._request).fetch(id, input);
	}
	/**
	* Unarchives a roadmap.
	*
	* @param id - required id to pass to unarchiveRoadmap
	* @returns RoadmapArchivePayload
	*/
	unarchiveRoadmap(id) {
		return new UnarchiveRoadmapMutation(this._request).fetch(id);
	}
	/**
	* Updates a roadmap.
	*
	* @param id - required id to pass to updateRoadmap
	* @param input - required input to pass to updateRoadmap
	* @returns RoadmapPayload
	*/
	updateRoadmap(id, input) {
		return new UpdateRoadmapMutation(this._request).fetch(id, input);
	}
	/**
	* Authenticates a user account via email and authentication token for SAML.
	*
	* @param input - required input to pass to samlTokenUserAccountAuth
	* @returns AuthResolverResponse
	*/
	samlTokenUserAccountAuth(input) {
		return new SamlTokenUserAccountAuthMutation(this._request).fetch(input);
	}
	/**
	* Creates a new team. The user who creates the team will automatically be added as a member to the newly created team.
	*
	* @param input - required input to pass to createTeam
	* @param variables - variables without 'input' to pass into the CreateTeamMutation
	* @returns TeamPayload
	*/
	createTeam(input, variables) {
		return new CreateTeamMutation(this._request).fetch(input, variables);
	}
	/**
	* Deletes team's cycles data
	*
	* @param id - required id to pass to deleteTeamCycles
	* @returns TeamPayload
	*/
	deleteTeamCycles(id) {
		return new DeleteTeamCyclesMutation(this._request).fetch(id);
	}
	/**
	* Deletes a team.
	*
	* @param id - required id to pass to deleteTeam
	* @returns DeletePayload
	*/
	deleteTeam(id) {
		return new DeleteTeamMutation(this._request).fetch(id);
	}
	/**
	* Deletes a previously used team key.
	*
	* @param id - required id to pass to deleteTeamKey
	* @returns DeletePayload
	*/
	deleteTeamKey(id) {
		return new DeleteTeamKeyMutation(this._request).fetch(id);
	}
	/**
	* Creates a new team membership.
	*
	* @param input - required input to pass to createTeamMembership
	* @returns TeamMembershipPayload
	*/
	createTeamMembership(input) {
		return new CreateTeamMembershipMutation(this._request).fetch(input);
	}
	/**
	* Deletes a team membership.
	*
	* @param id - required id to pass to deleteTeamMembership
	* @param variables - variables without 'id' to pass into the DeleteTeamMembershipMutation
	* @returns DeletePayload
	*/
	deleteTeamMembership(id, variables) {
		return new DeleteTeamMembershipMutation(this._request).fetch(id, variables);
	}
	/**
	* Updates a team membership.
	*
	* @param id - required id to pass to updateTeamMembership
	* @param input - required input to pass to updateTeamMembership
	* @returns TeamMembershipPayload
	*/
	updateTeamMembership(id, input) {
		return new UpdateTeamMembershipMutation(this._request).fetch(id, input);
	}
	/**
	* Unarchives a team and cancels deletion.
	*
	* @param id - required id to pass to unarchiveTeam
	* @returns TeamArchivePayload
	*/
	unarchiveTeam(id) {
		return new UnarchiveTeamMutation(this._request).fetch(id);
	}
	/**
	* Updates a team.
	*
	* @param id - required id to pass to updateTeam
	* @param input - required input to pass to updateTeam
	* @param variables - variables without 'id', 'input' to pass into the UpdateTeamMutation
	* @returns TeamPayload
	*/
	updateTeam(id, input, variables) {
		return new UpdateTeamMutation(this._request).fetch(id, input, variables);
	}
	/**
	* Creates a new template.
	*
	* @param input - required input to pass to createTemplate
	* @returns TemplatePayload
	*/
	createTemplate(input) {
		return new CreateTemplateMutation(this._request).fetch(input);
	}
	/**
	* Deletes a template.
	*
	* @param id - required id to pass to deleteTemplate
	* @returns DeletePayload
	*/
	deleteTemplate(id) {
		return new DeleteTemplateMutation(this._request).fetch(id);
	}
	/**
	* Updates an existing template.
	*
	* @param id - required id to pass to updateTemplate
	* @param input - required input to pass to updateTemplate
	* @returns TemplatePayload
	*/
	updateTemplate(id, input) {
		return new UpdateTemplateMutation(this._request).fetch(id, input);
	}
	/**
	* Creates a new time schedule.
	*
	* @param input - required input to pass to createTimeSchedule
	* @returns TimeSchedulePayload
	*/
	createTimeSchedule(input) {
		return new CreateTimeScheduleMutation(this._request).fetch(input);
	}
	/**
	* Deletes a time schedule.
	*
	* @param id - required id to pass to deleteTimeSchedule
	* @returns DeletePayload
	*/
	deleteTimeSchedule(id) {
		return new DeleteTimeScheduleMutation(this._request).fetch(id);
	}
	/**
	* Refresh the integration schedule information.
	*
	* @param id - required id to pass to timeScheduleRefreshIntegrationSchedule
	* @returns TimeSchedulePayload
	*/
	timeScheduleRefreshIntegrationSchedule(id) {
		return new TimeScheduleRefreshIntegrationScheduleMutation(this._request).fetch(id);
	}
	/**
	* Updates a time schedule.
	*
	* @param id - required id to pass to updateTimeSchedule
	* @param input - required input to pass to updateTimeSchedule
	* @returns TimeSchedulePayload
	*/
	updateTimeSchedule(id, input) {
		return new UpdateTimeScheduleMutation(this._request).fetch(id, input);
	}
	/**
	* Upsert an external time schedule.
	*
	* @param externalId - required externalId to pass to timeScheduleUpsertExternal
	* @param input - required input to pass to timeScheduleUpsertExternal
	* @returns TimeSchedulePayload
	*/
	timeScheduleUpsertExternal(externalId, input) {
		return new TimeScheduleUpsertExternalMutation(this._request).fetch(externalId, input);
	}
	/**
	* Creates a new triage responsibility.
	*
	* @param input - required input to pass to createTriageResponsibility
	* @returns TriageResponsibilityPayload
	*/
	createTriageResponsibility(input) {
		return new CreateTriageResponsibilityMutation(this._request).fetch(input);
	}
	/**
	* Deletes a triage responsibility.
	*
	* @param id - required id to pass to deleteTriageResponsibility
	* @returns DeletePayload
	*/
	deleteTriageResponsibility(id) {
		return new DeleteTriageResponsibilityMutation(this._request).fetch(id);
	}
	/**
	* Updates an existing triage responsibility.
	*
	* @param id - required id to pass to updateTriageResponsibility
	* @param input - required input to pass to updateTriageResponsibility
	* @returns TriageResponsibilityPayload
	*/
	updateTriageResponsibility(id, input) {
		return new UpdateTriageResponsibilityMutation(this._request).fetch(id, input);
	}
	/**
	* Changes the role of a user.
	*
	* @param id - required id to pass to userChangeRole
	* @param role - required role to pass to userChangeRole
	* @returns UserAdminPayload
	*/
	userChangeRole(id, role) {
		return new UserChangeRoleMutation(this._request).fetch(id, role);
	}
	/**
	* [DEPRECATED] Makes user a regular user. Can only be called by an admin or owner.
	*
	* @param id - required id to pass to userDemoteAdmin
	* @returns UserAdminPayload
	*/
	userDemoteAdmin(id) {
		return new UserDemoteAdminMutation(this._request).fetch(id);
	}
	/**
	* [DEPRECATED] Makes user a guest. Can only be called by an admin.
	*
	* @param id - required id to pass to userDemoteMember
	* @returns UserAdminPayload
	*/
	userDemoteMember(id) {
		return new UserDemoteMemberMutation(this._request).fetch(id);
	}
	/**
	* Connects the Discord user to this Linear account via OAuth2.
	*
	* @param code - required code to pass to userDiscordConnect
	* @param redirectUri - required redirectUri to pass to userDiscordConnect
	* @returns UserPayload
	*/
	userDiscordConnect(code, redirectUri) {
		return new UserDiscordConnectMutation(this._request).fetch(code, redirectUri);
	}
	/**
	* Disconnects the external user from this Linear account.
	*
	* @param service - required service to pass to userExternalUserDisconnect
	* @returns UserPayload
	*/
	userExternalUserDisconnect(service) {
		return new UserExternalUserDisconnectMutation(this._request).fetch(service);
	}
	/**
	* Updates a user's settings flag.
	*
	* @param flag - required flag to pass to updateUserFlag
	* @param operation - required operation to pass to updateUserFlag
	* @returns UserSettingsFlagPayload
	*/
	updateUserFlag(flag, operation) {
		return new UpdateUserFlagMutation(this._request).fetch(flag, operation);
	}
	/**
	* [DEPRECATED] Makes user an admin. Can only be called by an admin or owner.
	*
	* @param id - required id to pass to userPromoteAdmin
	* @returns UserAdminPayload
	*/
	userPromoteAdmin(id) {
		return new UserPromoteAdminMutation(this._request).fetch(id);
	}
	/**
	* [DEPRECATED] Makes user a regular user. Can only be called by an admin.
	*
	* @param id - required id to pass to userPromoteMember
	* @returns UserAdminPayload
	*/
	userPromoteMember(id) {
		return new UserPromoteMemberMutation(this._request).fetch(id);
	}
	/**
	* Resets user's setting flags.
	*
	* @param variables - variables to pass into the UserSettingsFlagsResetMutation
	* @returns UserSettingsFlagsResetPayload
	*/
	userSettingsFlagsReset(variables) {
		return new UserSettingsFlagsResetMutation(this._request).fetch(variables);
	}
	/**
	* Updates the user's settings.
	*
	* @param id - required id to pass to updateUserSettings
	* @param input - required input to pass to updateUserSettings
	* @returns UserSettingsPayload
	*/
	updateUserSettings(id, input) {
		return new UpdateUserSettingsMutation(this._request).fetch(id, input);
	}
	/**
	* Suspends a user. Can only be called by an admin or owner.
	*
	* @param id - required id to pass to suspendUser
	* @returns UserAdminPayload
	*/
	suspendUser(id) {
		return new SuspendUserMutation(this._request).fetch(id);
	}
	/**
	* Unlinks a guest user from their identity provider. Can only be called by an admin when SCIM is enabled.
	*
	* @param id - required id to pass to userUnlinkFromIdentityProvider
	* @returns UserAdminPayload
	*/
	userUnlinkFromIdentityProvider(id) {
		return new UserUnlinkFromIdentityProviderMutation(this._request).fetch(id);
	}
	/**
	* Un-suspends a user. Can only be called by an admin or owner.
	*
	* @param id - required id to pass to unsuspendUser
	* @returns UserAdminPayload
	*/
	unsuspendUser(id) {
		return new UnsuspendUserMutation(this._request).fetch(id);
	}
	/**
	* Updates a user. Only available to organization admins and the user themselves.
	*
	* @param id - required id to pass to updateUser
	* @param input - required input to pass to updateUser
	* @returns UserPayload
	*/
	updateUser(id, input) {
		return new UpdateUserMutation(this._request).fetch(id, input);
	}
	/**
	* Creates a new ViewPreferences object.
	*
	* @param input - required input to pass to createViewPreferences
	* @returns ViewPreferencesPayload
	*/
	createViewPreferences(input) {
		return new CreateViewPreferencesMutation(this._request).fetch(input);
	}
	/**
	* Deletes a ViewPreferences.
	*
	* @param id - required id to pass to deleteViewPreferences
	* @returns DeletePayload
	*/
	deleteViewPreferences(id) {
		return new DeleteViewPreferencesMutation(this._request).fetch(id);
	}
	/**
	* Updates an existing ViewPreferences object.
	*
	* @param id - required id to pass to updateViewPreferences
	* @param input - required input to pass to updateViewPreferences
	* @returns ViewPreferencesPayload
	*/
	updateViewPreferences(id, input) {
		return new UpdateViewPreferencesMutation(this._request).fetch(id, input);
	}
	/**
	* Creates a new webhook.
	*
	* @param input - required input to pass to createWebhook
	* @returns WebhookPayload
	*/
	createWebhook(input) {
		return new CreateWebhookMutation(this._request).fetch(input);
	}
	/**
	* Deletes a Webhook.
	*
	* @param id - required id to pass to deleteWebhook
	* @returns DeletePayload
	*/
	deleteWebhook(id) {
		return new DeleteWebhookMutation(this._request).fetch(id);
	}
	/**
	* Updates an existing Webhook.
	*
	* @param id - required id to pass to updateWebhook
	* @param input - required input to pass to updateWebhook
	* @returns WebhookPayload
	*/
	updateWebhook(id, input) {
		return new UpdateWebhookMutation(this._request).fetch(id, input);
	}
	/**
	* Archives a state. Only states with issues that have all been archived can be archived.
	*
	* @param id - required id to pass to archiveWorkflowState
	* @returns WorkflowStateArchivePayload
	*/
	archiveWorkflowState(id) {
		return new ArchiveWorkflowStateMutation(this._request).fetch(id);
	}
	/**
	* Creates a new state, adding it to the workflow of a team.
	*
	* @param input - required input to pass to createWorkflowState
	* @returns WorkflowStatePayload
	*/
	createWorkflowState(input) {
		return new CreateWorkflowStateMutation(this._request).fetch(input);
	}
	/**
	* Updates a state.
	*
	* @param id - required id to pass to updateWorkflowState
	* @param input - required input to pass to updateWorkflowState
	* @returns WorkflowStatePayload
	*/
	updateWorkflowState(id, input) {
		return new UpdateWorkflowStateMutation(this._request).fetch(id, input);
	}
};

//#endregion
//#region src/client.ts
/**
* Validate and return default LinearGraphQLClient options
*
* @param options initial request options to pass to the graphql client
* @returns parsed graphql client options
*/
function parseClientOptions({ apiKey, accessToken, apiUrl, headers, ...opts }) {
	if (!accessToken && !apiKey) throw new Error("No accessToken or apiKey provided to the LinearClient - create one here: https://linear.app/settings/account/security");
	return {
		headers: {
			Authorization: accessToken ? accessToken.startsWith("Bearer ") ? accessToken : `Bearer ${accessToken}` : apiKey ?? "",
			...headers,
			"User-Agent": serializeUserAgent({ [process.env.npm_package_name ?? "@linear/sdk"]: process.env.npm_package_version ?? "unknown" })
		},
		apiUrl: apiUrl ?? "https://api.linear.app/graphql",
		...opts
	};
}
/**
* Create a Linear API client
*
* @param options request options to pass to the LinearGraphQLClient
*/
var LinearClient = class extends LinearSdk {
	options;
	client;
	constructor(options) {
		const parsedOptions = parseClientOptions(options);
		const graphQLClient = new LinearGraphQLClient(parsedOptions.apiUrl, parsedOptions);
		super((doc, vars) => this.client.request(doc, vars).catch((error) => {
			/** Catch and wrap errors from the LinearGraphQLClient */
			throw parseLinearError(error);
		}));
		this.options = parsedOptions;
		this.client = graphQLClient;
	}
};

//#endregion
//#region src/index.ts
/**
* @deprecated LinearWebhooks has been renamed: use LinearWebhookClient from `@linear/sdk/webhooks` instead. This alias will be removed in a future version.
*/
const LinearWebhooks = LinearWebhookClient;

//#endregion
export { ActorBot, AdministrableTeamsQuery, AgentActivitiesQuery, AgentActivity, AgentActivityActionContent, AgentActivityConnection, AgentActivityElicitationContent, AgentActivityErrorContent, AgentActivityPayload, AgentActivityPromptContent, AgentActivityQuery, AgentActivityResponseContent, AgentActivitySignal, AgentActivityThoughtContent, AgentActivityType, AgentActivityWebhookPayload, AgentSession, AgentSessionConnection, AgentSessionCreateOnCommentMutation, AgentSessionCreateOnIssueMutation, AgentSessionEventWebhookPayload, AgentSessionPayload, AgentSessionQuery, AgentSessionStatus, AgentSessionToPullRequest, AgentSessionToPullRequestConnection, AgentSessionType, AgentSessionUpdateExternalUrlMutation, AgentSessionWebhookPayload, AgentSession_ActivitiesQuery, AgentSessionsQuery, AiPromptRules, AirbyteIntegrationConnectMutation, AppUserNotificationWebhookPayload, AppUserTeamAccessChangedWebhookPayload, Application, ApplicationInfoQuery, ArchiveCustomerNeedMutation, ArchiveCycleMutation, ArchiveInitiativeMutation, ArchiveInitiativeUpdateMutation, ArchiveIntegrationMutation, ArchiveIssueMutation, ArchiveNotificationMutation, ArchivePayload, ArchiveProjectMutation, ArchiveProjectStatusMutation, ArchiveProjectUpdateMutation, ArchiveResponse, ArchiveRoadmapMutation, ArchiveWorkflowStateMutation, AsksChannelConnectPayload, AsksWebFormsAuthMutation, AsksWebFormsAuthResponse, Attachment, AttachmentConnection, AttachmentIssueQuery, AttachmentIssue_AttachmentsQuery, AttachmentIssue_BotActorQuery, AttachmentIssue_ChildrenQuery, AttachmentIssue_CommentsQuery, AttachmentIssue_DocumentsQuery, AttachmentIssue_FormerAttachmentsQuery, AttachmentIssue_FormerNeedsQuery, AttachmentIssue_HistoryQuery, AttachmentIssue_InverseRelationsQuery, AttachmentIssue_LabelsQuery, AttachmentIssue_NeedsQuery, AttachmentIssue_RelationsQuery, AttachmentIssue_SubscribersQuery, AttachmentLinkDiscordMutation, AttachmentLinkFrontMutation, AttachmentLinkGitHubIssueMutation, AttachmentLinkGitHubPrMutation, AttachmentLinkGitLabMrMutation, AttachmentLinkIntercomMutation, AttachmentLinkJiraIssueMutation, AttachmentLinkSalesforceMutation, AttachmentLinkSlackMutation, AttachmentLinkUrlMutation, AttachmentLinkZendeskMutation, AttachmentPayload, AttachmentQuery, AttachmentSourcesPayload, AttachmentSyncToSlackMutation, AttachmentWebhookPayload, AttachmentsForUrlQuery, AttachmentsQuery, AuditEntriesQuery, AuditEntry, AuditEntryConnection, AuditEntryType, AuditEntryTypesQuery, AuditEntryWebhookPayload, AuthIdentityProvider, AuthOrganization, AuthResolverResponse, AuthUser, AuthenticationLinearError, AuthenticationSessionResponse, AuthenticationSessionType, AuthenticationSessionsQuery, AvailableUsersQuery, BaseWebhookPayload, BootstrapLinearError, Comment, CommentChildWebhookPayload, CommentConnection, CommentPayload, CommentQuery, CommentResolveMutation, CommentUnresolveMutation, CommentWebhookPayload, Comment_BotActorQuery, Comment_ChildrenQuery, Comment_CreatedIssuesQuery, Comment_DocumentContentQuery, Comment_DocumentContent_AiPromptRulesQuery, Comment_ExternalThreadQuery, CommentsQuery, Connection, ContactPayload, ContextViewType, CreateAgentActivityMutation, CreateAttachmentMutation, CreateCommentMutation, CreateContactMutation, CreateCsvExportReportMutation, CreateCsvExportReportPayload, CreateCustomViewMutation, CreateCustomerMutation, CreateCustomerNeedMutation, CreateCustomerStatusMutation, CreateCustomerTierMutation, CreateCycleMutation, CreateDocumentMutation, CreateEmailIntakeAddressMutation, CreateEmojiMutation, CreateEntityExternalLinkMutation, CreateFavoriteMutation, CreateGitAutomationStateMutation, CreateGitAutomationTargetBranchMutation, CreateInitiativeMutation, CreateInitiativeRelationMutation, CreateInitiativeToProjectMutation, CreateInitiativeUpdateMutation, CreateInitiativeUpdateReminderMutation, CreateIntegrationGithubCommitMutation, CreateIntegrationTemplateMutation, CreateIntegrationsSettingsMutation, CreateIssueBatchMutation, CreateIssueLabelMutation, CreateIssueMutation, CreateIssueRelationMutation, CreateNotificationSubscriptionMutation, CreateOrJoinOrganizationResponse, CreateOrganizationFromOnboardingMutation, CreateOrganizationInviteMutation, CreateProjectLabelMutation, CreateProjectMilestoneMutation, CreateProjectMutation, CreateProjectRelationMutation, CreateProjectStatusMutation, CreateProjectUpdateMutation, CreateProjectUpdateReminderMutation, CreatePushSubscriptionMutation, CreateReactionMutation, CreateRoadmapMutation, CreateRoadmapToProjectMutation, CreateTeamMembershipMutation, CreateTeamMutation, CreateTemplateMutation, CreateTimeScheduleMutation, CreateTriageResponsibilityMutation, CreateViewPreferencesMutation, CreateWebhookMutation, CreateWorkflowStateMutation, CustomResourceWebhookPayload, CustomView, CustomViewConnection, CustomViewHasSubscribersPayload, CustomViewHasSubscribersQuery, CustomViewNotificationSubscription, CustomViewPayload, CustomViewQuery, CustomViewSuggestionPayload, CustomView_InitiativesQuery, CustomView_IssuesQuery, CustomView_OrganizationViewPreferencesQuery, CustomView_OrganizationViewPreferences_PreferencesQuery, CustomView_ProjectsQuery, CustomView_UserViewPreferencesQuery, CustomView_UserViewPreferences_PreferencesQuery, CustomView_ViewPreferencesValuesQuery, CustomViewsQuery, Customer, CustomerChildWebhookPayload, CustomerConnection, CustomerMergeMutation, CustomerNeed, CustomerNeedArchivePayload, CustomerNeedChildWebhookPayload, CustomerNeedConnection, CustomerNeedCreateFromAttachmentMutation, CustomerNeedNotification, CustomerNeedPayload, CustomerNeedQuery, CustomerNeedUpdatePayload, CustomerNeedWebhookPayload, CustomerNeed_ProjectAttachmentQuery, CustomerNeedsQuery, CustomerNotification, CustomerNotificationSubscription, CustomerPayload, CustomerQuery, CustomerStatus, CustomerStatusChildWebhookPayload, CustomerStatusConnection, CustomerStatusPayload, CustomerStatusQuery, CustomerStatusType, CustomerStatusesQuery, CustomerTier, CustomerTierChildWebhookPayload, CustomerTierConnection, CustomerTierPayload, CustomerTierQuery, CustomerTiersQuery, CustomerUnsyncMutation, CustomerUpsertMutation, CustomerVisibilityMode, CustomerWebhookPayload, CustomersQuery, Cycle, CycleArchivePayload, CycleChildWebhookPayload, CycleConnection, CycleNotificationSubscription, CyclePayload, CyclePeriod, CycleQuery, CycleShiftAllMutation, CycleStartUpcomingCycleTodayMutation, CycleWebhookPayload, Cycle_IssuesQuery, Cycle_UncompletedIssuesUponCloseQuery, CyclesQuery, DateResolutionType, Day, DeleteAttachmentMutation, DeleteCommentMutation, DeleteCustomViewMutation, DeleteCustomerMutation, DeleteCustomerNeedMutation, DeleteCustomerStatusMutation, DeleteCustomerTierMutation, DeleteDocumentMutation, DeleteEmailIntakeAddressMutation, DeleteEmojiMutation, DeleteEntityExternalLinkMutation, DeleteFavoriteMutation, DeleteGitAutomationStateMutation, DeleteGitAutomationTargetBranchMutation, DeleteInitiativeMutation, DeleteInitiativeRelationMutation, DeleteInitiativeToProjectMutation, DeleteIntegrationIntercomMutation, DeleteIntegrationMutation, DeleteIntegrationTemplateMutation, DeleteIssueImportMutation, DeleteIssueLabelMutation, DeleteIssueMutation, DeleteIssueRelationMutation, DeleteNotificationSubscriptionMutation, DeleteOrganizationCancelMutation, DeleteOrganizationDomainMutation, DeleteOrganizationInviteMutation, DeleteOrganizationMutation, DeletePayload, DeleteProjectLabelMutation, DeleteProjectMilestoneMutation, DeleteProjectMutation, DeleteProjectRelationMutation, DeleteProjectUpdateMutation, DeletePushSubscriptionMutation, DeleteReactionMutation, DeleteRoadmapMutation, DeleteRoadmapToProjectMutation, DeleteTeamCyclesMutation, DeleteTeamKeyMutation, DeleteTeamMembershipMutation, DeleteTeamMutation, DeleteTemplateMutation, DeleteTimeScheduleMutation, DeleteTriageResponsibilityMutation, DeleteViewPreferencesMutation, DeleteWebhookMutation, Document, DocumentArchivePayload, DocumentChildWebhookPayload, DocumentConnection, DocumentContent, DocumentContentChildWebhookPayload, DocumentContentHistoryPayload, DocumentContentHistoryQuery, DocumentContentHistoryType, DocumentNotification, DocumentPayload, DocumentQuery, DocumentSearchPayload, DocumentSearchResult, DocumentWebhookPayload, Document_CommentsQuery, DocumentsQuery, Draft, DraftConnection, EmailIntakeAddress, EmailIntakeAddressPayload, EmailIntakeAddressQuery, EmailIntakeAddressRotateMutation, EmailIntakeAddressType, EmailIntakeAddress_SesDomainIdentityQuery, EmailTokenUserAccountAuthMutation, EmailUnsubscribeMutation, EmailUnsubscribePayload, EmailUserAccountAuthChallengeMutation, EmailUserAccountAuthChallengeResponse, Emoji, EmojiConnection, EmojiPayload, EmojiQuery, EmojisQuery, Entity, EntityExternalLink, EntityExternalLinkConnection, EntityExternalLinkPayload, EntityExternalLinkQuery, EntityWebhookPayload, ExternalEntityInfo, ExternalEntityInfoGithubMetadata, ExternalEntityInfoJiraMetadata, ExternalEntitySlackMetadata, ExternalSyncService, ExternalUser, ExternalUserChildWebhookPayload, ExternalUserConnection, ExternalUserQuery, ExternalUsersQuery, Facet, FacetConnection, FacetPageSource, Favorite, FavoriteConnection, FavoritePayload, FavoriteQuery, Favorite_ChildrenQuery, FavoritesQuery, FeatureNotAccessibleLinearError, FeedSummarySchedule, FetchDataPayload, FileUploadDeletePayload, FileUploadMutation, ForbiddenLinearError, FrequencyResolutionType, FrontAttachmentPayload, GitAutomationState, GitAutomationStateConnection, GitAutomationStatePayload, GitAutomationStates, GitAutomationTargetBranch, GitAutomationTargetBranchPayload, GitHubCommitIntegrationPayload, GitHubEnterpriseServerInstallVerificationPayload, GitHubEnterpriseServerPayload, GitLabIntegrationCreatePayload, GitLinkKind, GithubOrgType, GoogleUserAccountAuthMutation, GraphQLClientError, GraphqlLinearError, GuidanceRuleWebhookPayload, IdentityProvider, IdentityProviderType, ImageUploadFromUrlMutation, ImageUploadFromUrlPayload, ImportFileUploadMutation, Initiative, InitiativeArchivePayload, InitiativeChildWebhookPayload, InitiativeConnection, InitiativeHistory, InitiativeHistoryConnection, InitiativeNotification, InitiativeNotificationSubscription, InitiativePayload, InitiativeQuery, InitiativeRelation, InitiativeRelationConnection, InitiativeRelationPayload, InitiativeRelationQuery, InitiativeRelationsQuery, InitiativeStatus, InitiativeTab, InitiativeToProject, InitiativeToProjectConnection, InitiativeToProjectPayload, InitiativeToProjectQuery, InitiativeToProjectsQuery, InitiativeUpdate, InitiativeUpdateArchivePayload, InitiativeUpdateChildWebhookPayload, InitiativeUpdateConnection, InitiativeUpdateHealthType, InitiativeUpdatePayload, InitiativeUpdateQuery, InitiativeUpdateReminderPayload, InitiativeUpdateWebhookPayload, InitiativeUpdate_CommentsQuery, InitiativeUpdatesQuery, InitiativeWebhookPayload, Initiative_DocumentContentQuery, Initiative_DocumentContent_AiPromptRulesQuery, Initiative_DocumentsQuery, Initiative_HistoryQuery, Initiative_InitiativeUpdatesQuery, Initiative_LinksQuery, Initiative_ProjectsQuery, Initiative_SubInitiativesQuery, InitiativesQuery, Integration, IntegrationActorWebhookPayload, IntegrationAsksConnectChannelMutation, IntegrationChildWebhookPayload, IntegrationConnection, IntegrationDiscordMutation, IntegrationFigmaMutation, IntegrationFrontMutation, IntegrationGitHubEnterpriseServerConnectMutation, IntegrationGitHubPersonalMutation, IntegrationGithubConnectMutation, IntegrationGithubImportConnectMutation, IntegrationGithubImportRefreshMutation, IntegrationGitlabConnectMutation, IntegrationGongMutation, IntegrationGoogleSheetsMutation, IntegrationHasScopesPayload, IntegrationHasScopesQuery, IntegrationIntercomMutation, IntegrationJiraPersonalMutation, IntegrationLoomMutation, IntegrationPayload, IntegrationQuery, IntegrationRequestMutation, IntegrationRequestPayload, IntegrationSalesforceMutation, IntegrationSentryConnectMutation, IntegrationService, IntegrationSlackAsksMutation, IntegrationSlackCustomViewNotificationsMutation, IntegrationSlackCustomerChannelLinkMutation, IntegrationSlackImportEmojisMutation, IntegrationSlackMutation, IntegrationSlackOrAsksUpdateSlackTeamNameMutation, IntegrationSlackOrgProjectUpdatesPostMutation, IntegrationSlackPersonalMutation, IntegrationSlackPostMutation, IntegrationSlackProjectPostMutation, IntegrationSlackWorkspaceNamePayload, IntegrationTemplate, IntegrationTemplateConnection, IntegrationTemplatePayload, IntegrationTemplateQuery, IntegrationTemplatesQuery, IntegrationZendeskMutation, IntegrationsQuery, IntegrationsSettings, IntegrationsSettingsPayload, IntegrationsSettingsQuery, InternalLinearError, InvalidInputLinearError, Issue, IssueAddLabelMutation, IssueArchivePayload, IssueAssignedToYouNotificationWebhookPayload, IssueBatchPayload, IssueChildWebhookPayload, IssueCommentMentionNotificationWebhookPayload, IssueCommentReactionNotificationWebhookPayload, IssueConnection, IssueEmojiReactionNotificationWebhookPayload, IssueExternalSyncDisableMutation, IssueFigmaFileKeySearchQuery, IssueFilterSuggestionPayload, IssueFilterSuggestionQuery, IssueHistory, IssueHistoryConnection, IssueImport, IssueImportCheckCsvQuery, IssueImportCheckPayload, IssueImportCheckSyncQuery, IssueImportCreateAsanaMutation, IssueImportCreateClubhouseMutation, IssueImportCreateCsvJiraMutation, IssueImportCreateGithubMutation, IssueImportCreateJiraMutation, IssueImportDeletePayload, IssueImportJqlCheckPayload, IssueImportJqlCheckQuery, IssueImportPayload, IssueImportProcessMutation, IssueImportSyncCheckPayload, IssueLabel, IssueLabelChildWebhookPayload, IssueLabelConnection, IssueLabelPayload, IssueLabelQuery, IssueLabelRestoreMutation, IssueLabelRetireMutation, IssueLabelWebhookPayload, IssueLabel_ChildrenQuery, IssueLabel_IssuesQuery, IssueLabelsQuery, IssueMentionNotificationWebhookPayload, IssueNewCommentNotificationWebhookPayload, IssueNotification, IssuePayload, IssuePriorityValue, IssuePriorityValuesQuery, IssueQuery, IssueRelation, IssueRelationConnection, IssueRelationHistoryPayload, IssueRelationPayload, IssueRelationQuery, IssueRelationType, IssueRelationsQuery, IssueReminderMutation, IssueRemoveLabelMutation, IssueSearchPayload, IssueSearchQuery, IssueSearchResult, IssueSlaWebhookPayload, IssueStateSpan, IssueStateSpanConnection, IssueStatusChangedNotificationWebhookPayload, IssueSubscribeMutation, IssueSuggestion, IssueSuggestionConnection, IssueSuggestionMetadata, IssueSuggestionState, IssueSuggestionType, IssueTitleSuggestionFromCustomerRequestPayload, IssueTitleSuggestionFromCustomerRequestQuery, IssueUnassignedFromYouNotificationWebhookPayload, IssueUnsubscribeMutation, IssueVcsBranchSearchQuery, IssueVcsBranchSearch_AttachmentsQuery, IssueVcsBranchSearch_BotActorQuery, IssueVcsBranchSearch_ChildrenQuery, IssueVcsBranchSearch_CommentsQuery, IssueVcsBranchSearch_DocumentsQuery, IssueVcsBranchSearch_FormerAttachmentsQuery, IssueVcsBranchSearch_FormerNeedsQuery, IssueVcsBranchSearch_HistoryQuery, IssueVcsBranchSearch_InverseRelationsQuery, IssueVcsBranchSearch_LabelsQuery, IssueVcsBranchSearch_NeedsQuery, IssueVcsBranchSearch_RelationsQuery, IssueVcsBranchSearch_SubscribersQuery, IssueWebhookPayload, IssueWithDescriptionChildWebhookPayload, Issue_AttachmentsQuery, Issue_BotActorQuery, Issue_ChildrenQuery, Issue_CommentsQuery, Issue_DocumentsQuery, Issue_FormerAttachmentsQuery, Issue_FormerNeedsQuery, Issue_HistoryQuery, Issue_InverseRelationsQuery, Issue_LabelsQuery, Issue_NeedsQuery, Issue_RelationsQuery, Issue_SubscribersQuery, IssuesQuery, JoinOrganizationFromOnboardingMutation, LabelNotificationSubscription, LeaveOrganizationMutation, LinearClient, LinearConnection, _generated_documents_exports as LinearDocument, LinearError, LinearErrorType, LinearGraphQLClient, LinearGraphQLError, LinearSdk, LinearWebhooks, LockTimeoutLinearError, LogoutAllSessionsMutation, LogoutMutation, LogoutOtherSessionsMutation, LogoutResponse, LogoutSessionMutation, NetworkLinearError, Node, Notification, NotificationArchiveAllMutation, NotificationArchivePayload, NotificationBatchActionPayload, NotificationCategory, NotificationCategoryPreferences, NotificationChannel, NotificationChannelPreferences, NotificationConnection, NotificationDeliveryPreferences, NotificationDeliveryPreferencesChannel, NotificationDeliveryPreferencesDay, NotificationDeliveryPreferencesSchedule, NotificationMarkReadAllMutation, NotificationMarkUnreadAllMutation, NotificationPayload, NotificationQuery, NotificationSnoozeAllMutation, NotificationSubscription, NotificationSubscriptionConnection, NotificationSubscriptionPayload, NotificationSubscriptionQuery, NotificationSubscriptionsQuery, NotificationUnsnoozeAllMutation, NotificationsQuery, OAuthAppWebhookPayload, OAuthAuthorizationWebhookPayload, OAuthClientApprovalStatus, OauthClientActorWebhookPayload, OauthClientApproval, OauthClientApprovalNotification, OauthClientChildWebhookPayload, Organization, OrganizationAcceptedOrExpiredInviteDetailsPayload, OrganizationCancelDeletePayload, OrganizationDeleteChallengeMutation, OrganizationDeletePayload, OrganizationDomain, OrganizationDomainAuthType, OrganizationExistsPayload, OrganizationExistsQuery, OrganizationInvite, OrganizationInviteConnection, OrganizationInviteFullDetailsPayload, OrganizationInvitePayload, OrganizationInviteQuery, OrganizationInviteStatus, OrganizationInvitesQuery, OrganizationIpRestriction, OrganizationMeta, OrganizationOriginWebhookPayload, OrganizationPayload, OrganizationQuery, OrganizationStartTrialForPlanMutation, OrganizationStartTrialMutation, OrganizationStartTrialPayload, Organization_IntegrationsQuery, Organization_LabelsQuery, Organization_ProjectLabelsQuery, Organization_SubscriptionQuery, Organization_TeamsQuery, Organization_TemplatesQuery, Organization_UsersQuery, OtherLinearError, OtherNotificationType, OtherNotificationWebhookPayload, PageInfo, PaginationNulls, PaginationOrderBy, PaginationSortOrder, PaidSubscription, PasskeyLoginStartResponse, PostNotification, PostType, ProductIntelligenceScope, Project, ProjectAddLabelMutation, ProjectArchivePayload, ProjectAttachment, ProjectChildWebhookPayload, ProjectConnection, ProjectExternalSyncDisableMutation, ProjectFilterSuggestionPayload, ProjectFilterSuggestionQuery, ProjectHistory, ProjectHistoryConnection, ProjectLabel, ProjectLabelChildWebhookPayload, ProjectLabelConnection, ProjectLabelPayload, ProjectLabelQuery, ProjectLabelRestoreMutation, ProjectLabelRetireMutation, ProjectLabelWebhookPayload, ProjectLabel_ChildrenQuery, ProjectLabel_ProjectsQuery, ProjectLabelsQuery, ProjectMilestone, ProjectMilestoneChildWebhookPayload, ProjectMilestoneConnection, ProjectMilestoneMoveIssueToTeam, ProjectMilestoneMovePayload, ProjectMilestoneMoveProjectTeams, ProjectMilestonePayload, ProjectMilestoneQuery, ProjectMilestoneStatus, ProjectMilestone_DocumentContentQuery, ProjectMilestone_DocumentContent_AiPromptRulesQuery, ProjectMilestone_IssuesQuery, ProjectMilestonesQuery, ProjectNotification, ProjectNotificationSubscription, ProjectPayload, ProjectQuery, ProjectRelation, ProjectRelationConnection, ProjectRelationPayload, ProjectRelationQuery, ProjectRelationsQuery, ProjectRemoveLabelMutation, ProjectSearchPayload, ProjectSearchResult, ProjectStatus, ProjectStatusArchivePayload, ProjectStatusChildWebhookPayload, ProjectStatusConnection, ProjectStatusCountPayload, ProjectStatusPayload, ProjectStatusQuery, ProjectStatusType, ProjectStatusesQuery, ProjectTab, ProjectUpdate, ProjectUpdateArchivePayload, ProjectUpdateChildWebhookPayload, ProjectUpdateConnection, ProjectUpdateHealthType, ProjectUpdatePayload, ProjectUpdateQuery, ProjectUpdateReminderFrequency, ProjectUpdateReminderPayload, ProjectUpdateWebhookPayload, ProjectUpdate_CommentsQuery, ProjectUpdatesQuery, ProjectWebhookPayload, Project_CommentsQuery, Project_DocumentContentQuery, Project_DocumentContent_AiPromptRulesQuery, Project_DocumentsQuery, Project_ExternalLinksQuery, Project_HistoryQuery, Project_InitiativeToProjectsQuery, Project_InitiativesQuery, Project_InverseRelationsQuery, Project_IssuesQuery, Project_LabelsQuery, Project_MembersQuery, Project_NeedsQuery, Project_ProjectMilestonesQuery, Project_ProjectUpdatesQuery, Project_RelationsQuery, Project_TeamsQuery, ProjectsQuery, PullRequestMergeMethod, PullRequestNotification, PullRequestReviewTool, PullRequestStatus, PushSubscription, PushSubscriptionPayload, PushSubscriptionTestPayload, PushSubscriptionTestQuery, PushSubscriptionType, RateLimitPayload, RateLimitResultPayload, RateLimitStatusQuery, RatelimitedLinearError, Reaction, ReactionPayload, ReactionWebhookPayload, RefreshGoogleSheetsDataMutation, ReleaseArchivePayload, ReleaseChannel, ReleasePayload, ReleasePipelineArchivePayload, ReleasePipelinePayload, ReleasePipelineType, ReleaseStageArchivePayload, ReleaseStagePayload, ReleaseStageType, RepositorySuggestion, RepositorySuggestionsPayload, Request, ResendOrganizationInviteByEmailMutation, ResendOrganizationInviteMutation, Roadmap, RoadmapArchivePayload, RoadmapConnection, RoadmapPayload, RoadmapQuery, RoadmapToProject, RoadmapToProjectConnection, RoadmapToProjectPayload, RoadmapToProjectQuery, RoadmapToProjectsQuery, Roadmap_ProjectsQuery, RoadmapsQuery, SLADayCountType, SamlTokenUserAccountAuthMutation, SearchDocumentsQuery, SearchDocuments_ArchivePayloadQuery, SearchIssuesQuery, SearchIssues_ArchivePayloadQuery, SearchProjectsQuery, SearchProjects_ArchivePayloadQuery, SemanticSearchPayload, SemanticSearchQuery, SemanticSearchResult, SemanticSearchResultType, SendStrategy, SesDomainIdentity, SesDomainIdentityDnsRecord, SlaStatus, SlackAsksTeamSettings, SlackChannelConnectPayload, SlackChannelNameMapping, SlackChannelType, SsoUrlFromEmailQuery, SsoUrlFromEmailResponse, SuccessPayload, SuspendUserMutation, SyncedExternalThread, Team, TeamArchivePayload, TeamChildWebhookPayload, TeamConnection, TeamMembership, TeamMembershipConnection, TeamMembershipPayload, TeamMembershipQuery, TeamMembershipsQuery, TeamNotificationSubscription, TeamOriginWebhookPayload, TeamPayload, TeamQuery, TeamRoleType, TeamWithParentWebhookPayload, Team_CyclesQuery, Team_GitAutomationStatesQuery, Team_IssuesQuery, Team_LabelsQuery, Team_MembersQuery, Team_MembershipsQuery, Team_ProjectsQuery, Team_StatesQuery, Team_TemplatesQuery, Team_WebhooksQuery, TeamsQuery, Template, TemplateConnection, TemplatePayload, TemplateQuery, TemplatesForIntegrationQuery, TemplatesQuery, TimeSchedule, TimeScheduleConnection, TimeScheduleEntry, TimeSchedulePayload, TimeScheduleQuery, TimeScheduleRefreshIntegrationScheduleMutation, TimeScheduleUpsertExternalMutation, TimeSchedulesQuery, TriageResponsibilitiesQuery, TriageResponsibility, TriageResponsibilityAction, TriageResponsibilityConnection, TriageResponsibilityManualSelection, TriageResponsibilityPayload, TriageResponsibilityQuery, TriageResponsibility_ManualSelectionQuery, UnarchiveCustomerNeedMutation, UnarchiveDocumentMutation, UnarchiveInitiativeMutation, UnarchiveInitiativeUpdateMutation, UnarchiveIssueMutation, UnarchiveNotificationMutation, UnarchiveProjectMutation, UnarchiveProjectStatusMutation, UnarchiveProjectUpdateMutation, UnarchiveRoadmapMutation, UnarchiveTeamMutation, UnknownLinearError, UnsuspendUserMutation, UpdateAgentSessionMutation, UpdateAttachmentMutation, UpdateCommentMutation, UpdateCustomViewMutation, UpdateCustomerMutation, UpdateCustomerNeedMutation, UpdateCustomerStatusMutation, UpdateCustomerTierMutation, UpdateCycleMutation, UpdateDocumentMutation, UpdateEmailIntakeAddressMutation, UpdateEntityExternalLinkMutation, UpdateFavoriteMutation, UpdateGitAutomationStateMutation, UpdateGitAutomationTargetBranchMutation, UpdateInitiativeMutation, UpdateInitiativeRelationMutation, UpdateInitiativeToProjectMutation, UpdateInitiativeUpdateMutation, UpdateIntegrationIntercomSettingsMutation, UpdateIntegrationsSettingsMutation, UpdateIssueBatchMutation, UpdateIssueImportMutation, UpdateIssueLabelMutation, UpdateIssueMutation, UpdateIssueRelationMutation, UpdateNotificationCategoryChannelSubscriptionMutation, UpdateNotificationMutation, UpdateNotificationSubscriptionMutation, UpdateOrganizationInviteMutation, UpdateOrganizationMutation, UpdateProjectLabelMutation, UpdateProjectMilestoneMutation, UpdateProjectMutation, UpdateProjectRelationMutation, UpdateProjectStatusMutation, UpdateProjectUpdateMutation, UpdateRoadmapMutation, UpdateRoadmapToProjectMutation, UpdateTeamMembershipMutation, UpdateTeamMutation, UpdateTemplateMutation, UpdateTimeScheduleMutation, UpdateTriageResponsibilityMutation, UpdateUserFlagMutation, UpdateUserMutation, UpdateUserSettingsMutation, UpdateViewPreferencesMutation, UpdateWebhookMutation, UpdateWorkflowStateMutation, UploadFile, UploadFileHeader, UploadPayload, UsageLimitExceededLinearError, User, UserActorWebhookPayload, UserAdminPayload, UserChangeRoleMutation, UserChildWebhookPayload, UserConnection, UserContextViewType, UserDemoteAdminMutation, UserDemoteMemberMutation, UserDiscordConnectMutation, UserExternalUserDisconnectMutation, UserFlagType, UserFlagUpdateOperation, UserLinearError, UserNotificationSubscription, UserPayload, UserPromoteAdminMutation, UserPromoteMemberMutation, UserQuery, UserRoleType, UserSettings, UserSettingsCustomSidebarTheme, UserSettingsCustomTheme, UserSettingsFlagPayload, UserSettingsFlagsResetMutation, UserSettingsFlagsResetPayload, UserSettingsPayload, UserSettingsQuery, UserSettingsTheme, UserSettingsThemeDeviceType, UserSettingsThemeMode, UserSettingsThemePreset, UserSettings_NotificationCategoryPreferencesQuery, UserSettings_NotificationCategoryPreferences_AppsAndIntegrationsQuery, UserSettings_NotificationCategoryPreferences_AssignmentsQuery, UserSettings_NotificationCategoryPreferences_CommentsAndRepliesQuery, UserSettings_NotificationCategoryPreferences_CustomersQuery, UserSettings_NotificationCategoryPreferences_DocumentChangesQuery, UserSettings_NotificationCategoryPreferences_FeedQuery, UserSettings_NotificationCategoryPreferences_MentionsQuery, UserSettings_NotificationCategoryPreferences_PostsAndUpdatesQuery, UserSettings_NotificationCategoryPreferences_ReactionsQuery, UserSettings_NotificationCategoryPreferences_RemindersQuery, UserSettings_NotificationCategoryPreferences_ReviewsQuery, UserSettings_NotificationCategoryPreferences_StatusChangesQuery, UserSettings_NotificationCategoryPreferences_SubscriptionsQuery, UserSettings_NotificationCategoryPreferences_SystemQuery, UserSettings_NotificationCategoryPreferences_TriageQuery, UserSettings_NotificationChannelPreferencesQuery, UserSettings_NotificationDeliveryPreferencesQuery, UserSettings_NotificationDeliveryPreferences_MobileQuery, UserSettings_NotificationDeliveryPreferences_Mobile_ScheduleQuery, UserSettings_NotificationDeliveryPreferences_Mobile_Schedule_FridayQuery, UserSettings_NotificationDeliveryPreferences_Mobile_Schedule_MondayQuery, UserSettings_NotificationDeliveryPreferences_Mobile_Schedule_SaturdayQuery, UserSettings_NotificationDeliveryPreferences_Mobile_Schedule_SundayQuery, UserSettings_NotificationDeliveryPreferences_Mobile_Schedule_ThursdayQuery, UserSettings_NotificationDeliveryPreferences_Mobile_Schedule_TuesdayQuery, UserSettings_NotificationDeliveryPreferences_Mobile_Schedule_WednesdayQuery, UserSettings_ThemeQuery, UserSettings_Theme_CustomQuery, UserSettings_Theme_Custom_SidebarQuery, UserUnlinkFromIdentityProviderMutation, UserWebhookPayload, User_AssignedIssuesQuery, User_CreatedIssuesQuery, User_DelegatedIssuesQuery, User_DraftsQuery, User_TeamMembershipsQuery, User_TeamsQuery, UsersQuery, VerifyGitHubEnterpriseServerInstallationQuery, ViewPreferences, ViewPreferencesPayload, ViewPreferencesType, ViewPreferencesValues, ViewType, ViewerQuery, Viewer_AssignedIssuesQuery, Viewer_CreatedIssuesQuery, Viewer_DelegatedIssuesQuery, Viewer_DraftsQuery, Viewer_TeamMembershipsQuery, Viewer_TeamsQuery, Webhook, WebhookConnection, WebhookFailureEvent, WebhookPayload, WebhookQuery, WebhooksQuery, WorkflowState, WorkflowStateArchivePayload, WorkflowStateChildWebhookPayload, WorkflowStateConnection, WorkflowStatePayload, WorkflowStateQuery, WorkflowState_IssuesQuery, WorkflowStatesQuery, parseLinearError };
//# sourceMappingURL=index.mjs.map